import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {

    // ---- permission-grid labels (QC 2026-09-21) ----------------------------
    // The grid derives its key from the catalog: resource + PascalCase action.
    // 129 of these had no entry, and i18n.js sets a parseMissingKeyHandler,
    // so the grid rendered a humanised echo of the key ("Delivery Verify") that
    // looks like a label but is not one. The seven hr* keys carry a DOTTED action
    // from the catalog; i18next resolves a flat key spelled with dots, so they
    // are written exactly as the grid builds them.
    job_cardsCreate: "Create new job cards",
    job_cardsCreate_only: "Create job cards with read-only access (for receptionists)",
    job_cardsRead: "View job cards",
    job_cardsUpdate: "Update job cards",
    job_cardsDelete: "Delete job cards",
    job_cardsAssign: "Assign job cards to staff",
    job_cardsView_all: "See all job cards and tasks in the garage (team lead). Without it, staff see only what is assigned to them.",
    job_cardsReopen: "Reopen closed job cards",
    job_cardsOverride_complete: "Force-complete a service, part or sub-task over an unapproved/incomplete state (admin override)",
    job_cardsForce_start: "Start work on a job card before its agreed scheduled start date (manager override)",
    job_cardsWaive_delay_fee: "Waive an accrued customer-delay fee (late parts or late collection) before it is billed",
    job_cardsReturn_car: "Cancel a job card and return the car when the customer refuses the price, including paying back or crediting any money already collected",
    job_cardsTransfer: "Move a job card to another branch",
    job_cardsOverride_intake_confirmation: "Confirm on the customer's behalf that they agreed to the job card in person (manager override)",
    issue_reportsManage: "Create, update and resolve issue reports",
    ai_diagnosisUse: "Use the AI diagnosis assistant (billable)",
    smart_assistantUse: "Use the smart assistant (speak or type queries and commands; billable)",
    findingsApprove: "Approve or reject inspection findings",
    insurance_companiesManage: "Manage insurance company registry",
    insurance_claimsManage: "Capture and update insurance claim details",
    tasksCreate: "Create tasks",
    tasksRead: "View tasks",
    tasksUpdate: "Update tasks",
    tasksDelete: "Delete tasks",
    tasksComplete: "Mark tasks as complete",
    tasksAssign: "Reassign a task to another staff member (team lead)",
    tasksApprove_diagnosis: "Approve or reject diagnosis performed by staff",
    mediaUpload: "Upload staff media (photos, videos, voice notes) and attach to tasks",
    partsRead: "View parts inventory",
    partsUpdate: "Update parts information",
    partsDelete: "Delete parts",
    partsRequest: "Request parts for jobs",
    part_categoriesManage: "Manage part categories",
    stockRead: "View stock levels and movement history",
    stockAdjust: "Add, remove or adjust stock quantities",
    stockOversell_reserved: "Add a part already promised to another quotation, job card or draft invoice (override)",
    purchase_invoicesRead: "View purchase invoices",
    purchase_invoicesCreate: "Create and edit purchase invoices",
    purchase_invoicesPost: "Post purchase invoices to the ledger (affects stock valuation)",
    purchase_invoicesPay: "Record supplier payments (full/partial) against purchase invoices",
    purchase_invoicesApprove: "Approve and post purchase invoices above the poster's limit, up to your own limit",
    discountsApprove: "Approve discounts above the requester's limit, up to your own limit",
    purchase_invoicesReverse: "Reverse a posted purchase invoice — issues a credit note, returns stock and voids supplier payments",
    vehiclesCreate: "Add new vehicles",
    vehiclesRead: "View vehicle information",
    vehiclesUpdate: "Update vehicle details",
    vehiclesDelete: "Delete vehicles",
    usersCreate: "Create new users",
    usersRead: "View users",
    usersUpdate: "Update user information and roles",
    usersDelete: "Deactivate or delete users",
    permissionsManage: "Manage roles and their permissions",
    appointmentsRead: "View appointments",
    appointmentsCreate: "Book new appointments",
    appointmentsManage: "Confirm, check-in, cancel, reschedule and convert appointments",
    appointmentsOverride: "Book or move appointments outside the booking rules, with a reason",
    booking_settingsManage: "Manage online booking settings and services",
    booking_pageManage: "Design and publish the online booking page and its custom domain",
    booking_analyticsRead: "View online booking visits, funnel and no-show rates",
    paymentsRead: "View payments",
    paymentsRecord: "Record payments against invoices",
    paymentsRefund: "Refund payments",
    paymentsSend_link: "Send a payment link to the customer",
    deliver_on_creditExecute: "Deliver a vehicle on credit before full payment",
    deliveryVerify: "Verify the delivery code (OTP) at vehicle handover",
    deliveryOverride: "Override delivery code (deliver without the customer code, with justification)",
    expensesView: "View the garage expenses ledger",
    expensesManage: "Manage job-card expenses and expense categories",
    expensesReverse: "Reverse an expense — issues a credit note and compensates any cash paid",
    credit_notesView: "View reversal credit notes issued against purchases and expenses",
    financialsAudit_view: "View the financial audit trail (who posted/edited/reversed, with before→after)",
    payment_settingsManage: "Configure payment provider, gateway and settlement settings",
    tenant_refundsRequest: "Request refunds on collected payments",
    payout_inquiriesCreate: "Raise payout inquiries",
    cashView: "View the cash & bank statement (balances, movements)",
    cashManage: "Set opening balances and post cash transfers/adjustments",
    cashEquity: "Post and view owner drawings, capital injections and partner loans",
    partnersView: "View partners (stakeholders) and their drawing/contribution statements",
    partnersManage: "Create, edit and deactivate partners (stakeholders)",
    account_mappingsView: "View the chart-of-accounts mapping used by the accountant's export",
    account_mappingsManage: "Set the account code for each cash reason, entry type and expense category",
    account_mappingsExport: "Download the accountant's journal export for a period",
    opening_balancesView: "View the company's opening-balance entry (cash, AR, AP)",
    opening_balancesManage: "Enter and sign off the opening balances at setup",
    bank_reconView: "View imported bank statements and their column mappings",
    bank_reconManage: "Upload bank statements, map a bank’s columns, and confirm OCR-read rows",
    reportsRead: "View reports and analytics",
    reportsExport: "Export reports",
    branchesView_all: "Access ALL branches of the company — the dashboard all-branches view, report branch selection, and switching to any branch — not just the home branch.",
    servicesCreate: "Create service types",
    servicesRead: "View service types",
    servicesUpdate: "Update service types",
    servicesDelete: "Delete service types",
    job_card_servicesApprove: "Approve or reject job-card service lines",
    quality_checkPerform: "Perform quality checks",
    quality_checkRead: "View quality check results",
    settingsRead: "View system settings",
    settingsUpdate: "Update system settings",
    garage_settingsRead: "View garage settings",
    garage_settingsUpdate: "Update garage information, VAT and notification settings",
    termsManage: "Draft, enhance and publish garage terms & conditions",
    companyManage: "Create, update or delete company records",
    gl_accountsView: "View the chart of accounts",
    gl_accountsManage: "Add accounts, rename them, deactivate them, and set opening balances",
    journal_entriesView: "View the trial balance, account ledgers, journal entries and period close",
    fixed_assetsView: "View the fixed asset register and depreciation preview",
    fixed_assetsManage: "Register, edit, dispose and depreciate fixed assets",
    journal_entriesPost: "Create and post journal entries, and close or reopen an accounting period",
    warrantiesView: "See warranty status on a job card and open the warranty monitor",
    warrantiesActivate: "Activate, renew and void a warranty on a completed service",
    warrantiesOverride_expired: "Reopen a job card under a warranty that has already expired",
    acceptance_evidenceRead: "Open customer approval records and download their certificates",
    acceptance_evidenceShare: "Send a customer their own approval certificate",
    journal_entriesReopen_period: "Reopen a closed accounting period (un-freeze a month a filed VAT return may rest on)",
    journal_entriesOverride_control: "Post a journal entry against a restricted control account (receivables, payables, VAT)",
    "fleet.reception.carCount_zero": "{{count}} cars",
    "fleet.reception.carCount_other": "{{count}} cars",
    "fleet.reception.carCount_many": "{{count}} cars",
    "fleet.reception.carCount_few": "{{count}} cars",
    "fleet.reception.carCount_two": "{{count}} cars",
    "fleet.reception.carCount_one": "{{count}} car",
    "fleet.reception.fleetTag": "Fleet",
    "fleet.reception.fleets": "Fleets",
    "fleet.reception.noCars": "No fleet cars match",
    "fleet.reception.searchCars": "Search by plate, VIN, make or group",
    "fleet.reception.pickCar": "Choose the fleet car",
    "hrEmployees.read": "View employee records",
    "hrEmployees.manage": "Create, update and deactivate employee records",
    "hrReports.read": "View HR reports",
    "hrPayroll.read": "View payroll runs, adjustments, loans, and leave records",
    "hrPayroll.manage": "Generate payroll runs and create/delete adjustments, loans, and leave entries",
    "hrPayroll.approve": "Approve, pay, and reverse payroll runs",
    "hrLabor_cost.view": "View and set technician hourly rates and labor-cost overrides",
    JobManagement: "Job Management",
    Financial: "Financial",
    TaskManagement: "Task Management",
    Inventory: "Inventory",
    CustomerManagement: "Customer Management",
    VehicleManagement: "Vehicle Management",
    UserManagement: "User Management",
    Appointments: "Appointments",
    Quality: "Quality",
    Reports: "Reports",
    ServiceManagement: "Service Management",
    System: "System",
    Finance: "Finance",
    Operations: "Operations",
    HR: "HR",

      // Payment-method picker fee breakdown (PaymentMethodPickerModal.js)
      "cancel": "Cancel",
      "fa.errSalvage": "Salvage value cannot be more than the cost.",
      "fa.purchaseCreated": "Asset registered and the purchase posted.",
      "fa.purchaseSaveFailed": "Couldn't register the asset or post its purchase.",
      "fa.selectCategory": "Choose a category",
      "pmp.commission": "Commission",
      "pmp.vat": "VAT {{pct}}%",
      "pmp.youReceive": "You receive",
      "pmp.notQualified": "Amount not qualified for this method (min {{min}})",
      // Permission grid label (PermissionManager.js keys off
      // `${resource}${Action}` — see companyManage below for the pre-existing
      // AR-only precedent this fills in for both languages).
      "companyData_export": "Export all company data (customers, HR/payroll, financials, media) as a downloadable archive",
      "purchase.invoiceDate": "Invoice date",
      "purchases.type.asset": "Fixed asset",
      "save": "Save",
      "settings.creditReminders": "Credit reminders",
      "settings.creditRemindersEnabled": "Enable credit reminders",
      "settings.leadDays": "Lead days (before due)",
      "settings.intervalDays": "Repeat every (days)",
      "settings.maxCount": "Max reminders",
      "settings.creditOverdueNotify": "Notify on overdue credit",
      "reports.creditAging": "Credit (AR aging)",
      "reports.bucketCurrent": "Current",
      "reports.bucket1_7": "1-7 days",
      "reports.bucket8_30": "8-30 days",
      "reports.bucket30plus": "30+ days",
      "reports.totalAr": "Total AR",
      "reports.overdueAr": "Overdue AR",
      "reports.customersWithAr": "Customers with AR",
      "reports.oldestDays": "Oldest (days)",
      "credit.outstandingAr": "Outstanding (AR)",
      "credit.dueBy": "Due by",
      "credit.byInvoice": "By invoice",
      "credit.deliverOnCredit": "Deliver on credit",
      "credit.dueInDays": "Due in (days)",
      "credit.dueDate": "Due date",
      "credit.daysFromToday": "{{days}} day(s) from today",
      "credit.dueInDaysRequired": "Due-in days is required",
      "credit.deliveredPendingPayment": "Delivered — payment pending",
      "credit.handoverConfirmed": "Handover confirmed by delivery code",
      "credit.sendPaymentLink": "Send payment link for balance",
      "credit.paymentLinkSent": "Payment link sent to customer",
      "credit.deliverOnCreditNote": "The tax invoice is issued now, at delivery. The remaining balance is recorded as a receivable due within the days you enter below.",
      "credit.deliverFailed": "Could not deliver on credit",
      "credit.delivering": "Delivering…",
      "credit.loadingBalance": "Loading balance…",
      "credit.invoicedTotal": "Invoiced total",
      "credit.amountPaid": "Amount paid",
      "credit.remainingBalance": "Remaining balance (receivable)",
      "credit.balanceUnavailable": "Balance unavailable",
      "credit.pendingPaymentNote": "The vehicle was released on credit. The tax invoice has been issued and the balance remains outstanding.",
      "credit.totalOwed": "Total outstanding",
      "credit.openingBalance": "Opening balance (carried forward)",
      "credit.customerCreditBalance": "Customer credit balance",
      "credit.customerCreditBalanceHint": "Available advance credit the customer can apply to a future invoice.",
      "credit.payableNow": "Payable now",
      "credit.payoutButton": "Pay out balance",
      "credit.payoutModalTitle": "Pay out customer credit",
      "credit.payoutAmountLabel": "Amount",
      "credit.payoutAmountHint": "Up to {{amount}} payable now",
      "credit.payoutMethodLabel": "Payout method",
      "credit.payoutConfirm": "Pay out",
      "credit.payoutInvalidAmount": "Enter an amount between 0 and {{amount}}",
      "credit.payoutSuccess": "Paid out {{amount}} — refund receipt(s): {{ids}}",
      "credit.payoutPartialError": "{{error}} (already paid out {{amount}} across {{count}} receipt(s) before failing)",
      "credit.payoutFailed": "Could not pay out customer credit",
      "credit.vehicleOwed": "Outstanding on this vehicle",
      "credit.outstandingAlert": "This {{who}} has an outstanding deliver-on-credit balance of {{amount}}.",
      "credit.who.customer": "customer",
      "credit.who.vehicle": "vehicle",
      "invoiced": "Invoiced",
      "itemInvoicedLocked": "This item is on a posted invoice and is locked. Reverse the invoice to edit it.",
      "pt.customerApprovedBadge": "Customer-approved",
      "pt.customerApprovedHint": "The customer approved this price. It can be lowered, not raised.",
      "credit.daysOverdue": "Days overdue",
      "credit.remindersTitle": "Credit reminders",
      "credit.remindersDesc": "Automated reminders for vehicles delivered on credit with an outstanding balance.",
      "credit.remindersEnabled": "Enable credit reminders",
      "credit.remindersEnabledDesc": "Send reminders to customers with unpaid credit balances.",
      "credit.leadDays": "Lead days",
      "credit.leadDaysDesc": "Days before the due date to start reminding.",
      "credit.intervalDays": "Interval (days)",
      "credit.intervalDaysDesc": "How often to resend a reminder.",
      "credit.maxCount": "Max reminders",
      "credit.maxCountDesc": "Cap on reminders sent per invoice.",
      "credit.remindersSave": "Save reminders",
      "credit.remindersSaving": "Saving…",
      "credit.remindersSaved": "Credit reminder settings saved",
      "credit.remindersSaveFailed": "Could not save credit reminder settings",
      "credit.remindersLoadFailed": "Could not load credit reminder settings",
      "settings.feedback.title": "Post-delivery feedback",
      "settings.feedback.desc": "Ask customers to rate their experience after the vehicle is delivered.",
      "settings.feedback.enabled": "Enable feedback requests",
      "settings.feedback.enabledDesc": "Send a rating request to customers after their vehicle is delivered.",
      "settings.feedback.delayHours": "Delay (hours)",
      "settings.feedback.delayHoursDesc": "Hours to wait after delivery before sending the request (0–168). 0 = send immediately on delivery.",
      "settings.feedback.googleUrl": "Google review link",
      "settings.feedback.googleUrlHelp": "The branch's Google review link, shown to customers after they submit feedback.",
      "settings.feedback.save": "Save",
      "settings.feedback.saving": "Saving…",
      "settings.feedback.saved": "Feedback settings saved",
      "settings.feedback.saveFailed": "Could not save feedback settings",
      "settings.feedback.loadFailed": "Could not load feedback settings",
      "settings.rejectionReasons.title": "Custom rejection reasons",
      "settings.rejectionReasons.desc": "Add up to 3 garage-specific reasons customers can pick when rejecting a quotation item, alongside the 7 built-in ones.",
      "settings.rejectionReasons.empty": "No custom reasons yet — customers see only the 7 built-in reasons.",
      "settings.rejectionReasons.arLabel": "Arabic label",
      "settings.rejectionReasons.enLabel": "English label",
      "settings.rejectionReasons.arPlaceholder": "e.g. قطعة غير متوفرة",
      "settings.rejectionReasons.enPlaceholder": "e.g. Part unavailable",
      "settings.rejectionReasons.codePreview": "Code: {{code}}",
      "settings.rejectionReasons.add": "Add reason",
      "settings.rejectionReasons.maxReached": "Maximum of 3 custom reasons",
      "settings.rejectionReasons.save": "Save reasons",
      "settings.rejectionReasons.saved": "Rejection reasons saved",
      "settings.rejectionReasons.saveFailed": "Could not save rejection reasons",
      "settings.rejectionReasons.errorRequired": "Both Arabic and English labels are required",
      "settings.rejectionReasons.errorTooLong": "Label must be 120 characters or fewer",
      "settings.rejectionReasons.errorCodeTooShort": "English label needs at least 2 letters or digits",
      "settings.rejectionReasons.errorCollision": "\"{{code}}\" matches a built-in reason — try different wording",
      "settings.rejectionReasons.errorDuplicate": "Two reasons produced the same code (\"{{code}}\") — try different wording",
      "feedback.title": "Customer Feedback",
      "feedback.avgRating": "Average rating",
      "feedback.responseRate": "Response rate",
      "feedback.totalResponses": "Total responses",
      "feedback.filterRating": "Rating",
      "feedback.allRatings": "All ratings",
      "feedback.noData": "No feedback yet",
      "feedback.viewJob": "View job",
      "feedback.comment": "Comment",
      "feedback.customer": "Customer",
      "feedback.branch": "Branch",
      "feedback.date": "Date",
      "supplier": "Supplier",
      "taskMonitoring.navTitle": "Task Monitoring",
      "taskMonitoring.pageTitle": "Task Monitoring",
      "taskMonitoring.taskName": "Task",
      "taskMonitoring.total": "Total tasks",
      "taskMonitoring.notStarted": "Not started",
      "taskMonitoring.inProgress": "In progress",
      "taskMonitoring.completed": "Completed",
      "taskMonitoring.overdue": "Overdue",
      "taskMonitoring.shouldHaveStarted": "Should have started",
      "taskMonitoring.employee": "Employee",
      "taskMonitoring.allEmployees": "All employees",
      "taskMonitoring.status": "Status",
      "taskMonitoring.allStatuses": "All statuses",
      "taskMonitoring.priority": "Priority",
      "taskMonitoring.allPriorities": "All priorities",
      "taskMonitoring.overdueOnly": "Overdue only",
      "taskMonitoring.search": "Search tasks, jobs, vehicles...",
      "taskMonitoring.dateFrom": "Estimated from",
      "taskMonitoring.dateTo": "Estimated to",
      "taskMonitoring.nudge": "Nudge",
      "taskMonitoring.nudgeSent": "Reminder sent",
      "taskMonitoring.nudgeCooldown": "Already nudged this hour",
      "taskMonitoring.reassign": "Reassign",
      "taskMonitoring.reassigned": "Reassigned and notified",
      "taskMonitoring.noData": "No tasks match the filters",
      "taskMonitoring.jobCard": "Job card",
      "taskMonitoring.vehicle": "Vehicle",
      "taskMonitoring.startedAt": "Started at",
      "taskMonitoring.estimatedCompletion": "Est. completion",
      "set.expenseCategories": "Expense Categories",
      "purchase.lineTypeStock": "Stock part",
      "purchase.lineTypeNonStock": "Expense",
      "purchase.expenseCategory": "Expense category",
      "purchase.selectExpenseCategory": "Select a category",
      "purchase.expenseDescription": "Description",
      "purchase.expenseDescriptionPlaceholder": "e.g. Shop rent, utilities, tools",
      "purchase.netAmount": "Net amount",
      "purchase.netAmountHint": "Amount before VAT",
      "purchase.vatRate": "VAT rate %",
      "purchase.vatAmount": "VAT amount",
      "purchase.vatAmountHint": "Net × rate (auto)",
      "purchase.itemExpenseCategoryRequired": "Item {{n}}: select an expense category",
      "purchase.itemAmountGreaterThanZero": "Item {{n}}: amount must be greater than zero",
      "purchase.fixHighlightedFields": "{{count}} fields need attention. They are highlighted below.",
      "purchaseWarranty.duration": "{{count}} months",
      "purchaseWarranty.expires": "Expires {{date}}",
      "purchase.warrantyHasWarranty": "This batch has a supplier warranty",
      "purchase.warrantyMonths": "Warranty (months)",
      "purchase.warrantyMonthsPlaceholder": "e.g. 12",
      "purchase.warrantyNotes": "Warranty reference or notes",
      "purchase.warrantyNotesPlaceholder": "Supplier claim reference, conditions",
      "purchase.warrantyMonthsRequired": "Item {{n}}: enter the warranty period in months",
      "purchase.warrantyMonthsInvalid": "Item {{n}}: warranty must be a whole number of months between 1 and 600",
      "exp.catTitle": "Expense Categories",
      "exp.catDesc": "Categories used on non-stock (expense) purchase-invoice lines for input-VAT and expense tracking.",
      "exp.catName": "Name (English)",
      "exp.catNamePlaceholder": "e.g. Rent",
      "exp.catNameAr": "Name (Arabic)",
      "exp.catNameArPlaceholder": "مثال: الإيجار",
      "exp.catAdd": "Add",
      "exp.catAdded": "Expense category added",
      "exp.catAddFailed": "Could not add expense category",
      "exp.catNameRequired": "Category name is required",
      "exp.catDeleted": "Expense category removed",
      "exp.catDeleteFailed": "Could not remove expense category",
      "exp.catDeleteConfirm": "Remove the expense category \"{{name}}\"?",
      "exp.catLoadFailed": "Could not load expense categories",
      "exp.catPreset": "Preset",
      "exp.catEmptyTitle": "No expense categories yet",
      "exp.catEmptyDesc": "Add your first custom category above.",
      "vat.tabPosition": "VAT Position",
      "vat.outputVat": "Output VAT",
      "vat.inputVat": "Input VAT",
      "vat.netDue": "Net VAT due",
      "vat.net": "Net",
      "vat.rate": "Rate",
      "vat.category": "Category",
      "vat.byCategory": "Input VAT by category",
      "vat.netExpense": "Net expense",
      "vat.notApplicable": "VAT not applicable",
      "vat.notApplicableDesc": "This garage is not VAT-registered, so there is no VAT position to report.",
      "rpt.tabExpenseVsPlan": "Expense vs Plan",
      "rpt.tabGarageExpenses": "Garage Expenses",
      "rpt.tabSuppliers": "Suppliers",
      // Task 5.2 (payment-receipt redesign): Customer Advances mini-report.
      "rpt.tabCustomerAdvances": "Customer Advances",
      "rpt.rcRef": "Receipt #",
      "rpt.original": "Original",
      "rpt.reserved": "Reserved",
      "rpt.consumed": "Consumed",
      "rpt.refunded": "Refunded",
      "rpt.available": "Available",
      "rpt.planned": "Planned",
      "rpt.actual": "Actual",
      "rpt.variance": "Variance",
      "rpt.supplier": "Supplier",
      "rpt.categories": "Categories",
      "rpt.byCategory": "By category",
      // HR statutory v2, T9 — payroll leg synthetic category + both-sources warning.
      "rpt.category.payroll": "Payroll",
      "rpt.payrollBothSourcesWarning": "Both a payroll run and manual salary-related expenses were found for this period. Review your expense categories to avoid double-counting payroll costs.",
      "nav.grp.overview": "Overview",
      "nav.grp.tenants": "Tenants",
      "nav.grp.comms": "Communication",
      "nav.grp.platform": "Platform",
      "nav.grp.operations": "Operations",
      "nav.grp.sales": "Sales & Billing",
      "nav.grp.financials": "Financials",
      "nav.grp.financeReports": "Financial reports",
      "nav.grp.inventory": "Inventory",
      "nav.grp.contacts": "Contacts",
      "nav.grp.hr": "HR",
      "nav.grp.admin": "Administration",
      "nav.grp.customers": "Customers & vehicles",
      "nav.grp.followUp": "Follow-up",
      "nav.grp.purchasesInventory": "Purchases & inventory",
      "nav.grp.reports": "Reports",
      "nav.grp.help": "Help",
      "cash.nav": "Cash Statement",
      "cash.title": "Cash Statement",
      "cash.subtitle": "Cash-on-hand and bank position with every movement.",
      "cash.opening": "Set/adjust opening",
      "cash.openingEdit": "Edit",
      "cash.transfer": "Transfer",
      "cash.adjustment": "Adjustment",
      "cash.kpiHand": "Cash on hand",
      "cash.kpiBank": "Cash in bank",
      "cash.kpiTotal": "Total cash",
      "cash.kpiPending": "Pending from gateway",
      "cash.kpiPendingHint": "Collected online, not yet settled to your bank.",
      "cash.kpiScopeHint": "Current position — all time (the date filter below does not affect it).",
      "cash.kpiScopeBranchHint": "Current position for the selected branch, plus the shared accounts — all time.",
      "cash.openingHeading": "Opening balances",
      "cash.acct.heading": "Accounts",
      "cash.acct.hint": "Every drawer and every bank the business holds money in. Bank reconciliation matches a statement to one of these, so each bank needs its own account.",
      "cash.acct.add": "Add account",
      "cash.acct.rename": "Rename",
      "cash.acct.default": "Default",
      "cash.acct.inactive": "Inactive",
      "cash.acct.kind": "Type",
      "cash.acct.name": "Account name",
      "cash.acct.namePlaceholder": "e.g. Al Rajhi — main account",
      "cash.acct.addTitle": "Add an account",
      "cash.acct.addDesc": "A second drawer, or another bank. The type cannot be changed later.",
      "cash.acct.renameTitle": "Rename account",
      "cash.acct.renameDesc": "Only the name changes. Balances and movements stay exactly as they are.",
      "cash.acct.created": "Account added",
      "cash.acct.renamed": "Account renamed",
      "cash.acct.errName": "Enter a name for the account",
      "cash.acct.errDuplicate": "Another account already uses this name",
      "cash.acct.errNoAccount": "Choose an account",
      "cash.acct.branch": "Branch",
      "cash.acct.branchHint": "A shared account belongs to the whole company. A branch account belongs to that branch only — its cash, its balance.",
      "cash.acct.shared": "Shared (all branches)",
      "cash.acct.activate": "Activate",
      "cash.acct.deactivate": "Deactivate",
      "cash.acct.activated": "Account activated",
      "cash.acct.deactivated": "Account deactivated",
      "cash.acct.filter": "Account",
      "cash.acct.allAccounts": "All accounts",
      "cash.errSameAccount": "Choose two different accounts",
      "cash.account": "Account",
      "cash.kind.hand": "Cash on hand",
      "cash.kind.bank": "Bank",
      "cash.kind.credit": "On credit (unpaid)",
      "pay.method.label": "Payment method",
      "pay.method.hand": "Cash",
      "pay.method.bank": "Bank Transfer",
      "pay.method.pos": "POS",
      "pay.method.credit": "On credit (unpaid)",
      "pay.method.cash": "Cash",
      "pay.method.bank_transfer": "Bank Transfer",
      "pay.method.cheque": "Cheque",
      "pay.method.manual": "Manual",
      "pay.method.fromAccount": "Paid from account",
      "pay.method.toAccount": "Deposited to account",
      "pay.method.loadFailed": "Couldn't load accounts. The default account for this method will be used.",
      "pay.method.retiredAccount": "Previously used account (no longer available)",
      "cash.locked": "Locked",
      "cash.asOf": "As of",
      "cash.current": "Current",
      "cash.openingTitle": "Opening balance",
      "cash.openingDesc": "Set the starting balance for an account. This locks once the first movement is recorded.",
      "cash.openingBalance": "Opening balance",
      "cash.openingDate": "Opening date",
      "cash.openingLocked": "Opening balance is locked — movements already exist for this account.",
      "cash.openingLockedHint": "This account already has movements, so its opening balance can no longer be changed.",
      "cash.openingSaved": "Opening balance saved",
      "cash.saveFailed": "Could not save. Please try again.",
      "cash.errAmount": "Enter a valid amount",
      "cash.from": "From",
      "cash.to": "To",
      "cash.amount": "Amount",
      "cash.note": "Note",
      "cash.transferTitle": "Transfer between accounts",
      "cash.transferDesc": "Move money between cash-on-hand and the bank.",
      "cash.transferDo": "Transfer",
      "cash.transferSaved": "Transfer recorded",
      "cash.direction": "Direction",
      "cash.dirIn": "Money in",
      "cash.dirOut": "Money out",
      "cash.adjustmentTitle": "Manual adjustment",
      "cash.adjustmentDesc": "Correct an account balance up or down.",
      "cash.adjustmentNoteHint": "A short reason for this adjustment (recommended).",
      "cash.adjustmentSaved": "Adjustment recorded",
      "cash.col.date": "Date",
      "cash.col.type": "Type",
      "cash.col.reference": "Reference",
      "cash.col.branch": "Branch",
      "cash.col.in": "In",
      "cash.col.out": "Out",
      "cash.col.running": "Running balance",
      "cash.entry.opening": "Opening",
      "cash.entry.transfer": "Transfer",
      "cash.entry.adjustment": "Adjustment",
      "cash.entry.expense": "Expense",
      "cash.entry.payment": "Payment",
      "cash.entry.refund": "Refund",
      "cash.entry.ar_collection": "Customer Payment",
      "cash.entry.expense_reversal": "Expense Reversal",
      "cash.entry.supplier_payment": "Supplier Payment",
      "cash.entry.supplier_payment_reversal": "Supplier Payment Reversal",
      "cash.entry.payout_settlement": "Payout Settlement",
      "cash.entry.own_account_payment": "Gateway Payment (Own Account)",
      "cash.entry.own_account_refund": "Gateway Refund (Own Account)",
      "cash.entry.payment_gateway_fee": "Gateway Fee",
      "cash.entry.sales_return": "Sales Return Refund",
      "cash.entry.customer_advance_in": "Customer Advance",
      "cash.entry.advance_refund_out": "Advance Refund",
      "cash.entry.owner_drawing": "Owner's Drawing",
      "cash.entry.owner_drawing_reversal": "Owner's Drawing Reversal",
      "cash.entry.capital_injection": "Capital Contribution",
      "cash.entry.capital_injection_reversal": "Capital Contribution Reversal",
      "cash.mv.in": "Money in",
      "cash.mv.out": "Money out",
      "cash.mv.inTitle": "Money coming in",
      "cash.mv.outTitle": "Money going out",
      "cash.mv.inDesc": "Somebody put money into the shop.",
      "cash.mv.outDesc": "Money left the shop — not a supplier bill and not a customer refund.",
      "cash.mv.reason": "What is this money for?",
      "cash.mv.reasonPick": "Choose…",
      "cash.mv.category": "Category",
      "cash.mv.glAccount": "Account",
      "cash.mv.errGlAccount": "Choose the account this money belongs to.",
      "cash.mv.accountsUnavailable": "The chart of accounts didn't load, so this form is asking for the reason instead.",
      "cash.mv.noReasons": "Nothing to choose yet. Ask your manager to set up the money reasons.",
      "cash.mv.amount": "How much?",
      "cash.mv.account": "From cash or bank?",
      "cash.mv.who": "Whose money is it?",
      "cash.mv.whoPick": "Choose a name",
      "cash.mv.noPartners": "The name must match a person on the Partners screen. Add them there first, then record this money against them.",
      "cash.mv.date": "Day",
      "cash.mv.note": "Note (not required)",
      "cash.mv.lockedHint": "The amount, the account and the day come from the bank line and cannot be changed here — they are what pairs this entry with it.",
      "cash.mv.attach": "Photo of the paper (not required)",
      "cash.mv.attachRemove": "Remove",
      "cash.mv.attachFailed": "The photo did not upload. Try again.",
      "cash.mv.uploading": "Sending the photo…",
      "cash.mv.savedIn": "Money in — saved",
      "cash.mv.savedOut": "Money out — saved",
      "cash.mv.errReason": "Choose what this money is for",
      "cash.mv.errAccount": "Choose cash or bank",
      "cash.mv.reverse": "Reverse",
      "cash.mv.reverseTitle": "Reverse this entry?",
      "cash.mv.reverseMessage": "The original entry stays on the ledger. A matching opposite entry is posted to reverse it. Nothing is deleted, and this can only be done once.",
      "cash.mv.reverseConfirm": "Reverse it",
      "cash.mv.reversed": "Reversed. The balance has been restored.",
      "cash.mv.reverseFailed": "Could not reverse the entry. Please try again.",
      // Generic keys, not fp.*-namespaced on purpose: dozens of existing
      // screens already call t('error')/t('refresh')/t('total') and were
      // silently humanized with no en/ar entry until Financial Position
      // exposed the gap. Fixes every prior caller, not just this screen.
      "error": "Error",
      "refresh": "Refresh",
      "total": "Total",
      "fp.nav": "Financial position",
      "fp.title": "Financial position",
      "fp.subtitle": "Where the business stands today: cash, receivables, payables, and stock on hand.",
      "fp.notBalanceSheet": "This is not a balance sheet",
      "fp.notBalanceSheetNote": "MotorMind does keep books — a chart of accounts, a journal, a trial balance, opening balances and period close. This screen is not reading them: it is the operational view, adding up the documents and cash movements the system holds today, so the figures below will not balance the way a statement of financial position does, and no prior-year figures are carried forward.",
      "fp.scopeNote": "Cash and bank cover the whole company. Receivables, payables and stock cover the branches you have access to.",
      "fp.asOf": "As of",
      "fp.cash": "Cash and bank",
      "fp.cashTotal": "Total cash",
      "fp.hand": "Cash on hand",
      "fp.bank": "Bank",
      "fp.col.account": "Account",
      "fp.col.kind": "Type",
      "fp.col.balance": "Balance",
      "fp.kind.hand": "Cash",
      "fp.kind.bank": "Bank",
      "fp.noAccounts": "No cash or bank accounts yet",
      "fp.noAccountsDesc": "Add your cash account and bank account first, so every payment is recorded against the right one.",
      "fp.receivables": "Accounts receivable",
      "fp.payables": "Accounts payable",
      "fp.stock": "Stock on hand",
      "fp.fromDocuments": "From invoices and bills",
      "fp.opening": "From the opening entry",
      "fp.equity": "Owner's equity",
      "fp.equityNote": "Amounts the owner or partners have drawn from or contributed to the business. A drawing is money that has left the cash account, so it is already reflected in the cash figure above — but it is not a business expense.",
      "fp.cutover": "Counted from",
      "fp.cutoverNote": "Drawings made before this date are excluded — they were recorded as ordinary expenses at the time.",
      "fp.cutoverMissing": "No start date is set yet. Set one so it is clear from which date these figures are counted.",
      "fp.bs.title": "Statement of financial position",
      "fp.bs.derivedFrom": "Derived from the general ledger as of {{date}}.",
      "fp.bs.openedOn": "Cumulative since the books opened on {{date}}.",
      "fp.bs.sourceSnapshot": "Closed period — frozen figures",
      "fp.bs.sourceLive": "Live — recalculated from open activity",
      "fp.bs.zeroOmitted": "Accounts with a zero balance are not listed. They are still counted in the totals.",
      "fp.bs.assets": "Assets",
      "fp.bs.liabilities": "Liabilities",
      "fp.bs.equity": "Equity",
      "fp.bs.netResult": "Result for the period",
      "fp.bs.netResultNote": "Revenue less expenses since the books opened.",
      "fp.bs.col.code": "Code",
      "fp.bs.col.account": "Account",
      "fp.bs.col.balance": "Balance",
      "fp.bs.noAccounts": "No account in this section carries a balance",
      "fp.bs.checkLine": "Assets = liabilities + equity",
      "fp.bs.balanced": "Balanced — assets equal liabilities plus equity",
      "fp.bs.unbalanced": "This statement does not balance",
      "fp.bs.unbalancedDesc": "Assets differ from liabilities plus equity by the amount shown. Do not file or send these figures: something in the ledger is wrong, and every total on this page is suspect until it is fixed. Open the trial balance and clear the documents it lists as blocked.",
      "fp.bs.difference": "Out of balance by",
      "fp.bs.operationalDetail": "Operational detail",
      "fp.bs.operationalDetailNote": "The same money, read the way the workshop works: which drawer and which bank account holds the cash, what customers have not paid yet, and what has not been paid to suppliers yet. Nothing here is additional to the statement above.",
      "fp.bs.switchOn.general_ledger": "Turn on the general ledger feature to read the real statement of financial position from the books.",
      "fp.bs.switchOn.opening_balances": "Sign off the opening balances so the ledger knows what the books opened with, and the real statement of financial position appears here.",
      "fp.bs.switchOn.scope": "A statement of financial position is a company-level statement. View this screen with all branches in scope to see it.",
      "fp.bs.switchOn.generic": "Turn on the general ledger and sign off the opening balances to get the real statement of financial position here.",
      "fp.bs.blockedTitle": "The statement could not be read",
      "fp.bs.notReadyTitle": "The statement of financial position is not ready yet",
      "fp.bs.warnedTitle": "It adds up, but not everything is filed",
      "fp.bs.warnedDesc": "The totals are right, yet some money is still parked on the unreconciled account because a document did not agree with itself. Clear those before you treat this as final.",
      "fp.bs.blockedDesc": "Something in the ledger stopped the figures being worked out, so the sections below are not your real balances. The problems are listed underneath.",
      "fp.bs.switchOn.chart": "The general ledger is on and the opening balances are signed off, but this company has no chart of accounts yet. Seed the chart to read the statement.",
      "fp.bs.switchOn.ledger_unavailable": "The ledger could not be read just now. The operational figures below are unaffected — try again, and contact support if it keeps happening.",
      "fa.title": "Fixed assets",
      "fa.subtitle": "What the company owns, what it has depreciated so far, and what each asset is still worth on the books.",
      "fa.accessDenied": "You don't have access to fixed assets",
      "fa.accessDeniedDesc": "Ask an administrator to grant the fixed_assets.view permission.",
      "fa.featureDisabled": "General ledger isn't enabled for this company",
      "fa.featureDisabledDesc": "Fixed assets depreciate into the general ledger, so the general ledger feature must be enabled first.",
      "fa.addAsset": "Register asset",
      "fa.linkPurchase": "Link purchase",
      "fa.addLegacy": "Owned before MotorMind",
      "fa.status.cancelled": "Cancelled",
      "fa.reissueInvoice": "Reissue invoice",
      "fa.reissueTitle": "Reissue sale invoice",
      "fa.reissueDesc": "Reissuing replaces the void invoice with a new tax invoice dated today. You may optionally choose a corrected buyer.",
      "fa.reissueConfirm": "Reissue invoice",
      "fa.reissued": "Invoice reissued.",
      "fa.reissueFailed": "Couldn't reissue the invoice.",
      "fa.correctedBuyerOptional": "Corrected buyer (optional)",
      "fa.buyer": "Buyer",
      "fa.disposeReason": "Disposal reason",
      "fa.disposeReason.scrapped": "Scrapped",
      "fa.disposeReason.sale": "Sale",
      "fa.salePriceNet": "Sale price (net of VAT)",
      "fa.vatRate15": "VAT (15%)",
      "fa.totalWithVat": "Total",
      "fa.dateFixedToday": "The disposal date is fixed to today for an invoiced sale.",
      "fa.selectBranch": "Select branch",
      "fa.disposeSuccessTitle": "Disposal complete",
      "fa.disposeSuccessInvoice": "Sale invoice issued",
      "fa.invoiceNumber": "Invoice number",
      "fa.zatcaStatus": "ZATCA status",
      "fa.publicLink": "Public invoice link",
      "fa.copyLink": "Copy link",
      "fa.linkCopied": "Link copied to clipboard",
      "fa.done": "Done",
      "fa.saleInvoice": "Sale invoice",
      "fa.errBuyerRequired": "Choose the asset buyer.",
      "fa.errGarageRequired": "Choose an accessible branch for this company-wide asset sale.",
      "fa.errSaleProceedsRequired": "A fixed-asset sale requires positive proceeds, net of VAT.",
      "fa.errDisposalDateRequired": "Disposal date is required.",
      "fa.errDisposalDateFuture": "Disposal date cannot be in the future.",
      "fa.dep.postThrough": "Post through {{period}}",
      "fa.dep.foldedHeading": "Folded depreciation",
      "fa.dep.foldedIntro": "Charges that could not be posted in their original months and were folded into the next open month:",
      "fa.dep.foldedReason.closed": "Closed period",
      "fa.dep.foldedReason.before_books": "Before books opening",
      "fa.dep.foldedReason.before_recognition": "Before recognition",
      // Fixed assets — linking a posted purchase, and assets owned before MotorMind
      "fa.link.title": "Link this asset to its purchase",
      "fa.link.desc": "Pick the invoice line that paid for this asset. The cost moves off the expense account and onto the asset's own account, backdated to the invoice — the ledger is corrected, not annotated.",
      "fa.link.assetCost": "This asset is registered at",
      "fa.link.col.invoice": "Invoice",
      "fa.link.col.date": "Invoice date",
      "fa.link.col.description": "Line",
      "fa.link.col.qty": "Qty",
      "fa.link.col.cost": "Line total",
      "fa.link.exactMatch": "Matches the registered cost",
      "fa.link.linkCta": "Link",
      "fa.link.linkedBadge": "Linked",
      "fa.link.linkedDesc": "This asset's cost is already capitalised from this invoice line. Unlink it only if it was attached to the wrong line — unlinking puts the cost back where it was.",
      "fa.link.capitalised": "Capitalised",
      "fa.link.unlinkCta": "Unlink",
      "fa.link.unlinkTitle": "Unlink this purchase?",
      "fa.link.unlinkMessage": "The cost goes back to the account the invoice originally posted it to, and this asset is left with no purchase behind it.",
      "fa.link.unlinkSuccess": "Purchase unlinked.",
      "fa.link.linkSuccess": "Linked to invoice {{ref}}. The cost is now on the asset's account.",
      "fa.link.empty": "No invoice line to link",
      "fa.link.emptyDesc": "Nothing on a posted supplier invoice looks like it paid for this asset. That is normal when the company already owned the asset before it started using this system, or when the purchase was entered as a stock line. Stock lines cannot be capitalised — reverse the invoice and re-enter that line as a non-stock line if it really was an asset.",
      "fa.link.loadFailed": "Couldn't load the invoice lines that could be linked to this asset.",
      "fa.link.failed": "Couldn't link this purchase.",
      "fa.link.difference": "Difference:",
      "fa.link.excess": "Over the invoice by:",
      "fa.link.field.purchase_invoice_item_id": "Invoice line",
      "fa.link.field.purchase_invoice_id": "Invoice",
      "fa.link.field.asset_id": "Asset",
      "fa.link.why.FA_LINK_ALREADY_LINKED": "That line has already been capitalised onto a different asset. Pick another line, or unlink it from the other asset first.",
      "fa.link.why.FA_LINK_ASSET_LINKED": "This asset already has a purchase behind it. Unlink that one before attaching a different line.",
      "fa.link.why.FA_LINK_STOCK_LINE": "A stock line has already gone into inventory, so its cost cannot also become an asset. Reverse the invoice and re-enter that line as a non-stock line.",
      "fa.link.why.FA_LINK_INVOICE_DRAFT": "Only a posted invoice has moved any money. Post the invoice, then link it.",
      "fa.link.why.FA_LINK_INVOICE_REVERSED": "This invoice was reversed. Its credit note would not reverse the asset side, so the two would drift apart. Link the replacement invoice instead.",
      "fa.link.why.FA_LINK_PERIOD_CLOSED": "The month this invoice belongs to is closed, and this correction would post inside it. Reverse the invoice and re-enter it in an open month.",
      "fa.link.why.FA_LINK_EXCEEDS_INVOICE": "The asset lines on this invoice would add up to more than the invoice itself, net of VAT. Check whether the registered cost mistakenly includes VAT.",
      "fa.link.why.FA_LINK_COST_MISMATCH": "The line and the register disagree about what this asset cost. Correct whichever one is wrong before linking — the ledger will follow the invoice, not the register.",
      "fa.link.why.FA_LINK_DEPRECIATED": "This asset has already depreciated, so its cost is spread across posted months that unlinking cannot take back. Dispose of the asset instead.",
      "legacyAssets.title": "Record previously owned assets",
      "legacyAssets.done": "Close",
      "legacyAssets.partialTitle": "Part of this file was saved and part was not.",
      "legacyAssets.partialSaved": "{{count}} assets are now recorded. They are saved — do not upload the whole file again.",
      "legacyAssets.partialFailedRows": "Rows {{rows}} could not be saved.",
      "legacyAssets.partialFailedCount": "{{count}} rows could not be saved.",
      "legacyAssets.partialRow": "Row {{row}}: {{message}}",
      "legacyAssets.partialRowFields": "Check: {{fields}}",
      "legacyAssets.rowJoin": ", ",
      "legacyAssets.rowNumbersAreFileLines": "Row numbers are the line numbers in your file, counting the header line at the top.",
      "legacyAssets.partialRetrySafe": "Fix only these rows and send them again. The assets already saved will not be recorded twice.",
      "legacyAssets.partialRetryCheck": "Open the asset register and check what was saved before you send this file again.",
      "legacyAssets.description": "Bring assets the company already owned into MotorMind at their current book position.",
      "legacyAssets.mode": "Entry method",
      "legacyAssets.single": "One asset",
      "legacyAssets.many": "Many assets (CSV)",
      "legacyAssets.noCashEffect": "This records an existing asset and its opening depreciation. It creates no purchase invoice, pays nobody, and has no cash effect.",
      "legacyAssets.branch": "Branch",
      "legacyAssets.companyLevel": "Company-level",
      "legacyAssets.cost": "Original cost",
      "legacyAssets.asOfPeriod": "As-of month",
      "legacyAssets.asOfHint": "the as-of month must have ended and be on or after your opening-balance date",
      "legacyAssets.asOfRequired": "As-of month is required: the first depreciation charge is posted in the month after it.",
      "legacyAssets.asOfRequiredRows": "As-of month is missing: as_of_period is required in every row. Not sent: rows {{rows}}.",
      "legacyAssets.bookValue": "Book value",
      "legacyAssets.remainingMonthsInput": "Remaining months",
      "legacyAssets.sar": "SAR",
      "legacyAssets.save": "Record asset",
      "legacyAssets.import": "Import assets",
      "legacyAssets.csvHelp": "Upload or paste a CSV using these exact headers. Money columns are whole halalas; as_of_period is required in every row.",
      "legacyAssets.csvFile": "Upload CSV",
      "legacyAssets.csvPaste": "Or paste CSV",
      "legacyAssets.rowsReady": "{{count}} data rows ready",
      "legacyAssets.errorRowField": "Row {{row}}, {{field}}",
      "legacyAssets.unknownField": "Unknown field",
      "legacyAssets.nothingSaved": "Nothing was saved. Every row is checked before any asset is written, so it is safe to fix the file and upload it again.",
      "legacyAssets.saveFailed": "Couldn't record the legacy asset.",
      "legacyAssets.readFailed": "Couldn't read this CSV file.",
      "fa.loadFailed": "Couldn't load the fixed asset register.",
      "fa.totals": "Total",
      "fa.empty": "Nothing registered yet",
      "fa.emptyDesc": "A fixed asset is something the company keeps and uses for more than a year — a vehicle lift, a workshop building, a laptop, a delivery van. Registering it here lets the system depreciate it into the general ledger automatically every month, instead of someone remembering to journal it by hand. The purchase itself is not booked here; that already happened in the invoice or expense that paid for it.",
      "fa.col.code": "Code",
      "fa.col.name": "Asset",
      "fa.col.branch": "Branch",
      "fa.col.acquired": "Acquired",
      "fa.col.cost": "Cost",
      "fa.col.accumulated": "Accumulated depreciation",
      "fa.col.nbv": "Net book value",
      "fa.col.status": "Status",
      "fa.cat.equipment": "Equipment",
      "fa.cat.tools": "Tools",
      "fa.cat.vehicles": "Vehicles",
      "fa.cat.furniture": "Furniture",
      "fa.cat.fittings": "Fittings",
      "fa.cat.buildings": "Buildings",
      "fa.cat.improvements": "Improvements",
      "fa.cat.computers": "Computers",
      "fa.cat.software": "Software",
      "fa.status.active": "Active",
      "fa.status.fully_depreciated": "Fully depreciated",
      "fa.status.disposed": "Disposed",
      "fa.branch": "Branch",
      "fa.branchCompany": "Company-level",
      "fa.nameEn": "Name (English)",
      "fa.nameAr": "Name (Arabic)",
      "fa.category": "Category",
      "fa.cost": "Cost",
      "fa.acquiredOn": "Acquisition date",
      "fa.salvage": "Salvage value",
      "fa.salvageHint": "What it will be worth at the end of its life. Usually 0.",
      "fa.life": "Useful life",
      "fa.lifeHint": "Straight-line over this many months, starting the month of acquisition.",
      "fa.lifeLockedHint": "Locked — depreciation has already been posted against this asset.",
      "fa.months": "months",
      "fa.addTitle": "Register asset",
      "fa.addDesc": "The cost is not booked here — it already reached the journal through the purchase that paid for it. This register is what the monthly depreciation reads.",
      "fa.editTitle": "Edit asset",
      "fa.editDesc": "Cost, acquisition date and useful life are locked once depreciation has been posted for this asset.",
      "fa.lockedBanner": "Depreciation has already been posted for this asset, so its cost, acquisition date and useful life can no longer change — editing them would rewrite months already in the ledger. Everything else can still be edited.",
      "fa.saved": "Asset updated.",
      "fa.created": "Asset registered.",
      "fa.saveFailed": "Couldn't save the asset.",
      "fa.errName": "Enter a name in English or Arabic.",
      "fa.errCost": "Enter the asset's cost.",
      "fa.errLife": "The useful life must be at least one month.",
      "fa.edit": "Edit",
      "fa.dispose": "Dispose",
      "fa.disposeTitle": "Dispose of this asset?",
      "fa.disposeMessage": "“{{name}}” ({{code}}) leaves the register and stops depreciating from the date you give. This cannot be undone from this screen.",
      "fa.disposeDate": "Disposal date",
      "fa.disposeProceeds": "Sale proceeds",
      "fa.disposeProceedsHint": "What the asset sold for, if anything. 0 if it was scrapped.",
      "fa.disposeConfirm": "Dispose",
      "fa.disposed": "Asset disposed.",
      "fa.disposeFailed": "Couldn't dispose of the asset.",
      "fa.dep.prevMonth": "Previous month",
      "fa.dep.nextMonth": "Next month",
      "fa.dep.run": "Run depreciation",
      "fa.dep.total": "Total for the month",
      "fa.dep.nothing": "Nothing to depreciate for this month — no active asset has reached its acquisition month yet, or everything is fully depreciated or disposed.",
      "fa.dep.idempotentHint": "Posting is per asset per month: running the same month twice charges once. MotorMind posts each month automatically on the 1st day of the month after it, dating the charge to the month end; this button is for when you need a month posted sooner.",
      "fa.dep.runSuccess": "Depreciation posted for {{period}}.",
      "fa.dep.runFailed": "Couldn't post depreciation for this month.",
      "fa.dep.postedTitle": "Already posted for {{period}}",
      "fa.dep.postedDated": "Dated {{date}} in the ledger — the last day of the month it covers. A trial balance or account statement that ends before that date will not show it.",
      "fa.dep.postedAccounts": "Dr {{expense}} / Cr {{accum}}",
      "fa.dep.confirmTitle": "Post depreciation for {{period}} now?",
      "fa.dep.confirmCta": "Post it now",
      "fa.dep.confirmAutomatic": "You do not normally need this button. MotorMind posts each month's depreciation by itself, early on the 1st day of the month after it (Riyadh time). Posting by hand only brings that forward.",
      "fa.dep.confirmWhatPosts": "{{assets}} asset(s) will be charged across {{months}} month(s), {{total}} SAR in total. Each month is dated its own month-end. Each asset is debited to its depreciation expense account and credited to its accumulated depreciation account.",
      "fa.dep.confirmCurrentMonth": "{{period}} has not ended yet. A full month is charged anyway, dated {{date}} — the last day of the month, so the charge will not appear in any report whose window ends before then.",
      "fa.dep.confirmIrreversible": "A month already posted is never charged twice, and this screen cannot undo it. Correcting a posted month needs a manual journal voucher.",
      "fa.coverage.title": "An asset\u2019s cost was never recorded in the ledger",
      "fa.coverage.line": "{{n}} asset(s) registered on account {{code}} are worth {{registered}} SAR, but the ledger holds only {{booked}} SAR on that account.",
      "fa.coverage.fix": "Registering an asset here does not record what it cost — the purchase invoice or expense does that. Enter the purchase; or, if the company already owned the asset before MotorMind, add it to your opening balances while they are still a draft. Once opening balances are signed off, the correction has to be a journal voucher.",
      "pt.nav": "Partners",
      "pt.title": "Partners",
      "pt.subtitle": "The people who put money into the shop or take money out of it.",
      "pt.new": "Add a person",
      "pt.newTitle": "Add a person",
      "pt.editTitle": "Edit person",
      "pt.edit": "Edit",
      "pt.name": "Name",
      "pt.nameAr": "Name in Arabic",
      "pt.notes": "Notes",
      "pt.active": "Working with us",
      "pt.inactive": "Stopped",
      "pt.inactiveHint": "Stopping someone keeps all their old records. Names cannot be changed after money is recorded under them.",
      "pt.errName": "Write the name",
      "pt.saved": "Saved",
      "pt.empty": "No people yet",
      "pt.emptyDesc": "Add the owner or a partner first. Then you can record the money they put in or take out.",
      "pt.statement": "Money record",
      "pt.stmtTitle": "Money record",
      "pt.startDate": "Counts from",
      "pt.disclosureNote": "Money moved before this date is not on this record — it was written down as a regular expense back then, before this feature existed.",
      "pt.drawn": "Money taken out",
      "pt.contributed": "Money put in",
      "pt.net": "Difference",
      "pt.loanBalance": "Outstanding loan balance",
      "pt.col.reason": "What for",
      "pt.stmtEmpty": "Nothing in these dates",
      "pt.stmtEmptyDesc": "This person has no money in or out between these dates.",
      "pt.accessDenied": "No access",
      "pt.accessDeniedDesc": "You do not have permission to see the partners.",
      "bank.reviewTitle": "Check the bank statement",
      "bank.reviewSubtitle": "The computer read these lines off a photo. Read them and say they are right.",
      "bank.reviewEmpty": "Nothing to check",
      "bank.reviewEmptyDesc": "Statements that come as a file are read exactly. Only a photo of a statement needs checking here.",
      "bank.ocrWarning": "Nothing is recorded until you say it is right",
      "bank.ocrWarningDesc": "These lines were read from a picture, so a number can be read wrong. Compare them with the paper in your hand.",
      "bank.opening": "Balance at the start",
      "bank.closing": "Balance at the end",
      "bank.period": "Dates",
      "bank.rows": "Lines",
      "bank.balancedNote": "The start balance plus these lines equals the end balance. If it did not, the file would have been refused.",
      "bank.balancedSelfNote": "The start and end balance were read by AI from the same page as these rows, so the arithmetic agreeing does not prove a row was not missed. Check the totals against the statement itself.",
      "bank.col.date": "Date",
      "bank.col.description": "What it says",
      "bank.col.reference": "Number",
      "bank.col.amount": "Amount",
      "bank.col.direction": "In or out",
      "bank.in": "Money in",
      "bank.out": "Money out",
      "bank.confirm": "Yes, these are right",
      "bank.confirmed": "Saved",
      "bank.confirmFailed": "Could not save. Try again.",
      "bank.loadFailed": "Could not open the statement.",
      "br.title": "Bank reconciliation",
      "br.subtitle": "Match what the bank says against what your books say, and show what is left.",
      "br.startHint": "Import the bank statement for the period first — the bank's own opening and closing balances are read from it, not typed here.",
      "br.chooseAccount": "Choose a bank account",
      "br.importStatement": "Import a statement",
      "bank.imp.title": "Import a bank statement",
      "bank.imp.subtitle": "The statement is the authority. Its opening and closing balances are read from the file you upload, and the reconciliation is proved against them.",
      "bank.imp.back": "Back to reconciliation",
      "bank.imp.account": "Bank account",
      "bank.imp.bank": "Bank",
      "bank.imp.bankPlaceholder": "e.g. Al Rajhi",
      "bank.imp.opening": "Opening balance on the statement",
      "bank.imp.closing": "Closing balance on the statement",
      "bank.imp.file": "Statement file",
      "bank.imp.fileHint": "CSV, Excel, or PDF. A PDF with real text is read exactly; a scan is read by OCR and you will be asked to check every row before it counts.",
      "bank.imp.read": "Read the file",
      "bank.imp.readAi": "Read it with AI",
      "bank.imp.readHint": "Read the file first to see what it contains before importing. \u201cRead it with AI\u201d needs no column mapping and fills the balances from the statement itself.",
      "bank.imp.balanceHint": "Leave empty to read it from the statement",
      "bank.imp.balanceAuto": "From the statement",
      "bank.imp.prevTitle": "What this file contains",
      "bank.imp.prevCount": "{{count}} transactions found",
      "bank.imp.prevByAi": "read by AI \u2014 check it before importing",
      "bank.imp.prevBalanced": "Balances check out",
      "bank.imp.prevSelfReported": "Balances agree — but the model reported both, so this is not an independent check",
      "bank.imp.prevUnbalanced": "Off by {{delta}} \u2014 a row was dropped or misread",
      "bank.imp.prevTruncated": "Showing the first {{shown}} of {{total}} transactions.",
      "bank.imp.submit": "Import",
      "bank.imp.done": "Statement imported",
      "bank.imp.errAccount": "Choose the bank account this statement belongs to",
      "bank.imp.errBank": "Enter the bank's name",
      "bank.imp.errBalances": "Enter both the opening and the closing balance from the statement",
      "bank.imp.errFile": "Choose the statement file",
      "bank.imp.mapNeeded": "Tell us which column is which — once for this bank",
      "bank.imp.mapTitle": "Which column is which?",
      "bank.imp.mapDesc": "We never guess: a column read as the amount when it is the running balance would prove a reconciliation that is false. Answer once and every later statement from this bank imports straight away.",
      "bank.imp.col.date": "Date",
      "bank.imp.col.description": "Description",
      "bank.imp.col.reference": "Reference",
      "bank.imp.col.amount": "Amount (signed)",
      "bank.imp.col.debit": "Debit (out)",
      "bank.imp.col.credit": "Credit (in)",
      "bank.imp.col.balance": "Running balance",
      "bank.imp.colNone": "— not in this file —",
      "bank.imp.amountRule": "Either one signed Amount column, or a Debit and a Credit column together.",
      "bank.imp.dateFormat": "Date format (only if the dates are read wrongly)",
      "bank.imp.saveMapping": "Save and import",
      "bank.imp.errMapDate": "Choose the date column",
      "bank.imp.errMapAmount": "Choose an Amount column, or both Debit and Credit",
      "bank.err.NO_STATEMENT": "Import the bank statement for this period first — there is nothing to reconcile against.",
      "bank.err.STATEMENT_GAP": "The imported statements do not cover the whole period. Import the missing days before reconciling.",
      "bank.err.IMPORT_UNREVIEWED": "Review the imported statement rows first — a statement nobody has read is not an authority.",
      "bank.err.NOT_A_BANK_ACCOUNT": "This is not a bank account. A reconciliation is made against a bank.",
      "bank.err.ACCOUNT_NOT_FOUND": "That account no longer exists.",
      "bank.err.SESSION_NOT_FOUND": "That reconciliation no longer exists.",
      "bank.err.SESSION_OPEN_EXISTS": "This account already has an open reconciliation. Finish or lock it first.",
      "bank.err.SESSION_LOCKED": "This reconciliation is locked. A locked one is a signed working paper and does not change.",
      "bank.err.MATCH_CLAIMED": "Someone else matched one of these rows a moment ago. The screen has been refreshed.",
      "bank.err.MATCH_INVALID": "These two cannot be matched — a reconciliation matches equal amounts inside the period.",
      "bank.err.UNEXPLAINED_DIFFERENCE": "There is still a difference. Explain it in the note before locking, or match what is left.",
      "bank.err.MAPPING_REQUIRED": "Tell us which column is which for this bank, once.",
      "bank.err.NO_ROWS": "No transaction rows could be read from this file. Check the column mapping and the date format.",
      "bank.err.BALANCES_REQUIRED": "The opening and closing balance could not be read from this file. Type them from the statement to import it.",
      "bank.err.FILE_TOO_LARGE_FOR_AI": "This file has too many rows for AI reading. Map the columns once instead — it is exact, instant and free, and every later statement from this bank imports straight away.",
      "bank.err.EXTRACTION_FAILED": "The file could not be read. Try mapping the columns instead, or upload a clearer copy.",
      "bank.err.BALANCE_MISMATCH": "The rows read from this file do not add up to its closing balance, so at least one movement is missing or misread. Nothing was imported.",
      "bank.err.DUPLICATE_IMPORT": "This statement has already been imported for this account. Importing it again would post every movement twice.",
      "bank.err.QUOTA_EXCEEDED": "This company has used its AI allowance for the month. Map the columns instead, or raise the allowance on the subscription.",
      "bank.err.UNSUPPORTED_FILE": "That file type cannot be read. Upload a CSV, an Excel file (.xlsx) or a PDF.",
      "bank.err.INVALID_INPUT": "The file is too large or has too many pages to read. Upload a smaller file, or split the statement by month.",
      "bank.err.BAD_MAPPING": "The stored column mapping for this bank no longer matches the file. Set it again.",
      "bank.err.NOT_BANK_ACCOUNT": "This is not a bank account. A statement is imported against a bank.",
      "bank.err.ACCOUNT_NOT_FOUND": "That account no longer exists. Reload the page and pick it again.",
      "br.noAccounts": "No bank account has been set up yet. Add one under Cash Statement → Accounts, then come back here.",
      "br.start": "Start",
      "br.newSession": "New reconciliation",
      "br.statusOpen": "In progress",
      "br.statusLocked": "Locked",
      "br.started": "Reconciliation started",
      "br.bankClosing": "Bank closing balance",
      "br.systemBalance": "Your books",
      "br.outstanding": "Still outstanding",
      "br.bankOnly": "bank only",
      "br.systemOnly": "books only",
      "br.difference": "Difference",
      "br.balancedNote": "The bank and your books agree once the outstanding items are counted.",
      "br.unbalancedNote": "There is a difference the outstanding items do not explain. Find it, or write down why before locking.",
      "br.postToBooks": "Record in the books",
      "br.postedAndMatched": "Recorded and matched to the bank line",
      "br.postedNotMatched": "The money was recorded, but pairing it with the bank line failed. It is waiting in your books — tick it there.",
      "br.openingGapNote": "This difference is the opening balance, not a missing transaction. The bank record starts this account at {{statement}} and your books start it at {{books}}. Ticking lines cannot change it, and neither can a cash adjustment — an adjustment adds the same amount to your balance and to the outstanding column, so they cancel. Set the opening balance on the Cash Statement; that is only possible before the account's first movement, so if it is already locked, record this amount in the lock note instead.",
      "br.preStatementNote": "This difference is not a missing transaction. Your books hold {{amount}} of movements from before {{from}}, which is the earliest bank statement imported for this account — no bank line exists that could ever meet them. Import the earlier statements to close that part of it. Do not set an opening balance: your books already contain this money, and it would be counted twice.",
      "br.bankOnlyTitle": "In the bank only",
      "br.matchedTitle": "Agreed",
      "br.systemOnlyTitle": "In your books only",
      "br.nothingHere": "Nothing here",
      "br.nothingMatched": "Nothing matched yet",
      "br.suggestMatch": "looks like a match",
      "br.confidence.high": "Very likely",
      "br.confidence.medium": "Likely",
      "br.confidence.low": "Possible",
      "br.confidence.manual": "Matched by hand",
      "br.confirmSelected": "Match selected ({{count}})",
      "br.matched": "Matched {{count}}",
      "br.unmatch": "Undo",
      "br.lock": "Lock reconciliation",
      "br.locked": "Reconciliation locked",
      "br.lockedOn": "Locked on",
      "br.lockHint": "Locking freezes these figures and the outstanding list as the record for this period. It cannot be reopened.",
      "br.notePlaceholder": "Note (optional)",
      "br.noteRequired": "Explain the difference before locking",
      "cash.empty": "No cash movements",
      "cash.emptyDesc": "There are no cash movements in this period.",
      "cash.accessDenied": "No access",
      "cash.accessDeniedDesc": "You do not have permission to view the cash statement.",
      "opex.nav": "Expenses",
      "opex.title": "Expenses",
      "opex.subtitle": "Operating expenses paid from cash or bank.",
      "opex.add": "Add expense",
      "opex.addTitle": "Add expense",
      "opex.editTitle": "Edit expense",
      "opex.col.date": "Date",
      "opex.col.category": "Category",
      "opex.col.vendor": "Vendor",
      "opex.col.paidFrom": "Paid from",
      "opex.col.branch": "Branch",
      "opex.col.amount": "Amount",
      "opex.allCategories": "All categories",
      "opex.allAccounts": "All accounts",
      "opex.allSources": "All sources",
      "opex.totalLabel": "Total",
      "opex.countLabel": "{{count}} expense(s)",
      "opex.empty": "No expenses",
      "opex.emptyDesc": "No expenses recorded for these filters.",
      "opex.branch": "Branch",
      "opex.selectBranch": "Select a branch",
      "opex.selectCategory": "Select a category",
      "opex.col.account": "Account",
      "opex.account": "Expense account",
      "opex.selectAccount": "Choose an account",
      "opex.searchAccount": "Search by name or account number",
      "opex.noAccounts": "No matching account",
      "opex.accountsLoadFailed": "The account list didn't load, so an expense can't be saved right now. Refresh the page and try again.",
      "opex.amount": "Amount",
      "opex.amountGross": "Amount (incl. VAT)",
      "opex.hasVat": "This invoice includes VAT",
      "opex.hasVatHint": "Untick for a receipt with no VAT — an unregistered supplier, a government fee, a petty-cash slip.",
      "opex.vatRate": "VAT rate",
      "opex.vatIncluded": "VAT included",
      "opex.netOfVat": "Net of VAT",
      "opex.col.vat": "VAT",
      "opex.totalVat": "VAT total",
      "opex.totalNet": "Net total",
      "opex.expenseDate": "Expense date",
      "opex.attachmentUrl": "Attachment URL",
      "opex.attachmentHint": "Optional link to a receipt or invoice.",
      "opex.attachment": "Attachment",
      "opex.documents": "Supporting documents",
      "opex.documentsHint": "Attach receipts or invoices (images or PDF, up to 10MB each).",
      "opex.addDocuments": "Add documents",
      "opex.uploading": "Uploading…",
      "opex.uploadFailed": "Could not upload the file.",
      // ── Bounded media intake (utils/mediaUploadClient.js). Keys are chosen by
      // the server's STABLE code, never by HTTP status or raw message, so an
      // English sentence from the API can never leak into the UI.
      "mediaUpload.failed": "Could not upload this file. Please try again.",
      "mediaUpload.unsupportedType": "This file type is not allowed here. Use a JPEG, PNG, WebP image or a PDF.",
      "mediaUpload.typeMismatch": "This file's type does not match its name. Please pick the file again.",
      "mediaUpload.tooLarge": "This file is larger than the limit for this kind of attachment.",
      "mediaUpload.emptyFile": "This file is empty.",
      "mediaUpload.quotaExceeded": "Too many uploads are in progress right now. Please try again in a moment.",
      "mediaUpload.grantExpired": "This upload took too long to start. Please pick the file again.",
      "mediaUpload.notUploaded": "The file did not reach the server. Please try again.",
      "mediaUpload.stillProcessing": "The file is still being checked. Please try again shortly.",
      "mediaUpload.notReady": "This attachment is not available any more. Please upload it again.",
      "mediaUpload.pdfEncrypted": "This PDF is password-protected. Remove the password and try again.",
      "mediaUpload.pdfPages": "This PDF has more pages than allowed.",
      "mediaUpload.pdfMalformed": "This PDF could not be read. Please try another file.",
      "mediaUpload.forbidden": "You do not have permission to upload this attachment.",
      "mediaUpload.sessionChanged": "Your session changed, so this upload was cancelled.",
      "opex.errCategory": "Select a category",
      "opex.errAccount": "Choose the account this expense belongs to",
      "opex.errAmount": "Enter a valid amount",
      "opex.errBranch": "Select a branch",
      "opex.saveFailed": "Could not save the expense.",
      "opex.created": "Expense added",
      "opex.updated": "Expense updated",
      "opex.detailTitle": "Expense details",
      "opex.loadFailed": "Could not load the expense.",
      "opex.deleted": "Expense deleted",
      "opex.deleteFailed": "Could not delete the expense.",
      "opex.post": "Post",
      "opex.postedLockHint": "Posted — to change the amount, reverse it with a credit note and post a new one.",
      "opex.postLeft": "Post ({{count}} left)",
      "opex.deleteTitle": "Delete expense",
      "opex.deleteConfirm": "Are you sure you want to delete this expense? This cannot be undone.",
      "opex.accessDenied": "No access",
      "opex.accessDeniedDesc": "You do not have permission to view expenses.",
      "purchases.nav": "Purchases",
      "purchases.title": "Purchases",
      "purchases.subtitle": "Supplier invoices and expenses in one place.",
      "purchases.add": "Add",
      "purchases.addInvoice": "Supplier / stock invoice",
      "purchases.addExpense": "Quick expense",
      "purchases.allTypes": "All types",
      "purchases.allStatuses": "All statuses",
      "purchases.loadFailed": "Could not load purchases.",
      "purchases.accessDenied": "No access",
      "purchases.accessDeniedDesc": "You do not have permission to view purchases.",
      "purchases.empty": "No purchases yet",
      "purchases.emptyDesc": "Supplier invoices and expenses will appear here.",
      "purchases.col.date": "Date",
      "purchases.col.type": "Type",
      "purchases.col.party": "Supplier / Vendor",
      "purchases.col.ref": "Reference",
      "purchases.col.category": "Category",
      "purchases.col.status": "Status",
      "purchases.col.amount": "Amount",
      "purchases.type.invoice": "Purchase invoice",
      "purchases.type.expense": "Expense",
      "purchases.type.stock": "Stock",
      "purchases.type.nonStock": "Non-stock",
      "purchases.type.mixed": "Mixed",
      "purchases.type.creditNote": "Credit note",
      "purchases.reverses": "Reverses {{ref}}",
      "purchases.action.viewSource": "View source document",
      "postingRef": "Posting ref",
      "purchases.status.paid": "Paid",
      "purchases.status.partial": "Partial",
      "purchases.status.unpaid": "Unpaid",
      "purchases.status.reversed": "Reversed",
      "purchases.status.issued": "Issued",
      "purchases.status.na": "—",
      "purchases.kpi.totalOut": "Total out",
      "purchases.kpi.cashOut": "Cash out",
      "purchases.kpi.owed": "Payable to suppliers",
      "purchases.kpi.vat": "VAT reclaimable",
      "purchases.pay.title": "Payments",
      "purchases.pay.paid": "Paid",
      "purchases.pay.total": "Total",
      "purchases.pay.balance": "Balance due",
      "purchases.pay.none": "No payments recorded yet.",
      "purchases.pay.amount": "Amount",
      "purchases.pay.from": "Paid from",
      "purchases.pay.note": "Note (optional)",
      "purchases.pay.record": "Record payment",
      "purchases.pay.recorded": "Payment recorded",
      "purchases.pay.voided": "Payment voided",
      "purchases.pay.void": "Void payment",
      "purchases.pay.confirmVoidTitle": "Void this payment?",
      "purchases.pay.confirmVoidMessage": "The payment will be reversed and the amount returned to the invoice balance.",
      "purchases.pay.errAmount": "Enter a valid amount",
      "purchases.pay.saveFailed": "Could not record the payment.",
      "purchases.pay.voidFailed": "Could not void the payment.",
      "purchases.pay.loadFailed": "Could not load payments.",
      "purchases.scan.button": "Scan invoice",
      "purchases.scan.hint": "Printed invoices scan best; handwritten ones are best-effort — review before saving.",
      "purchases.scan.quotaExceeded": "This month's AI quota has been used up",
      "purchases.scan.extractionFailed": "Could not read the invoice — enter it manually",
      "purchases.scan.failed": "Could not scan the invoice",
      "purchases.scan.supplierMatchedVat": "Matched by VAT number",
      "purchases.scan.supplierSuggestion": "Suggested match — confirm below",
      "purchases.scan.supplierNoMatch": "No supplier match — select or create one",
      "purchases.scan.createSupplier": "Create supplier from scan",
      "purchases.newSupplierBeforeSave": "New supplier — create it before saving",
      "purchases.supplierPermissionRequired": "This supplier is not registered. Ask a user who can add suppliers to create it.",
      "purchases.scan.lineUnmatched": "No matching part — will create a new one",
      "purchases.scan.reconciliationMismatch": "The scanned totals don't add up — please review before saving.",
      "purchases.scan.deltaLinesVsSubtotal": "Lines vs. subtotal: {{amount}}",
      "purchases.scan.deltaSubtotalVsTotal": "Subtotal + VAT vs. total: {{amount}}",
      "purchases.scan.duplicateWarning": "This looks like a duplicate of an existing invoice ({{ref}}).",
      "purchases.scan.viewExisting": "View existing invoice",
      "purchases.scan.modalTitle": "Scan invoice with AI",
      "purchases.scan.optionCamera": "Take a photo",
      "purchases.scan.optionUpload": "Upload image or PDF",
      "purchases.scan.typeQuestion": "What kind of document is this?",
      "purchases.scan.typeAuto": "Detect automatically",
      "purchases.scan.typeStock": "Stock purchase",
      "purchases.scan.typeExpense": "Expense",
      "purchases.scan.typeAsset": "Fixed asset",
      "purchases.scan.live.reading": "Reading the invoice…",
      "purchases.scan.live.readingLines": "Reading line items…",
      "purchases.scan.live.category": "Category",
      "purchases.scan.live.why": "Why this category",
      "purchases.scan.live.supplier": "Supplier",
      "purchases.scan.live.vatNumber": "VAT number",
      "purchases.scan.live.invoiceNumber": "Invoice number",
      "purchases.scan.live.invoiceDate": "Invoice date",
      "purchases.scan.live.totals": "Printed totals",
      "purchases.scan.live.subtotal": "Subtotal",
      "purchases.scan.live.vat": "VAT",
      "purchases.scan.live.grandTotal": "Grand total",
      "purchases.scan.live.notPrinted": "Not printed",
      "purchases.scan.live.lines": "Line items",
      "purchases.scan.live.continue": "Continue",
      "purchases.scan.live.dropped": "The connection dropped before the scan finished. Nothing was saved.",
      "purchases.scan.live.tryAgain": "Try again",
      "purchases.scan.pages.add": "Add page",
      "purchases.scan.pages.remove": "Remove page",
      "purchases.scan.pages.scan": "Scan these pages",
      "purchases.scan.pages.hint": "Several photos of one invoice are sent together as one PDF.",
      "purchases.scan.pages.tooMany": "Up to 10 pages per invoice.",
      "purchases.scan.pages.tooLarge": "These pages are larger than 10 MB together. Remove a page or take the photos again.",
      "purchases.scan.pages.unreadable": "This photo could not be read. Choose another one.",
      "purchases.scan.review.title": "Please check",
      "purchases.scan.review.confirm": "I checked these items",
      "purchases.scan.review.confirmRequired": "Tick “I checked these items” before saving.",
      "purchases.scan.review.chooseCategory": "Choose the category:",
      "purchases.scan.review.scanAgain": "Scan again",
      "purchases.scan.review.reason.totals_mismatch": "The line items do not add up to the printed total.",
      "purchases.scan.review.reason.vat_number_invalid": "The supplier's VAT number does not look valid. It should be 15 digits.",
      "purchases.scan.review.reason.doc_type_disputed": "The category is uncertain. Choose between the two suggestions.",
      "purchases.scan.review.reason.doc_type_missing": "The category could not be read. Choose one.",
      "purchases.scan.review.reason.doc_type_unknown": "The category was not recognised. Choose one.",
      "purchases.scan.review.reason.line_invalid": "Some line items look incomplete. Check the quantities and prices.",
      "purchases.scan.review.reason.no_lines": "No line items could be read. Take a clearer photo and try again.",
      "purchases.scan.review.reason.multiple_invoices": "The file contains more than one invoice; only the first was read. Scan the others separately.",
      "purchases.scan.missing.title": "Still needed",
      "purchases.scan.missing.date": "Date",
      "purchases.scan.missing.amount": "Amount",
      "purchases.scan.missing.category": "Category",
      "purchases.scan.missing.account": "Account",
      "purchases.scan.missing.vendor": "Vendor",
      "purchases.scan.missing.supplier": "Supplier",
      "purchases.scan.missing.invoiceNumber": "Invoice number",
      "purchases.scan.missing.assetName": "Asset name",
      "purchases.scan.missing.usefulLife": "Useful life",
      "purchases.scan.missing.saveLeft": "Save ({{count}} left)",
      "purchases.scan.missing.saveVat": "Save VAT number {{vat}} to this supplier",
      "purchases.scan.missing.saveVatFailed": "Could not save the VAT number to this supplier.",
      "purchases.scan.missing.readFromInvoice": "Read from the invoice",
      "purchases.scan.dup.sameFile": "Same photo was already saved",
      "purchases.scan.dup.sameNumber": "Same invoice number from this supplier",
      "purchases.scan.dup.sameAmountDate": "Same supplier, amount and date",
      "purchases.scan.dup.open": "Open",
      "purchases.scan.date.useToday": "Use today",
      "purchases.scan.date.future": "This date is in the future.",
      "purchases.scan.date.old": "This date is more than 12 months old.",
      "purchases.scan.date.closed": "That month is closed in the books. Pick a date in an open month, or ask your accountant to reopen it.",
      "purchases.post.button": "Post now",
      "purchases.post.hint": "Updates stock and costs and records the invoice in the books.",
      "purchases.post.done": "Invoice posted",
      "purchases.post.failed": "Could not post the invoice",
      "purchases.scan.dup.confirmTitle": "This invoice may already be recorded",
      "purchases.scan.dup.confirmMessage": "We found the same photo, or the same invoice number from this supplier. Save it anyway?",
      "purchases.scan.dup.confirmSave": "Save anyway",
      "purchases.scan.viewer.title": "Scanned invoice",
      "purchases.scan.viewer.pdfTitle": "Scanned invoice PDF",
      "purchases.scan.viewer.zoomIn": "Zoom in",
      "purchases.scan.viewer.zoomOut": "Zoom out",
      "purchases.scan.viewer.reset": "Reset zoom",
      "purchases.scan.viewer.show": "Show invoice",
      "purchases.scan.viewer.hide": "Hide invoice",
      "purchases.scan.remembered.hint": "From your last invoice from this supplier",
      "purchases.scan.remembered.lineLink": "Linked like last time",
      "purchases.scan.typeAutoHint":
        "The AI will decide. If it is unsure it defaults to a stock purchase — pick the type yourself to be certain.",
      "purchases.scan.typeManualHint": "Your choice is used — the AI will not override it.",
      "purchases.scan.step1": "Photograph or upload the supplier invoice",
      "purchases.scan.step2": "TzamunAI reads the supplier, items and totals",
      "purchases.scan.step3": "Review the prefilled draft and post",
      "purchases.scan.scanning": "Reading the invoice…",

      "reversal.title": "Reverse document",
      "reversal.confirm": "Reverse & issue credit note",
      "reversal.reverseInvoice": "Reverse invoice",
      "reversal.reverseInvoiceHint": "Issues a credit note, returns the stock and voids any supplier payments (cash returned).",
      "reversal.reverseExpense": "Reverse expense",
      "reversal.reason": "Reason",
      "reversal.reasonPlaceholder": "Why is this being reversed?",
      "reversal.warnInvoice": "This will reverse invoice {{ref}}: a credit note is issued, the received stock is returned, and any recorded supplier payments are voided (the cash is returned). This cannot be undone.",
      "reversal.warnExpense": "This will reverse the expense {{ref}}: a credit note is issued and any cash paid is returned. This cannot be undone.",
      "reversal.consumedTitle": "Cannot reverse — parts already used",
      "reversal.consumedLine": "{{part}} — need to return {{required}}, only {{available}} in stock",
      "reversal.consumedToast": "Some parts have already been used; reversal blocked.",
      "reversal.reversed": "Reversed",
      "reversal.reversedBanner": "This document has been reversed.",
      "reversal.creditNoteNo": "Credit note",
      "reversal.invoiceReversed": "Invoice reversed and credit note issued",
      "reversal.expenseReversed": "Expense reversed and credit note issued",
      "purchases.vatInCost.explanation": "Before 25 Aug 2026 MotorMind did not save whether VAT was reclaimed or added to parts cost. The VAT report does not claim this invoice's VAT until you answer.",
      "purchases.vatInCost.reclaimed": "VAT was reclaimed",
      "purchases.vatInCost.addedToCost": "VAT was added to parts cost",
      "purchases.vatInCost.confirm": "Record this answer permanently: {{answer}}?",
      "purchases.vatInCost.confirmButton": "Confirm answer",
      "purchases.vatInCost.cancel": "Cancel",
      "purchases.vatInCost.saved": "VAT answer recorded",
      "purchases.vatInCost.failed": "Couldn't record the VAT answer",
      "vat.unrecordedInCost.notice": "{{count}} purchase invoices from before 25 Aug 2026 don't record whether their VAT was reclaimed or added to parts cost. Their VAT ({{vat}} SAR) is not claimed here until you answer on each invoice.",
      "vat.unrecordedInCost.invoice": "{{ref}} — {{date}} — {{vat}} SAR",
      "vat.priorCorrections.title": "Corrections from previous periods",
      "vat.priorCorrections.number": "Credit note",
      "vat.priorCorrections.source": "Source",
      "vat.priorCorrections.period": "Original period",
      "vat.priorCorrections.inputVat": "Input VAT",
      "vat.priorCorrections.total": "Total corrections",
      "vat.priorCorrections.amendWarning": "Corrections from earlier periods total more than SAR 15,000. ZATCA requires you to notify it within 20 days and amend the original return(s) instead of correcting them here.",
      "reversal.creditNotePeriodClosed": "The credit note's month is closed.",
      "reversal.paymentCancelled": "Payment cancelled / money returned",
      "reversal.postToday": "Post on today's date instead",
      "reversal.refundAccount": "Refund to account",
      "reversal.refundReceived": "Refund received",
      "reversal.settlement": "What happened to the payment?",
      "reversal.settlementLabel": "Settlement",
      "reversal.supplierKeepsCredit": "Supplier keeps it as credit",
      "reversal.postedOnSupplierDate": "Posted on {{posting}} (supplier's credit note dated {{supplier}})",
      "reversal.postingDate": "Posted on",
      "reversal.recordRefund": "Record refund",
      "reversal.refundRecorded": "Refund recorded",
      "reversal.vatAmendmentNotice": "This document alone removes {{amount}} of VAT from an earlier tax period. If that period's VAT return was already filed and the total correction exceeds SAR 15,000, ZATCA requires you to notify it within 20 days and submit an amended return for that period.",
      "reversal.basis": "Type of reversal",
      "reversal.basisSupplierCreditNote": "Supplier credit note",
      "reversal.basisEnteredInError": "Entered by mistake",
      "reversal.fieldsRequired": "Fill in the required fields.",
      "reversal.supplierCreditNoteNumber": "Supplier credit note number",
      "reversal.supplierCreditNoteDate": "Supplier credit note date",
      "reversal.supplierCreditNoteFile": "Supplier credit note file",
      "reversal.failed": "Could not reverse this document.",

      "audit.nav": "Financial Audit",
      "audit.pageTitle": "Financial Audit",
      "audit.pageSubtitle": "Who posted, edited, paid or reversed — with before → after on edits.",
      "audit.timelineTitle": "Activity",
      "audit.empty": "No activity recorded yet.",
      "audit.emptyDesc": "Financial actions will appear here.",
      "audit.loadFailed": "Could not load the audit trail.",
      "audit.accessDenied": "No access",
      "audit.accessDeniedDesc": "You do not have permission to view the financial audit trail.",
      "audit.allEntities": "All documents",
      "audit.allActions": "All actions",
      "audit.col.when": "When",
      "audit.col.who": "User",
      "audit.col.entity": "Document",
      "audit.col.action": "Action",
      "audit.col.change": "Change",
      "audit.entity.purchase_invoice": "Purchase invoice",
      "audit.entity.expense": "Expense",
      "audit.entity.supplier_payment": "Supplier payment",
      "audit.entity.credit_note": "Credit note",
      "audit.entity.cash": "Cash",
      "audit.action.created": "Created",
      "audit.action.approval_requested": "Approval requested",
      "audit.action.approval_approved": "Approved",
      "audit.action.approval_rejected": "Approval rejected",
      "audit.action.approval_expired": "Approval request expired",
      "audit.action.credit_payout": "Credit payout",
      "audit.action.marked_paid": "Marked paid",
      "audit.action.possible_double_paid": "Possible double payment",
      "audit.action.purchase_invoice.scan": "Purchase invoice scan",
      "audit.action.receipt_delete": "Receipt deleted",
      "audit.action.receipt_refund": "Receipt refunded",
      "audit.action.receipt_void": "Receipt voided",
      "audit.entity.payment_receipt": "Payment receipt",
      "audit.action.posted": "Posted",
      "audit.action.edited": "Edited",
      "audit.action.paid": "Paid",
      "audit.action.payment_voided": "Payment voided",
      "audit.action.reversed": "Reversed",
      "audit.action.deleted": "Deleted",
      "audit.action.marked_paid": "Marked paid",
      "audit.action.issued": "Issued",
      "audit.action.recorded": "Recorded",
      "audit.action.voided": "Voided",
      "audit.field.amount": "Amount",
      "audit.field.paid_from": "Paid from",
      "audit.field.vendor": "Vendor",
      "audit.field.expense_date": "Date",
      "audit.field.category_id": "Category",
      "audit.field.note": "Note",
      "audit.field.status": "Status",

      "set.general": "General",
      "set.notifications": "Notifications",
      "set.revenue": "Revenue Planning",
      "set.payments": "Payments",
      "set.terms": "Terms & Conditions",
      "set.features": "Features",
      "set.appointments": "Appointments",
      "set.creditReminders": "Credit Reminders",
      "set.feedback": "Customer Feedback",
      "set.whatsapp": "WhatsApp",
      "set.noBranchInScope": "Choose a branch to see its settings.",
      "set.delayFees": "Delay Fees",
      "settings.delayFees.title": "Delay Fees Settings",
      "settings.delayFees.desc": "Configure default terms for customer parts deadlines and vehicle collection delays. These defaults pre-fill new job cards at car reception.",
      "settings.delayFees.partsSectionTitle": "Customer-Provided Parts Defaults",
      "settings.delayFees.partsSectionDesc": "Default deadline and daily fee when the customer provides their own parts.",
      "settings.delayFees.defaultPartsDeadlineDays": "Default parts deadline (days)",
      "settings.delayFees.defaultPartsDeadlineDaysDesc": "Pre-fills parts deadline to today + N days.",
      "settings.delayFees.defaultPartsFeePerDay": "Default parts delay fee / day",
      "settings.delayFees.defaultPartsFeePerDayDesc": "Fee charged for each calendar day past the parts deadline.",
      "settings.delayFees.collectionSectionTitle": "Vehicle Collection Delay Defaults",
      "settings.delayFees.collectionSectionDesc": "Default grace period and daily fee when customer delays picking up the ready vehicle.",
      "settings.delayFees.defaultCollectionGraceHours": "Default collection grace period (hours)",
      "settings.delayFees.defaultCollectionGraceHoursDesc": "Hours after vehicle is marked ready before delay fees begin.",
      "settings.delayFees.defaultCollectionFeePerDay": "Default collection fee / day",
      "settings.delayFees.defaultCollectionFeePerDayDesc": "Fee charged for each calendar day past the grace period.",
      "settings.delayFees.saved": "Delay fee settings saved successfully",
      "settings.delayFees.saveFailed": "Failed to save delay fee settings",
      "settings.delayFees.loadFailed": "Failed to load delay fee settings",

      "delayFees.customerProvidesParts": "Customer provides parts",
      "delayFees.partsDelayFeePerDay": "Delay fee / day",
      "delayFees.collectionFeeCheckbox": "Vehicle collection delay fees",
      "delayFees.collectionStartsAfter": "Starts after",
      "delayFees.hoursAfterReady": "hours after vehicle is ready",
      "delayFees.collectionFeePerDay": "Delay fee / day",
      "delayFees.sar": "SAR",
      "delayFees.sarPerDay": "SAR / day",
      "delayFees.panelTitle": "Delay Fees & Storage",
      "delayFees.panelSubtitle": "Tracking parts delivery and vehicle collection delay meters",
      "delayFees.partsMeterTitle": "Customer Parts Delivery Delay",
      "delayFees.collectionMeterTitle": "Vehicle Collection Delay",
      "delayFees.meterRunning": "Running",
      "delayFees.meterStopped": "Stopped",
      "delayFees.meterPending": "Pending Ready",
      "delayFees.meterWithinGrace": "Within Grace Period",
      "delayFees.meterWithinDeadline": "Within Deadline",
      "delayFees.meterNotConfigured": "Not Configured",
      "delayFees.deadlineDate": "Deadline Date",
      "delayFees.clockStarted": "Fees Clock Started",
      "delayFees.clockStopped": "Fees Clock Stopped",
      "delayFees.gracePeriod": "Grace Period",
      "delayFees.readyAt": "Vehicle Ready At",
      "delayFees.partsReceivedAt": "Parts Received At",
      "delayFees.collectedAt": "Vehicle Collected At",
      "delayFees.dailyRate": "Daily Rate",
      "delayFees.daysCharged": "Chargeable Days",
      "delayFees.accruedTotal": "Accrued Total",
      "delayFees.grandTotal": "Total Accrued Delay Fees",
      "delayFees.grandTotalNow": "Outstanding now",
      "history.actor.customer": "Customer",
      "history.service_completion_override": "Force-completed the service {{service_name}}",
      "history.part_completion_override": "Force-completed the part {{part_name}}",
      "history.task_completion_override": "Force-completed the task {{task_name}}",
      "history.part_receipt_overridden": "Completed {{task_name}} without confirming receipt of {{count}} customer part(s): {{parts}}",
      "history.sub_task_completion_override": "Force-completed the step {{sub_task_name}}",
      "history.delivery_code_overridden": "Released the vehicle without the pickup code — {{reason}}",
      "history.delivered_on_credit": "Vehicle handed over on credit, due in {{due_in_days}} day(s)",
      "history.payment_link_sent": "Sent the customer a payment link",
      "history.payment_requested": "Requested a payment of {{amount}}",
      "history.quotation_linked": "Linked a quotation to this job card",
      "history.quotation_converted_to_invoice": "Converted the quotation to invoice {{reference_number}}",
      "history.branch_transferred": "Moved to another branch",
      "history.job_card_cancelled_no_agreement": "Cancelled — no agreement reached. {{reason}}",
      "history.actor.insurer": "Insurer",
      "delayFees.accruedTotalNow": "Accrued to now",
      "delayFees.clockStartedAt": "Clock started",
      "delayFees.chargeStartsAt": "Charging starts",
      "delayFees.chargeStartsIn": "Charging starts in",
      "delayFees.countdownHm": "{{hours}}h {{minutes}}m",
      "delayFees.countdownM": "{{minutes}}m",
      "delayFees.countdownSeconds": "{{seconds}}s",
      "delayFees.projectionNote": "SAR {{settled}} is posted to the ledger below; SAR {{live}} is outstanding as of right now. The difference is charged but not yet written — it posts overnight, or the moment the vehicle is handed over.",
      "delayFees.dayUnit": "day(s)",
      "delayFees.partsReceivedDone": "Parts marked as received",
      "delayFees.partsReceivedFailed": "Could not mark the parts as received",
      "delayFees.waive": "Waive",
      "delayFees.waiveReasonPrompt": "Why is this day being waived? The reason is recorded against the fee.",
      "delayFees.waiveReasonRequired": "A reason is required to waive a fee",
      "delayFees.waiveFailed": "Could not waive this fee",
      "delayFees.actionsColumn": "Actions",
      "delayFees.partsFeeLabel": "Customer-supplied parts delay",
      "delayFees.collectionFeeLabel": "Late vehicle collection",
      "jc.delayFeeOutstandingTitle": "Unpaid delay fees",
      "jc.delayFeeOutstandingDesc": "This customer has unbilled delay fees that are not on any invoice yet. The original invoice is already issued and cannot be changed, so continue to raise a separate invoice for these fees and send the customer a payment link. No new delivery code is sent — once the fee invoice is paid, the code the customer already has works again. Tell them to come back and enter that same code.",
      "jc.delayFeeProceed": "Create fee invoice",
      "jc.delayFeeOpenInvoice": "Open fee invoice",
      "jc.delayFeeInvoiceUnpaidDesc": "The fee invoice for this vehicle has already been created and is still unpaid. The vehicle can be released once it is settled.",
      "jc.delayFeeInvoiceNumber": "Invoice number",
      "jc.delayFeeInvoiceBalance": "Balance due",
      "jc.delayFeeInvoiceFailed": "Could not create the delay fee invoice",
      "jc.delayFeeInvoiceSent": "Fee invoice created — the payment link is on its way to the customer",
      "jc.delayFeeWaiveAll": "Waive the outstanding fees",
      "jc.delayFeeWaiveAllDesc": "Forgive every unbilled delay-fee day on this card. The reason is recorded against each day. The vehicle is then released using the code the customer already has.",
      "jc.delayFeeWaiveReasonLabel": "Reason for waiving",
      "jc.delayFeeWaiveReasonRequired": "A reason is required to waive these fees",
      "jc.delayFeeWaiveConfirm": "Waive and deliver",
      "jc.delayFeeWaiveFailed": "Could not waive the delay fees",
      "jc.delayFeeReleaseOnCredit": "Release on credit",
      "jc.delayFeeReleaseOnCreditDesc": "Raise the fee invoice and hand the vehicle over now. The card becomes delivered — payment pending, and the amount is chased as a receivable.",
      "jc.delayFeeReleaseConfirm": "Release on credit",
      "jc.delayFeeReleaseReason": "Reason for releasing the vehicle",
      "jc.delayFeeReleaseReasonPlaceholder": "e.g. long-standing customer, fees agreed by phone",
      "jc.delayFeeReleaseReasonRequired": "A written reason is required to release a vehicle against unpaid delay fees",
      "delayFees.hoursUnit": "hour(s)",
      "delayFees.itemizedBreakdown": "Daily Accrual Breakdown",
      "delayFees.feeDate": "Date",
      "delayFees.feeType": "Fee Type",
      "delayFees.status": "Status",
      "delayFees.billed": "Billed",
      "delayFees.unbilled": "Unbilled",
      "delayFees.waived": "Waived",
      "delayFees.noAccruedFees": "No delay fees accrued so far",
      "delayFees.waivedBy": "Waived by {{name}}",
      "delayFees.partExpectedAt": "Target receive date",
      "delayFees.partExpectedAtHint": "The day the customer promised this part. Fees start the day after it. A past date is fine — the customer is already late.",
      "delayFees.perPartDeadlineNote": "Each part the customer brings gets its own target date, set on the part when it is added.",
      "delayFees.partDueOn": "Customer part — due {{date}}",
      "delayFees.partReceivedOn": "Customer part — received {{date}}",
      "delayFees.markPartReceived": "Received",
      "delayFees.receivedModalTitle": "Record when this part arrived",
      "delayFees.receivedDateLabel": "Date received",
      "delayFees.receivedDateHint": "Today or earlier. Back-dating is normal — the days after that date stop being charged.",
      "delayFees.receivedConfirm": "Save received date",
      "delayFees.receivedDateRequired": "Enter the date the part arrived",
      "delayFees.receivedNotFuture": "A part cannot arrive in the future. Pick today or an earlier date.",
      "delayFees.receivedAlreadyBilled": "Some of the days after that date are already on an invoice, so the date cannot be moved back over them. Issue a credit note against that invoice instead.",
      "delayFees.receivedNoDeadline": "This part has no target date, so there is no clock to stop.",

      "jc.scheduledStartDate": "Scheduled Start Date",
      "jc.scheduledStartDateHint": "Work is blocked before this date. Leave empty to start immediately.",
      "jc.scheduledStartBadge": "Scheduled: {{date}}",
      "jc.scheduledStartBannerTitle": "Scheduled repair start: {{date}}",
      "jc.scheduledStartBannerDesc": "Agreed with the customer that work begins on this date. Technicians cannot start tasks before this date.",
      "jc.repairNotYetStartable": "Cannot start work before the scheduled start date: {{date}}",
      "jc.clearScheduledStart": "Start immediately (clear schedule)",
      "jc.editScheduledStart": "Edit start date",
      "jc.scheduledStartCleared": "Scheduled start date cleared — work may start immediately",
      "jc.scheduledStartUpdated": "Scheduled start date updated successfully",
      "setup.title": "Setup guide",
      "setup.subtitle": "Finish setting up your workshop",
      "setup.progress": "{{done}} of {{total}} done",
      "setup.allDone": "You're all set! Your workshop is fully configured.",
      "setup.dismiss": "Dismiss",
      "setup.show": "Show setup guide",
      "setup.optional": "Optional",
      "setup.done": "Done",
      "setup.pending": "Pending",
      "setup.setUp": "Set up",
      "setup.review": "Review",
      "setup.step.company_info": "Complete workshop info",
      "setup.step.company_info.desc": "Add your name, email, phone and national address.",
      "setup.step.logo": "Upload your logo",
      "setup.step.logo.desc": "Your logo appears on invoices, quotations and the customer portal.",
      "setup.step.branches": "Create a branch",
      "setup.step.branches.desc": "At least one branch must exist to operate.",
      "setup.step.staff": "Invite your team",
      "setup.step.staff.desc": "Add staff users so your team can work in MotorMind.",
      "setup.step.services": "Add your services",
      "setup.step.services.desc": "Build your service catalog so it can be picked on job cards.",
      "setup.step.payments": "Configure payment methods",
      "setup.step.payments.desc": "Enable at least one payment method to collect online.",
      "setup.step.vat": "Set VAT / tax",
      "setup.step.vat.desc": "Register your tax number and rate, or mark the company VAT-exempt.",
      "setup.step.terms": "Publish terms & conditions",
      "setup.step.terms.desc": "Publish your T&C so customers can review and accept them.",
      "setup.step.appointments": "Enable online booking",
      "setup.step.appointments.desc": "Let customers book appointments from your public page.",
      "rplan.periodMonthly": "Monthly",
      "rplan.periodYearly": "Yearly",
      "rplan.periodHint": "Yearly amounts are divided by 12 automatically",
      "rplan.periodNote": "Pick Monthly or Yearly per row — yearly amounts are divided by 12.",
      "rplan.fixedTotalMonthly": "Total fixed expenses (monthly)",
      "rplan.startMonth": "Plan starts from",
      "rplan.startMonthHint": "Target applies from this month to December; earlier months get no target.",
      "rplan.appliesRange": "Applied from {{month}} to December",
      "rplan.savedFrom": "Plan saved — applied from {{month}} to December",
      "tgt.planInSettings": "Plan it in Settings ▸ Revenue Planning",
      "rplan.title": "Revenue Planning",
      "rplan.subtitle": "Enter fixed expenses, gross margin and target profit — we compute the monthly revenue target and apply it to all 12 months.",
      "rplan.fixedExpenses": "Fixed monthly expenses",
      "rplan.addExpense": "Add expense",
      "rplan.expensePlaceholder": "e.g. Rent, Salaries, Marketing",
      "rplan.exRent": "Rent",
      "rplan.exSalaries": "Salaries",
      "rplan.exUtilities": "Utilities",
      "rplan.fixedTotal": "Total fixed expenses",
      "rplan.grossMargin": "Gross margin %",
      "rplan.grossMarginHint": "Your average margin after parts cost: (revenue − parts cost) ÷ revenue.",
      "rplan.targetProfit": "Target profit / month",
      "rplan.monthlyTarget": "Monthly revenue target",
      "rplan.formulaNote": "Monthly target = (fixed expenses + target profit) ÷ gross margin %.",
      "rplan.noMarginWarn": "Set a gross margin above 0 for an accurate, margin-based target.",
      "rplan.remove": "Remove",
      "rplan.save": "Save plan",
      "rplan.saved": "Plan saved",
      "rplan.saveFailed": "Could not save plan",
      "history.job_hold": "Put on hold",
      "history.job_resume": "Work resumed",
      "history.job_converted": "Converted to regular service",
      "history.job_reopened": "Job card re-opened",
      "history.delivery_code_sent": "Delivery code sent",
      "history.photos_uploaded": "Photos uploaded",
      "history.signature_added": "Signature captured",
      "history.mechanic_assigned": "Assigned technician {{mechanic}}",
      "history.walk_in_created": "Walk-in job card created",
      "history.task_updated": "Task updated: {{task_name}}",
      "history.task_deleted": "Task deleted: {{task_name}}",
      "history.task_duplicated": "Task duplicated: {{task_name}}",
      "history.task_status_changed": "Task {{task_name}} → {{status}}",
      "history.part_removed": "Part removed: {{part_name}}",
      "history.part_approved": "Part approved: {{part_name}}",
      "history.service_removed": "Service removed: {{service_name}}",
      "history.service_approved": "Service approved: {{service_name}}",
      "history.service_rejected": "Service rejected: {{service_name}}",
      "history.quotation_rejected": "Quotation {{quotation_number}} rejected",
      "history.quotation_updated": "Quotation {{quotation_number}} updated",
      "history.quotation_deleted": "Quotation {{quotation_number}} deleted",
      "history.quotation_resent": "Quotation {{quotation_number}} resent",
      "history.invoice_deleted": "Invoice {{invoice_number}} deleted",
      "history.invoice_updated": "Invoice {{invoice_number}} updated",
      "history.invoice_item_added": "Item added to invoice {{invoice_number}}",
      "history.invoice_sms_resent": "Invoice {{invoice_number}} SMS resent",
      "history.return_invoice_created": "Return invoice {{invoice_number}} created",
      "history.delivery_verified": "Delivery code verified — vehicle handed over",
      "history.claim_created": "Insurance claim created",
      "history.claim_updated": "Insurance claim updated",
      "history.claim_status_changed": "Claim status → {{status}}",
      "history.claim_estimate_sent": "Estimate sent to insurer",
      "history.claim_decided": "Claim decision: {{decision}}",
      "history.expense_added": "Expense added: {{description}} ({{amount}})",
      "history.expense_deleted": "Expense deleted: {{description}}",
      "history.user_unassigned": "Unassigned {{user}}",
      "history.comment_added": "Comment added",
      "history.issue_reported": "Issue reported: {{title}}",
      "history.issue_resolved": "Issue resolved",
      "jc.requoteRejected": "Revise declined items ({{count}})",
      "repairAfterReject.note": "The customer rejected one or more items. You can proceed to repair with the approved items, or revise the quotation — your choice.",
      "repairAfterReject.noApproved": "No approved items to repair. Revise the quotation first.",
      "repairAfterReject.proceed": "Proceed to repair",
      "tgt.title": "Monthly Revenue Target",
      "tgt.adjust": "Set / adjust",
      "tgt.saved": "Target saved",
      "tgt.saveFailed": "Could not save target",
      "tgt.none": "No target set for this month.",
      "tgt.setNow": "Set a target",
      "tgt.met": "Target met 🎉",
      "tgt.progress": "{{pct}}% of target",
      "tgt.target": "Base target",
      "tgt.carryForward": "Carried forward",
      "tgt.remaining": "Remaining",
      "tgt.carryNote": "Includes unachieved target carried forward from the previous month.",
      "tgt.baseTarget": "Base target (this month)",
      "tgt.carryOverride": "Carry-forward override (optional)",
      "tgt.auto": "Auto",
      "tgt.carryHint": "Leave blank to auto-carry the previous month shortfall.",
      "tgt.salariesFromHr": "Calculate salaries from the HR module",
      "tgt.salariesHrLine": "Salaries — HR module",
      "tgt.salariesHrNote": "Remove any manual salary rows to avoid double counting",
      "rpt.tabLedger": "Money Ledger",
      "rpt.tabByJob": "Per Job",
      "rpt.tabLineItems": "Line Items",
      "rpt.tabVat": "VAT",
      "rpt.date": "Date",
      "rpt.type": "Type",
      "rpt.ref": "Reference",
      "rpt.job": "Job #",
      "rpt.party": "Party",
      "rpt.method": "Method",
      "rpt.amount": "Amount",
      "rpt.status": "Status",
      "rpt.customer": "Customer",
      "rpt.revenue": "Revenue",
      "rpt.cost": "Cost",
      "rpt.profit": "Profit",
      "rpt.margin": "Margin %",
      "rpt.paid": "Paid",
      "rpt.outstanding": "Outstanding",
      "rpt.description": "Description",
      "rpt.qty": "Qty",
      "rpt.unitPrice": "Unit price",
      "rpt.invoiceNo": "Invoice #",
      "rpt.invoiceDate": "Date",
      "rpt.zatcaType": "Type",
      "rpt.buyer": "Buyer",
      "rpt.taxable": "Taxable",
      "rpt.vat": "VAT (15%)",
      "rpt.total": "Total",
      "rpt.kind_invoice": "Invoice",
      "rpt.kind_return": "Return",
      "rpt.kind_payment": "Payment",
      "rpt.kind_part": "Part",
      "rpt.kind_service": "Service",
      "rpt.kind_expense": "Expense",
      // Task 5.3 i18n sweep: reportsTransactionService.ledger() now emits a
      // 'refund' kind row (downpayment-invoice cancellation refund) — was
      // missing, falling through parseMissingKeyHandler to a humanized key.
      "rpt.kind_refund": "Refund",
      "rpt.kind_collection": "Collection",
      "rpt.kind_credit_note": "Credit note",
      "rpt.kind_disbursement": "Disbursement",
      "rpt.forInvoice_one": "For invoice {{n}}",
      "rpt.forInvoice_other": "For invoices {{n}}",
      "rpt.kind_downpayment": "Downpayment",
      "rpt.lastActivity": "Last activity",
      "rpt.inv.title": "Invoice {{number}}",
      "rpt.inv.vehicle": "Vehicle",
      "rpt.inv.activity": "Activity",
      "rpt.inv.none": "No activity recorded yet.",
      "rpt.inv.loadFailed": "Could not load this invoice's activity.",
      "rpt.inv.openHint": "Click an invoice to see its activity.",
      "rpt.ev.issued": "Invoice issued",
      "rpt.ev.payment": "Payment received",
      "rpt.ev.receipt": "Paid from receipt",
      "rpt.ev.refund": "Refund",
      "rpt.ev.return": "Return invoice",
      "rpt.ev.credit_note": "Credit note",
      "rpt.ev.write_off": "Written off",
      "rpt.totInvoiced": "Invoiced",
      "rpt.totReturned": "Returned",
      "rpt.totCollected": "Collected",
      "rpt.totNet": "Net invoiced",
      "rpt.totRevenue": "Revenue",
      "rpt.totCost": "Cost",
      "rpt.totProfit": "Profit",
      "rpt.totParts": "Parts",
      "rpt.totServices": "Services",
      "rpt.totExpenses": "Expenses",
      "rpt.totGrand": "Total",
      "rpt.totTaxable": "Taxable",
      "rpt.totVat": "VAT",
      "rpt.totTotal": "Total",
      "rpt.exportExcel": "Export Excel",
      "rpt.empty": "No transactions in this period",
      "rpt.emptyDesc": "Try a wider date range.",
      "fin.title": "Financial Insight",
      "fin.loadFailed": "Could not load financials",
      "fin.empty": "No financial data.",
      "fin.profit": "Profit",
      "fin.margin": "Margin",
      "fin.onRevenue": "on revenue",
      "fin.expected": "Expected (chargeable)",
      "fin.actual": "Actual (billed)",
      "fin.parts": "Parts",
      "fin.services": "Services",
      "fin.expectedTotal": "Expected total",
      "fin.invoiced": "Invoiced",
      "fin.paid": "Paid",
      "fin.outstanding": "Outstanding",
      "fin.returned": "Returned / credited",
      "fin.cost": "Cost",
      "fin.partsCost": "Parts cost",
      "fin.servicesCost": "Services / labor cost",
      "fin.expensesCost": "Other expenses",
      "fin.costTotal": "Total cost",
      "fin.customer": "Customer",
      "fin.insurer": "Insurer",
      "fin.expectedShort": "Expected",
      "fin.invoicesList": "Invoices",
      "fin.returnTag": "return",
      "fin.view": "View financials",
      "fin.cardHint": "Expected vs actual revenue, cost, profit and margin for this job card.",
      "fin.note": "Expected reflects approved parts & services (pre-VAT). Actual reflects issued invoices. Profit = revenue − parts − services − other expenses.",
      "AC/Heating": "AC/Heating",
      "Add New Task": "Add New Task",
      "Add Task": "Add Task",
      "Assign Mechanic": "Assign Technician",
      "Bodywork": "Bodywork",
      "Cancel": "Cancel",
      "Describe the task in detail...": "Describe the task in detail...",
      "Electrical": "Electrical",
      "Estimated Hours": "Estimated Hours",
      "High": "High",
      "Issue Type": "Issue Type",
      "Low": "Low",
      "Mechanical": "Mechanical",
      "Normal": "Normal",
      "Other": "Other",
      "Priority": "Priority",
      "Routine Service": "Routine Service",
      "Task Description": "Task Description",
      "Task Name": "Task Name",
      "Tires/Wheels": "Tires/Wheels",
      "Unassigned": "Unassigned",
      "Urgent": "Urgent",
      "Vehicle Body Type": "Vehicle Body Type",
      "addServicesOrPartsWithCostGreaterThan0": "Add services or parts with a cost greater than 0",
      "adm.fieldConfiguredKeepBlank": "Configured ({{hint}}) — leave blank to keep",
      "allQCPassedGenerateInvoice": "All quality checks passed. You can now generate the invoice.",
      "back": "Back",
      "board.listView": "List View",
      "board.view": "Board View",
      "sort.label": "Sort by",
      "sort.smart": "Smart order",
      "sort.newest": "Newest first",
      "sort.oldest": "Oldest first",
      "sort.lastUpdated": "Last updated",
      "sort.jobNumber": "Job number",
      "cannotAddTasksWhileQuotationIsPendingApproval": "Cannot add tasks while the quotation is pending approval",
      "cannotModifyDeliveredOrClosedJobCard": "Cannot modify a delivered or closed job card",
      "cm.header.account": "Account menu",
      "commercialYellow": "Commercial Yellow",
      "completeJobCardConfirmNote": "The job card status will be changed to Received and diagnosis can begin.",
      "converting": "Converting...",
      "decoding": "Decoding...",
      "e.g., Engine Oil Change": "e.g., Engine Oil Change",
      "enter17CharacterVINOrScanBarcode": "Enter 17-character VIN or scan barcode",
      "history.activityHistory": "Activity History",
      "history.assignedTo": "Assigned to",
      "history.created": "Created",
      "history.detailsUpdated": "Details updated",
      "history.findingApproved": "Finding approved",
      "history.findingRejected": "Finding rejected",
      "history.findingSubmitted": "Finding submitted",
      "history.invoiceGenerated": "Invoice generated",
      "history.invoicePaid": "Invoice paid",
      "history.invoicePaidShort": "Invoice paid",
      "history.loadFailed": "Failed to load activity history",
      "history.noActivity": "No activity yet",
      "history.partAdded": "Added part {{part}} (x{{qty}})",
      "history.partAddedToTask": "Added part {{part}} (x{{qty}}) to task {{task}}",
      "history.partApproved": "Approved part {{part}}",
      "history.partInstalled": "Installed part {{part}} (x{{qty}})",
      "history.partRejected": "Rejected part {{part}}",
      "history.serviceCompleted": "Service {{service}} marked completed",
      "history.partInstalled": "Part {{part}} marked installed",
      "history.taskCompletionOverride": "Task {{task}} completed by override ({{services}} service(s), {{parts}} part(s) cascaded)",
      "ntf.approvalRequested.quotationDiscount.messageNoFigures": "{{requester}} requested a discount on quotation {{doc_number}}",
      "ntf.approvalRequested.invoiceDiscount.messageNoFigures": "{{requester}} requested a discount on invoice {{doc_number}}",
      "ntf.approvalRequested.quotationDiscount.messageNoAmount": "{{requester}} requested a {{percent}}% discount on quotation {{doc_number}}",
      "ntf.approvalRequested.invoiceDiscount.messageNoAmount": "{{requester}} requested a {{percent}}% discount on invoice {{doc_number}}",
      "ntf.approvalRequested.purchaseInvoice.messageNoAmount": "{{requester}} submitted purchase invoice {{doc_number}}, above their limit",
      "ntf.approvalRequested.quotationDiscount.title": "Quotation discount awaiting your approval",
      "ntf.approvalRequested.quotationDiscount.message": "{{requester}} requested a discount of SAR {{amount}} ({{percent}}%) on quotation {{doc_number}}",
      "ntf.approvalRequested.invoiceDiscount.title": "Invoice discount awaiting your approval",
      "ntf.approvalRequested.invoiceDiscount.message": "{{requester}} requested a discount of SAR {{amount}} ({{percent}}%) on invoice {{doc_number}}",
      "ntf.approvalRequested.purchaseInvoice.title": "Purchase invoice awaiting your approval",
      "ntf.approvalRequested.purchaseInvoice.message": "{{requester}} submitted purchase invoice {{doc_number}} for SAR {{amount}}, above their limit",
      "ntf.approvalApproved.title": "Your request was approved",
      "ntf.approvalApproved.message": "{{decider}} approved your request on {{doc_number}}",
      "ntf.approvalRejected.title": "Your request was rejected",
      "ntf.approvalRejected.message": "{{decider}} rejected your request on {{doc_number}}: {{reason}}",
      "appr.inbox.title": "Approvals",
      "appr.heldDiscountSend": "Awaiting discount approval — not sent to customer",
      "appr.heldDiscountSendQuote": "Awaiting discount approval — quotation not sent to customer",
      "appr.heldPurchasePost": "Awaiting purchase approval — not posted",
      "appr.heldActionReason": "On hold until the discount is approved or rejected",
      "appr.notSentToCustomer": "Not sent to customer — send it from here",
      "appr.discountExpired": "Discount request expired — issued at full price",
      "appr.discountPendingSendHeld": "Saved. The discount is above your limit and went for approval — the customer gets it once the discount is approved or rejected.",
      "appr.inbox.subtitle": "Discounts and purchase invoices waiting for your decision",
      "appr.inbox.col.type": "Type",
      "appr.inbox.col.document": "Document",
      "appr.inbox.col.requester": "Requested by",
      "appr.inbox.col.amount": "Amount",
      "appr.inbox.type.quotationDiscount": "Quotation discount",
      "appr.inbox.type.invoiceDiscount": "Invoice discount",
      "appr.inbox.type.purchaseInvoice": "Purchase invoice",
      "appr.inbox.emptyTitle": "Nothing is waiting for your approval",
      "appr.inbox.emptyDescription": "Discount and purchase requests above a colleague's limit will appear here.",
      "ntf.taskAssigned.title": "New task assigned to you",
      "ntf.taskAssigned.message": "Task {{task_name}} on job {{job_number}} was assigned to you",
      "ntf.taskNudge.title": "Task reminder",
      "ntf.taskNudge.message": "Reminder: {{task_name}} on job {{job_number}}",
      "ntf.repairStarted.title": "Work started — your tasks are ready",
      "ntf.repairStarted.message": "Job {{job_number}} moved to repair; you have {{count}} task(s)",
      "ntf.fleetSla.atRisk.title": "Fleet job close to its SLA limit",
      "ntf.fleetSla.atRisk.quote": "Job {{job_number}}: the quotation is close to the fleet's quote time limit",
      "ntf.fleetSla.atRisk.ready": "Job {{job_number}}: the car is close to the fleet's ready-by limit",
      "ntf.fleetSla.breached.title": "Fleet job past its SLA limit",
      "ntf.fleetSla.breached.quote": "Job {{job_number}}: the quotation is past the fleet's quote time limit",
      "ntf.fleetSla.breached.ready": "Job {{job_number}}: the car is past the fleet's ready-by limit",
      "ntf.fleetReplacementDue.title": "Replacement car due",
      "ntf.fleetReplacementDue.message": "Job {{job_number}}: the fleet's SLA promises a replacement car from {{due_date}}, and none is logged",
      "ntf.jobAssigned.title": "New job assigned to you",
      "ntf.jobAssigned.message": "Job {{job_number}} was assigned to you",
      "ntf.jobReassignedAway.title": "Job reassigned",
      "ntf.jobReassignedAway.message": "Job {{job_number}} has been reassigned to another technician",
      "ntf.jobUnassigned.title": "Removed from job",
      "ntf.jobUnassigned.message": "You have been removed from job {{job_number}}",
      "ntf.qcFailed.title": "Quality check failed — rework needed",
      "ntf.qcFailed.message": "Task {{task_name}} on job {{job_number}} failed QC: {{notes}}",
      "ntf.qcRecheck.title": "Rework done — ready to check again",
      "ntf.qcRecheck.message": "Task {{task_name}} on job {{job_number}} has been reworked and is ready for your re-check",
      "ntf.quotationApproved.title": "Quotation approved",
      "ntf.quotationApproved.message": "Customer approved quotation {{quotation_number}} — you can start the work",
      "ntf.receiptDoublePaid.title": "Possible double payment",
      "ntf.receiptDoublePaid.message": "Receipt {{receipt_number}} may have been paid twice (cash + online) — review and decide on a refund",
      "ntf.feedbackLow.title": "Low feedback rating",
      "ntf.feedbackLow.message": "Job {{jobNumber}} received a {{rating}}/5 rating from the customer",
      "ntf.hrExpiry.title": "Employee document/contract expiring",
      "ntf.hrExpiry.message": "{{item}} for {{employee_name}} expires in {{days}} day(s)",
      "ntf.contractNonRenewal.title": "Contract notice period approaching",
      "ntf.contractNonRenewal.message": "{{employee_name}}'s contract ends {{end_date}} ({{days_to_end}} day(s) away) — the {{notice_days}}-day notice period starts in {{lead_days}} day(s). Decide on renewal or notify the employee.",
      "ntf.hrWpsReminder.title": "WPS file not uploaded",
      "ntf.hrWpsReminder.message": "Payroll period {{period}} has had no WPS file uploaded for {{days}} day(s)",
      "svc.confirmCompleteTitle": "Complete service?",
      "svc.confirmCompleteMessage": "Mark this service as completed? It will count toward finishing the task and the job.",
      "pt.confirmInstallTitle": "Mark part installed?",
      "pt.confirmInstallMessage": "Mark this part as installed? It will count toward finishing the task and the job.",
      "history.partRemoved": "Removed part {{part}}",
      "history.paymentReceived": "Payment received",
      "history.paymentReceivedForInvoice": "Payment received for invoice {{number}} via {{method}}",
      "history.qcFailed": "Quality check failed for task {{task}}",
      "history.qcPassed": "Quality check passed for task {{task}}",
      "history.quotationApproved": "Quotation {{number}} approved ({{amount}})",
      "history.quotationApprovedByCustomer": "Quotation {{number}} approved by customer",
      "history.quotationGenerated": "Quotation {{number}} generated ({{amount}})",
      "history.serviceAdded": "Added service {{service}}",
      "history.serviceAddedToTask": "Added service {{service}} to task {{task}} ({{price}})",
      "history.serviceCompleted": "Completed service {{service}}",
      "history.serviceRemoved": "Removed service {{service}}",
      "history.statusChange": "Status changed",
      "history.taskAssigned": "Task {{task}} assigned to {{mechanic}}",
      "history.taskCompleted": "Completed task {{task}}",
      "history.taskCreated": "Created task {{task}}",
      "history.taskStarted": "Started task {{task}}",
      "history.unassignedFrom": "Unassigned from {{name}}",
      "history.userAssigned": "Assigned to {{user}}",
      // PR#2 Task 2.4 — the three activity-log entries that carry the
      // reject -> edit -> re-send story on the job card (owner decision 8).
      // ActivityLog.js renders an unrecognised action_type through
      // t(`history.${action_type}`, {...action_data}), so these need no
      // renderer change — but the reason CODE has to be part of the key,
      // because interpolated data cannot be translated.
      "history.quotation_item_rejected": "Customer declined “{{line}}” — {{reason}}",
      "history.quotation_item_rejected_too_expensive": "Customer declined “{{line}}” — the price is too high",
      "history.quotation_item_rejected_later": "Customer declined “{{line}}” — will do it later",
      "history.quotation_item_rejected_elsewhere": "Customer declined “{{line}}” — will do it at another workshop",
      "history.quotation_item_rejected_not_needed": "Customer declined “{{line}}” — does not need it",
      "history.quotation_item_rejected_own_part": "Customer declined “{{line}}” — will supply the part himself",
      "history.quotation_item_rejected_distrust_diagnosis": "Customer declined “{{line}}” — does not trust the diagnosis",
      "history.job_card_line_edited": "Re-priced “{{line}}”: {{old_price}} → {{new_price}}",
      "history.quotation_resent_after_edit": "Re-priced and re-sent as quotation {{quotation_number}} ({{lines}} line(s), {{amount}})",
      "jobCompletedSuccessfully": "Job completed successfully",
      "login.adminAccess": "Admin Access",
      "login.adminLogin": "Admin Login",
      "login.adminSubtitle": "Sign in to access the admin dashboard",
      "login.authFailed": "Authentication failed",
      "login.change": "Change",
      "login.copyright": "© {{year}} MotorMind. All rights reserved.",
      "login.emailAddress": "Email Address",
      "login.enterOtp": "Enter Verification Code",
      "login.enterOtpError": "Please enter the verification code",
      "login.otpDeck.checkingTitle": "Checking your code",
      "login.otpDeck.checkingSubtitle": "One moment, we're confirming it.",
      "login.otpDeck.verifiedTitle": "Verified",
      "login.otpDeck.verifiedSubtitle": "Setting up your session…",
      "login.otpDeck.errorAnnouncement": "Incorrect code. Please try again.",
      "jc.tzDecide.quickServiceTitle": "This looks like a quick service",
      "jc.tzDecide.quickServiceBody": "TzamunAI Decide suggests converting this job card to a quick service.",
      "jc.tzDecide.convertToQuickService": "Convert to quick service",
      "jc.tzDecide.proceedAsIs": "Proceed as is",
      "jc.tzDecide.fullCheckTitle": "This looks like a full inspection",
      "jc.tzDecide.fullCheckBody": "TzamunAI Decide suggests converting this job card to a full inspection.",
      "jc.tzDecide.convertToFullCheck": "Convert to full inspection",
      "jc.tzDecide.regularTitle": "This doesn't look like a quick service",
      "jc.tzDecide.regularBody": "TzamunAI Decide suggests converting this job card to a regular service.",
      "jc.tzDecide.convertToRegular": "Convert to regular service",
      "login.enterPhone": "Please enter your phone number",
      "login.incompletePhone": "Enter the full mobile number, for example 0501234567 or 501234567",
      "login.heroPoint1": "Manage job cards, parts, and invoices in one place",
      "login.heroPoint2": "Track every vehicle from check-in to delivery",
      "login.heroPoint3": "Grow your garage with insights and automation",
      "login.heroSubtitle": "The all-in-one platform to run your auto garage with ease.",
      "login.heroTitle": "Run your garage smarter",
      "login.loginSuccess": "Logged in successfully",
      "login.mobileLogin": "Mobile Login",
      "login.mobileNumber": "Mobile Number",
      "login.otpSent": "If this number is registered, a code is on its way.",
      "login.otpSentTo": "If this number is registered, we sent a code to",
      "login.otpSubtitle": "Enter the verification code sent to your mobile",
      "login.password": "Password",
      "login.phoneHint": "Enter your registered mobile number",
      "login.resendOtp": "Resend code",
      "login.sendOtp": "Send code",
      "login.sendingOtp": "Sending code...",
      "login.signIn": "Sign In",
      "login.signingIn": "Signing in...",
      "login.verificationCode": "Verification Code",
      "login.verifyLogin": "Verify & Sign In",
      "login.verifying": "Verifying...",
      "login.welcomeBack": "Welcome back",
      "makeModel": "Make / Model",
      "noResults": "No customers found",
      "noVehicleFound": "No vehicle found with that plate number",
      "openJobWarning.createAnyway": "Create anyway",
      "openJobWarning.message": "This vehicle already has an open job card. Do you want to continue?",
      "openJobWarning.title": "Open Job Card Exists",
      "or": "OR",
      "paymentMethods.bank_transfer": "Bank Transfer",
      "paymentMethods.card": "Card",
      "paymentMethods.cash": "Cash",
      "paymentMethods.check": "Check",
      "paymentMethods.mobile_payment": "Mobile Payment",
      "paymentMethods.pos": "POS",
      "paymentMethods.manual": "Manual",
      "pendingQC": "Pending QC",
      "plate": "Plate",
      "pleaseEnterRequiredFields": "Please fill all required fields",
      "pleaseSelectOrCreateVehicle": "Please select or create a vehicle",
      "publicTransportBlue": "Public Transport (Blue)",
      "qualityCheckProgress": "Quality Check Progress",
      "readyForDelivery": "Ready for Delivery",
      "registerNewVehicle": "Register New Vehicle",
      "resendCode": "Resend Code",
      "searchExistingVehicle": "Search Existing Vehicle",
      "selectAll": "Select All",
      "somethingWentWrong": "Something went wrong",
      "startDiagnosisFirstToAddTasks": "Start diagnosis first to add tasks",
      "startRepair": "Start Repair",
      "theme.dark": "Dark",
      "theme.light": "Light",
      "vehicleDelivered": "Vehicle Delivered",
      "vehicleReadyDeliveryCodeSent": "Vehicle is ready and the delivery code has been sent.",
      "vehicleShape": "Vehicle Shape",
      "verifyDeliveryCode": "Verify Delivery Code",
      "pcfg.tabPayments": "Payments",
      "zatca.tab": "ZATCA",
      "zatca.title": "ZATCA E-Invoicing",
      "zatca.phase2ActiveBadge": "Phase 2 active",
      "zatca.phase1": "Phase 1 (current)",
      "zatca.phase1Desc": "Offline invoice generation with TLV QR — today's behavior. Nothing to configure.",
      "zatca.phase2": "Phase 2 (Fatoora integration)",
      "zatca.phase2Desc": "Real-time clearance for B2B and 24h reporting for B2C via ZATCA's Fatoora platform.",
      "zatca.activatedSince": "Phase 2 active — cut-over {{date}}. Invoices posted since then are cleared/reported.",
      "zatca.deactivate": "Switch back to Phase 1",
      "zatca.deactivateConfirm": "Switch this company back to Phase 1? New invoices will stop being cleared/reported (already-cleared documents stay valid). Re-activation restarts the chain.",
      "zatca.deactivated": "Reverted to Phase 1",
      "zatca.setupTitle": "Phase 2 setup",
      "zatca.preconditionsFail": "Fix the company profile before onboarding:",
      "zatca.step.csr": "Keys & CSR",
      "zatca.step.ccsid": "OTP & compliance certificate",
      "zatca.step.compliance": "Compliance checks",
      "zatca.step.pcsid": "Production certificate",
      "zatca.step.cutover": "Cut-over",
      "zatca.lastError": "Last error",
      "zatca.environment": "Environment",
      "zatca.envSimulation": "Simulation (rehearsal)",
      "zatca.envProduction": "Production (live)",
      "zatca.envSandbox": "Developer sandbox",
      "zatca.invoiceTypes": "Invoice types this company issues",
      "zatca.typesBoth": "Standard (B2B) + Simplified (B2C)",
      "zatca.typesStandard": "Standard (B2B) only",
      "zatca.typesSimplified": "Simplified (B2C) only",
      "zatca.vatGroup": "VAT group number (optional)",
      "zatca.vatGroupHint": "Only for VAT-group members: the 15-digit group registration; the company's own TIN stays on the certificate.",
      "zatca.generateCsr": "Generate keys & CSR",
      "zatca.csrDone": "Keys generated and CSR ready",
      "zatca.otpExplain": "Ask the tenant to log into the Fatoora portal and generate an onboarding OTP (valid 1 hour), then enter it here.",
      "zatca.otp": "Fatoora OTP",
      "zatca.requestCcsid": "Request compliance certificate",
      "zatca.ccsidDone": "Compliance certificate issued (valid 24h — finish the next steps today)",
      "zatca.ccsidCountdown": "Compliance certificate expires in ~{{hours}}h — finish compliance + production before then.",
      "zatca.runCompliance": "Run ZATCA compliance checks",
      "zatca.warningsCount": "{{count}} warning(s)",
      "zatca.doc.invoice": "Invoice",
      "zatca.doc.credit_note": "Credit note",
      "zatca.doc.debit_note": "Debit note",
      "zatca.requestPcsid": "Issue production certificate",
      "zatca.pcsidDone": "Production certificate issued (valid 5 years)",
      "zatca.cutoverWarning": "From the cut-over moment, every invoice POSTED for this company goes through ZATCA: B2B invoices are held from the customer until cleared; B2C are reported within 24h. Documents posted before the cut-over stay Phase 1 forever.",
      "zatca.cutoverDate": "Cut-over date & time",
      "zatca.cutoverHint": "May be in the future (e.g. tonight 00:00). Cannot be in the past.",
      "zatca.activateNow": "Activate Phase 2",
      "zatca.activated": "Phase 2 activated",
      "zatca.submissions": "ZATCA submissions",
      "zatca.noSubmissions": "No submissions yet.",
      "zatca.pcsidExpiring": "Production certificate expires {{date}} — plan renewal",
      "zatca.colDoc": "Document",
      "zatca.colApi": "API",
      "zatca.colStatus": "Status",
      "zatca.colAttempts": "Attempts",
      "zatca.loadFailed": "Failed to load ZATCA configuration",
      "zatca.backToKeys": "Back to Keys & CSR",
      "zatca.restartConfirm": "Regenerating keys discards the current compliance/production certificates for this company — onboarding restarts from step 1. Continue?",
      "zatca.restartNotice": "Editing step 1 — generating a new CSR restarts onboarding.",
      "zatca.restartCancel": "Cancel",
      "zatca.cancelSetup": "Cancel setup",
      "zatca.cancelSetupConfirm": "Cancel Phase 2 setup completely? All generated keys and certificates for this company are discarded and the company simply stays on Phase 1. You can start setup again later from scratch.",
      "zatca.cancelSetupDone": "Setup cancelled — company remains on Phase 1",
      "zatca.companyVat": "Company VAT number: {{vat}} (used on the certificate unless a group number is entered below)",
      "zatca.sub.accepted": "Accepted",
      "zatca.sub.accepted_with_warnings": "Warnings",
      "zatca.sub.rejected": "Rejected",
      "zatca.sub.reconcile_required": "Needs review",
      "zatca.sub.queued": "Queued",
      "zatca.sub.sending": "Sending",
      "zatca.stuck.title": "Stuck ZATCA Submissions",
      "zatca.stuck.reconcileRequired": "Reconcile Required",
      "zatca.stuck.pastDeadline": "Past Deadline (SLA)",
      "zatca.stuck.rejected": "Rejected",
      "zatca.stuck.allClear": "No stuck submissions — all Phase-2 invoices are cleared or reported within SLA.",
      "zatca.stuck.notOnThisPage": "Stuck submissions exist but are older than this page — page through the list to reach them.",
      "zatca.stuck.retry": "Retry",
      "zatca.stuck.retryDone": "Submission re-queued for ZATCA",
      "zatca.stuck.abandon": "Abandon & void",
      "zatca.stuck.abandonDone": "Document abandoned; the invoice was voided",
      "zatca.stuck.abandonReasonPlaceholder": "Reason (required, kept in the audit trail)",
      "zatca.stuck.colInvoice": "Invoice / Document",
      "zatca.stuck.colState": "State",
      "zatca.stuck.colSubmitted": "Submitted At",
      "zatca.stuck.colDeadline": "Deadline",
      "zatca.stuck.colFailure": "Failure Cause",
      "zatca.stuck.colError": "ZATCA Error Details",
      "zatca.stuck.typeRejected": "Rejected by ZATCA (clearance / reporting refused)",
      "zatca.stuck.typeReconcile": "Worker Stalled (never sent or outcome ambiguous)",
      "zatca.stuck.typeDeadline": "24h SLA Breached (past reporting deadline)",
      "zatca.stuck.noErrorDetails": "No specific error text returned",
      "zatca.complianceFailedTitle": "ZATCA Compliance Check Failed",
      "zatca.complianceFailedDesc": "The compliance check was rejected by ZATCA. Per ZATCA specification (§3.3.4.2), the compliance certificate (CCSID) is spent and invalidated. Retrying requires generating a fresh CSR and obtaining a new single-use OTP from the Fatoora portal.",
      "pcfg.heading": "Payment Setup",
      "pcfg.payInGarage": "Pay in Garage",
      "pcfg.payInGarageHint": "Allow this tenant to offer pay-at-the-counter. Staff can then remove online card methods from individual payment links. No platform commission applies to these payments.",
      "pcfg.subtitle": "Configure settlement mode, per-method fees, and payout settings for this company.",
      "pcfg.allowTenantManage": "Allow tenant to manage payment options",
      "pcfg.allowTenantManageHint": "When off, this tenant’s payment settings are read-only and managed by MotorMind only.",
      "pcfg.methodsHeading": "Payment Methods",
      "pcfg.payoutHeading": "Bank Account & Settlement",
      "pcfg.save": "Save",
      "pcfg.saving": "Saving…",
      "pcfg.saved": "Payment configuration saved",
      "pcfg.failedToLoad": "Failed to load payment configuration",
      "pcfg.failedToSave": "Failed to save payment configuration",
      "pcfg.enabled": "Enabled",
      "pcfg.disabled": "Disabled",
      "pcfg.provider_myfatoorah": "MyFatoorah",
      "pcfg.provider_tabby": "Tabby",
      "pcfg.provider_tamara": "Tamara",
      "pcfg.settlementMode": "Settlement Mode",
      "pcfg.settlementModeHint": "Where funds are settled for this provider.",
      "pcfg.minimumAmount": "Minimum amount (SAR)",
      "pcfg.minimumAmountHint": "Below this amount the method is shown disabled in the picker. Leave blank for no minimum.",
      "pcfg.minimumAmountPlaceholder": "No minimum",
      "pcfg.settlementMode_own_account": "Own Account",
      "pcfg.settlementMode_motormind_managed": "MotorMind Managed",
      "pcfg.merchantOfRecord": "Merchant of Record",
      "pcfg.merchantOfRecordHint": "Legal merchant shown to the customer.",
      "pcfg.merchantOfRecord_garage": "Garage",
      "pcfg.merchantOfRecord_motormind": "MotorMind",
      "pcfg.providerFee": "Provider Fee",
      "pcfg.motormindFee": "MotorMind Fee",
      "pcfg.refundFee": "Refund Fee",
      "pcfg.feeType": "Fee Type",
      "pcfg.feeType_none": "None",
      "pcfg.feeType_percentage": "Percentage",
      "pcfg.feeType_fixed": "Fixed",
      "pcfg.feeType_both": "Percentage + Fixed",
      "pcfg.feePercentage": "Percentage",
      "pcfg.feePercentageHint": "e.g. 2.5 means 2.5%",
      "pcfg.feeFixed": "Fixed Amount",
      "pcfg.feeFixedHint": "Amount in SAR",
      "pcfg.sar": "SAR",
      "pcfg.feesOwnAccountNote": "Fees are typically used for MotorMind-managed settlement; for own-account they are informational.",
      "pcfg.disabledMethodNote": "Enable this method to configure its fees.",
      "pcfg.testMode": "Test / Sandbox mode",
      "pcfg.ownCredsHeading": "The garage's own gateway credentials (encrypted; never displayed).",
      "pcfg.managedCredsNote": "Uses MotorMind's platform credentials. Set the fees MotorMind charges this tenant.",
      "pcfg.credKeepPlaceholder": "Configured ({{hint}}) — leave blank to keep",
      "pcfg.credEnterPlaceholder": "Enter value",
      "pcfg.webhookUrl": "Webhook URL",
      "pcfg.webhookHint": "Register this URL in the provider dashboard.",
      "pcfg.webhookCopied": "Webhook URL copied",
      "pcfg.testConnection": "Test connection",
      "pcfg.testFailed": "Test failed",
      "pcfg.testHint": "Save credentials first, then test.",
      "pcfg.bankIban": "IBAN",
      "pcfg.bankIbanPlaceholder": "SA0000000000000000000000",
      "pcfg.bankName": "Bank Name",
      "pcfg.accountHolder": "Account Holder",
      "pcfg.payoutInterval": "Settlement Interval",
      "pcfg.payoutIntervalHint": "How often payouts run (with the unit).",
      "pcfg.payoutUnit": "Unit",
      "pcfg.payoutUnit_day": "Day(s)",
      "pcfg.payoutUnit_week": "Week(s)",
      "pcfg.payoutUnit_month": "Month(s)",
      "pcfg.negativeBalancePolicy": "Negative Balance Policy",
      "pcfg.negativeBalancePolicyHint": "What happens when the tenant balance goes negative.",
      "pcfg.negativeBalancePolicy_carry": "Carry forward",
      "pcfg.negativeBalancePolicy_block": "Block payouts",
      "pcfg.negativeBalancePolicy_absorb": "MotorMind absorbs",
      "mp.pageTitle": "Managed Payments",
      "mp.pageSubtitle": "Payments MotorMind collected on behalf of managed tenants",
      "mp.applyFilters": "Apply",
      "mp.clearFilters": "Clear",
      "mp.colMotorMindFee": "MotorMind Fee",
      "mp.colNetToTenant": "Net to Tenant",
      "mp.colPayoutStatus": "Payout Status",
      "mp.emptyTitle": "No managed payments yet",
      "mp.emptyDesc": "Collected payments for MotorMind-managed tenants will appear here.",
      "mp.exportPdf": "Print / PDF",
      "mp.kpiMotorMindFee": "MotorMind Fees",
      "mp.kpiNetToTenants": "Net to Tenants",
      "mp.kpiProviderFee": "Provider Fees",
      "mp.status.accrued": "Accrued",
      "mp.status.settled": "Settled",
      "mp.totalsFor": "Totals for {{count}}",
      "mp.allDates": "All dates",
      "mp.batch": "Batch",
      "ppc.title": "MotorMind-Managed Credentials",
      "ppc.subtitle": "Platform payment credentials used for MotorMind-managed tenants. Encrypted at rest; secrets are never displayed.",
      "ppc.configured": "Configured",
      "ppc.notConfigured": "Not configured",
      "ppc.enterPlaceholder": "Enter value",
      "ppc.keepPlaceholder": "Configured ({{h}}) — leave blank to keep",
      "ppc.environment": "Environment",
      "ppc.production": "Production",
      "ppc.sandbox": "Sandbox",
      "ppc.active": "Active",
      "ppc.updated": "Last updated",
      "ppc.saved": "{{p}} credentials saved",
      "ppc.saveFailed": "Could not save credentials",
      "ppc.webhookUrl": "Webhook URL",
      "ppc.webhookHint": "Register this URL in the provider dashboard to receive payment events.",
      "ppc.copied": "Webhook URL copied",
      "ppc.test": "Test",
      "ppc.testFailed": "Test failed",
      "ppc.testHint": "Test checks the saved credentials — click Save first, then Test.",
      "mfee.title": "Payment Method Fees",
      "mfee.subtitle": "Global fees applied to all MotorMind-managed tenants. Provider + MotorMind commission per method; tenants see only the total.",
      "mfee.method": "Method",
      "mfee.providerCommission": "Provider commission",
      "mfee.motormindCommission": "MotorMind commission",
      "mfee.total": "Total",
      "mfee.saved": "{{p}} fees saved",
      "mfee.saveAll": "Save all fees",
      "mfee.savedAll": "Payment fees saved",
      "mfee.saveFailed": "Could not save fees",
      "tfee.title": "Payment Commissions",
      "tfee.subtitle": "The total commission deducted per payment method on managed payments.",
      "tfee.allCards": "All cards",
      "tpg.title": "Tabby & Tamara (Your Own Account)",
      "tpg.title2": "Tabby & Tamara",
      "tpg.subtitle": "Connect your own Tabby and Tamara accounts. Credentials are encrypted and never displayed.",
      "tpg.subtitle2": "Use your own merchant account, or let MotorMind collect and settle to you. Credentials are encrypted and never displayed.",
      "tpg.configured": "Configured",
      "tpg.ownAccount": "Your own account",
      "tpg.ownAccountDesc": "Your own merchant credentials",
      "tpg.managed": "MotorMind account",
      "tpg.managedDesc": "MotorMind collects & settles to you",
      "tpg.managedTitle": "Collected by MotorMind",
      "tpg.managedNote": "This provider is managed by MotorMind and cannot be edited here.",
      "tpg.managedNote2": "MotorMind collects these payments on its account and settles your share to your payout bank account. MotorMind enables this provider and sets the fees — it goes live once configured on their side.",
      "tpg.modeSwitched": "Settlement mode updated",
      "tpg.lockedTip": "Managed by MotorMind — contact MotorMind to change these settings.",
      "tpg.keepPlaceholder": "Configured ({{h}}) — leave blank to keep",
      "tpg.enterPlaceholder": "Enter value",
      "tpg.enabled": "Enabled",
      "tpg.testMode": "Test / Sandbox mode",
      "tpg.saved": "{{p}} settings saved",
      "tpg.saveFailed": "Could not save",
      "mp.filterTenant": "Tenant",
      "mp.filterProvider": "Provider",
      "mp.filterStatus": "Payout Status",
      "mp.filterFrom": "From",
      "mp.filterTo": "To",
      "mp.btnFilter": "Apply",
      "mp.btnReset": "Reset",
      "mp.btnPdf": "Print / PDF",
      "mp.btnCsv": "Export CSV",
      "mp.colTenant": "Tenant",
      "mp.colDate": "Date",
      "mp.colProvider": "Provider",
      "mp.colGross": "Gross",
      "mp.colProviderFee": "Provider Fee",
      "mp.colPlatformFee": "MotorMind Fee",
      "mp.colNet": "Net to Tenant",
      "mp.colStatus": "Payout Status",
      "mp.colBatch": "Batch",
      "mp.statusAccrued": "Accrued",
      "mp.statusSettled": "Settled",
      "mp.kpiGross": "Total Gross",
      "mp.kpiProviderFees": "Provider Fees",
      "mp.kpiPlatformFees": "MotorMind Fees",
      "mp.kpiNet": "Net to Tenants",
      "mp.totalsRow": "Totals",
      "mp.noData": "No payments found.",
      "mp.allTenants": "All Tenants",
      "mp.allProviders": "All Providers",
      "mp.allStatuses": "All Statuses",
      "po.title": "Payouts",
      "po.subtitle": "Manage tenant payouts, refund approvals and inquiries",
      "po.tabPayouts": "Payouts",
      "po.tabRefunds": "Refund Approvals",
      "po.tabInquiries": "Inquiries",
      "po.provider": "Provider",
      "po.provider_myfatoorah": "MyFatoorah",
      "po.provider_tabby": "Tabby",
      "po.provider_tamara": "Tamara",
      "po.companyId": "Company ID",
      "po.companyIdPlaceholder": "Company UUID",
      "po.filterCompanyId": "Filter by company ID",
      "po.allStatuses": "All statuses",
      "po.period": "Period",
      "po.periodStart": "Period start",
      "po.periodEnd": "Period end",
      "po.outstanding": "Outstanding",
      "po.netTotal": "Net total",
      "po.due": "Due",
      "po.bnplUnsettled": "BNPL not yet settled",
      "po.bnplSettling": "BNPL settling",
      "po.noPayoutYet": "No payout",
      "po.generate": "Generate",
      "po.generatePayout": "Generate payout",
      "po.payoutGenerated": "Draft payout generated",
      "po.approve": "Approve",
      "po.approvePayout": "Approve payout",
      "po.approvePayoutConfirm": "Approve this draft payout? It will be ready to record once paid.",
      "po.payoutApproved": "Payout approved",
      "po.void": "Void",
      "po.voidPayout": "Void payout",
      "po.voidPayoutConfirm": "Void this payout? This cannot be undone.",
      "po.payoutVoided": "Payout voided",
      "po.record": "Record",
      "po.recordPayout": "Record payout",
      "po.payoutRecorded": "Payout recorded",
      "po.statement": "Statement",
      "po.bankReference": "Bank reference",
      "po.bankReferencePlaceholder": "Transfer / SWIFT reference",
      "po.bankReferenceRequired": "Bank reference is required",
      "po.settledToIban": "Settled to IBAN",
      "po.ibanRequired": "IBAN is required",
      "po.onFileIban": "On-file IBAN",
      "po.ibanMismatchWarning": "This IBAN differs from the one on file for this tenant. Double-check before recording.",
      "po.evidence": "Receipt files",
      "po.evidenceHint": "Upload one or more transfer receipts (image or PDF).",
      "po.evidenceRequired": "At least one receipt file is required",
      "po.removeFile": "Remove file",
      "po.payoutStatus_draft": "Draft",
      "po.payoutStatus_approved": "In process",
      "po.payoutStatus_paid": "Completed",
      "po.payoutStatus_void": "Void",
      "po.companyIdRequired": "Company ID is required",
      "po.periodRequired": "Period start and end are required",
      "po.periodInvalid": "Period end must be after period start",
      "po.noPayouts": "No outstanding payouts",
      "po.amount": "Amount",
      "po.reason": "Reason",
      "po.message": "Message",
      "po.refundType_full": "Full",
      "po.refundType_partial": "Partial",
      "po.approveRefund": "Approve refund",
      "po.approveRefundConfirm": "Approve this refund? The gateway refund will be triggered.",
      "po.refundApproved": "Refund approved",
      "po.reject": "Reject",
      "po.rejectRefund": "Reject refund",
      "po.rejectReason": "Rejection reason",
      "po.rejectReasonPlaceholder": "Explain why this refund is rejected",
      "po.rejectReasonRequired": "A rejection reason is required",
      "po.refundRejected": "Refund rejected",
      "po.refundStatus_requested": "Requested",
      "po.refundStatus_approved": "Approved",
      "po.refundStatus_processing": "Processing",
      "po.refundStatus_completed": "Completed",
      "po.refundStatus_rejected": "Rejected",
      "po.refundStatus_failed": "Failed",
      "po.noRefunds": "No refund requests pending",
      "po.respond": "Respond",
      "po.respondInquiry": "Respond to inquiry",
      "po.adminResponse": "Response",
      "po.adminResponsePlaceholder": "Write your response to the tenant",
      "po.responseRequired": "A response is required",
      "po.sendResponse": "Send response",
      "po.inquiryResponded": "Response sent",
      "po.inquiryStatus": "Status",
      "po.inquiryStatus_open": "Open",
      "po.inquiryStatus_under_review": "Under review",
      "po.inquiryStatus_resolved": "Resolved",
      "po.inquiryStatus_rejected": "Rejected",
      "po.inquiryReason_not_received": "Not received",
      "po.inquiryReason_wrong_amount": "Wrong amount",
      "po.inquiryReason_other": "Other",
      "po.noInquiries": "No open inquiries",
      "po.tabEligible": "Eligible transactions",
      "po.reference": "Reference",
      "po.gross": "Gross",
      "po.commission": "Commission",
      "po.gatewayFee": "Gateway fee",
      "po.net": "Net",
      "po.paidAt": "Paid at",
      "po.stage": "Stage",
      "po.allCompanies": "All companies",
      "po.allStages": "All stages",
      "po.stage_awaiting_payout": "Awaiting settlement",
      "po.stage_processing": "Processing",
      "po.stage_approved": "Approved",
      "po.stage_paid": "Paid",
      "po.noEligibleTransactions": "No eligible transactions",
      "po.noOutstandingBalances": "No outstanding balances",
      "tp.pageTitle": "Gateway Collections",
      "tp.pageSubtitle": "View your payments, reconciliation status and payouts",
      "tp.paymentsTab": "Payments",
      "tp.payoutsTab": "Payouts",
      "tp.paymentsCount": "Payments",
      "tp.payoutsCount": "Payouts",
      "tp.refresh": "Refresh",
      "tp.retry": "Retry",
      "tp.close": "Close",
      "tp.actions": "Actions",
      "tp.date": "Date",
      "tp.provider": "Provider",
      "tp.gross": "Gross",
      "tp.net": "Net",
      "tp.netOwed": "Net payable",
      "tp.fees": "Fees",
      "tp.vat": "VAT",
      "onb.navTitle": "Onboarding",
      "onb.title": "Client onboarding",
      "onb.subtitle": "Invite prospects and review their applications.",
      "onb.deleteInvitation": "Delete invitation",
      "onb.deleteConfirm": "Delete the invitation for {{name}}? This permanently removes the lead and any uploaded documents. This cannot be undone.",
      "onb.invitationDeleted": "Invitation deleted",
      "onb.inviteClient": "Invite client",
      "onb.inviteClientDesc": "Send a personalized onboarding link via SMS.",
      "onb.sendInvite": "Send invite",
      "onb.contactName": "Contact name",
      "onb.contactNamePlaceholder": "e.g. Ahmed Al-Saud",
      "onb.mobile": "Mobile",
      "onb.mobileHint": "Saudi mobile, e.g. 05XXXXXXXX or +9665XXXXXXXX",
      "onb.language": "Language",
      "onb.langAr": "العربية",
      "onb.langEn": "English",
      "onb.contactAndMobileRequired": "Contact name and mobile are required",
      "onb.invitedSmsSent": "Client invited — SMS sent",
      "onb.invitedSmsFailed": "Client invited but SMS failed — copy the link and send it manually",
      "onb.allStatuses": "All statuses",
      "onb.noLeads": "No onboarding leads yet",
      "onb.noLeadsDesc": "Invite a client to get started.",
      "onb.client": "Client",
      "onb.linkActivity": "Link activity",
      "onb.created": "Created",
      "onb.justNow": "just now",
      "onb.minutesAgo": "{{count}} min ago",
      "onb.hoursAgo": "{{count}} h ago",
      "onb.daysAgo": "{{count}} d ago",
      "onb.onboardingLink": "Onboarding link",
      "onb.copy": "Copy",
      "onb.copyLink": "Copy link",
      "onb.linkCopied": "Link copied",
      "onb.copyFailed": "Could not copy — select and copy manually",
      "onb.smsFailedSendManually": "SMS failed — copy the link and send it manually",
      "onb.notOpened": "Not opened",
      "onb.openedAt": "Opened",
      "onb.submittedAt": "Submitted",
      "onb.resend": "Resend",
      "onb.resendIn": "Resend in {{s}}s",
      "onb.view": "View",
      "onb.convert": "Convert to tenant",
      "onb.smsResent": "SMS resent",
      "onb.smsResendFailed": "SMS failed — copy the link and send it manually",
      "onb.applicationDetails": "Application details",
      "onb.invitedOn": "Invited",
      "onb.sectionPersonal": "Personal",
      "onb.sectionEntity": "Entity",
      "onb.sectionOperations": "Operations",
      "onb.sectionDocuments": "Documents",
      "onb.fullName": "Full name",
      "onb.email": "Email",
      "onb.legalName": "Legal name",
      "onb.commercialName": "Commercial name",
      "onb.crNumber": "CR number",
      "onb.vatNumber": "VAT number",
      "onb.vatExempt": "VAT exempt",
      "onb.nationalAddress": "National address",
      "onb.addressLine2": "Address line 2",
      "onb.city": "City",
      "onb.region": "Region",
      "onb.avgJobCards": "Avg job cards / month",
      "onb.branches": "Branches",
      "onb.ownGateway": "Has own payment gateway",
      "onb.gatewayProvider": "Gateway provider",
      "onb.referralSource": "How did you hear about us",
      "onb.docCr": "Commercial Registration",
      "onb.docVat": "VAT Certificate",
      "onb.docNationalAddress": "National Address Proof",
      "onb.docIban": "IBAN Letter",
      "adm.ibanDocument": "IBAN Letter",
      "onb.docLogo": "Logo",
      "onb.noDocuments": "No documents uploaded yet.",
      "onb.setStatus": "Set status",
      "onb.selectStatus": "Select status…",
      "onb.saveStatus": "Save status",
      "onb.statusUpdated": "Status updated",
      "onb.notes": "Internal notes",
      "onb.convertConfirm": "Create a pending tenant from this application?",
      "onb.converted": "Tenant created (pending approval)",
      "onb.readingDocuments": "Reading the uploaded documents…",
      "onb.scanFailed": "The official documents could not be read — the form shows only what the client typed. Check every field.",
      "onb.prefillFailed": "Could not load this application",
      "onb.alreadyConverted": "This application has already been converted to a tenant",
      "onb.convertedFromLead": "Tenant created from the application",
      "onb.fillMissing": "Missing — fill these in before creating the tenant:",
      "onb.verifyAgainstDocs": "Check against the official documents",
      "onb.verifyAgainstDocsHint": "The document value is used by default. Where it differs from what the client typed, both are shown — confirm each one.",
      "onb.fromDocument": "From the document",
      "onb.fromApplication": "From the application",
      "onb.useDocument": "Use document",
      "onb.useApplication": "Use application",
      "onb.usingDocument": "Using document",
      "onb.usingApplication": "Using application",
      "onb.documentValueApplied": "Taken from the document — the application left this blank",
      "onb.docFromApplication": "Sent by the client — will be attached on create.",
      "onb.clientVatExempt": "Client declared VAT exempt — no certificate was sent.",
      "onb.ibanSeedsPayoutConfig": "Saved as this tenant's payout account. The bank is read from the IBAN.",
      "onb.viewDoc": "View",
      "onb.status_invited": "Invited",
      "onb.status_opened": "Opened",
      "onb.status_submitted": "Submitted",
      "onb.status_under_review": "Under review",
      "onb.status_approved": "Approved",
      "onb.status_converted": "Converted",
      "onb.status_rejected": "Rejected",
      "onb.status_needs_more_info": "Needs more info",
      "onb.approve": "Approve",
      "onb.reject": "Reject",
      "onb.requestMoreInfo": "Request more information",
      "onb.selectFieldsToUpdate": "Select the fields the client must update",
      "onb.requestNote": "Message to the client",
      "onb.requestNotePlaceholder": "Explain what the client needs to fix or provide…",
      "onb.sendRequest": "Send request",
      "onb.approveConfirm": "Approve this application? The client will be notified by SMS.",
      "onb.rejectConfirm": "Reject this application?",
      "onb.approved": "Application approved",
      "onb.rejected": "Application rejected",
      "onb.moreInfoSent": "Requested more info — SMS sent to the client",
      "onb.requestedFields": "Fields requested",
      "onb.reviewNote": "Review note",
      "onb.first_name": "First name",
      "onb.last_name": "Last name",
      "onb.legal_name": "Legal name",
      "onb.commercial_name": "Commercial name",
      "onb.cr_number": "CR number",
      "onb.cr_document": "CR document (PDF)",
      "onb.vat_number": "VAT number",
      "onb.vat_certificate": "VAT certificate",
      "onb.national_address": "National address",
      "onb.national_address_proof": "National address proof",
      "onb.iban": "IBAN",
      "onb.iban_certificate": "IBAN letter",
      "onb.address_line2": "Address line 2",
      "onb.avg_job_cards_range": "Avg job cards / month",
      "onb.branches_range": "Number of branches",
      "onb.branches_count": "Branches (under same CR)",
      "onb.branchesCount": "Branches (under same CR)",
      "onb.business_mobile": "Business mobile",
      "onb.businessMobile": "Business mobile",
      "onb.has_own_gateway": "Own payment gateway",
      "onb.gateway_provider_name": "Gateway provider name",
      "onb.referral_source": "How did you hear about us",
      "onb.logo": "Logo",
      "tp.providerFee": "Provider Fee",
      "tp.motormindCommission": "MotorMind Commission",
      "tp.status": "Status",
      "tp.reconciliationStatus": "Reconciliation",
      "tp.outstandingBalance": "Outstanding Balance",
      "tp.period": "Period",
      "tp.settledDate": "Settled Date",
      "tp.bankReference": "Bank Reference",
      "tp.statement": "Statement",
      // Task 5.3 i18n sweep: pre-existing gap — t('tp.view', { defaultValue: 'View' })
      // on the payouts table (payout row + statement row) predates this branch;
      // defaultValue is ignored by parseMissingKeyHandler so it rendered "Tp.view".
      "tp.view": "View",
      "tp.jobOrderRef": "Job / Order",
      "tp.allProviders": "All Providers",
      "tp.allStatuses": "All Statuses",
      "tp.fromDate": "From",
      "tp.toDate": "To",
      "tp.clearFilters": "Clear Filters",
      "tp.amount": "Amount",
      "tp.amountPlaceholder": "Enter amount",
      "tp.reason": "Reason",
      "tp.requestRefund": "Request Refund",
      "tp.refundForPayment": "Refund for payment",
      "tp.refundType": "Refund Type",
      "tp.refundMode": "Refund Type",
      "tp.fullRefund": "Full Refund",
      "tp.partialRefund": "Partial Refund",
      "tp.fullOnly": "Full refund only",
      "tp.fullOrPartialAllowed": "Full or partial refund allowed",
      "tp.refundReasonPlaceholder": "Reason for the refund",
      "tp.submitRefundRequest": "Submit Refund Request",
      "tp.refundRequested": "Refund requested",
      "tp.existingRefunds": "Refund Requests",
      "tp.loadingRefunds": "Loading refunds…",
      "tp.noRefunds": "No refunds",
      "tp.noRefundsDescription": "You have not requested any refunds.",
      "tp.payoutInquiry": "Payout Inquiry",
      "tp.inquiryForPayout": "Inquiry for payout",
      "tp.inquiryReason": "Reason",
      "tp.inquiryMessagePlaceholder": "Describe the issue",
      "tp.tenantMessage": "Message",
      "tp.submitInquiry": "Submit Inquiry",
      "tp.inquirySubmitted": "Inquiry submitted",
      "tp.inquiryHistory": "Inquiry History",
      "tp.adminResponse": "Admin Response",
      "tp.loadingInquiries": "Loading inquiries…",
      "tp.noInquiries": "No inquiries",
      "tp.noInquiriesDescription": "You have not raised any payout inquiries.",
      "tp.noPayments": "No payments",
      "tp.noPaymentsDescription": "No payments have been recorded yet.",
      "tp.noPaymentsWithFilters": "No payments match the filters.",
      "tp.noPayouts": "No payouts",
      "tp.noPayoutsDescription": "No payouts have been issued yet.",
      "tp.failedToLoadPayments": "Failed to load payments",
      "tp.failedToLoadPayouts": "Failed to load payouts",
      "tp.failedToLoadRefunds": "Failed to load refunds",
      "tp.failedToLoadInquiries": "Failed to load inquiries",
      "tp.failedToRequestRefund": "Failed to request refund",
      "tp.failedToSubmitInquiry": "Failed to submit inquiry",
      "tp.provider.myfatoorah": "MyFatoorah",
      "tp.provider.tabby": "Tabby",
      "tp.provider.tamara": "Tamara",
      "tp.provider.other": "Other",
      "tp.paymentStatus.PENDING": "Pending",
      "tp.paymentStatus.PAID": "Paid",
      "tp.paymentStatus.FAILED": "Failed",
      "tp.paymentStatus.CANCELLED": "Cancelled",
      "tp.paymentStatus.EXPIRED": "Expired",
      "tp.status.unknown": "Unknown",
      "tp.reconciliation.accrued": "Accrued",
      "tp.reconciliation.settled": "Settled",
      "tp.reconciliation.unaccrued": "Not accrued",
      "tp.payoutStatus.draft": "Draft",
      "tp.payoutStatus.approved": "In process",
      "tp.payoutStatus.paid": "Completed",
      "tp.payoutStatus.void": "Void",
      "tp.payoutStage": "Payout stage",
      "tp.payoutStage.awaiting_payout": "Awaiting settlement",
      "tp.payoutStage.processing": "Processing",
      "tp.payoutStage.approved": "Approved",
      "tp.payoutStage.paid": "Paid",
      "tp.refundStatus.requested": "Requested",
      "tp.refundStatus.approved": "Approved",
      "tp.refundStatus.rejected": "Rejected",
      "tp.refundStatus.processing": "Processing",
      "tp.refundStatus.completed": "Completed",
      "tp.refundStatus.failed": "Failed",
      "tp.inquiryReason.not_received": "Not received",
      "tp.inquiryReason.wrong_amount": "Wrong amount",
      "tp.inquiryReason.other": "Other",
      "tp.inquiryStatus.open": "Open",
      "tp.inquiryStatus.under_review": "Under review",
      "tp.inquiryStatus.resolved": "Resolved",
      "tp.inquiryStatus.rejected": "Rejected",
      // Task 5.2 (payment-receipt redesign): source column/filter + Tamara notice.
      "tp.source": "Source",
      "tp.allSources": "All Sources",
      "tp.source.invoice": "Invoice Payment",
      "tp.source.downpayment_advance": "Downpayment Advance",
      "tp.source.receipt_payment": "Receipt Payment",
      "tp.source.refund": "Refund",
      "tp.tamaraFullRefundOnly": "Tamara refunds are full-refund only — a partial refund is not supported for this payment.",
      "tp.refundBlockedSettled": "Refund unavailable — this payment has already been settled and paid out. Contact support to arrange a refund.",
      // --- auto-merged i18n sweep keys ---
      'cat.filters': 'Filters',
      'cat.brakes': 'Brakes',
      'cat.engine': 'Engine',
      'cat.electrical': 'Electrical',
      'cat.fluids': 'Fluids',
      'cat.suspension': 'Suspension',
      'cat.body': 'Body',
      "adm.editCompany": "Edit Company",
      "adm.addNewCompany": "Add New Company",
      "adm.updateCompany": "Update Company",
      "adm.createCompany": "Create Company",
      "adm.basicInformation": "Basic Information",
      "adm.companyNameEnglish": "Company Name (English)",
      "adm.companyNameArabic": "Company Name (Arabic)",
      "adm.commercialNameEnglish": "Commercial Name (English)",
      "adm.commercialNameArabic": "Commercial Name (Arabic)",
      "adm.commercialNameHint": "Trade name shown to customers on invoices, quotations and public pages.",
      "adm.legalNameEnglish": "Legal Name (English)",
      "adm.legalNameArabic": "Legal Name (Arabic)",
      "adm.legalNameHint": "Registered name as on the CR. Printed in small font under the commercial name on tax documents.",
      "adm.subdomain": "Subdomain",
      "adm.companyPortalUrl": "(Company portal URL)",
      "adm.subdomainPermanentHint": "Permanent portal URL — cannot be changed after creation.",
      "adm.subdomainNewHint": "Only lowercase letters, numbers, and hyphens. This will be your company's unique portal URL.",
      "adm.businessVerification": "Business Verification",
      "adm.crNumber": "CR Number",
      "adm.vatNumber": "VAT Number",
      "adm.vatExempt": "VAT Exempt",
      "adm.vatRate": "VAT Rate (%)",
      "adm.crDocument": "CR Document",
      "adm.vatDocument": "VAT Document",
      "adm.upload": "Upload",
      "adm.headOfficeLocation": "Head Office Location",
      "adm.nationalAddress": "National Address (Short Address)",
      "adm.branchAddress": "Branch Address (Google Maps)",
      "adm.buildingNumber": "Building Number",
      "adm.buildingNumberHint": "4 digits",
      "adm.postalCode": "Postal Code",
      "adm.postalCodeHint": "5 digits",
      "adm.typeAddressToSearch": "Type address to search...",
      "adm.nationalAddressDocument": "National Address Document",
      "adm.uploadNationalAddressDocument": "Upload National Address Document",
      "adm.subscriptionPayment": "Subscription & Payment",
      "adm.selectPlan": "Select Plan",
      "adm.chooseAPlan": "Choose a Plan...",
      "adm.planOption": "{{name}} - {{price}} SAR/Yr",
      "adm.subscriptionStatus": "Subscription Status",
      "adm.subPendingApproval": "Pending Approval",
      "adm.subActive": "Active",
      "adm.subSuspended": "Suspended",
      "adm.subExpired": "Expired",
      "adm.subscriptionStartDate": "Subscription Start Date",
      "adm.subscriptionEndDate": "Subscription End Date",
      "adm.creditCard": "Credit Card",
      "adm.editBranch": "Edit Branch",
      "adm.addNewBranch": "Add New Branch",
      "adm.updateBranch": "Update Branch",
      "adm.createBranch": "Create Branch",
      "adm.branchInformation": "Branch Information",
      "adm.branchNameEnglish": "Branch Name (English)",
      "adm.branchNameArabic": "Branch Name (Arabic)",
      "adm.branchNamePlaceholder": "e.g. Al-Riyadh Auto Center",
      "adm.branchName": "Branch Name",
      "adm.branchNameLockedHint": "Set automatically from the company commercial name — the same across all branches.",
      "adm.branchCommercialNameEnglish": "Branch commercial name (English)",
      "adm.branchCommercialNameArabic": "Branch commercial name (Arabic)",
      "adm.branchCommercialNameHint": "Printed prominently on this branch's invoices, quotations and receipts. A new branch starts with the company commercial name.",
      "adm.companyLegalName": "Company legal name",
      "adm.companyLegalNameLockedHint": "Set from the company profile — the same for all branches; it cannot be edited here.",
      "adm.branchNameFromCompany": "Set from the company",
      "adm.branchReference": "Branch Reference",
      "adm.branchReferenceHint": "Short label to tell this branch apart in the branch picker (not shown to customers).",
      "adm.branchReferencePlaceholder": "e.g. Riyadh, Main, North",
      "adm.shortCodeUrl": "Short Code (URL)",
      "adm.shortCodeAutoPlaceholder": "Auto-generated if left blank",
      "adm.shortCodeAutoHint": "Optional. A short per-branch code (e.g. TXG1) for branch-specific links. Leave blank and we generate one — it is NOT a separate website; your login URL is the company subdomain.",
      "adm.branchLogo": "Branch Logo",
      "adm.uploadLogoHint": "Upload Logo (will be displayed on branch portal)",
      "adm.chooseLogoImage": "Choose logo image",
      "adm.logoRecommendation": "Recommended: PNG or JPG, max 2MB, square format (e.g., 512x512px)",
      "adm.preview": "Preview",
      "adm.logoPreviewAlt": "Logo preview",
      "adm.branchLocation": "Branch Location",
      "adm.branchIsActive": "Branch is Active",
      "adm.editPaymentGateway": "Edit Payment Gateway",
      "adm.addPaymentGateway": "Add Payment Gateway",
      "adm.updateGateway": "Update Gateway",
      "adm.addGateway": "Add Gateway",
      "adm.gatewayUpdatedSuccess": "Payment gateway updated successfully",
      "adm.gatewayAddedSuccess": "Payment gateway added successfully",
      "adm.gatewaySaveFailed": "Failed to save payment gateway",
      "adm.gatewayType": "Gateway Type",
      "adm.gatewayTypeCard": "Credit/Debit Card",
      "adm.gatewayTypeBnpl": "Buy Now Pay Later (BNPL)",
      "adm.gatewayTypeWallet": "Digital Wallet",
      "adm.provider": "Provider",
      "adm.selectProvider": "Select Provider",
      "adm.displayNameForCustomers": "Display Name (for customers)",
      "adm.displayNamePlaceholder": "e.g., Credit/Debit Card, Tabby - Pay in 4",
      "adm.enabled": "Enabled",
      "adm.testSandboxMode": "Test/Sandbox Mode",
      "adm.apiCredentialsEncrypted": "API credentials are encrypted and stored securely",
      "adm.enterField": "Enter {{field}}",
      "adm.noConfigFields": "No configuration fields available. Add a provider first.",
      "adm.displayOrder": "Display Order",
      "adm.lowerNumbersFirst": "Lower numbers appear first",
      "adm.editPlanTitle": "Edit Plan: {{name}}",
      "adm.planName": "Plan Name",
      "adm.priceSarYear": "Price (SAR/Year)",
      "adm.maxGarages": "Max Garages",
      "adm.maxUsers": "Max Users",
      "adm.featuresClickToToggle": "Features (Click to toggle) - {{count}} features",
      "adm.noFeaturesFound": "No features found. Please check the plan data.",
      "adm.bulkUploadParts": "Bulk Upload Parts",
      "adm.uploadParts": "Upload Parts",
      "adm.selectCsvFile": "Please select a CSV file",
      "adm.previewFailed": "Failed to preview file",
      "adm.uploadFailed": "Failed to upload file",
      "adm.downloadTemplateFailed": "Failed to download template",
      "adm.downloadCsvTemplate": "Download CSV Template",
      "adm.downloadTemplateDesc": "Download the template file to see the required format and example data.",
      "adm.downloadTemplate": "Download Template",
      "adm.dragDropCsv": "Drag and drop your CSV file here",
      "adm.or": "or",
      "adm.browseFiles": "Browse Files",
      "adm.countValid": "{{count}} Valid",
      "adm.countInvalid": "{{count}} Invalid",
      "adm.validationErrors": "Validation Errors:",
      "adm.andMoreErrors": "... and {{count}} more errors",
      "adm.row": "Row",
      "adm.valid": "Valid",
      "adm.invalid": "Invalid",
      "adm.noGarageSelected": "No garage selected",
      "adm.failedToLoadSettings": "Failed to load settings",
      "adm.garageInfoUpdated": "Garage information updated successfully",
      "adm.failedToUpdateGarageInfo": "Failed to update garage information",
      "adm.vatSettingsUpdated": "VAT settings updated successfully",
      "adm.failedToUpdateVat": "Failed to update VAT settings",
      "adm.enterPhoneNumber": "Please enter a phone number",
      "adm.numberAlreadyAdded": "This number is already added",
      "adm.notificationSettingsUpdated": "Notification settings updated successfully",
      "adm.failedToUpdateNotifications": "Failed to update notification settings",
      "adm.intakeDisputes": "Intake Confirmation Disputes",
      "adm.intakeConfirmations": "Intake Confirmations",
      "usr.assignedBranches": "Assigned Branches",
      "usr.primaryBranch": "Primary",
      "appr.pendingApproval": "Pending approval",
      "appr.discountRequested": "Requested discount: {{amount}}",
      "appr.rejectReasonPlaceholder": "Why is it rejected?",
      "appr.decideFailed": "The decision could not be saved. Please try again.",
      "appr.discountPendingSaved": "Saved. The discount is above your limit, so it was sent for approval. Nothing goes to the customer, and the document cannot be posted or paid, until it is approved or rejected.",
      "appr.aboveYourLimit": "Above your discount limit — this will be sent for approval.",
      "appr.approvalFilter": "Approval",
      "appr.discountExpiredOnPost": "Invoice issued at full price: the discount is above your limit and could not wait for approval on an invoice paid from a receipt. Issue a credit note if it is approved.",
      "usr.discountApprovalLimit": "Discount approval limit (%)",
      "usr.purchaseApprovalLimit": "Purchase approval limit (SAR)",
      "usr.approvalLimitHint": "When you add a user or change their role, empty limits take your own limits (unlimited only if yours are). On other edits, empty means no limit, which only an unlimited approver can grant. 0 means every discount or purchase needs approval.",
      "usr.ownLimitsLocked": "You can't change your own approval limits — ask another admin.",
      "usr.limitUnlimitedPlaceholder": "No limit",
      "usr.primaryBranchHint": "Tick every branch this user may work in. The primary branch is where they are based and what their login is scoped to.",
      "usr.branchInactive": "inactive",
      "usr.notificationLanguage": "Notification Language",
      "usr.notificationLanguageHint": "The language this user receives their notifications in.",
      "usr.languageArabic": "Arabic",
      "usr.languageEnglish": "English",
      "adm.newAppointment": "New Bookings",
      "adm.payoutInquiryResponse": "Payout Inquiry Replies",
      "adm.creditOverdue": "Overdue Credit Reminders",
      // One press instead of 21: turns every notification type on for this
      // recipient, including the eight job-status chips.
      "adm.selectAll": "Select all",
      "adm.clearAll": "Clear all",
      "adm.noGarageData": "No garage data available",
      "adm.zeroExempt": "0 (Exempt)",
      "adm.noNotificationNumbers": "No notification numbers added yet. Add a number above to configure notifications.",
      "cd.activateCompany": "Activate Company",
      "cd.activateCompanyDesc": "Restores access for all users. Portal becomes accessible again.",
      "cd.activateUser": "Activate user",
      "cd.activeBranches": "Active Branches",
      "cd.addBranch": "Add Branch",
      "cd.addCompany": "Add Company",
      "cd.addConfiguration": "Add Configuration",
      "cd.addFirstBranch": "Add First Branch",
      "cd.addFirstGateway": "Add First Gateway",
      "cd.addFirstItem": "Add the first item",
      "cd.addFirstUser": "Add First User",
      "cd.addGateway": "Add Gateway",
      "cd.addNewModelPlaceholder": "Add new model...",
      "cd.addUser": "Add User",
      "cd.authorizedBranches": "Authorized Branches",
      "cd.bank": "Bank",
      "cd.bnpl": "BNPL",
      "cd.branchCreated": "Branch created successfully",
      "cd.branchUpdated": "Branch updated successfully",
      "cd.branches": "Branches",
      "cd.branchesActive": "{{active}} active",
      "cd.branchesActiveOfAllowed": "{{active}} active of {{allowed}} allowed",
      "cd.businessDetails": "Business Details",
      "cd.cannotBeChanged": "Cannot be changed",
      "cd.cannotDeleteSystemItems": "Cannot delete system-defined items",
      "cd.catFuelLevels": "Fuel Levels",
      "cd.catFuelTypes": "Fuel Types",
      "cd.catInsuranceCompanies": "Insurance Companies",
      "cd.catJobPriorities": "Job Priorities",
      "cd.catJobStatuses": "Job Card Statuses",
      "cd.catPartStatuses": "Part Statuses",
      "cd.catPaymentMethods": "Payment Methods",
      "cd.catPaymentStatuses": "Payment Statuses",
      "cd.catQuotationStatuses": "Quotation Statuses",
      "cd.catTaskStatuses": "Task Statuses",
      "cd.catTransmissionTypes": "Transmission Types",
      "cd.catVehicleColors": "Vehicle Colors",
      "cd.catVehicleMakes": "Vehicle Makes & Models",
      "cd.catVehicleShapes": "Vehicle Shapes",
      "cd.catWarningLights": "Warning Lights",
      "cd.code": "Code",
      "cd.companiesSubtitle": "Manage companies and their branches (workshops/service centers)",
      "cd.companyActivated": "Company activated successfully",
      "cd.companyBranches": "Company Branches",
      "cd.companyCreated": "Company created successfully",
      "cd.notifyOwner": "Notify owner",
      "cd.notifyOwnerAgain": "Notify owner again",
      "cd.notifyOwnerTitle": "Send the owner their link?",
      "cd.notifyOwnerTo": "This goes by SMS to the mobile on their application, ending {{mobile}}.",
      "cd.notifyOwnerSend": "Send now",
      "cd.notifyOwnerLater": "Not yet",
      "cd.notifyOwnerEditable": "Edit the message before sending if you need to.",
      "cd.notifyOwnerNoUsersYet": "This tenant has no users yet, so the message says the account is being set up rather than inviting them to sign in. Send it again once their first user exists.",
      "cd.ownerNoticeSent": "Sent to the number ending {{mobile}}",
      "cd.ownerNoticeSentOn": "Last sent {{date}}",
      "cd.companyDeactivated": "Company deactivated successfully",
      "cd.companyDetailsHeading": "Company Details",
      "cd.companyDetailsUpdated": "Company details updated successfully",
      "cd.applyContactToBranches": "Also use this phone and email on every branch",
      "cd.applyContactToBranchesHint": "Invoices and quotations print the branch's contact details, not the company's. Leave this ticked so the documents match what you typed above. Untick it to leave a branch that has its own number untouched.",
      "cd.companyId": "Company ID",
      "cd.companyInformation": "Company Information",
      "cd.companyNameArabic": "Company Name (Arabic)",
      "cd.companyNameEnglish": "Company Name (English)",
      "cd.companyNotFound": "Company not found",
      "cd.companySettings": "Company Settings",
      "cd.companyStatus": "Company Status",
      "cd.companyStatusActiveDesc": "Company is active — all users can log in and access the portal.",
      "cd.companyStatusInactiveDesc": "Company is inactive — all users are blocked from accessing the portal.",
      "cd.companyUpdated": "Company updated successfully",
      "cd.companyUsers": "Company Users",
      "cd.companyUsersSubtitle": "Manage users and their branch access permissions",
      "cd.configCreated": "Configuration created successfully",
      "cd.configDeleted": "Configuration deleted successfully",
      "cd.configUpdated": "Configuration updated successfully",
      "cd.confirmDeleteGateway": "Are you sure you want to delete this payment gateway?",
      "cd.confirmDeleteItem": "Are you sure you want to delete \"{{value}}\"?",
      "cd.confirmRemoveModel": "Remove model \"{{model}}\"?",
      "cd.copied": "{{label}} copied",
      "cd.copyCompanyId": "Copy Company ID",
      "cd.copyPortalUrl": "Copy portal URL",
      "cd.copyTenantId": "Copy Tenant ID",
      "cd.crNumber": "CR Number",
      "cd.create": "Create",
      "cd.creditCard": "Credit Card",
      "cd.currentStatus": "Current Status",
      "cd.dangerZone": "Danger Zone",
      "cd.dangerZoneDesc": "These actions take effect immediately and may disrupt service.",
      "cd.daysCount": "{{count}} days",
      "cd.daysRemaining": "Days Remaining",
      "cd.deactivateCompany": "Deactivate Company",
      "cd.deactivateCompanyDesc": "Immediately blocks all tenant users from logging in. Subdomain becomes inaccessible.",
      "cd.deactivateUser": "Deactivate user",
      "cd.disabled": "Disabled",
      "cd.displayValueArabic": "Display Value (Arabic)",
      "cd.displayValueArabicPlaceholder": "e.g. تويوتا، أحمر، أوتوماتيك",
      "cd.displayValueEnglish": "Display Value (English)",
      "cd.displayValueEnglishPlaceholder": "e.g., Toyota, Red, Automatic",
      "cd.editBranch": "Edit Branch",
      "cd.editCompany": "Edit Company",
      "cd.editConfiguration": "Edit Configuration",
      "cd.editPlan": "Edit Plan",
      "cd.editSubscription": "Edit Subscription",
      "cd.editSubscriptionHint": "To edit subscription details, go to the \"Subscription & Plan\" tab",
      "cd.enabled": "Enabled",
      "cd.endDate": "End Date",
      "cd.enterModelName": "Please enter a model name",
      "cd.exempt": "Exempt",
      "cd.expired": "Expired",
      "cd.failedToAddModel": "Failed to add model",
      "cd.failedToCreateBranch": "Failed to create branch",
      "cd.failedToCreateCompany": "Failed to create company",
      "cd.failedToDeleteConfig": "Failed to delete configuration",
      "cd.failedToDeleteGateway": "Failed to delete payment gateway",
      "cd.failedToLoadCompanies": "Failed to load companies",
      "cd.failedToLoadCompanyDetails": "Failed to load company details",
      "cd.failedToRemoveModel": "Failed to remove model",
      "cd.failedToSaveConfig": "Failed to save configuration",
      "cd.failedToUpdateBranch": "Failed to update branch",
      "cd.failedToUpdateCompany": "Failed to update company",
      "cd.failedToUpdateCompanyDetails": "Failed to update company details",
      "cd.failedToUpdateCompanyStatus": "Failed to update company status",
      "cd.failedToUpdatePlan": "Failed to update plan",
      "cd.failedToUpdateSubscription": "Failed to update subscription",
      "cd.failedToUpdateUserStatus": "Failed to update user status",
      "cd.gatewayDeleted": "Payment gateway deleted",
      "cd.gatewayInfo1": "Add multiple payment methods (Cards, BNPL like Tabby/Tamara, Wallets)",
      "cd.gatewayInfo2": "Customers can choose their preferred payment method at checkout",
      "cd.gatewayInfo3": "Each gateway can be enabled/disabled independently",
      "cd.gatewayInfo4": "Display order controls which option appears first",
      "cd.keyLabel": "Key",
      "cd.keyPlaceholder": "e.g., toyota, red, automatic",
      "cd.keyUniqueIdentifier": "Key (Unique Identifier)",
      "cd.lastUpdated": "Last updated {{date}}",
      "cd.location": "Location",
      "cd.manageCompanyDetails": "Manage Company Details",
      "cd.manageSubscription": "Manage Subscription",
      "cd.maxBranchesUsers": "Max Branches: {{branches}} • Max Users: {{users}}",
      "cd.modelAdded": "Model added successfully",
      "cd.modelRemoved": "Model removed successfully",
      "cd.modelsCount": "{{count}} Models",
      "cd.multipleGatewaySupport": "Multiple Gateway Support",
      "cd.noBranchesAssigned": "No branches assigned",
      "cd.noBranchesForCompany": "No branches added for this company yet.",
      "cd.noBranchesYet": "No branches added yet",
      "cd.noCity": "No City",
      "cd.noCompaniesFound": "No companies found.",
      "cd.noGatewaysConfigured": "No payment gateways configured",
      "cd.noItemsInCategory": "No items found in this category",
      "cd.noPlan": "No Plan",
      "cd.noUsersYet": "No users added yet",
      "cd.paymentGatewayConfig": "Payment Gateway Configuration",
      "cd.paymentGatewayConfigSubtitle": "Configure multiple payment gateways (Cards, BNPL, Wallets) for all branches",
      "cd.permanentPortalUrl": "Permanent portal URL — cannot be changed after creation",
      "cd.plan": "Plan",
      "cd.planUpdated": "Plan updated successfully",
      "cd.portalUrl": "Portal URL",
      "cd.sarPerYear": "{{price}} SAR/Year",
      "cd.saving": "Saving…",
      "cd.searchCompaniesPlaceholder": "Search companies by name or CR number...",
      "cd.selectPlan": "Select Plan",
      "cd.shortCode": "Short Code",
      "cd.shortCodeHint": "3–4 character code used in legacy portal URLs (e.g. TXG)",
      "cd.shortCodePlaceholder": "e.g. TXG",
      "cd.sortOrder": "Sort Order",
      "cd.statusExpired": "Expired",
      "cd.statusPendingApproval": "Pending Approval",
      "cd.statusSuspended": "Suspended",
      "cd.subdomain": "Subdomain",
      "cd.subscription": "Subscription",
      "cd.subscriptionDetails": "Subscription Details",
      "cd.subscriptionEndDate": "Subscription End Date",
      "cd.subscriptionPlan": "Subscription Plan",
      "cd.subscriptionPlanManagement": "Subscription & Plan Management",
      "cd.subscriptionPlanManagementSubtitle": "Manage subscription plan, status, and billing details",
      "cd.subscriptionPlans": "Subscription Plans",
      "cd.subscriptionStartDate": "Subscription Start Date",
      "cd.subscriptionStatus": "Subscription Status",
      "cd.subscriptionSummary": "Subscription Summary",
      "cd.subscriptionUpdated": "Subscription updated successfully",
      "sub.amount": "Amount",
      "sub.aiQuota": "AI Agent requests / month",
      "sub.aiVoiceQuota": "Voice typing requests / month",
      "sub.apply": "Apply change",
      "sub.applyAnyway": "Apply anyway",
      "sub.branches": "Branches",
      "sub.change": "Change plan",
      "sub.changed": "Subscription changed",
      "sub.changeHint": "Pick the plan and the start date — the end date follows from the plan's term.",
      "sub.changeTitle": "Change subscription",
      "sub.colAmount": "Amount",
      "sub.colBy": "By",
      "sub.colPlan": "Plan",
      "sub.colReason": "Reason",
      "sub.colTerm": "Term",
      "sub.colWhat": "Change",
      "sub.colWhen": "When",
      "sub.confirmRenew": "Renew",
      "sub.custom": "Custom subscription",
      "sub.daysLeft": "{{count}} day left",
      "sub.daysLeft_other": "{{count}} days left",
      "sub.endComputed": "Calculated from the plan's term",
      "sub.endDate": "End date",
      "sub.expiredOn": "Expired on {{date}}",
      "sub.history": "Subscription history",
      "sub.keepCustom": "— No plan (keep the current settings) —",
      "sub.noHistory": "Nothing recorded yet",
      "sub.noTerm": "No end date set",
      "sub.openEnded": "open-ended",
      "sub.overageBody": "This subscription allows {{limit}} {{resource}}, and the company is already using {{inUse}}. Applying it will not remove anything, but nothing new can be added until the limit is raised.",
      "sub.overageTitle": "Below what is already in use",
      "sub.plan": "Plan",
      "sub.reason": "Reason",
      "sub.reasonHint": "Recorded on the subscription history against your name.",
      "sub.reasonPlaceholder": "e.g. upgraded to add a second branch",
      "sub.reasonRequired": "A reason is required when the subscription changes",
      "sub.err.REASON_REQUIRED": "A reason is required when the subscription changes.",
      "sub.err.NO_TERM": "This subscription has no term to renew. Pick a plan, or set the end date on the form below.",
      "sub.err.LIMIT_BELOW_USAGE": "That plan is smaller than what this company is already using.",
      "sub.renew": "Renew",
      "sub.renewed": "Subscription renewed",
      "sub.renewHint": "The new term starts the day after the current one ends.",
      "sub.renewTitle": "Renew subscription",
      "sub.startDate": "Start date",
      "sub.status.active": "Active",
      "sub.status.expired": "Expired",
      "sub.status.pending_approval": "Pending approval",
      "sub.status.suspended": "Suspended",
      "sub.term.day": "{{count}} day",
      "sub.term.day_other": "{{count}} days",
      "sub.term.month": "{{count}} month",
      "sub.term.month_other": "{{count}} months",
      "sub.term.week": "{{count}} week",
      "sub.term.week_other": "{{count}} weeks",
      "sub.term.year": "{{count}} year",
      "sub.term.year_other": "{{count}} years",
      "sub.type.cancellation": "Cancelled",
      "sub.type.change": "Changed",
      "sub.type.downgrade": "Downgrade",
      "sub.type.initial": "Started",
      "sub.type.renewal": "Renewed",
      "sub.type.upgrade": "Upgrade",
      "sub.unlimited": "Unlimited",
      "sub.users": "Users",
      "cd.system": "System",
      "cd.systemConfigurations": "System Configurations",
      "cd.systemConfigurationsSubtitle": "Manage dropdown values and system settings across all tenants",
      "cd.tabOverview": "Overview",
      "cd.tabPaymentGateway": "Payment Gateway",
      "cd.tabSubscriptionPlan": "Subscription & Plan",
      "cd.taxable": "Taxable",
      "cd.tenantId": "Tenant ID",
      "cd.toggleBranches": "Toggle branches",
      "cd.tryAgain": "Try Again",
      "cd.unnamedGarage": "Unnamed Garage",
      "cd.unsavedChangesConfirm": "You have unsaved changes. Discard and switch tab?",
      "cd.user": "User",
      "cd.userActivated": "User activated",
      "cd.userDeactivated": "User deactivated",
      "cd.hidden": "Hidden",
      "cd.hideFromTenant": "Hide from tenant users list",
      "cd.unhideFromTenant": "Show in tenant users list",
      "cd.userHidden": "User hidden from the tenant",
      "cd.userUnhidden": "User now visible to the tenant",
      "cd.vatEffectiveDate": "VAT Effective Date",
      "cd.vatEffectiveDateHint": "Date when VAT exemption status becomes effective",
      "cd.vatExempt": "VAT Exempt",
      "cd.vatRate": "VAT Rate (%)",
      "cd.vatRatePlaceholder": "e.g., 15",
      "cd.vatStatus": "VAT Status",
      "cd.wallet": "Wallet",
      "cd.warnExpired": "Subscription has expired.",
      "cd.warnExpiresInDays": "Subscription expires in {{count}} days.",
      "cd.warnExpiresSoonRenew": "Subscription expires in {{count}} days — renew now.",
      "cd.warnSuspended": "Subscription is suspended. Users may lose access.",
      "cd.working": "Working…",
      "cd.zeroExempt": "0 (Exempt)",
      "cm.vehicleSpecs.loading": "Loading specifications...",
      "cm.vehicleSpecs.unavailable": "Vehicle Specifications Unavailable",
      "cm.vehicleSpecs.rateLimit": "The free API tier has reached its rate limit.",
      "cm.vehicleSpecs.upgrade": "Upgrade to a paid API plan to enable this feature.",
      "cm.vehicleSpecs.engine": "Engine",
      "cm.vehicleSpecs.fillHint": "Add the engine size, fuel type and transmission on the vehicle profile — the AI assistant uses them for a more accurate diagnosis.",
      "cm.vehicleSpecs.fuelType": "Fuel Type",
      "cm.vehicleSpecs.transmission": "Transmission",
      "cm.vehicleSpecs.driveType": "Drive Type",
      "cm.vehicleSpecs.cityFuelEconomy": "City Fuel Economy",
      "cm.vehicleSpecs.highwayFuelEconomy": "Highway Fuel Economy",
      "cm.vehicleSpecs.combinedMpg": "Combined MPG",
      "cm.vehicleSpecs.vehicleClass": "Vehicle Class",
      "cm.vehicleSpecs.automatic": "Automatic",
      "cm.vehicleSpecs.manual": "Manual",
      "cm.vehicleSpecs.na": "N/A",
      "cm.vehicleSpecs.cylinderSuffix": "-Cylinder",
      "cm.vehicleSpecs.rwd": "Rear Wheel Drive",
      "cm.vehicleSpecs.fwd": "Front Wheel Drive",
      "cm.vehicleSpecs.awd": "All Wheel Drive",
      "cm.vehicleSpecs.fourWd": "Four Wheel Drive",
      "cm.vehicleSpecs.gasoline": "Gasoline",
      "cm.vehicleSpecs.diesel": "Diesel",
      "cm.vehicleSpecs.electric": "Electric",
      "cm.vehicleSpecs.hybrid": "Hybrid",
      "cm.vehicle.istamaraScanExplainer": "Reads the plate, owner, VIN and model automatically and saves the Istamara photo",
      "cm.vehicle.saudiPlateGroup": "Saudi plate",
      "cm.vehicle.istamaraPhotoTag": "Istamara",
      "cm.vehicle.insuranceOptionalHint": "optional — feeds expiry alerts",
      "cm.vehicle.photosUploaded": "{{count}} photo(s) uploaded",
      "cm.vehicle.photosAdded": "{{count}} photo(s) added",
      "cm.vehicle.photosUploadFailed": "Failed to upload photos",
      "cm.vehicle.photoDeleted": "Photo deleted",
      "cm.vehicle.photoDeleteFailed": "Failed to delete photo",
      "cm.vehicle.vinScanned": "VIN scanned successfully!",
      "cm.vehicle.vinInvalid": "Please enter a valid 17-character VIN",
      "cm.vehicle.vinDecoded": "VIN decoded! Auto-filled: {{fields}}",
      "cm.vehicle.vinFillRemaining": "Please fill in the remaining fields manually",
      "cm.vehicle.vinDecodedNoData": "VIN decoded but no vehicle data found. Please enter details manually.",
      "cm.vehicle.vinNoData": "No vehicle data found for this VIN",
      "cm.vehicle.vinDecodeFailed": "Failed to decode VIN",
      "cm.vehicle.istamaraAutoFilled": "Istamara photo saved! Auto-filled: {{fields}}",
      "cm.vehicle.istamaraSavedNoData": "Istamara photo saved, but could not extract data automatically. Please enter vehicle details manually.",
      "cm.vehicle.istamaraExtractFailed": "Could not extract data from Istamara. Please try again with better lighting.",
      "cm.vehicle.field.ownerName": "Owner Name",
      "cm.vehicle.field.customerMatch": "Customer Match",
      "cm.vehicle.field.vin": "VIN",
      "cm.vehicle.field.plateNumber": "Plate Number",
      "cm.vehicle.field.plateLetters": "Plate Letters",
      "cm.vehicle.field.brand": "Brand",
      "cm.vehicle.field.model": "Model",
      "cm.vehicle.field.year": "Year",
      "cm.vehicle.field.color": "Color",
      "cm.vehicle.field.sequenceNumber": "Sequence Number",
      "cm.vehicle.field.fuelType": "Fuel Type",
      "cm.vehicle.field.transmission": "Transmission",
      "cm.vehicle.photoAlt": "Vehicle {{index}}",
      "cm.vehicle.photoAltSingle": "Vehicle",
      "cm.vehicle.searchBrands": "Search brands...",
      "cm.vehicle.searchModels": "Search models...",
      "cm.vehicle.searchColors": "Search colors...",
      "cm.vehicle.selectGeneration": "Select Generation",
      "cm.vehicle.noGenerations": "No generations available",
      "cm.vehicle.enterModelFreeText": "Type the model",
      "cm.vehicle.present": "Present",
      "cm.vehicle.selectYear": "Select Year",
      "cm.vehicle.plateTemporary": "Temporary (Gray)",
      "cm.vehicle.plateDiplomatic": "Diplomatic (Green)",
      "cm.vehicle.selectType": "Select Type",
      "cm.vehicle.specsAiHint": "Engine size, fuel type and transmission help the AI assistant give a more accurate diagnosis.",
      "cm.vehicle.engineSizePlaceholder": "e.g. 3.0L I6, 2.0L Turbo",
      "cm.vehicle.vinScanOrType": "Scan the VIN barcode from the car door, or type it manually.",
      "cm.vehicle.chassisNumber": "Chassis Number",
      "cm.vehicle.chassisNumberPlaceholder": "Chassis number",
      "cm.customer.phonePrefillChip": "Phone filled from quick create",
      "zatca.column": "ZATCA",
      "zatca.state.cleared": "Cleared",
      "zatca.state.cleared_with_warnings": "Cleared (warnings)",
      "zatca.state.reported": "Reported",
      "zatca.state.reported_with_warnings": "Reported (warnings)",
      "zatca.state.rejected": "Rejected",
      "zatca.state.reconcile_required": "Needs reconciling",
      "zatca.state.superseded": "Superseded",
      "zatca.state.pending_clearance": "Awaiting clearance",
      "zatca.state.pending_reporting": "Awaiting reporting",
      "zatca.state.not_submitted": "Not submitted",
      "zatca.sandboxValidatedOnly": "Sandbox: validated, not reported",
      "zatca.pdfPendingClearance": "PDF available once ZATCA clears this invoice",
      "zatca.invalid.vat": "Must be 15 digits, starting and ending with 3",
      "zatca.invalid.cr": "Must be exactly 10 digits",
      "zatca.invalid.building": "Must be exactly 4 digits",
      "zatca.invalid.postal": "Must be exactly 5 digits",
      "zatca.invalid.additional": "Must be exactly 4 digits",
      "zatca.invalid.tooLong": "This value is too long",
      "zatca.invalid.buyerId": "Does not match the selected identifier type",
      "zatca.invalid.buyerIdRequired": "A business customer needs a VAT number or an identifier number",
      "zatca.invalid.buyerIdTypeRequired": "Choose the identifier type",
      "zatca.invalid.tin": "TIN must be exactly 10 digits",
      "cm.customer.vatRegistered": "VAT-registered?",
      "cm.customer.vatRegisteredYes": "Yes",
      "cm.customer.vatRegisteredNo": "No",
      "cm.customer.notVatHint": "If available, choose the identifier type and enter its number",
      "cm.customer.legacyIdWarning": "Business identifier incomplete. The standard invoice may receive a ZATCA warning.",
      "val.invalid.saMobile": "Enter a 9-digit mobile starting with 5",
      "val.invalid.email": "Enter a valid email address",
      "val.invalid.nationalId": "Must be 10 digits starting with 1 or 2",
      "val.invalid.iban": "Enter a valid Saudi IBAN (SA + 22 characters)",
      "val.invalid.year": "Enter a valid 4-digit year",
      "val.invalid.number": "Enter a valid number",
      "val.invalid.wholeNumber": "Enter a whole number",
      "val.invalid.min": "Value is below the allowed minimum",
      "val.invalid.max": "Value is above the allowed maximum",
      "zatca.retry": "Re-report to ZATCA",
      "zatca.retrying": "Re-reporting…",
      "zatca.retryQueued": "Re-queued for ZATCA",
      "zatca.evidence.title": "ZATCA audit trail",
      "zatca.evidence.uuid": "Document UUID",
      "zatca.evidence.hash": "Invoice hash",
      "zatca.evidence.pih": "Previous invoice hash (PIH)",
      "zatca.evidence.downloadSigned": "Download signed XML",
      "zatca.evidence.downloadCleared": "Download cleared XML (from ZATCA)",
      "zatca.evidence.downloadFailed": "Could not download the XML file.",
      "zatca.evidence.nonProduction": "{{environment}} environment — this document is signed with ZATCA's shared test certificate and is NOT valid evidence. The environment is stamped into the downloaded filename.",
      "zatca.evidence.explainB2C": "Simplified invoices are reported to ZATCA after issuance. The QR is generated by this system from its certified device, and the reporting status above is what records ZATCA's acceptance.",
      "zatca.evidence.explainB2B": "Standard invoices are cleared by ZATCA before being shared with the buyer. The cleared XML is ZATCA's own counter-signed document and carries the QR ZATCA generated — it is the strongest evidence available.",
      "zatca.retryHint": "The invoice stays as it is — the same document is sent again. Use Create Return only when the invoice CONTENT is wrong.",
      "cm.customer.addressOptional": "Address (Optional)",
      "cm.customer.addressB2b": "Street Address",
      "cm.customer.addressB2bHint": "Required for VAT invoices (ZATCA BR-KSA-10)",
      "cm.customer.addressSearchPlaceholder": "Search for an address…",
      "cm.customer.b2bCustomer": "B2B Customer (Business)",
      "cm.customer.b2bHint": "Check if this is a business customer (enables standard tax invoice)",
      "cm.customer.businessInfo": "Business Information",
      "cm.customer.companyName": "Company Name",
      "cm.customer.companyNamePlaceholder": "ABC Company Ltd.",
      "cm.customer.vatHint": "15-digit Saudi VAT number",
      "cm.customer.crNumber": "CR Number (Commercial Registration)",
      "cm.customer.idTypeLabel": "Identifier type",
      "cm.customer.idNumber": "Identifier number",
      "cm.customer.b2bIdRule": "Enter the VAT number, or an identifier number with its type",
      "cm.customer.idType.TIN": "Tax Identification Number (TIN)",
      "cm.customer.idType.CRN": "Commercial Registration (CR)",
      "cm.customer.idType.MOM": "MOMRAH license",
      "cm.customer.idType.MLS": "MHRSD license",
      "cm.customer.idType.700": "700 Number",
      "cm.customer.idType.SAG": "MISA license",
      "cm.customer.idType.NAT": "National ID",
      "cm.customer.idType.GCC": "GCC ID",
      "cm.customer.idType.IQA": "Iqama Number",
      "cm.customer.idType.PAS": "Passport",
      "cm.customer.idType.OTH": "Other ID",
      "cm.customers.editCustomer": "Edit Customer",
      "cm.customers.vehicleCount": "{{count}} vehicle(s)",
      "cm.notifications.title": "Notifications",
      "cm.notifications.markAllRead": "Mark all read",
      "cm.notifications.empty": "No notifications",
      "cm.notifications.viewDetails": "View Details",
      "cm.notifications.allMarkedRead": "All notifications marked as read",
      "cm.notifications.markAllFailed": "Failed to mark all as read",
      "cm.actions.close": "Close",
      "cm.actions.download": "Download",
      "cm.time.justNow": "Just now",
      "cm.time.minutesAgo": "{{count}}m ago",
      "cm.time.hoursAgo": "{{count}}h ago",
      "cm.time.daysAgo": "{{count}}d ago",
      "cm.qr.title": "QR Code",
      "cm.qr.scanToAccess": "Scan to access job card",
      "cm.qr.scanHint": "Scan this QR code to quickly access this job card",
      "cm.qr.imageAlt": "Job Card QR Code",
      "cm.qr.print": "Print QR Code",
      "cm.qr.invalid": "Invalid QR code",
      "cm.qr.notFound": "Job card not found",
      "cm.lightbox.zoomIn": "Zoom in",
      "cm.lightbox.zoomOut": "Zoom out",
      "cm.lightbox.fullSizeAlt": "Full size",
      "cm.confirmAction": "Confirm Action",
      "cm.confirmProceed": "Are you sure you want to proceed?",
      "cm.close": "Close",
      "cm.search": "Search...",
      "cm.clear": "Clear",
      "cm.progress": "progress",
      "cm.login.sendOtpFailed": "Failed to send OTP",
      "cm.login.invalidOtp": "Invalid OTP",
      "cm.login.loginFailed": "Login failed",
      "cm.header.menu": "Menu",
      "cm.header.theme": "Theme",
      "cm.header.help": "Help & manual",
      "cm.header.more": "More",
      "qc.title": "Create new",
      "qc.newJobCard": "New job card",
      "qc.newCustomer": "New customer",
      "qc.newVehicle": "New vehicle",
      "qc.newInvoice": "New invoice",
      "cm.header.switchToArabic": "Switch to Arabic",
      "cm.header.switchToEnglish": "Switch to English",
      "cm.header.switchToStaffView": "Switch to Staff view",
      "cm.header.switchToAdminView": "Switch to Admin view",
      "cm.header.switchedToArabic": "Switched to Arabic",
      "cm.header.switchedToEnglish": "Switched to English",
      "cm.sidebar.poweredBy": "Powered by",
      "cm.processing": "Processing...",
      "cm.scanning": "Scanning...",
      "cm.retry": "Retry",
      "cm.instructions_label": "Instructions:",
      "cm.tip_label": "Tip:",
      "cm.camera.start": "Start Camera",
      "cm.camera.stop": "Stop Camera",
      "cm.camera.capture": "Capture Photo",
      "cm.camera.upload_photo": "Upload Photo",
      "cm.camera.take_photo": "Take Photo",
      "cm.camera.preview_placeholder": "Camera preview will appear here",
      "cm.camera.start_failed": "Failed to start camera. Please check camera permissions.",
      "cm.camera.access_failed": "Unable to access camera. Please check permissions.",
      "cm.camera.access_generic": "Could not access camera.",
      "cm.camera.access_permission_hint": "Could not access camera. Please ensure camera permissions are granted.",
      "cm.camera.busy": "Camera is busy using another app (e.g. Zoom/Teams). Please close it and refresh.",
      "cm.camera.permission_denied": "Camera permission denied. Please allow access in browser settings.",
      "cm.camera.not_found": "No camera found on this device.",
      "cm.location.requesting": "Requesting location…",
      "cm.location.required_title": "Location required",
      "cm.location.required_msg": "Enable location to capture. Photos and videos are stamped with the location.",
      "cm.location.denied": "Location is blocked. Allow location for this site in your browser settings, then retry.",
      "cm.location.retry": "Enable & retry",
      "cm.istamara.title": "Scan Istamara (Vehicle Registration)",
      "cm.istamara.tip_flat": "Position the Istamara card flat and well-lit",
      "cm.istamara.tip_focus": "Ensure all text is visible and in focus",
      "cm.istamara.tip_glare": "Avoid shadows and glare",
      "cm.istamara.tip_ai_extract": "TzamunAI Vision will extract all fields automatically",
      "cm.istamara.processing_vision": "Processing with TzamunAI Vision...",
      "cm.istamara.video_play_failed": "Failed to play video stream",
      "cm.istamara.extract_failed": "Failed to extract Istamara data",
      "cm.istamara.process_failed": "Failed to process image",
      "cm.istamara.align_hint": "Fit the whole card inside the frame",
      "cm.istamara.captured_hint": "Photo captured",
      "cm.istamara.q_ready": "Ready",
      "cm.istamara.q_dark": "Too dark — find more light",
      "cm.istamara.q_glare": "Reflection on the card — tilt it slightly",
      "cm.istamara.q_blurry": "Hold steady — not in focus",
      "cm.istamara.q_empty": "Place the card inside the frame",
      "cm.istamara.capture_anyway": "Capture Anyway",
      "cm.istamara.rotate_frame": "Rotate frame",
      "cm.istamara.retake": "Retake Photo",
      "cm.istamara.retry_read": "Try Reading Again",
      "cm.istamara.stage_uploading": "Sending the photo...",
      "cm.istamara.stage_reading": "Reading the card...",
      "cm.istamara.stage_finishing": "Almost done...",
      "cm.qr.scan_title": "Scan Code",
      "cm.qr.position_hint": "Position the QR code or barcode within the frame",
      "cm.qr.tip_body": "Hold the camera steady and ensure good lighting for best results. Supports QR codes and CODE128 barcodes.",
      "cm.vin.title": "Scan VIN Barcode",
      "cm.vin.mode_barcode": "Barcode",
      "cm.vin.mode_text": "Text OCR",
      "cm.vin.invalid_format": "Invalid VIN format. VIN must be 17 characters.",
      "cm.vin.point_barcode": "Point camera at VIN barcode",
      "cm.vin.barcode_location": "Usually found on driver's side door jamb",
      "cm.vin.point_text": "Point camera at VIN text",
      "cm.vin.text_location": "Found on dashboard near windshield or door jamb",
      "cm.vin.scanning_barcode": "Scanning barcode...",
      "cm.vin.reading_text": "Reading VIN text...",
      "cm.video.start_recording": "Start Recording",
      "cm.video.stop_recording": "Stop Recording",
      "cm.plate.alt": "Saudi Plate",
      "cm.diagram.clear_all": "Clear All",
      "cm.diagram.delete_point": "Delete Point",
      "cm.diagram.no_photo_attached": "No photo attached",
      "cm.diagram.view_alt": "{{view}} view",
      "cm.diagram.damage_alt": "Damage",
      "cm.diagram.vehicle_damage_inspection": "Vehicle Damage Inspection",
      "cm.diagram.click_to_mark": "Click on the car diagram to mark damage locations ({{count}})",
      "cm.diagram.front": "FRONT",
      "cm.diagram.rear": "REAR",
      "cm.diagram.left_short": "L",
      "cm.diagram.right_short": "R",
      "cm.diagram.damage_inspection_notes": "Damage Inspection Notes",
      "cm.diagram.notes_placeholder": "Describe any damage, scratches, dents, or issues observed on the vehicle...",
      "cm.diagram.locations_marked": "{{count}} damage location(s) marked on diagram",
      "cm.appt.detail.jobCard": "Job Card",
      "cm.appt.checkin.msg": "Check in {{name}}?",
      "cm.app.accessDenied": "Access Denied",
      "cm.errorBoundary.title": "Oops! Something went wrong",
      "cm.errorBoundary.message": "We're sorry for the inconvenience. The error has been reported to our team.",
      "cm.errorBoundary.reload": "Reload Page",
      "cm.errorBoundary.updating": "Updating to the latest version…",
      "cm.update.available": "A new version is available.",
      "cm.update.refresh": "Refresh",
      "iv.failedToLoadData": "Failed to load data",
      "iv.failedToLoadJobCardDetails": "Failed to load job card details",
      "iv.pleaseSelectCustomer": "Please select a customer",
      "iv.pleaseAddLineItem": "Please add at least one line item",
      "iv.lineNeedsPrice": "Line {{n}}: enter a price",
      "qt.lineNegativePrice": "Line {{n}}: a price cannot be less than zero",
      "qt.lineNeedsQuantity": "Line {{n}}: quantity must be greater than zero",
      "iv.lineNeedsQuantity": "Line {{n}}: quantity must be greater than zero",
      "iv.invoiceTotalMustBePositive": "An invoice must be for more than zero. Price at least one line.",
      "iv.selectCustomerFirst": "Select a customer first",
      "iv.invoiceCreatedSuccessfully": "Invoice created successfully",
      "iv.failedToCreateInvoice": "Failed to create invoice",
      "iv.loadingCustomer": "Loading Customer...",
      "iv.loadingVehicle": "Loading Vehicle...",
      "iv.noVehicleSelected": "No Vehicle Selected",
      "iv.vehicleOptional": "Vehicle (Optional)",
      "iv.zatcaInvoiceType": "ZATCA Invoice Type",
      "iv.zatcaB2C": "B2C - Business to Consumer (Simplified)",
      "iv.zatcaB2B": "B2B - Business to Business (Full Details)",
      "vat.includeVat": "Price includes VAT",
      "manual.nav": "User Manual",
      "manual.title": "User Manual",
      "manual.subtitle": "Step-by-step guide to everything in MotorMind",
      "manual.searchPlaceholder": "Search the manual…",
      "manual.askTitle": "Ask AI",
      "manual.askSubtitle": "Ask how to do anything — the answer comes from this manual.",
      "manual.askPlaceholder": "e.g. How do I send a quotation to the customer?",
      "manual.askButton": "Ask",
      "manual.answerFrom": "From",
      "manual.noResults": "No sections match your search.",
      "manual.loadFailed": "Failed to load the manual",
      "manual.askFailed": "The assistant could not answer — try again",
      "manual.quotaError": "Your company reached its monthly AI quota.",
      "jc.branch": "Branch",
      "manual.helpTitle": "Page help",
      "manual.poweredBy": "Powered by",
      "manual.helpAskPlaceholder": "Ask about this page…",
      "manual.openManual": "Open the full user manual",
      "manual.contactSupport": "Talk to support",
      "manual.supportUnavailable": "Support chat is not responding right now. Please try again shortly.",
      "manual.feedbackAsk": "Was this helpful?",
      "manual.feedbackUp": "Helpful",
      "manual.feedbackDown": "Not helpful",
      "manual.feedbackThanks": "Thanks for the feedback!",
      "mi.nav": "Manual insights",
      "mi.title": "Manual Q&A insights",
      "mi.subtitle": "What tenants ask the manual AI — and where it fails",
      "mi.total": "Total questions",
      "mi.noMatch": "No manual match",
      "mi.down": "Thumbs down",
      "mi.up": "Thumbs up",
      "mi.filterAll": "All questions",
      "mi.filterNoMatch": "No match only",
      "mi.filterDown": "Thumbs-down only",
      "mi.question": "Question",
      "mi.company": "Company",
      "mi.sections": "Matched sections",
      "mi.signal": "Signal",
      "mi.date": "Date",
      "mi.fromPage": "From page",
      "mi.noMatchBadge": "no match",
      "mi.downBadge": "bad answer",
      "mi.empty": "No questions yet.",
      "mi.loadFailed": "Failed to load insights",
      "svc.billingOnly": "Staff/labor cost — billing only",
      "svc.billingOnlyHint": "No workshop action needed: hidden from the technician's task view and completes automatically with the task. Still priced on the quotation and invoice.",
      "svc.billingOnlyBadge": "Billing only",
      "svc.markDone": "Complete",
      "svc.markedDone": "Service marked completed",
      "pt.markInstalled": "Installed",
      "pt.markedInstalled": "Part marked installed",
      "jobCardDetailsUpdateFailed": "Update failed",
      "vat.inclusiveHint": "= {{net}} + {{vat}} VAT ({{rate}}%)",
      "iv.autoDetected": "Auto-detected",
      "iv.zatcaAutoB2B": "Customer has VAT number (B2B)",
      "iv.zatcaAutoB2C": "Regular consumer (B2C)",
      "iv.zatcaAutoB2BCompany": "Standard invoice for a business customer",
      "iv.zatcaAutoDetectHint": "Type will be auto-detected based on customer. B2B if customer has VAT number, otherwise B2C.",
      "iv.sendInvoiceLinkSMS": "Send invoice link to customer via SMS",
      "iv.sendInvoiceLinkSMSOn": "Customer will receive an SMS with a link to view and pay this invoice.",
      "iv.sendInvoiceLinkSMSOff": "Invoice will be created with a public link but no SMS will be sent.",
      "iv.hidePaidAndDue": "Hide \"Amount Paid\" and \"Amount Due\" on this invoice",
      "iv.hidePaidAndDueHint": "Only those two lines are removed. Subtotal, discount, VAT and the total still print as usual.",
      "iv.delayFeeTag": "DELAY FEE",
      "iv.dontSendOnCreate": "Do not send this invoice to the customer now",
      "iv.dontSendOnCreateHint": "The invoice is still created with its link — send it any time from the invoice list.",
      "iv.invoiceNoPublicLink": "This invoice has no public link",
      "iv.smsSentRemaining": "SMS sent successfully ({{count}} resends remaining today)",
      "iv.resendDuplicate": "Not sent — the customer already has this exact message, so the gateway suppressed the repeat. It will send again once something on the invoice changes, such as a payment.",
      "iv.failedToSendSMS": "Failed to send SMS",
      "iv.viewPublicLink": "View Public Link",
      "iv.resendInvoiceLinkSMS": "Resend invoice link via SMS",
      "iv.downloadPDF": "Download PDF",
      "invoice.status_draft": "Draft",
      "invoice.status_posted": "Issued",
      "invoice.status_pending_clearance": "Awaiting ZATCA clearance",
      "invoice.status_void": "Void",
      "invoice.not_issued_yet": "not issued yet",
      "invoice.reference_label": "Reference",
      "invoice.payref_label": "Payment reference",
      "invoice.draft_notice": "Invoice not issued yet — it will be issued (with QR) once payment is received or the car is delivered.",
      "invoice.draft_public_notice": "Your invoice is not finalized yet. The official tax invoice (with QR code) will be issued once payment is received or your car is delivered.",
      "invoice.amount_due": "Amount due",
      "invoice.creditNotes.title": "Credit notes (ZATCA 381)",
      "invoice.creditNotes.amount": "Amount",
      "invoice.creditNotes.reported": "Reported to ZATCA",
      "invoice.creditNotes.type.partial": "Partial",
      "invoice.creditNotes.type.full": "Full",
      "creditNote.issue": "Issue credit note",
      "creditNote.title": "Issue credit note",
      "creditNote.issued": "Credit note {{number}} issued",
      "creditNote.gatewayRefundFailed": "The card refund failed — retry it from the payment",
      "creditNote.back": "Back",
      "creditNote.confirmIssue": "Yes, issue credit note",
      "creditNote.confirmWarning": "This credit note cannot be undone and is reported to ZATCA.",
      "creditNote.total": "Credit total",
      "creditNote.full": "Full invoice",
      "creditNote.chooseLines": "Choose lines",
      "creditNote.line": "Line",
      "creditNote.invoicedQty": "Invoiced qty",
      "creditNote.alreadyCredited": "Already credited",
      "creditNote.qtyToCredit": "Qty to credit",
      "creditNote.returnToStock": "Return to stock",
      "creditNote.net": "Net",
      "creditNote.vat": "VAT",
      "creditNote.ofWhichOwed": "Of which unpaid",
      "creditNote.ofWhichPaid": "Of which already paid",
      "creditNote.paidMoney": "The already-paid amount",
      "creditNote.refundNow": "Refund now",
      "creditNote.refundMethod": "Refund method",
      "creditNote.method.cash": "Cash",
      "creditNote.method.bank_transfer": "Bank transfer",
      "creditNote.method.card": "Card (online payment refund)",
      "creditNote.keepAsCredit": "Keep as customer credit",
      "creditNote.reason": "Reason",
      "creditNote.status.pending_clearance": "Awaiting ZATCA clearance",
      "creditNote.status.rejected": "Rejected by ZATCA",
      "creditNote.disposition.refund": "Refunded",
      "creditNote.disposition.customer_credit": "Kept as customer credit",
      "creditNote.badge.credited": "Credited",
      "creditNote.badge.partlyCredited": "Partly credited",
      "creditNote.outcome.card_unknown": "Card refund status unknown — being checked",
      "creditNote.clearanceRefundAsCredit": "B2B invoice: a cash or bank refund is kept as customer credit when ZATCA clears the credit note. A cashier pays it out from the customer page.",
      "gl.close.refusal.GL_CLOSE_PENDING_CREDIT_NOTE": "Credit note {{doc_ref}} is awaiting ZATCA clearance. Close this month after it clears or is rejected.",
      "creditNote.outcome.refunded": "Refunded",
      "creditNote.outcome.customer_credit": "Kept as customer credit",
      "creditNote.outcome.card_pending": "Card refund in progress",
      "creditNote.cardRefundCompletedByProvider": "The payment provider has completed this card refund. Nothing else is needed.",
      "creditNote.cardRefundedConfirmWarning": "Choose this only if the money was already refunded to the card (for example from the provider dashboard). This cannot be undone.",
      "creditNote.cardKeepAsCreditWarning": "The amount becomes customer credit and will not be refunded to the card. This cannot be undone.",
      "creditNote.gatewayRef": "Gateway reference (optional)",
      "creditNote.resolutionNote": "Note (optional)",
      "creditNote.cardResolveConfirm": "Confirm",
      "creditNote.outcome.card_needs_check": "Card refund needs checking",
      "creditNote.cardNeedsCheckToast": "The card refund needs checking. Resolve it from the invoice page.",
      "creditNote.cardRefundedConfirm": "Refunded on card",
      "creditNote.cardKeepAsCredit": "Keep as customer credit",
      "creditNote.cardRefundedConfirmed": "Card refund confirmed",
      "creditNote.cardKeptAsCredit": "Kept as customer credit",
      "creditNote.cardNeedsCheckNoLonger": "This credit note is no longer waiting on a staff decision.",
      "creditNote.outcome.card_refunded": "Refunded to the card",
      "creditNote.outcome.card_failed_credit": "Card refund failed — kept as customer credit",
      "creditNote.cardRefundFailedCredit": "The card refund failed. The amount is now customer credit — you can pay it out from the customer page.",
      "creditNote.clearanceNote": "This is a B2B invoice: the money and the stock move only when ZATCA clears the credit note.",
      "creditNote.notReturnable": "Nothing of this part left stock on this invoice",
      "creditNote.returnableShort": "Only {{qty}} can go back to stock",
      "invoice.taxable_amount": "Taxable Amount",
      "invoice.grand_total": "Grand Total",
      "invoice.amount_paid": "Amount Paid",
      "invoice.icv_label": "ICV",
      "invoice.posted_at_label": "Issued on",
      "invoice.official_number_label": "Invoice number",
      "invoice.payment_recorded_delivered": "Payment recorded — invoice sent and delivery code issued",
      "invoice.payment_recorded_partial": "Partial payment recorded",
      "iv.sendInvoiceViaSMS": "Send Invoice via SMS",
      "iv.confirmBeforeSending": "Confirm before sending",
      "iv.sendSMS": "Send SMS",
      "iv.sending": "Sending...",
      "iv.smsLinkDownloadPDF": "The customer will receive an SMS with a link to download their invoice PDF.",
      "iv.smsLinkViewAndPay": "The customer will receive an SMS with a link to view and pay this invoice.",
      "iv.fillAllItemFields": "Please fill all item fields",
      "iv.itemRemoved": "Item removed",
      "iv.invoicePreview": "Invoice Preview",
      "iv.confirmGenerateInvoice": "Confirm & Generate Invoice",
      "iv.action": "Action",
      "iv.downpaymentCredit": "Downpayment Credit",
      "iv.downpaymentCreditReceipt": "Downpayment (Receipt {{ref}}) — deducted",
      "iv.paidUpper": "PAID",
      // Task 5.2 (payment-receipt redesign): job-card receipt timeline.
      "rc.timelineTitle": "Payment Receipts",
      "rc.availableCredit": "Available advance credit",
      "rc.kind.downpayment": "Downpayment",
      "rc.kind.payment": "Payment",
      "rc.kind.refund": "Refund",
      "rc.status.draft": "Draft",
      "rc.status.issued": "Issued",
      "rc.status.paid": "Paid",
      "rc.status.voided": "Voided",
      "rc.status.refunded": "Refunded",
      "rc.status.partially_refunded": "Partially refunded",
      "rc.advanceBreakdown": "Available {{available}} · Reserved {{reserved}} · Consumed {{consumed}}",
      "rc.recordPayment": "Record payment",
      "rc.recordPaySuccess": "Receipt {{number}} paid",
      "rc.recordPayFail": "Failed to record payment",
      "rc.openCheckoutWarning": "This receipt has an open online payment link. Please confirm the customer has NOT already paid online before recording this payment.",
      "rc.openCheckoutConfirm": "Customer has not paid online — proceed",
      // Task (payment-receipt frontend): Payment Receipts page + create-manual
      // modal + invoice-against-receipt UX (nav item, list filters, due-math
      // prompts on CreateInvoice).
      "rc.navTitle": "Receipts & Payment Vouchers",
      "rc.pageSubtitle": "Payment receipts (سند قبض) for money in and payment vouchers (سند صرف) for money out — each numbered its own way",
      "rc.family.rc": "Payment Receipt",
      "rc.family.pv": "Payment Voucher",
      "rc.newReceipt": "New receipt",
      "rc.newDisbursement": "New disbursement",
      "rc.kind.disbursement": "Disbursement",
      "rc.pvHint": "Money paid out that is not a customer refund, a supplier invoice or a recorded expense — for example rent, a courier or a staff advance.",
      "rc.pvPayee": "Paid to",
      "rc.pvPayeePhone": "Payee mobile",
      "rc.pvPayeePick": "Select a partner",
      "rc.pvPayeePartnerHint": "This reason is owner or partner money, so the name must match a partner registered on the Partners screen exactly.",
      "rc.pvReason": "Reason",
      "rc.pvReasonPick": "Select a reason…",
      "rc.pvNoReasons": "No outbound reasons have been set up yet. Add them on the cash movements screen first — a disbursement must state why the money is leaving.",
      "rc.pvReasonsForbidden": "Cash movements are not enabled on your plan, so disbursement reasons cannot be loaded. Contact your administrator.",
      "rc.pvValidation": "Enter who was paid, the reason, and an amount greater than zero.",
      "rc.pvCreateSuccess": "Disbursement voucher {{number}} issued",
      "rc.pvCreateFail": "The disbursement voucher could not be issued",
      "rc.receiptNumber": "Receipt #",
      "rc.kindLabel": "Kind",
      "rc.allKinds": "All Kinds",
      "rc.searchCustomer": "Search customer",
      "rc.linkedTo": "Linked to",
      "rc.noReceipts": "No payment receipts",
      "rc.noReceiptsDescription": "Payment receipts issued to customers will appear here.",
      "rc.noReceiptsFiltered": "No receipts match the current filters.",
      "rc.loadFail": "Failed to load payment receipts",
      "rc.createValidation": "Select a customer and enter an amount greater than 0",
      "rc.createSuccess": "Receipt {{number}} issued",
      "rc.createFail": "Failed to issue receipt",
      "rc.applyReceipt": "Apply payment receipt",
      "rc.noReceiptApplied": "No receipt — manual entry",
      // Customer-credit payout/reuse feature: pooled-freed receipts (from a
      // dead job card/quotation) show WHERE the credit came from in the picker.
      "rc.originJobCard": "from job {{number}}",
      "rc.originQuotation": "from quotation {{number}}",
      // Record-payment-via-existing-receipt (invoice + quotation surfaces).
      "rc.paymentSourceDirect": "Direct payment",
      "rc.paymentSourceReceipt": "Use customer receipt",
      "rc.selectReceiptPlaceholder": "Select a receipt",
      "rc.noEligibleReceipts": "No eligible receipts with available credit for this customer",
      // Multi-receipt funding (Task 5): checkbox multi-pickers + linked-receipts panel.
      "rc.selectedCredit": "Selected credit: {{amount}}",
      "rc.linkedReceipts": "Linked receipts",
      "rc.contributed": "Applied to this invoice",
      "rc.payWithExistingReceipt": "Pay with existing receipt",
      "rc.payWithExistingReceiptSuccess": "Quotation paid from the existing receipt",
      "rc.payWithExistingReceiptFail": "Failed to pay with the existing receipt",
      // Item 5 (portal UI reachability for the Item 6 partial-installment
      // bind rule): a receipt smaller than what's remaining now shows up in
      // the picker too — this line makes the partial contribution explicit.
      "rc.willApplyPartial": "This will apply {{applied}} now — {{after}} will remain",
      // Task 3 (quotation multi-downpayment UI): progress line above the
      // quotation's downpayment receipts list.
      "quo.dp.progress": "Downpayment: paid {{paid}} of {{required}} — remaining {{remaining}}",
      // Finding 3b (adversarial review — end-to-end installment journey):
      // staff-initiated "issue a new installment receipt for an arbitrary
      // amount ≤ remaining" modal on the quotation's receipts card.
      "quo.dp.issueInstallment": "Issue installment receipt",
      "quo.dp.installmentAmountLabel": "Amount (remaining {{remaining}})",
      "quo.dp.issueInstallmentSuccess": "Installment receipt issued",
      "quo.dp.issueInstallmentFail": "Failed to issue installment receipt",
      "quo.dp.installmentAmountInvalid": "Enter an amount greater than 0 and up to {{remaining}}",
      "rc.sendLinkPrompt": "The invoice total is {{amount}} more than the applied receipt. Send a payment link to the customer for the remaining amount?",
      "rc.sendLinkConfirm": "Send payment link",
      "rc.sendLinkSkip": "Not now",
      "rc.sendLinkSuccess": "Payment link sent to the customer",
      "rc.sendLinkFail": "Failed to send payment link",
      "rc.creditWarning": "This receipt exceeds the invoice total by {{amount}}. The difference will be held as credit due to the customer.",
      "rc.creditWarningConfirm": "Proceed",
      "rc.creditAction.title": "Customer credit available",
      "rc.creditAction.excessLine": "This payment covers {{amount}} SAR more than the invoice total. Choose what happens to the difference.",
      "rc.creditAction.addToAccount": "Add to customer account",
      "rc.creditAction.addToAccountHint": "Keep the difference as available credit for the customer's next invoice.",
      "rc.creditAction.payback": "Pay back to customer",
      "rc.creditAction.paybackHint": "Refund the difference to the customer now.",
      "rc.creditAction.methodLabel": "Refund method",
      "rc.creditAction.methodRequired": "Select a refund method",
      "rc.customersWithCreditTitle": "Customers with available credit",
      "rc.actions": "Actions",
      "rc.viewPdf": "View PDF",
      "rc.viewPdfFail": "Failed to open receipt PDF",
      "rc.delete": "Delete receipt",
      "rc.deleteConfirmTitle": "Delete this receipt?",
      "rc.deleteConfirmMessage": "Receipt {{number}} will be cancelled and the money reversed. This cannot be undone.",
      "rc.deleteSuccess": "Receipt deleted",
      "rc.deleteFail": "Failed to delete receipt",
      "iv.sendInvoiceVia": "Send Invoice Via:",
      "iv.billedToInsurer": "Billed to insurer (B2B)",
      "iv.insurerBillingMissing": "The insurer is missing VAT number, CR number or address. Complete it under Insurance Companies before invoicing.",
      "iv.bothLanguages": "Both Languages",
      "iv.discountPercentage": "Percentage (%)",
      "iv.discountFixedAmount": "Fixed Amount (SAR)",
      "iv.subtotalAfterDiscount": "Subtotal After Discount",
      "iv.refundDue": "Refund Due",
      "iv.finalTotal": "Final Total",
      "jc.issueTypeOther": "Other",
      "jc.issueDescriptionPlaceholder": "Describe the issue in detail...",
      "jc.rejectPartRequest": "Reject Part Request",
      "jc.rejectRequest": "Reject Request",
      "jc.rejectPartRequestHint": "Please provide a reason for rejecting this part request. This will be sent to the technician.",
      "jc.reasonForRejection": "Reason for Rejection",
      "jc.rejectionReasonPlaceholder": "e.g., Out of stock, Not required",
      "jc.selectViewMode": "Select View Mode",
      "jc.selectViewModeDesc": "Choose how you want to access the system",
      "jc.adminViewDesc": "Full management access",
      "jc.staffViewDesc": "Task-focused interface",
      "jc.deliveryCodeLength": "Delivery code must be 6 characters",
      "jc.vehicleDeliveredSuccess": "Vehicle delivered successfully!",
      "jc.invalidDeliveryCode": "Invalid delivery code",
      "jc.verifyDeliveryCode": "Verify Delivery Code",
      "jc.verifyAndDeliver": "Verify & Deliver",
      "jc.deliveryCodePrompt": "Enter the 6-character delivery code provided by the customer",
      "jc.deliveryCode": "Delivery Code",
      "jc.overrideCode": "Override code",
      "jc.overrideReasonLabel": "Justification",
      "jc.overrideReasonRequired": "Justification is required to override the delivery code",
      "jc.overrideConfirm": "This bypass will be audited on the job card.",
      "jc.closeReasonRequired": "Please provide a reason for closing this job card",
      "jc.jobCardClosedSuccess": "Job card closed successfully",
      "jc.failedToCloseJobCard": "Failed to close job card",
      "jc.closing": "Closing...",
      "jc.closeJobCardWarning": "You are about to close this job card. This action will mark the job as closed and stop all further work.",
      "jc.reasonForClosing": "Reason for Closing",
      "jc.closeReasonHint": "This reason will be recorded in the job card history",
      "jc.closeReasonPlaceholder": "e.g., Customer cancelled the service, Vehicle not repairable, Customer not responding...",
      "jc.reopenJobCard": "Re-open",
      "jc.hero.more": "More",
      "jc.lens.managerActions": "Manager actions",
      "jc.hero.photo": "Photo",
      "jc.hero.photoAdded": "Photo added",
      "jc.hero.photoFailed": "Failed to add photo",
      "jc.cta.completeInfo": "Complete Info",
      "jc.cta.generateQuotation": "Create Quote",
      "jc.cta.resend": "Resend",
      "jc.cta.deliver": "Deliver",
      "jc.cta.runQualityCheck": "Run quality check",
      "qt.listSubtitle": "{{count}} quotations this month · {{awaiting}} awaiting customer",
      "qt.downpaymentDue": "Downpayment due:",
      "qt.downpaymentRemaining": "Downpayment remaining",
      "qt.lockedByJobCard": "Disabled — can't be combined with a job card",
      "qt.lockedByReceipt": "Disabled — can't be combined with a payment receipt",
      "qt.createSendHint": "The payment-method picker opens before sending · sent by SMS or WhatsApp",
      "iv.lifecycleAxis": "Lifecycle",
      "iv.zatcaCardTitle": "E-invoicing — ZATCA",
      "iv.zatcaQrCompliant": "ZATCA-compliant QR code",
      "rc.band.issuedUnpaid": "Issued · unpaid",
      "rc.kind.standaloneCredit": "Standalone — credit",
      "tp.collectedTotal": "Collected",
      "tp.refundsPending": "Refunds awaiting approval",
      "cm.customers.owingLead": "With outstanding balance",
      "cm.customers.creditLead": "Hold credit",
      "credit.customerCreditBalanceEmptyHint": "No credit available — any prepaid amount not yet consumed appears here, with a payout action for staff who can issue refunds.",
      "pt.locked": "Locked",
      "jc.cta.sendLink": "Send Link",
      "jc.cta.addTask": "Add Task",
      "jc.cta.addService": "Add Service",
      "jc.timeline.downpayment": "Downpayment",
      "jc.timeline.paymentOutstanding": "Payment outstanding",
      "jc.timeline.done": "Done",
      "jc.paymentMethodPicker.title": "Payment methods for this link",
      "jc.transfer.action": "Transfer branch",
      "jc.transfer.currentBranch": "Current branch",
      "jc.transfer.title": "Transfer to another branch",
      "jc.transfer.desc": "Move this job card to another branch so the car is serviced and completed there. The vehicle and its history stay intact.",
      "jc.transfer.destination": "Destination branch",
      "jc.transfer.select": "Select a branch…",
      "jc.transfer.confirm": "Transfer",
      "jc.transfer.success": "Job card transferred",
      "jc.transfer.failed": "Transfer failed",
      "jc.transfer.loadFailed": "Failed to load branches",
      "jc.transfer.noOther": "No other branches available.",
      "parking.title": "Car Parking Area",
      "parking.current": "Current location",
      "parking.none": "No parking location recorded yet.",
      "parking.update": "Update parking",
      "parking.areaLabel": "Parking spot",
      "parking.areaPlaceholder": "e.g. Bay 4, back lot row C",
      "parking.takePhoto": "Take / choose photo",
      "parking.captureGps": "Capture GPS location",
      "parking.gpsCaptured": "Location captured",
      "parking.gpsFailed": "Could not get location",
      "parking.viewMap": "View on map",
      "parking.save": "Save",
      "parking.saved": "Parking location saved",
      "parking.failed": "Failed to save",
      "parking.areaRequired": "Enter the parking spot",
      "parking.history": "Change history",
      "jc.reopenJobCardTitle": "Re-open Job Card",
      "jc.reopenReasonLabel": "Reason for Re-opening",
      "jc.reopenReasonHint": "This reason will be recorded in the job card history",
      "jc.reopenReasonPlaceholder": "Explain why this job card needs to be re-opened...",
      "jc.reopenReasonTypeLabel": "Reason Type",
      "jc.reopenReasonTypeWarranty": "Warranty",
      "jc.reopenReasonTypeChargeable": "Chargeable",
      "jc.reopenIntakeModeLabel": "Intake Mode",
      "jc.reopenIntakeModeDropOff": "Drop-off",
      "jc.reopenIntakeModeWait": "Wait",
      "jc.reopenConfirmationNote": "A customer confirmation SMS/OTP will be sent for this new drop-off damage map.",
      "jc.reopenedBadge": "Re-opened",
      "jc.reopenedBadgeWithCount": "Re-opened ({{count}})",
      "jc.reopenReasonRequired": "Please provide a reason for re-opening this job card",
      "jc.reopenReasonTypeRequired": "Please select a reason type",
      "jc.reopenIntakeModeRequired": "Please select an intake mode",
      "jc.reopenDamageMapRequired": "Please add at least one damage marker for drop-off intake",
      "jc.jobCardReopenedSuccess": "Job card re-opened successfully",
      "jc.failedToReopenJobCard": "Failed to re-open job card",
      "jc.reopening": "Re-opening...",
      // Return-car-no-agreement (2026-07-31): CancelReturnCarModal.js + the
      // hero action/badge in JobCardDetails.js. cancellation_type ===
      // 'customer_declined_price' drives the badge.
      "jc.returnCar.action": "Return the Car & Close Job Card",
      "jc.returnCar.badge": "Car returned",
      "jc.returnCar.modalTitle": "Return the Car & Close Job Card",
      "jc.returnCar.warningIntro": "This will close the job card and end every pending action on it. This cannot be undone from here.",
      "jc.returnCar.fundLotsTitle": "Money to resolve",
      "jc.returnCar.noFundLots": "No money has been collected on this job card.",
      "jc.returnCar.originOnline": "Paid online",
      "jc.returnCar.originCounter": "Paid at the garage",
      "jc.returnCar.methodUnknown": "Payment method unknown",
      "jc.returnCar.reservedAgainstPosted": "Currently applied to a posted invoice — will require a credit note",
      "jc.returnCar.receiptLabel": "Receipt",
      "jc.returnCar.advanceLabel": "Customer advance",
      "jc.returnCar.allocationsLabel": "Applied to invoice",
      "jc.returnCar.gatewayPaymentsLabel": "Gateway payment attempts (for reference only)",
      "jc.returnCar.refundsLabel": "Prior refunds",
      "jc.returnCar.dispositionLabel": "What happens to this money?",
      "jc.returnCar.dispositionAddToAccount": "Add to customer's account",
      "jc.returnCar.dispositionAddToAccountHint": "Stays as credit the customer can use next time.",
      "jc.returnCar.dispositionPayback": "Pay the customer back",
      "jc.returnCar.dispositionPaybackHint": "Paid in cash or bank transfer from the garage's own account — not refunded through the original payment gateway.",
      "jc.returnCar.payoutMethodLabel": "Payout method",
      "jc.returnCar.payoutMethodPlaceholder": "Choose a payout method…",
      "jc.returnCar.pendingActionsTitle": "Will also be closed",
      "jc.returnCar.noPendingActions": "No other pending items.",
      "jc.returnCar.pendingQuotations": "Live quotations",
      "jc.returnCar.pendingConfirmations": "Pending confirmations",
      "jc.returnCar.pendingPartApprovals": "Pending part-approval requests",
      "jc.returnCar.pendingServices": "Open services",
      "jc.returnCar.pendingSubTasks": "Open sub-tasks",
      "jc.returnCar.invoicesTitle": "Invoices affected",
      "jc.returnCar.invoicesDraftNote": "{{count}} draft invoice(s) will be deleted.",
      "jc.returnCar.invoicesPostedNote": "{{count}} posted invoice(s) require a credit note.",
      "jc.returnCar.reasonLabel": "Reason",
      "jc.returnCar.reasonPlaceholder": "e.g., customer felt the price was too high and declined to proceed...",
      "jc.returnCar.confirmButton": "Confirm Cancellation",
      "jc.returnCar.confirming": "Cancelling...",
      "jc.returnCar.previewFailed": "Failed to load the cancellation summary",
      "jc.returnCar.retry": "Retry",
      "jc.returnCar.installedPartsTitle": "This flow can't be used",
      "jc.returnCar.installedPartsBlocked": "This job card has installed parts, so this flow can't be used.",
      "jc.returnCar.artifactsChanged": "The money on this job card changed since this screen opened. Review the refreshed summary and confirm again.",
      "jc.returnCar.confirmFailed": "Failed to cancel the job card",
      "jc.returnCar.confirmSuccessTitle": "Job card cancelled",
      "jc.returnCar.pickupCodeIntro": "A pickup code has been sent to the customer by SMS.",
      "jc.returnCar.pickupCodeSmsNote": "Ask the customer for the code when they collect the car, then enter it under \"Verify pickup code\" on the job card. The code is not shown here — it is what proves the person collecting the car is the customer.",
      "jc.returnCar.awaitingPickupTitle": "Awaiting collection",
      "jc.returnCar.awaitingPickupBody": "The job card is closed. The customer has the pickup code by SMS — enter it when they arrive to record the handover.",
      "jc.returnCar.verifyPickupCode": "Verify pickup code",
      "jc.returnCar.done": "Done",
      // Blocking issues (findBlockingLotIssues, jobCardCancellationService.js)
      // — money this flow can never safely dispose of. Non-empty ⇒ the whole
      // form locks (see hasBlockingIssues in CancelReturnCarModal.js).
      "jc.returnCar.blockersTitle": "This job card can't be returned through this flow",
      "jc.returnCar.blockersIntro": "Resolve every item below, then reopen this screen and try again.",
      "jc.returnCar.blocker.RECEIPT_NOT_CUSTOMER_MONEY": "Receipt {{ref}} was paid by the insurer, not the customer — settle it with the insurer separately before returning the car.",
      "jc.returnCar.blocker.RECEIPT_NOT_REFUNDABLE": "Receipt {{ref}} can't be paid back or credited by this flow — settle it manually first.",
      "jc.returnCar.blocker.RECEIPT_PARTLY_CONSUMED": "Part of receipt {{ref}} is already applied to a posted invoice — reverse that invoice with a credit note first.",
      "jc.returnCar.blocker.RECEIPT_RESERVED_ELSEWHERE": "Receipt {{ref}} still has money reserved against another invoice — release that reservation first.",
      "jc.returnCar.blocker.RECEIPT_LEDGER_INCONSISTENT": "Receipt {{ref}}'s numbers don't add up — ask accounting to reconcile it before returning the car.",
      "jc.returnCar.blocker.INVOICE_PARTLY_PAID": "Invoice {{ref}} has already been partly paid — reverse it manually before returning the car.",
      "jc.returnCar.blocker.INVOICE_PAYMENT_UNSETTLED": "A completed payment is recorded against invoice {{ref}}, but the invoice still shows nothing paid — reconcile that payment before returning the car.",
      "jc.returnCar.blocker.LOTS_NOT_DISPOSABLE": "This job card holds money this flow can't return safely — see the blockers above.",
      "jc.fileSizeLimit": "File size must be less than 5MB",
      "jc.manualApprovalReasonRequired": "Please provide a reason for manual approval",
      "jc.quotationApprovedSuccess": "Quotation manually approved successfully",
      "jc.quotationApprovedReceiptSuccess": "Quotation approved — payment receipt issued and sent to the customer",
      "jc.failedToApproveQuotation": "Failed to approve quotation",
      "jc.manualApproval": "Manual Approval",
      "jc.approving": "Approving...",
      "jc.approveQuotation": "Approve Quotation",
      "jc.manualApprovalWarning": "You are about to manually approve this quotation. Please provide a reason and any supporting evidence.",
      "jc.reasonForManualApproval": "Reason for Manual Approval",
      "jc.manualApprovalReasonPlaceholder": "e.g., Customer approved verbally over phone call...",
      "jc.evidenceOptional": "Evidence (Optional)",
      "jc.clickToUploadFile": "Click to upload file (Max 5MB)",
      "jc.allowedFileTypes": "Images, PDF, or Word documents",
      "jc.removeFile": "Remove file",
      "jc.addDescriptionFirst": "Please add a description first",
      "jc.identifiedDuringDiagnosis": "Identified during diagnosis: {{description}}",
      "jc.taskAddedToList": "Task added to creation list",
      "jc.addAtLeastOneFinding": "Please add at least one finding",
      "jc.vehicleDiagnosis": "Vehicle Diagnosis",
      "jc.completeDiagnosis": "Complete Diagnosis",
      "jc.overallDiagnosisNotes": "Overall Diagnosis Notes",
      "jc.diagnosisSummaryPlaceholder": "Enter overall diagnosis summary...",
      "jc.findingsAndIssues": "Findings & Issues",
      "jc.addFinding": "Add Finding",
      "jc.lowPriority": "Low Priority",
      "jc.mediumPriority": "Medium Priority",
      "jc.highPriority": "High Priority",
      "jc.findingPlaceholder": "Describe the finding or issue...",
      "jc.createTaskFromFinding": "+ Create task from this finding",
      "jc.tasksToCreate": "Tasks to Create ({{count}})",
      "jc.priorityLabel": "Priority: {{priority}}",
      "jc.payerType": "Payer",
      "jc.payerCustomer": "Customer",
      "jc.payerInsurance": "Insurance",
      "jc.insurer": "Insurer",
      "jc.selectInsurer": "Select insurer",
      "jc.selectInsurerRequired": "Please select an insurer",
      "jc.noInsurers": "No active insurers configured",
      "jc.photosUploaded": "{{count}} photo(s) uploaded",
      "jc.failedToUploadPhotos": "Failed to upload photos",
      "jc.qcPassedToast": "Quality check passed",
      "jc.qcFailedToast": "Quality check failed - Task reopened for rework",
      "jc.qcRework.notice": "Quality check failed — redo the work, then mark the rework done.",
      "jc.qcRework.done": "Rework done",
      "jc.failedToSubmitQc": "Failed to submit quality check",
      "jc.submitPass": "Submit Pass",
      "jc.submitFail": "Submit Fail",
      "jc.qcResult": "Quality Check Result",
      "jc.qcFailNotesPlaceholder": "Describe what needs to be fixed...",
      "jc.qcPassNotesPlaceholder": "Add any observations or comments...",
      "jc.photosOptional": "Photos (Optional)",
      "jc.qcPhotoAlt": "QC photo {{number}}",
      "jc.addPhotos": "Add Photos",
      "jc.failedToLoadUsers": "Failed to load users",
      "jc.failedToLoadAssignments": "Failed to load assigned users",
      "jc.userAssignedSuccess": "User assigned successfully",
      "jc.failedToAssignUser": "Failed to assign user",
      "jc.userRemovedSuccess": "User removed successfully",
      "jc.failedToRemoveUser": "Failed to remove user",
      "jc.currentlyAssigned": "Currently Assigned ({{count}})",
      "jc.searchUsersToAdd": "Search users to add...",
      "jc.allUsersAssigned": "All users are assigned",
      "jc.noUsersFound": "No users found",
      "jc.failedToLoadComments": "Failed to load comments",
      "jc.failedToAddComment": "Failed to add comment",
      "jc.commentTypeDiagnosis": "Diagnosis",
      "jc.commentTypeStatusUpdate": "Status Update",
      "jc.commentTypeComment": "Comment",
      "jc.justNow": "Just now",
      "jc.minutesAgo": "{{count}}m ago",
      "jc.hoursAgo": "{{count}}h ago",
      "jc.daysAgo": "{{count}}d ago",
      "jc.conversationAndUpdates": "Conversation & Updates",
      "jc.collaborateWithTeam": "Collaborate with your team on this job card",
      "jc.loadingComments": "Loading comments...",
      "jc.noCommentsYet": "No comments yet",
      "jc.startConversation": "Start the conversation below",
      "jc.commentType": "Comment Type",
      "jc.writeComment": "Write a comment...",
      "jc.sending": "Sending...",
      "jc.issueResolvedSuccess": "Issue marked as resolved",
      "req.navTitle": "Requests",
      "req.subtitle": "Staff problem reports and assistance requests waiting on you",
      "req.kindCol": "Type",
      "req.kind.assistance": "Assistance",
      "req.kind.task": "Task problem",
      "req.kind.service": "Service issue",
      "req.kind.part": "Part issue",
      "req.task": "Task",
      "req.emptyTitle": "Nothing needs your attention",
      "req.emptyDescription": "Open staff problem reports and assistance requests will show up here.",
      "req.resolveModalTitle": "Resolve request",
      "status.open": "Open",
      "jc.issueResolveFailed": "Failed to resolve issue",
      "jc.andMore": "+{{count}} more",
      "jc.issueEvidenceAlt": "Issue evidence",
      "jc.partApprovedStockDeducted": "Part approved and stock deducted",
      "jc.failedToApprovePart": "Failed to approve part",
      "jc.partRequestRejected": "Part request rejected",
      "jc.failedToRejectPart": "Failed to reject part",
      "jc.pendingPartApprovals": "Pending Part Approvals",
      "jc.reviewApprovePartRequests": "Review and approve part requests from technicians",
      "jc.failedToLoadPartApprovals": "Failed to load pending part approvals",
      "jc.requestedByLabel": "Requested By:",
      "jc.partNumberLabel": "Part Number:",
      "jc.quantityLabel": "Quantity:",
      "jc.unitPriceLabel": "Unit Price:",
      "jc.requestedAtLabel": "Requested At:",
      "jc.currentStockLabel": "Current Stock:",
      "jc.unitsCount": "{{count}} units",
      "jc.approveAndDeductStock": "Approve & Deduct Stock",
      "pa.confirmApproveTitle": "Approve part & deduct stock?",
      "pa.confirmApproveMessage": "This will approve the requested part and deduct it from inventory immediately.",
      "jc.insufficientStockCannotApprove": "Insufficient stock! Cannot approve this request.",
      "jc.failedToLoadJobs": "Failed to load jobs",
      "jc.oneSelected": "1 selected",
      "jc.warningLightsSelectedCount": "{{count}} warning lights selected",
      "jc.clearAll": "Clear All",
      "jc.searchWarningLights": "Search warning lights...",
      "jc.noWarningLightsMatching": "No warning lights found matching \"{{query}}\"",
      "jc.selectedCount": "{{count}} selected",
      "jc.photosAdded": "{{count}} photo(s) added",
      "jc.failedToAddPhotos": "Failed to add photos",
      "jc.interior": "Interior",
      "jc.exterior": "Exterior",
      "jc.photo": "photo",
      "jc.video": "video",
      "jc.mediaAddedSuccess": "{{location}} {{media}} added successfully! ({{count}})",
      "jc.failedToAddMedia": "Failed to add {{media}}",
      "jc.mileagePlaceholder": "e.g., 45000",
      // Odometer sanity check at check-in (owner 2026-07-26) — advisory only.
      "jc.mileageBelowLast": "Lower than the last reading we recorded: {{km}} km. Please double-check the odometer.",
      "jc.mileageLastSeenOn": "Recorded on {{job}}, {{date}}.",
      "jc.mileageLastSeenDate": "Recorded {{date}}.",
      "pt.bulkUploadSuccess": "Successfully processed {{created}} new parts and updated {{updated}} existing parts",
      "pt.bulkUploadErrors": "{{count}} errors occurred during upload",
      "pt.purchaseInvoiceCreated": "Purchase invoice created successfully",
      "pt.purchaseHistory": "Purchase History",
      "pt.rv.held": "held",
      "pt.rv.free": "free",
      "pt.rv.onHand": "On hand",
      "pt.rv.title": "Who's holding this part",
      "pt.rv.document": "Document",
      "pt.rv.heldQty": "Held",
      "pt.rv.state": "State",
      "pt.rv.totalHeld": "Total held",
      "pt.rv.none": "Nothing is holding this part.",
      "pt.rv.kind.job_card": "Job card",
      "pt.rv.kind.quotation": "Quotation",
      "pt.rv.kind.invoice": "Draft invoice",
      "pt.rv.kind.part_request": "Part request",
      "pt.rv.blockedTitle": "This part is spoken for",
      "pt.rv.blockedBody": "{{name}} — {{onHand}} in stock, {{reserved}} held elsewhere, {{available}} free. Ask whoever holds it, or ask a manager to override.",
      "pt.rv.overrideTitle": "Use a part someone else is holding?",
      "pt.rv.overrideBody": "{{name}} — only {{available}} free of {{onHand}} in stock. Taking it may leave the other document short.",
      "pt.rv.overrideConfirm": "Use it anyway",
      "pt.rv.overrideCancel": "Pick something else",
      "pt.rv.availableHeader": "Available",
      "pt.dt.pricing": "Pricing",
      "pt.dt.lastPurchase": "Last purchase",
      "pt.dt.lastRestocked": "Last restocked",
      "pt.sh.title": "Stock History",
      "pt.sh.movement": "Movement",
      "pt.sh.balanceAfter": "Balance after",
      "pt.sh.source": "Source",
      "pt.sh.performedBy": "By",
      "pt.sh.totalIn": "Total received",
      "pt.sh.totalOut": "Total issued",
      "pt.sh.onHand": "On hand now",
      "pt.sh.empty": "No stock movements yet",
      "pt.sh.emptyHint": "Every receipt, issue, transfer and correction for this part will appear here.",
      "pt.sh.failedToLoad": "Could not load this part's stock history.",
      "pt.sh.type.in": "Received",
      "pt.sh.type.out": "Issued",
      "pt.sh.type.return": "Returned",
      "pt.sh.type.adjustment": "Corrected",
      "pt.sh.type.transfer": "Transferred out",
      "pt.sh.type.damage": "Damaged",
      "pt.sh.type.initial": "Opening stock",
      "pt.sh.ref.bulk_upload": "Bulk upload",
      "pt.sh.ref.manual": "Manual entry",
      "pt.sh.ref.manual_adjustment": "Manual correction",
      "pt.sh.ref.stock_transfer": "Branch transfer",
      "pt.uncategorized": "Uncategorized",
      "pt.universal": "Universal",
      "pt.upload": "Upload",
      "pt.purchase": "Purchase",
      "pt.partUpdatedSuccessfully": "Part updated successfully",
      "pt.failedToUpdatePart": "Failed to update part",
      "pt.skuReadOnly": "SKU: {{sku}} (Read-only)",
      "pt.compatibilityPlaceholder": "e.g. Toyota Camry 2020-2025",
      "pt.lowStockThreshold": "Low Stock Threshold",
      "pt.addToPurchase": "Add to purchase",
      "pt.inventoryValue": "Inventory value",
      "pt.itemsWord": "items",
      "pt.descriptionAr": "Description (Arabic)",
      "jc.delayFeeGateUnavailable": "Couldn't check outstanding fees — the vehicle was NOT released. Please try again.",
      "pt.stockLimit": "limit",
      "staff.myWork.workingNow": "Working on now",
      "staff.myWork.continue": "Continue task",
      "staff.myWork.voiceSearch": "Voice search",
      "pt.lowStockThresholdHint": "Alert when stock falls below this number",
      "pt.partDescriptionPlaceholder": "Part description...",
      "pt.confirmRemovePart": "Are you sure you want to remove this part?",
      "pt.partRemoved": "Part removed",
      "pt.failedToRemovePart": "Failed to remove part",
      "pt.listedPart": "Listed Part",
      "pt.manualPart": "Manual Part",
      "pt.selectPart": "Select Part",
      "pt.choosePart": "Choose a part...",
      "pt.insufficientStock": "Warning: Insufficient stock ({{count}} available)",
      "pt.partNamePlaceholder": "e.g. Unique Pipe",
      "pt.addAnother": "Add another",
      "pt.adding": "Adding...",
      "pt.part": "Part",
      "pt.unknownPart": "Unknown Part",
      "pt.fit": "Fit",
      "pt.unknown": "Unknown",
      "pt.failedToLoadParts": "Failed to load parts",
      "pt.partRequestSubmitted": "Part request submitted - Admin will review and approve",
      "pt.addPartToTask": "Add Part to Task",
      "pt.useCustomPart": "Use custom part",
      "pt.partNameBrakePadPlaceholder": "e.g., Brake Pad",
      "pt.costPriceSAR": "Cost Price (SAR)",
      "pt.internalLabel": "(internal)",
      "pt.profitPerUnit": "Profit per unit:",
      "pt.partProvidedByCustomer": "Part provided by customer",
      "pt.customerProvidedExplainer": "No charge, no cost, no stock taken — and no customer approval needed.",
      "pt.customerFacingLabel": "shown on the quotation",
      "pt.searchByPartNameOrNumber": "Search by part name or part#...",
      "pt.selectAPart": "Select a part",
      "pt.unnamedPart": "Unnamed Part",
      "pt.showingCount": "Showing {{shown}} of {{total}} parts",
      "pt.additionalNotesPlaceholder": "Additional notes...",
      "pt.enterValidQuantity": "Please enter a valid quantity",
      "pt.restockSuccess": "Successfully added {{quantity}} units to stock",
      "pt.failedToRestockPart": "Failed to restock part",
      "pt.enterQuantity": "Enter quantity",
      "pt.restockNotesPlaceholder": "Add notes about this restock...",
      "pt.newStock": "New Stock",
      "pt.failedToUpdateStock": "Failed to update stock",
      "pt.stockMovement": "Stock Movement",
      "pt.minimumLevel": "Minimum Level",
      "pt.movementType": "Movement Type",
      "pt.removeStock": "Remove Stock",
      "pt.adjustStock": "Adjust Stock",
      "pt.newQuantity": "New Quantity",
      "pt.enterNewTotalQuantity": "Enter new total quantity",
      "pt.newStockWillBe": "New stock will be",
      "pt.unitCostOptional": "Unit Cost (Optional)",
      "pt.reasonPlaceholder": "e.g., Received shipment, Damaged items, Stock count correction",
      "pt.invoiceFileUploaded": "Invoice file uploaded successfully",
      "pt.failedToUploadInvoiceFile": "Failed to upload invoice file",
      "pt.partFound": "Part found: {{name}}",
      "pt.partNotFound": "Part not found. You can create a new one.",
      "pt.failedToScanBarcode": "Failed to scan barcode",
      "pt.pleaseAddAtLeastOneItem": "Please add at least one item",
      "pt.itemSelectPartOrNew": "Item {{n}}: Please select a part or check \"New Part\"",
      "pt.itemPartNumberNameRequired": "Item {{n}}: Part number and name are required for new parts",
      "pt.itemQuantityGreaterThanZero": "Item {{n}}: Quantity must be greater than 0",
      "pt.itemUnitCostGreaterThanZero": "Item {{n}}: Unit cost must be greater than 0",
      "pt.failedToCreatePurchaseInvoice": "Failed to create purchase invoice",
      "pt.stock": "Stock",
      "pt.current": "Current",
      "pt.failedToLoadCategories": "Failed to load categories",
      "pt.categoryNameRequired": "Category name is required",
      "pt.categoryUpdated": "Category updated successfully",
      "pt.categoryCreated": "Category created successfully",
      "pt.failedToSaveCategory": "Failed to save category",
      "pt.confirmDeleteCategory": "Are you sure you want to delete this category?",
      "pt.categoryDeleted": "Category deleted successfully",
      "pt.failedToDeleteCategory": "Failed to delete category",
      "pt.cannotEditGlobalCategories": "Cannot edit global categories",
      "pt.categoryNamePlaceholder": "e.g., Suspension Parts",
      "pt.categoryUnit": "Unit of measure",
      "pt.categoryUnitHint": "Every part created in this category is measured in this unit. Litres, millilitres and kilograms allow decimals; pieces and sets are whole numbers only.",
      "pt.categoryUnitAllRestamped": "Unit applied to all {{restamped}} part(s).",
      "pt.categoryUnitRestampedWithSkipped": "Unit applied to {{restamped}} part(s). {{skipped}} part(s) with stock on hand were left unchanged — zero their stock and re-save the category to update them.",
      "pt.categoryUnitSkippedOnly": "{{skipped}} part(s) with stock on hand remain on the previous unit — zero their stock and re-save the category to update them.",
      "pt.unit.piece": "Piece",
      "gc.title": "Part Categories",
      "gc.subtitle": "The global catalogue every workshop on the platform uses. One row here is read by every tenant.",
      "gc.search": "Search a category",
      "gc.add": "Add category",
      "gc.editTitle": "Edit category",
      "gc.none": "No categories match that search.",
      "gc.name": "Name (English)",
      "gc.nameAr": "Name (Arabic)",
      "gc.description": "Description (English)",
      "gc.descriptionAr": "Description (Arabic)",
      "gc.unit": "Unit of measure",
      "gc.unitHint": "The unit every NEW part under this category inherits. Existing parts keep the unit they were counted in. The change is refused while any workshop still holds stock in the old unit.",
      "gc.usageParts": "{{count}} part(s)",
      "gc.usageTenants": "{{count}} workshop(s)",
      "gc.usageWithStock": "{{count}} holding stock",
      "gc.retire": "Retire",
      "gc.retiredBadge": "Retired",
      "gc.restore": "Restore",
      "gc.restored": "Category restored",
      "gc.saved": "Category saved",
      "gc.saveFailed": "Could not save the category",
      "gc.loadFailed": "Could not load the global categories",
      "gc.retired": "Category retired",
      "gc.retireFailed": "Could not retire the category",
      "gc.confirmRetireTitle": "Retire this category?",
      "gc.confirmRetireMsg": "\"{{name}}\" will stop appearing in every workshop's category picker. Nothing already filed under it is affected, and it can be brought back.",
      "pt.unit.L": "Litre (L)",
      "pt.unit.mL": "Millilitre (mL)",
      "pt.unit.kg": "Kilogram (kg)",
      "pt.unit.set": "Set",
      "pt.unit.roll": "Roll",
      "pt.unitShort.piece": "pcs",
      "pt.inStockHint": "In stock: {{qty}}",
      "pt.unitShort.L": "L",
      "pt.unitShort.mL": "mL",
      "pt.unitShort.kg": "kg",
      "pt.unitShort.set": "set",
      "pt.unitShort.roll": "roll",
      "pt.categoryNameArabicPlaceholder": "مثال: قطع التعليق",
      "pt.optionalDescription": "Optional description",
      "pt.failedToFetchPurchaseInvoices": "Failed to fetch purchase invoices",
      "pt.failedToPostInvoice": "Failed to post invoice",
      "pt.accepted": "Accepted",
      "pt.ordered": "Ordered",
      "pt.autoApprovedQuickService": "Auto-Approved Quick Service",
      "pt.viewQuotation": "View Quotation",
      "pt.acceptPart": "Accept Part",
      "pt.rejectPart": "Reject Part",
      "pt.waitingForCustomerApprovalShort": "Waiting for customer approval",
      "pt.enterEditModeToRemoveItems": "Enter edit mode to remove items",
      "pt.searchByNamePartNoSku": "Search by Name, Part No, SKU...",
      "pt.partNoLabel": "No: {{number}}",
      "pt.categoryLabel": "Cat: {{category}}",
      "pt.stockLabel": "Stock: {{count}}",
      "pt.compatLabel": "Compat: {{value}}",
      "pt.request": "Request",
      "pt.noPartsFoundShort": "No parts found",
      "pt.failedToLoadPurchaseHistory": "Failed to load purchase history",
      "pt.partsRequestedSuccess": "{{count}} part(s) requested successfully",
      "pt.failedToRequestParts": "Failed to request parts",
      "pt.partsSelected": "{{count}} Part(s) Selected",
      "pt.sarEach": "{{price}} SAR each",
      "pt.requesting": "Requesting...",
      "pt.requestPartsWithTotal": "Request {{count}} Part(s) ({{total}} SAR)",
      "pt.searchPartsPlaceholder": "Search parts...",
      "pt.priceSetByAdmin": "Price will be set by admin after review",
      "pt.inStockCount": "{{count}} in stock",
      "pt.failedToLoadServiceTypes": "Failed to load service types",
      "pt.serviceAddedSuccessfully": "Service added successfully",
      "pt.failedToAddService": "Failed to add service",
      "pt.addServiceToTask": "Add Service to Task",
      "pt.useCustomService": "Use custom service",
      "pt.egOilChange": "e.g., Oil Change",
      "pt.sellingPriceSAR": "Selling Price (SAR)",
      "pt.inStock": "In Stock",
      "pt.avgCost": "Avg Cost",
      "pt.sellingPrice": "Selling Price",
      "pt.newAvgCost": "New Avg Cost",
      "pt.projectedMargin": "Projected Margin",
      "pt.priceWillUpdate": "Price will be updated on save",
      "pt.supplierRequired": "Select the supplier this stock was bought from.",
      "pt.newPartNoPrice": "No selling price — this part will be received into stock but cannot be sold until you price it.",
      "pt.sellingPriceUpdated": "Selling price(s) updated",
      "pt.failedToUpdatePrice": "Failed to update one or more selling prices",
      "pt.cataloguePriceNotUpdated": "The company selling price was not changed. Ask a catalogue manager to update it.",
      "aidx.title": "AI Diagnosis Copilot",
      "aidx.subtitle": "AI assistant that helps diagnose job cards",
      "aidx.enable": "Enable AI diagnosis",
      "aidx.enableDesc": "Allow staff to consult the AI assistant from job cards.",
      "aidx.allowMechanics": "Allow technicians",
      "aidx.allowMechanicsDesc": "Let technicians use the copilot at the bay.",
      "aidx.webSearch": "Web search by default",
      "aidx.webSearchDesc": "Default the recalls/TSB web search on for new queries.",
      "aidx.saved": "AI diagnosis settings saved",
      "aidx.saveFailed": "Failed to save AI diagnosis settings",
      "aidx.usageNearLimit": "Approaching the monthly limit ({{pct}}% used).",
      "aidx.agentUsage": "AI Agent: {{used}} of {{limit}} this month",
      "aidx.voiceUsage": "AI Voice: {{used}} of {{limit}} this month",
      "aidx.agentUsageUnlimited": "AI Agent: {{used}} this month · no limit",
      "aidx.agentUsageBlocked": "AI Agent: blocked",
      "aidx.voiceUsageUnlimited": "AI Voice: {{used}} this month · no limit",
      "aidx.voiceUsageBlocked": "AI Voice: blocked",
      "aidx.limitsSetByMotorMind": "Limits are set by MotorMind.",
      "insuranceJobs.title": "Insurance Jobs",
      "insuranceJobs.subtitle": "Allow job cards to be paid by an insurer (B2B claims & invoices)",
      "insuranceJobs.enable": "Enable insurance jobs",
      "insuranceJobs.enableDesc": "Allow job cards to be paid by an insurer (B2B claims & invoices).",
      "insuranceJobs.saved": "Insurance jobs settings saved",
      "insuranceJobs.saveFailed": "Failed to save insurance jobs settings",
      "pt.internal": "(internal)",
      "pt.serviceType": "Service Type",
      "pt.selectAService": "Select a service",
      "pt.serviceRequestSent": "Service request sent",
      "pt.failedToRequestService": "Failed to request service",
      "pt.cannotRemoveServiceRequest": "Cannot remove service request once submitted. Ask admin to reject/remove.",
      "pt.requestService": "Request Service",
      "pt.selectService": "Select Service",
      "pt.chooseAService": "Choose a service...",
      "pt.reasonForRequest": "Reason for request...",
      "pt.sending": "Sending...",
      "pt.noServicesAssociated": "No services associated with this job.",
      "pt.unnamedService": "Unnamed Service",
      "pt.quotationNumber": "Quotation #{{number}}",
      "pt.enterEditModeToRemove": "Enter edit mode to remove items",
      "pt.manageStandardServices": "Manage standard services and pricing",
      "pt.searchServices": "Search services...",
      "pt.noServicesFound": "No services found.",
      "pt.updateService": "Update Service",
      // — Unified task-creation modal (AddTaskModal.js): task + parts + services
      // in one drawer, so per-row Parts/Services fields need their own labels
      // (the old per-item modals leaned on some never-translated bare keys —
      // this scopes fresh ones instead of propagating that gap).
      "tm.partsOptional": "Parts (optional)",
      "tm.servicesOptional": "Services (optional)",
      "tm.addPartRow": "+ Add part",
      "tm.addServiceRow": "+ Add service",
      "tm.partName": "Part Name",
      "tm.serviceName": "Service Name",
      "tm.quantity": "Quantity",
      "tm.unitPriceSAR": "Unit Price (SAR)",
      "tm.taskCreatedFor": "Task created: {{name}}",
      "tm.itemsAttachFailed": "{{count}} item(s) failed to add: {{names}}",
      "tm.retryFailedItems": "Retry Failed Items",
      "tm.discardFailedItemsConfirm": "The task was created, but some items were not added yet. Close and discard them?",
      "qt.loadDataFailed": "Failed to load data",
      "qt.loadJobCardFailed": "Failed to load job card details",
      "qt.pleaseSelectCustomer": "Please select a customer",
      "qt.pleaseAddLineItem": "Please add at least one line item",
      "qt.pleaseFixDownpayment": "Please fix the downpayment amount before continuing.",
      "qt.itemRejected": "Rejected",
      "qt.itemRejectedBecause": "Customer's reason: {{reason}}",
      "qt.itemAccepted": "Accepted",
      "qt.createSuccess": "Quotation created successfully",
      "qt.createFailed": "Failed to create quotation",
      "qt.selectJobCardOptional": "Select Job Card (Optional)",
      "qt.autoFilledFromJobCard": "Auto-filled from job card",
      "qt.enterDetailsManually": "Enter quotation details manually",
      "qt.manualEntryNoJobCard": "-- Manual Entry (No Job Card) --",
      "qt.loadingCustomer": "Loading Customer...",
      "qt.vehicleOptional": "Vehicle (Optional)",
      "qt.loadingVehicle": "Loading Vehicle...",
      "qt.noVehicleSelected": "No Vehicle Selected",
      "qt.unitPriceSar": "Unit Price (SAR)",
      "qt.taxRate": "Tax Rate",
      "qt.taxAmount": "Tax Amount",
      "qt.additionalNotesPlaceholder": "Additional notes...",
      "qt.creatingEllipsis": "Creating...",
      "qt.filterAwaitingCustomer": "Awaiting customer",
      "qt.filterExpiring24h": "Expiring in 24h",
      "qt.filterApprovedNotConverted": "Approved, not converted",
      "qt.liveSummary": "Live summary",
      "qt.createAndChoosePayment": "Create & Send — Choose Payment Methods",
      "qt.pdfDownloadFailed": "Failed to download PDF",
      "qt.resendFailed": "Failed to resend the quotation",
      "qt.resendSms": "Resend SMS",
      "qt.smsSent": "Quotation SMS sent",
      "qt.sendSmsFailed": "Failed to send SMS",
      "qt.convertToInvoice": "Convert to Invoice",
      "qt.convertSuccess": "Invoice draft created",
      "qt.convertedTo": "Converted →",
      "qt.convertErrNotApproved": "Only approved quotations can be converted",
      "qt.convertErrAlreadyConverted": "This quotation has already been converted",
      "qt.convertErrNoItems": "This quotation has no items to convert",
      "qt.convertBlockedJobCard": "Must complete the job card process to issue the invoice from the job card",
      "qt.convertErrNoPermission": "You do not have permission to create invoices",
      "qt.itemType": "Item type",
      "qt.itemTypePart": "Part",
      "qt.itemTypeService": "Service",
      "jc.linkQuotation.action": "Link Approved Quotation",
      "jc.linkQuotation.title": "Link Approved Quotation",
      "jc.linkQuotation.hint": "Pick a quotation the customer approved before the car arrived. Its accepted items will be added to this job card.",
      "jc.linkQuotation.linkAction": "Link to Job Card",
      "jc.linkQuotation.empty": "No approved unlinked quotations",
      "jc.linkQuotation.success": "Quotation linked — approved items added to this job card",
      "jc.linkQuotation.failed": "Failed to link quotation",
      "jc.linkQuotation.loadFailed": "Failed to load quotations",
      "jc.linkQuotation.blockedOwnQuotation": "This job card already has its own quotation",
      "jc.cta.startWork": "Start Repair",
      "jc.cta.startInspection": "Start Inspection",
      "inspection.state.pass": "Good",
      "inspection.state.monitor": "Watch",
      "inspection.state.fail": "Needs repair",
      "inspection.state.failBinary": "Not good",
      "inspection.state.na": "Not checked",
      "inspection.answer.yes": "Yes",
      "inspection.answer.no": "No",
      "inspection.title": "Full Check",
      "inspection.progress": "Points answered",
      "inspection.prevSection": "Previous section",
      "inspection.nextSection": "Next section",
      "inspection.note": "Note",
      "inspection.notePlaceholder": "Anything the customer should know",
      "inspection.finish": "Finish inspection",
      "inspection.finishEmpty": "Answer at least one point before finishing.",
      "inspection.finishRemaining": "{{count}} point(s) left unanswered — they will be recorded as not checked.",
      "inspection.completed": "Inspection completed",
      "inspection.completeFailed": "Could not complete the inspection",
      "inspection.loadFailed": "Could not load the inspection",
      "inspection.startFailed": "Could not start the inspection.",
      "inspection.issueFailed": "Could not issue the report.",
      "inspection.saveFailed": "That answer was not saved — please try again",
      "inspection.state.none": "Not answered",
      "inspection.verdict.pass": "Roadworthy",
      "inspection.verdict.monitor": "Watch these",
      "inspection.verdict.fail": "Repairs needed",
      "inspection.verdict.none": "No verdict",
      "inspection.summaryTitle": "Inspection summary",
      "inspection.summaryCounts": "{{fail}} need repair · {{monitor}} to watch · {{pass}} good",
      "inspection.summaryNotChecked": "{{count}} not checked",
      "inspection.verdictProvisional": "provisional",
      "inspection.vin": "VIN",
      "inspection.registerTitle": "Full Checks",
      "inspection.tpl.title": "Checklist templates",
      "inspection.tpl.subtitle": "Choose which points your full checks ask.",
      "inspection.tpl.new": "New template",
      "inspection.tpl.edit": "Edit template",
      "inspection.tpl.mine": "Your templates",
      "inspection.tpl.builtIn": "Built-in checklists",
      "inspection.tpl.builtInHint": "Supplied by the platform. Create your own template from one of these to change which points it asks.",
      "inspection.tpl.readOnly": "Read-only",
      "inspection.tpl.nameAr": "Name (Arabic)",
      "inspection.tpl.nameEn": "Name (English)",
      "inspection.tpl.basedOn": "Based on",
      "inspection.tpl.basedOnHint": "The checklist the points are taken from. It cannot be changed after the template is saved.",
      "inspection.tpl.selectAll": "Select all",
      "inspection.tpl.points": "{{n}} points",
      "inspection.tpl.pointsChosen": "{{n}} points chosen",
      "inspection.tpl.saved": "Template saved.",
      "inspection.tpl.retire": "Remove template",
      "inspection.tpl.retireBody": "Remove \"{{name}}\"? Checks already performed with it keep their reports.",
      "inspection.tpl.retired": "Template removed.",
      "inspection.tpl.emptyTitle": "No templates of your own yet",
      "inspection.tpl.emptyBody": "Every check runs the built-in checklist until you create one.",
      "inspection.tpl.backToRegister": "Back to full checks",
      "inspection.tpl.pickTitle": "Which checklist?",
      "inspection.tpl.pickBody": "Choose the checklist this check will run from.",
      "inspection.tpl.noSource": "No built-in checklist is available to build a template from.",
      "inspection.tpl.listFailed": "Could not load the checklist templates.",
      "inspection.tpl.loadFailed": "Could not load the checklist points.",
      "inspection.tpl.saveFailed": "Could not save the template.",
      "inspection.tpl.retireFailed": "Could not remove the template.",
      "inspection.registerEmpty": "No full checks have been performed yet.",
      "inspection.listFailed": "Could not load the full checks.",
      "inspection.searchPlaceholder": "Job number, plate or customer",
      "inspection.verdictColumn": "Result",
      "inspection.notBilled": "Not billed yet",
      "inspection.resendReport": "Send the report again",
      "inspection.resent": "The report was sent again on a fresh link",
      "inspection.resendFailed": "Could not send the report",
      "inspection.status.all": "Every status",
      "inspection.status.in_progress": "In progress",
      "inspection.status.completed": "Awaiting confirmation",
      "inspection.status.issued": "Issued",
      "inspection.noLightsLit": "No warning lights are lit",
      "inspection.lightsLoadFailed": "Could not load the warning lights — reload the page before answering this point.",
      "inspection.noCodesStored": "Scanned — no stored fault codes",
      "inspection.dtcCode": "Code",
      "inspection.dtcDescription": "What the scanner said",
      "inspection.dtcInvalid": "Codes look like P0301 or P0A80.",
      "inspection.addCode": "Add code",
      "inspection.notRecorded": "Not recorded",
      "inspection.coverage": "{{checked}} of {{total}} points inspected",
      "inspection.needsAttention": "Needs attention",
      "inspection.showAllPoints": "All {{count}} points",
      "inspection.photoCount": "{{count}} photo(s)",
      "inspection.photoHeic": "HEIC photos are not accepted. On the iPhone, set Settings → Camera → Formats to \"Most Compatible\", or send the photo as JPEG.",
      "inspection.photoFailed": "Could not upload the photo.",
      "inspection.charges": "Charges",
      "inspection.noCharges": "Nothing has been priced on this inspection yet.",
      "inspection.chargedOnInvoice": "The inspection fee was billed on invoice {{number}}.",
      "inspection.chargedOnInvoiceDraft": "The inspection fee was billed on the invoice below.",
      "inspection.feeLine": "Inspection fee",
      "inspection.price": "Price",
      "inspection.editPrice": "Edit price",
      "inspection.priceSaved": "Price updated",
      "inspection.priceFailed": "Could not update the price",
      "inspection.editAnswers": "Edit answers",
      "inspection.confirmIssue": "Confirm & issue report",
      "inspection.confirmNote": "You can still edit until you confirm. After that the report is final.",
      "inspection.reopened": "You can edit the checklist again",
      "inspection.reopenFailed": "Could not reopen the checklist",
      "inspection.issued": "Report issued",
      "inspection.issuedNote": "The customer receives the report once the invoice is paid.",
      "inspection.printReport": "Print report",
      "inspection.printFailed": "Could not open the report",
      "inspection.createInvoice": "Create invoice",
      "inspection.invoiceCreated": "Invoice created",
      "inspection.invoiceReady": "Invoice",
      "inspection.invoiceFailed": "Could not create the invoice",
      "inspection.invoiceNeedsJobCard": "This customer has credit on account. Open the job card to choose how to apply it.",
      "inspection.invoiceStatus.paid": "Paid",
      "inspection.invoiceStatus.partial": "Partly paid",
      "inspection.invoiceStatus.unpaid": "Unpaid",
      "inspection.invoiceStatus.void": "Voided",
      "inspection.recordPayment": "Record a payment taken in the garage",
      "jc.cta.continueInspection": "Continue Inspection",
      "jc.cta.issueReport": "Issue Report",
      "jc.cta.reviewInspection": "Review & Issue Report",
      "jc.cta.openReport": "Open Report",
      "jc.cta.dpBlocksRepair": "A requested downpayment is still unpaid — work starts automatically once it is paid.",
      "jc.cta.resendPayLink": "Resend Pay Link",
      "jc.dp.vatPreview": "Customer pays {{gross}} SAR (includes {{rate}}% VAT)",
      "jc.dp.depositPreview": "Customer pays {{gross}} SAR (trust deposit, no VAT)",
      "jc.dp.title": "Request Downpayment",
      "jc.dp.hint": "Quotation {{number}} is approved (total {{total}}). Choose how the downpayment is calculated — VAT is added automatically. The customer gets an SMS with the payment link.",
      "jc.dp.hintReceipt": "Quotation {{number}} is approved (total {{total}}). Choose how the downpayment is calculated — no VAT, held as a trust deposit until the final invoice. The customer gets an SMS with the payment link.",
      "jc.dp.amountPlaceholder": "Amount (SAR)",
      "jc.dp.send": "Send Payment Request",
      "jc.dp.chooseMethodsAndSend": "Choose payment methods & send",
      "jc.dp.success": "Downpayment requested",
      "jc.dp.successSms": "Downpayment requested — SMS sent to the customer",
      "jc.dp.receiptSuccess": "Payment receipt issued",
      "jc.dp.receiptSuccessSms": "Payment receipt issued — SMS sent to the customer",
      "jc.dp.failed": "Failed to request downpayment",
      "jc.dp.invalidAmount": "Enter a valid amount",
      "jc.aiAnalysis.title": "AI Analysis",
      "jc.aiAnalysis.run": "Run analysis",
      "jc.aiAnalysis.rerun": "Re-run analysis",
      "jc.aiAnalysis.lastRun": "Last analysis: {{time}}",
      "jc.aiAnalysis.empty": "Get a full AI summary of this job card, from check-in to now.",
      "jc.aiAnalysis.failed": "AI analysis failed. Please try again.",
      "jc.aiAnalysis.sections.workDone": "Completed",
      "jc.aiAnalysis.sections.remaining": "Remaining",
      "jc.aiAnalysis.sections.risks": "Risks",
      "jc.aiAnalysis.sections.recommendations": "Recommendations",
      "jc.rp.request": "Request payment",
      "jc.rp.title": "Request payment",
      "jc.rp.amountLabel": "Amount (SAR)",
      "jc.rp.methodsLabel": "Payment methods for this request",
      "jc.rp.estimateLine": "Estimate total {{total}} · Already paid {{paid}} · Remaining {{remaining}}",
      "jc.rp.fullAmount": "Full remaining amount incl. VAT ({{amount}})",
      "jc.rp.success": "Payment requested — receipt created, link is being sent to the customer",
      "jc.rp.failed": "Failed to request payment",
      "qt.cannotDeleteApproved": "Cannot delete approved quotation",
      "qt.deleteConfirmNumbered": "Delete quotation {{number}}?",
      "qt.deleteSuccess": "Quotation deleted",
      "qt.deleteFailed": "Failed to delete",
      "qt.viewDetails": "View Details",
      "qt.renew": "Renew",
      "qt.renewTitle": "Renew quotation",
      "qt.renewSubtitle": "This quotation has expired. Give it a new validity date, then send it as it is or edit it first.",
      "qt.renewNewValidUntil": "Valid until",
      "qt.renewAndSend": "Send as is",
      "qt.renewAndEdit": "Update first",
      "qt.renewed": "Quotation renewed",
      "qt.renewedAndSent": "Quotation renewed and sent to the customer",
      "qt.renewedSmsDuplicate": "Renewed, but the SMS gateway suppressed the message as a repeat of one the customer already has. Change something on the quotation and send again.",
      "qt.resendDuplicate": "Not sent — the customer already has this exact message, so the gateway suppressed the repeat. Change the quotation or renew it first.",
      "qt.renewFailed": "Could not renew the quotation",
      "qt.manualApproval": "Manual Approval",
      "qt.resendReminder": "Send Reminder",
      "qt.loadFailed": "Failed to load quotation",
      "qt.fillDescriptionPrice": "Please fill description and price",
      "qt.itemAdded": "Item added",
      "qt.addItemFailed": "Failed to add item",
      "qt.itemsChangedRefresh": "This quotation changed on another screen. It has been reloaded — please try again.",
      // PR#3 Task 3.4 — the mandatory-line policy in the builder
      // (CreateQuotation.js) and the grouped internal preview
      // (QuotationPreviewModal.js). The consequence is always shown IN WORDS:
      // staff never see "reject_group".
      "qt.mandatoryLine": "Mandatory line",
      "qt.mandatoryLineHint": "Applies only when the customer declines this line. Every other line keeps its own rule.",
      "qt.mandatoryChainNote": "Another line in this service rejects the whole quotation, so declining this line rejects the whole quotation too.",
      "qt.mandatoryConsequenceLegend": "If the customer declines it:",
      "qt.mandatoryActionRejectGroup": "Reject this service and its parts",
      "qt.mandatoryActionRejectQuotation": "Reject the whole quotation",
      "qt.mandatoryActionRequired": "Choose what declining this line should do.",
      "qt.mandatoryGroupRequired": "This line belongs to no service, so there is no service to reject. Pick \"Reject the whole quotation\" instead.",
      "qt.mandatoryFixPolicies": "Fix the mandatory-line settings before continuing.",
      "qt.lineGroup": "Part of: {{service}}",
      "qt.lineNoGroup": "Not attached to a service",
      "qt.groupUnnamedService": "Service",
      "qt.requoteTitle": "Revise the declined items",
      "qt.requoteHint": "These are the same job-card lines the customer declined, from every service on this card. Editing them here changes those very lines — it does not add new ones. Work stays on hold until the customer approves the new prices.",
      "qt.requoteBoundHint": "Fixed by the job card being revised.",
      "qt.requoteSubmit": "Send the revised quotation",
      "qt.requoteAndChoosePayment": "Send revision — Choose Payment Methods",
      "qt.requoteDone": "Revised quotation {{number}} sent to the customer",
      "qt.requoteNoSms": "Quotation {{number}} was created, but the message to the customer did NOT go out — send them the link yourself.",
      "qt.requoteLocked": "Cannot re-price: {{lines}} is billed on a posted invoice. Reverse that invoice (credit note) first.",
      "qt.builderHint": "Start with the service, then list the parts it needs underneath it. The customer sees the same grouping, and can accept or reject a service together with its parts.",
      "qt.addService": "Add service",
      "qt.addPart": "Add a part to this service",
      "qt.removeService": "Remove service and its parts",
      "qt.cardParts": "Parts for this service",
      "qt.cardNoParts": "No parts under this service yet",
      "qt.looseItems": "Items with no service",
      "qt.looseItemsHint": "Charges that stand on their own — a call-out fee, a diagnostic, a part sold over the counter.",
      "qt.serviceNamePlaceholder": "Name of the service, e.g. front brake overhaul",
      "qt.mandatoryBadge": "Mandatory",
      "qt.mandatoryConsequenceGroup": "If declined: this service and its parts are rejected",
      "qt.mandatoryConsequenceGroupChained": "If declined: the whole quotation is rejected (another line in this service requires it)",
      "qt.mandatoryConsequenceQuotation": "If declined: the whole quotation is rejected",
      "qt.ungroupedItems": "Other items (not attached to a service)",
      "qt.pdfDownloadStarted": "PDF download started",
      "qt.resendReminderSent": "Reminder sent to the customer",
      "qt.resendNothingDelivered": "Not delivered: the customer has no phone number",
      "qt.deleteConfirm": "Are you sure you want to delete this quotation? This action cannot be undone.",
      "qt.deleteSuccessFull": "Quotation deleted successfully",
      "qt.deleteFailedFull": "Failed to delete quotation",
      "qt.jobCard": "Job Card",
      "qt.viewInvoice": "View Invoice",
      "qt.viewJobCard": "View job card",
      "qt.pdf": "PDF",
      "qt.pricePlaceholder": "Price",
      "qt.add": "Add",
      "qt.invalidOrExpiredLink": "Invalid or expired link",
      "qt.dpFixedAmount": "Fixed amount: {{amount}} SAR",
      "qt.dpPercentOfSelected": "{{value}}% of selected items",
      "qt.dpPercentOfParts": "{{value}}% of parts cost",
      "qt.dpAmountPartsOnly": "{{amount}} SAR (parts only)",
      "qt.selectAtLeastOneService": "Please select at least one service to approve",
      "qt.approvalFailed": "Approval failed",
      "qt.processApprovalFailed": "Failed to process approval",
      "qt.rejectConfirm": "Are you sure you want to reject this entire quotation?",
      "qt.rejectionFailed": "Rejection failed",
      "qt.processRejectionFailed": "Failed to process rejection",
      "qt.loadingQuotation": "Loading quotation...",
      "qt.oops": "Oops!",
      "qt.contactGarageDirectly": "If you believe this is an error, please contact the garage directly.",
      "qt.servicesApproved": "Services Approved!",
      "qt.quotationRejectedTitle": "Quotation Rejected",
      "qt.thankYouApproved": "Thank you for approving the selected services. An invoice has been generated and the garage has been notified.",
      "qt.quotationLabel": "Quotation",
      "qt.approvedServicesLabel": "Approved Services",
      "qt.countOf": "{{count}} of {{total}}",
      "qt.garageWillContact": "The garage will contact you shortly to arrange the service.",
      "qt.responseRecorded": "Your response has been recorded. The garage has been notified.",
      "qt.quotationApproval": "Quotation Approval",
      "qt.reviewAndSelect": "Please review and select the services you wish to approve",
      "qt.quotationNumberLabel": "Quotation Number",
      "qt.totalAmount": "Total Amount",
      "qt.selectServicesToApprove": "Select Services to Approve",
      "qt.checkServicesHint": "Check the services you want to approve. Unchecked services will be rejected.",
      "qt.selectedServices": "Selected Services",
      "qt.downpaymentRequiredBadge": "DOWNPAYMENT REQUIRED",
      "qt.typeLabel": "Type",
      "qt.downpaymentAmount": "Downpayment Amount",
      "qt.noteLabel": "Note",
      "qt.downpaymentNote": "After approval, you'll be redirected to pay the downpayment. The remaining amount will be due upon service completion.",
      "qt.approveSelectedCount": "Approve Selected ({{count}})",
      "qt.rejectAll": "Reject All",
      "qt.approvalAgreement": "By approving, you agree to the selected services and their costs",
      "qt.dpExceedFixed": "Downpayment cannot exceed {{max}} SAR (excl. VAT). With VAT: {{total}} SAR",
      "qt.dpExceedFixedReceipt": "Downpayment cannot exceed {{max}} SAR (trust deposit — no VAT)",
      "qt.dpExceedTotal": "Downpayment ({{amount}} SAR) cannot exceed total amount ({{total}} SAR)",
      "qt.updatedAndSmsSent": "Quotation updated and SMS sent to customer!",
      "qt.updatedSmsSkippedExpired": "Saved. No SMS was sent — this quotation has expired. Renew it, then send.",
      "qt.updatedAs": "Updated as {{number}}",
      "qt.updateFailedReason": "Failed to update quotation: {{reason}}",
      "qt.editQuotation": "Edit Quotation",
      "qt.previewQuotation": "Preview Quotation",
      "qt.sendingEllipsis": "Sending...",
      "qt.generatingEllipsis": "Generating...",
      "qt.generateForExport": "Generate for export",
      "qt.insurerEstimateNote": "This is an insurer estimate. It will be generated for manual export and is NOT sent to the customer. Enter the approval reference in the claim section once the insurer responds.",
      "qt.saveResendSms": "Save & Resend SMS",
      "qt.confirmSendQuotation": "Confirm & Send Quotation",
      "qt.customerInformation": "Customer Information",
      "qt.jobCardId": "Job Card ID",
      "qt.action": "Action",
      "qt.customerProvided": "Customer Provided",
      "qt.downpaymentRequired": "Downpayment Required",
      "qt.downpaymentType": "Downpayment Type",
      "qt.dpTypePercentage": "Percentage of Total",
      "qt.dpTypeFixed": "Fixed Amount",
      "qt.dpTypePartsOnly": "Parts Only (Full Amount)",
      "qt.dpTypeFullAmount": "Full amount (incl. VAT)",
      "qt.dpFullAmountReadout": "{{amount}} SAR — full quotation total incl. VAT",
      "qt.dpDescFullAmount": "Full quotation total incl. VAT — capped at the lines the customer accepts",
      "qt.dpFullAmountZero": "Full amount needs a quotation total above zero",
      "qt.dpLabelPercentage": "Percentage (%)",
      "qt.dpLabelAmount": "Amount (SAR)",
      "qt.dpLabelPartsTotal": "Parts Total Amount (SAR)",
      "qt.partsTotalAutoCalculated": "{{amount}} SAR (Auto-calculated)",
      "qt.dpExamplePercent": "e.g., 30",
      "qt.dpExampleAmount": "e.g., 500.00",
      "qt.calculatedDownpayment": "Calculated Downpayment",
      "qt.dpDescPercentage": "{{value}}% of total amount",
      "qt.dpDescFixed": "Fixed amount (includes VAT)",
      "qt.dpDescFixedReceipt": "Fixed amount (no VAT — trust deposit)",
      "qt.dpDescPartsOnly": "100% of parts cost",
      "qt.dpPartialAcceptNote": "If customer partially accepts items, the downpayment will be automatically adjusted based on accepted items.",
      "qt.sendQuotationVia": "Send Quotation Via:",
      "qt.emailOnly": "Email Only",
      "qt.smsOnly": "SMS Only",
      "qt.bothEmailSms": "Both Email & SMS",
      "qt.messageToCustomer": "Message to Customer (Optional):",
      "qt.messageToCustomerPlaceholder": "Enter any additional message or instructions for the customer (will be displayed on the quotation approval page)...",
      "qt.messageToCustomerHint": "This message will be shown to the customer when they view the quotation.",
      "qt.arabicDefault": "Arabic (العربية) - Default",
      "qt.bothLanguages": "Both Languages (Bilingual)",
      "qt.notificationLanguageHint": "The notification will be sent in the selected language. Bilingual option includes both Arabic and English.",
      "sa.superAdmin": "Super Admin",
      "sa.user": "User",
      "sa.systemUsers": "System Users",
      "sa.systemUsersSubtitle": "Manage system administrators and support staff",
      "sa.searchUsers": "Search users...",
      "sa.noUsersMatch": "No users found matching your criteria",
      "sa.notAvailable": "N/A",
      "sa.deleteUser": "Delete User",
      "sa.activateUser": "Activate User",
      "sa.deactivateUser": "Deactivate User",
      "sa.confirmDeleteUserName": "Are you sure you want to delete {{name}}? This action cannot be undone.",
      "sa.failedToCreateUser": "Failed to create user",
      "sa.failedToUpdateUser": "Failed to update user",
      "sa.failedToSaveUser": "Failed to save user",
      "sa.addNewUser": "Add New User",
      "sa.phoneRequiredForNewUsers": "A mobile number is required — it is how this user signs in",
      "sa.personalInformation": "Personal Information",
      "sa.firstNamePlaceholder": "John",
      "sa.lastNamePlaceholder": "Doe",
      "sa.emailPlaceholder": "john@example.com",
      "sa.enterPassword": "Enter password",
      "sa.leaveBlankKeepPassword": "Leave blank to keep current password",
      "sa.authorizedBranches": "Authorized Branches",
      "sa.noBranchesAvailable": "No branches available",
      "sa.selectBranchesHint": "Select one or more branches this user can access",
      "sa.activeUser": "Active User",
      "sa.activeStatus": "Active Status",
      "sa.globalAccess": "Global Access",
      "sa.companiesSubtitle": "Manage companies and their branches (workshops/service centers)",
      "sa.addCompany": "Add Company",
      "sa.searchCompanies": "Search companies by name or CR number...",
      "sa.failedToLoadCompanies": "Failed to load companies",
      "sa.tryAgain": "Try Again",
      "sa.noCompaniesFound": "No companies found.",
      "sa.noCompaniesYet": "No companies yet",
      "sa.subscription": "Subscription",
      "sa.noPlan": "No Plan",
      "sa.branches": "Branches",
      "sa.companyBranches": "Company Branches",
      "sa.addBranch": "Add Branch",
      "sa.unnamedGarage": "Unnamed Garage",
      "sa.noCity": "No City",
      "sa.noBranchesYet": "No branches added for this company yet.",
      "sa.manageCompanyDetails": "Manage Company Details",
      "sa.companyCreatedSuccessfully": "Company created successfully",
      "sa.companyUpdatedSuccessfully": "Company updated successfully",
      "sa.failedToCreateCompany": "Failed to create company",
      "sa.failedToUpdateCompany": "Failed to update company",
      "sa.branchCreatedSuccessfully": "Branch created successfully",
      "sa.branchUpdatedSuccessfully": "Branch updated successfully",
      "sa.failedToCreateBranch": "Failed to create branch",
      "sa.failedToUpdateBranch": "Failed to update branch",
      "sa.superAdminDashboard": "Super Admin Dashboard",
      "sa.superAdminDashboardSubtitle": "Manage tenants, subscriptions, and system configurations",
      "sa.totalCompanies": "Total Companies",
      "sa.activeSubscriptions": "Active Subscriptions",
      "sa.totalBranches": "Total Branches",
      "sa.pendingApproval": "Pending Approval",
      "sa.expired": "Expired",
      "sa.suspended": "Suspended",
      "sa.pendingApprovals": "Pending Approvals",
      "sa.companiesAwaitingApproval": "{{count}} companies awaiting approval",
      "sa.expiringSoon": "Expiring Soon",
      "sa.subscriptionsExpiring": "{{count}} subscriptions expiring in 30 days",
      "sa.manageSubscriptions": "Manage Subscriptions",
      "sa.systemConfigurations": "System Configurations",
      "sa.voiceProvider.title": "Voice conversation engine",
      "sa.voiceProvider.hint": "One setting for the whole platform: every tenant's next voice call uses this engine. Calls in progress keep theirs.",
      "sa.voiceProvider.active": "In use",
      "sa.voiceProvider.choose": "Switch to this engine",
      "sa.voiceProvider.saving": "Switching…",
      "sa.voiceProvider.notConfigured": "No API key on the server",
      "sa.voiceProvider.saved": "Voice calls now use {{provider}}",
      "sa.voiceProvider.saveFailed": "Could not switch the voice engine",
      "sa.voiceProvider.loadFailed": "Could not load the voice engine setting",
      "sa.subscriptionStatus": "Subscription Status",
      "sa.recentCompanies": "Recent Companies",
      "sa.failedToLoadPermissions": "Failed to load permissions",
      "sa.failedToLoadRolePermissions": "Failed to load role permissions",
      "sa.permissionsUpdatedFor": "Permissions updated for {{role}}",
      "sa.failedToSavePermissions": "Failed to save permissions",
      "sa.roleNameRequired": "Role name is required",
      "sa.roleCreatedSuccessfully": "Role \"{{name}}\" created successfully",
      "sa.failedToCreateRole": "Failed to create role",
      "sa.createCustomRole": "Create Custom Role",
      "perm.view.jobs": "Jobs",
      "perm.area.workshop": "Workshop",
      "perm.area.sales": "Sales",
      "perm.area.money": "Payments & cash",
      "perm.area.purchasing": "Purchasing & stock",
      "perm.area.hr": "Human resources",
      "perm.area.admin": "Accounting & administration",
      "perm.view.advanced": "Advanced",
      "perm.jobs.searchPlaceholder": "Search jobs or permission keys",
      "perm.jobs.none": "No jobs match this search",
      "perm.jobs.roleNameGated": "Also limited by role name — this switch alone may not be enough",
      "perm.switchRoleConfirm": "You have unsaved changes to this role. Discard them and open another role?",
      "perm.analysis.button": "What this role can do",
      "perm.analysis.modalTitle": "What this role can do",
      "perm.analysis.title": "TzamunAI analysis",
      "perm.analysis.builtInRole": "Built-in",
      "perm.analysis.exactJobsTitle": "What is switched on for this role",
      "perm.analysis.exactJobsSubtitle": "Read directly from the role's permissions, without AI",
      "perm.analysis.noActiveJobs": "No tasks are switched on for this role.",
      "perm.analysis.roleNameGatedNote": "also limited by the role name today",
      "perm.analysis.verdict.fits": "Fits the role",
      "perm.analysis.verdict.missing": "Missing permissions",
      "perm.analysis.verdict.over": "Over-permissioned",
      "perm.analysis.verdict.both": "Needs adjusting",
      "perm.analysis.canDo": "Can do",
      "perm.analysis.cannotDo": "Cannot do",
      "perm.analysis.watchOut": "Watch out",
      "perm.analysis.changedFromStandard": "Changed from the standard role",
      "perm.analysis.baselineAdded": "Added beyond the standard role",
      "perm.analysis.baselineRemoved": "Removed from the standard role",
      "perm.analysis.suggestions": "Suggestions",
      "perm.analysis.suggestedAdd": "Suggested to add",
      "perm.analysis.suggestedRemove": "Suggested to remove",
      "perm.analysis.apply": "Apply",
      "perm.analysis.applied": "Applied",
      "perm.analysis.applyAll": "Apply all",
      "perm.analysis.analyseAgain": "Analyse again",
      "perm.analysis.error": "Could not analyse this role. Try again.",
      "perm.suggest.title": "TzamunAI Suggestions",
      "perm.suggest.subtitle": "Suggest permissions tailored to this role using TzamunAI",
      "perm.suggest.button": "Suggest permissions with TzamunAI",
      "perm.suggest.step.reading": "Reading the role",
      "perm.suggest.step.checking": "Checking {{count}} permissions",
      "perm.suggest.step.rules": "Applying the separation-of-duties rule",
      "perm.suggest.scanningAreas": "Checking permission areas:",
      "perm.suggest.summary": "TzamunAI selected {{count}} permissions — review before creating",
      "perm.suggest.tickedNotice": "{{count}} currently ticked",
      "perm.suggest.needsReview": "Needs review",
      "perm.suggest.needsReviewHint": "Borderline permissions — unticked by default, review carefully before including",
      "perm.suggest.adminOnly": "Kept for Admin",
      "perm.suggest.usePermissions": "Use these permissions",
      "perm.suggest.permissionsApplied": "Permissions applied",
      "perm.suggest.tryAgain": "Try again",
      "perm.suggest.activeSource": "Starting from TzamunAI ({{count}} permissions)",
      "perm.suggest.activeTemplate": "Starting template:",
      "perm.suggest.clearActive": "Clear",
      "perm.suggest.error": "Could not suggest permissions. Try again.",
      "ai.stage.announcement.reading": "Reading your brief",
      "ai.stage.announcement.drafting": "TzamunAI is drafting both languages",
      "ai.stage.announcement.checking": "Checking the draft",
      "ai.stage.damageScan.reading_photos": "Loading the job card",
      "ai.stage.damageScan.detecting": "TzamunAI is finding the damage",
      "ai.stage.damageScan.processing": "Storing the scanned photos",
      "ai.stage.damageScan.saving": "Saving the scan",
      "ai.stage.istamara.reading_registration": "Receiving the card image",
      "ai.stage.istamara.extracting": "TzamunAI is reading the card",
      "ai.stage.istamara.matching": "Matching make and model",
      "ai.stage.jobCardAnalysis.reading": "Reading the job card and visit history",
      "ai.stage.jobCardAnalysis.model": "TzamunAI is analysing",
      "ai.stage.jobCardAnalysis.validating": "Checking the answer",
      "ai.stage.jobCardAnalysis.saving": "Saving the analysis",
      "ai.stage.jobCardAssistant.thinking": "Reading the job card and vehicle",
      "ai.stage.jobCardAssistant.web_search": "Searching the web for recalls and bulletins",
      "ai.stage.jobCardAssistant.model": "TzamunAI is diagnosing",
      "ai.stage.jobCardAssistant.parts": "Matching parts with your stock",
      "ai.stage.jobCardAssistant.saving": "Saving the consultation",
      "ai.stage.purchaseInvoice.reading": "Reading the uploaded file",
      "ai.stage.purchaseInvoice.extracting": "TzamunAI is reading the invoice",
      "ai.stage.purchaseInvoice.matching": "Matching the supplier and items",
      "ai.stage.qiwaScan.reading": "Reading the contract file",
      "ai.stage.qiwaScan.extracting": "TzamunAI is reading the contract",
      "ai.stage.qiwaScan.validating": "Checking the employee details",
      "ai.stage.qiwaScan.saving": "Saving the draft",
      "ai.stage.roleAnalysis.cached": "Loading the saved analysis",
      "ai.stage.roleAnalysis.baseline": "Comparing with the standard role",
      "ai.stage.roleAnalysis.analyzing": "TzamunAI is analysing the role",
      "ai.stage.roleAnalysis.evaluating": "Checking the answer",
      "ai.stage.roleSuggest.reading": "Preparing the role and permissions",
      "ai.stage.roleSuggest.checking": "TzamunAI is checking every permission",
      "ai.stage.roleSuggest.rules": "Applying the admin-only rule",
      "ai.stage.terms.drafting": "TzamunAI is writing the English version",
      "ai.stage.terms.translating": "TzamunAI is writing the Arabic version",
      "ai.stage.terms.saving": "Saving the draft",
      "ai.thinking.stillWorking": "TzamunAI is still working…",
      "ai.thinking.elapsed": "{{seconds}}s",
      "perm.analysis.step.reading": "Reading the role's permissions",
      "perm.analysis.step.matching": "Comparing with the standard roles",
      "perm.analysis.step.risks": "Weighing risks and suggestions",
      "jc.aiAnalysis.step.reading": "Reading the job card and visit history",
      "jc.aiAnalysis.step.progress": "Checking finished and remaining work",
      "jc.aiAnalysis.step.synthesis": "Writing the cost and risk summary",
      "damageScan.ai.readingPhotos": "Reading the photos",
      "damageScan.ai.findingDamage": "Finding the damage",
      "damageScan.ai.markingMap": "Marking the damage map",
      "cm.istamara.step.reading": "Reading the registration card",
      "cm.istamara.step.extracting": "Extracting vehicle and owner details",
      "cm.istamara.step.verifying": "Checking the plate and VIN",
      "hr.wizard.qiwa.step.reading": "Reading the contract",
      "hr.wizard.qiwa.step.extracting": "Extracting employee and salary details",
      "hr.wizard.qiwa.step.filling": "Filling in the new employee draft",
      "tnc.ai.step.drafting": "Drafting the terms",
      "tnc.ai.step.translating": "Writing the Arabic and English versions",
      "tnc.ai.step.compliance": "Reviewing legal compliance",
      "tnc.ai.step.refining": "Still working on the full agreement — this can take a few minutes",
      "ann.ai.step.reading": "Reading the brief",
      "ann.ai.step.drafting": "Drafting the announcement in both languages",
      "ann.ai.step.polishing": "Polishing tone and clarity",
      "purchases.scan.step.reading": "Reading the invoice image",
      "purchases.scan.step.extracting": "Extracting lines and totals",
      "purchases.scan.step.matching": "Matching the supplier and VAT number",
      // ---- document designs, invoice bank details, branch stamp (2026-10-02) ----
      "docDesign.title": "Print document designs",
      "docDesign.desc": "Choose the print look for each document type. Preview renders sample data before saving.",
      "docDesign.preview": "Preview",
      "docDesign.save": "Save designs",
      "docDesign.loadFailed": "Could not load document designs",
      "docDesign.saved": "Document designs saved",
      "docDesign.saveFailed": "Could not save document designs",
      "docDesign.previewFailed": "Could not generate the preview",
      "docDesign.thumbFailed": "Preview unavailable",
      "bookingPage.arabic": "Arabic",
      "bookingPage.english": "English",
      "bookingPage.uploadImage": "Upload image",
      "bookingPage.closed": "Closed",
      "bookingPage.startingFrom": "Starting from",
      "bookingPage.sar": "SAR",
      "bookingPage.minutes": "minutes",
      "bookingPage.noServices": "No services available.",
      "bookingPage.noBranches": "No branch details available.",
      "bookingPage.openOsm": "Open in OpenStreetMap",
      "bookingPage.noReviews": "No reviews yet.",
      "bookingPage.noFaq": "No frequently asked questions available.",
      "bookingPage.openingHours": "Opening Hours",
      "bookingPage.contactUs": "Contact Us",
      "bookingPage.phone": "Phone",
      "bookingPage.email": "Email",
      "bookingPage.address": "Address",
      "bookingPage.bookNow": "Book Now",
      "bookingPage.bookAppointment": "Book an Appointment",
      "bookingPage.onlineBooking": "Online Booking",
      "bookingPage.selectBranchAndService": "Select branch, service, and appointment time.",
      "bookingPage.days.sunday": "Sunday",
      "bookingPage.days.monday": "Monday",
      "bookingPage.days.tuesday": "Tuesday",
      "bookingPage.days.wednesday": "Wednesday",
      "bookingPage.days.thursday": "Thursday",
      "bookingPage.days.friday": "Friday",
      "bookingPage.days.saturday": "Saturday",
      "notify.dontSend": "Don't send to the customer",
      "notify.dontSendHint": "The document is still issued; only the message is skipped.",
      "notify.notSentBadge": "Not sent to the customer",
      "notify.issuedNotSent": "Issued and not sent to the customer",
      "assistant.quote.askDownpayment": "Do you want to request a down payment? (amount or %)",
      "qc.hold.QC_PASSED_AWAITING_QUOTATION_APPROVAL": "Quality check passed. The job card is waiting for the customer to approve the quotation, then it moves to invoicing.",
      "qc.hold.QC_PASSED_AWAITING_DOWNPAYMENT": "Quality check passed. The job card is waiting for the down payment, then it moves to invoicing.",
      "docDesign.retry": "Retry",
      "docDesign.current": "Current",
      "docDesign.ledger": "A · Ledger",
      "docDesign.band": "B · Brand band",
      "docDesign.formal": "C · Formal bordered",
      "docDesign.rail": "D · Side rail",
      "docDesign.type.invoice": "Invoice",
      "docDesign.type.credit_note": "Credit / debit note",
      "docDesign.type.quotation": "Quotation",
      "docDesign.type.receipt": "Payment receipt / voucher",
      "docDesign.type.payslip": "Payslip",
      "docDesign.type.insurance_report": "Insurance report",
      "docDesign.type.statement": "Account statement",
      "docDesign.type.report": "Reports",
      "settings.digitsHeading": "Digits",
      "settings.digitsDescription": "Choose how numbers appear on screens and in documents",
      "settings.digitsLatin": "Latin 123",
      "settings.digitsArabic": "Arabic-Indic ١٢٣",
      "settings.digitsSaved": "Digits preference saved",
      "settings.digitsSaveFailed": "Could not save the digits preference",
      "settings.digitsLoadFailed": "Could not load the digits preference",
      "copilot.panelTitle": "TzamunAI Suggest",
      "copilot.dismiss": "Hide suggestions",
      "copilot.matchesRequest": "Matches the customer request: {{words}}",
      "vehicleSpecs.disclaimer": "Automatic TzamunAI suggestion. A technician must verify it fits this vehicle (manufacturer manual or VIN) before use.",
      "vehicleSpecs.candidateMatch": "May match stock: {{name}}",
      "vehicleSpecs.inStock": "In stock:",
      "vehicleSpecs.outOfStock": "Out of stock",
      "vehicleSpecs.notInStock": "Not in stock",
      "vehicleSpecs.quota": "The monthly AI limit has been reached",
      "vehicleSpecs.assumption.engine_size": "Engine size not recorded — assumed {{value}}",
      "vehicleSpecs.assumption.fuel_type": "Fuel type not recorded — assumed {{value}}",
      "vehicleSpecs.assumption.transmission": "Transmission not recorded — assumed {{value}}",
      "vehicleSpecs.assumption.year": "Model year not recorded — assumed {{value}}",
      "vehicleSpecs.addTitle": "Add from stock",
      "vehicleSpecs.quantity": "Quantity",
      "vehicleSpecs.unitQt": "qt",
      "svcCheck.button": "TzamunAI check",
      "svcCheck.running": "TzamunAI is checking this service…",
      "svcCheck.recheck": "Check again",
      "svcCheck.vinNeeded": "The VIN is needed to check this service",
      "svcCheck.missingDetails": "These details are missing from the vehicle record. Add them for a more accurate check.",
      "svcCheck.run": "Check",
      "svcCheck.needsTitle": "What this vehicle needs",
      "svcCheck.partsTitle": "Parts added",
      "svcCheck.missingTitle": "Missing parts",
      "svcCheck.stepsTitle": "Steps",
      "svcCheck.missingStepsTitle": "Missing steps",
      "svcCheck.ok": "Matches",
      "svcCheck.wrongSpec": "{{part}} — this vehicle needs {{spec}}",
      "svcCheck.wrongQuantity": "Quantity {{qty}} — this vehicle needs {{needed}}",
      "svcCheck.notNeeded": "This service may not need it",
      "svcCheck.unlinked": "Not linked to a service",
      "svcCheck.stepUnfinished": "Not finished yet",
      "svcCheck.addStep": "Add step",
      "svcCheck.stepAdded": "Step added",
      "svcCheck.nothingConfirmed": "TzamunAI found no reliable information for this service on this vehicle",
      "svcCheck.partNumber": "Part no. {{number}}",
      "svcCheck.verifyPartNumber": "Check the number in the dealer catalogue before ordering",
      "svcCheck.vinMismatch": "The VIN points to {{make}} {{year}} — check the vehicle details",
      "svcCheck.needsChanged": "The vehicle or service details changed — check again",
      "svcCheck.workChanged": "The parts or steps changed — check again",
      "svcCheck.failed": "Could not check right now",
      "svcCheck.unitG": "g",
      "svcCheck.notAdded": "Not added yet",
      "svcCheck.unconfirmed": "Not confirmed for this vehicle: {{names}}",
      "svcCheck.stepsSuggested": "Suggested steps ({{count}})",
      "copilot.panelEmpty": "No suggestions yet for this card.",
      "copilot.loading": "Loading suggestions…",
      "copilot.add": "Add",
      "copilot.added": "Added",
      "copilot.confidenceHigh": "High confidence",
      "copilot.confidenceMedium": "Medium confidence",
      "copilot.confidenceLow": "Low confidence",
      "copilot.sectionServices": "Suggested services",
      "copilot.sectionParts": "Suggested parts",
      "copilot.sectionTechnicians": "Recommended technicians",
      "copilot.partNeedsTaskTitle": "Choose a task for this part",
      "copilot.partNeedsTaskHelp": "Select the task this part should be added to.",
      "copilot.selectTask": "Select a task",
      "copilot.noTasksAvailable": "Create a task first to add parts to it.",
      "copilot.addFailed": "Could not add the suggestion",
      "lossWarning.title": "Loss check required",
      "lossWarning.headline": "The job cost exceeds its price. Please state a reason to continue.",
      "lossWarning.revenue": "Revenue",
      "lossWarning.cost": "Cost",
      "lossWarning.shortfall": "Shortfall",
      "lossWarning.reason": "Reason to continue",
      "lossWarning.reasonPlaceholder": "Type the reason for issuing this invoice at a loss",
      "lossWarning.reasonRequired": "A reason of 3 to 500 characters is required.",
      "lossWarning.confirm": "Issue invoice anyway",
      "lossWarning.costIncomplete": "Cost figures are still incomplete; profit or loss cannot be confirmed.",
      "lossWarning.saveFailed": "Could not issue the invoice",
      "copilot.evidence_one": "Suggested from {{count}} similar job card",
      "copilot.evidence_other": "Suggested from {{count}} similar job cards",
      "copilot.technicianLoad_one": "{{count}} open job",
      "copilot.technicianLoad_other": "{{count}} open jobs",
      "pw.riseAboveLast": "{{percent}}% above the last purchase price",
      "pw.lastPaid": "Last paid {{price}} — {{supplier}}, {{date}}",
      "pw.comparisonUnavailable": "Price comparison unavailable",
      "pw.priceRiseBadge": "Price rise",
      "pw.cheapestRecentSupplier": "Cheapest supplier in the last 90 days",
      "adm.applyPlan": "Apply a plan (optional)",
      "adm.noPlanManual": "— None (design manually) —",
      "change": "Change",
      "verifying": "Verifying…",
      "addNewFinding": "Add new finding",
      "submitFinding": "Submit finding",
      "svcCard.confirmAcceptService": "Do you accept this service?",
      "inspection.addPhoto": "Add photo",
      "inspection.photoAlt": "Inspection photo",
      "odometerReading": "Odometer Reading",
      "task": "Task",
      "cd.customSubscription": "Custom",
      "po.view": "View",
      "docDesign.type.job_card": "Job card",
      "docDesign.stampSection": "Branch stamp",
      "docDesign.stampChoose": "Choose stamp image (PNG)",
      "docDesign.stampHint": "A transparent background is recommended; up to 1 MB.",
      "docDesign.stampRemove": "Remove",
      "docDesign.stampPreviewAlt": "Stamp preview",
      "docDesign.stampTooLarge": "The stamp must be 1 MB or less.",
      "invBank.title": "Invoice bank details",
      "invBank.desc": "Control the bank account printed on your invoices.",
      "invBank.show": "Show bank details on invoices",
      "invBank.bankName": "Bank name (optional)",
      "invBank.accountHolder": "Account holder (optional)",
      "invBank.iban": "IBAN",
      "invBank.payoutNote": "When the IBAN is empty, invoices show the payout account on file.",
      "invBank.save": "Save bank details",
      "invBank.loadFailed": "Could not load the invoice bank details",
      "invBank.saved": "Invoice bank details saved",
      "invBank.saveFailed": "Could not save the invoice bank details",
      "invBank.scopeBranch": "This branch",
      "invBank.scopeCompany": "Company default",
      "invBank.inheritedNote": "This branch uses the company default.",
      "invBank.useCompany": "Use the company default",
      "mb.branch": "Branch",
      "mb.chooseBranch": "Choose a branch",
      "mb.lockedToJobCard": "Same branch as job card {{number}}",
      "mb.lockedToQuotation": "Same branch as quotation {{number}}",
      "mb.lockedToInvoice": "Same branch as invoice {{number}}",
      "mb.branchChip": "Branch: {{name}}",
      "mb.allBranches": "All branches",
      "mb.allBranchesHint": "View every branch together",
      "mb.signinPickTitle": "Choose the branch you will work in",
      "mb.signinPickHint": "You can switch any time from the top bar.",
      "mb.homeBranch": "Home branch",
      "mb.branchChangeResets": "Changing the branch clears the customer and the items.",
      "mb.assistantPickBranch": "Which branch is this for?",
      "mb.customerOtherBranch": "This customer belongs to another branch.",
      "stamp.title": "Branch stamp",
      "stamp.desc": "Upload your branch stamp. It prints on every document except payslips.",
      "stamp.hint": "A transparent background is recommended; PNG format up to 1 MB. Prints on every document except payslips.",
      "stamp.current": "Current stamp",
      "stamp.currentAlt": "Current branch stamp",
      "stamp.empty": "No stamp uploaded yet",
      "stamp.choose": "Choose stamp image (PNG)",
      "stamp.choosePrompt": "Choose a PNG file",
      "stamp.preview": "Preview",
      "stamp.previewAlt": "Stamp preview",
      "stamp.upload": "Upload stamp",
      "stamp.uploading": "Uploading…",
      "stamp.remove": "Remove stamp",
      "stamp.removing": "Removing…",
      "stamp.tooLarge": "The stamp must be 1 MB or less.",
      "stamp.pngOnly": "Only PNG files are supported.",
      "stamp.uploaded": "Branch stamp uploaded successfully",
      "stamp.removed": "Branch stamp removed successfully",
      "stamp.loadFailed": "Could not load the branch stamp",
      "stamp.uploadFailed": "Could not upload the branch stamp",
      "stamp.removeFailed": "Could not remove the branch stamp",
      "jc.printPdfFailed": "Could not generate the job card PDF",
      "perm.advanced.noEffect": "No effect today",
      "perm.warn.partial": "{{job}} will not work: missing {{keys}}",
      "perm.warn.lost": "The role will lose: {{job}}",
      "perm.warn.lostTitle": "Save changes that remove jobs?",
      "perm.warn.noUsers": "This role has no users",
      "perm.compare.title": "Compare roles and templates",
      "perm.compare.open": "Compare",
      "perm.compare.differencesOnly": "Show differences only",
      "perm.compare.group.templates": "Templates",
      "perm.compare.pickHint": "You can compare up to 3 items",
      "perm.compare.unsaved": "(unsaved)",
      "perm.compare.type.role": "Role",
      "perm.compare.type.template": "Template",
      "perm.compare.state.on": "On",
      "perm.compare.state.partial": "Partial",
      "perm.compare.state.off": "Off",
      "perm.compare.remove": "Remove",
      "perm.compare.differCount_one": "{{count}} job differs",
      "perm.compare.differCount_other": "{{count}} jobs differ",
      "perm.compare.added": "Added vs template",
      "perm.compare.removed": "Removed vs template",
      "perm.compare.same": "Identical to the template",
      "perm.reset.title": "Reset to template",
      "perm.reset.confirm": "Reset this role to the \"{{template}}\" template? Unsaved manual changes will be replaced. Confirm with Save afterwards.",
      "perm.template.jobCount_one": "Covers {{count}} job",
      "perm.template.jobCount_other": "Covers {{count}} jobs",
      "perm.template.start": "Start from this",
      "perm.template.reset": "Reset to this",
      "perm.create.pickTemplate": "Start from a template (optional)",
      "perm.create.blank": "Start blank",
      "perm.roles.noUsers": "No users",
      "perm.roles.userCount_one": "{{count}} user",
      "perm.roles.userCount_other": "{{count}} users",
      "perm.users.title": "Users in this role",
      "perm.users.none": "No users in this role",
      "sa.templateRole": "Template",
      "unsavedChanges": "Unsaved changes",
      "revert": "Discard",
      "sa.rolePermissionsHeading": "{{role}} Permissions",
      "sa.customRole": "Custom Role",
      "sa.rolePermissionCount_one": "{{count}} permission",
      "sa.rolePermissionCount_other": "{{count}} permissions",
      "sa.creatingRole": "Creating...",
      "sa.createRole": "Create Role",
      "sa.roleName": "Role name (English)",
      "sa.roleNamePlaceholder": "e.g., Supervisor, Technician",
      "sa.roleNameAr": "Role name (Arabic)",
      "sa.roleNameArPlaceholder": "The name shown in the Arabic interface",
      "sa.roleDescriptionPlaceholder": "Brief description of this role",
      "sa.afterCreatingRoleHint": "After creating the role, you can assign permissions to it.",
      "purchaseHistory": "Purchase History",
      "totalPurchases": "Total Purchases",
      "totalQuantity": "Total Quantity",
      "averageCost": "Average Cost",
      "lastPurchase": "Last Purchase",
      "noPurchaseHistory": "No purchase history",
      "noPurchaseHistoryDescription": "No purchases recorded for this part yet.",
      "viewDetails": "View Details",
      "enterNotes": "Enter notes",
      "create": "Create",
      // Unified status labels — single source of truth used by StatusBadge (statusConfig.js).
      // NOTE: written as flat dotted keys (not a nested object) because a later flat
      // `status: 'Status'` label key would otherwise clobber the nested block; i18next
      // resolves these via its flat-key fallback (ignoreJSONStructure).
      'status.draft': 'Draft',
      'status.received': 'Received',
      'status.diagnosis': 'Diagnosis',
      'status.pending_customer_approval': 'Pending Customer Approval',
      'status.waiting_parts': 'Waiting Parts',
      'status.in_progress': 'In Progress',
      'status.quality_check': 'Quality Check',
      'status.ready': 'Ready',
      'status.pending_payment': 'Pending Payment',
      'status.awaiting_payment': 'Awaiting Payment',
      'status.delivered_pending_payment': 'Delivered — Payment Pending',
      'status.delivered': 'Delivered',
      'status.closed': 'Closed',
      'status.on_hold': 'On Hold',
      'status.cancelled': 'Cancelled',
      'status.pending': 'Pending',
      'status.completed': 'Completed',
      'status.approved': 'Approved',
      'status.rejected': 'Rejected',
      'status.partially_approved': 'Partially Approved',
      'status.qc_passed': 'QC Passed',
      'status.qc_failed': 'QC Failed',
      'status.under_qc': 'Under QC',
      'status.sent': 'Sent',
      'status.paid': 'Paid',
      'status.partially_paid': 'Partially Paid',
      'status.unpaid': 'Unpaid',
      'status.overdue': 'Overdue',
      'status.expired': 'Expired',
      'status.converted': 'Converted',
      'status.active': 'Active',
      'status.inactive': 'Inactive',
      'status.pending_approval': 'Pending Approval',
      'status.accepted_by_admin': 'Accepted by Admin',
      'status.installed': 'Installed',
      'status.partial': 'Partially Paid',
      'claimStatus.intake': 'Intake',
      'claimStatus.report_attached': 'Report Attached',
      'claimStatus.estimate_received': 'Estimate Received',
      'claimStatus.submitted_to_insurer': 'Submitted to Insurer',
      'claimStatus.approved': 'Approved',
      'claimStatus.partial': 'Partial Approval',
      'claimStatus.rejected': 'Rejected',
      'claimStatus.surveyor_inspected': 'Surveyor Inspected',
      'claimStatus.in_repair': 'In Repair',
      'claimStatus.supplementary_approval': 'Supplementary Approval',
      'claimStatus.supplementary_approved': 'Supplementary Approved',
      'claimStatus.completed': 'Completed',
      'claimStatus.surveyor_verified': 'Surveyor Verified',
      'claimStatus.billed_to_insurer': 'Billed to Insurer',
      'claimStatus.settled': 'Settled',
      'insuranceClaim.title': 'Insurance Claim',
      'insuranceClaim.subtitle': 'Capture claim details for this job card.',
      'insuranceClaim.loadFailed': 'Failed to load insurance claim',
      'insuranceClaim.saved': 'Insurance claim saved',
      'insuranceClaim.saveFailed': 'Failed to save insurance claim',
      'insuranceClaim.payerType': 'Payer Type',
      'insuranceClaim.payerTypeInsurerCashless': 'Insurer Cashless',
      'insuranceClaim.payerTypeCustomerCash': 'Customer Cash',
      'insuranceClaim.insurer': 'Insurer',
      'insuranceClaim.selectInsurer': 'Select insurer',
      'insuranceClaim.policyNumber': 'Policy Number',
      'insuranceClaim.claimNumber': 'Claim Number',
      'insuranceClaim.najmReportNo': 'Najm Report No.',
      'insuranceClaim.faultPercent': 'Fault %',
      'insuranceClaim.taqdeerReportNo': 'Taqdeer Report No.',
      'insuranceClaim.approvalReference': 'Approval Reference',
      'insuranceClaim.waitingInsurerApproval': 'Waiting for insurer approval',
      'insuranceClaim.estimatePreparedNote': 'Estimate prepared for manual export — not sent to the customer.',
      'insuranceClaim.downloadEstimate': 'Download estimate (PDF)',
      'insuranceClaim.approvalRefPlaceholder': 'Insurer approval reference',
      'insuranceClaim.confirmApprovalStartRepair': 'Confirm approval & start repair',
      'insuranceClaim.approvalRecorded': 'Insurer approval recorded — repair can start',
      'insuranceClaim.approvalRefRequired': 'Enter the insurer approval reference',
      'insuranceClaim.approvedAmountOptional': 'Approved amount (optional)',
      'insuranceClaim.approvedAmountHint': 'Leave empty to approve the full estimate total',
      'insuranceClaim.estimateApproved': 'Estimate approved',
      'insuranceClaim.estimateApprovedNote': 'The insurer approved this estimate.',
      'insuranceClaim.estimateNo': 'Estimate No.',
      'insuranceClaim.invoices': 'Invoices',
      'insuranceClaim.invoiceDownloadFailed': 'Failed to download invoice',
      'insuranceClaim.estimateDownloadFailed': 'Failed to download estimate',
      'insuranceClaim.noClaim': 'No insurance claim found for this card',
      'insuranceClaim.surveyorName': 'Surveyor Name',
      'insuranceClaim.deductibleAmount': 'Deductible Amount',
      'insuranceClaim.liabilityPercent': 'Liability %',
      'insuranceClaim.totalLoss': 'Total Loss',
      'insuranceClaim.totalLossThreshold': 'Total Loss Threshold',
      'insuranceClaim.sumInsured': 'Sum Insured',
      'insuranceClaim.sumInsuredPayout': 'Sum Insured Payout',
      'insuranceClaim.iban': 'IBAN',
      'insuranceClaim.excludedItems': 'Excluded Items',
      'insuranceClaim.addExcludedItem': 'Add Item',
      'insuranceClaim.noExcludedItems': 'No excluded items',
      'insuranceClaim.itemType': 'Type',
      'insuranceClaim.itemDescription': 'Description',
      'insuranceClaim.itemAmount': 'Amount',
      'insuranceClaim.notes': 'Notes',
      'insuranceClaim.createClaim': 'Create Claim',
      'insuranceClaim.updateClaim': 'Update Claim',
      'insuranceClaim.lifecycleActions': 'Lifecycle Actions',
      'insuranceClaim.repairGated': 'Repair is blocked until the claim is approved and the surveyor has inspected the vehicle.',
      'insuranceClaim.exposure': 'Garage exposure: {{amount}} (actual cost above the approved amount)',
      'insuranceClaim.action.report_attached': 'Attach report',
      'insuranceClaim.action.estimate_received': 'Estimate received',
      'insuranceClaim.action.send_estimate': 'Send estimate to insurer',
      'insuranceClaim.action.decision': 'Record insurer decision',
      'insuranceClaim.action.surveyor_inspect': 'Surveyor inspected',
      'insuranceClaim.action.start_repair': 'Start repair',
      'insuranceClaim.action.request_supplementary': 'Request supplementary',
      'insuranceClaim.action.mark_completed': 'Mark completed',
      'insuranceClaim.action.approve_supplementary': 'Approve supplementary',
      'insuranceClaim.action.resume_repair': 'Resume repair',
      'insuranceClaim.action.surveyor_verify': 'Surveyor verified',
      'insuranceClaim.action.submit': 'Submit',
      'insuranceClaim.action.confirm': 'Proceed with this action?',
      'insuranceClaim.action.done': 'Claim updated',
      'insuranceClaim.action.failed': 'Action failed',
      'insuranceClaim.action.fillRequired': 'Please fill the required fields',
      'insuranceClaim.field.channel': 'Channel (email/portal/manual)',
      'insuranceClaim.field.reference': 'Reference',
      'insuranceClaim.field.decision': 'Decision',
      'insuranceClaim.field.approval_reference': 'Approval reference',
      'insuranceClaim.field.approved_amount': 'Approved amount',
      'insuranceClaim.field.rejected_reason': 'Rejection reason',
      'insuranceClaim.field.surveyor_name': 'Surveyor name',
      'insuranceClaim.field.notes': 'Notes',
      'insuranceClaim.field.explanation': 'Explanation',
      'insuranceClaim.field.requested_amount': 'Requested amount',
      'insuranceClaim.field.selectOption': 'Select…',
      'insuranceClaim.decision.approved': 'Approved',
      'insuranceClaim.decision.partial': 'Partial',
      'insuranceClaim.decision.rejected': 'Rejected',
      // Priority labels — flat dotted keys for the same reason as status.* above.
      'priority.low': 'Low',
      'priority.normal': 'Normal',
      'priority.medium': 'Medium',
      'priority.high': 'High',
      'priority.urgent': 'Urgent',
      // Payment methods
      paymentMethods: {
        cash: 'Cash',
        card: 'Card',
        bank_transfer: 'Bank Transfer',
        online: 'Online',
        pos: 'POS',
        check: 'Check',
        cheque: 'Cheque',
        mobile_payment: 'Mobile Payment',
        myfatoorah: 'MyFatoorah',
        tamara: 'Tamara',
        tabby: 'Tabby',
        manual: 'Manual',
        return: 'Return',
        other: 'Other',
        // Payment-receipt redesign: an invoice payment funded from an existing
        // customer receipt's advance credit (invoiceController.recordPayment's
        // receipt leg) — no cash actually moved here, it moved when the
        // receipt itself was paid.
        receipt_credit: 'Customer Receipt Credit',
        // Generic "paid via a payment receipt" label — used as the fallback
        // when a payment's own method column doesn't map to a more specific
        // entry above.
        receipt: 'Payment Receipt',
      },
      // Issue report types
      issueTypes: {
        quality_issue: 'Quality Issue',
        missing_part: 'Missing Part',
        wrong_part: 'Wrong Part',
        damage: 'Damage',
        additional_work: 'Additional Work',
        customer_approval: 'Customer Approval',
        assistance: 'Assistance Request',
        other: 'Other',
      },
      // Job card activity history messages
      history: {
        activityHistory: 'Activity History',
        noActivity: 'No activity history available yet.',
        created: 'Job card created',
        statusChange: 'Status changed from "{{from}}" to "{{to}}"',
        partAdded: 'Added part: {{part}} (Qty: {{qty}})',
        partRemoved: 'Removed part: {{part}}',
        serviceAdded: 'Added service: {{service}}',
        serviceRemoved: 'Removed service: {{service}}',
        quotationGenerated: 'Generated quotation {{number}} ({{amount}} SAR)',
        quotationApproved: 'Customer approved quotation {{number}} ({{amount}} SAR)',
        invoiceGenerated: 'Generated invoice {{number}} - {{status}} ({{amount}} SAR)',
        invoicePaid: 'Invoice {{number}} marked as paid ({{amount}} SAR)',
        paymentReceived: 'Payment received: {{amount}} SAR ({{method}}) - Total paid: {{totalPaid}} SAR, Remaining: {{remaining}} SAR',
        assignedTo: 'Assigned to {{name}}',
        unassignedFrom: 'Unassigned from {{name}}',
        detailsUpdated: 'Job details updated',
        partApproved: 'Approved part: {{part}}',
        partRejected: 'Rejected part: {{part}}',
        taskCreated: 'Created task: {{task}}',
        taskAssigned: 'Assigned task "{{task}}" to {{mechanic}}',
        taskStarted: 'Started task: {{task}}',
        taskCompleted: 'Completed task: {{task}}',
        qcPassed: 'QC passed: {{task}}',
        qcFailed: 'QC failed: {{task}}',
        serviceAddedToTask: 'Added service "{{service}}" to {{task}} ({{price}} SAR)',
        serviceCompleted: 'Completed service: {{service}}',
        partAddedToTask: 'Added part "{{part}}" (Qty: {{qty}}) to {{task}}',
        partInstalled: 'Installed part: {{part}} (Qty: {{qty}})',
        findingSubmitted: 'Submitted finding: {{description}} ({{severity}})',
        findingApproved: 'Approved finding: {{description}}',
        findingRejected: 'Rejected finding: {{description}}',
        userAssigned: 'Assigned user: {{user}}',
        quotationApprovedByCustomer: 'Quotation {{number}} approved by customer',
        paymentReceivedForInvoice: 'Payment received for invoice {{number}} via {{method}} ({{amount}} SAR)',
        invoicePaidShort: 'Invoice {{number}} paid ({{amount}} SAR)',
      },
      theme: {
        light: 'Light mode',
        dark: 'Dark mode',
      },
      navMenu: 'Menu',
      noJobsHere: 'No jobs',
      collapseMenu: 'Collapse menu',
      expandMenu: 'Expand menu',
      // Pin/unpin, not expand/collapse: an unpinned menu still opens on hover,
      // so what the button really changes is whether it STAYS open.
      'nav.pinMenu': 'Keep menu open',
      'nav.unpinMenu': 'Collapse menu',
      commandPlaceholder: 'Search jobs, plates, customers, pages…',
      noResultsFound: 'No results found',
      // Palette fallbacks. A typed QUESTION never matches a job number or a
      // plate, so the palette offers the two places that can answer one.
      'cmd.needHelp': 'Looking for an answer?',
      'cmd.askAi': 'Ask the assistant: “{{query}}”',
      'cmd.searchManual': 'Search the manual for “{{query}}”',
      board: {
        view: 'Board',
        listView: 'List',
        new: 'New',
        diagnosis: 'Diagnosis',
        inService: 'In Service',
        qc: 'Quality Check',
        ready: 'Ready',
        payment: 'Payment',
        done: 'Finished',
      },
      login: {
        welcomeBack: 'Welcome Back!',
        otpSubtitle: 'Enter your mobile number to receive an OTP',
        mobileLogin: 'Mobile Login',
        adminLogin: 'Admin Login',
        mobileNumber: 'Mobile Number',
        phoneHint: 'Enter without leading zero (e.g., 501234567 or 0501234567)',
        sendOtp: 'Send OTP',
        sendingOtp: 'Sending OTP...',
        enterOtp: 'Enter OTP',
        otpSentTo: 'If this number is registered, we sent a code to',
        change: 'Change',
        verificationCode: 'Verification Code',
        verifyLogin: 'Verify & Login',
        verifying: 'Verifying...',
        resendOtp: 'Resend OTP',
        resendIn: 'Resend in {{seconds}}s',
        otpAttemptsLeft_one: '{{count}} attempt remaining',
        otpAttemptsLeft_other: '{{count}} attempts remaining',
        otpLockedTitle: 'Too many incorrect codes',
        otpLockedIn: 'Try again in {{time}}',
        otpSentWhatsapp: 'If this number is registered, the code is on its way on WhatsApp.',
        checkWhatsapp: 'Check your WhatsApp',
        checkWhatsappHint: 'If this number is registered, your login code is on its way as a WhatsApp message.',
        otpNotReceivedHint: "Didn't get a code? Check that the number above is correct and registered with your workshop, or tap Change.",
        sendSmsInstead: 'Send by SMS instead',
        adminAccess: 'Admin Access',
        adminSubtitle: 'Secure login for Super Administrators',
        emailAddress: 'Email Address',
        password: 'Password',
        signIn: 'Sign In',
        signingIn: 'Signing In...',
        authFailed: 'Authentication Failed',
        enterPhone: 'Please enter your mobile number',
        enterOtpError: 'Please enter the OTP',
        otpSent: 'If this number is registered, a code is on its way.',
        loginSuccess: 'Login successful!',
        copyright: '© {{year}} Tzamun Arabia IT Co. All rights reserved.',
        heroTitle: 'Run your garage with confidence',
        heroSubtitle:
          'MotorMind brings job cards, inventory, quotations and payments together in one intelligent workshop platform.',
        heroPoint1: 'Real-time job tracking',
        heroPoint2: 'ZATCA-compliant invoicing',
        heroPoint3: 'Customer approvals via SMS',
      },

      // Navigation
      dashboard: 'Dashboard',
      jobCards: 'Job Cards',
      customers: 'Customers',
      vehicles: 'Vehicles',
      users: 'Users',
      invoices: 'Invoices',
      quotationsEstimates: 'Quotations / Estimates',
      'qe.tabQuotations': 'Quotations',
      'qe.tabEstimates': 'Estimates',
      'qe.serial': 'Estimate No.',
      'qe.version': 'Version',
      'qe.insurer': 'Insurer',
      'qe.vehicle': 'Vehicle',
      'qe.approvedAmount': 'Approved Amount',
      'qe.approvalRef': 'Approval Ref.',
      'qe.download': 'Download PDF',
      'qe.openJobCard': 'Open job card',
      'qe.empty': 'No estimates yet',
      'qe.statusPrepared': 'Prepared',
      permissions: 'Permissions',
      reports: 'Reports',
      logout: 'Logout',
      printJobCard: 'Print job card',
      'exp.title': 'Other Expenses',
      'exp.note': 'Recorded for tracking only — not included in the customer invoice.',
      'exp.total': 'Total',
      'exp.add': 'Add expense',
      'exp.description': 'Description',
      'exp.descriptionPlaceholder': 'e.g. Tow truck, outsourced welding',
      'exp.amount': 'Amount',
      'exp.notes': 'Notes (optional)',
      'exp.attach': 'Attach receipt (image/PDF)',
      'exp.view': 'Receipt',
      'exp.empty': 'No expenses recorded yet.',
      'exp.added': 'Expense recorded',
      'exp.addFailed': 'Could not add expense',
      'exp.fillFields': 'Enter a description and a valid amount',
      'exp.deleteConfirm': 'Delete this expense?',
      'exp.deleteFailed': 'Could not delete expense',
      'session.expiredIdle': 'Signed out due to 30 minutes of inactivity',
      companies: 'Companies',
      subscriptions: 'Subscriptions',
      allUsers: 'All Users',
      configurations: 'Configurations',

      // Insurance companies
      'ins.nav': 'Insurance Companies',
      'ins.title': 'Insurance Companies',
      'ins.subtitle': '{{count}} companies, {{active}} active',
      'ins.add': 'Add Company',
      'ins.addTitle': 'Add Insurance Company',
      'ins.editTitle': 'Edit Insurance Company',
      'ins.name': 'Name',
      'ins.nameAr': 'Arabic Name',
      'ins.vatNumber': 'VAT Number',
      'ins.crNumber': 'CR Number',
      'ins.address': 'Address',
      'ins.buildingNumber': 'Building Number',
      'ins.district': 'District',
      'ins.city': 'City',
      'ins.postalCode': 'Postal Code',
      'ins.buildingNumberInvalid': 'Building number must be exactly 4 digits',
      'ins.postalCodeInvalid': 'Postal code must be exactly 5 digits',
      'ins.contactEmail': 'Contact Email',
      'ins.contactPhone': 'Contact Phone',
      'ins.defaultPaymentTerms': 'Default Payment Terms',
      'ins.agreedLaborRate': 'Agreed Labor Rate',
      'ins.partsPricingBasis': 'Parts Pricing Basis',
      'ins.approvalLimit': 'Approval Limit',
      'ins.status': 'Status',
      'ins.active': 'Active',
      'ins.inactive': 'Inactive',
      'ins.actions': 'Actions',
      'ins.edit': 'Edit',
      'ins.deactivate': 'Deactivate',
      'ins.save': 'Save Company',
      'ins.saving': 'Saving...',
      'ins.created': 'Insurance company created',
      'ins.updated': 'Insurance company updated',
      'ins.deactivated': 'Insurance company deactivated',
      'ins.loadFailed': 'Failed to load insurance companies',
      'ins.saveFailed': 'Failed to save insurance company',
      'ins.deactivateFailed': 'Failed to deactivate insurance company',
      'ins.confirmDeactivate': 'Deactivate {{name}}?',
      'ins.emptyTitle': 'No insurance companies',
      'ins.emptyDesc': 'Create the first insurer record for this garage.',
      'ins.notSet': 'Not set',
      'ins.accessDenied': 'Access denied',
      'ins.accessDeniedDesc': 'You do not have permission to manage insurance companies.',

      // Header
      welcomeBack: 'Welcome back',
      staffView: 'Staff View',
      adminView: 'Admin View',

      // Common
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      add: 'Add',
      search: 'Search',
      filter: 'Filter',
      export: 'Export',
      import: 'Import',
      submit: 'Submit',
      submitting: 'Submitting...',
      close: 'Close',
      confirm: 'Confirm',
      yes: 'Yes',
      no: 'No',
      loading: 'Loading...',
      noData: 'No data available',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      info: 'Information',

      // Forms
      time: 'Time',

      // Vehicle
      vehicleNotSaudiRegistered: 'Vehicle not Saudi Registered',
      countryOfRegistration: 'Country of Registration',
      enterPlateNumberFreeText: 'Enter plate number',
      egDubaiUAE: 'e.g. Dubai, UAE',
      make: 'Make',
      vinPlaceholder: 'Vehicle VIN — for exact OEM part numbers',
      vinHelpMissing: 'No VIN on the vehicle profile — enter it for accurate OEM part numbers.',

      // Job Card
      jobCard: 'Job Card',
      jobNumber: 'Job Number',
      mechanic: 'Technician',
      partsUsed: 'Parts Used',
      laborCost: 'Labor Cost',
      partsCost: 'Parts Cost',

      // Language
      language: 'Language',
      english: 'English',
      arabic: 'العربية',

      // Dashboard
      quickActions: 'Quick Actions',
      generateInvoice: 'Generate Invoice',
      addCustomer: 'Add Customer',
      createJobCard: 'Create Job Card',
      creating: 'Creating',
      recentActivity: 'Recent Activity',
      jobsByStatus: 'Jobs by Status',
      totalCustomers: 'Total Customers',
      totalRevenue: 'Total Revenue',
      activeJobs: 'Active Jobs',
      // AI Assistant
      aiAssistant: 'AI Assistant',
      aiAssistantTitle: 'AI Diagnostic Assistant',
      aiAssistantSubtitle: 'Ask about error codes, part installation, or symptoms for this vehicle',
      errorCode: 'Error / DTC code',
      errorCodePlaceholder: 'e.g. P0301',
      partInstall: 'Part install / replace',
      partInstallPlaceholder: 'e.g. front brake pads, ignition coil',
      symptomIssue: 'Sound / symptom',
      symptomPlaceholder: 'Describe the noise or behavior…',
      freeFormQuestion: 'Other question',
      freeFormPlaceholder: 'Ask anything about this vehicle…',
      searchWebForLatest: 'Search the web for latest recalls / TSBs / specs',
      askAssistant: 'Ask Assistant',
      aiAnalyzing: 'Analyzing… this can take up to ~20 seconds',
      // AI Diagnosis Copilot — animated progress phases + TzamunAI branding
      aiPhaseThinking: 'Thinking…',
      aiPhaseContext: 'Gathering vehicle context…',
      aiPhaseWeb: 'Searching the web for the latest bulletins…',
      aiPhaseParts: 'Cross-referencing parts with your inventory…',
      aiPhaseBuilding: 'Building the structured diagnosis…',
      aiPoweredBy: 'Powered by TzamunAI',
      aiDisclaimer: 'AI guidance — verify critical specs (torque, part numbers) against the OEM service manual.',
      aiCodeNotApplicable: 'This code does not belong to this vehicle',
      aiVehicleDetailsHint: 'These details are missing from the vehicle profile. Add them for a more accurate diagnosis.',
      previousQueries: 'Previous queries',
      noPreviousQueries: 'No previous queries yet',
      provideAtLeastOne: 'Fill at least one field',
      aiRequestFailed: 'AI request failed. Please try again.',
      // AI Diagnosis Copilot — structured card renderer (P2)
      aiReasoningAbout: 'Reasoning about',
      aiUseJobCardContext: 'Use job-card context',
      aiCauses: 'Likely causes',
      aiDiagnosticSteps: 'Diagnostic steps',
      aiSuggestedParts: 'Suggested parts',
      aiSuggestedServices: 'Suggested services',
      aiSpecsToVerify: 'Specs to verify',
      aiNotes: 'Notes',
      aiWebSources: 'Web sources',
      aiSearchedWeb: 'searched web',
      aiHowToConfirm: 'How to confirm',
      aiProbability: 'Probability',
      aiConfidence: 'Confidence',
      aiSeverity: 'Severity',
      aiProbabilityHigh: 'High',
      aiProbabilityMedium: 'Medium',
      aiProbabilityLow: 'Low',
      aiSeverityCritical: 'Critical',
      aiSeverityHigh: 'High',
      aiSeverityMedium: 'Medium',
      aiSeverityLow: 'Low',
      aiMustVerifyOem: 'Must verify against OEM',
      aiVerifyOem: 'Verify OEM part number',
      aiInStock: 'In stock',
      aiOutOfStock: 'Out of stock — order',
      aiNotInCatalog: 'Not in catalog',
      aiPartNumber: 'P/N',
      aiAiHint: 'AI hint — verify',
      aiPartNumberSource: 'part number source',
      aiAvgCost: 'Avg cost',
      aiSellingPrice: 'Price',
      aiThinking: 'Thinking…',
      addFromCatalog: 'From catalog',
      addCustomPart: 'Custom',
      aiEstLaborHours: 'est. labor',
      aiHours: 'h',
      aiQty: 'Qty',
      aiAcceptSelected: 'Accept selected → create findings',
      aiAcceptSelectedCount: 'Accept {{count}} selected → create findings',
      aiApplied: 'Findings created',
      aiAcceptSuccess: 'Created {{count}} pending finding(s) for review',
      aiNothingSelected: 'Select at least one item to accept',
      aiRegenerate: 'Regenerate',
      aiFollowUp: 'Follow-up',
      aiFollowUpPlaceholder: 'Ask a follow-up about this diagnosis…',
      aiSend: 'Send',
      aiCopy: 'Copy',
      aiCopied: 'Copied to clipboard',
      aiCopyFailed: 'Could not copy',
      aiAnalyzingShort: 'Analyzing…',
      aiBuildingDiagnosis: 'Building structured diagnosis…',
      aiSummary: 'Summary',
      aiNoStructuredData: 'No structured diagnosis — showing notes.',

      // Dashboard — enhanced
      totalJobs: 'Total Jobs',
      pendingPayments: 'Pending Payments',
      revenueTrend: 'Revenue Trend',
      last6Months: 'Last 6 months',
      shortcuts: 'Shortcuts',
      quickLaunch: 'Quick Launch',
      viewAll: 'View all',
      needsAttention: 'Needs Attention',
      allCaughtUp: 'All caught up — nothing needs attention.',
      noRevenueData: 'No revenue recorded yet',
      "dash.allBranches": 'All branches',
      "dash.customize.edit": 'Arrange',
      "dash.customize.done": 'Done',
      "dash.customize.reset": 'Reset to default',
      "dash.customize.saved": 'Your dashboard has been saved',
      "dash.customize.saveFailed": "Couldn't save your dashboard — try again",
      "dash.customize.resetDone": 'Dashboard restored to the default arrangement',
      "dash.customize.remove": 'Remove this card',
      "dash.customize.dragHint": 'Drag to reorder',
      "dash.customize.addTitle": 'Widgets',
      "dash.customize.allAdded": 'Every card is already on your dashboard',
      "dash.customize.addWidget": 'Add',
      "dash.customize.placed": 'Added',
      "dash.customize.emptyGrid": 'Your dashboard is empty — drag a widget here, or pick one from the panel',
      "dash.customize.resizeHint": 'Drag to resize',
      "dash.ask.title": 'Ask TzamunAI',
      "dash.ask.placeholder": 'Ask about a job card, a customer, or your numbers…',
      "dash.ask.send": 'Ask',
      "dash.ask.hint": 'Press Enter to open the assistant with your question',
      "dash.customize.widthThird": 'Third',
      "dash.customize.widthHalf": 'Half',
      "dash.customize.widthTwoThirds": 'Two-thirds',
      "dash.customize.widthFull": 'Full width',
      "dash.widget.attention": 'Needs attention',
      "dash.widget.pendingApprovals": 'Pending approvals',
      "dash.pendingApprovals.title": 'Waiting for your approval',
      "dash.pendingApprovals.viewAll": 'View all',
      "dash.pendingApprovals.partial": 'Some requests may not appear',
      "dash.pendingApprovals.empty": 'No requests awaiting your approval',
      "dash.pendingApprovals.loadFailed": 'Could not load pending approvals',
      "dash.pendingApprovals.retry": 'Retry',
      "dash.pendingApprovals.expand": 'Show pending approvals',
      "dash.pendingApprovals.collapse": 'Hide pending approvals',
      "dash.pendingApprovals.chip.discount": 'Discounts',
      "dash.pendingApprovals.chip.purchaseInvoice": 'Purchase invoices',
      "dash.widget.kpis": 'Key figures',
      "dash.widget.setupGuide": 'Setup guide',
      "dash.widget.branchBreakdown": 'Branch breakdown',
      "dash.widget.revenueTarget": 'Monthly revenue target',
      greetingMorning: 'Good morning',
      greetingAfternoon: 'Good afternoon',
      greetingEvening: 'Good evening',
      partsLowOnStock: 'parts low on stock',
      awaitingCollection: 'awaiting collection',

      // Job Cards
      jobDetails: 'Job Details',
      selectMechanic: 'Select Technician',
      taskDescription: 'Task Description',
      estimatedTime: 'Estimated Time',
      actualTime: 'Actual Time',
      tax: 'Tax',
      grandTotal: 'Grand Total',
      dueDate: 'Due Date',
      startDate: 'Start Date',
      completionDate: 'Completion Date',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      jobType: 'Job Type',
      regularService: 'Regular Service',
      quickService: 'Quick Service',
      convertToRegular: 'Convert to Regular Service',
      convertToRegularConfirm: 'Are you sure you want to convert this Quick Service job to a Regular Service? This will enable quotation workflow.',
      quickServiceNote: 'Quick Service - No quotation required. Generate invoice directly when ready.',
      sendInvoiceToCustomer: 'Send Invoice to Customer',
      recordPaymentManually: 'Record Payment Manually',
      invoiceSentSuccessfully: 'Invoice sent to customer successfully',
      paymentRecordedSuccessfully: 'Payment recorded successfully',
      selectJobType: 'Select Job Type',
      selectJobTypeDescription: 'Choose the type of service for this job card',
      regularServiceDescription: 'Standard workflow with quotation and customer approval',
      regularServiceFeature1: 'Send quotation to customer',
      regularServiceFeature2: 'Wait for customer approval',
      regularServiceFeature3: 'Generate invoice after completion',
      quickServiceDescription: 'Fast service for on-site customers without quotation',
      quickServiceFeature1: 'No quotation required',
      quickServiceFeature2: 'Direct invoice generation',
      quickServiceFeature3: 'Can convert to regular if needed',
      fullCheckService: 'Full Check',
      fullCheckServiceDescription: 'A complete inspection of the vehicle, with a report the customer can download.',
      fullCheckServiceFeature1: 'Choose the checklist the workshop uses',
      fullCheckServiceFeature2: 'Every point recorded, with photos where they help',
      fullCheckServiceFeature3: 'A PDF report the customer downloads from a link',
      insuranceJob: 'Insurance',
      insuranceJobDescription: 'Insurer-paid repair with claim tracking and B2B invoicing.',
      insuranceJobFeature1: 'Pick the insurer & capture claim details',
      insuranceJobFeature2: 'Tag each task as insurer- or customer-paid',
      insuranceJobFeature3: 'Estimate, final report & B2B tax invoice',
      fast: 'FAST',
      skipped: 'Skipped',

      // Customers
      newCustomer: 'New Customer',
      customerDetails: 'Customer Details',
      company: 'Company',
      idNumber: 'ID Number',
      country: 'Country',
      postalCode: 'Postal Code',
      customerType: 'Customer Type',
      individual: 'Individual',
      business: 'Business',

      // Vehicles
      newVehicle: 'New Vehicle',
      vehicleDetails: 'Vehicle Details',
      mileage: 'Mileage',
      engineNumber: 'Engine Number',
      registrationDate: 'Registration Date',
      lastServiceDate: 'Last Service Date',
      nextServiceDate: 'Next Service Date',
      motorcycle: 'Motorcycle',

      // Parts
      newPart: 'New Part',
      partDetails: 'Part Details',
      supplier: 'Supplier',
      minStock: 'Minimum Stock',
      restock: 'Restock',
      lowStock: 'Low Stock',
      outOfStock: 'Out of Stock',
      inStock: 'In Stock',
      avgCost: 'Avg Cost',
      profit: 'Profit',
      bulkUpload: 'Bulk Upload',
      newPurchase: 'New Purchase',
      manageInventory: 'Manage your parts inventory and track costs',
      allCategories: 'All Categories',
      partCategories: 'Part Categories',
      // ---- multi-location inventory (loc.*) ----
      inventoryLocations: 'Warehouses',
      'loc.pageTitle': 'Warehouses',
      'loc.pageSubtitle': 'Stores and warehouses in this branch. Stock lands in the default unless another is chosen.',
      'loc.addLocation': 'Add warehouse',
      'loc.colName': 'Name',
      'loc.colCode': 'Code',
      'loc.default': 'Default',
      'loc.inactive': 'Retired',
      'loc.retire': 'Retire',
      'loc.restore': 'Restore',
      'loc.editTitle': 'Edit warehouse',
      'loc.newTitle': 'New warehouse',
      'loc.fieldName': 'Name',
      'loc.fieldNameAr': 'Name (Arabic)',
      'loc.fieldCode': 'Code',
      'loc.codeHint': 'Short code, e.g. MAIN or OUT',
      'loc.makeDefault': 'Make this the default warehouse',
      'loc.saved': 'Warehouse saved',
      'loc.saveFailed': 'Could not save the warehouse',
      'loc.loadFailed': 'Could not load warehouses',
      'loc.nameAndCodeRequired': 'Name and code are both required',
      'loc.defaultChanged': 'Default warehouse changed',
      'loc.emptyTitle': 'No warehouses yet',
      'loc.emptyBody': 'Add one for each place you keep parts — a main store, an outside store, a van.',
      'loc.stockHere': 'Stock here',
      'loc.noStockHere': 'Nothing is stored here yet.',
      'loc.pickPlaceholder': 'Choose a warehouse',
      'loc.useDefault': 'Branch default',
      'loc.transferTitle': 'Move stock',
      'loc.transferAction': 'Move',
      'loc.transferDone': 'Stock moved',
      'loc.transferFailed': 'Could not move the stock',
      'loc.from': 'From',
      'loc.to': 'To',
      'loc.quantity': 'Quantity',
      'loc.notes': 'Notes',
      'loc.currentBreakdown': 'Where this part is now',
      'loc.noStockAnywhere': 'This part has no stock in any warehouse.',
      'loc.sameLocation': 'Pick two different warehouses.',
      'loc.availableHere': '{{count}} available here',
      'loc.overAvailable': 'Only {{count}} available at that warehouse',
      'loc.moveStock': 'Move stock',
      'loc.byLocation': 'By warehouse',
      'loc.destination': 'Receive into',
      'loc.unitOfMeasure': 'Unit',
      'loc.detailsSection': 'Details',
      'loc.totalOnHand': 'Total on hand',
      'loc.totalValue': 'Total value',
      'loc.value': 'Value',
      'loc.nothingToMove': 'This part has no stock in any warehouse, so there is nothing to move.',
      'loc.singleSourceHint': 'This part is only held in one warehouse, so the source is fixed.',
      'loc.viewDetails': 'Part details',
      'loc.inWarehouses': 'in {{count}} warehouses',
      // ---- stock reports (rpt.*) ----
      'rpt.tabStockOnHand': 'Stock on Hand',
      'rpt.tabStockValuation': 'Stock Valuation',
      'rpt.tabStockMovements': 'Stock Movements',
      'rpt.partNumber': 'Part #',
      'rpt.part': 'Part',
      'rpt.stockLines': 'Lines',
      'rpt.stockUnits': 'Units',
      'rpt.distinctParts': 'Distinct parts',
      'rpt.belowThreshold': 'Below threshold',
      'rpt.locations': 'Warehouses',
      'rpt.balanceAfter': 'Balance after',
      'rpt.performedBy': 'By',
      'rpt.movesIn': 'Units in',
      'rpt.movesOut': 'Units out',
      'rpt.valueIn': 'Value in',
      'rpt.valueOut': 'Value out',
      'rpt.positionReportNote': 'This report shows stock as it stands now, so the date range does not apply to it.',
      // Finance Completeness Task 1.8 — drawings cutover date + suspect-expenses report.
      'rpt.suspectExpensesTab': 'Suspect Expenses',
      'rpt.suspectExpensesDisclaimer': 'This is a hint list, not a finding. It never re-classes anything and has no action buttons here. The round-1,000 rule also matches ordinary rent, utilities and salary advances — review every row yourself before acting on it.',
      'rpt.suspectExpensesVendor': 'Payee',
      'rpt.suspectExpensesNote': 'Note',
      'rpt.suspectExpensesAmount': 'Amount',
      'rpt.suspectExpensesReasons': 'Why flagged',
      'rpt.suspectExpensesFlagged': 'Flagged',
      'rpt.suspectExpensesEmpty': 'No expenses before the cutover date matched the hint rules.',
      'rpt.suspectReason_partner_name': 'Matches a partner name',
      'rpt.suspectReason_keyword': 'Note mentions a drawing/withdrawal',
      'rpt.suspectReason_round_no_attachment': 'Round 1,000 amount, no attachment',
      'rpt.drawingsCutoverLabel': 'Drawings cutover date',
      'rpt.drawingsCutoverHelp': 'Scans expenses booked before this date for possible owner/partner drawings. History is never changed automatically.',
      'rpt.drawingsCutoverNotSet': 'Set a cutover date to scan expenses booked before it.',
      'rpt.drawingsCutoverSave': 'Save',
      'rpt.drawingsCutoverSaved': 'Cutover date saved',
      'rpt.drawingsCutoverSaveError': 'Could not save the cutover date',
      'rpt.move_in': 'In',
      'rpt.move_out': 'Out',
      'rpt.move_transfer': 'Transfer',
      'rpt.move_return': 'Return',
      'rpt.move_damage': 'Damage',
      'rpt.move_adjustment': 'Adjustment',
      'rpt.move_initial': 'Opening',
      manageCategoriesDescription: 'Manage part categories for your garage',
      addCategory: 'Add Category',
      editCategory: 'Edit Category',
      categoryName: 'Category Name',
      categoryNameArabic: 'Category Name (Arabic)',
      globalCategories: 'Global Categories',
      customCategories: 'Custom Categories',
      sharedAcrossAllGarages: 'Shared across all garages',
      noCustomCategories: 'No custom categories yet',
      clickAddToCreate: 'Click "Add Category" to create your first custom category',
      selectCategory: 'Select Category',
      
      // Purchase Invoices
      purchaseInvoices: 'Purchase Invoices',
      newPurchaseInvoice: 'New Purchase Invoice',
      purchaseInvoiceDetails: 'Purchase Invoice Details',
      invoiceFile: 'Invoice File (PDF/Image)',
      clickToUpload: 'Click to upload invoice file',
      viewFile: 'View File',
      items: 'Items',
      scanBarcode: 'Scan or enter SKU/Barcode',
      selectExistingPart: 'Select Existing Part',
      searchPartByName: 'Search part by name, number, or SKU...',
      noPartsFound: 'No parts found. Try different keywords or create a new part.',
      newPartCheckbox: 'New Part',
      shippingCost: 'Shipping Cost (SAR)',
      vatIncludedInCost: 'VAT included in cost',
      vatIncludedInCostDescription: 'For garages that cannot reclaim VAT',
      shipping: 'Shipping',
      postImmediately: 'Post immediately (update stock and costs now)',
      createAndPost: 'Create & Post',
      saveAsDraft: 'Save as Draft',
      enterSupplierName: 'Enter supplier name',
      unitCost: 'Unit Cost',
      posted: 'Posted',
      postInvoice: 'Post Invoice',
      bin: 'Bin',
      sellingPrice: 'Selling Price',
      margin: 'Margin',

      // Invoices
      newInvoice: 'New Invoice',
      invoiceDetails: 'Invoice Details',
      invoiceDate: 'Invoice Date',
      paymentDue: 'Payment Due',
      paymentMethod: 'Payment Method',
      cash: 'Cash',
      card: 'Card',
      online: 'Online',
      myfatoorah: 'MyFatoorah',
      tamara: 'Tamara',
      bankTransfer: 'Bank Transfer',
      notSpecified: 'Not Specified',
      searchInvoices: 'Search invoices...',
      allStatuses: 'All Statuses',
      allPaymentMethods: 'All Payment Methods',
      dateFrom: 'Date From',
      dateTo: 'Date To',
      invoicesFound: 'invoices found',
      filtersActive: 'filters active',
      clearFilters: 'Clear Filters',
      bank: 'Bank Transfer',
      partiallyPaid: 'Partially Paid',
      overdue: 'Overdue',
      amountPaid: 'Amount Paid',
      amountDue: 'Amount Due',
      balance: 'Balance',

      // Services
      newService: 'New Service',
      serviceDetails: 'Service Details',
      serviceCategory: 'Service Category',
      duration: 'Duration',
      hours: 'Hours',
      minutes: 'Minutes',

      // Users
      newUser: 'New User',
      userDetails: 'User Details',
      username: 'Username',
      confirmPassword: 'Confirm Password',
      inactive: 'Inactive',
      lastLogin: 'Last Login',

      // Reports
      salesReport: 'Sales Report',
      inventoryReport: 'Inventory Report',
      customerReport: 'Customer Report',
      mechanicReport: 'Technician Report',
      from: 'From',
      generate: 'Generate',
      download: 'Download',
      print: 'Print',

      // Common Actions
      update: 'Update',
      duplicate: 'Duplicate',
      archive: 'Archive',
      restore: 'Restore',
      approve: 'Approve',
      reject: 'Reject',
      send: 'Send',
      receive: 'Receive',
      transfer: 'Transfer',

      // Messages
      areYouSure: 'Are you sure?',
      deleteConfirm: 'Are you sure you want to delete this item?',
      cannotUndo: 'This action cannot be undone.',
      savedSuccessfully: 'Saved successfully',
      deletedSuccessfully: 'Deleted successfully',
      updatedSuccessfully: 'Updated successfully',
      errorOccurred: 'An error occurred',
      pleaseWait: 'Please wait...',
      processing: 'Processing...',

      // Validation
      required: 'This field is required',
      invalidEmail: 'Invalid email address',
      invalidPhone: 'Invalid phone number',
      passwordMismatch: 'Passwords do not match',
      minLength: 'Minimum length is',
      maxLength: 'Maximum length is',

      // Pagination
      showing: 'Showing',
      of: 'of',
      entries: 'entries',
      previous: 'Previous',
      next: 'Next',
      page: 'Page',

      // Filters
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      custom: 'Custom',
      apply: 'Apply',
      clear: 'Clear',
      reset: 'Reset',

      // Job Card Statuses
      received: 'Received',
      qualityCheck: 'Quality Check',
      ready: 'Ready',
      repair: 'Repair',
      approval: 'Approval',
      payment: 'Payment',
      pendingPayment: 'Pending Payment',
      onHold: 'On Hold',
      closed: 'Closed',

      // Hold Feature
      putJobCardOnHold: 'Put Job Card on Hold',
      reasonForHold: 'Reason for Hold',
      selectReason: 'Select a reason...',
      customReason: 'Custom Reason',
      enterCustomReason: 'Enter custom reason...',
      holdWarningMessage: 'This job card will be paused and marked as "On Hold". You can resume it later from the job card details.',
      putOnHold: 'Put on Hold',
      resumeJob: 'Resume Job',
      pleaseSelectReason: 'Please select or enter a reason',
      jobCardOnHold: 'Job Card On Hold',
      reason: 'Reason',
      since: 'Since',
      waitingForParts: 'Waiting for Parts',
      waitingCustomerParts: 'Waiting Customer to Provide Parts',
      customerRequest: 'Customer Request',
      technicalIssue: 'Technical Issue',
      awaitingExternalService: 'Awaiting External Service',
      partsOnOrder: 'Parts on Order',
      other: 'Other',

      // Table Headers
      createdAt: 'Created At',
      updatedAt: 'Updated At',

      // Error Messages & Toasts
      customerUpdatedSuccessfully: 'Customer updated successfully',
      customerCreatedSuccessfully: 'Customer created successfully',
      failedToCreateCustomer: 'Failed to create customer',
      failedToUpdateCustomer: 'Failed to update customer',
      vehicleCreatedSuccessfully: 'Vehicle created successfully',
      vehicleUpdatedSuccessfully: 'Vehicle updated successfully',
      failedToCreateVehicle: 'Failed to create vehicle',
      failedToUpdateVehicle: 'Failed to update vehicle',
      jobCardCreatedSuccessfully: 'Job card created successfully',
      failedToCreateJobCard: 'Failed to create job card',
      openJobWarning: {
        title: 'Vehicle already has an open job card',
        message:
          'This vehicle already has an open job card #{{jobNumber}} ({{status}}). Do you want to create another one anyway?',
        createAnyway: 'Create anyway',
      },
      partAddedSuccessfully: 'Part added successfully',
      failedToAddPart: 'Failed to add part',
      serviceCreatedSuccessfully: 'Service created successfully',
      serviceUpdatedSuccessfully: 'Service updated successfully',
      serviceDeletedSuccessfully: 'Service deleted successfully',
      failedToDeleteService: 'Failed to delete service',
      userCreatedSuccessfully: 'User created successfully',
      userUpdatedSuccessfully: 'User updated successfully',
      userDeletedSuccessfully: 'User deleted successfully',
      failedToDeleteUser: 'Failed to delete user',
      operationFailed: 'Operation failed',
      pdfDownloaded: 'PDF downloaded',
      failedToDownloadPDF: 'Failed to download PDF',
      invoiceDeletedSuccessfully: 'Invoice deleted successfully',
      failedToDeleteInvoice: 'Failed to delete invoice',
      cannotDeletePaidInvoice: 'Cannot delete paid invoice. Create a return invoice instead.',
      areYouSureDeleteInvoice: 'Are you sure you want to delete invoice',
      areYouSureDeleteService: 'Are you sure you want to delete this service?',
      areYouSureToggleUser: 'Are you sure you want to',
      activate: 'activate',
      deactivate: 'deactivate',
      thisUser: 'this user?',
      userActivated: 'User activated successfully',
      userDeactivated: 'User deactivated successfully',
      failedToUpdateStatus: 'Failed to update status',
      areYouSureDeleteUser: 'Are you sure you want to delete',
      thisWillDeactivateUser: 'This will deactivate the user.',
      'users.confirmActivate': 'Are you sure you want to activate this user?',
      'users.confirmDeactivate': 'Are you sure you want to deactivate this user?',
      'users.confirmDelete': 'Are you sure you want to delete {{name}}? This will deactivate the user.',

      // Form Placeholders
      enterName: 'Enter name',
      enterEmail: 'Enter email',
      enterPhone: 'Enter phone',
      enterAddress: 'Enter address',
      selectOption: 'Select option',
      searchCustomers: 'Search customers',
      searchVehicles: 'Search vehicles',
      searchParts: 'Search parts',

      // Customer Search Modal
      findCustomer: 'Find Customer',
      customerNameMobile: 'Customer (Name/Mobile)',
      vinChassis: 'VIN / Chassis',
      enterPlateNumber: 'Enter plate number...',
      enterVIN: 'Enter VIN or Chassis number...',
      enterCustomerNameOrMobile: 'Enter customer name or mobile number...',
      pleaseEnterSearchTerm: 'Please enter a search term',
      failedToSearchCustomers: 'Failed to search customers',
      searching: 'Searching...',
      noCustomerFound: 'No customer found',
      createNewCustomerProfile: 'Create New Customer Profile',

      // Modal Common
      editUser: 'Edit User',
      createNewUser: 'Create New User',
      editCustomer: 'Edit Customer',
      addNewCustomer: 'Add New Customer',
      updateCustomer: 'Update Customer',
      createCustomer: 'Create Customer',
      editVehicle: 'Edit Vehicle',
      viewVehicle: 'View Vehicle',
      edit: 'Edit',
      view: 'View',
      addNewVehicle: 'Add New Vehicle',
      updateVehicle: 'Update Vehicle',
      createVehicle: 'Create Vehicle',
      selectVehicle: 'Select Vehicle',
      noVehiclesFound: 'No vehicles found',
      addVehicleForCustomer: 'Add a vehicle for this customer',

      // Job Card Modal
      vehicleReception: 'Vehicle Reception',
      selectACustomerFirst: 'Select a customer first',
      receptionChecklist: 'Reception Checklist',
      mileageKM: 'Mileage (KM)',
      priority: 'Priority',

      // Part Modal
      addNewPart: 'Add New Part',
      editPart: 'Edit Part',
      sku: 'SKU (Stock Keeping Unit)',
      optional: 'Optional',
      partName: 'Part Name',
      description: 'Description',
      quantityInStock: 'Quantity in Stock',
      minimumStockLevel: 'Minimum Stock Level',
      costPrice: 'Cost Price',
      branch: 'Branch',
      addPart: 'Add Part',

      // Restock Modal
      restockPart: 'Restock Part',
      part: 'Part',
      currentStock: 'Current Stock',
      quantityToAdd: 'Quantity to Add',
      notes: 'Notes',
      addStock: 'Add Stock',

      // Assign Mechanic Modal
      manageAssignments: 'Manage Assignments',
      assignedMechanics: 'Assigned Technicians',
      availableMechanics: 'Available Technicians',
      searchMechanics: 'Search technicians...',
      noMechanicsAssigned: 'No technicians assigned yet',
      noMechanicsAvailable: 'No technicians available',

      // Job Cards List
      assignedUsers: 'Assigned Users',
      unassigned: 'Unassigned',
      manageAssignmentsTitle: 'Manage Assignments',

      // Table Headers - Common
      actions: 'Actions',
      status: 'Status',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      role: 'Role',

      // Table Headers - Parts
      price: 'Price',
      stock: 'Stock',
      compatibility: 'Compatibility',
      category: 'Category',
      partNumber: 'Part Number',
      partNumberPlaceholder: 'e.g. 04465-33450',

      // Table Headers - Vehicles
      vin: 'VIN',
      owner: 'Owner',
      year: 'Year',
      brandModel: 'Brand & Model',

      // Table Headers - Invoices
      invoiceNumber: 'Invoice #',
      date: 'Date',
      amount: 'Amount',
      paymentStatus: 'Payment Status',

      // Status Labels (for display)
      cancelled: 'Cancelled',
      delivered: 'Delivered',
      waiting_parts: 'Waiting Parts',
      waitingParts: 'Waiting Parts',
      in_progress: 'In Progress',

      // Services Table
      estTime: 'Est. Time',

      // Quotations Table
      quotationNumber: 'Quotation #',
      validUntil: 'Valid Until',

      // Invoice/Quotation Items
      qty: 'Qty',
      total: 'Total',

      // Payment Status
      paid: 'Paid',
      unpaid: 'Unpaid',
      partial: 'Partial',

      // Service Modal
      addNewService: 'Add New Service',
      editService: 'Edit Service',
      estMinutes: 'Est. Minutes',
      basePriceSAR: 'Base Price (SAR)',
      sellingPriceSAR: 'Selling Price (SAR)',
      costPriceSAR: 'Cost Price (SAR)',
      costPriceHint: 'What this service costs the garage. Used for profitability.',
      marginLabel: 'Margin',
      costIsMaterials: 'What does this cost represent?',
      costTypeLabour: 'Technician time (labour)',
      costTypeLabourEffect:
        'If hours are logged on the task, the real labour cost REPLACES this amount — the same hour is never counted twice.',
      costTypeMaterials: 'Parts & consumables (materials)',
      costTypeMaterialsEffect:
        'This amount is ALWAYS ADDED on top of the labour cost — e.g. the oil in an oil change.',
      costTypeNoHoursNote:
        'If nobody logs time on the task, your cost price is used exactly as entered — either way.',
      svcPartsIncluded: 'Price includes the parts (package)',
      svcPartsIncludedHint:
        "This service's parts are added to the job card at 0.00 — the customer pays the package price once. Parts still leave stock.",
      svcPartIncludedBadge: 'Included in package',
      createService: 'Create Service',

      // Permissions Page
      permissionManager: 'Permission Manager',
      customizeRolePermissions: 'Customize role permissions for your organization (Tenant-Level)',
      roles: 'Roles',
      admin: 'Admin',
      manager: 'Manager',
      receptionist: 'Receptionist',
      staff: 'Staff',

      // Reports Page
      reportsAnalytics: 'Reports & Analytics',
      customerInsights: 'Customer Insights',
      maximizeMetrics: 'Maximize metrics',
      profitAnalysis: 'Profit Analysis',
      marginsPerJob: 'Margins per job',
      performance: 'Performance',
      mechanicEfficiency: 'Technician efficiency',
      revenueReport: 'Revenue Report',
      dailyWeeklyMonthly: 'Daily, weekly, monthly',
      generateCustomReport: 'Generate Custom Report',
      dateRange: 'Date Range',
      reportType: 'Report Type',
      last7Days: 'Last 7 Days',
      last30Days: 'Last 30 Days',
      last3Months: 'Last 3 Months',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      thisYear: 'This Year',
      custom: 'Custom Range',
      fromDate: 'From',
      toDate: 'To',
      'sup.title': 'Suppliers',
      'sup.new': 'New Supplier',
      'sup.code': 'Supplier Code',
      'sup.pick': 'Pick a supplier…',
      'sup.edit': 'Edit Supplier',
      'sup.name': 'Supplier Name',
      'sup.nameAr': 'Supplier Name (Arabic)',
      'sup.contact': 'Contact Person',
      'sup.taxNumber': 'VAT Number',
      'sup.paymentTerms': 'Payment Terms',
      'sup.balance': 'Balance',
      'sup.invoiced': 'Total Invoiced',
      'sup.paid': 'Total Paid',
      'sup.credited': 'Credit Notes',
      'sup.invoices': 'Invoices',
      'sup.active': 'Active',
      'sup.inactive': 'Inactive',
      'sup.empty': 'No suppliers yet — they are created automatically from purchase invoices, or add one here.',
      'sup.saved': 'Supplier saved',
      'sup.statement': 'Statement of Account',
      'sup.sectionBasic': 'Basic information',
      'sup.sectionTax': 'Tax & legal',
      'sup.sectionAddress': 'National address',
      'sup.sectionBank': 'Bank details',
      'sup.sectionTerms': 'Terms & notes',
      'sup.supplierType': 'Supplier type',
      'sup.type.company': 'Company',
      'sup.type.individual': 'Individual',
      'sup.category': 'Category',
      'sup.category.parts': 'Parts',
      'sup.category.oils': 'Oils',
      'sup.category.tyres_batteries': 'Tyres & batteries',
      'sup.category.sublet': 'Sublet',
      'sup.category.utilities': 'Utilities',
      'sup.category.rent': 'Rent',
      'sup.category.tools': 'Tools',
      'sup.category.gov_fees': 'Government fees',
      'sup.category.other': 'Other',
      'sup.pickCategory': 'Select category…',
      'sup.crNumber': 'CR number',
      'sup.buildingNo': 'Building no.',
      'sup.street': 'Street',
      'sup.district': 'District',
      'sup.city': 'City',
      'sup.postalCode': 'Postal code',
      'sup.additionalNo': 'Additional no.',
      'sup.countryCode': 'Country',
      'sup.iban': 'IBAN',
      'sup.bankName': 'Bank',
      'sup.accountHolder': 'Account holder',
      'sup.creditLimit': 'Credit limit',
      'sup.terms': 'Payment terms',
      'sup.terms.cash': 'Cash',
      'sup.terms.net': 'Net {{n}} days',
      'sup.terms.custom': 'Custom',
      'sup.terms.customDays': '{{days}} days',
      'sup.customDays': 'Days',
      'sup.paymentTermsDays': 'Payment terms (days)',
      'sup.legacyTerms': 'Previous terms: {{terms}}',
      'sup.notes': 'Notes',
      'sup.supplier': 'Supplier',
      'reports.apAging': 'Payables aging',
      'reports.bySupplier': 'By supplier',
      'reports.totalAp': 'Total payables',
      'reports.overdueAp': 'Overdue payables',
      'reports.suppliersWithAp': 'Suppliers with a balance',
      'reports.oldestDays': 'Oldest (days)',
      'reports.bucket1_30': '1–30 days',
      'reports.bucket31_60': '31–60 days',
      'reports.bucket61_90': '61–90 days',
      'reports.bucket90plus': '90+ days',
      'sup.openingBalance': 'Opening Balance',
      'sup.closingBalance': 'Closing Balance',
      'sup.debit': 'Debit',
      'sup.credit': 'Credit',
      'sup.runningBalance': 'Balance',
      'sup.txType.invoice': 'Purchase Invoice',
      'sup.txType.payment': 'Payment',
      'sup.txType.credit_note': 'Credit Note',
      'sup.txType.expense': 'Expense',
      'sup.txType.refund_received': 'Refund received',
      'sup.supplierOwesYou': 'Due from supplier',
      'sup.stmtEmpty': 'No transactions in this period',
      'sup.profile': 'Supplier Profile',
      'sup.pay': 'Pay',
      'sup.searchRef': 'Search reference…',
      'sup.filteredNote': 'Filtered view — running balance hidden',
      revenue: 'Revenue',
      generateReport: 'Generate Report',
      exportCsv: 'Export CSV',
      noReportData: 'No data for selected range',
      noReportDataDesc: 'Try a different report type or date range.',

      // Report titles & descriptions (flat dotted keys)
      'reportTitle.revenue': 'Revenue Report',
      'reportDesc.revenue': 'Daily, weekly, monthly',
      'reportTitle.mechanicPerformance': 'Technician Performance',
      'reportDesc.mechanicPerformance': 'Completed jobs & efficiency',
      'reportTitle.popularServices': 'Popular Services',
      'reportDesc.popularServices': 'Most requested service types',
      'reportTitle.partsConsumption': 'Parts Consumption',
      'reportDesc.partsConsumption': 'Parts used and value',

      // Report table columns
      reportPeriod: 'Period',
      reportTotal: 'Total Revenue',
      reportInvoiceCount: 'Invoices',
      reportInvoices: 'invoices',
      reportMechanic: 'Technician',
      reportCompletedJobs: 'Completed Jobs',
      reportAvgHours: 'Avg. Hours',
      reportServiceName: 'Service',
      reportJobCount: 'Jobs',
      reportPartName: 'Part',
      reportPartNumber: 'Part No.',
      reportTotalQty: 'Qty Used',
      reportTotalValue: 'Total Value',
      reportSummaryRevenue: 'Total Revenue',
      reportMechanics: 'technicians',
      reportTotalJobs: 'total jobs',
      reportUnitsUsed: 'units used',

      // New report type titles & descriptions
      'reportTitle.jobProfitability': 'Job Profitability',
      'reportDesc.jobProfitability': 'Margin per job card',
      'reportTitle.inventoryValuation': 'Inventory Valuation',
      'reportDesc.inventoryValuation': 'Stock value at cost & sales price',
      'reportTitle.vatSummary': 'VAT Summary',
      'reportDesc.vatSummary': 'ZATCA VAT by month',

      // New report columns & summaries
      reportJobNumber: 'Job No.',
      reportCustomerName: 'Customer',
      reportRevenue: 'Revenue',
      reportPartsCost: 'Parts Cost',
      reportMargin: 'Margin',
      reportMarginPct: 'Margin %',
      reportTotalMargin: 'Total Margin',
      reportQtyInStock: 'In Stock',
      reportAvgCost: 'Avg. Cost',
      reportUnitPrice: 'Unit Price',
      reportPotentialProfit: 'Potential Profit',
      reportTotalInventoryAtCost: 'Total Inventory at Cost',
      reportTaxableAmount: 'Taxable Amount',
      reportVatAmount: 'VAT Amount',
      reportTotalVat: 'Total VAT',

      // Reports: export, table & PDF
      exportPdf: 'Export PDF',
      reportTableTitle: 'Details',
      'reportPdf.generated': 'Generated',
      'reportPdf.failed': 'Could not generate PDF. Please try again.',
      'reportPdf.truncated': '+{{count}} more rows (see CSV export)',

      // Reports: KPI cards
      'reportKpi.totalRevenue': 'Total Revenue',
      'reportKpi.invoices': 'Invoices',
      'reportKpi.avgInvoice': 'Avg / Invoice',
      'reportKpi.mechanics': 'Technicians',
      'reportKpi.completedJobs': 'Completed Jobs',
      'reportKpi.avgHours': 'Avg. Hours',
      'reportKpi.services': 'Services',
      'reportKpi.totalJobs': 'Total Jobs',
      'reportKpi.parts': 'Parts',
      'reportKpi.unitsUsed': 'Units Used',
      'reportKpi.totalValue': 'Total Value',
      'reportKpi.jobs': 'Jobs',
      'reportKpi.totalMargin': 'Total Margin',
      'reportKpi.avgMarginPct': 'Avg Margin %',
      'reportKpi.inventoryAtCost': 'Inventory at Cost',
      'reportKpi.potentialProfit': 'Potential Profit',
      'reportKpi.skus': 'SKUs',
      'reportKpi.taxable': 'Taxable',
      'reportKpi.vat': 'VAT',
      'reportKpi.total': 'Total',

      // Activity type labels (used in dashboard recentActivity)
      'activityType.job_card': 'Job Card',
      'activityType.invoice': 'Invoice',
      'activityType.customer': 'Customer',
      'activityType.quotation': 'Quotation',
      justNow: 'just now',
      minuteAgo: 'minute ago',

      // Settings Page
      settings: 'Settings',
      garageInformation: 'Garage Information',
      garageName: 'Garage Name',
      garageNameAr: 'Garage Name (Arabic)',
      taxNumber: 'Tax Number',
      taxRegistrationNumber: 'Tax Registration Number',
      address: 'Address',
      fullAddress: 'Full address',
      taxPricing: 'Tax & Pricing',
      currency: 'Currency',
      defaultTaxRate: '(%) Default Tax Rate',
      notifications: 'Notifications',
      emailNotifications: 'Email notifications for new job cards',
      smsNotifications: 'SMS notifications for job status changes',
      smsNotificationSettings: 'SMS Notification Settings',
      addNotificationRecipient: 'Add Notification Recipient',
      newJobCard: 'New Job Card',
      jobStatusChanges: 'Job Status Changes',
      selectStatuses: 'Select Statuses',
      hideStatuses: 'Hide Statuses',
      qualityCheckCompleted: 'Quality Check Completed',
      paymentReceived: 'Payment Received',
      lowStockAlerts: 'Low Stock Alerts',
      partsRequests: 'Parts Requests',
      customerFeedback: 'Customer Feedback',
      saveNotificationSettings: 'Save Notification Settings',
      'hrSettings.reminderRecipients': 'HR expiry reminders',
      'hrSettings.reminderRecipientsHint': 'Choose who gets notified when employee documents or contracts are approaching expiry. Only the people you pick are notified — leave it empty and nobody is.',
      'hrSettings.noUsers': 'No active users to choose from.',
      'hrSettings.save': 'Save recipients',
      'hrSettings.saved': 'Reminder recipients saved',
      'hrSettings.saveFailed': 'Failed to save reminder recipients',
      'hrSettings.loadFailed': 'Failed to load reminder recipients',
      // HR — Employees list page (PR-1, Task 7).
      'hr.employees.navTitle': 'Employees',
      'hr.employees.new': 'Add employee',
      'hr.employees.searchPlaceholder': 'Search by name or employee number',
      'hr.employees.status.all': 'All',
      'hr.employees.status.active': 'Active',
      'hr.employees.status.on_leave': 'On leave',
      'hr.employees.status.terminated': 'Terminated',
      'hr.employees.col.name': 'Employee',
      'hr.employees.col.employeeNo': 'Employee no.',
      'hr.employees.col.jobTitle': 'Job title',
      'hr.employees.col.branch': 'Branch',
      'hr.employees.linkedAccount': 'Linked account',
      'hr.employees.expired': 'Expired',
      'hr.employees.expiresIn': 'Expires in {{days}}d',
      'hr.employees.empty': 'No employees yet',
      'hr.employees.created': 'Employee added',
      'hr.employees.form.firstName': 'First name',
      'hr.employees.form.lastName': 'Last name',
      'hr.employees.form.firstNameAr': 'First name (Arabic)',
      'hr.employees.form.lastNameAr': 'Last name (Arabic)',
      'hr.employees.form.sex': 'Sex',
      'hr.employees.form.sexMale': 'Male',
      'hr.employees.form.sexFemale': 'Female',
      'hr.employees.form.dob': 'Date of birth',
      'hr.employees.form.nationality': 'Nationality',
      'hr.employees.form.nationalityOther': 'Other',
      'hr.employees.form.nationalityOtherHint': 'Enter nationality',
      'hr.employees.form.jobTitle': 'Job title',
      'hr.employees.form.jobTitleAr': 'Job title (Arabic)',
      'hr.employees.form.hireDate': 'Hire date',
      // HR — Employee file page (PR-1, Task 8).
      'hr.file.notFound': 'Employee not found',
      'hr.file.tabs.info': 'Info',
      'hr.file.tabs.contract': 'Contract & salary',
      'hr.file.tabs.documents': 'Documents',
      'hr.file.edit': 'Edit',
      'hr.file.updated': 'Employee updated',
      'hr.file.terminate': 'Terminate',
      'hr.file.terminate.title': 'Terminate employee',
      'hr.file.terminate.message': 'This marks the employee as terminated and makes their file read-only.',
      'hr.file.terminate.date': 'Termination date',
      'hr.file.terminate.reason': 'Reason',
      'hr.file.terminate.confirm': 'Terminate',
      'hr.file.terminate.done': 'Employee terminated',
      'hr.file.terminated.banner': 'This employee was terminated on {{date}}.',
      'hr.file.terminated.reason': 'Reason: {{reason}}',
      'hr.file.terminate.noticeShortfall':
        'This date is {{days}} day(s) short of the statutory {{required}}-day notice period.',
      'hr.file.terminate.noticeShortfallEstimate': 'Estimated pay-in-lieu of notice',
      'hr.file.terminate.noticeShortfallNote':
        'The exact figure is calculated and editable when the EOSB settlement is posted.',

      // Job-card actual-cost panel + service-cost controls + EOSB settlement
      // (HR PR-3, Task 6).
      'hr.cost.title': 'Actual cost',
      'hr.cost.empty': 'No tasks on this job card yet.',
      'hr.cost.rateApplied': 'Rate applied',
      'hr.cost.noRate': 'No rate',
      'hr.cost.noRateMechanics': 'No rate resolved',
      'hr.cost.taskLabor': 'Labor',
      'hr.cost.taskNonLabor': 'Non-labor',
      'hr.cost.taskTotal': 'Total',
      'hr.cost.orphanEvents': '{{count}} unmatched time-log event(s)',
      'hr.cost.overridden.cost': 'Overridden (flat cost)',
      'hr.cost.overridden.hours': 'Overridden (hours)',
      'hr.cost.override.button': 'Override',
      'hr.cost.override.title': 'Override labor cost',
      'hr.cost.override.computed': 'Computed',
      'hr.cost.override.mode.hours': 'Hours',
      'hr.cost.override.mode.cost': 'Flat cost (SAR)',
      'hr.cost.override.hoursLabel': 'Hours',
      'hr.cost.override.costLabel': 'Cost (SAR)',
      'hr.cost.override.reasonLabel': 'Reason',
      'hr.cost.override.save': 'Save override',
      'hr.cost.override.saved': 'Override saved',
      'hr.cost.override.by': 'By',
      'hr.cost.override.reason': 'Reason',
      'hr.cost.override.clear': 'Clear',
      'hr.cost.override.clearTitle': 'Clear override?',
      'hr.cost.override.clearMessage': 'This restores the computed labor cost for this task.',
      'hr.cost.override.cleared': 'Override cleared',
      'hr.cost.totalLabor': 'Total labor',
      'hr.cost.totalNonLabor': 'Total non-labor',
      'hr.cost.totalCost': 'Total cost',
      'hr.cost.billed': 'Billed (labor)',
      'hr.cost.margin': 'Margin vs billed',
      'hr.cost.otherThanManHours': 'Cost other than man-hours (SAR)',
      'hr.cost.costFromHours': 'Cost as per man-hours',
      'hr.cost.costFromHoursHint': "Checked: this line's cost is already covered by the task's logged hours, and is excluded from the cost total. Unchecked: the amount above adds on top.",
      'hr.cost.hourlyRate': 'Hourly rate (SAR/hour)',

      'hr.settlement.action': 'EOSB settlement',
      'hr.settlement.title': 'EOSB settlement',
      'hr.settlement.exitType': 'Exit type',
      'hr.settlement.exitType.resignation': 'Resignation',
      'hr.settlement.exitType.termination': 'Termination',
      'hr.settlement.exitType.contract_end': 'Contract end',
      'hr.settlement.exitType.dismissal_for_cause': 'Dismissal for cause (Art. 80)',
      'hr.settlement.wage': 'Monthly wage',
      'hr.settlement.tenure': 'Tenure (years)',
      'hr.settlement.fraction': 'Fraction applied',
      'hr.settlement.unusedLeaveDays': 'Unused annual leave (days)',
      'hr.settlement.eosb': 'EOSB amount',
      'hr.settlement.leaveEncashment': 'Leave encashment',
      'hr.settlement.payInLieuDaysLabel': 'Pay-in-lieu of notice (days)',
      'hr.settlement.payInLieuDaysHint': 'Defaults to 0 — only what you enter here is ever posted.',
      'hr.settlement.noticeEstimate': 'Days calculated vs. today: {{days}} — this compares to today, not the actual notice date given. Verify before using it.',
      'hr.settlement.useShortfallDays': 'Use {{days}} days',
      'hr.settlement.payInLieu': 'Pay-in-lieu of notice ({{days}} days)',
      'hr.settlement.loanOutstanding': 'Outstanding loan balance',
      'hr.settlement.loanDeduction': 'Deducted from this settlement',
      'hr.settlement.loanRemaining': 'Remaining loan balance (stays open)',
      'hr.settlement.total': 'Total settlement',
      'hr.settlement.confirm': 'Confirm settlement',
      'hr.settlement.done': 'EOSB settlement posted',
      'hr.settlement.alreadySettled': 'Already settled on {{date}}.',
      'hr.settlement.note': 'Note',
      'hr.settlement.loadFailed': 'Failed to load the settlement preview',

      'hr.file.info.nationalId': 'National ID',
      // HR statutory v2, T11 — id_kind label (next to the decrypted value,
      // derived from the number itself) vs the field-level label (which side
      // of the form the input renders as, driven by the employee's claimed
      // nationality) are deliberately separate key pairs — see
      // employeeInfoFields.js's idKindLabelKey/nationalIdFieldLabelKey.
      'hr.file.info.nationalIdLabelSaudi': 'National ID number',
      'hr.file.info.nationalIdLabelIqama': 'Iqama number',
      'hr.file.info.idKindNational': 'National ID card',
      'hr.file.info.idKindIqama': 'Iqama',
      'hr.file.info.gosiRegMissingChip': 'GOSI registration date missing — affects the GOSI track used',
      'hr.file.info.iqamaDocMissingChip': 'No Iqama document on file',
      'hr.file.info.iban': 'IBAN',
      'hr.iban.invalid': 'Invalid IBAN',
      'hr.file.info.reveal': 'Show',
      'hr.file.info.hide': 'Hide',
      'hr.file.info.linkedUser': 'Linked user account',
      'hr.file.info.noLinkedUser': '— none —',
      'hr.file.info.address': 'Address',
      'hr.file.info.notes': 'Notes',
      'hr.file.info.hajjLeaveTaken': 'Hajj leave taken',
      'hr.file.info.isDisabled': 'Person with disability (Nitaqat)',
      'hr.file.info.gosiRegDate': 'GOSI registration date',
      // Contract non-renewal pre-alert — mirrors the backend's notice-days
      // rule (indefinite -> 60, fixed -> 30) purely for display; the actual
      // reminder scheduling lives server-side.
      'hr.file.info.contractEnd': 'Contract end date',
      'hr.file.info.daysToRenewal': 'Days to renewal',
      'hr.file.info.renewalIn': '{{days}} day(s) left',
      'hr.file.info.renewalOverdue': 'Renewal overdue',
      'hr.file.info.noticePeriod': '{{days}}-day notice period',
      'hr.file.info.contractAlert': 'Contract non-renewal alert',
      'hr.file.info.contractAlertLead': 'Alert lead time (days)',
      'hr.file.info.contractAlertHint': 'Alert the reminder recipients this many days before the notice period begins, so you can decide on renewal or notify the employee in time.',
      'hr.file.contract.active': 'Active contract',
      'hr.file.contract.none': 'No active contract',
      'hr.file.contract.type': 'Contract type',
      'hr.file.contract.typeFixed': 'Fixed-term',
      'hr.file.contract.typeIndefinite': 'Indefinite',
      'hr.file.contract.startDate': 'Start date',
      'hr.file.contract.endDate': 'End date',
      'hr.file.contract.probationEnd': 'Probation end',
      'hr.file.contract.qiwaRef': 'Qiwa reference',
      'hr.file.contract.weeklyHours': 'Weekly hours',
      'hr.file.contract.leaveDays': 'Annual leave days',
      'hr.file.contract.salary': 'Salary',
      'hr.file.contract.basic': 'Basic',
      'hr.file.contract.housing': 'Housing',
      'hr.file.contract.transport': 'Transport',
      'hr.file.contract.otherAllowances': 'Other allowances',
      'hr.file.contract.allowanceLabel': 'Label',
      'hr.file.contract.allowanceAmount': 'Amount',
      'hr.file.contract.addAllowance': 'Add allowance',
      'hr.file.contract.total': 'Total',
      'hr.file.contract.new': 'New contract',
      'hr.file.contract.newTitle': 'Create new contract',
      'hr.file.contract.edit': 'Edit contract',
      'hr.file.contract.editTitle': 'Edit current contract',
      'hr.file.contract.saveChanges': 'Save changes',
      'hr.file.contract.updated': 'Contract updated',
      'hr.file.contract.promote': 'Promote',
      'hr.file.contract.promoteTitle': 'Promotion — adjusted contract',
      'hr.file.contract.promoteNote':
        'Creates a new contract with the adjusted salary and keeps the current one in history',
      'hr.file.contract.confirmRenewTitle': 'Replace active contract?',
      'hr.file.contract.confirmRenewMessage': 'The current active contract will be marked as renewed.',
      'hr.file.contract.created': 'Contract created',
      'hr.file.contract.history': 'Contract history',
      'hr.file.contract.noHistory': 'No previous contracts',
      'hr.file.contract.status.active': 'Active',
      'hr.file.contract.status.renewed': 'Renewed',
      'hr.file.contract.status.expired': 'Expired',
      'hr.costs.title': 'Additional costs (employer)',
      'hr.costs.label': 'Cost item',
      'hr.costs.method': 'Method',
      'hr.costs.monthly': 'Monthly',
      'hr.costs.totalForContract': 'Total for contract',
      'hr.costs.amount': 'Amount',
      'hr.costs.monthlyEquivalent': '≈ monthly',
      'hr.costs.needsEndDate': 'Set a contract end date to use a contract-total amount',
      'hr.costs.add': 'Add cost',
      'hr.costs.total': 'Total additional (monthly)',
      // HR statutory v2, T11 — mirrors RevenuePlanCard's tgt.salariesHrNote
      // double-count warning: these contract cost amounts feed the same
      // labor-rate/profitability math, so recording the real invoice as a
      // company expense TOO would double-count it.
      'hr.costs.doubleCountNote': "These amounts feed labor cost and profitability calculations — if you also record the real invoice as a company expense, don't add it here too, to avoid double counting.",
      'hr.file.doc.type': 'Type',
      'hr.file.doc.number': 'Document number',
      'hr.file.doc.issueDate': 'Issue date',
      'hr.file.doc.expiryDate': 'Expiry date',
      'hr.file.doc.file': 'File',
      'hr.file.doc.upload': 'Upload document',
      'hr.file.doc.view': 'View',
      'hr.file.doc.delete': 'Delete',
      'hr.file.doc.deleteTitle': 'Delete document?',
      'hr.file.doc.deleteMessage': 'This document will be permanently removed.',
      'hr.file.doc.uploaded': 'Document uploaded',
      'hr.file.doc.deleted': 'Document deleted',
      'hr.file.doc.empty': 'No documents yet',
      'hr.file.doc.invalidType': 'File must be a JPG, PNG, WEBP, or PDF',
      'hr.file.doc.tooLarge': 'File must be under 10MB',
      'hr.file.doc.viewFailed': 'Failed to open document',
      'hr.file.docExpired': 'Expired',
      'hr.file.doc.type.iqama': 'Iqama',
      'hr.file.doc.type.passport': 'Passport',
      'hr.file.doc.type.contract': 'Contract',
      'hr.file.doc.type.driving_license': 'Driving license',
      'hr.file.doc.type.gosi_cert': 'GOSI certificate',
      'hr.file.doc.type.other': 'Other',

      // HR PR-2, Task 11: payroll pages, reports, loans & leave tabs, photo_url.
      'hr.file.info.photoUrl': 'Photo URL',
      'hr.file.tabs.loans': 'Loans',
      'hr.file.tabs.leave': 'Leave',
      'hr.file.loans.new': 'New loan',
      'hr.file.loans.created': 'Loan created',
      'hr.file.loans.updated': 'Loan updated',
      'hr.file.loans.empty': 'No loans yet',
      'hr.file.loans.principal': 'Principal',
      'hr.file.loans.principalShort': 'Principal',
      'hr.file.loans.installment': 'Monthly installment',
      'hr.file.loans.installmentShort': 'Installment',
      'hr.file.loans.balance': 'Balance',
      'hr.file.loans.note': 'Note',
      'hr.file.loans.pause': 'Pause',
      'hr.file.loans.resume': 'Resume',
      'hr.file.loans.settle': 'Settle',
      'hr.file.loans.status.active': 'Active',
      'hr.file.loans.status.paused': 'Paused',
      'hr.file.loans.status.settled': 'Settled',
      'hr.file.loans.edit': 'Edit',
      'hr.file.loans.editTitle': 'Edit loan',
      'hr.file.loans.delete': 'Delete',
      'hr.file.loans.deleted': 'Loan deleted',
      'hr.file.loans.deleteTitle': 'Delete this loan?',
      'hr.file.loans.deleteBody': 'The loan record is removed completely. Payroll has never collected against it, so nothing is written off.',
      'hr.file.loans.settleTitle': 'Write off the remaining balance?',
      'hr.file.loans.settleBody': 'The employee stops repaying and the outstanding balance is forgiven. This cannot be undone.',
      'hr.file.loans.settleAmount': 'Balance written off',
      'hr.file.loans.repaid': 'Repaid',
      'hr.file.loans.repaidOf': '{{repaid}} of {{principal}}',
      'hr.file.loans.recordedOn': 'Recorded {{date}}',
      'hr.file.loans.recordedBy': 'by {{name}}',
      'hr.file.loans.remainingMonths': '≈{{count}} month(s) left',
      'hr.file.loans.history': 'Repayment history',
      'hr.file.loans.historyEmpty': 'No payroll run has collected against this loan yet.',
      'hr.file.loans.historyRun': 'Run {{period}}',
      'hr.file.loans.reversedRun': 'reversed — given back',
      'hr.file.loans.draftRun': 'draft — not collected yet',
      'hr.file.loans.principalLocked': 'Payroll has already collected an installment, so the principal is fixed.',
      'hr.file.loans.staleDraft': 'Regenerate the draft payroll run for {{periods}} so it stops deducting this loan.',
      'hr.file.loans.cannotDelete': 'Payroll has already collected against this loan — settle it instead.',
      'hr.file.leave.balance': 'Leave balance',
      'hr.file.leave.entitlement': 'Entitlement (days)',
      'hr.file.leave.taken': 'Taken (days)',
      'hr.file.leave.remaining': 'Remaining (days)',
      'hr.file.leave.sickTier': 'Sick leave tier',
      'hr.file.leave.sickTier.full': 'Full pay',
      'hr.file.leave.sickTier.threeQuarters': 'Three-quarter pay',
      'hr.file.leave.sickTier.unpaid': 'Unpaid',
      'hr.file.leave.sickTier.exhausted': 'Exhausted',
      'hr.file.leave.hireDateMissingHint': 'Set a hire date on the employee to get an accurate prorated balance.',
      'hr.file.leave.entries': 'Leave entries',
      'hr.file.leave.new': 'Add leave',
      'hr.file.leave.created': 'Leave entry added',
      'hr.file.leave.deleted': 'Leave entry deleted',
      'hr.file.leave.deleteTitle': 'Delete leave entry?',
      'hr.file.leave.deleteMessage': 'This leave entry will be permanently removed.',
      'hr.file.leave.delete': 'Delete',
      'hr.file.leave.empty': 'No leave entries yet',
      'hr.file.leave.type': 'Type',
      'hr.file.leave.type.annual': 'Annual',
      'hr.file.leave.type.sick': 'Sick',
      'hr.file.leave.type.maternity': 'Maternity',
      'hr.file.leave.type.hajj': 'Hajj',
      'hr.file.leave.type.unpaid': 'Unpaid',
      'hr.file.leave.type.other': 'Other',
      'hr.file.leave.type.paternity': 'Paternity',
      'hr.file.leave.type.marriage': 'Marriage',
      'hr.file.leave.type.bereavement_family': 'Bereavement (spouse/parent/child)',
      'hr.file.leave.type.bereavement_sibling': 'Bereavement (sibling)',
      'hr.file.leave.type.iddah': 'Iddah',
      'hr.file.leave.type.exam': 'Exam',
      'hr.file.leave.capHint': 'Statutory maximum: {{days}} days',
      'hr.file.leave.iddahNote': 'Single-source figure (~130 days) — verify with HRSD before relying on it',
      'hr.file.leave.hajjTaken': 'already taken',
      'hr.file.leave.start': 'Start date',
      'hr.file.leave.end': 'End date',
      'hr.file.leave.days': 'Days',
      'hr.file.leave.daysHint': 'Auto-calculated from the date range — adjust if needed',
      'hr.file.leave.note': 'Note',

      // HR PR-4, Task 5: the "add employee" wizard (source/info/contract/documents/account/review).
      'hr.wizard.steps.source': 'Source',
      'hr.wizard.steps.info': 'Basic info',
      'hr.wizard.steps.contract': 'Contract',
      'hr.wizard.steps.documents': 'Documents',
      'hr.wizard.steps.account': 'Account',
      'hr.wizard.steps.review': 'Review',
      'hr.wizard.source.manual': 'Enter details manually',
      'hr.wizard.source.manualHint': "Fill in the employee's details yourself, step by step.",
      'hr.wizard.source.qiwa': 'Scan a Qiwa contract',
      'hr.wizard.source.qiwaUpload': 'Upload contract',
      // PR-4, Task 9: TzamunAI branding on the scan card — replaces the
      // former hr.wizard.source.qiwaHint subtitle.
      'hr.wizard.qiwa.aiHint': "TzamunAI will read the contract and fill in the employee and contract details automatically — you review everything before saving.",
      'hr.wizard.qiwa.scanning': 'TzamunAI is reading the contract…',
      'hr.wizard.qiwa.notQiwa': "This file doesn't look like a Qiwa contract — please fill in the details manually.",
      'hr.wizard.qiwa.filled': 'Filled {{count}} field(s) from the scan',
      'hr.wizard.qiwa.extractionFailed': "TzamunAI couldn't read this contract — upload a clearer copy or fill in the details manually.",
      'hr.wizard.qiwa.quotaExceeded': 'Monthly AI scan limit reached — enter the details manually instead.',
      'hr.wizard.contract.hint': 'Optional — leave blank to skip; a contract can be added later from the employee file.',
      'hr.wizard.documents.hint': 'Upload any documents now, or add them later from the employee file.',
      'hr.wizard.account.existing': 'Link an existing user account',
      'hr.wizard.account.create': 'Create a new user account',
      'hr.wizard.account.none': 'No portal account',
      'hr.wizard.account.noneHint': 'This employee will not get portal/app access.',
      'hr.wizard.account.selectUser': 'User account',
      'hr.wizard.account.selectUserPlaceholder': 'Select a user',
      'hr.wizard.account.change': 'Change',
      'hr.wizard.account.createNote': "This creates a real user account right away — even if you don't finish this wizard.",
      'hr.wizard.account.createButton': 'Create user',
      'hr.wizard.account.created': 'User account created',
      'hr.wizard.review.edit': 'Edit',
      'hr.wizard.review.noContract': 'No contract will be created — it can be added later from the employee file.',
      'hr.wizard.review.pendingAccount': 'Pending selection',
      'hr.wizard.finished': 'Employee added',
      'hr.wizard.back': 'Back',
      'hr.wizard.next': 'Next',
      'hr.wizard.finish': 'Finish',
      'hr.wizard.saveDraft': 'Save draft',
      'hr.wizard.draftSaved': 'Draft saved',
      'hr.wizard.resumed': 'Draft resumed',
      'hr.wizard.resumeFailed': 'Failed to load draft',
      'hr.wizard.leaveTitle': 'Leave without saving?',
      'hr.wizard.leaveMessage': 'You have unsaved changes and no saved draft. Leaving now will discard them.',
      'hr.wizard.leaveConfirm': 'Discard and leave',
      'hr.wizard.errors.namesRequired': 'First and last name are required',
      'hr.wizard.errors.startDateRequired': 'Start date is required when any contract field is filled',
      'hr.wizard.errors.probationInvalid': 'Probation end must be a valid date within 180 days of the start date',
      'hr.wizard.draft.stripTitle': 'Drafts in progress',
      'hr.wizard.draft.unnamed': '(no name)',
      'hr.wizard.draft.badge': 'Draft',
      'hr.wizard.draft.resume': 'Resume',
      'hr.wizard.draft.delete': 'Delete',
      'hr.wizard.draft.deleteTitle': 'Delete this draft?',
      'hr.wizard.draft.deleteMessage': 'This will permanently delete the draft and any uploaded files. This cannot be undone.',
      'hr.wizard.draft.deleted': 'Draft deleted',

      'hr.payroll.navTitle': 'Payroll',
      'hr.payroll.newRun': 'New run',
      'hr.payroll.period': 'Period',
      'hr.payroll.created': 'Payroll run created',
      'hr.payroll.empty': 'No payroll runs yet',
      'hr.payroll.col.period': 'Period',
      'hr.payroll.col.netTotal': 'Net total',
      'hr.payroll.col.postingRef': 'Posting ref.',
      'hr.payroll.status.draft': 'Draft',
      'hr.payroll.status.approved': 'Approved',
      'hr.payroll.status.paid': 'Paid',
      'hr.payroll.status.reversed': 'Reversed',
      'hr.payroll.notFound': 'Payroll run not found',
      'hr.payroll.regenerate': 'Regenerate',
      'hr.payroll.regenerateMessage': "This rebuilds every line from current employee, contract, and adjustment data. Manually added adjustments for this period are kept.",
      'hr.payroll.regenerated': 'Payroll run regenerated',
      'hr.payroll.approve': 'Approve',
      'hr.payroll.approveMessage': "Approving locks in the run and consumes this period's once-off adjustments. This cannot be undone by regenerating.",
      'hr.payroll.approved': 'Payroll run approved',
      'hr.payroll.paid': 'Payroll run marked as paid',
      'hr.payroll.reversed': 'Payroll run reversed',
      'hr.payroll.wps': 'Download WPS file',
      'hr.payroll.wpsFailed': 'Failed to generate the WPS file',
      'hr.payroll.wpsSkippedTitle': 'Some employees will be missing from the file',
      'hr.payroll.wpsSkippedMessage': '{{count}} employee(s) will be skipped from the WPS file: {{list}}. They will NOT be in the file. Download it anyway?',
      'hr.payroll.wpsSkipReason.missing_iban': 'missing IBAN',
      'hr.payroll.wpsSkipReason.invalid_iban': 'invalid IBAN',
      'hr.payroll.wpsSkipReason.non_positive_net': 'net pay not positive',
      // Prod-gap fix: the skipped list (header or 422 body) is capped
      // server-side to the first 20 + a total — this fills the gap.
      'hr.payroll.wpsSkippedMore': '+{{count}} more',
      'hr.payroll.wpsDownloadAnyway': 'Download anyway',
      // Prod-gap fix (Task 8): every line excluded (or the run has no
      // lines) is now a hard 422 (WPS_NO_PAYABLE_LINES) — there is no file
      // to download, so this replaces the old "download the header-only
      // file anyway" dialog.
      'hr.payroll.wpsNoPayableTitle': 'No employees can be paid via WPS',
      'hr.payroll.wpsNoPayableHint': "Add a valid IBAN to each employee's file, then try exporting again.",
      // PR-4, Task 9: WPS establishment settings modal (GET/PUT .../payroll/wps-settings).
      'hr.payroll.wpsSettings.title': 'WPS establishment settings',
      'hr.payroll.wpsSettings.bank': 'Bank',
      'hr.payroll.wpsSettings.bankPlaceholder': 'Select a bank',
      'hr.payroll.wpsSettings.account': 'Establishment account IBAN',
      'hr.payroll.wpsSettings.estbId': 'Establishment ID',
      'hr.payroll.wpsSettings.molEstbId': 'MoL establishment ID',
      'hr.payroll.wpsSettings.saved': 'WPS settings saved',
      'hr.payroll.payslipFailed': 'Failed to generate the payslip',
      'hr.payroll.skipped.banner': '{{count}} employee(s) were skipped when this run was generated:',
      'hr.payroll.skip.on_leave': 'On leave',
      'hr.payroll.skip.terminated': 'Terminated',
      'hr.payroll.skip.no_active_contract': 'No active contract',
      'hr.payroll.pay.action': 'Pay',
      'hr.payroll.pay.title': 'Pay this run?',
      'hr.payroll.pay.message': "The following amounts will be posted to each branch's cash ledger:",
      'hr.payroll.pay.confirm': 'Confirm payment',
      'hr.payroll.reverse.action': 'Reverse',
      'hr.payroll.reverse.title': 'Reverse this payroll run?',
      'hr.payroll.reverse.message': 'This reverses every posted cash entry and restores consumed adjustments and loan balances. A reason is required.',
      'hr.payroll.reverse.reason': 'Reason',
      'hr.payroll.reverse.confirm': 'Reverse run',
      'hr.payroll.lines.empty': 'No lines in this run',
      'hr.payroll.line.gross': 'Gross',
      'hr.payroll.line.gosiEe': 'GOSI (employee)',
      'hr.payroll.line.gosiEr': 'GOSI (employer)',
      'hr.payroll.line.deductions': 'Deductions',
      'hr.payroll.line.net': 'Net',
      'hr.payroll.line.basic': 'Basic',
      'hr.payroll.line.housing': 'Housing',
      'hr.payroll.line.transport': 'Transport',
      'hr.payroll.line.otherAllowances': 'Other allowances',
      'hr.payroll.line.gosiBase': 'GOSI base',
      'hr.payroll.line.gosiTrack': 'GOSI track',
      'hr.payroll.line.eosbAccrual': 'EOSB accrual',
      'hr.payroll.line.adjustmentDetail': 'Adjustments & loan',
      'hr.payroll.line.loanEntry': 'Loan installment',
      'hr.payroll.line.payslip': 'Download payslip',
      'hr.payroll.line.adjust': 'Add/deduct',
      'hr.payroll.line.gosiBreakdown': 'GOSI breakdown',
      'hr.payroll.line.gosiPensionEr': 'Pension (employer)',
      'hr.payroll.line.gosiPensionEe': 'Pension (employee)',
      'hr.payroll.line.gosiSanedEr': 'SANED (employer)',
      'hr.payroll.line.gosiSanedEe': 'SANED (employee)',
      'hr.payroll.line.gosiHazardEr': 'Occupational hazard (employer)',
      'hr.payroll.line.leaveDeductions': 'Leave deductions',
      'hr.payroll.line.days': 'days',
      'hr.payroll.leaveDeduction.unpaid': 'Unpaid leave',
      'hr.payroll.leaveDeduction.sick_tier2': 'Sick leave (75% pay)',
      'hr.payroll.leaveDeduction.sick_tier3': 'Sick leave (unpaid)',
      'hr.payroll.gosiTrack.saudi_legacy': 'Saudi (legacy rate)',
      'hr.payroll.gosiTrack.saudi_new': 'Saudi (new rate)',
      'hr.payroll.gosiTrack.expat': 'Expat',
      'hr.payroll.gosiTrack.none': 'Not registered',
      'hr.payroll.adj.title': 'Pre-run adjustments',
      'hr.payroll.adj.employee': 'Employee',
      'hr.payroll.adj.selectEmployee': 'Select an employee',
      'hr.payroll.adj.empty': 'No pending adjustments for this employee this period',
      'hr.payroll.adj.kindLabel': 'Type',
      'hr.payroll.adj.categoryLabel': 'Category',
      'hr.payroll.adj.recurrenceLabel': 'Recurrence',
      'hr.payroll.adj.amountLabel': 'Amount',
      'hr.payroll.adj.add': 'Add',
      'hr.payroll.adj.added': 'Adjustment added',
      'hr.payroll.adj.remove': 'Remove',
      'hr.payroll.adj.removed': 'Adjustment removed',
      'hr.payroll.adj.removeTitle': 'Remove adjustment?',
      'hr.payroll.adj.removeMessage': 'This adjustment will no longer be picked up when the run is regenerated.',
      'hr.payroll.adj.once': 'Once (this period)',
      'hr.payroll.adj.recurring': 'Recurring',
      'hr.payroll.adj.kind.addition': 'Addition',
      'hr.payroll.adj.kind.deduction': 'Deduction',
      'hr.payroll.adj.category.overtime': 'Overtime',
      'hr.payroll.adj.category.bonus': 'Bonus',
      'hr.payroll.adj.category.commission': 'Commission',
      'hr.payroll.adj.category.absence': 'Absence',
      'hr.payroll.adj.category.penalty': 'Penalty',
      'hr.payroll.adj.category.gosi_arrears': 'GOSI arrears',
      'hr.payroll.adj.category.other': 'Other',
      'hr.payroll.adj.runTitle': 'Add/deduct for this run',
      'hr.payroll.adj.appliedNet': 'Adjustment applied — new net {{net}}',
      'hr.payroll.adj.applied': 'Adjustment applied and the run was regenerated',
      'hr.payroll.adj.otHoursLabel': 'Overtime hours',
      'hr.payroll.adj.otSuggest': 'Suggest overtime',
      'hr.payroll.adj.otSuggestionBanner': 'Suggested {{hours}} overtime hour(s) ≈',
      'hr.payroll.adj.otNote': 'Overtime {{hours}}h',

      // HR statutory v2, T8 — payslip delivery WhatsApp→SMS.
      'hr.payroll.line.payslipSent.whatsapp': 'Sent via WhatsApp',
      'hr.payroll.line.payslipSent.sms': 'Sent via SMS',
      'hr.payroll.payslipSend.action': 'Send payslips',
      'hr.payroll.payslipSend.confirmTitle': 'Send payslips now?',
      'hr.payroll.payslipSend.confirmMessage': 'Each employee gets their payslip via WhatsApp, or an SMS link if WhatsApp is unavailable.',
      'hr.payroll.payslipSend.sentLabel': 'Sent',
      'hr.payroll.payslipSend.skippedLabel': 'Already sent',
      'hr.payroll.payslipSend.failedLabel': 'Failed',
      'hr.payroll.payslipSend.noneToSend': 'All payslips for this run have already been sent',
      'hr.payroll.payslipSend.failReason.no_phone': 'no phone number on file',
      'hr.payroll.payslipSend.failReason.send_failed': 'send failed',
      'hr.payroll.payslipSend.failReason.error': 'unexpected error',
      'hr.payroll.payslipSend.failed': 'Failed to send payslips',

      'hr.reports.navTitle': 'HR reports',
      'hr.reports.empty': 'Nothing to report yet',
      'hr.reports.emptyDesc': 'Try a wider date range, or another branch.',
      'hr.reports.asOfToday': 'As of today',
      'hr.reports.asOfTodayHint': 'This report is a snapshot as of today — no date range applies.',
      'hr.reports.kpi.employees': 'Employees',
      'hr.reports.kpi.balanceDays': 'Leave balance (days)',
      'hr.reports.kpi.gosiUnpaid': 'Unpaid periods',
      'hr.settings.navTitle': 'HR settings',
      'hr.settings.subtitle': 'Payroll calendar, Saudization thresholds, WPS bank details and who gets HR reminders.',
      'hr.settings.wps.title': 'WPS (wage protection file)',
      'hr.settings.wps.hint': 'Bank and establishment details the WPS payroll file needs before it can be generated.',
      'hr.settings.wps.edit': 'Edit WPS details',
      'hr.reports.col.period': 'Period',
      'hr.reports.col.employee': 'Employee',
      'hr.reports.col.branch': 'Branch',
      'hr.reports.gosi.title': 'GOSI payable',
      'hr.reports.gosi.empty': 'No approved payroll runs yet',
      'hr.reports.gosi.employer': 'Employer share',
      'hr.reports.gosi.employee': 'Employee share',
      'hr.reports.gosi.sanedEmployer': 'SANED (employer)',
      'hr.reports.gosi.sanedEmployee': 'SANED (employee)',
      'hr.reports.gosi.total': 'Total',
      'hr.reports.gosi.paidBadge': 'Paid',
      'hr.reports.gosi.unpaidBadge': 'Not paid',
      'hr.reports.gosi.pay': 'Pay GOSI',
      'hr.reports.gosi.payTitle': 'Post GOSI expense',
      'hr.reports.gosi.paidFrom': 'Paid from',
      'hr.reports.gosi.paidFrom.hand': 'Cash on hand',
      'hr.reports.gosi.paidFrom.bank': 'Bank',
      'hr.reports.gosi.paid': 'GOSI expense posted',
      'hr.reports.gosi.alreadyPaid': 'A GOSI expense already exists for this period',
      // HR statutory v2, T10 — GOSI reconciliation (real portal bill vs computed).
      'hr.reports.gosi.actualBill': 'Actual GOSI bill',
      'hr.reports.gosi.variance': 'Variance',
      'hr.reports.gosi.actualBillInvalid': 'Enter an amount of 0 or more',
      'hr.reports.gosi.actualBillSaved': 'GOSI bill saved',
      'hr.reports.eosb.title': 'EOSB liability',
      'hr.reports.eosb.empty': 'No active employees with a wage basis',
      'hr.reports.eosb.tenure': 'Tenure (years)',
      'hr.reports.eosb.accrued': 'Accrued EOSB',
      'hr.reports.eosb.companyTotal': 'Company total',
      'hr.reports.leave.title': 'Leave balances',
      'hr.reports.leave.empty': 'No active employees',
      'hr.reports.leave.entitlement': 'Entitlement',
      'hr.reports.leave.taken': 'Taken',
      'hr.reports.leave.balance': 'Balance',
      'hr.reports.leave.sickTier': 'Sick leave tier',
      // HR statutory v2, T10 — SAR encashment exposure.
      'hr.reports.leave.valuation': 'Valuation',
      'hr.reports.leave.companyTotalValuation': 'Company total valuation',
      'hr.reports.cost.title': 'Payroll cost by branch',
      'hr.reports.cost.empty': 'No paid payroll runs yet',
      'hr.reports.cost.employees': 'Employees',
      'hr.reports.cost.gross': 'Gross',
      'hr.reports.cost.total': 'Total cost',
      // HR statutory v2, T10 — full employer cost (gross + employer GOSI +
      // EOSB accrual + additional contract costs).
      'hr.reports.cost.eosbAccrual': 'EOSB accrual',
      'hr.reports.cost.additionalCosts': 'Additional costs',
      'hr.reports.cost.fullyLoaded': 'Fully loaded cost',

      // Public-holiday calendar (HR statutory v2, T2). GET/PUT /api/hr/holidays.
      'hr.reports.holidays.title': 'Public holidays',
      'hr.reports.holidays.eidHint': 'Eid al-Fitr and Eid al-Adha dates follow the Umm-al-Qura calendar and shift every year — confirm them against the official cabinet order and update here before each Eid.',
      'hr.reports.holidays.seedNotice': 'Showing suggested default dates for this year — nothing is saved until you press Save.',
      'hr.reports.holidays.year': 'Year',
      'hr.reports.holidays.name': 'Name (English)',
      'hr.reports.holidays.nameAr': 'Name (Arabic)',
      'hr.reports.holidays.start': 'Start date',
      'hr.reports.holidays.days': 'Days',
      'hr.reports.holidays.add': 'Add holiday',
      'hr.reports.holidays.remove': 'Remove holiday',
      'hr.reports.holidays.empty': 'No holidays configured for this year',
      'hr.reports.holidays.saved': 'Holiday calendar saved',
      // Each year is stored on its own — saving one never touches another.
      // The picker and these labels exist to make that visible.
      'hr.reports.holidays.savedBadge': 'Saved',
      'hr.reports.holidays.notSetYet': 'not set up yet',
      'hr.reports.holidays.copyFrom': 'Copy from {{year}}',
      'hr.reports.holidays.copied': 'Copied from {{year}} — check the Eid dates, then Save.',
      'hr.reports.holidays.nextYearUnset': '{{year}} has no holiday calendar yet. Leave and overtime for next year will be worked out as if there were no public holidays until you set it up.',
      'hr.reports.ramadan.title': 'Ramadan window',
      'hr.reports.ramadan.hint': 'Sets the reduced 6-hour daily overtime threshold (Art.98) for this window. Ramadan shifts about 11 days earlier every Gregorian year — confirm the dates and update here before each Ramadan.',
      'hr.reports.ramadan.start': 'Start date',
      'hr.reports.ramadan.end': 'End date',
      'hr.reports.ramadan.saved': 'Ramadan window saved',
      'hr.reports.ramadan.saveAria': 'Save Ramadan window',

      // Nitaqat (Saudization) status report + threshold settings (HR
      // statutory v2, T7). NO hardcoded thresholds — the tenant copies its
      // own color-band percentages from Qiwa; see nitaqatService.
      'hr.reports.nitaqat.title': 'Nitaqat (Saudization) status',
      'hr.reports.nitaqat.empty': 'No active employees to count toward Nitaqat',
      'hr.reports.nitaqat.ratio': 'Saudization ratio',
      'hr.reports.nitaqat.totalEmployees': 'Total active employees',
      'hr.reports.nitaqat.weightedSaudis': 'Weighted Saudis',
      'hr.reports.nitaqat.others': 'Non-Saudi (non-GCC)',
      'hr.reports.nitaqat.disabledBonus': 'Disabled-employee bonus weight',
      'hr.reports.nitaqat.disabledBonusHint': 'Extra weight from disabled Saudi employees (×4 of their own weight), capped at 10% of the counted workforce.',
      'hr.reports.nitaqat.microNotice': 'Simplified micro-entity rule applies (5 or fewer employees): at least 1 Saudi employee = green, none = red.',
      'hr.reports.nitaqat.saudiCount': 'Saudi employees',
      'hr.reports.nitaqat.band.red': 'Red',
      'hr.reports.nitaqat.band.green': 'Green',
      'hr.reports.nitaqat.band.low_green': 'Low Green',
      'hr.reports.nitaqat.band.medium_green': 'Medium Green',
      'hr.reports.nitaqat.band.high_green': 'High Green',
      'hr.reports.nitaqat.band.platinum': 'Platinum',
      'hr.reports.nitaqat.emptyThresholds': 'Enter your activity’s bands from Qiwa to see your official color band here.',
      'hr.reports.nitaqat.qiwaLink': 'Open Qiwa Nitaqat Calculator',
      'hr.reports.nitaqat.disclaimer': 'This is an estimate for planning only — the official Nitaqat band is whatever Qiwa shows for your establishment.',
      'hr.reports.nitaqatSettings.title': 'Nitaqat color-band thresholds (from Qiwa)',
      'hr.reports.nitaqatSettings.hint': 'Copy your activity’s current band percentages from Qiwa (Manage Establishment → Nitaqat, or the Nitaqat Calculator tool) — MotorMind has no access to these and never guesses them.',
      'hr.reports.nitaqatSettings.activityLabel': 'Activity label (for your reference)',
      'hr.reports.nitaqatSettings.lowGreen': 'Low Green %',
      'hr.reports.nitaqatSettings.mediumGreen': 'Medium Green %',
      'hr.reports.nitaqatSettings.highGreen': 'High Green %',
      'hr.reports.nitaqatSettings.platinum': 'Platinum %',
      'hr.reports.nitaqatSettings.saved': 'Nitaqat thresholds saved',
      'hr.reports.nitaqatSettings.saveAria': 'Save Nitaqat thresholds',

      // Employee profitability report (HR PR-5, Task 5).
      'hr.profit.title': 'Employee profitability',
      'hr.profit.employee': 'Employee',
      'hr.profit.hours': 'Hours',
      'hr.profit.utilization': 'Utilization %',
      'hr.profit.income': 'Income',
      'hr.profit.laborCost': 'Labor cost',
      'hr.profit.fullCost': 'Full cost (salary+GOSI+EOSB+extras)',
      'hr.profit.profit': 'Profit',
      'hr.profit.margin': 'Margin %',
      'hr.profit.export': 'Export CSV',
      'hr.profit.empty': 'No data for this period',
      'hr.profit.searchEmployee': 'Search employee',
      'hr.profit.periodNote': 'Hours, income and cost cover {{from}} → {{to}}. Salary and additional costs are prorated to the days in this period.',
      'hr.profit.accessDenied': 'No access',
      'hr.profit.accessDeniedDesc': 'You do not have permission to view the employee profitability report.',
      // HR statutory v2, T11 — same double-count warning as
      // hr.costs.doubleCountNote, for the report page that consumes those
      // same contract cost figures.
      'hr.profit.doubleCountNote': "Full cost is computed from HR contract data (salary + additional costs). If the same costs are also recorded as separate company expenses, avoid double counting them elsewhere.",

      vatSettingsManagedAtCompanyLevel: 'VAT Settings are Managed at Company Level',
      vatSettingsCompanyLevelDescription: 'VAT registration number, rate, and exemption status are configured by your company administrator and apply to all garages under your company.',
      vatRegistrationNumber: 'VAT Registration Number',
      notSet: 'Not Set',
      toModifyVatSettings: 'To modify VAT settings, please contact your company administrator or access the Admin Portal.',
      jobStatusDraft: 'Draft',
      jobStatusReceived: 'Received',
      jobStatusDiagnosis: 'Diagnosis',
      jobStatusInProgress: 'In Progress',
      jobStatusQualityCheck: 'Quality Check',
      jobStatusPendingPayment: 'Pending Payment',
      jobStatusReady: 'Ready',
      jobStatusDelivered: 'Delivered',
      jobStatusClosed: 'Closed',

      // Walk-in Vehicle
      walkInVehicle: 'Walk-in Vehicle',
      walkInVehicleTitle: 'Register Walk-in Vehicle',
      walkInVehicleDesc: 'Register a vehicle with unknown owner. Customer info can be added later.',
      walkInIncomplete: 'Incomplete Job Card',
      walkInIncompleteBanner: 'This job card is incomplete — customer information is missing. Complete it before starting work.',
      completeCustomerInfo: 'Complete Customer Info',
      completeJobCard: 'Complete Job Card',
      completingJobCard: 'Completing...',
      completeJobCardTitle: 'Complete Job Card',
      completeJobCardDesc: 'Add customer information to activate this job card.',
      selectRealCustomer: 'Select or Create Customer',
      reviewVehicleDetails: 'Review Vehicle Details',
      confirmCompletion: 'Confirm & Activate',
      jobCardActivated: 'Job card activated successfully',
      walkInCreated: 'Walk-in job card created successfully',
      fillKnownInfo: 'Fill what is known, leave blank if unknown',
      unknownPlaceholder: 'Unknown',

      // Quotations Page
      quotations: 'Quotations',
      'jc.quotRow.emptyHint': 'None yet — created after diagnosis',
      'jc.quoteBlock.noOpen': 'No open quotation',
      'jc.quoteBlock.validUntil': 'Valid until',
      'jc.quoteBlock.deposit': 'Deposit',
      'jc.quoteBlock.paid': 'Paid',
      'jc.quoteBlock.due': 'Due',
      'jc.quoteBlock.none': 'None',
      'jc.quoteBlock.sent': 'Sent to customer',
      'jc.quoteBlock.notSent': 'Not sent to customer',
      'jc.quoteBlock.resends': 'Resends used',
      'jc.quoteBlock.ofFive': 'of 5',
      'jc.quoteBlock.open': 'Open quotation',
      'jc.quoteBlock.previous': 'Previous quotations',
      quotation: 'Quotation',
      sent: 'Sent',
      rejected: 'Rejected',
      draft: 'Draft',
      
      // Finding Approval Modal
      editTaskBeforeCreating: 'Edit Task Before Creating',
      reviewAndAdjustBeforeSending: 'Review and adjust parts/services before sending to customer',
      findingDescription: 'Finding Description',
      recommendedAction: 'Recommended Action',
      reportedBy: 'Reported by',
      reportedOn: 'Reported on',
      severity: 'Severity',
      noServicesAdded: 'No services added',
      noPartsAdded: 'No parts added',
      serviceName: 'Service Name',
      enterServiceName: 'Enter service name',
      enterPartName: 'Enter part name',
      priceSAR: 'Price (SAR)',
      qtySAR: 'Qty',
      removeService: 'Remove service',
      removePart: 'Remove part',
      estimatedTotal: 'Estimated Total',
      serviceCount: 'service(s)',
      partCount: 'part(s)',
      saveAndCreateTask: 'Save & Create Task',
      creatingTask: 'Creating Task...',
      findingApprovedAndTaskCreated: 'Finding approved and task created',
      
      // Task Status Messages
      itemsMustBeApprovedBeforeStarting: 'Items must be approved before starting',
      
      // Calculation Labels
      grossTotal: 'Gross Total',
      netTaxableAmount: 'Net Taxable Amount',
      advancePayment: 'Advance Payment',

      // Create Invoice/Quotation Forms
      createInvoice: 'Create Invoice',
      backToInvoices: 'Back to Invoices',
      backToQuotations: 'Back to Quotations',
      selectJobCard: 'Select Job Card (Optional)',
      manualEntryNoJobCard: 'Manual Entry (No Job Card)',
      enterInvoiceDetailsManually: 'Enter invoice details manually',
      enterQuotationDetailsManually: 'Enter quotation details manually',
      invoiceItems: 'Invoice Items',
      quotationItems: 'Quotation Items',
      itemDescription: 'Item description',
      unitPriceSAR: 'Unit Price (SAR)',
      quantity: 'Quantity',
      manualEntry: 'Manual Entry',
      remove: 'Remove',
      addItem: 'Add Item',
      subtotal: 'Subtotal',
      taxRate: 'Tax Rate',
      taxAmount: 'Tax Amount',
      discount: 'Discount',
      notesOptional: 'Notes (Optional)',
      additionalNotes: 'Additional notes or payment terms',
      createQuotation: 'Create Quotation',
      validUntilDate: 'Valid Until',
      autoFilledFromJobCard: 'Auto-filled from job card',
      selectItem: 'Select Item',
      parts: 'Parts',
      services: 'Services',

      // Dashboard Recent Activity
      newJobCardCreated: 'New job card created',
      invoiceGenerated: 'Invoice generated',
      customerAdded: 'Customer added',
      minutesAgo: 'minutes ago',
      hourAgo: 'hour ago',
      hoursAgo: 'hours ago',
      daysAgo: 'days ago',

      // Common Form Labels
      firstName: 'First Name',
      lastName: 'Last Name',
      emailAddress: 'Email Address',
      password: 'Password',
      phoneNumber: 'Phone Number',
      phoneRequiredForOtp: 'Phone number is required — it is used to sign in via SMS code.',
      enterFirstName: 'Enter first name',
      enterLastName: 'Enter last name',
      location: 'Location',
      locationBranch: 'Location (Branch)',
      selectBranchOrType: 'Select Branch or type',
      selectOrType: 'Select or type',
      costPriceOptional: 'Cost Price (Optional)',
      compatibilityCarModels: 'Compatibility (Car Models)',
      initialStock: 'Initial Stock',
      minLevel: 'Min. Level',
      unitPrice: 'Unit Price',

      // Customer Modal
      locationDetails: 'LOCATION DETAILS',
      areaDistrict: 'Area/District',
      city: 'City',
      region: 'Region',
      selectArea: 'Select Area',
      selectCity: 'Select City',
      selectRegion: 'Select Region',
      buildingNumber: 'Building Number',
      district: 'District',
      postalCode: 'Postal Code',
      buildingNumberInvalid: 'Building number must be exactly 4 digits',
      postalCodeInvalid: 'Postal code must be exactly 5 digits',
      'customers.buildingNumber': 'Building Number',
      'customers.district': 'District',
      'customers.postalCode': 'Postal Code',
      'customers.buildingNumberInvalid': 'Building number must be exactly 4 digits',
      'customers.postalCodeInvalid': 'Postal code must be exactly 5 digits',
      nationalAddress: 'National Address (ZATCA)',
      // NationalAddressFields — the one buyer-address block, shared by all
      // three customer forms.
      'na.title': 'National Address',
      'na.requiredForB2b': 'Required for tax invoices',
      'na.lookup': 'Find the address',
      'na.lookupHint': 'Type the national short address or the street — we fill in what we find, you complete the rest.',
      'na.lookupPlaceholder': 'e.g. RAAA1111 or a street name',
      'na.autofilled': 'Filled {{n}} field(s) from the lookup — check them before saving.',
      'na.buildingNumber': 'Building No.',
      'na.postalCode': 'Postal Code',
      'na.district': 'District',
      'na.districtPlaceholder': 'e.g. Al Olaya',
      enterDistrict: 'e.g. Al Olaya',
      additionalAddressInfo: 'Additional Address Info (Optional)',
      emailOptional: 'Email (Optional)',

      // Vehicle Modal
      quickVehicleRegistration: 'Quick Vehicle Registration',
      scanIstamara: 'Scan Istamara',
      vehiclePhotos: 'Vehicle Photos',
      registrationAndInsurance: 'Registration & Insurance',
      vehicleOwnerName: 'Vehicle Owner Name',
      optionalFromIstamara: 'Optional - from Istamara',
      ownerNameAsShownOnVehicleRegistration: 'Owner name as shown on vehicle registration',
      thisIsTheRegisteredOwnerNameFromIstamara: 'This is the registered owner name from Istamara (may differ from customer)',
      istamaraDetails: 'Istamara Details',
      sequenceNumber: 'Sequence Number',
      chassisNumber: 'Chassis Number',
      selectCustomer: 'Select Customer',
      noCustomersFound: 'No customers found',
      searchByNameOrPhone: 'Search by name or phone...',
      uploadPhotos: 'Upload Photos',
      model: 'Model',
      brand: 'Brand',
      generation: 'Generation',
      selectModel: 'Select Model',
      selectBrand: 'Select Brand',
      selectYear: 'Select Year',
      selectGeneration: 'Select Generation',
      transportBlue: 'Transport (Blue)',
      taxiYellow: 'Taxi (Yellow)',
      petrol: 'Petrol',
      diesel: 'Diesel',
      electric: 'Electric',
      hybrid: 'Hybrid',
      white: 'White',
      black: 'Black',
      silver: 'Silver',
      gray: 'Gray',
      red: 'Red',
      blue: 'Blue',
      green: 'Green',
      yellow: 'Yellow',
      brown: 'Brown',
      beige: 'Beige',
      gold: 'Gold',
      bronze: 'Bronze',
      maroon: 'Maroon',
      purple: 'Purple',
      orange: 'Orange',
      automatic: 'Automatic',
      manual: 'Manual',
      scanVINBarcodeFromCarDoor: 'Scan VIN barcode from car door or Enter manually, then click "Decode"',
      policyNumber: 'Policy Number',
      insuranceExpiry: 'Insurance Expiry',
      egTawuniyaMedgulf: 'e.g. Tawuniya, Medgulf',
      insurancePolicyNumber: 'Insurance policy number',
      plateNumberMaxDigitsLetters: 'Plate Number * (أرقام: Max 4, حروف: Max 3)',
      typeInBottomRowEnglish: 'Type in the bottom row (English). Arabic will appear automatically in the top row',
      plateType: 'Plate Type',
      privateCarsWhite: 'Private Cars (White)',
      fuelType: 'Fuel Type',
      color: 'Color',
      selectType: 'Select Type',
      selectColor: 'Select Color',
      transmission: 'Transmission',
      vinVehicleIdentificationNumber: 'VIN (Vehicle Identification Number)',
      decode: 'Decode',
      scan: 'Scan',
      registrationInsuranceOptional: 'Registration & Insurance (Optional)',
      insuranceCompany: 'Insurance Company',
      registrationExpiry: 'Registration Expiry',

      // User Modal
      assignedGarage: 'Assigned Garage',
      selectGarage: 'Select Garage',
      selectRole: 'Select Role',
      activeStatus: 'Active Status',

      // Buttons
      refresh: 'Refresh',
      new: 'New',
      addService: 'Add Service',
      uploading: 'Uploading...',
      passwordLeaveEmpty: 'Password (leave empty to keep current)',
      createUser: 'Create User',
      updateUser: 'Update User',

      // Permission Manager - Categories
      adminPermissions: 'Admin Permissions',
      permissionsGranted: 'permissions granted',
      customerManagement: 'Customer Management',
      financial: 'Financial',
      inventory: 'Inventory',
      deselectAll: 'Deselect All',

      // Permission Manager - Customer Management
      customersDelete: 'Deactivate customers (reversible; customers are never deleted)',
      customersCreate: 'Add new customers',
      customersUpdate: 'Update customer details and reactivate them',
      customersRead: 'View customer information',

      // Permission Manager - Financial
      invoicesDelete: 'Delete invoices',
      invoicesCreate: 'Create invoices',
      invoicesSend: 'Send invoices to customers',
      invoicesRead: 'View invoices',
      pricingView: 'View parts and service prices',
      invoicesUpdate: 'Update invoices',
      quotationsCreate: 'Create quotations',
      quotationsApprove: 'Approve quotations',
      quotationsClose: 'Close (cancel) quotations',
      quotationsRead: 'View quotations',
      quotationsDelete: 'Delete quotations',
      quotationsUpdate: 'Update quotations',

      // Permission Manager - Inventory
      partsCreate: 'Add new parts',
      partsApprove: 'Approve parts requests',
      
      // Customer Provided Parts
      customerProvidedNoCost: 'Customer Provided (No cost)',
      checkIfCustomerWillBringPart: 'Check if customer will bring their own part',
      
      // Findings
      findingsMaxImagesAllowed: 'Maximum 5 images allowed',
      findingsDescribeFindingRequired: 'Please describe the finding',
      findingsAddPartOrServiceRequired: 'Please add at least one part or service',
      findingsSubmittedForReview: 'New finding submitted for admin review',
      findingsSubmitFailed: 'Failed to submit finding',
      findingsPlaceholderDescribeFinding: 'Describe what you found...',
      findingsSeverityLowDetail: 'Low - Can wait',
      findingsSeverityMediumDetail: 'Medium - Should be addressed',
      findingsSeverityHighDetail: 'High - Urgent attention needed',
      findingsPlaceholderRecommendedAction: 'What should be done about this?',
      findingsRequiredParts: 'Required Parts',
      findingsPartNumber: 'Part {{n}}',
      findingsSelectPart: 'Select Part',
      findingsSelectFromInventory: 'Select from inventory...',
      findingsOrCustomPartName: 'Or Custom Part Name',
      findingsCustomPartNamePlaceholder: 'Enter custom part name',
      findingsRequiredServices: 'Required Services',
      findingsAddService: 'Add Service',
      findingsServiceNumber: 'Service {{n}}',
      findingsServiceNameExample: 'e.g., Brake Pad Replacement',
      findingsServiceDetailsPlaceholder: 'Service details...',
      findingsEstHours: 'Est. Hours',
      findingsPhotosMax: 'Photos (Max 5)',
      findingsPhotoPreviewAlt: 'Preview {{n}}',
      findingsClickUploadPhotos: 'Click to upload photos',
      findingsSubmitFinding: 'Submit Finding',
      findingsLoadFailed: 'Failed to load findings',
      findingsApproveFailed: 'Failed to approve finding',
      findingsRejectionReasonRequired: 'Please provide a reason for rejection',
      findingsFindingRejected: 'Finding rejected',
      findingsRejectFailed: 'Failed to reject finding',
      findingsStatusApproved: 'Approved',
      findingsStatusRejected: 'Rejected',
      findingsStatusPendingReview: 'Pending Review',
      findingsSectionTitle: 'Findings',
      findingsLoadingFindings: 'Loading findings...',
      findingsNoFindingsYet: 'No findings reported yet.',
      findingsCount: 'Findings ({{count}})',
      findingsLabelDescription: 'Description:',
      findingsUnnamedPart: 'Unnamed Part',
      findingsApproveAndCreateTask: 'Approve & Create Task',
      findingsRejectionReasonLabel: 'Rejection Reason:',
      findingsRejectFindingTitle: 'Reject Finding',
      findingsRejectionReasonField: 'Reason for Rejection *',
      findingsRejectPlaceholder: 'Please provide a reason for rejecting this finding...',
      findingsRejecting: 'Rejecting...',
      findingsRejectFinding: 'Reject Finding',
      
      // Task Card
      taskPriorityUrgent: 'Urgent',
      taskPriorityHigh: 'High',
      taskPriorityNormal: 'Normal',
      taskPriorityLow: 'Low',
      taskStatusPending: 'Pending',
      taskStatusInProgress: 'In Progress',
      taskStatusCompleted: 'Completed',
      taskStatusApproved: 'Approved',
      taskStatusRejected: 'Rejected',
      taskStatusPartiallyApproved: 'Partially Approved',
      taskStatusQCPassed: 'QC Passed',
      taskStatusQCFailed: 'QC Failed',
      taskIssueTypeMechanical: 'Mechanical',
      taskIssueTypeElectrical: 'Electrical',
      taskIssueTypeBodywork: 'Bodywork',
      taskIssueTypeDiagnostic: 'Diagnostic',
      taskIssueTypePreventive: 'Preventive',
      taskIssueTypeOther: 'Other',
      taskQualityCheck: 'Quality Check',
      'qc.hub.title': 'Quality check',
      'qc.hub.awaiting': 'Awaiting check ({{count}})',
      'qc.hub.check': 'Check',
      'qc.hub.done': 'Already checked',
      'qc.hub.passed': 'Passed',
      'qc.hub.failed': 'Failed',
      'qc.hub.allChecked': 'Every task has been checked',
      'qc.hub.allCheckedHint': 'Nothing is waiting on quality check for this job card.',
      taskDeleteDisabledQuotation: 'Cannot delete - items in quotation',
      taskDeleteTask: 'Delete Task',
      taskTemporary: 'Temporary',
      taskPriceLine: 'Price: {{price}} SAR',
      taskNoServicesYet: 'No services added yet',
      taskServicesTotal: 'Services Total:',
      taskNoPartsYet: 'No parts added yet',
      taskRequestedBy: 'Requested by',
      taskPartsTotal: 'Parts Total:',
      taskProgress: 'Progress',
      taskTaskTotal: 'Task Total',
      taskEstHours: 'Est. {{hours}} hrs',
      taskQCRequired: 'Quality check required before completion',
      taskPerformQCNow: 'Perform QC Now',
      taskQCPassed: 'Quality Check: Passed',
      taskQCFailed: 'Quality Check: Failed',
      taskStatusOnHold: 'On Hold',
      taskStatusUnderQC: 'Under QC',
      taskIssueTypeACHeating: 'AC/Heating',
      taskIssueTypeTiresWheels: 'Tires/Wheels',
      taskIssueTypeRoutineService: 'Routine Service',
      taskQCRequiredSubtext: 'This task needs to be inspected before proceeding',
      taskEditTask: 'Edit Task',
      taskStatusLabel: 'Status:',
      taskPriorityLabel: 'Priority:',
      taskDescriptionPlaceholder: 'Task description...',
      taskHoursShort: '{{hours}} hrs',
      taskActualHours: 'Actual: {{hours}} hrs',
      taskTimeTaken: 'Time taken',
      taskTimeTakenValue: '{{duration}}',
      taskPayerLabel: 'Task payer',
      taskPayerInsurer: 'Insurer',
      taskPayerCustomer: 'Customer',
      taskPayerPermissionRequired: 'Insurance claim permission required',
      taskCollapse: 'Collapse',
      taskExpand: 'Expand',
      taskServicesCount_one: '{{count}} service',
      taskServicesCount_other: '{{count}} services',
      taskPartsCount_one: '{{count}} part',
      taskPartsCount_other: '{{count}} parts',
      taskEnterEditToDelete: 'Enter edit mode to delete tasks',
      taskCannotAddServicesPendingApproval: 'Cannot add services while quotation is pending approval',
      taskCannotAddPartsPendingApproval: 'Cannot add parts while quotation is pending approval',
      taskPerformQualityCheckTitle: 'Perform Quality Check',
      taskAccept: 'Accept',
      taskRequestedByMechanic: 'Requested by {{first}} {{last}}',
      taskPartQtyLabel: 'Qty: {{qty}}',
      taskPartUnitPrice: 'Unit Price: {{price}} SAR',
      taskPartLineTotal: 'Total: {{total}} SAR',
      
      // Mechanic Portal - Job Details
      fuelLevel: 'Fuel Level',
      full: 'Full',
      mileageIn: 'Mileage in',
      reportedIssues: 'Reported Issues',
      tasks: 'Tasks',
      pending: 'Pending',
      breakCheck: 'Break check',
      approved: 'Approved',
      startTask: 'Start Task',
      taskStartGateHint: 'Unlocks automatically after customer intake confirmation',
      startDiagnosis: 'Start Diagnosis',
      noTasksYet: 'No Tasks Yet',
      clickStartDiagnosisAboveToAddTasks: 'Click Start Diagnosis Above To Add Tasks',
      waitForQuotationApproval: 'Wait for quotation approval',
      waitingForCustomerApproval: 'Waiting For Customer Approval...',
      resendQuotationSMS: 'Resend Quotation SMS',
      resendInvoiceSMS: 'Resend Invoice SMS',
      sendingSMS: 'Sending SMS...',
      quotationSMSResent: 'Quotation SMS resent successfully',
      invoiceSMSResent: 'Invoice SMS sent successfully',
      maxResendsReached: 'Maximum resends reached (5/5 per day)',
      waitBeforeResending: 'Please wait 5 minutes before resending',
      resendsRemaining: '{{count}} resend(s) remaining',
      waitMinutesBeforeResending: 'Wait {{minutes}} minute(s) before resending',
      newFinding: 'New Finding',
      startRepairFirst: 'Start repair first',
      addTask: 'Add Task',
      onlyDuringDiagnosis: 'Only during diagnosis',
      takePhoto: 'Take Photo',
      activityLog: 'Activity Log',
      myJobs: 'My Jobs',
      job: 'job',
      jobs: 'jobs',
      searchByJobPlateCustomer: 'Search by job #, plate, customer...',
      mechanicJobBackToJobs: 'Back to Jobs',
      mechanicJobPhotosSection: 'Photos ({{count}})',
      mechanicJobStartTask: 'Start Task',
      mechanicJobCompleteTask: 'Complete Task',
      mechanicJobTaskCompleted: 'Task Completed',
      mechanicJobNoTasks: 'No tasks yet',
      mechanicJobAddTask: 'Add Task',
      mechanicJobOnlyDuringDiagnosis: 'Only during diagnosis',
      mechanicJobNewFindingDisabled: 'Start repair first',
      mechanicJobTakePhoto: 'Take Photo',
      mechanicJobConfirmMarkServiceDone: 'Mark this service as done?',
      mechanicJobConfirmMarkPartDone: 'Mark this part as done?',
      mechanicJobLoadFailed: 'Failed to load job details',
      mechanicJobStartTaskSuccess: 'Task started successfully',
      mechanicJobStartTaskFailed: 'Failed to start task',
      mechanicJobCompleteTaskSuccess: 'Task completed successfully',
      mechanicJobCompleteTaskFailed: 'Failed to complete task',
      mechanicJobPhotoUploadSuccess: 'Photo uploaded successfully',
      mechanicJobPhotoUploadFailed: 'Failed to upload photo',
      mechanicJobPhotosUploadSuccess: '{{count}} photos uploaded successfully',
      mechanicJobConfirmMarkServiceTitle: 'Mark service as completed',
      mechanicJobConfirmMarkPartTitle: 'Mark part as installed',
      mechanicJobServiceMarkedCompleted: 'Service marked as completed',
      mechanicJobServiceStatusUpdateFailed: 'Failed to update service status',
      mechanicJobPartMarkedInstalled: 'Part marked as installed',
      mechanicJobPartStatusUpdateFailed: 'Failed to update part status',
      mechanicJobIssueReportedSuccess: 'Issue reported successfully',
      mechanicJobIssueReportFailed: 'Failed to report issue',
      mechanicJobPartiallyApprovedWarning: 'Some items rejected — work on approved items only',
      mechanicJobTaskCancelled: 'Task cancelled',
      mechanicJobAllItemsRejected: 'All items rejected by customer',
      mechanicJobPartsCount: '{{count}} part(s)',
      mechanicJobServicesCount: '{{count}} service(s)',
      mechanicJobPhotoAlt: 'Job photo {{index}}',
      mechanicJobTaskStatusPartiallyApproved: 'Partially approved',
      mechanicJobPendingApproval: 'Pending approval',
      mechanicJobInstalled: 'Installed',
      mechanicJobHoursUnit: 'h',
      mechanicJobVehicle: 'Vehicle',
      mechanicJobPlate: 'Plate',
      mechanicJobCustomer: 'Customer',
      mechanicJobMileageIn: 'Mileage In',
      mechanicJobFuelLevel: 'Fuel Level',
      mechanicJobReportedIssues: 'Reported Issues',
      mechanicJobTasks: 'Tasks',
      mechanicJobExpand: 'Expand',
      mechanicJobCollapse: 'Collapse',
      mechanicJobNoTasksAssigned: 'No tasks assigned yet',
      mechanicJobDone: 'Done',
      mechanicJobReportIssue: 'Report Issue',
      mechanicJobUploading: 'Uploading...',
      mechanicJobWaitingQuotationApproval: 'Waiting for Quotation Approval',
      mechanicJobPendingPartsDesc: 'The following parts are pending admin quotation approval and cannot be installed yet:',
      mechanicJobPendingServicesDesc: 'The following services are pending admin quotation approval:',
      mechanicJobPendingCount: '{{count}} pending approval',
      mechanicJobQCFailedTitle: 'Quality Check Failed - Rework Required',
      mechanicJobQCFailedReason: 'Reason',
      mechanicJobQCFailedNote: 'Please fix the issues and complete the task again for re-inspection.',
      mechanicJobQCFailedMustFix: 'QC Failed Tasks Must Be Fixed First',
      mechanicJobReworkBeforeFinding: 'Complete rework before adding new findings',
      mechanicJobCompleteAfterRework: 'Complete Task (After Rework)',
      mechanicJobStartRepairFirst: 'Start repair first',
      mechanicJobOK: 'OK',
      mechanicJobCancel: 'Cancel',
      // QR Scan (Feature 1)
      scanQR: 'Scan QR Code',
      scanQRTooltip: 'Scan windshield QR to open job',
      qrScanTitle: 'Scan Job QR Code',
      qrScanHint: 'Position the windshield QR code within the frame',
      qrScanSuccess: 'Job found — opening…',
      qrScanNotFound: 'QR code not recognised or job not in this garage',
      qrScanForeignGarage: 'This job belongs to a different garage',
      qrScanError: 'Failed to scan QR code',
      // Request Assistance (Feature 3)
      requestAssistance: 'Request Assistance',
      assistanceModalTitle: 'Request Assistance',
      assistanceReasonCategory: 'Reason',
      assistanceNotes: 'Details / Notes',
      assistanceNotesPlaceholder: 'e.g., Need help with engine timing check…',
      assistanceSend: 'Send Request',
      assistanceSending: 'Sending…',
      assistanceSentSuccess: 'Assistance request sent to admin',
      assistanceSendFailed: 'Failed to send assistance request',
      assistanceReasonRequired: 'Please enter a reason or notes',
      assistanceCategory_stuck: 'Stuck / Unable to proceed',
      assistanceCategory_tools: 'Need special tools',
      assistanceCategory_parts: 'Part not in stock',
      assistanceCategory_safety: 'Safety concern',
      assistanceCategory_other: 'Other',
      // Super-admin Garages page (Feature 4)
      allGarages: 'All Garages',
      garagesPageTitle: 'Garages',
      garagesPageDescription: 'All garages across all companies',
      garageNameColumn: 'Garage',
      garageCompanyColumn: 'Company',
      garageCityColumn: 'City',
      garageActiveColumn: 'Active',
      garageCreatedColumn: 'Created',
      garageDeleteTitle: 'Delete Garage',
      garageDeleteMessage: 'Permanently delete this garage? This cannot be undone.',
      garageDeleteConfirm: 'Delete Garage',
      garageDeleteSuccess: 'Garage deleted',
      garageDeleteFailed: 'Failed to delete garage',
      garageHasJobCards: 'Cannot delete — garage has {{count}} job card(s). Reassign or archive them first.',
      // Report Issue Modal
      issueModalTitle: 'Report Issue',
      issueModalIssueType: 'Issue Type',
      issueModalPriority: 'Priority',
      issueModalDescription: 'Description',
      issueModalDescriptionPlaceholder: 'Describe the issue in detail...',
      issueModalPhotoEvidence: 'Photo Evidence',
      issueModalOpenCamera: 'Open Camera',
      issueModalCameraHint: 'Tap to take a photo of the issue',
      issueModalRetake: 'Retake Photo',
      issueModalViewPhoto: 'View Photo',
      issueModalHidePhoto: 'Hide Photo',
      issueModalSubmit: 'Submit Report',
      issueModalSubmitting: 'Submitting...',
      issueModalCancel: 'Cancel',
      issueTypeQuality: 'Quality Issue',
      issueTypeMissingPart: 'Missing Part',
      issueTypeWrongPart: 'Wrong Part',
      issueTypeDamage: 'Damage Found',
      issueTypeAdditional: 'Additional Work Needed',
      issueTypeCustomer: 'Need Customer Approval',
      issueTypeOther: 'Other',
      issueModalPriorityLow: 'Low',
      issueModalPriorityNormal: 'Normal',
      issueModalPriorityHigh: 'High',
      issueModalPriorityUrgent: 'Urgent',
      
      // Job Card Details
      jobCardDetailsTaskAddedSuccess: 'Task added successfully',
      jobCardDetailsTaskAddFailed: 'Failed to add task',
      jobCardDetailsConfirmDeleteTask: 'Are you sure you want to delete this task? This action cannot be undone.',
      jobCardDetailsTaskDeletedSuccess: 'Task deleted successfully',
      jobCardDetailsTaskDeleteFailed: 'Failed to delete task',
      jobCardDetailsEditModeEnabled: 'Edit mode enabled!',
      jobCardDetailsEditModeCancelled: 'Edit mode cancelled',
      jobCardDetailsGenerating: 'Generating...',
      jobCardDetailsGenerateUpdated: 'Generate Updated Quotation',
      jobCardDetailsEditQuotation: 'Edit Quotation',
      jobCardDetailsResendSMS: 'Resend SMS',
      jobCardDetailsSending: 'Sending...',
      jobCardDetailsEditModeActive: 'Edit Mode Active • Modify items below',
      jobCardDetailsStaffViewActive: 'Staff View Active - Prices Hidden',
      jobCardDetailsViewMode: 'View Mode:',
      jobCardDetailsManageAssignments: 'Manage Assignments',
      jobCardDetailsInteriorCondition: 'Interior Condition',
      jobCardDetailsCustomerNotifiedSMS: 'Customer notified via SMS • Awaiting response',
      jobCardDetailsWaitBeforeResending: 'Wait {{minutes}}m before resending',
      jobCardDetailsConfirmTaskStatusChange: 'Are you sure you want to change the task status to {{status}}?',
      jobCardDetailsTaskUpdatedSuccess: 'Task updated successfully',
      jobCardDetailsTaskUpdateFailed: 'Failed to update task',
      jobCardDetailsTaskPayerUpdated: 'Task payer updated',
      jobCardDetailsTaskPayerUpdateFailed: 'Failed to update task payer',
      jobCardDetailsTaskStatusUpdated: 'Task status updated',
      jobCardDetailsTaskStatusUpdateFailed: 'Failed to update task status',
      jobCardDetailsMechanicAssignedSuccess: 'Technician assigned successfully',
      jobCardDetailsMechanicAssignFailed: 'Failed to assign technician',
      jobCardDetailsRemoveServiceTitle: 'Remove Service',
      jobCardDetailsRemoveServiceFromQuotationMessage: 'Are you sure you want to remove this service from the quotation? It will be hidden until you generate the updated quotation.',
      jobCardDetailsServiceHiddenToast: 'Service hidden (will be removed when you generate updated quotation)',
      jobCardDetailsRemoveServicePermanentMessage: 'Are you sure you want to permanently remove this service?',
      jobCardDetailsServiceRemoved: 'Service removed',
      jobCardDetailsRemoveServiceFailed: 'Failed to remove service',
      jobCardDetailsRemovePartTitle: 'Remove Part',
      jobCardDetailsRemovePartFromQuotationMessage: 'Are you sure you want to remove this part from the quotation? It will be hidden until you generate the updated quotation.',
      jobCardDetailsPartHiddenToast: 'Part hidden (will be removed when you generate updated quotation)',
      jobCardDetailsRemovePartPermanentMessage: 'Are you sure you want to permanently remove this part?',
      jobCardDetailsPartRemovedFromTask: 'Part removed from task',
      jobCardDetailsRemovePartFailed: 'Failed to remove part',
      jobCardDetailsConfirmAcceptPart: 'Are you sure you want to accept this part request?',
      jobCardDetailsPartApprovedSuccess: 'Part approved successfully',
      jobCardDetailsPartApproveFailed: 'Failed to approve part',
      jobCardDetailsPromptRejectionReason: 'Please enter rejection reason:',
      jobCardDetailsPartRejected: 'Part rejected',
      jobCardDetailsPartRejectFailed: 'Failed to reject part',
      jobCardDetailsPromptEnterQuantity: 'Enter new quantity:',
      jobCardDetailsPromptEnterUnitPriceSAR: 'Enter new unit price (SAR):',
      jobCardDetailsPromptEnterPartNumber: 'Enter part number (optional):',
      jobCardDetailsPartUpdatedSuccess: 'Part updated successfully',
      jobCardDetailsPartUpdateFailed: 'Failed to update part',
      jobCardDetailsTaskDuplicatedSuccess: 'Task duplicated successfully',
      jobCardDetailsTaskDuplicateFailed: 'Failed to duplicate task',
      jobCardDetailsJobDetailsUpdatedSuccess: 'Job details updated successfully',
      jobCardDetailsJobDetailsUpdateFailed: 'Failed to update job details',
      jobCardDetailsDiagnosisStartedSuccess: 'Diagnosis started! You can now add tasks.',
      jobCardDetailsDiagnosisStartFailed: 'Failed to start diagnosis',
      jobCardDetailsRepairStartedSuccess: 'Repair started!',
      jobCardDetailsRepairStartFailed: 'Failed to start repair',
      jobCardDetailsNotAssignedError: "This job card isn't assigned to you — ask your supervisor to assign it.",
      jobCardDetailsJobPutOnHoldSuccess: 'Job card put on hold',
      jobCardDetailsJobPutOnHoldFailed: 'Failed to put job on hold',
      jobCardDetailsJobResumedSuccess: 'Job card resumed',
      jobCardDetailsJobResumeFailed: 'Failed to resume job',
      jobCardDetailsDeliveryCodeResentSuccess: 'New delivery code generated and sent to customer via SMS',
      jobCardDetailsDeliveryCodeResendFailed: 'Failed to resend delivery code',
      jobCardDetailsInvoiceGeneratingLoading: 'Generating invoice and sending payment link...',
      jobCardDetailsInvoiceGeneratedSuccess: 'Invoice generated and payment link sent to customer!',
      jobCardDetailsInvoiceGenerateFailed: 'Failed to generate invoice',
      jobCardDetailsVehicleDeliveredSuccess: 'Vehicle delivered successfully!',
      jobCardDetailsNoQuotationFound: 'No quotation found',
      jobCardDetailsQuotationSMSSentWithRemaining: 'Quotation SMS sent! {{remaining}} resend(s) remaining',
      jobCardDetailsMaxResendsReachedCount: 'Maximum resends reached ({{used}}/{{max}})',
      jobCardDetailsResendsUsedCount: '{{used}}/{{max}} resends used',
      jobCardDetailsResendSMSFailed: 'Failed to resend SMS',
      jobCardDetailsNoInvoiceFound: 'No invoice found',
      jobCardDetailsConvertToRegularSuccess: 'Job card converted to regular service successfully',
      jobCardDetailsConvertToRegularFailed: 'Failed to convert job card',
      jobCardDetailsCreateQuotationFailed: 'Failed to create quotation',
      jobCardDetailsNoExistingQuotation: 'No existing quotation found',
      jobCardDetailsNoItemsForQuotation: 'No services or parts to include in quotation',
      jobCardDetailsPrepareQuotationFailed: 'Failed to prepare quotation: {{error}}',
      jobCardDetailsQuotationCreatedSentApproval: 'Quotation created and sent for customer approval!',
      jobCardDetailsInvoicePreviewPrepareFailed: 'Failed to prepare invoice preview',
      jobCardDetailsNoNewItemsQuoteTooltip: 'No new items to quote. All pending items are already in a quotation.',
      jobCardDetailsPendingApprovalBadge: 'Pending',
      jobCardDetailsServiceAddedTemp: 'Service added temporarily (will be saved when you generate updated quotation)',
      jobCardDetailsPartAddedTemp: 'Part added temporarily (will be saved when you generate updated quotation)',
      jobCardDetailsConfirmRemove: 'Remove',
      jobCardDetailsTaskBasedViewNew: 'Task-Based View (NEW)',
      jobCardDetailsLegacyView: 'Legacy View',
      jobCardDetailsInteriorPhotoAlt: 'Interior {{number}}',
      jobCardDetailsExteriorCondition: 'Exterior Condition',
      jobCardDetailsExteriorPhotoAlt: 'Exterior {{number}}',
      jobCardDetailsExteriorVideos: 'Exterior Video',
      jobCardDetailsInteriorVideos: 'Interior Video',
      jobCardDetailsMaxResendsReachedMessage: 'Maximum resends reached',
      jobCardDetailsInvoiceResendNoMoreInfo: 'No more resends available',
      jobCardDetailsInvoiceSMSSentWithRemaining: 'Invoice SMS sent! {{remaining}} resend(s) remaining',
      noUsersAssigned: 'No users assigned',
      waitingForPayment: 'Waiting for Payment',
      paymentLinkSentToCustomer: 'A payment link has been sent to the customer.',
      viewPaymentLink: 'View payment link',
      awaitingFinalPayment: 'Awaiting Final Invoice Payment',
      finalInvoicePaymentLinkSent: 'The final invoice payment link has been sent to the customer.',
      
      // Public Quotation
      publicQuotationItemTypePart: 'Part',
      publicQuotationItemTypeService: 'Service',
      publicQuotationFooterPoweredBy: 'Powered by MotorMind',
      publicQuotationFooterCopyright: '© {{year}} MotorMind. All rights reserved.',
      publicQuotationContinueToPayment: 'Continue to Payment',
      publicQuotationReceiptPaymentIntro:
        "You'll be taken to a secure page to complete your downpayment.",
      publicQuotationLoadingData: 'Loading quotation data...',
      publicQuotationInvalidOTP: 'Invalid OTP. Please try again.',
      publicQuotationOTPVerified: 'OTP verified successfully',
      publicQuotationApprovalSuccess: 'Your approval has been submitted successfully',
      publicQuotationApprovalFailed: 'Failed to submit approval',
      publicQuotationToastInvalidOTP: 'Invalid OTP',
      publicQuotationToastOTPVerified: 'OTP verified',
      publicQuotationToastApprovalSubmitted: 'Approval submitted',
      publicQuotationToastApprovalFailed: 'Failed to submit',
      publicQuotationLoadFailed: 'Failed to load quotation',
      publicQuotationOtpSentMobile: 'OTP sent to your mobile number',
      publicQuotationSendOtpFailed: 'Failed to send OTP',
      publicQuotationOtpResentToast: 'New OTP sent',
      publicQuotationResendOtpFailed: 'Failed to resend OTP',
      publicQuotationSelectPaymentMethod: 'Please select a payment method',
      publicQuotationPreparingPayment: 'Preparing payment...',
      publicQuotationPaymentUnavailable: 'Payment not available. Please contact support.',
      publicQuotationRedirectingPayment: 'Redirecting to payment page...',
      publicQuotationCreatePaymentFailed: 'Failed to create payment',
      publicQuotationTamaraCheckoutFailed: 'Failed to create Tamara checkout',
      publicQuotationEnterValidOtp: 'Please enter valid 6-digit OTP',
      publicQuotationConfirmedSuccess: 'Confirmed successfully!',
      publicQuotationApprovedSuccess: 'Quotation approved successfully!',
      publicQuotationApproveFailed: 'Failed to approve quotation',
      
      // Common UI
      commonOk: 'OK',
      commonDone: 'Done',
      commonReportIssue: 'Report Issue',
      commonUploading: 'Uploading...',
      commonSubmitting: 'Submitting...',
      commonPlaceholderZero: '0.00',
      commonQuantityPlaceholder: '1',
      commonSARSuffix: 'SAR',
      commonPhotos: 'Photos',
      commonImages: 'Images',
      active: 'Active',
      all: 'All',
      noMatchingJobs: 'No matching jobs',
      noJobsFound: 'No jobs found',
      tryDifferentSearchTerm: 'Try a different search term',
      checkBackLaterForNewAssignments: 'Check back later for new assignments',
      plateNumber: 'Plate Number',
      reportedIssue: 'Reported Issue',
      createdJobCard: 'Created job card',
      changedStatusFrom: 'Changed status from',
      to: 'to',
      partAdded: 'Part Added',
      addNewTask: 'Add New Task',
      // Service-first restructure: the create-task entry point is now the
      // secondary "group work under a named complaint" action (used for
      // split insurer/customer billing) — a service no longer needs a task
      // first. Reused in the Tasks-section header and the empty-state.
      addComplaint: 'Add Complaint',
      addYourFirstTask: 'Add Your First Task',
      noTasksAddedYet: 'No tasks added yet',
      noActivityRecorded: 'No activity recorded yet',
      km: 'KM',
      
      // Job Card Details Page
      readOnlyAccess: 'Read-Only Access',
      readOnlyMessage: 'You can view this job card and its activities, but cannot make any changes. Contact your manager for assistance.',
      closeJobCard: 'Close Job Card',
      editDetails: 'Edit Details',
      printCodes: 'Print Codes',
      printCode: 'Print Code',
      barcode: 'Barcode',
      qrCode: 'QR Code',
      forCarKeySticker: 'For car key sticker',
      forWindshieldPlacement: 'For windshield placement',
      printBarcode: 'Print Barcode',
      printQRCode: 'Print QR Code',
      qrCodeInfo: 'Print and place on windshield for easy scanning',
      barcodeInfo: 'Print as small sticker for car key attachment',
      saving: 'Saving...',
      saveChanges: 'Save Changes',
      basicInformation: 'Basic Information',
      vehicleSpecifications: 'Vehicle Specifications',
      damageInspectionDetails: 'Damage Inspection & Details',
      workflowProgress: 'Workflow Progress',
      nextSteps: 'Next Steps',
      generateQuotation: 'Generate Quotation',
      totalCost: 'Total Cost',
      inProgress: 'In Progress',
      completed: 'Completed',
      totalTasks: 'Total Tasks',
      vehicle: 'Vehicle',
      customer: 'Customer',
      customerInformation: 'Customer Information',
      vehicleInformation: 'Vehicle Information',
      generating: 'Generating...',
      passed: 'Passed',
      failed: 'Failed',
      notStarted: 'Not Started',
      diagnosis: 'Diagnosis',
      recentlyFixed: 'Recently fixed',
      test: 'Test',
      diagnosisNotes: 'Diagnosis Notes',
      na: 'N/A',
      vehicleBodyType: 'Vehicle Body Type',
      sedan: 'Sedan',
      checkEngine: 'Check Engine',
      
      // Job Card Modal - Additional
      empty: 'Empty',
      quarterTank: '1/4 Tank',
      halfTank: '1/2 Tank',
      threeQuarterTank: '3/4 Tank',
      fullTank: 'Full Tank',
      selectLevel: 'Select Level',
      customerComplaint: 'Customer Complaint',
      describeIssue: 'Describe the issue',
      assignMechanic: 'Assign Technician',
      assignLater: 'Assign Later',
      normal: 'Normal',
      urgent: 'Urgent',
      interiorCondition: 'Interior Condition',
      documentInteriorCondition: 'Document vehicle interior condition at reception',
      exteriorCondition: 'Exterior Condition',
      documentExteriorCondition: 'Document vehicle exterior condition at reception',
      takeVideo: 'Take Video',
      soon: 'Soon',
      notifyCustomerVia: 'Notify Customer Via',
      whatsapp: 'WhatsApp',
      WhatsApp: 'WhatsApp',
      sms: 'SMS',
      notifyChannelWhatsappSms: 'WhatsApp & SMS',
      notifyChannelSmsOnly: 'SMS only',
      notifyChannelWhatsappSmsHint:
        'Notifications go via WhatsApp (falls back to SMS if unavailable). OTP & delivery codes always via SMS.',
      notifyChannelSmsOnlyHint: 'Everything, including notifications, is delivered via SMS.',
      notifyContactNumber: 'Will notify',
      waNotConnectedWarning:
        'WhatsApp is not connected — all notifications and OTP codes will be delivered via SMS.',
      otpAlwaysViaSms: 'One-time codes (OTP) and delivery codes are always sent via SMS.',
      notifChannelBadgeWhatsapp: 'Notifications: WhatsApp',
      notifChannelBadgeSms: 'Notifications: SMS',
      notifChannelChange: 'Change notification channel',
      notifChannelUpdated: 'Notification channel updated',
      notifChannelUpdateFailed: 'Failed to update notification channel',
      'qt.sendsViaWhatsappSms': 'This quotation will be sent to the customer via WhatsApp & SMS.',
      'qt.sendsViaSmsOnly': 'This quotation will be sent to the customer via SMS.',
      'qt.channelSetOnJobCard': 'The delivery channel follows the job card notification settings.',
      notificationLanguage: 'Notification Language',
      
      // Warning Lights
      warningLights: 'Warning Lights',
      warningLightsIfAny: 'Warning Lights (if any)',
      noWarningLightsSelected: 'No warning lights selected',
      
      // Damage Inspection
      damageInspection: 'Damage Inspection',
      vehicleType: 'Vehicle Type',
      sedanStandardCar: 'Sedan (4-door) - Standard passenger car',
      top: 'Top',
      right: 'Right',
      left: 'Left',
      rear: 'Rear',
      front: 'Front',
      clickDiagramToMarkDamage: 'Click on the diagram to mark damage. Click a marker to add photos/comments',
      generalGlobalNotes: 'General/ Global Notes',
      anyGeneralNotes: 'Any general notes about the vehicle condition...',
      point: 'Point',
      describeDamage: 'Describe damage (e.g. scratch, dent)',
      done: 'Done',
      
      // Vehicle Shapes
      suv: 'SUV',
      truck: 'Truck',
      van: 'Van',
      coupe: 'Coupe',
      hatchback: 'Hatchback',
      convertible: 'Convertible',
      wagon: 'Wagon',
      pickup: 'Pickup',

      // i18n sweep — job card experience (preview modal, history, mechanic pages, invoices)
      sar: 'SAR',
      sarPerUnit: 'SAR/unit',
      service: 'Service',
      remaining: 'Remaining',
      paidAmount: 'Paid Amount',
      accessRestricted: 'Access Restricted',
      notAssignedToYou: 'Not Assigned to You',
      notAssignedToJobCard: 'You are not assigned to this job card',
      jobCardInformation: 'Job Card Information',
      noteLabel: 'Note',
      previewSupervisorNote: 'Contact your supervisor if you need access to this job card.',
      backToJob: 'Back to Job',
      adminComment: 'Admin Comment',
      timeExceeded: 'Time Exceeded!',
      timeRemaining: 'Time Remaining',
      instructionsFromAdmin: 'Instructions from Admin',
      audioNotSupported: 'Your browser does not support audio playback.',
      requestPart: 'Request Part',
      noPartsAddedYet: 'No parts added yet',
      noServicesAddedYet: 'No services added yet',
      taskPhotos: 'Task Photos',
      taskPhotoAlt: 'Task photo {{index}}',
      taskNotFound: 'Task not found',
      serviceRequestSubmitted: 'Service request submitted - Admin will review',
      failedToAddService: 'Failed to add service',
      taskAddedSuccessfully: 'Task added successfully',
      failedToAddTask: 'Failed to add task',
      hidePhoto: 'Hide Photo',
      viewPhoto: 'View Photo',
      resolve: 'Resolve',
      resolutionNotePlaceholder: 'Resolution note (optional)',
      markResolved: 'Mark Resolved',
      images: 'Images',
      downpayment: 'Downpayment',
      finalInvoice: 'Final Invoice',
      invoice: 'Invoice',
      returnInvoice: 'Return Invoice',
      noInvoices: 'No invoices found for this job card',
      type: 'Type',
      totalAmount: 'Total Amount',
      totalPaid: 'Total Paid',
      totalPending: 'Total Pending',
      lineItems: 'Line Items',
      addFeeItem: 'Add Fee/Item',
      recordPayment: 'Record Payment',
      amountSAR: 'Amount (SAR)',
      remainingBalanceHint: 'Remaining balance: {{amount}} SAR',
      maxAmountPlaceholder: 'Max: {{amount}}',
      createReturn: 'Create Return',
      createReturnInvoice: 'Create Return Invoice',
      returnInvoiceWarning: 'This will create a return invoice with negative amounts for all items in this invoice.',
      returnReason: 'Return Reason',
      returnReasonPlaceholder: 'Enter the reason for this return...',
      returnForOriginalInvoice: 'Return for original invoice (click to view original)',
      failedToLoadInvoice: 'Failed to load invoice',
      fillDescriptionAndPrice: 'Please fill description and price',
      itemAdded: 'Item added',
      failedToAddItem: 'Failed to add item',
      enterValidPaymentAmount: 'Please enter a valid payment amount',
      amountExceedsRemaining: 'Amount cannot exceed remaining balance of {{amount}} SAR',
      failedToRecordPayment: 'Failed to record payment',
      confirmDeleteInvoice: 'Are you sure you want to delete this invoice? This action cannot be undone.',
      provideReturnReason: 'Please provide a reason for the return',
      returnInvoiceCreated: 'Return invoice created successfully',
      failedToCreateReturnInvoice: 'Failed to create return invoice',
      quotationsAndInvoices: 'Quotations & Invoices',
      failedToLoadQuotationsInvoices: 'Failed to load quotations and invoices',
      // Appointments
      appointments: 'Appointments',
      'appt.status.pending': 'Pending',
      'appt.status.confirmed': 'Confirmed',
      'appt.status.checked_in': 'Checked In',
      'appt.status.converted': 'Converted',
      'appt.status.cancelled': 'Cancelled',
      'appt.status.no_show': 'No-show',
      'appt.view.day': 'Day',
      'appt.view.3day': '3 Days',
      'appt.view.week': 'Week',
      'appt.view.list': 'List',
      'appt.new': 'New Appointment',
      'appt.title': 'Appointments',
      'appt.loading': 'Loading appointments...',
      'appt.empty': 'No appointments found',
      'appt.emptyDesc': 'Create a new appointment to get started.',
      'appt.actions.confirm': 'Confirm',
      'appt.actions.checkIn': 'Check In',
      'appt.actions.cancel': 'Cancel',
      'appt.actions.noShow': 'No-show',
      'appt.actions.convert': 'Convert to Job Card',
      'appt.actions.converting': 'Converting...',
      'appt.booking.otpPromise': 'We will send a one-time code to your phone to confirm — no account needed.',
      'apa.openJobCard': 'Open Job Card',
      'board.new': 'New',
      'board.diagnosis': 'Diagnosis',
      'board.inService': 'In Service',
      'board.qc': 'Quality Check',
      'board.payment': 'Payment',
      'board.ready': 'Ready',
      'board.done': 'Done',
      'board.showingOfTotal': 'Showing {{shown}} of {{total}}',
      'board.loadMore': 'Load more',
      'board.countFromLoaded': 'Counted from the {{count}} job cards loaded so far — more exist',
      'paymentMethods.myfatoorah': 'MyFatoorah',
      'paymentMethods.tamara': 'Tamara',
      'paymentMethods.online': 'Online',
      'iv.customerLinkTitle': 'Customer link',
      'iv.collectRemaining': 'Collect Remaining',
      'iv.postSuccess': 'Invoice posted and issued',
      'iv.postFailed': 'Failed to post the invoice',
      'wa.ai.summaryTitle': 'TzamunAI summary',
      'wa.ai.summarize': 'Summarize conversation',
      'wa.ai.openFull': 'Open full analysis',
      'wa.openProfile': 'Open profile',
      'wa.templates': 'Quick replies',
      'wa.activeJobCard': 'Active job card',
      'ipa.post': 'Post & Issue',
      'appt.detail.title': 'Appointment Details',
      'appt.detail.customer': 'Customer',
      'appt.detail.linkedProfile': 'Linked profile',
      'appt.detail.phone': 'Phone',
      'appt.detail.service': 'Service',
      'appt.detail.dateTime': 'Date & Time',
      'appt.detail.vehicle': 'Vehicle',
      'appt.detail.notes': 'Notes',
      'appt.detail.source': 'Source',
      'appt.detail.status': 'Status',
      'appt.detail.number': 'Appointment #',
      'appt.confirm.title': 'Confirm Appointment',
      'appt.confirm.msg': 'Confirm this appointment and notify the customer?',
      'appt.cancel.title': 'Cancel Appointment',
      'appt.cancel.msg': 'Are you sure you want to cancel this appointment?',
      'appt.cancel.reason': 'Reason (optional)',
      'appt.noshow.title': 'Mark as No-show',
      'appt.noshow.msg': 'Mark this appointment as no-show?',
      'appt.convert.title': 'Convert to Job Card',
      'appt.convert.msg': 'Convert this appointment to a job card? A customer and vehicle will be created if needed.',
      'appt.converted.toast': 'Appointment converted — opening job card',
      'appt.confirmed.toast': 'Appointment confirmed',
      'appt.checkedIn.toast': 'Customer checked in',
      'appt.cancelled.toast': 'Appointment cancelled',
      'appt.noshow.toast': 'Marked as no-show',
      'appt.created.toast': 'Appointment created',
      'appt.error.load': 'Failed to load appointments',
      'appt.error.action': 'Action failed',
      'appt.modal.title': 'New Appointment',
      'appt.modal.customerName': 'Customer Name',
      'appt.modal.phone': 'Phone',
      'appt.modal.serviceType': 'Service Type',
      'appt.modal.selectService': 'Select a service...',
      'appt.modal.date': 'Date',
      'appt.modal.slot': 'Time Slot',
      'appt.modal.selectSlot': 'Select a slot...',
      'appt.modal.notes': 'Notes',
      'appt.modal.linkCustomer': 'Link existing customer',
      'appt.modal.unlinkCustomer': 'Unlink',
      'appt.modal.selectVehicle': 'Customer vehicle',
      'appt.modal.manualVehicle': 'Enter vehicle manually',
      'appt.modal.vehicle': 'Vehicle (optional)',
      'appt.modal.make': 'Make',
      'appt.modal.model': 'Model',
      'appt.modal.year': 'Year',
      'appt.modal.plate': 'Plate',
      'appt.modal.create': 'Create Appointment',
      'appt.modal.creating': 'Creating...',
      'appt.modal.loadingSlots': 'Loading slots...',
      'appt.modal.noSlots': 'No available slots for this date',
      'appt.col.number': 'Appt #',
      'appt.col.customer': 'Customer',
      'appt.col.phone': 'Phone',
      'appt.col.service': 'Service',
      'appt.col.dateTime': 'Date & Time',
      'appt.col.status': 'Status',
      'appt.col.actions': 'Actions',
      'appt.filter.allStatuses': 'All Statuses',
      'appt.calendar.closedDay': 'Closed',
      'appt.calendar.today': 'Today',
      'appt.calendar.prev': 'Previous',
      'appt.calendar.next': 'Next',
      'appt.settings.title': 'Appointment Settings',
      'appt.settings.slotSettings': 'Slot settings',
      'appt.settings.timezoneNote': 'Riyadh time',
      'appt.settings.shiftStart': 'Shift start',
      'appt.settings.shiftEnd': 'Shift end',
      'appt.settings.enabled': 'Enable online booking',
      'appt.settings.enabledDesc': 'Allow customers to book appointments online',
      'appt.settings.publicUrl': 'Public booking link',
      'appt.settings.publicUrlDesc': 'Share this link with your customers so they can book online.',
      'appt.settings.copy': 'Copy',
      'appt.settings.copied': 'Copied',
      'appt.settings.urlCopied': 'Booking link copied',
      'appt.settings.openLink': 'Open',
      'appt.settings.autoConfirm': 'Auto-confirm bookings',
      'appt.settings.autoConfirmDesc': 'Automatically confirm new bookings without manual review',
      'appt.settings.slotMinutes': 'Slot length (minutes)',
      'appt.settings.capacity': 'Bookings per slot',
      'appt.settings.leadTime': 'Lead time (minutes)',
      'appt.settings.horizon': 'Booking horizon (days)',
      'appt.settings.reminderLead': 'Reminder lead time (minutes)',
      'appt.settings.workingHours': 'Working Hours',
      'appt.settings.daysOff': 'Days Off',
      'appt.settings.addDayOff': 'Add day off',
      'appt.settings.removeDayOff': 'Remove day off',
      'appt.settings.closed': 'Closed',
      'appt.settings.addShift': 'Add shift',
      'appt.settings.removeShift': 'Remove shift',
      'appt.settings.save': 'Save Settings',
      'appt.settings.saving': 'Saving...',
      'appt.settings.saved': 'Appointment settings saved',
      'appt.settings.saveFailed': 'Failed to save settings',
      'appt.settings.loadFailed': 'Failed to load settings',
      'appt.settings.sun': 'Sun',
      'appt.settings.mon': 'Mon',
      'appt.settings.tue': 'Tue',
      'appt.settings.wed': 'Wed',
      'appt.settings.thu': 'Thu',
      'appt.settings.fri': 'Fri',
      'appt.settings.sat': 'Sat',
      'appt.booking.title': 'Book an Appointment',
      'appt.booking.selectService': 'Select Service',
      'appt.booking.selectServiceDesc': 'Choose the type of service you need',
      'appt.booking.selectDate': 'Choose Date & Time',
      'appt.booking.selectDateDesc': 'Pick an available slot',
      'appt.booking.details': 'Your Details',
      'appt.booking.detailsDesc': 'Tell us about your vehicle',
      'appt.booking.verify': 'Verify Phone',
      'appt.booking.verifyDesc': 'Confirm your phone number',
      'appt.booking.done': 'Confirmed!',
      'appt.booking.name': 'Full Name',
      'appt.booking.phone': 'Phone Number',
      'appt.booking.phoneHint': 'Saudi mobile (e.g. 05XXXXXXXX)',
      'appt.booking.vehicleMake': 'Vehicle Make',
      'appt.booking.vehicleModel': 'Vehicle Model',
      'appt.booking.vehicleYear': 'Year',
      'appt.booking.vehiclePlate': 'Plate Number',
      'appt.booking.notes': 'Requested service',
      'appt.booking.selectMake': 'Select make',
      'appt.booking.selectModel': 'Select model',
      'appt.booking.selectYear': 'Select year',
      'appt.booking.selectMakeFirst': 'Select make first',
      'appt.booking.other': 'Other',
      'appt.booking.enterMake': 'Enter vehicle make',
      'appt.booking.enterModel': 'Enter vehicle model',
      'appt.booking.language': 'Language',
      'appt.booking.next': 'Next',
      'appt.booking.back': 'Back',
      'appt.booking.sendOtp': 'Send Verification Code',
      'appt.booking.verifyOtp': 'Verify',
      'appt.booking.resendOtp': 'Resend code',
      'appt.booking.otpSent': 'Code sent to your phone',
      'appt.booking.otpLabel': 'Verification Code',
      'appt.booking.otpHint': 'Enter the 6-digit code',
      'appt.booking.submit': 'Book Appointment',
      'appt.booking.submitting': 'Booking...',
      'appt.booking.confirmationTitle': 'Appointment Confirmed!',
      'appt.booking.confirmationDesc': 'Your appointment has been booked. A confirmation SMS has been sent.',
      'appt.booking.appointmentNumber': 'Appointment Number',
      'appt.booking.dateTime': 'Date & Time',
      'appt.booking.slotFull': 'That slot was just taken — please choose another.',
      'appt.booking.notAvailable': 'Online booking is not available for this garage.',
      'appt.booking.loadFailed': 'Failed to load booking page.',
      'appt.booking.noSlots': 'No available slots for this date.',
      'appt.booking.sendingOtp': 'Sending...',
      'appt.booking.otpExpired': 'Code expired. Please request a new one.',
      'appt.booking.otpInvalid': 'Invalid code.',
      'appt.booking.nameRequired': 'Name is required',
      'appt.booking.phoneRequired': 'Phone is required',
      'appt.booking.serviceRequired': 'Please select a service',
      'appt.booking.slotRequired': 'Please select a time slot',
      'appt.booking.bookAgain': 'Book Another Appointment',
      'appt.source.public': 'Online',
      'appt.source.staff': 'Staff',
      'appt.source.walk_in': 'Walk-in',

      // ── T&C settings keys (tnc.*)
      'tnc.title': 'Terms & Conditions',
      'tnc.subtitle': 'Generate and publish bilingual T&C for your customers',
      'tnc.legalInfo': 'Legal Information',
      'tnc.legalNameEn': 'Legal Name (English)',
      'tnc.legalNameAr': 'Legal Name (Arabic)',
      'tnc.crNumber': 'CR Number',
      'tnc.vatNumber': 'VAT Number',
      'tnc.address': 'Address',
      'tnc.phone': 'Phone',
      'tnc.email': 'Email',
      'tnc.specialArticles': 'Special Articles / Additional Terms',
      'tnc.specialArticlesPlaceholder': 'List any special terms, e.g. "No liability for personal items left in vehicle"',
      'tnc.generateWithAI': 'Generate with AI',
      'tnc.generating': 'Generating (this may take a couple of minutes)...',
      'tnc.generatingNote': 'AI is writing your T&C — this can take a few minutes.',
      'tnc.generated': 'T&C generated successfully',
      'tnc.generateFailed': 'Generation failed. Please try again.',
      'tnc.writeOwn': 'Write my own',
      'tnc.enhanceWithAI': 'Enhance with AI',
      'tnc.enhancing': 'Enhancing...',
      'tnc.enhanced': 'T&C enhanced successfully',
      'tnc.enhanceFailed': 'Enhancement failed. Please try again.',
      'tnc.enhanceHint': 'Enhance keeps your articles and expands the document; anything in "Special Articles" above is added as new clauses.',
      'tnc.suggestionsTitle': 'Recommended additions',
      'tnc.suggestionsHint': 'Your document does not yet cover these. Select any to add, then regenerate.',
      'tnc.regenerateWithSelected': 'Regenerate with selected ({{count}})',
      'tnc.edit': 'Edit',
      'tnc.preview': 'Preview',
      'tnc.nothingToPreview': 'Nothing to preview yet.',
      'tnc.editor': 'Edit Content',
      'tnc.saveDraft': 'Save Draft',
      'tnc.draftSaved': 'Draft saved',
      'tnc.saveDraftFailed': 'Failed to save draft',
      'tnc.publish': 'Publish',
      'tnc.publishVersion': 'Publish v{{version}}',
      'tnc.publishRules': 'Publishing requires content in both languages · what customers agree to cannot be undone',
      'set.featuresTitle': 'Garage features',
      'set.featuresHint': 'Enabled per company — related items appear or disappear from the menus directly (hidden, not disabled)',
      'tnc.published': 'Terms published successfully',
      'tnc.publishFailed': 'Failed to publish',
      'tnc.publishConfirmTitle': 'Confirm Publish',
      'tnc.publishConfirmDesc': 'Version {{version}} will be what customers agree to. This cannot be undone.',
      'tnc.confirmPublish': 'Yes, Publish',
      'tnc.publishedVersion': 'Published version',
      'tnc.unpublishedDraft': 'There is an unpublished draft with changes.',
      'tnc.loadFailed': 'Failed to load T&C settings',

      // Tenant Users
      tenantUsers: 'Tenant Users',
      manageTenantUsers: 'View and manage users across all tenants',
      allCompanies: 'All companies',
      allRoles: 'All roles',
      resetPassword: 'Reset password',
      newPassword: 'New password',
      selectCompany: 'Select company',
      assignedGarages: 'Assigned garages',
      passwordResetSuccess: 'Password reset successfully',
      userUpdated: 'User updated',
      userCreated: 'User created',
      userDeleted: 'User deleted',
      deleteUserConfirm: 'Delete this user? This cannot be undone.',
      leaveBlankToKeep: 'Leave blank to keep current password',

      // ── Intake confirmation keys (intake.*)
      'intake.settingsTitle': 'Customer Intake Confirmation',
      'intake.settingsSubtitle': 'Require customer to confirm their job card before work begins',
      'intake.enableToggle': 'Require intake confirmation',
      'intake.enableToggleDesc': 'Job cards cannot leave "Received" status until the customer confirms via SMS.',
      'intake.noPublishedTermsHint': 'You must publish Terms & Conditions before enabling this feature.',
      'intake.configSaved': 'Setting saved',
      'intake.configFailed': 'Failed to save setting',
      'intake.title': 'Customer Intake Confirmation',
      'intake.send': 'Send for Customer Confirmation',
      'intake.resend': 'Re-send Confirmation Link',
      'intake.sendSuccess': 'Confirmation link sent',
      'intake.sendFailed': 'Failed to send confirmation',
      'intake.override': 'Override',
      'intake.overrideTitle': 'Override Intake Confirmation',
      'intake.overrideDesc': 'Provide a reason for overriding the confirmation requirement. This action is audit-logged.',
      'intake.overrideReasonField': 'Reason *',
      'intake.overrideReasonPlaceholder': 'e.g., Customer confirmed verbally, vehicle damage pre-existing...',
      'intake.overrideReasonRequired': 'A reason is required',
      'intake.overrideSuccess': 'Status overridden successfully',
      'intake.overrideFailed': 'Override failed',
      'intake.confirmOverride': 'Confirm Override',
      'intake.overrideReasonLabel': 'Override reason:',
      'intake.overriddenBy': 'By',
      'intake.disputeNoteLabel': "Customer's reported issue:",
      'intake.pendingHint': 'Waiting for customer to confirm via the link sent by SMS.',
      'intake.confirmationRequired': 'Customer confirmation is required before moving this job forward.',
      'intake.disputeHelperText': 'The customer reported an issue with the job card. Correct the job card details, then re-send the confirmation link — or override with a documented reason.',
      'intake.fixAndResend': 'Fix & Re-send',
      'intake.correctResendSuccess': 'Job card corrected and confirmation re-sent',
      'intake.correctResendFailed': 'Failed to correct and re-send',
      'intake.status.pending': 'Confirmation Pending',
      'intake.status.confirmed': 'Customer Confirmed',
      'intake.status.disputed': 'Disputed',
      'intake.status.overridden': 'Overridden',
      'jc.editReception': 'Correct Vehicle Reception',
      'jc.saveAndResend': 'Save & Re-send',
      'jc.homeBranchSuffix': '(Home)',
      'onb.view': 'View',
      // F5: Arabic voice notes (job-card level)
      'jc.voiceNotes.title': 'Voice Notes',
      'jc.voiceNotes.record': 'Voice note',
      'jc.voiceNotes.stop': 'Stop',
      'jc.voiceNotes.recording': 'Recording…',
      'jc.voiceNotes.save': 'Save',
      'jc.voiceNotes.discard': 'Discard',
      'jc.voiceNotes.empty': 'No voice notes yet',
      'jc.voiceNotes.delete': 'Delete note',
      'jc.voiceNotes.confirmDeleteTitle': 'Delete voice note?',
      'jc.voiceNotes.confirmDeleteMessage': 'This voice note will be permanently deleted.',
      'jc.voiceNotes.micDenied': "Couldn't access the microphone",
      'jc.voiceNotes.notSupported': 'Voice recording is not supported on this device',
      'jc.voiceNotes.saved': 'Voice note saved',
      'jc.voiceNotes.saveFailed': 'Failed to save voice note',
      'jc.voiceNotes.deleted': 'Voice note deleted',
      'jc.voiceNotes.deleteFailed': 'Failed to delete voice note',
      // MyFatoorah payments
      // — Garage settings: online payments
      'mfp.onlinePayments': 'Online Payments',
      'mfp.onlinePaymentsDesc': 'Accept card and online payments from your customers through MyFatoorah.',
      'mfp.paymentMode': 'Payment Mode',
      'mfp.useOwnAccount': 'Use My Own Account',
      'mfp.useOwnAccountDesc': 'Connect your own MyFatoorah merchant account. Funds settle directly to you.',
      'mfp.useMotorMindManaged': 'Managed by MotorMind',
      'mfp.useMotorMindManagedDesc': 'MotorMind sets up and manages the payment account on your behalf. A commission applies per transaction.',
      'mfp.apiKey': 'API Key',
      'mfp.webhookSecret': 'Webhook Secret',
      'mfp.testMode': 'Test Mode',
      'mfp.saveCredentials': 'Save Credentials',
      'mfp.credentialsConfigured': 'Credentials configured',
      'mfp.credentialsNotConfigured': 'Credentials not configured',
      // — Garage settings: supplier onboarding (KYC)
      'mfp.supplierOnboarding': 'Supplier Onboarding',
      'mfp.legalName': 'Legal Name',
      'mfp.mobile': 'Mobile Number',
      'mfp.email': 'Email',
      'mfp.iban': 'IBAN',
      'mfp.crNumber': 'Commercial Registration (CR) Number',
      'mfp.startOnboarding': 'Start Onboarding',
      'mfp.updateProfile': 'Update Profile',
      'mfp.uploadDocuments': 'Upload Documents',
      'mfp.documentType': 'Document Type',
      'mfp.docCr': 'Commercial Registration',
      'mfp.docIbanLetter': 'IBAN Letter',
      'mfp.docNationalId': 'National ID',
      'mfp.docOther': 'Other',
      'mfp.uploadKyc': 'Upload KYC Documents',
      'mfp.documentsUploaded': 'Documents uploaded successfully',
      'mfp.kycStatus': 'KYC Status',
      'mfp.statusNotStarted': 'Not Started',
      'mfp.statusSubmitted': 'Submitted',
      'mfp.statusUnderReview': 'Under Review',
      'mfp.statusApproved': 'Approved',
      'mfp.statusRejected': 'Rejected',
      'mfp.statusSuspended': 'Suspended',
      'mfp.rejectionReason': 'Rejection Reason',
      'mfp.enableOnlinePay': 'Enable Online Pay',
      'mfp.disableOnlinePay': 'Disable Online Pay',
      'mfp.onlinePayEnabled': 'Online pay enabled',
      'mfp.onlinePayDisabled': 'Online pay disabled',
      'mfp.commission': 'Commission',
      'mfp.commissionRate': 'Commission Rate',
      'mfp.commissionReadOnly': 'The commission is set by MotorMind and cannot be changed here.',
      'mfp.syncStatus': 'Sync Status',
      // — Super-admin: payment suppliers
      'mfp.paymentSuppliers': 'Payment Suppliers',
      'mfp.paymentSuppliersDesc': 'Manage online payment accounts and commissions across all companies.',
      'mfp.searchCompanies': 'Search companies...',
      'mfp.filterByMode': 'Filter by Mode',
      'mfp.filterByStatus': 'Filter by Status',
      'mfp.allModes': 'All Modes',
      'mfp.allStatuses': 'All Statuses',
      'mfp.setCommission': 'Set Commission',
      'mfp.commissionType': 'Commission Type',
      'mfp.commissionPercentage': 'Percentage (%)',
      'mfp.commissionFixed': 'Fixed (SAR)',
      'mfp.commissionNone': 'None',
      'mfp.commissionTypePercentage': 'Percentage',
      'mfp.commissionTypeFixed': 'Fixed',
      'mfp.commissionTypeBoth': 'Percentage + Fixed',
      'mfp.approveSupplier': 'Approve Supplier',
      'mfp.suspendSupplier': 'Suspend Supplier',
      'mfp.suspendSupplierConfirm': 'This will suspend the supplier and disable online payments for this company.',
      'mfp.commissionSaved': 'Commission saved',
      'mfp.overrideMode': 'Override Mode',
      'mfp.viewDocuments': 'View Documents',
      'mfp.downloadDocument': 'Download Document',
      'mfp.onboardOnBehalf': 'Onboard on Behalf',
      // — Refunds
      'mfp.refund': 'Refund',
      'mfp.refunds': 'Refunds',
      'mfp.refundPayment': 'Refund Payment',
      'mfp.refundAmount': 'Refund Amount',
      'mfp.refundReason': 'Refund Reason',
      'mfp.fullRefund': 'Full Refund',
      'mfp.partialRefund': 'Partial Refund',
      'mfp.issueRefund': 'Issue Refund',
      'mfp.refundIssued': 'Refund issued successfully',
      'mfp.refundFailed': 'Refund failed',
      'mfp.refundHistory': 'Refund History',
      'mfp.refundedAmount': 'Refunded Amount',
      'mfp.refundPending': 'Pending',
      'mfp.refundCompleted': 'Completed',

      // ── P5: Vehicle / Customer profiles + rich history ──────────────────
      'vp.title': 'Vehicle Profile',
      'vp.notFound': 'Vehicle not found',
      'vp.loadFailed': 'Failed to load history',
      'vp.backToVehicles': 'Back to vehicles',
      'vp.back': 'Back',
      'vp.history': 'History',
      'vp.registrationExpiry': 'Registration expiry',
      'vp.insuranceExpiry': 'Insurance expiry',
      'vp.totalVisits': 'Total visits',
      'vp.totalSpend': 'Total spend',
      'vp.lastService': 'Last service',
      'vp.mileage': 'Mileage',
      'vp.recurringIssues': 'Recurring issues detected',
      'vp.serviceHistory': 'Service history',
      'vp.noHistory': 'No service history yet',
      'vp.findingsCount': '{{count}} findings',
      'vp.partsCount': '{{count}} parts',
      'vp.previous': 'Previous',
      'vp.next': 'Next',
      'vp.pageOf': 'Page {{page}} of {{pages}}',
      'vp.aiInsight': 'AI history insight',
      'vp.generate': 'Generate',
      'vp.regenerate': 'Regenerate',
      'vp.insightEmpty': 'No AI insight yet. Generate one to summarize this vehicle’s service history and flag patterns.',
      'vp.insightGenerated': 'AI insight generated',
      'vp.insightFailed': 'Failed to generate insight',
      'vp.insightNoHistory': 'No service history to summarize yet',
      'vp.insightStale': 'New job cards exist since this was generated — regenerate for an up-to-date insight.',
      'vp.insightGeneratedAt': 'Generated',
      'cp.title': 'Customer Profile',
      'cp.notFound': 'Customer not found',
      'cp.backToCustomers': 'Back to customers',
      'cp.vehicles': 'Vehicles',
      'cp.noVehicles': 'No vehicles',
      'cp.recentJobCards': 'Recent job cards',
      'cp.visitsCount': '{{count}} visits',
      'cm.customers.name': 'Name',
      'cm.customers.location': 'Location',
      'cm.customers.profile': 'Profile',
      'cm.customers.spend': 'Lifetime spend',
      'cm.customers.balance': 'Balance',
      'cm.customers.owes': 'Outstanding',
      'cm.customers.hasCredit': 'Has credit',
      'veh.alerts': 'Alerts',
      'veh.alert.daysUnit': 'days',
      'veh.alert.registeredAbroad': 'Registered abroad',
      'credit.recipientsNote': 'Reminder recipients are managed in Notification settings.',
      // Staff (shop-floor) view — five-state status system (StatusPill).
      'staff.status.waiting': 'Waiting',
      'staff.status.in_progress': 'In progress',
      'staff.status.done': 'Done',
      'staff.status.problem': 'Problem',
      'staff.status.needs_approval': 'Needs approval',
      // Staff (shop-floor) view — media capture (MediaCapture / MediaGallery).
      'staff.capture.photo': 'Photo',
      'staff.capture.video': 'Video',
      'staff.capture.voice': 'Voice',
      'staff.capture.recording': 'Recording…',
      'staff.capture.stop': 'Stop',
      'staff.capture.remove': 'Remove',
      'staff.capture.tooBig': 'File too big',
      'staff.capture.notSupported': 'Not supported',
      'staff.capture.uploading': 'Uploading…',
      'staff.capture.uploadFailed': 'Upload failed',
      'staff.capture.retry': 'Retry',
      'staff.capture.empty': 'No media yet',
      'staff.capture.voiceNote': 'Voice note',
      // Report Problem (redesigned ReportIssueModal).
      'staff.report.title': 'Report a problem',
      'staff.report.pickType': 'What is wrong?',
      'staff.report.noteOptional': 'Add a note (optional)',
      'staff.report.notePlaceholder': 'Describe the problem…',
      'staff.report.priority': 'Priority',
      'staff.report.submit': 'Send report',
      // Staff (shop-floor) view — language toggle (LangToggle).
      'staff.lang.toggle': 'العربية',
      'staff.lang.toggleAria': 'Switch language',
      // Staff (shop-floor) view — My Work / Job / Task screen redesigns.
      'staff.myWork.title': 'My Work',
      'staff.myWork.scanQr': 'Scan QR',
      'staff.myWork.steps': '{{done}} of {{total}} steps',
      'staff.filter.active': 'Active',
      'staff.filter.ready': 'Ready',
      'staff.filter.all': 'All',
      'staff.job.customer': 'Customer',
      'staff.job.photos': 'Photos',
      'staff.job.addPhoto': 'Add photo / video',
      'staff.job.tasks': 'Tasks',
      'staff.job.noTasks': 'No tasks yet',
      'staff.job.qcWait': 'Waiting for quality check',
      'staff.job.qcWaitHint': 'A supervisor will review this job shortly',
      'staff.job.start': 'Start Job',
      'staff.job.complete': 'Complete Job',
      'staff.task.parts': 'Parts',
      'staff.task.services': 'Services',
      'staff.task.done': 'DONE',
      'staff.task.reportProblem': 'Report Problem',
      'staff.task.media': 'Photos & Videos',
      'staff.task.start': 'Start Task',
      'staff.task.complete': 'Complete Task',
      'staff.task.confirmCompleteTitle': 'Finish this task?',
      'staff.task.confirmCompleteMessage': 'The task will be marked completed. Make sure all work is actually done.',
      'staff.task.completed': 'Task completed',
      'staff.task.timeLeft': 'Time left',
      'staff.task.overtime': 'Over estimate',

      'staff.empty.noJobs': 'No jobs',
      'staff.empty.noTasks': 'No tasks',
      'staff.empty.noMedia': 'No media',
      'staff.empty.noHistory': 'No history',
      'staff.history.title': 'History',
      'staff.toast.mediaUploaded': 'Media added',

      'staff.task.startNow': 'Start now',
      'staff.task.notReadyToInstall': "Awaiting approval — don't install yet",
      'staff.task.startHint': 'Tap to begin working on this task',
      'staff.task.awaitingApprovalHint': 'Waiting for the customer to approve the price — do not start yet',
      'staff.task.noWorkToCompleteHint': 'Add the parts or services you worked on before finishing',
      'staff.task.finishItemsFirst': 'Mark the parts and services done first',
      'staff.task.partsNotReceivedHint': 'The customer\u2019s part must be confirmed received first. Ask the front desk to record the day it arrived.',
      'staff.task.partsNotReceivedList': 'Not confirmed received: {{parts}}',
      'staff.task.inProgress': 'In progress',
      'staff.task.paused': 'Paused',
      'staff.task.waitingToStart': 'Ready to start',

      // ── F6 offline queue (mechanic task-status taps survive dropped connectivity) ──
      'offline.pendingSync': 'Will send once you\'re back online',
      'offline.queueFull': 'Too many actions waiting to sync — try again once you\'re back online',
      'offline.expiredDropped': '{{count}} pending action(s) were too old to send and were dropped',
      'offline.syncFailedGeneric': 'Could not sync — please try again',

      'staff.filter.mine': 'My work',
      'staff.filter.team': 'Whole team',
      'staff.card.assignedTo': 'Assigned to',
      'staff.card.unassigned': 'Unassigned',
      'staff.card.mine': 'You',
      'staff.sort.label': 'Sort',

      'staff.reassign.taskTitle': 'Reassign this task',
      'staff.reassign.jobTitle': 'Reassign this job',
      'staff.reassign.cta': 'Reassign',
      'staff.reassign.open': 'Reassign',
      'staff.reassign.pick': 'Choose a team member',
      'staff.reassign.search': 'Search staff…',
      'staff.reassign.unassign': 'Unassign',
      'staff.reassign.success': 'Reassigned',
      'staff.reassign.failed': 'Could not reassign',
      'staff.reassign.noStaff': 'No staff available',
      'staff.reassign.currentLabel': 'Currently',
      'plan.userLimit': 'Your plan allows {{limit}} users and you already have {{current}}. Contact MotorMind to raise your plan.',
      'plan.branchLimit': 'Your plan allows {{limit}} branches and you already have {{current}}. Contact MotorMind to raise your plan.',
      'plan.aiQuota': 'You have used all {{limit}} AI requests included in your plan this month. It resets next month, or contact MotorMind to raise your plan.',
      'plan.featureDisabled': '{{feature}} is not part of your plan. Contact MotorMind to add it.',
      'plan.notInPlanTitle': 'Not part of your plan',
      'plan.notInPlanBody': 'This section is not included in your current plan. Contact MotorMind to add it.',
      'plan.backToDashboard': 'Back',
      'subscription.endedTitle': 'Subscription ended',
      'subscription.endedBody': "Your workshop's MotorMind subscription has ended, so access is paused. Nothing has been deleted — everything comes back the moment the subscription is renewed.",
      'subscription.suspendedBody': 'Your workshop\'s MotorMind account is suspended. Nothing has been deleted — access returns as soon as the account is reactivated.',
      'subscription.deactivatedBody': 'Your workshop\'s MotorMind account has been deactivated. Nothing has been deleted — access returns as soon as the account is reactivated.',
      'subscription.unavailableBody': "We couldn't confirm your workshop's subscription just now, so access is paused as a precaution. Nothing has been deleted. Please contact support — this one is on us, not you.",
      'subscription.endedOn': 'Ended on',
      'subscription.contactSupport': 'Please contact MotorMind to renew.',
      'subscription.graceDays': 'Grace period (days)',
      'subscription.graceDaysHint': 'Days of full access after the end date. 0 = locked out on the end date.',
      'settings.lockedNotice': 'Settings Locked',
      'settings.companyLevelViewOnly': 'At the company level, view only',
      'settings.feedback.repliesNote': 'Replies show up on the {{page}} page.',
      'settings.statusesSelected': 'statuses selected',
      'settings.contactAccountManager': 'To update this information, please contact your account manager.',
      'settings.namesCompanyLevel': 'Set at the company level — the branch display name mirrors the commercial name.',
      'settings.namesBranchAndCompany': 'The commercial name belongs to this branch (edit it in Edit Branch); the legal name is set on the company.',

      // ── Platform Analytics (F1 super-admin oversight) ──
      'pa.navTitle': 'Analytics',
      'pa.pageTitle': 'Platform Analytics',
      'pa.pageDescription': 'Cross-tenant oversight: job-card activity and AI usage across every company and branch.',
      'pa.totalCompanies': 'Companies',
      'pa.totalBranches': 'Branches',
      'pa.totalJobCards': 'Job Cards',
      'pa.aiUsageMonth': 'AI Usage (This Month)',
      'pa.recentTrend': '{{count}} in 30d',
      'pa.statusBreakdown': 'Job Card Status Breakdown',
      'pa.companies': 'Companies',
      'pa.noCompanies': 'No companies yet',
      'pa.noJobCards': 'No job cards',
      'pa.noBranches': 'No branches',
      'pa.branchCount': '{{count}} branch(es)',
      'pa.jobCards': 'Job Cards',
      'pa.total': 'Total',
      'pa.recent7': 'Last 7d',
      'pa.recent30': 'Last 30d',
      'pa.tasks': 'Tasks',
      'pa.aiUsage': 'AI usage',
      'pa.unlimited': 'Unlimited',
      'pa.loadError': 'Could not load analytics',
      'pa.viewJobCards': 'View this branch’s job cards',

      // ── Notification channels (Super Admin) ──
      'notifChannels.navTitle': 'Notification channels',
      'onbAlerts.title': 'Onboarding application alerts',
      'onbAlerts.subtitle': 'Super admins picked here get a notification and a mobile message when a client submits their onboarding application.',
      'onbAlerts.fallbackNotice': 'Nobody is picked, so every super admin is notified. Pick names below to narrow it down.',
      'onbAlerts.noAdmins': 'No active super admins to pick from.',
      'onbAlerts.noMobile': 'No mobile — alert only',
      'onbAlerts.save': 'Save recipients',
      'onbAlerts.saved': 'Recipients saved',
      'onbAlerts.saveFailed': 'Could not save the recipients',
      'onbAlerts.loadError': 'Could not load the super admin list',
      'adminAlerts.navTitle': 'Admin Notifications',
      'adminAlerts.title': 'Admin notifications',
      'adminAlerts.subtitle': 'Who on the platform team hears about each event. No selection on a panel means every super admin is notified.',
      'waLink.navTitle': 'WhatsApp Link',
      'waLink.title': 'WhatsApp Link',
      'waLink.subtitle': 'Link the phone the platform sends its own messages from. WhatsApp drops a linked device from time to time — when that happens, scan the code again here.',
      'waLink.testTitle': 'Send a test message',
      'waLink.testHint': 'A green Connected badge only means the link is open. Send a test to confirm a message actually arrives.',
      'waLink.testPhone': 'Send to',
      'waLink.testSend': 'Send test',
      'waLink.testSent': 'Test message sent',
      'waLink.testFailed': 'Could not send the test message',
      'qcAlerts.title': 'Dev deploy — ready to test',
      'qcAlerts.subtitle': 'Super admins picked here get a WhatsApp message from the linked phone when a build reaches dev, listing what went out and a short note on what to check.',
      'qcAlerts.fallbackNotice': 'Nobody is picked, so every super admin is notified. Pick names below to narrow it down.',
      'qcAlerts.noAdmins': 'No active super admins to pick from.',
      'qcAlerts.noMobile': 'No mobile — cannot receive this alert',
      'qcAlerts.save': 'Save recipients',
      'qcAlerts.saved': 'Recipients saved',
      'qcAlerts.saveFailed': 'Could not save the recipients',
      'qcAlerts.loadError': 'Could not load the super admin list',
      'prodAlerts.title': 'Production deploy — released',
      'prodAlerts.subtitle': 'A separate list: these people hear when a release reaches customers on production, with what went out in it. Keep it separate from the dev list if the two are different teams.',
      'prodAlerts.fallbackNotice': 'Nobody is picked, so every super admin is notified. Pick names below to narrow it down.',
      'prodAlerts.noAdmins': 'No active super admins to pick from.',
      'prodAlerts.noMobile': 'No mobile — cannot receive this alert',
      'prodAlerts.save': 'Save recipients',
      'prodAlerts.saved': 'Recipients saved',
      'prodAlerts.saveFailed': 'Could not save the recipients',
      'prodAlerts.loadError': 'Could not load the super admin list',
      'payRcvAlerts.title': 'Payment received alerts',
      'payRcvAlerts.subtitle': 'Super admins picked here get a WhatsApp message (SMS fallback) when a tenant payment lands through a managed gateway — tenant, payment method and amount.',
      'payRcvAlerts.fallbackNotice': 'Nobody is picked, so every super admin is notified. Pick names below to narrow it down.',
      'payRcvAlerts.noAdmins': 'No active super admins to pick from.',
      'payRcvAlerts.noMobile': 'No mobile — cannot receive this alert',
      'payRcvAlerts.save': 'Save recipients',
      'payRcvAlerts.saved': 'Recipients saved',
      'payRcvAlerts.saveFailed': 'Could not save the recipients',
      'payRcvAlerts.loadError': 'Could not load the super admin list',
      'payInqAlerts.title': 'Payout inquiry alerts',
      'payInqAlerts.subtitle': 'Super admins picked here get a WhatsApp message (SMS fallback) when a tenant raises an inquiry on a payout.',
      'payInqAlerts.fallbackNotice': 'Nobody is picked, so every super admin is notified. Pick names below to narrow it down.',
      'payInqAlerts.noAdmins': 'No active super admins to pick from.',
      'payInqAlerts.noMobile': 'No mobile — cannot receive this alert',
      'payInqAlerts.save': 'Save recipients',
      'payInqAlerts.saved': 'Recipients saved',
      'payInqAlerts.saveFailed': 'Could not save the recipients',
      'payInqAlerts.loadError': 'Could not load the super admin list',
      'extAlerts.title': 'External numbers',
      'extAlerts.subtitle': 'Phone numbers without a portal account that also receive this alert — an accountant, a partner, a second phone.',
      'extAlerts.namePlaceholder': 'Name (e.g. Accountant)',
      'extAlerts.phonePlaceholder': '05xxxxxxxx',
      'extAlerts.language': 'Message language',
      'extAlerts.langAr': 'Arabic',
      'extAlerts.langEn': 'English',
      'extAlerts.add': 'Add number',
      'extAlerts.added': 'Number added',
      'extAlerts.remove': 'Remove number',
      'extAlerts.removed': 'Number removed',
      'extAlerts.saveFailed': 'Could not save the number',
      'ann.gotIt': 'Got it',
      'ann.readMore': 'Read more',
      'ann.dontShowAgain': "Don't show again",
      'ann.dismissibleLabel': 'Appear once',
      'ann.dismissibleHint': "The reader can clear this for good. It will not come back for them, but a new announcement still will. Tick this and the end time becomes optional — every announcement must either expire on its own or be clearable.",
      'ann.endsAtOptionalHint': 'Optional now that it can be cleared. Leave it empty and it stays until each person dismisses it.',
      'ann.navTitle': 'Announcements',
      'ann.title': 'Platform announcements',
      'ann.subtitle': 'Tell tenants about planned maintenance, or anything else they must not miss. It pops up once, then stays on their dashboard until it ends.',
      'ann.audience': 'Who sees it',
      'ann.audienceAll': 'Every tenant',
      'ann.audienceCompany': 'One tenant',
      'ann.audienceUser': 'One person',
      'ann.pickCompany': 'Tenant',
      'ann.pickUser': 'Person',
      'ann.pickUserHint': 'Choose the tenant first, then the person.',
      'ann.severity': 'How serious',
      'ann.sevInfo': 'Information',
      'ann.sevWarning': 'Warning',
      'ann.sevCritical': 'Critical',
      'ann.severityHint': 'Changes the colour only. It does not send a message, and it does not come back once someone has closed it.',
      'ann.environment': 'Which environment',
      'ann.envProd': 'Production',
      'ann.envDev': 'Development',
      'ann.envAll': 'All environments',
      'ann.environmentHint': 'Shown on the banner so nobody reads a development window as a live outage. It is a label only — it does not change who sees this.',
      'ann.titleEn': 'Title (English)',
      'ann.titleAr': 'Title (Arabic)',
      'ann.bodyEn': 'Message (English)',
      'ann.bodyAr': 'Message (Arabic)',
      'ann.bilingualHint': 'Both languages are required — each person reads it in the language they use.',
      'ann.startsAt': 'Shows from',
      'ann.endsAt': 'Disappears at',
      'ann.endsAtHint': 'It goes away on its own at this time. Nobody has to clear it.',
      'ann.isMaintenance': 'This is a maintenance window',
      'ann.isMaintenanceHint': 'Tick this when the system will be down or degraded. It adds the maintenance start and end, which are separate from "Shows from" and "Disappears at" above — those control the banner, not the outage.',
      'ann.windowStartsAt': 'Maintenance starts',
      'ann.windowEndsAt': 'Maintenance ends',
      'ann.windowHint': 'When the system is actually down. Sent on WhatsApp, so it is what customers plan around.',
      'ann.affectedServicesEn': 'Affected services (English)',
      'ann.affectedServicesAr': 'Affected services (Arabic)',
      'ann.affectedServicesHint': 'Optional. Which parts of the platform this affects — leave blank and the notice reads as all services.',
      'ann.affectedServicesPlaceholderEn': 'Invoices, WhatsApp notifications',
      'ann.affectedServicesPlaceholderAr': 'الفواتير، إشعارات واتساب',
      'ann.publish': 'Publish',
      'ann.publishAndSend': 'Publish & send',
      'ann.aiTitle': 'Draft it with AI',
      'ann.aiBrief': 'What is the announcement about?',
      'ann.aiBriefHint': 'One or two lines. The assistant writes both languages; you can edit everything afterwards.',
      'ann.aiBriefPlaceholder': 'e.g. HR module is live — payroll, leave and attendance are now in the menu for every shop on the Pro plan.',
      'ann.aiTone': 'Tone',
      'ann.aiTone_professional': 'Professional',
      'ann.aiTone_friendly': 'Friendly',
      'ann.aiTone_promotional': 'Promotional',
      'ann.aiDraft': 'Draft with AI',
      'ann.aiDrafted': 'Draft written — review it before publishing',
      'ann.waLabel': 'Also send on WhatsApp',
      'ann.waHint': 'Sends the announcement from the platform WhatsApp number to the shop owners in the audience above, laid out as subject, timing and affected services. The banner reaches whoever logs in; this reaches the ones who will not today.',
      'ann.waCount': 'Will message {{count}} shop owner(s)',
      'ann.waNoPhone': '{{count}} without a mobile number',
      'ann.waOverCap': '{{count}} phones — over the {{cap}} limit for one send. Narrow the audience.',
      'ann.waSent': 'WhatsApp sent to {{count}} recipient(s)',
      'ann.waPartial': '{{count}} message(s) could not be delivered',
      'ann.waViaSms': '{{count}} went out as SMS because WhatsApp would not take the template — those are billed per segment',
      'ann.waAlreadyReached': '{{count}} already had it from an earlier send and were skipped',
      'ann.waRemaining': '{{count}} not reached before the send timed out. Press "Send on WhatsApp" again to carry on — everyone already reached is skipped.',
      'ann.waSendNow': 'Send on WhatsApp',
      'ann.smsLabel': 'Also send by SMS',
      'ann.smsHint': 'Sends the same announcement as a text message to the audience above. Reaches a phone with no app and no internet — and every message is billed, so keep it short.',
      'ann.smsCount': 'Will text {{count}} recipient(s)',
      'ann.smsOverCap': '{{count}} phones — over the {{cap}} limit for one send. Narrow the audience.',
      'ann.smsSent': 'SMS sent to {{count}} recipient(s)',
      'ann.smsPartial': '{{count}} text message(s) could not be sent',
      'ann.smsSendNow': 'Send by SMS'
      ,'ann.confirmResendMsg': 'Send this announcement as a WhatsApp message to {{target}} from the platform number? Anyone who already received it is skipped, so this only reaches the ones it has not.',
      'ann.confirmWaTitle': 'Publish and send on {{channels}}?',
      'ann.confirmWaMsg': 'This publishes the announcement to {{target}} and sends it on {{channels}} to {{count}} shop owner(s) from the platform number. Messages cannot be recalled.',
      'ann.confirmWaMsgUnknown': 'This publishes the announcement to {{target}} and sends it on {{channels}} to every shop owner in that audience from the platform number. The audience size could not be checked. Messages cannot be recalled.',
      'ann.channelWhatsapp': 'WhatsApp',
      'ann.channelSms': 'SMS',
      'ann.channelJoin': ' and ',
      'ann.published': 'Announcement published',
      'ann.history': 'Sent so far',
      'ann.noneYet': 'Nothing has been sent yet.',
      'ann.endNow': 'End now',
      'ann.statusActive': 'Showing now',
      'ann.statusScheduled': 'Scheduled',
      'ann.statusExpired': 'Finished',
      'ann.confirmEndTitle': 'End this announcement?',
      'ann.confirmEndMsg': 'It disappears from every screen straight away. What it said stays on this list.',
      'ann.toAll': 'Every tenant',
      'notifChannels.title': 'Notification channels',
      'notifChannels.subtitle': 'Choose SMS or WhatsApp-with-SMS-fallback for each notification type.',
      'notifChannels.search': 'Search by notification key or template name',
      'notifChannels.noMatches': 'No notification types match that search',
      'notifChannels.loadError': 'Could not load notification channels',
      'notifChannels.loadErrorHint': 'The list comes from the notification catalogue. Try again, and check the API is reachable.',
      'notifChannels.saved': 'Channel updated',
      'notifChannels.saveFailed': 'Could not update the channel',
      'notifChannels.noTemplate': 'No WhatsApp template',
      'notifChannels.precedence': 'A message can only leave SMS when three things agree: the template is approved by Meta, hybrid routing is enabled on the server, and the type is switched to Hybrid below. Anything else stays on SMS.',
      'notifChannels.summary': '{{eligible}} of {{total}} types can use WhatsApp today. {{hybrid}} are switched on.',
      'notifChannels.killSwitchOffTitle': 'Hybrid routing is switched off',
      'notifChannels.killSwitchOffBody': 'Every message is going by SMS right now, whatever the switches below say. Turn this on and they take effect immediately.',
      'notifChannels.killSwitchOnTitle': 'Hybrid routing is on',
      'notifChannels.killSwitchOnBody': 'Each type below now follows its own switch. Turning this off puts everything back on SMS at once, without changing any of them.',
      'notifChannels.switchOn': 'Turn on',
      'notifChannels.switchOff': 'Turn off',
      'notifChannels.switchLocked': 'HYBRID_ROUTING_ENABLED is set on the server and overrides this switch. Clear it there to control routing from this screen.',
      'notifChannels.mode.sms': 'SMS',
      'notifChannels.mode.hybrid': 'Hybrid',
      'notifChannels.audience.garage': 'Garage admin',
      'notifChannels.audience.staff': 'Staff',
      'notifChannels.audience.customer': 'Customer',
      'notifChannels.refusal.carriesLink': 'Carries a private link — always SMS',
      'notifChannels.refusal.customerAuthCode': 'Customer one-time code — always SMS',
      'notifChannels.refusal.notApproved': 'Not enabled yet — template or sender still pending',
      'notifChannels.state.wait': 'Waiting',
      'notifChannels.state.lock': 'Always SMS',
      'notifChannels.queue': '{{waiting}} waiting on a Meta template. {{locked}} stay on SMS by policy and will not change.',
      'notifChannels.cost.title': 'Measured delivery, last 30 days',
      'notifChannels.cost.noData': 'Nothing has been sent over WhatsApp yet, so there is nothing to measure. This fills in once a notification type is switched to Hybrid and traffic runs through it.',
      'notifChannels.cost.summary': '{{attempts}} template sends in {{days}} days. {{whatsapp}} delivered over WhatsApp ({{percent}}%), {{fallbacks}} fell back to SMS, costing {{segments}} SMS segments.',
      'notifChannels.cost.row': '{{attempts}} sent · {{whatsapp}} on WhatsApp · {{fallbacks}} fell back to SMS',
      'notifChannels.cost.caveat': 'Counted in SMS segments, not riyals — no per-segment rate is configured. SMS-only sends are not counted here; compare against your SMS provider’s billing for the before-and-after.',

      // ── HA Status (Super Admin) ──
      'ha.navTitle': 'HA Status',
      'ha.title': 'High Availability Status',
      'ha.subtitle': 'Live primary/standby health, replication lag, and failover events across both nodes.',
      'ha.loadError': 'Could not load HA status',
      'ha.testAlert': 'Send test alert',
      'ha.testAlertSent': 'Test alert sent',
      'ha.testAlertConfirm': 'This sends a real test notification through the HA alert channel. Continue?',
      'ha.servingFrom': 'Serving from',
      'ha.unknown': 'Unknown',
      'ha.standbyModeOn': 'STANDBY_MODE',
      'ha.lastChecked': 'Last checked: {{time}}',
      'ha.database': 'Database',
      'ha.inRecovery': 'In recovery',
      'ha.receiveLsn': 'Receive LSN',
      'ha.lastReplay': 'Last replay',
      'ha.currentLsn': 'Current LSN',
      'ha.replication': 'Replication',
      'ha.noStandby': 'No standby connected',
      'ha.node': 'Node',
      'ha.state': 'State',
      'ha.syncState': 'Sync state',
      'ha.lag': 'Lag',
      'ha.replayLag': 'Replay lag',
      'ha.events': 'Events',
      'ha.noEvents': 'No events yet',
      'ha.time': 'Time',
      'ha.eventType': 'Type',
      'ha.message': 'Message',
      'ha.eventsPage': 'Page {{page}} · {{total}} total',
      'ha.prev': 'Previous',
      'ha.next': 'Next',
      'ha.primary': 'PRIMARY',
      'ha.standby': 'STANDBY',

      // ── F1: WhatsApp share (quotation / credit payment link / invoice) ──
      'wa.share': 'WhatsApp',
      'wa.quotationMessage': 'Hello, here is your quotation #{{number}}: {{link}}',
      'wa.paymentLinkMessage': 'Hello, please complete payment for job #{{number}}: {{link}}',
      'wa.invoiceMessage': 'Hello, here is your invoice #{{number}}: {{link}}',
      'wa.thankYouMessage': 'Thank you for choosing us! Here is your invoice for job #{{number}}: {{link}}',

      // ── Super-admin Subscriptions (plan catalog) ──
      'sub.title': 'Subscription Plans',
      'sub.subtitle': 'Design your own plans — features, limits and pricing',
      'sub.newPlan': 'New plan',
      'sub.noPlans': 'No plans yet',
      'sub.noPlansDesc': 'Create your first subscription plan.',
      'sub.planUpdated': 'Plan updated',
      'sub.planCreated': 'Plan created',
      'sub.saveFailed': 'Failed to save plan',
      'sub.planDeleted': 'Plan deleted',
      'sub.deleteFailed': 'Failed to delete plan',
      'sub.unitDay': 'day',
      'sub.unitWeek': 'week',
      'sub.unitMonth': 'month',
      'sub.unitYear': 'year',
      'sub.unlimited': 'Unlimited',
      'sub.usedNoLimit': '{{used}} · no limit',
      'sub.active': 'Active',
      'sub.inactive': 'Inactive',
      'sub.features': 'features',
      'sub.deletePlan': 'Delete plan',
      'sub.deleteConfirm': 'Delete "{{name}}"? Companies already on it keep their subscription.',

      // Voice Conversation minutes — blank = unlimited, like the AI quotas
      // above (owner ruling 2026-09-19).
      'voiceConversationMinutes': 'Voice conversation minutes / month',
      'voiceConversationUsage': 'Voice conversation: {{used}} / {{allowance}} min this month',
      'voiceConversationUsageUnlimited': 'Voice conversation: {{used}} min used this month (unlimited)',
      'voiceConversationReset': 'Reset usage',
      'voiceConversationResetConfirm': 'Reset the voice-conversation meter to zero? Counting restarts from now; past sessions stay on record.',
      'voiceConversationResetDone': 'Voice minutes reset — counting restarts now.',
      'wa.connect': 'Connect WhatsApp',
      'wa.disconnect': 'Disconnect',
      'wa.disconnectConfirm': 'Are you sure you want to disconnect your WhatsApp session?',
      'wa.banRisk': 'Protect your WhatsApp number: WhatsApp may block numbers that send bulk or unwanted messages. Send to at most 50 recipients per campaign, target existing customers who expect your messages, and space campaigns out. MotorMind automatically waits 10–25 seconds between each message and caps marketing at 200 messages per day.',
      'wa.scanInstructions': 'Scan the QR code below using your WhatsApp on your phone to link your account. Keep this page open while pairing.',
      'wa.status.title': 'WhatsApp Session',
      'wa.multi.branches.title': 'Branch WhatsApp Lines',
      'wa.multi.branches.description': 'Assign each branch to a WhatsApp line or SMS only.',
      'wa.multi.branches.branch': 'Branch',
      'wa.multi.branches.currentLine': 'Current Line',
      'wa.multi.branches.assignLine': 'Assigned Line',
      'wa.multi.branches.mainLine': 'Main line',
      'wa.multi.branches.smsOnly': 'SMS only',
      'wa.multi.branches.defaultLine': 'Default line',
      'wa.multi.branches.saving': 'Saving...',
      'wa.multi.branches.saved': 'Saved successfully',
      'wa.multi.branches.inboxTitle': 'Inbox Visibility',
      'wa.multi.branches.inboxDescription': 'Control which branches can view conversations for each WhatsApp line.',
      'wa.multi.branches.inboxEnabled': 'Inbox enabled',
      'wa.multi.branches.inboxDisabled': 'Inbox disabled',
      'wa.multi.branches.visibilityDefault': 'Branches that use this line',
      'wa.multi.branches.visibilityExplicit': 'Specific branches',
      'wa.multi.branches.hiddenZeroBranches': 'Hidden from all branches — pick branches or restore the default',
      'wa.multi.branches.restoreDefault': 'Restore default',
      'wa.multi.branches.saveInbox': 'Save visibility',
      'wa.multi.branches.noBranches': 'No branches found',
      'wa.multi.branches.noLines': 'No lines found',
      'wa.multi.branches.save': 'Save',
      'wa.multi.tpl.title': 'Account Templates',
      'wa.multi.tpl.toggle': 'Templates',
      'wa.multi.tpl.refresh': 'Refresh',
      'wa.multi.tpl.loading': 'Loading templates...',
      'wa.multi.tpl.empty': 'No templates found for this account',
      'wa.multi.tpl.error': 'Failed to load templates',
      'wa.multi.tpl.name': 'Template Name',
      'wa.multi.tpl.language': 'Language',
      'wa.multi.tpl.status': 'Status',
      'wa.multi.tpl.reason': 'Rejection reason',
      'wa.multi.invalidAuth': "Meta refused this account's access token. Update the credentials.",
      'wa.sessionHint': 'Scan this code from WhatsApp on the workshop phone to relink when the session drops',
      'wa.qrAlt': 'WhatsApp pairing QR code',
      'wa.status.disconnected': 'Disconnected',
      'wa.status.pairing': 'Pairing',
      'wa.status.connected': 'Connected',
      'wa.status.logged_out': 'Logged out',
      'wa.unlinkedBanner': 'WhatsApp is unlinked — conversations are hidden until you connect again.',
      'wa.linkedNumber': 'Linked Number',
      'wa.linkedAt': 'Linked At',
      'wa.lastConnected': 'Last Connected',
      'wa.accessDenied': 'Access Denied',
      'wa.accessDeniedDesc': 'You do not have permission to connect WhatsApp.',
      'wa.confirmTitle': 'Disconnect WhatsApp?',
      'wa.successConnected': 'WhatsApp connected successfully!',
      'wa.saveFailed': 'Failed to connect WhatsApp',
      'wa.deleteFailed': 'Failed to disconnect WhatsApp',
      'wa.disconnectSuccess': 'WhatsApp disconnected',
      'wa.loadingQr': 'Loading QR Code...',
      'nav.whatsapp': 'WhatsApp',
      'wa.chat': 'WhatsApp Inbox',
      'wa.searchPlaceholder': 'Search chats...',
      'wa.linkCustomer': 'Link Customer',
      'wa.noCustomerMatched': 'No customer matched',
      'wa.createCustomer': 'Create Customer',
      'wa.outstandingBalance': 'Outstanding Balance',
      'wa.jobCards': 'Job Cards',
      'wa.quotations': 'Quotations',
      'wa.invoices': 'Invoices',
      'wa.disconnectedBanner': 'WhatsApp disconnected — messages won\'t send',
      'wa.attachFile': 'Attach File',
      'wa.send': 'Send',
      'wa.typeMessage': 'Type a message...',
      'wa.retry': 'Retry',
      'wa.optedOut': 'Opted Out',
      'wa.linkCustomerModalTitle': 'Link Customer to Chat',
      'wa.selectCustomer': 'Select a customer',
      'wa.back': 'Back',
      'wa.toggleList': 'Toggle conversations',
      'wa.activityHeader': 'MotorMind Activity',
      'wa.loading': 'Loading...',
      'wa.failedToSend': 'Failed to send',
      'wa.viewMedia': 'View Media',
      'wa.today': 'Today',
      'wa.yesterday': 'Yesterday',
      'wa.noChatsFound': 'No chats found',
      'wa.noJobCards': 'No job cards',
      'wa.noQuotationsFound': 'No quotations',
      'wa.noInvoicesFound': 'No invoices',
      'wa.selectChatPrompt': 'Select a chat to start messaging',
      'wa.customerLinkedSuccess': 'Customer linked successfully',
      'wa.linkCustomerFailed': 'Failed to link customer',
      'wa.downloadFailed': 'Failed to download file',
      'wa.lines.main': 'Main',
      'wa.lines.selectLine': 'WhatsApp line',
      'wa.lines.line': 'Line',
      'wa.lines.allLines': 'All lines',
      'wa.lines.default': 'Default',
      'wa.lines.connected': 'Connected',
      'wa.lines.disconnected': 'Disconnected',
      'wa.lines.hiddenFromAll': 'Hidden from all branches — pick branches or restore the default',
      'wa.lines.selectLinePrompt': 'Select a line to send from',
      'wa.reattachToRetry': 'Re-attach the file to resend',
      'wa.unsupportedPhonePrefill': 'Could not prefill phone {{phone}} — please enter it manually',
      'wa.confirmProceed': 'Are you sure you want to proceed?',
      'wa.fileTooLarge': 'File size exceeds 16MB',
      'wa.vehicleFallback': 'Vehicle',
      'wa.mediaAlt': 'Media',
      'wa.downloadFile': 'Download File',

      // WhatsApp marketing campaigns
      'wa.mkt.title': 'Marketing Campaigns',
      'wa.mkt.subtitle': 'Send WhatsApp campaigns to your customers',
      'wa.mkt.marketing': 'Marketing',
      'wa.mkt.newCampaign': 'New campaign',
      'wa.mkt.backToCampaigns': 'Back to campaigns',
      'wa.mkt.loading': 'Loading campaigns...',
      'wa.mkt.noCampaigns': 'No campaigns yet',
      'wa.mkt.createFirst': 'Create your first campaign to get started',
      'wa.mkt.name': 'Campaign name',
      'wa.mkt.body': 'Message',
      'wa.mkt.nameRequired': 'Campaign name is required',
      'wa.mkt.bodyRequired': 'Message body is required',
      'wa.mkt.nameHint': 'Use {{name}} to personalize the message',
      'wa.mkt.attachImage': 'Attach image',
      'wa.mkt.removeImage': 'Remove image',
      'wa.mkt.changeImage': 'Change image',
      'wa.mkt.imageAttached': 'Image attached',
      'wa.mkt.preview': 'Preview',
      'wa.mkt.previewSampleName': 'Ahmed',
      'wa.mkt.recipients': 'Recipients',
      'wa.mkt.searchRecipients': 'Search customers...',
      'wa.mkt.selectAllFiltered': 'Select all filtered',
      'wa.mkt.deselectAllFiltered': 'Deselect all filtered',
      'wa.mkt.selectedCount': '{{count}} selected',
      'wa.mkt.continue': 'Continue',
      'wa.mkt.cancel': 'Cancel',
      'wa.mkt.composerTitle': 'New campaign',
      'wa.mkt.confirmTitle': 'Confirm campaign',
      'wa.mkt.confirmRecipients': 'You are about to send to {{count}} customers',
      'wa.mkt.autoExcluded': 'Invalid, opted-out, or duplicate numbers will be excluded automatically',
      'wa.mkt.guardrail': 'Messages are sent 10–25 seconds apart, up to 200 per day to protect your number',
      'wa.mkt.maxRecipients': 'Maximum 50 recipients per campaign — split larger audiences across multiple days to protect your number.',
      'wa.mkt.createCampaign': 'Create campaign',
      'wa.mkt.createdSuccess': 'Campaign created',
      'wa.mkt.createFailed': 'Failed to create campaign',
      'wa.mkt.invalidCustomer': 'One or more selected customers cannot receive WhatsApp messages',
      'wa.mkt.send': 'Send',
      'wa.mkt.pause': 'Pause',
      'wa.mkt.cancelCampaign': 'Cancel campaign',
      'wa.mkt.sendFailed': 'Failed to send campaign',
      'wa.mkt.pauseFailed': 'Failed to pause campaign',
      'wa.mkt.cancelFailed': 'Failed to cancel campaign',
      'wa.mkt.sessionDisconnected': 'WhatsApp session disconnected',
      'wa.mkt.sessionDisconnectedTooltip': 'WhatsApp session is not connected',
      'wa.mkt.status.draft': 'Draft',
      'wa.mkt.status.sending': 'Sending',
      'wa.mkt.status.paused': 'Paused',
      'wa.mkt.status.completed': 'Completed',
      'wa.mkt.status.cancelled': 'Cancelled',
      'wa.mkt.status.failed': 'Failed',
      'wa.mkt.queued': 'Queued',
      'wa.mkt.sent': 'Sent',
      'wa.mkt.failed': 'Failed',
      'wa.mkt.skipped': 'Skipped',
      'wa.mkt.progress': 'Progress',
      'wa.mkt.recipientsTitle': 'Recipients',
      'wa.mkt.recipientsCapped': 'Showing first {{count}}',
      'wa.mkt.noRecipients': 'No recipients loaded',
      'wa.mkt.loadingRecipients': 'Loading recipients...',
      'wa.mkt.createdAt': 'Created',
      'wa.mkt.back': 'Back',
      'wa.mkt.accessDenied': 'Access Denied',
      'wa.mkt.accessDeniedDesc': 'You do not have permission to use WhatsApp marketing',
      'wa.mkt.recipientStatus.queued': 'Queued',
      'wa.mkt.recipientStatus.sending': 'Sending',
      'wa.mkt.recipientStatus.sent': 'Sent',
      'wa.mkt.recipientStatus.failed': 'Failed',
      'wa.mkt.recipientStatus.skipped': 'Skipped',
      'wa.mkt.recipientStatus.cancelled': 'Cancelled',
      'wa.mkt.recipientStatus.invalid_phone': 'Invalid phone',
      'wa.mkt.recipientStatus.duplicate': 'Duplicate',
      'wa.mkt.recipientStatus.opted_out': 'Opted out',
      // AGY4/F1 — the delivery outcome is genuinely unknown: not a proven
      // failure, not a confirmed delivery. Worded so nobody reads it as
      // "try again"; these are never resent automatically.
      'wa.mkt.recipientStatus.uncertain': 'Unknown',
      'wa.mkt.uncertainCount_one': '{{count}} recipient with an unknown delivery outcome',
      'wa.mkt.uncertainCount_other': '{{count}} recipients with an unknown delivery outcome',
      'wa.mkt.uncertainHelp':
        'These messages may or may not have reached the customer. They are not counted as sent and are never resent automatically.',

      // T9 — inbox toolbar, new-message compose, settings page, mobile layout
      'wa.newMessage': 'New message',
      'wa.newMessageTitle': 'New message',
      'wa.pickContact': 'Choose a contact',
      'wa.startChat': 'Start chat',
      // T11 — compose modal mode switch (existing customer vs. brand-new number)
      'wa.fromCustomers': 'From customers',
      'wa.newNumber': 'New number',
      'wa.contactName': 'Name',
      'wa.phoneNumber': 'Phone number',
      'wa.phoneNumberPlaceholder': '05xxxxxxxx',
      'wa.statusConnected': 'Connected',
      'wa.statusDisconnected': 'Disconnected',
      'wa.manageInSettings': 'Manage in Settings',
      'wa.invalidPhone': 'This customer does not have a valid phone number',
      'wa.startChatFailed': 'Could not start the conversation',
      'wa.openActivity': 'Open Activity',
      'wa.onCredit': 'On credit',
      'wa.routeTransactional': 'Send transactional messages & notifications via WhatsApp when connected (otherwise SMS)',
      'wa.routeTransactionalHelp': 'When off, automatic messages (quotations, invoices, reminders) always go out via SMS, even if WhatsApp is connected.',
      'wa.cloud.accessToken': 'System User Permanent Access Token',
      'wa.cloud.allFieldsRequired': 'Please fill in all required fields',
      'wa.cloud.appSecret': 'App Secret',
      'wa.cloud.confirmDeleteBtn': 'Disconnect',
      'wa.cloud.confirmDeleteDesc': 'Are you sure you want to disconnect? Active campaigns will be paused and notifications will fall back to SMS.',
      'wa.cloud.confirmDeleteTitle': 'Disconnect WhatsApp Cloud API',
      'wa.cloud.connect': 'Connect WhatsApp Cloud API',
      'wa.cloud.connectButton': 'Connect Account',
      'wa.cloud.connectionStatusTitle': 'Cloud API Connection',
      'wa.cloud.copied': 'Copied!',
      'wa.cloud.copy': 'Copy',
      'wa.cloud.disconnect': 'Disconnect Cloud API',
      'wa.cloud.disconnectFailed': 'Failed to disconnect Cloud API',
      'wa.cloud.disconnectSuccess': 'Cloud API connection removed',
      'wa.cloud.displayPhone': 'Display Phone Number',
      'wa.cloud.helpText': 'A phone number registered with Meta Cloud API cannot be linked via QR code simultaneously. Ensure this number is dedicated to Meta Cloud API.',
      'wa.cloud.helpTitle': 'Important Note',
      'wa.cloud.invalidCredentials': 'Invalid credentials: could not verify WhatsApp Business Account',
      'wa.cloud.invalidExplanation': 'The access token has expired or permissions were revoked in Meta Business Manager. Please enter a valid Permanent Access Token below to restore messaging.',
      'wa.cloud.phoneInUse': 'This phone number is already connected to another tenant.',
      'wa.cloud.phoneNumberId': 'Phone Number ID',
      'wa.cloud.qrConflict': 'A QR session is currently being paired. Cancel it before enabling Cloud API.',
      'wa.cloud.saveFailed': 'Failed to save Cloud API settings',
      'wa.cloud.saveSuccess': 'WhatsApp Cloud API connected successfully',
      'wa.cloud.secretHint': 'Used to verify webhook signature',
      'wa.cloud.setupTitle': 'WhatsApp Cloud API Setup',
      'wa.cloud.statusActive': 'Active',
      'wa.cloud.title': 'WhatsApp Cloud API Configuration',
      'wa.cloud.tokenExpired': 'Token Expired',
      'wa.cloud.tokenHint': 'Encrypted and never displayed again',
      'wa.cloud.updateButton': 'Update Settings',
      'wa.cloud.updateCredentialsTitle': 'Update Credentials',
      'wa.cloud.verifiedName': 'Verified Name',
      'wa.cloud.verifyToken': 'Verify Token',
      'wa.cloud.wabaId': 'WhatsApp Business Account ID (WABA ID)',
      'wa.cloud.webhookSection': 'Meta Webhook Configuration',
      'wa.cloud.webhookUrl': 'Webhook URL',
      'wa.inbox.cloudTokenExpired': 'Meta Access Token Expired',
      'wa.inbox.windowClosed': 'Customer 24-hour messaging window has closed. You can only respond using an approved message template or wait for the customer to message you.',
      'wa.inbox.windowClosedPlaceholder': 'Messaging window closed — waiting for customer message or use template',
      'wa.inbox.windowClosedToast': 'Cannot reply: customer 24-hour service window is closed',
      'wa.manageConnection': 'Manage Connection',
      'wa.marketingOptIn': 'Customer agreed to receive offers on WhatsApp',
      'wa.marketingOptInHint': 'Promotional WhatsApp messages from this center. Service messages are not affected.',
      'wa.intakeOptIn': 'I agree to receive offers from the center on WhatsApp',
      'wa.intakeOptInHint': 'Optional. You can stop them at any time by replying STOP.',
      'wa.mkt.approval.approved': 'Approved by Meta',
      'wa.mkt.approval.pending': 'Pending Meta Approval',
      'wa.mkt.approval.pendingBanner': 'This campaign uses a WhatsApp Cloud template that is pending approval from Meta. Sending will be available once approved.',
      'wa.mkt.line.mainLine': 'Main line',
      'wa.mkt.line.sendFrom': 'Send from',
      'wa.mkt.line.selectPlaceholder': 'Select a WhatsApp line',
      'wa.mkt.line.lineRequired': 'Please select a WhatsApp line',
      'wa.mkt.approval.rejected': 'Rejected by Meta',
      'wa.mkt.approval.rejectedTitle': 'Meta Template Rejected',
      'wa.mkt.cloudTemplateNotApproved': 'Campaign template is not approved by Meta yet',
      'wa.mkt.uncertainCount': 'Uncertain: {{count}}',
      'wa.mode.cloud': 'WhatsApp Business API (Meta)',
      'wa.mode.branches': 'Branches & inbox',
      'wa.mode.tablist': 'WhatsApp settings sections',
      'wa.multi.extraNumbersHeading': 'Additional numbers',
      'wa.multi.noQrNumbers': 'No extra phones linked yet.',
      'wa.multi.noMetaNumbers': 'No Meta numbers added yet.',
      'wa.multi.mainLineHeading': 'Main line',
      'wa.multi.extraHeading': 'Additional accounts and numbers',
      'wa.multi.qrNumbersTitle': 'Linked phones (QR)',
      'wa.multi.qrNumbersDesc': 'Extra phones linked by QR code. Each branch can send from one.',
      'wa.multi.metaNumbersTitle': 'Meta numbers',
      'wa.multi.metaNumbersDesc': 'Phone numbers under your Meta accounts.',
      'wa.mode.qr': 'Linked Device (QR)',
      'wa.notifyChannelCloud': 'WhatsApp (Business API) & SMS',
      'wa.notifyChannelCloudHint': 'Updates go through the workshop\'s WhatsApp Business API number',
      'wa.routingTitle': 'Message Routing',
      'wa.template.add': 'Add',
      'wa.template.empty': 'No templates yet.',
      'wa.template.filter.added': 'Added',
      'wa.template.filter.all': 'All',
      'wa.template.filter.notAdded': 'Not added',
      'wa.template.filterLabel': 'Filter templates',
      'wa.template.loading': 'Loading templates...',
      'wa.template.state.approved': 'Approved',
      'wa.template.state.not_added': 'Not added',
      'wa.template.state.pending': 'Waiting for Meta approval',
      'wa.template.state.rejected': 'Rejected',
      'wa.template.title': 'Meta Message Templates',
      'wa.template.tryAgain': 'Try again',
      'wa.cloud.help.open': 'Where do I find this?',
      'wa.cloud.webhookAfterSave':
        'After you save these four values, MotorMind shows your Webhook URL and verify token here to paste in Meta.',
      'wa.cloud.help.wabaId': 'In Meta for Developers, open your app, then WhatsApp → API Setup, and copy “WhatsApp Business Account ID”. It is also under Business Settings → Accounts → WhatsApp accounts.',
      'wa.cloud.help.phoneNumberId': 'On the same API Setup page, under the sending number (From), copy “Phone number ID”. It is a long numeric ID, not the phone number itself.',
      'wa.cloud.help.accessToken': 'In Meta Business Settings: Users → System users → add a system user with Admin access and assign it your app and your WhatsApp account. Then Generate new token with expiry “Never” and the permissions whatsapp_business_messaging and whatsapp_business_management. Do not use the temporary token from API Setup; it expires after 24 hours.',
      'wa.cloud.help.appSecret': 'In Meta for Developers, open your app, then App settings → Basic, and click Show next to “App secret”.',
      'wa.cloud.help.webhookUrl': 'Paste this in Meta for Developers: your app → WhatsApp → Configuration → Webhook → Edit, in “Callback URL”. Then, under Webhook fields, click Manage and subscribe to the messages, message_template_status_update and template_category_update fields.',
      'wa.cloud.help.verifyToken': 'Paste this in the “Verify token” box of the same Webhook window, then click Verify and save.',
      'wa.template.addsBoth': 'Add submits the Arabic and the English version together to your Meta account.',
      'wa.template.lang.ar': 'Arabic',
      'wa.template.lang.en': 'English',
      'wa.template.preview.open': 'Preview',
      'wa.template.preview.title': 'Template preview',
      'wa.template.preview.examplesNote': 'Highlighted values are examples. Each message fills in the customer’s real details.',
      'wa.template.preview.name': 'Name in Meta',
      'wa.template.preview.category': 'Category',
      'wa.template.preview.utility': 'Utility',
      'wa.template.preview.marketing': 'Marketing',
      'wa.settingsSaved': 'Settings saved',
      'wa.settingsSaveFailed': 'Could not save settings',

      // wa.multi.* (Task 10a1: WhatsApp Multi-Number Settings)
      'wa.multi.accountsTitle': 'Meta Accounts',
      'wa.multi.cancel': 'Cancel',
      'wa.multi.save': 'Save',
      'wa.multi.status.active': 'Active',
      'wa.multi.status.disconnected': 'Disconnected',
      'wa.multi.status.invalid': 'Invalid',
      'wa.multi.status.retired': 'Retired',
      'wa.multi.accountsDesc': 'Connect one or more WhatsApp Business accounts (WABAs).',
      'wa.multi.addAccount': 'Add Meta Account',
      'wa.multi.accountLabel': 'Account label',
      'wa.multi.accountLabelPlaceholder': 'e.g. Main Office, Riyadh Branch',
      'wa.multi.accountAdded': 'Meta account added successfully',
      'wa.multi.updateCredentials': 'Update credentials',
      'wa.multi.credentialsUpdated': 'Credentials updated successfully',
      'wa.multi.retrySubscribe': 'Retry subscribe',
      'wa.multi.registerTemplates': 'Register templates',
      'wa.multi.templatesRegistered': 'Templates registered successfully',
      'wa.multi.removeAccount': 'Remove account',
      'wa.multi.removeConfirmTitle': 'Remove Meta Account',
      'wa.multi.removeConfirmMsg': 'Are you sure you want to remove this Meta account? Numbers under this account will be disconnected.',
      'wa.multi.accountRemoved': 'Meta account removed',
      'wa.multi.removeFailed': 'Failed to remove account',
      'wa.multi.removing': 'Removing...',
      'wa.multi.removed': 'Removed',
      'wa.multi.noAccounts': 'No Meta accounts added yet.',
      'wa.multi.numbersTitle': 'Phone Numbers',
      'wa.multi.numbersDesc': 'Manage phone numbers across Meta accounts and linked QR phones.',
      'wa.multi.addMetaNumber': 'Add Meta Number',
      'wa.multi.addNumberTitle': 'Add Phone Number under Meta Account',
      'wa.multi.selectAccount': 'Meta Account',
      'wa.multi.numberLabel': 'Number label',
      'wa.multi.numberLabelPlaceholder': 'e.g. Service Advisor, Reception',
      'wa.multi.phoneNumberId': 'Phone Number ID',
      'wa.multi.phoneNumberIdPlaceholder': 'e.g. 102938475610293',
      'wa.multi.numberAdded': 'Phone number added successfully',
      'wa.multi.linkPhone': 'Link a phone',
      'wa.multi.linkPhoneTitle': 'Link a phone via QR',
      'wa.multi.phoneLinked': 'Phone QR number created',
      'wa.multi.markPrimary': 'Make primary',
      'wa.multi.primary': 'Primary',
      'wa.multi.default': 'Default',
      'wa.multi.primarySet': 'Primary number updated',
      'wa.multi.retire': 'Retire',
      'wa.multi.retireConfirmTitle': 'Retire Phone Number',
      'wa.multi.retireConfirmMsg': 'Are you sure you want to retire this phone number? Branches using this number will need to be reassigned.',
      'wa.multi.retired': 'Phone number retired',
      'wa.multi.retireFailed': 'Failed to retire number',
      'wa.multi.branchesUsing': 'Branches using this line',
      'wa.multi.noBranches': 'None',
      'wa.multi.qrCardTitle': 'WhatsApp QR Session',
      'wa.multi.qrModalTitle': 'Scan QR Code',
      'wa.multi.scanQr': 'Scan QR',
      'wa.multi.manageSession': 'Manage session',
      'wa.multi.noNumbers': 'No WhatsApp numbers configured yet.',
      'wa.multi.allFieldsRequired': 'Please fill in all required fields.',
      'wa.multi.copyWebhook': 'Copy Webhook URL',
      'wa.multi.copyToken': 'Copy Verify Token',
      'wa.multi.copied': 'Copied to clipboard',
      'wa.multi.connected': 'Connected successfully',
      'wa.multi.webhookSection': 'Webhook Configuration',
      'wa.multi.webhookUrl': 'Callback URL',
      'wa.multi.verifyToken': 'Verify token',
      'wa.multi.active': 'Active',
      'wa.multi.subscribing': 'Subscribing...',
      'wa.multi.invalid': 'Invalid',
      'wa.mkt.addContact': 'Add someone not in your customer list',
      'wa.mkt.contactName': 'Name',
      'wa.mkt.contactPhone': 'Phone number',
      'wa.mkt.addContactBtn': 'Add',
      'wa.mkt.newTag': 'New',
      'wa.mkt.duplicateContact': 'Already added',

      // T12 — AI in WhatsApp (campaign copywriter + conversation analysis)
      'wa.ai.write': 'Write with AI',
      'wa.ai.brief': 'What are you promoting?',
      'wa.ai.briefPlaceholder': 'Describe the offer — what you\'re promoting, price/discount, deadline...',
      'wa.ai.audience': 'Audience (optional)',
      'wa.ai.tone': 'Tone',
      'wa.ai.tonePro': 'Professional',
      'wa.ai.toneFriendly': 'Friendly',
      'wa.ai.tonePromo': 'Promotional',
      'wa.ai.language': 'Language',
      'wa.ai.languageBoth': 'Both',
      'wa.ai.generate': 'Generate',
      'wa.ai.regenerate': 'Regenerate',
      'wa.ai.copyApplied': 'AI copy applied to the message',
      'wa.ai.usedImage': 'The image content was used in the message',
      'wa.ai.imageWillBeUsed': 'The AI will read this image and tie the message to it — it will also be sent with the campaign',
      'wa.ai.attachImageHint': 'Attach the poster/image you will send — the AI will read it and link the message to it',
      'wa.ai.quotaExceeded': 'AI quota exceeded for this month',
      'wa.ai.failed': 'AI request failed — please try again',
      'wa.ai.analyze': 'AI analysis',
      'wa.ai.analysisTitle': 'Conversation AI analysis',
      'wa.ai.rerun': 'Re-run',

      whatsappConnect: 'Connect WhatsApp session',
      whatsappChat: 'Use WhatsApp chat features',
      whatsappMarketing: 'Use WhatsApp marketing campaigns',

      // Staff notification keys (staffNotificationService)
      'ntf.serviceApproved.title': 'Service request approved',
      'ntf.serviceApproved.message': 'Your request for {{service_name}} has been approved',
      'ntf.serviceRejected.title': 'Service request rejected',
      'ntf.serviceRejected.message': 'Your request for {{service_name}} was rejected: {{reason}}',
      'ntf.partApproved.title': 'Part request approved',
      'ntf.partApproved.message': 'Your request for {{quantity}}× {{part_name}} has been approved',
      'ntf.partRejected.title': 'Part request rejected',
      'ntf.partRejected.message': 'Your request for {{quantity}}× {{part_name}} has been rejected',
      'ntf.issueAnswered.title': 'Issue report answered',
      'ntf.issueAnswered.message': 'Your issue report ($t(issueTypes.{{issue_type}})) is now $t(ntf.issueStatus.{{status}})',
      'ntf.issueStatus.open': 'open',
      'ntf.issueStatus.in_progress': 'in progress',
      'ntf.issueStatus.resolved': 'resolved',
      'ntf.issueStatus.closed': 'closed',

      // Vehicle catalog super-admin bell notification (vehicleCatalogMatcher.js,
      // fires once when an unmatched make/model crosses 5 scan/manual hits).
      'ntf.onboardingLead.submitted.title': 'New onboarding application',
      'ntf.onboardingLead.submitted.message': '{{name}} has submitted their onboarding application — it needs your review',
      'ntf.onboardingLead.resubmitted.title': 'Onboarding application updated',
      'ntf.onboardingLead.resubmitted.message': '{{name}} has completed the details you asked for — it needs your review',
      'ntf.catalogMiss.title': 'Vehicle catalog: unmatched entry',
      'ntf.catalogMiss.message': '"{{raw}}" has been seen {{count}} times without a catalog match — review it',

      // Vehicle catalog — VehicleModal make/model matching UX
      'catalog.ocrUnmatchedHint': 'Scan read: "{{raw}}" — choose manually',
      'catalog.useFreeText': 'Use "{{text}}"',

      // Voice → job card (spec docs/superpowers/specs/2026-07-18-voice-job-card-design.md)
      'voice.recorder.record': 'Record',
      'voice.recorder.stop': 'Stop',
      'voice.recorder.recording': 'Recording…',
      'voice.recorder.use': 'Use this recording',
      'voice.recorder.discard': 'Discard',
      'voice.recorder.processing': 'Analyzing the recording…',
      'voice.recorder.micDenied': 'Microphone access was denied or is unavailable.',
      'voice.recorder.notSupported': 'Voice recording isn\'t supported on this device/browser.',
      'voice.errors.noSpeech': 'No speech was detected in the recording. Please try again.',
      'voice.errors.quotaExceeded': 'Monthly AI quota exhausted for this company.',
      'voice.errors.voiceQuotaExceeded': 'Voice limit reached for this month — you can still type.',
      'voice.errors.sttFailed': 'Speech-to-text failed. Please try again.',
      'voice.errors.extractionFailed': 'Could not extract structured data from the recording.',
      'voice.errors.invalidInput': 'The recording could not be read.',
      'voice.errors.generic': 'Voice intake failed. Please try again.',
      'voice.jobCard.button': 'Record the problem description',
      'voice.jobCard.modalTitle': 'Voice Intake — New Job Card',
      'voice.jobCard.transcriptToggle': 'Show transcript',
      'voice.jobCard.filled': 'Form filled from the recording — review before saving.',
      'voice.jobCard.unmatchedHint': 'Heard: "{{raw}}" — no match found, choose manually',
      'voice.jobCard.servicesLabel': 'Requested services',
      'voice.findings.button': 'Dictate finding',
      'voice.findings.modalTitle': 'Voice Intake — Findings',
      'voice.findings.transcriptToggle': 'Show transcript',
      'voice.findings.draftsTitle': 'Extracted findings ({{count}})',
      'voice.findings.useDraft': 'Use this draft',
      'voice.findings.applied': 'Draft applied — add parts/services and submit.',
      'voice.findings.none': 'No findings could be extracted from the recording.',
      'voice.dictate.button': 'Dictate by voice',
      'voice.dictate.modalTitle': 'Voice Dictation',
      'voice.dictate.appended': 'Question filled from the recording — review before sending.',
      'voice.askByVoice': 'Ask by voice',

      // Smart Assistant (spec docs/superpowers/specs/2026-07-18-voice-assistant-design.md,
      // v2 amendments: renamed from "Voice Assistant" — chat-agent UI, strictly MotorMind-scoped)
      'assistant.confirm.needsApproval': 'Needs your approval before it runs',
      'sa.permissionHidesElement': 'Permission hides element — does not disable it',
      'assistant.nav': 'Smart Assistant',
      'assistant.title': 'Smart Assistant',
      'assistant.subtitle': 'Ask by voice or type — reads answer instantly, actions ask before running.',
      'assistant.connected': 'Connected',
      'assistant.viaVoice': 'By voice',
      'assistant.viaText': 'Typed',
      'assistant.hint.idle': 'Tap and talk',
      'assistant.hint.listening': 'Listening… tap to stop',
      'assistant.hint.thinking': 'Thinking…',
      'assistant.thinking': 'Thinking…',
      'assistant.heard': 'Heard: "{{text}}"',
      'assistant.typeInstead': 'Or type your question',
      'assistant.typePlaceholder': 'Message Smart Assistant…',
      'assistant.send': 'Send',
      'assistant.empty.title': 'How can I help?',
      'assistant.empty.subtitle': 'Ask about job cards, customers, vehicles, appointments, or your financial position.',
      'assistant.empty.chip1': 'Show me open job cards',
      'assistant.empty.chip2': "What's our financial position this month?",
      'assistant.empty.chip3': "Today's appointments",
      'assistant.empty.chip4': 'How much oil filter stock do we have?',
      'assistant.empty.chip5': 'Create a quotation',
      'assistant.empty.chip6': "Who's on leave?",
      'assistant.outOfScope': "I can only help with MotorMind — job cards, customers, vehicles, appointments, and finances.",
      'assistant.featureDisabled.title': "Smart Assistant isn't enabled",
      'assistant.featureDisabled.body': 'Ask your account manager to enable the Smart Assistant for your company.',
      'assistant.confirm.execute': 'Run it',
      'assistant.confirm.cancel': 'Cancel',
      'assistant.confirm.executing': 'Running…',
      'assistant.confirm.done': 'Done',
      'assistant.confirm.failed': 'Failed — try again',
      'assistant.confirm.dismissed': 'Not done — you moved on',
      'assistant.history.title': 'Past conversations',
      'assistant.history.newConversation': 'New conversation',
      'assistant.history.empty': 'No past conversations yet.',
      'assistant.history.untitled': 'Untitled conversation',
      'assistant.history.readOnlyNote':
        'This is a saved conversation — shown as a read-only record. Anything you type continues it.',
      'assistant.history.deleteButton': 'Delete',
      'assistant.history.deleteConfirmTitle': 'Delete this conversation?',
      'assistant.history.deleteConfirmBody': "This can't be undone.",
      'assistant.history.deleteFailed': "Couldn't delete this conversation.",
      'assistant.history.loadFailed': "Couldn't load conversation history.",
      'assistant.history.loadConversationFailed': "Couldn't open this conversation.",
      'assistant.history.jobCardLabel': 'Looked up job card {{desc}}',
      'assistant.history.customerLabel': 'Looked up customer {{desc}}',
      'assistant.history.vehicleLabel': 'Looked up vehicle {{desc}}',
      'assistant.history.partLabel': 'Looked up part {{desc}}',
      'assistant.history.lowStockLabel': 'Showed {{count}} low-stock part(s)',
      'assistant.history.customerHistoryLabel': 'Looked up customer history for {{desc}}',
      'assistant.history.quotationLabel': 'Looked up quotation {{desc}}',
      'assistant.history.invoiceLabel': 'Looked up invoice {{desc}}',
      'assistant.history.supplierLabel': 'Looked up supplier {{desc}}',
      'assistant.history.employeeLabel': 'Looked up employee {{desc}}',
      'assistant.history.financialLabel': 'Showed the financial position',
      'assistant.history.taxLabel': 'Showed the tax position',
      'assistant.history.vatAdvisorLabel': 'VAT advisory',
      'assistant.history.appointmentsLabel': 'Showed {{count}} appointment(s)',
      'assistant.history.picklistLabel': 'Showed {{count}} options to choose from',
      'assistant.history.confirmLabel': '{{title}} — {{status}}',
      'assistant.history.statusDone': 'Completed',
      'assistant.history.statusFailed': 'Not completed',
      'assistant.history.statusPending': 'Left unconfirmed',
      'assistant.history.genericLabel': '(no details saved)',
      'assistant.notFound.title': "Couldn't find it",
      'assistant.notFound.body': 'I couldn\'t find anything for "{{text}}" — try the customer\'s name, the job card number, or the plate.',
      'assistant.notFound.emptyList': 'There are no {{label}} right now.',
      'assistant.pickOne': 'Which one did you mean?',
      'assistant.results.jobCard': 'Job Card',
      'assistant.results.jobCardList': 'You have {{count}} {{label}}.',
      'assistant.results.customer': 'Customer',
      'assistant.results.vehicle': 'Vehicle',
      'assistant.results.financial': 'Financial Position',
      'assistant.results.financialIntro': "Here's your financial position right now:",
      'assistant.results.unavailable': 'Unavailable right now.',
      'assistant.results.appointments': "Today's Appointments",
      'assistant.results.noAppointments': 'No appointments today.',
      'assistant.results.permissionHidden': 'Hidden — no permission',
      'assistant.statusLabel.open': 'open job card(s)',
      'assistant.statusLabel.undelivered': 'vehicle(s) not yet delivered',
      'assistant.statusLabel.generic': 'job card(s)',
      'assistant.errors.actionFailed': 'Something went wrong — try again.',
      'assistant.actions.createJobCard': 'Open the new job card form',
      'assistant.actions.createJobCardBody': 'Start a fresh job card and use voice intake there to fill it in.',
      'assistant.actions.open': 'Open',
      'assistant.actions.createAppointment': 'Create appointment',
      'assistant.actions.holdJobCard': 'Put job card on hold',
      'assistant.actions.resumeJobCard': 'Resume job card',
      'assistant.actions.assignTask': 'Assign task',
      'assistant.actions.sendPaymentLink': 'Send payment link',
      'assistant.fields.customer': 'Customer',
      'assistant.fields.time': 'Time',
      'assistant.fields.service': 'Service',
      'assistant.fields.jobCard': 'Job card',
      'assistant.fields.reason': 'Reason',
      'assistant.fields.task': 'Task',
      'assistant.fields.staff': 'Staff',
      'assistant.fields.amount': 'Amount',
      'assistant.fields.originalReceipt': 'Original receipt',
      'assistant.fields.availableCredit': 'Available credit',
      'assistant.needMoreInfo': "Couldn't match this to a real record — open the page to do it manually.",
      'assistant.confidence.low': "Not sure what you meant — treating it as a question.",
      'assistant.charts.revenueTrend': 'Revenue trend',
      'assistant.charts.previousPeriod': 'Previous',
      'assistant.charts.currentPeriod': 'Current',
      'assistant.charts.cashSplit': 'Cash on hand vs. bank',
      'assistant.charts.arAging': 'Receivables aging',
      'assistant.charts.analyzing': 'Analyzing…',
      'assistant.charts.analysisFailed': 'Analysis unavailable right now — the numbers above are still accurate.',
      'assistant.brief.checkedIn': 'Checked in {{date}}',
      'assistant.brief.done': 'Done',
      'assistant.brief.remaining': 'Remaining',
      'assistant.brief.noneYet': '—',
      'assistant.brief.total': 'Total',
      'assistant.brief.paid': 'Paid',
      'assistant.brief.due': 'Due',
      'assistant.brief.openCard': 'Open job card',
      'assistant.tax.title': 'Tax Position',
      'assistant.tax.period': '{{from}} – {{to}}',
      'assistant.tax.outputVat': 'Output VAT (sales)',
      'assistant.tax.inputVat': 'Input VAT (purchases)',
      'assistant.tax.notTracked': 'Not tracked here yet',
      'assistant.tax.netDue': 'Net VAT due',
      'assistant.tax.netDueNote': 'Based on sales VAT only — purchase (input) VAT isn\'t tracked in MotorMind yet.',
      'assistant.vat.title': 'VAT position & advice',
      'assistant.vat.due': 'Net VAT due',
      'assistant.vat.notRegistered': 'Not VAT-registered',
      'assistant.vat.cashAvailable': 'Cash available',
      'assistant.vat.cashUnavailable': 'Cash figures not available',
      'assistant.vat.covered': 'Covered',
      'assistant.vat.shortfall': 'Shortfall',
      'assistant.vat.forecastTitle': 'Collectible from open job cards',
      'assistant.vat.moreCards': '+{{count}} more',
      'assistant.vat.overdueLine': 'Overdue receivables',
      'assistant.vat.ofTotalAr': 'of total receivables',
      'assistant.actions.issueReceipt': 'Issue payment receipt',
      'assistant.actions.refundReceipt': 'Issue payment voucher (refund)',
      'assistant.actions.draftQuotation': 'Draft quotation',
      'assistant.actions.convertQuotationToInvoice': 'Convert quotation to invoice',
      'assistant.actions.jobCardStatusChange': 'Change job card status',
      'assistant.disclose.textsPaymentLink': 'This will text {{name}} a payment link.',
      'assistant.disclose.paidReceiptNoLink':
        "This records {{name}}'s payment as already received and sends them the receipt. No payment link is sent.",
      'assistant.disclose.textsAppointment': 'This will text {{name}} the appointment details.',
      'assistant.disclose.sendsQuotation': 'This will send {{name}} the quotation to approve, by SMS or WhatsApp.',
      'assistant.disclose.mayPostInvoice':
        'If the deposit covers the total, the invoice is posted immediately and cannot be edited.',
      'assistant.disclose.statusMayNotify': 'Delivered, closed or cancelled will notify the customer.',
      'assistant.disclose.refundIsFinal': 'A refund is final and cannot be undone.',
      'assistant.fields.items': 'Items',
      'assistant.fields.subtotal': 'Subtotal',
      'assistant.fields.vat': 'VAT ({{rate}}%)',
      'assistant.fields.total': 'Total',
      'assistant.fields.note': 'Note',
      'assistant.fields.quotation': 'Quotation',
      'assistant.fields.from': 'From',
      'assistant.fields.to': 'To',
      'assistant.quotation.willSend': 'Will be sent to the customer for approval.',
      'assistant.ask.customer': 'Which customer? Say or type the name.',
      'assistant.errors.noRefundableAdvance':
        '{{customer}} has no refundable advance. A payment voucher (سند صرف) can only be issued against a paid downpayment receipt that still has available credit.',
      'assistant.errors.refundExceedsCredit': 'The refund is larger than the available credit on that receipt.',
      'assistant.ask.amount': 'What amount (SAR)?',
      'assistant.ask.method':
        "How was it paid — cash, bank transfer, card (POS), or not yet (I'll text a payment link)?",
      'assistant.ask.job_card': 'Which job card? Say the number, customer, or plate.',
      'assistant.ask.time': 'When? Say a day and time.',
      'assistant.ask.task': 'Which task?',
      'assistant.ask.staff': 'Assign to which staff member?',
      'assistant.ask.items': 'What items, and their prices?',
      'assistant.ask.status': 'Which status should it move to?',
      'assistant.ask.quotation': 'Which quotation? Say the number or the customer name.',
      'assistant.ask.cancelled': 'Okay, cancelled.',
      'assistant.ask.pendingChip': 'Completing: {{action}}',
      'assistant.ask.dismiss': 'Dismiss',
      // assistant prerequisites (Task 6) — offer to create a missing entity,
      // pick between matches, warn on a duplicate, unwind a whole chain.
      'assistant.prereq.notFound': 'There is no customer named "{{query}}". Create one?',
      'assistant.prereq.createCustomer': 'Create customer',
      'assistant.prereq.nearMatches': 'No exact match for "{{query}}". Did you mean:',
      'assistant.prereq.manyMatches': 'Several customers match "{{query}}". Which one?',
      'assistant.prereq.noneCreateNew': 'None of these — create new customer',
      'assistant.prereq.duplicate': '{{label}} is already a customer with this phone. Use them instead?',
      'assistant.prereq.usingExisting': 'Using the existing customer {{name}}. No new customer was created.',
      'assistant.prereq.partial': '{{name}} was created, but the {{action}} was not completed: {{reason}}',
      'assistant.prereq.supersededReason': 'you started a new request',
      'assistant.prereq.cancelledChain': 'Cancelled: {{actions}}. Nothing was saved.',
      'assistant.prereq.blocked.NO_PERMISSION_TO_CREATE':
        'There is no customer named "{{query}}", and you can\'t create customers. Ask a manager to add them.',
      'assistant.prereq.blocked.NO_PERMISSION_FOR_ACTION':
        'You don\'t have permission to do this, so I won\'t create "{{query}}" for it.',
      'assistant.prereq.blocked.STACK_TOO_DEEP': 'That needs too many steps at once. Please finish the current one first.',
      'assistant.prereq.blocked.NOT_FOUND_NOT_CREATABLE': 'I couldn\'t find "{{query}}".',
      'assistant.prereq.blocked.QUOTATION_HOME_BRANCH_ONLY': 'Quotations can be converted from your home branch only. Use an account that belongs to a branch, then ask me again.',
      'assistant.prereq.createVehicle': 'Add vehicle',
      'assistant.prereq.noneCreateNewVehicle': 'None of these — add a new vehicle',
      'assistant.prereq.tooMany': 'Too many matches for "{{query}}". Please be more specific.',
      'assistant.confirm.unknownOutcome': "We couldn't reach the server, so we don't know whether this was completed. Please check the record before trying again.",
      'assistant.confirm.awaitingChoice': 'Pick an option above to continue — the card unlocks once the choice is made.',
      'assistant.prereq.phoneBelongsTo': "This phone number already belongs to {{label}}. Use this customer instead?",
      'assistant.ask.plate': 'What is the vehicle\'s plate number?',
      'assistant.ask.make': 'What is the vehicle\'s make?',
      'assistant.ask.model': 'What is the vehicle\'s model?',
      'assistant.ask.year': 'What is the vehicle\'s model year?',
      'assistant.fields.plate': 'Plate',
      'assistant.fields.make': 'Make',
      'assistant.fields.model': 'Model',
      'assistant.fields.year': 'Year',
      'assistant.prereq.blocked.LOOKUP_FAILED': 'I couldn\'t check "{{query}}" just now. Please try again.',
      'assistant.prereq.blocked.NO_BRANCH_CONTEXT': 'Pick a branch first, then ask me again.',
      'assistant.denied.issue_receipt': 'You don\'t have permission to record receipts.',
      'assistant.denied.refund_receipt': 'You don\'t have permission to issue refunds.',
      'assistant.denied.send_payment_link': 'You don\'t have permission to send payment links.',
      'assistant.denied.create_appointment': 'You don\'t have permission to create appointments.',
      'assistant.denied.assign_task': 'You don\'t have permission to assign tasks.',
      'assistant.denied.hold_job_card': 'You don\'t have permission to put job cards on hold.',
      'assistant.denied.resume_job_card': 'You don\'t have permission to resume job cards.',
      'assistant.denied.draft_quotation': 'You don\'t have permission to draft quotations.',
      'assistant.denied.convert_quotation_to_invoice': 'You don\'t have permission to convert quotations to invoices.',
      'assistant.denied.job_card_status_change': 'You don\'t have permission to change a job card\'s status.',
      'assistant.denied.create_customer': 'You don\'t have permission to add customers.',
      'assistant.denied.create_vehicle': 'You don\'t have permission to add vehicles.',
      'assistant.ask.first_name': "What is the customer's first name?",
      'assistant.ask.last_name': "What is the customer's last name?",
      'assistant.ask.phone': "What is the customer's mobile number?",
      'assistant.fields.phone': 'Phone',
      'assistant.results.part': 'Part',
      'assistant.parts.lowStock': 'Low stock',
      'assistant.parts.inStock': 'In stock',
      'assistant.parts.sellingPrice': 'Selling price',
      'assistant.parts.noLowStock': 'Nothing is low on stock right now.',
      'assistant.parts.lowStockTitle': '{{count}} part(s) low on stock',
      'assistant.results.customerHistory': 'Customer History',
      'assistant.results.visits': 'Visits',
      'assistant.results.vehicles': 'Vehicles',
      'assistant.results.totalSpent': 'Total spent',
      'assistant.results.openBalance': 'Open balance',
      'assistant.results.quotation': 'Quotation',
      'assistant.results.total': 'Total',
      'assistant.results.validUntil': 'Valid until',
      'assistant.results.invoice': 'Invoice',
      'assistant.results.supplier': 'Supplier',
      'assistant.results.balance': 'Balance',
      'assistant.results.lastPayment': 'Last payment',
      'assistant.results.none': 'None',
      'assistant.results.employee': 'Employee',
      'assistant.results.noEmployees': 'No employees found.',
      'assistant.results.staffOnLeave': '{{count}} staff on leave',
      'assistant.results.contractsExpiring': '{{count}} contract(s) expiring within {{days}} days',
      'assistant.results.daysLeft': '{{count}}d left',

      // Voice Conversation mode — the overlay on the Smart Assistant chat:
      // controls, the status line, and the SPOKEN replies speechReply builds.
      // The say.* strings are heard, not read: short, no URLs, no emoji.
      'assistant.voice.start': 'Start voice conversation',
      'assistant.voice.end': 'End conversation',
      'assistant.voice.back': 'Back to typing',
      'assistant.voice.settings': 'Conversation settings',
      'assistant.voice.mute': 'Mute microphone',
      'assistant.voice.unmute': 'Unmute microphone',
      'assistant.voice.typePlaceholder': 'Type a message…',
      'assistant.voice.savedNote': 'Your conversation stays in this chat',
      'assistant.voice.states.connecting': 'Connecting…',
      'assistant.voice.states.listening': 'Listening to you',
      'assistant.voice.states.thinking': 'Working on the details',
      'assistant.voice.states.speaking': 'Speaking to you',
      'assistant.voice.states.muted': 'Microphone muted',
      'assistant.voice.states.limit': 'Monthly voice minutes used up',
      'assistant.voice.states.unavailable': 'Voice conversation unavailable',
      'assistant.voice.limitReached': 'Voice minutes for this month are used up — you can keep typing.',
      'assistant.voice.keepTyping': 'Continue typing',
      'assistant.voice.micDenied': 'Microphone access was denied — allow it in your browser, or keep typing.',
      'assistant.voice.unavailable': 'Voice conversation is unavailable right now — you can keep typing.',
      'assistant.voice.say.done': 'All done — {{title}}.',
      'assistant.voice.say.failed': "Sorry, that didn't go through — try again, or finish it on the screen.",
      'assistant.voice.say.cancelled': 'Okay, cancelled — nothing was changed.',
      'assistant.voice.say.confirmQuestion': 'Shall I go ahead?',
      'assistant.voice.say.foundN_one': 'I found one match.',
      'assistant.voice.say.foundN_other': 'I found {{count}} matches.',
      'assistant.voice.say.andMore_one': 'and one more',
      'assistant.voice.say.andMore_other': 'and {{count}} more',
      'assistant.voice.say.pickQuestion': 'Which one do you mean? Just say the number.',
      'assistant.voice.say.orNoneCreate': "Or say “none of these” and I'll add a new one.",
      'assistant.voice.say.createQuestion': 'Want me to add it?',
      'assistant.voice.say.notFound': "I couldn't find {{query}}.",
      'assistant.voice.say.shownOnScreen': "It's on your screen now.",
      'assistant.voice.say.appointments_one': 'You have one appointment today.',
      'assistant.voice.say.appointments_other': 'You have {{count}} appointments today.',
      'assistant.voice.say.tax': 'The total tax is {{amount}} riyals.',
      'assistant.voice.say.paymentLink': 'A payment link for {{amount}} riyals is on the screen.',
      'assistant.voice.say.stillWorking': "Still on it — the result will show up on your screen.",
      'assistant.voice.say.chooseOnScreen': 'Please pick this one on the screen.',
      'assistant.voice.say.confirmOnScreen': 'Please confirm this one on the screen.',
      'assistant.voice.say.amount': '{{amount}} riyals',
      'assistant.voice.say.pickOption': 'Number {{n}}: {{label}}',
      'assistant.voice.say.pickByNumber': "They're numbered on your screen — say the number, or tap one.",
      'assistant.voice.say.greeting':
        "Welcome! I'm your assistant in MotorMind. How can I help — or would you like a quick overview of your center?",
      'assistant.voice.say.greetingNamed':
        "Welcome, {{name}}! I'm your assistant in MotorMind. How can I help — or would you like a quick overview of your center?",
      'assistant.voice.say.overview': "Here's your center at a glance.",
      'assistant.voice.say.anytime': "No problem — I'm here whenever you need me.",
      'assistant.voice.say.ask_customer': 'Which customer? Just tell me the name.',
      'assistant.voice.say.ask_amount': 'How much is it?',
      'assistant.voice.say.ask_method': "How did they pay — cash, bank transfer, or card? Or not yet, and I'll text them a payment link?",
      'assistant.voice.say.ask_job_card': 'Which job card? The number, the customer, or the plate is fine.',
      'assistant.voice.say.ask_time': 'When? Tell me the day and the time.',
      'assistant.voice.say.ask_task': 'Which task?',
      'assistant.voice.say.ask_staff': 'Who should I give it to?',
      'assistant.voice.say.ask_items': 'What are the items, and their prices?',
      'assistant.voice.say.ask_status': 'Which status should it move to?',
      'assistant.voice.say.ask_quotation': 'Which quotation? The number or the customer name is fine.',
      'assistant.voice.say.ask_first_name': "What's the customer's first name?",
      'assistant.voice.say.ask_last_name': 'And the last name?',
      'assistant.voice.say.ask_phone': "What's their mobile number?",
      'assistant.voice.pickTitle': 'Say the number, or tap one',
      'assistant.voice.session': 'Voice conversation',
      'assistant.voice.settingsVoice': 'Voice',
      'assistant.voice.review': 'Review before confirming',
      'assistant.voice.confirm': 'Confirm',
      'assistant.voice.cancel': 'Cancel',

      // Vehicle catalog — super-admin page (nav + Catalog/Review queue tabs)
      'catalog.navTitle': 'Vehicle Catalog',
      'catalog.pageTitle': 'Vehicle Catalog',
      'catalog.pageSubtitle': 'Manage makes and models, and review OCR/AI matching misses',
      'catalog.tabCatalog': 'Catalog',
      'catalog.tabQueue': 'Review Queue',
      'catalog.makes': 'Makes',
      'catalog.addMake': 'Add Make',
      'catalog.editMake': 'Edit Make',
      'catalog.models': 'Models',
      'catalog.addModel': 'Add Model',
      'catalog.editModel': 'Edit Model',
      'catalog.selectMakeHint': 'Select a make to see its models',
      'catalog.noMakes': 'No makes yet',
      'catalog.noModels': 'No models for this make yet',
      'catalog.nameEn': 'Name (English)',
      'catalog.nameAr': 'Name (Arabic)',
      'catalog.aliases': 'Aliases',
      'catalog.aliasesHint': 'Alternate spellings the matcher should accept — type and press Enter',
      'catalog.yearFrom': 'Year from',
      'catalog.yearTo': 'Year to (blank = still in production)',
      'catalog.makeSaved': 'Make saved',
      'catalog.modelSaved': 'Model saved',
      'catalog.saveFailed': 'Save failed',
      'catalog.filterKind': 'Kind',
      'catalog.filterSource': 'Source',
      'catalog.statusOpen': 'Open',
      'catalog.statusResolved': 'Resolved',
      'catalog.statusIgnored': 'Ignored',
      'catalog.kindMake': 'Make',
      'catalog.kindModel': 'Model',
      'catalog.allKinds': 'All kinds',
      'catalog.allSources': 'All sources',
      'catalog.sourceScan': 'Scan',
      'catalog.sourceManual': 'Manual',
      'catalog.sourceAI': 'AI',
      'catalog.sourceNhtsa': 'NHTSA',
      'catalog.colRawText': 'Raw text',
      'catalog.colHitCount': 'Hits',
      'catalog.colSource': 'Source',
      'catalog.colMake': 'Make',
      'catalog.noMisses': 'No misses in the review queue',
      'catalog.addAsAlias': 'Add as alias',
      'catalog.createNew': 'Create new',
      'catalog.ignore': 'Ignore',
      'catalog.aliasTargetKind': 'Attach to',
      'catalog.aliasTargetMake': 'Target make',
      'catalog.aliasTargetModel': 'Target model',
      'catalog.aliasConfirmTitle': 'Add as alias',
      'catalog.aliasConfirmMessage': 'Add "{{raw}}" as an alias of {{target}}?',
      'catalog.ignoreConfirmTitle': 'Ignore this miss?',
      'catalog.ignoreConfirmMessage': "This entry will be marked ignored and won't be suggested again.",
      'catalog.resolved': 'Resolved',
      'catalog.resolveFailed': 'Action failed',

      // Vehicle catalog — super-admin: force-run AI sweep + per-make backfill
      'catalog.sweepNow': 'Run AI update',
      'catalog.sweepRunning': 'Running…',
      'catalog.sweepRunningHint': 'Scanning for new makes/models via AI web search and NHTSA — this can take a few minutes.',
      'catalog.sweepAlreadyRunning': 'A sweep is already running',
      'catalog.resultsTitle': 'Last AI update',
      'catalog.resultsLastRun': 'Last run: {{time}}',
      'catalog.resultsAiLeg': 'AI web sweep',
      'catalog.resultsNhtsaLeg': 'NHTSA',
      'catalog.resultsCandidates': 'Candidates found',
      'catalog.resultsNewItems': 'New queue items',
      'catalog.resultsMakesChecked': 'Makes checked',
      'catalog.resultsErrors': 'Errors',
      'catalog.resultsReviewHint': 'New models are proposed to the Review queue — approve them there.',
      'catalog.resultsGoToQueue': 'Go to Review queue',
      'catalog.backfillMake': 'Complete with AI',
      'catalog.backfillInactive': 'Reactivate this make first to complete it with AI',
      'catalog.backfillSummary': 'Found {{candidates}} candidates, {{newMisses}} new for review',
      'catalog.backfillFailed': 'AI backfill failed',
      'catalog.categorizeNow': 'Fill categories with AI',
      'catalog.categorizeRunning': 'Categorizing…',
      'catalog.categorizeRunningHint': 'Filling in missing model categories via AI — this can take a few minutes.',
      'catalog.categorizeAlreadyRunning': 'Another AI job is already running',
      'catalog.resultsCategorizeLeg': 'AI categorize',
      'catalog.resultsMakesProcessed': 'Makes processed',
      'catalog.resultsCategoriesFilled': 'Categories filled',
      'catalog.classifyNow': 'Classify queue with AI',
      'catalog.classifyRunning': 'Classifying…',
      'catalog.classifyRunningHint': 'Tagging review-queue items with a suggested category via AI — this can take a few minutes.',
      'catalog.arabicNamesNow': 'Fill Arabic names with AI',
      'catalog.arabicNamesRunning': 'Filling Arabic names…',
      'catalog.arabicNamesRunningHint': 'Writing the Saudi-market Arabic name for every model that has none. Names already set are never changed.',
      'catalog.arabicNamesAlreadyRunning': 'Arabic names are already being filled',
      'catalog.resultsArabicLeg': 'Arabic names',
      'catalog.resultsArabicFilled': 'Names filled',
      'catalog.resultsArabicSkipped': 'Skipped (unsure)',
      'catalog.nameArSourceAi': 'AI',
      'catalog.nameArSourceAiHint': 'Filled by AI — edit it if it is wrong',
      'catalog.nameArMissing': 'No Arabic name',
      'catalog.triageNow': 'Smart review with TzamunAI Decide',
      'catalog.triageRunning': 'Reviewing the queue…',
      'catalog.triageRunningHint': 'TzamunAI Decide matches each item to an existing model, rejects non-vehicles, and suggests a category for new models. Confident answers are applied; the rest wait for you.',
      'catalog.triageAlreadyRunning': 'The queue is already being reviewed',
      'catalog.resultsTriageLeg': 'Smart review',
      'catalog.resultsAutoResolved': 'Resolved automatically',
      'catalog.resultsLeftForReview': 'Left for your review',
      'catalog.triageSuggestion': 'TzamunAI Decide suggests',
      'catalog.triageSuggestNew': 'New model',
      'catalog.triageConfidence': 'Confidence',
      'catalog.autoResolvedByJev': 'Resolved by TzamunAI Decide',
      'catalog.filterAutoResolved': 'Resolved by TzamunAI Decide',
      'catalog.undoAutoResolve': 'Undo',
      'catalog.undoAutoResolveConfirmTitle': 'Undo this automatic decision?',
      'catalog.undoAutoResolveConfirmMessage': 'The alias TzamunAI Decide added is removed and the item goes back to the queue.',
      'catalog.undoAutoResolveSuccess': 'Returned to the queue',
      'catalog.resultsJevAnswered': 'Answered by TzamunAI Decide',
      'catalog.resultsGatewayFallback': 'Sent to the AI fallback',
      'catalog.suggestedChangesTitle': 'Categories TzamunAI Decide would change',
      'catalog.suggestedChangesHint': 'TzamunAI Decide disagrees with these saved categories. Nothing was changed — apply the ones you agree with.',
      'catalog.applySuggestion': 'Apply',
      'jc.specialties': 'Specialties',
      'jc.suggestedByJev': 'Suggested by TzamunAI Decide',
      'jc.suggested': 'Suggested',
      'jc.jobType': 'Job type',
      'jc.jobTypeRegular': 'Repair work',
      'jc.jobTypeQuick': 'Quick service',
      'jc.jobTypeFullCheck': 'Full inspection',
      'jc.specialtyFilter': 'Filter by specialty',
      'jc.allSpecialties': 'All specialties',
      'jc.specialty.mechanical': 'Mechanical',
      'jc.specialty.electrical': 'Electrical',
      'jc.specialty.ac': 'Air conditioning',
      'jc.specialty.body_paint': 'Body and paint',
      'jc.specialty.tires_alignment': 'Tyres and alignment',
      'jc.specialty.brakes_suspension': 'Brakes and suspension',
      'jc.specialty.transmission': 'Transmission',
      'jc.specialty.diagnostics': 'Diagnostics',
      'jc.specialty.quick_service': 'Quick service',
      'wa.urgent': 'Urgent',
      'wa.intent.approve': 'Approved',
      'wa.intent.reject': 'Rejected',
      'wa.intent.price_question': 'Price question',
      'wa.intent.status_question': 'Status question',
      'wa.intent.reschedule': 'Reschedule',
      'wa.intent.payment': 'Payment',
      'wa.intent.complaint': 'Complaint',
      'wa.intent.thanks': 'Thanks',
      'wa.intent.other': 'Other',
      'ntf.whatsappUrgentTitle': 'Urgent: WhatsApp reply',
      'ntf.whatsappUrgentBody': 'Customer reply needs attention within the hour',
      'onb.sortFit': 'Sort by fit',
      'onb.aiFit.hot': 'Ready soon',
      'onb.aiFit.warm': 'Exploring',
      'onb.aiFit.cold': 'Unclear fit',
      'onb.aiFit.spam': 'Spam or test',
      'onb.aiSize.single': 'One branch',
      'onb.aiSize.multi': 'Several branches',
      'onb.aiSize.fleet': 'Fleet or dealer',
      'appr.inbox.mostImportantFirst': 'Most important first',
      'jev.suggested': 'Suggested',
      'jevVision.reception.dashboardPhoto': 'Dashboard photo',
      'jevVision.reception.scanPlate': 'Scan plate',
      'jevVision.reception.suggested': 'Suggested',
      'jevVision.reception.suggestedFuelChip': 'Suggested: {{level}}',
      'jevVision.reception.odometerPhoto': 'Odometer photo',
      'jevVision.reception.plateNotFound': 'Plate not found — type it',
      'jevVision.reception.clickToEnlarge': 'Click to enlarge',
      'jevVision.reception.readingDashboard': 'Reading dashboard...',
      'jevVision.reception.scanningPlate': 'Scanning plate...',
      'feedback.aiTags': 'AI tags',
      'feedback.sentiment.positive': 'Positive',
      'feedback.sentiment.neutral': 'Neutral',
      'feedback.sentiment.negative': 'Negative',
      'feedback.topic.price': 'Price',
      'feedback.topic.delay': 'Delay',
      'feedback.topic.quality': 'Quality',
      'feedback.topic.staff': 'Staff',
      'feedback.topic.cleanliness': 'Cleanliness',
      'feedback.topic.praise': 'Praise',
      'feedback.needsCallback': 'Needs a call back',
      'feedback.filterSentiment': 'Sentiment',
      'feedback.allSentiments': 'All sentiments',
      'feedback.filterNeedsCallback': 'Call back',
      'feedback.allCallbackStates': 'All',
      'ntf.feedbackCallback.title': 'Customer needs a call back',
      'ntf.feedbackCallback.message': 'Feedback on job {{jobNumber}} (rating {{rating}}/5) asks for a manager to call the customer back',
      'catalog.webSearchOn': 'Web search: on',
      'catalog.webSearchOff': 'Web search is not configured — the sweep uses AI knowledge only',
      'catalog.webSearchFailed': 'Web search failed — AI knowledge was used instead. Check the Tavily key.',
      'catalog.classifyAlreadyRunning': 'Another AI job is already running',
      'catalog.resultsClassifyLeg': 'AI classify',
      'catalog.resultsClassified': 'Classified',
      'catalog.resultsBatches': 'Batches',

      // Vehicle catalog — review queue: search/pagination + bulk-select (Gmail pattern)
      'catalog.searchPlaceholder': 'Search raw text',
      'catalog.selectAllOnPage': 'Select all on this page',
      'catalog.selectRow': 'Select row',
      'catalog.allOnPageSelected': 'All {{count}} on this page are selected.',
      'catalog.selectAllMatching': 'Select all {{count}} matching',
      'catalog.allMatchingSelected': 'All {{count}} matching selected',
      'catalog.selectedCount': '{{count}} selected',
      'catalog.clearSelection': 'Clear selection',
      'catalog.ignoreSelected': 'Ignore selected',
      'catalog.bulkIgnoreConfirmTitle': 'Ignore {{count}} entries?',
      'catalog.bulkIgnoreConfirmMessage': "These entries will be marked ignored and won't be suggested again.",
      'catalog.bulkIgnoreSuccess': '{{count}} entries ignored',
      'catalog.selectionCapped': "Selection capped at {{count}} — use 'Select all matching' for larger batches.",
      'catalog.addSelected': 'Add selected',
      'catalog.addSelectedTooltip': 'Select specific rows to add — not available in "select all matching" mode',
      'catalog.addSelectedConfirmTitle': 'Create {{count}} models/makes from the selected rows?',
      'catalog.addSelectedConfirmMessage':
        'Selected rows are created directly as new catalog entries, skipping the review queue.',
      'catalog.addSelectedSuccess': '{{created}} added, {{skipped}} skipped',
      'catalog.addSelectedSkippedTitle': '{{count}} skipped',
      'catalog.skipReasonNoMake': 'No make selected',
      'catalog.skipReasonDuplicate': 'Already exists in the catalog',
      'catalog.skipReasonInvalid': 'Invalid data',
      'catalog.skipReasonNotFound': 'Row no longer available',
      'catalog.skipReasonNotOpen': 'Already resolved',
      'catalog.bulkIgnoreSkipped': 'Skipped because already resolved: {{count}}',
      'catalog.skipReasonError': 'Unexpected error',

      // Vehicle catalog — model category metadata (VehicleModel.CATEGORIES)
      'catalog.categoryLabel': 'Category',
      'catalog.noCategory': 'No category',
      'catalog.allCategories': 'All categories',
      'catalog.filterCategory': 'Filter by category',
      'catalog.noModelsForCategory': 'No models match this category',
      'catalog.category.sedan': 'Sedan',
      'catalog.category.suv': 'SUV',
      'catalog.category.crossover': 'Crossover',
      'catalog.category.pickup': 'Pickup',
      'catalog.category.van': 'Van',
      'catalog.category.hatchback': 'Hatchback',
      'catalog.category.coupe': 'Coupe',
      'catalog.category.convertible': 'Convertible',
      'catalog.category.wagon': 'Wagon',
      'catalog.category.minivan': 'Minivan',
      'catalog.category.bus': 'Bus',
      'catalog.category.truck': 'Truck',
      'catalog.category.sport': 'Sport',
      'catalog.category.other': 'Other',
      'catalog.colCategory': 'Category',
      'catalog.categoryNotACar': 'Not a vehicle',
      'catalog.categoryUnclassified': 'Unclassified',
      'catalog.addAllMatching': 'Add all {{count}} matching',
      'catalog.addAllMatchingConfirmTitle': 'Create {{count}} models from this category?',
      'catalog.addAllMatchingConfirmMessage':
        'Every open row matching this category filter is created directly as a new catalog entry, skipping the review queue.',
      'catalog.continueRemaining': 'Continue ({{count}} left)',

      // AI Damage Scan (spec docs/superpowers/specs/2026-07-24-ai-damage-scan-design.md)
      'damageScan.section.title': 'AI Damage Scan',
      'damageScan.section.scanButton': 'Scan for damage',
      'damageScan.section.lastScanned': 'Scanned {{time}}',
      'damageScan.section.noneYet': 'No AI damage scan yet.',
      'damageScan.section.edit': 'Edit',
      'damageScan.section.save': 'Save',
      'damageScan.section.cancel': 'Cancel',
      'damageScan.section.rescan': 'Re-scan',
      'damageScan.section.delete': 'Delete',
      'damageScan.section.rescanConfirmTitle': 'Re-scan for damage?',
      'damageScan.section.rescanConfirmMessage': 'This replaces the current AI damage scan and all its edits.',
      'damageScan.section.deleteConfirmTitle': 'Delete damage scan?',
      'damageScan.section.deleteConfirmMessage': 'This permanently removes the scanned photos and detections.',
      'damageScan.section.cancelConfirmTitle': 'Discard changes?',
      'damageScan.section.cancelConfirmMessage': 'Your edits to this damage scan have not been saved.',
      'damageScan.section.saveSuccess': 'Damage scan updated',
      'damageScan.section.saveFailed': 'Could not save changes',
      'damageScan.section.deleteSuccess': 'Damage scan deleted',
      'damageScan.section.deleteFailed': 'Could not delete the damage scan',
      'damageScan.capture.title': 'AI Damage Scan — New Scan',
      'damageScan.capture.subtitle': 'Photograph the car from each angle',
      'damageScan.capture.closeupHint': 'Get close: shoot each side separately; small damage is missed in wide shots.',
      'damageScan.capture.slot.front': 'Front',
      'damageScan.capture.slot.rear': 'Rear',
      'damageScan.capture.slot.left': 'Left',
      'damageScan.capture.slot.right': 'Right',
      'damageScan.capture.slot.extra1': 'Close-up 1 (optional)',
      'damageScan.capture.slot.extra2': 'Close-up 2 (optional)',
      'damageScan.capture.addPhoto': 'Add photo',
      'damageScan.capture.remove': 'Remove',
      'damageScan.capture.submit': 'Scan for damage',
      'damageScan.capture.submitting': 'Scanning for damage…',
      'damageScan.capture.minPhotoHint': 'Add at least one photo',
      'damageScan.errors.quotaExceeded': 'Monthly AI quota exhausted for this company.',
      'damageScan.errors.extractionFailed': 'AI failed to analyze the photos. Please try again.',
      'damageScan.errors.invalidInput': 'The photos could not be read.',
      'damageScan.errors.generic': 'Damage scan failed. Please try again.',
      'jevVision.damage.ok': 'OK',
      'jevVision.damage.notCar': 'Not a car photo',
      'jevVision.damage.blurryDark': 'Blurry/dark',
      'jevVision.damage.blurry': 'Blurry',
      'jevVision.damage.looksLikeFront': 'Looks like the front',
      'jevVision.damage.looksLikeRear': 'Looks like the rear',
      'jevVision.damage.looksLikeLeft': 'Looks like the left',
      'jevVision.damage.looksLikeRight': 'Looks like the right',
      'jevVision.damage.moveToFront': 'Move to front',
      'jevVision.damage.moveToRear': 'Move to rear',
      'jevVision.damage.moveToLeft': 'Move to left',
      'jevVision.damage.moveToRight': 'Move to right',
      'damageScan.wizard.button': 'AI Damage Scan',
      'damageScan.wizard.analyze': 'Scan with AI',
      'damageScan.wizard.attach': 'Attach to job card',
      'damageScan.wizard.retake': 'Change photos',
      'damageScan.wizard.reviewHint': 'AI result — review and correct it, then attach it to the job card.',
      'damageScan.wizard.stagedCount': '{{count}} photo(s) attached',
      'damageScan.wizard.scanning': 'Scanning for damage…',
      'damageScan.wizard.scanDone': 'Damage scan complete — review the results on the job card',
      'damageScan.wizard.scanFailed': 'Damage scan failed',
      'damageScan.wizard.retry': 'Retry',
      'damageScan.wizard.skip': 'Skip',
      'damageScan.wizard.removeAll': 'Remove photos',
      'damageScan.review.noDamage': 'No damage detected. Use "Add box" below to mark damage the AI missed.',
      'damageScan.review.addBox': 'Add box',
      'damageScan.review.openPhoto': 'Open photo',
      'damageScan.review.resize': 'Resize finding',
      'damageScan.review.dragHint': 'Drag a box to move it, its corner to resize.',
      'damageScan.review.accept': 'Accept',
      'damageScan.review.reject': 'Reject',
      'damageScan.review.pending': 'needs review',
      'damageScan.review.pendingCount': '{{count}} finding(s) still need review',
      'damageScan.review.allReviewed': 'All findings reviewed',
      'damageScan.review.acceptAll': 'Accept all',
      'damageScan.review.mustReviewAll': 'Accept or reject the remaining {{count}} finding(s) first',
      'damageScan.review.deleteBox': 'Delete',
      'damageScan.review.done': 'Done',
      'damageScan.review.note': 'Note',
      'damageScan.review.type': 'Type',
      'damageScan.review.severity': 'Severity',
      'damageScan.review.confidence.label': 'AI confidence',
      'damageScan.review.confidence.high': 'High',
      'damageScan.review.confidence.medium': 'Medium',
      'damageScan.review.confidence.low': 'Low',
      'damageScan.review.scanFailedNotice': 'Failed to scan this photo — retry via re-scan.',
      'damageScan.type.dent': 'Dent',
      'damageScan.type.scratch': 'Scratch',
      'damageScan.type.crack': 'Crack',
      'damageScan.type.paint_damage': 'Paint damage',
      'damageScan.type.rust': 'Rust',
      'damageScan.type.broken_part': 'Broken part',
      'damageScan.type.glass_damage': 'Glass damage',
      'damageScan.type.other': 'Other',
      'damageScan.severity.minor': 'Minor',
      'damageScan.severity.moderate': 'Moderate',
      'damageScan.severity.severe': 'Severe',
      'damageScan.angle.front': 'Front',
      'damageScan.angle.rear': 'Rear',
      'damageScan.angle.left': 'Left',
      'damageScan.angle.right': 'Right',
      'damageScan.angle.other': 'Other',
      'subtask.section': 'Sub-tasks',
      'svcCard.resendDeadCard': 'This job card is closed — the car has been handed back, so its lines can no longer be re-priced.',
      'svcCard.finishSetup': 'Done',
      'svcCard.editService': 'Edit service',
      'svcCard.serviceUpdated': 'Service updated',
      'svcCard.overrideComplete': 'Force complete (admin)',
      'svcCode.label': 'Service code',
      'svcCode.autoHint': 'Leave blank to auto-assign (SRV-0001, SRV-0002, ...)',
      'svcCode.taken': 'This service code is already used by another service',
      'svcCode.jobPrice': 'Price for this job card (SAR)',
      'svcCode.jobPriceHint': 'Editing this changes THIS job card only — the catalog price is untouched',
      'svcCard.overrideConfirmMessage': 'Force "{{name}}" to completed even though it is not approved/finished? This is recorded in the activity log.',
      'svcCard.overrideDone': 'Marked completed (admin override)',
      'svcCard.taskCreatedLine': "A work task 'General' was created for this service — you'll find it in the Tasks list.",
      'svcCard.setupHint': 'Service saved. Add its parts and work steps here — everything saves as you add it. No steps? Just pick who performs it below.',
      'svcCard.noPartsYet': 'No parts yet',
      'svcCard.noStepsYet': 'No steps — the service is performed as one job',
      'svcCard.assignWho': 'Who will perform it?',
      'svcCard.keepUnassigned': 'Keep open — unassigned',
      'subtask.add': 'Add sub-tasks',
      'subtask.addStep': 'Add step',
      'subtask.stepAdded': 'Step added',
      'subtask.editStep': 'Edit step',
      'subtask.stepUpdated': 'Step updated',
      'subtask.errRunningReassign': 'This step is running — it must be paused or completed before reassigning',
      'subtask.errCycle': 'That order would make the steps wait on each other in a circle',
      'subtask.errAssignSupervisor': 'Only a supervisor can change who a step is assigned to',
      'subtask.removeStepTitle': 'Remove step',
      'subtask.removeStepMessage': 'Remove this step? A step that others wait on, or one currently running, cannot be removed.',
      'subtask.addTitle': 'Add sub-tasks',
      'subtask.parentTask': 'Parent task',
      'subtask.parentTaskHint': 'Pick the task these steps belong to',
      'subtask.name': 'Step name',
      'subtask.estimatedHours': 'Estimated hours',
      'subtask.assignee': 'Assigned to',
      'subtask.unassigned': 'Unassigned',
      'subtask.dependsOn': 'Starts after',
      'subtask.dependsOnNone': 'Nothing — can start immediately',
      'subtask.kind': 'Step type',
      'subtask.kindWork': 'Work',
      'subtask.kindQualityCheck': 'Quality check',
      'subtask.qcFailedWithNote': 'Failed quality check — {{notes}}',
      'qc.reopenTaskOnly': 'Reopen the task only (no specific step)',
      'qc.pickFailedServices': 'Which services failed?',
      'qc.stepsWithoutService': 'Steps not attached to a service',
      'qc.pickFailedServicesHint': 'Tick every service that needs rework. Leave them all unticked to reopen the task without naming anything.',
      'qc.pickFailedSteps': 'Which steps of this service were wrong?',
      'qc.serviceHasNoSteps': 'This service has no steps — the whole service will be sent back.',
      'qc.cascadeWarning_one': 'This also sends back 1 step built on your selection:',
      'qc.cascadeWarning_other': 'This also sends back {{count}} steps built on your selection:',
      'subtask.qcFailedNoNote': 'Failed quality check — no note left by the inspector.',
      'subtask.addRow': 'Add another step',
      'subtask.blockedBy': 'Waiting for {{name}}',
      'subtask.start': 'Start',
      'subtask.started': 'Step started — the clock is running',
      'subtask.completedToast': 'Step completed',
      'subtask.errServiceNotApproved': 'Cannot start — the customer has not approved this service yet',
      'staff.task.stepsProgress': '{{done}}/{{total}} steps done — completes automatically',
      'staff.task.stepMine': 'Your step',
      'staff.task.stepBlockedBy': 'Waiting for: {{name}}',
      'staff.task.qcPassedToast': 'QC passed — step closed',
      'staff.task.qcFailedToast': 'QC failed — the chosen step was reopened',
      'staff.task.qcTargetInvalid': 'That step cannot be reopened (running, or on another service)',
      'staff.task.qcFailTitle': 'Fail quality check',
      'staff.task.qcFailPickStep': 'Which step must be redone? Everything built on it will reopen too.',
      'staff.task.qcFailPreviousStep': 'Previous step only',
      'staff.task.qcFailNotesPlaceholder': 'What was wrong? (optional)',
      'subtask.complete': 'Complete',
      'subtask.skip': 'Skip',
      'subtask.reopen': 'Reopen',
      'subtask.qcPass': 'Pass',
      'subtask.qcFail': 'Fail',
      'subtask.progress': '{{done}} of {{total}} done',
      'subtask.empty': 'No sub-tasks yet. Add them to break this job into steps.',
      'subtask.statusPending': 'Pending',
      'subtask.statusInProgress': 'In progress',
      'subtask.statusCompleted': 'Completed',
      'subtask.statusSkipped': 'Skipped',
      'subtask.errBlocked': 'Cannot start — waiting for {{name}}.',
      'subtask.errAlreadyWorking': '{{name}} is already in progress for this technician.',
      'subtask.errHasDependents': 'Cannot delete — {{names}} depend on this step.',
      'subtask.errCycle': 'That would create a loop in the dependency chain.',
      'subtask.errRunningDelete': 'Stop this step before deleting it.',
      'subtask.errRunningReassign': 'Stop this step before changing who it is assigned to.',
      'subtask.errDrivenBySubTasks': 'This task is driven by its sub-tasks. Start or complete the sub-tasks instead.',
      'subtask.errInvoiceLocked': 'This task is billed on a posted invoice and cannot be changed.',
      'subtask.errItemsNotReady': 'All steps are done, but the task still has items awaiting approval.',
      'ntf.sub_task_assigned': 'You have {{count}} new step(s) on job {{job_number}}, starting with {{first}}',
      'ntf.sub_task_ready': 'Your step "{{name}}" on job {{job_number}} is ready to start',
      'ntf.sub_task_qc_failed': 'Step "{{name}}" on job {{job_number}} failed quality check — redo it. {{notes}}',
      'ntf.sub_task_qc_ready': 'Rework finished on job {{job_number}} — "{{name}}" is ready to be checked again',
      // Service-type template editor (default parts + default sub-tasks) — Services.js
      'svcTpl.manageTemplate': 'Manage template',
      'svcTpl.title': 'Template — {{name}}',
      'svcTpl.subtitle': 'Set the default parts and sub-tasks a job card starts with when this service is added.',
      'svcTpl.tabParts': 'Default parts',
      'svcTpl.tabSubTasks': 'Default sub-tasks',
      'svcTpl.addPart': 'Add part',
      'svcTpl.noParts': 'No default parts yet.',
      'svcTpl.errLoadParts': 'Failed to load default parts',
      'svcTpl.noPartChosen': 'No part chosen',
      'svcTpl.choosePart': 'Choose part',
      'svcTpl.changePart': 'Change',
      'svcTpl.pickPartTitle': 'Choose a part',
      'svcTpl.pickPartSelect': 'Select',
      'svcTpl.customPartBadge': 'Custom',
      'svcTpl.optionalBadge': 'Optional',
      'svcTpl.requiredPart': 'Required for this service',
      'svcTpl.sortOrder': 'Sort order',
      'svcTpl.partMeta': 'Qty {{qty}} · Order {{order}}',
      'svcTpl.partSaved': 'Default part saved',
      'svcTpl.partDeleted': 'Default part removed',
      'svcTpl.unknownPart': 'Unknown part',
      'svcTpl.confirmDeletePart': 'Remove this default part from the template?',
      'svcTpl.addSubTask': 'Add sub-task',
      'svcTpl.noSubTasks': 'No default sub-tasks yet.',
      'svcTpl.errLoadSubTasks': 'Failed to load default sub-tasks',
      'svcTpl.subTaskSaved': 'Default sub-task saved',
      'svcTpl.subTaskDeleted': 'Default sub-task removed',
      'svcTpl.confirmDeleteSubTask': 'Remove this default sub-task from the template?',
      'svcTpl.moveUp': 'Move up',
      'svcTpl.moveDown': 'Move down',
      'svcTpl.errDependencyNotSameServiceType': 'That dependency belongs to a different service.',
      'svcTpl.hoursBadge': '{{hours}}h',
      // Job-card detail — service card (service is the job line: JobCardDetails.js,
      // ServiceCard.js, ServicesForTask.js, AddServiceModal.js, AddPartToServiceModal.js)
      'svcCard.addServiceTitle': 'Add a service',
      'svcCard.addPartTitle': 'Add a part to this service',
      'svcCard.pickPartTitle': 'Choose a part',
      'svcCard.partAdded': 'Part added',
      'svcCard.sellingPriceSAR': 'Selling Price (SAR)',
      'svcCard.customerFacing': '(on the quotation)',
      'svcCard.serviceAddedWithDefaults': 'Service added — {{parts}} part(s) and {{subTasks}} sub-task(s) brought in from the template.',
      'svcCard.defaultsPartsNotAdded': '{{count}} template part(s) were NOT added:',
      'svcCard.searchService': 'Search by name or code…',
      'svcCard.noServiceMatch': 'No service matches',
      'svcCard.stockShortTitle': 'Not enough stock for {{count}} of {{total}} parts',
      'svcCard.stockShortLine': 'in stock {{have}}, needs {{need}}',
      'svcCard.stockShortPackageHint': 'You can still add the package. Its parts are added from the job order after approval, once the stock is received.',
      'svcCard.stockShortHint': 'These parts will not be added until the stock is received (purchase invoice).',
      'pkg.missingTitle': 'Package parts not on the job card yet',
      'pkg.inStock': 'in stock: {{qty}}',
      'pkg.noStockHint': 'Add them with the button. A part with no stock is refused until the stock is received (purchase invoice).',
      'pkg.pendingTitle': 'Parts included in this package',
      'pkg.pendingHint': 'Added to the job card once the package is approved.',
      'pkg.partsAfterApproval': 'Package added. Its parts are added once it is approved.',
      'pkg.addMissing': 'Add missing parts',
      'pkg.addMissingDone': '{{count}} part(s) added to the package.',
      'svcCard.defaultsPreviewTitle': 'This will add',
      'svcCard.noDefaults': 'This service has no default parts or sub-tasks.',
      'svcCard.defaultsPartsCount': '{{count}} part(s)',
      'svcCard.defaultsSubTasksCount': '{{count}} sub-task(s)',
      'svcCard.lineTotal': 'Line total',
      'svcCard.sectionCounts': '({{services}} services · {{done}} of {{total}} steps)',
      'svcCard.hoursOnly': '{{h}}h',
      'svcCard.hoursAndMinutes': '{{h}}h {{m}}m',
      'svcCard.estimatedVsActual': '{{estimated}} est · {{actual}} actual',
      'svcCard.estimatedOnly': '{{estimated}} estimated',
      'svcCard.actualOnly': '{{actual}} actual',
      'svcCard.stepEstimate': 'Est. {{hours}}',
      'svcCard.serviceLine': 'Service',
      'svcCard.serviceTotal': 'Service total',
      'svcCard.marginIncompleteTooltip': 'Cost is not fully recorded yet, so the margin is withheld rather than shown as an estimate.',
      'svcCard.noServicesYet': 'No services yet. Add one from the catalog or as a custom line.',
      'svcCard.addFirstService': 'Add the first service',
      'svcCard.unlinkedParts': 'Not linked to a specific service yet',
      // PR#2 Task 2.4 — the customer's per-line rejection reason on the job
      // card, and the in-place "edit & re-send" action.
      'svcCard.declinedBecause': 'Declined: {{reason}}',
      // The 7 built-in rejection reason codes (backend
      // QuotationItem.REJECTION_REASON_CODES). A garage's own custom slots are
      // NOT here — their bilingual label ships on the API payload.
      'qrr.too_expensive': 'Too expensive',
      'qrr.later': 'Will do it later',
      'qrr.elsewhere': 'Will do it elsewhere',
      'qrr.not_needed': "Doesn't need it",
      'qrr.own_part': 'Will supply the part himself',
      'qrr.distrust_diagnosis': "Doesn't trust the diagnosis",
      'qrr.other': 'Other reason',

      // Settings › Data Export (Task 4.1) — docs/superpowers/specs/2026-08-05-tenant-data-export.md §9
      'de.navTitle': 'Data Export',
      'de.pageTitle': 'Export your company data',
      'de.requestButton': 'Request export',
      'de.requestDisabledActive': 'An export is already in progress. You can request another once it finishes.',
      'de.requestVerified': 'Verified — your export is being prepared.',
      'de.codeStepTitle': 'Enter verification code',
      'de.sentToPhone': 'A code was sent to the phone number ending in {{last2}}.',
      'de.sentToPhoneUnknown': 'A code was sent to your registered phone number.',
      'de.codeInputLabel': 'Verification code',
      'de.codeExpiryNote': 'The code is valid for 10 minutes.',
      'de.verify': 'Verify',
      'de.progressTitle': 'Preparing your export',
      'de.progressBody': 'Large exports can take several minutes. You can leave this page — it will still be here when it is ready.',
      'de.historyTitle': 'Export history',
      'de.historyEmpty': 'No exports yet.',
      'de.colStatus': 'Status',
      'de.colRequested': 'Requested',
      'de.colSize': 'Size',
      'de.colExpires': 'Expires',
      'de.colActions': 'Actions',
      'de.download': 'Download',
      'de.deleteExportFile': 'Delete export file',
      'de.deleteConfirmBody': 'This deletes only the downloaded archive file. The garage’s own records — job cards, invoices, customers, HR — are not affected.',
      'de.deleteSuccess': 'Export file deleted.',
      'de.downloadModalTitle': 'Verify to download',
      'de.downloadVerify': 'Verify and download',
      'de.resend': 'Resend code',
      'de.resendCooldown': 'Resend in {{seconds}}s',
      'de.codeResent': 'A new code was sent.',
      // Super-admin data export tab (per-company oversight + platform actions)
      'sde.tabTitle': 'Data Export',
      'sde.intervalLabel': 'Export every (days)',
      'sde.intervalHelp': 'How often this company may complete an export. 30 is the default. Use 0 for no limit — useful while testing or during a migration.',
      'sde.intervalNoLimit': 'No limit',
      'sde.intervalCurrent': 'Currently: one export every {{days}} days.',
      'sde.intervalCurrentNoLimit': 'Currently: unlimited exports.',
      'sde.intervalSaved': 'Export interval updated',
      'sde.intervalInvalid': 'Enter a whole number of days between 0 and 3650.',
      'sde.tenantAccess': "Tenant's own export access",
      'sde.tenantAccessHelp': 'When revoked, nobody in this company can request an export, regardless of their role permissions. Archives they already produced are not affected.',
      'sde.revoke': 'Revoke access',
      'sde.restore': 'Restore access',
      'sde.revoked': 'Data export revoked for this company',
      'sde.restored': 'Data export restored for this company',
      'sde.revokedNotice': 'Data export is revoked for this company.',
      'sde.revokedTag': 'revoked',
      'sde.revokeReasonPrompt': 'Why is data export being revoked for this company? (recorded in the audit log)',
      'sde.reasonTooShort': 'Please give a reason of at least 10 characters.',
      'sde.requestOnBehalf': 'Export this tenant\u2019s data',
      'sde.requestWarning': 'This produces a complete copy of the company\u2019s customers, employees, payroll, financials and media. The reason you give is stored and shown to the tenant in their own export history.',
      'sde.reason': 'Reason',
      'sde.reasonPlaceholder': 'Reason for exporting this tenant\u2019s data',
      'sde.requestExport': 'Request export',
      'sde.codeSent': 'Verification code sent to your phone',
      'sde.enterCode': 'Enter the 6-digit code sent to your phone.',
      'sde.enterDownloadCode': 'Enter the 6-digit code sent to your phone to start the download.',
      'sde.code': 'Verification code',
      'sde.verify': 'Verify',
      'sde.queued': 'Export queued',
      'sde.requestFailed': 'Could not request the export',
      'sde.verifyFailed': 'Could not verify the code',
      'sde.downloadFailed': 'Could not start the download',
      'sde.updateFailed': 'Could not update data export access',
      'sde.loadFailed': 'Could not load export activity',
      'sde.activity': 'Export activity',
      'sde.noExports': 'No exports yet for this company',
      'sde.platformTag': 'MotorMind',
      'sde.download': 'Download',
      'sde.colStatus': 'Status',
      'sde.colRequestedBy': 'Requested by',
      'sde.colReason': 'Reason',
      'sde.colSize': 'Size',
      'sde.colRequestedAt': 'Requested',
      'sde.colActions': 'Actions',
      'de.status.awaiting_otp': 'Awaiting verification',
      'de.status.pending': 'Queued',
      'de.status.running': 'Preparing',
      'de.status.ready': 'Ready',
      'de.status.expired': 'Expired',
      'de.status.failed': 'Failed',
      'de.status.deleting': 'Deleting',
      'de.err.wrongCode': 'Incorrect verification code.',
      'de.err.expiredCode': 'That code has expired.',
      'de.err.maxAttempts': 'Too many incorrect attempts. Please request the export again.',
      'de.err.resendCooldown': 'Please wait before requesting another code.',
      'de.err.sendLimit': 'You’ve reached the maximum number of codes for this request.',
      'de.err.noPhone': 'Your account has no phone number on file. Add one before requesting an export.',
      'de.err.dailyLimit': 'Only one export is allowed per company every 24 hours.',
      'de.err.rateLimit': 'You have already exported recently. See the dialog for the next available date.',
      'de.rateLimitTitle': 'You have already exported recently',
      'de.rateLimitBody': 'Each company may complete one data export every {{days}} days.',
      'de.rateLimitNextLabel': 'You can request the next export on',
      'de.rateLimitLast': 'Your last export was requested on {{date}}.',
      'de.rateLimitHint': 'Archives you have already produced stay downloadable from the history below until they expire.',
      'de.rateLimitClose': 'Got it',
      'de.err.inProgress': 'An export is already in progress or awaiting verification.',
      'de.err.notReady': 'This export is not ready to download yet.',
      'de.err.alreadyUsed': 'This code has already been used.',
      'de.err.noActiveCode': 'No verification code is pending. Please request the export again.',
      'de.err.smsFailed': 'Failed to send the verification code. Please try again.',
      'de.err.notFound': 'This export could not be found.',
      'de.err.revoked': 'You no longer have permission to download this export.',
      'de.err.gone': 'This export archive is no longer available.',
      'de.err.deleteInProgress': 'This export is still in progress and cannot be deleted yet.',
      'de.err.invalidState': 'This export has changed state. Please refresh and try again.',
      'de.err.generic': 'Something went wrong. Please try again.',
      // Fleet F1 portal (lane fleet-fe1)
      "fleet.nav": "Fleets",
      "fleet.new": "New fleet",
      "fleet.templates": "SLA templates",
      "fleet.searchPlaceholder": "Search fleets…",
      "fleet.filter.all": "All statuses",
      "fleet.empty": "No fleets yet",
      "fleet.emptyDesc": "Fleet accounts you set up for corporate customers will appear here.",
      "fleet.setupIncomplete": "Setup not finished",
      "fleet.continueSetup": "Continue setup",
      "fleet.loadFailed": "Could not load fleets",
      "fleet.line.contractPrice": "Contract price",
      "fleet.line.changePayer": "Change payer",
      "fleet.line.payerChanged": "Payer updated",
      "fleet.line.payerFailed": "The payer could not be changed",
      "fleet.saveFailed": "Could not save",
      "fleet.col.name": "Fleet",
      "fleet.col.cars": "Cars",
      "fleet.col.groups": "Groups",
      "fleet.col.status": "Status",
      "fleet.status.draft": "Draft",
      "fleet.status.active": "Active",
      "fleet.status.suspended": "Suspended",
      "fleet.status.closed": "Closed",
      "fleet.stmt.sendFailed": "The statement did not reach any finance contact. Check their WhatsApp numbers and try again.",
      "fleet.stmt.title": "Statement",
      "fleet.stmt.typeCustomerCredit": "Fleet credit",
      "fleet.stmt.typeOpeningBalance": "Opening balance",
      "fleet.stmt.group": "Group",
      "fleet.stmt.groupAll": "All groups",
      "fleet.stmt.ageing": "Ageing",
      "fleet.stmt.opening": "Opening balance",
      "fleet.stmt.closing": "Closing balance",
      "fleet.stmt.credit": "Credit",
      "fleet.stmt.creditIn": "Credit in {{amount}}",
      "fleet.stmt.creditOut": "Credit out {{amount}}",
      "fleet.stmt.car": "Car",
      "fleet.stmt.send": "Send statement",
      "fleet.stmt.sendNow": "Send now",
      "fleet.stmt.sendConfirm": "Send this statement to the fleet's finance contacts on WhatsApp?",
      "fleet.stmt.sent_one": "Statement sent to one contact",
      "fleet.stmt.sent_other": "Statement sent to {{count}} contacts",
      "fleet.stmt.sent_two": "Statement sent to 2 contacts",
      "fleet.stmt.sent_few": "Statement sent to {{count}} contacts",
      "fleet.stmt.sent_many": "Statement sent to {{count}} contacts",
      "fleet.stmt.unassigned": "Cars without a group",
      "fleet.stmt.debit": "Debit",
      "fleet.stmt.empty": "No statement rows in this period",
      "fleet.stmt.balance": "Balance",
      "fleet.wizard.progress": "Setup steps",
      "fleet.wizard.step1": "Company",
      "fleet.wizard.step2": "Groups",
      "fleet.wizard.step3": "Contacts",
      "fleet.wizard.step4": "Cars",
      "fleet.wizard.step5": "SLA start",
      "fleet.wizard.step6": "Terms",
      "fleet.wizard.step7": "Prices",
      "fleet.wizard.step8": "Coverage",
      "fleet.wizard.step9": "Service levels",
      "fleet.wizard.step10": "Maintenance",
      "fleet.wizard.step11": "Review",
      "fleet.wizard.back": "Back",
      "fleet.wizard.next": "Save & next",
      "fleet.wizard.exit": "Save & exit",
      "fleet.wizard.noDraft": "The SLA has not been started yet — start it in step 5 first.",
      "fleet.company.name": "Legal name (English)",
      "fleet.company.nameAr": "Legal name (Arabic)",
      "fleet.company.phone": "Main phone",
      "fleet.company.email": "Email",
      "fleet.company.vat": "VAT number",
      "fleet.company.cr": "Commercial registration",
      "fleet.company.building": "Building number",
      "fleet.company.street": "Street",
      "fleet.company.district": "District",
      "fleet.company.city": "City",
      "fleet.company.postal": "Postal code",
      "fleet.company.region": "Region",
      "fleet.company.vatHint": "The buyer details follow the ZATCA rules: a 15-digit VAT number starting and ending with 3, a 10-digit commercial registration, a 4-digit building number and a 5-digit postal code.",
      "fleet.company.linkCustomer": "Link an existing company customer",
      "fleet.company.linkSearch": "Search company customers…",
      "fleet.company.linkEmpty": "No matching company customer",
      "fleet.company.linked": "Linked customer — details filled from their record",
      "fleet.company.linkFailed": "Could not link the customer",
      "fleet.groups.skippable": "Groups are optional — you can skip this step and add them later.",
      "fleet.groups.name": "Group name (English)",
      "fleet.groups.nameAr": "Group name (Arabic)",
      "fleet.groups.po": "PO number",
      "fleet.groups.add": "Add group",
      "fleet.groups.added": "Group added",
      "fleet.groups.updated": "Group updated",
      "fleet.groups.deactivate": "Deactivate",
      "fleet.groups.deactivated": "Group deactivated",
      "fleet.groups.deactivateConfirm": "Deactivate the group «{{name}}»? Its cars keep their history.",
      "fleet.groups.none": "No groups yet",
      "fleet.groups.poShort": "PO {{number}}",
      "fleet.contacts.title": "Contacts",
      "fleet.contacts.add": "Add contact",
      "fleet.contacts.name": "Name",
      "fleet.contacts.phone": "Mobile",
      "fleet.contacts.email": "Email",
      "fleet.contacts.role": "Role",
      "fleet.contacts.approvalLimit": "Approval limit",
      "fleet.contacts.approvalLimitHint": "Empty means no limit",
      "fleet.contacts.group": "Group",
      "fleet.contacts.allGroups": "All groups",
      "fleet.contacts.save": "Save contact",
      "fleet.contacts.none": "No contacts yet",
      "fleet.contacts.missingRoles": "No contact for these roles yet: {{roles}}",
      "fleet.contactAdded": "Contact added",
      "fleet.contacts.updated": "Contact updated",
      "fleet.roles.approver": "Approver",
      "fleet.roles.finance": "Finance",
      "fleet.roles.fleet_manager": "Fleet manager",
      "fleet.roles.driver": "Driver",
      "fleet.vehicles.title": "Cars",
      "fleet.vehicles.add": "Add a car",
      "fleet.vehicles.plate": "Plate number",
      "fleet.vehicles.plateType": "Plate type",
      "fleet.vehicles.vin": "VIN (chassis number)",
      "fleet.vehicles.make": "Make",
      "fleet.vehicles.model": "Model",
      "fleet.vehicles.year": "Year",
      "fleet.vehicles.group": "Group",
      "fleet.vehicles.noGroup": "No group",
      "fleet.vehicles.allGroups": "All groups",
      "fleet.vehicles.odometer": "Odometer reading",
      "fleet.vehicles.odometerHint": "The reading when the car joined the fleet",
      "fleet.vehicles.save": "Save car",
      "fleet.vehicles.added": "Car added",
      "fleet.vehicles.updated": "Car updated",
      "fleet.vehicles.none": "No cars yet — Next is allowed, but activation needs at least one car.",
      "fleet.vehicles.import": "Import from Excel",
      "fleet.vehicles.endMembership": "End membership",
      "fleet.vehicles.endConfirm": "End this car’s membership in the fleet? Its history stays with the fleet.",
      "fleet.vehicles.ended": "Membership ended",
      "fleet.plateType.private": "Private",
      "fleet.plateType.public_transport": "Public transport",
      "fleet.plateType.commercial": "Commercial",
      "fleet.plateType.temporary": "Temporary",
      "fleet.plateType.diplomatic": "Diplomatic",
      "fleet.slaStart.title": "Start the SLA",
      "fleet.slaStart.fromTemplate": "From a template",
      "fleet.slaStart.fromFleet": "Copy another fleet",
      "fleet.slaStart.fromBlank": "Start blank",
      "fleet.slaStart.fromActive": "Change current SLA",
      "fleet.slaStart.startBlank": "Start a blank SLA",
      "fleet.slaStart.startActive": "Start from the current SLA",
      "fleet.slaStart.templateEmpty": "No templates yet — create one in SLA templates",
      "fleet.slaStart.noFleets": "No other active fleets to copy from",
      "fleet.slaStart.fleetNote": "Group-specific maintenance rows of that fleet become whole-fleet in this copy.",
      "fleet.slaStart.replaceDraftTitle": "Replace the draft?",
      "fleet.slaStart.replaceDraftBody": "This fleet already has an SLA draft. Starting over replaces it.",
      "fleet.slaStart.replaceDraft": "Replace draft",
      "fleet.slaStart.started": "SLA draft started",
      "fleet.terms.paymentTerms": "Payment terms (days)",
      "fleet.terms.creditLimit": "Credit limit",
      "fleet.terms.creditLimitHint": "Empty means no credit limit",
      "fleet.terms.billingCycle": "Billing cycle",
      "fleet.terms.billingPerJob": "Per job (tax invoice per job)",
      "fleet.terms.billingMonthly": "Monthly statement",
      "fleet.terms.statementAutoSend": "Send the monthly statement automatically",
      "fleet.terms.autoApproval": "Auto-approval limit",
      "fleet.terms.autoApprovalHint": "SLA lines up to this amount need no approver. Empty means every fleet-paid line needs approval.",
      "fleet.prices.labour": "Labour discount %",
      "fleet.prices.parts": "Parts discount %",
      "fleet.prices.serviceFixed": "Fixed service prices",
      "fleet.prices.partFixed": "Fixed part prices",
      "fleet.prices.categoryDiscounts": "Category discounts",
      "fleet.prices.amount": "Amount",
      "fleet.prices.percent": "Percent",
      "fleet.prices.duplicate": "This one already has a rule — remove it first",
      "fleet.prices.precedence": "Precedence: a fixed price wins, then the category discount, then the labour/parts percent.",
      "fleet.pay.title": "Record a fleet payment",
      "fleet.pay.amount": "Amount",
      "fleet.pay.allocate": "Allocated",
      "fleet.pay.date": "Paid at",
      "fleet.pay.method": "Payment method",
      "fleet.pay.mustSum": "The allocations must not exceed the transfer amount.",
      "fleet.pay.reference": "Reference",
      "fleet.pay.remainderNeedsBranch": "Pick the branch that holds the remainder.",
      "fleet.pay.retry": "Retry",
      "fleet.pay.proposal": "Proposed split",
      "fleet.pay.unallocated": "Not allocated yet",
      "fleet.pay.creditBranch": "Credit the remainder at branch",
      "fleet.pay.close": "Close",
      "fleet.pay.save": "Save payment",
      "fleet.pay.saved": "Payment recorded — receipts issued per branch",
      "fleet.payList.title": "Payments",
      "fleet.payList.record": "Record payment",
      "fleet.payList.receipts": "Receipts per branch",
      "fleet.payList.none": "No payments recorded yet",
      "fleet.coverage.default": "Default payer",
      "fleet.coverage.defaultHint": "Applies to everything with no more specific row below.",
      "fleet.coverage.coverage": "Payer",
      "fleet.coverage.byService": "Per service",
      "fleet.coverage.byCategory": "Per category",
      "fleet.coverage.payer.sla": "Covered by the SLA",
      "fleet.coverage.payer.extra": "Extra charge outside the SLA",
      "fleet.coverage.payer.driver": "On the driver",
      "fleet.coverage.payer.excluded": "Excluded",
      "fleet.sla.quoteTarget": "Quotation target (hours)",
      "fleet.sla.quoteTargetHint": "From check-in to the quotation being sent",
      "fleet.sla.readyTarget": "Ready target (days)",
      "fleet.sla.readyTargetHint": "From check-in to the car being ready",
      "fleet.sla.partsWaitCounts": "Waiting for parts counts toward the ready target",
      "fleet.sla.replacementCar": "Replacement car after (days)",
      "fleet.sla.replacementCarHint": "Empty means no replacement car is promised",
      "fleet.sla.perServiceTargets": "Per-service ready targets",
      "fleet.sla.hours": "hours",
      "fleet.sla.hours_one": "hour",
      "fleet.sla.hours_other": "hours",
      "fleet.sla.hours_two": "hours",
      "fleet.sla.hours_few": "hours",
      "fleet.sla.hours_many": "hours",
      "fleet.sla.days": "days",
      "fleet.sla.days_one": "day",
      "fleet.sla.days_other": "days",
      "fleet.sla.days_two": "days",
      "fleet.sla.days_few": "days",
      "fleet.sla.days_many": "days",
      "fleet.sla.months": "months",
      "fleet.sla.km": "km",
      "fleet.slaPanel.title": "Fleet SLA",
      "fleet.slaPanel.quote": "Quote clock",
      "fleet.slaPanel.ready": "Ready clock",
      "fleet.slaPanel.replacementBanner": "A replacement car is due for this job.",
      "fleet.slaPanel.replacementDueOn": "Due on {{date}}",
      "fleet.slaPanel.returnCar": "Mark returned",
      "fleet.slaPanel.returned": "Returned {{date}}",
      "fleet.slaPanel.stateOk": "On track",
      "fleet.slaPanel.stored": "Evaluated at ready",
      "fleet.slaPanel.saved": "Saved",
      "fleet.slaPanel.atRisk": "At risk",
      "fleet.slaPanel.breached": "Breached",
      "fleet.slaPanel.onTime": "On time",
      "fleet.slaPanel.open": "Not returned yet",
      "fleet.slaPanel.late": "Late",
      "fleet.slaPanel.logCar": "Log replacement car",
      "fleet.slaPanel.noTargets": "No SLA targets set",
      "fleet.slaPanel.notes": "Notes",
      "fleet.slaPanel.givenAt": "Given at",
      "fleet.maintenance.title": "Maintenance plans",
      "fleet.maintenance.service": "Service",
      "fleet.maintenance.group": "Group",
      "fleet.maintenance.allGroups": "All cars",
      "fleet.maintenance.everyKm": "Every (km)",
      "fleet.maintenance.everyMonths": "Every (months)",
      "fleet.maintenance.needInterval": "Set kilometres or months",
      "fleet.maintenance.dueWindowDays": "Due window (days)",
      "fleet.maintenance.dueWindowKm": "Due window (km)",
      "fleet.review.title": "Review",
      "fleet.review.edit": "Edit",
      "fleet.review.done": "Done",
      "fleet.review.pending": "Not finished yet",
      "fleet.review.skipped": "Skipped (optional)",
      "fleet.review.notStarted": "Not started",
      "fleet.review.draft": "Draft saved",
      "fleet.review.noCars": "The fleet has no cars yet",
      "fleet.review.slaNotStarted": "The SLA has not been started",
      "fleet.review.missingRoles": "Some contact roles are still empty",
      "fleet.review.activate": "Activate fleet",
      "fleet.review.activated": "Fleet activated",
      "fleet.review.startSla": "Start the SLA first — activation needs an SLA.",
      "fleet.review.goToSla": "Go to the SLA start",
      "fleet.review.fix": "Go to the step",
      "fleet.review.missing.sla": "The SLA is missing — start it in step 5",
      "fleet.review.missing.vehicles": "Add at least one car in step 4",
      "fleet.review.missing.company": "Finish the company details in step 1",
      "fleet.review.applySla": "Apply SLA changes",
      "fleet.review.applyNote": "Jobs already open keep the version they were opened under.",
      "fleet.review.slaApplied": "SLA changes applied",
      "fleet.details.notFound": "Fleet not found",
      "fleet.details.company": "Company",
      "fleet.details.cars": "Cars",
      "fleet.details.sla": "SLA",
      "fleet.details.noSla": "No active SLA yet",
      "fleet.details.slaVersion": "Active SLA — version {{version}}",
      "fleet.details.slaHistory": "SLA history",
      "fleet.details.editSetup": "Edit setup",
      "fleet.details.suspend": "Suspend",
      "fleet.details.reactivate": "Reactivate",
      "fleet.details.close": "Close",
      "fleet.details.statusChanged": "Status updated",
      "fleet.details.statusConfirm": "Change this fleet’s status to «{{action}}»?",
      "fleet.dash.priceChangeOnJob": "Contract price changed on job card {{job}}",
      "fleet.dash.title": "Fleet dashboard",
      "fleet.dash.spend": "Spend",
      "fleet.dash.spendSla": "Within the agreement",
      "fleet.dash.spendExtra": "Extra charges",
      "fleet.dash.spendDriver": "Driver-paid",
      "fleet.dash.sla": "SLA",
      "fleet.dash.openJobs": "Open jobs",
      "fleet.dash.overdue": "Overdue",
      "fleet.dash.overdueCount": "Overdue",
      "fleet.dash.overdueCount_one": "Overdue item",
      "fleet.dash.overdueCount_other": "Overdue items",
      "fleet.dash.overdueCount_two": "Overdue items",
      "fleet.dash.overdueCount_few": "Overdue items",
      "fleet.dash.overdueCount_many": "Overdue items",
      "fleet.dash.noOpenJobs": "No open jobs",
      "fleet.dash.noBreaches": "No breaches",
      "fleet.dash.noDowntime": "No delivered jobs in this period",
      "fleet.dash.noCpk": "No car has enough readings yet",
      "fleet.dash.na": "Not available",
      "fleet.dash.credit": "Credit",
      "fleet.dash.creditUsed": "Credit used",
      "fleet.dash.costPerKm": "Cost per km",
      "fleet.dash.branch": "Branch",
      "fleet.dash.breaches": "Breaches",
      "fleet.dash.breaches_one": "Breach",
      "fleet.dash.breaches_other": "Breaches",
      "fleet.dash.breaches_two": "Breaches",
      "fleet.dash.breaches_few": "Breaches",
      "fleet.dash.breaches_many": "Breaches",
      "fleet.dash.bucketGarage": "Garage time",
      "fleet.dash.bucketParts": "Waiting for parts",
      "fleet.dash.bucketFleet": "Waiting for fleet",
      "fleet.dash.allBranches": "All branches",
      "fleet.dash.quoteOnTime": "Quote on time",
      "fleet.dash.readyOnTime": "Ready on time",
      "fleet.dash.replacementMisses": "Replacement-car misses",
      "fleet.dash.replacementMisses_one": "Replacement-car miss",
      "fleet.dash.replacementMisses_other": "Replacement-car misses",
      "fleet.dash.replacementMisses_two": "Replacement-car misses",
      "fleet.dash.replacementMisses_few": "Replacement-car misses",
      "fleet.dash.replacementMisses_many": "Replacement-car misses",
      "fleet.dash.priceChanges": "Contract price changes",
      "fleet.dash.perCar": "Per car",
      "fleet.dash.perGroup": "Per group",
      "fleet.dash.downtime": "Downtime",
      "fleet.dash.due": "Maintenance due",
      "fleet.dash.dueCount": "Due",
      "fleet.dash.dueCount_one": "Due item",
      "fleet.dash.dueCount_other": "Due items",
      "fleet.dash.dueCount_two": "Due items",
      "fleet.dash.dueCount_few": "Due items",
      "fleet.dash.dueCount_many": "Due items",
      "fleet.dash.viewDue": "Open the due list",
      "fleet.due.title": "Maintenance due",
      "fleet.due.fleet": "Fleet",
      "fleet.due.allFleets": "All fleets",
      "fleet.due.status": "Status",
      "fleet.due.statusAll": "All",
      "fleet.due.statusOk": "Up to date",
      "fleet.due.statusOverdue": "Overdue",
      "fleet.due.statusDue": "Due",
      "fleet.due.startAt": "Start at",
      "fleet.due.service": "Service",
      "fleet.due.dueDate": "Due date",
      "fleet.due.dueKm": "Due at (km)",
      "fleet.due.projectedKm": "Projected km",
      "fleet.due.book": "Create booking",
      "fleet.due.booked": "Booked",
      "fleet.due.garage": "Branch",
      "fleet.due.notes": "Notes",
      "fleet.due.empty": "Nothing is due",
      "fleet.due.created": "Booking created",
      "fleet.templates.title": "SLA templates",
      "fleet.templates.subtitle": "Reusable Gold/Silver/Basic terms. A fleet copies a template — editing it later never changes fleets already using it.",
      "fleet.templates.stepName": "Names",
      "fleet.templates.new": "New template",
      "fleet.templates.edit": "Edit",
      "fleet.templates.editTitle": "Edit template",
      "fleet.templates.name": "Template name (English)",
      "fleet.templates.nameAr": "Template name (Arabic)",
      "fleet.templates.nameCol": "Template",
      "fleet.templates.nameRequired": "Both names are required",
      "fleet.templates.updated": "Last updated",
      "fleet.templates.save": "Save template",
      "fleet.templates.saved": "Template saved",
      "fleet.templates.archive": "Archive",
      "fleet.templates.archived": "Template archived",
      "fleet.templates.archiveConfirm": "Archive this template? Fleets already using it are not affected.",
      "fleet.templates.empty": "No templates yet",
      "fleet.templates.emptyDesc": "Save your common SLA terms under a name, then start fleets from them.",
      "fleet.catalog.search": "Search services, parts or categories…",
      "fleet.catalog.empty": "No matches",
      "fleet.catalog.branches": "in {{count}} branches",
      "fleet.catalog.branches_one": "in one branch",
      "fleet.catalog.branches_two": "in two branches",
      "fleet.catalog.branches_few": "in {{count}} branches",
      "fleet.catalog.branches_many": "in {{count}} branches",
      "fleet.catalog.branches_other": "in {{count}} branches",
      "fleet.import.title": "Import cars from Excel",
      "fleet.import.file": "Excel file (.xlsx)",
      "fleet.import.columns": "Columns the file needs (English or Arabic headers)",
      "fleet.import.upload": "Import",
      "fleet.import.row": "Row",
      "fleet.import.field": "Field",
      "fleet.import.problem": "Problem",
      "fleet.import.nothingSaved": "Nothing was saved — fix the highlighted rows and import again.",
      "fleet.import.failed": "The import failed",
      "fleet.import.created": "Imported {{count}} cars",
      "fleet.import.created_one": "Imported one car",
      "fleet.import.created_two": "Imported two cars",
      "fleet.import.created_few": "Imported {{count}} cars",
      "fleet.import.created_many": "Imported {{count}} cars",
      "fleet.import.created_other": "Imported {{count}} cars",
      "fleet.invoice.fleetLabel": "Fleet invoice",
      "fleet.invoice.driverLabel": "Driver invoice",
      "fleet.checkin.title": "Fleet car",
      "fleet.checkin.placeholder": "Fleet car — plate or VIN",
      "fleet.checkin.search": "Search",
      "fleet.checkin.noMatch": "No fleet car matched that plate or VIN",
      "fleet.checkin.use": "Use this car",
      "fleet.checkin.checkingIn": "Checking in…",
      "fleet.checkin.linked": "Fleet car linked — the driver fields are below",
      "fleet.checkin.failed": "The check-in failed",
      "fleet.checkin.driverName": "Driver name",
      "fleet.checkin.driverPhone": "Driver mobile",
      "fleet.checkin.customerLocked": "The fleet is billed — the customer is fixed for this job.",
      "fleet.checkin.required": "This car belongs to a fleet, so it cannot be received as a walk-in: the fleet account, the driver and the SLA are recorded on the standard form.",
      "fleet.checkin.openStandardForm": "Open the standard reception form",
      "fleet.checkin.plateType": "Plate type",
      "fleet.credit.title": "Fleet credit limit",
      "fleet.credit.body": "Opening this job would exceed the fleet's credit terms.",
      "fleet.credit.balance": "Open fleet balance",
      "fleet.credit.overdue": "Overdue invoices",
      "fleet.credit.override": "Open with override",
      "fleet.credit.reason": "Override reason",
      "fleet.credit.reasonHint": "At least five characters — recorded on the override.",
      "fleet.credit.reasonShort": "Write at least five characters",
      "fleet.credit.driverPaid": "Open as driver-paid",
      "fleet.approval.title": "Fleet approval",
      "fleet.approval.send": "Send for approval",
      "fleet.approval.sent": "Quotations sent for approval",
      "fleet.approval.pickApprover": "Choose the approver",
      "fleet.approval.poRequired": "The PO number is required",
      "fleet.approval.autoApproved": "Approved under the agreement",
      "fleet.approval.approvedBy": "Approved by {{name}}",
      "fleet.approval.approverName": "Approver name",
      "fleet.approval.attachment": "Attachment (optional)",
      "fleet.approval.record": "Record approval",
      "fleet.approval.recordTitle": "Record the fleet approval",
      "fleet.approval.recorded": "Approval recorded",
      "fleet.approval.recordedComment": "Approval recorded from {{name}}, PO {{po}}",
      "fleet.approval.roleFleet": "Fleet quotation",
      "fleet.approval.roleDriver": "Driver quotation",
      "fleet.approval.fleetTotal": "Fleet total",
      "fleet.approval.driverTotal": "Driver total",
      "fleet.approval.empty": "No fleet quotations yet",
      "fleet.approval.noApprover": "No approver contact covers this total — add one on the fleet page.",
      "fleet.approval.note": "Note (optional)",
      "fleet.approval.nameRequired": "The approver name is required",
      "fleetRead": "View fleets and fleet reports",
      "fleetManage": "Create and edit fleets",
      "fleetCredit_override": "Open fleet jobs over the credit limit",
      "fleetStatements": "Send fleet statements",
      "fleetPayments": "Record fleet payments",
      // Multi-branch Release 2 (spec 2026-10-23-multi-branch-release-2.md §5 Step 0):
      // company catalogue, stock transfer, shared customers and suppliers.
      "set.companyCatalogue": "Company catalogue",
      "compCat.title": "Company list of parts and services",
      "compCat.navLabel": "Company catalogue",
      "compCat.needsBranches": "The company list needs at least two branches.",
      "compCat.prepare": "Prepare the company list",
      "compCat.referenceBranch": "Reference branch",
      "compCat.preview": "Preview",
      "compCat.apply": "Switch on the company list",
      "compCat.confirm": "Every branch will share one list of parts and services, with one selling price. Stock, cost and shelf location stay per branch. Continue?",
      "compCat.active": "The company list is active",
      "compCat.check": "Check the company list",
      "compCat.health.ok": "Every branch row is in the company list and matches it.",
      "compCat.health.unlinked": "{{count}} branch row(s) are not in the company list yet.",
      "compCat.health.drift": "{{count}} branch row(s) differ from the company list.",
      "compCat.health.missing": "{{count}} branch row(s) of the company list are missing.",
      "compCat.reason.EXCLUDE_NOT_ALLOWED": "Only an inactive row with no stock and no history can be left out of the company list",
      "compCat.reason.DEACTIVATE_NOT_ALLOWED": "This service is on open job cards, so it cannot be deactivated",
      "compCat.reason.FROM_REFERENCE": "Differs or is blank in some branches — the reference branch's value is used",
      "compCat.reason.CODE_DIFFERS": "The service code differs between branches — the reference branch's code is used",
      "compCat.companyFieldNote": "Company list — changes apply to every branch",
      "compCat.chipCompany": "Company",
      "compCat.status.clean": "Matches",
      "compCat.status.info": "Filled in automatically",
      "compCat.status.conflict": "Needs a decision",
      "compCat.reason.NAME_DIFFERS": "The name differs between branches",
      "compCat.reason.PRICE_DIFFERS": "The selling price differs between branches",
      "compCat.reason.UNIT_DIFFERS": "The unit differs between branches",
      "compCat.reason.BARCODE_DIFFERS": "The barcode differs between branches",
      "compCat.reason.SKU_DIFFERS": "The SKU differs between branches",
      "compCat.reason.BARCODE_TAKEN": "This barcode or SKU is used by another part number",
      "compCat.reason.DUPLICATE_IN_BRANCH": "A branch has more than one part with this number",
      "compCat.reason.INVALID_NUMBER": "The part number is not valid",
      "compCat.reason.CODE_NAME_MISMATCH": "Same service code, different names",
      "compCat.reason.AMBIGUOUS_NAME": "A branch has two services with this name",
      "compCat.reason.PACKAGE_DIFFERS": "The package setup differs between branches",
      "compCat.reason.CODE_REASSIGNED": "Code already used by another service — a new code was given",
      "compCat.reason.ACTIVE_DIFFERS": "Active in some branches and inactive in others — will be active",
      "compCat.reason.PACKAGE_HAS_BILLING_ONLY_LINES": "Open job lines bill this service on its own, so it cannot become a package yet",
      "compCat.reason.INACTIVE_WITH_STOCK": "Inactive in every branch but still holds stock — will be reactivated",
      "compCat.resolve.pickBranch": "Use this branch's value",
      "compCat.resolve.typeValue": "Type a value",
      "compCat.resolve.keepSeparate": "Keep as separate items",
      "compCat.resolve.exclude": "Leave out of the company list",
      "compCat.resolve.useBranchForAll": "Use this branch for every conflict",
      "compCat.companyFieldsSkipped": "Company fields were not changed. Edit them in the company list.",
      "compCat.skippedCompanyCatalogue": "Skipped: managed by the company list",
      "compCat.catalogReused": "Matched an existing company item",
      "compCat.unitMismatch": "The company item uses a different unit",
      "compCat.itemInStock": "Still has stock in a branch",
      "compCat.stockScopeBranch": "Stock shown for this branch only",
      "compCat.reactivated": "Reactivated",
      "pt.categoryUnitSkippedCompanyUnit": "{{skipped}} part(s) kept their unit because another branch stocks the same part number in that unit.",
      "pt.bulkUnitSkippedCompanyUnit": "{{part_number}}: unit not changed to {{to}} — another branch stocks this part number in {{from}}.",
      "bt.nav": "Branch transfers",
      "bt.new": "New transfer",
      "bt.from": "From",
      "bt.to": "To",
      "bt.status.draft": "Draft",
      "bt.status.sent": "In transit",
      "bt.status.partially_received": "Partially received",
      "bt.status.received": "Received",
      "bt.status.closed": "Closed",
      "bt.status.cancelled": "Cancelled",
      "bt.send": "Send",
      "bt.receive": "Receive",
      "bt.close": "Close",
      "bt.cancel": "Cancel transfer",
      "bt.inTransit": "In transit",
      "bt.received": "Received",
      "bt.returned": "Returned",
      "bt.willCreatePart": "A new part will be created in the receiving branch",
      "bt.cancelReturnsToSender": "Everything still in transit goes back to the sending branch's stock. Continue?",
      "bt.needTwoBranches": "Stock transfers need at least two branches.",
      "bt.qtyExceeds": "More than is still in transit",
      "bt.ambiguousPart": "The receiving branch has more than one part with this number. Choose one.",
      "bt.unitMismatch": "The receiving branch counts this part in a different unit ({{unit}}).",
      "bt.returnUnitMismatch": "This part's unit changed after it was sent. Set it back to {{unit}} first.",
      "bt.unitSent": "Sent as",
      "bt.draftNumber": "Draft",
      "bt.linkedPart": "Company list item",
      "sc.createdAt": "Created at",
      "sc.createdAtFilter": "Created at branch",
      "sc.phoneExists": "A customer with this phone already exists at {{branch}}.",
      "sc.useExisting": "Use this customer",
      "sc.createAnyway": "Create a new one anyway",
      "sc.vinExists": "This car is already registered at {{branch}}.",
      "sc.balanceByBranch": "Balance by branch",
      "sc.openingBalance": "Opening balance (company)",
      "sc.totalBalance": "Total",
      "sc.duplicatesTitle": "Duplicate customers",
      "sc.mergeConfirm": "Merge into {{name}}? Vehicles move to this customer; past documents keep their customer and stay visible in the statement.",
      "sc.mergedInto": "Merged into {{name}}",
      "sc.unmerge": "Undo merge",
      "sc.statusFilter": "Status",
      "sc.statusActive": "Active",
      "sc.statusInactive": "Inactive",
      "sc.statusAll": "All",
      "sc.inactive": "Inactive",
      "sc.reactivate": "Reactivate",
      "sc.reactivated": "Customer reactivated.",
      "sc.deactivate": "Deactivate",
      "sc.deactivateConfirm": "Deactivate {{name}}? Past documents and balances stay. The customer is hidden from pickers until reactivated.",
      "sc.deactivated": "Customer deactivated.",
      "sc.merge": "Merge",
      "sc.merged": "Customers merged.",
      "sc.unmerged": "Merge undone.",
      "ss.createdAt": "Created at",
      "ss.keep": "Keep this one",
      "ss.noDuplicates": "No duplicate suppliers.",
      "ss.sameCodeTitle": "Same code, different suppliers",
      "ss.sameCodeHint": "These suppliers share a code from the old per-branch numbering but do not look like the same supplier. Nothing to merge; check that the right supplier is used on each document.",
      "ss.createdAtFilter": "Created at branch",
      "ss.nameExists": "A supplier with this name already exists at {{branch}}.",
      "ss.vatExists": "A supplier with this VAT number already exists at {{branch}}.",
      "ss.useExisting": "Use this supplier",
      "ss.createAnyway": "Create a new one anyway",
      "ss.balanceByBranch": "Balance by branch",
      "ss.totalYourBranches": "Total (your branches)",
      "ss.companyTotal": "Company total",
      "ss.duplicatesTitle": "Duplicate suppliers",
      "ss.tierVat": "Same VAT number",
      "ss.tierLikely": "Likely the same",
      "ss.mergeConfirm": "Merge into {{name}}? Past invoices, expenses and payments stay as they are and show in this supplier's statement.",
      "ss.mergeAllSelected": "Merge all selected",
      "ss.mergedInto": "Merged into {{name}}",
      "ss.unmerge": "Undo merge",
      "stockTransfer_send": "Send stock to another branch and cancel transfers not yet received",
      "stockTransfer_receive": "Receive stock sent from another branch",
      "catalogManage": "Edit company catalogue items and services for every branch, and run the switch-on merge",
      "suppliersMerge": "Merge duplicate suppliers and view the duplicates report",
      "customersMerge": "Merge duplicate customers and view the duplicates report",
      // Multi-branch Release 2 (Step 0): per-branch roles, booking brands and
      // document designs per branch.
      "ubr.title": "Role per branch",
      "ubr.sameAsDefault": "Same as default role ({{role}})",
      "ubr.homeUsesDefault": "Home branch uses the default role",
      "ubr.roleInBranch": "{{role}} in this branch",
      "ubr.badge": "+{{count}} branch roles",
      "ubr.history": "Role history",
      "ubr.allBranchesBlocked": "With different roles per branch, this user cannot use \"All branches\".",
      "ubr.companyDiagnosticsHidden": "Company totals are hidden: you do not manage every branch.",
      "booking.tabs.brands": "Brands",
      "brand.mainPage": "Main page (all branches)",
      "brand.add": "Add brand page",
      "brand.name": "Brand name",
      "brand.slug": "Link name",
      "brand.branches": "Branches on this page",
      "brand.inOther": "In {{name}}",
      "brand.copyLink": "Copy link",
      "brand.deleteConfirm": "Delete this brand page? Its branches return to the main page.",
      "brand.pickPage": "Booking page",
      "brand.nameAr": "Brand name in Arabic",
      "brand.edit": "Edit",
      "brand.delete": "Delete",
      "brand.loadFailed": "Could not load brand pages",
      "docDesign.scopeBranch": "This branch",
      "docDesign.scopeCompany": "Company default",
      "docDesign.inheritsCompany": "Company default: {{name}}",
      "docDesign.useCompany": "Use company default",
      "docDesign.useCompanyAll": "Use company default for all",
      "docDesign.companyOnlyNote": "Payslips, statements and reports use the company design.",
      // API error catalogue (backend/data/errorCodes.json)
      // Multi-branch Release 2 (Step 0)
      "err.CATALOG_SINGLE_BRANCH": "The company list needs at least two branches.",
      "err.NEEDS_TWO_BRANCHES": "This needs at least two branches in the company.",
      "err.BRANCH_TRANSFER_OPENING_CUTOFF": "Opening balances are signed off as of {{opening_date}}. A transfer cannot be in transit across that date: send it from that date on.",
      "err.CATALOG_ALREADY_ACTIVE": "The company list is already active.",
      "err.CATALOG_MERGE_BUSY": "The company list is busy. Try again in a moment.",
      "err.CATALOG_MERGE_CONFLICTS_OPEN": "Resolve every conflict in the preview before switching the company list on.",
      "err.CATALOG_ITEM_STALE": "This item was changed by someone else. Reload and try again.",
      "err.CATALOG_NUMBER_TAKEN": "Another item in the company list already uses this part number.",
      "err.CATALOG_BARCODE_TAKEN": "Another item in the company list already uses this barcode.",
      "err.CATALOG_SKU_TAKEN": "Another item in the company list already uses this SKU.",
      "err.CATALOG_CODE_TAKEN": "Another service in the company list already uses this code.",
      "err.CATALOG_UNIT_MISMATCH": "The company list counts this part in a different unit. Use the unit of the company item.",
      "err.CATALOG_ITEM_IN_STOCK": "This item still has stock in a branch, so it cannot be deactivated.",
      "err.CATALOG_ITEM_FOREIGN": "This company item belongs to another company.",
      "err.PART_UNIT_COMPANY_CONFLICT": "Branch {{garage_name}} already stocks part {{part_number}} in {{company_unit}}. Use the same unit, or a different part number.",
      "err.SUPPLIER_NAME_EXISTS": "A supplier with this name already exists in the company.",
      "err.SUPPLIER_VAT_EXISTS": "A supplier with this VAT number already exists in the company.",
      "err.SUPPLIER_MERGE_INVALID": "Choose one supplier to keep and up to 20 other suppliers to merge into it.",
      "err.SUPPLIER_INACTIVE": "This supplier is inactive. Reactivate it first.",
      "err.GARAGE_MOVE_BLOCKED": "This branch still has merged records, open transfers or brand pages in its company. Resolve them before moving it.",
      "err.SUPPLIER_ALREADY_MERGED": "This supplier is already merged into another one. Undo that merge first.",
      "err.SUPPLIER_HAS_MERGES": "Other suppliers are merged into this one. Undo those merges first.",
      "err.SUPPLIER_MERGE_VAT_CONFLICT": "These suppliers have different VAT numbers, so they cannot be merged.",
      "err.SUPPLIER_MERGE_BUSY": "Another change to these suppliers is running. Try again in a moment.",
      "err.SHARED_SUPPLIERS_MERGES_EXIST": "Unmerge the {{count}} merged suppliers before turning this off.",
      "err.BRANCH_TRANSFER_STATUS": "This transfer cannot do that in its current status.",
      "err.BRANCH_TRANSFER_SAME_BRANCH": "Choose a different branch to send to.",
      "err.BRANCH_TRANSFER_DEST_INVALID": "That branch is not part of this company or is inactive.",
      "err.BRANCH_TRANSFER_QTY_EXCEEDS": "You cannot receive more than is still in transit.",
      "err.BRANCH_TRANSFER_DEST_PART_INVALID": "That part does not belong to the receiving branch.",
      "err.BRANCH_TRANSFER_DEST_PART_AMBIGUOUS": "The receiving branch has more than one part with this number. Choose which one receives the stock.",
      "err.BRANCH_TRANSFER_UNIT_MISMATCH": "The receiving branch counts this part in a different unit from the one it was sent in. Fix the unit before receiving.",
      "err.BRANCH_TRANSFER_RETURN_UNIT_MISMATCH": "This part's unit was changed after it was sent. Set it back to the unit it was sent in, then cancel again.",
      "err.BRANCH_TRANSFER_QTY_INVALID": "Enter a quantity greater than zero (up to 3 decimals).",
      "err.BRANCH_TRANSFER_GL_ACCOUNT_CONFLICT": "Account 1310 in your chart is already used for something else. Map 'Inventory in transit' to a free account in the account mappings first.",
      "err.BRANCH_TRANSFER_PART_NUMBER_REQUIRED": "This part has no part number, so it cannot be matched in the receiving branch. Choose the receiving part.",
      "bt.partNumberRequired": "No part number — choose the receiving part",
      "bt.addPart": "Add a part — search this branch",
      "bt.allStatuses": "All statuses",
      "bt.available": "Free after reservations: {{count}}",
      "bt.cancelledAt": "Cancelled on",
      "bt.cancelledDone": "Transfer cancelled",
      "bt.choosePart": "Choose another receiving part",
      "bt.col.number": "Number",
      "bt.createdAt": "Created on",
      "bt.draftSaved": "Draft saved",
      "bt.editDraft": "Edit draft",
      "bt.empty": "No transfers here yet.",
      "bt.fromLocation": "Send from store",
      "bt.lines": "Parts",
      "bt.loadFailed": "Could not load the transfer. Try again.",
      "bt.notFound": "This transfer does not exist, or you cannot open it.",
      "bt.onHand": "On hand here: {{count}}",
      "bt.overOnHand": "More than is on hand here ({{count}})",
      "bt.reason": "Reason",
      "bt.receiveTitle": "Receive transfer",
      "bt.receivedAt": "Received on",
      "bt.receivedDone": "Stock received",
      "bt.receivingPart": "Receiving part",
      "bt.saveDraft": "Save draft",
      "bt.sendNow": "Send now",
      "bt.sent": "Sent",
      "bt.sentAt": "Sent on",
      "bt.sentDone": "Transfer sent",
      "bt.tab.all": "All",
      "bt.tab.incoming": "Incoming",
      "bt.tab.outgoing": "Outgoing",
      "bt.toLocation": "Receive into store",
      "bt.zeroCost": "Cost is 0 for {{count}} line(s); they will move at zero value.",
      "bulk.unitSkippedInTransit": "Unit not changed: the part is on an open transfer ({{transfers}})",
      "err.OB_STOCK_IN_TRANSIT_AT_CUTOFF": "Stock was in transit between branches on the opening date. Choose an opening date when no transfer was in transit.",
      "err.CUSTOMER_PHONE_EXISTS": "A customer with this phone already exists in the company.",
      "err.VIN_IN_USE_COMPANY": "This car is already registered at another branch of the company. Open the existing car instead.",
      "err.CUSTOMER_MERGE_INVALID": "Choose one customer to keep and up to 20 other customers to merge into it.",
      "err.CUSTOMER_ALREADY_MERGED": "This customer is already merged into another one. Use Undo merge instead.",
      "err.CUSTOMER_HAS_MERGES": "Other customers are merged into this one. Undo those merges first.",
      "err.SHARED_CUSTOMERS_MERGES_EXIST": "Unmerge the {{count}} merged customers before turning this off.",
      "err.CUSTOMER_UNMERGE_CREDIT_IN_USE": "This customer's advance is reserved on an open document. Release or settle that document, then undo the merge.",
      "err.PAYOUT_RECEIPT_BRANCH_DENIED": "Part of this credit was received at a branch you cannot pay out for.",
      "err.RECEIPT_BRANCH_DENIED": "This receipt was taken at a branch you cannot use it for.",
      "err.CUSTOMER_MERGE_BUSY": "These customers are being changed right now. Try again in a moment.",
      "err.feature_disabled": "This feature is turned off for your company.",
      "err.RECEIPT_AUTH_REQUIRED": "The receipt could not be checked against your branches, so nothing was changed. Contact support.",
      "err.COMPANY_FEATURES_BUSY": "Another change to this company is running. Try again in a moment.",
      "err.COMPANY_FEATURES_CHANGED": "The company's features were changed by someone else. Reload and save again.",
      "err.BOOKING_PAGE_NOT_FOUND": "That booking page does not exist or has been archived.",
      "err.BRANCH_ROLE_HOME": "The home branch uses the default role.",
      "err.FEATURE_DISABLED_BRANCH_ROLES": "Per-branch roles are not enabled for your company.",
      "err.BRANCH_ROLE_ROW_KEPT": "This user has a role set for that branch. Turn on per-branch roles and clear it first.",
      "err.BRANCH_SCOPE_ALL_MIXED_ROLES": "Your role differs between branches, so \"All branches\" is not available. Pick one branch.",
      "err.BOOKING_PAGE_SLUG_TAKEN": "This link name is already used",
      "err.BOOKING_PAGE_SLUG_INVALID": "Use lowercase letters, digits and dashes",
      "err.BOOKING_PAGE_DEFAULT": "The main page cannot be deleted",
      "err.BOOKING_BRANCH_IN_OTHER_PAGE": "This branch is already on {{name}}",
      "err.BOOKING_BRANDS_NOT_READY": "Brand pages are not ready yet. Try again later.",
      "err.FLEET_JOB_LOCKED": "A fleet job's car and payer are set at check-in and cannot be changed. Cancel this job and check in the right car.",
      "err.FLEET_IMPORT_INVALID": "The file has rows that cannot be imported. Nothing was saved.",
      "err.FLEET_DRIVER_REQUIRED": "Enter the driver's name and a valid mobile number for a fleet car.",
      "err.FLEET_DUE_NOT_FOUND": "That due item was not found.",
      "err.FLEET_DUE_ALREADY_BOOKED": "This due item already has a booking.",
      "err.FLEET_CHECKIN_REQUIRED": "This car belongs to a fleet. Use the fleet check-in on the new job card form.",
      "err.FLEET_NOT_FOUND": "Fleet not found.",
      "err.FLEET_SETUP_INCOMPLETE": "Finish the fleet setup first: it needs an active SLA and at least one car.",
      "err.FLEET_ACCOUNT_INACTIVE": "This fleet is not active: its setup is unfinished, or it is suspended or closed.",
      "err.FLEET_APPROVAL_REQUIRED": "This fleet quotation needs a named approver from the fleet's contacts.",
      "err.FLEET_APPROVER_LIMIT": "This approver's limit is below the quotation total.",
      "err.FLEET_CUSTOMER_LINKED": "This customer is already linked to a fleet.",
      "err.FLEET_CREDIT_OVERRIDE_REQUIRED": "This fleet is over its credit limit or has overdue invoices. Give a reason to override, or open the job as driver-paid.",
      "err.FLEET_CREDIT_OVERRIDE_REASON_REQUIRED": "Write a reason of at least 5 characters to override the fleet's credit limit.",
      "err.FLEET_VEHICLE_IN_OTHER_FLEET": "This car is already active in a fleet.",
      "err.FLEET_BUYER_FIELDS_LOCKED": "This customer belongs to a fleet. Edit the company details on the Fleets page.",
      "err.FLEET_SLA_REQUIRED": "Start the fleet's SLA first: choose a template, copy another fleet, or start blank.",
      "err.FLEET_REPLACEMENT_OPEN": "A replacement car is already out on this job and has not come back yet.",
      "err.FLEET_REPLACEMENT_JOB_FINISHED": "A replacement car can be logged only while the job is open.",
      "err.FLEET_LINE_PAYER_REQUIRED": "Some lines of this fleet job have no payer yet. Set who pays for each line on the job card.",
      "err.FLEET_LINE_EXCLUDED": "This work is excluded from the fleet's agreement: only the driver can pay for it.",
      "err.FLEET_LINE_ON_PAID_INVOICE": "This line is on an invoice that has already been paid, so its payer cannot change. Post that invoice and correct it with a credit note.",
      "err.FLEET_LINES_CHANGED": "One or more lines on this fleet job changed while the invoice was being generated. Review the lines and generate the invoice again.",
      "err.FLEET_SLA_TERMS_REQUIRED": "This fleet's SLA has no payment terms. Set them in the fleet's SLA before invoicing.",
      "err.FLEET_STATEMENT_NO_FINANCE_CONTACT": "This fleet has no active finance contact to receive the statement. Add one on the fleet's contacts page.",
      "err.FLEET_STATEMENT_PDF_FAILED": "The statement PDF could not be generated, so nothing was sent. Try again; if it keeps failing, contact support.",
      "err.FLEET_FINANCE_CONTACT_REQUIRED": "Add a finance contact with a mobile number to this fleet to send its invoices.",
      "err.FLEET_QUOTATION_MIXED_PAYERS": "A fleet job needs one quotation for the fleet lines and another for the driver lines; they cannot be mixed.",
      "err.FLEET_QUOTATION_NO_DOWNPAYMENT": "A fleet quotation never asks for a downpayment: the fleet is on credit.",
      "err.FLEET_QUOTATION_LOCKED": "This fleet quotation is approved. Create a new quotation for the extra work.",
      "err.FLEET_QUOTATION_LINK_REFUSED": "A standalone quotation cannot be linked to a fleet job. Create the quotation from the job card.",
      "err.FLEET_QUOTATION_ROLE_MISSING": "This quotation was not made for this fleet job. Create the quotation from the job card.",
      "err.FLEET_PO_REQUIRED": "Recording a fleet approval needs the fleet's purchase-order number.",
      "err.FLEET_PAYMENT_SPLIT_MISMATCH": "The amounts allocated to invoices plus the amount left on account must equal the payment amount.",
      "taskMonitoring.completedAt": "Completed at",
      "jc.expectedCompletion": "Expected completion",
      "jc.expectedCompletionHint": "When this job is expected to be finished",
      "qcBr.branchToEdit": "Branch to edit",
      "qcBr.applyTo": "Apply settings to",
      "qcBr.thisBranch": "This branch only",
      "qcBr.allBranches": "All branches",
      "qcBr.specificBranches": "Specific branches…",
      "qcBr.notCopied": "Will not copy: ",
      "qcBr.apply": "Apply",
      "qcBr.noSharedFields": "This page has no shared policy fields; save each branch separately.",
      "qcBr.generalExcluded": "Name, address, phone, email, tax number and location",
      "qcBr.notificationExcluded": "Recipient phone numbers and per-number preferences",
      "qcBr.feedbackExcluded": "Branch Google review URL",
      "qcBr.creditExcluded": "Notification recipients and their phone numbers",
      "qcBr.featureExcluded": "Monthly quota and credit reminder recipients",
      "qcBr.appointmentExcluded": "Branch capacity, working hours and days off",
      "booking.aiDesign.notesLabel": "Describe your idea (optional)",
      "booking.aiDesign.notesHint": "In your own words: what to highlight, the mood, anything that helps the designer.",
      "booking.aiDesign.notesOf": "of",
      "err.BRANCH_NAME_TAKEN": "Each branch needs its own name — another branch of this company already uses it",
      "err.MAPPING_ACCOUNT_INVALID": "That account cannot take postings: it is missing from your chart, inactive, or a header account.",
      "err.MAPPING_TYPE_MISMATCH": "This posting role needs an account of a different type. Pick one of the types shown on the row.",
      "err.MAPPING_KEY_FROZEN": "A closed period already uses this mapping. Record a journal voucher in an open month to move the balance, or reopen the period first.",
      "err.COST_CENTER_SCOPE": "This report covers every branch, and your account cannot see all of them. Ask an administrator for access to every branch.",
      "err.COST_CENTER_REQUIRED": "Every revenue and expense line needs a cost center: a branch or «Unallocated».",
      "gl.acctmap.nav": "Account mapping",
      "gl.acctmap.title": "Account mapping",
      "gl.acctmap.subtitle": "Which account each posting uses. A change applies to every document that uses the role, in open periods.",
      "gl.acctmap.accessDenied": "No access to account mapping",
      "gl.acctmap.accessDeniedDesc": "Viewing this screen needs the «view account mappings» permission.",
      "gl.acctmap.featureDisabled": "Account mapping is not enabled",
      "gl.acctmap.featureDisabledDesc": "This screen needs the general ledger or the accounting export feature in your plan.",
      "gl.acctmap.readOnly": "You can view the mappings. Changing one needs the «manage account mappings» permission.",
      "gl.acctmap.loadFailed": "Could not load the account mappings",
      "gl.acctmap.empty": "No posting roles yet",
      "gl.acctmap.emptyDesc": "Posting roles appear here once the general ledger is set up for your company.",
      "gl.acctmap.unmappedBanner": "Posting roles with no account: {{count}}. Their amounts go to the suspense account until you map them.",
      "gl.acctmap.group.sales": "Sales and customers",
      "gl.acctmap.group.purchases": "Purchases and suppliers",
      "gl.acctmap.group.vat": "VAT",
      "gl.acctmap.group.inventory": "Inventory and cost",
      "gl.acctmap.group.payroll": "Payroll",
      "gl.acctmap.group.cash": "Cash and bank",
      "gl.acctmap.group.expenses": "Expense categories",
      "gl.acctmap.group.equity": "Equity and closing",
      "gl.acctmap.group.advanced": "Advanced",
      "gl.acctmap.advancedHint": "Cash reasons and entry types. Change these only on your accountant's advice.",
      "gl.acctmap.col.role": "Posting role",
      "gl.acctmap.col.current": "Current account",
      "gl.acctmap.col.default": "Default",
      "gl.acctmap.accepts": "Accepts: {{types}}",
      "gl.acctmap.customized": "Customized",
      "gl.acctmap.customizedCount": "Customized: {{count}}",
      "gl.acctmap.unmapped": "No account",
      "gl.acctmap.inactive": "Inactive",
      "gl.acctmap.change": "Change",
      "gl.acctmap.reset": "Reset",
      "gl.acctmap.resetNothing": "Already on the default",
      "gl.acctmap.resetTitle": "Reset «{{label}}»?",
      "gl.acctmap.resetMsg": "This posting role goes back to the default account {{code}}.",
      "gl.acctmap.resetMsgNoDefault": "This posting role has no default account, so it will have no account until you map it again.",
      "gl.acctmap.resetConfirm": "Reset",
      "gl.acctmap.resetDone": "Mapping reset to the default",
      "gl.acctmap.frozenShort": "Locked: a closed period already uses this account",
      "gl.acctmap.frozenReason": "A closed period already posted through this role, so changing it would restate that period. To move the balance, record a journal voucher in an open month, or reopen the period first.",
      "gl.acctmap.role.accounts_receivable": "Customer receivables (sales invoices)",
      "gl.acctmap.role.sales_revenue": "Sales revenue",
      "gl.acctmap.role.customer_advances": "Customer advances (down payments)",
      "gl.acctmap.role.gateway_clearing": "Payment gateway clearing",
      "gl.acctmap.role.gateway_fees": "Payment gateway fees",
      "gl.acctmap.role.bad_debt_expense": "Bad debt write-offs",
      "gl.acctmap.role.warranty_provision": "Warranty provision",
      "gl.acctmap.role.warranty_expense": "Warranty expense",
      "gl.acctmap.role.accounts_payable": "Supplier payables (supplier invoices)",
      "gl.acctmap.role.purchases": "Purchases (non-stock lines)",
      "gl.acctmap.role.accrued_expenses": "Accrued expenses (credit expenses)",
      "gl.acctmap.role.prepaid_expenses": "Prepaid expenses",
      "gl.acctmap.role.vat_output": "VAT payable (output tax)",
      "gl.acctmap.role.vat_input": "VAT recoverable (input tax)",
      "gl.acctmap.role.inventory": "Inventory",
      "gl.acctmap.role.cost_of_goods_sold": "Cost of parts sold",
      "gl.acctmap.role.inventory_adjustment": "Inventory losses and adjustments",
      "gl.acctmap.role.inventory_writedown": "Inventory write-down",
      "gl.acctmap.role.inventory_in_transit": "Inventory in transit between branches",
      "gl.acctmap.role.payroll_salaries_expense": "Salaries expense",
      "gl.acctmap.role.payroll_gosi_expense": "Social insurance (GOSI) expense",
      "gl.acctmap.role.payroll_eosb_expense": "End-of-service benefit expense",
      "gl.acctmap.role.payroll_salaries_payable": "Salaries payable",
      "gl.acctmap.role.payroll_gosi_payable": "Social insurance (GOSI) payable",
      "gl.acctmap.role.payroll_eosb_provision": "End-of-service provision",
      "gl.acctmap.role.payroll_fines_fund": "Employee fines fund",
      "gl.acctmap.role.payroll_employee_loans": "Employee loans",
      "gl.acctmap.role.leave_liability": "Annual leave liability",
      "gl.acctmap.role.leave_expense": "Annual leave expense",
      "gl.acctmap.role.retained_earnings": "Retained earnings (year-end close)",
      "gl.acctmap.role.document_discrepancy": "Suspense (document differences)",
      "gl.acctmap.role.rounding_difference": "Rounding differences",
      "gl.acctmap.role.asset_disposal_receivable": "Asset disposal receivable",
      "gl.acctmap.role.asset_disposal_gain": "Gain on asset disposal",
      "gl.acctmap.role.asset_disposal_loss": "Loss on asset disposal",
      "gl.cc.label": "Cost center",
      "gl.cc.unallocated": "Unallocated",
      "gl.cc.mixed": "Mixed",
      "gl.cc.choose": "Choose…",
      "gl.cc.headerHint": "Lines follow this unless you set their own.",
      "gl.cc.requiredOnLine": "Choose a cost center on every revenue and expense line (a branch or «Unallocated»).",
      "gl.cc.viewCompany": "Company",
      "gl.cc.viewCostCenter": "By cost center",
      "gl.cc.tbTitle": "Trial balance by cost center",
      "gl.cc.tbSubtitle": "The company trial balance with one column per branch, plus amounts not allocated to any branch.",
      "gl.cc.isTitle": "Income statement by cost center",
      "gl.cc.isSubtitle": "Revenue, expenses and net profit for each branch, side by side.",
      "gl.cc.tbNote": "Branch columns need not balance on their own: a document can debit one branch and credit another, or be company-wide. Only the Total column must balance, and it equals the company trial balance.",
      "gl.cc.isNote": "Amounts not tied to a branch appear under «Unallocated». The Total column equals the company income statement.",
      "gl.cc.currencyNote": "Amounts in Saudi riyals",
      "gl.cc.col.account": "Account",
      "gl.cc.total": "Total",
      "gl.cc.totalRow": "Total",
      "gl.cc.differenceRow": "Difference (debit − credit)",
      "gl.cc.sectionTotal": "Total {{section}}",
      "gl.cc.netProfit": "Net profit",
      "gl.cc.totalBalanced": "The Total column balances",
      "gl.cc.totalUnbalanced": "The Total column does not balance",
      "gl.cc.export": "Export CSV",
      "gl.cc.loadFailed": "Could not load the cost-center report",
      "gl.cc.empty": "Nothing posted in this period",
      "gl.cc.emptyDesc": "Pick another period, or check that documents were issued in it.",
      "gl.acctmap.changeDesc": "Currently {{code}}. The new account applies to postings in open periods; closed periods are not restated.",
      "gl.acctmap.changeDescUnmapped": "This role has no account yet, so its amounts sit in the suspense account. Pick the account they belong to.",
      "gl.close.warning.cost_center_unknown_branch": "The branch shown below is not one of this company's branches; its amounts were placed under Unallocated.",
      "gl.map.codePlaceholder": "Account code",
      "gl.cc.tbSubtitleV2": "Debit and credit movement in the selected period, per cost center. No opening or closing balances; always calculated live.",
      "gl.cc.tbNoteV2": "Branch columns need not balance on their own: one document can debit one branch and credit another, or be company-wide. Only the Total column must balance, and it equals the movement columns of the company trial balance.",
      "gl.cc.closedSuffix": "(closed)",
      "gl.acctmap.changeDescV2": "Currently {{code}}. Every open month that uses this posting role will be recalculated with the new account; closed months are protected.",
      "gl.acctmap.changeDescUnmappedV2": "This role has no account yet. Every open month that uses this posting role will be recalculated with the new account; closed months are protected.",
      "gl.acctmap.resetMsgV2": "This posting role goes back to the default account {{code}}. Every open month that uses it will be recalculated with that account; closed months are protected.",
      "gl.acctmap.resetMsgNoDefaultV2": "This posting role has no default account, so it will have no account until you map it again. Open months that use it will show its amounts as unmapped; closed months are protected.",
      "booking.aiDesign.builderOff": "AI page design is off: this company does not have the website builder.",
      "booking.tabs.branches": "Branches",
      "booking.branches.title": "Booking Branches",
      "booking.branches.summary": "{{enabled}} of {{total}} branches accept bookings",
      "booking.branches.loadFailed": "Could not load booking branches",
      "booking.branches.saveSuccess": "Branch status updated successfully",
      "booking.branches.saveFailed": "Failed to save branch status",
      "booking.branches.acceptsBookings": "Accepts online bookings",
      "booking.branches.hoursConfigured": "Working hours set",
      "booking.branches.noHoursWarning": "No working hours — no slots will show for customers",
      "booking.branches.editHoursLink": "Working hours",
      "booking.branches.editSettings": "Edit settings",
      "booking.branches.confirmDisableLastTitle": "Disable online booking",
      "booking.branches.confirmDisableLastMessage": "This is the only branch accepting online bookings. Disabling it will completely stop online booking for your business.",
      "booking.branches.confirmDisable": "Disable",
      "booking.general.branchesHint": "Manage online booking across all branches from the ",
      "booking.editor.skip.branchMultipleActiveLead": "Skipped because only one branch accepts bookings — ",
      "booking.editor.skip.branchMultipleActiveLink": "enable the rest from the Branches tab",
      "err.INVALID_WAITLIST_REQUEST": "Enter valid contact details and choose a service to join the waitlist.",
      "err.WAITLIST_DISABLED": "The waitlist is not available for this workshop.",
      "err.SLOTS_AVAILABLE": "Appointments are available now; choose an open time instead of joining the waitlist.",
      "err.APPROVAL_LIMIT_EXCEEDED": "This amount is above your approval limit. Someone with a higher limit has to approve it.",
      "err.APPROVAL_LIMIT_SELF_EDIT": "You can't change your own approval limits — ask another admin.",
      "err.APPROVAL_LIMIT_GRANT_EXCEEDS_OWN": "You can only give limits up to your own limit.",
      "appt.col.time": "Time",
      "appt.override.action": "Override rules",
      "appt.override.confirm": "Override rules",
      "appt.override.minChars": "At least 5 characters required",
      "appt.override.reasonLabel": "Override reason (at least 5 characters)",
      "appt.override.reasonPlaceholder": "Enter reason for overriding rules…",
      "appt.override.title": "Override booking rules",
      "appt.rescheduled": "Appointment rescheduled successfully",
      "booking.analyticsPlaceholder": "Booking analytics overview",
      "booking.confirm.auto": "Automatic",
      "booking.confirm.inherit": "Default (inherited)",
      "booking.confirm.manual": "Manual",
      "booking.deposit.credit": "Add to customer balance",
      "booking.deposit.desc": "Require customers to pay a deposit to confirm bookings, with deposit type and payment deadline",
      "booking.deposit.holdMinutes": "Payment hold deadline (minutes)",
      "booking.deposit.lateAction": "Late payment action",
      "booking.deposit.loadFailed": "Failed to load deposit settings",
      "booking.deposit.mode": "Deposit mode",
      "booking.deposit.modeAll": "Required for all bookings",
      "booking.deposit.modeOff": "Disabled",
      "booking.deposit.modePerService": "Per service",
      "booking.deposit.noShowAfter": "No-show deposit action deadline (minutes)",
      "booking.deposit.noShowPlaceholder": "e.g. 2",
      "booking.deposit.refund": "Auto-refund",
      "booking.deposit.saveFailed": "Failed to save deposit settings",
      "booking.deposit.saved": "Deposit settings saved successfully",
      "booking.deposit.title": "Deposit and prepayment settings",
      "booking.deposit.type": "Deposit calculation type",
      "booking.deposit.typeFixed": "Fixed amount (SAR)",
      "booking.deposit.typePercent": "Percentage (%)",
      "booking.deposit.value": "Deposit value",
      "booking.domain.cancel": "Cancel",
      "booking.domain.checkNow": "Check DNS now",
      "booking.editor.top.fullPreview": "Full preview",
      "booking.editor.top.fullPreviewHint": "Hides the panels and shows the whole page as a customer sees it.",
      "booking.editor.rail.pages": "Pages",
      "booking.editor.rail.header": "Menu and header",
      "booking.editor.empty.starterPage": "Add a hero",
      "booking.editor.publish.wholeSite": "Whole site",
      "booking.editor.preview.toolbar": "Preview tools",
      "booking.editor.preview.device": "Device",
      "booking.editor.preview.exit": "Exit preview",
      "booking.editor.pages.title": "Pages",
      "booking.editor.pages.booking": "Online booking",
      "booking.editor.pages.bookingOnHint": "Customers book through the booking form; the site has exactly one.",
      "booking.editor.pages.bookingOffHint": "The site only presents the business: no booking form and no links to it.",
      "booking.editor.pages.bookingOffWizard": "The site still has a booking form; the publish checklist will ask you to remove it.",
      "booking.editor.pages.bookingOffTitle": "Turn booking off",
      "booking.editor.pages.bookingOffMessage": "The booking form will be removed from the site, menu links to it removed and buttons leading to it hidden. You can turn booking on again later.",
      "booking.editor.pages.bookingOffConfirm": "Turn booking off",
      "booking.editor.pages.titleAr": "Title in Arabic",
      "booking.editor.pages.titleEn": "Title in English",
      "booking.editor.pages.slug": "Page address",
      "booking.editor.pages.slugProblem.format": "Use lowercase English letters, numbers and dashes only, up to 32 characters.",
      "booking.editor.pages.slugProblem.reserved": "This address is reserved; choose another.",
      "booking.editor.pages.slugProblem.taken": "A page already uses this address; choose another.",
      "booking.editor.pages.save": "Save",
      "booking.editor.pages.add": "Add",
      "booking.editor.pages.new": "Add a page",
      "booking.editor.pages.rename": "Rename “{{name}}”",
      "booking.editor.pages.up": "Move “{{name}}” up",
      "booking.editor.pages.down": "Move “{{name}}” down",
      "booking.editor.pages.delete": "Delete “{{name}}”",
      "booking.editor.pages.max": "Up to {{n}} pages besides home.",
      "booking.editor.pages.deleteTitle": "Delete page",
      "booking.editor.pages.deleteMessage": "The page “{{name}}” and all its sections will be deleted, and menu links to it removed. Continue?",
      "booking.editor.pages.deleteConfirm": "Delete page",
      "booking.editor.nav.title": "Menu and header",
      "booking.editor.nav.intro": "Add a header shown on every page: your logo, a menu of links and a booking button.",
      "booking.editor.nav.start": "Add a header and menu",
      "booking.editor.nav.items": "Menu items",
      "booking.editor.nav.itemsHint": "Each item opens a page or jumps to a section of a page.",
      "booking.editor.nav.untitled": "Unnamed item",
      "booking.editor.nav.up": "Move “{{name}}” up",
      "booking.editor.nav.down": "Move “{{name}}” down",
      "booking.editor.nav.remove": "Remove “{{name}}”",
      "booking.editor.nav.label": "Label",
      "booking.editor.nav.add": "Add an item",
      "booking.editor.nav.max": "Up to {{n}} items.",
      "booking.editor.nav.cta": "Call-to-action button",
      "booking.editor.nav.ctaHint": "A prominent button at the end of the header, such as “Book now”.",
      "booking.editor.nav.ctaShow": "Show the button",
      "booking.editor.nav.look": "Header look",
      "booking.editor.nav.height": "Header height",
      "booking.editor.nav.logoSize": "Logo size",
      "booking.editor.nav.size.s": "Small",
      "booking.editor.nav.size.m": "Medium",
      "booking.editor.nav.size.l": "Large",
      "booking.editor.nav.sticky": "Stays visible on scroll",
      "booking.editor.nav.removeHeader": "Remove the header",
      "booking.editor.target.label": "Links to",
      "booking.editor.target.choose": "Choose where it leads",
      "booking.editor.target.type.page": "A page of the site",
      "booking.editor.target.type.section": "A section on a page",
      "booking.editor.target.type.booking": "The booking form",
      "booking.editor.target.type.phone": "Phone call",
      "booking.editor.target.type.whatsapp": "WhatsApp",
      "booking.editor.target.type.url": "External link",
      "booking.editor.target.page": "Page",
      "booking.editor.target.section": "Section",
      "booking.editor.target.noSections": "This page has no sections yet",
      "booking.editor.target.url": "Link",
      "booking.editor.target.ownNumber": "Your business number on record is used.",
      "booking.editor.target.problem.page": "That page does not exist.",
      "booking.editor.target.problem.section": "That section is not on this page.",
      "booking.editor.target.problem.url": "Enter a full link starting with https://",
      "booking.editor.target.problem.booking": "Booking is off, so this link does not work.",
      "booking.editor.target.problem.type": "Choose where the link leads.",
      "booking.editor.slides.untitled": "Banner {{n}}",
      "booking.editor.slides.up": "Move “{{name}}” up",
      "booking.editor.slides.down": "Move “{{name}}” down",
      "booking.editor.slides.remove": "Delete “{{name}}”",
      "booking.editor.slides.needImage": "Add a photo to this banner before publishing.",
      "booking.editor.slides.image": "Banner photo",
      "booking.editor.slides.alt": "Photo description for screen readers",
      "booking.editor.slides.title": "Headline",
      "booking.editor.slides.text": "Text",
      "booking.editor.slides.align": "Text position",
      "booking.editor.slides.alignTo.start": "Start",
      "booking.editor.slides.alignTo.center": "Centre",
      "booking.editor.slides.alignTo.end": "End",
      "booking.editor.slides.overlay": "Shade",
      "booking.editor.slides.overlayHint": "A dark layer over the photo that keeps the text readable.",
      "booking.editor.slides.veil.none": "None",
      "booking.editor.slides.veil.s": "Light",
      "booking.editor.slides.veil.m": "Medium",
      "booking.editor.slides.veil.l": "Strong",
      "booking.editor.slides.button": "Button on the banner",
      "booking.editor.slides.buttonLabel": "Button label",
      "booking.editor.slides.add": "Add a banner",
      "booking.editor.slides.max": "Up to {{n}} banners.",
      "booking.editor.slider.slides": "Banners",
      "booking.editor.slider.on": "Yes",
      "booking.editor.slider.off": "No",
      "booking.editor.slider.autoplay": "Autoplay",
      "booking.editor.slider.interval": "Seconds per banner",
      "booking.editor.slider.loop": "Loop",
      "booking.editor.slider.arrows": "Arrows",
      "booking.editor.slider.dots": "Dots",
      "booking.editor.slider.transition": "Transition",
      "booking.editor.slider.transition.slide": "Slide",
      "booking.editor.slider.transition.fade": "Fade",
      "booking.editor.group.motion": "Motion",
      "booking.editor.group.size": "Size and spacing",
      "booking.editor.group.alignWidth": "Alignment and width",
      "booking.editor.size.device": "Device",
      "booking.editor.size.desktop": "Computer",
      "booking.editor.size.mobile": "Phone",
      "booking.editor.size.phoneHint": "Phone values follow the computer until you change them.",
      "booking.editor.size.inherited": "Same as computer",
      "booking.editor.size.followDesktop": "Follow computer",
      "booking.editor.size.height": "Height",
      "booking.editor.size.h.default": "Auto",
      "booking.editor.size.h.s": "Short",
      "booking.editor.size.h.m": "Medium",
      "booking.editor.size.h.l": "Tall",
      "booking.editor.size.h.full": "Full screen",
      "booking.editor.size.gap": "Gap between items",
      "booking.editor.size.gapStep.default": "Default",
      "booking.editor.size.gapStep.s": "Small",
      "booking.editor.size.gapStep.m": "Medium",
      "booking.editor.size.gapStep.l": "Large",
      "booking.editor.size.columns": "Columns",
      "booking.editor.size.auto": "Auto",
      "booking.editor.check.wizardOff": "Booking is off, but the site still has a booking form.",
      "booking.editor.check.bookingLink": "The link “{{name}}” leads to the booking form, but booking is off.",
      "booking.editor.check.link.nav": "The menu item “{{name}}” leads to a missing page or section, or a bad link.",
      "booking.editor.check.link.cta": "The header button “{{name}}” leads nowhere valid.",
      "booking.editor.check.link.slide": "A button in “{{name}}” leads nowhere valid.",
      "booking.editor.check.slideImage": "“{{name}}” has a banner without a photo.",
      "set.websiteDesign": "Website design",
      "booking.aiDesign.usage.usage": "Used {{used}} of {{limit}} this month, renews on {{date}}",
      "booking.aiDesign.usage.reset": "Reset tries",
      "booking.aiDesign.usage.resetTitle": "Reset design tries",
      "booking.aiDesign.usage.resetMessage": "AI page design tries start again from zero for this month. Earlier designs are kept. Continue?",
      "booking.aiDesign.usage.resetDone": "Tries reset",
      "booking.aiDesign.usage.resetFailed": "Could not reset the tries",
      "booking.aiDesign.usage.loadFailed": "Could not load the usage",
      "set.websiteDesignHint": "Your website’s pages, menu and design, with online booking when it is on.",
      "booking.editor.lang.ar": "In Arabic",
      "booking.editor.lang.en": "In English",
      "booking.editor.tabs.content": "Content",
      "booking.editor.tabs.design": "Design",
      "booking.editor.tabs.advanced": "Advanced",
      "booking.editor.panel.page": "Page settings",
      "booking.editor.panel.pageHint": "Select a section on the canvas to edit it, or change the page settings here.",
      "booking.editor.panel.noFields": "Nothing to set on this tab.",
      "booking.editor.group.text": "Text",
      "booking.editor.group.media": "Images",
      "booking.editor.group.items": "Items",
      "booking.editor.group.layout": "Layout",
      "booking.editor.group.colours": "Colours",
      "booking.editor.group.background": "Background",
      "booking.editor.group.spacing": "Spacing and width",
      "booking.editor.group.visibility": "Visibility",
      "booking.editor.group.brand": "Brand",
      "booking.editor.group.type": "Typography",
      "booking.editor.group.shape": "Shapes",
      "booking.editor.group.booking": "Booking steps",
      "booking.editor.group.contact": "Contact details",
      "booking.editor.hint.layout": "Pick a look; the content stays.",
      "booking.editor.hint.spacing": "Space above and below the section.",
      "booking.editor.hint.textColor": "A colour that does not read on the background is replaced automatically.",
      "booking.editor.hint.hide": "The section stays visible here, faded, so you can find it.",
      "booking.editor.hint.focal": "Click the photo to mark what matters; it stays in view when cropped.",
      "booking.editor.style.group": "Style",
      "booking.editor.style.background": "Background",
      "booking.editor.style.bg.none": "Default",
      "booking.editor.style.bg.color": "Colour",
      "booking.editor.style.bg.image": "Photo",
      "booking.editor.style.backgroundColor": "Background colour",
      "booking.editor.style.backgroundImage": "Background photo",
      "booking.editor.style.textColor": "Text colour",
      "booking.editor.style.textAuto": "Automatic",
      "booking.editor.style.alignment": "Alignment",
      "booking.editor.style.align.default": "Default",
      "booking.editor.style.align.start": "Start",
      "booking.editor.style.align.center": "Centre",
      "booking.editor.style.spacing": "Vertical spacing",
      "booking.editor.style.space.default": "Default",
      "booking.editor.style.space.s": "Small",
      "booking.editor.style.space.m": "Medium",
      "booking.editor.style.space.l": "Large",
      "booking.editor.style.width": "Width",
      "booking.editor.style.width.default": "Default",
      "booking.editor.style.width.full": "Full",
      "booking.editor.style.width.contained": "Boxed",
      "booking.editor.style.hideOnMobile": "Hide on phones",
      "booking.editor.style.hideOnDesktop": "Hide on computers",
      "booking.editor.style.focal": "Focal point",
      "booking.editor.style.reset": "Reset to default style",
      "booking.editor.contrast.low": "The text does not read on this background (contrast {{ratio}}). A readable colour is shown instead.",
      "booking.editor.contrast.fix": "Fix",
      "booking.editor.color.theme": "Theme colours",
      "booking.editor.color.custom": "Custom colour",
      "booking.editor.color.clear": "No colour",
      "booking.editor.color.primary": "Primary",
      "booking.editor.color.secondary": "Secondary",
      "booking.editor.color.accent": "Accent",
      "booking.editor.color.surface": "Background",
      "booking.editor.color.ink": "Text",
      "booking.editor.image.upload": "Upload photo",
      "booking.editor.image.replace": "Replace",
      "booking.editor.image.remove": "Remove",
      "booking.editor.image.uploading": "Uploading…",
      "booking.editor.image.empty": "No photo yet",
      "booking.editor.image.notImage": "Choose an image file.",
      "booking.editor.image.tooLarge": "The photo is larger than 5 MB.",
      "booking.editor.image.failed": "The photo could not be uploaded.",
      "booking.editor.image.types": "JPG, PNG or WebP up to 5 MB",
      "booking.editor.toolbar.up": "Move up",
      "booking.editor.toolbar.down": "Move down",
      "booking.editor.toolbar.duplicate": "Duplicate",
      "booking.editor.toolbar.hideMobile": "Hide on phones",
      "booking.editor.toolbar.hideDesktop": "Hide on computers",
      "booking.editor.toolbar.style": "Design",
      "booking.editor.toolbar.delete": "Delete",
      "booking.editor.toolbar.parent": "Select parent",
      "booking.editor.toast.deleted": "Section deleted.",
      "booking.editor.toast.undo": "Undo",
      "booking.editor.top.title": "Website design",
      "booking.editor.top.undo": "Undo",
      "booking.editor.top.redo": "Redo",
      "booking.editor.top.preview": "Preview",
      "booking.editor.top.previewHint": "Opens the draft in a new tab as customers will see it.",
      "booking.editor.top.publish": "Publish",
      "booking.editor.top.more": "More",
      "booking.editor.top.tour": "Quick tour",
      "booking.editor.save.saving": "Saving…",
      "booking.editor.save.saved": "Saved",
      "booking.editor.save.error": "Not saved",
      "booking.editor.save.idle": "No changes",
      "booking.editor.save.publishing": "Publishing…",
      "booking.editor.publish.title": "Before you publish",
      "booking.editor.publish.ready": "All set. Customers will see the page as soon as you publish.",
      "booking.editor.publish.blocked": "Fix the following to publish:",
      "booking.editor.publish.warnings": "Notes that do not block publishing:",
      "booking.editor.publish.confirm": "Publish now",
      "booking.editor.publish.cancel": "Cancel",
      "booking.editor.publish.fix": "Fix",
      "booking.editor.publish.go": "Go to it",
      "booking.editor.check.wizardMissing": "The page has no booking form, so customers cannot book.",
      "booking.editor.check.wizardDuplicate": "The page has more than one booking form; only one is allowed.",
      "booking.editor.check.primaryContrast": "The main colour is too light for white button text.",
      "booking.editor.check.inkContrast": "The page text colour does not read on its background.",
      "booking.editor.check.blockContrast": "The text in “{{name}}” does not read on its background.",
      "booking.editor.check.image": "A photo in “{{name}}” is not one of this page’s uploads and will be refused.",
      "booking.editor.check.rootImage": "A photo in the page settings is not one of this page’s uploads.",
      "booking.editor.check.empty": "The section “{{name}}” is empty and will show nothing to customers.",
      "booking.editor.library.title": "Add a section",
      "booking.editor.library.hint": "Click a section to add it after the selected one, or drag it from the compact list.",
      "booking.editor.library.gallery": "Gallery",
      "booking.editor.library.compact": "Compact list",
      "booking.editor.library.added": "Added “{{name}}”.",
      "booking.editor.library.wizardExists": "The page already has a booking form.",
      "booking.editor.purpose.hero": "Hero",
      "booking.editor.purpose.services": "Services",
      "booking.editor.purpose.trust": "Trust",
      "booking.editor.purpose.gallery": "Gallery",
      "booking.editor.purpose.booking": "Booking",
      "booking.editor.purpose.contact": "Contact",
      "booking.editor.purpose.basics": "Basics",
      "booking.editor.rail.sections": "Sections",
      "booking.editor.rail.layers": "Layers",
      "booking.editor.rail.identity": "Identity",
      "booking.editor.identity.title": "Page identity",
      "booking.editor.identity.hint": "Pick a ready-made theme, then fine-tune it in Page settings.",
      "booking.editor.identity.applied": "Applied the “{{name}}” theme.",
      "booking.editor.identity.current": "Current",
      "booking.editor.identity.fromLogo": "From the logo",
      "booking.editor.identity.fromLogoHint": "Builds a readable theme from your logo colours.",
      "booking.editor.identity.noLogo": "Upload the logo in Page settings first.",
      "booking.editor.identity.logoFailed": "Could not read the logo colours.",
      "booking.editor.identity.working": "Reading the colours…",
      "booking.editor.tour.next": "Next",
      "booking.editor.tour.done": "Start",
      "booking.editor.tour.skip": "Skip",
      "booking.editor.tour.step": "Step {{n}} of {{total}}",
      "booking.editor.tour.1.title": "Add ready-made sections",
      "booking.editor.tour.1.body": "Open Sections and pick a pre-designed section; it lands after the selected one.",
      "booking.editor.tour.2.title": "Edit by clicking",
      "booking.editor.tour.2.body": "Click a section for its toolbar; double-click any text to edit it in the panel.",
      "booking.editor.tour.3.title": "Pick a look, then publish",
      "booking.editor.tour.3.body": "Apply a theme from Identity; Publish checks the page before customers see it.",
      "booking.editor.empty.title": "Your page is empty",
      "booking.editor.empty.body": "Start with a hero and the booking form, or add any section from Sections.",
      "booking.editor.empty.starter": "Add a hero and booking form",
      "booking.editor.kbd.title": "Keyboard shortcuts",
      "booking.editor.previewRibbon": "Draft preview — not published yet",
      "booking.editor.device.mobile": "Phone",
      "booking.editor.device.tablet": "Tablet",
      "booking.editor.device.desktop": "Computer",
      "booking.domain.checking": "Checking…",
      "booking.domain.confirm": "Confirm remove",
      "booking.domain.confirmRemove": "Are you sure you want to remove this custom domain? Your booking page will stop being served on this domain.",
      "booking.domain.connect": "Connect domain",
      "booking.domain.connecting": "Connecting…",
      "booking.domain.copied": "Copied!",
      "booking.domain.copy": "Copy",
      "booking.domain.description": "Serve your online booking page on your own domain",
      "booking.domain.dnsInstructions": "Add the following DNS records with your domain provider:",
      "booking.domain.errors.CNAME_MISMATCH": "CNAME record does not point to book.mmind.app",
      "booking.domain.errors.DOMAIN_TAKEN": "This domain is already registered to another workshop",
      "booking.domain.errors.INVALID_HOST": "Invalid domain format. Please enter a valid host (e.g. book.yourdomain.sa)",
      "booking.domain.errors.RESERVED_HOST": "This domain name cannot be used",
      "booking.domain.errors.certFailed": "The security certificate could not be issued for this domain. Check both records, then check again, and contact support if it continues.",
      "booking.domain.errors.TXT_MISSING": "Verification TXT record is missing or incorrect",
      "booking.domain.hostLabel": "Domain name",
      "booking.domain.hostPlaceholder": "e.g. book.autofix.sa",
      "booking.domain.recordName": "Name",
      "booking.domain.recordType": "Type",
      "booking.domain.recordValue": "Value",
      "booking.domain.remove": "Remove domain",
      "booking.domain.status.cert_issued": "Active & secure",
      "booking.domain.status.failed": "Configuration failed",
      "booking.domain.status.none": "Not configured",
      "booking.domain.status.pending_dns": "Pending DNS",
      "booking.domain.status.verified": "DNS verified (issuing SSL)",
      "booking.domain.title": "Custom domain",
      "booking.domainPlaceholder": "Custom domain settings",
      "booking.errors.FIELD_OUT_OF_RANGE": "Value is out of allowed range",
      "booking.errors.durationRequired": "Set a duration before turning on online booking for this service",
      "booking.errors.invalid": "Invalid data",
      "booking.errors.outOfRange": "Value is out of allowed range",
      "booking.general.autoConfirm": "Automatic confirmation",
      "booking.general.autoConfirmDesc": "Confirm bookings immediately without requiring manual staff approval",
      "booking.general.cancelCutoffMinutes": "Cancellation cutoff (minutes)",
      "booking.general.enabled": "Enable online booking",
      "booking.general.enabledDesc": "Allow customers to book appointments through the public booking page",
      "booking.general.horizonDays": "Booking horizon (days)",
      "booking.general.leadTimeMinutes": "Minimum lead time (minutes)",
      "booking.general.loadFailed": "Failed to load settings",
      "booking.general.multiService": "Allow multiple services",
      "booking.general.multiServiceDesc": "Customers can select more than one service in a single booking",
      "booking.general.noShowGraceMinutes": "No-show grace period (minutes)",
      "booking.general.pendingExpiryMinutes": "Pending booking expiry (minutes)",
      "booking.general.rescheduleCutoffMinutes": "Rescheduling cutoff (minutes)",
      "booking.general.saveFailed": "Failed to save settings",
      "booking.general.saved": "Settings saved successfully",
      "booking.general.slotMinutes": "Slot duration (minutes)",
      "booking.general.timezone": "Timezone",
      "booking.general.title": "General booking settings",
      "booking.header.subtitle": "Manage working hours, service bays, services, and online booking preferences",
      "booking.header.title": "Online booking settings",
      "booking.hours.addClosure": "Add closure",
      "booking.hours.addShift": "Add shift",
      "booking.hours.closed": "Closed",
      "booking.hours.closureDate": "Closure date",
      "booking.hours.closureExists": "This date has already been added",
      "booking.hours.closuresTitle": "Closures and public holidays",
      "booking.hours.invalidFormat": "Working hours format is invalid",
      "booking.hours.loadFailed": "Failed to load working hours",
      "booking.hours.noClosures": "No scheduled closures",
      "booking.hours.overlap": "Working shifts overlap on this day",
      "booking.hours.saveFailed": "Failed to save working hours",
      "booking.hours.saved": "Working hours saved successfully",
      "booking.hours.title": "Weekly working hours and shifts",
      "booking.messages.desc": "Send automated WhatsApp and SMS reminders to customers before their appointment",
      "booking.messages.previewsDesc": "Messages are sent in Arabic or English according to customer preference",
      "booking.messages.previewsTitle": "Sample customer message previews",
      "booking.messages.reminder1": "First reminder timing (minutes before appointment)",
      "booking.messages.reminder1Hint": "e.g. 1440 minutes equals 24 hours before appointment",
      "booking.messages.reminder2": "Second reminder timing (minutes before appointment)",
      "booking.messages.reminder2Hint": "e.g. 120 minutes equals 2 hours before appointment",
      "booking.messages.reminder2Placeholder": "Optional (leave empty to disable)",
      "booking.messages.saved": "Message settings saved successfully",
      "booking.messages.title": "Automated reminder message settings",
      "booking.noPermissions": "You do not have permission to view booking settings",
      "booking.pagePlaceholder": "Page design editor",
      "booking.refusal.beyondHorizon": "Appointment exceeds maximum booking horizon",
      "booking.refusal.branchNotBookable": "Branch is not available for online booking",
      "booking.refusal.dayClosed": "Workshop is closed on this day",
      "booking.refusal.general": "Unable to reschedule appointment due to conflicting rules",
      "booking.refusal.noBayFree": "No service bay available at this time",
      "booking.refusal.offGrid": "Appointment time does not match slot intervals",
      "booking.refusal.outsideHours": "Appointment is outside working hours",
      "booking.refusal.serviceNotBookable": "Selected service is not bookable online",
      "booking.refusal.tooSoon": "Appointment is too soon (less than minimum lead time)",
      "booking.selectTab": "Online booking sections",
      "booking.services.colBayTypes": "Service bay types",
      "booking.services.colBookable": "Online bookable",
      "booking.services.colBuffer": "Buffer (min)",
      "booking.services.colConfirm": "Confirmation mode",
      "booking.services.colDeposit": "Custom deposit (SAR)",
      "booking.services.colDuration": "Duration (min)",
      "booking.services.colName": "Service",
      "booking.services.desc": "Choose which services customers can book online and how long each one takes",
      "booking.services.saveFailed": "Failed to save service",
      "booking.services.saved": "Service settings updated",
      "booking.services.title": "Online booking services settings",
      "booking.tabs.analytics": "Analytics",
      "booking.tabs.deposit": "Deposit",
      "booking.tabs.domain": "Custom domain",
      "booking.tabs.general": "General",
      "booking.tabs.hours": "Working hours",
      "booking.tabs.messages": "Messages & reminders",
      "booking.tabs.page": "Page design",
      "booking.domain.emptyTitle": "What is a custom domain?",
      "booking.domain.emptyExplainer": "Connect your own domain (e.g. book.yourworkshop.com) so your customers book directly under your workshop's brand identity instead of the MotorMind direct link.",
      "booking.domain.defaultLinkTitle": "Current default booking link:",
      "booking.domain.stepGuideTitle": "Setup Guide (3 Steps)",
      "booking.domain.step1Title": "Add DNS Records",
      "booking.domain.step1Desc": "Add these two records in your domain provider's dashboard (e.g. Cloudflare, GoDaddy):",
      "booking.domain.step2Title": "Verify DNS Records",
      "booking.domain.step2Desc": "After saving the records at your domain provider, click \"Check DNS Now\". DNS lookups are available once every 5 minutes.",
      "booking.domain.step3Title": "Automatic SSL Certificate",
      "booking.domain.step3Desc": "The SSL certificate is issued automatically once DNS records are verified, and your booking page goes live.",
      "booking.domain.cooldownNotice": "DNS checks are limited to once every 5 minutes.",
      "booking.domain.cooldownRemaining": "Next check available in {{time}}",
      "booking.domain.liveUrlTitle": "Live Booking URL:",
      "booking.domain.openLink": "Open link",
      "booking.domain.confirmRemoveTitle": "Remove Custom Domain",
      "booking.domain.errors.DOMAIN_NOT_SET": "No custom domain configured",
      "booking.domain.validation.noSpaces": "Domain name cannot contain spaces",
      "booking.domain.validation.noProtocol": "Enter the domain name only, without http:// or paths",
      "booking.domain.validation.noIp": "Please enter a valid domain name, not an IP address or localhost",
      "booking.domain.validation.reserved": "This domain is reserved and cannot be used",
      "booking.domain.validation.needDot": "Domain name must contain at least one dot (e.g. book.autofix.sa)",
      "booking.editor.customer.vehicle": "After verification, returning customers choose a saved car or add one; new customers enter a car.",
      "booking.editor.customerPreview": "Preview customer type",
      "booking.editor.customerPreview.new": "New customer",
      "booking.editor.customerPreview.returning": "Returning customer",
      "booking.wizard.sampleData": "Sample preview data",
      "booking.wizard.addVehicle": "Add a new vehicle",
      "booking.aiDesign.disabled": "AI page design is not enabled for your account",
      "booking.aiDesign.briefInvalid": "Some design brief answers are invalid.",
      "booking.aiDesign.rateLimited": "Too many requests. Wait before trying again.",
      "booking.aiDesign.business": "Business",
      "booking.aiDesign.identity": "Identity",
      "booking.aiDesign.sections": "Sections",
      "booking.aiDesign.materials": "Materials",
      "booking.aiDesign.analyse": "Analysing requirements",
      "booking.aiDesign.design": "Designing page",
      "booking.aiDesign.verify": "Validating",
      "booking.aiDesign.save": "Saving draft",
      "booking.aiDesign.unavailable": "Design service is unavailable. Try later.",
      "booking.aiDesign.limit": "Monthly limit reached. Resets on",
      "booking.aiDesign.invalid": "Generated design could not be validated. Try again.",
      "booking.aiDesign.uploadFailed": "Image upload failed or exceeds the limit.",
      "booking.aiDesign.logoNeeded": "Upload a logo first.",
      "booking.aiDesign.title": "Design with AI",
      "booking.aiDesign.used": "Used",
      "booking.aiDesign.of": "of",
      "booking.aiDesign.thisMonth": "this month",
      "booking.aiDesign.steps": "Design steps",
      "booking.aiDesign.type": "Business type",
      "booking.aiDesign.audience": "Audience",
      "booking.aiDesign.tone": "Tone",
      "booking.aiDesign.logo": "Logo",
      "booking.aiDesign.primary": "Primary colour",
      "booking.aiDesign.secondary": "Secondary colour",
      "booking.aiDesign.extract": "Extract from logo",
      "booking.aiDesign.style": "Style",
      "booking.aiDesign.font": "Font",
      "booking.aiDesign.wizardAlways": "Booking form is always included.",
      "booking.aiDesign.order": "Section order",
      "booking.aiDesign.recommended": "Recommended",
      "booking.aiDesign.selectedOrder": "Selection order",
      "booking.aiDesign.photos": "Workshop and car photos (up to 8)",
      "booking.aiDesign.references": "Style reference screenshots only (up to 3)",
      "booking.aiDesign.founded": "Founded year",
      "booking.aiDesign.branchesFromSystem": "Branch count comes from system",
      "booking.aiDesign.specialities": "Specialities",
      "booking.aiDesign.back": "Back",
      "booking.aiDesign.cancel": "Cancel",
      "booking.aiDesign.next": "Next",
      "booking.aiDesign.generate": "Design page",
      "booking.aiDesign.draftBanner": "This draft was designed by AI — review it before publishing",
      "booking.aiDesign.restorePrevious": "Restore previous version",
      "booking.aiDesign.monthlyLimit": "Monthly AI page designs (blank defaults to 5)",
      "err.AI_DESIGN_LIMIT": "You have used all AI page designs for this month.",
      "err.AI_DESIGN_INVALID": "The AI could not produce a valid page. Adjust the brief and try again.",
      "err.AI_DESIGN_UNAVAILABLE": "The AI designer is unavailable right now. Try again shortly.",
      "err.AI_DESIGN_BRIEF_INVALID": "Some answers in the design brief are not valid.",
      "err.AI_DESIGN_SECTION_INVALID": "One of the chosen page sections is not valid.",
      "err.AI_DESIGN_LIMIT_INVALID": "The monthly limit must be a whole number, 0 or more. Leave it empty for the default.",
      "booking.editor.cards": "Cards",
      "booking.editor.grid": "Grid",
      "booking.editor.list": "List",
      "booking.editor.option.branchLayout": "Branch layout",
      "booking.editor.option.daysAhead": "Days ahead",
      "booking.editor.option.emailRequired": "Require email",
      "booking.editor.option.emailShown": "Show email",
      "booking.editor.option.multiService": "Multiple services",
      "booking.editor.option.notesRequired": "Require notes",
      "booking.editor.option.notesShown": "Show notes",
      "booking.editor.option.showAddress": "Show address",
      "booking.editor.option.showDuration": "Show duration",
      "booking.editor.option.showMapLink": "Map link",
      "booking.editor.option.showPrice": "Show price",
      "booking.editor.option.slotLayout": "Slot layout",
      "booking.editor.option.termsRequired": "Require terms acceptance",
      "booking.editor.option.termsText": "Terms text",
      "booking.editor.options": "Step options",
      "booking.editor.stepType": "Step type",
      "booking.editor.template.empty": "Empty step",
      "booking.editor.template.info": "Customer information",
      "booking.editor.template.photos": "Photo upload",
      "booking.editor.template.questions": "Questions",
      "booking.editor.template.terms": "Terms acceptance",
      "booking.editor.templateDesc.empty": "Start with an empty step",
      "booking.editor.templateDesc.info": "Show information without input",
      "booking.editor.templateDesc.photos": "Collect vehicle photos",
      "booking.editor.templateDesc.questions": "Ask customers questions",
      "booking.editor.templateDesc.terms": "Require agreement",
      "booking.general.bookingOffNote": "This page is not accepting bookings because online booking is off",
      "booking.general.copyUrl": "Copy URL",
      "booking.general.downloadQr": "Download QR",
      "booking.general.openUrl": "Open page",
      "booking.general.publicUrl": "Booking page URL",
      "booking.page.alignment": "Alignment",
      "booking.page.background": "Background",
      "booking.page.backgroundColor": "Background colour",
      "booking.page.backgroundImage": "Background image",
      "booking.page.buttonStyle": "Button style",
      "booking.page.cornerRadius": "Corner radius",
      "booking.page.headerBookNow": "Book now",
      "booking.page.headerStyle": "Header style",
      "booking.page.hideOnDesktop": "Hide on desktop",
      "booking.page.hideOnMobile": "Hide on mobile",
      "booking.page.oneBranchNote": "The branch step is hidden for customers because you have one branch",
      "booking.page.spacing": "Vertical spacing",
      "booking.page.style": "Style",
      "booking.page.textColor": "Text colour",
      "booking.page.width": "Width",
      "booking.wizard.email": "Email",
      "booking.wizard.invalidEmail": "Invalid email",
      "booking.wizard.mapLink": "View map",
      "booking.wizard.minutes": "minutes",
      "booking.editor.customFields": "Step fields",
      "booking.editor.details.name": "Name — required",
      "booking.editor.details.notes": "Notes — optional",
      "booking.editor.details.phone": "Mobile number — required, verified with a code sent to it",
      "booking.editor.detailsFields": "What this step asks for",
      "booking.editor.expand": "Step settings",
      "booking.editor.fieldShown": "Shown",
      "booking.editor.fixOrder": "Move details before vehicle",
      "booking.editor.hasErrors": "This step has errors",
      "booking.editor.newField": "New field",
      "booking.editor.newFieldAr": "Field name in Arabic",
      "booking.editor.newFieldEn": "Field name in English",
      "booking.editor.newStep": "New step",
      "booking.editor.newStepAr": "Step title in Arabic",
      "booking.editor.newStepEn": "Step title in English",
      "booking.editor.noFields": "Add at least one field so customers see this step.",
      "booking.editor.orderHint": "To show the customer's saved vehicles, put the details step before the vehicle step",
      "booking.editor.previewFrame": "The booking page as customers see it",
      "booking.editor.previewSample": "Preview only: no messages are sent and no booking is made.",
      "booking.editor.previewSteps": "Preview steps",
      "booking.editor.savedVehicles": "Show the customer's saved vehicles after the mobile number is verified",
      "booking.editor.savedVehiclesHint": "A returning customer picks a car from the list or enters another one. Plate numbers are partly hidden.",
      "booking.editor.skip.branch": "Skipped automatically because you have one branch",
      "booking.editor.skip.deposit": "Shown only when the booking needs a deposit",
      "booking.editor.skip.empty": "Skipped automatically because it has no fields",
      "booking.editor.stepTitle": "Step title",
      "booking.editor.untitled": "Untitled step",
      "booking.editor.vehicleField.make": "Make",
      "booking.editor.vehicleField.model": "Model",
      "booking.editor.vehicleField.plate": "Plate number",
      "booking.editor.vehicleField.year": "Year",
      "booking.editor.vehicleFields": "Vehicle fields",
      "booking.wizardTab.intro": "Drag to reorder the steps, switch them on or off, and open a step to change its settings. The preview updates with every change.",
      "booking.wizardTab.title": "Booking form steps",
      "err.STEP_OPTION_INVALID": "A setting of this step is not valid.",
      "booking.general.capacityPerSlot": "Cars at the same time",
      "booking.info.open": "About this setting",
      "booking.info.auto_confirm": "Confirms bookings immediately unless a service specifically requires manual approval.",
      "booking.info.enabled": "Allows customers to book through the public branch page.",
      "booking.info.multi_service": "Allows several services in one booking; their durations determine required time.",
      "booking.info.timezone": "Determines the time zone for branch hours and appointments.",
      "booking.info.slot_minutes": "Sets the interval between appointment start times. For example, 30 minutes allows 9:00 and 9:30.",
      "booking.info.capacity_per_slot": "Maximum cars booked at this branch at the same time, even when appointments start at different times.",
      "booking.info.horizon_days": "How far ahead customers can book. For example, 14 days opens the next two weeks.",
      "booking.info.lead_time_minutes": "Minimum time between booking and appointment. For example, 120 minutes requires two hours notice.",
      "booking.info.pending_expiry_minutes": "A booking waiting for staff approval expires after this time or when its appointment starts, whichever comes first.",
      "booking.info.cancel_cutoff_minutes": "Last time before the appointment when a customer may cancel it.",
      "booking.info.reschedule_cutoff_minutes": "Last time before the appointment when a customer may reschedule it.",
      "booking.info.no_show_grace_minutes": "Time after the appointment starts before the system marks a customer as absent.",
      "booking.services.colActions": "Actions",
      "booking.info.service.colName": "Service name shown to customers on the booking page.",
      "booking.info.service.colBookable": "Controls whether customers can book this service online.",
      "booking.info.service.colDuration": "Time this service occupies in the appointment schedule.",
      "booking.info.service.colBuffer": "Extra time after service before capacity becomes available again.",
      "booking.info.service.colConfirm": "This service can inherit the general setting, require manual approval, or confirm automatically.",
      "booking.info.service.colActions": "Saves changes to this service only.",
      "booking.services.colDepositAmount": "Service deposit",
      "booking.info.service.colDepositAmount": "Deposit amount for this service; leave empty to use the general setting.",
      "booking.deposit.typeFixedSymbolFree": "Fixed amount",
      "booking.deposit.holdMinutesPayment": "Payment deadline (minutes)",
      "booking.header.subtitleCars": "Manage branch hours, services, and customer booking details",
      "booking.tabs.wizard": "Booking Form",
      "booking.page.reset": "Reset to standard page",
      "booking.page.resetTitle": "Reset to standard page",
      "booking.page.resetMessage": "The standard page will replace your current draft and will not be published. Continue?",
      "booking.page.wizardNeedsConfig": "Turn booking on and set at least one service to show the actual form",
      "booking.editor.openTab": "Edit the booking steps",
      "booking.wizardTab.missing": "The booking page has no booking form yet",
      "booking.wizardTab.add": "Add the booking form",
      "booking.wizardTab.save": "Save draft",
      "booking.wizardTab.publish": "Publish",
      "booking.wizardTab.published": "Published",
      "booking.wizardTab.invalid": "The booking form has invalid content. Fix the items below and try again.",
      "err.SCHEMA_INVALID": "The booking form is not valid. Reset it to the default.",
      "err.TOO_MANY_STEPS": "A booking form can have at most 12 steps.",
      "err.TOO_MANY_FIELDS": "A booking form can have at most 40 custom fields.",
      "err.STEP_LOCKED": "The date, details and review steps are required and cannot be hidden or removed.",
      "err.REVIEW_NOT_LAST": "The review step must stay last.",
      "err.STEP_INVALID": "This step is not valid.",
      "err.STEP_KEY_DUPLICATE": "Two steps share the same key.",
      "err.STEP_KEY_INVALID": "The step key is not valid.",
      "err.LABEL_INVALID": "Enter the text in both Arabic and English.",
      "err.LABEL_TOO_LONG": "The text is too long.",
      "err.VEHICLE_FIELD_INVALID": "A vehicle field setting is not valid.",
      "err.FIELD_KEY_INVALID": "The field key is not valid.",
      "err.FIELD_KEY_DUPLICATE": "Two fields share the same key.",
      "err.FIELD_TYPE_INVALID": "Choose a field type.",
      "err.REQUIRED_INVALID": "The required setting is not valid.",
      "err.VISIBILITY_INVALID": "Choose where this answer is visible.",
      "err.OPTIONS_REQUIRED": "Add at least one option.",
      "err.OPTIONS_INVALID": "The options are not valid (at most 30, each with a value and both labels).",
      "err.CONDITIONS_INVALID": "Use at most 3 conditions.",
      "err.CONDITION_INVALID": "The condition is not valid.",
      "err.CONDITION_FORWARD_REF": "A condition can only use a field that comes before this one.",
      "err.CONDITION_UNKNOWN_FIELD": "The condition uses a field that does not exist.",
      "booking.editor.drag": "Drag to reorder",
      "booking.editor.shown": "Shown",
      "booking.editor.required": "Required",
      "booking.editor.showStep": "Show step",
      "booking.editor.lockedWhy": "This step is required for every booking and cannot be hidden.",
      "booking.editor.deleteStep": "Delete step",
      "booking.editor.addStep": "Add step",
      "booking.editor.newStepTitle": "New step title (English)",
      "booking.editor.addField": "Add field",
      "booking.editor.newFieldLabel": "New field label (English)",
      "booking.editor.newFieldType": "New field type",
      "booking.editor.deleteField": "Delete field",
      "booking.editor.moveUp": "Move up",
      "booking.editor.moveDown": "Move down",
      "booking.editor.type": "Field type",
      "booking.editor.type.text": "Short text",
      "booking.editor.type.textarea": "Long text",
      "booking.editor.type.number": "Number",
      "booking.editor.type.select": "Single choice",
      "booking.editor.type.multiselect": "Multiple choice",
      "booking.editor.type.yesno": "Yes / No",
      "booking.editor.type.date": "Date",
      "booking.editor.type.photo": "Photo",
      "booking.editor.type.note": "Note (no answer)",
      "booking.editor.visibility": "Visibility",
      "booking.editor.internal": "Internal only",
      "booking.editor.printable": "On printouts",
      "booking.editor.min": "Min",
      "booking.editor.max": "Max",
      "booking.editor.optValue": "Option value",
      "booking.editor.optAr": "Option (Arabic)",
      "booking.editor.optEn": "Option (English)",
      "booking.editor.addOption": "Add option",
      "booking.editor.remove": "Remove",
      "booking.editor.conditions": "Show only when",
      "booking.editor.condWhen": "Condition field",
      "booking.editor.condService": "Chosen services",
      "booking.editor.condOp": "Condition operator",
      "booking.editor.condValue": "Condition value",
      "booking.editor.op.in": "is one of (comma separated)",
      "booking.editor.op.eq": "equals",
      "booking.editor.op.neq": "does not equal",
      "booking.editor.op.truthy": "has an answer",
      "booking.editor.addCondition": "Add condition",
      "booking.editor.preview": "Live preview",
      "booking.editor.previewBlocked": "Fix the errors above to see the preview.",
      "booking.wizard.choose": "Choose…",
      "booking.wizard.yes": "Yes",
      "booking.wizard.no": "No",
      "booking.wizard.branch": "Branch",
      "booking.wizard.branchDesc": "Choose a branch",
      "booking.wizard.servicesDescMulti": "Choose one or more services",
      "booking.wizard.vehicle": "Vehicle",
      "booking.wizard.savedVehicles": "Your saved vehicles",
      "booking.wizard.useAnotherVehicle": "Enter a different vehicle",
      "booking.wizard.phoneVerified": "Phone number verified",
      "booking.wizard.uploading": "Uploading…",
      "booking.wizard.photoN": "Photo {{n}}",
      "booking.wizard.welcomeBack": "Welcome back, {{name}}",
      "booking.wizard.notYou": "Not you? Change the mobile number above.",
      "booking.wizard.newCustomer": "This number is not registered with us yet. Enter your name to register as a new customer.",
      "booking.wizard.removePhoto": "Remove",
      "booking.wizard.depositDesc": "A deposit is needed to confirm this booking.",
      "booking.wizard.depositAmount": "Deposit due",
      "booking.wizard.depositReason.setting": "This workshop asks for a deposit on online bookings.",
      "booking.wizard.depositReason.no_show_rule": "A deposit is required because of repeated no-shows.",
      "booking.wizard.payToConfirm": "Pay the deposit to confirm your booking.",
      "booking.wizard.payNow": "Pay now",
      "booking.manage.title": "Manage your booking",
      "booking.manage.status": "Status",
      "booking.manage.statuses.pending": "Pending",
      "booking.manage.statuses.confirmed": "Confirmed",
      "booking.manage.statuses.awaiting_payment": "Awaiting deposit payment",
      "booking.manage.reschedule": "Change date or time",
      "booking.manage.confirmReschedule": "Confirm new time",
      "booking.manage.rescheduled": "Your appointment time was changed.",
      "booking.manage.cancel": "Cancel booking",
      "booking.manage.cancelConfirm": "Cancel this booking? This cannot be undone.",
      "booking.manage.cancelled": "Your booking was cancelled.",
      "booking.manage.addToCalendar": "Add to calendar",
      "err.NO_BAY_FREE": "The time you chose was just taken. Please pick another time.",
      "booking.page.wholePage": "Page",
      "booking.page.settingsRoot": "Page settings",
      "booking.page.loadFailed": "Could not load the page design",
      "booking.page.retry": "Retry",
      "booking.page.saving": "Saving…",
      "booking.page.saved": "Draft saved",
      "booking.page.saveFailed": "Draft not saved",
      "booking.page.conflict": "A newer version was published by another user",
      "booking.page.reload": "Reload",
      "booking.page.invalid": "The page has invalid content. Fix the items below and try again. Your draft is kept on this device.",
      "booking.page.canvasLang": "Preview language",
      "booking.page.history": "History",
      "booking.page.publishing": "Publishing…",
      "booking.page.noVersions": "No published versions yet",
      "booking.page.version": "Version",
      "booking.page.restore": "Restore",
      "booking.page.restoreTitle": "Restore version",
      "booking.page.restoreMessage": "This version will replace your current draft. Continue?",
      "booking.page.status.published": "Published",
      "booking.page.status.archived": "Archived",
      "err.PAGE_IMAGE_INVALID": "Upload a JPG, PNG, WebP or GIF image.",
      "err.PAGE_IMAGE_TOO_LARGE": "The image is larger than 5 MB. Choose a smaller one.",
      "booking.page.localDraft": "This browser has unsaved changes from {{time}}",
      "booking.page.useLocal": "Use them",
      "booking.page.discardLocal": "Discard",
      "err.CONTRAST_TOO_LOW": "The main colour is too light for white text. In Page design, open the page settings and press «Use a darker shade» under the main colour.",
      "err.DEPTH_EXCEEDED": "This section is nested too deeply. Move it out of the sections around it.",
      "err.DOC_TOO_LARGE": "The page is too big to save. Remove some sections or images.",
      "err.IMAGE_NOT_ALLOWED": "This image cannot be used. Upload it again from the image field.",
      "err.PROP_INVALID": "This setting has a wrong value. Re-enter it.",
      "err.PROP_UNKNOWN": "This setting is not supported. Remove it or reset the section.",
      "err.TEXT_TOO_LONG": "This text is too long. Shorten it in both languages.",
      "err.TOO_MANY_COMPONENTS": "The page has too many sections. Remove some.",
      "err.TYPE_UNKNOWN": "This section type is not available. Delete it.",
      "err.WIZARD_DUPLICATE": "The booking form appears more than once. Keep only one.",
      "err.WIZARD_MISSING": "The page needs a booking form. Add one before publishing.",
      "booking.tabs.services": "Services",
      "booking.tabs.waitlist": "Waitlist",
      "booking.waitlist.desc": "When all appointment slots are fully booked on a day, customers can join the waitlist to receive an automated booking offer when a slot opens up",
      "booking.waitlist.enabled": "Enable waitlist",
      "booking.waitlist.enabledDesc": "Display a join waitlist button to customers on fully booked days",
      "booking.waitlist.loadFailed": "Failed to load waitlist settings",
      "booking.waitlist.offerMinutes": "Slot offer validity (minutes)",
      "booking.waitlist.offerMinutesHint": "Time window available for the customer to confirm before offering the slot to the next person",
      "booking.waitlist.saved": "Waitlist settings saved successfully",
      "booking.waitlist.title": "Smart waitlist settings",
      "bookingAnalytics.bookings": "Bookings",
      "bookingAnalytics.cancelled": "Cancelled",
      "bookingAnalytics.completed": "Completed",
      "bookingAnalytics.confirmed": "Confirmed",
      "bookingAnalytics.deposit_paid_rate": "Deposit paid rate",
      "bookingAnalytics.expired": "Expired",
      "bookingAnalytics.from": "From",
      "bookingAnalytics.funnel": "Booking funnel",
      "bookingAnalytics.loadError": "Failed to load booking analytics",
      "bookingAnalytics.no_show_rate": "No-show rate",
      "bookingAnalytics.no_shows": "No-shows",
      "bookingAnalytics.step.branch": "Branch selection",
      "bookingAnalytics.step.datetime": "Date and time",
      "bookingAnalytics.step.deposit": "Deposit payment",
      "bookingAnalytics.step.details": "Customer details",
      "bookingAnalytics.step.review": "Review and confirm",
      "bookingAnalytics.step.services": "Services selection",
      "bookingAnalytics.step.vehicle": "Vehicle details",
      "bookingAnalytics.title": "Booking analytics",
      "bookingAnalytics.to": "To",
      "bookingAnalytics.viewed": "Viewed",
      "bookingAnalytics.visits": "Visits",
      "common.accessDenied": "Access denied",
      "common.noChanges": "No changes to save",
      "err.BODY_TOO_LARGE": "The request is too large.",
      "err.BOOKING_NOT_FOUND": "That booking was not found or the link has expired.",
      "err.BRANCH_NOT_BOOKABLE": "This branch is not taking online bookings.",
      "err.BRANCH_NOT_FOUND": "That branch does not take online bookings or does not exist.",
      "err.COMPANY_NOT_FOUND": "This booking page was not found.",
      "err.CONFIG_FAILED": "The booking page could not be loaded. Try again.",
      "err.CUTOFF_PASSED": "It is too close to the appointment to change it online. Contact the workshop.",
      "err.EVENT_INGEST_FAILED": "The event could not be recorded.",
      "err.FILE_INVALID": "Upload a JPG, PNG or WebP photo up to 8 MB.",
      "err.INVALID_DATE_RANGE": "Choose a valid date range.",
      "err.INVALID_EVENT": "Unknown booking event.",
      "err.APPOINTMENT_ALREADY_CONVERTED": "This appointment has already been converted to a job card.",
      "err.INVALID_TRANSITION": "This appointment cannot move to that status from its current status.",
      "err.MANAGE_FAILED": "The booking could not be changed. Try again.",
      "err.OTP_NOT_VERIFIED": "Verify your phone number first.",
      "err.OVERRIDE_NOT_ALLOWED": "You do not have permission to book outside the booking rules.",
      "err.PAGE_INVALID": "The booking page has invalid content. Fix the highlighted sections.",
      "err.PAGE_VERSION_NOT_FOUND": "That page version was not found.",
      "err.QUOTE_FAILED": "The deposit could not be calculated. Try again.",
      "err.TOO_MANY_FILES": "Upload one photo at a time.",
      "err.UNKNOWN": "Something went wrong. Try again.",
      "err.WAITLIST_JOIN_FAILED": "You could not be added to the waitlist. Try again.",
      "err.INVALID_HOST": "Enter a valid hostname, such as book.example.sa.",
      "err.RESERVED_HOST": "MotorMind domains cannot be used as a custom domain.",
      "err.DOMAIN_TAKEN": "This domain is already linked to another account.",
      "err.DOMAIN_NOT_SET": "No custom domain has been added yet.",
      "err.CNAME_MISMATCH": "The CNAME record is missing or does not point to book.mmind.app.",
      "err.TXT_MISSING": "The TXT verification record is missing or its value is wrong.",
      "err.APPROVAL_LIMIT_GRANT_EXCEEDS_OWN_PCT": "You can only give up to your own limit ({{own}}%).",
      "err.APPROVAL_LIMIT_GRANT_EXCEEDS_OWN_SAR": "You can only give up to your own limit ({{own}} SAR).",
      "err.APPROVAL_PENDING": "This purchase invoice is waiting for approval, so it cannot be paid yet.",
      "err.DISCOUNT_APPROVAL_PENDING": "A discount on this document is waiting for approval. Nothing goes to the customer, and it cannot be posted or paid, until the discount is approved or rejected.",
      "err.PURCHASE_APPROVAL_PENDING": "This purchase invoice is waiting for approval. It is not posted until an approver approves it.",
      "err.APPROVAL_REJECTED": "This purchase invoice was rejected, so it cannot be paid.",
      "err.APPROVAL_REQUIRED": "This is above your limit, so it has been sent for approval.",
      "err.SELF_APPROVAL_FORBIDDEN": "You cannot approve or reject your own request.",
      "err.GARAGE_SCOPE_ALL_WRITE": "Changes are saved to one branch at a time. Select a branch, then try again.",
      "err.LINKED_BRANCH_MISMATCH": "The linked records belong to different branches. Link records from one branch only.",
      "err.GARAGE_ACCESS_DENIED": "You do not have access to this workshop branch.",
      "err.GARAGE_SCOPE_ALL_UNSUPPORTED": "Reading every branch at once is not supported here. Select one branch.",
      "err.IDEMPOTENCY_AUTH_CONTEXT_MISSING": "The authenticated request context is unavailable for this operation.",
      "err.IDEMPOTENCY_KEY_INVALID": "The operation identifier must be a valid UUID.",
      "err.EXPECTED_VERSION_REQUIRED": "This change needs the version of the record you loaded. Refresh and try again.",
      "err.INVALID_EXPECTED_VERSION": "The record version sent with this change is not valid.",
      "err.INVALID_FINDING_ITEMS": "The parts and services on this finding are not in a valid format.",
      "err.INVALID_OCCURRED_AT": "The time recorded for this action is not valid for this job card.",
      "err.INVALID_REQUEST": "The request format is invalid.",
      "err.INVALID_ROLE_ANALYSIS": "The role analysis request is not valid.",
      "err.ROLE_ANALYSIS_UNAVAILABLE": "Role analysis is temporarily unavailable. Try again shortly.",
      "err.INVALID_ROLE_SUGGEST": "Enter a role name to get suggestions.",
      "err.ROLE_SUGGEST_UNAVAILABLE": "Permission suggestions are temporarily unavailable. Try again shortly.",
      "err.CATALOG_SMART_REVIEW_UNAVAILABLE": "Smart review is unavailable — TzamunAI Decide is not configured on this server.",
      "err.MISS_NOT_OPEN": "This item was already resolved. Refresh the list.",
      "err.INVALID_QUERY": "The page, limit, search or status filter is not valid.",
      "err.MISSING_TENANT": "A tenant could not be resolved for this request.",
      "err.NOT_AUTHENTICATED": "A valid mobile session is required.",
      "err.OPERATION_IN_FLIGHT": "This operation is still being processed. Retry with the same operation identifier.",
      "err.INSPECTION_MANDATORY_POINTS_MISSING": "Some mandatory checklist points are still empty. Answer every mandatory point before finishing.",
      "err.INSPECTION_NOT_FOUND": "This checklist no longer exists or belongs to a different branch.",
      "err.INSPECTION_ALREADY_ISSUED": "This inspection has already been completed.",
      "err.INSPECTION_RESULT_INVALID_VERSION": "This checklist point could not be saved. Refresh and try again.",
      "err.PHOTOS_NOT_ACCEPTED": "Photos cannot be sent on this write — attach photos from the point's photo button instead.",
      "err.VEHICLE_NOT_FOUND": "That vehicle no longer exists, or it belongs to another branch.",
      "err.VEHICLE_INCOMPLETE": "Add the vehicle's make and model first",
      "err.VIN_IN_USE": "This VIN is already registered to another vehicle in this branch",
      "err.VIN_REQUIRED": "Enter the vehicle's VIN to run this check",
      "err.OPERATION_NOT_FOUND": "No operation was recorded under that identifier.",
      "err.RATE_LIMITED": "Too many requests. Wait before trying again.",
      "err.UNAVAILABLE": "The service is temporarily unavailable. Try again later.",
      "err.VERSION_CONFLICT": "This record changed since you loaded it. Refresh before trying again.",

      "err.CREDIT_NOTE_REJECTED_NO_PDF": "ZATCA rejected this credit note, so it has no printable document.",
      "err.CARD_REFUND_STILL_PROCESSING": "The card refund is still being processed or is awaiting approval. Nothing was changed; check again later.",
      "err.CARD_RESOLUTION_INPUT_INVALID": "The gateway reference (up to 128 characters) or the note (up to 255 characters) is not valid.",
      "err.CREDIT_NOTE_NOT_FOUND": "That credit note does not exist on this invoice.",
      "err.CARD_NEEDS_CHECK_REQUIRED": "This credit note is not waiting on a staff decision, so the card refund no longer needs checking.",
      "err.CREDIT_NOTE_CLEARED_RESEND_REFUSED": "ZATCA has cleared this credit note. It is applied from the stored answer and is never sent again.",
      "err.INVOICE_WRITE_OFF_PENDING_CREDIT_NOTE": "A credit note on this invoice is still awaiting ZATCA clearance. Write the invoice off after it clears or is rejected.",
      "err.GL_CLOSE_PENDING_CREDIT_NOTE": "A credit note dated in this period is still awaiting ZATCA clearance. Close the period after it clears or is rejected.",
      "err.CREDIT_NOTE_REJECTED": "ZATCA rejected this credit note, so it can't be sent again or take effect. Issue a new credit note instead.",
      "err.CREDIT_NOTE_STATE": "Only an issued invoice can be credited.",
      "err.CREDIT_NOTE_DOWNPAYMENT": "A downpayment invoice is not credited — refund the advance instead.",
      "err.NOTHING_TO_CREDIT": "Nothing is left to credit on this invoice.",
      "err.CREDIT_QTY_EXCEEDS": "The quantity is more than is left to credit on that line.",
      "err.CREDIT_EXCEEDS_REMAINING": "The credit is more than is left to credit on this invoice.",
      "err.CREDIT_LINE_INVALID": "Check the lines and quantities to credit.",
      "err.CREDIT_LINES_REQUIRED": "Choose the lines to credit, or credit the full invoice.",
      "err.CREDIT_NOTE_REASON_REQUIRED": "Enter a reason of 3 to 255 characters.",
      "err.CREDIT_DISPOSITION_REQUIRED": "Part of this credit was already paid — choose refund now or keep as customer credit.",
      "err.CREDIT_DISPOSITION_INVALID": "Choose refund now or keep as customer credit.",
      "err.CREDIT_REFUND_METHOD_INVALID": "Choose a refund method.",
      "err.CREDIT_REFUND_NO_CARD_PAYMENT": "This invoice has no online card payment to refund — refund by cash or bank transfer.",
      "err.CREDIT_NO_CUSTOMER": "This invoice has no customer to hold the credit for.",
      "err.CREDIT_NOTE_KEY_REUSED": "This request was already used for another invoice. Reopen the form and try again.",
      "err.IDEMPOTENCY_KEY_REQUIRED": "Reopen the form and try again.",
      "err.RETURNS_MOVED": "Return invoices have been replaced by credit notes. Use \"Issue credit note\" on the invoice.",
      "err.CREDIT_NOTE_LEGACY_RETURN": "This invoice already has a return invoice, which reversed it in full, so it can't be credited again.",
      "err.CREDIT_STOCK_NOT_RETURNABLE": "Some of these parts never left stock on this invoice, so they can't be returned to stock. Untick \\\"Return to stock\\\" for them.",
      "err.INVOICE_WRITTEN_OFF": "This invoice was written off as bad debt, so it can't be credited. Reverse the write-off first.",
      "err.JOB_CARD_ALREADY_ENDED": "This job card has already been closed or the car has been delivered, so it cannot be moved back. Reopen it instead, so the reason and the person are recorded.",
      "err.TEMPLATE_NOT_FOUND": "Checklist template not found.",
      "err.TEMPLATE_NAME_REQUIRED": "A checklist template needs a name in both Arabic and English.",
      "err.TEMPLATE_NO_POINTS": "Choose at least one checklist point for this template.",
      "err.TEMPLATE_POINT_UNKNOWN": "Some of the chosen checklist points are not part of the source checklist.",
      "err.TEMPLATE_READ_ONLY": "The built-in checklists cannot be changed. Create your own template from one instead.",
      "err.TEMPLATE_NO_SOURCE": "This template was not built from a checklist, so its points cannot be re-selected.",
      "err.TEMPLATE_SOURCE_INVALID": "Choose one of the built-in checklists to build this template from.",
      "err.TEMPLATE_MODE_MISMATCH": "That checklist cannot be used for this kind of check.",
      "err.CHALLENGE_ALREADY_USED": "This sign-in code has already been used.",
      "err.CHALLENGE_EXPIRED": "This sign-in code has expired. Request a new one to continue.",
      "err.CHALLENGE_LOCKED": "Too many wrong guesses. Wait 15 minutes, then request a new code.",
      "err.INVALID_AMOUNT": "The amount is not valid.",
      "err.INVOICE_NO_PUBLIC_LINK": "This invoice has no payment link to send. Re-issue it from the job card, then try again.",
      "err.INVOICE_POSTED_IMMUTABLE": "An issued invoice can't be changed or deleted. To correct it, issue a credit note.",
      // Per-service warranty (owner, 2026-09-07). Merged BY HAND from the six
      // *.i18n.json fragments. Arabic carries full CLDR plural forms for the day
      // and distance counts — Arabic has six, and an _one/_other pair renders
      // "متبقٍ ٣ يوم" where it should read "٣ أيام". Counts interpolate {{count}},
      // the variable i18next supplies itself, so a key can never render its own
      // placeholder because a caller forgot to pass a second copy of the number.
      //
      // wty.mon.* and wty.inv.* are namespaced because the monitor and the invoice
      // needed the same concept worded differently, and wty.kmOver took different
      // interpolation variables per surface — merging them would have broken one
      // at runtime rather than merely reading oddly.
      "err.WARRANTY_ALREADY_ACTIVE": "This service already has an active warranty. Cancel it first, or renew it from the job card.",
      "err.WARRANTY_ALREADY_VOID": "This warranty has already been cancelled.",
      "err.WARRANTY_CORRECTION_REASON_REQUIRED": "A reason is required to correct this warranty.",
      "err.WARRANTY_CORRECTION_NO_CHANGE": "Change at least one limit to correct this warranty.",
      "err.WARRANTY_CORRECTION_STALE": "This warranty was modified by another user. Refresh the page and try again.",
      "err.WARRANTY_CHANGED_CONCURRENTLY": "A warranty on this service changed while the credit note was being issued. Please try again.",
      "err.WARRANTY_CORRECTION_ALREADY_ENDED": "This duration has already ended from the original start date. Choose a longer duration or void the warranty.",
      "err.WARRANTY_DURATION_INVALID": "Check the duration: enter a whole number and pick days, months or years.",
      "err.WARRANTY_EXPIRED": "This warranty is no longer valid — $t(err.WARRANTY_EXPIRED_{{limit}})",
      "err.WARRANTY_EXPIRED_distance": "it was exceeded by {{km_over}} km (limit {{km_ceiling}} km, as of the {{km_as_of}} reading).",
      "err.WARRANTY_EXPIRED_time": "it expired {{days_over}} day(s) ago (on {{expires_on}}).",
      "err.WARRANTY_KM_BELOW_ANCHOR": "That reading is lower than the one recorded when the warranty started. Check the odometer and enter it again.",
      "err.WARRANTY_KM_REQUIRED": "This warranty carries a distance limit — the vehicle's current odometer reading is required to reopen against it.",
      "err.WARRANTY_NOT_FOUND": "No active warranty found for that service.",
      "err.WARRANTY_NOT_ON_BILLING_LINE": "This is a billing line, not workshop work, so it carries no warranty.",
      "err.WARRANTY_NO_LIMIT": "Set a duration, a distance limit, or both — a warranty needs at least one.",
      "err.WARRANTY_NO_ODOMETER": "No odometer reading on file for this vehicle, so a distance limit has nothing to count from. Record the mileage first.",
      "err.WARRANTY_OVERRIDE_FORBIDDEN": "You do not have permission to override an expired warranty.",
      "err.WARRANTY_OVERRIDE_REASON_REQUIRED": "Type a reason for overriding this expired warranty — it is recorded against the reopen.",
      "err.WARRANTY_RENEWAL_DECISION_INVALID": "Choose whether to continue the current warranty or issue a new one — no other decision is valid here.",
      "err.WARRANTY_SERVICE_NOT_COMPLETE": "Finish this service first — a warranty starts when the work is done.",
      "err.WARRANTY_SERVICE_REQUIRED": "Pick the service this warranty claim is for, so its cover can be checked.",
      "err.SERVICE_APPROVED_CANNOT_DELETE": "An approved or completed service can't be deleted — it can only be cancelled with a reason.",
      "err.JOB_CARD_ENDED_NO_DELETE": "This job card has ended — nothing on it can be deleted.",
      "err.JOB_CARD_ENDED": "This job card has ended — its services can no longer be changed.",
      "err.PAYMENT_FLOW_RETIRED": "This payment method is no longer available. Please pay with the payment link.",
      "err.SERVICE_CANCELLED_CANNOT_DELETE": "A cancelled service stays on the card as the record of what was agreed and why it stopped — it can't be deleted.",
      "err.SERVICE_CANCEL_HAS_WARRANTY": "This service has an active warranty. Void the warranty first, then cancel the service.",
      "err.SERVICE_NOT_FOUND": "Service not found",
      "err.SERVICE_ALREADY_CANCELLED": "This service is already cancelled.",
      "err.SERVICE_NOT_CANCELLABLE": "Only an approved or completed service can be cancelled. A service still waiting for approval can be removed instead.",
      "err.FINAL_INVOICE_DRAFT_EXISTS": "A draft final invoice exists on this job card. Delete the draft first, then cancel, then generate the invoice again.",
      "err.PART_DECISION_INVALID": "Choose \"back to stock\" or \"stays on the bill\" for a part fitted under this service.",
      "err.PART_DECISIONS_REQUIRED": "Choose, for every fitted part, whether it goes back to stock or stays on the bill.",
      "err.PARTS_UNDECIDED": "This work is completed: decide each fitted part first (back to stock or stays on the bill), then cancel the service.",
      "err.PART_NOT_FITTED": "Only a fitted part can be decided this way.",
      "err.LEGACY_DEPOSIT_SETTLE_FIRST": "Cancelling this leaves no customer work on the card, and its deposit was taken on an older-style invoice. Settle the deposit with a return invoice first, then cancel.",
      "err.SUB_TASK_CLOSE_FAILED": "A step under this service could not be closed. Refresh and try again.",
      "err.TRY_AGAIN": "Someone else changed this job card at the same moment. Nothing was saved — please try again.",
      "err.ITEM_INVOICED_LOCKED": "This item is on a posted invoice and is locked. Reverse the invoice (credit note) to edit it.",
      "err.SERVICE_CANCELLED": "This service was cancelled — no more work, parts or steps can be added to it.",
      "err.INVOICE_SOURCES_CHANGED": "The work on this job card changed while the invoice was being made (a service was cancelled or a part returned). Refresh and generate the invoice again.",
      "err.SOURCE_CANCELLED": "A cancelled service can't be re-quoted.",
      "err.PART_CANCEL_DECIDED": "This part already has a cancel decision (back to stock or stays on the bill). Change it from the service's cancel window.",
      "svcCancel.action": "Cancel service",
      "svcCancel.title": "Cancel this approved service",
      "svcCancel.description": "The service stays on the card as cancelled, with your reason. It is not billed.",
      "svcCancel.reason": "Reason",
      "svcCancel.reasonPlaceholder": "Why is this service cancelled?",
      "svcCancel.reasonRequired": "Write a reason (at least 3 characters).",
      "svcCancel.confirm": "Cancel service",
      "svcCancel.done": "Service cancelled",
      "svcCancel.failed": "Could not cancel the service",
      "svcCancel.previewFailed": "Could not load the cancel details",
      "svcCancel.decideFailed": "Could not save the decision for this part",
      "svcCancel.notCancellable": "This service can't be cancelled now.",
      "svcCancel.fittedParts": "Parts already fitted",
      "svcCancel.fittedHint": "Choose for each part: back to stock, or it stays on the car and on the bill.",
      "svcCancel.fittedHintCompleted": "The work is finished. Decide each fitted part first; the service can be cancelled once every part has a decision.",
      "svcCancel.toStock": "Back to stock",
      "svcCancel.handBack": "Handed back to the customer",
      "svcCancel.keep": "Stays on the bill",
      "svcCancel.decided.returned": "Back to stock",
      "svcCancel.decided.kept": "Kept on the bill",
      "svcCancel.decideAll": "{{count}} part(s) still need a decision.",
      "svcCancel.unnamedPart": "Part",
      "svcCancel.changeDecisions": "Return kept parts",
      "svcCancel.decisionsTitle": "Parts kept on a cancelled service",
      "svcCancel.decisionsDescription": "A part kept on the bill can still go back to stock until it is invoiced. A returned part is final.",
      "svcCancel.legacyMaybe": "There is an older deposit on this card. If nothing billable is left for the customer after this cancel, it will be refused until that deposit is settled.",
      "svcCancel.badge": "Cancelled after approval",
      "svcCancel.byLine": "Cancelled by {{who}} on {{when}} — {{reason}}",
      "svcCancel.depositPreview": "After this cancel no customer work is left on the card. The customer's deposit will be offered as a refund or credit at the final invoice:",
      "svcCancel.depositNotice": "No customer work is left on this card. The deposit of {{amount}} will be offered as a refund or credit at the final invoice.",
      "svcCancel.allCancelled": "All services on this job card were cancelled and no work is left. Add a new service, or end the job card.",
      "ntf.serviceCancelled.title": "A service was cancelled",
      "ntf.serviceCancelled.message": "{{service_name}} was cancelled: {{reason}}",
      "history.serviceCancelled": "Cancelled the service {{service}} — {{reason}}",
      "history.cancelPartDecided.returned": "{{part}} ({{service}}) back to stock",
      "history.cancelPartDecided.kept": "{{part}} ({{service}}) kept on the bill",
      "err.SERVICE_HAS_LIVE_WARRANTY": "This service still has an active warranty. Void the warranty first — deleting the service would erase it and its history.",
      "err.GL_OPENING_EMPTY": "There's nothing to sign off yet — add at least one opening balance first.",
      "err.GL_OPENING_NOTHING_TO_SIGN": "There's no saved draft yet — save your opening balances before signing off.",
      "err.GL_OPENING_DATE_REQUIRED": "Set the opening date first — it's the date these balances are true as of.",
      "wty.panelLoadFailed": "Could not load warranty status — this is not the same as \"not covered\".",
      "wty.retry": "Retry",
      "jc.reopenWarrantyExpiredKm": "This warranty is {{km}} km over its distance limit (as of the {{date}} reading).",
      "jc.reopenWarrantyExpiredTime_one": "This warranty expired {{count}} day ago (on {{date}}).",
      "jc.reopenWarrantyExpiredTime_other": "This warranty expired {{count}} days ago (on {{date}}).",
      "jc.reopenWarrantyKmHint": "Required — this warranty has a distance limit. Type what the odometer shows right now.",
      "jc.reopenWarrantyKmLabel": "Current odometer reading",
      "jc.reopenWarrantyKmRequiredMsg": "Enter the current odometer reading to continue",
      "jc.reopenWarrantyLoadingServices": "Loading covered services…",
      "jc.reopenWarrantyOverrideHint": "Recorded on the reopen — who overrode this and the reading that was refused.",
      "jc.reopenWarrantyOverrideLabel": "Override and reopen anyway",
      "jc.reopenWarrantyOverrideReasonLabel": "Reason for the override",
      "jc.reopenWarrantyOverrideReasonRequiredMsg": "Enter a reason for the override",
      "jc.reopenWarrantyServiceLabel": "Covered service",
      "jc.reopenWarrantyServicePlaceholder": "Select the covered service",
      "jc.reopenWarrantyServiceRequiredMsg": "Please select which covered service this claim is against",
      "wty.accessDenied": "You don't have access to warranties",
      "wty.accessDeniedDesc": "Ask an administrator to grant the \"View warranties\" permission.",
      "wty.activate": "Activate warranty",
      "wty.activateFailed": "Could not activate the warranty",
      "wty.activateSubmit": "Activate",
      "wty.activateSuccess": "Warranty activated",
      "wty.asOf": "as of {{date}}",
      "wty.cancel": "Cancel",
      "wty.catalog.hint": "Prefills the duration when staff activate a warranty on this service. Leave the duration blank for a distance-only default, or leave everything blank if this service has no default.",
      "wty.catalog.sectionTitle": "Default warranty (optional)",
      "wty.col.activated": "Activated",
      "wty.col.customer": "Customer",
      "wty.col.daysRemaining": "Days remaining",
      "wty.col.expires": "Expires",
      "wty.col.kmRemaining": "Km remaining",
      "wty.col.service": "Service",
      "wty.col.starts": "Coverage start",
      "wty.col.vehicle": "Vehicle",
      "wty.covered": "Covered",
      "wty.customerPartsNote": "Parts supplied by the customer are not covered under this warranty.",
      "wty.daysExpiredAgo_one": "Expired {{count}} day ago",
      "wty.daysExpiredAgo_other": "Expired {{count}} days ago",
      "wty.daysRemaining_one": "{{count}} day remaining",
      "wty.daysRemaining_other": "{{count}} days remaining",
      "wty.dialogTitle": "Activate warranty — {{service}}",
      "wty.duration.day_one": "{{count}} day",
      "wty.duration.day_other": "{{count}} days",
      "wty.duration.month_one": "{{count}} month",
      "wty.duration.month_other": "{{count}} months",
      "wty.duration.year_one": "{{count}} year",
      "wty.duration.year_other": "{{count}} years",
      "wty.empty": "No live warranties",
      "wty.emptyDesc": "Nothing is currently covered under a service warranty.",
      "wty.expiredDistance": "Warranty expired (distance)",
      "wty.expiredKmLimit": "Over the km limit",
      "wty.expiredTime": "Warranty expired",
      "wty.expiresOn": "Expires",
      "wty.expiryPreview": "{{duration}} — expires on {{date}}",
      "wty.featureDisabled": "Service warranty isn't enabled",
      "wty.featureDisabledDesc": "Ask an administrator to turn on the Service warranty feature for this company.",
      "wty.inv.title": "Warranty Coverage",
      "wty.invalidKm": "Enter a whole number greater than zero",
      "wty.invalidValue": "Enter a whole number greater than zero",
      "wty.km": "km",
      "wty.kmHint": "Leave blank for a time-only warranty",
      "wty.kmLabel": "Distance limit (optional)",
      "wty.kmAnchorLabel": "Odometer at activation",
      "wty.kmLimitLabel": "Warranty distance",
      "wty.kmOver": "{{km}} km over the limit (as of {{date}} reading)",
      "wty.kmPlaceholder": "e.g. 5000",
      "wty.kmRemaining": "{{km}} km remaining (as of {{date}} reading)",
      "wty.kmRemainingLabel": "Distance remaining",
      "wty.kmUnknown": "No odometer reading on file yet",
      "wty.kmUndated": "A reading is on file, but it isn't dated",
      "wty.kmValue": "{{km}} km",
      "wty.limitationNote": "Does not cover customer-supplied parts.",
      "wty.loadFailed": "Couldn't load warranties.",
      "wty.mon.kmOver_one": "{{n}} km over",
      "wty.mon.kmOver_other": "{{n}} km over",
      "wty.mon.kmRemaining_one": "{{n}} km left",
      "wty.mon.kmRemaining_other": "{{n}} km left",
      "wty.mon.kmUnknown": "No reading yet",
      "wty.mon.kmUndated": "Reading undated",
      "wty.mon.noKmLimit": "No km limit",
      "wty.mon.noTimeLimit": "No time limit",
      "wty.navTitle": "Warranty Monitor",
      "wty.noKmLimit": "No distance limit — time limit only",
      "wty.noLimitError": "Enter a duration, a distance limit, or both.",
      "wty.noTimeLimit": "No time limit — distance limit only",
      "wty.notCovered": "Not covered",
      "wty.notEligibleBillingOnly": "Billing-only lines aren't covered by a warranty",
      "wty.notEligibleNotCompleted": "Available once this service is marked complete",
      "wty.openJobCard": "Open job card",
      "wty.panelTitle": "Service Warranty",
      "wty.renewPrompt.continueHint": "The clock does not restart — this rework is the garage finishing what it already promised. The original expiry and distance limit stand.",
      "wty.renewPrompt.continueTitle": "Continue the same warranty",
      "wty.renewPrompt.currentLabel": "Current coverage",
      "wty.renewPrompt.intro": "This card was reopened under warranty. Choose what happens to each covered service before closing it again.",
      "wty.renewPrompt.submit": "Continue closing",
      "wty.renewPrompt.submitFailed": "Could not update the warranty. Please try again.",
      "wty.renewPrompt.supersedeHint": "A new promise to the customer — the clock restarts from today and this prints on the next invoice.",
      "wty.renewPrompt.supersedeTitle": "Issue a new warranty",
      "wty.renewPrompt.title": "Continue or renew warranty coverage?",
      "wty.showExpired": "Show expired warranties",
      "wty.status.active": "Active",
      "wty.status.expired": "Expired",
      "wty.subtitle": "Every service warranty that is still live, soonest to expire first.",
      "wty.timeLimitLabel": "Time limit",
      "wty.timeRemainingLabel": "Time remaining",
      "wty.title": "Warranty monitor",
      "wty.unit.day": "day(s)",
      "wty.unit.month": "month(s)",
      "wty.unit.year": "year(s)",
      "wty.unitDay": "Days",
      "wty.unitLabel": "Unit",
      "wty.unitMonth": "Months",
      "wty.unitYear": "Years",
      "wty.unitChoose": "Choose unit",
      "wty.unitRequired": "Choose a duration unit: days, months, or years",
      "wty.correct": "Correct warranty",
      "wty.correctTitle": "Correct warranty — {{service}}",
      "wty.correctReasonLabel": "Reason for correction",
      "wty.correctReasonPlaceholder": "e.g. Duration was entered in months instead of years",
      "wty.correctSubmit": "Save correction",
      "wty.correctSuccess": "Warranty corrected",
      "wty.correctFailed": "Could not correct the warranty",
      "wty.correctNoChange": "Warranty limits have not changed",
      "wty.correctStartHint": "Duration is calculated from the original warranty start date: {{date}}",
      "wty.void": "Void warranty",
      "wty.voidTitle": "Void warranty — {{service}}",
      "wty.voidBody": "This warranty will stop immediately and remain in history. You can activate a new warranty for the service afterwards.",
      "wty.voidReasonLabel": "Reason for voiding",
      "wty.voidSubmit": "Void warranty",
      "wty.voidSuccess": "Warranty voided",
      "wty.voidFailed": "Could not void the warranty",
      "wty.unnamedService": "Service",
      "wty.valueLabel": "Duration",
      "wty.valuePlaceholder": "e.g. 3",
      "wty.whichever": "whichever comes first",
      "wty.mon.kmOver": "{{n}} km over",
      "err.SLOT_FULL": "That time is fully booked.",
      "err.SUB_TASK_DEPENDENCY_CYCLE": "These steps wait on each other in a loop.",
      // Short form shown UNDER the field, where the label already named it.
      // Optional per code: fieldErrorMap falls back to the full err.* sentence.
      "err.field.BRANCH_NAME_TAKEN": "Another branch of this company already uses this name",
      "err.field.REQUIRED_FIELD": "Required",
      "err.field.REQUIRED_SELECTION": "Choose a value",
      "err.field.REQUIRED_FILE": "Attach a file",
      "err.field.LAST_UNLIMITED_APPROVER": "At least one user must stay unlimited so someone can approve.",
      "err.field.REQUIRED_REASON": "Write the reason",
      "err.field.REASON_REQUIRED": "Write the reason",
      "err.field.WAIVER_REASON_REQUIRED": "Write the reason",
      "err.field.VALIDATION_FAILED": "Wrong value",
      "err.field.FIELD_INVALID": "Wrong value",
      "err.field.INVALID_INPUT": "Wrong value",
      "err.field.FIELD_MUST_BE_ARRAY": "Wrong value",
      "err.field.FIELD_OPTION_INVALID": "Not one of the allowed choices",
      "err.field.FIELD_TOO_LONG": "Too long",
      "err.field.FIELD_OUT_OF_RANGE": "Outside the allowed range",
      "err.field.FIELD_MUST_BE_NUMBER": "Write a number",
      "err.field.DATE_INVALID": "Wrong date",
      "err.field.DATE_RANGE_INVALID": "Wrong date range",
      "err.field.AMOUNT_POSITIVE_REQUIRED": "Write an amount above zero",
      "err.field.AMOUNT_NON_NEGATIVE_REQUIRED": "Cannot be less than zero",
      "err.field.ALREADY_EXISTS": "Already used",
      "err.field.INVALID_REFERENCE": "Not found",
      "err.field.MUTUALLY_EXCLUSIVE_FIELDS": "Cannot be used with the other field",
      "err.field.TOO_MANY_ITEMS": "More than the allowed number",
      "err.ACCOUNT_CODE_INVALID": "Account code must use only allowed characters and be 50 characters or fewer.",
      "err.DOCUMENT_NOT_FOUND": "The requested document does not exist.",
      "err.DOWNPAYMENT_ARTIFACT_PENDING":
        "Your approval was recorded, but the payment document could not be created yet. Please retry shortly — you will not be charged twice.",
      "err.APPROVAL_NOT_RECORDED_RETRY":
        "Your approval could not be saved and nothing was changed. Please try again — you have not been charged.",
      "err.ACCOUNT_INACTIVE": "An inactive account cannot be the default.",
      "err.ACCOUNT_IS_DEFAULT": "A default account is replaced by promoting another account, never cleared.",
      "err.ACCOUNT_NOT_EMPTY": "This account still holds money. Transfer the balance to another account first, then deactivate it.",
      "err.ACTION_NOT_ALLOWED": "This action is not allowed.",
      "err.AI_QUOTA_EXCEEDED": "This company has used its monthly AI allowance. Contact MotorMind to raise it.",
      "err.AI_QUOTA_SUPER_ADMIN_ONLY": "AI limits are set by MotorMind.",
      "err.ALREADY_EXISTS": "A matching record already exists.",
      "err.ALREADY_TENANT": "This mobile number already belongs to a MotorMind account.",
      "err.AMBIGUOUS_IDENTIFIER": "Send exactly one identifier, not several.",
      "err.INVALID_CR_NUMBER": "The commercial registration number is invalid.",
      "err.INVALID_VAT_NUMBER": "The VAT registration number is invalid.",
      "err.AMOUNT_INVALID": "Amount must be a valid number.",
      "err.AMOUNT_NON_NEGATIVE_REQUIRED": "Amount must be zero or more.",
      "err.AMOUNT_POSITIVE_REQUIRED": "Amount must be greater than zero.",
      "err.ANNOUNCEMENT_INVALID": "Announcement request is invalid.",
      "err.ANNOUNCEMENT_NOT_FOUND": "That announcement no longer exists.",
      "err.ANNOUNCEMENT_ENDED": "That announcement has ended. Republish it before sending it on WhatsApp.",
      "err.CATEGORY_UNIT_IN_USE": "Stock is still held in the current unit. Run it down before changing the unit.",
      "err.PART_UNIT_IN_USE": "This part still has stock on the shelf. Bring it to zero before changing how it is measured — the quantity does not convert itself.",
      "deployNotice.title": "Send a notice to this list",
      "deployNotice.hint": "One WhatsApp message from the linked platform phone, to everyone above. Sent immediately — there is no draft to come back to.",
      "deployNotice.placeholder": "What is in this build, and what should be tested…",
      "deployNotice.count": "{{n}} of {{max}} characters",
      "deployNotice.send": "Send now",
      "deployNotice.sent": "Sent to {{n}} — {{failed}} failed",
      "deployNotice.failed": "The message did not send. Check the WhatsApp link and try again.",
      "err.DEPLOY_NO_RECIPIENTS": "Nobody is on this list yet. Add someone above, then send.",
      "err.DEPLOY_EVENT_INVALID": "Choose which team this notice goes to.",
      "err.DEPLOY_NOTICE_FAILED": "The message did not reach anyone. Check the WhatsApp link and try again.",
      "err.DEPLOY_RECIPIENTS_UNAVAILABLE": "Could not read the recipient list. Try again in a moment."
      ,"err.CATEGORY_IN_USE": "Parts are still filed under this category."
      ,"err.CATEGORY_NAME_IN_USE": "Parts are already filed under that name. Renaming onto it would merge two catalogues."
      ,"err.QTY_MUST_BE_WHOLE": "This part is counted in whole units. Enter a whole number."
      ,"err.NOT_FOUND_CATEGORY": "That category no longer exists."
      ,"err.PART_CATEGORY_INVALID": "Check the highlighted fields.",
      "err.DEPLOY_EVENT_INVALID": "That deploy event is not one this server knows.",
      "err.ASSIGNEE_NOT_IN_BRANCH": "This user is not linked to this branch. Link them to the branch to assign work to them.",
      "err.ASSISTANT_DATA_INVALID": "Assistant data must be a valid JSON object.",
      "err.ASSISTANT_DATA_TOO_LARGE": "Assistant data payload is too large.",
      "err.ASSISTANT_GL_NOT_AVAILABLE": "The general ledger is not available to you: the feature is off for this company, or you do not have journal-entry access.",
      "err.ASSISTANT_GL_WRITE_FORBIDDEN": "The assistant prepares journal entries as DRAFTS only. Posting an entry, closing or reopening a period, and signing off opening balances stay with the accountant.",
      "err.ASSISTANT_MESSAGES_INVALID": "Assistant messages are invalid.",
      "err.ATTACHMENT_INVALID": "Attachment is invalid.",
      "err.ATTACHMENTS_NOT_A_LIST": "Attachments must be sent as a list.",
      "err.ATTACHMENT_URL_INVALID": "That file was not uploaded through MotorMind, so it was not saved. Upload the document again.",
      "err.AUDIO_OR_TEXT_REQUIRED": "Provide an audio recording or text.",
      "err.AUTH_PASSWORD_INVALID": "Password input is invalid.",
      "err.BANK_SARIE_INVALID": "SARIE bank code must be four letters.",
      "err.BANK_STATEMENT_BALANCE_INVALID": "Bank statement balances are invalid.",
      "err.BNPL_NOT_ELIGIBLE": "Buy-now-pay-later is not available for this order.",
      "err.BRANCH_INVALID": "Branch is invalid.",
      "err.BRANCH_REQUIRED": "Branch is required.",
      "err.BUILDING_NUMBER_INVALID": "Building number must be exactly 4 digits.",
      "err.CAR_HANDED_OVER_USE_RETURN_FLOW": "This car was handed over to the garage. Close it using the return flow.",
      "err.CASH_ACCOUNT_INVALID": "Cash account is invalid.",
      "err.CASH_TRANSFER_INVALID": "Cash transfer requires two valid branch accounts.",
      "err.COMPANY_CONTEXT_REQUIRED": "Company context is required.",
      "err.CONFIG_INVALID": "Configuration is invalid.",
      "err.CONFIRMATION_STATE_INVALID": "Confirmation is not in a valid state for this action.",
      "err.CREDIT_ACTION_REQUIRED": "The customer's credit is more than this invoice. Choose whether to keep it on account or refund it.",
      "err.CREDIT_REASON_REQUIRED": "A written credit-release reason is required.",
      "err.CR_NUMBER_INVALID": "Commercial registration number must be exactly 10 digits.",
      "err.LAST_UNLIMITED_APPROVER": "This change would leave the company with no one who can approve. Give another user unlimited approval and the approve permission first.",
      "err.BUYER_ID_REQUIRED": "A business customer needs a VAT number, or an identifier number with its type.",
      "err.ID_NUMBER_INVALID": "The identifier number does not match its type.",
      "err.ID_TYPE_INVALID": "The identifier type is not one of the ZATCA buyer identifier types.",
      "err.CUSTOMER_INVALID": "Customer is invalid.",
      "err.CUSTOMER_PROVIDED_REQUIRES_CUSTOM_PART": "Customer-provided stock parts must be added as custom parts.",
      "err.CUSTOM_REJECTION_REASONS_INVALID": "Custom rejection reasons are invalid.",
      "err.DATE_INVALID": "Date must be valid.",
      "err.DATE_RANGE_INVALID": "Date range is invalid.",
      "err.DAY_CLOSED": "The branch is closed on the chosen day.",
      "err.DEFAULT_PART_SOURCE_INVALID": "Provide either a catalog part or a custom part name, not both.",
      "err.DELAY_FEE_ALREADY_BILLED": "Some delay days after that date are already on an invoice. Reverse or credit-note that invoice first, then set the received date.",
      "err.DELAY_FEE_GATE_UNAVAILABLE": "Could not check outstanding delay fees — the vehicle was NOT released. Please try again.",
      "err.DELAY_FEE_OUTSTANDING": "This job card still has unpaid delay fees.",
      "err.DELAY_TERMS_NOT_ACCEPTED": "Delay terms must be accepted before continuing.",
      "err.DELIVERY_CODE_INVALID": "Delivery code is invalid.",
      "err.DEPENDENCY_NOT_ON_JOB_CARD": "Dependency task is not on this job card.",
      "err.DIAGNOSIS_STATE_INVALID": "Diagnosis is not in a valid state for this action.",
      "err.EMAIL_ALREADY_REGISTERED": "Email address is already registered in this portal.",
      "err.EMAIL_INVALID": "Invalid email format.",
      "err.EMPLOYEE_CONTRACT_INVALID": "Employee contract information is invalid.",
      "err.EXPENSE_ACCOUNT_REQUIRED": "Choose the account this expense belongs to, so it reaches the right place in the books.",
      "err.EXPENSE_INVALID": "Expense is invalid.",
      "err.EXPORT_IN_FLIGHT": "This company already has an export in progress.",
      "err.EXPORT_RATE_LIMIT": "Only one data export per company is allowed in this period.",
      "err.EXPORT_REVOKED": "Data export has been disabled for this company. Contact MotorMind support.",
      "err.EXTRACTION_FAILED": "AI extraction failed for every photo.",
      "err.SCAN_META_INVALID": "The scan details sent with this record are not valid.",
      "err.STREAM_FAILED": "The invoice scan was interrupted. Try again.",
      "err.FEE_ALREADY_BILLED": "This fee is already on an invoice. Reverse it with a credit note rather than waiving it.",
      "err.FIELD_INVALID": "Check the highlighted fields.",
      "err.FIELD_MUST_BE_ARRAY": "Field must be an array.",
      "err.FIELD_MUST_BE_BOOLEAN": "Field must be true or false.",
      "err.FIELD_MUST_BE_NUMBER": "Field must be a number.",
      "err.FIELD_MUST_BE_OBJECT": "Field must be an object.",
      "err.FIELD_OPTION_INVALID": "Field option is invalid.",
      "err.FIELD_OUT_OF_RANGE": "Field value is out of range.",
      "err.FIELD_TOO_LONG": "Field value is too long.",
      "err.FIELD_TOO_SHORT": "Field value is too short.",
      "err.FILE_EMPTY": "File is empty.",
      "err.FILE_UNREADABLE": "Uploaded file could not be read.",
      "err.GARAGE_CONTEXT_REQUIRED": "Garage context is required.",
      "err.HOURS_OVERRIDE_NOT_VALID_WITH_SUB_TASKS": "Hours override is not valid while sub-tasks exist.",
      "err.IBAN_INVALID": "IBAN is invalid.",
      "err.INVALID_DOCUMENT_DESIGNS": "These document design settings are not valid. Pick one of the listed designs for each document.",
      "err.INVALID_INVOICE_BANK": "These invoice bank details are not valid. Check the IBAN and the account holder name.",
      "err.INVALID_STAMP": "This stamp file can't be used. Upload a PNG image of 1 MB or less, at most 2000 × 2000 pixels.",
      "err.INSURER_INVALID": "Insurer is invalid.",
      "err.INVALID_ATTACHMENT": "Attachment selection is invalid.",
      "err.INVALID_CODE": "That sign-in code is not right. Check the latest message and try again.",
      "err.INVALID_DELAY_TERMS": "Delay terms are invalid.",
      "err.INVALID_DEVICE_METADATA": "Device name or app version is too long.",
      "err.INVALID_EXPECTED_AT": "Expected date is invalid.",
      "err.INVALID_INTERVAL": "Interval is invalid.",
      "err.INVALID_MOBILE_NUMBER": "Mobile number is invalid.",
      "err.INVALID_PHONE": "WhatsApp phone number is invalid.",
      "err.INVALID_PLATFORM": "Device platform must be iOS or Android.",
      "err.INVALID_QC_RESULT": "Quality-check result is invalid.",
      "err.INVALID_RECEIVED_AT": "Received date is invalid.",
      "err.INVALID_REFERENCE": "Referenced record is invalid.",
      "err.INVALID_SUB_TASK_STATUS": "Sub-task status is invalid.",
      "err.INVALID_TOKEN": "Push registration token is missing or too long.",
      "err.INVENTORY_LOCATION_INVALID": "Inventory location is invalid.",
      "err.JOB_CARD_BRANCH_INVALID": "Job card branch is invalid.",
      "err.JOB_CARD_CLOSED": "Job card is closed.",
      "err.JOB_CARD_NOT_APPROVABLE": "This job card is closed — the customer could no longer approve the quotation.",
      "err.JOB_CARD_NOT_LIVE": "This job card is no longer open.",
      "err.JOB_CARD_NOT_ON_HOLD": "Job card is not on hold.",
      "err.JOB_CARD_STATE_INVALID": "Job card is not in a valid state for this action.",
      "err.LIMIT_BELOW_USAGE": "The new limit is below what this company is already using.",
      "err.LIMIT_FILE_SIZE": "This file is too large. Choose a smaller one.",
      "err.LIMIT_FILE_COUNT": "Too many files. Attach fewer.",
      "err.LIMIT_UNEXPECTED_FILE": "That file could not be accepted here.",
      "err.UPLOAD_TYPE_NOT_ALLOWED": "This file type is not allowed here.",
      "err.MEDIA_INVALID": "Media is invalid.",
      "err.TOO_LARGE": "That file is larger than this upload allows.",
      "err.INSPECTION_RESULT_NOT_FOUND": "Checklist point not found.",
      "err.INVOICE_FILE_URL_INVALID": "The invoice file must be one uploaded in MotorMind, so it was not saved. Upload the file again.",
      "err.MEDIA_DIRECT_UPLOAD_DISABLED": "Not found.",
      "err.MEDIA_REBIND_FAILED": "A document attached to this draft could not be transferred, so nothing was created. The draft is unchanged — remove and re-add that document, then try again.",
      "err.MOBILE_AUTH_NOT_CONFIGURED": "Mobile sign-in is temporarily unavailable. Please try again later.",
      "err.MONEY_COLLECTED_USE_RETURN_FLOW": "This job card has money collected on it. Close it using the return flow.",
      "err.MUTUALLY_EXCLUSIVE_FIELDS": "Fields are mutually exclusive.",
      "err.NOT_A_QUALITY_CHECK_SUB_TASK": "Sub-task is not a quality check.",
      "err.NOT_QIWA_CONTRACT": "File is not a Qiwa contract.",
      "err.NO_ACCOUNT": "No cash account exists for this transfer.",
      "err.NO_BILLABLE_ITEMS": "No billable items remain for invoicing.",
      "err.NO_CHANGES_PROVIDED": "No changes were provided.",
      "err.NO_EDITABLE_FIELDS": "No editable fields were provided.",
      "err.NO_FEES_OWED": "This job card has no outstanding delay fees to release on credit.",
      "err.NO_HISTORY": "Vehicle has no service history yet.",
      "err.NO_INSURER_CONFIGURED": "No active insurer is configured.",
      "err.NO_PHOTOS": "No photo was uploaded.",
      "err.NO_TERM": "Subscription has no term to renew.",
      "err.NO_VALID_ATTACHMENTS": "No valid attachments were provided.",
      "err.OPENING_BALANCE_NEGATIVE": "Opening balance cannot be negative. A till cannot hold less than nothing. If this account has no cash movements yet, enter a non-negative figure to correct it; once movements exist, leave its opening balance unchanged instead, and ask an owner or company admin to correct the reported balance with a Money in under the \"Capital injection\" reason, or a Money out under the \"Owner drawing\" reason.",
      "err.OPENING_LOCKED": "Opening balance is locked because movements already exist. To correct it now, ask an owner or company admin to record a Money in under the \"Capital injection\" reason to add money, or a Money out under the \"Owner drawing\" reason to remove it.",
      "err.OPENING_LOCKED_BANK": "Opening balance is locked because movements already exist. A bank account's opening balance cannot be fixed with a cash movement once locked — a same-day Money in or Money out only leaves a permanently unmatched line in Bank Reconciliation. Ask an owner or company admin to reconcile the account and record the difference in its lock note instead.",
      "err.OPENING_SIGNED_OFF": "Opening balance is locked because opening balances are signed off. To correct it now, ask an owner or company admin to record a Money in under the \"Capital injection\" reason to add money, or a Money out under the \"Owner drawing\" reason to remove it.",
      "err.OPENING_SIGNED_OFF_BANK": "Opening balance is locked because opening balances are signed off. A bank account's opening balance cannot be fixed with a cash movement once locked — a same-day Money in or Money out only leaves a permanently unmatched line in Bank Reconciliation. Ask an owner or company admin to reconcile the account and record the difference in its lock note instead.",
      "err.OPEN_JOB_CARD_EXISTS": "This vehicle already has an open job card.",
      "err.OTP_EXPIRED": "OTP has expired.",
      "err.OTP_INVALID": "Verification code is invalid. {{remaining}} attempt(s) remaining.",
      "err.OTP_REQUIRED": "OTP is required.",
      "err.OTP_SEND_FAILED": "The verification code could not be sent. Please try again.",
      "err.OTP_TOO_MANY_ATTEMPTS": "Too many OTP attempts.",
      // SEC-01 (review F2) — backend PHOTO_URL_INVALID, see utils/safeUrl.js.
      "err.PHOTO_URL_INVALID": "The photo must be an image uploaded in MotorMind or an https:// link.",
      "err.PARENT_TASK_NOT_ON_JOB_CARD": "Parent task is not on this job card.",
      "err.PART_HAS_NO_DEADLINE": "Part has no delay deadline.",
      "err.PART_INVALID": "Part is invalid.",
      "err.PART_UNIT_INVALID": "Part unit is invalid.",
      "err.PAYMENT_AMOUNT_INVALID": "Payment amount is invalid.",
      "err.PAYMENT_EXCEEDS_BALANCE": "Payment exceeds the remaining balance.",
      "err.PAYMENT_METHOD_INVALID": "Payment method is invalid.",
      "err.PAYMENT_METHOD_UNAVAILABLE": "Payment method is unavailable for this payment.",
      "err.PAYMENT_REFUND_INVALID": "Payment refund request is invalid.",
      "err.PAYMENT_TOKEN_REQUIRED": "Payment token is required.",
      "err.PAYMENT_VERIFICATION_FAILED": "Payment verification failed.",
      "err.PAYOUT_INVALID": "Payout request is invalid.",
      "err.PERIOD_INVALID": "Period must be valid.",
      "err.PERMISSION_DENIED": "You do not have permission to do this. Ask your administrator for access.",
      "err.PERMISSION_DENIED_named": "You do not have the “{{permission}}” permission. Ask your administrator for access.",
      "err.PHONE_INVALID": "Phone number is invalid.",
      "err.PACKAGE_PART_INVALID": "This service does not include its parts in its price.",
      "err.PACKAGE_PART_NOT_IN_TEMPLATE": "This part is not one of the package parts.",
      "err.PACKAGE_PART_QTY_EXCEEDED": "This quantity is more than the package includes.",
      "err.PACKAGE_NOT_APPROVED": "Package parts are added once the package is approved.",
      "err.PACKAGE_PARTS_MISSING": "Package parts are not on the job card yet: {{list}}. Add them from the package box first (a part with no stock needs a purchase invoice).",
      "err.PACKAGE_HAS_BILLING_ONLY_LINES": "This service has billing-only lines on {{count}} job card line(s), so it cannot become a package.",
      "err.PART_NUMBER_TOO_LONG": "The part number is too long — 100 characters at most.",
      "err.PHONE_REQUIRED": "Phone number is required.",
      "err.PHOTO_REQUIRED": "At least one photo is required.",
      "err.PLAN_BRANCH_LIMIT": "Branch limit reached for this subscription plan.",
      "err.PLAN_USER_LIMIT": "User limit reached for this subscription plan.",
      "err.PLATE_INVALID": "Invalid plate letters: {{letters}}. Allowed letters: {{allowed}}.",
      "err.POSTAL_CODE_INVALID": "Postal code must be exactly 5 digits.",
      "err.PURCHASE_INVOICE_LINE_INVALID": "Purchase invoice line is invalid.",
      "err.PURCHASE_INVOICE_STATE_INVALID": "Purchase invoice is not in a valid state for this action.",
      "err.QC_REOPEN_SERVICE_INVALID": "Quality-check reopen service is invalid.",
      "err.QC_REOPEN_TARGET_INVALID": "Quality-check reopen target is invalid.",
      "err.QR_CODE_INVALID": "QR code is invalid.",
      "err.QUANTITY_POSITIVE_REQUIRED": "Quantity must be greater than zero.",
      "err.QUOTATION_EXPIRED": "Quotation has expired.",
      "err.ITEM_CUSTOMER_APPROVED_LOCKED": "The customer approved {{accepted_quantity}} × {{accepted_unit_price}} for this line. You can lower it; raising the price or quantity, or renaming it, needs a new quotation approval.",
      "err.QUOTATION_CUSTOMER_APPROVED_LOCKED": "The customer has approved this quotation, so it can no longer be edited, re-sent or deleted. Add the new work on the job card and send it for approval.",
      "err.INVOICE_LINE_EXCEEDS_CUSTOMER_APPROVAL": "{{count}} invoice line(s) are priced above what the customer approved. Lower them to the approved price, or send a new quotation first.",
      "err.INVOICE_LINE_NOT_AGREED": "{{count}} invoice line(s) were refused by the customer and can't be billed. Remove them, or send them to the customer for approval again.",
      "err.QUOTATION_STATE_INVALID": "Quotation is not in a valid state for this action.",
      "err.REASON_REQUIRED": "Reason is required.",
      "err.RECEIVED_AT_IN_FUTURE": "Received date cannot be in the future.",
      "err.RECEIPT_CREDIT_SHORTFALL": "Not enough receipt credit was available on the selected receipts to cover this payment.",
      "err.REQUIRED_FIELD": "Required field is missing.",
      "err.NO_RUNNING_JOB": "There is no AI generation running to cancel.",
      "err.REQUIRED_FILE": "Required file is missing.",
      "err.REQUIRED_REASON": "Required reason is missing.",
      "err.REQUIRED_SELECTION": "Required selection is missing.",
      "err.ROLE_INVALID": "Role is invalid.",
      "err.SERVICE_NOT_APPROVED": "Service is not approved.",
      "err.SERVICE_NOT_BOOKABLE": "This service cannot be booked at this branch.",
      "err.SERVICE_TEMPLATE_INVALID": "Service template is invalid.",
      "err.SERVICE_TYPE_INVALID": "Service type is invalid.",
      "err.SESSION_NOT_CONNECTED": "Session is not connected.",
      "err.SMS_BUDGET_EXHAUSTED": "The daily mobile sign-in SMS limit has been reached. Please try again later.",
      "err.STATE_ALREADY_SET": "Record is already in this state.",
      "err.STATE_INVALID": "Record is not in a valid state for this action.",
      "err.STOCK_CONSUMED": "Cannot reverse — some parts from this invoice have already been used.",
      "err.STOCK_INSUFFICIENT": "Insufficient stock.",
      "err.PART_REQUEST_RELINKED": "This part request was moved to another job card or part. Refresh and try again.",
      "err.PART_REQUEST_INVOICE_OTHER_BRANCH": "This job's draft invoice belongs to another branch, so the request cannot be approved here.",
      "err.BRANCH_COMPANY_MISSING": "This branch is not linked to a company, so part requests cannot be answered here. Contact support.",
      "err.STOCK_LINE_INVALID": "Stock line is invalid.",
      "err.STOCK_REQUIRES_PURCHASE_INVOICE": "Stock movement requires a purchase invoice.",
      "err.SUB_TASK_NAME_REQUIRED": "Sub-task name is required.",
      "err.SUPERSEDED": "A newer confirmation link was sent to your phone. Please use the latest one.",
      "err.SUPPLIER_REQUIRED": "Supplier is required.",
      "err.WARRANTY_MONTHS_INVALID": "Warranty must be a whole number of months between 1 and 600",
      "err.WA_NOT_CONNECTED": "WhatsApp is not linked. Scan the QR code first, then send the test again.",
      "err.WA_SEND_FAILED": "WhatsApp accepted the link but refused the message. Try re-linking the phone.",
      "err.WA_CLOUD_INVALID_ID": "The WhatsApp Business account ID and phone number ID must be digits only.",
      "err.WA_CLOUD_INVALID_CREDENTIALS": "Meta did not accept these details. Check the access token, app secret and IDs, then try again.",
      "err.WA_CLOUD_PHONE_IN_USE": "This phone number is already connected to another workshop.",
      "err.WA_QR_PAIRING": "A QR pairing is in progress. Finish or cancel it before connecting the WhatsApp Business API.",
      "err.WA_QR_CLOUD_ACTIVE": "This workshop uses the WhatsApp Business API. Remove that connection before linking a phone by QR.",
      "err.WA_CLOUD_WINDOW_CLOSED": "More than 24 hours have passed since the customer's last message. You can reply once they message you again.",
      "err.WA_ACCOUNT_NOT_FOUND": "This WhatsApp account was not found.",
      "err.WA_UNSUBSCRIBE_PENDING": "Meta did not confirm the removal yet. Try removing the account again in a minute.",
      "err.LINE_NOT_ACCESSIBLE": "This WhatsApp line is not assigned to your branch. Select a line available to your branch.",
      "err.LINE_NOT_FOUND": "The selected WhatsApp line was not found. Please choose an active line.",
      "err.LINE_REQUIRED": "Please select a WhatsApp line to send this message or campaign from.",
      "err.NO_LINES_ACCESSIBLE": "No WhatsApp lines are assigned to your branch. Ask an administrator to assign a line.",
      "err.NO_LINE_AVAILABLE": "No WhatsApp line is currently available for this branch. Connect or assign a line in WhatsApp settings.",
      "err.WA_ACCOUNT_INVALID": "This WhatsApp account is invalid or disconnected. Re-authenticate or reconnect the account.",
      "err.WA_ACCOUNT_IN_USE": "This WhatsApp account is in use by branches or active campaigns. Remove branch assignments or wait for campaigns to finish.",
      "err.WA_CANNOT_ASSIGN_LEGACY_NUMBER": "Cannot assign a branch to a legacy WhatsApp number. Migrate to native multi-number first.",
      "err.WA_CLOUD_FAILED": "WhatsApp Cloud delivery failed. Check your Meta Business account status and templates.",
      "err.WA_CLOUD_SEND_FAILED": "Failed to send message via WhatsApp Cloud API. Try again in a moment.",
      "err.WA_CREDENTIALS_DECRYPT_FAILED": "Could not decrypt WhatsApp credentials. Re-enter your credentials in WhatsApp settings.",
      "err.WA_CREDENTIALS_INVALID_JSON": "WhatsApp credentials format is invalid. Check the JSON payload and save again.",
      "err.WA_GARAGE_NOT_FOUND": "The selected branch was not found. Please refresh the page and select an existing branch.",
      "err.WA_MAIN_LINE_EXISTS": "Cannot set a native default line while a main line exists. Unlink the main line first.",
      "err.WA_MISSING_CREDENTIALS": "WhatsApp credentials are missing. Enter your access token and phone number ID to connect.",
      "err.WA_MULTI_ACTIVATING": "WhatsApp multi-number is activating during the 2-minute switch-over window. Please try again in 2 minutes.",
      "err.WA_MULTI_DISABLED": "WhatsApp multi-number is disabled for your company. Contact support to enable it.",
      "err.WA_MULTI_NO_NATIVE_NUMBER": "Your company has no native WhatsApp numbers configured. Add a number in WhatsApp settings.",
      "err.WA_NATIVE_DEFAULT_EXISTS": "An active native default number already exists. Unset the current default before setting a new one.",
      "err.WA_NOT_NATIVE_ACCOUNT": "This account is not a native WhatsApp account. This action requires a native account.",
      "err.WA_NUMBER_IN_USE": "This WhatsApp number is in use by branches or active campaigns. Unassign it or cancel active campaigns to retire.",
      "err.WA_NUMBER_NOT_ACTIVE": "This WhatsApp number is not active. Activate or reconnect the number before using it.",
      "err.WA_NUMBER_NOT_NATIVE": "This operation is only available for native WhatsApp numbers.",
      "err.WA_NUMBER_NOT_QR": "This WhatsApp number does not use QR code session pairing.",
      "err.WA_PHONE_IN_USE": "This phone number is already registered to another WhatsApp line or account.",
      "err.WA_PHONE_NOT_IN_WABA": "This phone number is not registered under your selected WhatsApp Business Account (WABA).",
      "err.WA_WABA_IN_USE": "This WhatsApp Business Account (WABA) ID is already connected to another company.",
      "err.number_unavailable": "This WhatsApp number is currently unavailable or disconnected. Reconnect the line or choose another line.",
      "err.CHAT_ID_REQUIRED": "Open a chat to see the customer's activity.",
      "err.WA_CLOUD_API_REQUIRED": "Adding a Meta account or number needs the WhatsApp Cloud API feature.",
      "err.WA_NOT_NATIVE_QR": "This number is not linked by QR code.",
      "err.WA_NUMBER_NOT_FOUND": "This WhatsApp number was not found.",
      "err.WA_CLOUD_TEMPLATE_UNKNOWN": "This is not one of the standard WhatsApp templates.",
      "err.WA_CLOUD_TEMPLATE_UNAVAILABLE": "Meta no longer has this template. Contact support.",
      "err.WA_CLOUD_CONNECTION_UNAVAILABLE": "Connect the WhatsApp Business API first, or reconnect it in Settings.",
      "err.WA_CLOUD_REMOVED": "The WhatsApp Business API connection was removed, so this campaign is paused.",
      "err.WA_CLOUD_MEDIA_NOT_SUPPORTED": "Sending files from the inbox is not supported on the WhatsApp Business API yet.",
      "err.WA_CLOUD_CONFIG_ERROR": "The public app address is not configured. Contact support.",
      "err.WA_CLOUD_INTERNAL": "Something went wrong with the WhatsApp Business API connection. Try again.",
      "err.WA_CLOUD_CAMPAIGN_TOO_LONG": "The campaign text is too long for a WhatsApp template (1,024 characters with the greeting and footer).",
      "err.WA_CLOUD_CAMPAIGN_MEDIA": "Campaigns on the WhatsApp Business API are text only for now.",
      "err.WA_CLOUD_CAMPAIGN_ADHOC": "Campaigns on the WhatsApp Business API can only go to saved customers.",
      "err.WA_CLOUD_CAMPAIGN_CONSENT_REQUIRED": "Every customer in the campaign must have agreed to WhatsApp offers and not sent STOP.",
      "err.WA_CLOUD_TEMPLATE_NOT_APPROVED": "Meta has not approved this campaign's message yet.",
      "err.WA_CLOUD_RATE_LIMITED": "Meta paused this campaign for a while. You can send it again in a few minutes.",
      "err.LOSS_REASON_REQUIRED": "This invoice would be issued at a loss. Enter a reason to continue.",
      "err.INVOICE_ZERO_TOTAL": "This invoice has no amount. Add at least one priced item before issuing it.",
      "err.TASK_ASSIGNMENT_INVALID": "Task assignment is invalid.",
      "err.TASK_STATE_INVALID": "Task is not in a valid state for this action.",
      "err.TENANT_CONTEXT_REQUIRED": "Tenant context is required.",
      "err.TERMS_NOT_PUBLISHED": "Published terms are required.",
      "err.TOO_MANY_ITEMS": "Too many items were submitted.",
      "err.TOO_MANY_ATTACHMENTS": "You can attach up to 10 photos at a time.",
      "err.TOO_MANY_RECIPIENTS": "That audience is too large for one WhatsApp send. Narrow it and try again.",
      "err.TOO_SOON": "That time is too soon to book. Please pick a later time.",
      "err.UNKNOWN_QUOTATION_ITEM": "Quotation item is unknown.",
      "err.URL_INVALID": "URL is invalid.",
      "err.VALIDATION_FAILED": "Validation failed. Check the highlighted fields.",
      "err.VAT_NUMBER_INVALID": "VAT number must be 15 digits starting and ending with 3.",
      "err.VAT_NOT_REGISTERED": "This company is not registered for VAT, so an expense cannot record reclaimable input VAT. Enter the amount including VAT instead.",
      "err.VAT_RATE_INVALID": "A company that is not VAT exempt must have a VAT rate above 0. Tick \"VAT exempt\" instead of setting the rate to 0.",
      "err.VEHICLE_INVALID": "Vehicle is invalid.",
      "err.WAIVER_REASON_REQUIRED": "Waiver reason is required.",
      "err.WPS_NO_PAYABLE_LINES": "WPS has no payable lines.",
      "err.WPS_SETTINGS_MISSING": "WPS establishment settings are not configured.",
      "err.YEAR_INVALID": "Year must be a 4-digit year.",
      "err.INVALID_REJECTION_REASONS": "The rejection-reasons list is not valid. Check the entries and try again.",
      "err.CASH_MOVEMENT_DIRECTION_IN": "\"{{reason}}\" is always money coming IN.",
      "err.CASH_MOVEMENT_DIRECTION_OUT": "\"{{reason}}\" is always money going OUT.",
      "err.CASH_MOVEMENT_REASON_REQUIRED": "That account records money that belongs to a partner, so it needs the reason and the partner it belongs to.",
      "err.CASH_REASON_MISSING": "No active “{{reason_name}}” ({{reason_name_ar}}) cash reason is set up for this company, so money recorded without a reason cannot be posted. Choose a reason on the form to record it now, and ask an administrator to re-activate that reason in the company's cash reasons.",
      "err.CASH_MOVEMENT_REASON_ACCOUNT_MISMATCH": "This account and this reason do not describe the same kind of money. Partner money needs a partner reason.",
      "err.PLATE_FORMAT_INVALID": "Plate number format is not valid. Enter up to 4 digits and up to 3 allowed letters.",
      "err.FEATURE_DISABLED": "The \"{{feature}}\" feature is not enabled for this garage.",
      "err.INTAKE_CONFIRMATION_REQUIRED": "The customer must confirm the intake before this job card moves forward. Send the confirmation link, or use Override.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS": "This job card has ended, so no new parts or tasks can be added.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS_closed": "This job card has ended, so no new parts or tasks can be added. Reopen it to add more work.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS_delivered": "This job card has ended, so no new parts or tasks can be added. Reopen it to add more work.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS_delivered_pending_payment": "This job card has ended, so no new parts or tasks can be added. The car was delivered on credit — settle the outstanding balance first.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS_cancelled": "This job card was cancelled, so no new parts or tasks can be added. Create a new job card instead.",
      "err.INTAKE_NOT_APPLICABLE": "This job type has no intake step — a full check or a quick service has no handover to confirm.",
      "err.INVALID_INPUT": "The file was rejected: {{reason}}",
      "err.JOB_CARD_NOT_APPROVABLE": "This work order is closed. Please contact the service centre.",
      "err.MECHANICS_NOT_ALLOWED": "Technicians are not permitted to use \"{{feature}}\" in this garage.",
      "err.CHECKOUT_IN_PROGRESS": "A payment link for this receipt is already being opened. Wait a moment and tap pay again.",
      "err.OPEN_CHECKOUT_SESSION": "This receipt has an open online payment link. Confirm the customer has not already paid online before recording a manual payment.",
      "err.TENANT_PAYMENT_MANAGEMENT_DISABLED": "Payment settings are managed by MotorMind for this account.",
      "err.PAYEE_REQUIRED": "\"{{reason}}\" must name the partner it belongs to. Choose a payee.",
      "err.JOB_CARD_UNPAID_BALANCE": "This job card still has unpaid invoices. Remaining balance: {{amount}} SAR.",
      "err.JOB_CARD_UNQUOTED_WORK": "This job card has new work the customer has not been quoted for. Send a quotation, or remove the work, before handing the car over.",
      "err.SKU_ALREADY_EXISTS": "SKU \"{{sku}}\" is already used by \"{{part}}\". Enter a different SKU.",
      "err.TASK_PARTS_NOT_INSTALLED": "Cannot complete the task — {{count}} part(s) are not marked as installed yet.",
      "err.TASK_SERVICES_INCOMPLETE": "Cannot complete the task — {{count}} service(s) are not marked as done yet.",
      "err.CUSTOMER_EMAIL_MISSING": "This customer has no email address. Add one on the customer record first.",
      "err.CUSTOMER_PHONE_MISSING": "This customer has no phone number. Add one on the customer record first.",
      "err.PARTNER_INVALID": "That person is not an active partner. Add them on the Partners screen first, then record this money against them.",
      "err.TEXT_OR_FILE_REQUIRED": "Type a message or attach a file before sending.",
      "err.unexpected": "Something went wrong. Please try again, and if it keeps happening tell the workshop administrator.",
      // Fallback messages for toast.error(errorText(t, err, 'key')) replacements
      "intake.sendOtpFailed": "Failed to send OTP",
      "intake.invalidOtp": "Invalid OTP",
      "intake.describeIssue": "Please describe the issue",
      "intake.submitFailed": "Failed to submit",
      "intake.confirmationRequired": "Customer confirmation is required before moving this job forward.",
      "public.paymentFailed": "Payment failed. Please try again.",
      "public.paymentCancelled": "Payment was cancelled.",
      "public.invoiceLoadFailed": "Failed to load invoice",
      "public.selectPaymentMethod": "Please select a payment method",
      "public.paymentCreateFailed": "Failed to create payment",
      "public.tamaraCheckoutFailed": "Failed to create Tamara checkout",
      "public.tabbyCheckoutFailed": "Failed to create Tabby checkout",
      "public.payInGarageFailed": "Failed to confirm Pay in Garage",
      "public.paymentUnavailable": "Payment is unavailable. Please contact support.",
      "public.createPaymentFailed": "Failed to create payment",
      "public.approveFailed": "Failed to approve quotation",
      "payouts.nothingGenerated": "Nothing was generated",
      "payouts.generateFailed": "Failed to generate",
      "tnc.loadFailed": "Failed to load T&C",
      "tnc.generateFailed": "Generation failed",
      "tnc.enhanceFailed": "Enhancement failed",
      "tnc.cancelGeneration": "Cancel",
      "tnc.generationCancelled": "Generation cancelled",
      "jc.inspectionPrice": "Inspection price (SAR)",
      "jc.inspectionPricePlaceholder": "e.g. 250",
      "jc.inspectionPriceHint": "You can change this any time until the invoice is paid.",
      "tnc.generatingNote": "AI is writing your T&C — this can take a few minutes.",
      "reportPdf.failed": "Could not generate PDF. Please try again.",
      "vp.insightDisabled": "AI diagnosis is not enabled for this workshop",
      "vp.insightNoHistory": "No service history to summarize yet",
      "vp.insightFailed": "Failed to generate insight",
      "rpt.drawingsCutoverSaveError": "Could not save the cutover date",
      "ins.buildingNumberInvalid": "Building number must be exactly 4 digits",
      "ins.postalCodeInvalid": "Postal code must be exactly 5 digits",
      "insuranceClaim.approvalRefRequired": "Enter the insurer approval reference",
      // General ledger — English (merged from the financials/*.i18n.json fragments)
      "err.GL_ACCOUNT_CODE_DUPLICATE": "An account with this code already exists.",
      "gl.chart.errDuplicateInactive": "Account code {{code}} already exists but is currently deactivated. Reactivate it, or pick another code.",
      "err.GL_ACCOUNT_CODE_INVALID": "An account code must be exactly four digits.",
      "err.GL_RANGE_INVALID": "Dates must be in YYYY-MM-DD format, and \"from\" must not be after \"to\".",
      "err.GL_RANGE_SPANS_FISCAL_YEAR": "This report cannot span a year-end. Pick a range inside one year — This month or This year, for example.",
      "err.RETAINED_EARNINGS_MUST_BE_EQUITY": "Retained earnings must point at an equity account. Prior years' profit is carried onto it, so filing it under revenue or an expense would show every past year as part of this one.",
      "err.GL_GARAGE_INVALID": "Branch must be a branch id or \"all\".",
      "err.GL_GARAGE_UNKNOWN": "That branch was not found.",
      "err.GL_PERIOD_INVALID": "That is not a period. Use YYYYMM, for example 202608.",
      "err.GL_VERIFY_NOT_CLOSED": "Only a closed period can be verified against its snapshot.",
      "err.field.JV_ACCOUNT_INVALID": "This account cannot be posted to.",
      "err.field.CASH_MOVEMENT_REASON_REQUIRED": "Needs a partner reason.",
      "err.field.CASH_MOVEMENT_REASON_ACCOUNT_MISMATCH": "Does not match this reason.",
      "err.field.JV_ACCOUNT_LOCKED": "Locked account — never journallable.",
      "err.FA_ACQUIRED_BEFORE_BOOKS": "Asset cannot be acquired before the books opening date.",
      "err.FA_ACQUIRED_IN_CLOSED_PERIOD": "The acquisition month is closed.",
      "err.FA_FOLD_TARGET_INVALID": "The fold target period is not a postable month.",
      "err.FA_FOLD_TARGET_TAKEN": "Target fold period already has depreciation for this asset.",
      "err.FA_OPENING_REQUIRES_LEGACY_IMPORT": "Assets with opening accumulated depreciation must be imported through the legacy asset import.",
      "err.FA_RETRY": "Something changed while saving — please try again.",
      "err.FA_ASSET_CANCELLED": "This fixed asset was cancelled by its purchase reversal and cannot be changed.",
      "err.FA_LEGACY_ALREADY_BOOKED": "This legacy asset's cost is already booked by its opening journal and cannot be capitalised again.",
      "err.FA_REVERSAL_ASSET_DISPOSED": "A disposed fixed asset cannot be cancelled by reversing its purchase invoice.",
      "err.FA_REVERSAL_BEFORE_LAST_CHARGE": "This asset already has depreciation in or after this month. Date the reversal in a month after its last depreciation charge.",
      "err.PERIOD_NOT_ENDED": "This period has not ended yet. Depreciation can only be run for ended periods.",
      "err.FA_LIFE_INVALID": "The useful life must be a whole number of months, at least 1.",
      "err.FA_SALVAGE_EXCEEDS_COST": "The salvage value cannot be more than the cost.",
      "err.FA_OPENING_ACCUMULATED_INVALID": "Opening accumulated depreciation must be between zero and cost less salvage.",
      "err.FA_GARAGE_FORBIDDEN": "You do not have access to that branch.",
      "err.FA_DISPOSED_ON_INVALID": "Enter a valid disposal date.",
      "err.FA_DISPOSED_ON_FUTURE": "The disposal date cannot be in the future",
      "err.FA_DISPOSED_BEFORE_RECOGNITION": "The disposal date is before the asset was recognised (its acquisition or opening-balance date)",
      "err.FA_DISPOSAL_PROCEEDS_INVALID": "Disposal proceeds must be a whole, non-negative amount.",
      "err.FA_SALE_CREDIT_BLOCKED": "Credit notes on fixed-asset sale invoices are not supported yet.",
      "err.FA_DISPOSAL_GIFT_UNSUPPORTED": "Gift and donation disposals are not supported.",
      "err.FA_DISPOSAL_REASON_INVALID": "Disposal reason must be sale or scrapped.",
      "err.FA_SALE_PROCEEDS_REQUIRED": "A fixed-asset sale requires positive proceeds, net of VAT.",
      "err.FA_SALE_BUYER_REQUIRED": "Choose the asset buyer.",
      "err.FA_SALE_DATE_TODAY_ONLY": "An invoiced sale is dated today.",
      "err.FA_SALE_BUYER_INCOMPLETE": "Complete the B2B buyer name and structured postal address before issuing the asset-sale invoice.",
      "err.FA_SALE_GARAGE_REQUIRED": "Choose an accessible branch for this company-wide asset sale.",
      "err.FA_VAT_RATE_UNSUPPORTED": "Fixed-asset sales support only the configured 0% or 15% VAT paths.",
      "err.FA_SALE_REISSUE_NOT_ELIGIBLE": "This asset-sale invoice is not eligible for reissue.",
      "err.FA_SALE_NOT_PAYABLE": "Only posted fixed-asset sale invoices can receive payment.",
      "err.FA_REMAINING_MONTHS_REQUIRED": "Remaining depreciation period must be at least 1 month, or 0 only when book value equals salvage value.",
      "err.FA_BOOK_VALUE_INVALID": "Book value must be between zero and cost.",
      "err.FA_SALVAGE_EXCEEDS_BOOK_VALUE": "Salvage value cannot exceed book value.",
      "err.FA_ACQUIRED_AFTER_AS_OF": "Asset cannot be acquired after the as-of date.",
      "err.FA_AS_OF_BEFORE_BOOKS": "The as-of month must have ended and be on or after your opening-balance date.",
      "err.FA_AS_OF_PERIOD_INVALID": "The as-of month must be a valid YYYYMM period.",
      "err.PAYROLL_ACCRUAL_ACCOUNT_UNMAPPED": "No account is mapped for {{account}}. Map it in your chart settings, then approve again.",
      "err.PAYROLL_ACCRUAL_ACCOUNT_MISSING": "Your chart of accounts has no row for {{account}}. Add it, then approve again.",
      "err.PAYROLL_ACCRUAL_OUT_OF_ORDER": "Period {{laterPeriod}} is already accrued. Approve {{period}} before it, or reverse {{laterPeriod}} first.",
      "err.PAYROLL_ACCRUAL_LATER_PERIOD_LIVE": "Period {{laterPeriod}} is accrued on top of {{period}}. Reverse it first.",
      "err.INVENTORY_WRITEDOWN_HAS_LATER_ACTIVITY": "This part was received or revalued after the write-down, so restoring the old cost would discard that. Adjust the cost directly instead.",
      "err.FA_DISPOSED_BEFORE_BOOKS": "The disposal date is before your opening-balance date.",
      "err.FA_DISPOSED_ON_AFTER_DEPRECIATION": "This month is already depreciated \u2014 date the disposal in the next month.",
      "err.PAYROLL_ACCRUAL_UNBALANCED": "The payroll accrual does not balance. Contact support — do not re-approve.",
      "err.PAYROLL_PERIOD_INVALID": "The payroll period must be in YYYYMM form.",
      "err.PAYROLL_ACCRUAL_SETTLEMENT_MISSING": "This run has no settlement account, so it cannot be paid yet.",
      "err.GOSI_ACCRUAL_SETTLEMENT_MISSING": "This run has no GOSI settlement account, so the GOSI payment cannot be recorded yet.",
      "err.EOSB_ACCRUAL_SETTLEMENT_MISSING": "This employee has an accrued end-of-service provision with no provision account frozen on the payroll runs, so the settlement cannot be recorded yet.",
      "err.FA_COST_INVALID": "The cost must be more than zero, in whole halalas.",
      "err.FA_CATEGORY_INVALID": "Pick a category for this asset.",
      "err.FA_ALREADY_DEPRECIATED": "Depreciation has already been posted for this asset, so its cost, acquisition date and useful life can no longer change.",
      "err.FA_ALREADY_DISPOSED": "This asset has already been disposed of.",
      "err.PURCHASE_INVOICE_REVERSED_IMMUTABLE": "This purchase invoice has been reversed and can no longer be edited. Its accounting entries are rebuilt from these rows, so a change here would move the original purchase into a different period from its reversal.",
      "err.IDEMPOTENCY_KEY_REUSED": "This retry reference was already used for a different request. Start again.",
      "err.FA_IDEMPOTENCY_KEY_INVALID": "The retry reference must be text, and no longer than 200 characters.",
      "err.FA_LINKED_TO_PURCHASE": "This asset is capitalised from a posted purchase invoice, so its accounting fields cannot be changed. Unlink the purchase line or reverse the invoice first.",
      "err.FA_IMPORT_ROW_FAILED": "This row could not be imported.",
      "err.ASSET_LINE_REQUIRES_POSTING": "An invoice that buys a fixed asset has to be posted when you create it, not saved as a draft.",
      "err.FA_ACQUIRED_ON_INVALID": "The acquisition date must be a real date.",
      "err.FA_ACQUIRED_ON_AFTER_INVOICE": "An asset cannot have been acquired after the invoice that bought it.",
      "err.FA_ACQUIRED_ON_REQUIRED": "Enter the original purchase date — the depreciation schedule counts from it.",
      "err.FA_OPENING_ACCUMULATED_REQUIRED": "Say how much depreciation has already been charged. Enter zero if none.",
      "err.FA_GARAGE_UNKNOWN": "Branch not found.",
      "err.FA_IMPORT_EMPTY": "There is nothing to import.",
      "err.FA_IMPORT_INVALID": "Some rows could not be imported, so nothing was saved. Fix the rows listed below and upload the file again.",
      "err.FA_LINK_ALREADY_LINKED": "That purchase line is already linked to another fixed asset.",
      "err.FA_LINK_ASSET_LINKED": "This asset is already linked to a purchase line. Unlink that one first.",
      "err.FA_LINK_STOCK_LINE": "A stock line cannot be capitalised — its inventory movement has already been recorded.",
      "err.FA_LINK_INVOICE_DRAFT": "That purchase invoice is still a draft, so it is not a journal document yet. Post it, then link.",
      "err.FA_LINK_INVOICE_REVERSED": "That purchase invoice has been reversed, and its credit note would not reverse the asset side.",
      "err.FA_LINK_PERIOD_CLOSED": "The month that invoice belongs to is closed. Reverse the invoice and re-enter it in an open period.",
      "err.FA_LINK_EXCEEDS_INVOICE": "Capitalising this line would put the asset lines {{excess}} over that invoice, which is worth {{invoiceNet}} net of VAT.",
      "err.FA_LINK_COST_MISMATCH": "That line is worth {{lineCost}} but this asset is registered at {{assetCost}}. Correct one of them before linking.",
      "err.FA_LINK_DEPRECIATED": "This asset has already depreciated, so its purchase link can no longer be undone.",
      "err.field.JV_ACCOUNT_RESTRICTED": "Control account — needs the override.",
      "err.field.JV_FRACTIONAL_HALALA": "At most two decimal places.",
      "err.field.GL_ACCOUNT_CODE_UNKNOWN": "Not in your chart of accounts, or inactive.",
      "err.field.GL_ACCOUNT_CODE_DUPLICATE": "Already used",
      "err.field.GL_ACCOUNT_CODE_INVALID": "Four digits",
      "err.field.GL_ACCOUNT_PARENT_UNKNOWN": "Unknown account",
      "err.field.PERIOD_CLOSED": "Period closed",
      "err.OB_FRACTIONAL_HALALA": "Amounts are whole halalas — a fraction cannot be stored.",
      "err.OB_AMOUNT_NEGATIVE": "An opening amount cannot be negative.",
      "err.OB_AMOUNT_REQUIRED": "Enter an amount for this line.",
      "err.OB_LINE_TYPE_INVALID": "This line's type is not one this screen offers.",
      "err.OB_COUNTERPART_REQUIRED": "Pick the cash account, customer or supplier this line belongs to.",
      "err.OB_SCHEMA_NOT_MIGRATED": "Opening-balance account codes are not available on this database yet. Contact support.",
      "err.OB_PREFILL_ALREADY_BOOKED": "This total already comes from your posted invoices and bills — there is nothing to enter here.",
      "err.OB_CASH_ACCOUNT_HAS_MOVEMENTS": "This account's opening balance is {{amount}} SAR and cannot be changed once cash movements exist. Enter that same amount, or leave this account off the opening balance.",
      "err.field.OB_ACCOUNT_CODE_REQUIRED": "Pick an account",
      "err.GL_ACCOUNT_CODE_UNKNOWN": "That account isn't in your chart of accounts, or it's inactive.",
      "err.GL_ACCOUNT_DEACTIVATE_MAPPED": "This account is still mapped to a document type, so deactivating it would send those documents to suspense. Remap it first.",
      "err.GL_ACCOUNT_IMMUTABLE_POSTED": "This account already carries posted entries, so its code and type can no longer be changed. Rename it instead, or deactivate it and add a new one.",
      "err.GL_ACCOUNT_PARENT_UNKNOWN": "The parent account you picked does not exist.",
      "err.GL_ACCOUNT_TYPE_DIGIT_MISMATCH": "The first digit of the code must match the account type: 1 assets, 2 liabilities, 3 equity, 4 revenue, 5 expenses.",
      "err.GL_CLOSE_BLOCKED_DOCUMENT": "A document in this period is not ready to close. Fix the listed documents, then close again.",
      "err.GL_CLOSE_EARLIER_PERIOD_OPEN": "An earlier period is still open. Periods close in order, oldest first.",
      "err.GL_CLOSE_WARNINGS_PRESENT": "The journal for this period has warnings. Review them and close again with the override, or fix them first.",
      "err.GL_CLOSE_YEAR_END_SUSPENSE_NONZERO": "The year cannot close while the suspense account still holds money. Reclassify it with a journal voucher dated in the year, then close again.",
      "err.GRANT_EXPIRED": "The selection window for this sign-in has expired. Verify a fresh code to continue.",
      "err.DEPRECIATION_UNPOSTED": "Post depreciation through YYYY-MM before closing it",
      "err.ACCOUNT_CODE_UNKNOWN": "This mapping points at an account code the chart of accounts does not have.",
      "err.ALREADY_FINISHED": "This draft is already finished, so it cannot be changed.",
      "err.ALREADY_REVERSED": "This has already been reversed.",
      "err.EXPENSE_POSTED_FIELDS_LOCKED": "This expense is posted, so its amount, VAT, supplier, date and payment method cannot be changed. Reverse it with a credit note and post a new one.",
      "err.EXPENSE_POSTED_USE_REVERSE": "A posted expense cannot be deleted. Reverse it with a credit note instead.",
      "err.REVERSAL_BASIS_INVALID": "Choose why this document is being reversed.",
      "err.REVERSAL_REASON_REQUIRED": "Write why this was entered by mistake.",
      "err.CREDIT_NOTE_PERIOD_CLOSED": "The credit note's month is closed. Post it on today's date instead, or reopen the month.",
      "err.VAT_IN_COST_INVALID": "Choose whether the VAT was reclaimed or added to parts cost.",
      "err.VAT_IN_COST_ALREADY_RECORDED": "This invoice's VAT answer is already recorded, or the invoice is not posted.",
      "err.REVERSAL_SETTLEMENT_REQUIRED": "This document was paid. Choose whether the money was refunded or the supplier keeps it as credit.",
      "err.REVERSAL_SETTLEMENT_INVALID": "The refund choice or refund account is not valid for this document.",
      "err.REVERSAL_SUPPLIER_FIELDS_FORBIDDEN": "A document entered by mistake has no supplier credit note. Remove the credit note details.",
      "err.SUPPLIER_CN_NUMBER_INVALID": "Enter the supplier's credit note number (up to 64 characters).",
      "err.SUPPLIER_CN_DATE_INVALID": "Enter the date shown on the supplier's credit note.",
      "err.SUPPLIER_CN_DATE_FUTURE": "The supplier's credit note date cannot be in the future.",
      "err.SUPPLIER_CN_DATE_BEFORE_DOCUMENT": "The credit note date cannot be before the invoice date.",
      "err.SUPPLIER_CN_ATTACHMENT_REQUIRED": "Attach the supplier's credit note.",
      "err.SUPPLIER_CN_NUMBER_DUPLICATE": "This credit note number is already recorded for this supplier.",
      "err.ALREADY_SETTLED": "End-of-service has already been settled for this employee.",
      "err.BRANCH_NET_NOT_POSITIVE": "A branch on this payroll run has a net pay of zero or less.",
      "err.BEYOND_HORIZON": "That date is too far ahead to book.",
      "err.EMPLOYEE_NET_NOT_POSITIVE": "An employee on this payroll run has a net pay of zero or less.",
      "err.EOSB_CATEGORY_MISSING": "No expense category is set for end-of-service. Add one in expense categories, then try again.",
      "err.GOSI_CATEGORY_MISSING": "No expense category is set for GOSI. Add one in expense categories, then try again.",
      "err.GOSI_EXISTS": "A GOSI bill is already recorded for this period.",
      "err.GOSI_RUN_NOT_FOUND": "No approved or paid payroll run exists for this period.",
      "err.GOSI_RUN_NOT_RECONCILABLE": "Only an approved or paid payroll run can be reconciled with a GOSI bill.",
      "err.JOURNAL_BLOCKED": "The journal cannot be exported while it still has problems. Clear them and try again.",
      "err.LOAN_DISBURSED_AT_INVALID": "Enter a real disbursement date that is not in the future.",
      "err.NOT_APPROVED": "This payroll run must be approved first.",
      "err.NOT_FOUND": "That record no longer exists.",
      "err.INSPECTION_LINK_EXPIRED": "This inspection report link has expired. Ask the workshop to send a new one.",
      "err.INSPECTION_NOT_ISSUED": "This report has not been issued yet, so there is nothing to print.",
      "err.INVALID_DTC_CODE": "That is not a fault code. Codes look like P0301 or P0A80.",
      "err.INVALID_DTC_LIST": "Fault codes must be a list.",
      "err.INVALID_WARNING_LIGHTS": "Warning lights must be a list of keys.",
      "err.INSPECTION_REPORT_MISSING": "The report file could not be read. Issue the report again to rebuild it.",
      "err.INSPECTION_REPORT_UNPAID": "The inspection report is available once the balance on this card is settled.",
      "err.JOB_CARD_NOT_FOUND": "That job card no longer exists, or it belongs to another branch.",
      "err.ASSIGNMENT_STALE": "The card was reassigned since the last sync. Refresh the picker and decide again.",
      "err.INVALID_EXPECTED_ASSIGNEE": "This assignment request is invalid. Refresh and try again.",
      "err.CUSTOMER_NOT_FOUND": "Customer not found in your workshop.",
      "err.NOT_PAID": "Only a paid payroll run can do this.",
      "err.NOT_TERMINATED": "End-of-service can only be settled after the employee is terminated.",
      "err.NO_CONTRACT": "This employee has no contract or hire date, so a settlement cannot be calculated.",
      "err.PAID_TO_CREDIT": "A paid expense cannot be switched to credit. Reverse it instead.",
      "err.RUN_EXISTS": "A payroll run already exists for this period.",
      "err.STALE_RUN_REGENERATE": "This run still deducts loans that no longer exist. Regenerate it before approving.",
      "err.ZAKAT_BASE_BLOCKED": "The journal for this year still has problems, so the Zakat base cannot be derived. Clear them and try again.",
      "err.ZAKAT_NO_COMPANY": "No company is in context for this Zakat report.",
      "err.ZAKAT_YEAR_INVALID": "Enter a four-digit year between 2000 and 2100.",
      "err.EXPENSE_TIMING_INVALID": "Check the spread months and dates on this expense.",
      "err.EOSB_OPENING_NO_EMPLOYEES": "No active employee has a contract and hire date to attribute the opening provision to.",
      "err.GL_SETTING_INVALID": "This accounting setting is out of range. Enter a whole number inside the allowed range.",
      "err.GARAGE_SCOPE_FORBIDDEN": "The requested branch does not belong to your company.",
      "err.TILL_NEGATIVE_BALANCE": "This payment would leave the till below zero. Record money in first, or take it from a bank.",
      "err.NOT_DRAFT": "Only a draft can be changed here. This one has already been posted or approved.",
      "bank.featureDisabled": "Bank reconciliation isn't enabled for this company",
      "bank.featureDisabledDesc": "Ask an administrator to turn on the bank reconciliation feature for your plan.",
      "br.featureDisabled": "Bank reconciliation isn't enabled for this company",
      "br.featureDisabledDesc": "Ask an administrator to turn on the bank reconciliation feature for your plan.",
      "gl.ce.agrees": "Agrees with the balance sheet",
      "gl.ce.checkLine": "Closing equity here must equal assets minus liabilities.",
      "gl.ce.closingTotal": "Closing per this statement",
      "gl.ce.col.movement": "Movement",
      "gl.ce.difference": "Difference",
      "gl.ce.disagrees": "Doesn't agree with the balance sheet",
      "gl.ce.disagreesDesc": "These two figures are computed different ways and must match. They don't, so at least one of them is wrong — don't file on this statement until it's resolved.",
      "gl.ce.face": "Statement of changes in equity",
      "gl.ce.nav": "Changes in equity",
      "gl.ce.perBalanceSheet": "Equity per the balance sheet",
      "gl.ce.resultForPeriod": "Result for the period",
      "gl.ce.resultForPeriodNote": "The same figure as the income statement's result for this window.",
      "gl.ce.subtitle": "Opening equity, what moved it, and where it closed.",
      "gl.ce.title": "Changes in equity",
      "gl.cf.cashAccounts": "Movement by cash account",
      "gl.cf.cashAccountsNote": "The movement over this window per cash and bank account, not the closing balance.",
      "gl.cf.closing": "Cash at the end of the period",
      "gl.cf.methodNote": "Indirect method — the result for the period, adjusted back to cash.",
      "gl.cf.movement": "Movement in cash over the period",
      "gl.cf.nav": "Cash flow",
      "gl.cf.netFor": "Net for this section",
      "gl.cf.opening": "Cash at the start of the period",
      "gl.cf.reconciliation": "Opening cash to closing cash",
      "gl.cf.subtitle": "Where the cash came from and where it went, over the period.",
      "gl.cf.title": "Cash flow statement",
      "gl.cf.unexplained": "Cash movement no section claimed",
      "gl.cf.unexplainedDesc": "Real cash that none of the sections above accounts for — usually a code missing from your chart, or a disposal collected outside this window. It is shown rather than absorbed.",
      "gl.cf.unexplainedZero": "Zero — every riyal of the period's cash movement was claimed by a section above.",
      "gl.close.yearEndSuspense.account": "Suspense account",
      "gl.close.yearEndSuspense.notOverridable": "This one can't be overridden. The monthly “close over the warnings” option doesn't apply at year end — post a journal voucher dated inside the year that reclassifies this balance, then close again.",
      "gl.export.action": "Export journal",
      "gl.export.blockedDesc": "No file was produced. Fix the documents below, then export again — a file whose debits don't equal its credits is one your accountant's package will reject on import.",
      "gl.export.blockedDoc": "Document",
      "gl.export.done": "Journal exported",
      "gl.export.download": "Download",
      "gl.export.format": "File format",
      "gl.export.range": "Period: {{from}} to {{to}}",
      "gl.export.subtitle": "Every document in the selected period, in a format your accountant's package can import.",
      "gl.export.title": "Export the general journal",
      "gl.export.warnings": "Exported, but {{count}} document(s) in the file are sitting on the suspense account. Map them and export again before you hand the file over.",
      "gl.gj.docRefLabel": "Find a document",
      "gl.gj.docRefPlaceholder": "Invoice, receipt or memo text…",
      "gl.is.face": "Statement of comprehensive income",
      "gl.is.nav": "Income statement",
      "gl.is.subtitle": "Revenue, costs and the result for the period — the statement of comprehensive income.",
      "gl.is.title": "Income statement",
      "gl.is.totalComprehensive": "Total comprehensive income",
      "gl.is.totalComprehensiveNote": "Equal to the result for the period: this business holds no revaluation reserve, foreign operation or hedge, so there is no other comprehensive income.",
      "gl.is.unexplainedDesc": "These codes were posted to but aren't in your chart of accounts, so they are NOT part of the result above. Add them to the chart, then refresh.",
      "gl.is.unexplainedTitle": "Accounts your chart doesn't contain",
      "gl.link.viewInLedger": "View in ledger",
      "gl.set.accessDenied": "You don't have access to the accounting settings",
      "gl.set.accessDeniedDesc": "Ask an administrator to grant you the journal entries view permission.",
      "gl.set.featureDisabled": "General ledger isn't enabled for this company",
      "gl.set.featureDisabledDesc": "Ask an administrator to turn on the general ledger feature for your plan.",
      "gl.set.fyDesc": "The month your financial year starts. Everything year-to-date is measured from it.",
      "gl.set.fyMonth": "Fiscal year starts in",
      "gl.set.fyTitle": "Fiscal year",
      "gl.set.fyWarning": "Changing this restates every year-to-date figure in the product, and moves the year-end that period close checks the suspense account against.",
      "gl.set.invalid": "Check the values above before saving.",
      "gl.set.loadFailed": "Couldn't load the accounting settings",
      "gl.set.nav": "Accounting settings",
      "gl.set.provisionOff": "Zero — no warranty provision is posted.",
      "gl.set.provisionRate": "Provision rate",
      "gl.set.provisionRateHint": "Percentage of labour revenue, to two decimals. 2.50 = 250 basis points.",
      "gl.set.readOnly": "You can read these settings but not change them. Changing them restates the books, so it needs the journal entries post permission.",
      "gl.set.saved": "Accounting settings saved",
      "gl.set.subtitle": "The policies the ledger reads: your fiscal year, and how the warranty provision is calculated.",
      "gl.set.title": "Accounting settings",
      "gl.set.warrantyDesc": "A share of labour revenue set aside for rework under warranty, and released when the warranty expires unused.",
      "gl.set.warrantyMonths": "Warranty period",
      "gl.set.warrantyMonthsHint": "Months from the invoice until the unused provision is released. 1 to 60.",
      "gl.set.warrantyTitle": "Warranty provision",
      "gl.stmt.accessDenied": "You don't have access to the financial statements",
      "gl.stmt.accessDeniedDesc": "Ask an administrator to grant you the journal entries view permission.",
      "gl.stmt.blockedDesc": "At least one document doesn't balance, so every figure on this statement would be wrong. Fix the documents below, then refresh.",
      "gl.stmt.blockedDoc": "Document",
      "gl.stmt.blockedTitle": "This period can't be trusted yet",
      "gl.stmt.breakdownNote": "Listed as positive amounts totalling {{total}}; the face of the statement adds or deducts that total.",
      "gl.stmt.col.account": "Account",
      "gl.stmt.col.amount": "Amount",
      "gl.stmt.col.code": "Code",
      "gl.stmt.empty": "Nothing to show for this range",
      "gl.stmt.emptyDesc": "No activity in this period and scope. Try a wider date range.",
      "gl.stmt.export": "Export CSV",
      "gl.stmt.featureDisabled": "General ledger isn't enabled for this company",
      "gl.stmt.featureDisabledDesc": "Ask an administrator to turn on the general ledger feature for your plan.",
      "gl.stmt.loadFailed": "Couldn't load this statement",
      "gl.stmt.noAccounts": "No accounts behind this line",
      "gl.stmt.scopeBranch": "One branch only. Company-level accounts are still included in full.",
      "gl.stmt.scopeCompany": "Whole company — every branch.",
      "gl.stmt.showAccounts": "Show the {{count}} account(s) behind this",
      "gl.stmt.warningsCount": "{{count}} document(s) sitting in suspense — could not be mapped to an account",
      "hr.eosb.accessDenied": "You don't have access to the opening EOSB provision",
      "hr.eosb.accessDeniedDesc": "Ask an administrator to grant you the payroll read permission.",
      "hr.eosb.asOf": "As at",
      "hr.eosb.asOfHint": "Usually your opening-balance date. Each employee's figure is computed from their hire date and contract wage on this day.",
      "hr.eosb.col.amount": "Their share",
      "hr.eosb.col.employee": "Employee",
      "hr.eosb.expected": "Signed-off opening",
      "hr.eosb.gap": "Out by",
      "hr.eosb.got": "This split",
      "hr.eosb.invalidRows": "{{count}} row(s) don't hold a valid amount.",
      "hr.eosb.mismatchDesc": "The rows must add up to the end-of-service figure in your signed-off opening balances. Adjust the shares until the two agree.",
      "hr.eosb.nav": "Opening EOSB split",
      "hr.eosb.noEmployees": "No employees to split across",
      "hr.eosb.noEmployeesDesc": "Only active employees with a hire date and a current contract are included.",
      "hr.eosb.notPreviewed": "Pick a date, then calculate",
      "hr.eosb.notPreviewedDesc": "Nothing is computed until you say which date the provision was opened at — a figure for the wrong day is worse than no figure.",
      "hr.eosb.postCatchUp": "Post the catch-up",
      "hr.eosb.postCatchUpDesc": "Posts the difference between this split and the opening figure to the ledger. This can only be done once.",
      "hr.eosb.posted": "Catch-up posted",
      "hr.eosb.postingDate": "Posting date",
      "hr.eosb.postingDateHint": "Defaults to the date above. That month must be open.",
      "hr.eosb.preview": "Calculate",
      "hr.eosb.readyHint": "The total above must match your signed-off opening provision.",
      "hr.eosb.save": "Save the split",
      "hr.eosb.saved": "Split saved",
      "hr.eosb.splitHeading": "Per employee",
      "hr.eosb.subtitle": "Split the end-of-service provision you opened with across the employees who earned it.",
      "hr.eosb.title": "Opening end-of-service provision",
      "hr.eosb.unnamed": "Unnamed employee",
      "hr.file.loans.disbursedAt": "Paid out on",
      "hr.file.loans.disbursedAtHint": "The day the money left the drawer. Defaults to today.",
      "hr.payroll.accrue.action": "Accrue this run",
      "hr.payroll.accrue.desc": "Book the salary liability for a run approved before accruals existed. Only possible while the run is approved, unpaid and not already accrued.",
      "hr.payroll.accrue.done": "Payroll run accrued",
      "hr.payroll.accrue.postingDate": "Posting date",
      "hr.payroll.accrue.postingDateHint": "Defaults to the last day of the run's own month. That month must be open.",
      "hr.payroll.unbanked.action": "Pay the cash portion",
      "hr.payroll.unbanked.confirm": "Record {{amount}} SAR as paid in cash to the employees WPS couldn't reach. This posts the cash movement — do it once the money has actually been handed over.",
      "hr.payroll.unbanked.heading": "How this run was paid out",
      "hr.payroll.unbanked.headingDesc": "WPS only moves money to employees with an IBAN on file. The rest is paid in cash and has to be recorded separately.",
      "hr.payroll.unbanked.inCash": "In cash",
      "hr.payroll.unbanked.paid": "Cash portion recorded",
      "hr.payroll.unbanked.settledOn": "Cash portion recorded on {{date}}",
      "hr.payroll.unbanked.viaBank": "Through WPS",
      "inv.wd.action": "Write down value",
      "inv.wd.carryingCost": "Carrying cost per unit",
      "inv.wd.done": "Value written down",
      "inv.wd.doneDesc": "{{amount}} SAR came off the carrying value. Each unit is now held at {{unit}} SAR.",
      "inv.wd.doneTitle": "Written down",
      "inv.wd.impact": "Taken off the balance sheet",
      "inv.wd.intro": "Lower what this stock is worth on the books. The quantity on the shelf doesn't change — use Adjust stock for that.",
      "inv.wd.nrv": "New value per unit",
      "inv.wd.nrvHint": "Must be below the carrying cost of {{cost}}.",
      "inv.wd.onHand": "On hand",
      "inv.wd.reason": "Reason",
      "inv.wd.reasonHint": "Why the stock is worth less — damage, obsolescence, a lower market price.",
      "inv.wd.reversed": "Write-down reversed",
      "inv.wd.title": "Write down to net realisable value",
      "inv.wd.undo": "Undo this write-down",
      "inv.wd.undoWindow": "You can undo this here, now. Once you close this dialog there's no way back to it — and once any of this stock leaves the shelf it can't be undone at all.",
      "inv.wd.writtenOn": "Written down on",
      "inv.wd.writtenOnHint": "That month must still be open.",
      "opex.timing.accruedOn": "Accrued on {{date}}.",
      "opex.timing.accruedOnLabel": "Accrued",
      "opex.timing.heading": "When it hits the books",
      "opex.timing.hint": "The expense date above is the day the cost was incurred and never moves. These say when the money leaves and over how long the cost is spread.",
      "opex.timing.paidOn": "Paid on",
      "opex.timing.paidOnBadge": "Paid {{date}}",
      "opex.timing.paidOnHint": "The day the money actually left. Defaults to today; that month must be open.",
      "opex.timing.paidOnLabel": "Paid on",
      "opex.timing.spreadBadge_one": "Over {{count}} month",
      "opex.timing.spreadBadge_other": "Over {{count}} months",
      "opex.timing.spreadDetail_one": "{{count}} month from {{date}}",
      "opex.timing.spreadDetail_other": "{{count}} months from {{date}}",
      "opex.timing.spreadLabel": "Spread",
      "opex.timing.spreadMonths": "Spread over",
      "opex.timing.spreadMonthsHint": "Months this payment covers, 1 to 120. Leave blank to expense it in full.",
      "opex.timing.spreadNone": "Not spread",
      "opex.timing.spreadStart": "Spread starts",
      "opex.timing.spreadStartHint": "The first month a share is released. Defaults to the expense date.",
      "opex.timing.unpaidBadge": "Unpaid",
      "pt.featureDisabled": "Money In/Out isn't enabled for this company",
      "pt.featureDisabledDesc": "Partners exist to be drawn from and contributed to, which needs the Money In/Out feature. Ask an administrator to turn it on for your plan.",
      "rpt.stock.inactiveValue": "Value on deactivated parts",
      "rpt.stock.inactiveValueDesc": "Parts you've deactivated that still hold stock. This is inside the total above and the ledger carries it too — the on-hand screen hides these parts, which is why the two figures differ by exactly this amount.",
      "rpt.stock.writedowns": "Written down to net realisable value",
      "rpt.stock.writedownsDesc": "How much carrying cost has been written off this stock. Already deducted from the value above.",
      "rpt.stock.inTransit": "In transit between branches",
      "rpt.stock.inTransitDesc": "Sent from here and not yet received ({{count}} transfers). Not in the value above.",
      "vat.byTaxCategory": "By ZATCA tax category",
      "vat.glDifference": "The ledger and the transaction rows disagree by {{sar}} SAR ({{halalas}} halalas)",
      "vat.glDifferenceDesc": "The boxes above are the served figures. The line-by-line computation for the same period is shown below — a healthy period has no difference at all.",
      "vat.glWarningDoc": "Document",
      "vat.glWarnings": "{{count}} document(s) the ledger could not read cleanly",
      "vat.glWarningsMore": "…and {{count}} more.",
      "vat.inputNet": "Input net",
      "vat.legacyPrefix": "Line by line:",
      "vat.lines": "Lines",
      "vat.outputNet": "Output net",
      "vat.source.general_ledger": "From the general ledger",
      "vat.source.legacy": "From the transaction rows",
      "vat.source.legacy_gl_unavailable": "General ledger unavailable",
      "vat.sourceDesc.general_ledger": "These figures are derived from the journal — the same source as your financial statements.",
      "vat.sourceDesc.legacy": "The general ledger is off for this company, so VAT is computed line by line from invoices and purchases.",
      "vat.sourceDesc.legacy_gl_unavailable": "The ledger was consulted and could not answer, so these are the line-by-line figures. Don't file on them until the ledger reads cleanly.",
      "vat.taxCategory.exempt": "Exempt",
      "vat.taxCategory.out_of_scope": "Out of scope",
      "vat.taxCategory.standard": "Standard rated",
      "vat.taxCategory.unclassified": "Unclassified",
      "vat.taxCategory.zero_rated": "Zero rated",
      "vat.unattributed": "{{output}} SAR of output VAT and {{input}} SAR of input VAT can't be attributed to a single line — stock-line input VAT is recorded on the document header. That's why the category rows below don't add up to the boxes above.",
      "vat.unclassifiedLines": "{{count}} line(s) have no VAT category",
      "vat.unclassifiedLinesDesc": "These are zero-rate lines whose supply classification was never recorded, so they can't be placed in a ZATCA box. They're counted under \"Unclassified\" below. Set the tax category on the invoice and purchase lines before you file.",
      "err.CASH_ADJUSTMENT_GATED": "This company keeps its books in the general ledger with a signed-off opening balance. Adjust through a journal entry instead.",
      "err.INVOICE_WRITE_OFF_STATE": "Only a posted invoice can be written off. Waive a draft with a discount instead.",
      "err.INVOICE_ALREADY_WRITTEN_OFF": "This invoice has already been written off.",
      "err.INVOICE_WRITE_OFF_NOTHING_OWED": "Nothing to write off — the invoice has no outstanding balance.",
      "err.WRITE_OFF_PERIOD_CLOSED": "That accounting period is closed. Reopen it, or date this entry in an open period.",
      "err.JOURNAL_EXPORT_RANGE_REQUIRED": "An export needs both a from and a to date.",
      "err.JOURNAL_EXPORT_RANGE_INVALID": "Check the export dates: use YYYY-MM-DD and keep the start on or before the end.",
      "err.JOURNAL_EXPORT_ADAPTER_UNKNOWN": "That export format is not available.",
      "err.JOURNAL_EXPORT_BLOCKED": "The journal for this period does not balance, so no file was produced. Fix the listed documents and export again.",
      "err.JOURNAL_RANGE_TOO_WIDE": "Export at most one year at a time.",
      "err.JOURNAL_TOO_LARGE": "Too many entries for one file. Export a shorter period.",
      "err.INVENTORY_WRITEDOWN_INVALID": "NRV, date and reason are required",
      "err.INVENTORY_WRITEDOWN_UNAVAILABLE": "Inventory write-down is unavailable",
      "err.PART_NOT_FOUND": "Part not found",
      "err.INVENTORY_WRITEDOWN_NO_STOCK": "Part has no stock on hand",
      "err.INVENTORY_WRITEDOWN_NOT_LOWER": "NRV must be below carrying cost",
      "err.INVENTORY_WRITEDOWN_NOT_FOUND": "Inventory write-down not found",
      "err.INVENTORY_WRITEDOWN_ALREADY_REVERSED": "Inventory write-down is already reversed",
      "err.INVENTORY_WRITEDOWN_HAS_OUTBOUND": "Cannot reverse after stock has left the shelf",
      "err.PAYROLL_EMPLOYEE_LOANS_ACCOUNT_UNMAPPED": "The employee loans account is not mapped",
      "err.LEAVE_ACCRUAL_SETTLEMENT_MISSING": "The annual leave provision account is missing",
      "err.PAYROLL_RUN_ALREADY_PAID": "A paid payroll run cannot be accrued",
      "err.PAYROLL_RUN_ALREADY_ACCRUED": "This payroll run is already accrued",
      "err.PAYROLL_RUN_PREDATES_OPENING": "This payroll run predates signed-off opening balances",
      "payroll.zeroGosi": "No GOSI remittance was created because the amount is zero",
      "err.EOSB_OPENING_SPLIT_MISMATCH": "The employee EOSB split does not match the signed-off opening provision",
      "err.EOSB_CATCHUP_ALREADY_POSTED": "The EOSB opening catch-up has already been posted",
      "err.OPENING_BALANCE_NOT_SIGNED_OFF": "Opening balances must be signed off first",
      "err.GL_OBLIGATION_EOSB": "The end-of-service provision for this period has not been recorded.",
      "err.GL_OBLIGATION_GOSI": "The GOSI contribution for this period has not been recorded.",
      "err.GL_OBLIGATION_PAYROLL_UNPAID": "A payroll run in this period is approved but not paid.",
      "err.GL_OBLIGATION_VAT_FILING": "The VAT return for this period has not been filed yet.",
      "err.GL_OPENING_LOCKED": "Starting balances are already locked. To change them, use a journal entry.",
      "err.GL_OPENING_UNBALANCED": "Something didn't add up while saving. Please try again or contact support.",
      "err.GL_REOPEN_VAT_FILED": "This period cannot be reopened: its VAT return has already been filed. Post a correction in an open period instead.",
      "err.GL_CLOSE_PERIOD_NOT_ENDED": "This month hasn't ended yet. You can close it once the month is over.",
      "err.GL_PERIOD_ALREADY_CLOSED": "This period is already closed. Reopen it first if you want to close it again.",
      "err.JV_ACCOUNT_INVALID": "An account on this entry is not usable: it is not in this company's chart, is inactive, or is a grouping account.",
      "err.JV_ACCOUNT_LOCKED": "A line uses a locked account. Cash and suspense accounts can never be journalled against.",
      "err.JV_ACCOUNT_RESTRICTED": "A line uses a control account, which needs the control override to be switched on for this entry.",
      "err.JV_ALREADY_REVERSED": "This entry has already been reversed.",
      "err.JV_SYSTEM_OWNED": "This entry was posted by the system, not from the journal screen. A payroll accrual is undone by reversing its payroll run. An opening provision or an end-of-service settlement has no undo screen — post a correcting entry instead.",
      "err.JV_FRACTIONAL_HALALA": "An amount is not a whole number of halalas. Use at most two decimal places.",
      "err.JV_NOT_DRAFT": "This entry is no longer a draft, so it cannot be changed or deleted.",
      "err.JV_NOT_POSTED": "Only a posted entry can be reversed.",
      "err.JV_OVERRIDE_FORBIDDEN": "You do not have permission to override control accounts.",
      "err.JV_PERIOD_CLOSED": "The accounting period for this date is closed, so nothing can be posted into it.",
      "err.JV_TOO_FEW_LINES": "A journal entry needs at least two lines, with at least one debit and one credit.",
      "err.JV_UNBALANCED": "Total debit does not equal total credit.",
      "err.JV_AMOUNT_TOO_LARGE": "An amount is larger than this ledger accepts.",
      "err.field.JV_AMOUNT_TOO_LARGE": "This amount is larger than a single journal line may carry.",
      "err.OB_ACCOUNT_CODE_REQUIRED": "Pick a specific account for this row. \"Other\" needs one \u2014 cash, receivables and payables have theirs built in.",
      "err.OB_ACCOUNT_CODE_CONTROLLED": "That account is a control account \u2014 use its cash, customer or supplier line if there is one, otherwise record it with a manual journal entry instead of \"Other\".",
      "err.OUTSIDE_HOURS": "The chosen time is outside the branch's working hours.",
      "err.OFF_GRID": "Please pick one of the offered times.",
      "err.PERIOD_CLOSED": "That date falls in a closed accounting period. Use a date in an open period, or ask an admin to reopen it.",
      "err.CREDIT_NOTE_AMOUNT_INVALID": "A partial credit note amount must be greater than zero.",
      "err.CREDIT_NOTE_EXCEEDS_BALANCE": "The credit note amount is more than the invoice's outstanding balance.",
      "err.CREDIT_NOTE_TYPE_INVALID": "The credit note type must be full or partial.",
      "err.PAYMENT_EXCEEDS_ADJUSTED_TOTAL": "This payment is more than the invoice total after the discount. Enter an amount up to the remaining balance.",
      "err.QUOTATION_LINE_ALREADY_QUOTED": "Some items are already on another open quotation. Close or revise that quotation first.",
      "err.QUOTATION_CLOSED": "This quotation was closed by the garage.",
      "err.DOWNPAYMENT_ACTION_REQUIRED": "Choose whether to refund the downpayment or keep it as the customer's credit.",
      "err.CARD_AWAITING_CUSTOMER_APPROVAL": "The customer hasn't approved this job card yet. Approve the quotation manually or close it first.",
      "err.DOWNPAYMENT_GATEWAY_REFUND_UNSUPPORTED": "This downpayment was paid online and cannot be refunded from here yet. Keep it as the customer's credit.",
      "err.QUOTATION_PAYMENT_IN_PROGRESS": "The customer is paying this quotation right now. Try again in a few minutes.",
      "qclose.closeAction": "Close quotation",
      "qclose.manualApproveAction": "Approve manually",
      "qclose.modalTitle": "Close quotation {{number}}",
      "qclose.modalBody": "The quotation stays in the history marked as closed, and the customer's link stops working.",
      "qclose.reasonLabel": "Reason",
      "qclose.reason.duplicate": "Duplicate quotation",
      "qclose.reason.customer_changed_request": "Customer changed the request",
      "qclose.reason.price_changed": "Price changed",
      "qclose.reason.revised": "Replaced by an updated quotation",
      "qclose.reason.other": "Other",
      "qclose.noteLabel": "Note",
      "qclose.noteRequired": "Write a note when the reason is Other.",
      "qclose.downpaymentTitle": "The customer already paid a downpayment of {{amount}}",
      "qclose.downpayment.refund": "Refund it to the customer",
      "qclose.downpayment.credit": "Keep it as the customer's credit",
      "qclose.confirm": "Close quotation",
      "qclose.closed": "Quotation closed",
      "qclose.badge": "Closed",
      "qclose.backToDiagnosis": "The job card is back in Diagnosis.",
      "qclose.showClosed": "Show closed",
      "qclose.publicClosedTitle": "This quotation was closed",
      "qclose.publicClosedBody": "The garage closed this quotation. Please contact them.",
      "qclose.publicReplaced": "This quotation was replaced by a newer one. Please use the latest link from the garage.",
      "qclose.history": "Quotation {{number}} closed: {{reason}}",
      "qclose.flag.noOpenQuotation": "Waiting for customer approval with no open quotation",
      "qclose.flag.waitingDays": "Waiting for customer approval for {{days}} days",
      "qclose.override.reasonLabel": "Why complete before the customer approved?",
      "gl.chart.accessDenied": "You don't have access to the chart of accounts",
      "gl.chart.accessDeniedDesc": "Ask an administrator to grant the gl_accounts.view permission.",
      "gl.chart.activate": "Activate",
      "gl.chart.activated": "Account reactivated.",
      "gl.chart.active": "Active",
      "gl.chart.addAccount": "Add account",
      "gl.chart.addDesc": "Pick where this account belongs — its type and normal balance come from that parent.",
      "gl.chart.addTitle": "Add account",
      "gl.chart.advanced": "Advanced",
      "gl.chart.advancedCollapse": "Use inherited values instead",
      "gl.chart.advancedConfirmButton": "I understand, let me override",
      "gl.chart.advancedConfirmMessage": "This is how a contra account gets made, and it's rarely the right choice. Only change these if you know this account needs to behave differently from its parent.",
      "gl.chart.advancedConfirmTitle": "Override the inherited type and balance?",
      "gl.chart.code": "Code",
      "gl.chart.codeHint": "Suggested from the parent's account range. You can change it.",
      "gl.chart.col.account": "Account",
      "gl.chart.col.state": "State",
      "gl.chart.col.type": "Type",
      "gl.chart.createFailed": "Couldn't create the account.",
      "gl.chart.created": "Account created.",
      "gl.chart.credit": "Credit",
      "gl.chart.deactivate": "Deactivate",
      "gl.chart.deactivateConfirm": "Deactivate",
      "gl.chart.deactivateFailed": "Couldn't deactivate the account.",
      "gl.chart.deactivateMessage": "The account stays in the chart and in every past record — it just leaves the pickers, so it can no longer be selected for a new journal line.",
      "gl.chart.deactivateTitle": "Deactivate this account?",
      "gl.chart.deactivated": "Account deactivated.",
      "gl.chart.debit": "Debit",
      "gl.chart.edit": "Edit",
      "gl.chart.editTitle": "Edit account",
      "gl.chart.empty": "No accounts yet",
      "gl.chart.emptyDesc": "Accounts you add will appear here, grouped by type.",
      "gl.chart.errName": "Enter both the English and Arabic name.",
      "gl.chart.errParent": "Select a parent account.",
      "gl.chart.featureDisabled": "General ledger isn't enabled for this company",
      "gl.chart.featureDisabledDesc": "Ask an administrator to enable the general ledger feature.",
      "gl.chart.inactive": "Inactive",
      "gl.chart.includeInactive": "Show inactive accounts",
      "gl.chart.inherited": "Type & normal balance",
      "gl.chart.inheritedHint": "Inherited from the parent account.",
      "gl.chart.loadFailed": "Couldn't load the chart of accounts.",
      "gl.chart.lockedTooltip": "Owned by the system — this account can never be journalled against.",
      "gl.chart.nameAr": "Name (Arabic)",
      "gl.chart.nameEn": "Name (English)",
      "gl.chart.normalBalance": "Normal balance",
      "gl.chart.parent": "Parent account",
      "gl.chart.restrictedTooltip": "A journal entry against this account needs the override permission.",
      "gl.chart.saveFailed": "Couldn't save the account.",
      "gl.chart.saved": "Account updated.",
      "gl.chart.selectParent": "Select a parent account",
      "gl.chart.sortOrder": "Sort order",
      "gl.chart.subtitle": "Every account, grouped by type and nested under its parent.",
      "gl.chart.title": "Chart of accounts",
      "gl.chart.type.asset": "Asset",
      "gl.chart.type.equity": "Equity",
      "gl.chart.type.expense": "Expense",
      "gl.chart.type.liability": "Liability",
      "gl.chart.type.revenue": "Revenue",
      "gl.chart.typeLabel": "Type",
      "gl.chart.warnDigitMismatch": "This account's code doesn't match the usual pattern for its type. It was still created — double-check the code if that wasn't intentional.",
      "gl.close.accessDenied": "You don't have access to period close",
      "gl.close.accessDeniedDesc": "Ask an administrator to grant the journal entries permission to view this screen.",
      "gl.close.acknowledgeCheckbox": "I know about this and am closing anyway",
      "gl.close.closeAction": "Close month",
      "gl.close.closeAnywayAction": "Close anyway",
      "gl.close.overrideWarningsHeading": "Closing with warnings",
      "gl.close.overrideWarningsBody": "The journal for this period has {{count}} warning(s). Closing now freezes the snapshot as it stands; correcting one afterwards needs a journal entry in an open period.",
      "gl.close.closeFailed": "Couldn't close this period.",
      "gl.close.closeSeq": "Closed {{n}} times",
      "gl.close.closeSuccess": "Period closed.",
      "gl.close.closedBy": "Closed by",
      "gl.close.closedOn": "Closed on",
      "gl.close.col.accountKey": "Account",
      "gl.close.col.docRef": "Document",
      "gl.close.col.recomputed": "Recomputed today",
      "gl.close.col.snapshot": "Frozen amount",
      "gl.close.confirmAckHeading": "You are acknowledging, without recording, that these are still outstanding:",
      "gl.close.confirmAction": "Close this month",
      "gl.close.confirmIntro": "You're about to close {{period}}. Once closed, every number below is frozen — correcting anything afterward means posting a journal entry in the open period, never editing this one.",
      "gl.close.confirmTitle": "Close {{period}}?",
      "gl.close.confirmWarning": "This cannot be undone from this screen. After closing, corrections need a journal entry in the open period.",
      "gl.close.featureDisabled": "General ledger isn't enabled",
      "gl.close.featureDisabledDesc": "Ask an administrator to enable the general ledger feature for this company.",
      "gl.close.jumpToPeriod": "Go to {{period}}",
      "gl.close.loadFailed": "Couldn't load this. Try again.",
      "gl.close.nextYear": "Next year",
      "gl.close.obligation.GL_OBLIGATION_EOSB": "End-of-service benefit accrued this period",
      "gl.close.obligation.GL_OBLIGATION_GOSI": "Employer's GOSI share for this period",
      "gl.close.obligation.GL_OBLIGATION_PAYROLL_UNPAID": "A payroll run was approved but not yet paid",
      "gl.close.obligation.GL_OBLIGATION_PENDING_GATEWAY": "Card or BNPL payments not yet confirmed by the gateway",
      "gl.close.obligation.GL_OBLIGATION_VAT_FILING": "The VAT filing entry for this period",
      "gl.close.obligationsHeading": "Money not yet recorded",
      "gl.close.openDocument": "Open it",
      "gl.close.openDocuments": "Open documents",
      "gl.close.openHint": "Not closed yet",
      "gl.close.periodNotEnded": "This month hasn't ended yet. It can be closed once the month is over.",
      "gl.close.pickMonth": "Pick a month",
      "gl.close.pickMonthDesc": "Select a month above to review what would happen if you closed it.",
      "gl.close.prevYear": "Previous year",
      "gl.close.readyToClose": "Nothing is blocking this close. Review the totals above, then close when ready.",
      "gl.close.recordIt": "Record it",
      "gl.close.refusal.GL_CLOSE_BLOCKED_DOCUMENT": "A document doesn't balance and must be fixed before this month can close.",
      "gl.close.refusal.GL_CLOSE_EARLIER_PERIOD_OPEN": "An earlier period, {{period}}, is still open. Months close in order.",
      "gl.close.refusal.GL_CLOSE_WARNINGS_PRESENT": "Money landed in suspense because a document couldn't be mapped to an account.",
      "gl.close.refusal.GL_CLOSE_YEAR_END_SUSPENSE_NONZERO": "Year end: the suspense account still holds unattributed money. Reclassify it with a journal voucher before closing December.",
      "gl.close.refusal.DEPRECIATION_UNPOSTED": "Depreciation is not posted through this month yet. Post depreciation, then close the month.",
      "gl.close.refusalsHeading": "This month can't be closed yet",
      "gl.close.warning.total_mismatch": "{{doc_ref}} adds up to {{recomputed}} SAR from its lines but was issued for {{stored}} SAR. The difference is already booked to unreconciled document differences, so this month still balances. A posted invoice cannot be edited — open it to check the figures, then close the month anyway and leave the difference for your accountant to settle.",
      "gl.close.warning.no_line_items": "{{doc_ref}} has no line items, so none of its {{stored}} SAR could be attributed to revenue or VAT — the whole amount is booked to unreconciled document differences. A posted invoice cannot be edited — open it to check, then close the month anyway and leave the amount for your accountant to settle.",
      "gl.close.warning.unmapped_account": "No account is mapped for \"{{label}}\" on {{doc_ref}}, so the amount was parked in suspense. Use the Map button on this warning in the trial balance to choose the account, then verify this month again.",
      "gl.close.warning.unbalanced_document": "{{doc_ref}} does not balance — its debits and credits differ. It must be corrected before this month can close.",
      "gl.close.warning.excluded_parent_document": "{{doc_ref}} was recorded against {{parent_ref}}, which is not in this month's books, so its amount was parked in suspense. Resolve {{parent_ref}} first.",
      "gl.close.warning.unprojectable_document": "{{doc_ref}} could not be turned into a journal entry, so its amount was parked in suspense. Send this reference to support.",
      "gl.close.warning.total_mismatch.purchase_invoice": "{{doc_ref}} does not add up to the amount it was recorded for. The difference is booked to unreconciled document differences, so this month still balances — open it to check the figures.",
      "gl.close.warning.total_mismatch.stock_movement": "A stock movement ({{doc_ref}}) could not be valued from its own recorded cost, so the difference is booked to unreconciled document differences. Give the part a cost and later movements value correctly.",
      "gl.close.warning.total_mismatch.stock_transfer": "A stock transfer ({{doc_ref}}) does not net to zero across the two branches. The difference is booked to unreconciled document differences.",
      "gl.map.action": "Map account",
      "gl.close.openParts": "Open parts",
      "gl.close.noAction": "No screen to open for this document",
      "gl.map.affected": "{{count}} document(s) on this key — {{amount}} total passed through suspense",
      "gl.map.andMore": "and {{count}} more",
      "gl.map.title": "Map \"{{label}}\" to an account",
      "gl.map.description": "Money on this key sits in suspense until it points at an account. Set it once and every document that uses it is mapped from now on.",
      "gl.map.account": "Account",
      "gl.map.pick": "Choose an account",
      "gl.map.save": "Save mapping",
      "gl.map.saved": "Mapped — re-reading the trial balance.",
      "gl.map.saveFailed": "The mapping could not be saved.",
      "gl.map.loadFailed": "The chart of accounts could not be loaded, so no account can be chosen right now.",
      "gl.close.reopen": "Reopen",
      "gl.close.reopenConfirmAction": "Reopen this month",
      "gl.close.reopenConfirmMessage": "Reopening lets journal entries post into this period again. The books were frozen for a reason — only reopen to correct a real error, and re-close as soon as you're done. This bumps the close count and is visible to an auditor.",
      "gl.close.reopenConfirmTitle": "Reopen {{period}}?",
      "gl.close.reopenFailed": "Couldn't reopen this period.",
      "gl.close.reopenSuccess": "Period reopened.",
      "gl.close.status.closed": "Closed",
      "gl.close.status.open": "Open",
      "gl.close.subtitle": "Close, reopen, or verify a month's books. Closing is irreversible in practice — corrections after that need a journal entry in the open period.",
      "gl.close.title": "Period close",
      "gl.close.totalCredit": "Total credit",
      "gl.close.totalDebit": "Total debit",
      "gl.close.vatFiledFinal": "VAT has been filed for this period. It can never be reopened.",
      "gl.close.verify": "Verify",
      "gl.close.verifyFailed": "Couldn't run verify.",
      "gl.close.verifyMatches": "The frozen numbers still match the documents. Nothing to do.",
      "gl.close.verifyMismatch": "The frozen numbers no longer match what the documents say today.",
      "gl.close.verifyNote": "Verify never rewrites the frozen period. If something is wrong, fix it with a journal entry in the open period.",
      "gl.gj.blockedDesc": "A document does not balance, so no journal can be shown for this window: {{refs}}. Fix that document and the journal reappears.",
      "gl.gj.blockedTitle": "The journal cannot be shown",
      "gl.gj.col.document": "Document",
      "gl.gj.col.entry": "Entry",
      "gl.gj.cr": "Cr",
      "gl.gj.derived": "From its module",
      "gl.gj.derivedDialog.desc": "This entry was created by the document below, not typed here. Correct it in the module that owns it.",
      "gl.gj.derivedDialog.noSource": "This entry has no document to open.",
      "gl.gj.derivedDialog.number": "Document",
      "gl.gj.derivedDialog.openSource": "Open the source document",
      "gl.gj.derivedDialog.reference": "Ledger reference",
      "gl.gj.derivedDialog.title": "Posted from another module",
      "gl.gj.dr": "Dr",
      "gl.gj.empty": "Nothing moved in this window",
      "gl.gj.emptyDesc": "No invoice, receipt, purchase or voucher posted between these dates.",
      "gl.gj.kind.advance_application": "Downpayment applied",
      "gl.gj.kind.bad_debt_writeoff": "Bad debt written off",
      "gl.gj.kind.zatca_credit_note_resolution": "Credit note card refund kept as customer credit",
      "gl.gj.kind.expense_accrual": "Expense accrual",
      "gl.gj.kind.fixed_asset_disposal": "Asset disposal",
      "gl.gj.kind.fixed_asset_cancellation": "Asset cancellation",
      "gl.gj.kind.inventory_writedown": "Inventory write-down",
      "gl.gj.kind.prepaid_release": "Prepaid expense released",
      "gl.gj.kind.stock_movement": "Stock movement",
      "gl.gj.kind.stock_transfer": "Stock transfer",
      "gl.gj.kind.warranty_credit_note": "Warranty credit note",
      "gl.gj.kind.warranty_provision": "Warranty provision",
      "gl.gj.kind.warranty_release": "Warranty provision released",
      "gl.gj.kind.warranty_utilisation": "Warranty work charged",
      "gl.gj.kind.cash_movement": "Cash",
      "gl.gj.kind.fixed_asset_depreciation": "Depreciation",
      "gl.gj.kind.gateway_settlement": "Gateway settlement",
      "gl.gj.kind.invoice": "Sales invoice",
      "gl.gj.kind.journal_entry": "Manual voucher",
      "gl.gj.kind.journal_exception": "Unmatched amount",
      "gl.gj.kind.opening_balance": "Opening balance",
      "gl.gj.kind.purchase_credit_note": "Purchase credit note",
      "gl.gj.kind.expense_credit_note": "Expense credit note",
      "gl.gj.kind.purchase_invoice": "Purchase invoice",
      "gl.gj.kind.sales_credit_note": "Sales credit note",
      "gl.gj.kind.zatca_credit_note": "Sales credit note (ZATCA)",
      "gl.gj.readOnlyNote": "Everything here is shown as it was posted by the module it came from. To change one, go to that document — an invoice is corrected with a credit note, an asset with a disposal. Only manual vouchers can be edited from this screen.",
      "gl.gj.unmappedAccount": "Account not in your chart",
      "gl.gj.viewAll": "General journal — everything",
      "gl.gj.viewLabel": "Show",
      "gl.gj.viewManual": "Manual vouchers only",
      "gl.jv.account.headerBadge": "Group",
      "gl.jv.account.headerTip": "A grouping account. It totals the accounts underneath it and nothing can be posted to it directly.",
      "gl.jv.account.lockedBadge": "Locked",
      "gl.jv.account.lockedTip": "Cash and suspense accounts can never be journalled against, by anyone. Cash is proved against a drawer count, so it is corrected on the cash statement, not here.",
      "gl.jv.account.noMatch": "No account matches that search.",
      "gl.jv.account.noneDesc": "The chart of accounts is empty or could not be read, so there is nothing to journal against.",
      "gl.jv.account.noneTitle": "No accounts available",
      "gl.jv.account.pick": "Choose an account…",
      "gl.jv.account.restrictedBadge": "Control",
      "gl.jv.account.restrictedNoPermTip": "A control account the system normally owns. Posting to it needs the journal_entries.override_control permission, which you do not have.",
      "gl.jv.account.restrictedOnLine": "Control account — this entry needs the control override.",
      "gl.jv.account.restrictedTip": "A control account the system normally owns (receivables, payables, advances, gateway clearing, VAT). Selecting it turns on the control override for this entry.",
      "gl.jv.account.search": "Search by code or name",
      "gl.jv.accountInvalidHint": "One of the accounts is not in this company's chart, is inactive, or is a grouping account. Pick a different account on the highlighted line.",
      "gl.jv.accountsFailed": "The chart of accounts could not be loaded, so no account can be picked.",
      "gl.jv.addLine": "Add line",
      "gl.jv.amountInvalid": "An amount is not a valid figure. Use at most two decimal places.",
      "gl.jv.badge.overridden": "Control override",
      "gl.jv.badge.reversedBy": "Reversed by {{no}}",
      "gl.jv.badge.reverses": "Reverses {{no}}",
      "gl.jv.balanced": "Balanced — debit equals credit.",
      "gl.jv.branch": "Branch",
      "gl.jv.branchShared": "Company-wide (no branch)",
      "gl.jv.col.account": "Account",
      "gl.jv.col.amount": "Total debit",
      "gl.jv.col.createdBy": "By",
      "gl.jv.col.credit": "Credit",
      "gl.jv.col.date": "Entry date",
      "gl.jv.col.debit": "Debit",
      "gl.jv.col.description": "Description",
      "gl.jv.col.entryNo": "Entry no.",
      "gl.jv.col.memo": "Memo",
      "gl.jv.col.status": "Status",
      "gl.jv.createdBy": "Created by",
      "gl.jv.dateRequired": "An entry date is required.",
      "gl.jv.delete.message": "The draft and its lines are removed. Nothing has been posted to the ledger, so nothing else changes.",
      "gl.jv.delete.title": "Delete this draft?",
      "gl.jv.deleted": "Draft deleted",
      "gl.jv.difference": "Difference",
      "gl.jv.editor.desc": "Each line is either a debit or a credit. Total debit must equal total credit before the entry can be posted.",
      "gl.jv.editor.newTitle": "New journal entry",
      "gl.jv.editor.readOnlyDesc": "This entry has been posted. It is shown as it stands and cannot be changed.",
      "gl.jv.editor.title": "Journal entry",
      "gl.jv.empty": "No journal entries in this period",
      "gl.jv.emptyDesc": "Nothing was journalled for the dates and branch you selected. Widen the date range, or create the first entry.",
      "gl.jv.enableOverride": "Turn on the control override",
      "gl.jv.entryDate": "Entry date",
      "gl.jv.filterStatus": "Status",
      "gl.jv.fixBlockers": "Fix the points listed below before saving.",
      "gl.jv.impossible": "This should not have been possible from this screen — please report it.",
      "gl.jv.lines": "Lines",
      "gl.jv.lockedFinal": "This is final. Cash and suspense accounts are never journalled against, with any permission. Correct cash on the cash statement instead.",
      "gl.jv.memoAr": "Memo (Arabic)",
      "gl.jv.memoEn": "Memo (English)",
      "gl.jv.memoHint": "At least one of the two memos is required.",
      "gl.jv.memoRequired": "Write a memo in Arabic or in English.",
      "gl.jv.nav": "Journal entries",
      "gl.jv.needBothSigns": "The entry needs at least one debit line and at least one credit line.",
      "gl.jv.needTwoLines": "A journal entry needs at least two lines.",
      "gl.jv.new": "New journal entry",
      "gl.jv.noAccess": "You cannot view journal entries",
      "gl.jv.noAccessDesc": "Viewing journal entries needs the journal_entries.view permission. Ask your administrator for access.",
      "gl.jv.noAccountOnLine": "Every line needs an account.",
      "gl.jv.noFeature": "General ledger is not enabled",
      "gl.jv.noFeatureDesc": "Journal entries are part of the general ledger, which is not switched on for this company. Ask your administrator to enable it.",
      "gl.jv.notACreditNote": "A journal entry cannot correct a customer's invoice — that is a credit note.",
      "gl.jv.notACreditNoteDetail": "If a customer was invoiced wrongly, issue a credit note against the invoice. A journal entry moves the ledger without touching the invoice or the tax record, so the two stop agreeing.",
      "gl.jv.notDraftHint": "Only a draft can be changed or deleted. Refresh the list to see the entry's current state.",
      "gl.jv.openTwin": "Open the linked entry",
      "gl.jv.outOfBalance": "Out of balance by {{amount}} SAR. The entry cannot be posted until the difference is zero.",
      "gl.jv.outOfBalanceShort": "Total debit does not equal total credit.",
      "gl.jv.override.confirmAttribution": "The override is recorded against your name on this entry.",
      "gl.jv.override.confirmBody": "This entry will move a balance the system normally owns and keeps in step with the documents behind it — receivables, payables, customer advances, gateway clearing or VAT. After this, the ledger and those documents may no longer agree.",
      "gl.jv.override.confirmTitle": "Override the control accounts?",
      "gl.jv.override.confirmYes": "Yes, override",
      "gl.jv.override.hint": "Needed because this entry touches {{codes}}. The override applies to the whole entry and is recorded against your name.",
      "gl.jv.override.needPermission": "Posting to a control account needs the journal_entries.override_control permission.",
      "gl.jv.override.title": "Override control accounts",
      "gl.jv.overrideUsed": "Control override",
      "gl.jv.periodClosedHint": "That month is closed. Date the entry in the open period instead; a closed month is corrected by a fresh entry, not by back-dating one.",
      "gl.jv.post": "Post entry",
      "gl.jv.posted": "Entry posted",
      "gl.jv.postedAt": "Posted on",
      "gl.jv.postedBy": "Posted by",
      "gl.jv.readOnlyPosted": "Posted entries are read-only. There is no edit and no delete — the only correction is a reversal.",
      "gl.jv.readOnlyReversed": "This entry has already been reversed. It stays on the books next to its reversal.",
      "gl.jv.refreshHint": "Refresh the list to see the entry's current state.",
      "gl.jv.removeLine": "Remove this line",
      "gl.jv.restrictedNeedsOverride": "A control account is used, so the control override must be switched on.",
      "gl.jv.restrictedOfferOverride": "You hold the override permission. Switch it on to post this entry against the control account.",
      "gl.jv.reverse.action": "Reverse entry",
      "gl.jv.reverse.dateNote": "The reversal is dated today, {{date}} — never the original entry's date.",
      "gl.jv.reverse.done": "Reversing entry posted",
      "gl.jv.reverse.message": "Reversing creates a NEW posted entry with every debit and credit flipped. The original entry stays on the books, badged as reversed, and the two link to each other.",
      "gl.jv.reverse.notInPlace": "It does not undo the entry in place. A reversal landing back in a closed month is exactly what the period lock exists to stop.",
      "gl.jv.reverse.title": "Reverse this entry?",
      "gl.jv.saveDraft": "Save draft",
      "gl.jv.saveFailed": "The journal entry could not be saved.",
      "gl.jv.saved": "Draft saved",
      "gl.jv.status.all": "All statuses",
      "gl.jv.status.draft": "Draft",
      "gl.jv.status.posted": "Posted",
      "gl.jv.status.reversed": "Reversed",
      "gl.jv.subtitle": "Manual journal vouchers. A draft can be edited; once posted an entry is permanent and can only be reversed.",
      "gl.jv.title": "Journal entries",
      "gl.jv.totalCredit": "Total credit",
      "gl.jv.totalDebit": "Total debit",
      "gl.jv.zeroLine": "Every line needs an amount in either the debit or the credit column.",
      "gl.tb.accessDenied": "You don't have access to the trial balance",
      "gl.tb.accessDeniedDesc": "Ask an administrator to grant you the journal entries view permission.",
      "gl.tb.balanced": "Balanced — debits equal credits",
      "gl.tb.blockedDesc": "At least one document doesn't balance, so every number on this page would be wrong. Fix the documents below, then refresh.",
      "gl.tb.blockedDoc": "Document",
      "gl.tb.blockedTitle": "This period can't be trusted yet",
      "gl.tb.col.code": "Code",
      "gl.tb.col.closing": "Closing balance",
      "gl.tb.col.credit": "Credit",
      "gl.tb.col.debit": "Debit",
      "gl.tb.col.opening": "Opening balance",
      "gl.tb.col.name": "Account",
      "gl.tb.col.type": "Type",
      "gl.tb.empty": "Nothing to show for this range",
      "gl.tb.emptyDesc": "No account activity in this period and scope. Try a wider date range or enable zero-balance accounts.",
      "gl.tb.export": "Export CSV",
      "gl.tb.featureDisabled": "General ledger isn't enabled for this company",
      "gl.tb.featureDisabledDesc": "Ask an administrator to turn on the general ledger feature for your plan.",
      "gl.tb.grandTotal": "Grand total",
      "gl.tb.ledgerClosing": "Closing balance",
      "gl.tb.ledgerCol.amount": "Amount",
      "gl.tb.ledgerCol.date": "Date",
      "gl.tb.ledgerCol.reference": "Reference",
      "gl.tb.ledgerCol.running": "Running balance",
      "gl.tb.ledgerEmpty": "No activity on this account in this range",
      "gl.tb.ledgerLoadFailed": "Couldn't load the account ledger",
      "gl.tb.ledgerOpening": "Opening balance",
      "gl.tb.ledgerRange": "{{from}} – {{to}}",
      "gl.tb.ledgerViewEntry": "View journal entry",
      "gl.tb.loadFailed": "Couldn't load the trial balance",
      "gl.tb.manualOnly": "Manual entry only",
      "gl.tb.showZero": "Show zero-balance accounts",
      "gl.tb.sourceClosed": "Closed {{date}} — frozen figures",
      "gl.tb.sourceLive": "Live — recalculated from open activity",
      "gl.tb.subtitle": "Every account's balance: what it opened at, what moved, what it closed at.",
      "gl.tb.subtotal": "{{type}} subtotal",
      "gl.tb.title": "Trial balance",
      "gl.tb.type.asset": "Assets",
      "gl.tb.type.equity": "Equity",
      "gl.tb.type.expense": "Expenses",
      "gl.tb.type.liability": "Liabilities",
      "gl.tb.type.other": "Other",
      "gl.tb.type.revenue": "Revenue",
      "gl.tb.unbalanced": "Not balanced — debits and credits disagree",
      "gl.tb.warningsCount": "{{count}} document(s) sitting in suspense — could not be mapped to an account",
      "ob.accessDenied": "You don't have access to starting balances",
      "ob.accessDeniedDesc": "Ask an administrator to grant access to opening balances.",
      "ob.addRow.account": "Specific account (optional)",
      "ob.addRow.accountNone": "Use the default",
      "ob.addRow.amount": "Amount",
      "ob.addRow.button": "Add row",
      "ob.addRow.errAmount": "Enter an amount greater than zero.",
      "ob.addRow.heading": "Add anything else — a loan, equipment, your own money in the business",
      "ob.addRow.lineType": "What's this closest to?",
      "ob.addRow.note": "Note (optional)",
      "ob.addRow.notePlaceholder": "e.g. Loan from Al Rajhi Bank",
      "ob.date.hint": "The day your books start — usually when you began using MotorMind.",
      "ob.date.label": "As of date",
      "ob.empty.desc": "There's nothing we know about automatically, and you haven't added anything yet. Use \"Add something else\" below once you're ready.",
      "ob.empty.title": "Nothing here yet",
      "ob.errDate": "Pick the date your books start from.",
      "ob.errRows": "Some rows aren't ready yet — check the ones marked below.",
      "ob.featureDisabled": "Starting balances isn't enabled for this company",
      "ob.featureDisabledDesc": "Ask an administrator to turn on the opening balances feature.",
      "ob.lineType.ap": "Payable to suppliers",
      "ob.lineType.ar": "Receivable from customers",
      "ob.lineType.cash": "Cash or bank",
      "ob.loadFailed": "Couldn't load your starting balances.",
      "ob.locked.banner": "Starting balances are locked",
      "ob.locked.bannerDate": "Locked on {{date}}. To change them now, use a journal entry.",
      "ob.prefill.alreadyBooked.note": "Already included in your books — there is nothing to enter for these.",
      "ob.prefill.button": "Use what we already know",
      "ob.prefill.chip.ap_open": "From unpaid supplier bills",
      "ob.prefill.chip.ar_open": "From unpaid customer invoices",
      "ob.prefill.chip.cash_account": "From your cash accounts",
      "ob.prefill.chip.inventory": "From your inventory",
      "ob.prefill.count.ap_open_one": "{{count}} unpaid supplier bill",
      "ob.prefill.count.ap_open_other": "{{count}} unpaid supplier bills",
      "ob.prefill.count.ar_open_one": "{{count}} unpaid customer invoice",
      "ob.prefill.count.ar_open_other": "{{count}} unpaid customer invoices",
      "ob.prefill.count.cash_account_one": "{{count}} cash account",
      "ob.prefill.count.cash_account_other": "{{count}} cash accounts",
      "ob.prefill.count.inventory_one": "{{count}} inventory item",
      "ob.prefill.count.inventory_other": "{{count}} inventory items",
      "ob.prefill.heading": "Use what we already know",
      "ob.prefill.inventory.estimatedNote": "This inventory total is valued at today's cost for each part, not its cost as of your opening date. It also treats a part's first stock, or a count from zero, as opening stock when it's keyed on or after your opening date — which can overstate the total, so check it carefully. A later correction to stock that was already recorded doesn't count this way. Once you lock these balances in, this cannot be undone.",
      "ob.prefill.inventory.inactiveNote_one": "{{count}} of these parts is no longer active in your catalogue. It's still counted here because it held stock on your opening date, but the stock valuation report will not include it — the two figures will not match by that amount.",
      "ob.prefill.inventory.inactiveNote_other": "{{count}} of these parts are no longer active in your catalogue. They're still counted here because they held stock on your opening date, but the stock valuation report will not include them — the two figures will not match by that amount.",
      "ob.prefill.inventory.unresolvedNote_one": "{{count}} part in your current stock could not be confirmed as of your opening date — it may already be counted elsewhere (for example, the branch it was transferred from), so check before adding it by hand.",
      "ob.prefill.inventory.unresolvedNote_other": "{{count}} parts in your current stock could not be confirmed as of your opening date — they may already be counted elsewhere (for example, the branch they were transferred from), so check before adding them by hand.",
      "ob.prefill.inventory.inTransitNote": "Stock was in transit between branches on this date ({{transfers}}). Sign-off is refused until you pick a date when nothing was in transit.",
      "ob.prefill.inventory.zeroCostNote_one": "{{count}} of these parts has no cost on file and is valued at zero — check it before you lock these balances in.",
      "ob.prefill.inventory.zeroCostNote_other": "{{count}} of these parts have no cost on file and are valued at zero — check them before you lock these balances in.",
      "ob.prefill.none": "We don't have anything to bring in automatically yet — add what you know below.",
      "ob.prefill.summaryLead": "Pressing this button will bring in:",
      "ob.row.remove": "Remove",
      "ob.rows.empty": "No rows yet",
      "ob.rows.emptyDesc": "Press \"Use what we already know\" above, or add a row yourself.",
      "ob.saveFailed": "Couldn't save your starting balances.",
      "ob.saved": "Starting balances saved.",
      "ob.signOff.button": "Lock in these balances",
      "ob.signOff.confirm": "Yes, lock them in",
      "ob.signOff.done": "Starting balances locked.",
      "ob.signOff.failed": "Couldn't lock in your starting balances.",
      "ob.signOff.message": "Once you do this, your starting balances are locked. Changing them later will need a journal entry, not this screen.",
      "ob.signOff.title": "Lock in your starting balances?",
      "ob.subtitle": "Tell us what your business owns and what it is liable for on day one \u2014 most of it, we already know.",
      "ob.summary.assets": "What you have",
      "ob.summary.equity": "Owner's equity (calculated)",
      "ob.summary.equityDeficitLabel": "Shortfall",
      "ob.summary.equityTooltip": "This is simply what's left over once everything else is counted — it's normal, and you never fill it in yourself. It shows here as capital you put into the business.",
      "ob.summary.equityTooltipDeficit": "This is simply what's left over once everything else is counted — it's normal, and you never fill it in yourself. Because what your business is liable for is more than what it owns, it shows here as a shortfall rather than money put in.",
      "ob.summary.liabilities": "Total liabilities",
      "ob.table.col.account": "Specific account",
      "ob.table.col.amount": "Amount",
      "ob.table.col.category": "Category",
      "ob.table.col.note": "Note",
      "ob.table.col.what": "What it is",
      "ob.table.heading": "Your starting numbers",
      "ob.title": "Starting balances",
      "ob.lineType.other": "Something else (loan, equipment, your own money)",
      "ob.table.col.counterpart": "Who or where",
      "ob.counterpart.notNeeded": "Not needed",
      "ob.counterpart.chosen": "Chosen",
      "ob.counterpart.choose.cash": "Choose the cash or bank account",
      "ob.counterpart.choose.ar": "Choose the customer",
      "ob.counterpart.choose.ap": "Choose the supplier",
      "ob.counterpart.title.cash": "Which cash or bank account?",
      "ob.counterpart.title.ar": "Which customer is this receivable from?",
      "ob.counterpart.title.ap": "Which supplier is this payable to?",
      "ob.counterpart.desc.cash": "Pick the till or bank account this money is sitting in on your start date.",
      "ob.counterpart.desc.ar": "Pick the customer this amount was still outstanding from on your start date.",
      "ob.counterpart.desc.ap": "Pick the supplier this amount was still payable to on your start date.",
      "ob.counterpart.field.cash": "Cash or bank account",
      "ob.counterpart.field.ar": "Customer",
      "ob.counterpart.field.ap": "Supplier",
      "ob.counterpart.placeholder.cash": "Choose an account",
      "ob.counterpart.placeholder.ar": "Choose a customer",
      "ob.counterpart.placeholder.ap": "Choose a supplier",
      "ob.counterpart.empty.cash": "No cash or bank accounts found",
      "ob.counterpart.empty.ar": "No customers found",
      "ob.counterpart.empty.ap": "No suppliers found",
      "ob.counterpart.loadFailed": "Couldn't load the list.",
      "ob.addRow.counterpart": "Who or where",
      "ob.addRow.accountRequired": "Which account (required)",
      "ob.addRow.accountPick": "Choose an account",
      "ob.incomplete.title": "A few rows still need an answer",
      "ob.incomplete.desc": "The rows marked in amber need you to say who the money belongs to, or which account it sits on. Still to answer: {{remaining}}.",
      "ob.err.counterpart.cash": "Choose the cash or bank account this money is in.",
      "ob.err.counterpart.ar": "Choose the customer this amount is receivable from.",
      "ob.err.counterpart.ap": "Choose the supplier this amount is payable to.",
      "ob.err.accountRequired": "Choose the account this money sits on.",
      "ob.err.fractional": "Amounts can have at most two decimal places.",
      "err.field.OB_COUNTERPART_REQUIRED": "Choose one.",
      "err.field.OB_AMOUNT_NEGATIVE": "Enter a positive amount.",
      "err.field.OB_AMOUNT_REQUIRED": "Enter an amount.",
      "err.field.OB_FRACTIONAL_HALALA": "At most two decimals.",
      "apv.title": "Customer approvals",
      "apv.drawerTitle": "Approval Evidence",
      "apv.empty": "No customer approvals recorded yet",
      "apv.loadFailed": "Could not load customer approvals",
      "apv.subjectTitle": "Subject",
      "apv.proofStrength": "Proof strength",
      "apv.actorTitle": "Actor",
      "apv.staffBannerTitle": "Attested by garage staff; the customer did not sign electronically",
      "apv.legacyBannerTitle": "No live proof was captured for this decision; fingerprint computed later from stored data",
      "apv.whatCustomerSaw": "What the customer saw",
      "apv.howApproved": "How they approved",
      "apv.mediaTitle": "Photos & Media Fingerprints",
      "apv.integrityTitle": "Chain & Integrity",
      "apv.downloadPdf": "Download PDF",
      "apv.shareWhatsApp": "Share on WhatsApp",
      "apv.hashCopied": "Fingerprint copied",
      "apv.errViewPdf": "Could not open the record",
      "apv.errDownloadPdf": "Could not download the record",
      "apv.errCopyLink": "Could not copy link",
      "apv.approved": "Approved",
      "apv.rejected": "Rejected",
      "apv.approvedTotal": "Approved total",
      "apv.delayTerms": "Delay terms",
      "apv.latePartsFee": "Late parts fee/day",
      "apv.lateCollectionFee": "Late collection fee/day",
      "apv.graceHours": "Grace period",
      "apv.termsVersion": "Terms version",
      "apv.showFullTerms": "Show full terms",
      "apv.hideFullTerms": "Hide full terms",
      "apv.actorStaff": "Staff actor",
      "apv.codeSentAt": "Code sent at",
      "apv.codeVerifiedAt": "Code verified at",
      "apv.responseDuration": "Response duration",
      "apv.otpAttempts": "Verification attempts",
      "apv.ipAddress": "IP address",
      "apv.device": "Device / Browser",
      "apv.mediaCounts": "{{fingerprinted}} fingerprinted, {{missing}} not fingerprinted",
      "apv.fingerprinted": "fingerprinted",
      "apv.notFingerprinted": "not fingerprinted",
      "apv.noMedia": "No media attached",
      "apv.snapshotOk": "Content fingerprint",
      "apv.eventHashOk": "Event hash",
      "apv.prevLinkOk": "Chain link",
      "apv.snapshotHash": "Content fingerprint",
      "apv.prevHash": "Previous event hash",
      "apv.eventHash": "Event hash",
      "apv.hashedAt": "Fingerprinted on",
      "apv.certificateHeading": "Record",
      "apv.issueCertificate": "Issue record",
      "apv.openCertificate": "Open record",
      "apv.certificateNo": "Record No.",
      "apv.certificateDate": "Record date",
      "apv.copyShareText": "Copy share text",
      "apv.shareCopied": "Share text copied",
      "apv.certificateShare": "Record No. {{number}}, dated {{date}}. To verify: {{url}}",
      "apv.certificateUnavailable": "The record cannot be issued right now; contact support",
      "apv.certificate": "Approval record",
      "apv.verifyLink": "Verification link",
      "apv.fingerprint": "Fingerprint",
      "apv.viewProofOnJobCard": "View approval proof on the job card",
      "apv.actor.customer": "Customer",
      "apv.actor.staff": "Staff",
      "apv.actor.system": "System",
      "apv.media.inlinePhoto": "Inline photo {{n}}",
      "history.acceptance_certificate_opened": "Customer approval certificate opened",
      "apv.subject.intake_tnc": "Intake terms",
      "apv.subject.quotation": "Quotation",
      "apv.subject.quotation_revision": "Quotation revision",
      "apv.subject.supplementary_quotation": "Supplementary quotation",
      "apv.subject.vehicle_handover": "Vehicle handover",
      "apv.subject.online_payment": "Online payment",
      "apv.tier.otp_in_person": "Code given at handover",
      "apv.tier.payment_gateway": "Confirmed by the payment gateway",
      "apv.handover.title": "Vehicle handed over",
      "apv.handover.method": "How it was handed over",
      "apv.handover.methodCode": "Customer gave the delivery code sent to their phone",
      "apv.handover.methodOverride": "Handed over by staff without the code",
      "apv.handover.overrideReason": "Reason",
      "apv.handover.handedOverBy": "Handed over by",
      "apv.handover.deliveredAt": "Handed over at",
      "apv.handover.onCredit": "Handed over on credit",
      "apv.handover.creditDue": "Payment due",
      "apv.handover.cancelledReturn": "Returned without work (cancelled job card)",
      "apv.handover.invoice": "Invoice",
      "apv.handover.invoiceTotal": "Invoice total",
      "apv.handover.paid": "Paid",
      "apv.handover.balanceDue": "Balance due",
      "apv.payment.title": "Paid online",
      "apv.payment.amount": "Amount",
      "apv.payment.gateway": "Payment provider",
      "apv.payment.reference": "Provider reference",
      "apv.payment.paidFor": "Paid for",
      "apv.payment.paidAt": "Paid at",
      "apv.result.accepted": "Accepted",
      "apv.result.partially_accepted": "Partially accepted",
      "apv.result.rejected": "Rejected",
      "apv.result.disputed": "Disputed",
      "apv.result.staff_attested": "Staff attested",
      "apv.result.sent": "Sent",
      "apv.result.withdrawn": "Withdrawn",
      "apv.result.superseded": "Superseded",
      "apv.result.link_expired": "Link expired",
      "apv.tier.legacy": "From stored data (no live proof)",
      "apv.tier.staff_attested": "Staff only",
      "apv.tier.link_only": "Link only",
      "apv.tier.otp_link": "Verified by SMS code",
      "apv.tier.otp_link_anchored": "Verified by SMS code",
      "apv.tier.certified": "Certified signature",
      "apv.retry": "Retry",
      "apv.note": "Note",
      "apv.vat": "VAT",
      "apv.seconds": "seconds",
      "apv.copy": "Copy",
      // Jev Vision documents (Task S2)
      "jevVision.docs.retake": "Retake",
      "jevVision.docs.scanAnyway": "Scan anyway",
      "jevVision.docs.rescanOriginal": "Rescan original photo",
      "jevVision.docs.checking": "Checking photo…",
      "jevVision.docs.cancel": "Cancel",
      "jevVision.docs.unreadable": "The photo is too blurry or unclear to read.",
      "jevVision.docs.wrongDocument": "This photo does not appear to be the expected document.",
      "jevVision.docs.photoLabel": "Photo {{number}}",
      "jevVision.docs.bannerTitle": "Document check issue"
    }
  },
  ar: {
    translation: {

    // ---- permission-grid labels (QC 2026-09-21) ----------------------------
    // The grid derives its key from the catalog: resource + PascalCase action.
    // 48 of these had no entry, and i18n.js sets a parseMissingKeyHandler,
    // so the grid rendered a humanised echo of the key ("Delivery Verify") that
    // looks like a label but is not one. The seven hr* keys carry a DOTTED action
    // from the catalog; i18next resolves a flat key spelled with dots, so they
    // are written exactly as the grid builds them.
    job_cardsOverride_complete: "إتمام خدمة أو قطعة أو مهمة فرعية رغم عدم اكتمالها أو اعتمادها (صلاحية استثنائية)",
    job_cardsForce_start: "بدء العمل في أمر العمل قبل تاريخ البدء المتفق عليه (صلاحية استثنائية للمدير)",
    job_cardsWaive_delay_fee: "إعفاء العميل من رسوم التأخير المستحقة قبل إصدار فاتورتها",
    job_cardsReturn_car: "إلغاء أمر العمل وإرجاع المركبة عند رفض العميل للسعر، بما في ذلك رد أو تقييد المبالغ المحصلة",
    job_cardsTransfer: "نقل أمر العمل إلى فرع آخر",
    job_cardsOverride_intake_confirmation: "تأكيد موافقة العميل على أمر العمل حضوريًا نيابةً عنه (صلاحية استثنائية للمدير)",
    smart_assistantUse: "استخدام المساعد الذكي بالصوت أو الكتابة (خدمة مدفوعة)",
    stockOversell_reserved: "صرف قطعة محجوزة لعرض سعر أو أمر عمل أو فاتورة أخرى (صلاحية استثنائية)",
    purchase_invoicesPay: "سداد فواتير المشتريات للموردين",
    purchase_invoicesApprove: "اعتماد فواتير المشتريات التي تتجاوز حدّ مُدخِلها وترحيلها، ضمن حدّك",
    discountsApprove: "اعتماد الخصومات التي تتجاوز حدّ طالبها، ضمن حدّك",
    purchase_invoicesReverse: "عكس فاتورة مشتريات مرحّلة وقيودها المحاسبية",
    paymentsSend_link: "إرسال رابط دفع إلى العميل",
    deliveryVerify: "التحقق من هوية المستلم قبل تسليم المركبة",
    deliveryOverride: "تسليم المركبة دون استكمال التحقق من المستلم (صلاحية استثنائية)",
    expensesReverse: "عكس مصروف مسجَّل وقيده المحاسبي",
    credit_notesView: "عرض إشعارات الخصم الصادرة للعملاء",
    financialsAudit_view: "عرض سجل التدقيق المالي والقيود المرتبطة به",
    cashEquity: "تسجيل إيداعات الملاك وسحوباتهم من الصندوق",
    partnersView: "عرض الشركاء وحصصهم",
    partnersManage: "إضافة الشركاء وتعديل حصصهم وتسجيل حركاتهم",
    account_mappingsView: "عرض ربط الحسابات المحاسبية بعمليات النظام",
    account_mappingsManage: "تعديل ربط الحسابات المحاسبية بعمليات النظام",
    account_mappingsExport: "تصدير ربط الحسابات المحاسبية",
    opening_balancesView: "عرض الأرصدة الافتتاحية",
    opening_balancesManage: "إدخال الأرصدة الافتتاحية وتعديلها واعتمادها",
    bank_reconView: "عرض تسويات الحسابات البنكية",
    bank_reconManage: "إجراء تسويات الحسابات البنكية ومطابقة الحركات",
    gl_accountsView: "عرض شجرة الحسابات",
    gl_accountsManage: "إضافة حسابات في شجرة الحسابات وتعديلها",
    journal_entriesView: "عرض قيود اليومية",
    fixed_assetsView: "عرض الأصول الثابتة وإهلاكها",
    fixed_assetsManage: "إضافة الأصول الثابتة وتعديلها واستبعادها",
    journal_entriesPost: "ترحيل قيود اليومية إلى الأستاذ العام",
    warrantiesView: "عرض ضمانات الخدمات وقطع الغيار",
    warrantiesActivate: "تفعيل ضمان لخدمة أو قطعة غيار",
    warrantiesOverride_expired: "قبول مطالبة ضمان بعد انتهاء مدته (صلاحية استثنائية)",
    acceptance_evidenceRead: "عرض أدلة استلام العميل للمركبة",
    acceptance_evidenceShare: "مشاركة أدلة الاستلام مع العميل",
    journal_entriesReopen_period: "إعادة فتح فترة محاسبية مقفلة (صلاحية استثنائية)",
    journal_entriesOverride_control: "تجاوز ضوابط القيود المحاسبية (صلاحية استثنائية)",
    "fleet.reception.carCount_zero": "لا توجد سيارات",
    "fleet.reception.carCount_other": "{{count}} سيارة",
    "fleet.reception.carCount_many": "{{count}} سيارة",
    "fleet.reception.carCount_few": "{{count}} سيارات",
    "fleet.reception.carCount_two": "سيارتان",
    "fleet.reception.carCount_one": "سيارة واحدة",
    "fleet.reception.fleetTag": "أسطول",
    "fleet.reception.fleets": "الأساطيل",
    "fleet.reception.noCars": "لا توجد سيارات مطابقة في الأسطول",
    "fleet.reception.searchCars": "ابحث باللوحة أو رقم الهيكل أو الطراز أو المجموعة",
    "fleet.reception.pickCar": "اختر سيارة الأسطول",
    "hrEmployees.read": "عرض ملفات الموظفين",
    "hrEmployees.manage": "إنشاء ملفات الموظفين وتعديلها وإيقافها",
    "hrReports.read": "عرض تقارير الموارد البشرية",
    "hrPayroll.read": "عرض مسيّرات الرواتب والتعديلات والسلف والإجازات",
    "hrPayroll.manage": "إنشاء مسيّرات الرواتب وإدارة التعديلات والسلف والإجازات",
    "hrPayroll.approve": "اعتماد مسيّرات الرواتب وصرفها وعكسها",
    "hrLabor_cost.view": "عرض أجور الفنيين بالساعة وتعديل تكلفة العمالة",
    Reports: "التقارير",
    Finance: "المحاسبة",
    Operations: "العمليات",
    HR: "الموارد البشرية",

      // Payment-method picker fee breakdown (PaymentMethodPickerModal.js)
      "pmp.commission": "العمولة",
      "pmp.vat": "ضريبة {{pct}}%",
      "pmp.youReceive": "تستلم",
      "pmp.notQualified": "المبلغ غير مؤهل لهذه الطريقة (الحد الأدنى {{min}})",
      // ── RBAC permission catalog: category headers + per-permission labels ──
      // These power PermissionManager.js (t(CategoryNoSpace) and
      // t(`${resource}${Action}`)). Without an AR entry, the i18n
      // parseMissingKeyHandler humanizes the key to Title-Case ENGLISH, which is
      // why the permissions page showed English in the Arabic UI.
      "JobManagement": "إدارة أوامر العمل",
      "Financial": "المالية",
      "TaskManagement": "إدارة المهام",
      "Inventory": "المخزون",
      "CustomerManagement": "إدارة العملاء",
      "VehicleManagement": "إدارة المركبات",
      "UserManagement": "إدارة المستخدمين",
      "Appointments": "المواعيد",
      "ServiceManagement": "إدارة الخدمات",
      "Quality": "الجودة",
      "System": "النظام",
      "job_cardsCreate": "إنشاء أوامر عمل جديدة",
      "job_cardsCreate_only": "إنشاء أوامر العمل مع صلاحية القراءة فقط (لموظفي الاستقبال)",
      "job_cardsRead": "عرض أوامر العمل",
      "job_cardsUpdate": "تعديل أوامر العمل",
      "job_cardsDelete": "حذف أوامر العمل",
      "job_cardsAssign": "إسناد أوامر العمل للفنيين",
      "job_cardsView_all": "عرض جميع أوامر العمل والمهام في الفرع (قائد الفريق)",
      "job_cardsReopen": "إعادة فتح أوامر العمل المغلقة",
      "issue_reportsManage": "إنشاء وتحديث وحل بلاغات الأعطال",
      "ai_diagnosisUse": "استخدام مساعد التشخيص الذكي (مدفوع)",
      "findingsApprove": "اعتماد أو رفض نتائج الفحص",
      "insurance_companiesManage": "إدارة سجل شركات التأمين",
      "insurance_claimsManage": "تسجيل وتحديث مطالبات التأمين",
      "tasksCreate": "إنشاء المهام",
      "tasksRead": "عرض المهام",
      "tasksUpdate": "تعديل المهام",
      "tasksDelete": "حذف المهام",
      "tasksComplete": "إنهاء المهام",
      "tasksAssign": "إعادة إسناد المهمة لفني آخر (قائد الفريق)",
      "tasksApprove_diagnosis": "اعتماد أو رفض التشخيص المنفذ من الفنيين",
      "mediaUpload": "رفع وسائط الفنيين (صور وفيديو وتسجيلات صوتية) وإرفاقها بالمهام",
      "partsRead": "عرض مخزون قطع الغيار",
      "partsUpdate": "تحديث معلومات القطع",
      "partsDelete": "حذف القطع",
      "partsRequest": "طلب قطع الغيار للأعمال",
      "part_categoriesManage": "إدارة فئات القطع",
      "stockRead": "عرض مستويات المخزون وحركته",
      "stockAdjust": "إضافة أو خصم أو تعديل كميات المخزون",
      "purchase_invoicesRead": "عرض فواتير المشتريات",
      "purchase_invoicesCreate": "إنشاء وتعديل فواتير المشتريات",
      "purchase_invoicesPost": "ترحيل فواتير المشتريات للدفاتر (يؤثر على تقييم المخزون)",
      "vehiclesCreate": "إضافة مركبات جديدة",
      "vehiclesRead": "عرض معلومات المركبات",
      "vehiclesUpdate": "تحديث بيانات المركبات",
      "vehiclesDelete": "حذف المركبات",
      "usersCreate": "إنشاء مستخدمين جدد",
      "usersRead": "عرض المستخدمين",
      "usersUpdate": "تحديث معلومات وأدوار المستخدمين",
      "usersDelete": "تعطيل أو حذف المستخدمين",
      "permissionsManage": "إدارة الأدوار وصلاحياتها",
      "appointmentsRead": "عرض المواعيد",
      "appointmentsCreate": "حجز مواعيد جديدة",
      "appointmentsManage": "تأكيد وتسجيل وصول وإلغاء وإعادة جدولة وتحويل المواعيد",
      "appointmentsOverride": "حجز المواعيد أو نقلها خارج قواعد الحجز مع ذكر السبب",
      "booking_settingsManage": "إدارة إعدادات الحجز الإلكتروني والخدمات",
      "booking_pageManage": "تصميم صفحة الحجز الإلكتروني ونشرها وإدارة نطاقها المخصص",
      "booking_analyticsRead": "عرض زيارات الحجز الإلكتروني ومراحل التحويل ونسب عدم الحضور",
      "paymentsRead": "عرض المدفوعات",
      "paymentsRecord": "تسجيل المدفوعات على الفواتير",
      "paymentsRefund": "استرداد المدفوعات",
      "deliver_on_creditExecute": "تسليم المركبة بالأجل قبل السداد الكامل",
      "expensesView": "عرض مصاريف أوامر العمل وفئاتها",
      "expensesManage": "إدارة مصاريف أوامر العمل وفئاتها",
      "cashView": "عرض أرصدة الصندوق والمعاملات النقدية",
      "cashManage": "تسجيل الإيداع والصرف النقدي وتسوية الصندوق",
      "payment_settingsManage": "إعداد مزود الدفع والبوابة وإعدادات التسوية",
      "tenant_refundsRequest": "طلب استرداد المبالغ المحصلة",
      "payout_inquiriesCreate": "إنشاء استفسارات الدفعات",
      "reportsRead": "عرض التقارير والتحليلات",
      "reportsExport": "تصدير التقارير",
      "branchesView_all": "الوصول إلى جميع فروع المؤسسة — عرض كل الفروع في لوحة المعلومات واختيار الفرع في التقارير والتبديل بين الفروع — وليس الفرع الرئيسي فقط",
      "servicesCreate": "إنشاء أنواع الخدمات",
      "servicesRead": "عرض أنواع الخدمات",
      "servicesUpdate": "تحديث أنواع الخدمات",
      "servicesDelete": "حذف أنواع الخدمات",
      "job_card_servicesApprove": "اعتماد أو رفض بنود خدمات أمر العمل",
      "quality_checkPerform": "تنفيذ فحوصات الجودة",
      "quality_checkRead": "عرض نتائج فحص الجودة",
      "settingsRead": "عرض إعدادات النظام",
      "settingsUpdate": "تحديث إعدادات النظام",
      "garage_settingsRead": "عرض إعدادات الفرع",
      "job_cardsOverride_complete": "إتمام خدمة أو قطعة أو مهمة فرعية قسراً رغم عدم اعتمادها أو اكتمالها (تجاوز إداري)",
      "job_cardsForce_start": "بدء العمل على أمر العمل قبل موعده المتفق عليه (تجاوز إداري)",
      "job_cardsWaive_delay_fee": "الإعفاء من رسوم تأخير العميل المستحقة قبل فوترتها",
      "job_cardsReturn_car": "إلغاء أمر العمل وإرجاع السيارة عند رفض العميل للسعر، مع رد المبالغ المحصّلة أو قيدها رصيداً له",
      "job_cardsTransfer": "نقل أمر العمل إلى فرع آخر (مع إلغاء إسنادات الفرع الأصلي)",
      "job_cardsOverride_intake_confirmation": "تسجيل موافقة الاستلام حضورياً عند تعذّر تأكيد العميل عبر الرابط (بإقرار الموظف وذكر السبب)",
      "smart_assistantUse": "استخدام المساعد الذكي (بالصوت أو الكتابة، باستهلاك مدفوع)",
      "partsCreate": "إضافة قطع غيار جديدة",
      "partsApprove": "اعتماد طلبات قطع الغيار",
      "stockOversell_reserved": "إضافة قطعة محجوزة لعرض سعر أو أمر عمل أو مسودة فاتورة أخرى (تجاوز)",
      "purchase_invoicesPay": "تسجيل مدفوعات الموردين (كاملة أو جزئية) على فواتير الشراء",
      "purchase_invoicesReverse": "عكس فاتورة شراء مرحّلة بإشعار دائن وإرجاع المخزون وإلغاء مدفوعات المورد",
      "purchase_invoicesApprove": "اعتماد فواتير الشراء وترحيلها فوق حد المُرحِّل وضمن حدك",
      "customersCreate": "إضافة عملاء جدد",
      "customersRead": "عرض بيانات العملاء",
      "customersUpdate": "تعديل بيانات العملاء وإعادة تنشيطهم",
      "customersDelete": "إيقاف العملاء (قابل للتراجع؛ لا يُحذف العملاء أبداً)",
      "invoicesCreate": "إنشاء الفواتير",
      "invoicesRead": "عرض الفواتير",
      "invoicesUpdate": "تعديل الفواتير",
      "invoicesDelete": "حذف الفواتير",
      "invoicesSend": "إرسال الفواتير إلى العملاء",
      "paymentsSend_link": "إرسال رابط دفع إلى العميل",
      "deliveryVerify": "التحقق من رمز التسليم عند تسليم السيارة",
      "deliveryOverride": "التسليم دون رمز العميل مع ذكر المبرر (تجاوز)",
      "expensesReverse": "عكس مصروف بإشعار دائن وتعويض أي مبلغ نقدي مدفوع",
      "credit_notesView": "عرض إشعارات العكس الدائنة الصادرة على المشتريات والمصروفات",
      "financialsAudit_view": "عرض سجل التدقيق المالي (من رحّل أو عدّل أو عكس، مع القيم قبل التغيير وبعده)",
      "cashEquity": "ترحيل وعرض مسحوبات المالك وزيادات رأس المال وقروض الشركاء",
      "partnersView": "عرض الشركاء وكشوف مسحوباتهم ومساهماتهم",
      "partnersManage": "إضافة الشركاء وتعديلهم وإيقافهم",
      "account_mappingsView": "عرض ربط دليل الحسابات المستخدم في تصدير المحاسب",
      "account_mappingsManage": "تحديد رمز الحساب لكل سبب نقدي ونوع قيد وفئة مصروف",
      "account_mappingsExport": "تنزيل تصدير القيود للمحاسب لفترة محددة",
      "opening_balancesView": "عرض قيد الأرصدة الافتتاحية للمنشأة",
      "opening_balancesManage": "إدخال الأرصدة الافتتاحية واعتمادها عند الإعداد",
      "bank_reconView": "عرض كشوف الحسابات البنكية المستوردة وربط أعمدتها",
      "bank_reconManage": "رفع كشوف الحسابات البنكية وربط أعمدتها وتأكيد الصفوف المقروءة آلياً",
      "pricingView": "عرض أسعار القطع والخدمات",
      "quotationsCreate": "إنشاء عروض الأسعار",
      "quotationsRead": "عرض عروض الأسعار",
      "quotationsUpdate": "تعديل عروض الأسعار",
      "quotationsDelete": "حذف عروض الأسعار",
      "quotationsApprove": "اعتماد عروض الأسعار",
      "discountsApprove": "اعتماد الخصومات فوق حد الطالب وضمن حدك",
      "whatsappConnect": "ربط رقم واتساب وفصله وإدارة إعداداته",
      "whatsappChat": "استخدام صندوق محادثات واتساب (العرض والرد)",
      "whatsappMarketing": "إنشاء حملات واتساب التسويقية وإرسالها",
      "gl_accountsView": "عرض دليل الحسابات",
      "gl_accountsManage": "إضافة الحسابات وتسميتها وإيقافها وتحديد أرصدتها الافتتاحية",
      "journal_entriesView": "عرض ميزان المراجعة ودفاتر الحسابات والقيود وإقفال الفترات",
      "fixed_assetsView": "عرض سجل الأصول الثابتة ومعاينة الإهلاك",
      "fixed_assetsManage": "تسجيل الأصول الثابتة وتعديلها واستبعادها وإهلاكها",
      "journal_entriesPost": "إنشاء القيود اليومية وترحيلها، وإقفال الفترة المحاسبية أو إعادة فتحها",
      "warrantiesView": "عرض حالة الضمان في أمر العمل وفتح متابعة الضمانات",
      "warrantiesActivate": "تفعيل الضمان وتجديده وإلغاؤه على خدمة مكتملة",
      "warrantiesOverride_expired": "إعادة فتح أمر عمل بموجب ضمان منتهٍ",
      "acceptance_evidenceRead": "فتح سجلات موافقات العملاء وتنزيل شهاداتها",
      "acceptance_evidenceShare": "إرسال شهادة الموافقة إلى العميل نفسه",
      "journal_entriesReopen_period": "إعادة فتح فترة محاسبية مقفلة (قد يستند إليها إقرار ضريبة قيمة مضافة مقدَّم)",
      "journal_entriesOverride_control": "ترحيل قيد على حساب رقابي مقيّد (الذمم المدينة أو الدائنة أو ضريبة القيمة المضافة)",
      "garage_settingsUpdate": "تحديث معلومات الفرع والضريبة وإعدادات الإشعارات",
      "termsManage": "صياغة وتحسين ونشر شروط وأحكام المركز",
      "companyManage": "إنشاء أو تحديث أو حذف سجلات الشركات",
      "companyData_export": "تصدير جميع بيانات المنشأة (العملاء، الموارد البشرية، الماليات، الوسائط) كملف أرشيف قابل للتنزيل",
      "settings.creditReminders": "تذكيرات الآجل",
      "settings.creditRemindersEnabled": "تفعيل تذكيرات الآجل",
      "settings.leadDays": "أيام التنبيه المسبق",
      "settings.intervalDays": "التكرار كل (أيام)",
      "settings.maxCount": "الحد الأقصى للتذكيرات",
      "settings.creditOverdueNotify": "إشعار عند تأخر السداد",
      "reports.creditAging": "الذمم (تقادم المستحقات)",
      "reports.bucketCurrent": "حالية",
      "reports.bucket1_7": "1-7 أيام",
      "reports.bucket8_30": "8-30 يوم",
      "reports.bucket30plus": "أكثر من 30 يوم",
      "reports.totalAr": "إجمالي المستحقات",
      "reports.overdueAr": "المستحقات المتأخرة",
      "reports.customersWithAr": "عملاء لديهم مستحقات",
      "reports.oldestDays": "الأقدم (أيام)",
      "credit.outstandingAr": "المستحقات (الذمم)",
      "credit.dueBy": "يستحق بحلول",
      "credit.byInvoice": "حسب الفاتورة",
      "credit.deliverOnCredit": "تسليم بالآجل",
      "credit.dueInDays": "يستحق خلال (أيام)",
      "credit.dueDate": "تاريخ الاستحقاق",
      "credit.daysFromToday": "{{days}} يوم من اليوم",
      "credit.dueInDaysRequired": "عدد أيام الاستحقاق مطلوب",
      "credit.deliveredPendingPayment": "تم التسليم — بانتظار الدفع",
      "credit.handoverConfirmed": "تم تأكيد التسليم برمز الاستلام",
      "credit.sendPaymentLink": "إرسال رابط دفع المبلغ المتبقي",
      "credit.paymentLinkSent": "تم إرسال رابط الدفع للعميل",
      "credit.deliverOnCreditNote": "يتم إصدار الفاتورة الضريبية الآن عند التسليم. ويُسجَّل المبلغ المتبقي كمستحق يجب سداده خلال عدد الأيام الذي تُدخله أدناه.",
      "credit.deliverFailed": "تعذّر التسليم بالآجل",
      "credit.delivering": "جارٍ التسليم…",
      "credit.loadingBalance": "جارٍ تحميل الرصيد…",
      "credit.invoicedTotal": "إجمالي الفاتورة",
      "credit.amountPaid": "المبلغ المدفوع",
      "credit.remainingBalance": "الرصيد المتبقي (مستحق)",
      "credit.balanceUnavailable": "الرصيد غير متاح",
      "credit.pendingPaymentNote": "تم تسليم المركبة بالآجل. تم إصدار الفاتورة الضريبية، والمبلغ المتبقي مستحق على العميل.",
      "credit.totalOwed": "إجمالي المستحق",
      "credit.openingBalance": "رصيد افتتاحي (مرحّل)",
      "credit.customerCreditBalance": "رصيد العميل المتاح",
      "credit.customerCreditBalanceHint": "رصيد عربون متاح يمكن للعميل استخدامه في فاتورة قادمة.",
      "credit.payableNow": "قابل للصرف الآن",
      "credit.payoutButton": "صرف الرصيد",
      "credit.payoutModalTitle": "صرف رصيد العميل",
      "credit.payoutAmountLabel": "المبلغ",
      "credit.payoutAmountHint": "حتى {{amount}} قابل للصرف الآن",
      "credit.payoutMethodLabel": "طريقة الصرف",
      "credit.payoutConfirm": "صرف المبلغ",
      "credit.payoutInvalidAmount": "أدخل مبلغاً بين 0 و {{amount}}",
      "credit.payoutSuccess": "تم صرف {{amount}} — سند(ات) الاسترداد: {{ids}}",
      "credit.payoutPartialError": "{{error}} (تم صرف {{amount}} بالفعل عبر {{count}} سند(ات) قبل الفشل)",
      "credit.payoutFailed": "تعذّر صرف رصيد العميل",
      "credit.vehicleOwed": "المستحقات الآجلة على هذه المركبة",
      "credit.outstandingAlert": "لدى هذا {{who}} رصيد مستحق بالآجل قدره {{amount}}.",
      "credit.who.customer": "العميل",
      "credit.who.vehicle": "المركبة",
      "invoiced": "تمت الفوترة",
      "itemInvoicedLocked": "هذا العنصر على فاتورة معتمدة ومقفل. يجب عكس الفاتورة لتعديله.",
      "pt.customerApprovedBadge": "معتمد من العميل",
      "pt.customerApprovedHint": "وافق العميل على هذا السعر. يمكن تخفيضه ولا يمكن رفعه.",
      "credit.daysOverdue": "أيام التأخير",
      "credit.remindersTitle": "تذكيرات الآجل",
      "credit.remindersDesc": "تذكيرات تلقائية للمركبات المسلّمة بالآجل والتي عليها رصيد مستحق.",
      "credit.remindersEnabled": "تفعيل تذكيرات الآجل",
      "credit.remindersEnabledDesc": "إرسال تذكيرات للعملاء الذين عليهم أرصدة آجلة غير مسددة.",
      "credit.leadDays": "أيام الإشعار المسبق",
      "credit.leadDaysDesc": "عدد الأيام قبل تاريخ الاستحقاق لبدء التذكير.",
      "credit.intervalDays": "الفترة (أيام)",
      "credit.intervalDaysDesc": "عدد الأيام بين كل تذكير والذي يليه.",
      "credit.maxCount": "أقصى عدد للتذكيرات",
      "credit.maxCountDesc": "الحد الأقصى لعدد التذكيرات لكل فاتورة.",
      "credit.remindersSave": "حفظ التذكيرات",
      "credit.remindersSaving": "جارٍ الحفظ…",
      "credit.remindersSaved": "تم حفظ إعدادات تذكيرات الآجل",
      "credit.remindersSaveFailed": "تعذّر حفظ إعدادات تذكيرات الآجل",
      "credit.remindersLoadFailed": "تعذّر تحميل إعدادات تذكيرات الآجل",
      "settings.feedback.title": "تقييم ما بعد التسليم",
      "settings.feedback.desc": "اطلب من العملاء تقييم تجربتهم بعد استلام المركبة.",
      "settings.feedback.enabled": "تفعيل طلبات التقييم",
      "settings.feedback.enabledDesc": "إرسال طلب تقييم للعميل بعد تسليم مركبته.",
      "settings.feedback.delayHours": "التأخير (ساعات)",
      "settings.feedback.delayHoursDesc": "عدد الساعات بعد التسليم قبل إرسال الطلب (0–168). 0 = إرسال فوري عند التسليم.",
      "settings.feedback.googleUrl": "رابط تقييم Google",
      "settings.feedback.googleUrlHelp": "رابط تقييم Google الخاص بالفرع، يُعرض للعميل بعد إرسال تقييمه.",
      "settings.feedback.save": "حفظ",
      "settings.feedback.saving": "جارٍ الحفظ…",
      "settings.feedback.saved": "تم حفظ إعدادات التقييم",
      "settings.feedback.saveFailed": "تعذّر حفظ إعدادات التقييم",
      "settings.feedback.loadFailed": "تعذّر تحميل إعدادات التقييم",
      "settings.rejectionReasons.title": "أسباب رفض مخصّصة",
      "settings.rejectionReasons.desc": "أضف حتى 3 أسباب خاصة بالمركز يختارها العميل عند رفض بند من عرض السعر، بالإضافة إلى الأسباب السبعة الجاهزة.",
      "settings.rejectionReasons.empty": "لا توجد أسباب مخصّصة بعد — يرى العميل الأسباب السبعة الجاهزة فقط.",
      "settings.rejectionReasons.arLabel": "التسمية بالعربية",
      "settings.rejectionReasons.enLabel": "التسمية بالإنجليزية",
      "settings.rejectionReasons.arPlaceholder": "مثال: قطعة غير متوفرة",
      "settings.rejectionReasons.enPlaceholder": "مثال: Part unavailable",
      "settings.rejectionReasons.codePreview": "الرمز: {{code}}",
      "settings.rejectionReasons.add": "إضافة سبب",
      "settings.rejectionReasons.maxReached": "الحد الأقصى 3 أسباب مخصّصة",
      "settings.rejectionReasons.save": "حفظ الأسباب",
      "settings.rejectionReasons.saved": "تم حفظ أسباب الرفض",
      "settings.rejectionReasons.saveFailed": "تعذّر حفظ أسباب الرفض",
      "settings.rejectionReasons.errorRequired": "التسمية بالعربية والإنجليزية مطلوبتان معًا",
      "settings.rejectionReasons.errorTooLong": "يجب ألا تتجاوز التسمية 120 حرفًا",
      "settings.rejectionReasons.errorCodeTooShort": "التسمية الإنجليزية تحتاج حرفين أو رقمين على الأقل",
      "settings.rejectionReasons.errorCollision": "\"{{code}}\" يطابق سببًا جاهزًا — جرّب صياغة مختلفة",
      "settings.rejectionReasons.errorDuplicate": "سببان أنتجا نفس الرمز (\"{{code}}\") — جرّب صياغة مختلفة",
      "feedback.title": "تقييمات العملاء",
      "feedback.avgRating": "متوسط التقييم",
      "feedback.responseRate": "نسبة الاستجابة",
      "feedback.totalResponses": "إجمالي الردود",
      "feedback.filterRating": "التقييم",
      "feedback.allRatings": "كل التقييمات",
      "feedback.noData": "لا توجد تقييمات بعد",
      "feedback.viewJob": "عرض أمر العمل",
      "feedback.comment": "التعليق",
      "feedback.customer": "العميل",
      "feedback.branch": "الفرع",
      "feedback.date": "التاريخ",
      "taskMonitoring.navTitle": "متابعة المهام",
      "taskMonitoring.pageTitle": "متابعة المهام",
      "taskMonitoring.taskName": "المهمة",
      "taskMonitoring.total": "إجمالي المهام",
      "taskMonitoring.notStarted": "لم تبدأ",
      "taskMonitoring.inProgress": "قيد التنفيذ",
      "taskMonitoring.completed": "مكتملة",
      "taskMonitoring.overdue": "متأخرة",
      "taskMonitoring.shouldHaveStarted": "يجب أن تبدأ",
      "taskMonitoring.employee": "الفني",
      "taskMonitoring.allEmployees": "كل الفنيين",
      "taskMonitoring.status": "الحالة",
      "taskMonitoring.allStatuses": "كل الحالات",
      "taskMonitoring.priority": "الأولوية",
      "taskMonitoring.allPriorities": "كل الأولويات",
      "taskMonitoring.overdueOnly": "المتأخرة فقط",
      "taskMonitoring.search": "ابحث في المهام وأوامر العمل والمركبات...",
      "taskMonitoring.dateFrom": "الانتهاء المتوقع من",
      "taskMonitoring.dateTo": "الانتهاء المتوقع حتى",
      "taskMonitoring.nudge": "تذكير",
      "taskMonitoring.nudgeSent": "تم إرسال التذكير",
      "taskMonitoring.nudgeCooldown": "تم التذكير مسبقًا خلال الساعة",
      "taskMonitoring.reassign": "إعادة الإسناد",
      "taskMonitoring.reassigned": "تمت إعادة الإسناد والتنبيه",
      "taskMonitoring.noData": "لا توجد مهام تطابق الفلاتر",
      "taskMonitoring.jobCard": "أمر العمل",
      "taskMonitoring.vehicle": "المركبة",
      "taskMonitoring.startedAt": "تاريخ البدء",
      "taskMonitoring.estimatedCompletion": "الانتهاء المتوقع",
      "set.expenseCategories": "فئات المصروفات",
      "purchase.lineTypeStock": "قطعة مخزون",
      "purchase.lineTypeNonStock": "مصروف",
      "purchase.expenseCategory": "فئة المصروف",
      "purchase.selectExpenseCategory": "اختر فئة",
      "purchase.expenseDescription": "الوصف",
      "purchase.expenseDescriptionPlaceholder": "مثال: إيجار المركز، مرافق، أدوات",
      "purchase.netAmount": "المبلغ الصافي",
      "purchase.netAmountHint": "المبلغ قبل الضريبة",
      "purchase.vatRate": "نسبة الضريبة %",
      "purchase.vatAmount": "مبلغ الضريبة",
      "purchase.vatAmountHint": "الصافي × النسبة (تلقائي)",
      "purchase.itemExpenseCategoryRequired": "البند {{n}}: اختر فئة مصروف",
      "purchase.itemAmountGreaterThanZero": "البند {{n}}: يجب أن يكون المبلغ أكبر من صفر",
      "purchase.fixHighlightedFields": "{{count}} حقول تحتاج إلى مراجعة، وهي مُظلّلة بالأسفل.",
      "purchaseWarranty.duration": "{{count}} شهر",
      "purchaseWarranty.expires": "ينتهي في {{date}}",
      "purchase.warrantyHasWarranty": "على هذه الدفعة ضمان من المورّد",
      "purchase.warrantyMonths": "مدة الضمان (بالأشهر)",
      "purchase.warrantyMonthsPlaceholder": "مثال: ١٢",
      "purchase.warrantyNotes": "مرجع الضمان أو ملاحظات",
      "purchase.warrantyNotesPlaceholder": "رقم مطالبة المورّد، الشروط",
      "purchase.warrantyMonthsRequired": "البند {{n}}: اكتب مدة الضمان بالأشهر",
      "purchase.warrantyMonthsInvalid": "البند {{n}}: يجب أن تكون مدة الضمان رقمًا صحيحًا بين ١ و ٦٠٠ شهرًا",
      "exp.catTitle": "فئات المصروفات",
      "exp.catDesc": "الفئات المستخدمة في بنود فواتير الشراء غير المخزنية (المصروفات) لتتبّع ضريبة المدخلات والمصروفات.",
      "exp.catName": "الاسم (إنجليزي)",
      "exp.catNamePlaceholder": "مثال: Rent",
      "exp.catNameAr": "الاسم (عربي)",
      "exp.catNameArPlaceholder": "مثال: الإيجار",
      "exp.catAdd": "إضافة",
      "exp.catAdded": "تمت إضافة فئة المصروف",
      "exp.catAddFailed": "تعذّر إضافة فئة المصروف",
      "exp.catNameRequired": "اسم الفئة مطلوب",
      "exp.catDeleted": "تمت إزالة فئة المصروف",
      "exp.catDeleteFailed": "تعذّر إزالة فئة المصروف",
      "exp.catDeleteConfirm": "إزالة فئة المصروف \"{{name}}\"؟",
      "exp.catLoadFailed": "تعذّر تحميل فئات المصروفات",
      "exp.catPreset": "افتراضي",
      "exp.catEmptyTitle": "لا توجد فئات مصروفات بعد",
      "exp.catEmptyDesc": "أضف أول فئة مخصصة من الأعلى.",
      "vat.tabPosition": "وضع الضريبة",
      "vat.outputVat": "ضريبة المخرجات",
      "vat.inputVat": "ضريبة المدخلات",
      "vat.netDue": "صافي الضريبة المستحقة",
      "vat.net": "الصافي",
      "vat.rate": "النسبة",
      "vat.category": "الفئة",
      "vat.byCategory": "ضريبة المدخلات حسب الفئة",
      "vat.netExpense": "صافي المصروف",
      "vat.notApplicable": "الضريبة غير مطبّقة",
      "vat.notApplicableDesc": "هذا المركز غير مسجّل في ضريبة القيمة المضافة، لذا لا يوجد وضع ضريبي لعرضه.",
      "rpt.tabExpenseVsPlan": "المصروفات مقابل الخطة",
      "rpt.tabGarageExpenses": "مصروفات المركز",
      "rpt.tabSuppliers": "الموردون",
      // Task 5.2 (payment-receipt redesign): Customer Advances mini-report.
      "rpt.tabCustomerAdvances": "عربون العملاء",
      "rpt.rcRef": "رقم السند",
      "rpt.original": "الأصلي",
      "rpt.reserved": "المحجوز",
      "rpt.consumed": "المستهلك",
      "rpt.refunded": "المسترد",
      "rpt.available": "المتاح",
      "rpt.planned": "المخطط",
      "rpt.actual": "الفعلي",
      "rpt.variance": "الفرق",
      "rpt.supplier": "المورّد",
      "rpt.categories": "الفئات",
      "rpt.byCategory": "حسب الفئة",
      // HR statutory v2, T9 — payroll leg synthetic category + both-sources warning.
      "rpt.category.payroll": "الرواتب",
      "rpt.payrollBothSourcesWarning": "تم العثور على مسير رواتب مدفوع ومصروفات يدوية متعلقة بالرواتب لهذه الفترة. راجع فئات المصروفات لتجنب احتساب تكلفة الرواتب مرتين.",
      "nav.grp.overview": "نظرة عامة",
      "nav.grp.tenants": "المشتركون",
      "nav.grp.comms": "المراسلات",
      "nav.grp.platform": "المنصة",
      "nav.grp.operations": "العمليات",
      "nav.grp.sales": "المبيعات والفوترة",
      "nav.grp.financials": "المالية",
      "nav.grp.financeReports": "التقارير المالية",
      "nav.grp.inventory": "المخزون",
      "nav.grp.contacts": "جهات الاتصال",
      "nav.grp.hr": "الموارد البشرية",
      "nav.grp.admin": "الإدارة",
      "nav.grp.customers": "العملاء والمركبات",
      "nav.grp.followUp": "المتابعة",
      "nav.grp.purchasesInventory": "المشتريات والمخزون",
      "nav.grp.reports": "التقارير",
      "nav.grp.help": "المساعدة",
      "cash.nav": "كشف النقدية",
      "cash.title": "كشف النقدية",
      "cash.subtitle": "النقد في الصندوق ورصيد البنك مع كل حركة.",
      "cash.opening": "تعيين/تعديل الرصيد الافتتاحي",
      "cash.openingEdit": "تعديل",
      "cash.transfer": "تحويل",
      "cash.adjustment": "تسوية",
      "cash.kpiHand": "النقد في الصندوق",
      "cash.kpiBank": "النقد في البنك",
      "cash.kpiTotal": "إجمالي النقد",
      "cash.kpiPending": "معلّق من بوابة الدفع",
      "cash.kpiPendingHint": "تم تحصيله إلكترونياً ولم يُسوَّ بعد إلى حسابك البنكي.",
      "cash.kpiScopeHint": "الوضع الحالي — كامل الفترة (لا يتأثر بتصفية التاريخ أدناه).",
      "cash.kpiScopeBranchHint": "الوضع الحالي للفرع المحدد والحسابات المشتركة — كامل الفترة.",
      "cash.openingHeading": "الأرصدة الافتتاحية",
      "cash.acct.heading": "الحسابات",
      "cash.acct.hint": "كل صندوق وكل حساب بنكي تحتفظ فيه المنشأة بالنقد. تتم التسوية البنكية بمطابقة كشف الحساب مع أحد هذه الحسابات، لذا يلزم لكل بنك حساب مستقل.",
      "cash.acct.add": "إضافة حساب",
      "cash.acct.rename": "إعادة تسمية",
      "cash.acct.default": "افتراضي",
      "cash.acct.inactive": "غير نشط",
      "cash.acct.kind": "النوع",
      "cash.acct.name": "اسم الحساب",
      "cash.acct.namePlaceholder": "مثال: الراجحي — الحساب الرئيسي",
      "cash.acct.addTitle": "إضافة حساب",
      "cash.acct.addDesc": "صندوق إضافي أو حساب بنكي آخر. لا يمكن تغيير النوع بعد الإنشاء.",
      "cash.acct.renameTitle": "إعادة تسمية الحساب",
      "cash.acct.renameDesc": "يتغيَّر الاسم فقط، ولا تتأثَّر الأرصدة ولا الحركات المسجَّلة.",
      "cash.acct.created": "تمت إضافة الحساب",
      "cash.acct.renamed": "تم تغيير اسم الحساب",
      "cash.acct.errName": "أدخل اسماً للحساب",
      "cash.acct.errDuplicate": "يوجد حساب آخر بالاسم نفسه",
      "cash.acct.errNoAccount": "اختر حساباً",
      "cash.acct.branch": "الفرع",
      "cash.acct.branchHint": "الحساب المشترك يخص المنشأة كاملة، أما حساب الفرع فيخص ذلك الفرع وحده: نقده ورصيده.",
      "cash.acct.shared": "مشترك (جميع الفروع)",
      "cash.acct.activate": "تفعيل",
      "cash.acct.deactivate": "إيقاف",
      "cash.acct.activated": "تم تفعيل الحساب",
      "cash.acct.deactivated": "تم إيقاف الحساب",
      "cash.acct.filter": "الحساب",
      "cash.acct.allAccounts": "جميع الحسابات",
      "cash.errSameAccount": "اختر حسابين مختلفين",
      "cash.account": "الحساب",
      "cash.kind.hand": "النقد في الصندوق",
      "cash.kind.bank": "البنك",
      "cash.kind.credit": "آجل (غير مدفوع)",
      "pay.method.label": "طريقة الدفع",
      "pay.method.hand": "نقداً",
      "pay.method.bank": "تحويل بنكي",
      "pay.method.pos": "نقاط بيع (شبكة)",
      "pay.method.credit": "آجل (غير مدفوع)",
      "pay.method.cash": "نقداً",
      "pay.method.bank_transfer": "تحويل بنكي",
      "pay.method.cheque": "شيك",
      "pay.method.manual": "يدوي",
      "pay.method.fromAccount": "الحساب المدفوع منه",
      "pay.method.toAccount": "الحساب المودع فيه",
      "pay.method.loadFailed": "تعذّر تحميل الحسابات. سيتم استخدام الحساب الافتراضي لهذه الطريقة.",
      "pay.method.retiredAccount": "حساب مستخدم سابقاً (لم يعد متاحاً)",
      "cash.locked": "مقفل",
      "cash.asOf": "حتى تاريخ",
      "cash.current": "الحالي",
      "cash.openingTitle": "الرصيد الافتتاحي",
      "cash.openingDesc": "حدّد الرصيد الافتتاحي للحساب. يُقفل بمجرد تسجيل أول حركة.",
      "cash.openingBalance": "الرصيد الافتتاحي",
      "cash.openingDate": "تاريخ الافتتاح",
      "cash.openingLocked": "الرصيد الافتتاحي مقفل — توجد حركات مسجّلة لهذا الحساب.",
      "cash.openingLockedHint": "يوجد لهذا الحساب حركات بالفعل، لذا لم يعد بالإمكان تغيير رصيده الافتتاحي.",
      "cash.openingSaved": "تم حفظ الرصيد الافتتاحي",
      "cash.saveFailed": "تعذّر الحفظ. حاول مرة أخرى.",
      "cash.errAmount": "أدخل مبلغاً صحيحاً",
      "cash.from": "من",
      "cash.to": "إلى",
      "cash.amount": "المبلغ",
      "cash.note": "ملاحظة",
      "cash.transferTitle": "تحويل بين الحسابات",
      "cash.transferDesc": "نقل الأموال بين الصندوق والبنك.",
      "cash.transferDo": "تحويل",
      "cash.transferSaved": "تم تسجيل التحويل",
      "cash.direction": "الاتجاه",
      "cash.dirIn": "وارد",
      "cash.dirOut": "صادر",
      "cash.adjustmentTitle": "تسوية يدوية",
      "cash.adjustmentDesc": "تصحيح رصيد حساب بالزيادة أو النقصان.",
      "cash.adjustmentNoteHint": "سبب مختصر لهذه التسوية (مستحسن).",
      "cash.adjustmentSaved": "تم تسجيل التسوية",
      "cash.col.date": "التاريخ",
      "cash.col.type": "النوع",
      "cash.col.reference": "المرجع",
      "cash.col.branch": "الفرع",
      "cash.col.in": "وارد",
      "cash.col.out": "صادر",
      "cash.col.running": "الرصيد الجاري",
      "cash.entry.opening": "افتتاحي",
      "cash.entry.transfer": "تحويل",
      "cash.entry.adjustment": "تسوية",
      "cash.entry.expense": "مصروف",
      "cash.entry.payment": "دفعة",
      "cash.entry.refund": "استرداد",
      "cash.entry.ar_collection": "دفعة عميل",
      "cash.entry.expense_reversal": "عكس مصروف",
      "cash.entry.supplier_payment": "دفعة مورد",
      "cash.entry.supplier_payment_reversal": "عكس دفعة مورد",
      "cash.entry.payout_settlement": "تسوية مستحقات",
      "cash.entry.own_account_payment": "دفعة بوابة (حساب خاص)",
      "cash.entry.own_account_refund": "استرداد بوابة (حساب خاص)",
      "cash.entry.payment_gateway_fee": "رسوم بوابة الدفع",
      "cash.entry.sales_return": "استرداد مرتجع مبيعات",
      "cash.entry.customer_advance_in": "دفعة مقدمة من العميل",
      "cash.entry.advance_refund_out": "استرداد دفعة مقدمة",
      "cash.entry.owner_drawing": "مسحوبات المالك",
      "cash.entry.owner_drawing_reversal": "عكس مسحوبات المالك",
      "cash.entry.capital_injection": "إيداع رأس مال من المالك",
      "cash.entry.capital_injection_reversal": "عكس إيداع رأس المال",
      "cash.mv.in": "نقد وارد",
      "cash.mv.out": "نقد صادر",
      "cash.mv.inTitle": "تسجيل نقد وارد",
      "cash.mv.outTitle": "تسجيل نقد صادر",
      "cash.mv.inDesc": "سجِّل مبلغاً نقدياً دخل إلى المنشأة.",
      "cash.mv.outDesc": "سجِّل مبلغاً نقدياً خرج من المنشأة — ليس فاتورة مورِّد ولا استرداداً لعميل.",
      "cash.mv.reason": "سبب الحركة",
      "cash.mv.reasonPick": "اختر…",
      "cash.mv.category": "التصنيف",
      "cash.mv.glAccount": "الحساب",
      "cash.mv.errGlAccount": "اختر الحساب الذي تخصه هذه الحركة.",
      "cash.mv.accountsUnavailable": "تعذّر تحميل دليل الحسابات، لذلك يطلب هذا النموذج سبب الحركة بدلاً من ذلك.",
      "cash.mv.noReasons": "لا توجد أسباب مُعرَّفة بعد. راجع المدير لإضافة أسباب الحركات النقدية.",
      "cash.mv.amount": "المبلغ",
      "cash.mv.account": "الحساب (نقد أو بنك)",
      "cash.mv.who": "الطرف المعني",
      "cash.mv.whoPick": "اختر الاسم",
      "cash.mv.noPartners": "يجب أن يكون الاسم مُسجَّلاً في شاشة الشركاء. أضِفه هناك أولاً، ثم سجِّل الحركة عليه.",
      "cash.mv.date": "التاريخ",
      "cash.mv.note": "ملاحظة (اختياري)",
      "cash.mv.lockedHint": "المبلغ والحساب والتاريخ مأخوذة من سطر البنك ولا يمكن تغييرها هنا — بها تُربط هذه الحركة به.",
      "cash.mv.attach": "صورة المستند (اختياري)",
      "cash.mv.attachRemove": "حذف",
      "cash.mv.attachFailed": "تعذَّر رفع الصورة. حاول مرة أخرى.",
      "cash.mv.uploading": "جارٍ رفع الصورة…",
      "cash.mv.savedIn": "تم حفظ النقد الوارد",
      "cash.mv.savedOut": "تم حفظ النقد الصادر",
      "cash.mv.errReason": "اختر سبب الحركة",
      "cash.mv.errAccount": "اختر الحساب",
      "cash.mv.reverse": "عكس الحركة",
      "cash.mv.reverseTitle": "عكس هذه الحركة؟",
      "cash.mv.reverseMessage": "يبقى القيد الأصلي كما هو، ويُضاف قيد عكسي يردُّ المبلغ. لا يُحذف شيء، ولا يمكن عكس الحركة أكثر من مرة.",
      "cash.mv.reverseConfirm": "نعم، اعكس الحركة",
      "cash.mv.reversed": "تم عكس الحركة وردُّ المبلغ.",
      "cash.mv.reverseFailed": "تعذَّر عكس الحركة. حاول مرة أخرى.",
      // See matching en comment: global keys, not fp.*.
      "error": "خطأ",
      "refresh": "تحديث",
      "total": "الإجمالي",
      "fp.nav": "المركز المالي",
      "fp.title": "المركز المالي",
      "fp.subtitle": "الوضع المالي للمنشأة اليوم: النقد، والمستحق لك لدى العملاء، والمستحق عليك، وقيمة القطع في المستودع.",
      "fp.notBalanceSheet": "هذه ليست قائمة مركز مالي",
      "fp.notBalanceSheetNote": "يمسك موتورمايند دفاتر محاسبية فعلاً: دليل حسابات، ودفتر يومية، وميزان مراجعة، وأرصدة افتتاحية، وإقفال فترات. غير أن هذه الشاشة لا تقرأ منها؛ فهي العرض التشغيلي الذي يجمع المستندات والحركات النقدية المسجَّلة حتى اليوم، ولذلك لن تتوازن أرقامها كما تتوازن قائمة المركز المالي، ولا تتضمَّن أرصدة مُرحَّلة من سنوات سابقة.",
      "fp.scopeNote": "النقد والبنك على مستوى الشركة بالكامل. أما المستحق لك والمستحق عليك وقيمة القطع فهي للفروع المصرَّح لك بالاطِّلاع عليها.",
      "fp.asOf": "لتاريخ",
      "fp.cash": "النقد والبنك",
      "fp.cashTotal": "إجمالي النقد",
      "fp.hand": "في الصندوق",
      "fp.bank": "في البنك",
      "fp.col.account": "الحساب",
      "fp.col.kind": "النوع",
      "fp.col.balance": "الرصيد",
      "fp.kind.hand": "نقد",
      "fp.kind.bank": "بنك",
      "fp.noAccounts": "لا توجد حسابات نقد أو بنك",
      "fp.noAccountsDesc": "عرِّف صندوق النقد والحساب البنكي أولاً، لتُسجَّل كل دفعة في حسابها الصحيح.",
      "fp.receivables": "ذمم مدينة",
      "fp.payables": "ذمم دائنة",
      "fp.stock": "قطع الغيار في المستودع",
      "fp.fromDocuments": "من الفواتير",
      "fp.opening": "من القيد الافتتاحي",
      "fp.equity": "حقوق الملّاك",
      "fp.equityNote": "ما سحبه المالك أو الشركاء من المنشأة أو أودعوه فيها. المبالغ المسحوبة خرجت فعلاً من الصندوق، وهي محتسبة ضمن النقد أعلاه — غير أنها ليست مصروفاً على المنشأة.",
      "fp.cutover": "محتسب اعتباراً من",
      "fp.cutoverNote": "المسحوبات السابقة لهذا التاريخ غير مُدرجة هنا، إذ كانت تُقيَّد آنذاك كمصروف عادي.",
      "fp.cutoverMissing": "لم يُحدَّد تاريخ بداية. حدِّده ليُعرف من أي تاريخ تُحتسب هذه المبالغ.",
      "fp.bs.title": "قائمة المركز المالي",
      "fp.bs.derivedFrom": "مستخرجة من دفتر الأستاذ العام حتى تاريخ {{date}}.",
      "fp.bs.openedOn": "تراكمية منذ افتتاح الدفاتر في {{date}}.",
      "fp.bs.sourceSnapshot": "فترة مُقفلة — أرقام مجمَّدة",
      "fp.bs.sourceLive": "مباشر — يُعاد احتسابه من الحركة المفتوحة",
      "fp.bs.zeroOmitted": "الحسابات ذات الرصيد صفر غير مُدرجة في القوائم، لكنها محتسبة ضمن الإجماليات.",
      "fp.bs.assets": "الأصول",
      "fp.bs.liabilities": "الالتزامات",
      "fp.bs.equity": "حقوق الملكية",
      "fp.bs.netResult": "صافي نتيجة الفترة",
      "fp.bs.netResultNote": "الإيرادات ناقصاً المصروفات منذ افتتاح الدفاتر.",
      "fp.bs.col.code": "الرمز",
      "fp.bs.col.account": "الحساب",
      "fp.bs.col.balance": "الرصيد",
      "fp.bs.noAccounts": "لا يوجد في هذا القسم حساب ذو رصيد",
      "fp.bs.checkLine": "الأصول = الالتزامات + حقوق الملكية",
      "fp.bs.balanced": "متوازنة — الأصول تساوي الالتزامات مضافاً إليها حقوق الملكية",
      "fp.bs.unbalanced": "هذه القائمة غير متوازنة",
      "fp.bs.unbalancedDesc": "تختلف الأصول عن الالتزامات مضافاً إليها حقوق الملكية بالمبلغ الظاهر. لا تعتمد هذه الأرقام ولا ترسلها: هناك خلل في الدفاتر، وكل إجمالي في هذه الصفحة موضع شك إلى أن يُصحَّح. افتح ميزان المراجعة وعالج المستندات المدرجة فيه كمعوِّقات.",
      "fp.bs.difference": "مقدار عدم التوازن",
      "fp.bs.operationalDetail": "التفصيل التشغيلي",
      "fp.bs.operationalDetailNote": "المال نفسه معروضاً بطريقة العمل اليومي: أين يوجد النقد من صندوق وحساب بنكي، وما لم يسدده العملاء بعد، وما لم يُسدَّد للموردين بعد. لا شيء هنا يُضاف إلى القائمة أعلاه.",
      "fp.bs.switchOn.general_ledger": "فعِّل خاصية دفتر الأستاذ العام ليُعرض المركز المالي الحقيقي من الدفاتر.",
      "fp.bs.switchOn.opening_balances": "اعتمد الأرصدة الافتتاحية ليعرف الدفتر بمَ افتُتحت الحسابات، فتظهر هنا قائمة المركز المالي الحقيقية.",
      "fp.bs.switchOn.scope": "قائمة المركز المالي بيان على مستوى الشركة. اعرض هذه الشاشة بنطاق جميع الفروع للاطلاع عليها.",
      "fp.bs.switchOn.generic": "فعِّل دفتر الأستاذ العام واعتمد الأرصدة الافتتاحية لتظهر هنا قائمة المركز المالي الحقيقية.",
      "fp.bs.blockedTitle": "تعذّر عرض القائمة",
      "fp.bs.notReadyTitle": "قائمة المركز المالي غير جاهزة بعد",
      "fp.bs.warnedTitle": "متوازنة، لكن ليس كل شيء مبوَّباً",
      "fp.bs.warnedDesc": "الإجماليات صحيحة، لكن بعض المبالغ ما زالت في حساب الفروقات غير المسوّاة لأن مستنداً لم يتطابق مع نفسه. صفِّ هذه المبالغ قبل اعتماد القائمة.",
      "fp.bs.blockedDesc": "منع شيء في الدفاتر احتساب الأرقام، لذا فالأقسام أدناه ليست أرصدتك الحقيقية. المشكلات مذكورة تحتها.",
      "fp.bs.switchOn.chart": "دفتر الأستاذ العام مفعَّل والأرصدة الافتتاحية معتمدة، لكن لا يوجد دليل حسابات لهذه الشركة بعد. أنشئ دليل الحسابات لعرض القائمة.",
      "fp.bs.switchOn.ledger_unavailable": "تعذّرت قراءة الدفاتر الآن. الأرقام التشغيلية أدناه غير متأثرة — أعد المحاولة، وتواصل مع الدعم إن تكرر ذلك.",
      "fa.title": "الأصول الثابتة",
      "fa.subtitle": "ما تملكه الشركة، وما استُهلك من قيمته حتى الآن، وما زالت قيمته الدفترية لكل أصل.",
      "fa.accessDenied": "لا تملك صلاحية الوصول إلى الأصول الثابتة",
      "fa.accessDeniedDesc": "اطلب من المسؤول منحك صلاحية fixed_assets.view.",
      "fa.featureDisabled": "ميزة دفتر الأستاذ العام غير مُفعّلة لهذه الشركة",
      "fa.featureDisabledDesc": "يُرحّل إهلاك الأصول الثابتة إلى دفتر الأستاذ العام، لذا يجب تفعيل هذه الميزة أولًا.",
      "fa.addAsset": "تسجيل أصل",
      "fa.linkPurchase": "ربط بفاتورة",
      "fa.addLegacy": "أصل مملوك سابقاً",
      "fa.status.cancelled": "ملغي",
      "fa.reissueInvoice": "إعادة إصدار الفاتورة",
      "fa.reissueTitle": "إعادة إصدار فاتورة بيع الأصل",
      "fa.reissueDesc": "إعادة إصدار الفاتورة الضريبية لهذا الأصل المستبعد. يمكن تحديد مشترٍ معدل (اختياري).",
      "fa.reissueConfirm": "إعادة الإصدار",
      "fa.reissued": "تمت إعادة إصدار فاتورة البيع.",
      "fa.reissueFailed": "تعذّر إعادة إصدار فاتورة البيع.",
      "fa.correctedBuyerOptional": "المشتري المصحح (اختياري)",
      "fa.buyer": "المشتري",
      "fa.disposeReason": "سبب الاستبعاد",
      "fa.disposeReason.scrapped": "خردة / شطب",
      "fa.disposeReason.sale": "بيع",
      "fa.salePriceNet": "سعر البيع (غير شامل ضريبة القيمة المضافة)",
      "fa.vatRate15": "ضريبة القيمة المضافة (15%)",
      "fa.totalWithVat": "الإجمالي (شامل ضريبة القيمة المضافة)",
      "fa.dateFixedToday": "ستصدر فاتورة البيع بتاريخ اليوم.",
      "fa.selectBranch": "الفرع",
      "fa.disposeSuccessTitle": "تم استبعاد الأصل",
      "fa.disposeSuccessInvoice": "تم إصدار فاتورة البيع",
      "fa.invoiceNumber": "رقم الفاتورة",
      "fa.zatcaStatus": "حالة الفوترة الإلكترونية",
      "fa.publicLink": "رابط الفاتورة العام",
      "fa.copyLink": "نسخ الرابط",
      "fa.linkCopied": "تم نسخ الرابط إلى الحافظة",
      "fa.done": "تم",
      "fa.saleInvoice": "فاتورة بيع",
      "fa.errBuyerRequired": "اختر مشتري الأصل.",
      "fa.errGarageRequired": "اختر فرعًا متاحًا لبيع هذا الأصل على مستوى الشركة.",
      "fa.errSaleProceedsRequired": "يتطلب بيع الأصل الثابت متحصلات موجبة قبل ضريبة القيمة المضافة.",
      "fa.errDisposalDateRequired": "تاريخ الاستبعاد مطلوب.",
      "fa.errDisposalDateFuture": "لا يمكن أن يكون تاريخ الاستبعاد في المستقبل.",
      "fa.dep.postThrough": "ترحيل حتى {{period}}",
      "fa.dep.foldedHeading": "الإهلاك المرحل المدمج",
      "fa.dep.foldedIntro": "أقساط تعذر ترحيلها في أشهرها الأصلية ودُمجت في أول شهر مفتوح:",
      "fa.dep.foldedReason.closed": "فترة مغلقة",
      "fa.dep.foldedReason.before_books": "قبل فتح الدفاتر",
      "fa.dep.foldedReason.before_recognition": "قبل الاعتراف",
      // Fixed assets — linking a posted purchase, and assets owned before MotorMind
      "fa.link.title": "اربط هذا الأصل بفاتورة شرائه",
      "fa.link.desc": "اختر بند الفاتورة الذي دفع ثمن هذا الأصل. تُنقل التكلفة من حساب المصروف إلى حساب الأصل نفسه بتاريخ الفاتورة — أي أن القيد يُصحَّح فعلياً ولا يُكتفى بالإشارة إليه.",
      "fa.link.assetCost": "هذا الأصل مسجَّل بقيمة",
      "fa.link.col.invoice": "الفاتورة",
      "fa.link.col.date": "تاريخ الفاتورة",
      "fa.link.col.description": "البند",
      "fa.link.col.qty": "الكمية",
      "fa.link.col.cost": "إجمالي البند",
      "fa.link.exactMatch": "مطابق للتكلفة المسجَّلة",
      "fa.link.linkCta": "اربط",
      "fa.link.linkedBadge": "مرتبط",
      "fa.link.linkedDesc": "تكلفة هذا الأصل مرسملة بالفعل من بند الفاتورة هذا. لا تلغِ الربط إلا إذا كان مرتبطاً بالبند الخطأ — فإلغاء الربط يعيد التكلفة إلى مكانها السابق.",
      "fa.link.capitalised": "المُرسمَل",
      "fa.link.unlinkCta": "إلغاء الربط",
      "fa.link.unlinkTitle": "إلغاء ربط هذه الفاتورة؟",
      "fa.link.unlinkMessage": "ستعود التكلفة إلى الحساب الذي قيّدتها فيه الفاتورة أصلاً، ويبقى هذا الأصل بلا فاتورة شراء خلفه.",
      "fa.link.unlinkSuccess": "تم إلغاء ربط الفاتورة.",
      "fa.link.linkSuccess": "تم الربط بالفاتورة {{ref}}. التكلفة الآن على حساب الأصل.",
      "fa.link.empty": "لا يوجد بند فاتورة قابل للربط",
      "fa.link.emptyDesc": "لا يوجد في فواتير الموردين المرحَّلة ما يبدو أنه دفع ثمن هذا الأصل. هذا طبيعي إذا كانت الشركة تملك الأصل قبل استخدام النظام، أو إذا أُدخل الشراء كبند مخزون. بنود المخزون لا يمكن رسملتها — إن كان البند أصلاً بالفعل، فاعكس الفاتورة وأعد إدخاله كبند غير مخزني.",
      "fa.link.loadFailed": "تعذَّر تحميل بنود الفواتير القابلة للربط بهذا الأصل.",
      "fa.link.failed": "تعذَّر ربط هذه الفاتورة.",
      "fa.link.difference": "الفرق:",
      "fa.link.excess": "يتجاوز الفاتورة بمقدار:",
      "fa.link.field.purchase_invoice_item_id": "بند الفاتورة",
      "fa.link.field.purchase_invoice_id": "الفاتورة",
      "fa.link.field.asset_id": "الأصل",
      "fa.link.why.FA_LINK_ALREADY_LINKED": "هذا البند مُرسمَل بالفعل على أصل آخر. اختر بنداً غيره، أو ألغِ ربطه بالأصل الآخر أولاً.",
      "fa.link.why.FA_LINK_ASSET_LINKED": "لهذا الأصل فاتورة شراء مرتبطة به بالفعل. ألغِ ذلك الربط قبل ربط بند آخر.",
      "fa.link.why.FA_LINK_STOCK_LINE": "بند المخزون دخل المخزون بالفعل، فلا يمكن أن تصبح تكلفته أصلاً كذلك. اعكس الفاتورة وأعد إدخال البند كبند غير مخزني.",
      "fa.link.why.FA_LINK_INVOICE_DRAFT": "الفاتورة المرحَّلة وحدها هي التي حرّكت المال. رحِّل الفاتورة ثم اربطها.",
      "fa.link.why.FA_LINK_INVOICE_REVERSED": "هذه الفاتورة معكوسة، وإشعارها الدائن لن يعكس جانب الأصل، فيفترق الطرفان. اربط الفاتورة البديلة بدلاً منها.",
      "fa.link.why.FA_LINK_PERIOD_CLOSED": "الشهر الذي تنتمي إليه هذه الفاتورة مقفل، وهذا التصحيح سيُقيَّد داخله. اعكس الفاتورة وأعد إدخالها في شهر مفتوح.",
      "fa.link.why.FA_LINK_EXCEEDS_INVOICE": "مجموع بنود الأصول في هذه الفاتورة سيتجاوز قيمة الفاتورة نفسها بعد استبعاد ضريبة القيمة المضافة. تحقّق مما إذا كانت التكلفة المسجَّلة تشمل الضريبة خطأً.",
      "fa.link.why.FA_LINK_COST_MISMATCH": "البند والسجل غير متفقين على تكلفة هذا الأصل. صحِّح الخطأ منهما قبل الربط — فالقيد سيتبع الفاتورة لا السجل.",
      "fa.link.why.FA_LINK_DEPRECIATED": "هذا الأصل بدأ إهلاكه بالفعل، وتكلفته موزَّعة على شهور مرحَّلة لا يستردها إلغاء الربط. استبعد الأصل بدلاً من ذلك.",
      "legacyAssets.title": "تسجيل أصول مملوكة سابقًا",
      "legacyAssets.done": "إغلاق",
      "legacyAssets.partialTitle": "حُفظ جزء من هذا الملف ولم يُحفظ جزء آخر.",
      "legacyAssets.partialSaved": "تم تسجيل {{count}} أصلًا الآن. هذه محفوظة — لا ترفع الملف كاملًا مرة أخرى.",
      "legacyAssets.partialFailedRows": "الصفوف {{rows}} لم تُحفظ.",
      "legacyAssets.partialFailedCount": "{{count}} صفًا لم تُحفظ.",
      "legacyAssets.partialRow": "الصف {{row}}: {{message}}",
      "legacyAssets.partialRowFields": "راجع: {{fields}}",
      "legacyAssets.rowJoin": "، ",
      "legacyAssets.rowNumbersAreFileLines": "أرقام الصفوف هي أرقام الأسطر في ملفك، بحساب سطر العناوين في الأعلى.",
      "legacyAssets.partialRetrySafe": "صحّح هذه الصفوف فقط وأرسلها مرة أخرى. الأصول المحفوظة لن تُسجَّل مرتين.",
      "legacyAssets.partialRetryCheck": "افتح سجل الأصول وتحقق مما حُفظ قبل إرسال هذا الملف مرة أخرى.",
      "legacyAssets.description": "أدخل إلى موتورمايند الأصول التي كانت الشركة تملكها مسبقًا بوضعها الدفتري الحالي.",
      "legacyAssets.mode": "طريقة الإدخال",
      "legacyAssets.single": "أصل واحد",
      "legacyAssets.many": "عدة أصول (CSV)",
      "legacyAssets.noCashEffect": "يسجّل هذا أصلًا موجودًا وإهلاكه الافتتاحي. لا ينشئ فاتورة شراء ولا يدفع لأحد ولا يؤثر في النقد.",
      "legacyAssets.branch": "الفرع",
      "legacyAssets.companyLevel": "على مستوى الشركة",
      "legacyAssets.cost": "التكلفة الأصلية",
      "legacyAssets.asOfPeriod": "شهر الوضع الدفتري",
      "legacyAssets.asOfHint": "يجب أن يكون شهر الوضع الدفتري قد انتهى وأن يكون في تاريخ الأرصدة الافتتاحية أو بعده",
      "legacyAssets.asOfRequired": "شهر الوضع الدفتري مطلوب: يُسجَّل أول قيد إهلاك في الشهر الذي يليه.",
      "legacyAssets.asOfRequiredRows": "شهر الوضع الدفتري مفقود: حقل as_of_period مطلوب في كل صف. لم تُرسل الصفوف: {{rows}}.",
      "legacyAssets.bookValue": "القيمة الدفترية",
      "legacyAssets.remainingMonthsInput": "الأشهر المتبقية",
      "legacyAssets.sar": "ر.س",
      "legacyAssets.save": "تسجيل الأصل",
      "legacyAssets.import": "استيراد الأصول",
      "legacyAssets.csvHelp": "ارفع ملف CSV أو الصقه باستخدام هذه العناوين حرفيًا. حقول المبالغ بهلالات صحيحة، وحقل as_of_period مطلوب في كل صف.",
      "legacyAssets.csvFile": "رفع ملف CSV",
      "legacyAssets.csvPaste": "أو الصق CSV",
      "legacyAssets.rowsReady": "{{count}} صف بيانات جاهز",
      "legacyAssets.errorRowField": "الصف {{row}}، {{field}}",
      "legacyAssets.unknownField": "حقل غير معروف",
      "legacyAssets.nothingSaved": "لم يُحفظ أي شيء. تُفحص كل الصفوف قبل كتابة أي أصل، لذا يمكنك تصحيح الملف ورفعه مجددًا بأمان.",
      "legacyAssets.saveFailed": "تعذّر تسجيل الأصل السابق.",
      "legacyAssets.readFailed": "تعذّرت قراءة ملف CSV هذا.",
      "fa.loadFailed": "تعذّر تحميل سجل الأصول الثابتة.",
      "fa.totals": "الإجمالي",
      "fa.empty": "لا توجد أصول مسجّلة بعد",
      "fa.emptyDesc": "الأصل الثابت هو ما تحتاجه الشركة وتستخدمه لأكثر من سنة — رافعة سيارات، مبنى مركز، حاسوب، سيارة توصيل. تسجيله هنا يجعل النظام يُهلكه ويُرحّل إهلاكه إلى دفتر الأستاذ العام تلقائيًا كل شهر، بدل أن يتذكّر أحدهم تدوين القيد يدويًا. ثمن الشراء لا يُقيَّد هنا؛ فقد وصل إلى القيود مسبقًا عبر الفاتورة أو المصروف الذي دفعه.",
      "fa.col.code": "الرمز",
      "fa.col.name": "الأصل",
      "fa.col.branch": "الفرع",
      "fa.col.acquired": "تاريخ الاقتناء",
      "fa.col.cost": "التكلفة",
      "fa.col.accumulated": "مجمع الإهلاك",
      "fa.col.nbv": "القيمة الدفترية",
      "fa.col.status": "الحالة",
      "fa.cat.equipment": "معدات",
      "fa.cat.tools": "أدوات",
      "fa.cat.vehicles": "مركبات",
      "fa.cat.furniture": "أثاث",
      "fa.cat.fittings": "تجهيزات",
      "fa.cat.buildings": "مبانٍ",
      "fa.cat.improvements": "تحسينات",
      "fa.cat.computers": "حواسيب",
      "fa.cat.software": "برمجيات",
      "fa.status.active": "قيد الاستخدام",
      "fa.status.fully_depreciated": "مُهلك بالكامل",
      "fa.status.disposed": "مستبعد",
      "fa.branch": "الفرع",
      "fa.branchCompany": "على مستوى الشركة",
      "fa.nameEn": "الاسم (بالإنجليزية)",
      "fa.nameAr": "الاسم (بالعربية)",
      "fa.category": "الفئة",
      "fa.cost": "التكلفة",
      "fa.acquiredOn": "تاريخ الاقتناء",
      "fa.salvage": "قيمة الإنقاذ",
      "fa.salvageHint": "قيمته المتوقعة في نهاية عمره الافتراضي. غالبًا صفر.",
      "fa.life": "العمر الافتراضي",
      "fa.lifeHint": "إهلاك قسط ثابت على هذا العدد من الأشهر، بدءًا من شهر الاقتناء.",
      "fa.lifeLockedHint": "مقفول — سبق أن رُحّل إهلاك لهذا الأصل.",
      "fa.months": "شهرًا",
      "fa.addTitle": "تسجيل أصل",
      "fa.addDesc": "التكلفة لا تُقيَّد هنا — فقد وصلت إلى القيود مسبقًا عبر عملية الشراء التي دفعتها. هذا السجل هو ما يقرأه الإهلاك الشهري.",
      "fa.editTitle": "تعديل الأصل",
      "fa.editDesc": "تُقفل التكلفة وتاريخ الاقتناء والعمر الافتراضي بعد رحل أي إهلاك على هذا الأصل.",
      "fa.lockedBanner": "سبق أن رُحّل إهلاك لهذا الأصل، لذا لا يمكن تغيير تكلفته أو تاريخ اقتنائه أو عمره الافتراضي بعد الآن — فتغييرها يعيد كتابة أشهر مسجّلة في دفتر الأستاذ. أما بقية البيانات فقابلة للتعديل.",
      "fa.saved": "تم تحديث الأصل.",
      "fa.created": "تم تسجيل الأصل.",
      "fa.saveFailed": "تعذّر حفظ الأصل.",
      "fa.errName": "أدخل الاسم بالإنجليزية أو العربية.",
      "fa.errCost": "أدخل تكلفة الأصل.",
      "fa.errLife": "العمر الافتراضي يجب أن يكون شهرًا واحدًا على الأقل.",
      "fa.errSalvage": "قيمة الخردة لا يمكن أن تتجاوز التكلفة.",
      "fa.purchaseCreated": "تم تسجيل الأصل وترحيل فاتورة الشراء.",
      "fa.purchaseSaveFailed": "تعذّر تسجيل الأصل وترحيل فاتورة الشراء.",
      "fa.selectCategory": "اختر التصنيف",
      "fa.edit": "تعديل",
      "fa.dispose": "استبعاد",
      "fa.disposeTitle": "استبعاد هذا الأصل؟",
      "fa.disposeMessage": "«{{name}}» ({{code}}) يخرج من السجل ويتوقف إهلاكه من التاريخ الذي تحدده. لا يمكن التراجع عن ذلك من هذه الشاشة.",
      "fa.disposeDate": "تاريخ الاستبعاد",
      "fa.disposeProceeds": "حصيلة البيع",
      "fa.disposeProceedsHint": "ما بِيع به الأصل، إن وُجد. صفر إذا أُلغي كخردة.",
      "fa.disposeConfirm": "استبعاد",
      "fa.disposed": "تم استبعاد الأصل.",
      "fa.disposeFailed": "تعذّر استبعاد الأصل.",
      "fa.dep.prevMonth": "الشهر السابق",
      "fa.dep.nextMonth": "الشهر التالي",
      "fa.dep.run": "ترحيل الإهلاك",
      "fa.dep.total": "إجمالي الشهر",
      "fa.dep.nothing": "لا يوجد إهلاك لهذا الشهر — لا يوجد أصل مستخدم بلغ شهر اقتنائه بعد، أو أنها كلها مُهلكة بالكامل أو مستبعدة.",
      "fa.dep.idempotentHint": "الترحيل لكل أصل عن كل شهر: تكرار ترحيل الشهر نفسه لا يكرّر الإهلاك. يرحّل موتورمايند كل شهر تلقائيًا في أول يوم من الشهر الذي يليه، ويُقيَّد بتاريخ نهاية الشهر؛ وهذا الزر لمن يحتاج ترحيل شهر أبكر من ذلك.",
      "fa.dep.runSuccess": "تم ترحيل إهلاك {{period}}.",
      "fa.dep.runFailed": "تعذّر ترحيل إهلاك هذا الشهر.",
      "fa.dep.postedTitle": "تم ترحيله بالفعل عن {{period}}",
      "fa.dep.postedDated": "مقيَّد في دفتر الأستاذ بتاريخ {{date}} — آخر يوم في الشهر الذي يغطيه. أي ميزان مراجعة أو كشف حساب ينتهي قبل هذا التاريخ لن يُظهره.",
      "fa.dep.postedAccounts": "من ح/ {{expense}} إلى ح/ {{accum}}",
      "fa.dep.confirmTitle": "ترحيل إهلاك {{period}} الآن؟",
      "fa.dep.confirmCta": "رحّله الآن",
      "fa.dep.confirmAutomatic": "لا تحتاج هذا الزر في العادة. يُرحّل موتورمايند إهلاك كل شهر تلقائيًا في أول يوم من الشهر الذي يليه بتوقيت الرياض، وهذا الزر يقدّم ذلك فقط.",
      "fa.dep.confirmWhatPosts": "سيُحمَّل الإهلاك على {{assets}} أصل على مدار {{months}} شهرًا بإجمالي {{total}} ريال. يُقيَّد كل شهر بتاريخ نهايته الخاصة. يُجعل كل أصل مدينًا في حساب مصروف الإهلاك الخاص به ودائنًا في حساب مجمّع الإهلاك.",
      "fa.dep.confirmCurrentMonth": "لم ينتهِ {{period}} بعد. سيُحمَّل شهر كامل رغم ذلك، بتاريخ {{date}} — وهو آخر يوم في الشهر، فلن يظهر القيد في أي تقرير تنتهي فترته قبل ذلك.",
      "fa.dep.confirmIrreversible": "الشهر المُرحَّل لا يُحمَّل مرتين، وهذه الشاشة لا تستطيع التراجع عنه. تصحيح شهر مُرحَّل يحتاج قيد يومية يدوي.",
      "fa.coverage.title": "تكلفة أصل لم تُقيَّد في دفتر الأستاذ",
      "fa.coverage.line": "قيمة {{n}} أصل مسجّلة على الحساب {{code}} تبلغ {{registered}} ريال، بينما لا يحمل هذا الحساب في دفتر الأستاذ سوى {{booked}} ريال.",
      "fa.coverage.fix": "تسجيل الأصل هنا لا يقيّد تكلفته — فاتورة الشراء أو المصروف هي التي تفعل ذلك. أدخل عملية الشراء، أو أضف الأصل إلى الأرصدة الافتتاحية ما دامت في حالة مسودة إن كانت الشركة تملكه قبل موتورمايند. وبعد اعتماد الأرصدة الافتتاحية يكون التصحيح بقيد يومية.",
      "pt.nav": "الشركاء",
      "pt.title": "الشركاء",
      "pt.subtitle": "الأشخاص الذين يودعون أموالاً في المنشأة أو يسحبون منها.",
      "pt.new": "إضافة شريك",
      "pt.newTitle": "إضافة شريك",
      "pt.editTitle": "تعديل بيانات الشريك",
      "pt.edit": "تعديل",
      "pt.name": "الاسم",
      "pt.nameAr": "الاسم بالعربية",
      "pt.notes": "ملاحظات",
      "pt.active": "نشط",
      "pt.inactive": "غير نشط",
      "pt.inactiveHint": "إيقاف الشريك يُبقي جميع سجلاته السابقة كما هي. ولا يمكن تغيير الاسم بعد تسجيل حركات باسمه.",
      "pt.errName": "أدخل الاسم",
      "pt.saved": "تم الحفظ",
      "pt.empty": "لا يوجد شركاء",
      "pt.emptyDesc": "أضِف المالك أو الشريك أولاً، ثم يمكنك تسجيل ما يودعه أو يسحبه.",
      "pt.statement": "كشف حساب الشريك",
      "pt.stmtTitle": "كشف حساب الشريك",
      "pt.startDate": "محتسب اعتباراً من",
      "pt.disclosureNote": "الحركات السابقة لهذا التاريخ غير مُدرجة في هذا الكشف، إذ كانت تُقيَّد آنذاك كمصروف عادي قبل توفُّر هذه الميزة.",
      "pt.drawn": "مسحوبات",
      "pt.contributed": "إيداعات",
      "pt.net": "الصافي",
      "pt.loanBalance": "رصيد القرض المستحق له",
      "pt.col.reason": "السبب",
      "pt.stmtEmpty": "لا توجد حركات في هذه الفترة",
      "pt.stmtEmptyDesc": "لا توجد حركات نقدية لهذا الطرف خلال الفترة المحدَّدة.",
      "pt.accessDenied": "لا صلاحية",
      "pt.accessDeniedDesc": "ليست لديك صلاحية لعرض الشركاء.",
      "bank.reviewTitle": "راجع كشف الحساب البنكي",
      "bank.reviewSubtitle": "قرأ النظام هذه السطور من صورة. راجِعها ثم اعتمِدها.",
      "bank.reviewEmpty": "لا يوجد ما يستدعي المراجعة",
      "bank.reviewEmptyDesc": "الكشف الوارد كملف يُقرأ قراءة دقيقة. المراجعة هنا مطلوبة لصورة الكشف فقط.",
      "bank.ocrWarning": "لا يُحفظ شيء قبل اعتمادك للسطور",
      "bank.ocrWarningDesc": "هذه السطور مقروءة من صورة، وقد يرد فيها رقم غير صحيح. قارِنها بالكشف الأصلي.",
      "bank.opening": "الرصيد في البداية",
      "bank.closing": "الرصيد في النهاية",
      "bank.period": "الفترة",
      "bank.rows": "عدد السطور",
      "bank.balancedNote": "الرصيد الافتتاحي مضافاً إليه هذه السطور يساوي الرصيد الختامي. ولو لم يتطابق لَرُفض الملف.",
      "bank.balancedSelfNote": "قرأ الذكاء الاصطناعي الرصيدين الافتتاحي والختامي من الصفحة نفسها التي قرأ منها هذه السطور، فتطابق الحساب لا يُثبت أنه لم يسقط أي سطر. طابِق المجاميع مع الكشف الأصلي.",
      "bank.col.date": "التاريخ",
      "bank.col.description": "البيان",
      "bank.col.reference": "المرجع",
      "bank.col.amount": "المبلغ",
      "bank.col.direction": "الاتجاه",
      "bank.in": "وارد",
      "bank.out": "صادر",
      "bank.confirm": "اعتماد السطور",
      "bank.confirmed": "تم الحفظ",
      "bank.confirmFailed": "تعذَّر الحفظ. حاول مرة أخرى.",
      "bank.loadFailed": "تعذَّر فتح الكشف.",
      "br.title": "التسوية البنكية",
      "br.subtitle": "طابق ما يقوله البنك مع ما تقوله دفاترك، وأظهر ما تبقّى.",
      "br.startHint": "استورد كشف الحساب للفترة أولاً — أرصدة البنك الافتتاحية والختامية تُقرأ منه ولا تُكتب هنا.",
      "br.chooseAccount": "اختر حساباً بنكياً",
      "br.importStatement": "استيراد كشف حساب",
      "bank.imp.title": "استيراد كشف حساب بنكي",
      "bank.imp.subtitle": "الكشف هو المرجع. تُقرأ منه الأرصدة الافتتاحية والختامية، وتُثبَت التسوية في مقابلها.",
      "bank.imp.back": "العودة إلى التسوية",
      "bank.imp.account": "الحساب البنكي",
      "bank.imp.bank": "البنك",
      "bank.imp.bankPlaceholder": "مثال: الراجحي",
      "bank.imp.opening": "الرصيد الافتتاحي في الكشف",
      "bank.imp.closing": "الرصيد الختامي في الكشف",
      "bank.imp.file": "ملف الكشف",
      "bank.imp.fileHint": "CSV أو Excel أو PDF. يُقرأ ملف PDF النصي قراءة دقيقة، أما الصورة الممسوحة فتُقرأ آلياً وسيُطلب منك مراجعة كل سطر قبل اعتماده.",
      "bank.imp.read": "قراءة الملف",
      "bank.imp.readAi": "قراءة بالذكاء الاصطناعي",
      "bank.imp.readHint": "اقرأ الملف أولاً لمعاينة محتواه قبل الاستيراد. القراءة بالذكاء الاصطناعي لا تحتاج إلى مطابقة الأعمدة، وتقرأ الأرصدة من الكشف نفسه.",
      "bank.imp.balanceHint": "اتركه فارغاً ليُقرأ من الكشف",
      "bank.imp.balanceAuto": "من الكشف",
      "bank.imp.prevTitle": "محتوى هذا الملف",
      "bank.imp.prevCount": "عدد الحركات: {{count}}",
      "bank.imp.prevByAi": "قراءة بالذكاء الاصطناعي — راجعها قبل الاستيراد",
      "bank.imp.prevBalanced": "الأرصدة متطابقة",
      "bank.imp.prevSelfReported": "الأرصدة متطابقة — لكن النموذج هو من ذكرها، فهذا ليس تحقّقاً مستقلاً",
      "bank.imp.prevUnbalanced": "فرق مقداره {{delta}} — سقطت حركة أو قُرئت خطأً",
      "bank.imp.prevTruncated": "يُعرض أول {{shown}} من أصل {{total}} حركة.",
      "bank.imp.submit": "استيراد",
      "bank.imp.done": "تم استيراد الكشف",
      "bank.imp.errAccount": "اختر الحساب البنكي الذي يخصُّه هذا الكشف",
      "bank.imp.errBank": "أدخل اسم البنك",
      "bank.imp.errBalances": "أدخل الرصيد الافتتاحي والرصيد الختامي كما وردا في الكشف",
      "bank.imp.errFile": "اختر ملف الكشف",
      "bank.imp.mapNeeded": "حدِّد الأعمدة — مرة واحدة لهذا البنك",
      "bank.imp.mapTitle": "أيُّ عمود يقابل أيَّ حقل؟",
      "bank.imp.mapDesc": "لا نخمِّن الأعمدة: قراءة عمود الرصيد الجاري على أنه المبلغ تُنتج تسوية تُثبِت أمراً غير صحيح. حدِّدها مرة واحدة، وستُستورَد كشوف هذا البنك مباشرة بعد ذلك.",
      "bank.imp.col.date": "التاريخ",
      "bank.imp.col.description": "البيان",
      "bank.imp.col.reference": "المرجع",
      "bank.imp.col.amount": "المبلغ (بالإشارة)",
      "bank.imp.col.debit": "مدين (صادر)",
      "bank.imp.col.credit": "دائن (وارد)",
      "bank.imp.col.balance": "الرصيد الجاري",
      "bank.imp.colNone": "— غير موجود في الملف —",
      "bank.imp.amountRule": "إمَّا عمود مبلغ واحد بالإشارة، وإمَّا عمودا مدين ودائن معاً.",
      "bank.imp.dateFormat": "صيغة التاريخ (عند قراءة التواريخ بشكل خاطئ فقط)",
      "bank.imp.saveMapping": "حفظ واستيراد",
      "bank.imp.errMapDate": "اختر عمود التاريخ",
      "bank.imp.errMapAmount": "اختر عمود المبلغ، أو عمودَي المدين والدائن معاً",
      "bank.err.NO_STATEMENT": "استورد كشف الحساب لهذه الفترة أولاً — لا يوجد ما تُجرى التسوية في مقابله.",
      "bank.err.STATEMENT_GAP": "الكشوف المستوردة لا تغطِّي الفترة كاملة. استورد الأيام الناقصة قبل إجراء التسوية.",
      "bank.err.IMPORT_UNREVIEWED": "راجِع سطور الكشف المستورد أولاً — الكشف الذي لم يقرأه أحد ليس مرجعاً.",
      "bank.err.NOT_A_BANK_ACCOUNT": "هذا ليس حساباً بنكياً. تُجرى التسوية في مقابل حساب بنكي.",
      "bank.err.ACCOUNT_NOT_FOUND": "لم يعد هذا الحساب موجوداً.",
      "bank.err.SESSION_NOT_FOUND": "لم تعد هذه التسوية موجودة.",
      "bank.err.SESSION_OPEN_EXISTS": "يوجد لهذا الحساب تسوية مفتوحة بالفعل. أكمِلها أو أقفِلها أولاً.",
      "bank.err.SESSION_LOCKED": "هذه التسوية مقفلة، وهي ورقة عمل معتمدة لا تُعدَّل.",
      "bank.err.MATCH_CLAIMED": "طابَق شخص آخر أحد هذين السطرين قبل قليل. تم تحديث الشاشة.",
      "bank.err.MATCH_INVALID": "لا يمكن مطابقة هذين السطرين — تُطابَق المبالغ المتساوية داخل الفترة فقط.",
      "bank.err.UNEXPLAINED_DIFFERENCE": "ما زال هناك فرق. وضِّحه في الملاحظة قبل الإقفال، أو طابِق ما تبقَّى.",
      "bank.err.MAPPING_REQUIRED": "حدِّد أعمدة هذا البنك مرة واحدة.",
      "bank.err.NO_ROWS": "تعذَّرت قراءة أي سطر حركة من هذا الملف. راجِع تحديد الأعمدة وصيغة التاريخ.",
      "bank.err.BALANCES_REQUIRED": "تعذّر قراءة الرصيد الافتتاحي والختامي من هذا الملف. اكتبهما من الكشف لإتمام الاستيراد.",
      "bank.err.FILE_TOO_LARGE_FOR_AI": "عدد سطور هذا الملف أكبر من أن يقرأه الذكاء الاصطناعي. حدِّد الأعمدة مرة واحدة بدلاً من ذلك — فهي دقيقة وفورية ومجانية، وكل كشف لاحق من هذا البنك يُستورد مباشرة.",
      "bank.err.EXTRACTION_FAILED": "تعذّرت قراءة الملف. جرِّب تحديد الأعمدة بدلاً من ذلك، أو ارفع نسخة أوضح.",
      "bank.err.BALANCE_MISMATCH": "السطور المقروءة من هذا الملف لا تصل إلى رصيده الختامي، أي أن حركة واحدة على الأقل ناقصة أو قُرئت خطأً. لم يُستورد أي شيء.",
      "bank.err.DUPLICATE_IMPORT": "سبق استيراد هذا الكشف لهذا الحساب. استيراده مرة أخرى سيُسجّل كل حركة مرتين.",
      "bank.err.QUOTA_EXCEEDED": "استنفدت هذه الشركة حصتها الشهرية من الذكاء الاصطناعي. حدِّد الأعمدة بدلاً من ذلك، أو ارفع الحصة من الاشتراك.",
      "bank.err.UNSUPPORTED_FILE": "تعذّرت قراءة هذا النوع من الملفات. ارفع ملف CSV أو Excel (.xlsx) أو PDF.",
      "bank.err.INVALID_INPUT": "الملف كبير جداً أو صفحاته كثيرة على القراءة. ارفع ملفاً أصغر، أو قسّم الكشف شهراً بشهر.",
      "bank.err.BAD_MAPPING": "تحديد الأعمدة المحفوظ لهذا البنك لم يعد مطابقاً للملف. حدِّدها من جديد.",
      "bank.err.NOT_BANK_ACCOUNT": "هذا ليس حساباً بنكياً. يُستورد الكشف في مقابل حساب بنكي.",
      "bank.err.ACCOUNT_NOT_FOUND": "لم يعد هذا الحساب موجوداً. أعد تحميل الصفحة واختره من جديد.",
      "br.noAccounts": "لا يوجد حساب بنكي مُعرَّف بعد. أضِف حساباً من «كشف النقدية ← الحسابات»، ثم عد إلى هنا.",
      "br.start": "ابدأ",
      "br.newSession": "تسوية جديدة",
      "br.statusOpen": "قيد العمل",
      "br.statusLocked": "مقفلة",
      "br.started": "بدأت التسوية",
      "br.bankClosing": "الرصيد الختامي لدى البنك",
      "br.systemBalance": "رصيد دفاترك",
      "br.outstanding": "ما زال معلقاً",
      "br.bankOnly": "لدى البنك فقط",
      "br.systemOnly": "في الدفاتر فقط",
      "br.difference": "الفرق",
      "br.balancedNote": "البنك ودفاترك متطابقان بعد احتساب البنود المعلقة.",
      "br.unbalancedNote": "هناك فرق لا تفسّره البنود المعلقة. ابحث عنه، أو دوّن سببه قبل الإقفال.",
      "br.postToBooks": "تسجيل في الدفاتر",
      "br.postedAndMatched": "تم التسجيل والمطابقة مع سطر البنك",
      "br.postedNotMatched": "سُجِّل المبلغ، لكن تعذّرت مطابقته مع سطر البنك. هو الآن في دفاترك — طابِقه من هناك.",
      "br.openingGapNote": "هذا الفرق هو الرصيد الافتتاحي وليس حركة ناقصة. سجل البنك يبدأ هذا الحساب بـ {{statement}} ودفاترك تبدأه بـ {{books}}. مطابقة السطور لن تُغيّره، ولا تُغيّره تسوية نقدية — التسوية تضيف المبلغ نفسه إلى رصيدك وإلى عمود البنود المعلقة فيلغي أحدهما الآخر. حدِّد الرصيد الافتتاحي من كشف النقدية؛ وهذا ممكن فقط قبل أول حركة على الحساب، فإن كان مقفلاً فدوِّن هذا المبلغ في ملاحظة الإقفال.",
      "br.preStatementNote": "هذا الفرق ليس حركة ناقصة. دفاترك تحتوي على حركات بقيمة {{amount}} قبل {{from}}، وهو أقدم كشف بنكي مستورد لهذا الحساب — ولا يوجد أي سطر بنكي يمكن أن يقابلها. استورد الكشوف الأقدم لإغلاق هذا الجزء من الفرق. ولا تُحدِّد رصيداً افتتاحياً: هذا المال موجود في دفاترك بالفعل، وسيُحتسب مرتين.",
      "br.bankOnlyTitle": "لدى البنك فقط",
      "br.matchedTitle": "متطابق",
      "br.systemOnlyTitle": "في دفاترك فقط",
      "br.nothingHere": "لا يوجد شيء",
      "br.nothingMatched": "لم تتم مطابقة شيء بعد",
      "br.suggestMatch": "يبدو أنه مطابق",
      "br.confidence.high": "مرجّح جداً",
      "br.confidence.medium": "مرجّح",
      "br.confidence.low": "محتمل",
      "br.confidence.manual": "مطابقة يدوية",
      "br.confirmSelected": "طابق المحدد ({{count}})",
      "br.matched": "تمت مطابقة {{count}}",
      "br.unmatch": "تراجع",
      "br.lock": "أقفل التسوية",
      "br.locked": "أُقفلت التسوية",
      "br.lockedOn": "أُقفلت في",
      "br.lockHint": "الإقفال يثبّت هذه الأرقام وقائمة البنود المعلقة كسجل لهذه الفترة، ولا يمكن إعادة فتحه.",
      "br.notePlaceholder": "ملاحظة (اختياري)",
      "br.noteRequired": "وضّح سبب الفرق قبل الإقفال",
      "cash.empty": "لا توجد حركات نقدية",
      "cash.emptyDesc": "لا توجد حركات نقدية في هذه الفترة.",
      "cash.accessDenied": "لا صلاحية",
      "cash.accessDeniedDesc": "ليست لديك صلاحية لعرض كشف النقدية.",
      "opex.nav": "المصروفات",
      "opex.title": "المصروفات",
      "opex.subtitle": "المصروفات التشغيلية المدفوعة نقداً أو من البنك.",
      "opex.add": "إضافة مصروف",
      "opex.addTitle": "إضافة مصروف",
      "opex.editTitle": "تعديل المصروف",
      "opex.col.date": "التاريخ",
      "opex.col.category": "الفئة",
      "opex.col.vendor": "المورّد",
      "opex.col.paidFrom": "مدفوع من",
      "opex.col.branch": "الفرع",
      "opex.col.amount": "المبلغ",
      "opex.allCategories": "كل الفئات",
      "opex.allAccounts": "كل الحسابات",
      "opex.allSources": "كل المصادر",
      "opex.totalLabel": "الإجمالي",
      "opex.countLabel": "{{count}} مصروف",
      "opex.empty": "لا توجد مصروفات",
      "opex.emptyDesc": "لا توجد مصروفات مسجّلة لهذه الفلاتر.",
      "opex.branch": "الفرع",
      "opex.selectBranch": "اختر فرعاً",
      "opex.selectCategory": "اختر فئة",
      "opex.col.account": "الحساب",
      "opex.account": "حساب المصروف",
      "opex.selectAccount": "اختر حساباً",
      "opex.searchAccount": "ابحث بالاسم أو برقم الحساب",
      "opex.noAccounts": "لا يوجد حساب مطابق",
      "opex.accountsLoadFailed": "تعذّر تحميل قائمة الحسابات، ولا يمكن حفظ المصروف الآن. حدّث الصفحة وحاول مرة أخرى.",
      "opex.amount": "المبلغ",
      "opex.amountGross": "المبلغ (شامل الضريبة)",
      "opex.hasVat": "هذه الفاتورة تشمل ضريبة القيمة المضافة",
      "opex.hasVatHint": "أزل العلامة للفاتورة بدون ضريبة — مورّد غير مسجّل، رسوم حكومية، أو سند صرف نثري.",
      "opex.vatRate": "نسبة الضريبة",
      "opex.vatIncluded": "الضريبة المشمولة",
      "opex.netOfVat": "المبلغ قبل الضريبة",
      "opex.col.vat": "الضريبة",
      "opex.totalVat": "إجمالي الضريبة",
      "opex.totalNet": "الإجمالي قبل الضريبة",
      "opex.expenseDate": "تاريخ المصروف",
      "opex.attachmentUrl": "رابط المرفق",
      "opex.attachmentHint": "رابط اختياري لإيصال أو فاتورة.",
      "opex.attachment": "مرفق",
      "opex.documents": "المستندات الداعمة",
      "opex.documentsHint": "أرفق الإيصالات أو الفواتير (صور أو PDF، حتى 10 ميجابايت لكل ملف).",
      "opex.addDocuments": "إضافة مستندات",
      "opex.uploading": "جارٍ الرفع…",
      "opex.uploadFailed": "تعذّر رفع الملف.",
      // ── الرفع المحدّد للوسائط (utils/mediaUploadClient.js). تُختار الرسائل بحسب
      // رمز الخادم الثابت لا بحسب حالة HTTP، فلا يتسرّب نص إنجليزي إلى الواجهة.
      "mediaUpload.failed": "تعذّر رفع هذا الملف. حاول مرة أخرى.",
      "mediaUpload.unsupportedType": "نوع الملف غير مسموح هنا. استخدم صورة JPEG أو PNG أو WebP أو ملف PDF.",
      "mediaUpload.typeMismatch": "نوع الملف لا يطابق اسمه. يرجى اختيار الملف من جديد.",
      "mediaUpload.tooLarge": "حجم الملف أكبر من الحد المسموح لهذا النوع من المرفقات.",
      "mediaUpload.emptyFile": "هذا الملف فارغ.",
      "mediaUpload.quotaExceeded": "هناك عدد كبير من عمليات الرفع الجارية الآن. حاول بعد قليل.",
      "mediaUpload.grantExpired": "استغرق بدء الرفع وقتاً طويلاً. يرجى اختيار الملف من جديد.",
      "mediaUpload.notUploaded": "لم يصل الملف إلى الخادم. حاول مرة أخرى.",
      "mediaUpload.stillProcessing": "لا يزال الملف قيد التحقق. حاول بعد قليل.",
      "mediaUpload.notReady": "لم يعد هذا المرفق متاحاً. يرجى رفعه من جديد.",
      "mediaUpload.pdfEncrypted": "ملف PDF محمي بكلمة مرور. أزل الحماية وحاول مرة أخرى.",
      "mediaUpload.pdfPages": "عدد صفحات ملف PDF أكبر من المسموح.",
      "mediaUpload.pdfMalformed": "تعذّرت قراءة ملف PDF. جرّب ملفاً آخر.",
      "mediaUpload.forbidden": "ليست لديك صلاحية رفع هذا المرفق.",
      "mediaUpload.sessionChanged": "تغيّرت جلستك، لذلك تم إلغاء هذا الرفع.",
      "opex.errCategory": "اختر فئة",
      "opex.errAccount": "اختر الحساب الذي يخصّ هذا المصروف",
      "opex.errAmount": "أدخل مبلغاً صحيحاً",
      "opex.errBranch": "اختر فرعاً",
      "opex.saveFailed": "تعذّر حفظ المصروف.",
      "opex.created": "تمت إضافة المصروف",
      "opex.updated": "تم تحديث المصروف",
      "opex.detailTitle": "تفاصيل المصروف",
      "opex.loadFailed": "تعذّر تحميل المصروف.",
      "opex.deleted": "تم حذف المصروف",
      "opex.deleteFailed": "تعذّر حذف المصروف.",
      "opex.post": "ترحيل",
      "opex.postedLockHint": "مُرحَّل — لتغيير المبلغ ألغِه بإشعار دائن ثم سجّل مصروفاً جديداً.",
      "opex.postLeft": "ترحيل ({{count}} متبقٍ)",
      "opex.deleteTitle": "حذف المصروف",
      "opex.deleteConfirm": "هل أنت متأكد من حذف هذا المصروف؟ لا يمكن التراجع عن ذلك.",
      "opex.accessDenied": "لا صلاحية",
      "opex.accessDeniedDesc": "ليست لديك صلاحية لعرض المصروفات.",
      "purchases.nav": "المشتريات",
      "purchases.title": "المشتريات",
      "purchases.subtitle": "فواتير الموردين والمصروفات في مكان واحد.",
      "purchases.add": "إضافة",
      "purchases.addInvoice": "فاتورة مورد / مخزون",
      "purchases.addExpense": "مصروف سريع",
      "purchases.allTypes": "كل الأنواع",
      "purchases.allStatuses": "كل الحالات",
      "purchases.loadFailed": "تعذّر تحميل المشتريات.",
      "purchases.accessDenied": "لا صلاحية",
      "purchases.accessDeniedDesc": "ليست لديك صلاحية لعرض المشتريات.",
      "purchases.empty": "لا توجد مشتريات بعد",
      "purchases.emptyDesc": "ستظهر فواتير الموردين والمصروفات هنا.",
      "purchases.col.date": "التاريخ",
      "purchases.col.type": "النوع",
      "purchases.col.party": "المورد / البائع",
      "purchases.col.ref": "المرجع",
      "purchases.col.category": "الفئة",
      "purchases.col.status": "الحالة",
      "purchases.col.amount": "المبلغ",
      "purchases.type.invoice": "فاتورة شراء",
      "purchases.type.expense": "مصروف",
      "purchases.type.asset": "أصل ثابت",
      "purchase.invoiceDate": "تاريخ الفاتورة",
      "supplier": "المورّد",
      "cancel": "إلغاء",
      "save": "حفظ",
      "purchases.type.stock": "مخزون",
      "purchases.type.nonStock": "غير مخزون",
      "purchases.type.mixed": "مختلط",
      "purchases.type.creditNote": "إشعار دائن",
      "purchases.reverses": "يلغي {{ref}}",
      "purchases.action.viewSource": "عرض المستند الأصلي",
      "postingRef": "مرجع القيد",
      "purchases.status.paid": "مدفوعة",
      "purchases.status.partial": "جزئية",
      "purchases.status.unpaid": "غير مدفوعة",
      "purchases.status.reversed": "ملغاة",
      "purchases.status.issued": "صادر",
      "purchases.status.na": "—",
      "purchases.kpi.totalOut": "إجمالي المصروف",
      "purchases.kpi.cashOut": "النقد الخارج",
      "purchases.kpi.owed": "مستحق للموردين",
      "purchases.kpi.vat": "ضريبة قابلة للاسترداد",
      "purchases.pay.title": "المدفوعات",
      "purchases.pay.paid": "المدفوع",
      "purchases.pay.total": "الإجمالي",
      "purchases.pay.balance": "المتبقي",
      "purchases.pay.none": "لا توجد مدفوعات مسجّلة بعد.",
      "purchases.pay.amount": "المبلغ",
      "purchases.pay.from": "مدفوع من",
      "purchases.pay.note": "ملاحظة (اختياري)",
      "purchases.pay.record": "تسجيل دفعة",
      "purchases.pay.recorded": "تم تسجيل الدفعة",
      "purchases.pay.voided": "تم إلغاء الدفعة",
      "purchases.pay.void": "إلغاء الدفعة",
      "purchases.pay.confirmVoidTitle": "إلغاء هذه الدفعة؟",
      "purchases.pay.confirmVoidMessage": "سيتم عكس الدفعة وإعادة المبلغ إلى رصيد الفاتورة.",
      "purchases.pay.errAmount": "أدخل مبلغاً صحيحاً",
      "purchases.pay.saveFailed": "تعذّر تسجيل الدفعة.",
      "purchases.pay.voidFailed": "تعذّر إلغاء الدفعة.",
      "purchases.pay.loadFailed": "تعذّر تحميل المدفوعات.",
      "purchases.scan.button": "مسح فاتورة",
      "purchases.scan.hint": "الفواتير المطبوعة تُقرأ بدقة عالية، أما المكتوبة بخط اليد فتُملأ تقريبياً — راجعها قبل الحفظ.",
      "purchases.scan.quotaExceeded": "استُهلكت حصة الذكاء الاصطناعي لهذا الشهر",
      "purchases.scan.extractionFailed": "تعذّر قراءة الفاتورة، أدخلها يدوياً",
      "purchases.scan.failed": "تعذّر مسح الفاتورة",
      "purchases.scan.supplierMatchedVat": "مطابق برقم ضريبة القيمة المضافة",
      "purchases.scan.supplierSuggestion": "مطابقة مقترحة — تأكيد بالأسفل",
      "purchases.scan.supplierNoMatch": "لا يوجد مورد مطابق — اختر أو أنشئ مورداً",
      "purchases.scan.createSupplier": "إنشاء مورد من الفاتورة",
      "purchases.newSupplierBeforeSave": "مورد جديد — أنشئه قبل الحفظ",
      "purchases.supplierPermissionRequired": "هذا المورد غير مسجّل. اطلب من مستخدم يملك صلاحية إضافة الموردين أن ينشئه.",
      "purchases.scan.lineUnmatched": "لا يوجد صنف مطابق — سيتم إنشاء صنف جديد",
      "purchases.scan.reconciliationMismatch": "إجماليات الفاتورة الممسوحة غير متطابقة — يرجى المراجعة قبل الحفظ.",
      "purchases.scan.deltaLinesVsSubtotal": "البنود مقابل الإجمالي الفرعي: {{amount}}",
      "purchases.scan.deltaSubtotalVsTotal": "الإجمالي الفرعي + الضريبة مقابل الإجمالي: {{amount}}",
      "purchases.scan.duplicateWarning": "يبدو أن هذه الفاتورة مكررة لفاتورة موجودة ({{ref}}).",
      "purchases.scan.viewExisting": "عرض الفاتورة الموجودة",
      "purchases.scan.modalTitle": "مسح الفاتورة بالذكاء الاصطناعي",
      "purchases.scan.optionCamera": "التقط صورة",
      "purchases.scan.optionUpload": "رفع صورة أو ملف PDF",
      "purchases.scan.typeQuestion": "ما نوع هذا المستند؟",
      "purchases.scan.typeAuto": "تحديد تلقائي",
      "purchases.scan.typeStock": "فاتورة مشتريات (مخزون)",
      "purchases.scan.typeExpense": "مصروف",
      "purchases.scan.typeAsset": "أصل ثابت",
      "purchases.scan.live.reading": "جارٍ قراءة الفاتورة…",
      "purchases.scan.live.readingLines": "جارٍ قراءة البنود…",
      "purchases.scan.live.category": "التصنيف",
      "purchases.scan.live.why": "سبب هذا التصنيف",
      "purchases.scan.live.supplier": "المورد",
      "purchases.scan.live.vatNumber": "الرقم الضريبي",
      "purchases.scan.live.invoiceNumber": "رقم الفاتورة",
      "purchases.scan.live.invoiceDate": "تاريخ الفاتورة",
      "purchases.scan.live.totals": "الإجماليات المطبوعة",
      "purchases.scan.live.subtotal": "المجموع قبل الضريبة",
      "purchases.scan.live.vat": "ضريبة القيمة المضافة",
      "purchases.scan.live.grandTotal": "الإجمالي",
      "purchases.scan.live.notPrinted": "غير مطبوع",
      "purchases.scan.live.lines": "البنود",
      "purchases.scan.live.continue": "متابعة",
      "purchases.scan.live.dropped": "انقطع الاتصال قبل اكتمال المسح، ولم يُحفظ أي شيء.",
      "purchases.scan.live.tryAgain": "إعادة المحاولة",
      "purchases.scan.pages.add": "إضافة صفحة",
      "purchases.scan.pages.remove": "حذف الصفحة",
      "purchases.scan.pages.scan": "مسح هذه الصفحات",
      "purchases.scan.pages.hint": "تُرسل صور الفاتورة الواحدة معاً في ملف PDF واحد.",
      "purchases.scan.pages.tooMany": "الحد الأقصى عشر صفحات للفاتورة الواحدة.",
      "purchases.scan.pages.tooLarge": "يتجاوز حجم هذه الصفحات عشرة ميغابايت. احذف صفحة أو أعد التصوير.",
      "purchases.scan.pages.unreadable": "تعذّرت قراءة هذه الصورة. اختر صورة أخرى.",
      "purchases.scan.review.title": "يُرجى المراجعة",
      "purchases.scan.review.confirm": "راجعتُ هذه البنود",
      "purchases.scan.review.confirmRequired": "ضع علامة على «راجعتُ هذه البنود» قبل الحفظ.",
      "purchases.scan.review.chooseCategory": "اختر التصنيف:",
      "purchases.scan.review.scanAgain": "أعد المسح",
      "purchases.scan.review.reason.totals_mismatch": "مجموع البنود لا يطابق الإجمالي المطبوع.",
      "purchases.scan.review.reason.vat_number_invalid": "الرقم الضريبي للمورد لا يبدو صحيحاً؛ يجب أن يتكوّن من خمسة عشر رقماً.",
      "purchases.scan.review.reason.doc_type_disputed": "التصنيف غير مؤكد. اختر بين الاقتراحين.",
      "purchases.scan.review.reason.doc_type_missing": "تعذّر تحديد التصنيف. اختر تصنيفاً.",
      "purchases.scan.review.reason.doc_type_unknown": "لم يُتعرّف على التصنيف. اختر تصنيفاً.",
      "purchases.scan.review.reason.line_invalid": "بعض البنود تبدو ناقصة. راجع الكميات والأسعار.",
      "purchases.scan.review.reason.no_lines": "تعذّرت قراءة البنود. التقط صورة أوضح وأعد المحاولة.",
      "purchases.scan.review.reason.multiple_invoices": "يحتوي الملف على أكثر من فاتورة، وقُرئت الأولى فقط. امسح الفواتير الأخرى كلّاً على حدة.",
      "purchases.scan.missing.title": "ما زال مطلوباً",
      "purchases.scan.missing.date": "التاريخ",
      "purchases.scan.missing.amount": "المبلغ",
      "purchases.scan.missing.category": "الفئة",
      "purchases.scan.missing.account": "الحساب",
      "purchases.scan.missing.vendor": "المورّد",
      "purchases.scan.missing.supplier": "المورّد",
      "purchases.scan.missing.invoiceNumber": "رقم الفاتورة",
      "purchases.scan.missing.assetName": "اسم الأصل",
      "purchases.scan.missing.usefulLife": "العمر الإنتاجي",
      "purchases.scan.missing.saveLeft": "حفظ (المتبقي: {{count}})",
      "purchases.scan.missing.saveVat": "حفظ الرقم الضريبي {{vat}} لهذا المورّد",
      "purchases.scan.missing.saveVatFailed": "تعذّر حفظ الرقم الضريبي لهذا المورّد.",
      "purchases.scan.missing.readFromInvoice": "قُرئ من الفاتورة",
      "purchases.scan.dup.sameFile": "حُفظت الصورة نفسها من قبل",
      "purchases.scan.dup.sameNumber": "رقم الفاتورة نفسه من هذا المورّد",
      "purchases.scan.dup.sameAmountDate": "تطابق المورّد والمبلغ والتاريخ",
      "purchases.scan.dup.open": "فتح",
      "purchases.scan.date.useToday": "استخدم تاريخ اليوم",
      "purchases.scan.date.future": "هذا التاريخ في المستقبل.",
      "purchases.scan.date.old": "مضى على هذا التاريخ أكثر من اثني عشر شهراً.",
      "purchases.scan.date.closed": "هذا الشهر مُغلق في الدفاتر. اختر تاريخاً في شهر مفتوح، أو اطلب من محاسبك إعادة فتحه.",
      "purchases.post.button": "ترحيل الآن",
      "purchases.post.hint": "يحدّث المخزون والتكاليف ويقيّد الفاتورة في الدفاتر.",
      "purchases.post.done": "تم ترحيل الفاتورة",
      "purchases.post.failed": "تعذّر ترحيل الفاتورة",
      "purchases.scan.dup.confirmTitle": "قد تكون هذه الفاتورة مسجّلة من قبل",
      "purchases.scan.dup.confirmMessage": "وجدنا الصورة نفسها، أو رقم الفاتورة نفسه من هذا المورّد. هل تريد حفظها على أيّ حال؟",
      "purchases.scan.dup.confirmSave": "حفظ على أيّ حال",
      "purchases.scan.viewer.title": "الفاتورة الممسوحة",
      "purchases.scan.viewer.pdfTitle": "ملف PDF للفاتورة الممسوحة",
      "purchases.scan.viewer.zoomIn": "تكبير",
      "purchases.scan.viewer.zoomOut": "تصغير",
      "purchases.scan.viewer.reset": "إعادة الحجم الأصلي",
      "purchases.scan.viewer.show": "عرض الفاتورة",
      "purchases.scan.viewer.hide": "إخفاء الفاتورة",
      "purchases.scan.remembered.hint": "من آخر فاتورة من هذا المورّد",
      "purchases.scan.remembered.lineLink": "رُبط كما في المرة السابقة",
      "purchases.scan.typeAutoHint":
        "سيحدده الذكاء الاصطناعي. وعند عدم التأكد يعتبره فاتورة مشتريات — اختر النوع بنفسك لتضمن النتيجة.",
      "purchases.scan.typeManualHint": "سيُعتمد اختيارك ولن يغيّره الذكاء الاصطناعي.",
      "purchases.scan.step1": "التقط صورة للفاتورة أو ارفعها",
      "purchases.scan.step2": "يقرأ TzamunAI المورد والبنود والإجماليات",
      "purchases.scan.step3": "راجع المسودة المعبأة مسبقاً ثم اعتمدها",
      "purchases.scan.scanning": "جاري قراءة الفاتورة…",

      "reversal.title": "إلغاء المستند",
      "reversal.confirm": "إلغاء وإصدار إشعار دائن",
      "reversal.reverseInvoice": "إلغاء الفاتورة",
      "reversal.reverseInvoiceHint": "يصدر إشعارًا دائنًا، ويعيد المخزون، ويلغي أي دفعات للمورّد (تُعاد النقدية).",
      "reversal.reverseExpense": "إلغاء المصروف",
      "reversal.reason": "السبب",
      "reversal.reasonPlaceholder": "ما سبب الإلغاء؟",
      "reversal.warnInvoice": "سيؤدي هذا إلى إلغاء الفاتورة {{ref}}: يُصدر إشعار دائن، ويُعاد المخزون المستلَم، وتُلغى أي دفعات مسجّلة للمورّد (تُعاد النقدية). لا يمكن التراجع عن ذلك.",
      "reversal.warnExpense": "سيؤدي هذا إلى إلغاء المصروف {{ref}}: يُصدر إشعار دائن وتُعاد أي نقدية مدفوعة. لا يمكن التراجع عن ذلك.",
      "reversal.consumedTitle": "تعذّر الإلغاء — تم استخدام قطع غيار بالفعل",
      "reversal.consumedLine": "{{part}} — يلزم إعادة {{required}}، المتوفّر {{available}} فقط",
      "reversal.consumedToast": "تم استخدام بعض القطع بالفعل؛ تم منع الإلغاء.",
      "reversal.reversed": "ملغاة",
      "reversal.reversedBanner": "تم إلغاء هذا المستند.",
      "reversal.creditNoteNo": "إشعار دائن",
      "reversal.invoiceReversed": "تم إلغاء الفاتورة وإصدار إشعار دائن",
      "reversal.expenseReversed": "تم إلغاء المصروف وإصدار إشعار دائن",
      "purchases.vatInCost.explanation": "قبل 25 أغسطس 2026 لم يكن MotorMind يحفظ ما إذا كانت ضريبة القيمة المضافة قد استُردت أو أُضيفت إلى تكلفة القطع. لا يطالب تقرير الضريبة بضريبة هذه الفاتورة حتى تجيب.",
      "purchases.vatInCost.reclaimed": "تم استرداد ضريبة القيمة المضافة",
      "purchases.vatInCost.addedToCost": "أُضيفت ضريبة القيمة المضافة إلى تكلفة القطع",
      "purchases.vatInCost.confirm": "هل تريد تسجيل هذه الإجابة نهائياً: {{answer}}؟",
      "purchases.vatInCost.confirmButton": "تأكيد الإجابة",
      "purchases.vatInCost.cancel": "إلغاء",
      "purchases.vatInCost.saved": "تم تسجيل إجابة الضريبة",
      "purchases.vatInCost.failed": "تعذّر تسجيل إجابة الضريبة",
      "vat.unrecordedInCost.notice": "هناك {{count}} فاتورة شراء من قبل 25 أغسطس 2026 لا تسجّل ما إذا كانت ضريبتها قد استُردت أو أُضيفت إلى تكلفة القطع. لا تُطالَب ضريبتها ({{vat}} ر.س) هنا حتى تجيب على كل فاتورة.",
      "vat.unrecordedInCost.invoice": "{{ref}} — {{date}} — {{vat}} ر.س",
      "vat.priorCorrections.title": "تصحيحات الفترات السابقة",
      "vat.priorCorrections.number": "إشعار دائن",
      "vat.priorCorrections.source": "المستند الأصلي",
      "vat.priorCorrections.period": "الفترة الأصلية",
      "vat.priorCorrections.inputVat": "ضريبة المدخلات",
      "vat.priorCorrections.total": "إجمالي التصحيحات",
      "vat.priorCorrections.amendWarning": "يتجاوز إجمالي تصحيحات الفترات السابقة 15,000 ريال سعودي. تُلزمك هيئة الزكاة والضريبة والجمارك بإبلاغها خلال 20 يوماً وتعديل الإقرار أو الإقرارات الأصلية بدلاً من تصحيحها هنا.",
      "reversal.creditNotePeriodClosed": "شهر الإشعار الدائن مغلق.",
      "reversal.paymentCancelled": "أُلغيت الدفعة / أُعيد المبلغ",
      "reversal.postToday": "رحّل بتاريخ اليوم بدلاً من ذلك",
      "reversal.refundAccount": "حساب استلام المبلغ المسترد",
      "reversal.refundReceived": "تم استلام المبلغ المسترد",
      "reversal.settlement": "ماذا حدث للمبلغ المدفوع؟",
      "reversal.settlementLabel": "التسوية",
      "reversal.supplierKeepsCredit": "يحتفظ به المورّد رصيداً لصالحك",
      "reversal.postedOnSupplierDate": "رُحّل في {{posting}} (إشعار المورّد الدائن بتاريخ {{supplier}})",
      "reversal.postingDate": "تاريخ الترحيل",
      "reversal.recordRefund": "تسجيل الاسترداد",
      "reversal.refundRecorded": "سُجّل الاسترداد",
      "reversal.vatAmendmentNotice": "يؤدي إلغاء هذا المستند وحده إلى استبعاد {{amount}} من ضريبة القيمة المضافة من فترة ضريبية سابقة. إذا كان إقرار ضريبة القيمة المضافة لتلك الفترة قد قُدّم بالفعل وتجاوز إجمالي التصحيح 15,000 ريال سعودي، فتُلزمك هيئة الزكاة والضريبة والجمارك بإشعارها خلال 20 يوماً وتقديم إقرار معدّل عن تلك الفترة.",
      "reversal.basis": "نوع الإلغاء",
      "reversal.basisSupplierCreditNote": "إشعار دائن من المورّد",
      "reversal.basisEnteredInError": "أُدخل بالخطأ",
      "reversal.fieldsRequired": "أكمل الحقول المطلوبة.",
      "reversal.supplierCreditNoteNumber": "رقم إشعار المورّد الدائن",
      "reversal.supplierCreditNoteDate": "تاريخ إشعار المورّد الدائن",
      "reversal.supplierCreditNoteFile": "ملف إشعار المورّد الدائن",
      "reversal.failed": "تعذّر إلغاء هذا المستند.",

      "audit.nav": "سجل التدقيق المالي",
      "audit.pageTitle": "سجل التدقيق المالي",
      "audit.pageSubtitle": "من سجّل أو عدّل أو دفع أو ألغى — مع المقارنة قبل ← بعد عند التعديل.",
      "audit.timelineTitle": "النشاط",
      "audit.empty": "لا يوجد نشاط مسجّل بعد.",
      "audit.emptyDesc": "ستظهر الإجراءات المالية هنا.",
      "audit.loadFailed": "تعذّر تحميل سجل التدقيق.",
      "audit.accessDenied": "لا صلاحية",
      "audit.accessDeniedDesc": "ليست لديك صلاحية لعرض سجل التدقيق المالي.",
      "audit.allEntities": "كل المستندات",
      "audit.allActions": "كل الإجراءات",
      "audit.col.when": "التاريخ",
      "audit.col.who": "المستخدم",
      "audit.col.entity": "المستند",
      "audit.col.action": "الإجراء",
      "audit.col.change": "التغيير",
      "audit.entity.purchase_invoice": "فاتورة مشتريات",
      "audit.entity.expense": "مصروف",
      "audit.entity.supplier_payment": "دفعة مورّد",
      "audit.entity.credit_note": "إشعار دائن",
      "audit.entity.cash": "نقدية",
      "audit.action.created": "أُنشئت",
      "audit.action.approval_requested": "طُلبت الموافقة",
      "audit.action.approval_approved": "اعتُمدت",
      "audit.action.approval_rejected": "رُفض طلب الموافقة",
      "audit.action.approval_expired": "انتهى طلب الموافقة",
      "audit.action.credit_payout": "صرف رصيد",
      "audit.action.marked_paid": "تحديد كمدفوعة",
      "audit.action.possible_double_paid": "احتمال دفع مزدوج",
      "audit.action.purchase_invoice.scan": "مسح فاتورة شراء",
      "audit.action.receipt_delete": "حذف سند",
      "audit.action.receipt_refund": "استرداد سند",
      "audit.action.receipt_void": "إلغاء سند",
      "audit.entity.payment_receipt": "سند قبض",
      "audit.action.posted": "رُحّلت",
      "audit.action.edited": "عُدّلت",
      "audit.action.paid": "دُفعت",
      "audit.action.payment_voided": "أُلغيت الدفعة",
      "audit.action.reversed": "أُلغيت",
      "audit.action.deleted": "حُذفت",
      "audit.action.marked_paid": "وُسمت كمدفوعة",
      "audit.action.issued": "صدرت",
      "audit.action.recorded": "سُجّلت",
      "audit.action.voided": "أُلغيت",
      "audit.field.amount": "المبلغ",
      "audit.field.paid_from": "مصدر الدفع",
      "audit.field.vendor": "المورّد",
      "audit.field.expense_date": "التاريخ",
      "audit.field.category_id": "الفئة",
      "audit.field.note": "ملاحظة",
      "audit.field.status": "الحالة",
      "set.general": "عام",
      "set.notifications": "الإشعارات",
      "set.revenue": "تخطيط الإيرادات",
      "set.payments": "المدفوعات",
      "set.terms": "الشروط والأحكام",
      "set.features": "الميزات",
      "set.appointments": "المواعيد",
      "set.creditReminders": "تذكيرات الآجل",
      "set.feedback": "تقييمات العملاء",
      "set.whatsapp": "واتساب",
      "set.noBranchInScope": "اختر فرعًا لعرض إعداداته.",
      "set.delayFees": "رسوم التأخير",
      "settings.delayFees.title": "إعدادات رسوم التأخير",
      "settings.delayFees.desc": "تحديد الشروط الافتراضية لمهلة قطع غيار العميل والتأخر في استلام السيارة. تُستخدم هذه القيم المسبقة عند استلام السيارة.",
      "settings.delayFees.partsSectionTitle": "الإعدادات الافتراضية لقطع العميل",
      "settings.delayFees.partsSectionDesc": "المهلة الافتراضية والرسوم اليومية عندما يوفر العميل قطع الغيار بنفسه.",
      "settings.delayFees.defaultPartsDeadlineDays": "مهلة تسليم القطع الافتراضية (أيام)",
      "settings.delayFees.defaultPartsDeadlineDaysDesc": "تحديد موعد التسليم الافتراضي بعدد الأيام بعد تاريخ الاستلام.",
      "settings.delayFees.defaultPartsFeePerDay": "رسوم تأخير القطع الافتراضية / يوم",
      "settings.delayFees.defaultPartsFeePerDayDesc": "الرسوم المحتسبة عن كل يوم تقويمي بعد انتهاء مهلة تسليم القطع.",
      "settings.delayFees.collectionSectionTitle": "الإعدادات الافتراضية لاستلام المركبة",
      "settings.delayFees.collectionSectionDesc": "فترة السماح الافتراضية والرسوم اليومية عند تأخر العميل في استلام السيارة بعد جاهزيتها.",
      "settings.delayFees.defaultCollectionGraceHours": "مهلة السماح الافتراضية للاستلام (ساعات)",
      "settings.delayFees.defaultCollectionGraceHoursDesc": "عدد الساعات بعد جاهزية السيارة قبل بدء احتساب رسوم التأخير.",
      "settings.delayFees.defaultCollectionFeePerDay": "رسوم تأخير استلام المركبة الافتراضية / يوم",
      "settings.delayFees.defaultCollectionFeePerDayDesc": "الرسوم المحتسبة عن كل يوم تقويمي بعد انتهاء مهلة السماح.",
      "settings.delayFees.saved": "تم حفظ إعدادات رسوم التأخير بنجاح",
      "settings.delayFees.saveFailed": "فشل حفظ إعدادات رسوم التأخير",
      "settings.delayFees.loadFailed": "فشل تحميل إعدادات رسوم التأخير",

      "delayFees.customerProvidesParts": "العميل سيوفر قطع الغيار",
      "delayFees.partsDelayFeePerDay": "رسوم التأخير / يوم",
      "delayFees.collectionFeeCheckbox": "رسوم التأخر في استلام السيارة",
      "delayFees.collectionStartsAfter": "تبدأ بعد",
      "delayFees.hoursAfterReady": "ساعة من جاهزية السيارة",
      "delayFees.collectionFeePerDay": "رسوم التأخير / يوم",
      "delayFees.sar": "ر.س",
      "delayFees.sarPerDay": "ر.س / يوم",
      "delayFees.panelTitle": "رسوم التأخير والأرضيات",
      "delayFees.panelSubtitle": "متابعة عدادات تأخير تسليم القطع واستلام المركبة",
      "delayFees.partsMeterTitle": "تأخير تسليم قطع الغيار (العميل)",
      "delayFees.collectionMeterTitle": "تأخير استلام المركبة",
      "delayFees.meterRunning": "قيد الاحتساب",
      "delayFees.meterStopped": "متوقف",
      "delayFees.meterPending": "بانتظار الجاهزية",
      "delayFees.meterWithinGrace": "ضمن فترة السماح",
      "delayFees.meterWithinDeadline": "ضمن المهلة",
      "delayFees.meterNotConfigured": "غير مفعل",
      "delayFees.deadlineDate": "موعد المهلة",
      "delayFees.clockStarted": "بدء احتساب الرسوم",
      "delayFees.clockStopped": "تاريخ التوقف",
      "delayFees.gracePeriod": "فترة السماح",
      "delayFees.readyAt": "تاريخ جاهزية السيارة",
      "delayFees.partsReceivedAt": "تاريخ استلام القطع",
      "delayFees.collectedAt": "تاريخ تسليم السيارة",
      "delayFees.dailyRate": "المعدل اليومي",
      "delayFees.daysCharged": "الأيام المحتسبة",
      "delayFees.accruedTotal": "الإجمالي المستحق",
      "delayFees.grandTotal": "إجمالي رسوم التأخير المستحقة",
      "delayFees.grandTotalNow": "المستحق الآن",
      "history.actor.customer": "العميل",
      "history.service_completion_override": "إكمال إجباري للخدمة {{service_name}}",
      "history.part_completion_override": "إكمال إجباري للقطعة {{part_name}}",
      "history.task_completion_override": "إكمال إجباري للمهمة {{task_name}}",
      "history.part_receipt_overridden": "إنهاء {{task_name}} دون تأكيد استلام {{count}} من قطع العميل: {{parts}}",
      "history.sub_task_completion_override": "إكمال إجباري للخطوة {{sub_task_name}}",
      "history.delivery_code_overridden": "تسليم المركبة بدون رمز الاستلام — {{reason}}",
      "history.delivered_on_credit": "تم تسليم المركبة آجلاً، الاستحقاق خلال {{due_in_days}} يوم",
      "history.payment_link_sent": "تم إرسال رابط الدفع للعميل",
      "history.payment_requested": "طلب دفعة بمبلغ {{amount}}",
      "history.quotation_linked": "تم ربط عرض سعر ببطاقة العمل",
      "history.quotation_converted_to_invoice": "تم تحويل عرض السعر إلى الفاتورة {{reference_number}}",
      "history.branch_transferred": "تم النقل إلى فرع آخر",
      "history.job_card_cancelled_no_agreement": "أُلغيت — لم يتم الاتفاق. {{reason}}",
      "history.actor.insurer": "شركة التأمين",
      "delayFees.accruedTotalNow": "المحتسب حتى الآن",
      "delayFees.clockStartedAt": "بدء العدّاد",
      "delayFees.chargeStartsAt": "يبدأ الاحتساب",
      "delayFees.chargeStartsIn": "يبدأ الاحتساب خلال",
      "delayFees.countdownHm": "{{hours}} س {{minutes}} د",
      "delayFees.countdownM": "{{minutes}} د",
      "delayFees.countdownSeconds": "{{seconds}} ث",
      "delayFees.projectionNote": "المسجَّل في السجل أدناه {{settled}} ريال، والمستحق حتى هذه اللحظة {{live}} ريال. الفرق محتسَب ولم يُسجَّل بعد — يُسجَّل ليلاً أو فور تسليم المركبة.",
      "delayFees.dayUnit": "يوم",
      "delayFees.partsReceivedDone": "تم تسجيل استلام القطع",
      "delayFees.partsReceivedFailed": "تعذّر تسجيل استلام القطع",
      "delayFees.waive": "إعفاء",
      "delayFees.waiveReasonPrompt": "ما سبب الإعفاء عن هذا اليوم؟ يُسجَّل السبب مع الرسوم.",
      "delayFees.waiveReasonRequired": "يجب إدخال سبب للإعفاء من الرسوم",
      "delayFees.waiveFailed": "تعذّر الإعفاء من هذه الرسوم",
      "delayFees.actionsColumn": "إجراءات",
      "delayFees.partsFeeLabel": "تأخير توريد قطع الغيار من العميل",
      "delayFees.collectionFeeLabel": "تأخر استلام المركبة",
      "jc.delayFeeOutstandingTitle": "رسوم تأخير غير مدفوعة",
      "jc.delayFeeOutstandingDesc": "على العميل رسوم تأخير لم تُدرج في أي فاتورة بعد. الفاتورة الأصلية صادرة بالفعل ولا يمكن تعديلها، لذا تابع لإصدار فاتورة منفصلة بهذه الرسوم وإرسال رابط الدفع للعميل. ولا يُرسَل رمز استلام جديد — فبمجرد سداد فاتورة الرسوم يعود الرمز الذي بحوزة العميل صالحاً. أبلغه بالعودة وإدخال الرمز نفسه.",
      "jc.delayFeeProceed": "إنشاء فاتورة الرسوم",
      "jc.delayFeeOpenInvoice": "فتح فاتورة الرسوم",
      "jc.delayFeeInvoiceUnpaidDesc": "تم إنشاء فاتورة الرسوم لهذه المركبة ولم تُسدَّد بعد. يمكن تسليم المركبة بعد سدادها.",
      "jc.delayFeeInvoiceNumber": "رقم الفاتورة",
      "jc.delayFeeInvoiceBalance": "المبلغ المتبقي",
      "jc.delayFeeInvoiceFailed": "تعذّر إنشاء فاتورة رسوم التأخير",
      "jc.delayFeeInvoiceSent": "تم إنشاء فاتورة الرسوم — جارٍ إرسال رابط الدفع إلى العميل",
      "jc.delayFeeWaiveAll": "إعفاء الرسوم المستحقة",
      "jc.delayFeeWaiveAllDesc": "إعفاء كل أيام رسوم التأخير غير المفوترة على هذا الكرت. ويُسجَّل السبب على كل يوم منها. ثم تُسلَّم المركبة بالرمز الذي بحوزة العميل أصلاً.",
      "jc.delayFeeWaiveReasonLabel": "سبب الإعفاء",
      "jc.delayFeeWaiveReasonRequired": "يلزم ذكر سبب لإعفاء هذه الرسوم",
      "jc.delayFeeWaiveConfirm": "إعفاء وتسليم",
      "jc.delayFeeWaiveFailed": "تعذّر إعفاء رسوم التأخير",
      "jc.delayFeeReleaseOnCredit": "التسليم بالآجل",
      "jc.delayFeeReleaseOnCreditDesc": "إصدار فاتورة الرسوم وتسليم المركبة الآن. يصبح الكرت «تم التسليم — بانتظار الدفع»، ويُتابَع المبلغ كذمة مدينة.",
      "jc.delayFeeReleaseConfirm": "التسليم بالآجل",
      "jc.delayFeeReleaseReason": "سبب الإفراج عن المركبة",
      "jc.delayFeeReleaseReasonPlaceholder": "مثال: عميل قديم، تم الاتفاق على الرسوم هاتفياً",
      "jc.delayFeeReleaseReasonRequired": "يجب كتابة سبب للإفراج عن المركبة مع وجود رسوم تأخير غير مسددة",
      "delayFees.hoursUnit": "ساعة",
      "delayFees.itemizedBreakdown": "تفاصيل الاحتساب اليومي",
      "delayFees.feeDate": "التاريخ",
      "delayFees.feeType": "نوع الرسم",
      "delayFees.status": "الحالة",
      "delayFees.billed": "مفوتر",
      "delayFees.unbilled": "غير مفوتر",
      "delayFees.waived": "معفى",
      "delayFees.noAccruedFees": "لا توجد رسوم تأخير محتسبة حتى الآن",
      "delayFees.waivedBy": "أعفاها {{name}}",
      "delayFees.partExpectedAt": "الموعد المستهدف لاستلام القطعة",
      "delayFees.partExpectedAtHint": "اليوم الذي وعد العميل بإحضار القطعة فيه. تبدأ الرسوم في اليوم التالي له. ويمكن اختيار تاريخ سابق — فالعميل متأخر بالفعل.",
      "delayFees.perPartDeadlineNote": "لكل قطعة يحضرها العميل موعدها المستهدف الخاص بها، ويُحدَّد على القطعة نفسها عند إضافتها.",
      "delayFees.partDueOn": "قطعة من العميل — موعدها {{date}}",
      "delayFees.partReceivedOn": "قطعة من العميل — استُلمت في {{date}}",
      "delayFees.markPartReceived": "تم الاستلام",
      "delayFees.receivedModalTitle": "تسجيل تاريخ وصول هذه القطعة",
      "delayFees.receivedDateLabel": "تاريخ الاستلام",
      "delayFees.receivedDateHint": "اليوم أو قبله. والتأريخ بأثر رجعي أمر معتاد — فتتوقف المحاسبة عن الأيام التالية لذلك التاريخ.",
      "delayFees.receivedConfirm": "حفظ تاريخ الاستلام",
      "delayFees.receivedDateRequired": "أدخل تاريخ وصول القطعة",
      "delayFees.receivedNotFuture": "لا يمكن أن تصل القطعة في المستقبل. اختر اليوم أو تاريخاً سابقاً.",
      "delayFees.receivedAlreadyBilled": "بعض الأيام التالية لذلك التاريخ مُدرجة في فاتورة بالفعل، لذا لا يمكن إرجاع التاريخ إلى ما قبلها. أصدر إشعاراً دائناً على تلك الفاتورة بدلاً من ذلك.",
      "delayFees.receivedNoDeadline": "لا يوجد موعد مستهدف لهذه القطعة، فلا يوجد عدّاد لإيقافه.",

      "jc.scheduledStartDate": "تاريخ البدء المجدول",
      "jc.scheduledStartDateHint": "يتم حظر بدء العمل قبل هذا التاريخ. اتركه فارغاً للبدء الفوري.",
      "jc.scheduledStartBadge": "مجدول: {{date}}",
      "jc.scheduledStartBannerTitle": "بدء الصيانة مجدول بتاريخ: {{date}}",
      "jc.scheduledStartBannerDesc": "تم الاتفاق مع العميل على بدء العمل في هذا التاريخ. لا يمكن للفنيين بدء المهام قبل هذا الموعد.",
      "jc.repairNotYetStartable": "لا يمكن بدء العمل قبل تاريخ البدء المجدول: {{date}}",
      "jc.clearScheduledStart": "بدء فوري (إلغاء الجدولة)",
      "jc.editScheduledStart": "تعديل موعد البدء",
      "jc.scheduledStartCleared": "تم إلغاء تاريخ البدء المجدول — يمكن بدء العمل فوراً",
      "jc.scheduledStartUpdated": "تم تحديث تاريخ البدء المجدول بنجاح",
      "setup.title": "دليل الإعداد",
      "setup.subtitle": "أكمل إعداد مركزك",
      "setup.progress": "{{done}} من {{total}} مكتمل",
      "setup.allDone": "كل شيء جاهز! تم إعداد مركزك بالكامل.",
      "setup.dismiss": "إخفاء",
      "setup.show": "عرض دليل الإعداد",
      "setup.optional": "اختياري",
      "setup.done": "مكتمل",
      "setup.pending": "قيد الإنجاز",
      "setup.setUp": "إعداد",
      "setup.review": "مراجعة",
      "setup.step.company_info": "أكمل معلومات المركز",
      "setup.step.company_info.desc": "أضف الاسم والبريد الإلكتروني والجوال والعنوان الوطني.",
      "setup.step.logo": "ارفع شعارك",
      "setup.step.logo.desc": "يظهر شعارك على الفواتير وعروض الأسعار وبوابة العملاء.",
      "setup.step.branches": "أنشئ فرعًا",
      "setup.step.branches.desc": "يجب وجود فرع واحد على الأقل للتشغيل.",
      "setup.step.staff": "ادعُ فريقك",
      "setup.step.staff.desc": "أضف مستخدمي الموظفين ليتمكن فريقك من العمل في موتورمايند.",
      "setup.step.services": "أضف خدماتك",
      "setup.step.services.desc": "أنشئ كتالوج الخدمات ليظهر عند إنشاء بطاقات العمل.",
      "setup.step.payments": "اضبط طرق الدفع",
      "setup.step.payments.desc": "فعّل طريقة دفع واحدة على الأقل للتحصيل إلكترونيًا.",
      "setup.step.vat": "اضبط ضريبة القيمة المضافة",
      "setup.step.vat.desc": "سجّل الرقم الضريبي ونسبته، أو حدد الشركة كمعفاة من الضريبة.",
      "setup.step.terms": "انشر الشروط والأحكام",
      "setup.step.terms.desc": "انشر شروطك وأحكامك ليتمكن العملاء من مراجعتها وقبولها.",
      "setup.step.appointments": "فعّل الحجز الإلكتروني",
      "setup.step.appointments.desc": "اسمح للعملاء بحجز المواعيد من صفحتك العامة.",
      "rplan.periodMonthly": "شهري",
      "rplan.periodYearly": "سنوي",
      "rplan.periodHint": "تُقسَّم المبالغ السنوية على 12 تلقائيًا",
      "rplan.periodNote": "اختر شهري أو سنوي لكل بند — تُقسَّم المبالغ السنوية على 12.",
      "rplan.fixedTotalMonthly": "إجمالي المصاريف الثابتة (شهري)",
      "rplan.startMonth": "تبدأ الخطة من",
      "rplan.startMonthHint": "يُطبَّق الهدف من هذا الشهر حتى ديسمبر؛ الأشهر السابقة بدون هدف.",
      "rplan.appliesRange": "مُطبَّق من {{month}} حتى ديسمبر",
      "rplan.savedFrom": "تم حفظ الخطة — مُطبَّقة من {{month}} حتى ديسمبر",
      "tgt.planInSettings": "خطّط له من الإعدادات ▸ تخطيط الإيرادات",
      "rplan.title": "تخطيط الإيرادات",
      "rplan.subtitle": "أدخل المصاريف الثابتة وهامش الربح والربح المستهدف — وسنحسب هدف الإيراد الشهري ونطبّقه على جميع أشهر السنة.",
      "rplan.fixedExpenses": "المصاريف الشهرية الثابتة",
      "rplan.addExpense": "إضافة مصروف",
      "rplan.expensePlaceholder": "مثال: إيجار، رواتب، تسويق",
      "rplan.exRent": "الإيجار",
      "rplan.exSalaries": "الرواتب",
      "rplan.exUtilities": "المرافق",
      "rplan.fixedTotal": "إجمالي المصاريف الثابتة",
      "rplan.grossMargin": "هامش الربح الإجمالي %",
      "rplan.grossMarginHint": "متوسط هامشك بعد تكلفة القطع: (الإيراد − تكلفة القطع) ÷ الإيراد.",
      "rplan.targetProfit": "الربح المستهدف / شهرياً",
      "rplan.monthlyTarget": "هدف الإيراد الشهري",
      "rplan.formulaNote": "الهدف الشهري = (المصاريف الثابتة + الربح المستهدف) ÷ هامش الربح الإجمالي %.",
      "rplan.noMarginWarn": "حدّد هامش ربح أكبر من 0 للحصول على هدف دقيق قائم على الهامش.",
      "rplan.remove": "إزالة",
      "rplan.save": "حفظ الخطة",
      "rplan.saved": "تم حفظ الخطة",
      "rplan.saveFailed": "تعذّر حفظ الخطة",
      "history.job_hold": "تم وضعها قيد الانتظار",
      "history.job_resume": "تم استئناف العمل",
      "history.job_converted": "تم التحويل إلى خدمة عادية",
      "history.job_reopened": "تمت إعادة فتح بطاقة العمل",
      "history.delivery_code_sent": "تم إرسال رمز التسليم",
      "history.photos_uploaded": "تم رفع الصور",
      "history.signature_added": "تم التقاط التوقيع",
      "history.mechanic_assigned": "تم تعيين الفني {{mechanic}}",
      "history.walk_in_created": "تم إنشاء بطاقة عمل فورية",
      "history.task_updated": "تم تحديث المهمة: {{task_name}}",
      "history.task_deleted": "تم حذف المهمة: {{task_name}}",
      "history.task_duplicated": "تم نسخ المهمة: {{task_name}}",
      "history.task_status_changed": "المهمة {{task_name}} ← {{status}}",
      "history.part_removed": "تمت إزالة القطعة: {{part_name}}",
      "history.part_approved": "تمت الموافقة على القطعة: {{part_name}}",
      "history.service_removed": "تمت إزالة الخدمة: {{service_name}}",
      "history.service_approved": "تمت الموافقة على الخدمة: {{service_name}}",
      "history.service_rejected": "تم رفض الخدمة: {{service_name}}",
      "history.quotation_rejected": "تم رفض عرض السعر {{quotation_number}}",
      "history.quotation_updated": "تم تحديث عرض السعر {{quotation_number}}",
      "history.quotation_deleted": "تم حذف عرض السعر {{quotation_number}}",
      "history.quotation_resent": "تمت إعادة إرسال عرض السعر {{quotation_number}}",
      "history.invoice_deleted": "تم حذف الفاتورة {{invoice_number}}",
      "history.invoice_updated": "تم تحديث الفاتورة {{invoice_number}}",
      "history.invoice_item_added": "تمت إضافة بند إلى الفاتورة {{invoice_number}}",
      "history.invoice_sms_resent": "تمت إعادة إرسال رسالة الفاتورة {{invoice_number}}",
      "history.return_invoice_created": "تم إنشاء فاتورة مرتجع {{invoice_number}}",
      "history.delivery_verified": "تم التحقق من رمز التسليم — تم تسليم المركبة",
      "history.claim_created": "تم إنشاء مطالبة تأمين",
      "history.claim_updated": "تم تحديث مطالبة التأمين",
      "history.claim_status_changed": "حالة المطالبة ← {{status}}",
      "history.claim_estimate_sent": "تم إرسال التقدير إلى شركة التأمين",
      "history.claim_decided": "قرار المطالبة: {{decision}}",
      "history.expense_added": "تمت إضافة مصروف: {{description}} ({{amount}})",
      "history.expense_deleted": "تم حذف المصروف: {{description}}",
      "history.user_unassigned": "تم إلغاء تعيين {{user}}",
      "history.comment_added": "تمت إضافة تعليق",
      "history.issue_reported": "تم الإبلاغ عن مشكلة: {{title}}",
      "history.issue_resolved": "تم حل المشكلة",
      "jc.requoteRejected": "مراجعة البنود المرفوضة ({{count}})",
      "repairAfterReject.note": "رفض العميل بنداً واحداً أو أكثر. يمكنك المتابعة إلى الإصلاح بالبنود المعتمدة، أو تعديل عرض السعر — القرار لك.",
      "repairAfterReject.noApproved": "لا توجد بنود معتمدة للإصلاح. عدّل عرض السعر أولاً.",
      "repairAfterReject.proceed": "المتابعة إلى الإصلاح",
      "tgt.title": "هدف الإيراد الشهري",
      "tgt.adjust": "تعيين / تعديل",
      "tgt.saved": "تم حفظ الهدف",
      "tgt.saveFailed": "تعذّر حفظ الهدف",
      "tgt.none": "لم يتم تحديد هدف لهذا الشهر.",
      "tgt.setNow": "حدّد هدفاً",
      "tgt.met": "تم تحقيق الهدف 🎉",
      "tgt.progress": "{{pct}}% من الهدف",
      "tgt.target": "الهدف الأساسي",
      "tgt.carryForward": "المُرحّل",
      "tgt.remaining": "المتبقي",
      "tgt.carryNote": "يشمل الهدف غير المحقق المُرحّل من الشهر السابق.",
      "tgt.baseTarget": "الهدف الأساسي (هذا الشهر)",
      "tgt.carryOverride": "تجاوز المبلغ المُرحّل (اختياري)",
      "tgt.auto": "تلقائي",
      "tgt.carryHint": "اتركه فارغاً لترحيل عجز الشهر السابق تلقائياً.",
      "tgt.salariesFromHr": "احتساب الرواتب من نظام الموارد البشرية",
      "tgt.salariesHrLine": "الرواتب — الموارد البشرية",
      "tgt.salariesHrNote": "احذف أي بند رواتب يدوي لتجنب الاحتساب المزدوج",
      "rpt.tabLedger": "دفتر الحركات المالية",
      "rpt.tabByJob": "حسب أمر العمل",
      "rpt.tabLineItems": "البنود التفصيلية",
      "rpt.tabVat": "ضريبة القيمة المضافة",
      "rpt.date": "التاريخ",
      "rpt.type": "النوع",
      "rpt.ref": "المرجع",
      "rpt.job": "رقم الأمر",
      "rpt.party": "الطرف",
      "rpt.method": "طريقة الدفع",
      "rpt.amount": "المبلغ",
      "rpt.status": "الحالة",
      "rpt.customer": "العميل",
      "rpt.revenue": "الإيراد",
      "rpt.cost": "التكلفة",
      "rpt.profit": "الربح",
      "rpt.margin": "هامش الربح %",
      "rpt.paid": "المدفوع",
      "rpt.outstanding": "المتبقي",
      "rpt.description": "الوصف",
      "rpt.qty": "الكمية",
      "rpt.unitPrice": "سعر الوحدة",
      "rpt.invoiceNo": "رقم الفاتورة",
      "rpt.invoiceDate": "التاريخ",
      "rpt.zatcaType": "النوع",
      "rpt.buyer": "المشتري",
      "rpt.taxable": "الوعاء الضريبي",
      "rpt.vat": "ضريبة القيمة المضافة (15%)",
      "rpt.total": "الإجمالي",
      "rpt.kind_invoice": "فاتورة",
      "rpt.kind_return": "مرتجع",
      "rpt.kind_payment": "دفعة",
      "rpt.kind_part": "قطعة غيار",
      "rpt.kind_service": "خدمة",
      "rpt.kind_expense": "مصروف",
      // Task 5.3 i18n sweep: see EN block comment.
      "rpt.kind_refund": "استرداد",
      "rpt.kind_collection": "تحصيل",
      "rpt.kind_credit_note": "إشعار دائن",
      "rpt.kind_disbursement": "صرف نقدي",
      "rpt.forInvoice_one": "للفاتورة {{n}}",
      "rpt.forInvoice_two": "للفاتورتين {{n}}",
      "rpt.forInvoice_few": "للفواتير {{n}}",
      "rpt.forInvoice_many": "للفواتير {{n}}",
      "rpt.forInvoice_other": "للفواتير {{n}}",
      "rpt.kind_downpayment": "دفعة مقدمة",
      "rpt.lastActivity": "آخر حركة",
      "rpt.inv.title": "الفاتورة {{number}}",
      "rpt.inv.vehicle": "المركبة",
      "rpt.inv.activity": "الحركات",
      "rpt.inv.none": "لا توجد حركات مسجلة بعد.",
      "rpt.inv.loadFailed": "تعذّر تحميل حركات هذه الفاتورة.",
      "rpt.inv.openHint": "اضغط على الفاتورة لعرض حركاتها.",
      "rpt.ev.issued": "إصدار الفاتورة",
      "rpt.ev.payment": "استلام دفعة",
      "rpt.ev.receipt": "سداد من سند قبض",
      "rpt.ev.refund": "استرداد",
      "rpt.ev.return": "فاتورة مرتجع",
      "rpt.ev.credit_note": "إشعار دائن",
      "rpt.ev.write_off": "شطب",
      "rpt.totInvoiced": "إجمالي الفواتير",
      "rpt.totReturned": "المرتجعات",
      "rpt.totCollected": "المُحصّل",
      "rpt.totNet": "صافي الفواتير",
      "rpt.totRevenue": "الإيراد",
      "rpt.totCost": "التكلفة",
      "rpt.totProfit": "الربح",
      "rpt.totParts": "قطع الغيار",
      "rpt.totServices": "الخدمات",
      "rpt.totExpenses": "المصاريف",
      "rpt.totGrand": "الإجمالي",
      "rpt.totTaxable": "الوعاء الضريبي",
      "rpt.totVat": "ضريبة القيمة المضافة",
      "rpt.totTotal": "الإجمالي",
      "rpt.exportExcel": "تصدير Excel",
      "rpt.empty": "لا توجد حركات في هذه الفترة",
      "rpt.emptyDesc": "جرّب نطاقاً زمنياً أوسع.",
      "fin.title": "النظرة المالية",
      "fin.loadFailed": "تعذّر تحميل البيانات المالية",
      "fin.empty": "لا توجد بيانات مالية.",
      "fin.profit": "الربح",
      "fin.margin": "هامش الربح",
      "fin.onRevenue": "من الإيراد",
      "fin.expected": "المتوقع (القابل للفوترة)",
      "fin.actual": "الفعلي (المفوتر)",
      "fin.parts": "قطع الغيار",
      "fin.services": "الخدمات",
      "fin.expectedTotal": "إجمالي المتوقع",
      "fin.invoiced": "المفوتر",
      "fin.paid": "المدفوع",
      "fin.outstanding": "المتبقي",
      "fin.returned": "المرتجع / المضاف دائناً",
      "fin.cost": "التكلفة",
      "fin.partsCost": "تكلفة قطع الغيار",
      "fin.servicesCost": "تكلفة الخدمات / العمالة",
      "fin.expensesCost": "مصاريف أخرى",
      "fin.costTotal": "إجمالي التكلفة",
      "fin.customer": "العميل",
      "fin.insurer": "شركة التأمين",
      "fin.expectedShort": "المتوقع",
      "fin.invoicesList": "الفواتير",
      "fin.returnTag": "مرتجع",
      "fin.view": "عرض البيانات المالية",
      "fin.cardHint": "الإيراد المتوقع مقابل الفعلي والتكلفة والربح والهامش لبطاقة العمل هذه.",
      "fin.note": "المتوقع يعكس قطع الغيار والخدمات المعتمدة (قبل الضريبة). الفعلي يعكس الفواتير الصادرة. الربح = الإيراد − قطع الغيار − الخدمات − المصاريف الأخرى.",
      "AC/Heating": "التكييف/التدفئة",
      "Add New Task": "إضافة مهمة جديدة",
      "Add Task": "إضافة مهمة",
      "Assign Mechanic": "تعيين فني",
      "Bodywork": "أعمال الهيكل",
      "Cancel": "إلغاء",
      "Describe the task in detail...": "صِف المهمة بالتفصيل...",
      "Electrical": "الكهرباء",
      "Estimated Hours": "الساعات المقدّرة",
      "High": "عالية",
      "Issue Type": "نوع المشكلة",
      "Low": "منخفضة",
      "Mechanical": "ميكانيكا",
      "Normal": "عادية",
      "Other": "أخرى",
      "Priority": "الأولوية",
      "Routine Service": "صيانة دورية",
      "Task Description": "وصف المهمة",
      "Task Name": "اسم المهمة",
      "Tires/Wheels": "الإطارات/العجلات",
      "Unassigned": "غير معيَّن",
      "Urgent": "عاجلة",
      "Vehicle Body Type": "نوع هيكل المركبة",
      "addServicesOrPartsWithCostGreaterThan0": "أضف خدمات أو قطع غيار بتكلفة أكبر من 0",
      "adm.fieldConfiguredKeepBlank": "مهيّأ ({{hint}}) — اتركه فارغًا للإبقاء عليه",
      "allQCPassedGenerateInvoice": "اجتازت جميع فحوصات الجودة. يمكنك الآن إنشاء الفاتورة.",
      "back": "رجوع",
      "board.listView": "عرض القائمة",
      "board.view": "عرض اللوحة",
      "sort.label": "ترتيب حسب",
      "sort.smart": "الترتيب الذكي",
      "sort.newest": "الأحدث أولاً",
      "sort.oldest": "الأقدم أولاً",
      "sort.lastUpdated": "آخر تحديث",
      "sort.jobNumber": "رقم أمر العمل",
      "cannotAddTasksWhileQuotationIsPendingApproval": "لا يمكن إضافة مهام أثناء انتظار الموافقة على عرض السعر",
      "cannotModifyDeliveredOrClosedJobCard": "لا يمكن تعديل بطاقة عمل مُسلَّمة أو مُغلقة",
      "cm.header.account": "قائمة الحساب",
      "commercialYellow": "أصفر تجاري",
      "completeJobCardConfirmNote": "ستتغيّر حالة بطاقة العمل إلى \"مُستلمة\" ويمكن بدء التشخيص.",
      "converting": "جارٍ التحويل...",
      "e.g., Engine Oil Change": "مثال: تغيير زيت المحرك",
      "enter17CharacterVINOrScanBarcode": "أدخل رقم الهيكل المكوّن من 17 خانة أو امسح الباركود",
      "history.activityHistory": "سجل النشاط",
      "history.assignedTo": "تم الإسناد إلى",
      "history.created": "تم الإنشاء",
      "history.detailsUpdated": "تم تحديث التفاصيل",
      "history.findingApproved": "تمت الموافقة على ملاحظة الفحص",
      "history.findingRejected": "تم رفض ملاحظة الفحص",
      "history.findingSubmitted": "تم إرسال ملاحظة الفحص",
      "history.invoiceGenerated": "تم إنشاء الفاتورة",
      "history.invoicePaid": "تم دفع الفاتورة",
      "history.invoicePaidShort": "تم دفع الفاتورة",
      "history.loadFailed": "تعذّر تحميل سجل النشاط",
      "history.noActivity": "لا يوجد نشاط بعد",
      "history.partAdded": "تمت إضافة قطعة الغيار {{part}} (×{{qty}})",
      "history.partAddedToTask": "تمت إضافة قطعة الغيار {{part}} (×{{qty}}) إلى المهمة {{task}}",
      "history.partApproved": "تمت الموافقة على قطعة الغيار {{part}}",
      "history.partInstalled": "تم تركيب قطعة الغيار {{part}} (×{{qty}})",
      "history.partRejected": "تم رفض قطعة الغيار {{part}}",
      "history.serviceCompleted": "تم إكمال الخدمة {{service}}",
      "history.partInstalled": "تم تركيب القطعة {{part}}",
      "history.taskCompletionOverride": "تم إكمال المهمة {{task}} بتجاوز إداري ({{services}} خدمة، {{parts}} قطعة)",
      "ntf.approvalRequested.quotationDiscount.messageNoFigures": "طلب {{requester}} خصمًا على عرض السعر {{doc_number}}",
      "ntf.approvalRequested.invoiceDiscount.messageNoFigures": "طلب {{requester}} خصمًا على الفاتورة {{doc_number}}",
      "ntf.approvalRequested.quotationDiscount.messageNoAmount": "طلب {{requester}} خصمًا بنسبة {{percent}}% على عرض السعر {{doc_number}}",
      "ntf.approvalRequested.invoiceDiscount.messageNoAmount": "طلب {{requester}} خصمًا بنسبة {{percent}}% على الفاتورة {{doc_number}}",
      "ntf.approvalRequested.purchaseInvoice.messageNoAmount": "قدّم {{requester}} فاتورة الشراء {{doc_number}}، وهي تتجاوز الحد المسموح لمقدّم الطلب",
      "ntf.approvalRequested.quotationDiscount.title": "خصم عرض سعر بانتظار موافقتك",
      "ntf.approvalRequested.quotationDiscount.message": "طلب {{requester}} خصمًا بقيمة {{amount}} ريال ({{percent}}%) على عرض السعر {{doc_number}}",
      "ntf.approvalRequested.invoiceDiscount.title": "خصم فاتورة بانتظار موافقتك",
      "ntf.approvalRequested.invoiceDiscount.message": "طلب {{requester}} خصمًا بقيمة {{amount}} ريال ({{percent}}%) على الفاتورة {{doc_number}}",
      "ntf.approvalRequested.purchaseInvoice.title": "فاتورة شراء بانتظار موافقتك",
      "ntf.approvalRequested.purchaseInvoice.message": "قدّم {{requester}} فاتورة الشراء {{doc_number}} بمبلغ {{amount}} ريال، وهو يتجاوز الحد المسموح لمقدّم الطلب",
      "ntf.approvalApproved.title": "تمت الموافقة على طلبك",
      "ntf.approvalApproved.message": "وافق {{decider}} على طلبك بشأن {{doc_number}}",
      "ntf.approvalRejected.title": "رُفض طلبك",
      "ntf.approvalRejected.message": "رفض {{decider}} طلبك بشأن {{doc_number}}: {{reason}}",
      "appr.inbox.title": "الموافقات",
      "appr.heldDiscountSend": "بانتظار اعتماد الخصم — لم تُرسل للعميل",
      "appr.heldDiscountSendQuote": "بانتظار اعتماد الخصم — لم يُرسل عرض السعر للعميل",
      "appr.heldPurchasePost": "بانتظار اعتماد الشراء — لم تُرحَّل",
      "appr.heldActionReason": "معلّقة حتى يُعتمد الخصم أو يُرفض",
      "appr.notSentToCustomer": "لم تُرسل للعميل — أرسلها من هنا",
      "appr.discountExpired": "انتهى طلب الخصم — صدرت بالسعر الكامل",
      "appr.discountPendingSendHeld": "تم الحفظ. الخصم يتجاوز حدّك فأُرسل للاعتماد، ويصل إلى العميل بعد اعتماد الخصم أو رفضه.",
      "appr.inbox.subtitle": "الخصومات وفواتير الشراء التي تنتظر قرارك",
      "appr.inbox.col.type": "النوع",
      "appr.inbox.col.document": "المستند",
      "appr.inbox.col.requester": "مقدّم الطلب",
      "appr.inbox.col.amount": "المبلغ",
      "appr.inbox.type.quotationDiscount": "خصم عرض سعر",
      "appr.inbox.type.invoiceDiscount": "خصم فاتورة",
      "appr.inbox.type.purchaseInvoice": "فاتورة شراء",
      "appr.inbox.emptyTitle": "لا يوجد ما ينتظر موافقتك",
      "appr.inbox.emptyDescription": "ستظهر هنا طلبات الخصم والشراء التي تتجاوز حدّ زميلك.",
      "ntf.taskAssigned.title": "مهمة جديدة مسندة إليك",
      "ntf.taskAssigned.message": "أُسندت إليك المهمة {{task_name}} في أمر العمل {{job_number}}",
      "ntf.taskNudge.title": "تذكير بمهمة",
      "ntf.taskNudge.message": "تذكير: {{task_name}} في أمر العمل {{job_number}}",
      "ntf.repairStarted.title": "بدأ العمل — مهامك جاهزة",
      "ntf.repairStarted.message": "انتقل أمر العمل {{job_number}} إلى مرحلة الإصلاح؛ لديك {{count}} مهمة",
      "ntf.fleetSla.atRisk.title": "أمر عمل لأسطول يقترب من حدّ اتفاقية مستوى الخدمة",
      "ntf.fleetSla.atRisk.quote": "أمر العمل {{job_number}}: عرض السعر يقترب من المهلة المتفق عليها مع الأسطول",
      "ntf.fleetSla.atRisk.ready": "أمر العمل {{job_number}}: السيارة تقترب من موعد الجاهزية المتفق عليه مع الأسطول",
      "ntf.fleetSla.breached.title": "أمر عمل لأسطول تجاوز حدّ اتفاقية مستوى الخدمة",
      "ntf.fleetSla.breached.quote": "أمر العمل {{job_number}}: تجاوز عرض السعر المهلة المتفق عليها مع الأسطول",
      "ntf.fleetSla.breached.ready": "أمر العمل {{job_number}}: تجاوزت السيارة موعد الجاهزية المتفق عليه مع الأسطول",
      "ntf.fleetReplacementDue.title": "حان موعد السيارة البديلة",
      "ntf.fleetReplacementDue.message": "أمر العمل {{job_number}}: تَعِد اتفاقية الأسطول بسيارة بديلة ابتداءً من {{due_date}}، ولم تُسجَّل أي سيارة",
      "ntf.jobAssigned.title": "أمر عمل جديد مسند إليك",
      "ntf.jobAssigned.message": "أُسند إليك أمر العمل {{job_number}}",
      "ntf.jobReassignedAway.title": "تمت إعادة إسناد أمر العمل",
      "ntf.jobReassignedAway.message": "أُعيد إسناد أمر العمل {{job_number}} إلى فني آخر",
      "ntf.jobUnassigned.title": "تمت إزالتك من أمر العمل",
      "ntf.jobUnassigned.message": "تمت إزالتك من أمر العمل {{job_number}}",
      "ntf.qcFailed.title": "فشل فحص الجودة — مطلوب إعادة العمل",
      "ntf.qcFailed.message": "المهمة {{task_name}} في أمر العمل {{job_number}} لم تجتز فحص الجودة: {{notes}}",
      "ntf.qcRecheck.title": "تم إصلاح الملاحظة — جاهزة لإعادة الفحص",
      "ntf.qcRecheck.message": "المهمة {{task_name}} في أمر العمل {{job_number}} تم إعادة العمل عليها وجاهزة لإعادة فحصها",
      "ntf.quotationApproved.title": "تمت الموافقة على عرض السعر",
      "ntf.quotationApproved.message": "وافق العميل على عرض السعر {{quotation_number}} — يمكنك بدء العمل",
      "ntf.receiptDoublePaid.title": "احتمال دفع مزدوج",
      "ntf.receiptDoublePaid.message": "قد يكون سند القبض {{receipt_number}} قد دُفع مرتين (نقدًا + إلكترونيًا) — يرجى المراجعة واتخاذ قرار بشأن الاسترداد",
      "ntf.feedbackLow.title": "تقييم منخفض من عميل",
      "ntf.feedbackLow.message": "أمر العمل {{jobNumber}} حصل على تقييم {{rating}}/5 من العميل",
      "ntf.hrExpiry.title": "اقتراب انتهاء مستند/عقد موظف",
      "ntf.hrExpiry.message": "{{item}} الخاص بـ{{employee_name}} ينتهي خلال {{days}} يوم",
      "ntf.contractNonRenewal.title": "اقتراب فترة إشعار انتهاء العقد",
      "ntf.contractNonRenewal.message": "عقد {{employee_name}} ينتهي بتاريخ {{end_date}} (خلال {{days_to_end}} يوم) — تبدأ فترة الإشعار البالغة {{notice_days}} يومًا خلال {{lead_days}} يوم. يرجى تحديد التجديد أو إبلاغ الموظف.",
      "ntf.hrWpsReminder.title": "لم يُرفع ملف WPS",
      "ntf.hrWpsReminder.message": "مدة الرواتب {{period}} لم يُرفع ملفها منذ {{days}} يوم",
      "svc.confirmCompleteTitle": "إكمال الخدمة؟",
      "svc.confirmCompleteMessage": "هل تريد تسجيل هذه الخدمة كمكتملة؟ سيُحتسب ذلك ضمن إنهاء المهمة وأمر العمل.",
      "pt.confirmInstallTitle": "تأكيد تركيب القطعة؟",
      "pt.confirmInstallMessage": "هل تريد تسجيل هذه القطعة كمركّبة؟ سيُحتسب ذلك ضمن إنهاء المهمة وأمر العمل.",
      "history.partRemoved": "تمت إزالة قطعة الغيار {{part}}",
      "history.paymentReceived": "تم استلام الدفعة",
      "history.paymentReceivedForInvoice": "تم استلام دفعة للفاتورة {{number}} عبر {{method}}",
      "history.qcFailed": "فشل فحص الجودة للمهمة {{task}}",
      "history.qcPassed": "اجتاز فحص الجودة للمهمة {{task}}",
      "history.quotationApproved": "تمت الموافقة على عرض السعر {{number}} ({{amount}})",
      "history.quotationApprovedByCustomer": "وافق العميل على عرض السعر {{number}}",
      "history.quotationGenerated": "تم إنشاء عرض السعر {{number}} ({{amount}})",
      "history.serviceAdded": "تمت إضافة الخدمة {{service}}",
      "history.serviceAddedToTask": "تمت إضافة الخدمة {{service}} إلى المهمة {{task}} ({{price}})",
      "history.serviceCompleted": "تم إنجاز الخدمة {{service}}",
      "history.serviceRemoved": "تمت إزالة الخدمة {{service}}",
      "history.statusChange": "تم تغيير الحالة",
      "history.taskAssigned": "تم إسناد المهمة {{task}} إلى {{mechanic}}",
      "history.taskCompleted": "تم إنجاز المهمة {{task}}",
      "history.taskCreated": "تم إنشاء المهمة {{task}}",
      "history.taskStarted": "تم بدء المهمة {{task}}",
      "history.unassignedFrom": "تم إلغاء الإسناد من {{name}}",
      "history.userAssigned": "تم الإسناد إلى {{user}}",
      "history.quotation_item_rejected": "رفض العميل «{{line}}» — {{reason}}",
      "history.quotation_item_rejected_too_expensive": "رفض العميل «{{line}}» — السعر مرتفع",
      "history.quotation_item_rejected_later": "رفض العميل «{{line}}» — سيقوم بها لاحقاً",
      "history.quotation_item_rejected_elsewhere": "رفض العميل «{{line}}» — سيقوم بها في مركز آخر",
      "history.quotation_item_rejected_not_needed": "رفض العميل «{{line}}» — لا يحتاجها",
      "history.quotation_item_rejected_own_part": "رفض العميل «{{line}}» — سيوفّر القطعة بنفسه",
      "history.quotation_item_rejected_distrust_diagnosis": "رفض العميل «{{line}}» — لا يثق في التشخيص",
      "history.job_card_line_edited": "إعادة تسعير «{{line}}»: {{old_price}} ← {{new_price}}",
      "history.quotation_resent_after_edit": "إعادة تسعير وإرسال عرض سعر جديد {{quotation_number}} ({{lines}} بند، {{amount}})",
      "jobCompletedSuccessfully": "تم إكمال العمل بنجاح",
      "login.adminAccess": "دخول المسؤول",
      "login.adminLogin": "تسجيل دخول المسؤول",
      "login.adminSubtitle": "سجّل الدخول للوصول إلى لوحة تحكم المسؤول",
      "login.authFailed": "فشل تسجيل الدخول",
      "login.change": "تغيير",
      "login.copyright": "© {{year}} موتورمايند. جميع الحقوق محفوظة.",
      "login.emailAddress": "البريد الإلكتروني",
      "login.enterOtp": "أدخل رمز التحقق",
      "login.enterOtpError": "يرجى إدخال رمز التحقق",
      "login.otpDeck.checkingTitle": "جارٍ التحقق من الرمز",
      "login.otpDeck.checkingSubtitle": "لحظة واحدة، نتحقق من صحته.",
      "login.otpDeck.verifiedTitle": "تم التحقق",
      "login.otpDeck.verifiedSubtitle": "جارٍ إعداد جلستك…",
      "login.otpDeck.errorAnnouncement": "الرمز غير صحيح. يُرجى المحاولة مرة أخرى.",
      "jc.tzDecide.quickServiceTitle": "تبدو هذه خدمة سريعة",
      "jc.tzDecide.quickServiceBody": "يقترح TzamunAI Decide تحويل أمر العمل هذا إلى خدمة سريعة.",
      "jc.tzDecide.convertToQuickService": "تحويل إلى خدمة سريعة",
      "jc.tzDecide.proceedAsIs": "المتابعة كما هو",
      "jc.tzDecide.fullCheckTitle": "تبدو هذه فحصاً شاملاً",
      "jc.tzDecide.fullCheckBody": "يقترح TzamunAI Decide تحويل أمر العمل هذا إلى فحص شامل.",
      "jc.tzDecide.convertToFullCheck": "تحويل إلى فحص شامل",
      "jc.tzDecide.regularTitle": "لا يبدو هذا خدمة سريعة",
      "jc.tzDecide.regularBody": "يقترح TzamunAI Decide تحويل أمر العمل هذا إلى خدمة عادية.",
      "jc.tzDecide.convertToRegular": "تحويل إلى خدمة عادية",
      "login.enterPhone": "يرجى إدخال رقم الجوال",
      "login.incompletePhone": "أدخل رقم الجوال كاملاً، مثال 0501234567 أو 501234567",
      "login.heroPoint1": "إدارة بطاقات العمل وقطع الغيار والفواتير في مكان واحد",
      "login.heroPoint2": "تتبّع كل مركبة من الاستلام حتى التسليم",
      "login.heroPoint3": "طوّر مركزك عبر التحليلات والأتمتة",
      "login.heroSubtitle": "المنصة المتكاملة لإدارة مركز خدمات السيارات بكل سهولة.",
      "login.heroTitle": "أدِر مركزك بذكاء",
      "login.loginSuccess": "تم تسجيل الدخول بنجاح",
      "login.mobileLogin": "الدخول عبر الجوال",
      "login.mobileNumber": "رقم الجوال",
      "login.otpSent": "إذا كان هذا الرقم مسجلاً لدينا، فالرمز في طريقه إليك.",
      "login.otpSentTo": "إذا كان هذا الرقم مسجلاً لدينا، فقد أرسلنا الرمز إلى",
      "login.otpSubtitle": "أدخل رمز التحقق المُرسل إلى جوالك",
      "login.password": "كلمة المرور",
      "login.phoneHint": "أدخل رقم جوالك المسجّل",
      "login.resendOtp": "إعادة إرسال الرمز",
      "login.sendOtp": "إرسال الرمز",
      "login.sendingOtp": "جارٍ إرسال الرمز...",
      "login.signIn": "تسجيل الدخول",
      "login.signingIn": "جارٍ تسجيل الدخول...",
      "login.verificationCode": "رمز التحقق",
      "login.verifyLogin": "تحقّق وتسجيل الدخول",
      "login.verifying": "جارٍ التحقق...",
      "login.welcomeBack": "مرحبًا بعودتك",
      "makeModel": "الصانع / الطراز",
      "noResults": "لم يتم العثور على عملاء",
      "noVehicleFound": "لم يتم العثور على مركبة بهذا رقم اللوحة",
      "openJobWarning.createAnyway": "إنشاء على أي حال",
      "openJobWarning.message": "توجد بالفعل بطاقة عمل مفتوحة لهذه المركبة. هل تريد المتابعة؟",
      "openJobWarning.title": "توجد بطاقة عمل مفتوحة",
      "or": "أو",
      "paymentMethods.bank_transfer": "تحويل بنكي",
      "paymentMethods.card": "بطاقة",
      "paymentMethods.cash": "نقداً",
      "paymentMethods.check": "شيك",
      "paymentMethods.mobile_payment": "دفع عبر الجوال",
      "paymentMethods.pos": "نقاط بيع (شبكة)",
      "paymentMethods.manual": "يدوي",
      "pendingQC": "بانتظار فحص الجودة",
      "pleaseEnterRequiredFields": "يرجى تعبئة جميع الحقول المطلوبة",
      "pleaseSelectOrCreateVehicle": "يرجى اختيار مركبة أو إنشاء واحدة",
      "publicTransportBlue": "نقل عام (أزرق)",
      "readyForDelivery": "جاهز للتسليم",
      "registerNewVehicle": "تسجيل مركبة جديدة",
      "resendCode": "إعادة إرسال الرمز",
      "searchExistingVehicle": "البحث عن مركبة موجودة",
      "selectAll": "تحديد الكل",
      "somethingWentWrong": "حدث خطأ ما",
      "startDiagnosisFirstToAddTasks": "ابدأ التشخيص أولاً لإضافة المهام",
      "theme.dark": "داكن",
      "theme.light": "فاتح",
      "vehicleDelivered": "تم تسليم المركبة",
      "vehicleReadyDeliveryCodeSent": "المركبة جاهزة وتم إرسال رمز التسليم.",
      "vehicleShape": "شكل المركبة",
      "verifyDeliveryCode": "التحقق من رمز التسليم",
      "pcfg.tabPayments": "المدفوعات",
      "zatca.tab": "هيئة الزكاة",
      "zatca.title": "الفوترة الإلكترونية (هيئة الزكاة)",
      "zatca.phase2ActiveBadge": "المرحلة الثانية مفعّلة",
      "zatca.phase1": "المرحلة الأولى (الحالية)",
      "zatca.phase1Desc": "إصدار الفواتير محلياً مع رمز QR — السلوك الحالي. لا يتطلب أي إعداد.",
      "zatca.phase2": "المرحلة الثانية (الربط مع فاتورة)",
      "zatca.phase2Desc": "اعتماد فوري لفواتير الشركات وإبلاغ خلال ٢٤ ساعة لفواتير الأفراد عبر منصة فاتورة.",
      "zatca.activatedSince": "المرحلة الثانية مفعّلة — تاريخ التحويل {{date}}. الفواتير الصادرة بعده تُعتمد/تُبلّغ.",
      "zatca.deactivate": "الرجوع للمرحلة الأولى",
      "zatca.deactivateConfirm": "إرجاع هذه الشركة للمرحلة الأولى؟ ستتوقف الفواتير الجديدة عن الاعتماد/الإبلاغ (الوثائق المعتمدة تبقى سارية). إعادة التفعيل تبدأ سلسلة جديدة.",
      "zatca.deactivated": "تم الرجوع للمرحلة الأولى",
      "zatca.setupTitle": "إعداد المرحلة الثانية",
      "zatca.preconditionsFail": "أكمل ملف الشركة قبل الربط:",
      "zatca.step.csr": "المفاتيح وطلب الشهادة",
      "zatca.step.ccsid": "رمز التحقق وشهادة الامتثال",
      "zatca.step.compliance": "فحوصات الامتثال",
      "zatca.step.pcsid": "شهادة الإنتاج",
      "zatca.step.cutover": "التحويل",
      "zatca.lastError": "آخر خطأ",
      "zatca.environment": "البيئة",
      "zatca.envSimulation": "المحاكاة (تجربة)",
      "zatca.envProduction": "الإنتاج (فعلي)",
      "zatca.envSandbox": "بيئة المطورين",
      "zatca.invoiceTypes": "أنواع الفواتير التي تصدرها الشركة",
      "zatca.typesBoth": "ضريبية (شركات) + مبسطة (أفراد)",
      "zatca.typesStandard": "ضريبية (شركات) فقط",
      "zatca.typesSimplified": "مبسطة (أفراد) فقط",
      "zatca.vatGroup": "رقم المجموعة الضريبية (اختياري)",
      "zatca.vatGroupHint": "لأعضاء المجموعات الضريبية فقط: رقم تسجيل المجموعة (15 رقماً)؛ يبقى الرقم المميز للشركة على الشهادة.",
      "zatca.generateCsr": "توليد المفاتيح وطلب الشهادة",
      "zatca.csrDone": "تم توليد المفاتيح وطلب الشهادة جاهز",
      "zatca.otpExplain": "اطلب من العميل الدخول إلى بوابة فاتورة وتوليد رمز تحقق للربط (صالح لساعة واحدة)، ثم أدخله هنا.",
      "zatca.otp": "رمز التحقق من فاتورة",
      "zatca.requestCcsid": "طلب شهادة الامتثال",
      "zatca.ccsidDone": "صدرت شهادة الامتثال (صالحة ٢٤ ساعة — أكمل الخطوات التالية اليوم)",
      "zatca.ccsidCountdown": "تنتهي شهادة الامتثال خلال ~{{hours}} ساعة — أكمل الامتثال وشهادة الإنتاج قبل ذلك.",
      "zatca.runCompliance": "تشغيل فحوصات الامتثال",
      "zatca.warningsCount": "{{count}} تحذير",
      "zatca.doc.invoice": "فاتورة",
      "zatca.doc.credit_note": "إشعار دائن",
      "zatca.doc.debit_note": "إشعار مدين",
      "zatca.requestPcsid": "إصدار شهادة الإنتاج",
      "zatca.pcsidDone": "صدرت شهادة الإنتاج (صالحة ٥ سنوات)",
      "zatca.cutoverWarning": "من لحظة التحويل، كل فاتورة تُصدر لهذه الشركة تمر عبر هيئة الزكاة: فواتير الشركات تُحجب عن العميل حتى الاعتماد؛ وفواتير الأفراد تُبلّغ خلال ٢٤ ساعة. الفواتير الصادرة قبل التحويل تبقى على المرحلة الأولى.",
      "zatca.cutoverDate": "تاريخ ووقت التحويل",
      "zatca.cutoverHint": "يمكن أن يكون مستقبلياً (مثلاً منتصف الليل). لا يمكن أن يكون في الماضي.",
      "zatca.activateNow": "تفعيل المرحلة الثانية",
      "zatca.activated": "تم تفعيل المرحلة الثانية",
      "zatca.submissions": "إرسالات هيئة الزكاة",
      "zatca.noSubmissions": "لا توجد إرسالات بعد.",
      "zatca.pcsidExpiring": "تنتهي شهادة الإنتاج في {{date}} — خطط للتجديد",
      "zatca.colDoc": "المستند",
      "zatca.colApi": "الواجهة",
      "zatca.colStatus": "الحالة",
      "zatca.colAttempts": "المحاولات",
      "zatca.loadFailed": "تعذر تحميل إعدادات هيئة الزكاة",
      "zatca.backToKeys": "رجوع إلى المفاتيح وطلب الشهادة",
      "zatca.restartConfirm": "إعادة توليد المفاتيح تلغي شهادات الامتثال/الإنتاج الحالية لهذه الشركة — يبدأ الربط من الخطوة الأولى. هل تريد المتابعة؟",
      "zatca.restartNotice": "تعديل الخطوة الأولى — توليد طلب شهادة جديد يعيد بدء الربط.",
      "zatca.restartCancel": "إلغاء",
      "zatca.cancelSetup": "إلغاء الإعداد",
      "zatca.cancelSetupConfirm": "إلغاء إعداد المرحلة الثانية بالكامل؟ ستُحذف جميع المفاتيح والشهادات المولّدة لهذه الشركة وتبقى الشركة على المرحلة الأولى. يمكنك بدء الإعداد من جديد لاحقاً.",
      "zatca.cancelSetupDone": "تم إلغاء الإعداد — تبقى الشركة على المرحلة الأولى",
      "zatca.companyVat": "الرقم الضريبي للشركة: {{vat}} (يُستخدم في الشهادة ما لم يُدخل رقم مجموعة أدناه)",
      "zatca.sub.accepted": "مقبول",
      "zatca.sub.accepted_with_warnings": "تحذيرات",
      "zatca.sub.rejected": "مرفوض",
      "zatca.sub.reconcile_required": "يتطلب مراجعة",
      "zatca.sub.queued": "بالانتظار",
      "zatca.sub.sending": "جارٍ الإرسال",
      "zatca.stuck.title": "المعاملات المعلقة في زكاة",
      "zatca.stuck.reconcileRequired": "مطلوب مراجعة",
      "zatca.stuck.pastDeadline": "متجاوزة الموعد النهائي",
      "zatca.stuck.rejected": "مرفوضة",
      "zatca.stuck.allClear": "لا توجد معاملات معلقة — جميع فواتير المرحلة الثانية مقبولة أو مُبلغة ضمن المهلة.",
      "zatca.stuck.notOnThisPage": "توجد معاملات معلقة لكنها أقدم من هذه الصفحة — تصفّح القائمة للوصول إليها.",
      "zatca.stuck.retry": "إعادة المحاولة",
      "zatca.stuck.retryDone": "تمت إعادة إرسال المعاملة إلى الهيئة",
      "zatca.stuck.abandon": "إلغاء وإبطال الفاتورة",
      "zatca.stuck.abandonDone": "تم التخلي عن المستند وإبطال الفاتورة",
      "zatca.stuck.abandonReasonPlaceholder": "السبب (إلزامي، يُحفظ في سجل التدقيق)",
      "zatca.stuck.colInvoice": "الفاتورة / المستند",
      "zatca.stuck.colState": "الحالة",
      "zatca.stuck.colSubmitted": "تاريخ الإرسال",
      "zatca.stuck.colDeadline": "الموعد النهائي",
      "zatca.stuck.colFailure": "سبب الفشل",
      "zatca.stuck.colError": "تفاصيل خطأ الهيئة",
      "zatca.stuck.typeRejected": "مرفوض من الهيئة (رفض الاعتماد أو الإبلاغ)",
      "zatca.stuck.typeReconcile": "توقف المعالجة (لم يُرسل أو النتيجة غير مؤكدة)",
      "zatca.stuck.typeDeadline": "تجاوز مهلة 24 ساعة (تجاوز الموعد المحدد)",
      "zatca.stuck.noErrorDetails": "لا توجد تفاصيل خطأ محددة",
      "zatca.complianceFailedTitle": "فشل فحص الامتثال من هيئة الزكاة",
      "zatca.complianceFailedDesc": "تم رفض فحص الامتثال من هيئة الزكاة. ووفقاً لمواصفات الهيئة (§3.3.4.2)، أصبحت شهادة الامتثال (CCSID) مستهلكة وملغاة. تتطلب إعادة المحاولة توليد طلب شهادة جديد (CSR) والحصول على رمز تحقق جديد لاستخدام واحد من بوابة فاتورة.",
      "pcfg.heading": "إعداد المدفوعات",
      "pcfg.payInGarage": "الدفع في المركز",
      "pcfg.payInGarageHint": "السماح لهذا المستأجر بعرض الدفع في المركز. يمكن للموظفين بعدها إزالة طرق الدفع الإلكترونية من روابط دفع محددة. لا تُطبَّق عمولة المنصة على هذه المدفوعات.",
      "pcfg.subtitle": "اضبط وضع التسوية والرسوم لكل طريقة دفع وإعدادات الدفعات لهذه الشركة.",
      "pcfg.allowTenantManage": "السماح للمستأجر بإدارة خيارات الدفع",
      "pcfg.allowTenantManageHint": "عند الإيقاف، تكون إعدادات الدفع لهذا المستأجر للقراءة فقط ويديرها موتورمايند فقط.",
      "pcfg.methodsHeading": "طرق الدفع",
      "pcfg.payoutHeading": "الحساب البنكي والتسوية",
      "pcfg.save": "حفظ",
      "pcfg.saving": "جارٍ الحفظ…",
      "pcfg.saved": "تم حفظ إعدادات الدفع",
      "pcfg.failedToLoad": "فشل تحميل إعدادات الدفع",
      "pcfg.failedToSave": "فشل حفظ إعدادات الدفع",
      "pcfg.enabled": "مفعّلة",
      "pcfg.disabled": "معطّلة",
      "pcfg.provider_myfatoorah": "ماي فاتورة",
      "pcfg.provider_tabby": "تابي",
      "pcfg.provider_tamara": "تمارا",
      "pcfg.settlementMode": "وضع التسوية",
      "pcfg.settlementModeHint": "أين تتم تسوية الأموال لهذا المزوّد.",
      "pcfg.minimumAmount": "الحد الأدنى للمبلغ (ر.س)",
      "pcfg.minimumAmountHint": "أقل من هذا المبلغ تظهر الطريقة معطّلة في قائمة الاختيار. اتركه فارغاً لعدم وجود حد أدنى.",
      "pcfg.minimumAmountPlaceholder": "بدون حد أدنى",
      "pcfg.settlementMode_own_account": "حساب خاص",
      "pcfg.settlementMode_motormind_managed": "مُدار عبر موتورمايند",
      "pcfg.merchantOfRecord": "التاجر المسجّل",
      "pcfg.merchantOfRecordHint": "التاجر القانوني الظاهر للعميل.",
      "pcfg.merchantOfRecord_garage": "المركز",
      "pcfg.merchantOfRecord_motormind": "موتورمايند",
      "pcfg.providerFee": "رسوم المزوّد",
      "pcfg.motormindFee": "رسوم موتورمايند",
      "pcfg.refundFee": "رسوم الاسترداد",
      "pcfg.feeType": "نوع الرسوم",
      "pcfg.feeType_none": "بدون",
      "pcfg.feeType_percentage": "نسبة مئوية",
      "pcfg.feeType_fixed": "ثابت",
      "pcfg.feeType_both": "نسبة + ثابت",
      "pcfg.feePercentage": "النسبة المئوية",
      "pcfg.feePercentageHint": "مثال: 2.5 تعني 2.5%",
      "pcfg.feeFixed": "المبلغ الثابت",
      "pcfg.feeFixedHint": "المبلغ بالريال السعودي",
      "pcfg.sar": "ر.س",
      "pcfg.feesOwnAccountNote": "تُستخدم الرسوم عادةً للتسوية المُدارة عبر موتورمايند؛ وفي الحساب الخاص تكون للعلم فقط.",
      "pcfg.disabledMethodNote": "فعّل هذه الطريقة لضبط رسومها.",
      "pcfg.testMode": "وضع الاختبار / التجريبي",
      "pcfg.ownCredsHeading": "بيانات بوابة الدفع الخاصة بالمركز (مشفّرة ولا تُعرض أبداً).",
      "pcfg.managedCredsNote": "يستخدم بيانات منصة موتورمايند. اضبط الرسوم التي تتقاضاها موتورمايند من هذه المنشأة.",
      "pcfg.credKeepPlaceholder": "مُهيأ ({{hint}}) — اتركه فارغاً للإبقاء عليه",
      "pcfg.credEnterPlaceholder": "أدخل القيمة",
      "pcfg.webhookUrl": "رابط الويب هوك",
      "pcfg.webhookHint": "سجّل هذا الرابط في لوحة مزوّد الدفع.",
      "pcfg.webhookCopied": "تم نسخ رابط الويب هوك",
      "pcfg.testConnection": "اختبار الاتصال",
      "pcfg.testFailed": "فشل الاختبار",
      "pcfg.testHint": "احفظ البيانات أولاً ثم اختبر.",
      "pcfg.bankIban": "الآيبان",
      "pcfg.bankIbanPlaceholder": "SA0000000000000000000000",
      "pcfg.bankName": "اسم البنك",
      "pcfg.accountHolder": "صاحب الحساب",
      "pcfg.payoutInterval": "فترة التسوية",
      "pcfg.payoutIntervalHint": "كم مرة تُنفَّذ الدفعات (مع الوحدة).",
      "pcfg.payoutUnit": "الوحدة",
      "pcfg.payoutUnit_day": "يوم/أيام",
      "pcfg.payoutUnit_week": "أسبوع/أسابيع",
      "pcfg.payoutUnit_month": "شهر/أشهر",
      "pcfg.negativeBalancePolicy": "سياسة الرصيد السالب",
      "pcfg.negativeBalancePolicyHint": "ما الذي يحدث عند أن يصبح رصيد المستأجر سالباً.",
      "pcfg.negativeBalancePolicy_carry": "ترحيل الرصيد",
      "pcfg.negativeBalancePolicy_block": "إيقاف الدفعات",
      "pcfg.negativeBalancePolicy_absorb": "موتورمايند يتحمّلها",
      "mp.pageTitle": "سجل المدفوعات المُدارة",
      "mp.pageSubtitle": "المدفوعات التي حصّلتها موتورمايند نيابةً عن المنشآت المُدارة",
      "mp.applyFilters": "تطبيق",
      "mp.clearFilters": "مسح",
      "mp.colMotorMindFee": "رسوم موتورمايند",
      "mp.colNetToTenant": "الصافي للمنشأة",
      "mp.colPayoutStatus": "حالة الدفعة",
      "mp.emptyTitle": "لا توجد مدفوعات مُدارة بعد",
      "mp.emptyDesc": "ستظهر هنا المدفوعات المُحصّلة للمنشآت المُدارة من موتورمايند.",
      "mp.exportPdf": "طباعة / PDF",
      "mp.kpiMotorMindFee": "رسوم موتورمايند",
      "mp.kpiNetToTenants": "الصافي للمنشآت",
      "mp.kpiProviderFee": "رسوم المزوّد",
      "mp.status.accrued": "مستحق",
      "mp.status.settled": "مُسوّى",
      "mp.totalsFor": "الإجماليات لـ {{count}}",
      "mp.allDates": "كل التواريخ",
      "mp.batch": "دفعة",
      "ppc.title": "بيانات الدفع لحساب موتورمايند",
      "ppc.subtitle": "بيانات اعتماد الدفع المستخدمة للمنشآت المُدارة من موتورمايند. مشفّرة في التخزين ولا تُعرض الأسرار أبداً.",
      "ppc.configured": "مُهيأ",
      "ppc.notConfigured": "غير مُهيأ",
      "ppc.enterPlaceholder": "أدخل القيمة",
      "ppc.keepPlaceholder": "مُهيأ ({{h}}) — اتركه فارغاً للإبقاء عليه",
      "ppc.environment": "البيئة",
      "ppc.production": "الإنتاج",
      "ppc.sandbox": "تجريبي",
      "ppc.active": "مُفعّل",
      "ppc.updated": "آخر تحديث",
      "ppc.saved": "تم حفظ بيانات {{p}}",
      "ppc.saveFailed": "تعذّر حفظ البيانات",
      "ppc.webhookUrl": "رابط الويب هوك",
      "ppc.webhookHint": "سجّل هذا الرابط في لوحة مزوّد الدفع لاستقبال أحداث الدفع.",
      "ppc.copied": "تم نسخ رابط الويب هوك",
      "ppc.test": "اختبار",
      "ppc.testFailed": "فشل الاختبار",
      "ppc.testHint": "يتحقق الاختبار من البيانات المحفوظة — اضغط حفظ أولاً ثم اختبر.",
      "mfee.title": "رسوم طرق الدفع",
      "mfee.subtitle": "رسوم عامة تُطبّق على جميع المنشآت المُدارة من موتورمايند. عمولة المزوّد + عمولة موتورمايند لكل طريقة؛ يرى المستأجر الإجمالي فقط.",
      "mfee.method": "الطريقة",
      "mfee.providerCommission": "عمولة المزوّد",
      "mfee.motormindCommission": "عمولة موتورمايند",
      "mfee.total": "الإجمالي",
      "mfee.saved": "تم حفظ رسوم {{p}}",
      "mfee.saveAll": "حفظ جميع الرسوم",
      "mfee.savedAll": "تم حفظ الرسوم",
      "mfee.saveFailed": "تعذّر حفظ الرسوم",
      "tfee.title": "عمولات الدفع",
      "tfee.subtitle": "إجمالي العمولة المخصومة لكل طريقة دفع على المدفوعات المُدارة.",
      "tfee.allCards": "جميع البطاقات",
      "tpg.title": "تابي وتمارا (حسابك الخاص)",
      "tpg.title2": "تابي وتمارا",
      "tpg.subtitle": "اربط حسابات تابي وتمارا الخاصة بك. البيانات مشفّرة ولا تُعرض أبداً.",
      "tpg.subtitle2": "استخدم حسابك التجاري الخاص، أو دع موتورمايند يحصّل ويحوّل لك. البيانات مشفّرة ولا تُعرض أبداً.",
      "tpg.configured": "مُهيأ",
      "tpg.ownAccount": "حسابك الخاص",
      "tpg.ownAccountDesc": "بيانات حسابك التجاري",
      "tpg.managed": "حساب موتورمايند",
      "tpg.managedDesc": "موتورمايند يحصّل ويحوّل لك",
      "tpg.managedTitle": "التحصيل عبر موتورمايند",
      "tpg.managedNote": "هذه البوابة مُدارة من موتورمايند ولا يمكن تعديلها من هنا.",
      "tpg.managedNote2": "يحصّل موتورمايند هذه المدفوعات على حسابه ويحوّل حصتك إلى حسابك البنكي للتسوية. يقوم موتورمايند بتفعيل البوابة وتحديد الرسوم — وتعمل بعد الإعداد من جهته.",
      "tpg.modeSwitched": "تم تحديث وضع التسوية",
      "tpg.lockedTip": "مُدارة من موتورمايند — تواصل مع موتورمايند لتغيير هذه الإعدادات.",
      "tpg.keepPlaceholder": "مُهيأ ({{h}}) — اتركه فارغاً للإبقاء عليه",
      "tpg.enterPlaceholder": "أدخل القيمة",
      "tpg.enabled": "مُفعّل",
      "tpg.testMode": "وضع الاختبار / التجريبي",
      "tpg.saved": "تم حفظ إعدادات {{p}}",
      "tpg.saveFailed": "تعذّر الحفظ",
      "mp.filterTenant": "المستأجر",
      "mp.filterProvider": "مزود الدفع",
      "mp.filterStatus": "حالة التسوية",
      "mp.filterFrom": "من",
      "mp.filterTo": "إلى",
      "mp.btnFilter": "تطبيق",
      "mp.btnReset": "إعادة تعيين",
      "mp.btnPdf": "طباعة / PDF",
      "mp.btnCsv": "تصدير CSV",
      "mp.colTenant": "المستأجر",
      "mp.colDate": "التاريخ",
      "mp.colProvider": "المزود",
      "mp.colGross": "الإجمالي",
      "mp.colProviderFee": "رسوم المزود",
      "mp.colPlatformFee": "رسوم MotorMind",
      "mp.colNet": "الصافي للمستأجر",
      "mp.colStatus": "حالة التسوية",
      "mp.colBatch": "الدفعة",
      "mp.statusAccrued": "مستحق",
      "mp.statusSettled": "مسوّى",
      "mp.kpiGross": "إجمالي المبالغ",
      "mp.kpiProviderFees": "رسوم المزودين",
      "mp.kpiPlatformFees": "رسوم MotorMind",
      "mp.kpiNet": "الصافي للمستأجرين",
      "mp.totalsRow": "المجاميع",
      "mp.noData": "لا توجد مدفوعات.",
      "mp.allTenants": "جميع المستأجرين",
      "mp.allProviders": "جميع المزودين",
      "mp.allStatuses": "جميع الحالات",
      "po.title": "المدفوعات",
      "po.subtitle": "إدارة مدفوعات المتاجر والموافقة على الاستردادات والاستفسارات",
      "po.tabPayouts": "المدفوعات",
      "po.tabRefunds": "الموافقة على الاسترداد",
      "po.tabInquiries": "الاستفسارات",
      "po.provider": "المزود",
      "po.provider_myfatoorah": "ماي فاتورة",
      "po.provider_tabby": "تابي",
      "po.provider_tamara": "تمارا",
      "po.companyId": "معرّف الشركة",
      "po.companyIdPlaceholder": "معرّف الشركة UUID",
      "po.filterCompanyId": "تصفية حسب معرّف الشركة",
      "po.allStatuses": "كل الحالات",
      "po.period": "الفترة",
      "po.periodStart": "بداية الفترة",
      "po.periodEnd": "نهاية الفترة",
      "po.outstanding": "المستحق",
      "po.netTotal": "صافي الإجمالي",
      "po.due": "مستحق",
      "po.bnplUnsettled": "الدفع الآجل لم تتم تسويته بعد",
      "po.bnplSettling": "دفعات آجلة قيد التسوية",
      "po.noPayoutYet": "لا يوجد دفعة",
      "po.generate": "إنشاء",
      "po.generatePayout": "إنشاء دفعة",
      "po.payoutGenerated": "تم إنشاء مسودة الدفعة",
      "po.approve": "اعتماد",
      "po.approvePayout": "اعتماد الدفعة",
      "po.approvePayoutConfirm": "اعتماد مسودة الدفعة؟ ستصبح جاهزة للتسجيل بعد السداد.",
      "po.payoutApproved": "تم اعتماد الدفعة",
      "po.void": "إلغاء",
      "po.voidPayout": "إلغاء الدفعة",
      "po.voidPayoutConfirm": "إلغاء هذه الدفعة؟ لا يمكن التراجع عن ذلك.",
      "po.payoutVoided": "تم إلغاء الدفعة",
      "po.record": "تسجيل",
      "po.recordPayout": "تسجيل الدفعة",
      "po.payoutRecorded": "تم تسجيل الدفعة",
      "po.statement": "كشف الحساب",
      "po.bankReference": "المرجع البنكي",
      "po.bankReferencePlaceholder": "مرجع التحويل / سويفت",
      "po.bankReferenceRequired": "المرجع البنكي مطلوب",
      "po.settledToIban": "تمت التسوية إلى الآيبان",
      "po.ibanRequired": "الآيبان مطلوب",
      "po.onFileIban": "الآيبان المسجل لدى العميل",
      "po.ibanMismatchWarning": "هذا الآيبان يختلف عن الآيبان المسجل لدى هذا العميل. تحقق قبل التسجيل.",
      "po.evidence": "ملفات الإيصال",
      "po.evidenceHint": "ارفع إيصالاً أو أكثر للتحويل (صورة أو PDF).",
      "po.evidenceRequired": "مطلوب ملف إيصال واحد على الأقل",
      "po.removeFile": "إزالة الملف",
      "po.payoutStatus_draft": "مسودة",
      "po.payoutStatus_approved": "قيد التنفيذ",
      "po.payoutStatus_paid": "مكتمل",
      "po.payoutStatus_void": "ملغاة",
      "po.companyIdRequired": "معرّف الشركة مطلوب",
      "po.periodRequired": "بداية ونهاية الفترة مطلوبتان",
      "po.periodInvalid": "يجب أن تكون نهاية الفترة بعد بدايتها",
      "po.noPayouts": "لا توجد مدفوعات مستحقة",
      "po.amount": "المبلغ",
      "po.reason": "السبب",
      "po.message": "الرسالة",
      "po.refundType_full": "كامل",
      "po.refundType_partial": "جزئي",
      "po.approveRefund": "اعتماد الاسترداد",
      "po.approveRefundConfirm": "اعتماد هذا الاسترداد؟ سيتم تنفيذ الاسترداد عبر البوابة.",
      "po.refundApproved": "تم اعتماد الاسترداد",
      "po.reject": "رفض",
      "po.rejectRefund": "رفض الاسترداد",
      "po.rejectReason": "سبب الرفض",
      "po.rejectReasonPlaceholder": "وضّح سبب رفض هذا الاسترداد",
      "po.rejectReasonRequired": "سبب الرفض مطلوب",
      "po.refundRejected": "تم رفض الاسترداد",
      "po.refundStatus_requested": "مطلوب",
      "po.refundStatus_approved": "معتمد",
      "po.refundStatus_processing": "قيد المعالجة",
      "po.refundStatus_completed": "مكتمل",
      "po.refundStatus_rejected": "مرفوض",
      "po.refundStatus_failed": "فشل",
      "po.noRefunds": "لا توجد طلبات استرداد معلّقة",
      "po.respond": "الرد",
      "po.respondInquiry": "الرد على الاستفسار",
      "po.adminResponse": "الرد",
      "po.adminResponsePlaceholder": "اكتب ردك على المتجر",
      "po.responseRequired": "الرد مطلوب",
      "po.sendResponse": "إرسال الرد",
      "po.inquiryResponded": "تم إرسال الرد",
      "po.inquiryStatus": "الحالة",
      "po.inquiryStatus_open": "مفتوح",
      "po.inquiryStatus_under_review": "قيد المراجعة",
      "po.inquiryStatus_resolved": "تم الحل",
      "po.inquiryStatus_rejected": "مرفوض",
      "po.inquiryReason_not_received": "لم يُستلم",
      "po.inquiryReason_wrong_amount": "مبلغ خاطئ",
      "po.inquiryReason_other": "أخرى",
      "po.noInquiries": "لا توجد استفسارات مفتوحة",
      "po.tabEligible": "المعاملات المؤهلة",
      "po.reference": "المرجع",
      "po.gross": "الإجمالي",
      "po.commission": "العمولة",
      "po.gatewayFee": "رسوم البوابة",
      "po.net": "الصافي",
      "po.paidAt": "تاريخ السداد",
      "po.stage": "المرحلة",
      "po.allCompanies": "جميع الشركات",
      "po.allStages": "جميع المراحل",
      "po.stage_awaiting_payout": "بانتظار التسوية",
      "po.stage_processing": "قيد المعالجة",
      "po.stage_approved": "معتمد",
      "po.stage_paid": "مدفوع",
      "po.noEligibleTransactions": "لا توجد معاملات مؤهلة",
      "po.noOutstandingBalances": "لا توجد أرصدة مستحقة",
      "tp.pageTitle": "تحصيلات بوابة الدفع",
      "tp.pageSubtitle": "اعرض مدفوعاتك وحالة التسوية والدفعات",
      "tp.paymentsTab": "المدفوعات",
      "tp.payoutsTab": "الدفعات",
      "tp.paymentsCount": "المدفوعات",
      "tp.payoutsCount": "الدفعات",
      "tp.refresh": "تحديث",
      "tp.retry": "إعادة المحاولة",
      "tp.close": "إغلاق",
      "tp.actions": "الإجراءات",
      "tp.date": "التاريخ",
      "tp.provider": "المزود",
      "tp.gross": "الإجمالي",
      "tp.net": "الصافي",
      "tp.netOwed": "الصافي المستحق",
      "tp.fees": "الرسوم",
      "tp.vat": "ضريبة الرسوم",
      "onb.navTitle": "انضمام العملاء",
      "onb.title": "انضمام العملاء",
      "onb.subtitle": "ادعُ العملاء المحتملين وراجع طلباتهم.",
      "onb.deleteInvitation": "حذف الدعوة",
      "onb.deleteConfirm": "حذف دعوة {{name}}؟ سيؤدي ذلك إلى إزالة الطلب وأي مستندات مرفوعة نهائيًا. لا يمكن التراجع عن هذا الإجراء.",
      "onb.invitationDeleted": "تم حذف الدعوة",
      "onb.inviteClient": "دعوة عميل",
      "onb.inviteClientDesc": "أرسل رابط انضمام مخصصًا عبر رسالة نصية.",
      "onb.sendInvite": "إرسال الدعوة",
      "onb.contactName": "اسم جهة الاتصال",
      "onb.contactNamePlaceholder": "مثال: أحمد آل سعود",
      "onb.mobile": "رقم الجوال",
      "onb.mobileHint": "جوال سعودي، مثال 05XXXXXXXX أو ‎+9665XXXXXXXX",
      "onb.language": "اللغة",
      "onb.langAr": "العربية",
      "onb.langEn": "English",
      "onb.contactAndMobileRequired": "اسم جهة الاتصال ورقم الجوال مطلوبان",
      "onb.invitedSmsSent": "تمت دعوة العميل — أُرسلت الرسالة",
      "onb.invitedSmsFailed": "تمت دعوة العميل لكن فشل إرسال الرسالة — انسخ الرابط وأرسله يدويًا",
      "onb.allStatuses": "كل الحالات",
      "onb.noLeads": "لا توجد طلبات انضمام بعد",
      "onb.noLeadsDesc": "ادعُ عميلاً للبدء.",
      "onb.client": "العميل",
      "onb.linkActivity": "نشاط الرابط",
      "onb.created": "تاريخ الإنشاء",
      "onb.justNow": "الآن",
      "onb.minutesAgo": "قبل {{count}} دقيقة",
      "onb.hoursAgo": "قبل {{count}} ساعة",
      "onb.daysAgo": "قبل {{count}} يوم",
      "onb.onboardingLink": "رابط الانضمام",
      "onb.copy": "نسخ",
      "onb.copyLink": "نسخ الرابط",
      "onb.linkCopied": "تم نسخ الرابط",
      "onb.copyFailed": "تعذّر النسخ — حدّد الرابط وانسخه يدويًا",
      "onb.smsFailedSendManually": "فشل إرسال الرسالة — انسخ الرابط وأرسله يدويًا",
      "onb.notOpened": "لم يُفتح",
      "onb.openedAt": "فُتح",
      "onb.submittedAt": "تم الإرسال",
      "onb.resend": "إعادة الإرسال",
      "onb.resendIn": "إعادة الإرسال خلال {{s}} ث",
      "onb.view": "عرض",
      "onb.convert": "تحويل إلى مستأجر",
      "onb.smsResent": "تمت إعادة إرسال الرسالة",
      "onb.smsResendFailed": "فشل إرسال الرسالة — انسخ الرابط وأرسله يدويًا",
      "onb.applicationDetails": "تفاصيل الطلب",
      "onb.invitedOn": "تاريخ الدعوة",
      "onb.sectionPersonal": "بيانات شخصية",
      "onb.sectionEntity": "بيانات المنشأة",
      "onb.sectionOperations": "بيانات التشغيل",
      "onb.sectionDocuments": "المستندات",
      "onb.fullName": "الاسم الكامل",
      "onb.email": "البريد الإلكتروني",
      "onb.legalName": "الاسم النظامي",
      "onb.commercialName": "الاسم التجاري",
      "onb.crNumber": "رقم السجل التجاري",
      "onb.vatNumber": "الرقم الضريبي",
      "onb.vatExempt": "معفى من الضريبة",
      "onb.nationalAddress": "العنوان الوطني",
      "onb.addressLine2": "تفاصيل العنوان الإضافية",
      "onb.city": "المدينة",
      "onb.region": "المنطقة",
      "onb.avgJobCards": "متوسط بطاقات العمل / الشهر",
      "onb.branches": "عدد الفروع",
      "onb.ownGateway": "لديه بوابة دفع خاصة",
      "onb.gatewayProvider": "مزوّد بوابة الدفع",
      "onb.referralSource": "كيف سمعت عنا",
      "onb.docCr": "السجل التجاري",
      "onb.docVat": "شهادة ضريبة القيمة المضافة",
      "onb.docNationalAddress": "إثبات العنوان الوطني",
      "onb.docIban": "شهادة الآيبان",
      "adm.ibanDocument": "شهادة الآيبان",
      "onb.docLogo": "الشعار",
      "onb.noDocuments": "لم يتم رفع أي مستندات بعد.",
      "onb.setStatus": "تعيين الحالة",
      "onb.selectStatus": "اختر الحالة…",
      "onb.saveStatus": "حفظ الحالة",
      "onb.statusUpdated": "تم تحديث الحالة",
      "onb.notes": "ملاحظات داخلية",
      "onb.convertConfirm": "إنشاء مستأجر قيد الموافقة من هذا الطلب؟",
      "onb.converted": "تم إنشاء المستأجر (قيد الموافقة)",
      "onb.readingDocuments": "جارٍ قراءة المستندات المرفوعة…",
      "onb.scanFailed": "تعذّرت قراءة المستندات الرسمية — النموذج يعرض ما أدخله العميل فقط. راجع كل حقل.",
      "onb.prefillFailed": "تعذّر تحميل بيانات هذا الطلب",
      "onb.alreadyConverted": "تم تحويل هذا الطلب إلى مستأجر من قبل",
      "onb.convertedFromLead": "تم إنشاء المستأجر من الطلب",
      "onb.fillMissing": "بيانات ناقصة — أكملها قبل إنشاء المستأجر:",
      "onb.verifyAgainstDocs": "التحقق من المستندات الرسمية",
      "onb.verifyAgainstDocsHint": "تُعتمد قيمة المستند افتراضيًا. وعند اختلافها عمّا أدخله العميل تُعرض القيمتان — أكِّد كل حقل.",
      "onb.fromDocument": "من المستند",
      "onb.fromApplication": "من الطلب",
      "onb.useDocument": "اعتماد المستند",
      "onb.useApplication": "اعتماد الطلب",
      "onb.usingDocument": "المعتمد: المستند",
      "onb.usingApplication": "المعتمد: الطلب",
      "onb.documentValueApplied": "مأخوذة من المستند — لم يعبّئ العميل هذا الحقل",
      "onb.docFromApplication": "أرسله العميل — سيُرفق عند الإنشاء.",
      "onb.clientVatExempt": "أفاد العميل بأن المنشأة غير خاضعة لضريبة القيمة المضافة — لم يُرسل شهادة.",
      "onb.ibanSeedsPayoutConfig": "يُحفظ كحساب التحويل لهذا المشترك. يُستخرج اسم البنك من رقم الآيبان.",
      "onb.viewDoc": "عرض",
      "onb.status_invited": "مدعو",
      "onb.status_opened": "فُتح",
      "onb.status_submitted": "تم الإرسال",
      "onb.status_under_review": "قيد المراجعة",
      "onb.status_approved": "مقبول",
      "onb.status_converted": "تم التحويل",
      "onb.status_rejected": "مرفوض",
      "onb.status_needs_more_info": "بحاجة لمعلومات إضافية",
      "onb.approve": "موافقة",
      "onb.reject": "رفض",
      "onb.requestMoreInfo": "طلب معلومات إضافية",
      "onb.selectFieldsToUpdate": "حدد الحقول التي يجب على العميل تحديثها",
      "onb.requestNote": "رسالة إلى العميل",
      "onb.requestNotePlaceholder": "اشرح ما يحتاج العميل إلى تصحيحه أو توفيره…",
      "onb.sendRequest": "إرسال الطلب",
      "onb.approveConfirm": "الموافقة على هذا الطلب؟ سيتم إشعار العميل برسالة نصية.",
      "onb.rejectConfirm": "رفض هذا الطلب؟",
      "onb.approved": "تمت الموافقة على الطلب",
      "onb.rejected": "تم رفض الطلب",
      "onb.moreInfoSent": "تم طلب معلومات إضافية — أُرسلت رسالة للعميل",
      "onb.requestedFields": "الحقول المطلوبة",
      "onb.reviewNote": "ملاحظة المراجعة",
      "onb.first_name": "الاسم الأول",
      "onb.last_name": "اسم العائلة",
      "onb.legal_name": "الاسم النظامي",
      "onb.commercial_name": "الاسم التجاري",
      "onb.cr_number": "رقم السجل التجاري",
      "onb.cr_document": "مستند السجل التجاري (PDF)",
      "onb.vat_number": "الرقم الضريبي",
      "onb.vat_certificate": "شهادة ضريبة القيمة المضافة",
      "onb.national_address": "العنوان الوطني",
      "onb.national_address_proof": "إثبات العنوان الوطني",
      "onb.iban": "رقم الآيبان",
      "onb.iban_certificate": "شهادة الآيبان",
      "onb.address_line2": "تفاصيل العنوان الإضافية",
      "onb.avg_job_cards_range": "متوسط بطاقات العمل / الشهر",
      "onb.branches_range": "عدد الفروع",
      "onb.branches_count": "عدد الفروع (تحت نفس السجل)",
      "onb.branchesCount": "عدد الفروع (تحت نفس السجل)",
      "onb.business_mobile": "جوال المنشأة",
      "onb.businessMobile": "جوال المنشأة",
      "onb.has_own_gateway": "بوابة دفع خاصة",
      "onb.gateway_provider_name": "اسم مزوّد بوابة الدفع",
      "onb.referral_source": "كيف سمعت عنا",
      "onb.logo": "الشعار",
      "tp.providerFee": "رسوم المزود",
      "tp.motormindCommission": "عمولة موتورمايند",
      "tp.status": "الحالة",
      "tp.reconciliationStatus": "حالة التسوية",
      "tp.outstandingBalance": "الرصيد المستحق",
      "tp.period": "الفترة",
      "tp.settledDate": "تاريخ التسوية",
      "tp.bankReference": "المرجع البنكي",
      "tp.statement": "كشف الحساب",
      // Task 5.3 i18n sweep: see EN block comment.
      "tp.view": "عرض",
      "tp.jobOrderRef": "أمر العمل / الطلب",
      "tp.allProviders": "جميع المزودين",
      "tp.allStatuses": "جميع الحالات",
      "tp.fromDate": "من",
      "tp.toDate": "إلى",
      "tp.clearFilters": "مسح عوامل التصفية",
      "tp.amount": "المبلغ",
      "tp.amountPlaceholder": "أدخل المبلغ",
      "tp.reason": "السبب",
      "tp.requestRefund": "طلب استرداد",
      "tp.refundForPayment": "استرداد للدفعة",
      "tp.refundType": "نوع الاسترداد",
      "tp.refundMode": "نوع الاسترداد",
      "tp.fullRefund": "استرداد كامل",
      "tp.partialRefund": "استرداد جزئي",
      "tp.fullOnly": "استرداد كامل فقط",
      "tp.fullOrPartialAllowed": "يُسمح بالاسترداد الكامل أو الجزئي",
      "tp.refundReasonPlaceholder": "سبب الاسترداد",
      "tp.submitRefundRequest": "إرسال طلب الاسترداد",
      "tp.refundRequested": "تم طلب الاسترداد",
      "tp.existingRefunds": "طلبات الاسترداد",
      "tp.loadingRefunds": "جارٍ تحميل الاستردادات…",
      "tp.noRefunds": "لا توجد استردادات",
      "tp.noRefundsDescription": "لم تطلب أي استرداد.",
      "tp.payoutInquiry": "استفسار عن الدفعة",
      "tp.inquiryForPayout": "استفسار بخصوص الدفعة",
      "tp.inquiryReason": "السبب",
      "tp.inquiryMessagePlaceholder": "صف المشكلة",
      "tp.tenantMessage": "الرسالة",
      "tp.submitInquiry": "إرسال الاستفسار",
      "tp.inquirySubmitted": "تم إرسال الاستفسار",
      "tp.inquiryHistory": "سجل الاستفسارات",
      "tp.adminResponse": "رد الإدارة",
      "tp.loadingInquiries": "جارٍ تحميل الاستفسارات…",
      "tp.noInquiries": "لا توجد استفسارات",
      "tp.noInquiriesDescription": "لم تقم بإنشاء أي استفسارات عن الدفعات.",
      "tp.noPayments": "لا توجد مدفوعات",
      "tp.noPaymentsDescription": "لم يتم تسجيل أي مدفوعات بعد.",
      "tp.noPaymentsWithFilters": "لا توجد مدفوعات مطابقة لعوامل التصفية.",
      "tp.noPayouts": "لا توجد دفعات",
      "tp.noPayoutsDescription": "لم يتم إصدار أي دفعات بعد.",
      "tp.failedToLoadPayments": "فشل تحميل المدفوعات",
      "tp.failedToLoadPayouts": "فشل تحميل الدفعات",
      "tp.failedToLoadRefunds": "فشل تحميل الاستردادات",
      "tp.failedToLoadInquiries": "فشل تحميل الاستفسارات",
      "tp.failedToRequestRefund": "فشل طلب الاسترداد",
      "tp.failedToSubmitInquiry": "فشل إرسال الاستفسار",
      "tp.provider.myfatoorah": "ماي فاتورة",
      "tp.provider.tabby": "تابي",
      "tp.provider.tamara": "تمارا",
      "tp.provider.other": "أخرى",
      "tp.paymentStatus.PENDING": "قيد الانتظار",
      "tp.paymentStatus.PAID": "مدفوع",
      "tp.paymentStatus.FAILED": "فشل",
      "tp.paymentStatus.CANCELLED": "ملغى",
      "tp.paymentStatus.EXPIRED": "منتهي الصلاحية",
      "tp.status.unknown": "غير معروف",
      "tp.reconciliation.accrued": "مستحق",
      "tp.reconciliation.settled": "مسوّى",
      "tp.reconciliation.unaccrued": "غير مستحق",
      "tp.payoutStatus.draft": "مسودة",
      "tp.payoutStatus.approved": "قيد التنفيذ",
      "tp.payoutStatus.paid": "مكتمل",
      "tp.payoutStatus.void": "ملغاة",
      "tp.payoutStage": "مرحلة الدفعة",
      "tp.payoutStage.awaiting_payout": "بانتظار التسوية",
      "tp.payoutStage.processing": "قيد المعالجة",
      "tp.payoutStage.approved": "معتمد",
      "tp.payoutStage.paid": "مدفوع",
      "tp.refundStatus.requested": "مطلوب",
      "tp.refundStatus.approved": "معتمد",
      "tp.refundStatus.rejected": "مرفوض",
      "tp.refundStatus.processing": "قيد المعالجة",
      "tp.refundStatus.completed": "مكتمل",
      "tp.refundStatus.failed": "فشل",
      "tp.inquiryReason.not_received": "لم يُستلم",
      "tp.inquiryReason.wrong_amount": "مبلغ خاطئ",
      "tp.inquiryReason.other": "أخرى",
      "tp.inquiryStatus.open": "مفتوح",
      "tp.inquiryStatus.under_review": "قيد المراجعة",
      "tp.inquiryStatus.resolved": "تم الحل",
      "tp.inquiryStatus.rejected": "مرفوض",
      // Task 5.2 (payment-receipt redesign): source column/filter + Tamara notice.
      "tp.source": "المصدر",
      "tp.allSources": "كل المصادر",
      "tp.source.invoice": "دفعة فاتورة",
      "tp.source.downpayment_advance": "عربون (دفعة مقدمة)",
      "tp.source.receipt_payment": "دفعة عبر سند قبض",
      "tp.source.refund": "استرداد",
      "tp.tamaraFullRefundOnly": "استردادات تمارا كاملة فقط — لا يمكن استرداد جزء من هذه الدفعة.",
      "tp.refundBlockedSettled": "لا يمكن طلب الاسترداد — تمت تسوية هذه الدفعة وتحويل مبلغها بالفعل. تواصل مع الدعم لترتيب الاسترداد.",
      // --- auto-merged i18n sweep keys ---
      'cat.filters': 'الفلاتر',
      'cat.brakes': 'الفرامل',
      'cat.engine': 'المحرك',
      'cat.electrical': 'الكهرباء',
      'cat.fluids': 'السوائل',
      'cat.suspension': 'نظام التعليق',
      'cat.body': 'الهيكل',
      "adm.editCompany": "تعديل الشركة",
      "adm.addNewCompany": "إضافة شركة جديدة",
      "adm.updateCompany": "تحديث الشركة",
      "adm.createCompany": "إنشاء شركة",
      "adm.basicInformation": "المعلومات الأساسية",
      "adm.commercialNameEnglish": "الاسم التجاري (بالإنجليزية)",
      "adm.commercialNameArabic": "الاسم التجاري (بالعربية)",
      "adm.commercialNameHint": "الاسم التجاري الذي يظهر للعملاء على الفواتير وعروض الأسعار والصفحات العامة.",
      "adm.legalNameEnglish": "الاسم القانوني (بالإنجليزية)",
      "adm.legalNameArabic": "الاسم القانوني (بالعربية)",
      "adm.legalNameHint": "الاسم المسجّل كما في السجل التجاري. يُطبع بخط صغير أسفل الاسم التجاري في المستندات الضريبية.",
      "adm.companyNameEnglish": "اسم الشركة (بالإنجليزية)",
      "adm.companyNameArabic": "اسم الشركة (بالعربية)",
      "adm.subdomain": "النطاق الفرعي",
      "adm.companyPortalUrl": "(رابط بوابة الشركة)",
      "adm.subdomainPermanentHint": "رابط البوابة دائم — لا يمكن تغييره بعد الإنشاء.",
      "adm.subdomainNewHint": "أحرف إنجليزية صغيرة وأرقام وشرطات فقط. سيكون هذا الرابط الفريد لبوابة شركتك.",
      "adm.businessVerification": "التحقق من النشاط التجاري",
      "adm.crNumber": "رقم السجل التجاري",
      "adm.vatNumber": "الرقم الضريبي",
      "adm.vatExempt": "معفى من ضريبة القيمة المضافة",
      "adm.vatRate": "نسبة ضريبة القيمة المضافة (%)",
      "adm.crDocument": "وثيقة السجل التجاري",
      "adm.vatDocument": "وثيقة ضريبة القيمة المضافة",
      "adm.upload": "رفع",
      "adm.headOfficeLocation": "موقع المكتب الرئيسي",
      "adm.nationalAddress": "العنوان الوطني (العنوان المختصر)",
      "adm.branchAddress": "عنوان الفرع (من خرائط Google)",
      "adm.buildingNumber": "رقم المبنى",
      "adm.buildingNumberHint": "4 أرقام",
      "adm.postalCode": "الرمز البريدي",
      "adm.postalCodeHint": "5 أرقام",
      "adm.typeAddressToSearch": "اكتب العنوان للبحث...",
      "adm.nationalAddressDocument": "وثيقة العنوان الوطني",
      "adm.uploadNationalAddressDocument": "رفع وثيقة العنوان الوطني",
      "adm.subscriptionPayment": "الاشتراك والدفع",
      "adm.selectPlan": "اختر الباقة",
      "adm.chooseAPlan": "اختر باقة...",
      "adm.planOption": "{{name}} - {{price}} ريال/سنوياً",
      "adm.subscriptionStatus": "حالة الاشتراك",
      "adm.subPendingApproval": "بانتظار الموافقة",
      "adm.subActive": "نشط",
      "adm.subSuspended": "موقوف",
      "adm.subExpired": "منتهي",
      "adm.subscriptionStartDate": "تاريخ بدء الاشتراك",
      "adm.subscriptionEndDate": "تاريخ انتهاء الاشتراك",
      "adm.creditCard": "بطاقة ائتمانية",
      "adm.editBranch": "تعديل الفرع",
      "adm.addNewBranch": "إضافة فرع جديد",
      "adm.updateBranch": "تحديث الفرع",
      "adm.createBranch": "إنشاء فرع",
      "adm.branchInformation": "معلومات الفرع",
      "adm.branchNameEnglish": "اسم الفرع (بالإنجليزية)",
      "adm.branchNameArabic": "اسم الفرع (بالعربية)",
      "adm.branchNamePlaceholder": "مثال: مركز الرياض للسيارات",
      "adm.branchName": "اسم الفرع",
      "adm.branchNameLockedHint": "يُضبط تلقائياً من الاسم التجاري للشركة — وهو ذاته في كل الفروع.",
      "adm.branchCommercialNameEnglish": "اسم الفرع التجاري (بالإنجليزية)",
      "adm.branchCommercialNameArabic": "اسم الفرع التجاري (بالعربية)",
      "adm.branchCommercialNameHint": "يُطبع بخطٍّ بارز على فواتير هذا الفرع وعروض أسعاره وسنداته. ويبدأ الفرع الجديد بالاسم التجاري للشركة.",
      "adm.companyLegalName": "الاسم القانوني للشركة",
      "adm.companyLegalNameLockedHint": "يُضبط من ملف الشركة، وهو واحد لجميع الفروع، ولا يمكن تعديله من هنا.",
      "adm.branchNameFromCompany": "يُضبط من الشركة",
      "adm.branchReference": "مرجع الفرع",
      "adm.branchReferenceHint": "تسمية مختصرة لتمييز هذا الفرع في قائمة اختيار الفروع (لا تظهر للعملاء).",
      "adm.branchReferencePlaceholder": "مثال: الرياض، الرئيسي، الشمال",
      "adm.shortCodeUrl": "الرمز المختصر (الرابط)",
      "adm.shortCodeAutoPlaceholder": "يُنشأ تلقائياً إذا تُرك فارغاً",
      "adm.shortCodeAutoHint": "اختياري. رمز قصير للفرع (مثل TXG1) يُستخدم لروابط خاصة بالفرع. اتركه فارغاً وسننشئه تلقائياً — وهو ليس موقعاً منفصلاً؛ رابط دخولك هو نطاق الشركة الفرعي.",
      "adm.branchLogo": "شعار الفرع",
      "adm.uploadLogoHint": "رفع الشعار (سيُعرض في بوابة الفرع)",
      "adm.chooseLogoImage": "اختر صورة الشعار",
      "adm.logoRecommendation": "موصى به: PNG أو JPG، بحد أقصى 2 ميجابايت، بصيغة مربعة (مثال: 512x512 بكسل)",
      "adm.preview": "معاينة",
      "adm.logoPreviewAlt": "معاينة الشعار",
      "adm.branchLocation": "موقع الفرع",
      "adm.branchIsActive": "الفرع نشط",
      "adm.editPaymentGateway": "تعديل بوابة الدفع",
      "adm.addPaymentGateway": "إضافة بوابة دفع",
      "adm.updateGateway": "تحديث البوابة",
      "adm.addGateway": "إضافة بوابة",
      "adm.gatewayUpdatedSuccess": "تم تحديث بوابة الدفع بنجاح",
      "adm.gatewayAddedSuccess": "تمت إضافة بوابة الدفع بنجاح",
      "adm.gatewaySaveFailed": "فشل حفظ بوابة الدفع",
      "adm.gatewayType": "نوع البوابة",
      "adm.gatewayTypeCard": "بطاقة ائتمان/خصم",
      "adm.gatewayTypeBnpl": "اشترِ الآن وادفع لاحقاً",
      "adm.gatewayTypeWallet": "محفظة رقمية",
      "adm.provider": "مزود الخدمة",
      "adm.selectProvider": "اختر مزود الخدمة",
      "adm.displayNameForCustomers": "الاسم المعروض (للعملاء)",
      "adm.displayNamePlaceholder": "مثال: بطاقة ائتمان/خصم، تابي - الدفع على 4 أقساط",
      "adm.enabled": "مفعّل",
      "adm.testSandboxMode": "وضع الاختبار/التجريب",
      "adm.apiCredentialsEncrypted": "بيانات اعتماد الـ API مشفّرة ومخزّنة بشكل آمن",
      "adm.enterField": "أدخل {{field}}",
      "adm.noConfigFields": "لا توجد حقول إعداد متاحة. أضف مزود خدمة أولاً.",
      "adm.displayOrder": "ترتيب العرض",
      "adm.lowerNumbersFirst": "تظهر الأرقام الأصغر أولاً",
      "adm.editPlanTitle": "تعديل الباقة: {{name}}",
      "adm.planName": "اسم الباقة",
      "adm.priceSarYear": "السعر (ريال/سنة)",
      "adm.maxGarages": "الحد الأقصى لمراكز خدمات السيارات",
      "adm.maxUsers": "الحد الأقصى للمستخدمين",
      "adm.featuresClickToToggle": "الميزات (انقر للتبديل) - {{count}} ميزة",
      "adm.noFeaturesFound": "لم يتم العثور على ميزات. يرجى التحقق من بيانات الباقة.",
      "adm.bulkUploadParts": "رفع قطع الغيار بالجملة",
      "adm.uploadParts": "رفع القطع",
      "adm.selectCsvFile": "يرجى اختيار ملف CSV",
      "adm.previewFailed": "فشل معاينة الملف",
      "adm.uploadFailed": "فشل رفع الملف",
      "adm.downloadTemplateFailed": "فشل تنزيل القالب",
      "adm.downloadCsvTemplate": "تنزيل قالب CSV",
      "adm.downloadTemplateDesc": "نزّل ملف القالب لمعرفة الصيغة المطلوبة وبيانات نموذجية.",
      "adm.downloadTemplate": "تنزيل القالب",
      "adm.dragDropCsv": "اسحب وأفلت ملف CSV هنا",
      "adm.or": "أو",
      "adm.browseFiles": "تصفّح الملفات",
      "adm.countValid": "{{count}} صالح",
      "adm.countInvalid": "{{count}} غير صالح",
      "adm.validationErrors": "أخطاء التحقق:",
      "adm.andMoreErrors": "... و{{count}} أخطاء أخرى",
      "adm.row": "الصف",
      "adm.valid": "صالح",
      "adm.invalid": "غير صالح",
      "adm.noGarageSelected": "لم يتم اختيار مركز",
      "adm.failedToLoadSettings": "فشل تحميل الإعدادات",
      "adm.garageInfoUpdated": "تم تحديث معلومات المركز بنجاح",
      "adm.failedToUpdateGarageInfo": "فشل تحديث معلومات المركز",
      "adm.vatSettingsUpdated": "تم تحديث إعدادات ضريبة القيمة المضافة بنجاح",
      "adm.failedToUpdateVat": "فشل تحديث إعدادات ضريبة القيمة المضافة",
      "adm.enterPhoneNumber": "يرجى إدخال رقم هاتف",
      "adm.numberAlreadyAdded": "هذا الرقم مضاف بالفعل",
      "adm.notificationSettingsUpdated": "تم تحديث إعدادات الإشعارات بنجاح",
      "adm.failedToUpdateNotifications": "فشل تحديث إعدادات الإشعارات",
      "adm.intakeDisputes": "اعتراضات تأكيد الاستلام",
      "adm.intakeConfirmations": "تأكيدات الاستلام",
      "usr.assignedBranches": "الفروع المعيّنة",
      "usr.primaryBranch": "رئيسي",
      "appr.pendingApproval": "بانتظار الاعتماد",
      "appr.discountRequested": "الخصم المطلوب: {{amount}}",
      "appr.rejectReasonPlaceholder": "ما سبب الرفض؟",
      "appr.decideFailed": "تعذّر حفظ القرار. يُرجى المحاولة مرة أخرى.",
      "appr.discountPendingSaved": "تم الحفظ. الخصم يتجاوز حدّك فأُرسل للاعتماد. لا يُرسَل شيء إلى العميل، ولا يمكن ترحيل المستند أو تحصيله، حتى يُعتمد الطلب أو يُرفض.",
      "appr.aboveYourLimit": "يتجاوز حدّ الخصم الخاص بك — سيُرسل للاعتماد.",
      "appr.approvalFilter": "الاعتماد",
      "appr.discountExpiredOnPost": "صدرت الفاتورة بالسعر الكامل: الخصم يتجاوز حدّك ولا يمكن أن ينتظر الاعتماد في فاتورة مدفوعة من سند قبض. أصدر إشعارًا دائنًا إذا اعتُمد.",
      "usr.discountApprovalLimit": "حدّ اعتماد الخصم (٪)",
      "usr.purchaseApprovalLimit": "حدّ اعتماد المشتريات (ريال)",
      "usr.approvalLimitHint": "عند إضافة مستخدم أو تغيير دوره، تأخذ الحدود الفارغة حدودك (ولا تكون بلا سقف إلا إذا كانت حدودك كذلك). وفي التعديلات الأخرى يعني الحقل الفارغ عدم وجود حد، ولا يمنح ذلك إلا من ليس له حد. القيمة 0 تعني أن كل خصم أو شراء يحتاج إلى اعتماد.",
      "usr.ownLimitsLocked": "لا يمكنك تعديل حدود الاعتماد الخاصة بحسابك؛ اطلب ذلك من مسؤول آخر.",
      "usr.limitUnlimitedPlaceholder": "بلا حد",
      "usr.primaryBranchHint": "اختر كل فرع يمكن لهذا المستخدم العمل فيه. الفرع الرئيسي هو مقر عمله ونطاق تسجيل دخوله.",
      "usr.branchInactive": "غير نشط",
      "usr.notificationLanguage": "لغة الإشعارات",
      "usr.notificationLanguageHint": "اللغة التي يستلم بها هذا المستخدم إشعاراته.",
      "usr.languageArabic": "العربية",
      "usr.languageEnglish": "الإنجليزية",
      "adm.newAppointment": "الحجوزات الجديدة",
      "adm.payoutInquiryResponse": "ردود استفسارات التحويلات",
      "adm.creditOverdue": "تذكيرات الآجل المتأخر",
      "adm.selectAll": "تحديد الكل",
      "adm.clearAll": "إلغاء تحديد الكل",
      "adm.noGarageData": "لا توجد بيانات مركز متاحة",
      "adm.zeroExempt": "0 (معفى)",
      "adm.noNotificationNumbers": "لم تتم إضافة أرقام إشعارات بعد. أضف رقماً أعلاه لإعداد الإشعارات.",
      "cd.activateCompany": "تفعيل الشركة",
      "cd.activateCompanyDesc": "يعيد الوصول لجميع المستخدمين، ويصبح البوابة متاحة مرة أخرى.",
      "cd.activateUser": "تفعيل المستخدم",
      "cd.activeBranches": "الفروع النشطة",
      "cd.addBranch": "إضافة فرع",
      "cd.addCompany": "إضافة شركة",
      "cd.addConfiguration": "إضافة إعداد",
      "cd.addFirstBranch": "إضافة أول فرع",
      "cd.addFirstGateway": "إضافة أول بوابة دفع",
      "cd.addFirstItem": "إضافة أول عنصر",
      "cd.addFirstUser": "إضافة أول مستخدم",
      "cd.addGateway": "إضافة بوابة دفع",
      "cd.addNewModelPlaceholder": "إضافة موديل جديد...",
      "cd.addUser": "إضافة مستخدم",
      "cd.authorizedBranches": "الفروع المصرّح بها",
      "cd.bank": "بنك",
      "cd.bnpl": "اشترِ الآن وادفع لاحقاً",
      "cd.branchCreated": "تم إنشاء الفرع بنجاح",
      "cd.branchUpdated": "تم تحديث الفرع بنجاح",
      "cd.branches": "الفروع",
      "cd.branchesActive": "{{active}} نشط",
      "cd.branchesActiveOfAllowed": "{{active}} نشط من أصل {{allowed}} مسموح",
      "cd.businessDetails": "بيانات النشاط التجاري",
      "cd.cannotBeChanged": "لا يمكن تغييره",
      "cd.cannotDeleteSystemItems": "لا يمكن حذف العناصر المعرّفة من النظام",
      "cd.catFuelLevels": "مستويات الوقود",
      "cd.catFuelTypes": "أنواع الوقود",
      "cd.catInsuranceCompanies": "شركات التأمين",
      "cd.catJobPriorities": "أولويات أوامر العمل",
      "cd.catJobStatuses": "حالات أوامر العمل",
      "cd.catPartStatuses": "حالات قطع الغيار",
      "cd.catPaymentMethods": "طرق الدفع",
      "cd.catPaymentStatuses": "حالات الدفع",
      "cd.catQuotationStatuses": "حالات عروض الأسعار",
      "cd.catTaskStatuses": "حالات المهام",
      "cd.catTransmissionTypes": "أنواع ناقل الحركة",
      "cd.catVehicleColors": "ألوان المركبات",
      "cd.catVehicleMakes": "ماركات وموديلات المركبات",
      "cd.catVehicleShapes": "أشكال المركبات",
      "cd.catWarningLights": "لمبات التحذير",
      "cd.code": "الرمز",
      "cd.companiesSubtitle": "إدارة الشركات وفروعها (مراكز خدمات السيارات)",
      "cd.companyActivated": "تم تفعيل الشركة بنجاح",
      "cd.companyBranches": "فروع الشركة",
      "cd.companyCreated": "تم إنشاء الشركة بنجاح",
      "cd.notifyOwner": "إبلاغ المالك",
      "cd.notifyOwnerAgain": "إبلاغ المالك مرة أخرى",
      "cd.notifyOwnerTitle": "إرسال الرابط إلى المالك؟",
      "cd.notifyOwnerTo": "سيُرسل برسالة نصية إلى الجوال المسجّل في طلبه، المنتهي بـ {{mobile}}.",
      "cd.notifyOwnerSend": "أرسل الآن",
      "cd.notifyOwnerLater": "ليس الآن",
      "cd.notifyOwnerEditable": "يمكنك تعديل نص الرسالة قبل إرسالها.",
      "cd.notifyOwnerNoUsersYet": "لا يوجد مستخدمون لهذا المشترك بعد، لذلك تُفيد الرسالة بأن الحساب قيد التجهيز بدل دعوته لتسجيل الدخول. أعد الإرسال بعد إنشاء أول مستخدم له.",
      "cd.ownerNoticeSent": "أُرسلت إلى الرقم المنتهي بـ {{mobile}}",
      "cd.ownerNoticeSentOn": "آخر إرسال {{date}}",
      "cd.companyDeactivated": "تم إلغاء تفعيل الشركة بنجاح",
      "cd.companyDetailsHeading": "تفاصيل الشركة",
      "cd.companyDetailsUpdated": "تم تحديث تفاصيل الشركة بنجاح",
      "cd.applyContactToBranches": "استخدم هذا الهاتف والبريد في جميع الفروع",
      "cd.applyContactToBranchesHint": "الفواتير وعروض الأسعار تطبع بيانات الفرع لا بيانات الشركة. اترك هذا الخيار مفعّلاً ليطابق المستند ما أدخلته أعلاه. أزل العلامة لترك فرع له رقمه الخاص دون تغيير.",
      "cd.companyId": "معرّف الشركة",
      "cd.companyInformation": "معلومات الشركة",
      "cd.companyNameArabic": "اسم الشركة (بالعربية)",
      "cd.companyNameEnglish": "اسم الشركة (بالإنجليزية)",
      "cd.companyNotFound": "لم يتم العثور على الشركة",
      "cd.companySettings": "إعدادات الشركة",
      "cd.companyStatus": "حالة الشركة",
      "cd.companyStatusActiveDesc": "الشركة نشطة — يمكن لجميع المستخدمين تسجيل الدخول والوصول إلى البوابة.",
      "cd.companyStatusInactiveDesc": "الشركة غير نشطة — جميع المستخدمين محظورون من الوصول إلى البوابة.",
      "cd.companyUpdated": "تم تحديث الشركة بنجاح",
      "cd.companyUsers": "مستخدمو الشركة",
      "cd.companyUsersSubtitle": "إدارة المستخدمين وصلاحيات وصولهم إلى الفروع",
      "cd.configCreated": "تم إنشاء الإعداد بنجاح",
      "cd.configDeleted": "تم حذف الإعداد بنجاح",
      "cd.configUpdated": "تم تحديث الإعداد بنجاح",
      "cd.confirmDeleteGateway": "هل أنت متأكد من رغبتك في حذف بوابة الدفع هذه؟",
      "cd.confirmDeleteItem": "هل أنت متأكد من رغبتك في حذف \"{{value}}\"؟",
      "cd.confirmRemoveModel": "إزالة الموديل \"{{model}}\"؟",
      "cd.copied": "تم نسخ {{label}}",
      "cd.copyCompanyId": "نسخ معرّف الشركة",
      "cd.copyPortalUrl": "نسخ رابط البوابة",
      "cd.copyTenantId": "نسخ معرّف المستأجر",
      "cd.crNumber": "رقم السجل التجاري",
      "cd.create": "إنشاء",
      "cd.creditCard": "بطاقة ائتمانية",
      "cd.currentStatus": "الحالة الحالية",
      "cd.dangerZone": "منطقة الإجراءات الحساسة",
      "cd.dangerZoneDesc": "تسري هذه الإجراءات فوراً وقد تؤدي إلى تعطيل الخدمة.",
      "cd.daysCount": "{{count}} يوم",
      "cd.daysRemaining": "الأيام المتبقية",
      "cd.deactivateCompany": "إلغاء تفعيل الشركة",
      "cd.deactivateCompanyDesc": "يحظر فوراً جميع مستخدمي المستأجر من تسجيل الدخول، ويصبح النطاق الفرعي غير متاح.",
      "cd.deactivateUser": "إلغاء تفعيل المستخدم",
      "cd.disabled": "معطّلة",
      "cd.displayValueArabic": "القيمة المعروضة (بالعربية)",
      "cd.displayValueArabicPlaceholder": "مثال: تويوتا، أحمر، أوتوماتيك",
      "cd.displayValueEnglish": "القيمة المعروضة (بالإنجليزية)",
      "cd.displayValueEnglishPlaceholder": "مثال: Toyota، Red، Automatic",
      "cd.editBranch": "تعديل الفرع",
      "cd.editCompany": "تعديل الشركة",
      "cd.editConfiguration": "تعديل الإعداد",
      "cd.editPlan": "تعديل الباقة",
      "cd.editSubscription": "تعديل الاشتراك",
      "cd.editSubscriptionHint": "لتعديل تفاصيل الاشتراك، انتقل إلى تبويب \"الاشتراك والباقة\"",
      "cd.enabled": "مفعّلة",
      "cd.endDate": "تاريخ الانتهاء",
      "cd.enterModelName": "يرجى إدخال اسم الموديل",
      "cd.exempt": "معفاة",
      "cd.expired": "منتهي",
      "cd.failedToAddModel": "تعذّر إضافة الموديل",
      "cd.failedToCreateBranch": "تعذّر إنشاء الفرع",
      "cd.failedToCreateCompany": "تعذّر إنشاء الشركة",
      "cd.failedToDeleteConfig": "تعذّر حذف الإعداد",
      "cd.failedToDeleteGateway": "تعذّر حذف بوابة الدفع",
      "cd.failedToLoadCompanies": "تعذّر تحميل الشركات",
      "cd.failedToLoadCompanyDetails": "تعذّر تحميل تفاصيل الشركة",
      "cd.failedToRemoveModel": "تعذّر إزالة الموديل",
      "cd.failedToSaveConfig": "تعذّر حفظ الإعداد",
      "cd.failedToUpdateBranch": "تعذّر تحديث الفرع",
      "cd.failedToUpdateCompany": "تعذّر تحديث الشركة",
      "cd.failedToUpdateCompanyDetails": "تعذّر تحديث تفاصيل الشركة",
      "cd.failedToUpdateCompanyStatus": "تعذّر تحديث حالة الشركة",
      "cd.failedToUpdatePlan": "تعذّر تحديث الباقة",
      "cd.failedToUpdateSubscription": "تعذّر تحديث الاشتراك",
      "cd.failedToUpdateUserStatus": "تعذّر تحديث حالة المستخدم",
      "cd.gatewayDeleted": "تم حذف بوابة الدفع",
      "cd.gatewayInfo1": "أضف طرق دفع متعددة (البطاقات، خدمات الدفع الآجل مثل تابي/تمارا، المحافظ)",
      "cd.gatewayInfo2": "يمكن للعملاء اختيار طريقة الدفع المفضّلة لديهم عند إتمام الدفع",
      "cd.gatewayInfo3": "يمكن تفعيل أو تعطيل كل بوابة بشكل مستقل",
      "cd.gatewayInfo4": "يتحكم ترتيب العرض في الخيار الذي يظهر أولاً",
      "cd.keyLabel": "المفتاح",
      "cd.keyPlaceholder": "مثال: toyota، red، automatic",
      "cd.keyUniqueIdentifier": "المفتاح (معرّف فريد)",
      "cd.lastUpdated": "آخر تحديث {{date}}",
      "cd.location": "الموقع",
      "cd.manageCompanyDetails": "إدارة تفاصيل الشركة",
      "cd.manageSubscription": "إدارة الاشتراك",
      "cd.maxBranchesUsers": "أقصى عدد للفروع: {{branches}} • أقصى عدد للمستخدمين: {{users}}",
      "cd.modelAdded": "تم إضافة الموديل بنجاح",
      "cd.modelRemoved": "تم إزالة الموديل بنجاح",
      "cd.modelsCount": "{{count}} موديل",
      "cd.multipleGatewaySupport": "دعم بوابات دفع متعددة",
      "cd.noBranchesAssigned": "لا توجد فروع مسندة",
      "cd.noBranchesForCompany": "لم تتم إضافة أي فروع لهذه الشركة بعد.",
      "cd.noBranchesYet": "لم تتم إضافة أي فروع بعد",
      "cd.noCity": "بدون مدينة",
      "cd.noCompaniesFound": "لا توجد شركات.",
      "cd.noGatewaysConfigured": "لم يتم تكوين أي بوابات دفع",
      "cd.noItemsInCategory": "لا توجد عناصر في هذه الفئة",
      "cd.noPlan": "بدون باقة",
      "cd.noUsersYet": "لم تتم إضافة أي مستخدمين بعد",
      "cd.paymentGatewayConfig": "تكوين بوابة الدفع",
      "cd.paymentGatewayConfigSubtitle": "قم بتكوين بوابات دفع متعددة (البطاقات، الدفع الآجل، المحافظ) لجميع الفروع",
      "cd.permanentPortalUrl": "رابط دائم للبوابة — لا يمكن تغييره بعد الإنشاء",
      "cd.plan": "الباقة",
      "cd.planUpdated": "تم تحديث الباقة بنجاح",
      "cd.portalUrl": "رابط البوابة",
      "cd.sarPerYear": "{{price}} ريال/سنوياً",
      "cd.saving": "جاري الحفظ…",
      "cd.searchCompaniesPlaceholder": "ابحث عن الشركات بالاسم أو رقم السجل التجاري...",
      "cd.selectPlan": "اختر الباقة",
      "cd.shortCode": "الرمز المختصر",
      "cd.shortCodeHint": "رمز من 3 إلى 4 أحرف يُستخدم في روابط البوابة القديمة (مثال: TXG)",
      "cd.shortCodePlaceholder": "مثال: TXG",
      "cd.sortOrder": "ترتيب العرض",
      "cd.statusExpired": "منتهي",
      "cd.statusPendingApproval": "بانتظار الموافقة",
      "cd.statusSuspended": "موقوف",
      "cd.subdomain": "النطاق الفرعي",
      "cd.subscription": "الاشتراك",
      "cd.subscriptionDetails": "تفاصيل الاشتراك",
      "cd.subscriptionEndDate": "تاريخ انتهاء الاشتراك",
      "cd.subscriptionPlan": "باقة الاشتراك",
      "cd.subscriptionPlanManagement": "إدارة الاشتراك والباقة",
      "cd.subscriptionPlanManagementSubtitle": "إدارة باقة الاشتراك وحالته وتفاصيل الفوترة",
      "cd.subscriptionPlans": "باقات الاشتراك",
      "cd.subscriptionStartDate": "تاريخ بدء الاشتراك",
      "cd.subscriptionStatus": "حالة الاشتراك",
      "cd.subscriptionSummary": "ملخص الاشتراك",
      "cd.subscriptionUpdated": "تم تحديث الاشتراك بنجاح",
      "sub.amount": "المبلغ",
      "sub.aiQuota": "طلبات الوكيل الذكي شهرياً",
      "sub.aiVoiceQuota": "طلبات الكتابة بالصوت شهرياً",
      "sub.apply": "تطبيق التغيير",
      "sub.applyAnyway": "تطبيق على أي حال",
      "sub.branches": "الفروع",
      "sub.change": "تغيير الباقة",
      "sub.changed": "تم تغيير الاشتراك",
      "sub.changeHint": "اختر الباقة وتاريخ البداية، ويُحسب تاريخ النهاية من مدة الباقة.",
      "sub.changeTitle": "تغيير الاشتراك",
      "sub.colAmount": "المبلغ",
      "sub.colBy": "بواسطة",
      "sub.colPlan": "الباقة",
      "sub.colReason": "السبب",
      "sub.colTerm": "المدة",
      "sub.colWhat": "التغيير",
      "sub.colWhen": "التاريخ",
      "sub.confirmRenew": "تجديد",
      "sub.custom": "اشتراك مخصّص",
      "sub.daysLeft": "متبقٍ {{count}} يوم",
      "sub.daysLeft_one": "متبقٍ يوم واحد",
      "sub.daysLeft_two": "متبقٍ يومان",
      "sub.daysLeft_few": "متبقٍ {{count}} أيام",
      "sub.daysLeft_many": "متبقٍ {{count}} يوماً",
      "sub.daysLeft_other": "متبقٍ {{count}} يوم",
      "sub.endComputed": "محسوب من مدة الباقة",
      "sub.endDate": "تاريخ النهاية",
      "sub.expiredOn": "انتهى في {{date}}",
      "sub.history": "سجل الاشتراكات",
      "sub.keepCustom": "— بدون باقة (إبقاء الإعدادات الحالية) —",
      "sub.noHistory": "لا يوجد سجل بعد",
      "sub.noTerm": "لا يوجد تاريخ نهاية",
      "sub.openEnded": "غير محدّد",
      "sub.overageBody": "هذا الاشتراك يسمح بـ {{limit}} {{resource}}، والشركة تستخدم {{inUse}} بالفعل. التطبيق لن يحذف أي شيء، لكن لن تتم إضافة جديد حتى يُرفع الحد.",
      "sub.overageTitle": "أقل من المستخدم فعلياً",
      "sub.plan": "الباقة",
      "sub.reason": "السبب",
      "sub.reasonHint": "يُسجَّل في سجل الاشتراكات باسمك.",
      "sub.reasonPlaceholder": "مثال: ترقية لإضافة فرع ثانٍ",
      "sub.reasonRequired": "السبب مطلوب عند تغيير الاشتراك",
      "sub.err.REASON_REQUIRED": "السبب مطلوب عند تغيير الاشتراك.",
      "sub.err.NO_TERM": "لا توجد مدة لتجديدها في هذا الاشتراك. اختر باقة، أو حدِّد تاريخ النهاية من النموذج أدناه.",
      "sub.err.LIMIT_BELOW_USAGE": "هذه الباقة أصغر مما تستخدمه الشركة بالفعل.",
      "sub.renew": "تجديد",
      "sub.renewed": "تم تجديد الاشتراك",
      "sub.renewHint": "تبدأ المدة الجديدة في اليوم التالي لانتهاء المدة الحالية.",
      "sub.renewTitle": "تجديد الاشتراك",
      "sub.startDate": "تاريخ البداية",
      "sub.status.active": "مفعّل",
      "sub.status.expired": "منتهي",
      "sub.status.pending_approval": "بانتظار الموافقة",
      "sub.status.suspended": "موقوف",
      "sub.term.day": "{{count}} يوم",
      "sub.term.day_one": "يوم واحد",
      "sub.term.day_two": "يومان",
      "sub.term.day_few": "{{count}} أيام",
      "sub.term.day_many": "{{count}} يوماً",
      "sub.term.day_other": "{{count}} يوم",
      "sub.term.month": "{{count}} شهر",
      "sub.term.month_one": "شهر واحد",
      "sub.term.month_two": "شهران",
      "sub.term.month_few": "{{count}} أشهر",
      "sub.term.month_many": "{{count}} شهراً",
      "sub.term.month_other": "{{count}} شهر",
      "sub.term.week": "{{count}} أسبوع",
      "sub.term.week_one": "أسبوع واحد",
      "sub.term.week_two": "أسبوعان",
      "sub.term.week_few": "{{count}} أسابيع",
      "sub.term.week_many": "{{count}} أسبوعاً",
      "sub.term.week_other": "{{count}} أسبوع",
      "sub.term.year": "{{count}} سنة",
      "sub.term.year_one": "سنة واحدة",
      "sub.term.year_two": "سنتان",
      "sub.term.year_few": "{{count}} سنوات",
      "sub.term.year_many": "{{count}} سنة",
      "sub.term.year_other": "{{count}} سنة",
      "sub.type.cancellation": "إلغاء",
      "sub.type.change": "تعديل",
      "sub.type.downgrade": "تخفيض",
      "sub.type.initial": "بداية الاشتراك",
      "sub.type.renewal": "تجديد",
      "sub.type.upgrade": "ترقية",
      "sub.unlimited": "غير محدود",
      "sub.users": "المستخدمون",
      "cd.system": "نظام",
      "cd.systemConfigurations": "إعدادات النظام",
      "cd.systemConfigurationsSubtitle": "إدارة قيم القوائم المنسدلة وإعدادات النظام لجميع المستأجرين",
      "cd.tabOverview": "نظرة عامة",
      "cd.tabPaymentGateway": "بوابة الدفع",
      "cd.tabSubscriptionPlan": "الاشتراك والباقة",
      "cd.taxable": "خاضعة للضريبة",
      "cd.tenantId": "معرّف المستأجر",
      "cd.toggleBranches": "إظهار/إخفاء الفروع",
      "cd.tryAgain": "حاول مرة أخرى",
      "cd.unnamedGarage": "مركز بدون اسم",
      "cd.unsavedChangesConfirm": "لديك تغييرات غير محفوظة. هل تريد تجاهلها والانتقال إلى تبويب آخر؟",
      "cd.user": "المستخدم",
      "cd.userActivated": "تم تفعيل المستخدم",
      "cd.userDeactivated": "تم إلغاء تفعيل المستخدم",
      "cd.hidden": "مخفي",
      "cd.hideFromTenant": "إخفاء من قائمة مستخدمي المستأجر",
      "cd.unhideFromTenant": "إظهار في قائمة مستخدمي المستأجر",
      "cd.userHidden": "تم إخفاء المستخدم عن المستأجر",
      "cd.userUnhidden": "أصبح المستخدم ظاهراً للمستأجر",
      "cd.vatEffectiveDate": "تاريخ سريان ضريبة القيمة المضافة",
      "cd.vatEffectiveDateHint": "التاريخ الذي تصبح فيه حالة الإعفاء من ضريبة القيمة المضافة سارية",
      "cd.vatExempt": "معفاة من ضريبة القيمة المضافة",
      "cd.vatRate": "نسبة ضريبة القيمة المضافة (%)",
      "cd.vatRatePlaceholder": "مثال: 15",
      "cd.vatStatus": "حالة ضريبة القيمة المضافة",
      "cd.wallet": "محفظة",
      "cd.warnExpired": "انتهى الاشتراك.",
      "cd.warnExpiresInDays": "ينتهي الاشتراك خلال {{count}} يوم.",
      "cd.warnExpiresSoonRenew": "ينتهي الاشتراك خلال {{count}} يوم — جدّد الآن.",
      "cd.warnSuspended": "الاشتراك موقوف. قد يفقد المستخدمون إمكانية الوصول.",
      "cd.working": "جاري التنفيذ…",
      "cd.zeroExempt": "0 (معفاة)",
      "cm.vehicleSpecs.loading": "جارٍ تحميل المواصفات...",
      "cm.vehicleSpecs.unavailable": "مواصفات المركبة غير متوفرة",
      "cm.vehicleSpecs.rateLimit": "وصلت الباقة المجانية إلى الحد الأقصى للطلبات.",
      "cm.vehicleSpecs.upgrade": "قم بالترقية إلى باقة مدفوعة لتفعيل هذه الميزة.",
      "cm.vehicleSpecs.engine": "المحرك",
      "cm.vehicleSpecs.fillHint": "أضِف سعة المحرك ونوع الوقود وناقل الحركة في ملف السيارة — يستخدمها المساعد الذكي لتشخيص أكثر دقة.",
      "cm.vehicleSpecs.fuelType": "نوع الوقود",
      "cm.vehicleSpecs.transmission": "ناقل الحركة",
      "cm.vehicleSpecs.driveType": "نظام الدفع",
      "cm.vehicleSpecs.cityFuelEconomy": "استهلاك الوقود داخل المدينة",
      "cm.vehicleSpecs.highwayFuelEconomy": "استهلاك الوقود على الطريق السريع",
      "cm.vehicleSpecs.combinedMpg": "الاستهلاك المشترك",
      "cm.vehicleSpecs.vehicleClass": "فئة المركبة",
      "cm.vehicleSpecs.automatic": "أوتوماتيكي",
      "cm.vehicleSpecs.manual": "يدوي",
      "cm.vehicleSpecs.na": "غير متوفر",
      "cm.vehicleSpecs.cylinderSuffix": " أسطوانة",
      "cm.vehicleSpecs.rwd": "دفع خلفي",
      "cm.vehicleSpecs.fwd": "دفع أمامي",
      "cm.vehicleSpecs.awd": "دفع رباعي دائم",
      "cm.vehicleSpecs.fourWd": "دفع رباعي",
      "cm.vehicleSpecs.gasoline": "بنزين",
      "cm.vehicleSpecs.diesel": "ديزل",
      "cm.vehicleSpecs.electric": "كهربائي",
      "cm.vehicleSpecs.hybrid": "هجين",
      "cm.vehicle.istamaraScanExplainer": "يقرأ اللوحة والمالك والهيكل والطراز تلقائيًا ويحفظ صورة الاستمارة",
      "cm.vehicle.saudiPlateGroup": "لوحة سعودية",
      "cm.vehicle.istamaraPhotoTag": "استمارة",
      "cm.vehicle.insuranceOptionalHint": "اختياري — تغذّي تنبيهات الانتهاء",
      "cm.vehicle.photosUploaded": "تم رفع {{count}} صورة",
      "cm.vehicle.photosAdded": "تمت إضافة {{count}} صورة",
      "cm.vehicle.photosUploadFailed": "فشل رفع الصور",
      "cm.vehicle.photoDeleted": "تم حذف الصورة",
      "cm.vehicle.photoDeleteFailed": "فشل حذف الصورة",
      "cm.vehicle.vinScanned": "تم مسح رقم الهيكل بنجاح!",
      "cm.vehicle.vinInvalid": "الرجاء إدخال رقم هيكل صحيح مكوّن من 17 خانة",
      "cm.vehicle.vinDecoded": "تم فك رقم الهيكل! تمت التعبئة التلقائية: {{fields}}",
      "cm.vehicle.vinFillRemaining": "الرجاء تعبئة الحقول المتبقية يدويًا",
      "cm.vehicle.vinDecodedNoData": "تم فك رقم الهيكل ولكن لم يتم العثور على بيانات المركبة. الرجاء إدخال التفاصيل يدويًا.",
      "cm.vehicle.vinNoData": "لم يتم العثور على بيانات مركبة لرقم الهيكل هذا",
      "cm.vehicle.vinDecodeFailed": "فشل فك رقم الهيكل",
      "cm.vehicle.istamaraAutoFilled": "تم حفظ صورة الاستمارة! تمت التعبئة التلقائية: {{fields}}",
      "cm.vehicle.istamaraSavedNoData": "تم حفظ صورة الاستمارة، ولكن تعذّر استخراج البيانات تلقائيًا. الرجاء إدخال تفاصيل المركبة يدويًا.",
      "cm.vehicle.istamaraExtractFailed": "تعذّر استخراج البيانات من الاستمارة. الرجاء المحاولة مرة أخرى مع إضاءة أفضل.",
      "cm.vehicle.field.ownerName": "اسم المالك",
      "cm.vehicle.field.customerMatch": "مطابقة العميل",
      "cm.vehicle.field.vin": "رقم الهيكل",
      "cm.vehicle.field.plateNumber": "رقم اللوحة",
      "cm.vehicle.field.plateLetters": "حروف اللوحة",
      "cm.vehicle.field.brand": "الماركة",
      "cm.vehicle.field.model": "الموديل",
      "cm.vehicle.field.year": "السنة",
      "cm.vehicle.field.color": "اللون",
      "cm.vehicle.field.sequenceNumber": "الرقم التسلسلي",
      "cm.vehicle.field.fuelType": "نوع الوقود",
      "cm.vehicle.field.transmission": "ناقل الحركة",
      "cm.vehicle.photoAlt": "المركبة {{index}}",
      "cm.vehicle.photoAltSingle": "المركبة",
      "cm.vehicle.searchBrands": "البحث عن الماركات...",
      "cm.vehicle.searchModels": "البحث عن الموديلات...",
      "cm.vehicle.searchColors": "البحث عن الألوان...",
      "cm.vehicle.selectGeneration": "اختر الجيل",
      "cm.vehicle.noGenerations": "لا توجد أجيال متاحة",
      "cm.vehicle.enterModelFreeText": "اكتب الموديل",
      "cm.vehicle.present": "حتى الآن",
      "cm.vehicle.selectYear": "اختر السنة",
      "cm.vehicle.plateTemporary": "مؤقتة (رمادية)",
      "cm.vehicle.plateDiplomatic": "دبلوماسية (خضراء)",
      "cm.vehicle.selectType": "اختر النوع",
      "cm.vehicle.specsAiHint": "سعة المحرك ونوع الوقود وناقل الحركة تساعد المساعد الذكي على تشخيص أكثر دقة.",
      "cm.vehicle.engineSizePlaceholder": "مثال: 3.0L I6، 2.0L تيربو",
      "cm.vehicle.vinScanOrType": "امسح باركود رقم الهيكل من باب السيارة، أو أدخله يدوياً.",
      "cm.vehicle.chassisNumber": "رقم الشاسيه",
      "cm.vehicle.chassisNumberPlaceholder": "رقم الشاسيه",
      "cm.customer.phonePrefillChip": "الجوال معبأ من الإنشاء السريع",
      "zatca.column": "هيئة الزكاة",
      "zatca.state.cleared": "معتمدة",
      "zatca.state.cleared_with_warnings": "معتمدة (بتنبيهات)",
      "zatca.state.reported": "مُبلَّغة",
      "zatca.state.reported_with_warnings": "مُبلَّغة (بتنبيهات)",
      "zatca.state.rejected": "مرفوضة",
      "zatca.state.reconcile_required": "تحتاج تسوية",
      "zatca.state.superseded": "مُستبدلة",
      "zatca.state.pending_clearance": "بانتظار الاعتماد",
      "zatca.state.pending_reporting": "بانتظار الإبلاغ",
      "zatca.state.not_submitted": "لم تُرسل",
      "zatca.sandboxValidatedOnly": "بيئة اختبار: تم التحقق، لم تُبلَّغ",
      "zatca.pdfPendingClearance": "ملف PDF متاح بعد اعتماد الهيئة للفاتورة",
      "zatca.invalid.vat": "يجب أن يكون 15 رقمًا يبدأ وينتهي بالرقم 3",
      "zatca.invalid.cr": "يجب أن يكون 10 أرقام بالضبط",
      "zatca.invalid.building": "يجب أن يكون 4 أرقام بالضبط",
      "zatca.invalid.postal": "يجب أن يكون 5 أرقام بالضبط",
      "zatca.invalid.additional": "يجب أن يكون 4 أرقام بالضبط",
      "zatca.invalid.tooLong": "هذه القيمة طويلة جدًا",
      "zatca.invalid.buyerId": "لا يطابق نوع المعرّف المحدد",
      "zatca.invalid.buyerIdRequired": "يلزم للعميل التجاري رقم ضريبي أو رقم معرّف",
      "zatca.invalid.buyerIdTypeRequired": "اختر نوع المعرّف",
      "zatca.invalid.tin": "يجب أن يتكوّن الرقم المميّز من 10 أرقام بالضبط",
      "cm.customer.vatRegistered": "هل المنشأة مسجّلة في ضريبة القيمة المضافة؟",
      "cm.customer.vatRegisteredYes": "نعم",
      "cm.customer.vatRegisteredNo": "لا",
      "cm.customer.notVatHint": "إن توفّر، اختر نوع المعرّف وأدخل رقمه",
      "cm.customer.legacyIdWarning": "بيانات تعريف المنشأة ناقصة، وقد تصدر الفاتورة الضريبية القياسية مع تحذير من الهيئة.",
      "val.invalid.saMobile": "أدخل رقم جوال من 9 أرقام يبدأ بالرقم 5",
      "val.invalid.email": "أدخل بريدًا إلكترونيًا صحيحًا",
      "val.invalid.nationalId": "يجب أن يكون 10 أرقام يبدأ بـ 1 أو 2",
      "val.invalid.iban": "أدخل رقم آيبان سعودي صحيح (SA ثم 22 خانة)",
      "val.invalid.year": "أدخل سنة صحيحة من 4 أرقام",
      "val.invalid.number": "أدخل رقمًا صحيحًا",
      "val.invalid.wholeNumber": "أدخل رقمًا صحيحًا بدون كسور",
      "val.invalid.min": "القيمة أقل من الحد المسموح",
      "val.invalid.max": "القيمة أعلى من الحد المسموح",
      "zatca.retry": "إعادة الإبلاغ للهيئة",
      "zatca.retrying": "جارٍ إعادة الإبلاغ…",
      "zatca.retryQueued": "تمت إعادة الإرسال للهيئة",
      "zatca.evidence.title": "سجل التدقيق — هيئة الزكاة والضريبة والجمارك",
      "zatca.evidence.uuid": "المعرّف الفريد للمستند",
      "zatca.evidence.hash": "بصمة الفاتورة",
      "zatca.evidence.pih": "بصمة الفاتورة السابقة (PIH)",
      "zatca.evidence.downloadSigned": "تنزيل ملف XML الموقّع",
      "zatca.evidence.downloadCleared": "تنزيل ملف XML المعتمد (من الهيئة)",
      "zatca.evidence.downloadFailed": "تعذّر تنزيل ملف XML.",
      "zatca.evidence.nonProduction": "بيئة {{environment}} — هذا المستند موقّع بشهادة الاختبار المشتركة من الهيئة وليس دليلاً معتمداً. اسم الملف عند التنزيل يحمل اسم البيئة.",
      "zatca.evidence.explainB2C": "الفواتير المبسّطة تُبلَّغ للهيئة بعد إصدارها. رمز QR يُنشئه هذا النظام من جهازه المعتمد، وحالة التبليغ أعلاه هي ما يثبت استلام الهيئة للفاتورة.",
      "zatca.evidence.explainB2B": "الفواتير الضريبية القياسية تعتمدها الهيئة قبل مشاركتها مع المشتري. ملف XML المعتمد هو مستند الهيئة نفسه وموقّع منها ويحمل رمز QR الصادر عنها — وهو أقوى دليل متاح.",
      "zatca.retryHint": "الفاتورة تبقى كما هي — يُعاد إرسال نفس المستند. استخدم إشعار الدائن فقط عندما تكون بيانات الفاتورة نفسها خاطئة.",
      "cm.customer.addressOptional": "العنوان (اختياري)",
      "cm.customer.addressB2b": "عنوان الشارع",
      "cm.customer.addressB2bHint": "مطلوب للفواتير الضريبية (هيئة الزكاة والضريبة والجمارك BR-KSA-10)",
      "cm.customer.addressSearchPlaceholder": "ابحث عن العنوان…",
      "cm.customer.b2bCustomer": "عميل شركات (منشأة تجارية)",
      "cm.customer.b2bHint": "حدّد إذا كان هذا عميل منشأة تجارية (يفعّل الفاتورة الضريبية القياسية)",
      "cm.customer.businessInfo": "معلومات المنشأة",
      "cm.customer.companyName": "اسم المنشأة",
      "cm.customer.companyNamePlaceholder": "شركة المثال المحدودة",
      "cm.customer.vatHint": "الرقم الضريبي السعودي المكوّن من 15 خانة",
      "cm.customer.crNumber": "رقم السجل التجاري",
      "cm.customer.idTypeLabel": "نوع المعرّف",
      "cm.customer.idNumber": "رقم المعرّف",
      "cm.customer.b2bIdRule": "أدخل الرقم الضريبي، أو رقم معرّف مع تحديد نوعه",
      "cm.customer.idType.TIN": "الرقم المميز (TIN)",
      "cm.customer.idType.CRN": "السجل التجاري",
      "cm.customer.idType.MOM": "ترخيص وزارة الشؤون البلدية والقروية والإسكان",
      "cm.customer.idType.MLS": "ترخيص وزارة الموارد البشرية والتنمية الاجتماعية",
      "cm.customer.idType.700": "الرقم الموحد (700)",
      "cm.customer.idType.SAG": "ترخيص وزارة الاستثمار",
      "cm.customer.idType.NAT": "الهوية الوطنية",
      "cm.customer.idType.GCC": "هوية مواطني دول مجلس التعاون الخليجي",
      "cm.customer.idType.IQA": "رقم الإقامة",
      "cm.customer.idType.PAS": "جواز السفر",
      "cm.customer.idType.OTH": "معرّف آخر",
      "cm.customers.editCustomer": "تعديل العميل",
      "cm.customers.vehicleCount": "{{count}} مركبة",
      "cm.notifications.title": "الإشعارات",
      "cm.notifications.markAllRead": "تعليم الكل كمقروء",
      "cm.notifications.empty": "لا توجد إشعارات",
      "cm.notifications.viewDetails": "عرض التفاصيل",
      "cm.notifications.allMarkedRead": "تم تعليم جميع الإشعارات كمقروءة",
      "cm.notifications.markAllFailed": "تعذّر تعليم الكل كمقروء",
      "cm.actions.close": "إغلاق",
      "cm.actions.download": "تنزيل",
      "cm.time.justNow": "الآن",
      "cm.time.minutesAgo": "قبل {{count}} د",
      "cm.time.hoursAgo": "قبل {{count}} س",
      "cm.time.daysAgo": "قبل {{count}} ي",
      "cm.qr.title": "رمز QR",
      "cm.qr.scanToAccess": "امسح الرمز للوصول إلى بطاقة العمل",
      "cm.qr.scanHint": "امسح رمز QR هذا للوصول السريع إلى بطاقة العمل",
      "cm.qr.imageAlt": "رمز QR لبطاقة العمل",
      "cm.qr.print": "طباعة رمز QR",
      "cm.qr.invalid": "رمز QR غير صالح",
      "cm.qr.notFound": "بطاقة العمل غير موجودة",
      "cm.lightbox.zoomIn": "تكبير",
      "cm.lightbox.zoomOut": "تصغير",
      "cm.lightbox.fullSizeAlt": "الحجم الكامل",
      "cm.confirmAction": "تأكيد الإجراء",
      "cm.confirmProceed": "هل أنت متأكد أنك تريد المتابعة؟",
      "cm.close": "إغلاق",
      "cm.search": "بحث...",
      "cm.clear": "مسح",
      "cm.progress": "التقدّم",
      "cm.login.sendOtpFailed": "تعذّر إرسال رمز التحقق",
      "cm.login.invalidOtp": "رمز التحقق غير صحيح",
      "cm.login.loginFailed": "فشل تسجيل الدخول",
      "cm.header.menu": "القائمة",
      "cm.header.theme": "المظهر",
      "cm.header.help": "المساعدة والدليل",
      "cm.header.more": "المزيد",
      "qc.title": "إنشاء جديد",
      "qc.newJobCard": "أمر عمل جديد",
      "qc.newCustomer": "عميل جديد",
      "qc.newVehicle": "مركبة جديدة",
      "qc.newInvoice": "فاتورة جديدة",
      "cm.header.switchToArabic": "التبديل إلى العربية",
      "cm.header.switchToEnglish": "التبديل إلى الإنجليزية",
      "cm.header.switchToStaffView": "التبديل إلى عرض الموظفين",
      "cm.header.switchToAdminView": "التبديل إلى عرض المسؤول",
      "cm.header.switchedToArabic": "تم التبديل إلى العربية",
      "cm.header.switchedToEnglish": "تم التبديل إلى الإنجليزية",
      "cm.sidebar.poweredBy": "مدعوم بواسطة",
      "cm.processing": "جارٍ المعالجة...",
      "cm.scanning": "جارٍ المسح...",
      "cm.retry": "إعادة المحاولة",
      "cm.instructions_label": "التعليمات:",
      "cm.tip_label": "نصيحة:",
      "cm.camera.start": "تشغيل الكاميرا",
      "cm.camera.stop": "إيقاف الكاميرا",
      "cm.camera.capture": "التقاط الصورة",
      "cm.camera.upload_photo": "رفع صورة",
      "cm.camera.take_photo": "التقاط صورة",
      "cm.camera.preview_placeholder": "ستظهر معاينة الكاميرا هنا",
      "cm.camera.start_failed": "تعذّر تشغيل الكاميرا. يُرجى التحقق من أذونات الكاميرا.",
      "cm.camera.access_failed": "تعذّر الوصول إلى الكاميرا. يُرجى التحقق من الأذونات.",
      "cm.camera.access_generic": "تعذّر الوصول إلى الكاميرا.",
      "cm.camera.access_permission_hint": "تعذّر الوصول إلى الكاميرا. يُرجى التأكد من منح أذونات الكاميرا.",
      "cm.camera.busy": "الكاميرا مشغولة بتطبيق آخر (مثل Zoom/Teams). يُرجى إغلاقه ثم تحديث الصفحة.",
      "cm.camera.permission_denied": "تم رفض إذن الكاميرا. يُرجى السماح بالوصول من إعدادات المتصفح.",
      "cm.camera.not_found": "لم يتم العثور على كاميرا في هذا الجهاز.",
      "cm.location.requesting": "جارٍ تحديد الموقع…",
      "cm.location.required_title": "الموقع مطلوب",
      "cm.location.required_msg": "فعّل الموقع للالتقاط. تُختم الصور والفيديوهات بالموقع.",
      "cm.location.denied": "الموقع محظور. اسمح بالوصول إلى الموقع لهذا الموقع من إعدادات المتصفح ثم أعد المحاولة.",
      "cm.location.retry": "تفعيل وإعادة المحاولة",
      "cm.istamara.title": "مسح الاستمارة (تسجيل المركبة)",
      "cm.istamara.tip_flat": "ضع بطاقة الاستمارة بشكل مستوٍ وفي إضاءة جيدة",
      "cm.istamara.tip_focus": "تأكد من وضوح جميع النصوص وظهورها بدقة",
      "cm.istamara.tip_glare": "تجنّب الظلال والوهج",
      "cm.istamara.tip_ai_extract": "سيستخرج TzamunAI Vision جميع الحقول تلقائيًا",
      "cm.istamara.processing_vision": "جارٍ المعالجة بواسطة TzamunAI Vision...",
      "cm.istamara.video_play_failed": "تعذّر تشغيل بث الفيديو",
      "cm.istamara.extract_failed": "تعذّر استخراج بيانات الاستمارة",
      "cm.istamara.process_failed": "تعذّرت معالجة الصورة",
      "cm.istamara.align_hint": "ضع البطاقة كاملة داخل الإطار",
      "cm.istamara.captured_hint": "تم التقاط الصورة",
      "cm.istamara.q_ready": "جاهز",
      "cm.istamara.q_dark": "الإضاءة ضعيفة — ابحث عن إضاءة أفضل",
      "cm.istamara.q_glare": "يوجد انعكاس على البطاقة — أمِلها قليلًا",
      "cm.istamara.q_blurry": "ثبّت يدك — الصورة غير واضحة",
      "cm.istamara.q_empty": "ضع البطاقة داخل الإطار",
      "cm.istamara.capture_anyway": "التقاط على أي حال",
      "cm.istamara.rotate_frame": "تدوير الإطار",
      "cm.istamara.retake": "إعادة التقاط الصورة",
      "cm.istamara.retry_read": "إعادة محاولة القراءة",
      "cm.istamara.stage_uploading": "جارٍ إرسال الصورة...",
      "cm.istamara.stage_reading": "جارٍ قراءة البطاقة...",
      "cm.istamara.stage_finishing": "أوشكنا على الانتهاء...",
      "cm.qr.scan_title": "مسح الرمز",
      "cm.qr.position_hint": "ضع رمز QR أو الباركود داخل الإطار",
      "cm.qr.tip_body": "ثبّت الكاميرا وتأكد من الإضاءة الجيدة للحصول على أفضل النتائج. يدعم رموز QR وباركود CODE128.",
      "cm.vin.title": "مسح باركود رقم الهيكل (VIN)",
      "cm.vin.mode_barcode": "باركود",
      "cm.vin.mode_text": "قراءة النص",
      "cm.vin.invalid_format": "تنسيق رقم الهيكل (VIN) غير صالح. يجب أن يتكون من 17 خانة.",
      "cm.vin.point_barcode": "وجّه الكاميرا نحو باركود رقم الهيكل (VIN)",
      "cm.vin.barcode_location": "يوجد عادةً على إطار باب السائق",
      "cm.vin.point_text": "وجّه الكاميرا نحو نص رقم الهيكل (VIN)",
      "cm.vin.text_location": "يوجد على لوحة القيادة بالقرب من الزجاج الأمامي أو على إطار الباب",
      "cm.vin.scanning_barcode": "جارٍ مسح الباركود...",
      "cm.vin.reading_text": "جارٍ قراءة نص رقم الهيكل (VIN)...",
      "cm.video.start_recording": "بدء التسجيل",
      "cm.video.stop_recording": "إيقاف التسجيل",
      "cm.plate.alt": "لوحة سعودية",
      "cm.diagram.clear_all": "مسح الكل",
      "cm.diagram.delete_point": "حذف النقطة",
      "cm.diagram.no_photo_attached": "لا توجد صورة مرفقة",
      "cm.diagram.view_alt": "عرض {{view}}",
      "cm.diagram.damage_alt": "الضرر",
      "cm.diagram.vehicle_damage_inspection": "فحص أضرار المركبة",
      "cm.diagram.click_to_mark": "انقر على مخطط السيارة لتحديد مواضع الضرر ({{count}})",
      "cm.diagram.front": "الأمام",
      "cm.diagram.rear": "الخلف",
      "cm.diagram.left_short": "يسار",
      "cm.diagram.right_short": "يمين",
      "cm.diagram.damage_inspection_notes": "ملاحظات فحص الأضرار",
      "cm.diagram.notes_placeholder": "صف أي أضرار أو خدوش أو انبعاجات أو مشكلات لوحظت على المركبة...",
      "cm.diagram.locations_marked": "تم تحديد {{count}} موضع ضرر على المخطط",
      "cm.appt.detail.jobCard": "بطاقة العمل",
      "cm.appt.checkin.msg": "تسجيل وصول {{name}}؟",
      "cm.app.accessDenied": "تم رفض الوصول",
      "cm.errorBoundary.title": "عذرًا! حدث خطأ ما",
      "cm.errorBoundary.message": "نعتذر عن الإزعاج. تم إبلاغ فريقنا بهذا الخطأ.",
      "cm.errorBoundary.reload": "إعادة تحميل الصفحة",
      "cm.errorBoundary.updating": "جارٍ التحديث إلى أحدث إصدار…",
      "cm.update.available": "يتوفر إصدار جديد.",
      "cm.update.refresh": "تحديث",
      "iv.failedToLoadData": "تعذّر تحميل البيانات",
      "iv.failedToLoadJobCardDetails": "تعذّر تحميل تفاصيل أمر العمل",
      "iv.pleaseSelectCustomer": "يرجى اختيار العميل",
      "iv.pleaseAddLineItem": "يرجى إضافة بند واحد على الأقل",
      "iv.lineNeedsPrice": "السطر {{n}}: اكتب السعر",
      "qt.lineNegativePrice": "السطر {{n}}: لا يمكن أن يكون السعر أقل من صفر",
      "qt.lineNeedsQuantity": "السطر {{n}}: يجب أن تكون الكمية أكبر من صفر",
      "iv.lineNeedsQuantity": "السطر {{n}}: يجب أن تكون الكمية أكبر من صفر",
      "iv.invoiceTotalMustBePositive": "لا يمكن إصدار فاتورة بقيمة صفر. ضع سعرًا لسطر واحد على الأقل.",
      "iv.selectCustomerFirst": "اختر العميل أولاً",
      "iv.invoiceCreatedSuccessfully": "تم إنشاء الفاتورة بنجاح",
      "iv.failedToCreateInvoice": "تعذّر إنشاء الفاتورة",
      "iv.loadingCustomer": "جاري تحميل بيانات العميل...",
      "iv.loadingVehicle": "جاري تحميل بيانات المركبة...",
      "iv.noVehicleSelected": "لم يتم اختيار مركبة",
      "iv.vehicleOptional": "المركبة (اختياري)",
      "iv.zatcaInvoiceType": "نوع فاتورة ZATCA",
      "iv.zatcaB2C": "B2C - من منشأة إلى مستهلك (مبسطة)",
      "iv.zatcaB2B": "B2B - من منشأة إلى منشأة (تفاصيل كاملة)",
      "vat.includeVat": "السعر شامل الضريبة",
      "manual.nav": "دليل الاستخدام",
      "manual.title": "دليل الاستخدام",
      "manual.subtitle": "دليل خطوة بخطوة لكل ما في موتورمايند",
      "manual.searchPlaceholder": "ابحث في الدليل…",
      "manual.askTitle": "اسأل الذكاء الاصطناعي",
      "manual.askSubtitle": "اسأل كيف تنفذ أي شيء — الإجابة من هذا الدليل.",
      "manual.askPlaceholder": "مثال: كيف أرسل عرض سعر للعميل؟",
      "manual.askButton": "اسأل",
      "manual.answerFrom": "من",
      "manual.noResults": "لا توجد أقسام مطابقة لبحثك.",
      "manual.loadFailed": "تعذر تحميل الدليل",
      "manual.askFailed": "تعذرت الإجابة — حاول مرة أخرى",
      "manual.quotaError": "استهلكت شركتك حصة الذكاء الاصطناعي الشهرية.",
      "jc.branch": "الفرع",
      "manual.helpTitle": "مساعدة الصفحة",
      "manual.poweredBy": "مدعوم من",
      "manual.helpAskPlaceholder": "اسأل عن هذه الصفحة…",
      "manual.openManual": "افتح دليل الاستخدام الكامل",
      "manual.contactSupport": "تواصل مع الدعم",
      "manual.supportUnavailable": "خدمة الدعم لا تستجيب حالياً. يرجى المحاولة بعد قليل.",
      "manual.feedbackAsk": "هل كانت الإجابة مفيدة؟",
      "manual.feedbackUp": "مفيدة",
      "manual.feedbackDown": "غير مفيدة",
      "manual.feedbackThanks": "شكراً لملاحظتك!",
      "mi.nav": "تحليلات الدليل",
      "mi.title": "تحليلات أسئلة الدليل",
      "mi.subtitle": "ماذا يسأل المستأجرون ذكاء الدليل — وأين يخفق",
      "mi.total": "إجمالي الأسئلة",
      "mi.noMatch": "بدون تطابق في الدليل",
      "mi.down": "غير مفيدة",
      "mi.up": "مفيدة",
      "mi.filterAll": "كل الأسئلة",
      "mi.filterNoMatch": "بدون تطابق فقط",
      "mi.filterDown": "غير المفيدة فقط",
      "mi.question": "السؤال",
      "mi.company": "الشركة",
      "mi.sections": "الأقسام المطابقة",
      "mi.signal": "الإشارة",
      "mi.date": "التاريخ",
      "mi.fromPage": "من صفحة",
      "mi.noMatchBadge": "بدون تطابق",
      "mi.downBadge": "إجابة ضعيفة",
      "mi.empty": "لا توجد أسئلة بعد.",
      "mi.loadFailed": "تعذر تحميل التحليلات",
      "svc.billingOnly": "أجور عمل / تكلفة موظفين — للفوترة فقط",
      "svc.billingOnlyHint": "لا يتطلب إجراءً في المركز: يُخفى من شاشة الفني ويكتمل تلقائياً مع اكتمال المهمة، ويُحتسب في عرض السعر والفاتورة.",
      "svc.billingOnlyBadge": "للفوترة فقط",
      "svc.markDone": "إكمال",
      "svc.markedDone": "تم إكمال الخدمة",
      "pt.markInstalled": "تم التركيب",
      "pt.markedInstalled": "تم تسجيل تركيب القطعة",
      "jobCardDetailsUpdateFailed": "فشل التحديث",
      "vat.inclusiveHint": "= {{net}} + {{vat}} ضريبة ({{rate}}٪)",
      "iv.autoDetected": "تم الاكتشاف تلقائياً",
      "iv.zatcaAutoB2B": "العميل لديه رقم ضريبي (B2B)",
      "iv.zatcaAutoB2C": "مستهلك عادي (B2C)",
      "iv.zatcaAutoB2BCompany": "فاتورة ضريبية قياسية لعميل تجاري",
      "iv.zatcaAutoDetectHint": "سيتم تحديد النوع تلقائياً بناءً على العميل. B2B إذا كان لدى العميل رقم ضريبي، وإلا فسيكون B2C.",
      "iv.sendInvoiceLinkSMS": "إرسال رابط الفاتورة إلى العميل عبر الرسائل النصية",
      "iv.sendInvoiceLinkSMSOn": "سيستلم العميل رسالة نصية تحتوي على رابط لعرض هذه الفاتورة ودفعها.",
      "iv.sendInvoiceLinkSMSOff": "سيتم إنشاء الفاتورة مع رابط عام دون إرسال رسالة نصية.",
      "iv.hidePaidAndDue": "إخفاء \"المبلغ المدفوع\" و\"المبلغ المستحق\" في هذه الفاتورة",
      "iv.hidePaidAndDueHint": "يتم إخفاء هذين السطرين فقط. الإجمالي قبل الضريبة والخصم والضريبة والإجمالي النهائي تظهر كالمعتاد.",
      "iv.delayFeeTag": "رسوم تأخير",
      "iv.dontSendOnCreate": "عدم إرسال الفاتورة إلى العميل الآن",
      "iv.dontSendOnCreateHint": "يتم إنشاء الفاتورة مع رابطها — يمكنك إرسالها في أي وقت من قائمة الفواتير.",
      "iv.invoiceNoPublicLink": "لا يوجد رابط عام لهذه الفاتورة",
      "iv.smsSentRemaining": "تم إرسال الرسالة النصية بنجاح (يتبقى {{count}} إعادة إرسال اليوم)",
      "iv.resendDuplicate": "لم تُرسل — العميل يملك هذه الرسالة نفسها، لذا منعت البوابة تكرارها. ستُرسل مرة أخرى عند تغيّر شيء في الفاتورة، كتسجيل دفعة مثلاً.",
      "iv.failedToSendSMS": "تعذّر إرسال الرسالة النصية",
      "iv.viewPublicLink": "عرض الرابط العام",
      "iv.resendInvoiceLinkSMS": "إعادة إرسال رابط الفاتورة عبر الرسائل النصية",
      "iv.downloadPDF": "تنزيل PDF",
      "invoice.status_draft": "مسودة",
      "invoice.status_posted": "صادرة",
      "invoice.status_pending_clearance": "بانتظار اعتماد هيئة الزكاة",
      "invoice.status_void": "ملغاة",
      "invoice.not_issued_yet": "لم تُصدر بعد",
      "invoice.reference_label": "المرجع",
      "invoice.payref_label": "مرجع الدفع",
      "invoice.draft_notice": "لم تُصدر الفاتورة بعد — سيتم إصدارها (مع رمز QR) عند استلام الدفع أو تسليم السيارة.",
      "invoice.draft_public_notice": "لم يتم إصدار فاتورتك بشكل نهائي بعد. ستصدر الفاتورة الضريبية الرسمية (مع رمز QR) عند استلام الدفع أو تسليم سيارتك.",
      "invoice.amount_due": "المبلغ المستحق",
      "invoice.creditNotes.title": "إشعارات دائنة (هيئة الزكاة 381)",
      "invoice.creditNotes.amount": "المبلغ",
      "invoice.creditNotes.reported": "مُبلَّغ إلى هيئة الزكاة",
      "invoice.creditNotes.type.partial": "جزئي",
      "invoice.creditNotes.type.full": "كامل",
      "creditNote.issue": "إصدار إشعار دائن",
      "creditNote.title": "إصدار إشعار دائن",
      "creditNote.issued": "صدر الإشعار الدائن {{number}}",
      "creditNote.gatewayRefundFailed": "تعذّر الاسترداد إلى البطاقة — أعد المحاولة من الدفعة",
      "creditNote.back": "رجوع",
      "creditNote.confirmIssue": "نعم، أصدر الإشعار الدائن",
      "creditNote.confirmWarning": "لا يمكن التراجع عن هذا الإشعار الدائن، ويُبلَّغ إلى هيئة الزكاة والضريبة والجمارك.",
      "creditNote.total": "إجمالي الإشعار",
      "creditNote.full": "الفاتورة كاملة",
      "creditNote.chooseLines": "اختيار البنود",
      "creditNote.line": "البند",
      "creditNote.invoicedQty": "الكمية المفوترة",
      "creditNote.alreadyCredited": "في إشعارات سابقة",
      "creditNote.qtyToCredit": "الكمية في الإشعار",
      "creditNote.returnToStock": "إرجاع إلى المخزون",
      "creditNote.net": "الصافي",
      "creditNote.vat": "ضريبة القيمة المضافة",
      "creditNote.ofWhichOwed": "منه غير مسدَّد",
      "creditNote.ofWhichPaid": "منه مسدَّد",
      "creditNote.paidMoney": "المبلغ المسدَّد",
      "creditNote.refundNow": "استرداده الآن",
      "creditNote.refundMethod": "طريقة الاسترداد",
      "creditNote.method.cash": "نقدًا",
      "creditNote.method.bank_transfer": "تحويل بنكي",
      "creditNote.method.card": "بطاقة (استرداد الدفع الإلكتروني)",
      "creditNote.keepAsCredit": "إبقاؤه رصيدًا دائنًا للعميل",
      "creditNote.reason": "السبب",
      "creditNote.status.pending_clearance": "بانتظار اعتماد الهيئة",
      "creditNote.status.rejected": "رفضته الهيئة",
      "creditNote.disposition.refund": "مُسترَد",
      "creditNote.disposition.customer_credit": "رصيد دائن للعميل",
      "creditNote.badge.credited": "صدر لها إشعار دائن",
      "creditNote.badge.partlyCredited": "صدر لها إشعار دائن جزئي",
      "creditNote.outcome.card_unknown": "حالة الاسترداد إلى البطاقة غير مؤكدة — جارٍ التحقق",
      "creditNote.clearanceRefundAsCredit": "فاتورة لمنشأة: يُبقى الاسترداد النقدي أو بالتحويل البنكي رصيدًا دائنًا للعميل عند اعتماد الهيئة للإشعار الدائن، ويصرفه أمين الصندوق من صفحة العميل.",
      "gl.close.refusal.GL_CLOSE_PENDING_CREDIT_NOTE": "الإشعار الدائن {{doc_ref}} بانتظار اعتماد الهيئة. أقفل هذا الشهر بعد اعتماده أو رفضه.",
      "creditNote.outcome.refunded": "مُسترَد",
      "creditNote.outcome.customer_credit": "رصيد دائن للعميل",
      "creditNote.outcome.card_pending": "جارٍ الاسترداد إلى البطاقة",
      "creditNote.cardRefundCompletedByProvider": "أتمّت جهة الدفع هذا الاسترداد إلى البطاقة، فلا يلزم إجراء آخر.",
      "creditNote.cardRefundedConfirmWarning": "اختر هذا فقط إن كان المبلغ قد رُدّ إلى البطاقة فعلًا (من لوحة جهة الدفع مثلًا). لا يمكن التراجع عن ذلك.",
      "creditNote.cardKeepAsCreditWarning": "يصبح المبلغ رصيدًا دائنًا للعميل ولا يُردّ إلى البطاقة. لا يمكن التراجع عن ذلك.",
      "creditNote.gatewayRef": "مرجع جهة الدفع (اختياري)",
      "creditNote.resolutionNote": "ملاحظة (اختيارية)",
      "creditNote.cardResolveConfirm": "تأكيد",
      "creditNote.outcome.card_needs_check": "يحتاج الاسترداد إلى البطاقة إلى تحقق",
      "creditNote.cardNeedsCheckToast": "يحتاج الاسترداد إلى البطاقة إلى تحقق، ويُحسم من صفحة الفاتورة.",
      "creditNote.cardRefundedConfirm": "تم الاسترداد إلى البطاقة",
      "creditNote.cardKeepAsCredit": "إبقاؤه رصيدًا دائنًا للعميل",
      "creditNote.cardRefundedConfirmed": "أُكِّد الاسترداد إلى البطاقة",
      "creditNote.cardKeptAsCredit": "أُبقي رصيدًا دائنًا للعميل",
      "creditNote.cardNeedsCheckNoLonger": "لم يعد هذا الإشعار الدائن بانتظار قرار الموظف.",
      "creditNote.outcome.card_refunded": "مُسترَد إلى البطاقة",
      "creditNote.outcome.card_failed_credit": "تعذّر الاسترداد إلى البطاقة — أُبقي رصيدًا دائنًا للعميل",
      "creditNote.cardRefundFailedCredit": "تعذّر الاسترداد إلى البطاقة، فأصبح المبلغ رصيدًا دائنًا للعميل — يمكن صرفه من صفحة العميل.",
      "creditNote.clearanceNote": "هذه فاتورة ضريبية لمنشأة: لا يُسترَد المال ولا يعود المخزون إلا بعد اعتماد الهيئة للإشعار الدائن.",
      "creditNote.notReturnable": "لم يخرج شيء من هذه القطعة من المخزون في هذه الفاتورة",
      "creditNote.returnableShort": "لا يمكن إرجاع أكثر من {{qty}} إلى المخزون",
      "invoice.taxable_amount": "المبلغ الخاضع للضريبة",
      "invoice.grand_total": "الإجمالي شامل الضريبة",
      "invoice.amount_paid": "المبلغ المدفوع",
      "invoice.icv_label": "العداد التسلسلي (ICV)",
      "invoice.posted_at_label": "تاريخ الإصدار",
      "invoice.official_number_label": "رقم الفاتورة",
      "invoice.payment_recorded_delivered": "تم تسجيل الدفعة — تم إرسال الفاتورة وإصدار رمز التسليم",
      "invoice.payment_recorded_partial": "تم تسجيل الدفعة الجزئية",
      "iv.sendInvoiceViaSMS": "إرسال الفاتورة عبر الرسائل النصية",
      "iv.confirmBeforeSending": "تأكيد قبل الإرسال",
      "iv.sendSMS": "إرسال رسالة نصية",
      "iv.sending": "جاري الإرسال...",
      "iv.smsLinkDownloadPDF": "سيستلم العميل رسالة نصية تحتوي على رابط لتنزيل فاتورته بصيغة PDF.",
      "iv.smsLinkViewAndPay": "سيستلم العميل رسالة نصية تحتوي على رابط لعرض هذه الفاتورة ودفعها.",
      "iv.fillAllItemFields": "يرجى تعبئة جميع حقول البند",
      "iv.itemRemoved": "تم حذف البند",
      "iv.invoicePreview": "معاينة الفاتورة",
      "iv.confirmGenerateInvoice": "تأكيد وإنشاء الفاتورة",
      "iv.action": "إجراء",
      "iv.downpaymentCredit": "رصيد العربون",
      "iv.downpaymentCreditReceipt": "عربون (سند قبض {{ref}}) — مخصوم",
      "iv.paidUpper": "مدفوع",
      // Task 5.2 (payment-receipt redesign): job-card receipt timeline.
      "rc.timelineTitle": "سندات القبض",
      "rc.availableCredit": "رصيد العربون المتاح",
      "rc.kind.downpayment": "عربون",
      "rc.kind.payment": "دفعة",
      "rc.kind.refund": "استرداد",
      "rc.status.draft": "مسودة",
      "rc.status.issued": "صادر",
      "rc.status.paid": "مدفوع",
      "rc.status.voided": "ملغى",
      "rc.status.refunded": "مسترد",
      "rc.status.partially_refunded": "مسترد جزئياً",
      "rc.advanceBreakdown": "المتاح {{available}} · المحجوز {{reserved}} · المستهلك {{consumed}}",
      "rc.recordPayment": "تسجيل الدفع",
      "rc.recordPaySuccess": "تم دفع السند {{number}}",
      "rc.recordPayFail": "فشل تسجيل الدفع",
      "rc.openCheckoutWarning": "يوجد رابط دفع إلكتروني مفتوح لهذا السند. يرجى التأكد من أن العميل لم يدفع إلكترونيًا مسبقًا قبل تسجيل هذه الدفعة.",
      "rc.openCheckoutConfirm": "العميل لم يدفع إلكترونيًا — متابعة",
      "rc.navTitle": "سندات القبض والصرف",
      "rc.pageSubtitle": "سندات القبض للمبالغ الواردة وسندات الصرف للمبالغ الصادرة — لكل نوع ترقيمه الخاص",
      "rc.family.rc": "سند قبض",
      "rc.family.pv": "سند صرف",
      "rc.newReceipt": "إنشاء سند قبض",
      "rc.newDisbursement": "إنشاء سند صرف",
      "rc.kind.disbursement": "صرف نقدي",
      "rc.pvHint": "مبالغ مصروفة لا تُعد استرداداً لعميل ولا فاتورة مورّد ولا مصروفاً مسجّلاً — مثل الإيجار أو المندوب أو سلفة موظف.",
      "rc.pvPayee": "المصروف له",
      "rc.pvPayeePhone": "جوال المستلم",
      "rc.pvPayeePick": "اختر الشريك",
      "rc.pvPayeePartnerHint": "هذا السبب يخص أموال المالك أو الشركاء، لذا يجب أن يطابق الاسم شريكاً مسجّلاً في شاشة الشركاء تماماً.",
      "rc.pvReason": "سبب الصرف",
      "rc.pvReasonPick": "اختر السبب…",
      "rc.pvNoReasons": "لم يتم إعداد أسباب صرف بعد. يُرجى إضافتها من شاشة الحركات النقدية أولاً — إذ يجب أن يوضّح سند الصرف سبب خروج المبلغ.",
      "rc.pvReasonsForbidden": "خاصية الحركات النقدية غير مفعّلة في باقتكم، لذا تعذّر تحميل أسباب الصرف. يُرجى التواصل مع المسؤول.",
      "rc.pvValidation": "أدخل اسم المصروف له وسبب الصرف ومبلغاً أكبر من صفر.",
      "rc.pvCreateSuccess": "تم إصدار سند الصرف {{number}}",
      "rc.pvCreateFail": "تعذّر إصدار سند الصرف",
      "rc.receiptNumber": "رقم السند",
      "rc.kindLabel": "النوع",
      "rc.allKinds": "جميع الأنواع",
      "rc.searchCustomer": "بحث عن عميل",
      "rc.linkedTo": "مرتبط بـ",
      "rc.noReceipts": "لا توجد سندات قبض",
      "rc.noReceiptsDescription": "ستظهر سندات القبض الصادرة للعملاء هنا.",
      "rc.noReceiptsFiltered": "لا توجد سندات مطابقة لعوامل التصفية الحالية.",
      "rc.loadFail": "فشل تحميل سندات القبض",
      "rc.createValidation": "اختر عميلاً وأدخل مبلغاً أكبر من 0",
      "rc.createSuccess": "تم إصدار السند {{number}}",
      "rc.createFail": "فشل إصدار السند",
      "rc.applyReceipt": "تطبيق سند قبض",
      "rc.noReceiptApplied": "بدون سند — إدخال يدوي",
      "rc.originJobCard": "من طلب العمل {{number}}",
      "rc.originQuotation": "من عرض السعر {{number}}",
      "rc.paymentSourceDirect": "دفع مباشر",
      "rc.paymentSourceReceipt": "استخدام سند قبض العميل",
      "rc.selectReceiptPlaceholder": "اختر سند قبض",
      "rc.noEligibleReceipts": "لا توجد سندات قبض مؤهلة برصيد متاح لهذا العميل",
      "rc.selectedCredit": "الرصيد المحدد: {{amount}}",
      "rc.linkedReceipts": "السندات المرتبطة",
      "rc.contributed": "المطبق على هذه الفاتورة",
      "rc.payWithExistingReceipt": "الدفع بسند قبض حالي",
      "rc.payWithExistingReceiptSuccess": "تم دفع عرض السعر من السند الحالي",
      "rc.payWithExistingReceiptFail": "فشل الدفع بالسند الحالي",
      "rc.willApplyPartial": "سيتم تطبيق {{applied}} الآن — سيتبقى {{after}}",
      // Task 3 (quotation multi-downpayment UI): progress line above the
      // quotation's downpayment receipts list.
      "quo.dp.progress": "العربون: تم دفع {{paid}} من {{required}} — المتبقي {{remaining}}",
      // Finding 3b (adversarial review — end-to-end installment journey):
      // staff-initiated "issue a new installment receipt for an arbitrary
      // amount ≤ remaining" modal on the quotation's receipts card.
      "quo.dp.issueInstallment": "إصدار سند دفعة",
      "quo.dp.installmentAmountLabel": "المبلغ (المتبقي {{remaining}})",
      "quo.dp.issueInstallmentSuccess": "تم إصدار سند الدفعة",
      "quo.dp.issueInstallmentFail": "فشل إصدار سند الدفعة",
      "quo.dp.installmentAmountInvalid": "أدخل مبلغاً أكبر من 0 ولا يتجاوز {{remaining}}",
      "rc.sendLinkPrompt": "إجمالي الفاتورة أكبر من السند المطبّق بمقدار {{amount}}. هل تريد إرسال رابط دفع للعميل بالمبلغ المتبقي؟",
      "rc.sendLinkConfirm": "إرسال رابط الدفع",
      "rc.sendLinkSkip": "ليس الآن",
      "rc.sendLinkSuccess": "تم إرسال رابط الدفع إلى العميل",
      "rc.sendLinkFail": "فشل إرسال رابط الدفع",
      "rc.creditWarning": "يغطي هذا السند {{amount}} أكثر من إجمالي الفاتورة. سيبقى الفرق كرصيد مستحق للعميل.",
      "rc.creditWarningConfirm": "متابعة",
      "rc.creditAction.title": "رصيد متاح للعميل",
      "rc.creditAction.excessLine": "تغطي هذه الدفعة {{amount}} ريال أكثر من إجمالي الفاتورة. اختر ما سيحدث للفرق.",
      "rc.creditAction.addToAccount": "إضافة إلى حساب العميل",
      "rc.creditAction.addToAccountHint": "الاحتفاظ بالفرق كرصيد متاح للفاتورة القادمة للعميل.",
      "rc.creditAction.payback": "إرجاع المبلغ للعميل",
      "rc.creditAction.paybackHint": "استرداد الفرق للعميل الآن.",
      "rc.creditAction.methodLabel": "طريقة الاسترداد",
      "rc.creditAction.methodRequired": "اختر طريقة الاسترداد",
      "rc.customersWithCreditTitle": "عملاء لديهم رصيد متاح",
      "rc.actions": "الإجراءات",
      "rc.viewPdf": "عرض PDF",
      "rc.viewPdfFail": "فشل فتح ملف PDF للسند",
      "rc.delete": "حذف السند",
      "rc.deleteConfirmTitle": "هل تريد حذف هذا السند؟",
      "rc.deleteConfirmMessage": "سيتم إلغاء السند {{number}} واسترجاع المبلغ. لا يمكن التراجع عن هذا الإجراء.",
      "rc.deleteSuccess": "تم حذف السند",
      "rc.deleteFail": "فشل حذف السند",
      "iv.sendInvoiceVia": "إرسال الفاتورة عبر:",
      "iv.billedToInsurer": "فاتورة على شركة التأمين (B2B)",
      "iv.insurerBillingMissing": "بيانات شركة التأمين ناقصة: الرقم الضريبي أو السجل التجاري أو العنوان. أكمِلها من شركات التأمين قبل إصدار الفاتورة.",
      "iv.bothLanguages": "اللغتان معاً",
      "iv.discountPercentage": "نسبة مئوية (%)",
      "iv.discountFixedAmount": "مبلغ ثابت (ريال)",
      "iv.subtotalAfterDiscount": "المجموع الفرعي بعد الخصم",
      "iv.refundDue": "المبلغ المسترد",
      "iv.finalTotal": "الإجمالي النهائي",
      "jc.issueTypeOther": "أخرى",
      "jc.issueDescriptionPlaceholder": "صف المشكلة بالتفصيل...",
      "jc.rejectPartRequest": "رفض طلب القطعة",
      "jc.rejectRequest": "رفض الطلب",
      "jc.rejectPartRequestHint": "يرجى تقديم سبب رفض طلب القطعة. سيتم إرسال السبب إلى الفني.",
      "jc.reasonForRejection": "سبب الرفض",
      "jc.rejectionReasonPlaceholder": "مثال: غير متوفر في المخزون، غير مطلوب",
      "jc.selectViewMode": "اختيار وضع العرض",
      "jc.selectViewModeDesc": "اختر طريقة وصولك إلى النظام",
      "jc.adminViewDesc": "صلاحية إدارة كاملة",
      "jc.staffViewDesc": "واجهة مركّزة على المهام",
      "jc.deliveryCodeLength": "يجب أن يتكون رمز التسليم من 6 أحرف",
      "jc.vehicleDeliveredSuccess": "تم تسليم المركبة بنجاح!",
      "jc.invalidDeliveryCode": "رمز التسليم غير صحيح",
      "jc.verifyDeliveryCode": "التحقق من رمز التسليم",
      "jc.verifyAndDeliver": "التحقق والتسليم",
      "jc.deliveryCodePrompt": "أدخل رمز التسليم المكوّن من 6 أحرف المقدّم من العميل",
      "jc.deliveryCode": "رمز التسليم",
      "jc.overrideCode": "تجاوز الرمز",
      "jc.overrideReasonLabel": "مبرر التجاوز",
      "jc.overrideReasonRequired": "يجب إدخال مبرر لتجاوز رمز التسليم",
      "jc.overrideConfirm": "سيتم تسجيل هذا التجاوز في سجل بطاقة العمل.",
      "jc.closeReasonRequired": "يرجى تقديم سبب إغلاق بطاقة العمل",
      "jc.jobCardClosedSuccess": "تم إغلاق بطاقة العمل بنجاح",
      "jc.failedToCloseJobCard": "تعذّر إغلاق بطاقة العمل",
      "jc.closing": "جارٍ الإغلاق...",
      "jc.closeJobCardWarning": "أنت على وشك إغلاق بطاقة العمل هذه. سيؤدي هذا الإجراء إلى وضع علامة على العمل كمغلق وإيقاف أي عمل إضافي.",
      "jc.reasonForClosing": "سبب الإغلاق",
      "jc.closeReasonHint": "سيتم تسجيل هذا السبب في سجل بطاقة العمل",
      "jc.closeReasonPlaceholder": "مثال: ألغى العميل الخدمة، المركبة غير قابلة للإصلاح، العميل لا يرد...",
      "jc.reopenJobCard": "إعادة فتح",
      "jc.hero.more": "المزيد",
      "jc.lens.managerActions": "إجراءات المدير",
      "jc.hero.photo": "صوّر",
      "jc.hero.photoAdded": "تمت إضافة الصورة",
      "jc.hero.photoFailed": "تعذّرت إضافة الصورة",
      "jc.cta.completeInfo": "إكمال البيانات",
      "jc.cta.generateQuotation": "إنشاء عرض",
      "jc.cta.resend": "إعادة إرسال",
      "jc.cta.deliver": "تسليم",
      "jc.cta.runQualityCheck": "بدء فحص الجودة",
      "qt.listSubtitle": "{{count}} عرضًا هذا الشهر · {{awaiting}} بانتظار العميل",
      "qt.downpaymentDue": "الدفعة المقدمة المستحقة:",
      "qt.downpaymentRemaining": "الدفعة المقدمة — المتبقي",
      "qt.lockedByJobCard": "معطّل — لا يجتمع مع بطاقة عمل",
      "qt.lockedByReceipt": "معطّل — لا يجتمع مع سند قبض",
      "qt.createSendHint": "يظهر منتقي طرق الدفع قبل الإرسال · يُرسل برسالة نصية أو واتساب",
      "iv.lifecycleAxis": "الدورة",
      "iv.zatcaCardTitle": "فوترة إلكترونية — ZATCA",
      "iv.zatcaQrCompliant": "رمز QR متوافق",
      "rc.band.issuedUnpaid": "صادرة غير مسددة",
      "rc.kind.standaloneCredit": "مستقل — رصيد",
      "tp.collectedTotal": "المحصّل",
      "tp.refundsPending": "استردادات بانتظار الاعتماد",
      "cm.customers.owingLead": "عليهم ذمم مستحقة",
      "cm.customers.creditLead": "لهم رصيد",
      "credit.customerCreditBalanceEmptyHint": "لا يوجد رصيد متاح — يظهر هنا أي مبلغ مدفوع مقدمًا لم يُستهلك، مع زر صرفه نقدًا لمن يملك صلاحية الاسترداد.",
      "pt.locked": "مقفلة",
      "jc.cta.sendLink": "إرسال الرابط",
      "jc.cta.addTask": "أضف مهمة",
      "jc.cta.addService": "أضف خدمة",
      "jc.timeline.downpayment": "دفعة مقدمة",
      "jc.timeline.paymentOutstanding": "الدفع متبقٍ",
      "jc.timeline.done": "تم",
      "jc.paymentMethodPicker.title": "طرق الدفع لهذا الرابط",
      "jc.transfer.action": "نقل لفرع",
      "jc.transfer.currentBranch": "الفرع الحالي",
      "jc.transfer.title": "النقل إلى فرع آخر",
      "jc.transfer.desc": "انقل أمر العمل هذا إلى فرع آخر ليتم خدمة السيارة وإكمالها هناك. تبقى السيارة وسجلها كما هي.",
      "jc.transfer.destination": "الفرع الوجهة",
      "jc.transfer.select": "اختر فرعاً…",
      "jc.transfer.confirm": "نقل",
      "jc.transfer.success": "تم نقل أمر العمل",
      "jc.transfer.failed": "فشل النقل",
      "jc.transfer.loadFailed": "فشل تحميل الفروع",
      "jc.transfer.noOther": "لا توجد فروع أخرى متاحة.",
      "parking.title": "موقف السيارة",
      "parking.current": "الموقع الحالي",
      "parking.none": "لم يتم تسجيل موقع وقوف بعد.",
      "parking.update": "تحديث الموقف",
      "parking.areaLabel": "مكان الوقوف",
      "parking.areaPlaceholder": "مثال: الحوض ٤، الساحة الخلفية صف ج",
      "parking.takePhoto": "التقط / اختر صورة",
      "parking.captureGps": "تحديد الموقع الجغرافي",
      "parking.gpsCaptured": "تم تحديد الموقع",
      "parking.gpsFailed": "تعذّر تحديد الموقع",
      "parking.viewMap": "عرض على الخريطة",
      "parking.save": "حفظ",
      "parking.saved": "تم حفظ موقع الوقوف",
      "parking.failed": "فشل الحفظ",
      "parking.areaRequired": "أدخل مكان الوقوف",
      "parking.history": "سجل التغييرات",
      "jc.reopenJobCardTitle": "إعادة فتح أمر العمل",
      "jc.reopenReasonLabel": "سبب إعادة الفتح",
      "jc.reopenReasonHint": "سيتم تسجيل هذا السبب في سجل أمر العمل",
      "jc.reopenReasonPlaceholder": "اشرح سبب الحاجة إلى إعادة فتح أمر العمل...",
      "jc.reopenReasonTypeLabel": "نوع السبب",
      "jc.reopenReasonTypeWarranty": "ضمان",
      "jc.reopenReasonTypeChargeable": "مدفوع",
      "jc.reopenIntakeModeLabel": "طريقة الاستلام",
      "jc.reopenIntakeModeDropOff": "تسليم المركبة",
      "jc.reopenIntakeModeWait": "انتظار",
      "jc.reopenConfirmationNote": "سيتم إرسال رسالة تأكيد للعميل مع رمز تحقق لخريطة الأضرار الجديدة عند تسليم المركبة.",
      "jc.reopenedBadge": "أعيد فتحه",
      "jc.reopenedBadgeWithCount": "أعيد فتحه ({{count}})",
      "jc.reopenReasonRequired": "يرجى تقديم سبب إعادة فتح أمر العمل",
      "jc.reopenReasonTypeRequired": "يرجى اختيار نوع السبب",
      "jc.reopenIntakeModeRequired": "يرجى اختيار طريقة الاستلام",
      "jc.reopenDamageMapRequired": "يرجى إضافة علامة ضرر واحدة على الأقل عند تسليم المركبة",
      "jc.jobCardReopenedSuccess": "تمت إعادة فتح أمر العمل بنجاح",
      "jc.failedToReopenJobCard": "تعذّرت إعادة فتح أمر العمل",
      "jc.reopening": "جارٍ إعادة الفتح...",
      "jc.returnCar.action": "إرجاع السيارة وإغلاق أمر العمل",
      "jc.returnCar.badge": "تم إرجاع السيارة",
      "jc.returnCar.modalTitle": "إرجاع السيارة وإغلاق أمر العمل",
      "jc.returnCar.warningIntro": "سيتم إغلاق بطاقة العمل هذه وإنهاء كل الإجراءات المعلّقة عليها. لا يمكن التراجع عن هذا من هنا.",
      "jc.returnCar.fundLotsTitle": "المبالغ المطلوب تسويتها",
      "jc.returnCar.noFundLots": "لا توجد أي مبالغ محصّلة على بطاقة العمل هذه.",
      "jc.returnCar.originOnline": "دُفع إلكترونياً",
      "jc.returnCar.originCounter": "دُفع في المركز",
      "jc.returnCar.methodUnknown": "طريقة الدفع غير معروفة",
      "jc.returnCar.reservedAgainstPosted": "مطبّق حالياً على فاتورة مرحّلة — سيتطلب إشعار دائن",
      "jc.returnCar.receiptLabel": "السند",
      "jc.returnCar.advanceLabel": "دفعة العميل المقدّمة",
      "jc.returnCar.allocationsLabel": "مطبّق على الفاتورة",
      "jc.returnCar.gatewayPaymentsLabel": "محاولات الدفع الإلكتروني (للعلم فقط)",
      "jc.returnCar.refundsLabel": "مبالغ مستردة سابقاً",
      "jc.returnCar.dispositionLabel": "ما مصير هذا المبلغ؟",
      "jc.returnCar.dispositionAddToAccount": "إضافته كرصيد لحساب العميل",
      "jc.returnCar.dispositionAddToAccountHint": "يبقى كرصيد يستخدمه العميل في مرة قادمة.",
      "jc.returnCar.dispositionPayback": "إرجاعه للعميل",
      "jc.returnCar.dispositionPaybackHint": "يُدفع نقداً أو تحويلاً بنكياً من حساب المركز — وليس استرداداً عبر بوابة الدفع الأصلية.",
      "jc.returnCar.payoutMethodLabel": "طريقة السداد",
      "jc.returnCar.payoutMethodPlaceholder": "اختر طريقة السداد…",
      "jc.returnCar.pendingActionsTitle": "سيتم إغلاقها أيضاً",
      "jc.returnCar.noPendingActions": "لا توجد أي إجراءات أخرى معلّقة.",
      "jc.returnCar.pendingQuotations": "عروض أسعار سارية",
      "jc.returnCar.pendingConfirmations": "تأكيدات معلّقة",
      "jc.returnCar.pendingPartApprovals": "طلبات اعتماد قطع معلّقة",
      "jc.returnCar.pendingServices": "خدمات مفتوحة",
      "jc.returnCar.pendingSubTasks": "مهام فرعية مفتوحة",
      "jc.returnCar.invoicesTitle": "الفواتير المتأثرة",
      "jc.returnCar.invoicesDraftNote": "سيتم حذف {{count}} فاتورة مسودة.",
      "jc.returnCar.invoicesPostedNote": "{{count}} فاتورة مرحّلة تتطلب إشعار دائن.",
      "jc.returnCar.reasonLabel": "السبب",
      "jc.returnCar.reasonPlaceholder": "مثال: رأى العميل أن السعر مرتفع ورفض المتابعة...",
      "jc.returnCar.confirmButton": "تأكيد الإلغاء",
      "jc.returnCar.confirming": "جارٍ الإلغاء...",
      "jc.returnCar.previewFailed": "تعذّر تحميل ملخص الإلغاء",
      "jc.returnCar.retry": "إعادة المحاولة",
      "jc.returnCar.installedPartsTitle": "لا يمكن استخدام هذا الإجراء",
      "jc.returnCar.installedPartsBlocked": "توجد في بطاقة العمل هذه قطع مركّبة، ولا يمكن استخدام هذا المسار.",
      "jc.returnCar.artifactsChanged": "تغيّرت المبالغ المالية في بطاقة العمل منذ فتح هذه الشاشة. راجع الملخص المحدّث وأكّد مرة أخرى.",
      "jc.returnCar.confirmFailed": "تعذّر إلغاء بطاقة العمل",
      "jc.returnCar.confirmSuccessTitle": "تم إلغاء بطاقة العمل",
      "jc.returnCar.pickupCodeIntro": "تم إرسال رمز الاستلام للعميل عبر رسالة نصية (SMS).",
      "jc.returnCar.pickupCodeSmsNote": "اطلب الرمز من العميل عند استلامه السيارة، ثم أدخله من \"التحقق من رمز الاستلام\" في أمر العمل. الرمز لا يظهر هنا — فهو ما يثبت أن المستلم هو العميل.",
      "jc.returnCar.awaitingPickupTitle": "بانتظار الاستلام",
      "jc.returnCar.awaitingPickupBody": "تم إغلاق أمر العمل. رمز الاستلام وصل للعميل عبر رسالة نصية — أدخله عند حضوره لتسجيل التسليم.",
      "jc.returnCar.verifyPickupCode": "التحقق من رمز الاستلام",
      "jc.returnCar.done": "تم",
      "jc.returnCar.blockersTitle": "لا يمكن إرجاع هذه السيارة عبر هذا المسار",
      "jc.returnCar.blockersIntro": "عالج كل بند أدناه، ثم أعد فتح هذه الشاشة وحاول مرة أخرى.",
      "jc.returnCar.blocker.RECEIPT_NOT_CUSTOMER_MONEY": "السند {{ref}} دفعته شركة التأمين وليس العميل — سوِّه مع شركة التأمين بشكل منفصل قبل إرجاع السيارة.",
      "jc.returnCar.blocker.RECEIPT_NOT_REFUNDABLE": "لا يمكن إرجاع السند {{ref}} أو إضافته كرصيد عبر هذا المسار — سوِّه يدوياً أولاً.",
      "jc.returnCar.blocker.RECEIPT_PARTLY_CONSUMED": "جزء من السند {{ref}} مطبّق بالفعل على فاتورة مرحّلة — راجع تلك الفاتورة بإشعار دائن أولاً.",
      "jc.returnCar.blocker.RECEIPT_RESERVED_ELSEWHERE": "السند {{ref}} لا يزال محجوزاً مقابل فاتورة أخرى — حرّر ذلك الحجز أولاً.",
      "jc.returnCar.blocker.RECEIPT_LEDGER_INCONSISTENT": "أرقام السند {{ref}} غير متطابقة — اطلب من المحاسبة تسويتها قبل إرجاع السيارة.",
      "jc.returnCar.blocker.INVOICE_PARTLY_PAID": "الفاتورة {{ref}} تم دفع جزء منها بالفعل — راجعها يدوياً قبل إرجاع السيارة.",
      "jc.returnCar.blocker.INVOICE_PAYMENT_UNSETTLED": "توجد دفعة مكتملة مسجّلة على الفاتورة {{ref}}، لكن الفاتورة ما زالت تُظهر أنه لم يُدفع شيء — سوِّ هذه الدفعة على الفاتورة قبل إرجاع السيارة.",
      "jc.returnCar.blocker.LOTS_NOT_DISPOSABLE": "تحتوي بطاقة العمل هذه على مبالغ لا يمكن لهذا المسار إرجاعها بأمان — راجع الأسباب أعلاه.",
      "jc.fileSizeLimit": "يجب أن يكون حجم الملف أقل من 5 ميجابايت",
      "jc.manualApprovalReasonRequired": "يرجى تقديم سبب الموافقة اليدوية",
      "jc.quotationApprovedSuccess": "تمت الموافقة على عرض السعر يدويًا بنجاح",
      "jc.quotationApprovedReceiptSuccess": "تمت الموافقة على عرض السعر — تم إصدار سند القبض وإرساله للعميل",
      "jc.failedToApproveQuotation": "تعذّرت الموافقة على عرض السعر",
      "jc.manualApproval": "الموافقة اليدوية",
      "jc.approving": "جارٍ الموافقة...",
      "jc.approveQuotation": "الموافقة على عرض السعر",
      "jc.manualApprovalWarning": "أنت على وشك الموافقة على عرض السعر هذا يدويًا. يرجى تقديم سبب وأي أدلة داعمة.",
      "jc.reasonForManualApproval": "سبب الموافقة اليدوية",
      "jc.manualApprovalReasonPlaceholder": "مثال: وافق العميل شفهيًا عبر مكالمة هاتفية...",
      "jc.evidenceOptional": "الأدلة (اختياري)",
      "jc.clickToUploadFile": "اضغط لرفع ملف (بحد أقصى 5 ميجابايت)",
      "jc.allowedFileTypes": "صور أو ملفات PDF أو مستندات Word",
      "jc.removeFile": "إزالة الملف",
      "jc.addDescriptionFirst": "يرجى إضافة وصف أولًا",
      "jc.identifiedDuringDiagnosis": "تم اكتشافه أثناء التشخيص: {{description}}",
      "jc.taskAddedToList": "تمت إضافة المهمة إلى قائمة الإنشاء",
      "jc.addAtLeastOneFinding": "يرجى إضافة نتيجة واحدة على الأقل",
      "jc.vehicleDiagnosis": "تشخيص المركبة",
      "jc.completeDiagnosis": "إكمال التشخيص",
      "jc.overallDiagnosisNotes": "ملاحظات التشخيص العامة",
      "jc.diagnosisSummaryPlaceholder": "أدخل ملخص التشخيص العام...",
      "jc.findingsAndIssues": "النتائج والمشكلات",
      "jc.addFinding": "إضافة نتيجة",
      "jc.lowPriority": "أولوية منخفضة",
      "jc.mediumPriority": "أولوية متوسطة",
      "jc.highPriority": "أولوية عالية",
      "jc.findingPlaceholder": "صف النتيجة أو المشكلة...",
      "jc.createTaskFromFinding": "+ إنشاء مهمة من هذه النتيجة",
      "jc.tasksToCreate": "المهام المراد إنشاؤها ({{count}})",
      "jc.priorityLabel": "الأولوية: {{priority}}",
      "jc.payerType": "الدافع",
      "jc.payerCustomer": "العميل",
      "jc.payerInsurance": "التأمين",
      "jc.insurer": "شركة التأمين",
      "jc.selectInsurer": "اختر شركة التأمين",
      "jc.selectInsurerRequired": "يرجى اختيار شركة التأمين",
      "jc.noInsurers": "لا توجد شركات تأمين مفعلة",
      "jc.photosUploaded": "تم رفع {{count}} صورة",
      "jc.failedToUploadPhotos": "تعذّر رفع الصور",
      "jc.qcPassedToast": "تم اجتياز فحص الجودة",
      "jc.qcFailedToast": "لم يجتز فحص الجودة - تمت إعادة فتح المهمة لإعادة العمل",
      "jc.qcRework.notice": "فشل فحص الجودة — أعد العمل ثم أكّد إنجاز الإصلاح.",
      "jc.qcRework.done": "تم إنجاز الإصلاح",
      "jc.failedToSubmitQc": "تعذّر إرسال فحص الجودة",
      "jc.submitPass": "إرسال النجاح",
      "jc.submitFail": "إرسال الإخفاق",
      "jc.qcResult": "نتيجة فحص الجودة",
      "jc.qcFailNotesPlaceholder": "صف ما يجب إصلاحه...",
      "jc.qcPassNotesPlaceholder": "أضف أي ملاحظات أو تعليقات...",
      "jc.photosOptional": "الصور (اختياري)",
      "jc.qcPhotoAlt": "صورة فحص الجودة {{number}}",
      "jc.addPhotos": "إضافة صور",
      "jc.failedToLoadUsers": "تعذّر تحميل المستخدمين",
      "jc.failedToLoadAssignments": "تعذّر تحميل المستخدمين المسندين",
      "jc.userAssignedSuccess": "تم تعيين المستخدم بنجاح",
      "jc.failedToAssignUser": "تعذّر تعيين المستخدم",
      "jc.userRemovedSuccess": "تمت إزالة المستخدم بنجاح",
      "jc.failedToRemoveUser": "تعذّرت إزالة المستخدم",
      "jc.currentlyAssigned": "المعيّنون حاليًا ({{count}})",
      "jc.searchUsersToAdd": "ابحث عن مستخدمين لإضافتهم...",
      "jc.allUsersAssigned": "تم تعيين جميع المستخدمين",
      "jc.noUsersFound": "لا يوجد مستخدمون",
      "jc.failedToLoadComments": "تعذّر تحميل التعليقات",
      "jc.failedToAddComment": "تعذّرت إضافة التعليق",
      "jc.commentTypeDiagnosis": "تشخيص",
      "jc.commentTypeStatusUpdate": "تحديث الحالة",
      "jc.commentTypeComment": "تعليق",
      "jc.justNow": "الآن",
      "jc.minutesAgo": "قبل {{count}} د",
      "jc.hoursAgo": "قبل {{count}} س",
      "jc.daysAgo": "قبل {{count}} ي",
      "jc.conversationAndUpdates": "المحادثة والتحديثات",
      "jc.collaborateWithTeam": "تعاون مع فريقك على بطاقة العمل هذه",
      "jc.loadingComments": "جارٍ تحميل التعليقات...",
      "jc.noCommentsYet": "لا توجد تعليقات بعد",
      "jc.startConversation": "ابدأ المحادثة أدناه",
      "jc.commentType": "نوع التعليق",
      "jc.writeComment": "اكتب تعليقًا...",
      "jc.sending": "جارٍ الإرسال...",
      "jc.issueResolvedSuccess": "تم وضع علامة على المشكلة كمحلولة",
      "req.navTitle": "الطلبات",
      "req.subtitle": "بلاغات المشاكل وطلبات المساعدة من الفريق بانتظارك",
      "req.kindCol": "النوع",
      "req.kind.assistance": "مساعدة",
      "req.kind.task": "مشكلة في مهمة",
      "req.kind.service": "مشكلة في خدمة",
      "req.kind.part": "مشكلة في قطعة",
      "req.task": "المهمة",
      "req.emptyTitle": "لا توجد طلبات بانتظارك",
      "req.emptyDescription": "ستظهر هنا بلاغات المشاكل وطلبات المساعدة المفتوحة من الفريق.",
      "req.resolveModalTitle": "إنهاء الطلب",
      "status.open": "مفتوح",
      "jc.issueResolveFailed": "تعذّر حل المشكلة",
      "jc.andMore": "+{{count}} المزيد",
      "jc.issueEvidenceAlt": "صورة توضيحية للمشكلة",
      "jc.partApprovedStockDeducted": "تمت الموافقة على القطعة وخصمها من المخزون",
      "jc.failedToApprovePart": "تعذّرت الموافقة على القطعة",
      "jc.partRequestRejected": "تم رفض طلب القطعة",
      "jc.failedToRejectPart": "تعذّر رفض القطعة",
      "jc.pendingPartApprovals": "موافقات القطع المعلقة",
      "jc.reviewApprovePartRequests": "مراجعة طلبات القطع من الفنيين والموافقة عليها",
      "jc.failedToLoadPartApprovals": "تعذّر تحميل موافقات القطع المعلقة",
      "jc.requestedByLabel": "مقدم الطلب:",
      "jc.partNumberLabel": "رقم القطعة:",
      "jc.quantityLabel": "الكمية:",
      "jc.unitPriceLabel": "سعر الوحدة:",
      "jc.requestedAtLabel": "وقت الطلب:",
      "jc.currentStockLabel": "المخزون الحالي:",
      "jc.unitsCount": "{{count}} وحدة",
      "jc.approveAndDeductStock": "موافقة وخصم من المخزون",
      "pa.confirmApproveTitle": "اعتماد القطعة وخصم المخزون؟",
      "pa.confirmApproveMessage": "سيتم اعتماد القطعة المطلوبة وخصمها من المخزون فوراً.",
      "jc.insufficientStockCannotApprove": "المخزون غير كافٍ! لا يمكن الموافقة على هذا الطلب.",
      "jc.failedToLoadJobs": "تعذّر تحميل المهام",
      "jc.oneSelected": "تم تحديد عنصر واحد",
      "jc.warningLightsSelectedCount": "تم تحديد {{count}} من أضواء التحذير",
      "jc.clearAll": "مسح الكل",
      "jc.searchWarningLights": "ابحث في أضواء التحذير...",
      "jc.noWarningLightsMatching": "لا توجد أضواء تحذير مطابقة لـ \"{{query}}\"",
      "jc.selectedCount": "تم تحديد {{count}}",
      "jc.photosAdded": "تمت إضافة {{count}} صورة",
      "jc.failedToAddPhotos": "تعذّرت إضافة الصور",
      "jc.interior": "الداخلية",
      "jc.exterior": "الخارجية",
      "jc.photo": "صورة",
      "jc.video": "فيديو",
      "jc.mediaAddedSuccess": "تمت إضافة {{media}} {{location}} بنجاح! ({{count}})",
      "jc.failedToAddMedia": "تعذّرت إضافة {{media}}",
      "jc.mileagePlaceholder": "مثال: 45000",
      "jc.mileageBelowLast": "أقل من آخر قراءة مسجلة لدينا: {{km}} كم. يرجى التأكد من عداد المسافة.",
      "jc.mileageLastSeenOn": "سُجّلت في {{job}} بتاريخ {{date}}.",
      "jc.mileageLastSeenDate": "سُجّلت بتاريخ {{date}}.",
      "pt.bulkUploadSuccess": "تمت معالجة {{created}} قطعة جديدة وتحديث {{updated}} قطعة موجودة بنجاح",
      "pt.bulkUploadErrors": "حدث {{count}} خطأ أثناء الرفع",
      "pt.purchaseInvoiceCreated": "تم إنشاء فاتورة الشراء بنجاح",
      "pt.purchaseHistory": "سجل المشتريات",
      "pt.rv.held": "محجوز",
      "pt.rv.free": "متاح",
      "pt.rv.onHand": "بالمخزن",
      "pt.rv.title": "من يحجز هذه القطعة",
      "pt.rv.document": "المستند",
      "pt.rv.heldQty": "المحجوز",
      "pt.rv.state": "الحالة",
      "pt.rv.totalHeld": "إجمالي المحجوز",
      "pt.rv.none": "لا يوجد حجز على هذه القطعة.",
      "pt.rv.kind.job_card": "أمر عمل",
      "pt.rv.kind.quotation": "عرض سعر",
      "pt.rv.kind.invoice": "فاتورة مسودة",
      "pt.rv.kind.part_request": "طلب قطعة",
      "pt.rv.blockedTitle": "هذه القطعة محجوزة",
      "pt.rv.blockedBody": "{{name}} — {{onHand}} بالمخزن، {{reserved}} محجوز لمستند آخر، {{available}} متاح. راجع صاحب الحجز أو اطلب من المدير التجاوز.",
      "pt.rv.overrideTitle": "استخدام قطعة محجوزة لمستند آخر؟",
      "pt.rv.overrideBody": "{{name}} — {{available}} متاح فقط من {{onHand}} بالمخزن. أخذها قد ينقص المستند الآخر.",
      "pt.rv.overrideConfirm": "استخدمها على أي حال",
      "pt.rv.overrideCancel": "اختر قطعة أخرى",
      "pt.rv.availableHeader": "المتاح",
      "pt.dt.pricing": "التسعير",
      "pt.dt.lastPurchase": "آخر شراء",
      "pt.dt.lastRestocked": "آخر تعبئة",
      "pt.sh.title": "سجل حركة المخزون",
      "pt.sh.movement": "الحركة",
      "pt.sh.balanceAfter": "الرصيد بعد الحركة",
      "pt.sh.source": "المصدر",
      "pt.sh.performedBy": "بواسطة",
      "pt.sh.totalIn": "إجمالي الوارد",
      "pt.sh.totalOut": "إجمالي المنصرف",
      "pt.sh.onHand": "الرصيد الحالي",
      "pt.sh.empty": "لا توجد حركات على هذه القطعة",
      "pt.sh.emptyHint": "كل استلام وصرف وتحويل وتصحيح لهذه القطعة سيظهر هنا.",
      "pt.sh.failedToLoad": "تعذّر تحميل سجل حركة المخزون لهذه القطعة.",
      "pt.sh.type.in": "استلام",
      "pt.sh.type.out": "صرف",
      "pt.sh.type.return": "إرجاع",
      "pt.sh.type.adjustment": "تصحيح",
      "pt.sh.type.transfer": "تحويل صادر",
      "pt.sh.type.damage": "تالف",
      "pt.sh.type.initial": "رصيد افتتاحي",
      "pt.sh.ref.bulk_upload": "رفع جماعي",
      "pt.sh.ref.manual": "إدخال يدوي",
      "pt.sh.ref.manual_adjustment": "تصحيح يدوي",
      "pt.sh.ref.stock_transfer": "تحويل بين الفروع",
      "pt.uncategorized": "غير مصنّف",
      "pt.universal": "عام",
      "pt.upload": "رفع",
      "pt.purchase": "شراء",
      "pt.partUpdatedSuccessfully": "تم تحديث القطعة بنجاح",
      "pt.failedToUpdatePart": "فشل تحديث القطعة",
      "pt.skuReadOnly": "‏SKU: {{sku}} (للقراءة فقط)",
      "pt.compatibilityPlaceholder": "مثال: تويوتا كامري 2020-2025",
      "pt.lowStockThreshold": "حد المخزون المنخفض",
      "pt.addToPurchase": "أضف للشراء",
      "pt.inventoryValue": "قيمة المخزون",
      "pt.itemsWord": "صنف",
      "pt.descriptionAr": "الوصف بالعربية",
      "jc.delayFeeGateUnavailable": "تعذّر التحقق من الرسوم المستحقة — لم يتم تسليم المركبة. الرجاء المحاولة مرة أخرى.",
      "pt.stockLimit": "حد",
      "staff.myWork.workingNow": "تعمل عليها الآن",
      "staff.myWork.continue": "متابعة المهمة",
      "staff.myWork.voiceSearch": "بحث صوتي",
      "pt.lowStockThresholdHint": "تنبيه عند انخفاض المخزون عن هذا الرقم",
      "pt.partDescriptionPlaceholder": "وصف القطعة...",
      "pt.confirmRemovePart": "هل أنت متأكد من رغبتك في إزالة هذه القطعة؟",
      "pt.partRemoved": "تمت إزالة القطعة",
      "pt.failedToRemovePart": "فشلت إزالة القطعة",
      "pt.listedPart": "قطعة مدرجة",
      "pt.manualPart": "قطعة يدوية",
      "pt.selectPart": "اختر القطعة",
      "pt.choosePart": "اختر قطعة...",
      "pt.insufficientStock": "تحذير: المخزون غير كافٍ ({{count}} متوفر)",
      "pt.partNamePlaceholder": "مثال: أنبوب خاص",
      "pt.addAnother": "إضافة أخرى",
      "pt.adding": "جارٍ الإضافة...",
      "pt.part": "القطعة",
      "pt.unknownPart": "قطعة غير معروفة",
      "pt.fit": "التوافق",
      "pt.unknown": "غير معروف",
      "pt.failedToLoadParts": "فشل تحميل القطع",
      "pt.partRequestSubmitted": "تم إرسال طلب القطعة - سيقوم المسؤول بالمراجعة والموافقة",
      "pt.addPartToTask": "إضافة قطعة إلى المهمة",
      "pt.useCustomPart": "استخدام قطعة مخصصة",
      "pt.partNameBrakePadPlaceholder": "مثال: تيل فرامل",
      "pt.costPriceSAR": "سعر التكلفة (ريال)",
      "pt.internalLabel": "(داخلي)",
      "pt.profitPerUnit": "الربح لكل وحدة:",
      "pt.partProvidedByCustomer": "القطعة مقدّمة من العميل",
      "pt.customerProvidedExplainer": "بدون سعر، وبدون تكلفة، ولا يُخصم من المخزون — ولا تحتاج موافقة العميل.",
      "pt.customerFacingLabel": "تظهر في عرض السعر",
      "pt.searchByPartNameOrNumber": "ابحث باسم القطعة أو رقمها...",
      "pt.selectAPart": "اختر قطعة",
      "pt.unnamedPart": "قطعة بدون اسم",
      "pt.showingCount": "عرض {{shown}} من {{total}} قطعة",
      "pt.additionalNotesPlaceholder": "ملاحظات إضافية...",
      "pt.enterValidQuantity": "الرجاء إدخال كمية صحيحة",
      "pt.restockSuccess": "تمت إضافة {{quantity}} وحدة إلى المخزون بنجاح",
      "pt.failedToRestockPart": "فشلت إعادة تخزين القطعة",
      "pt.enterQuantity": "أدخل الكمية",
      "pt.restockNotesPlaceholder": "أضف ملاحظات حول إعادة التخزين هذه...",
      "pt.newStock": "المخزون الجديد",
      "pt.failedToUpdateStock": "فشل تحديث المخزون",
      "pt.stockMovement": "حركة المخزون",
      "pt.minimumLevel": "الحد الأدنى",
      "pt.movementType": "نوع الحركة",
      "pt.removeStock": "سحب من المخزون",
      "pt.adjustStock": "تعديل المخزون",
      "pt.newQuantity": "الكمية الجديدة",
      "pt.enterNewTotalQuantity": "أدخل إجمالي الكمية الجديد",
      "pt.newStockWillBe": "سيصبح المخزون",
      "pt.unitCostOptional": "تكلفة الوحدة (اختياري)",
      "pt.reasonPlaceholder": "مثال: استلام شحنة، أصناف تالفة، تصحيح جرد المخزون",
      "pt.invoiceFileUploaded": "تم رفع ملف الفاتورة بنجاح",
      "pt.failedToUploadInvoiceFile": "فشل رفع ملف الفاتورة",
      "pt.partFound": "تم العثور على القطعة: {{name}}",
      "pt.partNotFound": "لم يتم العثور على القطعة. يمكنك إنشاء قطعة جديدة.",
      "pt.failedToScanBarcode": "فشل مسح الباركود",
      "pt.pleaseAddAtLeastOneItem": "يرجى إضافة صنف واحد على الأقل",
      "pt.itemSelectPartOrNew": "الصنف {{n}}: يرجى اختيار قطعة أو تحديد \"قطعة جديدة\"",
      "pt.itemPartNumberNameRequired": "الصنف {{n}}: رقم القطعة والاسم مطلوبان للقطع الجديدة",
      "pt.itemQuantityGreaterThanZero": "الصنف {{n}}: يجب أن تكون الكمية أكبر من 0",
      "pt.itemUnitCostGreaterThanZero": "الصنف {{n}}: يجب أن تكون تكلفة الوحدة أكبر من 0",
      "pt.failedToCreatePurchaseInvoice": "فشل إنشاء فاتورة الشراء",
      "pt.stock": "المخزون",
      "pt.current": "الحالي",
      "pt.failedToLoadCategories": "فشل تحميل الفئات",
      "pt.categoryNameRequired": "اسم الفئة مطلوب",
      "pt.categoryUpdated": "تم تحديث الفئة بنجاح",
      "pt.categoryCreated": "تم إنشاء الفئة بنجاح",
      "pt.failedToSaveCategory": "فشل حفظ الفئة",
      "pt.confirmDeleteCategory": "هل أنت متأكد من حذف هذه الفئة؟",
      "pt.categoryDeleted": "تم حذف الفئة بنجاح",
      "pt.failedToDeleteCategory": "فشل حذف الفئة",
      "pt.cannotEditGlobalCategories": "لا يمكن تعديل الفئات العامة",
      "pt.categoryNamePlaceholder": "مثال: قطع التعليق",
      "pt.categoryUnit": "وحدة القياس",
      "pt.categoryUnitHint": "كل قطعة تُنشأ في هذه الفئة تُقاس بهذه الوحدة. اللتر والمليلتر والكيلوغرام تقبل الكسور العشرية، أما القطعة والطقم فأرقام صحيحة فقط.",
      "pt.categoryUnitAllRestamped": "طُبِّقت الوحدة على جميع القطع ({{restamped}} قطعة).",
      "pt.categoryUnitRestampedWithSkipped": "طُبِّقت الوحدة على {{restamped}} قطعة. تُركت {{skipped}} قطعة لديها رصيد في المخزون دون تغيير — صفّر رصيدها ثم أعد حفظ الفئة لتحديثها.",
      "pt.categoryUnitSkippedOnly": "لا تزال {{skipped}} قطعة لديها رصيد في المخزون على الوحدة السابقة — صفّر رصيدها ثم أعد حفظ الفئة لتحديثها.",
      "pt.unit.piece": "قطعة",
      "gc.title": "فئات القطع",
      "gc.subtitle": "الكتالوج العام الذي يستخدمه كل مركز خدمات سيارات على المنصة. الصف الواحد هنا تقرأه كل المراكز.",
      "gc.search": "ابحث عن فئة",
      "gc.add": "إضافة فئة",
      "gc.editTitle": "تعديل الفئة",
      "gc.none": "لا توجد فئات مطابقة للبحث.",
      "gc.name": "الاسم (إنجليزي)",
      "gc.nameAr": "الاسم (عربي)",
      "gc.description": "الوصف (إنجليزي)",
      "gc.descriptionAr": "الوصف (عربي)",
      "gc.unit": "وحدة القياس",
      "gc.unitHint": "الوحدة التي ترثها كل قطعة جديدة تحت هذه الفئة. القطع الموجودة تحتفظ بالوحدة التي جُردت بها. يُرفض التغيير ما دام أي مركز يحتفظ برصيد بالوحدة القديمة.",
      "gc.usageParts": "{{count}} قطعة",
      "gc.usageTenants": "{{count}} مركز",
      "gc.usageWithStock": "{{count}} لديها رصيد",
      "gc.retire": "إيقاف",
      "gc.retiredBadge": "موقوفة",
      "gc.restore": "إعادة تفعيل",
      "gc.restored": "تمت إعادة تفعيل الفئة",
      "gc.saved": "تم حفظ الفئة",
      "gc.saveFailed": "تعذّر حفظ الفئة",
      "gc.loadFailed": "تعذّر تحميل الفئات العامة",
      "gc.retired": "تم إيقاف الفئة",
      "gc.retireFailed": "تعذّر إيقاف الفئة",
      "gc.confirmRetireTitle": "إيقاف هذه الفئة؟",
      "gc.confirmRetireMsg": "«{{name}}» لن تظهر بعد الآن في قائمة الفئات لدى مراكز خدمات السيارات. ما هو مسجّل تحتها لا يتأثر، ويمكن إعادتها لاحقًا.",
      "pt.unit.L": "لتر (L)",
      "pt.unit.mL": "مليلتر (mL)",
      "pt.unit.kg": "كيلوغرام (kg)",
      "pt.unit.set": "طقم",
      "pt.unit.roll": "لفة",
      "pt.unitShort.piece": "قطعة",
      "pt.inStockHint": "المتوفر: {{qty}}",
      "pt.unitShort.L": "لتر",
      "pt.unitShort.mL": "مل",
      "pt.unitShort.kg": "كجم",
      "pt.unitShort.set": "طقم",
      "pt.unitShort.roll": "لفة",
      "pt.categoryNameArabicPlaceholder": "مثال: قطع التعليق",
      "pt.optionalDescription": "وصف اختياري",
      "pt.failedToFetchPurchaseInvoices": "فشل جلب فواتير الشراء",
      "pt.failedToPostInvoice": "فشل ترحيل الفاتورة",
      "pt.accepted": "مقبول",
      "pt.ordered": "تم الطلب",
      "pt.autoApprovedQuickService": "خدمة سريعة معتمدة تلقائياً",
      "pt.viewQuotation": "عرض عرض السعر",
      "pt.acceptPart": "قبول القطعة",
      "pt.rejectPart": "رفض القطعة",
      "pt.waitingForCustomerApprovalShort": "في انتظار موافقة العميل",
      "pt.enterEditModeToRemoveItems": "ادخل وضع التعديل لإزالة العناصر",
      "pt.searchByNamePartNoSku": "البحث بالاسم أو رقم القطعة أو SKU...",
      "pt.partNoLabel": "رقم: {{number}}",
      "pt.categoryLabel": "الفئة: {{category}}",
      "pt.stockLabel": "المخزون: {{count}}",
      "pt.compatLabel": "التوافق: {{value}}",
      "pt.request": "طلب",
      "pt.noPartsFoundShort": "لم يتم العثور على قطع",
      "pt.failedToLoadPurchaseHistory": "تعذر تحميل سجل المشتريات",
      "pt.partsRequestedSuccess": "تم طلب {{count}} قطعة بنجاح",
      "pt.failedToRequestParts": "تعذر طلب القطع",
      "pt.partsSelected": "تم تحديد {{count}} قطعة",
      "pt.sarEach": "{{price}} ريال للقطعة",
      "pt.requesting": "جارٍ الطلب...",
      "pt.requestPartsWithTotal": "طلب {{count}} قطعة ({{total}} ريال)",
      "pt.searchPartsPlaceholder": "ابحث عن القطع...",
      "pt.priceSetByAdmin": "سيتم تحديد السعر من قبل المسؤول بعد المراجعة",
      "pt.inStockCount": "{{count}} في المخزون",
      "pt.failedToLoadServiceTypes": "تعذّر تحميل أنواع الخدمات",
      "pt.serviceAddedSuccessfully": "تمت إضافة الخدمة بنجاح",
      "pt.failedToAddService": "تعذّرت إضافة الخدمة",
      "pt.addServiceToTask": "إضافة خدمة إلى المهمة",
      "pt.useCustomService": "استخدام خدمة مخصّصة",
      "pt.egOilChange": "مثال: تغيير الزيت",
      "pt.sellingPriceSAR": "سعر البيع (ريال)",
      "pt.inStock": "المخزون المتوفر",
      "pt.avgCost": "متوسط التكلفة",
      "pt.sellingPrice": "سعر البيع",
      "pt.newAvgCost": "متوسط التكلفة الجديد",
      "pt.projectedMargin": "هامش الربح المتوقع",
      "pt.priceWillUpdate": "سيتم تحديث السعر عند الحفظ",
      "pt.supplierRequired": "اختر المورّد الذي تم شراء هذا المخزون منه.",
      "pt.newPartNoPrice": "لا يوجد سعر بيع — سيتم إدخال القطعة إلى المخزون ولكن لا يمكن بيعها حتى تحدد سعرها.",
      "pt.sellingPriceUpdated": "تم تحديث سعر/أسعار البيع",
      "pt.failedToUpdatePrice": "فشل تحديث سعر بيع واحد أو أكثر",
      "pt.cataloguePriceNotUpdated": "لم يتغير سعر البيع في قائمة الشركة. اطلب من مسؤول القائمة تحديثه.",
      "aidx.title": "مساعد التشخيص الذكي",
      "aidx.subtitle": "مساعد ذكي يساعد في تشخيص بطاقات العمل",
      "aidx.enable": "تفعيل التشخيص الذكي",
      "aidx.enableDesc": "السماح للموظفين باستشارة المساعد الذكي من بطاقات العمل.",
      "aidx.allowMechanics": "السماح للفنيين",
      "aidx.allowMechanicsDesc": "السماح للفنيين باستخدام المساعد في المركز.",
      "aidx.webSearch": "البحث في الإنترنت افتراضياً",
      "aidx.webSearchDesc": "تفعيل البحث عن الاستدعاءات/النشرات الفنية افتراضياً للاستعلامات الجديدة.",
      "aidx.saved": "تم حفظ إعدادات التشخيص الذكي",
      "aidx.saveFailed": "فشل حفظ إعدادات التشخيص الذكي",
      "aidx.usageNearLimit": "اقتربت من الحد الشهري ({{pct}}٪ مستخدم).",
      "aidx.agentUsage": "الوكيل الذكي: {{used}} من {{limit}} هذا الشهر",
      "aidx.voiceUsage": "الصوت الذكي: {{used}} من {{limit}} هذا الشهر",
      "aidx.agentUsageUnlimited": "الوكيل الذكي: {{used}} هذا الشهر · بلا حد",
      "aidx.agentUsageBlocked": "الوكيل الذكي: موقوف",
      "aidx.voiceUsageUnlimited": "الصوت الذكي: {{used}} هذا الشهر · بلا حد",
      "aidx.voiceUsageBlocked": "الصوت الذكي: موقوف",
      "aidx.limitsSetByMotorMind": "يتم تحديد الحدود من قبل موتورمايند.",
      "insuranceJobs.title": "أعمال التأمين",
      "insuranceJobs.subtitle": "السماح بسداد بطاقات العمل عبر شركة تأمين (مطالبات وفواتير بين الشركات)",
      "insuranceJobs.enable": "تفعيل أعمال التأمين",
      "insuranceJobs.enableDesc": "السماح بسداد بطاقات العمل عبر شركة تأمين (مطالبات وفواتير بين الشركات).",
      "insuranceJobs.saved": "تم حفظ إعدادات أعمال التأمين",
      "insuranceJobs.saveFailed": "فشل حفظ إعدادات أعمال التأمين",
      "pt.internal": "(داخلي)",
      "pt.serviceType": "نوع الخدمة",
      "pt.selectAService": "اختر خدمة",
      "pt.serviceRequestSent": "تم إرسال طلب الخدمة",
      "pt.failedToRequestService": "تعذّر إرسال طلب الخدمة",
      "pt.cannotRemoveServiceRequest": "لا يمكن إزالة طلب الخدمة بعد إرساله. اطلب من المسؤول رفضه أو إزالته.",
      "pt.requestService": "طلب خدمة",
      "pt.selectService": "اختر الخدمة",
      "pt.chooseAService": "اختر خدمة...",
      "pt.reasonForRequest": "سبب الطلب...",
      "pt.sending": "جارٍ الإرسال...",
      "pt.noServicesAssociated": "لا توجد خدمات مرتبطة بهذا العمل.",
      "pt.unnamedService": "خدمة بدون اسم",
      "pt.quotationNumber": "عرض السعر رقم {{number}}",
      "pt.enterEditModeToRemove": "ادخل وضع التعديل لإزالة العناصر",
      "pt.manageStandardServices": "إدارة الخدمات القياسية والأسعار",
      "pt.searchServices": "ابحث في الخدمات...",
      "pt.noServicesFound": "لا توجد خدمات.",
      "pt.updateService": "تحديث الخدمة",
      "tm.partsOptional": "قطع الغيار (اختياري)",
      "tm.servicesOptional": "الخدمات (اختياري)",
      "tm.addPartRow": "+ إضافة قطعة",
      "tm.addServiceRow": "+ إضافة خدمة",
      "tm.partName": "اسم القطعة",
      "tm.serviceName": "اسم الخدمة",
      "tm.quantity": "الكمية",
      "tm.unitPriceSAR": "سعر الوحدة (ريال)",
      "tm.taskCreatedFor": "تم إنشاء المهمة: {{name}}",
      "tm.itemsAttachFailed": "{{count}} عنصر لم تتم إضافته: {{names}}",
      "tm.retryFailedItems": "إعادة محاولة العناصر الفاشلة",
      "tm.discardFailedItemsConfirm": "تم إنشاء المهمة، لكن بعض العناصر لم تُضف بعد. إغلاق وتجاهلها؟",
      "qt.loadDataFailed": "فشل تحميل البيانات",
      "qt.loadJobCardFailed": "فشل تحميل تفاصيل بطاقة العمل",
      "qt.pleaseSelectCustomer": "يرجى اختيار عميل",
      "qt.pleaseAddLineItem": "يرجى إضافة بند واحد على الأقل",
      "qt.pleaseFixDownpayment": "يرجى تصحيح مبلغ العربون قبل المتابعة.",
      "qt.itemRejected": "مرفوض",
      "qt.itemRejectedBecause": "سبب العميل: {{reason}}",
      "qt.itemAccepted": "مقبول",
      "qt.createSuccess": "تم إنشاء عرض السعر بنجاح",
      "qt.createFailed": "فشل إنشاء عرض السعر",
      "qt.selectJobCardOptional": "اختر بطاقة العمل (اختياري)",
      "qt.autoFilledFromJobCard": "تمت التعبئة تلقائيًا من بطاقة العمل",
      "qt.enterDetailsManually": "أدخل تفاصيل عرض السعر يدويًا",
      "qt.manualEntryNoJobCard": "-- إدخال يدوي (بدون بطاقة عمل) --",
      "qt.loadingCustomer": "جاري تحميل العميل...",
      "qt.vehicleOptional": "المركبة (اختياري)",
      "qt.loadingVehicle": "جاري تحميل المركبة...",
      "qt.noVehicleSelected": "لم يتم اختيار مركبة",
      "qt.unitPriceSar": "سعر الوحدة (ريال)",
      "qt.taxRate": "نسبة الضريبة",
      "qt.taxAmount": "مبلغ الضريبة",
      "qt.additionalNotesPlaceholder": "ملاحظات إضافية...",
      "qt.creatingEllipsis": "جاري الإنشاء...",
      "qt.filterAwaitingCustomer": "بانتظار العميل",
      "qt.filterExpiring24h": "ينتهي خلال ٢٤ ساعة",
      "qt.filterApprovedNotConverted": "معتمدة لم تُحوّل",
      "qt.liveSummary": "الملخص المباشر",
      "qt.createAndChoosePayment": "إنشاء وإرسال — اختيار طرق الدفع",
      "qt.pdfDownloadFailed": "فشل تنزيل ملف PDF",
      "qt.resendFailed": "فشل إعادة إرسال عرض السعر",
      "qt.resendSms": "إعادة إرسال الرسالة النصية",
      "qt.smsSent": "تم إرسال عرض السعر برسالة نصية",
      "qt.sendSmsFailed": "فشل إرسال الرسالة النصية",
      "qt.convertToInvoice": "تحويل إلى فاتورة",
      "qt.convertSuccess": "تم إنشاء مسودة الفاتورة",
      "qt.convertedTo": "تم التحويل ←",
      "qt.convertErrNotApproved": "يمكن تحويل عروض السعر المعتمدة فقط",
      "qt.convertErrAlreadyConverted": "تم تحويل عرض السعر هذا مسبقًا",
      "qt.convertErrNoItems": "لا يحتوي عرض السعر على بنود للتحويل",
      "qt.convertBlockedJobCard": "يجب إكمال إجراءات بطاقة العمل لإصدار الفاتورة من بطاقة العمل",
      "qt.convertErrNoPermission": "ليست لديك صلاحية إنشاء الفواتير",
      "qt.itemType": "نوع البند",
      "qt.itemTypePart": "قطعة",
      "qt.itemTypeService": "خدمة",
      "jc.linkQuotation.action": "ربط عرض سعر معتمد",
      "jc.linkQuotation.title": "ربط عرض سعر معتمد",
      "jc.linkQuotation.hint": "اختر عرض سعر وافق عليه العميل قبل استلام السيارة. ستُضاف البنود المقبولة إلى بطاقة العمل.",
      "jc.linkQuotation.linkAction": "ربط ببطاقة العمل",
      "jc.linkQuotation.empty": "لا توجد عروض أسعار معتمدة غير مرتبطة",
      "jc.linkQuotation.success": "تم ربط عرض السعر — أُضيفت البنود المقبولة إلى بطاقة العمل",
      "jc.linkQuotation.failed": "فشل ربط عرض السعر",
      "jc.linkQuotation.loadFailed": "فشل تحميل عروض الأسعار",
      "jc.linkQuotation.blockedOwnQuotation": "بطاقة العمل لديها عرض سعر خاص بها بالفعل",
      "jc.cta.startWork": "بدء الإصلاح",
      "jc.cta.startInspection": "بدء الفحص",
      "inspection.state.pass": "سليم",
      "inspection.state.monitor": "يحتاج متابعة",
      "inspection.state.fail": "يحتاج إصلاح",
      "inspection.state.failBinary": "غير سليم",
      "inspection.state.na": "لم يُفحص",
      "inspection.answer.yes": "نعم",
      "inspection.answer.no": "لا",
      "inspection.title": "فحص شامل",
      "inspection.progress": "النقاط المُجابة",
      "inspection.prevSection": "القسم السابق",
      "inspection.nextSection": "القسم التالي",
      "inspection.note": "ملاحظة",
      "inspection.notePlaceholder": "أي ملاحظة يحتاج العميل معرفتها",
      "inspection.finish": "إنهاء الفحص",
      "inspection.finishEmpty": "أجب عن نقطة واحدة على الأقل قبل الإنهاء.",
      "inspection.finishRemaining": "{{count}} نقطة بدون إجابة — ستُسجَّل «لم تُفحص».",
      "inspection.completed": "تم إنهاء الفحص",
      "inspection.completeFailed": "تعذّر إنهاء الفحص",
      "inspection.loadFailed": "تعذّر تحميل الفحص",
      "inspection.startFailed": "تعذر بدء الفحص.",
      "inspection.issueFailed": "تعذر إصدار التقرير.",
      "inspection.saveFailed": "لم تُحفظ الإجابة — حاول مرة أخرى",
      "inspection.state.none": "بدون إجابة",
      "inspection.verdict.pass": "صالحة للطريق",
      "inspection.verdict.monitor": "تحتاج متابعة",
      "inspection.verdict.fail": "تحتاج إصلاح",
      "inspection.verdict.none": "بدون نتيجة",
      "inspection.summaryTitle": "ملخص الفحص",
      "inspection.summaryCounts": "{{fail}} تحتاج إصلاح · {{monitor}} تحتاج متابعة · {{pass}} سليمة",
      "inspection.summaryNotChecked": "{{count}} لم تُفحص",
      "inspection.verdictProvisional": "مبدئية",
      "inspection.vin": "رقم الهيكل",
      "inspection.registerTitle": "الفحص الشامل",
      "inspection.tpl.title": "قوالب قائمة الفحص",
      "inspection.tpl.subtitle": "اختر النقاط التي يسألها الفحص الشامل لديك.",
      "inspection.tpl.new": "قالب جديد",
      "inspection.tpl.edit": "تعديل القالب",
      "inspection.tpl.mine": "قوالبك",
      "inspection.tpl.builtIn": "قوائم الفحص الجاهزة",
      "inspection.tpl.builtInHint": "مقدَّمة من المنصة. أنشئ قالبك الخاص انطلاقًا من إحداها لتغيير النقاط التي يسألها.",
      "inspection.tpl.readOnly": "للقراءة فقط",
      "inspection.tpl.nameAr": "الاسم (بالعربية)",
      "inspection.tpl.nameEn": "الاسم (بالإنجليزية)",
      "inspection.tpl.basedOn": "مبني على",
      "inspection.tpl.basedOnHint": "قائمة الفحص التي تُؤخذ منها النقاط. لا يمكن تغييرها بعد حفظ القالب.",
      "inspection.tpl.selectAll": "تحديد الكل",
      "inspection.tpl.points": "{{n}} نقطة",
      "inspection.tpl.pointsChosen": "تم اختيار {{n}} نقطة",
      "inspection.tpl.saved": "تم حفظ القالب.",
      "inspection.tpl.retire": "إزالة القالب",
      "inspection.tpl.retireBody": "إزالة «{{name}}»؟ عمليات الفحص المنفَّذة به تحتفظ بتقاريرها.",
      "inspection.tpl.retired": "تمت إزالة القالب.",
      "inspection.tpl.emptyTitle": "لا توجد قوالب خاصة بك بعد",
      "inspection.tpl.emptyBody": "كل فحص يستخدم القائمة الجاهزة إلى أن تنشئ قالبًا.",
      "inspection.tpl.backToRegister": "العودة إلى الفحص الشامل",
      "inspection.tpl.pickTitle": "أي قائمة فحص؟",
      "inspection.tpl.pickBody": "اختر قائمة الفحص التي سيُنفَّذ عليها هذا الفحص.",
      "inspection.tpl.noSource": "لا توجد قائمة فحص جاهزة لبناء قالب منها.",
      "inspection.tpl.listFailed": "تعذّر تحميل قوالب قائمة الفحص.",
      "inspection.tpl.loadFailed": "تعذّر تحميل نقاط قائمة الفحص.",
      "inspection.tpl.saveFailed": "تعذّر حفظ القالب.",
      "inspection.tpl.retireFailed": "تعذّرت إزالة القالب.",
      "inspection.registerEmpty": "لم يتم تنفيذ أي فحص شامل بعد.",
      "inspection.listFailed": "تعذّر تحميل عمليات الفحص.",
      "inspection.searchPlaceholder": "رقم البطاقة أو اللوحة أو العميل",
      "inspection.verdictColumn": "النتيجة",
      "inspection.notBilled": "لم تُفوتر بعد",
      "inspection.resendReport": "إعادة إرسال التقرير",
      "inspection.resent": "تم إرسال التقرير مرة أخرى برابط جديد",
      "inspection.resendFailed": "تعذّر إرسال التقرير",
      "inspection.status.all": "كل الحالات",
      "inspection.status.in_progress": "قيد التنفيذ",
      "inspection.status.completed": "بانتظار التأكيد",
      "inspection.status.issued": "صادر",
      "inspection.noLightsLit": "لا توجد لمبات تحذير مضيئة",
      "inspection.lightsLoadFailed": "تعذّر تحميل لمبات التحذير — أعد تحميل الصفحة قبل الإجابة على هذه النقطة.",
      "inspection.noCodesStored": "تم الفحص — لا توجد أكواد أعطال",
      "inspection.dtcCode": "الكود",
      "inspection.dtcDescription": "ما ذكره جهاز الفحص",
      "inspection.dtcInvalid": "الرموز تكون بصيغة P0301 أو P0A80.",
      "inspection.addCode": "إضافة كود",
      "inspection.notRecorded": "غير مسجّل",
      "inspection.coverage": "تم فحص {{checked}} من {{total}} نقطة",
      "inspection.needsAttention": "تحتاج انتباه",
      "inspection.showAllPoints": "كل النقاط ({{count}})",
      "inspection.photoCount": "{{count}} صورة",
      "inspection.photoHeic": "صور HEIC غير مقبولة. في الآيفون اضبط الإعدادات ← الكاميرا ← التنسيقات على «الأكثر توافقًا»، أو أرسل الصورة بصيغة JPEG.",
      "inspection.photoFailed": "تعذّر رفع الصورة.",
      "inspection.charges": "الرسوم",
      "inspection.noCharges": "لم يُحدَّد سعر لهذا الفحص بعد.",
      "inspection.chargedOnInvoice": "رسوم الفحص مُحتسبة في الفاتورة {{number}}.",
      "inspection.chargedOnInvoiceDraft": "رسوم الفحص مُحتسبة في الفاتورة أدناه.",
      "inspection.feeLine": "رسوم الفحص",
      "inspection.price": "السعر",
      "inspection.editPrice": "تعديل السعر",
      "inspection.priceSaved": "تم تحديث السعر",
      "inspection.priceFailed": "تعذّر تحديث السعر",
      "inspection.editAnswers": "تعديل الإجابات",
      "inspection.confirmIssue": "تأكيد وإصدار التقرير",
      "inspection.confirmNote": "يمكنك التعديل حتى التأكيد. بعده يصبح التقرير نهائياً.",
      "inspection.reopened": "يمكنك تعديل قائمة الفحص مرة أخرى",
      "inspection.reopenFailed": "تعذّر إعادة فتح قائمة الفحص",
      "inspection.issued": "تم إصدار التقرير",
      "inspection.issuedNote": "يصل التقرير إلى العميل بعد سداد الفاتورة.",
      "inspection.printReport": "طباعة التقرير",
      "inspection.printFailed": "تعذّر فتح التقرير",
      "inspection.createInvoice": "إنشاء الفاتورة",
      "inspection.invoiceCreated": "تم إنشاء الفاتورة",
      "inspection.invoiceReady": "فاتورة",
      "inspection.invoiceFailed": "تعذّر إنشاء الفاتورة",
      "inspection.invoiceNeedsJobCard": "لدى العميل رصيد دائن. افتح بطاقة العمل لاختيار طريقة استخدامه.",
      "inspection.invoiceStatus.paid": "مدفوعة",
      "inspection.invoiceStatus.partial": "مدفوعة جزئياً",
      "inspection.invoiceStatus.unpaid": "غير مدفوعة",
      "inspection.invoiceStatus.void": "ملغاة",
      "inspection.recordPayment": "تسجيل دفعة مستلمة في المركز",
      "jc.cta.continueInspection": "متابعة الفحص",
      "jc.cta.issueReport": "إصدار التقرير",
      "jc.cta.reviewInspection": "مراجعة وإصدار التقرير",
      "jc.cta.openReport": "فتح التقرير",
      "jc.cta.dpBlocksRepair": "الدفعة المقدمة المطلوبة لم تُسدد بعد — سيبدأ العمل تلقائياً فور سدادها.",
      "jc.cta.resendPayLink": "إعادة رابط الدفع",
      "jc.dp.vatPreview": "سيدفع العميل {{gross}} ريال (شامل ضريبة {{rate}}٪)",
      "jc.dp.depositPreview": "سيدفع العميل {{gross}} ريال (أمانة، بدون ضريبة)",
      "jc.dp.title": "طلب عربون",
      "jc.dp.hint": "عرض السعر {{number}} معتمد (الإجمالي {{total}}). اختر طريقة حساب العربون — تُضاف الضريبة تلقائيًا. سيصل العميل رسالة نصية برابط الدفع.",
      "jc.dp.hintReceipt": "عرض السعر {{number}} معتمد (الإجمالي {{total}}). اختر طريقة حساب العربون — بدون ضريبة، يُحتفظ به كأمانة حتى الفاتورة النهائية. سيصل العميل رسالة نصية برابط الدفع.",
      "jc.dp.amountPlaceholder": "المبلغ (ريال)",
      "jc.dp.send": "إرسال طلب الدفع",
      "jc.dp.chooseMethodsAndSend": "اختر طرق الدفع وأرسل",
      "jc.dp.success": "تم طلب العربون",
      "jc.dp.successSms": "تم طلب العربون — أُرسلت رسالة نصية للعميل",
      "jc.dp.receiptSuccess": "تم إصدار سند القبض",
      "jc.dp.receiptSuccessSms": "تم إصدار سند القبض — أُرسلت رسالة نصية للعميل",
      "jc.dp.failed": "فشل طلب العربون",
      "jc.dp.invalidAmount": "أدخل مبلغًا صحيحًا",
      "jc.aiAnalysis.title": "تحليل الذكاء الاصطناعي",
      "jc.aiAnalysis.run": "بدء التحليل",
      "jc.aiAnalysis.rerun": "إعادة التحليل",
      "jc.aiAnalysis.lastRun": "آخر تحليل: {{time}}",
      "jc.aiAnalysis.empty": "احصل على ملخص كامل من الذكاء الاصطناعي لبطاقة العمل هذه، من الاستلام حتى الآن.",
      "jc.aiAnalysis.failed": "فشل تحليل الذكاء الاصطناعي. يرجى المحاولة مرة أخرى.",
      "jc.aiAnalysis.sections.workDone": "المنجز",
      "jc.aiAnalysis.sections.remaining": "المتبقي",
      "jc.aiAnalysis.sections.risks": "المخاطر",
      "jc.aiAnalysis.sections.recommendations": "التوصيات",
      "jc.rp.request": "طلب دفعة",
      "jc.rp.title": "طلب دفعة",
      "jc.rp.amountLabel": "المبلغ (ريال)",
      "jc.rp.methodsLabel": "طرق الدفع لهذا الطلب",
      "jc.rp.estimateLine": "إجمالي التقدير {{total}} · المدفوع {{paid}} · المتبقي {{remaining}}",
      "jc.rp.fullAmount": "كامل المبلغ المتبقي شامل الضريبة ({{amount}})",
      "jc.rp.success": "تم طلب الدفعة — تم إنشاء سند القبض وجارٍ إرسال الرابط للعميل",
      "jc.rp.failed": "فشل طلب الدفعة",
      "qt.cannotDeleteApproved": "لا يمكن حذف عرض سعر معتمد",
      "qt.deleteConfirmNumbered": "حذف عرض السعر {{number}}؟",
      "qt.deleteSuccess": "تم حذف عرض السعر",
      "qt.deleteFailed": "فشل الحذف",
      "qt.viewDetails": "عرض التفاصيل",
      "qt.renew": "تجديد",
      "qt.renewTitle": "تجديد عرض السعر",
      "qt.renewSubtitle": "انتهت صلاحية هذا العرض. حدّد تاريخ صلاحية جديد، ثم أرسله كما هو أو عدّله أولاً.",
      "qt.renewNewValidUntil": "صالح حتى",
      "qt.renewAndSend": "إرسال كما هو",
      "qt.renewAndEdit": "تعديل أولاً",
      "qt.renewed": "تم تجديد عرض السعر",
      "qt.renewedAndSent": "تم تجديد عرض السعر وإرساله للعميل",
      "qt.renewedSmsDuplicate": "تم التجديد، لكن بوابة الرسائل منعت الرسالة لأنها مطابقة لرسالة يملكها العميل بالفعل. غيّر شيئاً في عرض السعر ثم أرسله مرة أخرى.",
      "qt.resendDuplicate": "لم تُرسل — العميل يملك هذه الرسالة نفسها، لذا منعت البوابة تكرارها. عدّل عرض السعر أو جدّده ثم أرسله.",
      "qt.renewFailed": "تعذّر تجديد عرض السعر",
      "qt.manualApproval": "موافقة يدوية",
      "qt.resendReminder": "إرسال تذكير",
      "qt.loadFailed": "فشل تحميل عرض السعر",
      "qt.fillDescriptionPrice": "يرجى إدخال الوصف والسعر",
      "qt.itemAdded": "تمت إضافة البند",
      "qt.addItemFailed": "فشل إضافة البند",
      "qt.itemsChangedRefresh": "تم تعديل عرض السعر من شاشة أخرى. تم تحديث الصفحة، حاول مرة أخرى.",
      // PR#3 Task 3.4 — سياسة البند الإلزامي في شاشة الإنشاء والمعاينة الداخلية.
      "qt.mandatoryLine": "بند إلزامي",
      "qt.mandatoryLineHint": "تُطبَّق القاعدة فقط عند رفض العميل لهذا البند. ولكل بند آخر قاعدته الخاصة.",
      "qt.mandatoryChainNote": "يوجد بند آخر في هذه الخدمة يرفض عرض السعر بالكامل، لذا فإن رفض هذا البند يرفض عرض السعر بالكامل أيضاً.",
      "qt.mandatoryConsequenceLegend": "إذا رفضه العميل:",
      "qt.mandatoryActionRejectGroup": "ترفض هذه الخدمة وقطعها",
      "qt.mandatoryActionRejectQuotation": "يرفض عرض السعر بالكامل",
      "qt.mandatoryActionRequired": "اختر ما الذي يحدث عند رفض هذا البند.",
      "qt.mandatoryGroupRequired": "هذا البند غير مرتبط بأي خدمة، فلا توجد خدمة تُرفض. اختر «يرفض عرض السعر بالكامل» بدلاً من ذلك.",
      "qt.mandatoryFixPolicies": "صحّح إعدادات البنود الإلزامية قبل المتابعة.",
      "qt.lineGroup": "ضمن: {{service}}",
      "qt.lineNoGroup": "غير مرتبط بخدمة",
      "qt.groupUnnamedService": "خدمة",
      "qt.requoteTitle": "مراجعة البنود المرفوضة",
      "qt.requoteHint": "هذه هي نفس بنود بطاقة العمل التي رفضها العميل، من كل خدمات هذه البطاقة. تعديلها هنا يغيّر البنود نفسها ولا يضيف بنوداً جديدة. يبقى العمل متوقفاً حتى يوافق العميل على الأسعار الجديدة.",
      "qt.requoteBoundHint": "محدَّد ببطاقة العمل التي تتم مراجعتها.",
      "qt.requoteSubmit": "إرسال عرض السعر المعدّل",
      "qt.requoteAndChoosePayment": "إرسال التعديل — اختيار طرق الدفع",
      "qt.requoteDone": "تم إرسال عرض السعر المعدّل {{number}} إلى العميل",
      "qt.requoteNoSms": "تم إنشاء عرض السعر {{number}}، لكن الرسالة لم تصل العميل — أرسل له الرابط بنفسك.",
      "qt.requoteLocked": "تعذّرت إعادة التسعير: {{lines}} مُدرج في فاتورة مُرحَّلة. قم بعكس الفاتورة (إشعار دائن) أولاً.",
      "qt.builderHint": "ابدأ بالخدمة، ثم أضف القطع التي تحتاجها تحتها. العميل يرى نفس التجميع، ويستطيع قبول أو رفض الخدمة مع قطعها معاً.",
      "qt.addService": "إضافة خدمة",
      "qt.addPart": "إضافة قطعة لهذه الخدمة",
      "qt.removeService": "حذف الخدمة وقطعها",
      "qt.cardParts": "قطع هذه الخدمة",
      "qt.cardNoParts": "لا توجد قطع تحت هذه الخدمة بعد",
      "qt.looseItems": "بنود غير مرتبطة بخدمة",
      "qt.looseItemsHint": "رسوم مستقلة بذاتها — رسوم زيارة، فحص، أو قطعة تُباع مباشرة.",
      "qt.serviceNamePlaceholder": "اسم الخدمة، مثال: صيانة فرامل أمامية",
      "qt.mandatoryBadge": "إلزامي",
      "qt.mandatoryConsequenceGroup": "عند الرفض: تُرفض هذه الخدمة وقطعها",
      "qt.mandatoryConsequenceGroupChained": "عند الرفض: يُرفض عرض السعر بالكامل (لأن بنداً آخر في هذه الخدمة يتطلب ذلك)",
      "qt.mandatoryConsequenceQuotation": "عند الرفض: يُرفض عرض السعر بالكامل",
      "qt.ungroupedItems": "بنود أخرى (غير مرتبطة بخدمة)",
      "qt.pdfDownloadStarted": "بدأ تنزيل ملف PDF",
      "qt.resendReminderSent": "تم إرسال التذكير إلى العميل",
      "qt.resendNothingDelivered": "لم يتم الإرسال: لا يوجد رقم جوال للعميل",
      "qt.deleteConfirm": "هل أنت متأكد من حذف عرض السعر هذا؟ لا يمكن التراجع عن هذا الإجراء.",
      "qt.deleteSuccessFull": "تم حذف عرض السعر بنجاح",
      "qt.deleteFailedFull": "فشل حذف عرض السعر",
      "qt.jobCard": "بطاقة العمل",
      "qt.viewInvoice": "عرض الفاتورة",
      "qt.viewJobCard": "عرض بطاقة العمل",
      "qt.pdf": "PDF",
      "qt.pricePlaceholder": "السعر",
      "qt.add": "إضافة",
      "qt.invalidOrExpiredLink": "الرابط غير صالح أو منتهي الصلاحية",
      "qt.dpFixedAmount": "مبلغ ثابت: {{amount}} ريال",
      "qt.dpPercentOfSelected": "{{value}}٪ من البنود المختارة",
      "qt.dpPercentOfParts": "{{value}}٪ من تكلفة قطع الغيار",
      "qt.dpAmountPartsOnly": "{{amount}} ريال (قطع الغيار فقط)",
      "qt.selectAtLeastOneService": "يرجى اختيار خدمة واحدة على الأقل للموافقة عليها",
      "qt.approvalFailed": "فشلت الموافقة",
      "qt.processApprovalFailed": "فشل تنفيذ الموافقة",
      "qt.rejectConfirm": "هل أنت متأكد من رفض عرض السعر بالكامل؟",
      "qt.rejectionFailed": "فشل الرفض",
      "qt.processRejectionFailed": "فشل تنفيذ الرفض",
      "qt.loadingQuotation": "جاري تحميل عرض السعر...",
      "qt.oops": "عذرًا!",
      "qt.contactGarageDirectly": "إذا كنت تعتقد أن هذا خطأ، يرجى التواصل مع المركز مباشرة.",
      "qt.servicesApproved": "تمت الموافقة على الخدمات!",
      "qt.quotationRejectedTitle": "تم رفض عرض السعر",
      "qt.thankYouApproved": "شكرًا لموافقتك على الخدمات المختارة. تم إنشاء فاتورة وتم إشعار المركز.",
      "qt.quotationLabel": "عرض السعر",
      "qt.approvedServicesLabel": "الخدمات المعتمدة",
      "qt.countOf": "{{count}} من {{total}}",
      "qt.garageWillContact": "سيتواصل معك المركز قريبًا لترتيب الخدمة.",
      "qt.responseRecorded": "تم تسجيل ردك. وتم إشعار المركز.",
      "qt.quotationApproval": "الموافقة على عرض السعر",
      "qt.reviewAndSelect": "يرجى مراجعة واختيار الخدمات التي ترغب في الموافقة عليها",
      "qt.quotationNumberLabel": "رقم عرض السعر",
      "qt.totalAmount": "المبلغ الإجمالي",
      "qt.selectServicesToApprove": "اختر الخدمات للموافقة عليها",
      "qt.checkServicesHint": "حدد الخدمات التي تريد الموافقة عليها. سيتم رفض الخدمات غير المحددة.",
      "qt.selectedServices": "الخدمات المختارة",
      "qt.downpaymentRequiredBadge": "العربون مطلوب",
      "qt.typeLabel": "النوع",
      "qt.downpaymentAmount": "مبلغ العربون",
      "qt.noteLabel": "ملاحظة",
      "qt.downpaymentNote": "بعد الموافقة، ستتم إعادة توجيهك لدفع العربون. وسيُستحق المبلغ المتبقي عند اكتمال الخدمة.",
      "qt.approveSelectedCount": "الموافقة على المحدد ({{count}})",
      "qt.rejectAll": "رفض الكل",
      "qt.approvalAgreement": "بالموافقة، فإنك توافق على الخدمات المختارة وتكاليفها",
      "qt.dpExceedFixed": "لا يمكن أن يتجاوز العربون {{max}} ريال (غير شامل ضريبة القيمة المضافة). مع الضريبة: {{total}} ريال",
      "qt.dpExceedFixedReceipt": "لا يمكن أن يتجاوز العربون {{max}} ريال (أمانة — بدون ضريبة)",
      "qt.dpExceedTotal": "لا يمكن أن يتجاوز العربون ({{amount}} ريال) المبلغ الإجمالي ({{total}} ريال)",
      "qt.updatedAndSmsSent": "تم تحديث عرض السعر وإرسال رسالة نصية إلى العميل!",
      "qt.updatedSmsSkippedExpired": "تم الحفظ. لم تُرسل رسالة — انتهت صلاحية عرض السعر. جدّده ثم أرسله.",
      "qt.updatedAs": "تم الحفظ كعرض سعر جديد {{number}}",
      "qt.updateFailedReason": "فشل تحديث عرض السعر: {{reason}}",
      "qt.editQuotation": "تعديل عرض السعر",
      "qt.previewQuotation": "معاينة عرض السعر",
      "qt.sendingEllipsis": "جاري الإرسال...",
      "qt.generatingEllipsis": "جاري الإنشاء...",
      "qt.generateForExport": "إنشاء للتصدير",
      "qt.insurerEstimateNote": "هذا تقدير لشركة التأمين. سيتم إنشاؤه للتصدير اليدوي ولن يُرسل إلى العميل. أدخل المرجع بعد رد شركة التأمين في قسم المطالبة.",
      "qt.saveResendSms": "حفظ وإعادة إرسال الرسالة النصية",
      "qt.confirmSendQuotation": "تأكيد وإرسال عرض السعر",
      "qt.customerInformation": "معلومات العميل",
      "qt.jobCardId": "رقم بطاقة العمل",
      "qt.action": "إجراء",
      "qt.customerProvided": "مقدَّم من العميل",
      "qt.downpaymentRequired": "العربون مطلوب",
      "qt.downpaymentType": "نوع العربون",
      "qt.dpTypePercentage": "نسبة من الإجمالي",
      "qt.dpTypeFixed": "مبلغ ثابت",
      "qt.dpTypePartsOnly": "قطع الغيار فقط (المبلغ الكامل)",
      "qt.dpTypeFullAmount": "المبلغ كاملاً شامل الضريبة",
      "qt.dpFullAmountReadout": "{{amount}} ريال — إجمالي عرض السعر شامل الضريبة",
      "qt.dpDescFullAmount": "إجمالي عرض السعر شامل الضريبة — بحد أقصى ما يوافق عليه العميل من البنود",
      "qt.dpFullAmountZero": "المبلغ الكامل يتطلب إجمالي عرض سعر أكبر من صفر",
      "qt.dpLabelPercentage": "النسبة (٪)",
      "qt.dpLabelAmount": "المبلغ (ريال)",
      "qt.dpLabelPartsTotal": "إجمالي مبلغ قطع الغيار (ريال)",
      "qt.partsTotalAutoCalculated": "{{amount}} ريال (محتسب تلقائيًا)",
      "qt.dpExamplePercent": "مثال: 30",
      "qt.dpExampleAmount": "مثال: 500.00",
      "qt.calculatedDownpayment": "العربون المحتسب",
      "qt.dpDescPercentage": "{{value}}٪ من المبلغ الإجمالي",
      "qt.dpDescFixed": "مبلغ ثابت (شامل ضريبة القيمة المضافة)",
      "qt.dpDescFixedReceipt": "مبلغ ثابت (بدون ضريبة — أمانة)",
      "qt.dpDescPartsOnly": "100٪ من تكلفة قطع الغيار",
      "qt.dpPartialAcceptNote": "إذا وافق العميل على بعض البنود، فسيتم تعديل العربون تلقائيًا بناءً على البنود المقبولة.",
      "qt.sendQuotationVia": "إرسال عرض السعر عبر:",
      "qt.emailOnly": "البريد الإلكتروني فقط",
      "qt.smsOnly": "الرسائل النصية فقط",
      "qt.bothEmailSms": "البريد الإلكتروني والرسائل النصية معًا",
      "qt.messageToCustomer": "رسالة إلى العميل (اختياري):",
      "qt.messageToCustomerPlaceholder": "أدخل أي رسالة أو تعليمات إضافية للعميل (ستظهر في صفحة الموافقة على عرض السعر)...",
      "qt.messageToCustomerHint": "ستظهر هذه الرسالة للعميل عند اطلاعه على عرض السعر.",
      "qt.arabicDefault": "العربية - الافتراضية",
      "qt.bothLanguages": "كلتا اللغتين (ثنائي اللغة)",
      "qt.notificationLanguageHint": "سيتم إرسال الإشعار باللغة المختارة. يشمل خيار ثنائي اللغة العربية والإنجليزية معًا.",
      "sa.superAdmin": "المسؤول الأعلى",
      "sa.user": "المستخدم",
      "sa.systemUsers": "مستخدمو النظام",
      "sa.systemUsersSubtitle": "إدارة مسؤولي النظام وفريق الدعم",
      "sa.searchUsers": "البحث عن المستخدمين...",
      "sa.noUsersMatch": "لا يوجد مستخدمون مطابقون لمعاييرك",
      "sa.notAvailable": "غير متوفر",
      "sa.deleteUser": "حذف المستخدم",
      "sa.activateUser": "تفعيل المستخدم",
      "sa.deactivateUser": "إلغاء تفعيل المستخدم",
      "sa.confirmDeleteUserName": "هل أنت متأكد من حذف {{name}}؟ لا يمكن التراجع عن هذا الإجراء.",
      "sa.failedToCreateUser": "فشل إنشاء المستخدم",
      "sa.failedToUpdateUser": "فشل تحديث المستخدم",
      "sa.failedToSaveUser": "فشل حفظ المستخدم",
      "sa.addNewUser": "إضافة مستخدم جديد",
      "sa.phoneRequiredForNewUsers": "رقم الجوال مطلوب — وهو وسيلة دخول المستخدم",
      "sa.personalInformation": "المعلومات الشخصية",
      "sa.firstNamePlaceholder": "محمد",
      "sa.lastNamePlaceholder": "العتيبي",
      "sa.emailPlaceholder": "name@example.com",
      "sa.enterPassword": "أدخل كلمة المرور",
      "sa.leaveBlankKeepPassword": "اتركها فارغة للاحتفاظ بكلمة المرور الحالية",
      "sa.authorizedBranches": "الفروع المصرّح بها",
      "sa.noBranchesAvailable": "لا توجد فروع متاحة",
      "sa.selectBranchesHint": "اختر فرعًا واحدًا أو أكثر يمكن لهذا المستخدم الوصول إليه",
      "sa.activeUser": "مستخدم نشط",
      "sa.activeStatus": "الحالة النشطة",
      "sa.globalAccess": "وصول شامل",
      "sa.companiesSubtitle": "إدارة الشركات وفروعها (مراكز خدمات السيارات)",
      "sa.addCompany": "إضافة شركة",
      "sa.searchCompanies": "البحث عن الشركات بالاسم أو رقم السجل التجاري...",
      "sa.failedToLoadCompanies": "فشل تحميل الشركات",
      "sa.tryAgain": "حاول مرة أخرى",
      "sa.noCompaniesFound": "لا توجد شركات.",
      "sa.noCompaniesYet": "لا توجد شركات بعد",
      "sa.subscription": "الاشتراك",
      "sa.noPlan": "لا توجد خطة",
      "sa.branches": "الفروع",
      "sa.companyBranches": "فروع الشركة",
      "sa.addBranch": "إضافة فرع",
      "sa.unnamedGarage": "مركز بدون اسم",
      "sa.noCity": "لا توجد مدينة",
      "sa.noBranchesYet": "لم تتم إضافة فروع لهذه الشركة بعد.",
      "sa.manageCompanyDetails": "إدارة تفاصيل الشركة",
      "sa.companyCreatedSuccessfully": "تم إنشاء الشركة بنجاح",
      "sa.companyUpdatedSuccessfully": "تم تحديث الشركة بنجاح",
      "sa.failedToCreateCompany": "فشل إنشاء الشركة",
      "sa.failedToUpdateCompany": "فشل تحديث الشركة",
      "sa.branchCreatedSuccessfully": "تم إنشاء الفرع بنجاح",
      "sa.branchUpdatedSuccessfully": "تم تحديث الفرع بنجاح",
      "sa.failedToCreateBranch": "فشل إنشاء الفرع",
      "sa.failedToUpdateBranch": "فشل تحديث الفرع",
      "sa.superAdminDashboard": "لوحة تحكم المسؤول الأعلى",
      "sa.superAdminDashboardSubtitle": "إدارة المستأجرين والاشتراكات وإعدادات النظام",
      "sa.totalCompanies": "إجمالي الشركات",
      "sa.activeSubscriptions": "الاشتراكات النشطة",
      "sa.totalBranches": "إجمالي الفروع",
      "sa.pendingApproval": "بانتظار الموافقة",
      "sa.expired": "منتهي",
      "sa.suspended": "موقوف",
      "sa.pendingApprovals": "الموافقات المعلّقة",
      "sa.companiesAwaitingApproval": "{{count}} شركات بانتظار الموافقة",
      "sa.expiringSoon": "تنتهي قريبًا",
      "sa.subscriptionsExpiring": "{{count}} اشتراكات تنتهي خلال 30 يومًا",
      "sa.manageSubscriptions": "إدارة الاشتراكات",
      "sa.systemConfigurations": "إعدادات النظام",
      "sa.voiceProvider.title": "محرك المحادثة الصوتية",
      "sa.voiceProvider.hint": "إعداد واحد للمنصة كلها: تستخدم المكالمة الصوتية التالية لكل منشأة هذا المحرك، وتحتفظ المكالمات الجارية بمحركها.",
      "sa.voiceProvider.active": "قيد الاستخدام",
      "sa.voiceProvider.choose": "التحويل إلى هذا المحرك",
      "sa.voiceProvider.saving": "جارٍ التحويل…",
      "sa.voiceProvider.notConfigured": "لا يوجد مفتاح API على الخادم",
      "sa.voiceProvider.saved": "أصبحت المكالمات الصوتية تستخدم {{provider}}",
      "sa.voiceProvider.saveFailed": "تعذّر تحويل محرك الصوت",
      "sa.voiceProvider.loadFailed": "تعذّر تحميل إعداد محرك الصوت",
      "sa.subscriptionStatus": "حالة الاشتراك",
      "sa.recentCompanies": "الشركات الحديثة",
      "sa.failedToLoadPermissions": "فشل تحميل الصلاحيات",
      "sa.failedToLoadRolePermissions": "فشل تحميل صلاحيات الدور",
      "sa.permissionsUpdatedFor": "تم تحديث صلاحيات {{role}}",
      "sa.failedToSavePermissions": "فشل حفظ الصلاحيات",
      "sa.roleNameRequired": "اسم الدور مطلوب",
      "sa.roleCreatedSuccessfully": "تم إنشاء الدور \"{{name}}\" بنجاح",
      "sa.failedToCreateRole": "فشل إنشاء الدور",
      "sa.createCustomRole": "إنشاء دور مخصص",
      "perm.view.jobs": "الأعمال",
      "perm.area.workshop": "الورشة",
      "perm.area.sales": "المبيعات",
      "perm.area.money": "المدفوعات والنقد",
      "perm.area.purchasing": "المشتريات والمخزون",
      "perm.area.hr": "الموارد البشرية",
      "perm.area.admin": "المحاسبة والإدارة",
      "perm.view.advanced": "عرض متقدم",
      "perm.jobs.searchPlaceholder": "ابحث في الأعمال أو رموز الصلاحيات",
      "perm.jobs.none": "لا توجد أعمال تطابق هذا البحث",
      "perm.jobs.roleNameGated": "مقيّد أيضاً باسم الدور، وقد لا يكفي هذا الخيار وحده",
      "perm.switchRoleConfirm": "لديك تغييرات غير محفوظة على هذا الدور. هل تتجاهلها وتفتح دوراً آخر؟",
      "perm.analysis.button": "ماذا يستطيع هذا الدور؟",
      "perm.analysis.modalTitle": "ماذا يستطيع هذا الدور؟",
      "perm.analysis.title": "تحليل TzamunAI",
      "perm.analysis.builtInRole": "دور قياسي",
      "perm.analysis.exactJobsTitle": "المهام المفعّلة لهذا الدور",
      "perm.analysis.exactJobsSubtitle": "مأخوذة مباشرةً من صلاحيات الدور، دون ذكاء اصطناعي",
      "perm.analysis.noActiveJobs": "لا توجد مهام مفعّلة لهذا الدور.",
      "perm.analysis.roleNameGatedNote": "مقيّدة حالياً باسم الدور أيضاً",
      "perm.analysis.verdict.fits": "يناسب الدور",
      "perm.analysis.verdict.missing": "تنقصه صلاحيات",
      "perm.analysis.verdict.over": "صلاحيات زائدة",
      "perm.analysis.verdict.both": "يحتاج إلى تعديل",
      "perm.analysis.canDo": "ما يستطيع فعله",
      "perm.analysis.cannotDo": "ما لا يستطيع فعله",
      "perm.analysis.watchOut": "تنبيهات",
      "perm.analysis.changedFromStandard": "تغييرات عن الدور القياسي",
      "perm.analysis.baselineAdded": "أُضيفت زيادةً على الدور القياسي",
      "perm.analysis.baselineRemoved": "أُزيلت من الدور القياسي",
      "perm.analysis.suggestions": "الاقتراحات",
      "perm.analysis.suggestedAdd": "يُقترح إضافتها",
      "perm.analysis.suggestedRemove": "يُقترح إزالتها",
      "perm.analysis.apply": "تطبيق",
      "perm.analysis.applied": "طُبّق",
      "perm.analysis.applyAll": "تطبيق الكل",
      "perm.analysis.analyseAgain": "إعادة التحليل",
      "perm.analysis.error": "تعذّر تحليل هذا الدور. حاول مرة أخرى.",
      "perm.suggest.title": "اقتراحات TzamunAI",
      "perm.suggest.subtitle": "يقترح TzamunAI الصلاحيات المناسبة لهذا الدور من اسمه ووصفه",
      "perm.suggest.button": "اقترح الصلاحيات عبر TzamunAI",
      "perm.suggest.step.reading": "قراءة الدور ومهامه",
      "perm.suggest.step.checking": "فحص {{count}} صلاحية",
      "perm.suggest.step.rules": "تطبيق قاعدة الفصل بين المهام",
      "perm.suggest.scanningAreas": "مجالات الصلاحيات قيد الفحص:",
      "perm.suggest.summary": "اقترح TzamunAI عدد {{count}} من الصلاحيات — راجعها قبل إنشاء الدور",
      "perm.suggest.tickedNotice": "المحدد حالياً: {{count}}",
      "perm.suggest.needsReview": "تحتاج إلى مراجعة",
      "perm.suggest.needsReviewHint": "صلاحيات غير مؤكدة — غير محددة افتراضياً، راجعها بعناية قبل إضافتها",
      "perm.suggest.adminOnly": "محفوظة لمدير النظام",
      "perm.suggest.usePermissions": "اعتماد هذه الصلاحيات",
      "perm.suggest.permissionsApplied": "طُبّقت الصلاحيات",
      "perm.suggest.tryAgain": "أعد المحاولة",
      "perm.suggest.activeSource": "البداية من اقتراح TzamunAI ({{count}} صلاحية)",
      "perm.suggest.activeTemplate": "القالب المختار:",
      "perm.suggest.clearActive": "إلغاء",
      "perm.suggest.error": "تعذّر اقتراح الصلاحيات. حاول مرة أخرى.",
      "ai.stage.announcement.reading": "قراءة الموجز",
      "ai.stage.announcement.drafting": "يصوغ TzamunAI الإعلان باللغتين",
      "ai.stage.announcement.checking": "التحقق من المسودة",
      "ai.stage.damageScan.reading_photos": "تحميل أمر العمل",
      "ai.stage.damageScan.detecting": "يحدد TzamunAI الأضرار",
      "ai.stage.damageScan.processing": "حفظ الصور المفحوصة",
      "ai.stage.damageScan.saving": "حفظ نتيجة الفحص",
      "ai.stage.istamara.reading_registration": "استلام صورة الاستمارة",
      "ai.stage.istamara.extracting": "يقرأ TzamunAI الاستمارة",
      "ai.stage.istamara.matching": "مطابقة الشركة المصنّعة والطراز",
      "ai.stage.jobCardAnalysis.reading": "قراءة أمر العمل وسجل الزيارات",
      "ai.stage.jobCardAnalysis.model": "يحلّل TzamunAI",
      "ai.stage.jobCardAnalysis.validating": "التحقق من الإجابة",
      "ai.stage.jobCardAnalysis.saving": "حفظ التحليل",
      "ai.stage.jobCardAssistant.thinking": "قراءة أمر العمل والمركبة",
      "ai.stage.jobCardAssistant.web_search": "البحث في الإنترنت عن الاستدعاءات والنشرات",
      "ai.stage.jobCardAssistant.model": "يشخّص TzamunAI",
      "ai.stage.jobCardAssistant.parts": "مطابقة القطع مع مخزونك",
      "ai.stage.jobCardAssistant.saving": "حفظ الاستشارة",
      "ai.stage.purchaseInvoice.reading": "قراءة الملف المرفوع",
      "ai.stage.purchaseInvoice.extracting": "يقرأ TzamunAI الفاتورة",
      "ai.stage.purchaseInvoice.matching": "مطابقة المورد والأصناف",
      "ai.stage.qiwaScan.reading": "قراءة ملف العقد",
      "ai.stage.qiwaScan.extracting": "يقرأ TzamunAI العقد",
      "ai.stage.qiwaScan.validating": "التحقق من بيانات الموظف",
      "ai.stage.qiwaScan.saving": "حفظ المسودة",
      "ai.stage.roleAnalysis.cached": "تحميل التحليل المحفوظ",
      "ai.stage.roleAnalysis.baseline": "المقارنة بالدور القياسي",
      "ai.stage.roleAnalysis.analyzing": "يحلّل TzamunAI الدور",
      "ai.stage.roleAnalysis.evaluating": "التحقق من الإجابة",
      "ai.stage.roleSuggest.reading": "تجهيز الدور والصلاحيات",
      "ai.stage.roleSuggest.checking": "يفحص TzamunAI كل صلاحية",
      "ai.stage.roleSuggest.rules": "تطبيق قاعدة صلاحيات المدير فقط",
      "ai.stage.terms.drafting": "يكتب TzamunAI النسخة الإنجليزية",
      "ai.stage.terms.translating": "يكتب TzamunAI النسخة العربية",
      "ai.stage.terms.saving": "حفظ المسودة",
      "ai.thinking.stillWorking": "ما زال TzamunAI يعمل…",
      "ai.thinking.elapsed": "{{seconds}} ث",
      "perm.analysis.step.reading": "قراءة صلاحيات الدور",
      "perm.analysis.step.matching": "المقارنة بالأدوار القياسية",
      "perm.analysis.step.risks": "تقييم المخاطر والاقتراحات",
      "jc.aiAnalysis.step.reading": "قراءة أمر العمل وسجل الزيارات",
      "jc.aiAnalysis.step.progress": "فحص الأعمال المنجزة والمتبقية",
      "jc.aiAnalysis.step.synthesis": "صياغة ملخص التكلفة والمخاطر",
      "damageScan.ai.readingPhotos": "قراءة الصور",
      "damageScan.ai.findingDamage": "تحديد الأضرار",
      "damageScan.ai.markingMap": "تحديد المواضع على مخطط المركبة",
      "cm.istamara.step.reading": "قراءة بطاقة الاستمارة",
      "cm.istamara.step.extracting": "استخراج بيانات المركبة والمالك",
      "cm.istamara.step.verifying": "التحقق من رقم اللوحة ورقم الهيكل",
      "hr.wizard.qiwa.step.reading": "قراءة العقد",
      "hr.wizard.qiwa.step.extracting": "استخراج بيانات الموظف والراتب",
      "hr.wizard.qiwa.step.filling": "تعبئة مسودة الموظف الجديد",
      "tnc.ai.step.drafting": "صياغة الشروط والأحكام",
      "tnc.ai.step.translating": "كتابة النسختين العربية والإنجليزية",
      "tnc.ai.step.compliance": "مراجعة الالتزام النظامي",
      "tnc.ai.step.refining": "ما زال العمل جارياً على الاتفاقية كاملة — قد يستغرق ذلك بضع دقائق",
      "ann.ai.step.reading": "قراءة الموجز",
      "ann.ai.step.drafting": "صياغة الإعلان باللغتين",
      "ann.ai.step.polishing": "تحسين الأسلوب والوضوح",
      "purchases.scan.step.reading": "قراءة صورة الفاتورة",
      "purchases.scan.step.extracting": "استخراج البنود والإجماليات",
      "purchases.scan.step.matching": "مطابقة المورد والرقم الضريبي",
      // ---- document designs, invoice bank details, branch stamp (2026-10-02) ----
      "docDesign.title": "تصميمات المستندات المطبوعة",
      "docDesign.desc": "اختر شكل طباعة كل نوع مستند. تعرض المعاينة بيانات تجريبية قبل الحفظ.",
      "docDesign.preview": "معاينة",
      "docDesign.save": "حفظ التصميمات",
      "docDesign.loadFailed": "تعذر تحميل تصميمات المستندات",
      "docDesign.saved": "تم حفظ تصميمات المستندات",
      "docDesign.saveFailed": "تعذر حفظ تصميمات المستندات",
      "docDesign.previewFailed": "تعذر إنشاء المعاينة",
      "docDesign.thumbFailed": "تعذّر عرض المعاينة",
      "bookingPage.arabic": "العربية",
      "bookingPage.english": "الإنجليزية",
      "bookingPage.uploadImage": "رفع صورة",
      "bookingPage.closed": "مغلق",
      "bookingPage.startingFrom": "يبدأ من",
      "bookingPage.sar": "ريال",
      "bookingPage.minutes": "دقيقة",
      "bookingPage.noServices": "لا توجد خدمات متاحة حالياً.",
      "bookingPage.noBranches": "لا توجد فروع متاحة حالياً.",
      "bookingPage.openOsm": "فتح في الخريطة",
      "bookingPage.noReviews": "لا توجد تقييمات حالياً.",
      "bookingPage.noFaq": "لا توجد أسئلة شائعة متاحة.",
      "bookingPage.openingHours": "أوقات العمل",
      "bookingPage.contactUs": "اتصل بنا",
      "bookingPage.phone": "الهاتف",
      "bookingPage.email": "البريد الإلكتروني",
      "bookingPage.address": "العنوان",
      "bookingPage.bookNow": "احجز الآن",
      "bookingPage.bookAppointment": "حجز موعد صيانة",
      "bookingPage.onlineBooking": "حجز إلكتروني",
      "bookingPage.selectBranchAndService": "اختر الفرع، الخدمة، وموعد الحجز.",
      "bookingPage.days.sunday": "الأحد",
      "bookingPage.days.monday": "الإثنين",
      "bookingPage.days.tuesday": "الثلاثاء",
      "bookingPage.days.wednesday": "الأربعاء",
      "bookingPage.days.thursday": "الخميس",
      "bookingPage.days.friday": "الجمعة",
      "bookingPage.days.saturday": "السبت",
      "notify.dontSend": "عدم الإرسال للعميل",
      "notify.dontSendHint": "يُصدَر المستند كالمعتاد، ولا تُرسل الرسالة فقط.",
      "notify.notSentBadge": "لم يُرسل للعميل",
      "notify.issuedNotSent": "صدر المستند ولم يُرسل للعميل",
      "assistant.quote.askDownpayment": "هل تريد طلب دفعة مقدمة؟ (مبلغ أو نسبة)",
      "qc.hold.QC_PASSED_AWAITING_QUOTATION_APPROVAL": "اجتاز فحص الجودة. أمر العمل بانتظار موافقة العميل على عرض السعر، ثم ينتقل إلى الفوترة.",
      "qc.hold.QC_PASSED_AWAITING_DOWNPAYMENT": "اجتاز فحص الجودة. أمر العمل بانتظار سداد الدفعة المقدمة، ثم ينتقل إلى الفوترة.",
      "docDesign.retry": "إعادة المحاولة",
      "docDesign.current": "الحالي",
      "docDesign.ledger": "أ · دفتري",
      "docDesign.band": "ب · شريط الهوية",
      "docDesign.formal": "ج · رسمي بإطار",
      "docDesign.rail": "د · عمود جانبي",
      "docDesign.type.invoice": "فاتورة",
      "docDesign.type.credit_note": "إشعار دائن / مدين",
      "docDesign.type.quotation": "عرض سعر",
      "docDesign.type.receipt": "سند قبض / سند صرف",
      "docDesign.type.payslip": "قسيمة راتب",
      "docDesign.type.insurance_report": "تقرير تأمين",
      "docDesign.type.statement": "كشف الحساب",
      "docDesign.type.report": "التقارير",
      "settings.digitsHeading": "الأرقام",
      "settings.digitsDescription": "اختر طريقة عرض الأرقام في الشاشات والمستندات",
      "settings.digitsLatin": "لاتينية 123",
      "settings.digitsArabic": "عربية ١٢٣",
      "settings.digitsSaved": "تم حفظ إعداد الأرقام",
      "settings.digitsSaveFailed": "تعذّر حفظ إعداد الأرقام",
      "settings.digitsLoadFailed": "تعذّر تحميل إعداد الأرقام",
      "copilot.panelTitle": "اقتراحات TzamunAI",
      "copilot.dismiss": "إخفاء الاقتراحات",
      "copilot.matchesRequest": "يطابق طلب العميل: {{words}}",
      "vehicleSpecs.disclaimer": "اقتراح آلي من TzamunAI. يجب على الفني التحقق من توافقه مع هذه المركبة (دليل الشركة المصنعة أو رقم الهيكل) قبل الاستخدام.",
      "vehicleSpecs.candidateMatch": "قد يطابق من المخزون: {{name}}",
      "vehicleSpecs.inStock": "المتوفر:",
      "vehicleSpecs.outOfStock": "نفد من المخزون",
      "vehicleSpecs.notInStock": "غير متوفر في المخزون",
      "vehicleSpecs.quota": "تم بلوغ حد الذكاء الاصطناعي الشهري",
      "vehicleSpecs.assumption.engine_size": "سعة المحرك غير مسجلة — افتُرض {{value}}",
      "vehicleSpecs.assumption.fuel_type": "نوع الوقود غير مسجل — افتُرض {{value}}",
      "vehicleSpecs.assumption.transmission": "ناقل الحركة غير مسجل — افتُرض {{value}}",
      "vehicleSpecs.assumption.year": "سنة الصنع غير مسجلة — افتُرضت {{value}}",
      "vehicleSpecs.addTitle": "إضافة من المخزون",
      "vehicleSpecs.quantity": "الكمية",
      "vehicleSpecs.unitQt": "كوارت",
      "svcCheck.button": "فحص TzamunAI",
      "svcCheck.running": "TzamunAI يفحص هذه الخدمة…",
      "svcCheck.recheck": "أعد الفحص",
      "svcCheck.vinNeeded": "رقم الهيكل مطلوب لفحص هذه الخدمة",
      "svcCheck.missingDetails": "هذه التفاصيل غير مسجلة في ملف المركبة. أضفها لفحص أدق.",
      "svcCheck.run": "افحص",
      "svcCheck.needsTitle": "ما تحتاجه هذه المركبة",
      "svcCheck.partsTitle": "القطع المضافة",
      "svcCheck.missingTitle": "قطع ناقصة",
      "svcCheck.stepsTitle": "الخطوات",
      "svcCheck.missingStepsTitle": "خطوات ناقصة",
      "svcCheck.ok": "مطابقة",
      "svcCheck.wrongSpec": "{{part}} — هذه المركبة تحتاج {{spec}}",
      "svcCheck.wrongQuantity": "الكمية {{qty}} — هذه المركبة تحتاج {{needed}}",
      "svcCheck.notNeeded": "قد لا تحتاجها هذه الخدمة",
      "svcCheck.unlinked": "غير مرتبطة بخدمة",
      "svcCheck.stepUnfinished": "لم تكتمل بعد",
      "svcCheck.addStep": "أضف الخطوة",
      "svcCheck.stepAdded": "تمت إضافة الخطوة",
      "svcCheck.nothingConfirmed": "لم يجد TzamunAI معلومات موثوقة لهذه الخدمة على هذه المركبة",
      "svcCheck.partNumber": "رقم القطعة {{number}}",
      "svcCheck.verifyPartNumber": "تحقق من الرقم في كتالوج الوكيل قبل الطلب",
      "svcCheck.vinMismatch": "رقم الهيكل يشير إلى {{make}} {{year}} — تحقق من بيانات المركبة",
      "svcCheck.needsChanged": "تغيّرت بيانات المركبة أو الخدمة — أعد الفحص",
      "svcCheck.workChanged": "تغيّرت القطع أو الخطوات — أعد الفحص",
      "svcCheck.failed": "تعذر الفحص الآن",
      "svcCheck.unitG": "غ",
      "svcCheck.notAdded": "لم تُضف بعد",
      "svcCheck.unconfirmed": "لم يتأكد TzamunAI منها لهذه المركبة: {{names}}",
      "svcCheck.stepsSuggested": "الخطوات المقترحة ({{count}})",
      "copilot.panelEmpty": "لا توجد اقتراحات لهذه البطاقة حتى الآن.",
      "copilot.loading": "جارٍ تحميل الاقتراحات…",
      "copilot.add": "إضافة",
      "copilot.added": "تمت الإضافة",
      "copilot.confidenceHigh": "ثقة عالية",
      "copilot.confidenceMedium": "ثقة متوسطة",
      "copilot.confidenceLow": "ثقة منخفضة",
      "copilot.sectionServices": "خدمات مقترحة",
      "copilot.sectionParts": "قطع غيار مقترحة",
      "copilot.sectionTechnicians": "فنيون موصى بهم",
      "copilot.partNeedsTaskTitle": "اختر مهمة لهذه القطعة",
      "copilot.partNeedsTaskHelp": "اختر المهمة التي ستُضاف إليها هذه القطعة.",
      "copilot.selectTask": "اختر مهمة",
      "copilot.noTasksAvailable": "أنشئ مهمة أولاً لإضافة القطع إليها.",
      "copilot.addFailed": "تعذرت إضافة الاقتراح",
      "lossWarning.title": "تأكيد الاستمرار رغم الخسارة",
      "lossWarning.headline": "تتجاوز تكلفة العمل سعره. يرجى كتابة سبب المتابعة.",
      "lossWarning.revenue": "الإيراد",
      "lossWarning.cost": "التكلفة",
      "lossWarning.shortfall": "العجز",
      "lossWarning.reason": "سبب المتابعة",
      "lossWarning.reasonPlaceholder": "اكتب سبب إصدار هذه الفاتورة بخسارة",
      "lossWarning.reasonRequired": "يجب كتابة سبب لا يقل عن 3 أحرف ولا يتجاوز 500 حرف.",
      "lossWarning.confirm": "إصدار الفاتورة على أي حال",
      "lossWarning.costIncomplete": "لم تكتمل بيانات التكلفة بعد؛ لا يمكن تأكيد الربح أو الخسارة.",
      "lossWarning.saveFailed": "تعذّر إصدار الفاتورة",
      "copilot.evidence_zero": "لا توجد بطاقات عمل مشابهة",
      "copilot.evidence_one": "مقترح من بطاقة عمل مشابهة واحدة",
      "copilot.evidence_two": "مقترح من بطاقتي عمل مشابهتين",
      "copilot.evidence_few": "مقترح من {{count}} بطاقات عمل مشابهة",
      "copilot.evidence_many": "مقترح من {{count}} بطاقة عمل مشابهة",
      "copilot.evidence_other": "مقترح من {{count}} بطاقة عمل مشابهة",
      "copilot.technicianLoad_zero": "لا توجد بطاقات عمل قيد التنفيذ",
      "copilot.technicianLoad_one": "بطاقة عمل واحدة قيد التنفيذ",
      "copilot.technicianLoad_two": "بطاقتا عمل قيد التنفيذ",
      "copilot.technicianLoad_few": "{{count}} بطاقات عمل قيد التنفيذ",
      "copilot.technicianLoad_many": "{{count}} بطاقة عمل قيد التنفيذ",
      "copilot.technicianLoad_other": "{{count}} بطاقة عمل قيد التنفيذ",
      "pw.riseAboveLast": "أعلى بنسبة {{percent}}% من آخر سعر شراء",
      "pw.lastPaid": "آخر سعر مدفوع {{price}} — {{supplier}}، {{date}}",
      "pw.comparisonUnavailable": "مقارنة السعر غير متاحة",
      "pw.priceRiseBadge": "ارتفاع في السعر",
      "pw.cheapestRecentSupplier": "أرخص مورد خلال آخر 90 يوماً",
      "adm.applyPlan": "تطبيق خطة (اختياري)",
      "adm.noPlanManual": "— بلا خطة (إعداد يدوي) —",
      "change": "تغيير",
      "verifying": "جارٍ التحقق…",
      "addNewFinding": "إضافة ملاحظة فحص جديدة",
      "submitFinding": "إرسال ملاحظة الفحص",
      "svcCard.confirmAcceptService": "هل تقبل هذه الخدمة؟",
      "inspection.addPhoto": "إضافة صورة",
      "inspection.photoAlt": "صورة فحص",
      "odometerReading": "قراءة العداد",
      "task": "مهمة",
      "cd.customSubscription": "مخصصة",
      "po.view": "عرض",
      "docDesign.type.job_card": "بطاقة عمل",
      "docDesign.stampSection": "ختم الفرع",
      "docDesign.stampChoose": "اختر صورة الختم (PNG)",
      "docDesign.stampHint": "يُنصح بخلفية شفافة، ولا يتجاوز الحجم 1 ميجابايت.",
      "docDesign.stampRemove": "إزالة",
      "docDesign.stampPreviewAlt": "معاينة الختم",
      "docDesign.stampTooLarge": "يجب ألا يتجاوز حجم الختم 1 ميجابايت.",
      "invBank.title": "تفاصيل الحساب البنكي للفواتير",
      "invBank.desc": "تحكَّم في الحساب البنكي المطبوع على فواتيرك.",
      "invBank.show": "إظهار التفاصيل البنكية على الفواتير",
      "invBank.bankName": "اسم البنك (اختياري)",
      "invBank.accountHolder": "اسم صاحب الحساب (اختياري)",
      "invBank.iban": "رقم الآيبان",
      "invBank.payoutNote": "عند ترك رقم الآيبان فارغاً، تُظهر الفواتير حساب التحويلات المسجَّل.",
      "invBank.save": "حفظ التفاصيل البنكية",
      "invBank.loadFailed": "تعذر تحميل التفاصيل البنكية للفواتير",
      "invBank.saved": "تم حفظ التفاصيل البنكية للفواتير",
      "invBank.saveFailed": "تعذر حفظ التفاصيل البنكية للفواتير",
      "invBank.scopeBranch": "هذا الفرع",
      "invBank.scopeCompany": "الافتراضي للشركة",
      "invBank.inheritedNote": "هذا الفرع يستخدم الحساب الافتراضي للشركة.",
      "invBank.useCompany": "استخدم الحساب الافتراضي للشركة",
      "mb.branch": "الفرع",
      "mb.chooseBranch": "اختر الفرع",
      "mb.lockedToJobCard": "نفس فرع أمر العمل {{number}}",
      "mb.lockedToQuotation": "نفس فرع عرض السعر {{number}}",
      "mb.lockedToInvoice": "نفس فرع الفاتورة {{number}}",
      "mb.branchChip": "الفرع: {{name}}",
      "mb.allBranches": "كل الفروع",
      "mb.allBranchesHint": "عرض جميع الفروع معاً",
      "mb.signinPickTitle": "اختر الفرع الذي ستعمل عليه",
      "mb.signinPickHint": "يمكنك تغيير الفرع في أي وقت من الشريط العلوي.",
      "mb.homeBranch": "الفرع الرئيسي",
      "mb.branchChangeResets": "تغيير الفرع يمسح العميل والبنود.",
      "mb.assistantPickBranch": "لأي فرع هذا؟",
      "mb.customerOtherBranch": "هذا العميل تابع لفرع آخر.",
      "stamp.title": "ختم الفرع",
      "stamp.desc": "ارفع ختم فرعك. يُطبع على جميع المستندات باستثناء قسائم الرواتب.",
      "stamp.hint": "يُنصح بخلفية شفافة، بصيغة PNG وبحجم لا يتجاوز 1 ميجابايت. يُطبع على جميع المستندات باستثناء قسائم الرواتب.",
      "stamp.current": "الختم الحالي",
      "stamp.currentAlt": "ختم الفرع الحالي",
      "stamp.empty": "لم يُرفع ختم بعد",
      "stamp.choose": "اختر صورة الختم (PNG)",
      "stamp.choosePrompt": "اختر ملف PNG",
      "stamp.preview": "معاينة",
      "stamp.previewAlt": "معاينة الختم",
      "stamp.upload": "رفع الختم",
      "stamp.uploading": "جارٍ الرفع…",
      "stamp.remove": "إزالة الختم",
      "stamp.removing": "جارٍ الإزالة…",
      "stamp.tooLarge": "يجب ألا يتجاوز حجم الختم 1 ميجابايت.",
      "stamp.pngOnly": "صيغة PNG فقط هي المدعومة.",
      "stamp.uploaded": "تم رفع ختم الفرع بنجاح",
      "stamp.removed": "تمت إزالة ختم الفرع بنجاح",
      "stamp.loadFailed": "تعذر تحميل ختم الفرع",
      "stamp.uploadFailed": "تعذر رفع ختم الفرع",
      "stamp.removeFailed": "تعذر إزالة ختم الفرع",
      "jc.printPdfFailed": "تعذر إنشاء ملف بطاقة العمل",
      "perm.advanced.noEffect": "بلا أثر حالياً",
      "perm.warn.partial": "«{{job}}» لن يعمل: ينقصه {{keys}}",
      "perm.warn.lost": "سيفقد الدور: {{job}}",
      "perm.warn.lostTitle": "هل تحفظ تغييرات تُزيل أعمالاً من هذا الدور؟",
      "perm.warn.noUsers": "لا يوجد مستخدمون في هذا الدور",
      "perm.compare.title": "مقارنة الأدوار والقوالب",
      "perm.compare.open": "مقارنة",
      "perm.compare.differencesOnly": "إظهار الاختلافات فقط",
      "perm.compare.group.templates": "القوالب",
      "perm.compare.pickHint": "يمكن مقارنة 3 عناصر كحد أقصى",
      "perm.compare.unsaved": "(غير محفوظ)",
      "perm.compare.type.role": "دور",
      "perm.compare.type.template": "قالب",
      "perm.compare.state.on": "ممنوح",
      "perm.compare.state.partial": "جزئي",
      "perm.compare.state.off": "غير ممنوح",
      "perm.compare.remove": "إزالة",
      "perm.compare.differCount_zero": "لا توجد أعمال مختلفة",
      "perm.compare.differCount_one": "عمل واحد مختلف",
      "perm.compare.differCount_two": "عملان مختلفان",
      "perm.compare.differCount_few": "{{count}} أعمال مختلفة",
      "perm.compare.differCount_many": "{{count}} عملاً مختلفاً",
      "perm.compare.differCount_other": "{{count}} عمل مختلف",
      "perm.compare.added": "مضاف عن القالب",
      "perm.compare.removed": "محذوف عن القالب",
      "perm.compare.same": "مطابق للقالب",
      "perm.reset.title": "إعادة إلى القالب",
      "perm.reset.confirm": "هل تعيد هذا الدور إلى قالب «{{template}}»؟ ستُستبدل التغييرات غير المحفوظة، ثم اضغط «حفظ» للاعتماد.",
      "perm.template.jobCount_zero": "لا يغطي أي عمل",
      "perm.template.jobCount_one": "يغطي عملاً واحداً",
      "perm.template.jobCount_two": "يغطي عملين",
      "perm.template.jobCount_few": "يغطي {{count}} أعمال",
      "perm.template.jobCount_many": "يغطي {{count}} عملاً",
      "perm.template.jobCount_other": "يغطي {{count}} عمل",
      "perm.template.start": "ابدأ من هذا القالب",
      "perm.template.reset": "أعِد إلى هذا القالب",
      "perm.create.pickTemplate": "ابدأ من قالب (اختياري)",
      "perm.create.blank": "ابدأ دون قالب",
      "perm.roles.noUsers": "بلا مستخدمين",
      "perm.roles.userCount_zero": "بلا مستخدمين",
      "perm.roles.userCount_one": "مستخدم واحد",
      "perm.roles.userCount_two": "مستخدمان",
      "perm.roles.userCount_few": "{{count}} مستخدمين",
      "perm.roles.userCount_many": "{{count}} مستخدماً",
      "perm.roles.userCount_other": "{{count}} مستخدم",
      "perm.users.title": "مستخدمو هذا الدور",
      "perm.users.none": "لا يوجد مستخدمون في هذا الدور",
      "sa.templateRole": "قالب",
      "unsavedChanges": "تغييرات غير محفوظة",
      "revert": "تجاهل",
      "sa.rolePermissionsHeading": "صلاحيات {{role}}",
      "sa.customRole": "دور مخصص",
      "sa.rolePermissionCount_zero": "لا صلاحيات",
      "sa.rolePermissionCount_one": "صلاحية واحدة",
      "sa.rolePermissionCount_two": "صلاحيتان",
      "sa.rolePermissionCount_few": "{{count}} صلاحيات",
      "sa.rolePermissionCount_many": "{{count}} صلاحية",
      "sa.rolePermissionCount_other": "{{count}} صلاحية",
      "sa.creatingRole": "جاري الإنشاء...",
      "sa.createRole": "إنشاء الدور",
      "sa.roleName": "اسم الدور بالإنجليزية",
      "sa.roleNamePlaceholder": "مثال: Supervisor",
      "sa.roleNameAr": "اسم الدور بالعربية",
      "sa.roleNameArPlaceholder": "مثال: مشرف، فني تكييف",
      "sa.roleDescriptionPlaceholder": "وصف موجز لهذا الدور",
      "sa.afterCreatingRoleHint": "بعد إنشاء الدور، يمكنك تعيين الصلاحيات له.",
      "purchaseHistory": "سجل المشتريات",
      "totalPurchases": "إجمالي المشتريات",
      "totalQuantity": "إجمالي الكمية",
      "averageCost": "متوسط التكلفة",
      "lastPurchase": "آخر عملية شراء",
      "noPurchaseHistory": "لا يوجد سجل مشتريات",
      "noPurchaseHistoryDescription": "لم يتم تسجيل أي مشتريات لهذه القطعة بعد.",
      "viewDetails": "عرض التفاصيل",
      "enterNotes": "أدخل الملاحظات",
      "create": "إنشاء",
      // Unified status labels — single source of truth used by StatusBadge (statusConfig.js).
      // NOTE: flat dotted keys — see the note in the English block.
      'status.draft': 'مسودة',
      'status.received': 'مستلم',
      'status.diagnosis': 'تشخيص',
      'status.pending_customer_approval': 'بانتظار موافقة العميل',
      'status.waiting_parts': 'في انتظار القطع',
      'status.in_progress': 'قيد التنفيذ',
      'status.quality_check': 'فحص الجودة',
      'status.ready': 'جاهز',
      'status.pending_payment': 'في انتظار الدفع',
      'status.awaiting_payment': 'بانتظار الدفع',
      'status.delivered_pending_payment': 'تم التسليم — بانتظار الدفع',
      'status.delivered': 'تم التسليم',
      'status.closed': 'مغلق',
      'status.on_hold': 'معلق',
      'status.cancelled': 'ملغي',
      'status.pending': 'قيد الانتظار',
      'status.completed': 'مكتمل',
      'status.approved': 'موافق عليه',
      'status.rejected': 'مرفوض',
      'status.partially_approved': 'موافق عليه جزئياً',
      'status.qc_passed': 'اجتاز فحص الجودة',
      'status.qc_failed': 'فشل في فحص الجودة',
      'status.under_qc': 'قيد فحص الجودة',
      'status.sent': 'مرسل',
      'status.paid': 'مدفوع',
      'status.partially_paid': 'مدفوع جزئياً',
      'status.unpaid': 'غير مدفوع',
      'status.overdue': 'متأخر',
      'status.expired': 'منتهي الصلاحية',
      'status.converted': 'تم التحويل',
      'status.active': 'نشط',
      'status.inactive': 'غير نشط',
      'status.pending_approval': 'بانتظار الموافقة',
      'status.accepted_by_admin': 'مقبولة من الإدارة',
      'status.installed': 'تم التركيب',
      'status.partial': 'مدفوع جزئياً',
      'claimStatus.intake': 'استلام أولي',
      'claimStatus.report_attached': 'تم إرفاق التقرير',
      'claimStatus.estimate_received': 'تم استلام التقدير',
      'claimStatus.submitted_to_insurer': 'مقدم لشركة التأمين',
      'claimStatus.approved': 'موافق عليه',
      'claimStatus.partial': 'موافقة جزئية',
      'claimStatus.rejected': 'مرفوض',
      'claimStatus.surveyor_inspected': 'تم فحص المعاين',
      'claimStatus.in_repair': 'قيد الإصلاح',
      'claimStatus.supplementary_approval': 'موافقة إضافية',
      'claimStatus.supplementary_approved': 'تمت الموافقة الإضافية',
      'claimStatus.completed': 'مكتمل',
      'claimStatus.surveyor_verified': 'تم تحقق المعاين',
      'claimStatus.billed_to_insurer': 'مفوتر لشركة التأمين',
      'claimStatus.settled': 'مسوى',
      'insuranceClaim.title': 'مطالبة التأمين',
      'insuranceClaim.subtitle': 'تسجيل تفاصيل المطالبة لبطاقة العمل هذه.',
      'insuranceClaim.loadFailed': 'فشل تحميل مطالبة التأمين',
      'insuranceClaim.saved': 'تم حفظ مطالبة التأمين',
      'insuranceClaim.saveFailed': 'فشل حفظ مطالبة التأمين',
      'insuranceClaim.payerType': 'نوع الدافع',
      'insuranceClaim.payerTypeInsurerCashless': 'تأمين بدون دفع نقدي',
      'insuranceClaim.payerTypeCustomerCash': 'دفع نقدي من العميل',
      'insuranceClaim.insurer': 'شركة التأمين',
      'insuranceClaim.selectInsurer': 'اختر شركة التأمين',
      'insuranceClaim.policyNumber': 'رقم الوثيقة',
      'insuranceClaim.claimNumber': 'رقم المطالبة',
      'insuranceClaim.najmReportNo': 'رقم تقرير نجم',
      'insuranceClaim.faultPercent': 'نسبة الخطأ %',
      'insuranceClaim.taqdeerReportNo': 'رقم تقرير تقدير',
      'insuranceClaim.approvalReference': 'مرجع الموافقة',
      'insuranceClaim.waitingInsurerApproval': 'بانتظار موافقة شركة التأمين',
      'insuranceClaim.estimatePreparedNote': 'تم إعداد التقدير للتصدير اليدوي — لا يُرسل إلى العميل.',
      'insuranceClaim.downloadEstimate': 'تنزيل التقدير (PDF)',
      'insuranceClaim.approvalRefPlaceholder': 'مرجع موافقة شركة التأمين',
      'insuranceClaim.confirmApprovalStartRepair': 'تأكيد الموافقة وبدء الإصلاح',
      'insuranceClaim.approvalRecorded': 'تم تسجيل موافقة شركة التأمين — يمكن بدء الإصلاح',
      'insuranceClaim.approvalRefRequired': 'أدخل مرجع موافقة شركة التأمين',
      'insuranceClaim.approvedAmountOptional': 'المبلغ المعتمد (اختياري)',
      'insuranceClaim.approvedAmountHint': 'اتركه فارغاً لاعتماد كامل قيمة التقدير',
      'insuranceClaim.estimateApproved': 'تم اعتماد التقدير',
      'insuranceClaim.estimateApprovedNote': 'وافقت شركة التأمين على هذا التقدير.',
      'insuranceClaim.estimateNo': 'رقم التقدير',
      'insuranceClaim.invoices': 'الفواتير',
      'insuranceClaim.invoiceDownloadFailed': 'تعذّر تنزيل الفاتورة',
      'insuranceClaim.estimateDownloadFailed': 'فشل تنزيل التقدير',
      'insuranceClaim.noClaim': 'لا توجد مطالبة تأمين لهذه البطاقة',
      'insuranceClaim.surveyorName': 'اسم المعاين',
      'insuranceClaim.deductibleAmount': 'مبلغ التحمل',
      'insuranceClaim.liabilityPercent': 'نسبة المسؤولية %',
      'insuranceClaim.totalLoss': 'خسارة كلية',
      'insuranceClaim.totalLossThreshold': 'حد الخسارة الكلية',
      'insuranceClaim.sumInsured': 'مبلغ التأمين',
      'insuranceClaim.sumInsuredPayout': 'تعويض مبلغ التأمين',
      'insuranceClaim.iban': 'آيبان',
      'insuranceClaim.excludedItems': 'العناصر المستثناة',
      'insuranceClaim.addExcludedItem': 'إضافة عنصر',
      'insuranceClaim.noExcludedItems': 'لا توجد عناصر مستثناة',
      'insuranceClaim.itemType': 'النوع',
      'insuranceClaim.itemDescription': 'الوصف',
      'insuranceClaim.itemAmount': 'المبلغ',
      'insuranceClaim.notes': 'ملاحظات',
      'insuranceClaim.createClaim': 'إنشاء مطالبة',
      'insuranceClaim.updateClaim': 'تحديث المطالبة',
      'insuranceClaim.lifecycleActions': 'إجراءات دورة حياة المطالبة',
      'insuranceClaim.repairGated': 'الإصلاح متوقّف حتى تتم الموافقة على المطالبة ويعاين المخمّن المركبة.',
      'insuranceClaim.exposure': 'انكشاف المركز: {{amount}} (التكلفة الفعلية أعلى من المبلغ المعتمد)',
      'insuranceClaim.action.report_attached': 'إرفاق التقرير',
      'insuranceClaim.action.estimate_received': 'تم استلام التقدير',
      'insuranceClaim.action.send_estimate': 'إرسال التقدير لشركة التأمين',
      'insuranceClaim.action.decision': 'تسجيل قرار شركة التأمين',
      'insuranceClaim.action.surveyor_inspect': 'تمت معاينة المخمّن',
      'insuranceClaim.action.start_repair': 'بدء الإصلاح',
      'insuranceClaim.action.request_supplementary': 'طلب موافقة تكميلية',
      'insuranceClaim.action.mark_completed': 'تحديد كمكتمل',
      'insuranceClaim.action.approve_supplementary': 'اعتماد التكميلي',
      'insuranceClaim.action.resume_repair': 'استئناف الإصلاح',
      'insuranceClaim.action.surveyor_verify': 'تحقق المخمّن',
      'insuranceClaim.action.submit': 'إرسال',
      'insuranceClaim.action.confirm': 'هل تريد المتابعة بهذا الإجراء؟',
      'insuranceClaim.action.done': 'تم تحديث المطالبة',
      'insuranceClaim.action.failed': 'فشل الإجراء',
      'insuranceClaim.action.fillRequired': 'يرجى تعبئة الحقول المطلوبة',
      'insuranceClaim.field.channel': 'القناة (بريد/بوابة/يدوي)',
      'insuranceClaim.field.reference': 'المرجع',
      'insuranceClaim.field.decision': 'القرار',
      'insuranceClaim.field.approval_reference': 'مرجع الاعتماد',
      'insuranceClaim.field.approved_amount': 'المبلغ المعتمد',
      'insuranceClaim.field.rejected_reason': 'سبب الرفض',
      'insuranceClaim.field.surveyor_name': 'اسم المخمّن',
      'insuranceClaim.field.notes': 'ملاحظات',
      'insuranceClaim.field.explanation': 'التفسير',
      'insuranceClaim.field.requested_amount': 'المبلغ المطلوب',
      'insuranceClaim.field.selectOption': 'اختر…',
      'insuranceClaim.decision.approved': 'موافق عليه',
      'insuranceClaim.decision.partial': 'جزئي',
      'insuranceClaim.decision.rejected': 'مرفوض',
      // Priority labels — flat dotted keys for the same reason as status.* above.
      'priority.low': 'منخفضة',
      'priority.normal': 'عادية',
      'priority.medium': 'متوسطة',
      'priority.high': 'عالية',
      'priority.urgent': 'عاجلة',
      // Payment methods
      paymentMethods: {
        cash: 'نقداً',
        card: 'بطاقة',
        bank_transfer: 'تحويل بنكي',
        online: 'دفع إلكتروني',
        pos: 'نقطة بيع',
        check: 'شيك',
        cheque: 'شيك',
        mobile_payment: 'دفع عبر الجوال',
        myfatoorah: 'ماي فاتورة',
        tamara: 'تمارا',
        tabby: 'تابي',
        manual: 'يدوي',
        return: 'مرتجع',
        other: 'أخرى',
        receipt_credit: 'رصيد سند قبض العميل',
        receipt: 'سند قبض',
      },
      // Issue report types
      issueTypes: {
        quality_issue: 'مشكلة جودة',
        missing_part: 'قطعة مفقودة',
        wrong_part: 'قطعة غير صحيحة',
        damage: 'ضرر',
        additional_work: 'عمل إضافي',
        customer_approval: 'موافقة العميل',
        assistance: 'طلب مساعدة',
        other: 'أخرى',
      },
      // Job card activity history messages
      history: {
        activityHistory: 'سجل النشاط',
        noActivity: 'لا يوجد سجل نشاط حتى الآن.',
        created: 'تم إنشاء أمر العمل',
        statusChange: 'تم تغيير الحالة من "{{from}}" إلى "{{to}}"',
        partAdded: 'تمت إضافة قطعة: {{part}} (الكمية: {{qty}})',
        partRemoved: 'تمت إزالة قطعة: {{part}}',
        serviceAdded: 'تمت إضافة خدمة: {{service}}',
        serviceRemoved: 'تمت إزالة خدمة: {{service}}',
        quotationGenerated: 'تم إنشاء عرض السعر {{number}} ({{amount}} ريال)',
        quotationApproved: 'وافق العميل على عرض السعر {{number}} ({{amount}} ريال)',
        invoiceGenerated: 'تم إنشاء الفاتورة {{number}} - {{status}} ({{amount}} ريال)',
        invoicePaid: 'تم تحديد الفاتورة {{number}} كمدفوعة ({{amount}} ريال)',
        paymentReceived: 'تم استلام دفعة: {{amount}} ريال ({{method}}) - إجمالي المدفوع: {{totalPaid}} ريال، المتبقي: {{remaining}} ريال',
        assignedTo: 'تم الإسناد إلى {{name}}',
        unassignedFrom: 'تم إلغاء الإسناد من {{name}}',
        detailsUpdated: 'تم تحديث تفاصيل أمر العمل',
        partApproved: 'تمت الموافقة على القطعة: {{part}}',
        partRejected: 'تم رفض القطعة: {{part}}',
        taskCreated: 'تم إنشاء مهمة: {{task}}',
        taskAssigned: 'تم إسناد المهمة "{{task}}" إلى {{mechanic}}',
        taskStarted: 'تم بدء المهمة: {{task}}',
        taskCompleted: 'تم إكمال المهمة: {{task}}',
        qcPassed: 'اجتاز فحص الجودة: {{task}}',
        qcFailed: 'لم يجتز فحص الجودة: {{task}}',
        serviceAddedToTask: 'تمت إضافة الخدمة "{{service}}" إلى {{task}} ({{price}} ريال)',
        serviceCompleted: 'تم إكمال الخدمة: {{service}}',
        partAddedToTask: 'تمت إضافة القطعة "{{part}}" (الكمية: {{qty}}) إلى {{task}}',
        partInstalled: 'تم تركيب القطعة: {{part}} (الكمية: {{qty}})',
        findingSubmitted: 'تم تسجيل ملاحظة فنية: {{description}} ({{severity}})',
        findingApproved: 'تمت الموافقة على الملاحظة الفنية: {{description}}',
        findingRejected: 'تم رفض الملاحظة الفنية: {{description}}',
        userAssigned: 'تم إسناد المستخدم: {{user}}',
        quotationApprovedByCustomer: 'تمت موافقة العميل على عرض السعر {{number}}',
        paymentReceivedForInvoice: 'تم استلام دفعة للفاتورة {{number}} عبر {{method}} ({{amount}} ريال)',
        invoicePaidShort: 'تم سداد الفاتورة {{number}} ({{amount}} ريال)',
      },
      theme: {
        light: 'الوضع الفاتح',
        dark: 'الوضع الداكن',
      },
      navMenu: 'القائمة',
      noJobsHere: 'لا توجد أوامر عمل',
      collapseMenu: 'طي القائمة',
      expandMenu: 'توسيع القائمة',
      'nav.pinMenu': 'إبقاء القائمة مفتوحة',
      'nav.unpinMenu': 'طي القائمة',
      commandPlaceholder: 'ابحث عن أمر عمل، لوحة، عميل، صفحة…',
      noResultsFound: 'لا توجد نتائج',
      'cmd.needHelp': 'تبحث عن إجابة؟',
      'cmd.askAi': 'اسأل المساعد الذكي: «{{query}}»',
      'cmd.searchManual': 'ابحث في دليل الاستخدام عن «{{query}}»',
      board: {
        view: 'لوحة',
        listView: 'قائمة',
        new: 'جديد',
        diagnosis: 'تشخيص',
        inService: 'قيد الصيانة',
        qc: 'فحص الجودة',
        ready: 'جاهز',
        payment: 'الدفع',
        done: 'منتهية',
      },
      login: {
        welcomeBack: 'مرحباً بعودتك!',
        otpSubtitle: 'أدخل رقم جوالك لاستلام رمز التحقق',
        mobileLogin: 'دخول بالجوال',
        adminLogin: 'دخول المشرف',
        mobileNumber: 'رقم الجوال',
        phoneHint: 'أدخل الرقم بدون الصفر (مثال: 501234567 أو 0501234567)',
        sendOtp: 'إرسال رمز التحقق',
        sendingOtp: 'جارٍ إرسال الرمز...',
        enterOtp: 'أدخل رمز التحقق',
        otpSentTo: 'إذا كان هذا الرقم مسجلاً لدينا، فقد أرسلنا الرمز إلى',
        change: 'تغيير',
        verificationCode: 'رمز التحقق',
        verifyLogin: 'تحقق وسجّل الدخول',
        verifying: 'جارٍ التحقق...',
        resendOtp: 'إعادة إرسال الرمز',
        resendIn: 'إعادة الإرسال بعد {{seconds}} ثانية',
        // Arabic takes its own plural categories (one/two/few/many), not just
        // one/other — "2 attempts" is a distinct form, and only 1 and 2 can ever
        // reach the screen since the third wrong code locks.
        otpAttemptsLeft_one: 'بقيت محاولة واحدة',
        otpAttemptsLeft_two: 'بقيت محاولتان',
        otpAttemptsLeft_few: 'بقيت {{count}} محاولات',
        otpAttemptsLeft_many: 'بقيت {{count}} محاولة',
        otpAttemptsLeft_other: 'بقيت {{count}} محاولة',
        otpLockedTitle: 'محاولات خاطئة كثيرة',
        otpLockedIn: 'حاول مرة أخرى بعد {{time}}',
        otpSentWhatsapp: 'إذا كان هذا الرقم مسجلاً لدينا، فسيصلك الرمز عبر واتساب.',
        checkWhatsapp: 'تحقق من واتساب',
        checkWhatsappHint: 'إذا كان هذا الرقم مسجلاً لدينا، فسيصلك رمز الدخول في رسالة واتساب.',
        otpNotReceivedHint: 'لم يصلك الرمز؟ تأكّد من أن الرقم أعلاه صحيح ومسجّل لدى مركزك، أو اضغط «تغيير».',
        sendSmsInstead: 'إرسال برسالة نصية بدلاً من ذلك',
        adminAccess: 'دخول الإدارة',
        adminSubtitle: 'تسجيل دخول آمن لمشرفي النظام',
        emailAddress: 'البريد الإلكتروني',
        password: 'كلمة المرور',
        signIn: 'تسجيل الدخول',
        signingIn: 'جارٍ تسجيل الدخول...',
        authFailed: 'فشل تسجيل الدخول',
        enterPhone: 'يرجى إدخال رقم الجوال',
        enterOtpError: 'يرجى إدخال رمز التحقق',
        otpSent: 'إذا كان هذا الرقم مسجلاً لدينا، فالرمز في طريقه إليك.',
        loginSuccess: 'تم تسجيل الدخول بنجاح!',
        copyright: '© {{year}} شركة تزامن العربية لتقنية المعلومات. جميع الحقوق محفوظة.',
        heroTitle: 'أدر مركزك بثقة',
        heroSubtitle:
          'موتورمايند يجمع أوامر العمل والمخزون وعروض الأسعار والمدفوعات في منصة واحدة ذكية لمراكز خدمات السيارات.',
        heroPoint1: 'متابعة الأعمال لحظة بلحظة',
        heroPoint2: 'فواتير إلكترونية متوافقة مع هيئة الزكاة والضريبة والجمارك (ZATCA)',
        heroPoint3: 'موافقات العملاء عبر الرسائل النصية',
      },

      // Navigation
      dashboard: 'لوحة التحكم',
      jobCards: 'أوامر العمل',
      customers: 'العملاء',
      vehicles: 'المركبات',
      users: 'المستخدمين',
      invoices: 'الفواتير',
      quotationsEstimates: 'عروض الأسعار / التقديرات',
      'qe.tabQuotations': 'عروض الأسعار',
      'qe.tabEstimates': 'التقديرات',
      'qe.serial': 'رقم التقدير',
      'qe.version': 'الإصدار',
      'qe.insurer': 'شركة التأمين',
      'qe.vehicle': 'المركبة',
      'qe.approvedAmount': 'المبلغ المعتمد',
      'qe.approvalRef': 'مرجع الاعتماد',
      'qe.download': 'تنزيل PDF',
      'qe.openJobCard': 'فتح بطاقة العمل',
      'qe.empty': 'لا توجد تقديرات بعد',
      'qe.statusPrepared': 'جاهز',
      permissions: 'الصلاحيات',
      reports: 'التقارير',
      logout: 'تسجيل الخروج',
      printJobCard: 'طباعة بطاقة العمل',
      'exp.title': 'مصاريف أخرى',
      'exp.note': 'تُسجَّل للمتابعة فقط — غير مُدرجة في فاتورة العميل.',
      'exp.total': 'الإجمالي',
      'exp.add': 'إضافة مصروف',
      'exp.description': 'الوصف',
      'exp.descriptionPlaceholder': 'مثال: سطحة، لحام خارجي',
      'exp.amount': 'المبلغ',
      'exp.notes': 'ملاحظات (اختياري)',
      'exp.attach': 'إرفاق إيصال (صورة/PDF)',
      'exp.view': 'الإيصال',
      'exp.empty': 'لا توجد مصاريف مسجّلة بعد.',
      'exp.added': 'تم تسجيل المصروف',
      'exp.addFailed': 'تعذّر إضافة المصروف',
      'exp.fillFields': 'أدخل وصفاً ومبلغاً صحيحاً',
      'exp.deleteConfirm': 'حذف هذا المصروف؟',
      'exp.deleteFailed': 'تعذّر حذف المصروف',
      'session.expiredIdle': 'تم تسجيل الخروج بسبب عدم النشاط لمدة 30 دقيقة',
      companies: 'الشركات',
      subscriptions: 'الاشتراكات',
      allUsers: 'جميع المستخدمين',
      configurations: 'التكوينات',

      // Insurance companies
      'ins.nav': 'شركات التأمين',
      'ins.title': 'شركات التأمين',
      'ins.subtitle': '{{count}} شركة، {{active}} نشطة',
      'ins.add': 'إضافة شركة',
      'ins.addTitle': 'إضافة شركة تأمين',
      'ins.editTitle': 'تعديل شركة التأمين',
      'ins.name': 'الاسم',
      'ins.nameAr': 'الاسم بالعربية',
      'ins.vatNumber': 'الرقم الضريبي',
      'ins.crNumber': 'رقم السجل التجاري',
      'ins.address': 'العنوان',
      'ins.buildingNumber': 'رقم المبنى',
      'ins.district': 'الحي',
      'ins.city': 'المدينة',
      'ins.postalCode': 'الرمز البريدي',
      'ins.buildingNumberInvalid': 'رقم المبنى يجب أن يتكون من 4 أرقام بالضبط',
      'ins.postalCodeInvalid': 'الرمز البريدي يجب أن يتكون من 5 أرقام بالضبط',
      'ins.contactEmail': 'البريد الإلكتروني للتواصل',
      'ins.contactPhone': 'هاتف التواصل',
      'ins.defaultPaymentTerms': 'شروط الدفع الافتراضية',
      'ins.agreedLaborRate': 'سعر العمالة المتفق عليه',
      'ins.partsPricingBasis': 'أساس تسعير القطع',
      'ins.approvalLimit': 'حد الموافقة',
      'ins.status': 'الحالة',
      'ins.active': 'نشطة',
      'ins.inactive': 'غير نشطة',
      'ins.actions': 'الإجراءات',
      'ins.edit': 'تعديل',
      'ins.deactivate': 'إلغاء التفعيل',
      'ins.save': 'حفظ الشركة',
      'ins.saving': 'جاري الحفظ...',
      'ins.created': 'تم إنشاء شركة التأمين',
      'ins.updated': 'تم تحديث شركة التأمين',
      'ins.deactivated': 'تم إلغاء تفعيل شركة التأمين',
      'ins.loadFailed': 'فشل تحميل شركات التأمين',
      'ins.saveFailed': 'فشل حفظ شركة التأمين',
      'ins.deactivateFailed': 'فشل إلغاء تفعيل شركة التأمين',
      'ins.confirmDeactivate': 'إلغاء تفعيل {{name}}؟',
      'ins.emptyTitle': 'لا توجد شركات تأمين',
      'ins.emptyDesc': 'أنشئ أول سجل لشركة تأمين لهذا الفرع.',
      'ins.notSet': 'غير محدد',
      'ins.accessDenied': 'تم رفض الوصول',
      'ins.accessDeniedDesc': 'ليست لديك صلاحية إدارة شركات التأمين.',

      // Header
      welcomeBack: 'مرحباً بعودتك',
      staffView: 'عرض الموظف',
      adminView: 'عرض المدير',

      // Common
      save: 'حفظ',
      cancel: 'إلغاء',
      delete: 'حذف',
      add: 'إضافة',
      search: 'بحث',
      filter: 'تصفية',
      export: 'تصدير',
      import: 'استيراد',
      submit: 'إرسال',
      submitting: 'جاري الإرسال...',
      close: 'إغلاق',
      confirm: 'تأكيد',
      yes: 'نعم',
      no: 'لا',
      loading: 'جاري التحميل...',
      noData: 'لا توجد بيانات',
      error: 'خطأ',
      success: 'نجح',
      warning: 'تحذير',
      info: 'معلومات',

      // Forms
      time: 'الوقت',

      // Vehicle
      vehicleNotSaudiRegistered: 'المركبة غير مسجلة في السعودية',
      countryOfRegistration: 'بلد التسجيل',
      enterPlateNumberFreeText: 'أدخل رقم اللوحة',
      egDubaiUAE: 'مثال: دبي، الإمارات',
      make: 'الماركة',
      vinPlaceholder: 'رقم هيكل السيارة (VIN) — للحصول على أرقام القطع الأصلية الدقيقة',
      vinHelpMissing: 'لا يوجد رقم هيكل في ملف السيارة — أدخله للحصول على أرقام قطع أصلية دقيقة.',

      // Job Card
      jobCard: 'أمر العمل',
      jobNumber: 'رقم الأمر',
      mechanic: 'الفني',
      partsUsed: 'القطع المستخدمة',
      laborCost: 'تكلفة العمالة',
      partsCost: 'تكلفة القطع',

      // Language
      language: 'اللغة',
      english: 'English',
      arabic: 'العربية',

      // Dashboard
      quickActions: 'إجراءات سريعة',
      generateInvoice: 'إنشاء فاتورة',
      addCustomer: 'إضافة عميل',
      createJobCard: 'إنشاء أمر عمل',
      creating: 'جاري الإنشاء',
      recentActivity: 'النشاط الأخير',
      jobsByStatus: 'الأعمال حسب الحالة',
      totalCustomers: 'إجمالي العملاء',
      totalRevenue: 'إجمالي الإيرادات',
      activeJobs: 'الأعمال النشطة',
      // AI Assistant
      aiAssistant: 'المساعد الذكي',
      aiAssistantTitle: 'مساعد التشخيص الذكي',
      aiAssistantSubtitle: 'اسأل عن أكواد الأعطال أو تركيب القطع أو الأعراض لهذه المركبة',
      errorCode: 'كود العطل / DTC',
      errorCodePlaceholder: 'مثال: P0301',
      partInstall: 'تركيب / استبدال قطعة',
      partInstallPlaceholder: 'مثال: تيل الأمامي، بوبين الإشعال',
      symptomIssue: 'صوت / عرض',
      symptomPlaceholder: 'صف الصوت أو السلوك…',
      freeFormQuestion: 'سؤال آخر',
      freeFormPlaceholder: 'اسأل أي شيء عن هذه السيارة…',
      searchWebForLatest: 'ابحث في الإنترنت عن آخر الاستدعاءات / نشرات TSB / المواصفات',
      askAssistant: 'اسأل المساعد',
      aiAnalyzing: 'جارٍ التحليل… قد يستغرق ذلك حتى ~20 ثانية',
      aiDisclaimer: 'إرشادات ذكاء اصطناعي — تحقق من المواصفات الحرجة (عزم الربط، أرقام القطع) في دليل الخدمة الأصلي.',
      aiCodeNotApplicable: 'هذا الرمز لا ينتمي لهذه السيارة',
      aiVehicleDetailsHint: 'هذه التفاصيل غير موجودة في ملف السيارة. أضِفها لتشخيص أكثر دقة.',
      previousQueries: 'الاستشارات السابقة',
      noPreviousQueries: 'لا توجد استشارات سابقة بعد',
      provideAtLeastOne: 'أدخل حقلاً واحداً على الأقل',
      aiRequestFailed: 'فشل طلب الذكاء الاصطناعي. يرجى المحاولة مرة أخرى.',
      // AI Diagnosis Copilot — animated progress phases + TzamunAI branding
      aiPhaseThinking: 'جارٍ التفكير…',
      aiPhaseContext: 'جمع بيانات المركبة…',
      aiPhaseWeb: 'البحث في الإنترنت عن أحدث النشرات…',
      aiPhaseParts: 'مطابقة القطع مع مخزونك…',
      aiPhaseBuilding: 'إعداد التشخيص المنظّم…',
      aiPoweredBy: 'مدعوم بواسطة TzamunAI',
      // AI Diagnosis Copilot — structured card renderer (P2)
      aiReasoningAbout: 'يحلّل',
      aiUseJobCardContext: 'استخدم بيانات بطاقة العمل',
      aiCauses: 'الأسباب المحتملة',
      aiDiagnosticSteps: 'خطوات التشخيص',
      aiSuggestedParts: 'القطع المقترحة',
      aiSuggestedServices: 'الخدمات المقترحة',
      aiSpecsToVerify: 'مواصفات يجب التحقق منها',
      aiNotes: 'ملاحظات',
      aiWebSources: 'مصادر الويب',
      aiSearchedWeb: 'بحث في الويب',
      aiHowToConfirm: 'كيفية التأكد',
      aiProbability: 'الاحتمالية',
      aiConfidence: 'الثقة',
      aiSeverity: 'الخطورة',
      aiProbabilityHigh: 'عالية',
      aiProbabilityMedium: 'متوسطة',
      aiProbabilityLow: 'منخفضة',
      aiSeverityCritical: 'حرجة',
      aiSeverityHigh: 'عالية',
      aiSeverityMedium: 'متوسطة',
      aiSeverityLow: 'منخفضة',
      aiMustVerifyOem: 'يجب التحقق من المصنّع الأصلي',
      aiVerifyOem: 'تحقق من رقم القطعة الأصلي',
      aiInStock: 'متوفر',
      aiOutOfStock: 'غير متوفر — يلزم الطلب',
      aiNotInCatalog: 'غير موجود في الكتالوج',
      aiPartNumber: 'رقم القطعة',
      aiAiHint: 'اقتراح ذكاء اصطناعي — تحقق',
      aiPartNumberSource: 'مصدر رقم القطعة',
      aiAvgCost: 'متوسط التكلفة',
      aiSellingPrice: 'السعر',
      aiThinking: 'جارٍ التفكير…',
      addFromCatalog: 'من الكتالوج',
      addCustomPart: 'مخصص',
      aiEstLaborHours: 'ساعات العمل التقديرية',
      aiHours: 'س',
      aiQty: 'الكمية',
      aiAcceptSelected: 'اعتماد المحدد ← إنشاء ملاحظات',
      aiAcceptSelectedCount: 'اعتماد {{count}} محدد ← إنشاء ملاحظات',
      aiApplied: 'تم إنشاء الملاحظات',
      aiAcceptSuccess: 'تم إنشاء {{count}} ملاحظة قيد المراجعة',
      aiNothingSelected: 'اختر عنصراً واحداً على الأقل للاعتماد',
      aiRegenerate: 'إعادة التوليد',
      aiFollowUp: 'سؤال متابعة',
      aiFollowUpPlaceholder: 'اطرح سؤال متابعة حول هذا التشخيص…',
      aiSend: 'إرسال',
      aiCopy: 'نسخ',
      aiCopied: 'تم النسخ إلى الحافظة',
      aiCopyFailed: 'تعذّر النسخ',
      aiAnalyzingShort: 'جارٍ التحليل…',
      aiBuildingDiagnosis: 'يُنشئ تشخيصاً منظّماً…',
      aiSummary: 'الملخص',
      aiNoStructuredData: 'لا يوجد تشخيص منظّم — عرض الملاحظات.',

      // Dashboard — enhanced
      totalJobs: 'إجمالي الأعمال',
      pendingPayments: 'مدفوعات معلقة',
      revenueTrend: 'اتجاه الإيرادات',
      last6Months: 'آخر 6 أشهر',
      shortcuts: 'الاختصارات',
      quickLaunch: 'وصول سريع',
      viewAll: 'عرض الكل',
      needsAttention: 'يحتاج إلى انتباه',
      allCaughtUp: 'كل شيء على ما يرام — لا يوجد ما يحتاج إلى انتباه.',
      noRevenueData: 'لا توجد إيرادات مسجلة بعد',
      "dash.allBranches": 'كل الفروع',
      "dash.customize.edit": 'ترتيب الصفحة',
      "dash.customize.done": 'تم',
      "dash.customize.reset": 'استعادة الترتيب الافتراضي',
      "dash.customize.saved": 'تم حفظ ترتيب لوحتك',
      "dash.customize.saveFailed": 'تعذّر حفظ الترتيب — حاول مرة أخرى',
      "dash.customize.resetDone": 'رجعت اللوحة إلى الترتيب الافتراضي',
      "dash.customize.remove": 'إزالة هذه البطاقة',
      "dash.customize.dragHint": 'اسحب لإعادة الترتيب',
      "dash.customize.addTitle": 'الأدوات',
      "dash.customize.allAdded": 'كل البطاقات موجودة في لوحتك',
      "dash.customize.addWidget": 'إضافة',
      "dash.customize.placed": 'مُضافة',
      "dash.customize.emptyGrid": 'لوحة التحكم فارغة — اسحب أداة إلى هنا، أو اخترها من اللوحة',
      "dash.customize.resizeHint": 'اسحب لتغيير الحجم',
      "dash.ask.title": 'اسأل تزامُن AI',
      "dash.ask.placeholder": 'اسأل عن أمر عمل، عميل، أو أرقامك…',
      "dash.ask.send": 'اسأل',
      "dash.ask.hint": 'اضغط Enter لفتح المساعد بسؤالك',
      "dash.customize.widthThird": 'ثلث',
      "dash.customize.widthHalf": 'نصف',
      "dash.customize.widthTwoThirds": 'ثلثان',
      "dash.customize.widthFull": 'عرض كامل',
      "dash.widget.attention": 'يحتاج انتباهك',
      "dash.widget.pendingApprovals": 'الموافقات المعلّقة',
      "dash.pendingApprovals.title": 'بانتظار موافقتك',
      "dash.pendingApprovals.viewAll": 'عرض الكل',
      "dash.pendingApprovals.partial": 'قد لا تظهر كل الطلبات',
      "dash.pendingApprovals.empty": 'لا طلبات بانتظار موافقتك',
      "dash.pendingApprovals.loadFailed": 'تعذّر تحميل الطلبات المعلّقة',
      "dash.pendingApprovals.retry": 'إعادة المحاولة',
      "dash.pendingApprovals.expand": 'إظهار الطلبات بانتظار موافقتك',
      "dash.pendingApprovals.collapse": 'إخفاء الطلبات بانتظار موافقتك',
      "dash.pendingApprovals.chip.discount": 'خصومات',
      "dash.pendingApprovals.chip.purchaseInvoice": 'فواتير المشتريات',
      "dash.widget.kpis": 'الأرقام الرئيسية',
      "dash.widget.setupGuide": 'دليل الإعداد',
      "dash.widget.branchBreakdown": 'توزيع الفروع',
      "dash.widget.revenueTarget": 'هدف الإيرادات الشهري',
      greetingMorning: 'صباح الخير',
      // Arabic has no separate afternoon greeting — مساء الخير covers everything
      // from noon onward. 'طاب يومك' ("have a good day") is a farewell, not a
      // greeting, so the dashboard read wrong every afternoon.
      greetingAfternoon: 'مساء الخير',
      greetingEvening: 'مساء الخير',
      partsLowOnStock: 'قطعة منخفضة المخزون',
      awaitingCollection: 'في انتظار التحصيل',

      // Job Cards
      selectMechanic: 'اختر الفني',
      taskDescription: 'وصف المهمة',
      estimatedTime: 'الوقت المقدر',
      actualTime: 'الوقت الفعلي',
      tax: 'الضريبة',
      grandTotal: 'الإجمالي الكلي',
      dueDate: 'تاريخ الاستحقاق',
      startDate: 'تاريخ البدء',
      completionDate: 'تاريخ الإنجاز',
      high: 'عالية',
      medium: 'متوسطة',
      low: 'منخفضة',
      jobType: 'نوع الخدمة',
      regularService: 'خدمة عادية',
      quickService: 'خدمة سريعة',
      convertToRegular: 'تحويل إلى خدمة عادية',
      convertToRegularConfirm: 'هل أنت متأكد من تحويل هذه الخدمة السريعة إلى خدمة عادية؟ سيتم تفعيل سير عمل عرض السعر.',
      quickServiceNote: 'خدمة سريعة - لا حاجة لعرض سعر. قم بإنشاء الفاتورة مباشرة عند الجاهزية.',
      sendInvoiceToCustomer: 'إرسال الفاتورة للعميل',
      recordPaymentManually: 'تسجيل الدفع يدوياً',
      invoiceSentSuccessfully: 'تم إرسال الفاتورة للعميل بنجاح',
      paymentRecordedSuccessfully: 'تم تسجيل الدفع بنجاح',
      selectJobType: 'اختر نوع الخدمة',
      selectJobTypeDescription: 'اختر نوع الخدمة لأمر العمل هذا',
      regularServiceDescription: 'سير عمل قياسي مع عرض سعر وموافقة العميل',
      regularServiceFeature1: 'إرسال عرض سعر للعميل',
      regularServiceFeature2: 'انتظار موافقة العميل',
      regularServiceFeature3: 'إنشاء فاتورة بعد الإنجاز',
      quickServiceDescription: 'خدمة سريعة للعملاء الحاضرين بدون عرض سعر',
      quickServiceFeature1: 'لا حاجة لعرض سعر',
      quickServiceFeature2: 'إنشاء فاتورة مباشرة',
      quickServiceFeature3: 'يمكن التحويل لخدمة عادية عند الحاجة',
      fullCheckService: 'فحص شامل',
      fullCheckServiceDescription: 'فحص كامل للسيارة مع تقرير يستطيع العميل تحميله.',
      fullCheckServiceFeature1: 'اختيار قائمة الفحص التي يعتمدها المركز',
      fullCheckServiceFeature2: 'تسجيل كل نقطة، مع صور عند الحاجة',
      fullCheckServiceFeature3: 'تقرير PDF يحمّله العميل من رابط',
      insuranceJob: 'تأمين',
      insuranceJobDescription: 'إصلاح على حساب شركة التأمين مع متابعة المطالبة وفاتورة ضريبية للمنشآت.',
      insuranceJobFeature1: 'اختيار شركة التأمين وإدخال بيانات المطالبة',
      insuranceJobFeature2: 'تحديد دافع كل مهمة: التأمين أو العميل',
      insuranceJobFeature3: 'تقدير وتقرير نهائي وفاتورة ضريبية للمنشآت',
      fast: 'سريع',
      skipped: 'تم التخطي',

      // Customers
      newCustomer: 'عميل جديد',
      customerDetails: 'تفاصيل العميل',
      company: 'الشركة',
      idNumber: 'رقم الهوية',
      country: 'الدولة',
      postalCode: 'الرمز البريدي',
      customerType: 'نوع العميل',
      individual: 'فرد',
      business: 'شركة',

      // Vehicles
      newVehicle: 'مركبة جديدة',
      vehicleDetails: 'تفاصيل المركبة',
      mileage: 'المسافة المقطوعة',
      engineNumber: 'رقم المحرك',
      registrationDate: 'تاريخ التسجيل',
      lastServiceDate: 'تاريخ آخر صيانة',
      nextServiceDate: 'تاريخ الصيانة القادمة',
      motorcycle: 'دراجة نارية',

      // Parts
      newPart: 'قطعة جديدة',
      partDetails: 'تفاصيل القطعة',
      supplier: 'المورد',
      minStock: 'الحد الأدنى للمخزون',
      restock: 'إعادة التخزين',
      lowStock: 'مخزون منخفض',
      outOfStock: 'نفذ من المخزون',
      inStock: 'متوفر',
      avgCost: 'متوسط التكلفة',
      profit: 'الربح',
      bulkUpload: 'رفع جماعي',
      newPurchase: 'مشتريات جديدة',
      manageInventory: 'إدارة مخزون القطع وتتبع التكاليف',
      allCategories: 'جميع الفئات',
      partCategories: 'فئات القطع',
      // ---- multi-location inventory (loc.*) ----
      inventoryLocations: 'المستودعات',
      'loc.pageTitle': 'المستودعات',
      'loc.pageSubtitle': 'المخازن والمستودعات في هذا الفرع. تدخل القطع إلى المستودع الافتراضي ما لم يتم اختيار غيره.',
      'loc.addLocation': 'إضافة مستودع',
      'loc.colName': 'الاسم',
      'loc.colCode': 'الرمز',
      'loc.default': 'افتراضي',
      'loc.inactive': 'موقوف',
      'loc.retire': 'إيقاف',
      'loc.restore': 'إعادة تفعيل',
      'loc.editTitle': 'تعديل المستودع',
      'loc.newTitle': 'مستودع جديد',
      'loc.fieldName': 'الاسم',
      'loc.fieldNameAr': 'الاسم بالعربية',
      'loc.fieldCode': 'الرمز',
      'loc.codeHint': 'رمز مختصر، مثل MAIN أو OUT',
      'loc.makeDefault': 'اجعله المستودع الافتراضي',
      'loc.saved': 'تم حفظ المستودع',
      'loc.saveFailed': 'تعذر حفظ المستودع',
      'loc.loadFailed': 'تعذر تحميل المستودعات',
      'loc.nameAndCodeRequired': 'الاسم والرمز مطلوبان',
      'loc.defaultChanged': 'تم تغيير المستودع الافتراضي',
      'loc.emptyTitle': 'لا توجد مستودعات بعد',
      'loc.emptyBody': 'أضف مستودعاً لكل مكان تحفظ فيه القطع — المخزن الرئيسي، مخزن خارجي، سيارة.',
      'loc.stockHere': 'المخزون هنا',
      'loc.noStockHere': 'لا يوجد مخزون في هذا المستودع بعد.',
      'loc.pickPlaceholder': 'اختر مستودعاً',
      'loc.useDefault': 'المستودع الافتراضي للفرع',
      'loc.transferTitle': 'نقل مخزون',
      'loc.transferAction': 'نقل',
      'loc.transferDone': 'تم نقل المخزون',
      'loc.transferFailed': 'تعذر نقل المخزون',
      'loc.from': 'من',
      'loc.to': 'إلى',
      'loc.quantity': 'الكمية',
      'loc.notes': 'ملاحظات',
      'loc.currentBreakdown': 'أماكن هذه القطعة الآن',
      'loc.noStockAnywhere': 'لا يوجد مخزون لهذه القطعة في أي مستودع.',
      'loc.sameLocation': 'اختر مستودعين مختلفين.',
      'loc.availableHere': 'المتوفر هنا {{count}}',
      'loc.overAvailable': 'المتوفر في ذلك المستودع {{count}} فقط',
      'loc.moveStock': 'نقل مخزون',
      'loc.byLocation': 'حسب المستودع',
      'loc.destination': 'الاستلام في',
      'loc.unitOfMeasure': 'الوحدة',
      'loc.detailsSection': 'التفاصيل',
      'loc.totalOnHand': 'الإجمالي بالمخزن',
      'loc.totalValue': 'إجمالي القيمة',
      'loc.value': 'القيمة',
      'loc.nothingToMove': 'لا يوجد مخزون لهذه القطعة في أي مستودع، فلا يوجد ما يمكن نقله.',
      'loc.singleSourceHint': 'هذه القطعة موجودة في مستودع واحد فقط، لذا تم تثبيت مصدر النقل.',
      'loc.viewDetails': 'تفاصيل القطعة',
      'loc.inWarehouses': 'في {{count}} مستودعات',
      // ---- stock reports (rpt.*) ----
      'rpt.tabStockOnHand': 'المخزون المتوفر',
      'rpt.tabStockValuation': 'تقييم المخزون',
      'rpt.tabStockMovements': 'حركات المخزون',
      'rpt.partNumber': 'رقم القطعة',
      'rpt.part': 'القطعة',
      'rpt.stockLines': 'السطور',
      'rpt.stockUnits': 'الوحدات',
      'rpt.distinctParts': 'عدد القطع',
      'rpt.belowThreshold': 'تحت الحد الأدنى',
      'rpt.locations': 'المستودعات',
      'rpt.balanceAfter': 'الرصيد بعد',
      'rpt.performedBy': 'بواسطة',
      'rpt.movesIn': 'وحدات داخلة',
      'rpt.movesOut': 'وحدات خارجة',
      'rpt.valueIn': 'قيمة داخلة',
      'rpt.valueOut': 'قيمة خارجة',
      'rpt.positionReportNote': 'يعرض هذا التقرير المخزون كما هو الآن، لذا لا ينطبق عليه النطاق الزمني.',
      // Finance Completeness Task 1.8 — درجة سياسة تحويل المسحوبات + تقرير المصاريف المشتبه بها.
      'rpt.suspectExpensesTab': 'مصاريف مشتبه بها',
      'rpt.suspectExpensesDisclaimer': 'هذه قائمة استرشادية فقط وليست نتيجة قطعية. لا تُعيد تصنيف أي شيء تلقائيًا ولا تحتوي على أي إجراء هنا. قاعدة المبالغ المستديرة بالآلاف قد تُطابق أيضًا إيجارًا أو فواتير خدمات أو سلفة راتب عادية — راجع كل سطر بنفسك قبل اتخاذ أي إجراء.',
      'rpt.suspectExpensesVendor': 'المستفيد',
      'rpt.suspectExpensesNote': 'الملاحظة',
      'rpt.suspectExpensesAmount': 'المبلغ',
      'rpt.suspectExpensesReasons': 'سبب الإشارة',
      'rpt.suspectExpensesFlagged': 'مشار إليها',
      'rpt.suspectExpensesEmpty': 'لا توجد مصاريف قبل تاريخ البدء تطابق القواعد الاسترشادية.',
      'rpt.suspectReason_partner_name': 'يطابق اسم شريك',
      'rpt.suspectReason_keyword': 'الملاحظة تذكر سحب/مسحوبات',
      'rpt.suspectReason_round_no_attachment': 'مبلغ مستدير بالآلاف بدون مرفق',
      'rpt.drawingsCutoverLabel': 'تاريخ بدء احتساب المسحوبات',
      'rpt.drawingsCutoverHelp': 'يفحص المصاريف المسجّلة قبل هذا التاريخ لاحتمال كونها مسحوبات مالك/شريك. لا يتم تعديل السجل التاريخي تلقائيًا أبدًا.',
      'rpt.drawingsCutoverNotSet': 'حدد تاريخ بدء لفحص المصاريف المسجّلة قبله.',
      'rpt.drawingsCutoverSave': 'حفظ',
      'rpt.drawingsCutoverSaved': 'تم حفظ تاريخ البدء',
      'rpt.drawingsCutoverSaveError': 'تعذر حفظ تاريخ البدء',
      'rpt.move_in': 'إدخال',
      'rpt.move_out': 'إخراج',
      'rpt.move_transfer': 'نقل',
      'rpt.move_return': 'إرجاع',
      'rpt.move_damage': 'تالف',
      'rpt.move_adjustment': 'تسوية',
      'rpt.move_initial': 'رصيد افتتاحي',
      manageCategoriesDescription: 'إدارة فئات القطع للمركز',
      addCategory: 'إضافة فئة',
      editCategory: 'تعديل الفئة',
      categoryName: 'اسم الفئة',
      categoryNameArabic: 'اسم الفئة (بالعربية)',
      globalCategories: 'الفئات العامة',
      customCategories: 'الفئات المخصصة',
      sharedAcrossAllGarages: 'مشتركة بين جميع مراكز خدمات السيارات',
      noCustomCategories: 'لا توجد فئات مخصصة بعد',
      clickAddToCreate: 'انقر على "إضافة فئة" لإنشاء أول فئة مخصصة',
      selectCategory: 'اختر الفئة',
      
      // Purchase Invoices
      purchaseInvoices: 'فواتير المشتريات',
      newPurchaseInvoice: 'فاتورة مشتريات جديدة',
      purchaseInvoiceDetails: 'تفاصيل فاتورة المشتريات',
      invoiceFile: 'ملف الفاتورة (PDF/صورة)',
      clickToUpload: 'انقر لرفع ملف الفاتورة',
      viewFile: 'عرض الملف',
      items: 'العناصر',
      scanBarcode: 'مسح أو إدخال رمز المخزون/الباركود',
      selectExistingPart: 'اختر قطعة موجودة',
      searchPartByName: 'البحث عن قطعة بالاسم أو الرقم أو رمز المخزون...',
      noPartsFound: 'لم يتم العثور على قطع. جرب كلمات مختلفة أو أنشئ قطعة جديدة.',
      newPartCheckbox: 'قطعة جديدة',
      shippingCost: 'تكلفة الشحن (ريال)',
      vatIncludedInCost: 'ضريبة القيمة المضافة مشمولة في التكلفة',
      vatIncludedInCostDescription: 'لمراكز خدمات السيارات التي لا يمكنها استرداد الضريبة',
      shipping: 'الشحن',
      postImmediately: 'ترحيل فوري (تحديث المخزون والتكاليف الآن)',
      createAndPost: 'إنشاء وترحيل',
      saveAsDraft: 'حفظ كمسودة',
      enterSupplierName: 'أدخل اسم المورد',
      unitCost: 'تكلفة الوحدة',
      posted: 'مرحّل',
      postInvoice: 'ترحيل الفاتورة',
      maxStock: 'الحد الأقصى للمخزون',
      warehouse: 'المستودع',
      shelf: 'الرف',
      bin: 'الصندوق',
      sellingPrice: 'سعر البيع',
      margin: 'هامش الربح',

      // Invoices
      newInvoice: 'فاتورة جديدة',
      invoiceDetails: 'تفاصيل الفاتورة',
      invoiceDate: 'تاريخ الفاتورة',
      paymentDue: 'موعد الدفع',
      paymentMethod: 'طريقة الدفع',
      cash: 'نقداً',
      card: 'بطاقة',
      online: 'إلكتروني',
      myfatoorah: 'ماي فاتورة',
      tamara: 'تمارا',
      bankTransfer: 'تحويل بنكي',
      notSpecified: 'غير محدد',
      searchInvoices: 'بحث في الفواتير...',
      allStatuses: 'جميع الحالات',
      allPaymentMethods: 'جميع طرق الدفع',
      dateFrom: 'من تاريخ',
      dateTo: 'إلى تاريخ',
      invoicesFound: 'فاتورة',
      filtersActive: 'فلاتر نشطة',
      clearFilters: 'مسح الفلاتر',
      bank: 'تحويل بنكي',
      partiallyPaid: 'مدفوع جزئياً',
      overdue: 'متأخر',
      amountPaid: 'المبلغ المدفوع',
      amountDue: 'المبلغ المستحق',
      balance: 'الرصيد',

      // Services
      newService: 'خدمة جديدة',
      serviceDetails: 'تفاصيل الخدمة',
      serviceCategory: 'فئة الخدمة',
      duration: 'المدة',
      hours: 'ساعات',
      minutes: 'دقائق',

      // Users
      newUser: 'مستخدم جديد',
      userDetails: 'تفاصيل المستخدم',
      username: 'اسم المستخدم',
      confirmPassword: 'تأكيد كلمة المرور',
      inactive: 'غير نشط',
      lastLogin: 'آخر تسجيل دخول',

      // Reports
      salesReport: 'تقرير المبيعات',
      inventoryReport: 'تقرير المخزون',
      customerReport: 'تقرير العملاء',
      mechanicReport: 'تقرير الفنيين',
      from: 'من',
      generate: 'إنشاء',
      download: 'تحميل',
      print: 'طباعة',

      // Common Actions
      update: 'تحديث',
      duplicate: 'نسخ',
      archive: 'أرشفة',
      restore: 'استعادة',
      approve: 'موافقة',
      reject: 'رفض',
      send: 'إرسال',
      receive: 'استلام',
      transfer: 'تحويل',

      // Messages
      areYouSure: 'هل أنت متأكد؟',
      deleteConfirm: 'هل أنت متأكد من حذف هذا العنصر؟',
      cannotUndo: 'لا يمكن التراجع عن هذا الإجراء.',
      savedSuccessfully: 'تم الحفظ بنجاح',
      deletedSuccessfully: 'تم الحذف بنجاح',
      updatedSuccessfully: 'تم التحديث بنجاح',
      errorOccurred: 'حدث خطأ',
      pleaseWait: 'يرجى الانتظار...',
      processing: 'جاري المعالجة...',

      // Validation
      required: 'هذا الحقل مطلوب',
      invalidEmail: 'عنوان البريد الإلكتروني غير صحيح',
      invalidPhone: 'رقم الهاتف غير صحيح',
      passwordMismatch: 'كلمات المرور غير متطابقة',
      minLength: 'الحد الأدنى للطول هو',
      maxLength: 'الحد الأقصى للطول هو',

      // Pagination
      showing: 'عرض',
      of: 'من',
      entries: 'إدخالات',
      previous: 'السابق',
      next: 'التالي',
      page: 'صفحة',

      // Filters
      today: 'اليوم',
      thisWeek: 'هذا الأسبوع',
      thisMonth: 'هذا الشهر',
      custom: 'مخصص',
      apply: 'تطبيق',
      clear: 'مسح',
      reset: 'إعادة تعيين',

      // Job Card Statuses

      // Hold Feature
      putJobCardOnHold: 'تعليق أمر العمل',
      reasonForHold: 'سبب التعليق',
      selectReason: 'اختر سبباً...',
      customReason: 'سبب مخصص',
      enterCustomReason: 'أدخل سبباً مخصصاً...',
      holdWarningMessage: 'سيتم إيقاف أمر العمل مؤقتاً ووضع علامة "معلق". يمكنك استئنافه لاحقاً من تفاصيل أمر العمل.',
      putOnHold: 'تعليق',
      resumeJob: 'استئناف العمل',
      pleaseSelectReason: 'الرجاء اختيار أو إدخال سبب',
      jobCardOnHold: 'أمر العمل معلق',
      reason: 'السبب',
      since: 'منذ',
      waitingForParts: 'في انتظار قطع الغيار',
      waitingCustomerParts: 'في انتظار العميل لتوفير القطع',
      customerRequest: 'طلب العميل',
      technicalIssue: 'مشكلة فنية',
      awaitingExternalService: 'في انتظار خدمة خارجية',
      partsOnOrder: 'قطع الغيار قيد الطلب',
      other: 'أخرى',

      // Table Headers
      createdAt: 'تاريخ الإنشاء',
      updatedAt: 'تاريخ التحديث',

      // Error Messages & Toasts
      customerUpdatedSuccessfully: 'تم تحديث العميل بنجاح',
      customerCreatedSuccessfully: 'تم إنشاء العميل بنجاح',
      failedToCreateCustomer: 'فشل في إنشاء العميل',
      failedToUpdateCustomer: 'فشل في تحديث العميل',
      vehicleCreatedSuccessfully: 'تم إنشاء المركبة بنجاح',
      vehicleUpdatedSuccessfully: 'تم تحديث المركبة بنجاح',
      failedToCreateVehicle: 'فشل في إنشاء المركبة',
      failedToUpdateVehicle: 'فشل في تحديث المركبة',
      jobCardCreatedSuccessfully: 'تم إنشاء أمر العمل بنجاح',
      failedToCreateJobCard: 'فشل في إنشاء أمر العمل',
      openJobWarning: {
        title: 'السيارة لديها أمر عمل مفتوح بالفعل',
        message:
          'هذه السيارة لديها أمر عمل مفتوح رقم ⁨#{{jobNumber}}⁩ ({{status}}). هل تريد إنشاء أمر عمل آخر على أي حال؟',
        createAnyway: 'إنشاء على أي حال',
      },
      partAddedSuccessfully: 'تم إضافة القطعة بنجاح',
      failedToAddPart: 'فشل في إضافة القطعة',
      serviceCreatedSuccessfully: 'تم إنشاء الخدمة بنجاح',
      serviceUpdatedSuccessfully: 'تم تحديث الخدمة بنجاح',
      serviceDeletedSuccessfully: 'تم حذف الخدمة بنجاح',
      failedToDeleteService: 'فشل في حذف الخدمة',
      userCreatedSuccessfully: 'تم إنشاء المستخدم بنجاح',
      userUpdatedSuccessfully: 'تم تحديث المستخدم بنجاح',
      userDeletedSuccessfully: 'تم حذف المستخدم بنجاح',
      failedToDeleteUser: 'فشل في حذف المستخدم',
      operationFailed: 'فشلت العملية',
      pdfDownloaded: 'تم تحميل PDF',
      failedToDownloadPDF: 'فشل في تحميل PDF',
      invoiceDeletedSuccessfully: 'تم حذف الفاتورة بنجاح',
      failedToDeleteInvoice: 'فشل في حذف الفاتورة',
      cannotDeletePaidInvoice: 'لا يمكن حذف فاتورة مدفوعة. قم بإنشاء فاتورة إرجاع بدلاً من ذلك.',
      areYouSureDeleteInvoice: 'هل أنت متأكد من حذف الفاتورة',
      areYouSureDeleteService: 'هل أنت متأكد من حذف هذه الخدمة؟',
      areYouSureToggleUser: 'هل أنت متأكد من',
      activate: 'تفعيل',
      deactivate: 'إلغاء تفعيل',
      thisUser: 'هذا المستخدم؟',
      userActivated: 'تم تفعيل المستخدم بنجاح',
      userDeactivated: 'تم إلغاء تفعيل المستخدم بنجاح',
      failedToUpdateStatus: 'فشل في تحديث الحالة',
      areYouSureDeleteUser: 'هل أنت متأكد من حذف',
      thisWillDeactivateUser: 'سيؤدي هذا إلى إلغاء تفعيل المستخدم.',
      'users.confirmActivate': 'هل أنت متأكد أنك تريد تفعيل هذا المستخدم؟',
      'users.confirmDeactivate': 'هل أنت متأكد أنك تريد إلغاء تفعيل هذا المستخدم؟',
      'users.confirmDelete': 'هل أنت متأكد أنك تريد حذف {{name}}؟ سيؤدي هذا إلى إلغاء تفعيل المستخدم.',

      // Form Placeholders
      enterName: 'أدخل الاسم',
      enterEmail: 'أدخل البريد الإلكتروني',
      enterPhone: 'أدخل رقم الهاتف',
      enterAddress: 'أدخل العنوان',
      selectOption: 'اختر خياراً',
      searchCustomers: 'بحث عن العملاء',
      searchVehicles: 'بحث عن المركبات',
      searchParts: 'بحث عن القطع',

      // Customer Search Modal
      findCustomer: 'البحث عن عميل',
      customerNameMobile: 'العميل (الاسم/الجوال)',
      vinChassis: 'رقم الهيكل / الشاسيه',
      enterPlateNumber: 'أدخل رقم اللوحة...',
      enterVIN: 'أدخل رقم الهيكل أو الشاسيه...',
      enterCustomerNameOrMobile: 'أدخل اسم العميل أو رقم الجوال...',
      pleaseEnterSearchTerm: 'يرجى إدخال كلمة البحث',
      failedToSearchCustomers: 'فشل في البحث عن العملاء',
      searching: 'جاري البحث...',
      noCustomerFound: 'لم يتم العثور على عميل',
      createNewCustomerProfile: 'إنشاء ملف عميل جديد',

      // Modal Common
      editUser: 'تعديل المستخدم',
      createNewUser: 'إنشاء مستخدم جديد',
      editCustomer: 'تعديل العميل',
      addNewCustomer: 'إضافة عميل جديد',
      updateCustomer: 'تحديث العميل',
      createCustomer: 'إنشاء العميل',
      editVehicle: 'تعديل المركبة',
      viewVehicle: 'عرض المركبة',
      edit: 'تعديل',
      view: 'عرض',
      addNewVehicle: 'إضافة مركبة جديدة',
      updateVehicle: 'تحديث المركبة',
      createVehicle: 'إنشاء المركبة',
      selectVehicle: 'اختر المركبة',
      noVehiclesFound: 'لم يتم العثور على مركبات',
      addVehicleForCustomer: 'أضف مركبة لهذا العميل',

      // Job Card Modal
      vehicleReception: 'استقبال المركبة',
      selectACustomerFirst: 'اختر عميلاً أولاً',
      receptionChecklist: 'قائمة الاستقبال',
      mileageKM: 'المسافة المقطوعة (كم)',
      priority: 'الأولوية',

      // Part Modal
      addNewPart: 'إضافة قطعة جديدة',
      editPart: 'تعديل القطعة',
      sku: 'رمز المخزون',
      optional: 'اختياري',
      partName: 'اسم القطعة',
      description: 'الوصف',
      quantityInStock: 'الكمية في المخزون',
      minimumStockLevel: 'الحد الأدنى للمخزون',
      costPrice: 'سعر التكلفة',
      branch: 'الفرع',
      addPart: 'إضافة القطعة',

      // Restock Modal
      restockPart: 'إعادة تخزين القطعة',
      part: 'القطعة',
      currentStock: 'المخزون الحالي',
      quantityToAdd: 'الكمية المراد إضافتها',
      notes: 'ملاحظات',
      addStock: 'إضافة للمخزون',

      // Assign Mechanic Modal
      assignedMechanics: 'الفنيون المعينون',
      availableMechanics: 'الفنيون المتاحون',
      searchMechanics: 'بحث عن فنيين...',
      noMechanicsAssigned: 'لم يتم تعيين فنيين بعد',
      noMechanicsAvailable: 'لا يوجد فنيون متاحون',

      // Job Cards List
      unassigned: 'غير معين',
      manageAssignmentsTitle: 'إدارة التعيينات',

      // Table Headers - Common
      actions: 'الإجراءات',
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      role: 'الدور',

      // Table Headers - Parts
      price: 'السعر',
      stock: 'المخزون',
      compatibility: 'التوافق',
      category: 'الفئة',
      partNumber: 'رقم القطعة',
      partNumberPlaceholder: 'مثال: 04465-33450',

      // Table Headers - Vehicles
      owner: 'المالك',
      year: 'السنة',
      brandModel: 'الماركة والموديل',

      // Table Headers - Invoices
      invoiceNumber: 'رقم الفاتورة',
      date: 'التاريخ',
      amount: 'المبلغ',
      paymentStatus: 'حالة الدفع',

      // Status Labels (for display)
      cancelled: 'ملغي',
      waiting_parts: 'في انتظار القطع',
      waitingParts: 'في انتظار القطع',
      in_progress: 'قيد التنفيذ',

      // Services Table
      estTime: 'الوقت المقدر',

      // Quotations Table
      quotationNumber: 'رقم العرض',
      validUntil: 'صالح حتى',

      // Invoice/Quotation Items
      qty: 'الكمية',
      total: 'المجموع',

      // Payment Status
      paid: 'مدفوع',
      unpaid: 'غير مدفوع',
      partial: 'مدفوع جزئياً',

      // Service Modal
      addNewService: 'إضافة خدمة جديدة',
      editService: 'تعديل الخدمة',
      estMinutes: 'الوقت المقدر (دقائق)',
      basePriceSAR: 'السعر الأساسي (ريال)',
      sellingPriceSAR: 'سعر البيع (ريال)',
      costPriceSAR: 'سعر التكلفة (ريال)',
      costPriceHint: 'تكلفة هذه الخدمة على المركز. تُستخدم في حساب الربحية.',
      marginLabel: 'هامش الربح',
      costIsMaterials: 'ماذا تمثّل هذه التكلفة؟',
      costTypeLabour: 'وقت الفني (عمالة)',
      costTypeLabourEffect:
        'عند تسجيل ساعات عمل على المهمة، تحل تكلفة العمالة الفعلية محل هذا المبلغ — فلا تُحتسب الساعة مرتين.',
      costTypeMaterials: 'قطع ومستهلكات (مواد)',
      costTypeMaterialsEffect:
        'يُضاف هذا المبلغ دائمًا فوق تكلفة العمالة — مثل الزيت في خدمة تغيير الزيت.',
      costTypeNoHoursNote:
        'إذا لم يسجّل أحد وقتًا على المهمة، يُستخدم سعر التكلفة كما أدخلته تمامًا — في كلتا الحالتين.',
      svcPartsIncluded: 'السعر شامل القطع (باقة)',
      svcPartsIncludedHint:
        'تُضاف قطع هذه الخدمة إلى أمر العمل بسعر 0.00 — يدفع العميل سعر الباقة مرة واحدة. وتُخصم القطع من المخزون كالمعتاد.',
      svcPartIncludedBadge: 'ضمن الباقة',
      createService: 'إنشاء الخدمة',

      // Permissions Page
      permissionManager: 'إدارة الصلاحيات',
      customizeRolePermissions: 'تخصيص صلاحيات الأدوار لمؤسستك (مستوى المستأجر)',
      roles: 'الأدوار',
      admin: 'مدير',
      manager: 'مدير عام',
      receptionist: 'موظف استقبال',
      staff: 'موظف',

      // Reports Page
      reportsAnalytics: 'التقارير والتحليلات',
      customerInsights: 'رؤى العملاء',
      maximizeMetrics: 'تعظيم المقاييس',
      profitAnalysis: 'تحليل الأرباح',
      marginsPerJob: 'الهوامش لكل وظيفة',
      performance: 'الأداء',
      mechanicEfficiency: 'كفاءة الفني',
      revenueReport: 'تقرير الإيرادات',
      dailyWeeklyMonthly: 'يومي، أسبوعي، شهري',
      generateCustomReport: 'إنشاء تقرير مخصص',
      dateRange: 'نطاق التاريخ',
      reportType: 'نوع التقرير',
      last7Days: 'آخر 7 أيام',
      last30Days: 'آخر 30 يوماً',
      last3Months: 'آخر 3 أشهر',
      thisMonth: 'هذا الشهر',
      lastMonth: 'الشهر الماضي',
      thisYear: 'هذا العام',
      custom: 'فترة مخصصة',
      fromDate: 'من',
      toDate: 'إلى',
      'sup.title': 'الموردون',
      'sup.new': 'مورد جديد',
      'sup.code': 'رمز المورد',
      'sup.pick': 'اختر المورد…',
      'sup.edit': 'تعديل المورد',
      'sup.name': 'اسم المورد',
      'sup.nameAr': 'اسم المورد (بالعربية)',
      'sup.contact': 'الشخص المسؤول',
      'sup.taxNumber': 'الرقم الضريبي',
      'sup.paymentTerms': 'شروط الدفع',
      'sup.balance': 'الرصيد',
      'sup.invoiced': 'إجمالي الفواتير',
      'sup.paid': 'إجمالي المدفوع',
      'sup.credited': 'إشعارات دائنة',
      'sup.invoices': 'الفواتير',
      'sup.active': 'نشط',
      'sup.inactive': 'غير نشط',
      'sup.empty': 'لا يوجد موردون بعد — يتم إنشاؤهم تلقائياً من فواتير الشراء، أو أضف مورداً هنا.',
      'sup.saved': 'تم حفظ المورد',
      'sup.statement': 'كشف حساب',
      'sup.sectionBasic': 'البيانات الأساسية',
      'sup.sectionTax': 'الضريبة والسجل التجاري',
      'sup.sectionAddress': 'العنوان الوطني',
      'sup.sectionBank': 'بيانات البنك',
      'sup.sectionTerms': 'الشروط والملاحظات',
      'sup.supplierType': 'نوع المورد',
      'sup.type.company': 'منشأة',
      'sup.type.individual': 'فرد',
      'sup.category': 'التصنيف',
      'sup.category.parts': 'قطع غيار',
      'sup.category.oils': 'زيوت',
      'sup.category.tyres_batteries': 'إطارات وبطاريات',
      'sup.category.sublet': 'أعمال خارجية',
      'sup.category.utilities': 'مرافق',
      'sup.category.rent': 'إيجار',
      'sup.category.tools': 'عدد وأدوات',
      'sup.category.gov_fees': 'رسوم حكومية',
      'sup.category.other': 'أخرى',
      'sup.pickCategory': 'اختر التصنيف…',
      'sup.crNumber': 'رقم السجل التجاري',
      'sup.buildingNo': 'رقم المبنى',
      'sup.street': 'الشارع',
      'sup.district': 'الحي',
      'sup.city': 'المدينة',
      'sup.postalCode': 'الرمز البريدي',
      'sup.additionalNo': 'الرقم الإضافي',
      'sup.countryCode': 'الدولة',
      'sup.iban': 'رقم الآيبان',
      'sup.bankName': 'البنك',
      'sup.accountHolder': 'اسم صاحب الحساب',
      'sup.creditLimit': 'حد الائتمان',
      'sup.terms': 'شروط الدفع',
      'sup.terms.cash': 'نقدًا',
      'sup.terms.net': 'أجل السداد بالأيام: {{n}}',
      'sup.terms.custom': 'مخصص',
      'sup.terms.customDays': 'المدة بالأيام: {{days}}',
      'sup.customDays': 'عدد الأيام',
      'sup.paymentTermsDays': 'أجل السداد (بالأيام)',
      'sup.legacyTerms': 'الشروط السابقة: {{terms}}',
      'sup.notes': 'ملاحظات',
      'sup.supplier': 'المورد',
      'reports.apAging': 'أعمار الذمم الدائنة',
      'reports.bySupplier': 'حسب المورد',
      'reports.totalAp': 'إجمالي الذمم الدائنة',
      'reports.overdueAp': 'الذمم الدائنة المتأخرة',
      'reports.suppliersWithAp': 'الموردون ذوو الأرصدة',
      'reports.oldestDays': 'الأقدم (بالأيام)',
      'reports.bucket1_30': '1–30 يومًا',
      'reports.bucket31_60': '31–60 يومًا',
      'reports.bucket61_90': '61–90 يومًا',
      'reports.bucket90plus': 'أكثر من 90 يومًا',
      'sup.openingBalance': 'الرصيد الافتتاحي',
      'sup.closingBalance': 'الرصيد الختامي',
      'sup.debit': 'مدين',
      'sup.credit': 'دائن',
      'sup.runningBalance': 'الرصيد',
      'sup.txType.invoice': 'فاتورة شراء',
      'sup.txType.payment': 'دفعة',
      'sup.txType.credit_note': 'إشعار دائن',
      'sup.txType.expense': 'مصروف',
      'sup.txType.refund_received': 'استرداد مستلم',
      'sup.supplierOwesYou': 'مستحق من المورّد',
      'sup.stmtEmpty': 'لا توجد معاملات في هذه الفترة',
      'sup.profile': 'ملف المورد',
      'sup.pay': 'سداد',
      'sup.searchRef': 'بحث عن مرجع…',
      'sup.filteredNote': 'عرض مُرشّح — بدون رصيد جارٍ',
      revenue: 'الإيرادات',
      generateReport: 'إنشاء التقرير',
      exportCsv: 'تصدير CSV',
      noReportData: 'لا توجد بيانات للنطاق المحدد',
      noReportDataDesc: 'جرّب نوع تقرير أو نطاق تاريخ مختلف.',

      // Report titles & descriptions (flat dotted keys)
      'reportTitle.revenue': 'تقرير الإيرادات',
      'reportDesc.revenue': 'يومي، أسبوعي، شهري',
      'reportTitle.mechanicPerformance': 'أداء الفنيين',
      'reportDesc.mechanicPerformance': 'الأعمال المكتملة والكفاءة',
      'reportTitle.popularServices': 'الخدمات الأكثر طلباً',
      'reportDesc.popularServices': 'أنواع الخدمات الأكثر طلباً',
      'reportTitle.partsConsumption': 'استهلاك قطع الغيار',
      'reportDesc.partsConsumption': 'القطع المستخدمة وقيمتها',

      // Report table columns
      reportPeriod: 'الفترة',
      reportTotal: 'إجمالي الإيرادات',
      reportInvoiceCount: 'الفواتير',
      reportInvoices: 'فاتورة',
      reportMechanic: 'الفني',
      reportCompletedJobs: 'الأعمال المكتملة',
      reportAvgHours: 'متوسط الساعات',
      reportServiceName: 'الخدمة',
      reportJobCount: 'الأعمال',
      reportPartName: 'قطعة الغيار',
      reportPartNumber: 'رقم القطعة',
      reportTotalQty: 'الكمية المستخدمة',
      reportTotalValue: 'القيمة الإجمالية',
      reportSummaryRevenue: 'إجمالي الإيرادات',
      reportMechanics: 'فني',
      reportTotalJobs: 'إجمالي الأعمال',
      reportUnitsUsed: 'وحدة مستخدمة',

      // New report type titles & descriptions
      'reportTitle.jobProfitability': 'ربحية أوامر العمل',
      'reportDesc.jobProfitability': 'هامش الربح لكل أمر عمل',
      'reportTitle.inventoryValuation': 'تقييم المخزون',
      'reportDesc.inventoryValuation': 'قيمة المخزون بسعر التكلفة والبيع',
      'reportTitle.vatSummary': 'ملخص ضريبة القيمة المضافة',
      'reportDesc.vatSummary': 'ضريبة القيمة المضافة (هيئة الزكاة والضريبة والجمارك) شهرياً',

      // New report columns & summaries
      reportJobNumber: 'رقم الأمر',
      reportCustomerName: 'العميل',
      reportRevenue: 'الإيراد',
      reportPartsCost: 'تكلفة القطع',
      reportMargin: 'هامش الربح',
      reportMarginPct: 'نسبة الهامش %',
      reportTotalMargin: 'إجمالي هامش الربح',
      reportQtyInStock: 'الكمية في المخزن',
      reportAvgCost: 'متوسط التكلفة',
      reportUnitPrice: 'سعر الوحدة',
      reportPotentialProfit: 'الربح المحتمل',
      reportTotalInventoryAtCost: 'إجمالي المخزون بسعر التكلفة',
      reportTaxableAmount: 'المبلغ الخاضع للضريبة',
      reportVatAmount: 'مبلغ ضريبة القيمة المضافة',
      reportTotalVat: 'إجمالي ضريبة القيمة المضافة',

      // Reports: export, table & PDF
      exportPdf: 'تصدير PDF',
      reportTableTitle: 'التفاصيل',
      'reportPdf.generated': 'تم الإنشاء',
      'reportPdf.failed': 'تعذّر إنشاء ملف PDF. حاول مرة أخرى.',
      'reportPdf.truncated': '+{{count}} صفوف إضافية (راجع تصدير CSV)',

      // Reports: KPI cards
      'reportKpi.totalRevenue': 'إجمالي الإيرادات',
      'reportKpi.invoices': 'الفواتير',
      'reportKpi.avgInvoice': 'متوسط الفاتورة',
      'reportKpi.mechanics': 'الفنيون',
      'reportKpi.completedJobs': 'الأعمال المنجزة',
      'reportKpi.avgHours': 'متوسط الساعات',
      'reportKpi.services': 'الخدمات',
      'reportKpi.totalJobs': 'إجمالي الأعمال',
      'reportKpi.parts': 'القطع',
      'reportKpi.unitsUsed': 'الوحدات المستخدمة',
      'reportKpi.totalValue': 'إجمالي القيمة',
      'reportKpi.jobs': 'الأعمال',
      'reportKpi.totalMargin': 'إجمالي الهامش',
      'reportKpi.avgMarginPct': 'متوسط نسبة الهامش',
      'reportKpi.inventoryAtCost': 'المخزون بالتكلفة',
      'reportKpi.potentialProfit': 'الربح المحتمل',
      'reportKpi.skus': 'الأصناف',
      'reportKpi.taxable': 'الخاضع للضريبة',
      'reportKpi.vat': 'الضريبة',
      'reportKpi.total': 'الإجمالي',

      // Activity type labels (used in dashboard recentActivity)
      'activityType.job_card': 'أمر عمل',
      'activityType.invoice': 'فاتورة',
      'activityType.customer': 'عميل',
      'activityType.quotation': 'عرض سعر',
      justNow: 'الآن',
      minuteAgo: 'منذ دقيقة',

      // Settings Page
      settings: 'الإعدادات',
      garageInformation: 'معلومات المركز',
      garageName: 'اسم المركز',
      garageNameAr: 'اسم المركز (بالعربية)',
      taxNumber: 'الرقم الضريبي',
      taxRegistrationNumber: 'رقم التسجيل الضريبي',
      address: 'العنوان',
      fullAddress: 'العنوان الكامل',
      taxPricing: 'الضرائب والتسعير',
      currency: 'العملة',
      defaultTaxRate: '(%) معدل الضريبة الافتراضي',
      notifications: 'الإشعارات',
      emailNotifications: 'إشعارات البريد الإلكتروني لأوامر العمل الجديدة',
      smsNotifications: 'إشعارات الرسائل القصيرة لتغييرات حالة العمل',
      smsNotificationSettings: 'إعدادات إشعارات الرسائل القصيرة',
      addNotificationRecipient: 'إضافة مستلم الإشعارات',
      newJobCard: 'أمر عمل جديد',
      jobStatusChanges: 'تغييرات حالة العمل',
      selectStatuses: 'اختر الحالات',
      hideStatuses: 'إخفاء الحالات',
      qualityCheckCompleted: 'اكتمل فحص الجودة',
      paymentReceived: 'تم استلام الدفع',
      lowStockAlerts: 'تنبيهات المخزون المنخفض',
      partsRequests: 'طلبات قطع الغيار',
      customerFeedback: 'ملاحظات العملاء',
      saveNotificationSettings: 'حفظ إعدادات الإشعارات',
      'hrSettings.reminderRecipients': 'تذكيرات انتهاء صلاحية الموارد البشرية',
      'hrSettings.reminderRecipientsHint': 'اختر من يتم إشعارهم عند اقتراب انتهاء صلاحية مستندات الموظفين أو عقودهم. لن يُشعَر إلا من تختارهم — وإذا تركتها فارغة فلن يُشعَر أحد.',
      'hrSettings.noUsers': 'لا يوجد مستخدمون نشطون للاختيار من بينهم.',
      'hrSettings.save': 'حفظ المستلمين',
      'hrSettings.saved': 'تم حفظ مستلمي التذكيرات',
      'hrSettings.saveFailed': 'فشل حفظ مستلمي التذكيرات',
      'hrSettings.loadFailed': 'فشل تحميل مستلمي التذكيرات',
      // HR — Employees list page (PR-1, Task 7).
      'hr.employees.navTitle': 'الموظفون',
      'hr.employees.new': 'إضافة موظف',
      'hr.employees.searchPlaceholder': 'ابحث بالاسم أو رقم الموظف',
      'hr.employees.status.all': 'الكل',
      'hr.employees.status.active': 'نشط',
      'hr.employees.status.on_leave': 'في إجازة',
      'hr.employees.status.terminated': 'منتهي الخدمة',
      'hr.employees.col.name': 'الموظف',
      'hr.employees.col.employeeNo': 'رقم الموظف',
      'hr.employees.col.jobTitle': 'المسمى الوظيفي',
      'hr.employees.col.branch': 'الفرع',
      'hr.employees.linkedAccount': 'حساب مرتبط',
      'hr.employees.expired': 'منتهي الصلاحية',
      'hr.employees.expiresIn': 'تنتهي خلال {{days}} يوم',
      'hr.employees.empty': 'لا يوجد موظفون بعد',
      'hr.employees.created': 'تمت إضافة الموظف',
      'hr.employees.form.firstName': 'الاسم الأول',
      'hr.employees.form.lastName': 'اسم العائلة',
      'hr.employees.form.firstNameAr': 'الاسم الأول (بالعربية)',
      'hr.employees.form.lastNameAr': 'اسم العائلة (بالعربية)',
      'hr.employees.form.sex': 'الجنس',
      'hr.employees.form.sexMale': 'ذكر',
      'hr.employees.form.sexFemale': 'أنثى',
      'hr.employees.form.dob': 'تاريخ الميلاد',
      'hr.employees.form.nationality': 'الجنسية',
      'hr.employees.form.nationalityOther': 'أخرى',
      'hr.employees.form.nationalityOtherHint': 'أدخل الجنسية',
      'hr.employees.form.jobTitle': 'المسمى الوظيفي',
      'hr.employees.form.jobTitleAr': 'المسمى الوظيفي (بالعربية)',
      'hr.employees.form.hireDate': 'تاريخ التعيين',
      // HR — Employee file page (PR-1, Task 8).
      'hr.file.notFound': 'الموظف غير موجود',
      'hr.file.tabs.info': 'المعلومات',
      'hr.file.tabs.contract': 'العقد والراتب',
      'hr.file.tabs.documents': 'المستندات',
      'hr.file.edit': 'تعديل',
      'hr.file.updated': 'تم تحديث بيانات الموظف',
      'hr.file.terminate': 'إنهاء الخدمة',
      'hr.file.terminate.title': 'إنهاء خدمة الموظف',
      'hr.file.terminate.message': 'سيتم وضع علامة على الموظف كمنتهي الخدمة وسيصبح ملفه للقراءة فقط.',
      'hr.file.terminate.date': 'تاريخ إنهاء الخدمة',
      'hr.file.terminate.reason': 'السبب',
      'hr.file.terminate.confirm': 'إنهاء الخدمة',
      'hr.file.terminate.done': 'تم إنهاء خدمة الموظف',
      'hr.file.terminated.banner': 'تم إنهاء خدمة هذا الموظف بتاريخ {{date}}.',
      'hr.file.terminated.reason': 'السبب: {{reason}}',
      'hr.file.terminate.noticeShortfall':
        'هذا التاريخ أقل بـ {{days}} يوم(أيام) من مدة الإشعار القانونية البالغة {{required}} يومًا.',
      'hr.file.terminate.noticeShortfallEstimate': 'البدل التقديري لمهلة الإشعار',
      'hr.file.terminate.noticeShortfallNote':
        'يتم احتساب المبلغ الدقيق وتعديله عند ترحيل تسوية نهاية الخدمة.',

      // لوحة التكلفة الفعلية لبطاقة العمل + ضبط تكلفة الخدمات + تسوية نهاية الخدمة
      'hr.cost.title': 'التكلفة الفعلية',
      'hr.cost.empty': 'لا توجد مهام في بطاقة العمل هذه بعد.',
      'hr.cost.rateApplied': 'تم تطبيق المعدل',
      'hr.cost.noRate': 'لا يوجد معدل',
      'hr.cost.noRateMechanics': 'لم يتم تحديد معدل',
      'hr.cost.taskLabor': 'الأجرة',
      'hr.cost.taskNonLabor': 'تكلفة أخرى',
      'hr.cost.taskTotal': 'الإجمالي',
      'hr.cost.orphanEvents': '{{count}} حدث غير مطابق في سجل الوقت',
      'hr.cost.overridden.cost': 'تم التجاوز (تكلفة ثابتة)',
      'hr.cost.overridden.hours': 'تم التجاوز (ساعات)',
      'hr.cost.override.button': 'تجاوز',
      'hr.cost.override.title': 'تجاوز تكلفة الأجرة',
      'hr.cost.override.computed': 'المحسوب',
      'hr.cost.override.mode.hours': 'ساعات',
      'hr.cost.override.mode.cost': 'تكلفة ثابتة (ريال)',
      'hr.cost.override.hoursLabel': 'الساعات',
      'hr.cost.override.costLabel': 'التكلفة (ريال)',
      'hr.cost.override.reasonLabel': 'السبب',
      'hr.cost.override.save': 'حفظ التجاوز',
      'hr.cost.override.saved': 'تم حفظ التجاوز',
      'hr.cost.override.by': 'بواسطة',
      'hr.cost.override.reason': 'السبب',
      'hr.cost.override.clear': 'إلغاء',
      'hr.cost.override.clearTitle': 'إلغاء التجاوز؟',
      'hr.cost.override.clearMessage': 'سيؤدي هذا إلى استعادة تكلفة الأجرة المحسوبة لهذه المهمة.',
      'hr.cost.override.cleared': 'تم إلغاء التجاوز',
      'hr.cost.totalLabor': 'إجمالي الأجرة',
      'hr.cost.totalNonLabor': 'إجمالي التكلفة الأخرى',
      'hr.cost.totalCost': 'إجمالي التكلفة',
      'hr.cost.billed': 'المفوتر (الأجرة)',
      'hr.cost.margin': 'الهامش مقابل المفوتر',
      'hr.cost.otherThanManHours': 'تكلفة غير أجرة العمل (ريال)',
      'hr.cost.costFromHours': 'التكلفة حسب ساعات العمل',
      'hr.cost.costFromHoursHint': 'محدد: تكلفة هذا البند مشمولة بالفعل في ساعات العمل المسجلة للمهمة، ويتم استبعادها من إجمالي التكلفة. غير محدد: يُضاف المبلغ أعلاه إلى الإجمالي.',
      'hr.cost.hourlyRate': 'الأجر بالساعة (ريال/ساعة)',

      'hr.settlement.action': 'تسوية نهاية الخدمة',
      'hr.settlement.title': 'تسوية نهاية الخدمة',
      'hr.settlement.exitType': 'نوع انتهاء الخدمة',
      'hr.settlement.exitType.resignation': 'استقالة',
      'hr.settlement.exitType.termination': 'إنهاء خدمة',
      'hr.settlement.exitType.contract_end': 'انتهاء العقد',
      'hr.settlement.exitType.dismissal_for_cause': 'فصل بسبب مخالفة (المادة ٨٠)',
      'hr.settlement.wage': 'الأجر الشهري',
      'hr.settlement.tenure': 'مدة الخدمة (سنوات)',
      'hr.settlement.fraction': 'النسبة المطبقة',
      'hr.settlement.unusedLeaveDays': 'رصيد الإجازة السنوية غير المستخدم (أيام)',
      'hr.settlement.eosb': 'مكافأة نهاية الخدمة',
      'hr.settlement.leaveEncashment': 'بدل الإجازة',
      'hr.settlement.payInLieuDaysLabel': 'بدل مهلة الإشعار (أيام)',
      'hr.settlement.payInLieuDaysHint': 'القيمة الافتراضية صفر — لا يتم ترحيل سوى القيمة التي تدخلها هنا.',
      'hr.settlement.noticeEstimate': 'الأيام المحسوبة مقارنةً بتاريخ اليوم: {{days}} يوم — تحقق من الإشعار الفعلي قبل الإدخال',
      'hr.settlement.useShortfallDays': 'استخدام {{days}} يوم',
      'hr.settlement.payInLieu': 'بدل مهلة الإشعار ({{days}} يوم)',
      'hr.settlement.loanOutstanding': 'رصيد القرض القائم',
      'hr.settlement.loanDeduction': 'المخصوم من التسوية',
      'hr.settlement.loanRemaining': 'المتبقي على الموظف (يبقى القرض مفتوحًا)',
      'hr.settlement.total': 'إجمالي التسوية',
      'hr.settlement.confirm': 'تأكيد التسوية',
      'hr.settlement.done': 'تم ترحيل تسوية نهاية الخدمة',
      'hr.settlement.alreadySettled': 'تمت التسوية بتاريخ {{date}}.',
      'hr.settlement.note': 'ملاحظة',
      'hr.settlement.loadFailed': 'تعذر تحميل معاينة التسوية',

      'hr.file.info.nationalId': 'رقم الهوية',
      'hr.file.info.nationalIdLabelSaudi': 'رقم الهوية الوطنية',
      'hr.file.info.nationalIdLabelIqama': 'رقم الإقامة',
      'hr.file.info.idKindNational': 'بطاقة هوية وطنية',
      'hr.file.info.idKindIqama': 'إقامة',
      'hr.file.info.gosiRegMissingChip': 'تاريخ التسجيل في التأمينات الاجتماعية غير مسجل — يؤثر على مسار التأمينات المستخدم',
      'hr.file.info.iqamaDocMissingChip': 'لا يوجد مستند إقامة',
      'hr.file.info.iban': 'الآيبان',
      'hr.iban.invalid': 'رقم الآيبان غير صحيح',
      'hr.file.info.reveal': 'إظهار',
      'hr.file.info.hide': 'إخفاء',
      'hr.file.info.linkedUser': 'حساب المستخدم المرتبط',
      'hr.file.info.noLinkedUser': '— لا يوجد —',
      'hr.file.info.address': 'العنوان',
      'hr.file.info.notes': 'ملاحظات',
      'hr.file.info.hajjLeaveTaken': 'تم أخذ إجازة الحج',
      'hr.file.info.isDisabled': 'شخص ذو إعاقة (نطاقات)',
      'hr.file.info.gosiRegDate': 'تاريخ التسجيل في التأمينات الاجتماعية',
      'hr.file.info.contractEnd': 'تاريخ انتهاء العقد',
      'hr.file.info.daysToRenewal': 'الأيام المتبقية للتجديد',
      'hr.file.info.renewalIn': 'متبقٍ {{days}} يوم',
      'hr.file.info.renewalOverdue': 'تجاوز موعد التجديد',
      'hr.file.info.noticePeriod': 'فترة إشعار {{days}} يوم',
      'hr.file.info.contractAlert': 'تنبيه عدم تجديد العقد',
      'hr.file.info.contractAlertLead': 'مدة التنبيه المسبق (أيام)',
      'hr.file.info.contractAlertHint': 'تنبيه المستلمين قبل بدء فترة الإشعار بهذا العدد من الأيام، حتى يتسنى تحديد التجديد أو إبلاغ الموظف في الوقت المناسب.',
      'hr.file.contract.active': 'العقد الحالي',
      'hr.file.contract.none': 'لا يوجد عقد نشط',
      'hr.file.contract.type': 'نوع العقد',
      'hr.file.contract.typeFixed': 'محدد المدة',
      'hr.file.contract.typeIndefinite': 'غير محدد المدة',
      'hr.file.contract.startDate': 'تاريخ البدء',
      'hr.file.contract.endDate': 'تاريخ الانتهاء',
      'hr.file.contract.probationEnd': 'نهاية فترة التجربة',
      'hr.file.contract.qiwaRef': 'رقم مرجع قوى',
      'hr.file.contract.weeklyHours': 'ساعات العمل الأسبوعية',
      'hr.file.contract.leaveDays': 'أيام الإجازة السنوية',
      'hr.file.contract.salary': 'الراتب',
      'hr.file.contract.basic': 'الأساسي',
      'hr.file.contract.housing': 'بدل السكن',
      'hr.file.contract.transport': 'بدل النقل',
      'hr.file.contract.otherAllowances': 'بدلات أخرى',
      'hr.file.contract.allowanceLabel': 'الوصف',
      'hr.file.contract.allowanceAmount': 'المبلغ',
      'hr.file.contract.addAllowance': 'إضافة بدل',
      'hr.file.contract.total': 'الإجمالي',
      'hr.file.contract.new': 'عقد جديد',
      'hr.file.contract.newTitle': 'إنشاء عقد جديد',
      'hr.file.contract.edit': 'تعديل العقد',
      'hr.file.contract.editTitle': 'تعديل العقد الحالي',
      'hr.file.contract.saveChanges': 'حفظ التعديلات',
      'hr.file.contract.updated': 'تم تحديث العقد',
      'hr.file.contract.promote': 'ترقية',
      'hr.file.contract.promoteTitle': 'ترقية — عقد معدّل',
      'hr.file.contract.promoteNote': 'ينشئ عقداً جديداً بالراتب المعدّل ويحفظ العقد الحالي في السجل',
      'hr.file.contract.confirmRenewTitle': 'استبدال العقد الحالي؟',
      'hr.file.contract.confirmRenewMessage': 'سيتم وضع علامة على العقد الحالي بأنه مُجدَّد.',
      'hr.file.contract.created': 'تم إنشاء العقد',
      'hr.file.contract.history': 'سجل العقود',
      'hr.file.contract.noHistory': 'لا يوجد عقود سابقة',
      'hr.file.contract.status.active': 'نشط',
      'hr.file.contract.status.renewed': 'مُجدَّد',
      'hr.file.contract.status.expired': 'منتهي',
      'hr.costs.title': 'تكاليف إضافية (على صاحب العمل)',
      'hr.costs.label': 'البند',
      'hr.costs.method': 'الطريقة',
      'hr.costs.monthly': 'شهري',
      'hr.costs.totalForContract': 'إجمالي للعقد',
      'hr.costs.amount': 'المبلغ',
      'hr.costs.monthlyEquivalent': 'يعادل شهرياً',
      'hr.costs.needsEndDate': 'حدد تاريخ نهاية العقد لاستخدام مبلغ إجمالي',
      'hr.costs.add': 'إضافة تكلفة',
      'hr.costs.total': 'إجمالي الإضافي (شهرياً)',
      'hr.costs.doubleCountNote': 'هذه المبالغ تُستخدم في حساب تكلفة العمالة والربحية — إذا سجّلت الفاتورة الفعلية أيضاً كمصروف على الشركة، لا تُدخلها هنا مرة أخرى تجنباً للاحتساب المزدوج.',
      'hr.file.doc.type': 'النوع',
      'hr.file.doc.number': 'رقم المستند',
      'hr.file.doc.issueDate': 'تاريخ الإصدار',
      'hr.file.doc.expiryDate': 'تاريخ الانتهاء',
      'hr.file.doc.file': 'الملف',
      'hr.file.doc.upload': 'رفع مستند',
      'hr.file.doc.view': 'عرض',
      'hr.file.doc.delete': 'حذف',
      'hr.file.doc.deleteTitle': 'حذف المستند؟',
      'hr.file.doc.deleteMessage': 'سيتم حذف هذا المستند نهائيًا.',
      'hr.file.doc.uploaded': 'تم رفع المستند',
      'hr.file.doc.deleted': 'تم حذف المستند',
      'hr.file.doc.empty': 'لا توجد مستندات بعد',
      'hr.file.doc.invalidType': 'يجب أن يكون الملف من نوع JPG أو PNG أو WEBP أو PDF',
      'hr.file.doc.tooLarge': 'يجب ألا يتجاوز حجم الملف 10 ميغابايت',
      'hr.file.doc.viewFailed': 'فشل فتح المستند',
      'hr.file.docExpired': 'منتهي الصلاحية',
      'hr.file.doc.type.iqama': 'الإقامة',
      'hr.file.doc.type.passport': 'جواز السفر',
      'hr.file.doc.type.contract': 'عقد',
      'hr.file.doc.type.driving_license': 'رخصة القيادة',
      'hr.file.doc.type.gosi_cert': 'شهادة التأمينات الاجتماعية',
      'hr.file.doc.type.other': 'أخرى',

      // HR PR-2, Task 11: payroll pages, reports, loans & leave tabs, photo_url.
      'hr.file.info.photoUrl': 'رابط الصورة',
      'hr.file.tabs.loans': 'السلف',
      'hr.file.tabs.leave': 'الإجازات',
      'hr.file.loans.new': 'سلفة جديدة',
      'hr.file.loans.created': 'تم إنشاء السلفة',
      'hr.file.loans.updated': 'تم تحديث السلفة',
      'hr.file.loans.empty': 'لا توجد سلف بعد',
      'hr.file.loans.principal': 'المبلغ الأساسي',
      'hr.file.loans.principalShort': 'المبلغ',
      'hr.file.loans.installment': 'القسط الشهري',
      'hr.file.loans.installmentShort': 'القسط',
      'hr.file.loans.balance': 'الرصيد المتبقي',
      'hr.file.loans.note': 'ملاحظة',
      'hr.file.loans.pause': 'إيقاف مؤقت',
      'hr.file.loans.resume': 'استئناف',
      'hr.file.loans.settle': 'تسوية',
      'hr.file.loans.status.active': 'نشطة',
      'hr.file.loans.status.paused': 'موقوفة مؤقتًا',
      'hr.file.loans.status.settled': 'مُسوَّاة',
      'hr.file.loans.edit': 'تعديل',
      'hr.file.loans.editTitle': 'تعديل السلفة',
      'hr.file.loans.delete': 'حذف',
      'hr.file.loans.deleted': 'تم حذف السلفة',
      'hr.file.loans.deleteTitle': 'حذف هذه السلفة؟',
      'hr.file.loans.deleteBody': 'سيتم حذف سجل السلفة نهائياً. لم يخصم منها أي راتب، لذلك لن يتم إعفاء أي مبلغ.',
      'hr.file.loans.settleTitle': 'إعفاء الرصيد المتبقي؟',
      'hr.file.loans.settleBody': 'سيتوقف خصم الأقساط من الموظف ويُعفى الرصيد المتبقي. لا يمكن التراجع عن هذا الإجراء.',
      'hr.file.loans.settleAmount': 'الرصيد المُعفى',
      'hr.file.loans.repaid': 'المسدّد',
      'hr.file.loans.repaidOf': '{{repaid}} من {{principal}}',
      'hr.file.loans.recordedOn': 'سُجّلت في {{date}}',
      'hr.file.loans.recordedBy': 'بواسطة {{name}}',
      'hr.file.loans.remainingMonths': 'يتبقى {{count}} شهر تقريباً',
      'hr.file.loans.history': 'سجل السداد',
      'hr.file.loans.historyEmpty': 'لم يخصم أي مسير رواتب من هذه السلفة بعد.',
      'hr.file.loans.historyRun': 'مسير {{period}}',
      'hr.file.loans.reversedRun': 'معكوس — أُعيد المبلغ',
      'hr.file.loans.draftRun': 'مسودة — لم يُخصم بعد',
      'hr.file.loans.principalLocked': 'تم خصم قسط من الراتب، لذلك لا يمكن تعديل أصل السلفة.',
      'hr.file.loans.staleDraft': 'أعد توليد مسودة مسير الرواتب لفترة {{periods}} حتى يتوقف خصم هذه السلفة.',
      'hr.file.loans.cannotDelete': 'تم الخصم من هذه السلفة في مسير رواتب — استخدم الإعفاء بدلاً من الحذف.',
      'hr.file.leave.balance': 'رصيد الإجازات',
      'hr.file.leave.entitlement': 'المستحق (أيام)',
      'hr.file.leave.taken': 'المستخدم (أيام)',
      'hr.file.leave.remaining': 'المتبقي (أيام)',
      'hr.file.leave.sickTier': 'فئة الإجازة المرضية',
      'hr.file.leave.sickTier.full': 'أجر كامل',
      'hr.file.leave.sickTier.threeQuarters': 'ثلاثة أرباع الأجر',
      'hr.file.leave.sickTier.unpaid': 'بدون أجر',
      'hr.file.leave.sickTier.exhausted': 'مستنفدة',
      'hr.file.leave.hireDateMissingHint': 'يرجى تحديد تاريخ التعيين للموظف للحصول على رصيد دقيق ومتناسب.',
      'hr.file.leave.entries': 'سجل الإجازات',
      'hr.file.leave.new': 'إضافة إجازة',
      'hr.file.leave.created': 'تمت إضافة الإجازة',
      'hr.file.leave.deleted': 'تم حذف الإجازة',
      'hr.file.leave.deleteTitle': 'حذف سجل الإجازة؟',
      'hr.file.leave.deleteMessage': 'سيتم حذف سجل الإجازة هذا نهائيًا.',
      'hr.file.leave.delete': 'حذف',
      'hr.file.leave.empty': 'لا توجد إجازات بعد',
      'hr.file.leave.type': 'النوع',
      'hr.file.leave.type.annual': 'سنوية',
      'hr.file.leave.type.sick': 'مرضية',
      'hr.file.leave.type.maternity': 'وضع',
      'hr.file.leave.type.hajj': 'حج',
      'hr.file.leave.type.unpaid': 'بدون أجر',
      'hr.file.leave.type.other': 'أخرى',
      'hr.file.leave.type.paternity': 'أبوة',
      'hr.file.leave.type.marriage': 'زواج',
      'hr.file.leave.type.bereavement_family': 'وفاة (زوج/أصل/فرع)',
      'hr.file.leave.type.bereavement_sibling': 'وفاة (أخ/أخت)',
      'hr.file.leave.type.iddah': 'عدة',
      'hr.file.leave.type.exam': 'امتحان',
      'hr.file.leave.capHint': 'الحد الأقصى النظامي: {{days}} أيام',
      'hr.file.leave.iddahNote': 'رقم من مصدر واحد (~130 يوم) — يُرجى التحقق منه لدى وزارة الموارد البشرية قبل الاعتماد عليه',
      'hr.file.leave.hajjTaken': 'تم استخدامها مسبقًا',
      'hr.file.leave.start': 'تاريخ البدء',
      'hr.file.leave.end': 'تاريخ الانتهاء',
      'hr.file.leave.days': 'عدد الأيام',
      'hr.file.leave.daysHint': 'يُحسب تلقائيًا من نطاق التاريخ — يمكن تعديله عند الحاجة',
      'hr.file.leave.note': 'ملاحظة',

      // HR PR-4, Task 5: the "add employee" wizard (source/info/contract/documents/account/review).
      'hr.wizard.steps.source': 'المصدر',
      'hr.wizard.steps.info': 'البيانات الأساسية',
      'hr.wizard.steps.contract': 'العقد',
      'hr.wizard.steps.documents': 'المستندات',
      'hr.wizard.steps.account': 'الحساب',
      'hr.wizard.steps.review': 'المراجعة',
      'hr.wizard.source.manual': 'إدخال البيانات يدويًا',
      'hr.wizard.source.manualHint': 'أدخل بيانات الموظف بنفسك خطوة بخطوة.',
      'hr.wizard.source.qiwa': 'مسح عقد قِوى',
      'hr.wizard.source.qiwaUpload': 'رفع العقد',
      'hr.wizard.qiwa.aiHint': 'سيقوم TzamunAI بقراءة العقد وتعبئة بيانات الموظف والعقد تلقائيًا — وستراجع كل شيء قبل الحفظ.',
      'hr.wizard.qiwa.scanning': 'يقرأ TzamunAI العقد الآن…',
      'hr.wizard.qiwa.notQiwa': 'هذا الملف لا يبدو عقد قوى — يرجى إدخال البيانات يدويًا.',
      'hr.wizard.qiwa.filled': 'تم تعبئة {{count}} حقل من نتائج المسح',
      'hr.wizard.qiwa.extractionFailed': 'تعذّر على TzamunAI قراءة هذا العقد — ارفع نسخة أوضح أو أدخل البيانات يدويًا.',
      'hr.wizard.qiwa.quotaExceeded': 'تم الوصول للحد الشهري لمسح الذكاء الاصطناعي — أدخل البيانات يدويًا بدلاً من ذلك.',
      'hr.wizard.contract.hint': 'اختياري — اتركه فارغًا للتخطي؛ يمكن إضافة عقد لاحقًا من ملف الموظف.',
      'hr.wizard.documents.hint': 'ارفع أي مستندات الآن، أو أضفها لاحقًا من ملف الموظف.',
      'hr.wizard.account.existing': 'ربط حساب مستخدم موجود',
      'hr.wizard.account.create': 'إنشاء حساب مستخدم جديد',
      'hr.wizard.account.none': 'بدون حساب في النظام',
      'hr.wizard.account.noneHint': 'لن يحصل هذا الموظف على وصول للنظام أو التطبيق.',
      'hr.wizard.account.selectUser': 'حساب المستخدم',
      'hr.wizard.account.selectUserPlaceholder': 'اختر مستخدمًا',
      'hr.wizard.account.change': 'تغيير',
      'hr.wizard.account.createNote': 'سيؤدي هذا إلى إنشاء حساب مستخدم حقيقي فورًا — حتى إن لم تُكمل هذا المعالج.',
      'hr.wizard.account.createButton': 'إنشاء المستخدم',
      'hr.wizard.account.created': 'تم إنشاء حساب المستخدم',
      'hr.wizard.review.edit': 'تعديل',
      'hr.wizard.review.noContract': 'لن يتم إنشاء عقد — يمكن إضافته لاحقًا من ملف الموظف.',
      'hr.wizard.review.pendingAccount': 'بانتظار الاختيار',
      'hr.wizard.finished': 'تمت إضافة الموظف',
      'hr.wizard.back': 'رجوع',
      'hr.wizard.next': 'التالي',
      'hr.wizard.finish': 'إنهاء',
      'hr.wizard.saveDraft': 'حفظ كمسودة',
      'hr.wizard.draftSaved': 'تم حفظ المسودة',
      'hr.wizard.resumed': 'تم استئناف المسودة',
      'hr.wizard.resumeFailed': 'فشل تحميل المسودة',
      'hr.wizard.leaveTitle': 'مغادرة بدون حفظ؟',
      'hr.wizard.leaveMessage': 'لديك تغييرات غير محفوظة ولا توجد مسودة محفوظة. المغادرة الآن ستؤدي إلى فقدانها.',
      'hr.wizard.leaveConfirm': 'تجاهل والمغادرة',
      'hr.wizard.errors.namesRequired': 'الاسم الأول والاسم الأخير مطلوبان',
      'hr.wizard.errors.startDateRequired': 'تاريخ البدء مطلوب عند تعبئة أي حقل من حقول العقد',
      'hr.wizard.errors.probationInvalid': 'يجب أن يكون تاريخ انتهاء فترة التجربة صالحًا وخلال 180 يومًا من تاريخ البدء',
      'hr.wizard.draft.stripTitle': 'مسودات قيد التنفيذ',
      'hr.wizard.draft.unnamed': '(بدون اسم)',
      'hr.wizard.draft.badge': 'مسودة',
      'hr.wizard.draft.resume': 'استئناف',
      'hr.wizard.draft.delete': 'حذف',
      'hr.wizard.draft.deleteTitle': 'حذف هذه المسودة؟',
      'hr.wizard.draft.deleteMessage': 'سيؤدي هذا إلى حذف المسودة وأي ملفات مرفوعة بشكل نهائي. لا يمكن التراجع عن هذا الإجراء.',
      'hr.wizard.draft.deleted': 'تم حذف المسودة',

      'hr.payroll.navTitle': 'الرواتب',
      'hr.payroll.newRun': 'دورة رواتب جديدة',
      'hr.payroll.period': 'الفترة',
      'hr.payroll.created': 'تم إنشاء دورة الرواتب',
      'hr.payroll.empty': 'لا توجد دورات رواتب بعد',
      'hr.payroll.col.period': 'الفترة',
      'hr.payroll.col.netTotal': 'إجمالي الصافي',
      'hr.payroll.col.postingRef': 'مرجع الترحيل',
      'hr.payroll.status.draft': 'مسودة',
      'hr.payroll.status.approved': 'معتمدة',
      'hr.payroll.status.paid': 'مدفوعة',
      'hr.payroll.status.reversed': 'مُلغاة',
      'hr.payroll.notFound': 'دورة الرواتب غير موجودة',
      'hr.payroll.regenerate': 'إعادة توليد',
      'hr.payroll.regenerateMessage': 'سيتم إعادة بناء جميع البنود من بيانات الموظفين والعقود والتسويات الحالية. التسويات المضافة يدويًا لهذه الفترة تبقى كما هي.',
      'hr.payroll.regenerated': 'تمت إعادة توليد دورة الرواتب',
      'hr.payroll.approve': 'اعتماد',
      'hr.payroll.approveMessage': 'الاعتماد يُثبّت الدورة ويستهلك التسويات لمرة واحدة الخاصة بهذه الفترة. لا يمكن التراجع عن هذا بإعادة التوليد.',
      'hr.payroll.approved': 'تم اعتماد دورة الرواتب',
      'hr.payroll.paid': 'تم تعليم دورة الرواتب كمدفوعة',
      'hr.payroll.reversed': 'تم إلغاء دورة الرواتب',
      'hr.payroll.wps': 'تحميل ملف WPS',
      'hr.payroll.wpsFailed': 'فشل إنشاء ملف WPS',
      'hr.payroll.wpsSkippedTitle': 'بعض الموظفين لن يظهروا في الملف',
      'hr.payroll.wpsSkippedMessage': 'سيتم استبعاد {{count}} موظف من ملف WPS: {{list}}. لن يظهروا في الملف. هل تريد تحميل الملف رغم ذلك؟',
      'hr.payroll.wpsSkipReason.missing_iban': 'لا يوجد آيبان مسجل',
      'hr.payroll.wpsSkipReason.invalid_iban': 'آيبان غير صالح',
      'hr.payroll.wpsSkipReason.non_positive_net': 'صافي الراتب غير موجب',
      'hr.payroll.wpsSkippedMore': '+{{count}} أخرى',
      'hr.payroll.wpsDownloadAnyway': 'تحميل على أي حال',
      'hr.payroll.wpsNoPayableTitle': 'لا يمكن دفع رواتب أي موظف عبر WPS',
      'hr.payroll.wpsNoPayableHint': 'أضف آيبان صحيح لكل ملف موظف أدناه، ثم أعد المحاولة.',
      'hr.payroll.wpsSettings.title': 'إعدادات منشأة WPS',
      'hr.payroll.wpsSettings.bank': 'البنك',
      'hr.payroll.wpsSettings.bankPlaceholder': 'اختر بنكًا',
      'hr.payroll.wpsSettings.account': 'آيبان حساب المنشأة',
      'hr.payroll.wpsSettings.estbId': 'رقم المنشأة',
      'hr.payroll.wpsSettings.molEstbId': 'رقم المنشأة في وزارة الموارد البشرية',
      'hr.payroll.wpsSettings.saved': 'تم حفظ إعدادات WPS',
      'hr.payroll.payslipFailed': 'فشل إنشاء قسيمة الراتب',
      'hr.payroll.skipped.banner': 'تم استبعاد {{count}} موظف عند توليد هذه الدورة:',
      'hr.payroll.skip.on_leave': 'في إجازة',
      'hr.payroll.skip.terminated': 'منتهي الخدمة',
      'hr.payroll.skip.no_active_contract': 'لا يوجد عقد نشط',
      'hr.payroll.pay.action': 'دفع',
      'hr.payroll.pay.title': 'دفع هذه الدورة؟',
      'hr.payroll.pay.message': 'سيتم ترحيل المبالغ التالية إلى دفتر النقدية لكل فرع:',
      'hr.payroll.pay.confirm': 'تأكيد الدفع',
      'hr.payroll.reverse.action': 'إلغاء',
      'hr.payroll.reverse.title': 'إلغاء دورة الرواتب هذه؟',
      'hr.payroll.reverse.message': 'سيتم إلغاء كل قيد نقدي مُرحَّل واستعادة التسويات المستهلكة وأرصدة السلف. السبب مطلوب.',
      'hr.payroll.reverse.reason': 'السبب',
      'hr.payroll.reverse.confirm': 'إلغاء الدورة',
      'hr.payroll.lines.empty': 'لا توجد بنود في هذه الدورة',
      'hr.payroll.line.gross': 'الإجمالي',
      'hr.payroll.line.gosiEe': 'التأمينات (الموظف)',
      'hr.payroll.line.gosiEr': 'التأمينات (صاحب العمل)',
      'hr.payroll.line.deductions': 'الخصومات',
      'hr.payroll.line.net': 'الصافي',
      'hr.payroll.line.basic': 'الأساسي',
      'hr.payroll.line.housing': 'بدل السكن',
      'hr.payroll.line.transport': 'بدل النقل',
      'hr.payroll.line.otherAllowances': 'بدلات أخرى',
      'hr.payroll.line.gosiBase': 'أساس التأمينات',
      'hr.payroll.line.gosiTrack': 'مسار التأمينات',
      'hr.payroll.line.eosbAccrual': 'استحقاق نهاية الخدمة',
      'hr.payroll.line.adjustmentDetail': 'التسويات والسلف',
      'hr.payroll.line.loanEntry': 'قسط السلفة',
      'hr.payroll.line.payslip': 'تحميل قسيمة الراتب',
      'hr.payroll.line.adjust': 'إضافة/خصم',
      'hr.payroll.line.gosiBreakdown': 'تفصيل التأمينات',
      'hr.payroll.line.gosiPensionEr': 'المعاشات (صاحب العمل)',
      'hr.payroll.line.gosiPensionEe': 'المعاشات (الموظف)',
      'hr.payroll.line.gosiSanedEr': 'ساند (صاحب العمل)',
      'hr.payroll.line.gosiSanedEe': 'ساند (الموظف)',
      'hr.payroll.line.gosiHazardEr': 'الأخطار المهنية (صاحب العمل)',
      'hr.payroll.line.leaveDeductions': 'خصومات الإجازات',
      'hr.payroll.line.days': 'يوم',
      'hr.payroll.leaveDeduction.unpaid': 'إجازة بدون راتب',
      'hr.payroll.leaveDeduction.sick_tier2': 'إجازة مرضية (٧٥٪ من الراتب)',
      'hr.payroll.leaveDeduction.sick_tier3': 'إجازة مرضية (بدون راتب)',
      'hr.payroll.gosiTrack.saudi_legacy': 'سعودي (النسبة القديمة)',
      'hr.payroll.gosiTrack.saudi_new': 'سعودي (النسبة الجديدة)',
      'hr.payroll.gosiTrack.expat': 'وافد',
      'hr.payroll.gosiTrack.none': 'غير مسجَّل',
      'hr.payroll.adj.title': 'تسويات ما قبل الدورة',
      'hr.payroll.adj.employee': 'الموظف',
      'hr.payroll.adj.selectEmployee': 'اختر موظفًا',
      'hr.payroll.adj.empty': 'لا توجد تسويات معلّقة لهذا الموظف في هذه الفترة',
      'hr.payroll.adj.kindLabel': 'النوع',
      'hr.payroll.adj.categoryLabel': 'الفئة',
      'hr.payroll.adj.recurrenceLabel': 'التكرار',
      'hr.payroll.adj.amountLabel': 'المبلغ',
      'hr.payroll.adj.add': 'إضافة',
      'hr.payroll.adj.added': 'تمت إضافة التسوية',
      'hr.payroll.adj.remove': 'إزالة',
      'hr.payroll.adj.removed': 'تمت إزالة التسوية',
      'hr.payroll.adj.removeTitle': 'إزالة التسوية؟',
      'hr.payroll.adj.removeMessage': 'لن يتم احتساب هذه التسوية عند إعادة توليد الدورة.',
      'hr.payroll.adj.once': 'مرة واحدة (هذه الفترة)',
      'hr.payroll.adj.recurring': 'متكررة',
      'hr.payroll.adj.kind.addition': 'إضافة',
      'hr.payroll.adj.kind.deduction': 'خصم',
      'hr.payroll.adj.category.overtime': 'عمل إضافي',
      'hr.payroll.adj.category.bonus': 'مكافأة',
      'hr.payroll.adj.category.commission': 'عمولة',
      'hr.payroll.adj.category.absence': 'غياب',
      'hr.payroll.adj.category.penalty': 'جزاء',
      'hr.payroll.adj.category.gosi_arrears': 'متأخرات تأمينات',
      'hr.payroll.adj.category.other': 'أخرى',
      'hr.payroll.adj.runTitle': 'إضافة/خصم لهذه الدورة',
      'hr.payroll.adj.appliedNet': 'تم تطبيق التسوية — الصافي الجديد {{net}}',
      'hr.payroll.adj.applied': 'تم تطبيق التسوية وإعادة توليد الدورة',
      'hr.payroll.adj.otHoursLabel': 'ساعات العمل الإضافي',
      'hr.payroll.adj.otSuggest': 'اقتراح العمل الإضافي',
      'hr.payroll.adj.otSuggestionBanner': 'المقترح {{hours}} ساعة عمل إضافي ≈',
      'hr.payroll.adj.otNote': 'عمل إضافي {{hours}} ساعة',

      // HR statutory v2, T8 — payslip delivery WhatsApp→SMS.
      'hr.payroll.line.payslipSent.whatsapp': 'أُرسلت عبر واتساب',
      'hr.payroll.line.payslipSent.sms': 'أُرسلت عبر رسالة نصية',
      'hr.payroll.payslipSend.action': 'إرسال قسائم الرواتب',
      'hr.payroll.payslipSend.confirmTitle': 'إرسال قسائم الرواتب الآن؟',
      'hr.payroll.payslipSend.confirmMessage': 'سيحصل كل موظف على قسيمة راتبه عبر واتساب، أو رسالة نصية برابط في حال عدم توفر واتساب.',
      'hr.payroll.payslipSend.sentLabel': 'أُرسلت',
      'hr.payroll.payslipSend.skippedLabel': 'أُرسلت مسبقاً',
      'hr.payroll.payslipSend.failedLabel': 'فشلت',
      'hr.payroll.payslipSend.noneToSend': 'تم إرسال جميع قسائم الرواتب لهذه الدورة مسبقاً',
      'hr.payroll.payslipSend.failReason.no_phone': 'لا يوجد رقم هاتف',
      'hr.payroll.payslipSend.failReason.send_failed': 'فشل الإرسال',
      'hr.payroll.payslipSend.failReason.error': 'خطأ غير متوقع',
      'hr.payroll.payslipSend.failed': 'فشل إرسال قسائم الرواتب',

      'hr.reports.navTitle': 'تقارير الموارد البشرية',
      'hr.reports.empty': 'لا توجد بيانات بعد',
      'hr.reports.emptyDesc': 'جرّب فترة أوسع أو فرعاً آخر.',
      'hr.reports.asOfToday': 'حتى اليوم',
      'hr.reports.asOfTodayHint': 'هذا التقرير لقطة حتى تاريخ اليوم — لا ينطبق عليه نطاق تواريخ.',
      'hr.reports.kpi.employees': 'الموظفون',
      'hr.reports.kpi.balanceDays': 'رصيد الإجازات (أيام)',
      'hr.reports.kpi.gosiUnpaid': 'فترات غير مدفوعة',
      'hr.settings.navTitle': 'إعدادات الموارد البشرية',
      'hr.settings.subtitle': 'تقويم الرواتب، نطاقات السعودة، بيانات بنك حماية الأجور، ومستلمو تنبيهات الموارد البشرية.',
      'hr.settings.wps.title': 'حماية الأجور (WPS)',
      'hr.settings.wps.hint': 'بيانات البنك والمنشأة اللازمة لتوليد ملف حماية الأجور.',
      'hr.settings.wps.edit': 'تعديل بيانات حماية الأجور',
      'hr.reports.col.period': 'الفترة',
      'hr.reports.col.employee': 'الموظف',
      'hr.reports.col.branch': 'الفرع',
      'hr.reports.gosi.title': 'التأمينات المستحقة',
      'hr.reports.gosi.empty': 'لا توجد دورات رواتب معتمدة بعد',
      'hr.reports.gosi.employer': 'حصة صاحب العمل',
      'hr.reports.gosi.employee': 'حصة الموظف',
      'hr.reports.gosi.sanedEmployer': 'ساند (صاحب العمل)',
      'hr.reports.gosi.sanedEmployee': 'ساند (الموظف)',
      'hr.reports.gosi.total': 'الإجمالي',
      'hr.reports.gosi.paidBadge': 'مدفوعة',
      'hr.reports.gosi.unpaidBadge': 'غير مدفوعة',
      'hr.reports.gosi.pay': 'دفع التأمينات',
      'hr.reports.gosi.payTitle': 'ترحيل مصروف التأمينات',
      'hr.reports.gosi.paidFrom': 'الدفع من',
      'hr.reports.gosi.paidFrom.hand': 'نقدًا',
      'hr.reports.gosi.paidFrom.bank': 'البنك',
      'hr.reports.gosi.paid': 'تم ترحيل مصروف التأمينات',
      'hr.reports.gosi.alreadyPaid': 'يوجد مصروف تأمينات مُرحَّل لهذه الفترة بالفعل',
      'hr.reports.gosi.actualBill': 'فاتورة التأمينات الفعلية',
      'hr.reports.gosi.variance': 'الفرق',
      'hr.reports.gosi.actualBillInvalid': 'أدخل مبلغًا 0 أو أكثر',
      'hr.reports.gosi.actualBillSaved': 'تم حفظ فاتورة التأمينات',
      'hr.reports.eosb.title': 'التزام نهاية الخدمة',
      'hr.reports.eosb.empty': 'لا يوجد موظفون نشطون لديهم أساس أجر',
      'hr.reports.eosb.tenure': 'مدة الخدمة (سنوات)',
      'hr.reports.eosb.accrued': 'المستحق حتى الآن',
      'hr.reports.eosb.companyTotal': 'إجمالي الشركة',
      'hr.reports.leave.title': 'أرصدة الإجازات',
      'hr.reports.leave.empty': 'لا يوجد موظفون نشطون',
      'hr.reports.leave.entitlement': 'المستحق',
      'hr.reports.leave.taken': 'المستخدم',
      'hr.reports.leave.balance': 'الرصيد',
      'hr.reports.leave.sickTier': 'فئة الإجازة المرضية',
      'hr.reports.leave.valuation': 'القيمة',
      'hr.reports.leave.companyTotalValuation': 'إجمالي قيمة الشركة',
      'hr.reports.cost.title': 'تكلفة الرواتب حسب الفرع',
      'hr.reports.cost.empty': 'لا توجد دورات رواتب مدفوعة بعد',
      'hr.reports.cost.employees': 'عدد الموظفين',
      'hr.reports.cost.gross': 'الإجمالي',
      'hr.reports.cost.total': 'إجمالي التكلفة',
      'hr.reports.cost.eosbAccrual': 'استحقاق نهاية الخدمة',
      'hr.reports.cost.additionalCosts': 'تكاليف إضافية',
      'hr.reports.cost.fullyLoaded': 'التكلفة الكاملة',

      // تقويم العطلات الرسمية (HR statutory v2, T2). GET/PUT /api/hr/holidays.
      'hr.reports.holidays.title': 'العطلات الرسمية',
      'hr.reports.holidays.eidHint': 'تتبع تواريخ عيد الفطر وعيد الأضحى تقويم أم القرى وتختلف كل عام — تأكد منها من القرار الرسمي وحدّثها هنا قبل كل عيد.',
      'hr.reports.holidays.seedNotice': 'يتم عرض تواريخ افتراضية مقترحة لهذا العام — لن يتم حفظ أي شيء حتى تضغط حفظ.',
      'hr.reports.holidays.year': 'السنة',
      'hr.reports.holidays.name': 'الاسم (إنجليزي)',
      'hr.reports.holidays.nameAr': 'الاسم (عربي)',
      'hr.reports.holidays.start': 'تاريخ البداية',
      'hr.reports.holidays.days': 'عدد الأيام',
      'hr.reports.holidays.add': 'إضافة عطلة',
      'hr.reports.holidays.remove': 'حذف العطلة',
      'hr.reports.holidays.empty': 'لا توجد عطلات مُعدّة لهذا العام',
      'hr.reports.holidays.saved': 'تم حفظ تقويم العطلات',
      'hr.reports.holidays.savedBadge': 'محفوظة',
      'hr.reports.holidays.notSetYet': 'لم تُعد بعد',
      'hr.reports.holidays.copyFrom': 'نسخ من {{year}}',
      'hr.reports.holidays.copied': 'تم النسخ من {{year}} — راجع تواريخ العيد ثم احفظ.',
      'hr.reports.holidays.nextYearUnset': 'لا يوجد تقويم عطلات لعام {{year}} حتى الآن. سيتم احتساب الإجازات والعمل الإضافي للعام القادم كأنه لا توجد عطلات رسمية إلى أن تقوم بإعداده.',
      'hr.reports.ramadan.title': 'فترة رمضان',
      'hr.reports.ramadan.hint': 'يحدد حد ساعات العمل الإضافي اليومي المخفّض إلى 6 ساعات (المادة 98) لهذه الفترة. يتقدّم رمضان نحو 11 يومًا كل عام ميلادي — تأكد من التواريخ وحدّثها هنا قبل كل رمضان.',
      'hr.reports.ramadan.start': 'تاريخ البداية',
      'hr.reports.ramadan.end': 'تاريخ النهاية',
      'hr.reports.ramadan.saved': 'تم حفظ فترة رمضان',
      'hr.reports.ramadan.saveAria': 'حفظ فترة رمضان',

      // Nitaqat (Saudization) status report + threshold settings (HR
      // statutory v2, T7). NO hardcoded thresholds — the tenant copies its
      // own color-band percentages from Qiwa; see nitaqatService.
      'hr.reports.nitaqat.title': 'حالة نطاقات (التوطين)',
      'hr.reports.nitaqat.empty': 'لا يوجد موظفون نشطون لاحتسابهم ضمن نطاقات',
      'hr.reports.nitaqat.ratio': 'نسبة التوطين',
      'hr.reports.nitaqat.totalEmployees': 'إجمالي الموظفين النشطين',
      'hr.reports.nitaqat.weightedSaudis': 'السعوديون الموزونون',
      'hr.reports.nitaqat.others': 'غير سعوديين (بخلاف دول الخليج)',
      'hr.reports.nitaqat.disabledBonus': 'وزن إضافي لذوي الإعاقة',
      'hr.reports.nitaqat.disabledBonusHint': 'وزن إضافي من الموظفين السعوديين ذوي الإعاقة (×4 من وزنهم الأصلي)، بحد أقصى 10% من القوى العاملة المحتسبة.',
      'hr.reports.nitaqat.microNotice': 'تُطبَّق قاعدة المنشآت متناهية الصغر (5 موظفين أو أقل): سعودي واحد على الأقل = أخضر، بلا سعوديين = أحمر.',
      'hr.reports.nitaqat.saudiCount': 'الموظفون السعوديون',
      'hr.reports.nitaqat.band.red': 'أحمر',
      'hr.reports.nitaqat.band.green': 'أخضر',
      'hr.reports.nitaqat.band.low_green': 'أخضر منخفض',
      'hr.reports.nitaqat.band.medium_green': 'أخضر متوسط',
      'hr.reports.nitaqat.band.high_green': 'أخضر مرتفع',
      'hr.reports.nitaqat.band.platinum': 'بلاتيني',
      'hr.reports.nitaqat.emptyThresholds': 'أدخل نطاقات نشاطك من قوى لرؤية نطاقك اللوني الرسمي هنا.',
      'hr.reports.nitaqat.qiwaLink': 'فتح حاسبة نطاقات في قوى',
      'hr.reports.nitaqat.disclaimer': 'هذا تقدير للتخطيط فقط — النطاق الرسمي هو ما تعرضه منصة قوى لمنشأتك.',
      'hr.reports.nitaqatSettings.title': 'حدود ألوان نطاقات (من قوى)',
      'hr.reports.nitaqatSettings.hint': 'انسخ النسب الحالية لنطاقات نشاطك من قوى (إدارة المنشأة ← نطاقات، أو أداة حاسبة نطاقات) — لا تملك MotorMind وصولًا لهذه القيم ولا تخمّنها أبدًا.',
      'hr.reports.nitaqatSettings.activityLabel': 'تسمية النشاط (للرجوع إليها)',
      'hr.reports.nitaqatSettings.lowGreen': 'أخضر منخفض %',
      'hr.reports.nitaqatSettings.mediumGreen': 'أخضر متوسط %',
      'hr.reports.nitaqatSettings.highGreen': 'أخضر مرتفع %',
      'hr.reports.nitaqatSettings.platinum': 'بلاتيني %',
      'hr.reports.nitaqatSettings.saved': 'تم حفظ حدود نطاقات',
      'hr.reports.nitaqatSettings.saveAria': 'حفظ حدود نطاقات',

      // تقرير ربحية الموظفين (HR PR-5, Task 5).
      'hr.profit.title': 'ربحية الموظفين',
      'hr.profit.employee': 'الموظف',
      'hr.profit.hours': 'الساعات',
      'hr.profit.utilization': 'نسبة الاستغلال',
      'hr.profit.income': 'الإيراد',
      'hr.profit.laborCost': 'تكلفة الأجرة',
      'hr.profit.fullCost': 'التكلفة الكاملة (راتب+تأمينات+نهاية خدمة+إضافية)',
      'hr.profit.profit': 'الربح',
      'hr.profit.margin': 'هامش الربح %',
      'hr.profit.export': 'تصدير CSV',
      'hr.profit.empty': 'لا توجد بيانات لهذه الفترة',
      'hr.profit.searchEmployee': 'بحث عن موظف',
      'hr.profit.periodNote': 'الساعات والإيراد والتكلفة تغطي {{from}} ← {{to}}. الراتب والتكاليف الإضافية موزّعة بالتناسب على أيام هذه الفترة.',
      'hr.profit.accessDenied': 'لا صلاحية',
      'hr.profit.accessDeniedDesc': 'ليست لديك صلاحية لعرض تقرير ربحية الموظفين.',
      'hr.profit.doubleCountNote': 'تُحسب التكلفة الكاملة من بيانات عقد الموارد البشرية (الراتب + التكاليف الإضافية). إذا كانت هذه التكاليف مسجّلة أيضاً كمصروفات منفصلة للشركة، تجنب احتسابها مرتين.',

      vatSettingsManagedAtCompanyLevel: 'إعدادات ضريبة القيمة المضافة تُدار على مستوى الشركة',
      vatSettingsCompanyLevelDescription: 'يتم تكوين رقم تسجيل ضريبة القيمة المضافة والمعدل وحالة الإعفاء من قبل مسؤول شركتك وتنطبق على جميع مراكز خدمات السيارات التابعة لشركتك.',
      vatRegistrationNumber: 'رقم تسجيل ضريبة القيمة المضافة',
      notSet: 'غير محدد',
      toModifyVatSettings: 'لتعديل إعدادات ضريبة القيمة المضافة، يرجى الاتصال بمسؤول شركتك أو الوصول إلى بوابة المسؤول.',
      jobStatusDraft: 'مسودة',
      jobStatusReceived: 'مستلم',
      jobStatusDiagnosis: 'تشخيص',
      jobStatusInProgress: 'قيد التنفيذ',
      jobStatusQualityCheck: 'فحص الجودة',
      jobStatusPendingPayment: 'في انتظار الدفع',
      jobStatusReady: 'جاهز',
      jobStatusDelivered: 'تم التسليم',
      jobStatusClosed: 'مغلق',

      // Walk-in Vehicle
      walkInVehicle: 'سيارة زيارة مباشرة',
      walkInVehicleTitle: 'تسجيل سيارة زيارة مباشرة',
      walkInVehicleDesc: 'تسجيل سيارة بدون معلومات المالك. يمكن إضافة معلومات العميل لاحقاً.',
      walkInIncomplete: 'أمر عمل غير مكتمل',
      walkInIncompleteBanner: 'أمر العمل هذا غير مكتمل — معلومات العميل مفقودة. أكمل البيانات قبل بدء العمل.',
      completeCustomerInfo: 'إكمال معلومات العميل',
      completeJobCard: 'إكمال أمر العمل',
      completingJobCard: 'جارٍ الإكمال...',
      completeJobCardTitle: 'إكمال أمر العمل',
      completeJobCardDesc: 'أضف معلومات العميل لتفعيل أمر العمل هذا.',
      selectRealCustomer: 'اختر أو أنشئ عميلاً',
      reviewVehicleDetails: 'مراجعة تفاصيل السيارة',
      confirmCompletion: 'تأكيد والتفعيل',
      jobCardActivated: 'تم تفعيل أمر العمل بنجاح',
      walkInCreated: 'تم إنشاء أمر عمل الزيارة المباشرة بنجاح',
      fillKnownInfo: 'أدخل ما هو معروف، واتركه فارغاً إذا كان غير معروف',
      unknownPlaceholder: 'غير معروف',

      // Quotations Page
      quotations: 'عروض الأسعار',
      'jc.quotRow.emptyHint': 'لا يوجد — يُنشأ بعد التشخيص',
      'jc.quoteBlock.noOpen': 'لا يوجد عرض سعر مفتوح',
      'jc.quoteBlock.validUntil': 'صالح حتى',
      'jc.quoteBlock.deposit': 'العربون',
      'jc.quoteBlock.paid': 'مدفوع',
      'jc.quoteBlock.due': 'مستحق',
      'jc.quoteBlock.none': 'لا يوجد',
      'jc.quoteBlock.sent': 'أُرسل للعميل',
      'jc.quoteBlock.notSent': 'لم يُرسل للعميل',
      'jc.quoteBlock.resends': 'إعادة الإرسال المستخدمة',
      'jc.quoteBlock.ofFive': 'من 5',
      'jc.quoteBlock.open': 'فتح عرض السعر',
      'jc.quoteBlock.previous': 'عروض سابقة',
      sent: 'مرسل',
      rejected: 'مرفوض',
      draft: 'مسودة',
      
      // Finding Approval Modal
      editTaskBeforeCreating: 'تعديل المهمة قبل الإنشاء',
      reviewAndAdjustBeforeSending: 'راجع وعدّل القطع والخدمات قبل الإرسال للعميل',
      findingDescription: 'وصف الاكتشاف',
      recommendedAction: 'الإجراء الموصى به',
      reportedBy: 'تم الإبلاغ بواسطة',
      reportedOn: 'تاريخ الإبلاغ',
      severity: 'الخطورة',
      noServicesAdded: 'لم تتم إضافة خدمات',
      noPartsAdded: 'لم تتم إضافة قطع',
      serviceName: 'اسم الخدمة',
      enterServiceName: 'أدخل اسم الخدمة',
      enterPartName: 'أدخل اسم القطعة',
      priceSAR: 'السعر (ريال)',
      qtySAR: 'الكمية',
      removeService: 'إزالة الخدمة',
      removePart: 'إزالة القطعة',
      estimatedTotal: 'الإجمالي المقدر',
      serviceCount: 'خدمة',
      partCount: 'قطعة',
      saveAndCreateTask: 'حفظ وإنشاء المهمة',
      creatingTask: 'جاري إنشاء المهمة...',
      findingApprovedAndTaskCreated: 'تم الموافقة على الاكتشاف وإنشاء المهمة',
      
      // Task Status Messages
      itemsMustBeApprovedBeforeStarting: 'يجب الموافقة على البنود قبل البدء',
      
      // Calculation Labels
      grossTotal: 'المجموع الفرعي',
      netTaxableAmount: 'المبلغ الخاضع للضريبة',
      advancePayment: 'دفعة مقدمة',

      // Create Invoice/Quotation Forms
      createInvoice: 'إنشاء فاتورة',
      backToInvoices: 'العودة للفواتير',
      backToQuotations: 'العودة لعروض الأسعار',
      selectJobCard: 'اختر أمر عمل (اختياري)',
      manualEntryNoJobCard: 'إدخال يدوي (بدون أمر عمل)',
      enterInvoiceDetailsManually: 'أدخل تفاصيل الفاتورة يدوياً',
      enterQuotationDetailsManually: 'أدخل تفاصيل عرض السعر يدوياً',
      invoiceItems: 'بنود الفاتورة',
      quotationItems: 'بنود عرض السعر',
      itemDescription: 'وصف البند',
      unitPriceSAR: 'سعر الوحدة (ريال)',
      quantity: 'الكمية',
      manualEntry: 'إدخال يدوي',
      remove: 'إزالة',
      addItem: 'إضافة بند',
      subtotal: 'المجموع الفرعي',
      taxRate: 'معدل الضريبة',
      taxAmount: 'مبلغ الضريبة',
      discount: 'الخصم',
      notesOptional: 'ملاحظات (اختياري)',
      additionalNotes: 'ملاحظات إضافية أو شروط الدفع',
      createQuotation: 'إنشاء عرض سعر',
      validUntilDate: 'صالح حتى',
      autoFilledFromJobCard: 'تم التعبئة تلقائياً من أمر العمل',
      selectItem: 'اختر البند',
      parts: 'قطع الغيار',
      services: 'الخدمات',

      // Dashboard Recent Activity
      newJobCardCreated: 'تم إنشاء أمر عمل جديد',
      invoiceGenerated: 'تم إنشاء فاتورة',
      customerAdded: 'تم إضافة عميل',
      minutesAgo: 'منذ دقائق',
      hourAgo: 'منذ ساعة',
      hoursAgo: 'منذ ساعات',
      daysAgo: 'منذ أيام',

      // Common Form Labels
      firstName: 'الاسم الأول',
      lastName: 'اسم العائلة',
      emailAddress: 'عنوان البريد الإلكتروني',
      password: 'كلمة المرور',
      phoneNumber: 'رقم الهاتف',
      phoneRequiredForOtp: 'رقم الهاتف مطلوب — يُستخدم لتسجيل الدخول عبر رمز SMS.',
      enterFirstName: 'أدخل الاسم الأول',
      enterLastName: 'أدخل اسم العائلة',
      location: 'الموقع',
      locationBranch: 'الموقع (الفرع)',
      selectBranchOrType: 'اختر الفرع أو اكتب',
      selectOrType: 'اختر أو اكتب',
      costPriceOptional: 'سعر التكلفة (اختياري)',
      compatibilityCarModels: 'التوافق (موديلات السيارات)',
      initialStock: 'المخزون الأولي',
      minLevel: 'الحد الأدنى',
      unitPrice: 'سعر الوحدة',

      // Customer Modal
      locationDetails: 'تفاصيل الموقع',
      areaDistrict: 'المنطقة/الحي',
      city: 'المدينة',
      region: 'المنطقة',
      selectArea: 'اختر المنطقة',
      selectCity: 'اختر المدينة',
      selectRegion: 'اختر المنطقة',
      buildingNumber: 'رقم المبنى',
      district: 'الحي',
      postalCode: 'الرمز البريدي',
      buildingNumberInvalid: 'رقم المبنى يجب أن يتكون من 4 أرقام بالضبط',
      postalCodeInvalid: 'الرمز البريدي يجب أن يتكون من 5 أرقام بالضبط',
      'customers.buildingNumber': 'رقم المبنى',
      'customers.district': 'الحي',
      'customers.postalCode': 'الرمز البريدي',
      'customers.buildingNumberInvalid': 'رقم المبنى يجب أن يتكون من 4 أرقام بالضبط',
      'customers.postalCodeInvalid': 'الرمز البريدي يجب أن يتكون من 5 أرقام بالضبط',
      nationalAddress: 'العنوان الوطني (هيئة الزكاة والضريبة والجمارك)',
      // NationalAddressFields — كتلة عنوان المشتري الموحّدة لنماذج العملاء الثلاثة.
      'na.title': 'العنوان الوطني',
      'na.requiredForB2b': 'مطلوب للفواتير الضريبية',
      'na.lookup': 'ابحث عن العنوان',
      'na.lookupHint': 'اكتب العنوان الوطني المختصر أو اسم الشارع — نعبّئ ما نجده، وتكمل الباقي يدوياً.',
      'na.lookupPlaceholder': 'مثال: RAAA1111 أو اسم الشارع',
      'na.autofilled': 'تم تعبئة {{n}} من الحقول من نتيجة البحث — راجعها قبل الحفظ.',
      'na.buildingNumber': 'رقم المبنى',
      'na.postalCode': 'الرمز البريدي',
      'na.district': 'الحي',
      'na.districtPlaceholder': 'مثال: العليا',
      enterDistrict: 'مثال: العليا',
      additionalAddressInfo: 'معلومات عنوان إضافية (اختياري)',
      emailOptional: 'البريد الإلكتروني (اختياري)',

      // Vehicle Modal
      quickVehicleRegistration: 'تسجيل سريع للمركبة',
      scanIstamara: 'مسح الاستمارة',
      vehiclePhotos: 'صور المركبة',
      registrationAndInsurance: 'التسجيل والتأمين',
      vehicleOwnerName: 'اسم مالك المركبة',
      optionalFromIstamara: 'اختياري - من الاستمارة',
      ownerNameAsShownOnVehicleRegistration: 'اسم المالك كما هو موضح في تسجيل المركبة',
      thisIsTheRegisteredOwnerNameFromIstamara: 'هذا هو اسم المالك المسجل من الاستمارة (قد يختلف عن العميل)',
      istamaraDetails: 'تفاصيل الاستمارة',
      sequenceNumber: 'الرقم التسلسلي',
      chassisNumber: 'رقم الشاسيه',
      selectCustomer: 'اختر العميل',
      noCustomersFound: 'لم يتم العثور على عملاء',
      searchByNameOrPhone: 'البحث بالاسم أو الهاتف...',
      uploadPhotos: 'رفع الصور',
      model: 'الموديل',
      brand: 'الماركة',
      generation: 'الجيل',
      selectModel: 'اختر الموديل',
      selectBrand: 'اختر الماركة',
      selectYear: 'اختر السنة',
      selectGeneration: 'اختر الجيل',
      transportBlue: 'نقل (أزرق)',
      taxiYellow: 'تاكسي (أصفر)',
      petrol: 'بنزين',
      diesel: 'ديزل',
      electric: 'كهربائي',
      hybrid: 'هجين',
      silver: 'فضي',
      gray: 'رمادي',
      red: 'أحمر',
      blue: 'أزرق',
      green: 'أخضر',
      yellow: 'أصفر',
      brown: 'بني',
      beige: 'بيج',
      gold: 'ذهبي',
      bronze: 'برونزي',
      maroon: 'عنابي',
      purple: 'بنفسجي',
      orange: 'برتقالي',
      automatic: 'أوتوماتيك',
      manual: 'يدوي',
      decoding: 'جاري فك التشفير',
      scanVINBarcodeFromCarDoor: 'امسح باركود VIN من باب السيارة أو أدخله يدوياً، ثم انقر "فك التشفير"',
      policyNumber: 'رقم الوثيقة',
      insuranceExpiry: 'تاريخ انتهاء التأمين',
      egTawuniyaMedgulf: 'مثال: التعاونية، ميدغلف',
      insurancePolicyNumber: 'رقم وثيقة التأمين',
      plateNumberMaxDigitsLetters: 'رقم اللوحة * (أرقام: حد أقصى 4، حروف: حد أقصى 3)',
      typeInBottomRowEnglish: 'اكتب في الصف السفلي (إنجليزي). ستظهر الأرقام العربية تلقائياً في الصف العلوي',
      plateType: 'نوع اللوحة',
      privateCarsWhite: 'سيارات خاصة (بيضاء)',
      fuelType: 'نوع الوقود',
      color: 'اللون',
      selectType: 'اختر النوع',
      selectColor: 'اختر اللون',
      transmission: 'ناقل الحركة',
      vinVehicleIdentificationNumber: 'رقم هيكل المركبة',
      decode: 'فك الترميز',
      scan: 'مسح',
      registrationInsuranceOptional: 'التسجيل والتأمين (اختياري)',
      insuranceCompany: 'شركة التأمين',
      registrationExpiry: 'انتهاء التسجيل',

      // User Modal
      assignedGarage: 'المركز المعيّن',
      selectGarage: 'اختر المركز',
      selectRole: 'اختر الدور',
      activeStatus: 'الحالة النشطة',

      // Buttons
      refresh: 'تحديث',
      new: 'جديد',
      addService: 'إضافة خدمة',
      uploading: 'جاري الرفع...',
      passwordLeaveEmpty: 'كلمة المرور (اتركها فارغة للاحتفاظ بالحالية)',
      createUser: 'إنشاء المستخدم',
      updateUser: 'تحديث المستخدم',

      // Permission Manager - Categories
      adminPermissions: 'صلاحيات المدير',
      permissionsGranted: 'صلاحيات ممنوحة',
      customerManagement: 'إدارة العملاء',
      financial: 'المالية',
      inventory: 'المخزون',
      deselectAll: 'إلغاء تحديد الكل',

      // Permission Manager - Customer Management
      customersDelete: 'إيقاف العملاء (قابل للتراجع؛ لا يُحذف العملاء أبداً)',
      customersCreate: 'إضافة عملاء جدد',
      customersUpdate: 'تحديث بيانات العملاء وإعادة تنشيطهم',
      customersRead: 'عرض معلومات العملاء',

      // Permission Manager - Financial
      invoicesDelete: 'حذف الفواتير',
      invoicesCreate: 'إنشاء الفواتير',
      invoicesSend: 'إرسال الفواتير للعملاء',
      invoicesRead: 'عرض الفواتير',
      pricingView: 'عرض أسعار القطع والخدمات',
      invoicesUpdate: 'تحديث الفواتير',
      quotationsCreate: 'إنشاء عروض الأسعار',
      quotationsApprove: 'الموافقة على عروض الأسعار',
      quotationsClose: 'إلغاء عروض الأسعار',
      quotationsRead: 'عرض عروض الأسعار',
      quotationsDelete: 'حذف عروض الأسعار',
      quotationsUpdate: 'تحديث عروض الأسعار',

      // Permission Manager - Inventory
      partsCreate: 'إضافة قطع جديدة',
      partsApprove: 'الموافقة على طلبات القطع',
      
      // Customer Provided Parts
      customerProvidedNoCost: 'يوفرها العميل (بدون تكلفة)',
      checkIfCustomerWillBringPart: 'حدد إذا كان العميل سيحضر القطعة بنفسه',
      
      // Findings
      findingsMaxImagesAllowed: 'الحد الأقصى 5 صور',
      findingsDescribeFindingRequired: 'يرجى وصف الاكتشاف',
      findingsAddPartOrServiceRequired: 'يرجى إضافة قطعة أو خدمة واحدة على الأقل',
      findingsSubmittedForReview: 'تم إرسال الاكتشاف الجديد للمراجعة من قبل المدير',
      findingsSubmitFailed: 'فشل في إرسال الاكتشاف',
      findingsPlaceholderDescribeFinding: 'صف ما وجدته...',
      findingsSeverityLowDetail: 'منخفض - يمكن الانتظار',
      findingsSeverityMediumDetail: 'متوسط - يجب معالجته',
      findingsSeverityHighDetail: 'عالي - يحتاج اهتمام عاجل',
      findingsPlaceholderRecommendedAction: 'ما الذي يجب فعله بشأن هذا؟',
      findingsRequiredParts: 'القطع المطلوبة',
      findingsPartNumber: 'القطعة {{n}}',
      findingsSelectPart: 'اختر القطعة',
      findingsSelectFromInventory: 'اختر من المخزون...',
      findingsOrCustomPartName: 'أو اسم قطعة مخصص',
      findingsCustomPartNamePlaceholder: 'أدخل اسم القطعة المخصص',
      findingsRequiredServices: 'الخدمات المطلوبة',
      findingsAddService: 'إضافة خدمة',
      findingsServiceNumber: 'الخدمة {{n}}',
      findingsServiceNameExample: 'مثال: استبدال فرامل',
      findingsServiceDetailsPlaceholder: 'تفاصيل الخدمة...',
      findingsEstHours: 'الساعات المقدرة',
      findingsPhotosMax: 'الصور (حد أقصى 5)',
      findingsPhotoPreviewAlt: 'معاينة {{n}}',
      findingsClickUploadPhotos: 'انقر لرفع الصور',
      findingsSubmitFinding: 'إرسال الاكتشاف',
      findingsLoadFailed: 'فشل في تحميل الاكتشافات',
      findingsApproveFailed: 'فشل في الموافقة على الاكتشاف',
      findingsRejectionReasonRequired: 'يرجى تقديم سبب الرفض',
      findingsFindingRejected: 'تم رفض الاكتشاف',
      findingsRejectFailed: 'فشل في رفض الاكتشاف',
      findingsStatusApproved: 'موافق عليه',
      findingsStatusRejected: 'مرفوض',
      findingsStatusPendingReview: 'قيد المراجعة',
      findingsSectionTitle: 'الاكتشافات',
      findingsLoadingFindings: 'جاري تحميل الاكتشافات...',
      findingsNoFindingsYet: 'لم يتم الإبلاغ عن اكتشافات بعد.',
      findingsCount: 'الاكتشافات ({{count}})',
      findingsLabelDescription: 'الوصف:',
      findingsUnnamedPart: 'قطعة بدون اسم',
      findingsApproveAndCreateTask: 'الموافقة وإنشاء المهمة',
      findingsRejectionReasonLabel: 'سبب الرفض:',
      findingsRejectFindingTitle: 'رفض الاكتشاف',
      findingsRejectionReasonField: 'سبب الرفض *',
      findingsRejectPlaceholder: 'يرجى تقديم سبب رفض هذا الاكتشاف...',
      findingsRejecting: 'جاري الرفض...',
      findingsRejectFinding: 'رفض الاكتشاف',
      
      // Task Card
      taskPriorityUrgent: 'عاجل',
      taskPriorityHigh: 'عالية',
      taskPriorityNormal: 'عادي',
      taskPriorityLow: 'منخفضة',
      taskStatusPending: 'قيد الانتظار',
      taskStatusInProgress: 'قيد التنفيذ',
      taskStatusCompleted: 'مكتمل',
      taskStatusApproved: 'موافق عليه',
      taskStatusRejected: 'مرفوض',
      taskStatusPartiallyApproved: 'موافق عليه جزئياً',
      taskStatusQCPassed: 'اجتاز فحص الجودة',
      taskStatusQCFailed: 'فشل في فحص الجودة',
      taskIssueTypeMechanical: 'ميكانيكي',
      taskIssueTypeElectrical: 'كهربائي',
      taskIssueTypeBodywork: 'هيكل السيارة',
      taskIssueTypeDiagnostic: 'تشخيصي',
      taskIssueTypePreventive: 'وقائي',
      taskIssueTypeOther: 'أخرى',
      taskQualityCheck: 'فحص الجودة',
      'qc.hub.title': 'فحص الجودة',
      'qc.hub.awaiting': 'بانتظار الفحص ({{count}})',
      'qc.hub.check': 'افحص',
      'qc.hub.done': 'تم فحصها',
      'qc.hub.passed': 'نجح',
      'qc.hub.failed': 'فشل',
      'qc.hub.allChecked': 'تم فحص كل المهام',
      'qc.hub.allCheckedHint': 'لا يوجد ما ينتظر فحص الجودة في أمر العمل هذا.',
      taskDeleteDisabledQuotation: 'لا يمكن الحذف - البنود في عرض السعر',
      taskDeleteTask: 'حذف المهمة',
      taskTemporary: 'مؤقت',
      taskPriceLine: 'السعر: {{price}} ريال',
      taskNoServicesYet: 'لم تتم إضافة خدمات بعد',
      taskServicesTotal: 'إجمالي الخدمات:',
      taskNoPartsYet: 'لم تتم إضافة قطع بعد',
      taskRequestedBy: 'تم الطلب بواسطة',
      taskPartsTotal: 'إجمالي القطع:',
      taskProgress: 'التقدم',
      taskTaskTotal: 'إجمالي المهمة',
      taskEstHours: 'تقدير {{hours}} ساعة',
      taskQCRequired: 'فحص الجودة مطلوب قبل الإكمال',
      taskPerformQCNow: 'إجراء فحص الجودة الآن',
      taskQCPassed: 'فحص الجودة: نجح',
      taskQCFailed: 'فحص الجودة: فشل',
      taskStatusOnHold: 'معلّق',
      taskStatusUnderQC: 'قيد فحص الجودة',
      taskIssueTypeACHeating: 'تكييف/تدفئة',
      taskIssueTypeTiresWheels: 'إطارات/عجلات',
      taskIssueTypeRoutineService: 'صيانة دورية',
      taskQCRequiredSubtext: 'يجب فحص هذه المهمة قبل المتابعة',
      taskEditTask: 'تعديل المهمة',
      taskStatusLabel: 'الحالة:',
      taskPriorityLabel: 'الأولوية:',
      taskDescriptionPlaceholder: 'وصف المهمة...',
      taskHoursShort: '{{hours}} ساعة',
      taskActualHours: 'الفعلي: {{hours}} ساعة',
      taskTimeTaken: 'الوقت المستغرق',
      taskTimeTakenValue: '{{duration}}',
      taskPayerLabel: 'دافع المهمة',
      taskPayerInsurer: 'شركة التأمين',
      taskPayerCustomer: 'العميل',
      taskPayerPermissionRequired: 'صلاحية مطالبات التأمين مطلوبة',
      taskCollapse: 'طي',
      taskExpand: 'توسيع',
      taskServicesCount_zero: 'لا خدمات',
      taskServicesCount_one: 'خدمة واحدة',
      taskServicesCount_two: 'خدمتان',
      taskServicesCount_few: '{{count}} خدمات',
      taskServicesCount_many: '{{count}} خدمة',
      taskServicesCount_other: '{{count}} خدمة',
      taskPartsCount_zero: 'لا قطع',
      taskPartsCount_one: 'قطعة واحدة',
      taskPartsCount_two: 'قطعتان',
      taskPartsCount_few: '{{count}} قطع',
      taskPartsCount_many: '{{count}} قطعة',
      taskPartsCount_other: '{{count}} قطعة',
      taskEnterEditToDelete: 'ادخل وضع التعديل لحذف المهام',
      taskCannotAddServicesPendingApproval: 'لا يمكن إضافة خدمات أثناء انتظار الموافقة على عرض السعر',
      taskCannotAddPartsPendingApproval: 'لا يمكن إضافة قطع أثناء انتظار الموافقة على عرض السعر',
      taskPerformQualityCheckTitle: 'إجراء فحص الجودة',
      taskAccept: 'قبول',
      taskRequestedByMechanic: 'طلب بواسطة {{first}} {{last}}',
      taskPartQtyLabel: 'الكمية: {{qty}}',
      taskPartUnitPrice: 'سعر الوحدة: {{price}} ريال',
      taskPartLineTotal: 'الإجمالي: {{total}} ريال',
      
      // Mechanic Portal - Job Details
      fuelLevel: 'مستوى الوقود',
      full: 'ممتلئ',
      mileageIn: 'المسافة المقطوعة',
      reportedIssues: 'طلب العميل',
      pending: 'قيد الانتظار',
      breakCheck: 'فحص الفرامل',
      approved: 'موافق عليه',
      startTask: 'بدء المهمة',
      taskStartGateHint: 'يُفتح تلقائياً بعد تأكيد الاستلام',
      startDiagnosis: 'بدء التشخيص',
      noTasksYet: 'لا توجد مهام بعد',
      clickStartDiagnosisAboveToAddTasks: 'انقر على بدء التشخيص أعلاه لإضافة المهام',
      waitForQuotationApproval: 'انتظر الموافقة على عرض السعر',
      newFinding: 'اكتشاف جديد',
      startRepairFirst: 'ابدأ الإصلاح أولاً',
      addTask: 'إضافة مهمة',
      onlyDuringDiagnosis: 'فقط أثناء التشخيص',
      takePhoto: 'التقاط صورة',
      myJobs: 'أعمالي',
      job: 'عمل',
      jobs: 'أعمال',
      searchByJobPlateCustomer: 'البحث برقم العمل، اللوحة، العميل...',
      mechanicJobBackToJobs: 'العودة للأعمال',
      mechanicJobPhotosSection: 'الصور ({{count}})',
      mechanicJobStartTask: 'بدء المهمة',
      mechanicJobCompleteTask: 'إكمال المهمة',
      mechanicJobTaskCompleted: 'تم إكمال المهمة',
      mechanicJobNoTasks: 'لا توجد مهام بعد',
      mechanicJobAddTask: 'إضافة مهمة',
      mechanicJobOnlyDuringDiagnosis: 'فقط أثناء التشخيص',
      mechanicJobNewFindingDisabled: 'ابدأ الإصلاح أولاً',
      mechanicJobTakePhoto: 'التقاط صورة',
      mechanicJobConfirmMarkServiceDone: 'تحديد هذه الخدمة كمكتملة؟',
      mechanicJobConfirmMarkPartDone: 'تحديد هذه القطعة كمكتملة؟',
      mechanicJobLoadFailed: 'فشل في تحميل تفاصيل العمل',
      mechanicJobStartTaskSuccess: 'تم بدء المهمة بنجاح',
      mechanicJobStartTaskFailed: 'فشل في بدء المهمة',
      mechanicJobCompleteTaskSuccess: 'تم إكمال المهمة بنجاح',
      mechanicJobCompleteTaskFailed: 'فشل في إكمال المهمة',
      mechanicJobPhotoUploadSuccess: 'تم رفع الصورة بنجاح',
      mechanicJobPhotoUploadFailed: 'فشل في رفع الصورة',
      mechanicJobPhotosUploadSuccess: 'تم رفع {{count}} صور بنجاح',
      mechanicJobConfirmMarkServiceTitle: 'تحديد الخدمة كمكتملة',
      mechanicJobConfirmMarkPartTitle: 'تحديد القطعة كمثبتة؟',
      mechanicJobServiceMarkedCompleted: 'تم تحديد الخدمة كمكتملة',
      mechanicJobServiceStatusUpdateFailed: 'فشل في تحديث حالة الخدمة',
      mechanicJobPartMarkedInstalled: 'تم تحديد القطعة كمثبتة',
      mechanicJobPartStatusUpdateFailed: 'فشل في تحديث حالة القطعة',
      mechanicJobIssueReportedSuccess: 'تم الإبلاغ عن المشكلة بنجاح',
      mechanicJobIssueReportFailed: 'فشل في الإبلاغ عن المشكلة',
      mechanicJobPartiallyApprovedWarning: 'تم رفض بعض البنود — اعمل على البنود الموافق عليها فقط',
      mechanicJobTaskCancelled: 'تم إلغاء المهمة',
      mechanicJobAllItemsRejected: 'رفض العميل جميع البنود',
      mechanicJobPartsCount_zero: 'لا قطع',
      mechanicJobPartsCount_one: 'قطعة واحدة',
      mechanicJobPartsCount_two: 'قطعتان',
      mechanicJobPartsCount_few: '{{count}} قطع',
      mechanicJobPartsCount_many: '{{count}} قطعة',
      mechanicJobPartsCount_other: '{{count}} قطعة',
      mechanicJobServicesCount_zero: 'لا خدمات',
      mechanicJobServicesCount_one: 'خدمة واحدة',
      mechanicJobServicesCount_two: 'خدمتان',
      mechanicJobServicesCount_few: '{{count}} خدمات',
      mechanicJobServicesCount_many: '{{count}} خدمة',
      mechanicJobServicesCount_other: '{{count}} خدمة',
      mechanicJobPhotoAlt: 'صورة العمل {{index}}',
      mechanicJobTaskStatusPartiallyApproved: 'موافقة جزئية',
      mechanicJobPendingApproval: 'في انتظار الموافقة',
      mechanicJobInstalled: 'مركّبة',
      mechanicJobHoursUnit: 'س',
      mechanicJobVehicle: 'المركبة',
      mechanicJobPlate: 'اللوحة',
      mechanicJobCustomer: 'العميل',
      mechanicJobMileageIn: 'عداد الكيلومترات',
      mechanicJobFuelLevel: 'مستوى الوقود',
      mechanicJobReportedIssues: 'المشاكل المُبلَّغ عنها',
      mechanicJobTasks: 'المهام',
      mechanicJobExpand: 'عرض',
      mechanicJobCollapse: 'إخفاء',
      mechanicJobNoTasksAssigned: 'لا توجد مهام مُسنَدة بعد',
      mechanicJobDone: 'تم',
      mechanicJobReportIssue: 'الإبلاغ عن مشكلة',
      mechanicJobUploading: 'جارٍ الرفع...',
      mechanicJobWaitingQuotationApproval: 'في انتظار الموافقة على عرض السعر',
      mechanicJobPendingPartsDesc: 'القطع التالية في انتظار موافقة المدير على عرض السعر ولا يمكن تركيبها بعد:',
      mechanicJobPendingServicesDesc: 'الخدمات التالية في انتظار موافقة المدير على عرض السعر:',
      mechanicJobPendingCount: '{{count}} في انتظار الموافقة',
      mechanicJobQCFailedTitle: 'فشل فحص الجودة - يلزم إعادة العمل',
      mechanicJobQCFailedReason: 'السبب',
      mechanicJobQCFailedNote: 'يرجى إصلاح المشاكل وإكمال المهمة مجددًا لإعادة الفحص.',
      mechanicJobQCFailedMustFix: 'يجب إصلاح مهام الجودة الفاشلة أولاً',
      mechanicJobReworkBeforeFinding: 'أكمل إعادة العمل قبل إضافة اكتشافات جديدة',
      mechanicJobCompleteAfterRework: 'إكمال المهمة (بعد إعادة العمل)',
      mechanicJobStartRepairFirst: 'ابدأ الإصلاح أولاً',
      mechanicJobOK: 'موافق',
      mechanicJobCancel: 'إلغاء',
      // QR Scan (Feature 1)
      scanQR: 'مسح رمز QR',
      scanQRTooltip: 'امسح رمز QR للزجاج الأمامي لفتح العمل',
      qrScanTitle: 'مسح رمز QR للعمل',
      qrScanHint: 'ضع رمز QR على الزجاج الأمامي في الإطار',
      qrScanSuccess: 'تم العثور على العمل — جاري الفتح…',
      qrScanNotFound: 'رمز QR غير معروف أو العمل غير موجود في هذا المركز',
      qrScanForeignGarage: 'هذا العمل ينتمي إلى مركز آخر',
      qrScanError: 'فشل في مسح رمز QR',
      // Request Assistance (Feature 3)
      requestAssistance: 'طلب مساعدة',
      assistanceModalTitle: 'طلب مساعدة',
      assistanceReasonCategory: 'السبب',
      assistanceNotes: 'التفاصيل / الملاحظات',
      assistanceNotesPlaceholder: 'مثال: أحتاج مساعدة في ضبط توقيت المحرك…',
      assistanceSend: 'إرسال الطلب',
      assistanceSending: 'جاري الإرسال…',
      assistanceSentSuccess: 'تم إرسال طلب المساعدة إلى المدير',
      assistanceSendFailed: 'فشل في إرسال طلب المساعدة',
      assistanceReasonRequired: 'يرجى إدخال سبب أو ملاحظات',
      assistanceCategory_stuck: 'عالق / غير قادر على المتابعة',
      assistanceCategory_tools: 'أحتاج أدوات خاصة',
      assistanceCategory_parts: 'القطعة غير متوفرة',
      assistanceCategory_safety: 'مخاوف أمنية',
      assistanceCategory_other: 'أخرى',
      // Super-admin Garages page (Feature 4)
      allGarages: 'جميع مراكز خدمات السيارات',
      garagesPageTitle: 'مراكز خدمات السيارات',
      garagesPageDescription: 'جميع مراكز خدمات السيارات في جميع الشركات',
      garageNameColumn: 'المركز',
      garageCompanyColumn: 'الشركة',
      garageCityColumn: 'المدينة',
      garageActiveColumn: 'نشط',
      garageCreatedColumn: 'تاريخ الإنشاء',
      garageDeleteTitle: 'حذف المركز',
      garageDeleteMessage: 'هل تريد حذف هذا المركز نهائياً؟ لا يمكن التراجع عن هذا الإجراء.',
      garageDeleteConfirm: 'حذف المركز',
      garageDeleteSuccess: 'تم حذف المركز',
      garageDeleteFailed: 'فشل في حذف المركز',
      garageHasJobCards: 'لا يمكن الحذف — المركز لديه {{count}} بطاقة عمل. أعد تخصيصها أو أرشفها أولاً.',
      // Report Issue Modal
      issueModalTitle: 'الإبلاغ عن مشكلة',
      issueModalIssueType: 'نوع المشكلة',
      issueModalPriority: 'الأولوية',
      issueModalDescription: 'الوصف',
      issueModalDescriptionPlaceholder: 'صف المشكلة بالتفصيل...',
      issueModalPhotoEvidence: 'صورة توضيحية',
      issueModalOpenCamera: 'فتح الكاميرا',
      issueModalCameraHint: 'انقر لالتقاط صورة للمشكلة',
      issueModalRetake: 'إعادة التقاط الصورة',
      issueModalViewPhoto: 'عرض الصورة',
      issueModalHidePhoto: 'إخفاء الصورة',
      issueModalSubmit: 'إرسال التقرير',
      issueModalSubmitting: 'جارٍ الإرسال...',
      issueModalCancel: 'إلغاء',
      issueTypeQuality: 'مشكلة جودة',
      issueTypeMissingPart: 'قطعة مفقودة',
      issueTypeWrongPart: 'قطعة خاطئة',
      issueTypeDamage: 'تلف مكتشف',
      issueTypeAdditional: 'عمل إضافي مطلوب',
      issueTypeCustomer: 'تلزم موافقة العميل',
      issueTypeOther: 'أخرى',
      issueModalPriorityLow: 'منخفضة',
      issueModalPriorityNormal: 'عادية',
      issueModalPriorityHigh: 'عالية',
      issueModalPriorityUrgent: 'عاجلة',
      
      // Job Card Details
      jobCardDetailsTaskAddedSuccess: 'تمت إضافة المهمة بنجاح',
      jobCardDetailsTaskAddFailed: 'فشل في إضافة المهمة',
      jobCardDetailsConfirmDeleteTask: 'هل أنت متأكد من حذف هذه المهمة؟ لا يمكن التراجع عن هذا الإجراء.',
      jobCardDetailsTaskDeletedSuccess: 'تم حذف المهمة بنجاح',
      jobCardDetailsTaskDeleteFailed: 'فشل في حذف المهمة',
      jobCardDetailsEditModeEnabled: 'تم تفعيل وضع التعديل!',
      jobCardDetailsEditModeCancelled: 'تم إلغاء وضع التعديل',
      jobCardDetailsGenerating: 'جاري الإنشاء...',
      jobCardDetailsGenerateUpdated: 'إنشاء عرض سعر محدث',
      jobCardDetailsEditQuotation: 'تعديل عرض السعر',
      jobCardDetailsResendSMS: 'إعادة إرسال الرسالة',
      jobCardDetailsSending: 'جاري الإرسال...',
      jobCardDetailsEditModeActive: 'وضع التعديل نشط • عدّل البنود أدناه',
      jobCardDetailsStaffViewActive: 'عرض الموظف نشط - الأسعار مخفية',
      jobCardDetailsViewMode: 'وضع العرض:',
      jobCardDetailsManageAssignments: 'إدارة التعيينات',
      jobCardDetailsInteriorCondition: 'حالة الداخلية',
      jobCardDetailsCustomerNotifiedSMS: 'تم إشعار العميل عبر الرسائل • في انتظار الرد',
      jobCardDetailsWaitBeforeResending: 'انتظر {{minutes}} د قبل الإرسال',
      jobCardDetailsConfirmTaskStatusChange: 'هل أنت متأكد من تغيير حالة المهمة إلى {{status}}؟',
      jobCardDetailsTaskUpdatedSuccess: 'تم تحديث المهمة بنجاح',
      jobCardDetailsTaskUpdateFailed: 'فشل في تحديث المهمة',
      jobCardDetailsTaskPayerUpdated: 'تم تحديث دافع المهمة',
      jobCardDetailsTaskPayerUpdateFailed: 'فشل في تحديث دافع المهمة',
      jobCardDetailsTaskStatusUpdated: 'تم تحديث حالة المهمة',
      jobCardDetailsTaskStatusUpdateFailed: 'فشل في تحديث حالة المهمة',
      jobCardDetailsMechanicAssignedSuccess: 'تم تعيين الفني بنجاح',
      jobCardDetailsMechanicAssignFailed: 'فشل في تعيين الفني',
      jobCardDetailsRemoveServiceTitle: 'إزالة الخدمة',
      jobCardDetailsRemoveServiceFromQuotationMessage: 'هل تريد إزالة هذه الخدمة من عرض السعر؟ ستُخفى حتى تنشئ عرض السعر المحدث.',
      jobCardDetailsServiceHiddenToast: 'تم إخفاء الخدمة (ستُزال عند إنشاء عرض السعر المحدث)',
      jobCardDetailsRemoveServicePermanentMessage: 'هل أنت متأكد من إزالة هذه الخدمة نهائياً؟',
      jobCardDetailsServiceRemoved: 'تمت إزالة الخدمة',
      jobCardDetailsRemoveServiceFailed: 'فشل في إزالة الخدمة',
      jobCardDetailsRemovePartTitle: 'إزالة القطعة',
      jobCardDetailsRemovePartFromQuotationMessage: 'هل تريد إزالة هذه القطعة من عرض السعر؟ ستُخفى حتى تنشئ عرض السعر المحدث.',
      jobCardDetailsPartHiddenToast: 'تم إخفاء القطعة (ستُزال عند إنشاء عرض السعر المحدث)',
      jobCardDetailsRemovePartPermanentMessage: 'هل أنت متأكد من إزالة هذه القطعة نهائياً؟',
      jobCardDetailsPartRemovedFromTask: 'تمت إزالة القطعة من المهمة',
      jobCardDetailsRemovePartFailed: 'فشل في إزالة القطعة',
      jobCardDetailsConfirmAcceptPart: 'هل أنت متأكد من قبول طلب هذه القطعة؟',
      jobCardDetailsPartApprovedSuccess: 'تمت الموافقة على القطعة بنجاح',
      jobCardDetailsPartApproveFailed: 'فشل في الموافقة على القطعة',
      jobCardDetailsPromptRejectionReason: 'يرجى إدخال سبب الرفض:',
      jobCardDetailsPartRejected: 'تم رفض القطعة',
      jobCardDetailsPartRejectFailed: 'فشل في رفض القطعة',
      jobCardDetailsPromptEnterQuantity: 'أدخل الكمية الجديدة:',
      jobCardDetailsPromptEnterUnitPriceSAR: 'أدخل سعر الوحدة الجديد (ريال):',
      jobCardDetailsPromptEnterPartNumber: 'أدخل رقم القطعة (اختياري):',
      jobCardDetailsPartUpdatedSuccess: 'تم تحديث القطعة بنجاح',
      jobCardDetailsPartUpdateFailed: 'فشل في تحديث القطعة',
      jobCardDetailsTaskDuplicatedSuccess: 'تم تكرار المهمة بنجاح',
      jobCardDetailsTaskDuplicateFailed: 'فشل في تكرار المهمة',
      jobCardDetailsJobDetailsUpdatedSuccess: 'تم تحديث تفاصيل أمر العمل بنجاح',
      jobCardDetailsJobDetailsUpdateFailed: 'فشل في تحديث تفاصيل أمر العمل',
      jobCardDetailsDiagnosisStartedSuccess: 'بدأ التشخيص! يمكنك الآن إضافة المهام.',
      jobCardDetailsDiagnosisStartFailed: 'فشل في بدء التشخيص',
      jobCardDetailsRepairStartedSuccess: 'بدأ الإصلاح!',
      jobCardDetailsRepairStartFailed: 'فشل في بدء الإصلاح',
      jobCardDetailsNotAssignedError: 'هذه البطاقة غير مسندة إليك — اطلب من المشرف إسنادها لك',
      jobCardDetailsJobPutOnHoldSuccess: 'تم تعليق أمر العمل',
      jobCardDetailsJobPutOnHoldFailed: 'فشل في تعليق أمر العمل',
      jobCardDetailsJobResumedSuccess: 'تم استئناف أمر العمل',
      jobCardDetailsJobResumeFailed: 'فشل في استئناف أمر العمل',
      jobCardDetailsDeliveryCodeResentSuccess: 'تم إنشاء رمز تسليم جديد وإرساله للعميل عبر الرسائل',
      jobCardDetailsDeliveryCodeResendFailed: 'فشل في إعادة إرسال رمز التسليم',
      jobCardDetailsInvoiceGeneratingLoading: 'جاري إنشاء الفاتورة وإرسال رابط الدفع...',
      jobCardDetailsInvoiceGeneratedSuccess: 'تم إنشاء الفاتورة وإرسال رابط الدفع للعميل!',
      jobCardDetailsInvoiceGenerateFailed: 'فشل في إنشاء الفاتورة',
      jobCardDetailsVehicleDeliveredSuccess: 'تم تسليم المركبة بنجاح!',
      jobCardDetailsNoQuotationFound: 'لا يوجد عرض سعر',
      jobCardDetailsQuotationSMSSentWithRemaining: 'تم إرسال رسالة عرض السعر! متبقي {{remaining}} إعادة إرسال',
      jobCardDetailsMaxResendsReachedCount: 'تم الوصول للحد الأقصى ({{used}}/{{max}})',
      jobCardDetailsResendsUsedCount: '{{used}}/{{max}} إعادة إرسال مستخدمة',
      jobCardDetailsResendSMSFailed: 'فشل في إعادة إرسال الرسالة',
      jobCardDetailsNoInvoiceFound: 'لا توجد فاتورة',
      jobCardDetailsConvertToRegularSuccess: 'تم تحويل أمر العمل إلى خدمة عادية بنجاح',
      jobCardDetailsConvertToRegularFailed: 'فشل في تحويل أمر العمل',
      jobCardDetailsCreateQuotationFailed: 'فشل في إنشاء عرض السعر',
      jobCardDetailsNoExistingQuotation: 'لا يوجد عرض سعر حالي',
      jobCardDetailsNoItemsForQuotation: 'لا توجد خدمات أو قطع لتضمينها في عرض السعر',
      jobCardDetailsPrepareQuotationFailed: 'فشل في تجهيز عرض السعر: {{error}}',
      jobCardDetailsQuotationCreatedSentApproval: 'تم إنشاء عرض السعر وإرساله لموافقة العميل!',
      jobCardDetailsInvoicePreviewPrepareFailed: 'فشل في تجهيز معاينة الفاتورة',
      jobCardDetailsNoNewItemsQuoteTooltip: 'لا توجد بنود جديدة للعرض. جميع البنود المعلقة موجودة بالفعل في عرض سعر.',
      jobCardDetailsPendingApprovalBadge: 'قيد الانتظار',
      jobCardDetailsServiceAddedTemp: 'تمت إضافة الخدمة مؤقتاً (ستُحفظ عند إنشاء عرض السعر المحدث)',
      jobCardDetailsPartAddedTemp: 'تمت إضافة القطعة مؤقتاً (ستُحفظ عند إنشاء عرض السعر المحدث)',
      jobCardDetailsConfirmRemove: 'إزالة',
      jobCardDetailsTaskBasedViewNew: 'عرض قائم على المهام (جديد)',
      jobCardDetailsLegacyView: 'العرض التقليدي',
      jobCardDetailsInteriorPhotoAlt: 'الداخلية {{number}}',
      jobCardDetailsExteriorCondition: 'حالة الخارجية',
      jobCardDetailsExteriorPhotoAlt: 'الخارجية {{number}}',
      jobCardDetailsExteriorVideos: 'فيديو خارجي',
      jobCardDetailsInteriorVideos: 'فيديو داخلي',
      jobCardDetailsMaxResendsReachedMessage: 'تم الوصول للحد الأقصى لإعادة الإرسال',
      jobCardDetailsInvoiceResendNoMoreInfo: 'لا توجد إعادة إرسال متاحة',
      jobCardDetailsInvoiceSMSSentWithRemaining: 'تم إرسال رسالة الفاتورة! متبقي {{remaining}} إعادة إرسال',
      waitingForPayment: 'بانتظار الدفع',
      paymentLinkSentToCustomer: 'تم إرسال رابط الدفع للعميل.',
      viewPaymentLink: 'عرض رابط الدفع',
      awaitingFinalPayment: 'بانتظار دفع الفاتورة النهائية',
      finalInvoicePaymentLinkSent: 'تم إرسال رابط دفع الفاتورة النهائية للعميل.',

      // Public Quotation
      publicQuotationItemTypePart: 'قطعة',
      publicQuotationItemTypeService: 'خدمة',
      publicQuotationFooterPoweredBy: 'مدعوم بواسطة موتورمايند',
      publicQuotationFooterCopyright: '© {{year}} موتورمايند. جميع الحقوق محفوظة.',
      publicQuotationContinueToPayment: 'المتابعة للدفع',
      publicQuotationReceiptPaymentIntro: 'سيتم تحويلك إلى صفحة آمنة لإكمال الدفعة المقدمة.',
      publicQuotationLoadingData: 'جاري تحميل بيانات عرض السعر...',
      publicQuotationInvalidOTP: 'رمز التحقق غير صحيح. يرجى المحاولة مرة أخرى.',
      publicQuotationOTPVerified: 'تم التحقق من الرمز بنجاح',
      publicQuotationApprovalSuccess: 'تم إرسال موافقتك بنجاح',
      publicQuotationApprovalFailed: 'فشل في إرسال الموافقة',
      publicQuotationToastInvalidOTP: 'رمز التحقق غير صحيح',
      publicQuotationToastOTPVerified: 'تم التحقق من الرمز',
      publicQuotationToastApprovalSubmitted: 'تم إرسال الموافقة',
      publicQuotationToastApprovalFailed: 'فشل في الإرسال',
      publicQuotationLoadFailed: 'فشل تحميل عرض السعر',
      publicQuotationOtpSentMobile: 'تم إرسال رمز التحقق إلى رقم جوالك',
      publicQuotationSendOtpFailed: 'فشل في إرسال رمز التحقق',
      publicQuotationOtpResentToast: 'تم إرسال رمز جديد',
      publicQuotationResendOtpFailed: 'فشل في إعادة إرسال رمز التحقق',
      publicQuotationSelectPaymentMethod: 'يرجى اختيار طريقة الدفع',
      publicQuotationPreparingPayment: 'جاري تجهيز الدفع...',
      publicQuotationPaymentUnavailable: 'الدفع غير متاح. يرجى الاتصال بالدعم.',
      publicQuotationRedirectingPayment: 'جاري التوجيه لصفحة الدفع...',
      publicQuotationCreatePaymentFailed: 'فشل في إنشاء الدفع',
      publicQuotationTamaraCheckoutFailed: 'فشل في إنشاء جلسة تمارا',
      publicQuotationEnterValidOtp: 'يرجى إدخال رمز تحقق صحيح مكون من 6 أرقام',
      publicQuotationConfirmedSuccess: 'تم التأكيد بنجاح!',
      publicQuotationApprovedSuccess: 'تم اعتماد عرض السعر بنجاح!',
      publicQuotationApproveFailed: 'فشل في اعتماد عرض السعر',
      
      // Common UI
      commonOk: 'موافق',
      commonDone: 'تم',
      commonReportIssue: 'الإبلاغ عن مشكلة',
      commonUploading: 'جاري الرفع...',
      commonSubmitting: 'جاري الإرسال...',
      commonPlaceholderZero: '0.00',
      commonQuantityPlaceholder: '1',
      commonSARSuffix: 'ريال',
      commonPhotos: 'الصور',
      commonImages: 'الصور',
      active: 'نشط',
      all: 'الكل',
      noMatchingJobs: 'لا توجد أعمال مطابقة',
      noJobsFound: 'لم يتم العثور على أعمال',
      tryDifferentSearchTerm: 'جرب مصطلح بحث مختلف',
      checkBackLaterForNewAssignments: 'تحقق لاحقاً من المهام الجديدة',
      plateNumber: 'رقم اللوحة',
      reportedIssue: 'طلب العميل',
      createdJobCard: 'تم إنشاء أمر العمل',
      changedStatusFrom: 'تم تغيير الحالة من',
      to: 'إلى',
      partAdded: 'تمت إضافة قطعة',
      km: 'كم',
      
      // Job Card Details Page
      readOnlyAccess: 'وصول للقراءة فقط',
      readOnlyMessage: 'يمكنك عرض أمر العمل وأنشطته، ولا يمكنك إجراء تغييرات. تواصل مع المشرف للمساعدة.',
      closeJobCard: 'إغلاق أمر العمل',
      editDetails: 'تعديل التفاصيل',
      printCodes: 'طباعة الرموز',
      printCode: 'طباعة الرمز',
      barcode: 'الباركود',
      qrCode: 'رمز الاستجابة السريعة',
      forCarKeySticker: 'لملصق مفتاح السيارة',
      forWindshieldPlacement: 'لوضعه على الزجاج الأمامي',
      printBarcode: 'طباعة الباركود',
      printQRCode: 'طباعة رمز الاستجابة السريعة',
      qrCodeInfo: 'اطبع وضعه على الزجاج الأمامي لسهولة المسح',
      barcodeInfo: 'اطبع كملصق صغير لتعليقه على مفتاح السيارة',
      saving: 'جاري الحفظ...',
      saveChanges: 'حفظ التغييرات',
      basicInformation: 'المعلومات الأساسية',
      vehicleSpecifications: 'مواصفات المركبة',
      damageInspectionDetails: 'فحص الأضرار والتفاصيل',
      nextSteps: 'الخطوات التالية',
      generateQuotation: 'إنشاء عرض سعر',
      totalCost: 'التكلفة الإجمالية',
      inProgress: 'قيد التنفيذ',
      completed: 'مكتمل',
      totalTasks: 'إجمالي المهام',
      vehicle: 'المركبة',
      customer: 'العميل',
      customerInformation: 'معلومات العميل',
      vehicleInformation: 'معلومات المركبة',
      generating: 'جاري الإنشاء...',
      passed: 'نجح',
      failed: 'فشل',
      notStarted: 'لم يبدأ',
      white: 'أبيض',
      black: 'أسود',
      ready: 'جاهز',
      payment: 'الدفع',
      qualityCheck: 'فحص الجودة',
      repair: 'الإصلاح',
      approval: 'الموافقة',
      quotation: 'عرض السعر',
      received: 'مستلم',
      jobDetails: 'تفاصيل العمل',
      status: 'الحالة',
      waitingForCustomerApproval: 'في انتظار موافقة العميل...',
      resendQuotationSMS: 'إعادة إرسال رسالة عرض السعر',
      resendInvoiceSMS: 'إعادة إرسال رسالة الفاتورة',
      sendingSMS: 'جاري الإرسال...',
      quotationSMSResent: 'تم إعادة إرسال رسالة عرض السعر بنجاح',
      invoiceSMSResent: 'تم إرسال رسالة الفاتورة بنجاح',
      maxResendsReached: 'تم الوصول للحد الأقصى (5/5 في اليوم)',
      waitBeforeResending: 'يرجى الانتظار 5 دقائق قبل الإرسال مرة أخرى',
      resendsRemaining: '{{count}} إعادة إرسال متبقية',
      waitMinutesBeforeResending: 'انتظر {{minutes}} دقيقة قبل الإرسال',
      startRepair: 'بدء الإصلاح',
      qualityCheckProgress: 'تقدم فحص الجودة',
      assignedUsers: 'المستخدمون المعينون',
      noUsersAssigned: 'لا يوجد مستخدمون معينون',
      manageAssignments: 'إدارة التعيينات',
      addNewTask: 'إضافة مهمة جديدة',
      addComplaint: 'إضافة شكوى',
      plate: 'اللوحة',
      vin: 'رقم الهيكل',
      tasks: 'المهام',
      noTasksAddedYet: 'لم تتم إضافة مهام بعد',
      noActivityRecorded: 'لم يتم تسجيل أي نشاط بعد',
      addYourFirstTask: 'أضف مهمتك الأولى',
      activityLog: 'سجل النشاط',
      workflowProgress: 'تقدم سير العمل',
      diagnosis: 'التشخيص',
      recentlyFixed: 'تم إصلاحه مؤخراً',
      delivered: 'تم التسليم',
      onHold: 'معلق',
      closed: 'مغلق',
      pendingPayment: 'في انتظار الدفع',
      test: 'اختبار',
      diagnosisNotes: 'ملاحظات التشخيص',
      na: 'غير متوفر',
      vehicleBodyType: 'نوع هيكل المركبة',
      sedan: 'سيدان',
      checkEngine: 'فحص المحرك',
      
      // Job Card Modal - Additional
      empty: 'فارغ',
      quarterTank: '1/4 خزان',
      halfTank: '1/2 خزان',
      threeQuarterTank: '3/4 خزان',
      fullTank: 'خزان ممتلئ',
      selectLevel: 'اختر المستوى',
      customerComplaint: 'طلب العميل',
      describeIssue: 'الوصف',
      assignMechanic: 'تعيين فني',
      assignLater: 'تعيين لاحقاً',
      normal: 'عادي',
      urgent: 'عاجل',
      interiorCondition: 'حالة الداخلية',
      documentInteriorCondition: 'توثيق حالة داخل المركبة عند الاستقبال',
      exteriorCondition: 'حالة الخارجية',
      documentExteriorCondition: 'توثيق حالة خارج المركبة عند الاستقبال',
      takeVideo: 'تسجيل فيديو',
      soon: 'قريباً',
      notifyCustomerVia: 'إشعار العميل عبر',
      whatsapp: 'واتساب',
      WhatsApp: 'واتساب',
      sms: 'رسالة نصية',
      notifyChannelWhatsappSms: 'واتساب ورسائل نصية',
      notifyChannelSmsOnly: 'رسائل نصية فقط',
      notifyChannelWhatsappSmsHint:
        'تُرسل الإشعارات عبر واتساب (وترجع للرسائل النصية عند عدم توفره). رموز التحقق والتسليم دائمًا عبر الرسائل النصية.',
      notifyChannelSmsOnlyHint: 'يتم إرسال كل شيء، بما في ذلك الإشعارات، عبر الرسائل النصية.',
      notifyContactNumber: 'سيتم الإشعار',
      waNotConnectedWarning:
        'واتساب غير متصل — سيتم إرسال جميع الإشعارات ورموز التحقق (OTP) عبر الرسائل النصية.',
      otpAlwaysViaSms: 'يتم دائمًا إرسال رموز التحقق (OTP) ورموز التسليم عبر الرسائل النصية.',
      notifChannelBadgeWhatsapp: 'الإشعارات: واتساب',
      notifChannelBadgeSms: 'الإشعارات: رسائل نصية',
      notifChannelChange: 'تغيير قناة الإشعارات',
      notifChannelUpdated: 'تم تحديث قناة الإشعارات',
      notifChannelUpdateFailed: 'تعذر تحديث قناة الإشعارات',
      'qt.sendsViaWhatsappSms': 'سيتم إرسال عرض السعر للعميل عبر واتساب والرسائل النصية.',
      'qt.sendsViaSmsOnly': 'سيتم إرسال عرض السعر للعميل عبر الرسائل النصية.',
      'qt.channelSetOnJobCard': 'تتبع قناة الإرسال إعدادات الإشعارات في بطاقة العمل.',
      notificationLanguage: 'لغة الإشعار',
      
      // Warning Lights
      warningLights: 'أضواء التحذير',
      warningLightsIfAny: 'أضواء التحذير (إن وجدت)',
      noWarningLightsSelected: 'لم يتم تحديد أضواء تحذير',
      
      // Damage Inspection
      damageInspection: 'فحص الأضرار',
      vehicleType: 'نوع المركبة',
      sedanStandardCar: 'سيدان (4 أبواب) - سيارة ركاب قياسية',
      top: 'أعلى',
      right: 'يمين',
      left: 'يسار',
      rear: 'خلفي',
      front: 'أمامي',
      clickDiagramToMarkDamage: 'انقر على الرسم لتحديد الضرر. انقر على علامة لإضافة صور/تعليقات',
      generalGlobalNotes: 'ملاحظات عامة/شاملة',
      anyGeneralNotes: 'أي ملاحظات عامة حول حالة المركبة...',
      point: 'نقطة',
      describeDamage: 'وصف الضرر (مثل خدش، انبعاج)',
      done: 'تم',
      
      // Vehicle Shapes
      suv: 'دفع رباعي',
      truck: 'شاحنة',
      van: 'فان',
      coupe: 'كوبيه',
      hatchback: 'هاتشباك',
      convertible: 'قابل للتحويل',
      wagon: 'واجن',
      pickup: 'بيك أب',

      // i18n sweep — job card experience (preview modal, history, mechanic pages, invoices)
      sar: 'ريال',
      sarPerUnit: 'ريال/الوحدة',
      service: 'خدمة',
      remaining: 'المتبقي',
      paidAmount: 'المبلغ المدفوع',
      accessRestricted: 'الوصول مقيد',
      notAssignedToYou: 'غير مسند إليك',
      notAssignedToJobCard: 'أنت غير مسند إلى أمر العمل هذا',
      jobCardInformation: 'معلومات أمر العمل',
      noteLabel: 'ملاحظة',
      previewSupervisorNote: 'يرجى التواصل مع المشرف إذا كنت بحاجة إلى الوصول لأمر العمل هذا.',
      backToJob: 'العودة إلى أمر العمل',
      adminComment: 'ملاحظة الإدارة',
      timeExceeded: 'تم تجاوز الوقت المحدد!',
      timeRemaining: 'الوقت المتبقي',
      instructionsFromAdmin: 'تعليمات من الإدارة',
      audioNotSupported: 'متصفحك لا يدعم تشغيل الصوت.',
      requestPart: 'طلب قطعة',
      noPartsAddedYet: 'لم تتم إضافة قطع بعد',
      noServicesAddedYet: 'لم تتم إضافة خدمات بعد',
      taskPhotos: 'صور المهمة',
      taskPhotoAlt: 'صورة المهمة {{index}}',
      taskNotFound: 'المهمة غير موجودة',
      serviceRequestSubmitted: 'تم إرسال طلب الخدمة - ستتم مراجعته من الإدارة',
      failedToAddService: 'فشل في إضافة الخدمة',
      taskAddedSuccessfully: 'تمت إضافة المهمة بنجاح',
      failedToAddTask: 'فشل في إضافة المهمة',
      hidePhoto: 'إخفاء الصورة',
      viewPhoto: 'عرض الصورة',
      resolve: 'حل',
      resolutionNotePlaceholder: 'ملاحظة الحل (اختياري)',
      markResolved: 'تأكيد الحل',
      images: 'الصور',
      downpayment: 'دفعة مقدمة',
      finalInvoice: 'الفاتورة النهائية',
      invoice: 'فاتورة',
      returnInvoice: 'فاتورة إرجاع',
      noInvoices: 'لا توجد فواتير لأمر العمل هذا',
      type: 'النوع',
      totalAmount: 'المبلغ الإجمالي',
      totalPaid: 'إجمالي المدفوع',
      totalPending: 'إجمالي المتبقي',
      lineItems: 'بنود الفاتورة',
      addFeeItem: 'إضافة رسم/بند',
      recordPayment: 'تسجيل دفعة',
      amountSAR: 'المبلغ (ريال)',
      remainingBalanceHint: 'الرصيد المتبقي: {{amount}} ريال',
      maxAmountPlaceholder: 'الحد الأقصى: {{amount}}',
      createReturn: 'إنشاء إرجاع',
      createReturnInvoice: 'إنشاء فاتورة إرجاع',
      returnInvoiceWarning: 'سيؤدي هذا إلى إنشاء فاتورة إرجاع بمبالغ سالبة لجميع بنود هذه الفاتورة.',
      returnReason: 'سبب الإرجاع',
      returnReasonPlaceholder: 'أدخل سبب هذا الإرجاع...',
      returnForOriginalInvoice: 'إرجاع للفاتورة الأصلية (انقر لعرض الفاتورة الأصلية)',
      failedToLoadInvoice: 'فشل في تحميل الفاتورة',
      fillDescriptionAndPrice: 'يرجى تعبئة الوصف والسعر',
      itemAdded: 'تمت إضافة البند',
      failedToAddItem: 'فشل في إضافة البند',
      enterValidPaymentAmount: 'يرجى إدخال مبلغ دفع صحيح',
      amountExceedsRemaining: 'لا يمكن أن يتجاوز المبلغ الرصيد المتبقي ({{amount}} ريال)',
      failedToRecordPayment: 'فشل في تسجيل الدفعة',
      confirmDeleteInvoice: 'هل أنت متأكد من حذف هذه الفاتورة؟ لا يمكن التراجع عن هذا الإجراء.',
      provideReturnReason: 'يرجى إدخال سبب الإرجاع',
      returnInvoiceCreated: 'تم إنشاء فاتورة الإرجاع بنجاح',
      failedToCreateReturnInvoice: 'فشل في إنشاء فاتورة الإرجاع',
      quotationsAndInvoices: 'عروض الأسعار والفواتير',
      failedToLoadQuotationsInvoices: 'فشل في تحميل عروض الأسعار والفواتير',
      // Appointments
      appointments: 'المواعيد',
      'appt.status.pending': 'معلق',
      'appt.status.confirmed': 'مؤكد',
      'appt.status.checked_in': 'وصل',
      'appt.status.converted': 'تحول لبطاقة عمل',
      'appt.status.cancelled': 'ملغى',
      'appt.status.no_show': 'لم يحضر',
      'appt.view.day': 'يوم',
      'appt.view.3day': '٣ أيام',
      'appt.view.week': 'أسبوع',
      'appt.view.list': 'قائمة',
      'appt.new': 'موعد جديد',
      'appt.title': 'المواعيد',
      'appt.loading': 'جاري تحميل المواعيد...',
      'appt.empty': 'لا توجد مواعيد',
      'appt.emptyDesc': 'أنشئ موعداً جديداً للبدء.',
      'appt.actions.confirm': 'تأكيد',
      'appt.actions.checkIn': 'تسجيل وصول',
      'appt.actions.cancel': 'إلغاء',
      'appt.actions.noShow': 'لم يحضر',
      'appt.actions.convert': 'تحويل لبطاقة عمل',
      'appt.actions.converting': 'جاري التحويل...',
      'appt.booking.otpPromise': 'سنرسل رمز تحقق لمرة واحدة إلى جوالك للتأكيد — لا حاجة لإنشاء حساب.',
      'apa.openJobCard': 'فتح البطاقة',
      'board.new': 'جديدة',
      'board.diagnosis': 'التشخيص',
      'board.inService': 'قيد الصيانة',
      'board.qc': 'فحص الجودة',
      'board.payment': 'الدفع',
      'board.ready': 'جاهزة',
      'board.done': 'منتهية',
      'board.showingOfTotal': 'عرض {{shown}} من {{total}}',
      'board.loadMore': 'تحميل المزيد',
      'board.countFromLoaded': 'محسوب من {{count}} بطاقة تم تحميلها حتى الآن — يوجد المزيد',
      'paymentMethods.myfatoorah': 'ماي فاتورة',
      'paymentMethods.tamara': 'تمارا',
      'paymentMethods.online': 'دفع إلكتروني',
      'iv.customerLinkTitle': 'رابط العميل',
      'iv.collectRemaining': 'تحصيل المتبقي',
      'iv.postSuccess': 'تم ترحيل الفاتورة وإصدارها',
      'iv.postFailed': 'فشل ترحيل الفاتورة',
      'wa.ai.summaryTitle': 'ملخص تزامن AI',
      'wa.ai.summarize': 'تلخيص المحادثة',
      'wa.ai.openFull': 'فتح التحليل الكامل',
      'wa.openProfile': 'فتح الملف',
      'wa.templates': 'ردود سريعة',
      'wa.activeJobCard': 'أمر عمل نشط',
      'ipa.post': 'ترحيل وإصدار',
      'appt.detail.title': 'تفاصيل الموعد',
      'appt.detail.customer': 'العميل',
      'appt.detail.linkedProfile': 'ملف مرتبط',
      'appt.detail.phone': 'الهاتف',
      'appt.detail.service': 'الخدمة',
      'appt.detail.dateTime': 'التاريخ والوقت',
      'appt.detail.vehicle': 'السيارة',
      'appt.detail.notes': 'ملاحظات',
      'appt.detail.source': 'المصدر',
      'appt.detail.status': 'الحالة',
      'appt.detail.number': 'رقم الموعد',
      'appt.confirm.title': 'تأكيد الموعد',
      'appt.confirm.msg': 'هل تريد تأكيد هذا الموعد وإخطار العميل؟',
      'appt.cancel.title': 'إلغاء الموعد',
      'appt.cancel.msg': 'هل أنت متأكد من إلغاء هذا الموعد؟',
      'appt.cancel.reason': 'السبب (اختياري)',
      'appt.noshow.title': 'تحديد كـ لم يحضر',
      'appt.noshow.msg': 'هل تريد تحديد هذا الموعد كـ لم يحضر؟',
      'appt.convert.title': 'تحويل لبطاقة عمل',
      'appt.convert.msg': 'تحويل هذا الموعد إلى بطاقة عمل؟ سيتم إنشاء العميل والسيارة إذا لزم الأمر.',
      'appt.converted.toast': 'تم التحويل — جاري فتح بطاقة العمل',
      'appt.confirmed.toast': 'تم تأكيد الموعد',
      'appt.checkedIn.toast': 'تم تسجيل وصول العميل',
      'appt.cancelled.toast': 'تم إلغاء الموعد',
      'appt.noshow.toast': 'تم التحديد كـ لم يحضر',
      'appt.created.toast': 'تم إنشاء الموعد',
      'appt.error.load': 'فشل تحميل المواعيد',
      'appt.error.action': 'فشلت العملية',
      'appt.modal.title': 'موعد جديد',
      'appt.modal.customerName': 'اسم العميل',
      'appt.modal.phone': 'الهاتف',
      'appt.modal.serviceType': 'نوع الخدمة',
      'appt.modal.selectService': 'اختر خدمة...',
      'appt.modal.date': 'التاريخ',
      'appt.modal.slot': 'الوقت',
      'appt.modal.selectSlot': 'اختر وقتاً...',
      'appt.modal.notes': 'ملاحظات',
      'appt.modal.linkCustomer': 'ربط بعميل حالي',
      'appt.modal.unlinkCustomer': 'إلغاء الربط',
      'appt.modal.selectVehicle': 'سيارة العميل',
      'appt.modal.manualVehicle': 'إدخال السيارة يدوياً',
      'appt.modal.vehicle': 'السيارة (اختياري)',
      'appt.modal.make': 'الماركة',
      'appt.modal.model': 'الموديل',
      'appt.modal.year': 'السنة',
      'appt.modal.plate': 'اللوحة',
      'appt.modal.create': 'إنشاء الموعد',
      'appt.modal.creating': 'جاري الإنشاء...',
      'appt.modal.loadingSlots': 'جاري تحميل الأوقات...',
      'appt.modal.noSlots': 'لا توجد أوقات متاحة لهذا اليوم',
      'appt.col.number': 'رقم الموعد',
      'appt.col.customer': 'العميل',
      'appt.col.phone': 'الهاتف',
      'appt.col.service': 'الخدمة',
      'appt.col.dateTime': 'التاريخ والوقت',
      'appt.col.status': 'الحالة',
      'appt.col.actions': 'إجراءات',
      'appt.filter.allStatuses': 'جميع الحالات',
      'appt.calendar.closedDay': 'مغلق',
      'appt.calendar.today': 'اليوم',
      'appt.calendar.prev': 'السابق',
      'appt.calendar.next': 'التالي',
      'appt.settings.title': 'إعدادات المواعيد',
      'appt.settings.slotSettings': 'إعدادات الفترات',
      'appt.settings.timezoneNote': 'توقيت الرياض',
      'appt.settings.shiftStart': 'بداية الوردية',
      'appt.settings.shiftEnd': 'نهاية الوردية',
      'appt.settings.enabled': 'تفعيل الحجز الإلكتروني',
      'appt.settings.enabledDesc': 'السماح للعملاء بحجز المواعيد عبر الإنترنت',
      'appt.settings.publicUrl': 'رابط الحجز العام',
      'appt.settings.publicUrlDesc': 'شارك هذا الرابط مع عملائك ليتمكنوا من الحجز عبر الإنترنت.',
      'appt.settings.copy': 'نسخ',
      'appt.settings.copied': 'تم النسخ',
      'appt.settings.urlCopied': 'تم نسخ رابط الحجز',
      'appt.settings.openLink': 'فتح',
      'appt.settings.autoConfirm': 'تأكيد تلقائي',
      'appt.settings.autoConfirmDesc': 'تأكيد المواعيد الجديدة تلقائياً دون مراجعة يدوية',
      'appt.settings.slotMinutes': 'مدة الفترة (دقائق)',
      'appt.settings.capacity': 'الحجوزات لكل فترة',
      'appt.settings.leadTime': 'وقت الإشعار المسبق (دقائق)',
      'appt.settings.horizon': 'أفق الحجز (أيام)',
      'appt.settings.reminderLead': 'وقت التذكير المسبق (دقائق)',
      'appt.settings.workingHours': 'ساعات العمل',
      'appt.settings.daysOff': 'أيام العطل',
      'appt.settings.addDayOff': 'إضافة يوم عطلة',
      'appt.settings.removeDayOff': 'حذف يوم العطلة',
      'appt.settings.closed': 'مغلق',
      'appt.settings.addShift': 'إضافة فترة',
      'appt.settings.removeShift': 'حذف الفترة',
      'appt.settings.save': 'حفظ الإعدادات',
      'appt.settings.saving': 'جاري الحفظ...',
      'appt.settings.saved': 'تم حفظ إعدادات المواعيد',
      'appt.settings.saveFailed': 'فشل حفظ الإعدادات',
      'appt.settings.loadFailed': 'فشل تحميل الإعدادات',
      'appt.settings.sun': 'أحد',
      'appt.settings.mon': 'اثنين',
      'appt.settings.tue': 'ثلاثاء',
      'appt.settings.wed': 'أربعاء',
      'appt.settings.thu': 'خميس',
      'appt.settings.fri': 'جمعة',
      'appt.settings.sat': 'سبت',
      'appt.booking.title': 'احجز موعداً',
      'appt.booking.selectService': 'اختر الخدمة',
      'appt.booking.selectServiceDesc': 'اختر نوع الخدمة التي تحتاجها',
      'appt.booking.selectDate': 'اختر التاريخ والوقت',
      'appt.booking.selectDateDesc': 'اختر وقتاً متاحاً',
      'appt.booking.details': 'بياناتك',
      'appt.booking.detailsDesc': 'أخبرنا عن سيارتك',
      'appt.booking.verify': 'تحقق من الهاتف',
      'appt.booking.verifyDesc': 'أكد رقم هاتفك',
      'appt.booking.done': 'تم التأكيد!',
      'appt.booking.name': 'الاسم الكامل',
      'appt.booking.phone': 'رقم الهاتف',
      'appt.booking.phoneHint': 'رقم سعودي (مثال: 05XXXXXXXX)',
      'appt.booking.vehicleMake': 'ماركة السيارة',
      'appt.booking.vehicleModel': 'موديل السيارة',
      'appt.booking.vehicleYear': 'سنة الصنع',
      'appt.booking.vehiclePlate': 'رقم اللوحة',
      'appt.booking.notes': 'الخدمة المطلوبة',
      'appt.booking.selectMake': 'اختر الشركة المصنعة',
      'appt.booking.selectModel': 'اختر الموديل',
      'appt.booking.selectYear': 'اختر سنة الصنع',
      'appt.booking.selectMakeFirst': 'اختر الشركة المصنعة أولًا',
      'appt.booking.other': 'أخرى',
      'appt.booking.enterMake': 'أدخل الشركة المصنعة',
      'appt.booking.enterModel': 'أدخل موديل المركبة',
      'appt.booking.language': 'اللغة',
      'appt.booking.next': 'التالي',
      'appt.booking.back': 'السابق',
      'appt.booking.sendOtp': 'إرسال رمز التحقق',
      'appt.booking.verifyOtp': 'تحقق',
      'appt.booking.resendOtp': 'إعادة إرسال الرمز',
      'appt.booking.otpSent': 'تم إرسال الرمز إلى هاتفك',
      'appt.booking.otpLabel': 'رمز التحقق',
      'appt.booking.otpHint': 'أدخل الرمز المكون من 6 أرقام',
      'appt.booking.submit': 'تأكيد الحجز',
      'appt.booking.submitting': 'جاري الحجز...',
      'appt.booking.confirmationTitle': 'تم تأكيد الموعد!',
      'appt.booking.confirmationDesc': 'تم حجز موعدك. سيصلك رسالة تأكيد على هاتفك.',
      'appt.booking.appointmentNumber': 'رقم الموعد',
      'appt.booking.dateTime': 'التاريخ والوقت',
      'appt.booking.slotFull': 'هذا الوقت أصبح محجوزاً — يرجى اختيار وقت آخر.',
      'appt.booking.notAvailable': 'الحجز الإلكتروني غير متاح لهذا المركز.',
      'appt.booking.loadFailed': 'فشل تحميل صفحة الحجز.',
      'appt.booking.noSlots': 'لا توجد أوقات متاحة لهذا اليوم.',
      'appt.booking.sendingOtp': 'جاري الإرسال...',
      'appt.booking.otpExpired': 'انتهت صلاحية الرمز. يرجى طلب رمز جديد.',
      'appt.booking.otpInvalid': 'رمز غير صحيح.',
      'appt.booking.nameRequired': 'الاسم مطلوب',
      'appt.booking.phoneRequired': 'الهاتف مطلوب',
      'appt.booking.serviceRequired': 'يرجى اختيار خدمة',
      'appt.booking.slotRequired': 'يرجى اختيار وقت',
      'appt.booking.bookAgain': 'حجز موعد آخر',
      'appt.source.public': 'إلكتروني',
      'appt.source.staff': 'موظف',
      'appt.source.walk_in': 'حضور مباشر',

      // ── T&C settings keys (tnc.*) — Arabic
      'tnc.title': 'الشروط والأحكام',
      'tnc.subtitle': 'إنشاء ونشر شروط وأحكام ثنائية اللغة لعملائك',
      'tnc.legalInfo': 'المعلومات القانونية',
      'tnc.legalNameEn': 'الاسم القانوني (إنجليزي)',
      'tnc.legalNameAr': 'الاسم القانوني (عربي)',
      'tnc.crNumber': 'رقم السجل التجاري',
      'tnc.vatNumber': 'رقم ضريبة القيمة المضافة',
      'tnc.address': 'العنوان',
      'tnc.phone': 'الهاتف',
      'tnc.email': 'البريد الإلكتروني',
      'tnc.specialArticles': 'بنود خاصة / شروط إضافية',
      'tnc.specialArticlesPlaceholder': 'أدرج أي شروط خاصة، مثل: "لا تتحمل الشركة مسؤولية الأغراض الشخصية داخل السيارة"',
      'tnc.generateWithAI': 'توليد بالذكاء الاصطناعي',
      'tnc.generating': 'جاري التوليد (قد يستغرق بضع دقائق)...',
      'tnc.generatingNote': 'يقوم الذكاء الاصطناعي بكتابة الشروط والأحكام — قد يستغرق ذلك بضع دقائق.',
      'tnc.generated': 'تم توليد الشروط والأحكام بنجاح',
      'tnc.generateFailed': 'فشل التوليد. يرجى المحاولة مرة أخرى.',
      'tnc.writeOwn': 'كتابة الشروط يدوياً',
      'tnc.enhanceWithAI': 'تحسين بالذكاء الاصطناعي',
      'tnc.enhancing': 'جاري التحسين...',
      'tnc.enhanced': 'تم تحسين الشروط والأحكام بنجاح',
      'tnc.enhanceFailed': 'فشل التحسين. يرجى المحاولة مرة أخرى.',
      'tnc.enhanceHint': 'خاصية التحسين تحافظ على بنودك الحالية وتوسّع المستند؛ وأي محتوى في حقل "البنود الخاصة" أعلاه يُضاف كبنود جديدة.',
      'tnc.suggestionsTitle': 'إضافات موصى بها',
      'tnc.suggestionsHint': 'لا يغطي مستندك هذه البنود بعد. اختر ما تريد إضافته ثم أعد الإنشاء.',
      'tnc.regenerateWithSelected': 'إعادة الإنشاء مع المحدد ({{count}})',
      'tnc.edit': 'تحرير',
      'tnc.preview': 'معاينة',
      'tnc.nothingToPreview': 'لا يوجد محتوى للمعاينة بعد.',
      'tnc.editor': 'تحرير المحتوى',
      'tnc.saveDraft': 'حفظ كمسودة',
      'tnc.draftSaved': 'تم حفظ المسودة',
      'tnc.saveDraftFailed': 'فشل حفظ المسودة',
      'tnc.publish': 'نشر',
      'tnc.publishVersion': 'نشر v{{version}}',
      'tnc.publishRules': 'النشر يتطلب المحتوى باللغتين · ما يوافق عليه العملاء لا يُتراجع عنه',
      'set.featuresTitle': 'مزايا المنشأة',
      'set.featuresHint': 'تُفعّل على مستوى الشركة — العناصر المرتبطة تظهر أو تختفي من القوائم مباشرة (إخفاء لا تعطيل)',
      'tnc.published': 'تم نشر الشروط والأحكام بنجاح',
      'tnc.publishFailed': 'فشل النشر',
      'tnc.publishConfirmTitle': 'تأكيد النشر',
      'tnc.publishConfirmDesc': 'الإصدار {{version}} سيكون ما يوافق عليه العملاء. لا يمكن التراجع عن هذا الإجراء.',
      'tnc.confirmPublish': 'نعم، نشر',
      'tnc.publishedVersion': 'الإصدار المنشور',
      'tnc.unpublishedDraft': 'توجد مسودة غير منشورة تحتوي على تغييرات.',
      'tnc.loadFailed': 'فشل تحميل إعدادات الشروط والأحكام',

      // ── Intake confirmation keys (intake.*) — Arabic
      // Tenant Users
      tenantUsers: 'مستخدمو المستأجرين',
      manageTenantUsers: 'عرض وإدارة المستخدمين عبر جميع الشركات',
      allCompanies: 'جميع الشركات',
      allRoles: 'جميع الأدوار',
      resetPassword: 'إعادة تعيين كلمة المرور',
      newPassword: 'كلمة المرور الجديدة',
      selectCompany: 'اختر الشركة',
      assignedGarages: 'مراكز خدمات السيارات المعيّنة',
      passwordResetSuccess: 'تمت إعادة تعيين كلمة المرور بنجاح',
      userUpdated: 'تم تحديث المستخدم',
      userCreated: 'تم إنشاء المستخدم',
      userDeleted: 'تم حذف المستخدم',
      deleteUserConfirm: 'هل تريد حذف هذا المستخدم؟ لا يمكن التراجع عن هذا الإجراء.',
      leaveBlankToKeep: 'اترك الحقل فارغاً للإبقاء على كلمة المرور الحالية',

      'intake.settingsTitle': 'تأكيد الاستلام من العميل',
      'intake.settingsSubtitle': 'اشترط تأكيد العميل لأمر العمل قبل البدء في العمل',
      'intake.enableToggle': 'اشتراط تأكيد الاستلام',
      'intake.enableToggleDesc': 'لا يمكن أن ينتقل أمر العمل من حالة "مستلم" حتى يؤكد العميل عبر الرسالة النصية.',
      'intake.noPublishedTermsHint': 'يجب نشر الشروط والأحكام أولاً قبل تفعيل هذه الميزة.',
      'intake.configSaved': 'تم حفظ الإعداد',
      'intake.configFailed': 'فشل حفظ الإعداد',
      'intake.title': 'تأكيد الاستلام من العميل',
      'intake.send': 'إرسال للتأكيد من العميل',
      'intake.resend': 'إعادة إرسال رابط التأكيد',
      'intake.sendSuccess': 'تم إرسال رابط التأكيد',
      'intake.sendFailed': 'فشل إرسال التأكيد',
      'intake.override': 'تجاوز',
      'intake.overrideTitle': 'تجاوز تأكيد الاستلام',
      'intake.overrideDesc': 'أدخل سبباً لتجاوز اشتراط التأكيد. سيتم تسجيل هذا الإجراء.',
      'intake.overrideReasonField': 'السبب *',
      'intake.overrideReasonPlaceholder': 'مثال: أكد العميل شفهياً، الأضرار موجودة مسبقاً...',
      'intake.overrideReasonRequired': 'السبب مطلوب',
      'intake.overrideSuccess': 'تم تجاوز الحالة بنجاح',
      'intake.overrideFailed': 'فشل التجاوز',
      'intake.confirmOverride': 'تأكيد التجاوز',
      'intake.overrideReasonLabel': 'سبب التجاوز:',
      'intake.overriddenBy': 'بواسطة',
      'intake.disputeNoteLabel': 'المشكلة التي أبلغ عنها العميل:',
      'intake.pendingHint': 'بانتظار تأكيد العميل عبر رابط الرسالة النصية.',
      'intake.confirmationRequired': 'يجب الحصول على تأكيد العميل قبل تحريك أمر العمل.',
      'intake.disputeHelperText': 'أبلغ العميل عن مشكلة في أمر العمل. قم بتصحيح تفاصيل أمر العمل ثم أعد إرسال رابط التأكيد — أو تجاوز الاشتراط مع توثيق السبب.',
      'intake.fixAndResend': 'تصحيح وإعادة الإرسال',
      'intake.correctResendSuccess': 'تم تصحيح أمر العمل وإعادة إرسال التأكيد',
      'intake.correctResendFailed': 'تعذّر التصحيح وإعادة الإرسال',
      'intake.status.pending': 'بانتظار التأكيد',
      'intake.status.confirmed': 'أكد العميل',
      'intake.status.disputed': 'موضع خلاف',
      'intake.status.overridden': 'تم التجاوز',
      'jc.editReception': 'تصحيح استلام المركبة',
      'jc.saveAndResend': 'حفظ وإعادة الإرسال',
      'jc.homeBranchSuffix': '(الرئيسي)',
      'onb.view': 'عرض',
      // F5: ملاحظات صوتية (على مستوى أمر العمل)
      'jc.voiceNotes.title': 'ملاحظات صوتية',
      'jc.voiceNotes.record': 'سجّل ملاحظة',
      'jc.voiceNotes.stop': 'إيقاف',
      'jc.voiceNotes.recording': 'جارٍ التسجيل…',
      'jc.voiceNotes.save': 'حفظ',
      'jc.voiceNotes.discard': 'تجاهل',
      'jc.voiceNotes.empty': 'لا توجد ملاحظات صوتية بعد',
      'jc.voiceNotes.delete': 'حذف الملاحظة',
      'jc.voiceNotes.confirmDeleteTitle': 'حذف الملاحظة الصوتية؟',
      'jc.voiceNotes.confirmDeleteMessage': 'سيتم حذف الملاحظة الصوتية نهائياً.',
      'jc.voiceNotes.micDenied': 'تعذّر الوصول إلى الميكروفون',
      'jc.voiceNotes.notSupported': 'تسجيل الصوت غير مدعوم على هذا الجهاز',
      'jc.voiceNotes.saved': 'تم حفظ الملاحظة الصوتية',
      'jc.voiceNotes.saveFailed': 'تعذّر حفظ الملاحظة الصوتية',
      'jc.voiceNotes.deleted': 'تم حذف الملاحظة الصوتية',
      'jc.voiceNotes.deleteFailed': 'تعذّر حذف الملاحظة الصوتية',
      // MyFatoorah payments
      // — Garage settings: online payments
      'mfp.onlinePayments': 'الدفع الإلكتروني',
      'mfp.onlinePaymentsDesc': 'استقبل مدفوعات عملائك بالبطاقات والدفع الإلكتروني عبر ماي فاتورة.',
      'mfp.paymentMode': 'طريقة الدفع',
      'mfp.useOwnAccount': 'استخدام حسابي الخاص',
      'mfp.useOwnAccountDesc': 'اربط حساب التاجر الخاص بك في ماي فاتورة، وتُحوَّل المبالغ إليك مباشرة.',
      'mfp.useMotorMindManaged': 'إدارة عبر موتورمايند',
      'mfp.useMotorMindManagedDesc': 'تتولى موتورمايند إعداد حساب الدفع وإدارته نيابةً عنك، مع احتساب عمولة على كل عملية.',
      'mfp.apiKey': 'مفتاح الواجهة البرمجية (API)',
      'mfp.webhookSecret': 'كلمة سر الويب هوك',
      'mfp.testMode': 'وضع الاختبار',
      'mfp.saveCredentials': 'حفظ بيانات الاعتماد',
      'mfp.credentialsConfigured': 'تم ضبط بيانات الاعتماد',
      'mfp.credentialsNotConfigured': 'لم تُضبط بيانات الاعتماد',
      // — Garage settings: supplier onboarding (KYC)
      'mfp.supplierOnboarding': 'تسجيل المورّد',
      'mfp.legalName': 'الاسم القانوني',
      'mfp.mobile': 'رقم الجوال',
      'mfp.email': 'البريد الإلكتروني',
      'mfp.iban': 'رقم الآيبان (IBAN)',
      'mfp.crNumber': 'رقم السجل التجاري',
      'mfp.startOnboarding': 'بدء التسجيل',
      'mfp.updateProfile': 'تحديث البيانات',
      'mfp.uploadDocuments': 'رفع المستندات',
      'mfp.documentType': 'نوع المستند',
      'mfp.docCr': 'السجل التجاري',
      'mfp.docIbanLetter': 'خطاب الآيبان',
      'mfp.docNationalId': 'الهوية الوطنية',
      'mfp.docOther': 'أخرى',
      'mfp.uploadKyc': 'رفع مستندات التحقق (KYC)',
      'mfp.documentsUploaded': 'تم رفع المستندات بنجاح',
      'mfp.kycStatus': 'حالة التحقق',
      'mfp.statusNotStarted': 'لم يبدأ',
      'mfp.statusSubmitted': 'تم الإرسال',
      'mfp.statusUnderReview': 'قيد المراجعة',
      'mfp.statusApproved': 'معتمد',
      'mfp.statusRejected': 'مرفوض',
      'mfp.statusSuspended': 'موقوف',
      'mfp.rejectionReason': 'سبب الرفض',
      'mfp.enableOnlinePay': 'تفعيل الدفع الإلكتروني',
      'mfp.disableOnlinePay': 'إيقاف الدفع الإلكتروني',
      'mfp.onlinePayEnabled': 'تم تفعيل الدفع الإلكتروني',
      'mfp.onlinePayDisabled': 'تم إيقاف الدفع الإلكتروني',
      'mfp.commission': 'العمولة',
      'mfp.commissionRate': 'نسبة العمولة',
      'mfp.commissionReadOnly': 'العمولة تُحدَّد من قبل موتورمايند ولا يمكن تعديلها من هنا.',
      'mfp.syncStatus': 'حالة المزامنة',
      // — Super-admin: payment suppliers
      'mfp.paymentSuppliers': 'موردو الدفع',
      'mfp.paymentSuppliersDesc': 'إدارة حسابات الدفع الإلكتروني والعمولات لجميع المنشآت.',
      'mfp.searchCompanies': 'ابحث عن المنشآت...',
      'mfp.filterByMode': 'التصفية حسب الطريقة',
      'mfp.filterByStatus': 'التصفية حسب الحالة',
      'mfp.allModes': 'جميع الطرق',
      'mfp.allStatuses': 'جميع الحالات',
      'mfp.setCommission': 'تحديد العمولة',
      'mfp.commissionType': 'نوع العمولة',
      'mfp.commissionPercentage': 'النسبة المئوية (%)',
      'mfp.commissionFixed': 'مبلغ ثابت (ريال)',
      'mfp.commissionNone': 'بدون',
      'mfp.commissionTypePercentage': 'نسبة مئوية',
      'mfp.commissionTypeFixed': 'مبلغ ثابت',
      'mfp.commissionTypeBoth': 'نسبة مئوية + مبلغ ثابت',
      'mfp.approveSupplier': 'اعتماد المورّد',
      'mfp.suspendSupplier': 'إيقاف المورّد',
      'mfp.suspendSupplierConfirm': 'سيؤدي هذا إلى إيقاف المورّد وتعطيل الدفع الإلكتروني لهذه الشركة.',
      'mfp.commissionSaved': 'تم حفظ العمولة',
      'mfp.overrideMode': 'تجاوز الطريقة',
      'mfp.viewDocuments': 'عرض المستندات',
      'mfp.downloadDocument': 'تنزيل المستند',
      'mfp.onboardOnBehalf': 'التسجيل نيابةً عنه',
      // — Refunds
      'mfp.refund': 'استرداد',
      'mfp.refunds': 'المبالغ المستردة',
      'mfp.refundPayment': 'استرداد الدفعة',
      'mfp.refundAmount': 'مبلغ الاسترداد',
      'mfp.refundReason': 'سبب الاسترداد',
      'mfp.fullRefund': 'استرداد كامل',
      'mfp.partialRefund': 'استرداد جزئي',
      'mfp.issueRefund': 'تنفيذ الاسترداد',
      'mfp.refundIssued': 'تم تنفيذ الاسترداد بنجاح',
      'mfp.refundFailed': 'فشل الاسترداد',
      'mfp.refundHistory': 'سجل المبالغ المستردة',
      'mfp.refundedAmount': 'المبلغ المسترد',
      'mfp.refundPending': 'قيد المعالجة',
      'mfp.refundCompleted': 'مكتمل',

      // ── P5: ملفات السيارة / العميل + السجل الكامل ───────────────────────
      'vp.title': 'ملف السيارة',
      'vp.notFound': 'لم يتم العثور على السيارة',
      'vp.loadFailed': 'تعذر تحميل السجل',
      'vp.backToVehicles': 'العودة إلى السيارات',
      'vp.back': 'رجوع',
      'vp.history': 'السجل',
      'vp.registrationExpiry': 'انتهاء الاستمارة',
      'vp.insuranceExpiry': 'انتهاء التأمين',
      'vp.totalVisits': 'إجمالي الزيارات',
      'vp.totalSpend': 'إجمالي الإنفاق',
      'vp.lastService': 'آخر صيانة',
      'vp.mileage': 'العداد',
      'vp.recurringIssues': 'تم اكتشاف أعطال متكررة',
      'vp.serviceHistory': 'سجل الخدمة',
      'vp.noHistory': 'لا يوجد سجل خدمة بعد',
      'vp.findingsCount': '{{count}} ملاحظات',
      'vp.partsCount': '{{count}} قطع',
      'vp.previous': 'السابق',
      'vp.next': 'التالي',
      'vp.pageOf': 'صفحة {{page}} من {{pages}}',
      'vp.aiInsight': 'رؤية الذكاء الاصطناعي للسجل',
      'vp.generate': 'إنشاء',
      'vp.regenerate': 'إعادة الإنشاء',
      'vp.insightEmpty': 'لا توجد رؤية بعد. أنشئ واحدة لتلخيص سجل خدمة هذه السيارة وإبراز الأنماط.',
      'vp.insightGenerated': 'تم إنشاء الرؤية',
      'vp.insightFailed': 'تعذر إنشاء الرؤية',
      'vp.insightNoHistory': 'لا يوجد سجل خدمة لتلخيصه بعد',
      'vp.insightStale': 'توجد بطاقات عمل جديدة منذ إنشاء هذه الرؤية — أعد الإنشاء للحصول على رؤية محدّثة.',
      'vp.insightGeneratedAt': 'أُنشئت',
      'cp.title': 'ملف العميل',
      'cp.notFound': 'لم يتم العثور على العميل',
      'cp.backToCustomers': 'العودة إلى العملاء',
      'cp.vehicles': 'السيارات',
      'cp.noVehicles': 'لا توجد سيارات',
      'cp.recentJobCards': 'أحدث بطاقات العمل',
      'cp.visitsCount': '{{count}} زيارات',
      'cm.customers.name': 'الاسم',
      'cm.customers.location': 'الموقع',
      'cm.customers.profile': 'الملف',
      'cm.customers.spend': 'إجمالي الإنفاق',
      'cm.customers.balance': 'الرصيد',
      'cm.customers.owes': 'عليه',
      'cm.customers.hasCredit': 'له رصيد',
      'veh.alerts': 'تنبيهات',
      'veh.alert.daysUnit': 'يوم',
      'veh.alert.registeredAbroad': 'مسجلة خارج المملكة',
      'credit.recipientsNote': 'تُدار قائمة مستلمي التذكيرات من إعدادات الإشعارات.',
      // Staff (shop-floor) view — five-state status system (StatusPill).
      'staff.status.waiting': 'في الانتظار',
      'staff.status.in_progress': 'قيد التنفيذ',
      'staff.status.done': 'مكتمل',
      'staff.status.problem': 'مشكلة',
      'staff.status.needs_approval': 'بحاجة لموافقة',
      // Staff (shop-floor) view — media capture (MediaCapture / MediaGallery).
      'staff.capture.photo': 'صورة',
      'staff.capture.video': 'فيديو',
      'staff.capture.voice': 'صوت',
      'staff.capture.recording': 'جارٍ التسجيل…',
      'staff.capture.stop': 'إيقاف',
      'staff.capture.remove': 'إزالة',
      'staff.capture.tooBig': 'الملف كبير جدًا',
      'staff.capture.notSupported': 'غير مدعوم',
      'staff.capture.uploading': 'جارٍ الرفع…',
      'staff.capture.uploadFailed': 'فشل الرفع',
      'staff.capture.retry': 'إعادة المحاولة',
      'staff.capture.empty': 'لا توجد وسائط بعد',
      'staff.capture.voiceNote': 'ملاحظة صوتية',
      // Report Problem (redesigned ReportIssueModal).
      'staff.report.title': 'الإبلاغ عن مشكلة',
      'staff.report.pickType': 'ما المشكلة؟',
      'staff.report.noteOptional': 'أضف ملاحظة (اختياري)',
      'staff.report.notePlaceholder': 'صف المشكلة…',
      'staff.report.priority': 'الأولوية',
      'staff.report.submit': 'إرسال البلاغ',
      // Staff (shop-floor) view — language toggle (LangToggle).
      'staff.lang.toggle': 'EN',
      'staff.lang.toggleAria': 'تبديل اللغة',
      // Staff (shop-floor) view — My Work / Job / Task screen redesigns.
      'staff.myWork.title': 'عملي',
      'staff.myWork.scanQr': 'مسح الرمز',
      'staff.myWork.steps': '{{done}} من {{total}} خطوات',
      'staff.filter.active': 'نشط',
      'staff.filter.ready': 'جاهز',
      'staff.filter.all': 'الكل',
      'staff.job.customer': 'العميل',
      'staff.job.photos': 'الصور',
      'staff.job.addPhoto': 'إضافة صورة / فيديو',
      'staff.job.tasks': 'المهام',
      'staff.job.noTasks': 'لا توجد مهام بعد',
      'staff.job.qcWait': 'بانتظار فحص الجودة',
      'staff.job.qcWaitHint': 'سيقوم المشرف بمراجعة هذا العمل قريبًا',
      'staff.job.start': 'بدء العمل',
      'staff.job.complete': 'إنهاء العمل',
      'staff.task.parts': 'القطع',
      'staff.task.services': 'الخدمات',
      'staff.task.done': 'تم',
      'staff.task.reportProblem': 'الإبلاغ عن مشكلة',
      'staff.task.media': 'الصور والفيديو',
      'staff.task.start': 'بدء المهمة',
      'staff.task.complete': 'إنهاء المهمة',
      'staff.task.confirmCompleteTitle': 'إنهاء هذه المهمة؟',
      'staff.task.confirmCompleteMessage': 'سيتم تسجيل المهمة كمكتملة. تأكد من إنجاز جميع الأعمال فعلياً.',
      'staff.task.completed': 'اكتملت المهمة',
      'staff.task.timeLeft': 'الوقت المتبقي',
      'staff.task.overtime': 'تجاوز الوقت المقدر',

      'staff.empty.noJobs': 'لا توجد أعمال',
      'staff.empty.noTasks': 'لا توجد مهام',
      'staff.empty.noMedia': 'لا توجد وسائط',
      'staff.empty.noHistory': 'لا يوجد سجل',
      'staff.history.title': 'السجل',
      'staff.toast.mediaUploaded': 'تمت إضافة الوسائط',

      'staff.task.startNow': 'ابدأ الآن',
      'staff.task.notReadyToInstall': 'بانتظار الموافقة — لا تركّب بعد',
      'staff.task.startHint': 'اضغط لبدء العمل على هذه المهمة',
      'staff.task.awaitingApprovalHint': 'بانتظار موافقة العميل على السعر — لا تبدأ بعد',
      'staff.task.noWorkToCompleteHint': 'أضف القطع أو الخدمات التي نفّذتها قبل الإنهاء',
      'staff.task.finishItemsFirst': 'علّم القطع والخدمات كمنتهية أولاً',
      'staff.task.partsNotReceivedHint': 'يجب تأكيد استلام قطعة العميل أولاً. اطلب من الاستقبال تسجيل يوم وصولها.',
      'staff.task.partsNotReceivedList': 'لم يتم تأكيد استلامها: {{parts}}',
      'staff.task.inProgress': 'قيد التنفيذ',
      'staff.task.paused': 'متوقفة مؤقتاً',
      'staff.task.waitingToStart': 'جاهزة للبدء',

      // ── F6 offline queue ──
      'offline.pendingSync': 'سيُرسل عند الاتصال',
      'offline.queueFull': 'عدد كبير من الإجراءات بانتظار الإرسال — حاول مجدداً بعد عودة الاتصال',
      'offline.expiredDropped': '{{count}} من الإجراءات المعلّقة كانت قديمة جداً ولم تُرسل',
      'offline.syncFailedGeneric': 'تعذر الإرسال — حاول مرة أخرى',

      'staff.filter.mine': 'مهامي',
      'staff.filter.team': 'كل الفريق',
      'staff.card.assignedTo': 'مُسند إلى',
      'staff.card.unassigned': 'غير مُسند',
      'staff.card.mine': 'أنت',
      'staff.sort.label': 'ترتيب',

      'staff.reassign.taskTitle': 'إعادة تعيين هذه المهمة',
      'staff.reassign.jobTitle': 'إعادة تعيين هذا العمل',
      'staff.reassign.cta': 'إعادة تعيين',
      'staff.reassign.open': 'إعادة تعيين',
      'staff.reassign.pick': 'اختر أحد أعضاء الفريق',
      'staff.reassign.search': 'ابحث عن فني…',
      'staff.reassign.unassign': 'إلغاء التعيين',
      'staff.reassign.success': 'تمت إعادة التعيين',
      'staff.reassign.failed': 'تعذرت إعادة التعيين',
      'staff.reassign.noStaff': 'لا يوجد فنيون متاحون',
      'staff.reassign.currentLabel': 'حالياً',
      'plan.userLimit': 'باقتك تسمح بـ {{limit}} مستخدم ولديك {{current}} بالفعل. تواصل مع موتورمايند لترقية الباقة.',
      'plan.branchLimit': 'باقتك تسمح بـ {{limit}} فرع ولديك {{current}} بالفعل. تواصل مع موتورمايند لترقية الباقة.',
      'plan.aiQuota': 'استهلكت كامل طلبات الذكاء الاصطناعي في باقتك لهذا الشهر ({{limit}}). تتجدد الشهر القادم، أو تواصل مع موتورمايند لترقية الباقة.',
      'plan.featureDisabled': '{{feature}} غير مشمولة في باقتك. تواصل مع موتورمايند لإضافتها.',
      'plan.notInPlanTitle': 'غير مشمول في باقتك',
      'plan.notInPlanBody': 'هذا القسم غير مشمول في باقتك الحالية. تواصل مع موتورمايند لإضافته.',
      'plan.backToDashboard': 'رجوع',
      'subscription.endedTitle': 'انتهى الاشتراك',
      'subscription.endedBody': 'انتهى اشتراك مركزك في موتورمايند، لذلك تم إيقاف الدخول مؤقتاً. لم يتم حذف أي بيانات — يعود كل شيء فور تجديد الاشتراك.',
      'subscription.suspendedBody': 'تم تعليق حساب مركزك في موتورمايند. لم يتم حذف أي بيانات — يعود الدخول فور إعادة تفعيل الحساب.',
      'subscription.deactivatedBody': 'تم إيقاف حساب مركزك في موتورمايند. لم يتم حذف أي بيانات — يعود الدخول فور إعادة تفعيل الحساب.',
      'subscription.unavailableBody': 'تعذّر التحقق من اشتراك مركزك الآن، لذلك تم إيقاف الدخول مؤقتاً كإجراء احترازي. لم يتم حذف أي بيانات. يرجى التواصل مع الدعم — الخلل من عندنا وليس منك.',
      'subscription.endedOn': 'انتهى في',
      'subscription.contactSupport': 'يرجى التواصل مع موتورمايند للتجديد.',
      'subscription.graceDays': 'فترة السماح (أيام)',
      'subscription.graceDaysHint': 'أيام دخول كامل بعد تاريخ الانتهاء. 0 = إيقاف الدخول في تاريخ الانتهاء.',
      'settings.lockedNotice': 'الإعدادات مغلقة',
      'settings.companyLevelViewOnly': 'على مستوى الشركة، للعرض فقط',
      'settings.feedback.repliesNote': 'تظهر الردود في صفحة {{page}}.',
      'settings.statusesSelected': 'حالات محددة',
      'settings.contactAccountManager': 'لتحديث هذه المعلومات، يُرجى التواصل مع مدير حسابك.',
      'settings.namesCompanyLevel': 'تُضبط على مستوى الشركة — ويعكس اسم الفرع الاسم التجاري.',
      'settings.namesBranchAndCompany': 'الاسم التجاري خاص بهذا الفرع (يُعدَّل من «تعديل الفرع»)، أمّا الاسم القانوني فيُضبط من ملف الشركة.',

      // ── Platform Analytics (F1 super-admin oversight) ──
      'pa.navTitle': 'التحليلات',
      'pa.pageTitle': 'تحليلات المنصة',
      'pa.pageDescription': 'نظرة شاملة عبر المستأجرين: نشاط بطاقات العمل واستخدام الذكاء الاصطناعي لكل شركة وفرع.',
      'pa.totalCompanies': 'الشركات',
      'pa.totalBranches': 'الفروع',
      'pa.totalJobCards': 'بطاقات العمل',
      'pa.aiUsageMonth': 'استخدام الذكاء الاصطناعي (هذا الشهر)',
      'pa.recentTrend': '{{count}} خلال ٣٠ يوماً',
      'pa.statusBreakdown': 'توزيع حالات بطاقات العمل',
      'pa.companies': 'الشركات',
      'pa.noCompanies': 'لا توجد شركات بعد',
      'pa.noJobCards': 'لا توجد بطاقات عمل',
      'pa.noBranches': 'لا توجد فروع',
      'pa.branchCount': '{{count}} فرع/فروع',
      'pa.jobCards': 'بطاقات العمل',
      'pa.total': 'الإجمالي',
      'pa.recent7': 'آخر ٧ أيام',
      'pa.recent30': 'آخر ٣٠ يوماً',
      'pa.tasks': 'المهام',
      'pa.aiUsage': 'استخدام الذكاء الاصطناعي',
      'pa.unlimited': 'غير محدود',
      'pa.loadError': 'تعذّر تحميل التحليلات',
      'pa.viewJobCards': 'عرض بطاقات عمل هذا الفرع',

      // ── قنوات الإشعارات (سوبر أدمن) ──
      'notifChannels.navTitle': 'قنوات الإشعارات',
      'onbAlerts.title': 'تنبيهات طلبات الانضمام',
      'onbAlerts.subtitle': 'مدراء النظام المحددون هنا يصلهم إشعار ورسالة على الجوال عند إرسال العميل طلب الانضمام.',
      'onbAlerts.fallbackNotice': 'لم يتم تحديد أحد، لذلك يصل التنبيه إلى جميع مدراء النظام. حدد أسماء بالأسفل لتخصيصه.',
      'onbAlerts.noAdmins': 'لا يوجد مدراء نظام نشطون للاختيار منهم.',
      'onbAlerts.noMobile': 'بدون جوال — إشعار فقط',
      'onbAlerts.save': 'حفظ المستلمين',
      'onbAlerts.saved': 'تم حفظ المستلمين',
      'onbAlerts.saveFailed': 'تعذّر حفظ المستلمين',
      'onbAlerts.loadError': 'تعذّر تحميل قائمة مدراء النظام',
      'adminAlerts.navTitle': 'إشعارات الإدارة',
      'adminAlerts.title': 'إشعارات الإدارة',
      'adminAlerts.subtitle': 'من في فريق المنصة يصله تنبيه عند كل حدث. عدم تحديد أحد في أي لوحة يعني وصول التنبيه لجميع مدراء النظام.',
      'waLink.navTitle': 'ربط واتساب',
      'waLink.title': 'ربط واتساب',
      'waLink.subtitle': 'اربط الهاتف الذي ترسل منه المنصة رسائلها. واتساب يفصل الجهاز المرتبط من وقت لآخر — عند حدوث ذلك امسح الرمز من هنا مرة أخرى.',
      'waLink.testTitle': 'إرسال رسالة تجريبية',
      'waLink.testHint': 'علامة «متصل» الخضراء تعني أن الربط قائم فقط. أرسل رسالة تجريبية للتأكد من وصولها فعلًا.',
      'waLink.testPhone': 'إرسال إلى',
      'waLink.testSend': 'إرسال تجريبي',
      'waLink.testSent': 'تم إرسال الرسالة التجريبية',
      'waLink.testFailed': 'تعذّر إرسال الرسالة التجريبية',
      'qcAlerts.title': 'نشر على dev — جاهز للاختبار',
      'qcAlerts.subtitle': 'مديرو النظام المختارون هنا تصلهم رسالة واتساب من الهاتف المرتبط عند وصول نسخة إلى dev، تحتوي على ما تم نشره وملاحظة قصيرة عمّا يجب تجربته.',
      'qcAlerts.fallbackNotice': 'لم يتم اختيار أحد، لذلك يتم إشعار جميع مديري النظام. اختر أسماء بالأسفل لتحديدهم.',
      'qcAlerts.noAdmins': 'لا يوجد مديرو نظام نشطون للاختيار منهم.',
      'qcAlerts.noMobile': 'لا يوجد جوال — لا يمكن استقبال هذا التنبيه',
      'qcAlerts.save': 'حفظ المستلمين',
      'qcAlerts.saved': 'تم حفظ المستلمين',
      'qcAlerts.saveFailed': 'تعذّر حفظ المستلمين',
      'qcAlerts.loadError': 'تعذّر تحميل قائمة مديري النظام',
      'prodAlerts.title': 'نشر على الإنتاج — تم الإطلاق',
      'prodAlerts.subtitle': 'قائمة منفصلة: هؤلاء تصلهم رسالة عند وصول نسخة إلى العملاء على الإنتاج، مع محتوى النشر. أبقِها منفصلة عن قائمة dev إذا كان الفريقان مختلفين.',
      'prodAlerts.fallbackNotice': 'لم يتم اختيار أحد، لذلك يتم إشعار جميع مديري النظام. اختر أسماء بالأسفل لتحديدهم.',
      'prodAlerts.noAdmins': 'لا يوجد مديرو نظام نشطون للاختيار منهم.',
      'prodAlerts.noMobile': 'لا يوجد جوال — لا يمكن استقبال هذا التنبيه',
      'prodAlerts.save': 'حفظ المستلمين',
      'prodAlerts.saved': 'تم حفظ المستلمين',
      'prodAlerts.saveFailed': 'تعذّر حفظ المستلمين',
      'prodAlerts.loadError': 'تعذّر تحميل قائمة مديري النظام',
      'payRcvAlerts.title': 'تنبيهات استلام الدفعات',
      'payRcvAlerts.subtitle': 'مدراء النظام المحددون هنا تصلهم رسالة واتساب (مع رسالة نصية احتياطية) عند وصول دفعة من عميل مستأجر عبر بوابة مُدارة — اسم المستأجر وطريقة الدفع والمبلغ.',
      'payRcvAlerts.fallbackNotice': 'لم يتم تحديد أحد، لذلك يصل التنبيه إلى جميع مدراء النظام. حدد أسماء بالأسفل لتخصيصه.',
      'payRcvAlerts.noAdmins': 'لا يوجد مدراء نظام نشطون للاختيار منهم.',
      'payRcvAlerts.noMobile': 'بدون جوال — لا يمكن استلام هذا التنبيه',
      'payRcvAlerts.save': 'حفظ المستلمين',
      'payRcvAlerts.saved': 'تم حفظ المستلمين',
      'payRcvAlerts.saveFailed': 'تعذّر حفظ المستلمين',
      'payRcvAlerts.loadError': 'تعذّر تحميل قائمة مدراء النظام',
      'payInqAlerts.title': 'تنبيهات استفسارات الدفعات',
      'payInqAlerts.subtitle': 'مدراء النظام المحددون هنا تصلهم رسالة واتساب (مع رسالة نصية احتياطية) عند إرسال مستأجر استفسارًا على دفعة مستحقة.',
      'payInqAlerts.fallbackNotice': 'لم يتم تحديد أحد، لذلك يصل التنبيه إلى جميع مدراء النظام. حدد أسماء بالأسفل لتخصيصه.',
      'payInqAlerts.noAdmins': 'لا يوجد مدراء نظام نشطون للاختيار منهم.',
      'payInqAlerts.noMobile': 'بدون جوال — لا يمكن استلام هذا التنبيه',
      'payInqAlerts.save': 'حفظ المستلمين',
      'payInqAlerts.saved': 'تم حفظ المستلمين',
      'payInqAlerts.saveFailed': 'تعذّر حفظ المستلمين',
      'payInqAlerts.loadError': 'تعذّر تحميل قائمة مدراء النظام',
      'extAlerts.title': 'أرقام خارجية',
      'extAlerts.subtitle': 'أرقام جوال بدون حساب في البوابة تصلها هذه التنبيهات أيضًا — محاسب، شريك، جوال إضافي.',
      'extAlerts.namePlaceholder': 'الاسم (مثال: المحاسب)',
      'extAlerts.phonePlaceholder': '05xxxxxxxx',
      'extAlerts.language': 'لغة الرسالة',
      'extAlerts.langAr': 'العربية',
      'extAlerts.langEn': 'الإنجليزية',
      'extAlerts.add': 'إضافة رقم',
      'extAlerts.added': 'تمت إضافة الرقم',
      'extAlerts.remove': 'حذف الرقم',
      'extAlerts.removed': 'تم حذف الرقم',
      'extAlerts.saveFailed': 'تعذّر حفظ الرقم',
      'ann.gotIt': 'تمام',
      'ann.readMore': 'اقرأ المزيد',
      'ann.dontShowAgain': 'عدم الإظهار مرة أخرى',
      'ann.dismissibleLabel': 'يظهر مرة واحدة',
      'ann.dismissibleHint': 'يمكن للقارئ إخفاؤه نهائياً، فلا يظهر له مجدداً — أما الإعلانات الجديدة فتظهر كالمعتاد. عند تفعيله يصبح وقت الانتهاء اختيارياً، إذ يجب أن ينتهي كل إعلان تلقائياً أو يكون قابلاً للإخفاء.',
      'ann.endsAtOptionalHint': 'اختياري الآن بعد أن أصبح قابلاً للإخفاء. اتركه فارغاً ليبقى حتى يخفيه كل مستخدم بنفسه.',
      'ann.navTitle': 'الإعلانات',
      'ann.title': 'إعلانات المنصة',
      'ann.subtitle': 'أخبر المشتركين بأعمال الصيانة المجدولة أو بأي أمر لا يصح أن يفوتهم. يظهر مرة كنافذة، ثم يبقى في لوحتهم حتى ينتهي.',
      'ann.audience': 'من يراه',
      'ann.audienceAll': 'كل المشتركين',
      'ann.audienceCompany': 'مشترك واحد',
      'ann.audienceUser': 'شخص واحد',
      'ann.pickCompany': 'المشترك',
      'ann.pickUser': 'الشخص',
      'ann.pickUserHint': 'اختر المشترك أولاً، ثم الشخص.',
      'ann.severity': 'درجة الأهمية',
      'ann.sevInfo': 'معلومة',
      'ann.sevWarning': 'تنبيه',
      'ann.sevCritical': 'حرج',
      'ann.severityHint': 'تغيّر اللون فقط. لا ترسل رسالة، ولا تعود بعد أن يغلقها الشخص.',
      'ann.environment': 'البيئة المعنية',
      'ann.envProd': 'الإنتاج',
      'ann.envDev': 'التطوير',
      'ann.envAll': 'جميع البيئات',
      'ann.environmentHint': 'يظهر على الشريط حتى لا تُقرأ صيانة بيئة التطوير على أنها انقطاع فعلي. وسم فقط — لا يغيّر من يرى هذا الإعلان.',
      'ann.titleEn': 'العنوان (بالإنجليزية)',
      'ann.titleAr': 'العنوان (بالعربية)',
      'ann.bodyEn': 'النص (بالإنجليزية)',
      'ann.bodyAr': 'النص (بالعربية)',
      'ann.bilingualHint': 'اللغتان مطلوبتان — كل شخص يقرأه بلغته.',
      'ann.startsAt': 'يظهر من',
      'ann.endsAt': 'يختفي في',
      'ann.endsAtHint': 'يختفي وحده في هذا الوقت. لا يحتاج أحد إلى حذفه.',
      'ann.isMaintenance': 'هذا إشعار صيانة',
      'ann.isMaintenanceHint': 'فعّل هذا عندما يكون النظام متوقفاً أو بأداء محدود. يضيف وقت بداية ونهاية الصيانة، وهما منفصلان عن «يظهر من» و«يختفي في» أعلاه — تلك تتحكم في الشريط لا في التوقف.',
      'ann.windowStartsAt': 'بداية الصيانة',
      'ann.windowEndsAt': 'نهاية الصيانة',
      'ann.windowHint': 'الوقت الذي يتوقف فيه النظام فعلياً. يُرسل عبر واتساب، وهو ما يخطط العملاء على أساسه.',
      'ann.affectedServicesEn': 'الخدمات المتأثرة (بالإنجليزية)',
      'ann.affectedServicesAr': 'الخدمات المتأثرة (بالعربية)',
      'ann.affectedServicesHint': 'اختياري. أي أجزاء المنصة يشملها هذا الإشعار — إن تُرك فارغاً فسيُقرأ الإشعار على أنه يشمل جميع الخدمات.',
      'ann.affectedServicesPlaceholderEn': 'Invoices, WhatsApp notifications',
      'ann.affectedServicesPlaceholderAr': 'الفواتير، إشعارات واتساب',
      'ann.publish': 'نشر',
      'ann.publishAndSend': 'نشر وإرسال',
      'ann.aiTitle': 'اكتبه بالذكاء الاصطناعي',
      'ann.aiBrief': 'عن ماذا الإعلان؟',
      'ann.aiBriefHint': 'سطر أو سطران. المساعد يكتب اللغتين، ويمكنك تعديل كل شيء بعدها.',
      'ann.aiBriefPlaceholder': 'مثال: وحدة الموارد البشرية أصبحت متاحة — الرواتب والإجازات والحضور في القائمة لكل مركز على باقة برو.',
      'ann.aiTone': 'الأسلوب',
      'ann.aiTone_professional': 'رسمي',
      'ann.aiTone_friendly': 'ودّي',
      'ann.aiTone_promotional': 'تسويقي',
      'ann.aiDraft': 'اكتب بالذكاء الاصطناعي',
      'ann.aiDrafted': 'تمت كتابة المسودة — راجعها قبل النشر',
      'ann.waLabel': 'إرسال عبر واتساب أيضًا',
      'ann.waHint': 'يرسل الإعلان من رقم واتساب المنصة إلى أصحاب مراكز خدمات السيارات ضمن الجمهور أعلاه، معروضًا كموضوع وتوقيت وخدمات متأثرة. الشريط يصل لمن يسجّل دخوله؛ هذا يصل لمن لن يفعل اليوم.',
      'ann.waCount': 'سيتم إرسال الرسالة إلى {{count}} من أصحاب مراكز خدمات السيارات',
      'ann.waNoPhone': '{{count}} بدون رقم جوال',
      'ann.waOverCap': '{{count}} رقمًا — أكثر من الحد المسموح {{cap}} للإرسال الواحد. ضيّق الجمهور.',
      'ann.waSent': 'تم إرسال واتساب إلى {{count}} مستلم',
      'ann.waPartial': 'تعذّر تسليم {{count}} رسالة',
      'ann.waViaSms': 'أُرسلت {{count}} رسالة كرسائل نصية لأن واتساب لم يقبل القالب — وتُحتسب تكلفتها لكل مقطع',
      'ann.waAlreadyReached': 'تم تخطي {{count}} لأنهم استلموا الإعلان في إرسال سابق',
      'ann.waRemaining': 'لم يتم الوصول إلى {{count}} قبل انتهاء مهلة الإرسال. اضغط «إرسال عبر واتساب» مرة أخرى للمتابعة — سيتم تخطي من وصلته بالفعل.',
      'ann.waSendNow': 'إرسال عبر واتساب',
      'ann.smsLabel': 'إرسال عبر الرسائل النصية أيضًا',
      'ann.smsHint': 'يرسل الإعلان نفسه كرسالة نصية إلى الجمهور أعلاه. يصل إلى الجوال بدون تطبيق وبدون إنترنت — وكل رسالة محتسبة، لذا اجعل النص قصيرًا.',
      'ann.smsCount': 'سيتم إرسال رسالة نصية إلى {{count}} مستلم',
      'ann.smsOverCap': '{{count}} رقمًا — أكثر من الحد المسموح {{cap}} للإرسال الواحد. ضيّق الجمهور.',
      'ann.smsSent': 'تم إرسال رسالة نصية إلى {{count}} مستلم',
      'ann.smsPartial': 'تعذّر إرسال {{count}} رسالة نصية',
      'ann.smsSendNow': 'إرسال عبر الرسائل النصية'
      ,'ann.confirmResendMsg': 'إرسال هذا الإعلان كرسالة واتساب إلى {{target}} من رقم المنصة؟ سيتم تخطي من استلمها سابقًا، فلن تصل إلا لمن لم تصله بعد.',
      'ann.confirmWaTitle': 'نشر وإرسال عبر {{channels}}؟',
      'ann.confirmWaMsg': 'سيتم نشر الإعلان إلى {{target}} وإرساله عبر {{channels}} إلى {{count}} من أصحاب مراكز خدمات السيارات من رقم المنصة. لا يمكن استرجاع الرسائل بعد إرسالها.',
      'ann.confirmWaMsgUnknown': 'سيتم نشر الإعلان إلى {{target}} وإرساله عبر {{channels}} إلى كل أصحاب مراكز خدمات السيارات ضمن هذا الجمهور من رقم المنصة. تعذّر التحقق من حجم الجمهور. لا يمكن استرجاع الرسائل بعد إرسالها.',
      'ann.channelWhatsapp': 'واتساب',
      'ann.channelSms': 'الرسائل النصية',
      'ann.channelJoin': ' و',
      'ann.published': 'تم نشر الإعلان',
      'ann.history': 'ما أُرسل حتى الآن',
      'ann.noneYet': 'لم يُرسل شيء بعد.',
      'ann.endNow': 'إنهاء الآن',
      'ann.statusActive': 'ظاهر الآن',
      'ann.statusScheduled': 'مجدول',
      'ann.statusExpired': 'انتهى',
      'ann.confirmEndTitle': 'إنهاء هذا الإعلان؟',
      'ann.confirmEndMsg': 'سيختفي من كل الشاشات فوراً. ويبقى نصه محفوظاً في هذه القائمة.',
      'ann.toAll': 'كل المشتركين',
      'notifChannels.title': 'قنوات الإشعارات',
      'notifChannels.subtitle': 'اختر رسالة نصية أو واتساب مع رجوع تلقائي للرسائل النصية لكل نوع إشعار.',
      'notifChannels.search': 'ابحث بمفتاح الإشعار أو اسم القالب',
      'notifChannels.noMatches': 'لا يوجد نوع إشعار مطابق لهذا البحث',
      'notifChannels.loadError': 'تعذّر تحميل قنوات الإشعارات',
      'notifChannels.loadErrorHint': 'القائمة تأتي من دليل الإشعارات. حاول مرة أخرى، وتأكد أن الواجهة البرمجية متاحة.',
      'notifChannels.saved': 'تم تحديث القناة',
      'notifChannels.saveFailed': 'تعذّر تحديث القناة',
      'notifChannels.noTemplate': 'لا يوجد قالب واتساب',
      'notifChannels.precedence': 'لا يمكن للرسالة أن تغادر القناة النصية إلا باجتماع ثلاثة شروط: اعتماد القالب من ميتا، وتفعيل التوجيه المختلط على الخادم، وضبط النوع على «مختلط» أدناه. وما عدا ذلك يبقى رسالة نصية.',
      'notifChannels.summary': 'يمكن لـ {{eligible}} من أصل {{total}} نوعًا استخدام واتساب اليوم. المفعّل حاليًا: {{hybrid}}.',
      'notifChannels.killSwitchOffTitle': 'التوجيه المختلط متوقف',
      'notifChannels.killSwitchOffBody': 'كل الرسائل تُرسل الآن عبر الرسائل النصية مهما كانت المفاتيح أدناه. فعّل هذا الخيار لتعمل فورًا.',
      'notifChannels.killSwitchOnTitle': 'التوجيه المختلط مفعّل',
      'notifChannels.killSwitchOnBody': 'كل نوع أدناه يتبع مفتاحه الخاص الآن. وإيقاف هذا الخيار يعيد كل الرسائل إلى الرسائل النصية دفعة واحدة دون تغيير أي منها.',
      'notifChannels.switchOn': 'تفعيل',
      'notifChannels.switchOff': 'إيقاف',
      'notifChannels.switchLocked': 'المتغيّر HYBRID_ROUTING_ENABLED مضبوط على الخادم ويتجاوز هذا المفتاح. احذفه من الخادم للتحكم في التوجيه من هذه الشاشة.',
      'notifChannels.mode.sms': 'رسالة نصية',
      'notifChannels.mode.hybrid': 'مختلط',
      'notifChannels.audience.garage': 'إدارة المركز',
      'notifChannels.audience.staff': 'الموظفون',
      'notifChannels.audience.customer': 'العملاء',
      'notifChannels.refusal.carriesLink': 'تحتوي على رابط خاص — رسالة نصية دائمًا',
      'notifChannels.refusal.customerAuthCode': 'رمز تحقق للعميل — رسالة نصية دائمًا',
      'notifChannels.refusal.notApproved': 'غير مفعّل بعد — القالب أو المُرسِل قيد الإعداد',
      'notifChannels.state.wait': 'بانتظار الاعتماد',
      'notifChannels.state.lock': 'رسالة نصية دائمًا',
      'notifChannels.queue': 'بانتظار قالب من ميتا: {{waiting}}. وتبقى على الرسائل النصية بحكم السياسة ولن تتغير: {{locked}}.',
      'notifChannels.cost.title': 'قياس التسليم خلال آخر ٣٠ يومًا',
      'notifChannels.cost.noData': 'لم يُرسل أي شيء عبر واتساب بعد، فلا يوجد ما يُقاس. سيظهر القياس هنا بمجرد تحويل أحد أنواع الإشعارات إلى «مختلط» ومرور رسائل فعلية عليه.',
      'notifChannels.cost.summary': 'عدد الرسائل المُرسلة بالقوالب خلال {{days}} يومًا: {{attempts}}. سُلّم عبر واتساب: {{whatsapp}} ({{percent}}٪)، ورجع إلى الرسائل النصية: {{fallbacks}}، بتكلفة مقاطع نصية: {{segments}}.',
      'notifChannels.cost.row': 'أُرسلت: {{attempts}} · عبر واتساب: {{whatsapp}} · رجعت للرسائل النصية: {{fallbacks}}',
      'notifChannels.cost.caveat': 'القياس بعدد المقاطع النصية وليس بالريال — لا يوجد سعر مقطع مُهيّأ في النظام. الرسائل النصية وحدها غير محسوبة هنا؛ قارنها بفاتورة مزوّد الرسائل لمعرفة الفرق قبل وبعد.',

      // ── حالة التوفر العالي (سوبر أدمن) ──
      'ha.navTitle': 'حالة التوفر العالي',
      'ha.title': 'حالة التوفر العالي (HA)',
      'ha.subtitle': 'الحالة الحيّة للخادم الأساسي والاحتياطي، فارق النسخ المتماثل، وأحداث التبديل التلقائي عبر كلا الخادمين.',
      'ha.loadError': 'تعذّر تحميل حالة التوفر العالي',
      'ha.testAlert': 'إرسال تنبيه تجريبي',
      'ha.testAlertSent': 'تم إرسال التنبيه التجريبي',
      'ha.testAlertConfirm': 'سيتم إرسال إشعار تجريبي فعلي عبر قناة تنبيهات التوفر العالي. هل تريد المتابعة؟',
      'ha.servingFrom': 'يعمل حالياً من',
      'ha.unknown': 'غير معروف',
      'ha.standbyModeOn': 'وضع الاحتياط (STANDBY_MODE)',
      'ha.lastChecked': 'آخر تحديث: {{time}}',
      'ha.database': 'قاعدة البيانات',
      'ha.inRecovery': 'في وضع الاسترداد',
      'ha.receiveLsn': 'موضع الاستقبال (LSN)',
      'ha.lastReplay': 'آخر إعادة تشغيل',
      'ha.currentLsn': 'الموضع الحالي (LSN)',
      'ha.replication': 'النسخ المتماثل',
      'ha.noStandby': 'لا يوجد خادم احتياطي متصل',
      'ha.node': 'الخادم',
      'ha.state': 'الحالة',
      'ha.syncState': 'حالة التزامن',
      'ha.lag': 'الفارق',
      'ha.replayLag': 'فارق إعادة التشغيل',
      'ha.events': 'الأحداث',
      'ha.noEvents': 'لا توجد أحداث بعد',
      'ha.time': 'الوقت',
      'ha.eventType': 'النوع',
      'ha.message': 'الرسالة',
      'ha.eventsPage': 'صفحة {{page}} · الإجمالي {{total}}',
      'ha.prev': 'السابق',
      'ha.next': 'التالي',
      'ha.primary': 'أساسي',
      'ha.standby': 'احتياطي',

      // ── F1: مشاركة واتساب (عرض السعر / رابط الدفع / الفاتورة) ──
      'wa.share': 'واتساب',
      'wa.quotationMessage': 'مرحباً، هذا عرض السعر رقم {{number}}: {{link}}',
      'wa.paymentLinkMessage': 'مرحباً، يرجى إتمام الدفع لطلب رقم {{number}}: {{link}}',
      'wa.invoiceMessage': 'مرحباً، هذه فاتورتك رقم {{number}}: {{link}}',
      'wa.thankYouMessage': 'شكراً لاختياركم لنا! إليك فاتورة طلب رقم {{number}}: {{link}}',

      // ── Super-admin Subscriptions (plan catalog) ──
      'sub.title': 'باقات الاشتراك',
      'sub.subtitle': 'صمّم باقاتك الخاصة — المزايا والحدود والتسعير',
      'sub.newPlan': 'باقة جديدة',
      'sub.noPlans': 'لا توجد باقات بعد',
      'sub.noPlansDesc': 'أنشئ أول باقة اشتراك.',
      'sub.planUpdated': 'تم تحديث الباقة',
      'sub.planCreated': 'تم إنشاء الباقة',
      'sub.saveFailed': 'تعذّر حفظ الباقة',
      'sub.planDeleted': 'تم حذف الباقة',
      'sub.deleteFailed': 'تعذّر حذف الباقة',
      'sub.unitDay': 'يوم',
      'sub.unitWeek': 'أسبوع',
      'sub.unitMonth': 'شهر',
      'sub.unitYear': 'سنة',
      'sub.unlimited': 'غير محدود',
      'sub.usedNoLimit': '{{used}} · بلا حد',
      'sub.active': 'مفعّلة',
      'sub.inactive': 'غير مفعّلة',
      'sub.features': 'مزايا',
      'sub.deletePlan': 'حذف الباقة',
      'sub.deleteConfirm': 'حذف "{{name}}"؟ الشركات المشتركة تحتفظ باشتراكها.',

      // دقائق المحادثة الصوتية — الحقل الفارغ يعني «غير محدود» مثل حصص
      // الذكاء الاصطناعي أعلاه، و0 يعني بلا دقائق (قرار المالك 2026-09-19).
      'voiceConversationMinutes': 'دقائق المحادثة الصوتية / شهر',
      'voiceConversationUsage': 'المحادثة الصوتية: {{used}} / {{allowance}} دقيقة هذا الشهر',
      'voiceConversationUsageUnlimited': 'المحادثة الصوتية: {{used}} دقيقة مستخدمة هذا الشهر (غير محدود)',
      'voiceConversationReset': 'تصفير الاستخدام',
      'voiceConversationResetConfirm': 'يُصفَّر عدّاد دقائق المحادثة الصوتية إلى الصفر ويبدأ العد من الآن، وتبقى الجلسات السابقة مسجّلة.',
      'voiceConversationResetDone': 'تم تصفير دقائق المحادثة الصوتية، ويبدأ العد من الآن.',
      'wa.connect': 'ربط واتساب',
      'wa.disconnect': 'قطع الاتصال',
      'wa.disconnectConfirm': 'هل أنت متأكد من رغبتك في قطع اتصال واتساب؟',
      'wa.banRisk': 'احمِ رقم واتساب الخاص بك: قد يحظر واتساب الأرقام التي ترسل رسائل جماعية أو غير مرغوب فيها. أرسل إلى 50 مستلمًا كحد أقصى في الحملة الواحدة، واستهدف عملاءك الحاليين الذين يتوقعون رسائلك، وباعد بين الحملات. يضيف موتورمايند تلقائيًا فاصل انتظار من 10 إلى 25 ثانية بين كل رسالة وأخرى، بحد أقصى 200 رسالة تسويقية يوميًا.',
      'wa.scanInstructions': 'امسح رمز الاستجابة السريعة (QR) أدناه باستخدام تطبيق واتساب على هاتفك لربط حسابك. يرجى إبقاء هذه الصفحة مفتوحة أثناء الاقتران.',
      'wa.status.title': 'جلسة واتساب',
      'wa.multi.branches.title': 'خطوط واتساب للفروع',
      'wa.multi.branches.description': 'تعيين خط واتساب أو رسائل SMS فقط لكل فرع.',
      'wa.multi.branches.branch': 'الفرع',
      'wa.multi.branches.currentLine': 'الخط الحالي',
      'wa.multi.branches.assignLine': 'الخط المعين',
      'wa.multi.branches.mainLine': 'الخط الرئيسي',
      'wa.multi.branches.smsOnly': 'رسائل SMS فقط',
      'wa.multi.branches.defaultLine': 'الخط الافتراضي',
      'wa.multi.branches.saving': 'جارٍ الحفظ...',
      'wa.multi.branches.saved': 'تم الحفظ بنجاح',
      'wa.multi.branches.inboxTitle': 'ظهور صندوق الوارد',
      'wa.multi.branches.inboxDescription': 'التحكم في الفروع التي يمكنها رؤية محادثات كل خط واتساب.',
      'wa.multi.branches.inboxEnabled': 'صندوق الوارد مفعّل',
      'wa.multi.branches.inboxDisabled': 'صندوق الوارد معطّل',
      'wa.multi.branches.visibilityDefault': 'الفروع التي تستخدم هذا الخط',
      'wa.multi.branches.visibilityExplicit': 'فروع محددة',
      'wa.multi.branches.hiddenZeroBranches': 'مخفي عن كل الفروع — اختر فروعًا أو استعد الإعداد الافتراضي',
      'wa.multi.branches.restoreDefault': 'استعادة الإعداد الافتراضي',
      'wa.multi.branches.saveInbox': 'حفظ إعدادات الظهور',
      'wa.multi.branches.noBranches': 'لا توجد فروع',
      'wa.multi.branches.noLines': 'لا توجد خطوط',
      'wa.multi.branches.save': 'حفظ',
      'wa.multi.tpl.title': 'قوالب الحساب',
      'wa.multi.tpl.toggle': 'القوالب',
      'wa.multi.tpl.refresh': 'تحديث',
      'wa.multi.tpl.loading': 'جارٍ تحميل القوالب...',
      'wa.multi.tpl.empty': 'لا توجد قوالب لهذا الحساب',
      'wa.multi.tpl.error': 'فشل تحميل القوالب',
      'wa.multi.tpl.name': 'اسم القالب',
      'wa.multi.tpl.language': 'اللغة',
      'wa.multi.tpl.status': 'الحالة',
      'wa.multi.tpl.reason': 'سبب الرفض',
      'wa.multi.invalidAuth': 'رفضت Meta رمز الوصول لهذا الحساب. حدّث بيانات الاعتماد.',
      'wa.sessionHint': 'امسح الرمز من واتساب في جوال المركز لإعادة الربط عند انقطاع الجلسة',
      'wa.qrAlt': 'رمز ربط واتساب',
      'wa.status.disconnected': 'غير متصل',
      'wa.status.pairing': 'جارٍ الاقتران',
      'wa.status.connected': 'متصل',
      'wa.status.logged_out': 'تم تسجيل الخروج',
      'wa.unlinkedBanner': 'تم إلغاء ربط واتساب — المحادثات مخفية حتى تتم إعادة الربط.',
      'wa.linkedNumber': 'الرقم المرتبط',
      'wa.linkedAt': 'تم الربط في',
      'wa.lastConnected': 'آخر اتصال',
      'wa.accessDenied': 'تم رفض الوصول',
      'wa.accessDeniedDesc': 'ليس لديك صلاحية لربط واتساب.',
      'wa.confirmTitle': 'قطع اتصال واتساب؟',
      'wa.successConnected': 'تم ربط واتساب بنجاح!',
      'wa.saveFailed': 'فشل ربط واتساب',
      'wa.deleteFailed': 'فشل قطع اتصال واتساب',
      'wa.disconnectSuccess': 'تم قطع اتصال واتساب',
      'wa.loadingQr': 'جارٍ تحميل رمز الاستجابة السريعة...',
      'nav.whatsapp': 'واتساب',
      'wa.chat': 'صندوق وارد واتساب',
      'wa.searchPlaceholder': 'البحث في المحادثات...',
      'wa.linkCustomer': 'ربط العميل',
      'wa.noCustomerMatched': 'لا يوجد عميل مطابق',
      'wa.createCustomer': 'إنشاء عميل',
      'wa.outstandingBalance': 'الرصيد المستحق',
      'wa.jobCards': 'بطاقات العمل',
      'wa.quotations': 'عروض الأسعار',
      'wa.invoices': 'الفواتير',
      'wa.disconnectedBanner': 'واتساب غير متصل — لن يتم إرسال الرسائل',
      'wa.attachFile': 'إرفاق ملف',
      'wa.send': 'إرسال',
      'wa.typeMessage': 'اكتب رسالة...',
      'wa.retry': 'إعادة المحاولة',
      'wa.optedOut': 'تم إلغاء الاشتراك',
      'wa.linkCustomerModalTitle': 'ربط العميل بالمحادثة',
      'wa.selectCustomer': 'اختر عميلاً',
      'wa.back': 'رجوع',
      'wa.toggleList': 'إظهار المحادثات',
      'wa.activityHeader': 'نشاط موتر مايند',
      'wa.loading': 'جارٍ التحميل...',
      'wa.failedToSend': 'فشل الإرسال',
      'wa.viewMedia': 'عرض الوسائط',
      'wa.today': 'اليوم',
      'wa.yesterday': 'أمس',
      'wa.noChatsFound': 'لا توجد محادثات',
      'wa.noJobCards': 'لا توجد بطاقات عمل',
      'wa.noQuotationsFound': 'لا توجد عروض أسعار',
      'wa.noInvoicesFound': 'لا توجد فواتير',
      'wa.selectChatPrompt': 'اختر محادثة لبدء المراسلة',
      'wa.customerLinkedSuccess': 'تم ربط العميل بنجاح',
      'wa.linkCustomerFailed': 'فشل ربط العميل',
      'wa.downloadFailed': 'فشل تحميل الملف',
      'wa.lines.main': 'الرئيسي',
      'wa.lines.selectLine': 'خط واتساب',
      'wa.lines.line': 'الخط',
      'wa.lines.allLines': 'جميع الخطوط',
      'wa.lines.default': 'الافتراضي',
      'wa.lines.connected': 'متصل',
      'wa.lines.disconnected': 'غير متصل',
      'wa.lines.hiddenFromAll': 'مخفي من جميع الفروع — اختر الفروع أو استعد الافتراضي',
      'wa.lines.selectLinePrompt': 'اختر خطاً للإرسال منه',
      'wa.reattachToRetry': 'أعد إرفاق الملف لإعادة الإرسال',
      'wa.unsupportedPhonePrefill': 'تعذر تعبئة الرقم {{phone}} تلقائياً — يرجى إدخاله يدوياً',
      'wa.confirmProceed': 'هل أنت متأكد من رغبتك في المتابعة؟',
      'wa.fileTooLarge': 'حجم الملف يتجاوز 16 ميجابايت',
      'wa.vehicleFallback': 'المركبة',
      'wa.mediaAlt': 'وسائط',
      'wa.downloadFile': 'تحميل الملف',

      // WhatsApp marketing campaigns
      'wa.mkt.title': 'حملات التسويق',
      'wa.mkt.subtitle': 'أرسل حملات واتساب لعملائك',
      'wa.mkt.marketing': 'التسويق',
      'wa.mkt.newCampaign': 'حملة جديدة',
      'wa.mkt.backToCampaigns': 'العودة إلى الحملات',
      'wa.mkt.loading': 'جارٍ تحميل الحملات...',
      'wa.mkt.noCampaigns': 'لا توجد حملات بعد',
      'wa.mkt.createFirst': 'أنشئ حملتك الأولى للبدء',
      'wa.mkt.name': 'اسم الحملة',
      'wa.mkt.body': 'الرسالة',
      'wa.mkt.nameRequired': 'اسم الحملة مطلوب',
      'wa.mkt.bodyRequired': 'نص الرسالة مطلوب',
      'wa.mkt.nameHint': 'استخدم {{name}} لتخصيص الرسالة',
      'wa.mkt.attachImage': 'إرفاق صورة',
      'wa.mkt.removeImage': 'إزالة الصورة',
      'wa.mkt.changeImage': 'تغيير الصورة',
      'wa.mkt.imageAttached': 'تم إرفاق الصورة',
      'wa.mkt.preview': 'معاينة',
      'wa.mkt.previewSampleName': 'أحمد',
      'wa.mkt.recipients': 'المستلمون',
      'wa.mkt.searchRecipients': 'البحث في العملاء...',
      'wa.mkt.selectAllFiltered': 'اختيار الكل',
      'wa.mkt.deselectAllFiltered': 'إلغاء اختيار الكل',
      'wa.mkt.selectedCount': 'تم اختيار {{count}}',
      'wa.mkt.continue': 'متابعة',
      'wa.mkt.cancel': 'إلغاء',
      'wa.mkt.composerTitle': 'حملة جديدة',
      'wa.mkt.confirmTitle': 'تأكيد الحملة',
      'wa.mkt.confirmRecipients': 'سيتم الإرسال إلى {{count}} عميل',
      'wa.mkt.autoExcluded': 'سيتم استبعاد الأرقام غير الصالحة أو المنسحبة أو المكررة تلقائياً',
      'wa.mkt.guardrail': 'تُرسل الرسائل على فاصل 10–25 ثانية، بحد أقصى 200 رسالة يومياً لحماية رقمك',
      'wa.mkt.maxRecipients': 'الحد الأقصى 50 مستلمًا لكل حملة — قسّم الجمهور الأكبر على عدة أيام لحماية رقمك.',
      'wa.mkt.createCampaign': 'إنشاء الحملة',
      'wa.mkt.createdSuccess': 'تم إنشاء الحملة',
      'wa.mkt.createFailed': 'فشل إنشاء الحملة',
      'wa.mkt.invalidCustomer': 'أحد العملاء المختارين أو أكثر لا يمكنه استلام رسائل واتساب',
      'wa.mkt.send': 'إرسال',
      'wa.mkt.pause': 'إيقاف مؤقت',
      'wa.mkt.cancelCampaign': 'إلغاء الحملة',
      'wa.mkt.sendFailed': 'فشل إرسال الحملة',
      'wa.mkt.pauseFailed': 'فشل إيقاف الحملة مؤقتاً',
      'wa.mkt.cancelFailed': 'فشل إلغاء الحملة',
      'wa.mkt.sessionDisconnected': 'جلسة واتساب غير متصلة',
      'wa.mkt.sessionDisconnectedTooltip': 'جلسة واتساب غير متصلة',
      'wa.mkt.status.draft': 'مسودة',
      'wa.mkt.status.sending': 'جارٍ الإرسال',
      'wa.mkt.status.paused': 'متوقفة مؤقتاً',
      'wa.mkt.status.completed': 'مكتملة',
      'wa.mkt.status.cancelled': 'ملغاة',
      'wa.mkt.status.failed': 'فاشلة',
      'wa.mkt.queued': 'في الانتظار',
      'wa.mkt.sent': 'تم الإرسال',
      'wa.mkt.failed': 'فشل',
      'wa.mkt.skipped': 'تم التخطي',
      'wa.mkt.progress': 'التقدم',
      'wa.mkt.recipientsTitle': 'المستلمون',
      'wa.mkt.recipientsCapped': 'عرض أول {{count}}',
      'wa.mkt.noRecipients': 'لا يوجد مستلمون',
      'wa.mkt.loadingRecipients': 'جارٍ تحميل المستلمين...',
      'wa.mkt.createdAt': 'تاريخ الإنشاء',
      'wa.mkt.back': 'رجوع',
      'wa.mkt.accessDenied': 'تم رفض الوصول',
      'wa.mkt.accessDeniedDesc': 'ليس لديك صلاحية استخدام تسويق واتساب',
      'wa.mkt.recipientStatus.queued': 'في الانتظار',
      'wa.mkt.recipientStatus.sending': 'جارٍ الإرسال',
      'wa.mkt.recipientStatus.sent': 'تم الإرسال',
      'wa.mkt.recipientStatus.failed': 'فشل',
      'wa.mkt.recipientStatus.skipped': 'تم التخطي',
      'wa.mkt.recipientStatus.cancelled': 'ملغى',
      'wa.mkt.recipientStatus.invalid_phone': 'رقم غير صالح',
      'wa.mkt.recipientStatus.duplicate': 'مكرر',
      'wa.mkt.recipientStatus.opted_out': 'تم إلغاء الاشتراك',
      // AGY4/F1 — نتيجة التسليم غير معروفة فعلياً: ليست فشلاً مؤكداً ولا تسليماً
      // مؤكداً. الصياغة تتجنب الإيحاء بإعادة الإرسال؛ هذه الرسائل لا يُعاد
      // إرسالها تلقائياً أبداً.
      'wa.mkt.recipientStatus.uncertain': 'غير معروف',
      'wa.mkt.uncertainCount_zero': 'لا يوجد مستلمون بنتيجة تسليم غير معروفة',
      'wa.mkt.uncertainCount_one': 'مستلم واحد بنتيجة تسليم غير معروفة',
      'wa.mkt.uncertainCount_two': 'مستلمان بنتيجة تسليم غير معروفة',
      'wa.mkt.uncertainCount_few': '{{count}} مستلمين بنتيجة تسليم غير معروفة',
      'wa.mkt.uncertainCount_many': '{{count}} مستلماً بنتيجة تسليم غير معروفة',
      'wa.mkt.uncertainCount_other': '{{count}} مستلم بنتيجة تسليم غير معروفة',
      'wa.mkt.uncertainHelp':
        'قد تكون هذه الرسائل وصلت إلى العميل وقد لا تكون. لا تُحتسب ضمن المُرسَل ولا يُعاد إرسالها تلقائياً.',

      // T9 — inbox toolbar, new-message compose, settings page, mobile layout
      'wa.newMessage': 'رسالة جديدة',
      'wa.newMessageTitle': 'رسالة جديدة',
      'wa.pickContact': 'اختر جهة اتصال',
      'wa.startChat': 'بدء المحادثة',
      // T11 — تبديل وضع نافذة الرسالة الجديدة (عميل حالي أو رقم جديد)
      'wa.fromCustomers': 'من العملاء',
      'wa.newNumber': 'رقم جديد',
      'wa.contactName': 'الاسم',
      'wa.phoneNumber': 'رقم الهاتف',
      'wa.phoneNumberPlaceholder': '05xxxxxxxx',
      'wa.statusConnected': 'متصل',
      'wa.statusDisconnected': 'غير متصل',
      'wa.manageInSettings': 'إدارة من الإعدادات',
      'wa.invalidPhone': 'لا يوجد رقم هاتف صالح لهذا العميل',
      'wa.startChatFailed': 'تعذر بدء المحادثة',
      'wa.openActivity': 'فتح النشاط',
      'wa.onCredit': 'آجل',
      'wa.routeTransactional': 'إرسال الرسائل والإشعارات التلقائية عبر واتساب عند الاتصال (وإلا عبر الرسائل النصية)',
      'wa.routeTransactionalHelp': 'عند الإيقاف، تُرسل جميع الرسائل التلقائية (عروض الأسعار، الفواتير، التذكيرات) دائمًا عبر الرسائل النصية، حتى لو كان واتساب متصلاً.',
      'wa.cloud.accessToken': 'رمز وصول مستخدم النظام (Permanent Access Token)',
      'wa.cloud.allFieldsRequired': 'يرجى تعبئة جميع الحقول المطلوبة',
      'wa.cloud.appSecret': 'المفتاح السري للتطبيق (App Secret)',
      'wa.cloud.confirmDeleteBtn': 'إلغاء الربط',
      'wa.cloud.confirmDeleteDesc': 'هل أنت متأكد من إلغاء الربط؟ سيتم إيقاف حملات التسويق الجارية والتحويل إلى الرسائل النصية القصيرة SMS.',
      'wa.cloud.confirmDeleteTitle': 'إلغاء ربط WhatsApp Cloud API',
      'wa.cloud.connect': 'ربط واجهة واتساب السحابية',
      'wa.cloud.connectButton': 'ربط الحساب',
      'wa.cloud.connectionStatusTitle': 'حالة الربط السحابي',
      'wa.cloud.copied': 'تم النسخ!',
      'wa.cloud.copy': 'نسخ',
      'wa.cloud.disconnect': 'فصل الاتصال السحابي',
      'wa.cloud.disconnectFailed': 'فشل إلغاء ربط الواجهة السحابية',
      'wa.cloud.disconnectSuccess': 'تم فصل الاتصال السحابي بنجاح',
      'wa.cloud.displayPhone': 'رقم الهاتف الظاهر',
      'wa.cloud.helpText': 'رقم الهاتف المسجل عبر واجهة واتساب السحابية (Meta Cloud API) لا يمكن ربطه عبر رمز الاستجابة السريعة (QR) في الوقت نفسه. يرجى التأكد من تخصيص الرقم للواجهة السحابية.',
      'wa.cloud.helpTitle': 'تنبيه هام',
      'wa.cloud.invalidCredentials': 'بيانات الاعتماد غير صالحة: تعذر التحقق من حساب واتساب للأعمال',
      'wa.cloud.invalidExplanation': 'انتهت صلاحية رمز الوصول أو تم إلغاء الأذونات من مدير أعمال Meta. يُرجى إدخال رمز وصول دائم وصالح أدناه لاستئناف الرسائل.',
      'wa.cloud.phoneInUse': 'رقم الهاتف هذا مرتبط مسبقاً بحساب منشأة أخرى.',
      'wa.cloud.phoneNumberId': 'معرّف رقم الهاتف (Phone Number ID)',
      'wa.cloud.qrConflict': 'توجد جلسة ربط عبر QR قيد الإجراء حالياً. يرجى إلغاؤها أولاً قبل تفعيل الاتصال السحابي.',
      'wa.cloud.saveFailed': 'فشل حفظ إعدادات الواجهة السحابية',
      'wa.cloud.saveSuccess': 'تم الاتصال بواجهة واتساب السحابية بنجاح',
      'wa.cloud.secretHint': 'يستخدم للتحقق من صحة توقيع الـ Webhook',
      'wa.cloud.setupTitle': 'بيانات ربط WhatsApp Cloud API',
      'wa.cloud.statusActive': 'نشط',
      'wa.cloud.title': 'إعدادات واجهة واتساب السحابية (Meta Cloud API)',
      'wa.cloud.tokenExpired': 'رمز الوصول منتهي الصلاحية',
      'wa.cloud.tokenHint': 'لا يتم تخزينه بنص صريح ولا يُعرض مجدداً بعد الحفظ',
      'wa.cloud.updateButton': 'تحديث البيانات',
      'wa.cloud.updateCredentialsTitle': 'تحديث بيانات الاعتماد والرمز',
      'wa.cloud.verifiedName': 'الاسم المعتمد',
      'wa.cloud.verifyToken': 'رمز التحقق (Verify Token)',
      'wa.cloud.wabaId': 'معرّف حساب واتساب للأعمال (WABA ID)',
      'wa.cloud.webhookSection': 'إعدادات خطاف الويب في Meta (Webhook)',
      'wa.cloud.webhookUrl': 'رابط خطاف الويب (Webhook URL)',
      'wa.inbox.cloudTokenExpired': 'انتهت صلاحية رمز الوصول إلى Meta',
      'wa.inbox.windowClosed': 'انتهت نافذة الـ 24 ساعة المتاحة للرد على العميل. يمكنك الرد فقط باستخدام قالب رسائل معتمد من Meta أو انتظار رسالة جديدة من العميل.',
      'wa.inbox.windowClosedPlaceholder': 'نافذة الرد مغلقة — بانتظار رسالة من العميل أو استخدم قالباً معتمداً',
      'wa.inbox.windowClosedToast': 'لا يمكن الرد: نافذة خدمة العملاء (24 ساعة) مغلقة',
      'wa.manageConnection': 'إدارة الاتصال',
      'wa.marketingOptIn': 'العميل وافق على تلقي العروض عبر واتساب',
      'wa.marketingOptInHint': 'رسائل ترويجية من المركز عبر واتساب، ولا يؤثر ذلك في رسائل الخدمة.',
      'wa.intakeOptIn': 'أوافق على تلقي العروض من المركز عبر واتساب',
      'wa.intakeOptInHint': 'اختياري، ويمكنك إيقافها في أي وقت بالرد بكلمة «إيقاف».',
      'wa.mkt.approval.approved': 'معتمد من Meta',
      'wa.mkt.approval.pending': 'بانتظار موافقة Meta',
      'wa.mkt.approval.pendingBanner': 'تستخدم هذه الحملة قالب واتساب سحابي قيد المراجعة من Meta. سيتاح الإرسال فور الاعتماد.',
      'wa.mkt.line.mainLine': 'الخط الرئيسي',
      'wa.mkt.line.sendFrom': 'الإرسال من',
      'wa.mkt.line.selectPlaceholder': 'اختر خط واتساب',
      'wa.mkt.line.lineRequired': 'يرجى اختيار خط واتساب',
      'wa.mkt.approval.rejected': 'مرفوض من Meta',
      'wa.mkt.approval.rejectedTitle': 'قالب Meta مرفوض',
      'wa.mkt.cloudTemplateNotApproved': 'قالب الحملة لم يعتمد بعد من Meta',
      'wa.mkt.uncertainCount': 'غير مؤكدة: {{count}}',
      'wa.mode.cloud': 'واجهة واتساب للأعمال (Meta)',
      'wa.mode.branches': 'الفروع وصندوق الوارد',
      'wa.mode.tablist': 'أقسام إعدادات واتساب',
      'wa.multi.extraNumbersHeading': 'أرقام إضافية',
      'wa.multi.noQrNumbers': 'لم يُربط أي هاتف إضافي بعد.',
      'wa.multi.noMetaNumbers': 'لم تُضف أرقام Meta بعد.',
      'wa.multi.mainLineHeading': 'الخط الرئيسي',
      'wa.multi.extraHeading': 'حسابات وأرقام إضافية',
      'wa.multi.qrNumbersTitle': 'هواتف مربوطة (QR)',
      'wa.multi.qrNumbersDesc': 'هواتف إضافية مربوطة برمز QR، ويمكن لكل فرع الإرسال من أحدها.',
      'wa.multi.metaNumbersTitle': 'أرقام Meta',
      'wa.multi.metaNumbersDesc': 'أرقام الهواتف التابعة لحسابات Meta.',
      'wa.mode.qr': 'جهاز مرتبط (QR)',
      'wa.notifyChannelCloud': 'واتساب (واجهة الأعمال) والرسائل النصية',
      'wa.notifyChannelCloudHint': 'تُرسل التحديثات عبر رقم واجهة واتساب للأعمال الخاص بالورشة',
      'wa.routingTitle': 'توجيه الرسائل',
      'wa.template.add': 'إضافة',
      'wa.template.empty': 'لا توجد قوالب بعد.',
      'wa.template.filter.added': 'المضافة',
      'wa.template.filter.all': 'الكل',
      'wa.template.filter.notAdded': 'غير المضافة',
      'wa.template.filterLabel': 'تصفية القوالب',
      'wa.template.loading': 'جارٍ تحميل القوالب...',
      'wa.template.state.approved': 'معتمد',
      'wa.template.state.not_added': 'غير مضاف',
      'wa.template.state.pending': 'بانتظار موافقة Meta',
      'wa.template.state.rejected': 'مرفوض',
      'wa.template.title': 'قوالب رسائل Meta',
      'wa.template.tryAgain': 'إعادة المحاولة',
      'wa.cloud.help.open': 'من أين أحصل عليه؟',
      'wa.cloud.webhookAfterSave':
        'بعد حفظ هذه القيم الأربع يعرض MotorMind هنا رابط Webhook ورمز التحقق لتلصقهما في إعدادات Meta.',
      'wa.cloud.help.wabaId': 'من Meta for Developers: افتح تطبيقك ثم WhatsApp ثم API Setup، وانسخ «WhatsApp Business Account ID». تجده أيضًا في إعدادات الأعمال ضمن الحسابات ثم حسابات واتساب.',
      'wa.cloud.help.phoneNumberId': 'في صفحة API Setup نفسها، تحت رقم الإرسال (From)، انسخ «Phone number ID». وهو معرّف رقمي طويل، وليس رقم الهاتف نفسه.',
      'wa.cloud.help.accessToken': 'من إعدادات الأعمال في Meta: المستخدمون ثم مستخدمو النظام، أضف مستخدم نظام بصلاحية مسؤول وامنحه تطبيقك وحساب واتساب. ثم اختر «إنشاء رمز جديد» بمدة صلاحية «أبدًا» مع الصلاحيتين whatsapp_business_messaging وwhatsapp_business_management. لا تستخدم الرمز المؤقت من صفحة API Setup، فصلاحيته تنتهي بعد 24 ساعة.',
      'wa.cloud.help.appSecret': 'من Meta for Developers: افتح تطبيقك ثم إعدادات التطبيق (App settings) ثم أساسي (Basic)، واضغط «إظهار» بجانب «App secret».',
      'wa.cloud.help.webhookUrl': 'ألصقه في Meta for Developers: تطبيقك ثم WhatsApp ثم Configuration ثم Webhook ثم تعديل، في خانة «Callback URL». ثم من قسم Webhook fields اضغط Manage واشترك في الحقول messages وmessage_template_status_update وtemplate_category_update كلها.',
      'wa.cloud.help.verifyToken': 'ألصقه في خانة «Verify token» في نافذة Webhook نفسها، ثم اضغط Verify and save.',
      'wa.template.addsBoth': 'زر «إضافة» يرسل النسختين العربية والإنجليزية معًا إلى حساب Meta الخاص بك.',
      'wa.template.lang.ar': 'العربية',
      'wa.template.lang.en': 'الإنجليزية',
      'wa.template.preview.open': 'معاينة',
      'wa.template.preview.title': 'معاينة القالب',
      'wa.template.preview.examplesNote': 'القيم المظللة أمثلة، وتُستبدل في كل رسالة ببيانات العميل الفعلية.',
      'wa.template.preview.name': 'الاسم في Meta',
      'wa.template.preview.category': 'التصنيف',
      'wa.template.preview.utility': 'خدمية',
      'wa.template.preview.marketing': 'تسويقية',
      'wa.settingsSaved': 'تم حفظ الإعدادات',
      'wa.settingsSaveFailed': 'تعذر حفظ الإعدادات',

      // wa.multi.* (Task 10a1: WhatsApp Multi-Number Settings)
      'wa.multi.accountsTitle': 'حسابات Meta',
      'wa.multi.cancel': 'إلغاء',
      'wa.multi.save': 'حفظ',
      'wa.multi.status.active': 'نشط',
      'wa.multi.status.disconnected': 'غير متصل',
      'wa.multi.status.invalid': 'غير صالح',
      'wa.multi.status.retired': 'موقوف',
      'wa.multi.accountsDesc': 'ربط حساب أو أكثر من حسابات واتساب للأعمال (WABA).',
      'wa.multi.addAccount': 'إضافة حساب Meta',
      'wa.multi.accountLabel': 'تسمية الحساب',
      'wa.multi.accountLabelPlaceholder': 'مثال: الفرع الرئيسي، فرع الرياض',
      'wa.multi.accountAdded': 'تمت إضافة حساب Meta بنجاح',
      'wa.multi.updateCredentials': 'تحديث بيانات الاعتماد',
      'wa.multi.credentialsUpdated': 'تم تحديث بيانات الاعتماد بنجاح',
      'wa.multi.retrySubscribe': 'إعادة محاولة الاشتراك',
      'wa.multi.registerTemplates': 'تسجيل القوالب',
      'wa.multi.templatesRegistered': 'تم تسجيل القوالب بنجاح',
      'wa.multi.removeAccount': 'إزالة الحساب',
      'wa.multi.removeConfirmTitle': 'إزالة حساب Meta',
      'wa.multi.removeConfirmMsg': 'هل أنت متأكد من إزالة حساب Meta هذا؟ سيتم فصل الأرقام التابعة له.',
      'wa.multi.accountRemoved': 'تمت إزالة حساب Meta',
      'wa.multi.removeFailed': 'تعذر إزالة الحساب',
      'wa.multi.removing': 'جارٍ الإزالة...',
      'wa.multi.removed': 'تمت الإزالة',
      'wa.multi.noAccounts': 'لم تتم إضافة حسابات Meta بعد.',
      'wa.multi.numbersTitle': 'أرقام الهواتف',
      'wa.multi.numbersDesc': 'إدارة أرقام واتساب عبر حسابات Meta والهواتف المربوطة برمز QR.',
      'wa.multi.addMetaNumber': 'إضافة رقم Meta',
      'wa.multi.addNumberTitle': 'إضافة رقم هاتف تحت حساب Meta',
      'wa.multi.selectAccount': 'حساب Meta',
      'wa.multi.numberLabel': 'تسمية الرقم',
      'wa.multi.numberLabelPlaceholder': 'مثال: مستشار الخدمة، الاستقبال',
      'wa.multi.phoneNumberId': 'معرّف رقم الهاتف',
      'wa.multi.phoneNumberIdPlaceholder': 'مثال: 102938475610293',
      'wa.multi.numberAdded': 'تمت إضافة رقم الهاتف بنجاح',
      'wa.multi.linkPhone': 'ربط هاتف',
      'wa.multi.linkPhoneTitle': 'ربط هاتف عبر رمز QR',
      'wa.multi.phoneLinked': 'تم إنشاء رقم QR للهاتف',
      'wa.multi.markPrimary': 'تعيين كرقم رئيسي',
      'wa.multi.primary': 'رئيسي',
      'wa.multi.default': 'افتراضي',
      'wa.multi.primarySet': 'تم تحديث الرقم الرئيسي',
      'wa.multi.retire': 'إيقاف الرقم',
      'wa.multi.retireConfirmTitle': 'إيقاف رقم الهاتف',
      'wa.multi.retireConfirmMsg': 'هل أنت متأكد من إيقاف هذا الرقم؟ سيلزم إعادة تعيين الفروع التي تستخدمه.',
      'wa.multi.retired': 'تم إيقاف رقم الهاتف',
      'wa.multi.retireFailed': 'تعذر إيقاف الرقم',
      'wa.multi.branchesUsing': 'الفروع المستخدمة لهذا الخط',
      'wa.multi.noBranches': 'لا يوجد',
      'wa.multi.qrCardTitle': 'جلسة رمز QR لواتساب',
      'wa.multi.qrModalTitle': 'مسح رمز QR',
      'wa.multi.scanQr': 'مسح رمز QR',
      'wa.multi.manageSession': 'إدارة الجلسة',
      'wa.multi.noNumbers': 'لم يتم ضبط أرقام واتساب بعد.',
      'wa.multi.allFieldsRequired': 'يرجى ملء جميع الحقول المطلوبة.',
      'wa.multi.copyWebhook': 'نسخ رابط Webhook',
      'wa.multi.copyToken': 'نسخ رمز التحقق',
      'wa.multi.copied': 'تم النسخ إلى الحافظة',
      'wa.multi.connected': 'تم الاتصال بنجاح',
      'wa.multi.webhookSection': 'إعدادات Webhook',
      'wa.multi.webhookUrl': 'رابط Webhook',
      'wa.multi.verifyToken': 'رمز التحقق',
      'wa.multi.active': 'نشط',
      'wa.multi.subscribing': 'جارٍ الاشتراك...',
      'wa.multi.invalid': 'غير صالح',
      'wa.mkt.addContact': 'إضافة شخص غير موجود في قائمة عملائك',
      'wa.mkt.contactName': 'الاسم',
      'wa.mkt.contactPhone': 'رقم الهاتف',
      'wa.mkt.addContactBtn': 'إضافة',
      'wa.mkt.newTag': 'جديد',
      'wa.mkt.duplicateContact': 'تمت إضافته مسبقًا',

      // T12 — الذكاء الاصطناعي في واتساب (كتابة الحملات + تحليل المحادثات)
      'wa.ai.write': 'الكتابة بالذكاء الاصطناعي',
      'wa.ai.brief': 'ما الذي تروّج له؟',
      'wa.ai.briefPlaceholder': 'صف العرض — ما الذي تروّجه، السعر أو الخصم، الموعد النهائي...',
      'wa.ai.audience': 'الجمهور المستهدف (اختياري)',
      'wa.ai.tone': 'النبرة',
      'wa.ai.tonePro': 'احترافية',
      'wa.ai.toneFriendly': 'ودّية',
      'wa.ai.tonePromo': 'ترويجية',
      'wa.ai.language': 'اللغة',
      'wa.ai.languageBoth': 'كلاهما',
      'wa.ai.generate': 'إنشاء',
      'wa.ai.regenerate': 'إعادة الإنشاء',
      'wa.ai.copyApplied': 'تم تطبيق نص الذكاء الاصطناعي على الرسالة',
      'wa.ai.usedImage': 'تم استخدام محتوى الصورة في الرسالة',
      'wa.ai.imageWillBeUsed': 'سيقرأ الذكاء الاصطناعي هذه الصورة ويربط الرسالة بها — وستُرسل أيضًا مع الحملة',
      'wa.ai.attachImageHint': 'أرفق الملصق/الصورة التي سترسلها — سيقرؤها الذكاء الاصطناعي ويربط الرسالة بها',
      'wa.ai.quotaExceeded': 'تم استنفاد حصة الذكاء الاصطناعي لهذا الشهر',
      'wa.ai.failed': 'فشل طلب الذكاء الاصطناعي — يرجى المحاولة مرة أخرى',
      'wa.ai.analyze': 'تحليل بالذكاء الاصطناعي',
      'wa.ai.analysisTitle': 'تحليل المحادثة بالذكاء الاصطناعي',
      'wa.ai.rerun': 'إعادة التشغيل',

      whatsappConnect: 'ربط جلسة واتساب',
      whatsappChat: 'استخدام ميزات محادثة واتساب',
      whatsappMarketing: 'استخدام حملات تسويق واتساب',

      // Staff notification keys (staffNotificationService)
      'ntf.serviceApproved.title': 'تمت الموافقة على طلب الخدمة',
      'ntf.serviceApproved.message': 'تمت الموافقة على طلبك للخدمة: {{service_name}}',
      'ntf.serviceRejected.title': 'تم رفض طلب الخدمة',
      'ntf.serviceRejected.message': 'تم رفض طلبك للخدمة {{service_name}}: {{reason}}',
      'ntf.partApproved.title': 'تمت الموافقة على طلب القطعة',
      'ntf.partApproved.message': 'تمت الموافقة على طلبك: {{quantity}}× {{part_name}}',
      'ntf.partRejected.title': 'تم رفض طلب القطعة',
      'ntf.partRejected.message': 'تم رفض طلبك: {{quantity}}× {{part_name}}',
      'ntf.issueAnswered.title': 'تم الرد على بلاغك',
      'ntf.issueAnswered.message': 'بلاغك ($t(issueTypes.{{issue_type}})) أصبحت حالته الآن: $t(ntf.issueStatus.{{status}})',
      'ntf.issueStatus.open': 'مفتوح',
      'ntf.issueStatus.in_progress': 'قيد المعالجة',
      'ntf.issueStatus.resolved': 'محلول',
      'ntf.issueStatus.closed': 'مغلق',

      // Vehicle catalog super-admin bell notification (vehicleCatalogMatcher.js,
      // fires once when an unmatched make/model crosses 5 scan/manual hits).
      'ntf.onboardingLead.submitted.title': 'طلب انضمام جديد',
      'ntf.onboardingLead.submitted.message': '{{name}} أرسل طلب الانضمام — بانتظار مراجعتك',
      'ntf.onboardingLead.resubmitted.title': 'تحديث على طلب انضمام',
      'ntf.onboardingLead.resubmitted.message': '{{name}} أكمل البيانات التي طلبتها — بانتظار مراجعتك',
      'ntf.catalogMiss.title': 'كتالوج المركبات: إدخال غير مطابق',
      'ntf.catalogMiss.message': '"{{raw}}" ظهرت {{count}} مرات دون مطابقة في الكتالوج — راجعها',

      // Vehicle catalog — VehicleModal make/model matching UX
      'catalog.ocrUnmatchedHint': 'المسح: «{{raw}}» — اختر يدوياً',
      'catalog.useFreeText': 'استخدام «{{text}}»',

      // Voice → job card (spec docs/superpowers/specs/2026-07-18-voice-job-card-design.md)
      'voice.recorder.record': 'تسجيل',
      'voice.recorder.stop': 'إيقاف',
      'voice.recorder.recording': 'جارٍ التسجيل…',
      'voice.recorder.use': 'استخدام هذا التسجيل',
      'voice.recorder.discard': 'حذف',
      'voice.recorder.processing': 'جارٍ تحليل التسجيل…',
      'voice.recorder.micDenied': 'تم رفض الوصول إلى الميكروفون أو أنه غير متاح.',
      'voice.recorder.notSupported': 'تسجيل الصوت غير مدعوم على هذا الجهاز/المتصفح.',
      'voice.errors.noSpeech': 'لم يُسمع أي كلام في التسجيل. حاول مرة أخرى.',
      'voice.errors.quotaExceeded': 'تم استنفاد حصة الذكاء الاصطناعي الشهرية لهذه الشركة.',
      'voice.errors.voiceQuotaExceeded': 'تم استنفاد حد الصوت لهذا الشهر — لا يزال بإمكانك الكتابة.',
      'voice.errors.sttFailed': 'فشل تحويل الصوت إلى نص. حاول مرة أخرى.',
      'voice.errors.extractionFailed': 'تعذّر استخراج بيانات منظمة من التسجيل.',
      'voice.errors.invalidInput': 'تعذّرت قراءة التسجيل.',
      'voice.errors.generic': 'فشل الإدخال الصوتي. حاول مرة أخرى.',
      'voice.jobCard.button': 'سجّل الوصف',
      'voice.jobCard.modalTitle': 'الإدخال الصوتي — بطاقة عمل جديدة',
      'voice.jobCard.transcriptToggle': 'عرض النص المفرغ',
      'voice.jobCard.filled': 'تم تعبئة النموذج من التسجيل — راجعه قبل الحفظ.',
      'voice.jobCard.unmatchedHint': 'المسموع: «{{raw}}» — لا توجد مطابقة، اختر يدوياً',
      'voice.jobCard.servicesLabel': 'الخدمات المطلوبة',
      'voice.findings.button': 'أملِ الملاحظة',
      'voice.findings.modalTitle': 'الإدخال الصوتي — الملاحظات',
      'voice.findings.transcriptToggle': 'عرض النص المفرغ',
      'voice.findings.draftsTitle': 'الملاحظات المستخرجة ({{count}})',
      'voice.findings.useDraft': 'استخدام هذه المسودة',
      'voice.findings.applied': 'تم تطبيق المسودة — أضف القطع/الخدمات ثم أرسل.',
      'voice.findings.none': 'لم يتم استخراج أي ملاحظات من التسجيل.',
      'voice.dictate.button': 'أملِ بصوتك',
      'voice.dictate.modalTitle': 'الإدخال الصوتي',
      'voice.dictate.appended': 'تم تعبئة السؤال من التسجيل — راجعه قبل الإرسال.',
      'voice.askByVoice': 'اسأل بالصوت',

      // Smart Assistant (spec docs/superpowers/specs/2026-07-18-voice-assistant-design.md,
      // v2 amendments: renamed from "المساعد الصوتي" — chat-agent UI, strictly MotorMind-scoped)
      'assistant.confirm.needsApproval': 'يتطلب تأكيدك قبل التنفيذ',
      'sa.permissionHidesElement': 'الصلاحية تخفي العنصر — لا تعطّله',
      'assistant.nav': 'المساعد الذكي',
      'assistant.title': 'المساعد الذكي',
      'assistant.subtitle': 'اسأل بصوتك أو اكتب — القراءة تجاوب فوراً، والإجراءات تسألك قبل التنفيذ.',
      'assistant.connected': 'متصل',
      'assistant.viaVoice': 'بالصوت',
      'assistant.viaText': 'كتابةً',
      'assistant.hint.idle': 'اضغط وتكلم',
      'assistant.hint.listening': 'أستمع… اضغط للإيقاف',
      'assistant.hint.thinking': 'أفكر…',
      'assistant.thinking': 'يفكر…',
      'assistant.heard': 'سمعت: «{{text}}»',
      'assistant.typeInstead': 'أو اكتب سؤالك',
      'assistant.typePlaceholder': 'اكتب رسالة للمساعد الذكي…',
      'assistant.send': 'إرسال',
      'assistant.empty.title': 'كيف يمكنني مساعدتك؟',
      'assistant.empty.subtitle': 'اسأل عن بطاقات العمل، العملاء، المركبات، المواعيد، أو وضعك المالي.',
      'assistant.empty.chip1': 'اعرض بطاقات العمل المفتوحة',
      'assistant.empty.chip2': 'ما وضعنا المالي هذا الشهر؟',
      'assistant.empty.chip3': 'مواعيد اليوم',
      'assistant.empty.chip4': 'ما رصيد مخزون فلتر الزيت؟',
      'assistant.empty.chip5': 'أنشئ عرض سعر',
      'assistant.empty.chip6': 'من في إجازة؟',
      'assistant.outOfScope': 'يمكنني مساعدتك في شؤون موتورمايند فقط — بطاقات العمل، والعملاء، والمركبات، والمواعيد، والوضع المالي.',
      'assistant.featureDisabled.title': 'المساعد الذكي غير مفعّل',
      'assistant.featureDisabled.body': 'تواصل مع مدير الحساب لتفعيل المساعد الذكي لشركتك.',
      'assistant.confirm.execute': 'تنفيذ',
      'assistant.confirm.cancel': 'إلغاء',
      'assistant.confirm.executing': 'جارٍ التنفيذ…',
      'assistant.confirm.done': 'تم',
      'assistant.confirm.failed': 'فشل — حاول مرة أخرى',
      'assistant.confirm.dismissed': 'لم يتم — انتقلت إلى طلب آخر',
      'assistant.history.title': 'المحادثات السابقة',
      'assistant.history.newConversation': 'محادثة جديدة',
      'assistant.history.empty': 'لا توجد محادثات سابقة بعد.',
      'assistant.history.untitled': 'محادثة بدون عنوان',
      'assistant.history.readOnlyNote':
        'هذه محادثة محفوظة — تُعرض كسجل للقراءة فقط. أي شيء تكتبه الآن يكمل المحادثة.',
      'assistant.history.deleteButton': 'حذف',
      'assistant.history.deleteConfirmTitle': 'حذف هذه المحادثة؟',
      'assistant.history.deleteConfirmBody': 'لا يمكن التراجع عن هذا الإجراء.',
      'assistant.history.deleteFailed': 'تعذّر حذف هذه المحادثة.',
      'assistant.history.loadFailed': 'تعذّر تحميل سجل المحادثات.',
      'assistant.history.loadConversationFailed': 'تعذّر فتح هذه المحادثة.',
      'assistant.history.jobCardLabel': 'تم البحث عن أمر العمل {{desc}}',
      'assistant.history.customerLabel': 'تم البحث عن العميل {{desc}}',
      'assistant.history.vehicleLabel': 'تم البحث عن المركبة {{desc}}',
      'assistant.history.partLabel': 'تم البحث عن القطعة {{desc}}',
      'assistant.history.lowStockLabel': 'تم عرض {{count}} قطعة منخفضة المخزون',
      'assistant.history.customerHistoryLabel': 'تم البحث عن سجل العميل {{desc}}',
      'assistant.history.quotationLabel': 'تم البحث عن عرض السعر {{desc}}',
      'assistant.history.invoiceLabel': 'تم البحث عن الفاتورة {{desc}}',
      'assistant.history.supplierLabel': 'تم البحث عن المورد {{desc}}',
      'assistant.history.employeeLabel': 'تم البحث عن الموظف {{desc}}',
      'assistant.history.financialLabel': 'تم عرض الوضع المالي',
      'assistant.history.taxLabel': 'تم عرض الوضع الضريبي',
      'assistant.history.vatAdvisorLabel': 'استشارة الضريبة',
      'assistant.history.appointmentsLabel': 'تم عرض {{count}} موعد',
      'assistant.history.picklistLabel': 'تم عرض {{count}} خيارات للاختيار من بينها',
      'assistant.history.confirmLabel': '{{title}} — {{status}}',
      'assistant.history.statusDone': 'تم التنفيذ',
      'assistant.history.statusFailed': 'لم يكتمل',
      'assistant.history.statusPending': 'تُرك دون تأكيد',
      'assistant.history.genericLabel': '(لا توجد تفاصيل محفوظة)',
      'assistant.notFound.title': 'لا توجد نتائج',
      'assistant.notFound.body': 'لم أعثر على أي نتيجة لـ «{{text}}» — جرّب اسم العميل أو رقم بطاقة العمل أو رقم اللوحة.',
      'assistant.notFound.emptyList': 'لا يوجد {{label}} حالياً.',
      'assistant.pickOne': 'أيَّها تقصد؟',
      'assistant.results.jobCard': 'بطاقة العمل',
      'assistant.results.jobCardList': 'لديك {{count}} {{label}}.',
      'assistant.results.customer': 'العميل',
      'assistant.results.vehicle': 'المركبة',
      'assistant.results.financial': 'الوضع المالي',
      'assistant.results.financialIntro': 'هذا وضعك المالي الآن:',
      'assistant.results.unavailable': 'غير متاح حالياً.',
      'assistant.results.appointments': 'مواعيد اليوم',
      'assistant.results.noAppointments': 'لا توجد مواعيد اليوم.',
      'assistant.results.permissionHidden': 'مخفي — لا تملك الصلاحية',
      'assistant.statusLabel.open': 'بطاقة عمل مفتوحة',
      'assistant.statusLabel.undelivered': 'مركبة لم تُسلَّم بعد',
      'assistant.statusLabel.generic': 'بطاقة عمل',
      'assistant.errors.actionFailed': 'حدث خطأ — حاول مرة أخرى.',
      'assistant.actions.createJobCard': 'افتح نموذج بطاقة عمل جديدة',
      'assistant.actions.createJobCardBody': 'ابدأ بطاقة عمل جديدة واستخدم الإدخال الصوتي هناك لتعبئتها.',
      'assistant.actions.open': 'فتح',
      'assistant.actions.createAppointment': 'إنشاء موعد',
      'assistant.actions.holdJobCard': 'إيقاف بطاقة العمل مؤقتاً',
      'assistant.actions.resumeJobCard': 'استئناف بطاقة العمل',
      'assistant.actions.assignTask': 'إسناد مهمة',
      'assistant.actions.sendPaymentLink': 'إرسال رابط دفع',
      'assistant.fields.customer': 'العميل',
      'assistant.fields.time': 'الوقت',
      'assistant.fields.service': 'الخدمة',
      'assistant.fields.jobCard': 'بطاقة العمل',
      'assistant.fields.reason': 'السبب',
      'assistant.fields.task': 'المهمة',
      'assistant.fields.staff': 'الموظف',
      'assistant.fields.amount': 'المبلغ',
      'assistant.fields.originalReceipt': 'السند الأصلي',
      'assistant.fields.availableCredit': 'الرصيد المتاح',
      'assistant.needMoreInfo': 'تعذَّرت مطابقتها بسجل فعلي — افتح الصفحة ونفِّذها يدوياً.',
      'assistant.confidence.low': 'لم أتبيَّن المقصود بدقة — سأعدُّ ذلك سؤالاً.',
      'assistant.charts.revenueTrend': 'اتجاه الإيرادات',
      'assistant.charts.previousPeriod': 'السابق',
      'assistant.charts.currentPeriod': 'الحالي',
      'assistant.charts.cashSplit': 'النقد في الصندوق مقابل البنك',
      'assistant.charts.arAging': 'أعمار المستحقات',
      'assistant.charts.analyzing': 'أحلل…',
      'assistant.charts.analysisFailed': 'التحليل غير متاح حالياً — الأرقام أعلاه صحيحة.',
      'assistant.brief.checkedIn': 'دخلت المركز بتاريخ {{date}}',
      'assistant.brief.done': 'تم',
      'assistant.brief.remaining': 'متبقي',
      'assistant.brief.noneYet': '—',
      'assistant.brief.total': 'الإجمالي',
      'assistant.brief.paid': 'المدفوع',
      'assistant.brief.due': 'المتبقي',
      'assistant.brief.openCard': 'فتح بطاقة العمل',
      'assistant.tax.title': 'الوضع الضريبي',
      'assistant.tax.period': '{{from}} – {{to}}',
      'assistant.tax.outputVat': 'ضريبة المخرجات (المبيعات)',
      'assistant.tax.inputVat': 'ضريبة المدخلات (المشتريات)',
      'assistant.tax.notTracked': 'غير متتبَّعة هنا حالياً',
      'assistant.tax.netDue': 'صافي الضريبة المستحقة',
      'assistant.tax.netDueNote': 'بناءً على ضريبة المبيعات فقط — ضريبة المشتريات (المدخلات) غير متتبَّعة في موتورمايند حالياً.',
      'assistant.vat.title': 'وضع الضريبة والتوصيات',
      'assistant.vat.due': 'صافي الضريبة المستحقة',
      'assistant.vat.notRegistered': 'غير مسجل في الضريبة',
      'assistant.vat.cashAvailable': 'النقد المتاح',
      'assistant.vat.cashUnavailable': 'أرقام النقد غير متاحة',
      'assistant.vat.covered': 'مغطاة',
      'assistant.vat.shortfall': 'عجز',
      'assistant.vat.forecastTitle': 'المتوقع تحصيله من أوامر العمل المفتوحة',
      'assistant.vat.moreCards': '+{{count}} أخرى',
      'assistant.vat.overdueLine': 'الذمم المتأخرة',
      'assistant.vat.ofTotalAr': 'من إجمالي ذمم',
      'assistant.actions.issueReceipt': 'إصدار سند قبض',
      'assistant.actions.refundReceipt': 'إصدار سند صرف (استرداد)',
      'assistant.actions.draftQuotation': 'إنشاء عرض سعر',
      'assistant.actions.convertQuotationToInvoice': 'تحويل عرض السعر إلى فاتورة',
      'assistant.actions.jobCardStatusChange': 'تغيير حالة بطاقة العمل',
      'assistant.disclose.textsPaymentLink': 'سيتم إرسال رابط دفع إلى {{name}} برسالة نصية.',
      'assistant.disclose.paidReceiptNoLink': 'سيتم تسجيل دفعة {{name}} كمبلغ مستلم مسبقاً وإرسال السند للعميل برسالة. لن يتم إرسال رابط دفع.',
      'assistant.disclose.textsAppointment': 'سيتم إرسال تفاصيل الموعد إلى {{name}} برسالة نصية.',
      'assistant.disclose.sendsQuotation': 'سيتم إرسال عرض السعر إلى {{name}} للموافقة عليه، برسالة نصية أو واتساب.',
      'assistant.disclose.mayPostInvoice':
        'إذا غطّت الدفعة المقدمة الإجمالي، تُرحَّل الفاتورة فوراً ولا يمكن تعديلها.',
      'assistant.disclose.statusMayNotify': 'حالة التسليم أو الإغلاق أو الإلغاء ترسل إشعاراً للعميل.',
      'assistant.disclose.refundIsFinal': 'الاسترداد نهائي ولا يمكن التراجع عنه.',
      'assistant.fields.items': 'الأصناف',
      'assistant.fields.subtotal': 'المجموع الفرعي',
      'assistant.fields.vat': 'ضريبة القيمة المضافة ({{rate}}%)',
      'assistant.fields.total': 'الإجمالي',
      'assistant.fields.note': 'ملاحظة',
      'assistant.fields.quotation': 'عرض السعر',
      'assistant.fields.from': 'من',
      'assistant.fields.to': 'إلى',
      'assistant.quotation.willSend': 'سيتم إرسالها للعميل للموافقة.',
      'assistant.ask.customer': 'لأي عميل؟ اذكر الاسم أو اكتبه.',
      'assistant.errors.noRefundableAdvance':
        'لا يوجد لدى {{customer}} مبلغ مسبق قابل للاسترداد. سند الصرف يُصدر فقط مقابل سند قبض مدفوع لا يزال به رصيد متاح.',
      'assistant.errors.refundExceedsCredit': 'مبلغ الاسترداد أكبر من الرصيد المتاح في السند.',
      'assistant.ask.amount': 'المبلغ (ريال)',
      'assistant.ask.method': 'كيف تم الدفع؟ نقداً، تحويل بنكي، شبكة/بطاقة، أم لم يُدفع بعد (سأرسل رابط دفع)؟',
      'assistant.ask.job_card': 'أيُّ بطاقة عمل؟ اذكر الرقم أو اسم العميل أو رقم اللوحة.',
      'assistant.ask.time': 'متى؟ اذكر اليوم والوقت.',
      'assistant.ask.task': 'أي مهمة؟',
      'assistant.ask.staff': 'لأي موظف يتم الإسناد؟',
      'assistant.ask.items': 'ما هي الأصناف وأسعارها؟',
      'assistant.ask.status': 'إلى أي حالة تريد تغييرها؟',
      'assistant.ask.quotation': 'أي عرض سعر؟ اذكر الرقم أو اسم العميل.',
      'assistant.ask.cancelled': 'تم الإلغاء.',
      'assistant.ask.pendingChip': 'استكمال: {{action}}',
      'assistant.ask.dismiss': 'إغلاق',
      // assistant prerequisites (Task 6)
      'assistant.prereq.notFound': 'لا يوجد عميل باسم "{{query}}". هل تريد إضافته؟',
      'assistant.prereq.createCustomer': 'إضافة عميل',
      'assistant.prereq.nearMatches': 'لا يوجد تطابق تام لـ "{{query}}". هل تقصد:',
      'assistant.prereq.manyMatches': 'يوجد أكثر من عميل يطابق "{{query}}". أيهم تقصد؟',
      'assistant.prereq.noneCreateNew': 'لا أحد منهم — إضافة عميل جديد',
      'assistant.prereq.duplicate': '{{label}} مسجّل مسبقاً بنفس رقم الجوال. هل تستخدمه بدلاً من ذلك؟',
      'assistant.prereq.usingExisting': 'سيتم استخدام العميل الحالي {{name}}. لم تتم إضافة عميل جديد.',
      'assistant.prereq.partial': 'تمت إضافة {{name}}، لكن لم يكتمل {{action}}: {{reason}}',
      'assistant.prereq.supersededReason': 'بدأت طلباً جديداً',
      'assistant.prereq.cancelledChain': 'تم الإلغاء: {{actions}}. لم يُحفظ شيء.',
      'assistant.prereq.blocked.NO_PERMISSION_TO_CREATE':
        'لا يوجد عميل باسم "{{query}}"، وليست لديك صلاحية إضافة العملاء. اطلب من المدير إضافته.',
      'assistant.prereq.blocked.NO_PERMISSION_FOR_ACTION':
        'ليست لديك صلاحية تنفيذ هذا الإجراء، لذلك لن أضيف "{{query}}".',
      'assistant.prereq.blocked.STACK_TOO_DEEP': 'هذا يتطلب خطوات كثيرة معاً. أكمل الخطوة الحالية أولاً.',
      'assistant.prereq.blocked.NOT_FOUND_NOT_CREATABLE': 'لم أجد "{{query}}".',
      'assistant.prereq.blocked.QUOTATION_HOME_BRANCH_ONLY': 'لا يمكن تحويل عروض الأسعار إلا من فرعك الأساسي. استخدم حساباً مرتبطاً بفرع، ثم أعد طلبك.',
      'assistant.prereq.createVehicle': 'إضافة مركبة',
      'assistant.prereq.noneCreateNewVehicle': 'لا شيء مما سبق — إضافة مركبة جديدة',
      'assistant.prereq.tooMany': 'توجد نتائج مطابقة كثيرة لـ "{{query}}". حدّد طلبك بدقة أكبر.',
      'assistant.confirm.unknownOutcome': "لم نتمكن من الوصول إلى الخادم، لذلك لا نعرف ما إذا كان هذا الإجراء قد اكتمل. يُرجى التحقق من السجل قبل إعادة المحاولة.",
      'assistant.confirm.awaitingChoice': 'اختر أحد الخيارات أعلاه للمتابعة؛ تُفتح البطاقة بعد إتمام الاختيار.',
      'assistant.prereq.phoneBelongsTo': "رقم الجوال هذا مسجَّل بالفعل باسم {{label}}. هل تريد استخدام هذا العميل بدلاً من ذلك؟",
      'assistant.ask.plate': 'ما رقم لوحة المركبة؟',
      'assistant.ask.make': 'ما الشركة المصنّعة للمركبة؟',
      'assistant.ask.model': 'ما طراز المركبة؟',
      'assistant.ask.year': 'ما سنة صنع المركبة؟',
      'assistant.fields.plate': 'رقم اللوحة',
      'assistant.fields.make': 'الشركة المصنّعة',
      'assistant.fields.model': 'الطراز',
      'assistant.fields.year': 'سنة الصنع',
      'assistant.prereq.blocked.LOOKUP_FAILED': 'تعذّر عليّ التحقق من "{{query}}" الآن. حاول مرة أخرى.',
      'assistant.prereq.blocked.NO_BRANCH_CONTEXT': 'اختر الفرع أولاً، ثم أعد طلبك.',
      'assistant.denied.issue_receipt': 'ليست لديك صلاحية تسجيل سندات القبض.',
      'assistant.denied.refund_receipt': 'ليست لديك صلاحية استرداد المبالغ.',
      'assistant.denied.send_payment_link': 'ليست لديك صلاحية إرسال روابط الدفع.',
      'assistant.denied.create_appointment': 'ليست لديك صلاحية إنشاء المواعيد.',
      'assistant.denied.assign_task': 'ليست لديك صلاحية إسناد المهام.',
      'assistant.denied.hold_job_card': 'ليست لديك صلاحية إيقاف أوامر العمل مؤقتاً.',
      'assistant.denied.resume_job_card': 'ليست لديك صلاحية استئناف أوامر العمل.',
      'assistant.denied.draft_quotation': 'ليست لديك صلاحية إعداد عروض الأسعار.',
      'assistant.denied.convert_quotation_to_invoice': 'ليست لديك صلاحية تحويل عروض الأسعار إلى فواتير.',
      'assistant.denied.job_card_status_change': 'ليست لديك صلاحية تغيير حالة أوامر العمل.',
      'assistant.denied.create_customer': 'ليست لديك صلاحية إضافة العملاء.',
      'assistant.denied.create_vehicle': 'ليست لديك صلاحية إضافة المركبات.',
      'assistant.ask.first_name': 'ما الاسم الأول للعميل؟',
      'assistant.ask.last_name': 'ما اسم العائلة للعميل؟',
      'assistant.ask.phone': 'ما رقم جوال العميل؟',
      'assistant.fields.phone': 'الهاتف',
      'assistant.results.part': 'القطعة',
      'assistant.parts.lowStock': 'مخزون منخفض',
      'assistant.parts.inStock': 'الكمية المتوفرة',
      'assistant.parts.sellingPrice': 'سعر البيع',
      'assistant.parts.noLowStock': 'لا يوجد نقص بالمخزون حالياً.',
      'assistant.parts.lowStockTitle': '{{count}} قطعة منخفضة المخزون',
      'assistant.results.customerHistory': 'سجل العميل',
      'assistant.results.visits': 'الزيارات',
      'assistant.results.vehicles': 'المركبات',
      'assistant.results.totalSpent': 'إجمالي الإنفاق',
      'assistant.results.openBalance': 'الرصيد المستحق',
      'assistant.results.quotation': 'عرض السعر',
      'assistant.results.total': 'الإجمالي',
      'assistant.results.validUntil': 'صالح حتى',
      'assistant.results.invoice': 'الفاتورة',
      'assistant.results.supplier': 'المورد',
      'assistant.results.balance': 'الرصيد',
      'assistant.results.lastPayment': 'آخر دفعة',
      'assistant.results.none': 'لا يوجد',
      'assistant.results.employee': 'الموظف',
      'assistant.results.noEmployees': 'لا يوجد موظفين.',
      'assistant.results.staffOnLeave': '{{count}} موظف في إجازة',
      'assistant.results.contractsExpiring': '{{count}} عقد ينتهي خلال {{days}} يوم',
      'assistant.results.daysLeft': 'باقي {{count}} يوم',

      // وضع المحادثة الصوتية — الغطاء فوق محادثة المساعد الذكي: الأزرار وسطر
      // الحالة والردود المنطوقة التي يبنيها speechReply. نصوص say.* تُسمع ولا
      // تُقرأ: قصيرة، بلا روابط ولا رموز، وباللهجة السعودية (قرار المالك
      // 2026-09-19) — كل سطر منها في سطر واحد. ما يظهر على الشاشة يبقى فصحى.
      'assistant.voice.start': 'بدء محادثة صوتية',
      'assistant.voice.end': 'إنهاء المحادثة',
      'assistant.voice.back': 'العودة للكتابة',
      'assistant.voice.settings': 'إعدادات المحادثة',
      'assistant.voice.mute': 'كتم الميكروفون',
      'assistant.voice.unmute': 'تشغيل الميكروفون',
      'assistant.voice.typePlaceholder': 'اكتب رسالة…',
      'assistant.voice.savedNote': 'تُضاف المحادثة إلى الدردشة عند الانتهاء',
      'assistant.voice.states.connecting': 'جارٍ الاتصال…',
      'assistant.voice.states.listening': 'أسمعك',
      'assistant.voice.states.thinking': 'لحظة، أرتّب التفاصيل',
      'assistant.voice.states.speaking': 'أتحدث معك',
      'assistant.voice.states.muted': 'الميكروفون مكتوم',
      'assistant.voice.states.limit': 'انتهت دقائق هذا الشهر',
      'assistant.voice.states.unavailable': 'المحادثة الصوتية غير متاحة',
      'assistant.voice.limitReached': 'انتهت دقائق المحادثة الصوتية لهذا الشهر، يمكنك المتابعة بالكتابة.',
      'assistant.voice.keepTyping': 'العودة للكتابة',
      'assistant.voice.micDenied': 'تم رفض إذن الميكروفون، فعّله من المتصفح أو تابع بالكتابة.',
      'assistant.voice.unavailable': 'المحادثة الصوتية غير متاحة حالياً، يمكنك المتابعة بالكتابة.',
      'assistant.voice.say.done': 'تم، خلصتها لك: {{title}}.',
      'assistant.voice.say.failed': 'معليش، ما مشت العملية. جرّب مرة ثانية، أو كمّلها من الشاشة.',
      'assistant.voice.say.cancelled': 'تمام، لغيتها وما تغيّر شي.',
      'assistant.voice.say.confirmQuestion': 'تبغاني أكمّل؟',
      'assistant.voice.say.foundN_zero': 'ما لقيت شي.',
      'assistant.voice.say.foundN_one': 'لقيت نتيجة وحدة.',
      'assistant.voice.say.foundN_two': 'لقيت نتيجتين.',
      'assistant.voice.say.foundN_few': 'لقيت {{count}} نتائج.',
      'assistant.voice.say.foundN_many': 'لقيت {{count}} نتيجة.',
      'assistant.voice.say.foundN_other': 'لقيت {{count}} نتيجة.',
      'assistant.voice.say.andMore_zero': 'وبس',
      'assistant.voice.say.andMore_one': 'وكمان واحد',
      'assistant.voice.say.andMore_two': 'وكمان اثنين',
      'assistant.voice.say.andMore_few': 'وكمان {{count}}',
      'assistant.voice.say.andMore_many': 'وكمان {{count}}',
      'assistant.voice.say.andMore_other': 'وكمان {{count}}',
      'assistant.voice.say.pickQuestion': 'أي واحد تقصد؟ قول الرقم بس.',
      'assistant.voice.say.orNoneCreate': 'وإذا مو واحد منهم، قول «ولا واحد» وأضيف لك واحد جديد.',
      'assistant.voice.say.createQuestion': 'تبغاني أضيفه؟',
      'assistant.voice.say.notFound': 'ما لقيت {{query}}.',
      'assistant.voice.say.shownOnScreen': 'حطيتها لك على الشاشة.',
      'assistant.voice.say.appointments_zero': 'ما عندك مواعيد اليوم.',
      'assistant.voice.say.appointments_one': 'عندك موعد واحد اليوم.',
      'assistant.voice.say.appointments_two': 'عندك موعدين اليوم.',
      'assistant.voice.say.appointments_few': 'عندك {{count}} مواعيد اليوم.',
      'assistant.voice.say.appointments_many': 'عندك {{count}} موعد اليوم.',
      'assistant.voice.say.appointments_other': 'عندك {{count}} موعد اليوم.',
      'assistant.voice.say.tax': 'الضريبة كلها {{amount}} ريال.',
      'assistant.voice.say.paymentLink': 'رابط الدفع بـ{{amount}} ريال، تلقاه على الشاشة.',
      'assistant.voice.say.stillWorking': 'لسّا شغّال عليها، وبتطلع لك النتيجة على الشاشة.',
      'assistant.voice.say.chooseOnScreen': 'هذي اخترها من الشاشة لو سمحت.',
      'assistant.voice.say.confirmOnScreen': 'هذي أكّدها من الشاشة لو سمحت.',
      'assistant.voice.say.amount': '{{amount}} ريال',
      'assistant.voice.say.pickOption': 'رقم {{n}}: {{label}}',
      'assistant.voice.say.pickByNumber': 'حطيتها لك على الشاشة بالأرقام، قول الرقم أو اضغط عليه.',
      'assistant.voice.say.greeting': 'يا هلا! أنا مساعدك في موتور مايند. كيف أقدر أخدمك؟ ولا تبغى ملخص سريع عن مركزك؟',
      'assistant.voice.say.greetingNamed': 'يا هلا {{name}}! أنا مساعدك في موتور مايند. كيف أقدر أخدمك؟ ولا تبغى ملخص سريع عن مركزك؟',
      'assistant.voice.say.overview': 'هذا ملخص سريع عن مركزك.',
      'assistant.voice.say.anytime': 'ولا يهمك، أنا موجود متى ما احتجتني.',
      'assistant.voice.say.ask_customer': 'لأي عميل؟ قول لي اسمه بس.',
      'assistant.voice.say.ask_amount': 'كم المبلغ؟',
      'assistant.voice.say.ask_method': 'كيف دفع؟ كاش، ولا تحويل بنكي، ولا شبكة؟ ولا لسه ما دفع وأرسل له رابط دفع؟',
      'assistant.voice.say.ask_job_card': 'أي كرت شغل؟ قول الرقم، أو اسم العميل، أو رقم اللوحة.',
      'assistant.voice.say.ask_time': 'متى؟ قول لي اليوم والساعة.',
      'assistant.voice.say.ask_task': 'أي مهمة؟',
      'assistant.voice.say.ask_staff': 'لمين من الموظفين أعطيها؟',
      'assistant.voice.say.ask_items': 'إيش الأصناف، وكم أسعارها؟',
      'assistant.voice.say.ask_status': 'لأي حالة تبغى تغيّرها؟',
      'assistant.voice.say.ask_quotation': 'أي عرض سعر؟ قول الرقم أو اسم العميل.',
      'assistant.voice.say.ask_first_name': 'إيش الاسم الأول للعميل؟',
      'assistant.voice.say.ask_last_name': 'وإيش اسم العائلة؟',
      'assistant.voice.say.ask_phone': 'كم رقم جواله؟',
      'assistant.voice.pickTitle': 'قل الرقم أو اضغط عليه',
      'assistant.voice.session': 'محادثة صوتية',
      'assistant.voice.settingsVoice': 'الصوت',
      'assistant.voice.review': 'راجِع قبل التأكيد',
      'assistant.voice.confirm': 'تأكيد',
      'assistant.voice.cancel': 'إلغاء',

      // Vehicle catalog — super-admin page (nav + Catalog/Review queue tabs)
      'catalog.navTitle': 'كتالوج المركبات',
      'catalog.pageTitle': 'كتالوج المركبات',
      'catalog.pageSubtitle': 'إدارة الماركات والموديلات، ومراجعة حالات عدم المطابقة من المسح/الذكاء الاصطناعي',
      'catalog.tabCatalog': 'الكتالوج',
      'catalog.tabQueue': 'قائمة المراجعة',
      'catalog.makes': 'الماركات',
      'catalog.addMake': 'إضافة ماركة',
      'catalog.editMake': 'تعديل الماركة',
      'catalog.models': 'الموديلات',
      'catalog.addModel': 'إضافة موديل',
      'catalog.editModel': 'تعديل الموديل',
      'catalog.selectMakeHint': 'اختر ماركة لعرض موديلاتها',
      'catalog.noMakes': 'لا توجد ماركات بعد',
      'catalog.noModels': 'لا توجد موديلات لهذه الماركة بعد',
      'catalog.nameEn': 'الاسم (إنجليزي)',
      'catalog.nameAr': 'الاسم (عربي)',
      'catalog.aliases': 'الأسماء البديلة',
      'catalog.aliasesHint': 'الإملاءات البديلة التي يقبلها المطابق — اكتب واضغط Enter',
      'catalog.yearFrom': 'من سنة',
      'catalog.yearTo': 'إلى سنة (اتركه فارغاً إذا كان لا يزال يُصنّع)',
      'catalog.makeSaved': 'تم حفظ الماركة',
      'catalog.modelSaved': 'تم حفظ الموديل',
      'catalog.saveFailed': 'فشل الحفظ',
      'catalog.filterKind': 'النوع',
      'catalog.filterSource': 'المصدر',
      'catalog.statusOpen': 'مفتوحة',
      'catalog.statusResolved': 'تمت المعالجة',
      'catalog.statusIgnored': 'متجاهَلة',
      'catalog.kindMake': 'ماركة',
      'catalog.kindModel': 'موديل',
      'catalog.allKinds': 'كل الأنواع',
      'catalog.allSources': 'كل المصادر',
      'catalog.sourceScan': 'مسح',
      'catalog.sourceManual': 'يدوي',
      'catalog.sourceAI': 'ذكاء اصطناعي',
      'catalog.sourceNhtsa': 'NHTSA',
      'catalog.colRawText': 'النص الأصلي',
      'catalog.colHitCount': 'عدد التكرار',
      'catalog.colSource': 'المصدر',
      'catalog.colMake': 'الماركة',
      'catalog.noMisses': 'لا توجد عناصر في قائمة المراجعة',
      'catalog.addAsAlias': 'إضافة كاسم بديل',
      'catalog.createNew': 'إنشاء جديد',
      'catalog.ignore': 'تجاهل',
      'catalog.aliasTargetKind': 'إلحاق بـ',
      'catalog.aliasTargetMake': 'الماركة المستهدفة',
      'catalog.aliasTargetModel': 'الموديل المستهدف',
      'catalog.aliasConfirmTitle': 'إضافة كاسم بديل',
      'catalog.aliasConfirmMessage': 'إضافة «{{raw}}» كاسم بديل لـ {{target}}؟',
      'catalog.ignoreConfirmTitle': 'تجاهل هذا العنصر؟',
      'catalog.ignoreConfirmMessage': 'سيتم تعليم هذا الإدخال كمتجاهَل ولن يُقترح مرة أخرى.',
      'catalog.resolved': 'تمت المعالجة',
      'catalog.resolveFailed': 'فشل الإجراء',

      // Vehicle catalog — super-admin: force-run AI sweep + per-make backfill
      'catalog.sweepNow': 'تشغيل تحديث الذكاء الاصطناعي',
      'catalog.sweepRunning': 'جارٍ التشغيل…',
      'catalog.sweepRunningHint': 'جارٍ البحث عن ماركات/موديلات جديدة عبر الذكاء الاصطناعي وNHTSA — قد يستغرق هذا بضع دقائق.',
      'catalog.sweepAlreadyRunning': 'هناك تحديث قيد التشغيل بالفعل',
      'catalog.resultsTitle': 'آخر تحديث بالذكاء الاصطناعي',
      'catalog.resultsLastRun': 'آخر تشغيل: {{time}}',
      'catalog.resultsAiLeg': 'مسح الذكاء الاصطناعي',
      'catalog.resultsNhtsaLeg': 'NHTSA',
      'catalog.resultsCandidates': 'مرشحات تم العثور عليها',
      'catalog.resultsNewItems': 'عناصر جديدة في القائمة',
      'catalog.resultsMakesChecked': 'ماركات تم فحصها',
      'catalog.resultsErrors': 'أخطاء',
      'catalog.resultsReviewHint': 'يتم اقتراح الموديلات الجديدة في قائمة المراجعة — اعتمدها من هناك.',
      'catalog.resultsGoToQueue': 'الذهاب لقائمة المراجعة',
      'catalog.backfillMake': 'استكمال بالذكاء الاصطناعي',
      'catalog.backfillInactive': 'أعد تفعيل هذه الماركة أولاً لاستكمالها بالذكاء الاصطناعي',
      'catalog.backfillSummary': 'تم العثور على {{candidates}} مرشح، {{newMisses}} جديد للمراجعة',
      'catalog.backfillFailed': 'فشل الاستكمال بالذكاء الاصطناعي',
      'catalog.categorizeNow': 'تعبئة الفئات بالذكاء الاصطناعي',
      'catalog.categorizeRunning': 'جارٍ التصنيف…',
      'catalog.categorizeRunningHint': 'جارٍ تعبئة فئات الموديلات الناقصة عبر الذكاء الاصطناعي — قد يستغرق هذا بضع دقائق.',
      'catalog.categorizeAlreadyRunning': 'هناك مهمة ذكاء اصطناعي أخرى قيد التشغيل بالفعل',
      'catalog.resultsCategorizeLeg': 'تصنيف الذكاء الاصطناعي',
      'catalog.resultsMakesProcessed': 'ماركات تمت معالجتها',
      'catalog.resultsCategoriesFilled': 'فئات تمت تعبئتها',
      'catalog.classifyNow': 'تصنيف قائمة المراجعة بالذكاء الاصطناعي',
      'catalog.classifyRunning': 'جارٍ تصنيف القائمة…',
      'catalog.classifyRunningHint': 'جارٍ وسم عناصر قائمة المراجعة بفئة مقترحة عبر الذكاء الاصطناعي — قد يستغرق هذا بضع دقائق.',
      'catalog.arabicNamesNow': 'تعبئة الأسماء العربية بالذكاء الاصطناعي',
      'catalog.arabicNamesRunning': 'جارٍ تعبئة الأسماء العربية…',
      'catalog.arabicNamesRunningHint': 'يكتب الاسم العربي المعتمد في السوق السعودية لكل طراز بلا اسم عربي. لا تُغيَّر الأسماء الموجودة.',
      'catalog.arabicNamesAlreadyRunning': 'تعبئة الأسماء العربية قيد التشغيل بالفعل',
      'catalog.resultsArabicLeg': 'الأسماء العربية',
      'catalog.resultsArabicFilled': 'أسماء عُبّئت',
      'catalog.resultsArabicSkipped': 'تُركت لعدم التأكد',
      'catalog.nameArSourceAi': 'ذكاء اصطناعي',
      'catalog.nameArSourceAiHint': 'عبّأه الذكاء الاصطناعي — عدّله إن كان خطأً',
      'catalog.nameArMissing': 'بلا اسم عربي',
      'catalog.triageNow': 'مراجعة ذكية عبر TzamunAI Decide',
      'catalog.triageRunning': 'جارٍ مراجعة القائمة…',
      'catalog.triageRunningHint': 'يطابق TzamunAI Decide كل عنصر بطراز موجود، ويستبعد ما ليس مركبة، ويقترح فئة للطرازات الجديدة. تُطبَّق الإجابات المؤكدة، ويبقى الباقي لمراجعتك.',
      'catalog.triageAlreadyRunning': 'مراجعة القائمة قيد التشغيل بالفعل',
      'catalog.resultsTriageLeg': 'المراجعة الذكية',
      'catalog.resultsAutoResolved': 'حُسمت تلقائياً',
      'catalog.resultsLeftForReview': 'تُركت لمراجعتك',
      'catalog.triageSuggestion': 'اقتراح TzamunAI Decide',
      'catalog.triageSuggestNew': 'طراز جديد',
      'catalog.triageConfidence': 'درجة الثقة',
      'catalog.autoResolvedByJev': 'حسمه TzamunAI Decide',
      'catalog.filterAutoResolved': 'ما حسمه TzamunAI Decide',
      'catalog.undoAutoResolve': 'تراجع',
      'catalog.undoAutoResolveConfirmTitle': 'التراجع عن هذا القرار التلقائي؟',
      'catalog.undoAutoResolveConfirmMessage': 'يُحذف الاسم البديل الذي أضافه TzamunAI Decide ويعود العنصر إلى القائمة.',
      'catalog.undoAutoResolveSuccess': 'أُعيد إلى القائمة',
      'catalog.resultsJevAnswered': 'أجاب عنها TzamunAI Decide',
      'catalog.resultsGatewayFallback': 'أُحيلت إلى الذكاء الاصطناعي الاحتياطي',
      'catalog.suggestedChangesTitle': 'فئات يقترح TzamunAI Decide تغييرها',
      'catalog.suggestedChangesHint': 'يختلف TzamunAI Decide مع هذه الفئات المحفوظة. لم يُغيَّر شيء — طبّق ما توافق عليه.',
      'catalog.applySuggestion': 'تطبيق',
      'jc.specialties': 'التخصصات',
      'jc.suggestedByJev': 'اقتراح TzamunAI Decide',
      'jc.suggested': 'مقترح',
      'jc.jobType': 'نوع العمل',
      'jc.jobTypeRegular': 'إصلاح',
      'jc.jobTypeQuick': 'خدمة سريعة',
      'jc.jobTypeFullCheck': 'فحص شامل',
      'jc.specialtyFilter': 'التصفية حسب التخصص',
      'jc.allSpecialties': 'جميع التخصصات',
      'jc.specialty.mechanical': 'ميكانيكا',
      'jc.specialty.electrical': 'كهرباء',
      'jc.specialty.ac': 'تكييف',
      'jc.specialty.body_paint': 'سمكرة ودهان',
      'jc.specialty.tires_alignment': 'إطارات وميزان',
      'jc.specialty.brakes_suspension': 'مكابح وتعليق',
      'jc.specialty.transmission': 'ناقل الحركة',
      'jc.specialty.diagnostics': 'تشخيص',
      'jc.specialty.quick_service': 'خدمة سريعة',
      'wa.urgent': 'عاجل',
      'wa.intent.approve': 'موافقة',
      'wa.intent.reject': 'رفض',
      'wa.intent.price_question': 'سؤال عن السعر',
      'wa.intent.status_question': 'سؤال عن الحالة',
      'wa.intent.reschedule': 'تغيير الموعد',
      'wa.intent.payment': 'دفع',
      'wa.intent.complaint': 'شكوى',
      'wa.intent.thanks': 'شكر',
      'wa.intent.other': 'أخرى',
      'ntf.whatsappUrgentTitle': 'عاجل: رد عبر واتساب',
      'ntf.whatsappUrgentBody': 'رد العميل يحتاج إلى متابعة خلال ساعة',
      'onb.sortFit': 'الترتيب حسب الملاءمة',
      'onb.aiFit.hot': 'جاهز قريباً',
      'onb.aiFit.warm': 'يستكشف الخيارات',
      'onb.aiFit.cold': 'الملاءمة غير واضحة',
      'onb.aiFit.spam': 'طلب غير جادّ أو تجريبي',
      'onb.aiSize.single': 'فرع واحد',
      'onb.aiSize.multi': 'عدة فروع',
      'onb.aiSize.fleet': 'أسطول أو وكيل',
      'appr.inbox.mostImportantFirst': 'الأهم أولاً',
      'jev.suggested': 'مقترح',
      'jevVision.reception.dashboardPhoto': 'صورة لوحة القيادة',
      'jevVision.reception.scanPlate': 'مسح اللوحة',
      'jevVision.reception.suggested': 'مقترح',
      'jevVision.reception.suggestedFuelChip': 'المقترح: {{level}}',
      'jevVision.reception.odometerPhoto': 'صورة العداد',
      'jevVision.reception.plateNotFound': 'لم يتم العثور على اللوحة — يرجى كتابتها',
      'jevVision.reception.clickToEnlarge': 'انقر للتكبير',
      'jevVision.reception.readingDashboard': 'جارٍ قراءة لوحة القيادة...',
      'jevVision.reception.scanningPlate': 'جارٍ مسح اللوحة...',
      'feedback.aiTags': 'الوسوم الآلية',
      'feedback.sentiment.positive': 'إيجابي',
      'feedback.sentiment.neutral': 'محايد',
      'feedback.sentiment.negative': 'سلبي',
      'feedback.topic.price': 'السعر',
      'feedback.topic.delay': 'التأخير',
      'feedback.topic.quality': 'الجودة',
      'feedback.topic.staff': 'الموظفون',
      'feedback.topic.cleanliness': 'النظافة',
      'feedback.topic.praise': 'ثناء',
      'feedback.needsCallback': 'يحتاج اتصالاً',
      'feedback.filterSentiment': 'الانطباع',
      'feedback.allSentiments': 'كل الانطباعات',
      'feedback.filterNeedsCallback': 'الاتصال',
      'feedback.allCallbackStates': 'الكل',
      'ntf.feedbackCallback.title': 'عميل يحتاج إلى اتصال',
      'ntf.feedbackCallback.message': 'تقييم أمر العمل {{jobNumber}} ({{rating}}/5) يستدعي أن يتصل المدير بالعميل',
      'catalog.webSearchOn': 'البحث على الإنترنت: مفعّل',
      'catalog.webSearchOff': 'البحث على الإنترنت غير مهيأ — يعتمد المسح على معرفة الذكاء الاصطناعي فقط',
      'catalog.webSearchFailed': 'تعذّر البحث على الإنترنت — استُخدمت معرفة الذكاء الاصطناعي بدلاً منه. تحقّق من مفتاح Tavily.',
      'catalog.classifyAlreadyRunning': 'هناك مهمة ذكاء اصطناعي أخرى قيد التشغيل بالفعل',
      'catalog.resultsClassifyLeg': 'تصنيف قائمة المراجعة',
      'catalog.resultsClassified': 'عناصر مصنَّفة',
      'catalog.resultsBatches': 'الدفعات',

      // Vehicle catalog — review queue: search/pagination + bulk-select (Gmail pattern)
      'catalog.searchPlaceholder': 'ابحث في النص الأصلي',
      'catalog.selectAllOnPage': 'تحديد الكل في هذه الصفحة',
      'catalog.selectRow': 'تحديد الصف',
      'catalog.allOnPageSelected': 'تم تحديد كل {{count}} في هذه الصفحة.',
      'catalog.selectAllMatching': 'تحديد كل {{count}} مطابقة',
      'catalog.allMatchingSelected': 'تم تحديد كل {{count}} مطابقة',
      'catalog.selectedCount': 'تم تحديد {{count}}',
      'catalog.clearSelection': 'إلغاء التحديد',
      'catalog.ignoreSelected': 'تجاهل المحدد',
      'catalog.bulkIgnoreConfirmTitle': 'تجاهل {{count}} عنصر؟',
      'catalog.bulkIgnoreConfirmMessage': 'سيتم تعليم هذه العناصر كمتجاهَلة ولن تُقترح مرة أخرى.',
      'catalog.bulkIgnoreSuccess': 'تم تجاهل {{count}} عنصر',
      'catalog.selectionCapped': "الحد الأقصى للتحديد {{count}} — استخدم 'تحديد كل المطابق' للدفعات الأكبر.",
      'catalog.addSelected': 'إضافة المحدد',
      'catalog.addSelectedTooltip': 'حدد صفوفاً معينة لإضافتها — غير متاح في وضع "تحديد كل المطابق"',
      'catalog.addSelectedConfirmTitle': 'إنشاء {{count}} موديل/ماركة من الصفوف المحددة؟',
      'catalog.addSelectedConfirmMessage': 'سيتم إنشاء الصفوف المحددة مباشرة كعناصر كتالوج جديدة، متجاوزة قائمة المراجعة.',
      'catalog.addSelectedSuccess': 'تمت إضافة {{created}}، وتخطي {{skipped}}',
      'catalog.addSelectedSkippedTitle': 'تم تخطي {{count}}',
      'catalog.skipReasonNoMake': 'لم يتم اختيار ماركة',
      'catalog.skipReasonDuplicate': 'موجود بالفعل في الكتالوج',
      'catalog.skipReasonInvalid': 'بيانات غير صالحة',
      'catalog.skipReasonNotFound': 'الصف لم يعد متاحاً',
      'catalog.skipReasonNotOpen': 'حُسم بالفعل',
      'catalog.bulkIgnoreSkipped': 'تُخطّيت لأنها حُسمت بالفعل: {{count}}',
      'catalog.skipReasonError': 'خطأ غير متوقع',

      // Vehicle catalog — model category metadata (VehicleModel.CATEGORIES)
      'catalog.categoryLabel': 'الفئة',
      'catalog.noCategory': 'بدون فئة',
      'catalog.allCategories': 'كل الفئات',
      'catalog.filterCategory': 'التصفية حسب الفئة',
      'catalog.noModelsForCategory': 'لا توجد موديلات مطابقة لهذه الفئة',
      'catalog.category.sedan': 'سيدان',
      'catalog.category.suv': 'دفع رباعي',
      'catalog.category.crossover': 'كروس أوفر',
      'catalog.category.pickup': 'بيك أب',
      'catalog.category.van': 'فان',
      'catalog.category.hatchback': 'هاتشباك',
      'catalog.category.coupe': 'كوبيه',
      'catalog.category.convertible': 'مكشوفة',
      'catalog.category.wagon': 'واغن',
      'catalog.category.minivan': 'ميني فان',
      'catalog.category.bus': 'حافلة',
      'catalog.category.truck': 'شاحنة',
      'catalog.category.sport': 'رياضية',
      'catalog.category.other': 'أخرى',
      'catalog.colCategory': 'الفئة',
      'catalog.categoryNotACar': 'ليست مركبة',
      'catalog.categoryUnclassified': 'غير مصنف',
      'catalog.addAllMatching': 'إضافة كل {{count}} مطابقة',
      'catalog.addAllMatchingConfirmTitle': 'إنشاء {{count}} موديل من هذه الفئة؟',
      'catalog.addAllMatchingConfirmMessage':
        'سيتم إنشاء كل صف مفتوح يطابق تصفية الفئة هذه مباشرة كعنصر كتالوج جديد، متجاوزاً قائمة المراجعة.',
      'catalog.continueRemaining': 'متابعة ({{count}} متبقٍ)',

      // فحص الأضرار بالذكاء الاصطناعي
      'damageScan.section.title': 'فحص الأضرار بالذكاء الاصطناعي',
      'damageScan.section.scanButton': 'فحص الأضرار',
      'damageScan.section.lastScanned': 'تم الفحص {{time}}',
      'damageScan.section.noneYet': 'لا يوجد فحص أضرار بالذكاء الاصطناعي بعد.',
      'damageScan.section.edit': 'تعديل',
      'damageScan.section.save': 'حفظ',
      'damageScan.section.cancel': 'إلغاء',
      'damageScan.section.rescan': 'إعادة الفحص',
      'damageScan.section.delete': 'حذف',
      'damageScan.section.rescanConfirmTitle': 'إعادة فحص الأضرار؟',
      'damageScan.section.rescanConfirmMessage': 'سيتم استبدال فحص الأضرار الحالي وكل التعديلات عليه.',
      'damageScan.section.deleteConfirmTitle': 'حذف فحص الأضرار؟',
      'damageScan.section.deleteConfirmMessage': 'سيتم حذف الصور والاكتشافات المفحوصة نهائياً.',
      'damageScan.section.cancelConfirmTitle': 'تجاهل التعديلات؟',
      'damageScan.section.cancelConfirmMessage': 'لم يتم حفظ التعديلات على فحص الأضرار هذا.',
      'damageScan.section.saveSuccess': 'تم تحديث فحص الأضرار',
      'damageScan.section.saveFailed': 'تعذّر حفظ التعديلات',
      'damageScan.section.deleteSuccess': 'تم حذف فحص الأضرار',
      'damageScan.section.deleteFailed': 'تعذّر حذف فحص الأضرار',
      'damageScan.capture.title': 'فحص الأضرار بالذكاء الاصطناعي — فحص جديد',
      'damageScan.capture.subtitle': 'صوّر السيارة من كل زاوية',
      'damageScan.capture.closeupHint': 'قرّب الكاميرا: صوّر كل جهة على حدة — الأضرار الصغيرة لا تظهر في اللقطات البعيدة.',
      'damageScan.capture.slot.front': 'أمامي',
      'damageScan.capture.slot.rear': 'خلفي',
      'damageScan.capture.slot.left': 'يسار',
      'damageScan.capture.slot.right': 'يمين',
      'damageScan.capture.slot.extra1': 'تقريب 1 (اختياري)',
      'damageScan.capture.slot.extra2': 'تقريب 2 (اختياري)',
      'damageScan.capture.addPhoto': 'إضافة صورة',
      'damageScan.capture.remove': 'إزالة',
      'damageScan.capture.submit': 'فحص الأضرار',
      'damageScan.capture.submitting': 'جاري فحص الأضرار…',
      'damageScan.capture.minPhotoHint': 'أضف صورة واحدة على الأقل',
      'damageScan.errors.quotaExceeded': 'تم استنفاد حصة الذكاء الاصطناعي الشهرية لهذه الشركة.',
      'damageScan.errors.extractionFailed': 'AI فشل في التحليل، حاول مرة أخرى',
      'damageScan.errors.invalidInput': 'تعذّرت قراءة الصور.',
      'damageScan.errors.generic': 'فشل فحص الأضرار. حاول مرة أخرى.',
      'jevVision.damage.ok': 'صحيحة',
      'jevVision.damage.notCar': 'ليست صورة سيارة',
      'jevVision.damage.blurryDark': 'غير واضحة/مظلمة',
      'jevVision.damage.blurry': 'غير واضحة',
      'jevVision.damage.looksLikeFront': 'تبدو كالجهة الأمامية',
      'jevVision.damage.looksLikeRear': 'تبدو كالجهة الخلفية',
      'jevVision.damage.looksLikeLeft': 'تبدو كالجهة اليسرى',
      'jevVision.damage.looksLikeRight': 'تبدو كالجهة اليمنى',
      'jevVision.damage.moveToFront': 'نقل إلى الأمام',
      'jevVision.damage.moveToRear': 'نقل إلى الخلف',
      'jevVision.damage.moveToLeft': 'نقل إلى اليسار',
      'jevVision.damage.moveToRight': 'نقل إلى اليمين',
      'damageScan.wizard.button': 'فحص الأضرار بالذكاء الاصطناعي',
      'damageScan.wizard.analyze': 'فحص بالذكاء الاصطناعي',
      'damageScan.wizard.attach': 'إضافة إلى أمر العمل',
      'damageScan.wizard.retake': 'تغيير الصور',
      'damageScan.wizard.reviewHint': 'نتيجة الذكاء الاصطناعي — راجعها وصحّحها ثم أضفها إلى أمر العمل.',
      'damageScan.wizard.stagedCount': 'تم إرفاق {{count}} صورة',
      'damageScan.wizard.scanning': 'جاري فحص الأضرار بالذكاء الاصطناعي…',
      'damageScan.wizard.scanDone': 'تم فحص الأضرار — راجع النتائج في أمر العمل',
      'damageScan.wizard.scanFailed': 'تعذّر فحص الأضرار',
      'damageScan.wizard.retry': 'إعادة المحاولة',
      'damageScan.wizard.skip': 'تخطي',
      'damageScan.wizard.removeAll': 'إزالة الصور',
      'damageScan.review.noDamage': 'لم يتم رصد أي ضرر. استخدم «إضافة تحديد» بالأسفل لتحديد ضرر فات الذكاء الاصطناعي.',
      'damageScan.review.addBox': 'إضافة تحديد',
      'damageScan.review.openPhoto': 'فتح الصورة',
      'damageScan.review.resize': 'تغيير حجم التحديد',
      'damageScan.review.dragHint': 'اسحب التحديد لتحريكه، ومن الزاوية لتغيير حجمه.',
      'damageScan.review.accept': 'قبول',
      'damageScan.review.reject': 'رفض',
      'damageScan.review.pending': 'بانتظار المراجعة',
      'damageScan.review.pendingCount': 'بقي {{count}} اكتشاف بحاجة إلى مراجعة',
      'damageScan.review.allReviewed': 'تمت مراجعة جميع الاكتشافات',
      'damageScan.review.acceptAll': 'قبول الكل',
      'damageScan.review.mustReviewAll': 'اقبل أو ارفض الاكتشافات المتبقية ({{count}}) أولاً',
      'damageScan.review.deleteBox': 'حذف',
      'damageScan.review.done': 'تم',
      'damageScan.review.note': 'ملاحظة',
      'damageScan.review.type': 'النوع',
      'damageScan.review.severity': 'الشدة',
      'damageScan.review.confidence.label': 'ثقة الذكاء الاصطناعي',
      'damageScan.review.confidence.high': 'عالية',
      'damageScan.review.confidence.medium': 'متوسطة',
      'damageScan.review.confidence.low': 'منخفضة',
      'damageScan.review.scanFailedNotice': 'فشل فحص هذه الصورة — أعد المحاولة',
      'damageScan.type.dent': 'خبطة',
      'damageScan.type.scratch': 'خدش',
      'damageScan.type.crack': 'تشقق',
      'damageScan.type.paint_damage': 'تلف الطلاء',
      'damageScan.type.rust': 'صدأ',
      'damageScan.type.broken_part': 'قطعة مكسورة',
      'damageScan.type.glass_damage': 'تلف الزجاج',
      'damageScan.type.other': 'أخرى',
      'damageScan.severity.minor': 'بسيطة',
      'damageScan.severity.moderate': 'متوسطة',
      'damageScan.severity.severe': 'شديدة',
      'damageScan.angle.front': 'أمامي',
      'damageScan.angle.rear': 'خلفي',
      'damageScan.angle.left': 'يسار',
      'damageScan.angle.right': 'يمين',
      'damageScan.angle.other': 'أخرى',
      'subtask.section': 'المهام الفرعية',
      'svcCard.resendDeadCard': 'بطاقة العمل هذه مغلقة — تم تسليم السيارة، لذا لا يمكن إعادة تسعير بنودها.',
      'svcCard.finishSetup': 'تم',
      'svcCard.editService': 'تعديل الخدمة',
      'svcCard.serviceUpdated': 'تم تحديث الخدمة',
      'svcCard.overrideComplete': 'إكمال إجباري (مشرف)',
      'svcCode.label': 'رمز الخدمة',
      'svcCode.autoHint': 'اتركه فارغًا ليتم إنشاؤه تلقائيًا (SRV-0001، SRV-0002، ...)',
      'svcCode.taken': 'رمز الخدمة مستخدم بالفعل لخدمة أخرى',
      'svcCode.jobPrice': 'السعر لهذه البطاقة (ريال)',
      'svcCode.jobPriceHint': 'تعديل هذا السعر يخص هذه البطاقة فقط — سعر قائمة الخدمات لا يتغير',
      'svcCard.overrideConfirmMessage': 'إكمال "{{name}}" إجباريًا رغم أنه غير معتمد/غير منتهٍ؟ سيتم تسجيل ذلك في سجل النشاط.',
      'svcCard.overrideDone': 'تم الإكمال (تجاوز إداري)',
      'svcCard.taskCreatedLine': 'أُنشئت مهمة عمل «General» لهذه الخدمة — ستجدها في قائمة المهام.',
      'svcCard.setupHint': 'تم حفظ الخدمة. أضف قطع الغيار وخطوات العمل هنا — كل إضافة تُحفظ فورًا. لا توجد خطوات؟ اختر من سينفّذها بالأسفل.',
      'svcCard.noPartsYet': 'لا توجد قطع غيار بعد',
      'svcCard.noStepsYet': 'بدون خطوات — تُنفَّذ الخدمة كمهمة واحدة',
      'svcCard.assignWho': 'من سينفّذ الخدمة؟',
      'svcCard.keepUnassigned': 'إبقاؤها مفتوحة — بدون إسناد',
      'subtask.add': 'إضافة مهام فرعية',
      'subtask.addStep': 'إضافة خطوة',
      'subtask.stepAdded': 'تمت إضافة الخطوة',
      'subtask.editStep': 'تعديل الخطوة',
      'subtask.stepUpdated': 'تم تحديث الخطوة',
      'subtask.errRunningReassign': 'هذه الخطوة قيد التنفيذ — يجب إيقافها أو إكمالها قبل إعادة الإسناد',
      'subtask.errCycle': 'هذا الترتيب يجعل الخطوات تنتظر بعضها في حلقة مغلقة',
      'subtask.errAssignSupervisor': 'تغيير إسناد الخطوة متاح للمشرف فقط',
      'subtask.removeStepTitle': 'حذف الخطوة',
      'subtask.removeStepMessage': 'حذف هذه الخطوة؟ لا يمكن حذف خطوة تعتمد عليها خطوات أخرى أو خطوة قيد التنفيذ.',
      'subtask.addTitle': 'إضافة مهام فرعية',
      'subtask.parentTask': 'المهمة الرئيسية',
      'subtask.parentTaskHint': 'اختر المهمة التي تتبع لها هذه الخطوات',
      'subtask.name': 'اسم الخطوة',
      'subtask.estimatedHours': 'الساعات المقدّرة',
      'subtask.assignee': 'مُسندة إلى',
      'subtask.unassigned': 'غير مُسندة',
      'subtask.dependsOn': 'تبدأ بعد',
      'subtask.dependsOnNone': 'لا شيء — يمكن البدء فورًا',
      'subtask.kind': 'نوع الخطوة',
      'subtask.kindWork': 'عمل',
      'subtask.kindQualityCheck': 'فحص جودة',
      'subtask.qcFailedWithNote': 'لم تجتز فحص الجودة — {{notes}}',
      'qc.reopenTaskOnly': 'إعادة فتح المهمة فقط (دون تحديد خطوة)',
      'qc.pickFailedServices': 'أي الخدمات لم تجتز الفحص؟',
      'qc.stepsWithoutService': 'خطوات غير مرتبطة بخدمة',
      'qc.pickFailedServicesHint': 'حدد كل خدمة تحتاج إعادة عمل. اتركها جميعاً بدون تحديد لإعادة فتح المهمة دون تحديد شيء.',
      'qc.pickFailedSteps': 'أي خطوات هذه الخدمة كانت خاطئة؟',
      'qc.serviceHasNoSteps': 'هذه الخدمة بدون خطوات — سيتم إرجاع الخدمة كاملة.',
      'qc.cascadeWarning_zero': 'سيتم أيضاً إرجاع خطوات مبنية على اختيارك:',
      'qc.cascadeWarning_one': 'سيتم أيضاً إرجاع خطوة واحدة مبنية على اختيارك:',
      'qc.cascadeWarning_two': 'سيتم أيضاً إرجاع خطوتين مبنيتين على اختيارك:',
      'qc.cascadeWarning_few': 'سيتم أيضاً إرجاع {{count}} خطوات مبنية على اختيارك:',
      'qc.cascadeWarning_many': 'سيتم أيضاً إرجاع {{count}} خطوة مبنية على اختيارك:',
      'qc.cascadeWarning_other': 'سيتم أيضاً إرجاع {{count}} خطوة مبنية على اختيارك:',
      'subtask.qcFailedNoNote': 'لم تجتز فحص الجودة — لم يترك الفاحص ملاحظة.',
      'subtask.addRow': 'إضافة خطوة أخرى',
      'subtask.blockedBy': 'بانتظار {{name}}',
      'subtask.start': 'بدء',
      'subtask.started': 'بدأت الخطوة — العدّاد يعمل الآن',
      'subtask.completedToast': 'اكتملت الخطوة',
      'subtask.errServiceNotApproved': 'لا يمكن البدء — العميل لم يعتمد هذه الخدمة بعد',
      'staff.task.stepsProgress': 'اكتملت {{done}} من {{total}} خطوات — تكتمل الخدمة تلقائياً',
      'staff.task.stepMine': 'خطوتك',
      'staff.task.stepBlockedBy': 'بانتظار: {{name}}',
      'staff.task.qcPassedToast': 'اجتاز فحص الجودة — أُغلقت الخطوة',
      'staff.task.qcFailedToast': 'فشل فحص الجودة — أُعيد فتح الخطوة المحددة',
      'staff.task.qcTargetInvalid': 'لا يمكن إعادة فتح هذه الخطوة (قيد التنفيذ أو في خدمة أخرى)',
      'staff.task.qcFailTitle': 'رفض فحص الجودة',
      'staff.task.qcFailPickStep': 'أي خطوة يجب إعادتها؟ سيُعاد فتح كل ما بُني عليها أيضاً.',
      'staff.task.qcFailPreviousStep': 'الخطوة السابقة فقط',
      'staff.task.qcFailNotesPlaceholder': 'ما المشكلة؟ (اختياري)',
      'subtask.complete': 'إنهاء',
      'subtask.skip': 'تخطّي',
      'subtask.reopen': 'إعادة فتح',
      'subtask.qcPass': 'نجح',
      'subtask.qcFail': 'فشل',
      'subtask.progress': '{{done}} من {{total}} مكتملة',
      'subtask.empty': 'لا توجد مهام فرعية بعد. أضفها لتقسيم العمل إلى خطوات.',
      'subtask.statusPending': 'قيد الانتظار',
      'subtask.statusInProgress': 'قيد التنفيذ',
      'subtask.statusCompleted': 'مكتملة',
      'subtask.statusSkipped': 'متخطّاة',
      'subtask.errBlocked': 'لا يمكن البدء — بانتظار {{name}}.',
      'subtask.errAlreadyWorking': '{{name}} قيد التنفيذ بالفعل لدى هذا الفني.',
      'subtask.errHasDependents': 'لا يمكن الحذف — {{names}} تعتمد على هذه الخطوة.',
      'subtask.errCycle': 'هذا سيُنشئ حلقة مغلقة في سلسلة الاعتماديات.',
      'subtask.errRunningDelete': 'أوقف هذه الخطوة قبل حذفها.',
      'subtask.errRunningReassign': 'أوقف هذه الخطوة قبل تغيير المُسند إليه.',
      'subtask.errDrivenBySubTasks': 'هذه المهمة تُدار عبر مهامها الفرعية. ابدأ أو أنهِ المهام الفرعية بدلاً من ذلك.',
      'subtask.errInvoiceLocked': 'هذه المهمة مفوترة على فاتورة معتمدة ولا يمكن تعديلها.',
      'subtask.errItemsNotReady': 'اكتملت كل الخطوات، لكن ما زالت هناك بنود بانتظار الموافقة.',
      'ntf.sub_task_assigned': 'لديك {{count}} خطوة/خطوات جديدة في المهمة {{job_number}}، تبدأ بـ {{first}}',
      'ntf.sub_task_ready': 'خطوتك «{{name}}» في أمر العمل {{job_number}} جاهزة للبدء',
      'ntf.sub_task_qc_failed': 'لم تجتز خطوة «{{name}}» في أمر العمل {{job_number}} فحص الجودة — أعد تنفيذها. {{notes}}',
      'ntf.sub_task_qc_ready': 'اكتمل الإصلاح في أمر العمل {{job_number}} — خطوة «{{name}}» جاهزة لإعادة الفحص',
      // Service-type template editor (default parts + default sub-tasks) — Services.js
      'svcTpl.manageTemplate': 'إدارة القالب',
      'svcTpl.title': 'قالب — {{name}}',
      'svcTpl.subtitle': 'حدد القطع والمهام الفرعية الافتراضية التي تبدأ بها بطاقة العمل عند إضافة هذه الخدمة.',
      'svcTpl.tabParts': 'القطع الافتراضية',
      'svcTpl.tabSubTasks': 'المهام الفرعية الافتراضية',
      'svcTpl.addPart': 'إضافة قطعة',
      'svcTpl.noParts': 'لا توجد قطع افتراضية بعد.',
      'svcTpl.errLoadParts': 'فشل تحميل القطع الافتراضية',
      'svcTpl.noPartChosen': 'لم يتم اختيار قطعة',
      'svcTpl.choosePart': 'اختيار قطعة',
      'svcTpl.changePart': 'تغيير',
      'svcTpl.pickPartTitle': 'اختيار قطعة',
      'svcTpl.pickPartSelect': 'اختيار',
      'svcTpl.customPartBadge': 'مخصصة',
      'svcTpl.optionalBadge': 'اختيارية',
      'svcTpl.requiredPart': 'مطلوبة لهذه الخدمة',
      'svcTpl.sortOrder': 'ترتيب العرض',
      'svcTpl.partMeta': 'الكمية {{qty}} · الترتيب {{order}}',
      'svcTpl.partSaved': 'تم حفظ القطعة الافتراضية',
      'svcTpl.partDeleted': 'تم حذف القطعة الافتراضية',
      'svcTpl.unknownPart': 'قطعة غير معروفة',
      'svcTpl.confirmDeletePart': 'إزالة هذه القطعة الافتراضية من القالب؟',
      'svcTpl.addSubTask': 'إضافة مهمة فرعية',
      'svcTpl.noSubTasks': 'لا توجد مهام فرعية افتراضية بعد.',
      'svcTpl.errLoadSubTasks': 'فشل تحميل المهام الفرعية الافتراضية',
      'svcTpl.subTaskSaved': 'تم حفظ المهمة الفرعية الافتراضية',
      'svcTpl.subTaskDeleted': 'تم حذف المهمة الفرعية الافتراضية',
      'svcTpl.confirmDeleteSubTask': 'إزالة هذه المهمة الفرعية من القالب؟',
      'svcTpl.moveUp': 'نقل لأعلى',
      'svcTpl.moveDown': 'نقل لأسفل',
      'svcTpl.errDependencyNotSameServiceType': 'هذه الاعتمادية تخص خدمة أخرى.',
      'svcTpl.hoursBadge': '{{hours}} ساعة',
      'svcCard.addServiceTitle': 'إضافة خدمة',
      'svcCard.addPartTitle': 'إضافة قطعة لهذه الخدمة',
      'svcCard.pickPartTitle': 'اختيار قطعة',
      'svcCard.partAdded': 'تمت إضافة القطعة',
      'svcCard.sellingPriceSAR': 'سعر البيع (ريال)',
      'svcCard.customerFacing': '(يظهر في عرض السعر)',
      'svcCard.serviceAddedWithDefaults': 'تمت إضافة الخدمة — تمت إضافة {{parts}} قطعة و{{subTasks}} مهمة فرعية من القالب.',
      'svcCard.defaultsPartsNotAdded': 'لم تتم إضافة {{count}} قطعة من القالب:',
      'svcCard.searchService': 'ابحث بالاسم أو الرمز…',
      'svcCard.noServiceMatch': 'لا توجد خدمة مطابقة',
      'svcCard.stockShortTitle': 'المخزون لا يكفي لـ {{count}} من {{total}} قطع',
      'svcCard.stockShortLine': 'المتوفر {{have}}، المطلوب {{need}}',
      'svcCard.stockShortPackageHint': 'يمكنك إضافة الباقة الآن. تُضاف قطعها من أمر العمل بعد الاعتماد عند توفر المخزون.',
      'svcCard.stockShortHint': 'لن تُضاف هذه القطع حتى يُدخل المخزون (فاتورة مشتريات).',
      'pkg.missingTitle': 'قطع من الباقة غير مضافة لأمر العمل',
      'pkg.inStock': 'المتوفر بالمخزون: {{qty}}',
      'pkg.noStockHint': 'أضفها بالزر. القطعة غير المتوفرة بالمخزون تُرفض حتى يُدخل المخزون (فاتورة مشتريات).',
      'pkg.pendingTitle': 'القطع المشمولة في الباقة',
      'pkg.pendingHint': 'تُضاف لأمر العمل بعد اعتماد الباقة.',
      'pkg.partsAfterApproval': 'تمت إضافة الباقة. تُضاف قطعها بعد اعتمادها.',
      'pkg.addMissing': 'إضافة القطع الناقصة',
      'pkg.addMissingDone': 'تمت إضافة {{count}} قطعة إلى الباقة.',
      'svcCard.defaultsPreviewTitle': 'سيتم إضافة',
      'svcCard.noDefaults': 'لا توجد قطع أو مهام فرعية افتراضية لهذه الخدمة.',
      'svcCard.defaultsPartsCount': '{{count}} قطعة',
      'svcCard.defaultsSubTasksCount': '{{count}} مهمة فرعية',
      'svcCard.lineTotal': 'إجمالي المبلغ',
      'svcCard.sectionCounts': '({{services}} خدمات · {{done}} من {{total}} خطوات)',
      'svcCard.hoursOnly': '{{h}} س',
      'svcCard.hoursAndMinutes': '{{h}} س {{m}} د',
      'svcCard.estimatedVsActual': '{{estimated}} مقدّرة · {{actual}} فعلية',
      'svcCard.estimatedOnly': '{{estimated}} مقدّرة',
      'svcCard.actualOnly': '{{actual}} فعلية',
      'svcCard.stepEstimate': 'المقدّر {{hours}}',
      'svcCard.serviceLine': 'الخدمة',
      'svcCard.serviceTotal': 'إجمالي الخدمة',
      'svcCard.marginIncompleteTooltip': 'التكلفة غير مسجّلة بالكامل بعد، لذلك يُخفى هامش الربح بدلاً من عرض تقدير غير دقيق.',
      'svcCard.noServicesYet': 'لا توجد خدمات بعد. أضف واحدة من الكتالوج أو كخدمة مخصصة.',
      'svcCard.addFirstService': 'إضافة أول خدمة',
      'svcCard.unlinkedParts': 'غير مرتبطة بخدمة محددة بعد',
      'svcCard.declinedBecause': 'مرفوض: {{reason}}',
      'qrr.too_expensive': 'السعر مرتفع',
      'qrr.later': 'سأقوم بها لاحقاً',
      'qrr.elsewhere': 'سأقوم بها في مركز آخر',
      'qrr.not_needed': 'لا أحتاجها',
      'qrr.own_part': 'سأوفّر القطعة بنفسي',
      'qrr.distrust_diagnosis': 'لا أثق في التشخيص',
      'qrr.other': 'سبب آخر',

      // Settings › Data Export (Task 4.1)
      'de.navTitle': 'تصدير البيانات',
      'de.pageTitle': 'تصدير بيانات الشركة',
      'de.requestButton': 'طلب تصدير',
      'de.requestDisabledActive': 'هناك عملية تصدير قيد التنفيذ حالياً. يمكنك طلب تصدير آخر بعد اكتمالها.',
      'de.requestVerified': 'تم التحقق — جارٍ تجهيز التصدير.',
      'de.codeStepTitle': 'أدخل رمز التحقق',
      'de.sentToPhone': 'تم إرسال رمز إلى رقم الهاتف المنتهي بـ {{last2}}.',
      'de.sentToPhoneUnknown': 'تم إرسال رمز إلى رقم هاتفك المسجل.',
      'de.codeInputLabel': 'رمز التحقق',
      'de.codeExpiryNote': 'الرمز صالح لمدة 10 دقائق.',
      'de.verify': 'تحقق',
      'de.progressTitle': 'جارٍ تجهيز التصدير',
      'de.progressBody': 'قد تستغرق عمليات التصدير الكبيرة عدة دقائق. يمكنك مغادرة هذه الصفحة — ستجدها هنا عند الانتهاء.',
      'de.historyTitle': 'سجل التصدير',
      'de.historyEmpty': 'لا توجد عمليات تصدير بعد.',
      'de.colStatus': 'الحالة',
      'de.colRequested': 'تاريخ الطلب',
      'de.colSize': 'الحجم',
      'de.colExpires': 'تاريخ الانتهاء',
      'de.colActions': 'إجراءات',
      'de.download': 'تنزيل',
      'de.deleteExportFile': 'حذف ملف التصدير',
      'de.deleteConfirmBody': 'هذا يحذف ملف الأرشيف الذي تم تنزيله فقط. سجلات المركز نفسه — أوامر العمل والفواتير والعملاء والموارد البشرية — لن تتأثر.',
      'de.deleteSuccess': 'تم حذف ملف التصدير.',
      'de.downloadModalTitle': 'تحقق للتنزيل',
      'de.downloadVerify': 'تحقق وتنزيل',
      'de.resend': 'إعادة إرسال الرمز',
      'de.resendCooldown': 'إعادة الإرسال خلال {{seconds}} ث',
      'de.codeResent': 'تم إرسال رمز جديد.',
      // تصدير بيانات المستأجر (لوحة المشرف العام)
      'sde.tabTitle': 'تصدير البيانات',
      'sde.intervalLabel': 'مدة التصدير (بالأيام)',
      'sde.intervalHelp': 'كم مرة يمكن لهذه المنشأة إتمام تصدير. القيمة الافتراضية ٣٠ يومًا. استخدم ٠ لإلغاء الحد — مفيد أثناء الاختبار أو الترحيل.',
      'sde.intervalNoLimit': 'بلا حد',
      'sde.intervalCurrent': 'الحالي: تصدير واحد كل {{days}} يوماً.',
      'sde.intervalCurrentNoLimit': 'الحالي: تصدير بلا حد.',
      'sde.intervalSaved': 'تم تحديث مدة التصدير',
      'sde.intervalInvalid': 'أدخل عدد أيام صحيحًا بين ٠ و ٣٦٥٠.',
      'sde.tenantAccess': 'صلاحية المنشأة في التصدير',
      'sde.tenantAccessHelp': 'عند الإلغاء لا يستطيع أي مستخدم في هذه المنشأة طلب تصدير، مهما كانت صلاحياته. الأرشيفات التي أنشأوها سابقًا لا تتأثر.',
      'sde.revoke': 'إلغاء الصلاحية',
      'sde.restore': 'إعادة الصلاحية',
      'sde.revoked': 'تم إلغاء تصدير البيانات لهذه المنشأة',
      'sde.restored': 'تمت إعادة تصدير البيانات لهذه المنشأة',
      'sde.revokedNotice': 'تصدير البيانات ملغى لهذه المنشأة.',
      'sde.revokedTag': 'ملغى',
      'sde.revokeReasonPrompt': 'ما سبب إلغاء تصدير البيانات لهذه المنشأة؟ (يُسجَّل في سجل التدقيق)',
      'sde.reasonTooShort': 'يرجى كتابة سبب لا يقل عن ١٠ أحرف.',
      'sde.requestOnBehalf': 'تصدير بيانات هذه المنشأة',
      'sde.requestWarning': 'ينتج عن ذلك نسخة كاملة من عملاء المنشأة وموظفيها ورواتبها وبياناتها المالية وملفاتها. السبب الذي تكتبه يُحفظ ويظهر للمنشأة في سجل التصدير الخاص بها.',
      'sde.reason': 'السبب',
      'sde.reasonPlaceholder': 'سبب تصدير بيانات هذه المنشأة',
      'sde.requestExport': 'طلب تصدير',
      'sde.codeSent': 'تم إرسال رمز التحقق إلى جوالك',
      'sde.enterCode': 'أدخل الرمز المكوَّن من ٦ أرقام المُرسل إلى جوالك.',
      'sde.enterDownloadCode': 'أدخل الرمز المكوَّن من ٦ أرقام المُرسل إلى جوالك لبدء التنزيل.',
      'sde.code': 'رمز التحقق',
      'sde.verify': 'تحقق',
      'sde.queued': 'تم إدراج التصدير في قائمة الانتظار',
      'sde.requestFailed': 'تعذّر طلب التصدير',
      'sde.verifyFailed': 'تعذّر التحقق من الرمز',
      'sde.downloadFailed': 'تعذّر بدء التنزيل',
      'sde.updateFailed': 'تعذّر تحديث صلاحية تصدير البيانات',
      'sde.loadFailed': 'تعذّر تحميل سجل التصدير',
      'sde.activity': 'سجل التصدير',
      'sde.noExports': 'لا توجد عمليات تصدير لهذه المنشأة بعد',
      'sde.platformTag': 'موتورمايند',
      'sde.download': 'تنزيل',
      'sde.colStatus': 'الحالة',
      'sde.colRequestedBy': 'مقدم الطلب',
      'sde.colReason': 'السبب',
      'sde.colSize': 'الحجم',
      'sde.colRequestedAt': 'تاريخ الطلب',
      'sde.colActions': 'إجراءات',
      'de.status.awaiting_otp': 'بانتظار التحقق',
      'de.status.pending': 'في قائمة الانتظار',
      'de.status.running': 'جارٍ التجهيز',
      'de.status.ready': 'جاهز',
      'de.status.expired': 'منتهي',
      'de.status.failed': 'فشل',
      'de.status.deleting': 'جارٍ الحذف',
      'de.err.wrongCode': 'رمز التحقق غير صحيح.',
      'de.err.expiredCode': 'انتهت صلاحية هذا الرمز.',
      'de.err.maxAttempts': 'محاولات خاطئة كثيرة جداً. يرجى طلب التصدير مرة أخرى.',
      'de.err.resendCooldown': 'يرجى الانتظار قبل طلب رمز آخر.',
      'de.err.sendLimit': 'لقد وصلت إلى الحد الأقصى لعدد الرموز لهذا الطلب.',
      'de.err.noPhone': 'لا يوجد رقم هاتف مسجل في حسابك. أضف رقماً قبل طلب التصدير.',
      'de.err.dailyLimit': 'يُسمح بعملية تصدير واحدة فقط لكل شركة كل 24 ساعة.',
      'de.err.rateLimit': 'سبق أن قمت بالتصدير مؤخراً. راجع النافذة لمعرفة موعد التصدير القادم.',
      'de.rateLimitTitle': 'سبق أن قمت بالتصدير مؤخراً',
      'de.rateLimitBody': 'يُسمح لكل شركة بعملية تصدير واحدة كل {{days}} يوماً.',
      'de.rateLimitNextLabel': 'يمكنك طلب التصدير القادم في',
      'de.rateLimitLast': 'كان آخر طلب تصدير في {{date}}.',
      'de.rateLimitHint': 'النسخ التي أنتجتها سابقاً تبقى متاحة للتنزيل من السجل أدناه حتى تنتهي صلاحيتها.',
      'de.rateLimitClose': 'حسناً',
      'de.err.inProgress': 'هناك عملية تصدير قيد التنفيذ أو بانتظار التحقق حالياً.',
      'de.err.notReady': 'هذا التصدير غير جاهز للتنزيل بعد.',
      'de.err.alreadyUsed': 'تم استخدام هذا الرمز مسبقاً.',
      'de.err.noActiveCode': 'لا يوجد رمز تحقق نشط. يرجى طلب التصدير مرة أخرى.',
      'de.err.smsFailed': 'فشل إرسال رمز التحقق. يرجى المحاولة مرة أخرى.',
      'de.err.notFound': 'تعذر العثور على هذا التصدير.',
      'de.err.revoked': 'لم تعد تملك صلاحية تنزيل هذا التصدير.',
      'de.err.gone': 'لم يعد أرشيف هذا التصدير متاحاً.',
      'de.err.deleteInProgress': 'هذا التصدير لا يزال قيد التنفيذ ولا يمكن حذفه بعد.',
      'de.err.invalidState': 'تغيّرت حالة هذا التصدير. يرجى تحديث الصفحة والمحاولة مرة أخرى.',
      'de.err.generic': 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
      // Fleet F1 portal (lane fleet-fe1)
      "fleet.nav": "الأساطيل",
      "fleet.new": "أسطول جديد",
      "fleet.templates": "قوالب اتفاقيات مستوى الخدمة",
      "fleet.searchPlaceholder": "ابحث في الأساطيل…",
      "fleet.filter.all": "كل الحالات",
      "fleet.empty": "لا توجد أساطيل بعد",
      "fleet.emptyDesc": "تظهر هنا حسابات الأساطيل التي تُنشئها للعملاء من الشركات.",
      "fleet.setupIncomplete": "إعداد غير مكتمل",
      "fleet.continueSetup": "متابعة الإعداد",
      "fleet.loadFailed": "تعذّر تحميل الأساطيل",
      "fleet.line.contractPrice": "سعر الاتفاقية",
      "fleet.line.changePayer": "تغيير جهة الدفع",
      "fleet.line.payerChanged": "تم تحديث جهة الدفع",
      "fleet.line.payerFailed": "تعذّر تغيير جهة الدفع",
      "fleet.saveFailed": "تعذّر الحفظ",
      "fleet.col.name": "الأسطول",
      "fleet.col.cars": "السيارات",
      "fleet.col.groups": "المجموعات",
      "fleet.col.status": "الحالة",
      "fleet.status.draft": "مسودة",
      "fleet.status.active": "نشط",
      "fleet.status.suspended": "موقوف",
      "fleet.status.closed": "مغلق",
      "fleet.stmt.sendFailed": "لم يصل كشف الحساب إلى أي جهة اتصال مالية. تحقّق من أرقام واتساب لديهم ثم أعد المحاولة.",
      "fleet.stmt.title": "كشف الحساب",
      "fleet.stmt.typeCustomerCredit": "رصيد الأسطول الدائن",
      "fleet.stmt.typeOpeningBalance": "رصيد افتتاحي",
      "fleet.stmt.group": "المجموعة",
      "fleet.stmt.groupAll": "كل المجموعات",
      "fleet.stmt.ageing": "أعمار الديون",
      "fleet.stmt.opening": "الرصيد الافتتاحي",
      "fleet.stmt.closing": "الرصيد الختامي",
      "fleet.stmt.credit": "دائن",
      "fleet.stmt.creditIn": "إضافة إلى رصيد الأسطول الدائن: {{amount}}",
      "fleet.stmt.creditOut": "خصم من رصيد الأسطول الدائن: {{amount}}",
      "fleet.stmt.car": "السيارة",
      "fleet.stmt.send": "إرسال كشف الحساب",
      "fleet.stmt.sendNow": "إرسال الآن",
      "fleet.stmt.sendConfirm": "إرسال هذا الكشف إلى جهات اتصال الأسطول المالية عبر واتساب؟",
      "fleet.stmt.sent_one": "أُرسل كشف الحساب إلى جهة اتصال واحدة",
      "fleet.stmt.sent_other": "أُرسل كشف الحساب إلى {{count}} جهة اتصال",
      "fleet.stmt.sent_two": "أُرسل كشف الحساب إلى جهتي اتصال",
      "fleet.stmt.sent_few": "أُرسل كشف الحساب إلى {{count}} جهات اتصال",
      "fleet.stmt.sent_many": "أُرسل كشف الحساب إلى {{count}} جهة اتصال",
      "fleet.stmt.unassigned": "سيارات بدون مجموعة",
      "fleet.stmt.debit": "مدين",
      "fleet.stmt.empty": "لا توجد حركات في هذه الفترة",
      "fleet.stmt.balance": "الرصيد",
      "fleet.wizard.progress": "خطوات الإعداد",
      "fleet.wizard.step1": "الشركة",
      "fleet.wizard.step2": "المجموعات",
      "fleet.wizard.step3": "جهات الاتصال",
      "fleet.wizard.step4": "السيارات",
      "fleet.wizard.step5": "بدء الاتفاقية",
      "fleet.wizard.step6": "الشروط",
      "fleet.wizard.step7": "الأسعار",
      "fleet.wizard.step8": "التغطية",
      "fleet.wizard.step9": "مستويات الخدمة",
      "fleet.wizard.step10": "الصيانة",
      "fleet.wizard.step11": "المراجعة",
      "fleet.wizard.back": "رجوع",
      "fleet.wizard.next": "حفظ ومتابعة",
      "fleet.wizard.exit": "حفظ وخروج",
      "fleet.wizard.noDraft": "لم تُبدأ اتفاقية مستوى الخدمة بعد — ابدأها من الخطوة 5 أولاً.",
      "fleet.company.name": "الاسم القانوني (بالإنجليزية)",
      "fleet.company.nameAr": "الاسم القانوني (بالعربية)",
      "fleet.company.phone": "الرقم الرئيسي",
      "fleet.company.email": "البريد الإلكتروني",
      "fleet.company.vat": "الرقم الضريبي",
      "fleet.company.cr": "السجل التجاري",
      "fleet.company.building": "رقم المبنى",
      "fleet.company.street": "الشارع",
      "fleet.company.district": "الحي",
      "fleet.company.city": "المدينة",
      "fleet.company.postal": "الرمز البريدي",
      "fleet.company.region": "المنطقة",
      "fleet.company.vatHint": "تتبع بيانات المشتري أنظمة الهيئة: رقم ضريبي من 15 خانة يبدأ وينتهي بـ 3، وسجل تجاري من 10 خانات، ورقم مبنى من 4 خانات، ورمز بريدي من 5 خانات.",
      "fleet.company.linkCustomer": "ربط عميل شركة قائم",
      "fleet.company.linkSearch": "ابحث في عملاء الشركات…",
      "fleet.company.linkEmpty": "لا يوجد عميل شركة مطابق",
      "fleet.company.linked": "تم ربط العميل — وُضعت بياناته من سجله",
      "fleet.company.linkFailed": "تعذّر ربط العميل",
      "fleet.groups.skippable": "المجموعات اختيارية — يمكنك تخطي هذه الخطوة وإضافتها لاحقاً.",
      "fleet.groups.name": "اسم المجموعة (بالإنجليزية)",
      "fleet.groups.nameAr": "اسم المجموعة (بالعربية)",
      "fleet.groups.po": "رقم أمر الشراء",
      "fleet.groups.add": "إضافة مجموعة",
      "fleet.groups.added": "أُضيفت المجموعة",
      "fleet.groups.updated": "حُدّثت المجموعة",
      "fleet.groups.deactivate": "إيقاف",
      "fleet.groups.deactivated": "أُوقفت المجموعة",
      "fleet.groups.deactivateConfirm": "إيقاف المجموعة «{{name}}»؟ تحتفظ سياراتها بسجلها.",
      "fleet.groups.none": "لا توجد مجموعات بعد",
      "fleet.groups.poShort": "أمر شراء {{number}}",
      "fleet.contacts.title": "جهات الاتصال",
      "fleet.contacts.add": "إضافة جهة اتصال",
      "fleet.contacts.name": "الاسم",
      "fleet.contacts.phone": "الجوال",
      "fleet.contacts.email": "البريد الإلكتروني",
      "fleet.contacts.role": "الدور",
      "fleet.contacts.approvalLimit": "حد الاعتماد",
      "fleet.contacts.approvalLimitHint": "اتركه فارغاً ليعني بلا حد",
      "fleet.contacts.group": "المجموعة",
      "fleet.contacts.allGroups": "كل المجموعات",
      "fleet.contacts.save": "حفظ جهة الاتصال",
      "fleet.contacts.none": "لا توجد جهات اتصال بعد",
      "fleet.contacts.missingRoles": "لا توجد جهة اتصال لهذه الأدوار بعد: {{roles}}",
      "fleet.contactAdded": "أُضيفت جهة الاتصال",
      "fleet.contacts.updated": "حُدّثت جهة الاتصال",
      "fleet.roles.approver": "معتمد",
      "fleet.roles.finance": "المالية",
      "fleet.roles.fleet_manager": "مدير الأسطول",
      "fleet.roles.driver": "السائق",
      "fleet.vehicles.title": "السيارات",
      "fleet.vehicles.add": "إضافة سيارة",
      "fleet.vehicles.plate": "رقم اللوحة",
      "fleet.vehicles.plateType": "نوع اللوحة",
      "fleet.vehicles.vin": "رقم الهيكل",
      "fleet.vehicles.make": "الشركة المصنعة",
      "fleet.vehicles.model": "الطراز",
      "fleet.vehicles.year": "سنة الصنع",
      "fleet.vehicles.group": "المجموعة",
      "fleet.vehicles.noGroup": "بلا مجموعة",
      "fleet.vehicles.allGroups": "كل المجموعات",
      "fleet.vehicles.odometer": "قراءة العداد",
      "fleet.vehicles.odometerHint": "القراءة عند انضمام السيارة إلى الأسطول",
      "fleet.vehicles.save": "حفظ السيارة",
      "fleet.vehicles.added": "أُضيفت السيارة",
      "fleet.vehicles.updated": "حُدّثت السيارة",
      "fleet.vehicles.none": "لا توجد سيارات بعد — يُسمح بالمتابعة، لكن التفعيل يحتاج سيارة واحدة على الأقل.",
      "fleet.vehicles.import": "استيراد من Excel",
      "fleet.vehicles.endMembership": "إنهاء العضوية",
      "fleet.vehicles.endConfirm": "إنهاء عضوية هذه السيارة في الأسطول؟ يبقى سجلها مع الأسطول.",
      "fleet.vehicles.ended": "أُنهيت العضوية",
      "fleet.plateType.private": "خاصة",
      "fleet.plateType.public_transport": "نقل عام",
      "fleet.plateType.commercial": "نقل تجاري",
      "fleet.plateType.temporary": "مؤقتة",
      "fleet.plateType.diplomatic": "دبلوماسية",
      "fleet.slaStart.title": "بدء اتفاقية مستوى الخدمة",
      "fleet.slaStart.fromTemplate": "من قالب",
      "fleet.slaStart.fromFleet": "نسخ من أسطول آخر",
      "fleet.slaStart.fromBlank": "البدء من الصفر",
      "fleet.slaStart.fromActive": "تغيير الاتفاقية الحالية",
      "fleet.slaStart.startBlank": "بدء اتفاقية جديدة فارغة",
      "fleet.slaStart.startActive": "البدء من الاتفاقية الحالية",
      "fleet.slaStart.templateEmpty": "لا توجد قوالب بعد — أنشئ قالباً من صفحة قوالب الاتفاقيات",
      "fleet.slaStart.noFleets": "لا توجد أساطيل نشطة أخرى للنسخ منها",
      "fleet.slaStart.fleetNote": "تتحول بنود الصيانة الخاصة بمجموعات ذلك الأسطول إلى أسطول كامل في هذه النسخة.",
      "fleet.slaStart.replaceDraftTitle": "استبدال المسودة؟",
      "fleet.slaStart.replaceDraftBody": "لدى هذا الأسطول مسودة اتفاقية بالفعل. البدء من جديد يستبدلها.",
      "fleet.slaStart.replaceDraft": "استبدال المسودة",
      "fleet.slaStart.started": "بدأت مسودة الاتفاقية",
      "fleet.terms.paymentTerms": "مدة السداد (أيام)",
      "fleet.terms.creditLimit": "حد الائتمان",
      "fleet.terms.creditLimitHint": "اتركه فارغاً ليعني بلا حد ائتمان",
      "fleet.terms.billingCycle": "دورة الفوترة",
      "fleet.terms.billingPerJob": "لكل أمر عمل (فاتورة ضريبية لكل أمر)",
      "fleet.terms.billingMonthly": "كشف حساب شهري",
      "fleet.terms.statementAutoSend": "إرسال كشف الحساب الشهري تلقائياً",
      "fleet.terms.autoApproval": "حد الاعتماد التلقائي",
      "fleet.terms.autoApprovalHint": "بنود الاتفاقية حتى هذا المبلغ لا تحتاج معتمداً. تركه فارغاً يعني أن كل بند على حساب الأسطول يحتاج اعتماداً.",
      "fleet.prices.labour": "خصم العمالة %",
      "fleet.prices.parts": "خصم القطع %",
      "fleet.prices.serviceFixed": "أسعار ثابتة للخدمات",
      "fleet.prices.partFixed": "أسعار ثابتة للقطع",
      "fleet.prices.categoryDiscounts": "خصومات الفئات",
      "fleet.prices.amount": "المبلغ",
      "fleet.prices.percent": "النسبة",
      "fleet.prices.duplicate": "لهذا العنصر قاعدة بالفعل — احذفها أولاً",
      "fleet.prices.precedence": "ترتيب الأولوية: السعر الثابت أولاً، ثم خصم الفئة، ثم نسبة خصم العمالة أو القطع.",
      "fleet.pay.title": "تسجيل دفعة للأسطول",
      "fleet.pay.amount": "المبلغ",
      "fleet.pay.allocate": "المخصص",
      "fleet.pay.date": "تاريخ الدفع",
      "fleet.pay.method": "طريقة الدفع",
      "fleet.pay.mustSum": "يجب ألا يتجاوز مجموع التخصيصات مبلغ التحويل.",
      "fleet.pay.reference": "المرجع",
      "fleet.pay.remainderNeedsBranch": "اختر الفرع الذي يحتفظ بالمبلغ المتبقي.",
      "fleet.pay.retry": "إعادة المحاولة",
      "fleet.pay.proposal": "التقسيم المقترح",
      "fleet.pay.unallocated": "غير مخصص بعد",
      "fleet.pay.creditBranch": "إضافة المتبقي كرصيد في فرع",
      "fleet.pay.close": "إغلاق",
      "fleet.pay.save": "حفظ الدفعة",
      "fleet.pay.saved": "تم تسجيل الدفعة — وصدرت الإيصالات لكل فرع",
      "fleet.payList.title": "الدفعات",
      "fleet.payList.record": "تسجيل دفعة",
      "fleet.payList.receipts": "الإيصالات لكل فرع",
      "fleet.payList.none": "لا توجد دفعات مسجلة بعد",
      "fleet.coverage.default": "الجهة الدافعة الافتراضية",
      "fleet.coverage.defaultHint": "تنطبق على كل ما ليس له بند أدق في الأسفل.",
      "fleet.coverage.coverage": "الجهة الدافعة",
      "fleet.coverage.byService": "حسب الخدمة",
      "fleet.coverage.byCategory": "حسب الفئة",
      "fleet.coverage.payer.sla": "ضمن الاتفاقية",
      "fleet.coverage.payer.extra": "رسوم إضافية خارج الاتفاقية",
      "fleet.coverage.payer.driver": "على حساب السائق",
      "fleet.coverage.payer.excluded": "مستثنى",
      "fleet.sla.quoteTarget": "هدف إرسال العرض (ساعات)",
      "fleet.sla.quoteTargetHint": "من الاستقبال حتى إرسال عرض السعر",
      "fleet.sla.readyTarget": "هدف الجاهزية (أيام)",
      "fleet.sla.readyTargetHint": "من الاستقبال حتى جاهزية السيارة",
      "fleet.sla.partsWaitCounts": "انتظار القطع يُحسب ضمن هدف الجاهزية",
      "fleet.sla.replacementCar": "سيارة بديلة بعد (أيام)",
      "fleet.sla.replacementCarHint": "اتركه فارغاً ليعني أن لا وعد بسيارة بديلة",
      "fleet.sla.perServiceTargets": "أهداف جاهزية لكل خدمة",
      "fleet.sla.hours": "ساعات",
      "fleet.sla.hours_one": "ساعة",
      "fleet.sla.hours_other": "ساعة",
      "fleet.sla.hours_two": "ساعتان",
      "fleet.sla.hours_few": "ساعات",
      "fleet.sla.hours_many": "ساعة",
      "fleet.sla.days": "أيام",
      "fleet.sla.days_one": "يوم",
      "fleet.sla.days_other": "يوم",
      "fleet.sla.days_two": "يومان",
      "fleet.sla.days_few": "أيام",
      "fleet.sla.days_many": "يومًا",
      "fleet.sla.months": "شهور",
      "fleet.sla.km": "كم",
      "fleet.slaPanel.title": "اتفاقية مستوى الخدمة للأسطول",
      "fleet.slaPanel.quote": "ساعة عرض السعر",
      "fleet.slaPanel.ready": "ساعة الجاهزية",
      "fleet.slaPanel.replacementBanner": "سيارة بديلة مستحقة لهذا العمل.",
      "fleet.slaPanel.replacementDueOn": "الاستحقاق في {{date}}",
      "fleet.slaPanel.returnCar": "تأكيد الإرجاع",
      "fleet.slaPanel.returned": "أُعيدت في {{date}}",
      "fleet.slaPanel.stateOk": "ضمن الحدود",
      "fleet.slaPanel.stored": "اعتُمد عند الجاهزية",
      "fleet.slaPanel.saved": "تم الحفظ",
      "fleet.slaPanel.atRisk": "قارب التجاوز",
      "fleet.slaPanel.breached": "متجاوز",
      "fleet.slaPanel.onTime": "في الوقت",
      "fleet.slaPanel.open": "لم تُرجع بعد",
      "fleet.slaPanel.late": "متأخر",
      "fleet.slaPanel.logCar": "تسجيل سيارة بديلة",
      "fleet.slaPanel.noTargets": "لا توجد مستهدفات في الاتفاقية",
      "fleet.slaPanel.notes": "ملاحظات",
      "fleet.slaPanel.givenAt": "أُعطيت في",
      "fleet.maintenance.title": "خطط الصيانة",
      "fleet.maintenance.service": "الخدمة",
      "fleet.maintenance.group": "المجموعة",
      "fleet.maintenance.allGroups": "كل السيارات",
      "fleet.maintenance.everyKm": "كل (كم)",
      "fleet.maintenance.everyMonths": "كل (شهور)",
      "fleet.maintenance.needInterval": "حدد الكيلومترات أو الشهور",
      "fleet.maintenance.dueWindowDays": "نافذة الاستحقاق (أيام)",
      "fleet.maintenance.dueWindowKm": "نافذة الاستحقاق (كم)",
      "fleet.review.title": "المراجعة",
      "fleet.review.edit": "تعديل",
      "fleet.review.done": "مكتمل",
      "fleet.review.pending": "غير مكتمل بعد",
      "fleet.review.skipped": "تخطٍ (اختياري)",
      "fleet.review.notStarted": "لم تبدأ",
      "fleet.review.draft": "المسودة محفوظة",
      "fleet.review.noCars": "لا سيارات في الأسطول بعد",
      "fleet.review.slaNotStarted": "لم تُبدأ اتفاقية مستوى الخدمة",
      "fleet.review.missingRoles": "بعض أدوار جهات الاتصال ما زالت فارغة",
      "fleet.review.activate": "تفعيل الأسطول",
      "fleet.review.activated": "فُعّل الأسطول",
      "fleet.review.startSla": "ابدأ اتفاقية مستوى الخدمة أولاً — التفعيل يحتاجها.",
      "fleet.review.goToSla": "الانتقال إلى بدء الاتفاقية",
      "fleet.review.fix": "الانتقال إلى الخطوة",
      "fleet.review.missing.sla": "اتفاقية مستوى الخدمة مفقودة — ابدأها في الخطوة 5",
      "fleet.review.missing.vehicles": "أضف سيارة واحدة على الأقل في الخطوة 4",
      "fleet.review.missing.company": "أكمل بيانات الشركة في الخطوة 1",
      "fleet.review.applySla": "تطبيق تغييرات الاتفاقية",
      "fleet.review.applyNote": "أوامر العمل المفتوحة تبقى على النسخة التي فُتحت بها.",
      "fleet.review.slaApplied": "طُبّقت تغييرات الاتفاقية",
      "fleet.details.notFound": "الأسطول غير موجود",
      "fleet.details.company": "الشركة",
      "fleet.details.cars": "السيارات",
      "fleet.details.sla": "اتفاقية مستوى الخدمة",
      "fleet.details.noSla": "لا توجد اتفاقية نشطة بعد",
      "fleet.details.slaVersion": "الاتفاقية النشطة — النسخة {{version}}",
      "fleet.details.slaHistory": "سجل الاتفاقيات",
      "fleet.details.editSetup": "تعديل الإعداد",
      "fleet.details.suspend": "إيقاف",
      "fleet.details.reactivate": "إعادة التنشيط",
      "fleet.details.close": "إغلاق",
      "fleet.details.statusChanged": "حُدّثت الحالة",
      "fleet.details.statusConfirm": "تغيير حالة هذا الأسطول إلى «{{action}}»؟",
      "fleet.dash.priceChangeOnJob": "تغيّر سعر الاتفاقية في أمر العمل {{job}}",
      "fleet.dash.title": "لوحة الأسطول",
      "fleet.dash.spend": "الإنفاق",
      "fleet.dash.spendSla": "ضمن اتفاقية مستوى الخدمة",
      "fleet.dash.spendExtra": "رسوم إضافية خارج الاتفاقية",
      "fleet.dash.spendDriver": "على حساب السائق",
      "fleet.dash.sla": "اتفاقية مستوى الخدمة",
      "fleet.dash.openJobs": "الأعمال المفتوحة",
      "fleet.dash.overdue": "المتأخر",
      "fleet.dash.overdueCount": "متأخر",
      "fleet.dash.overdueCount_one": "بند متأخر",
      "fleet.dash.overdueCount_other": "بند متأخر",
      "fleet.dash.overdueCount_two": "بندان متأخران",
      "fleet.dash.overdueCount_few": "بنود متأخرة",
      "fleet.dash.overdueCount_many": "بندًا متأخرًا",
      "fleet.dash.noOpenJobs": "لا توجد أعمال مفتوحة",
      "fleet.dash.noBreaches": "لا توجد تجاوزات",
      "fleet.dash.noDowntime": "لا توجد أعمال مكتملة في هذه الفترة",
      "fleet.dash.noCpk": "لا توجد قراءات كافية لأي سيارة بعد",
      "fleet.dash.na": "غير متاح",
      "fleet.dash.credit": "الائتمان",
      "fleet.dash.creditUsed": "الائتمان المستخدم",
      "fleet.dash.costPerKm": "التكلفة لكل كيلومتر",
      "fleet.dash.branch": "الفرع",
      "fleet.dash.breaches": "التجاوزات",
      "fleet.dash.breaches_one": "تجاوز",
      "fleet.dash.breaches_other": "تجاوز",
      "fleet.dash.breaches_two": "تجاوزان",
      "fleet.dash.breaches_few": "تجاوزات",
      "fleet.dash.breaches_many": "تجاوزًا",
      "fleet.dash.bucketGarage": "وقت المركز",
      "fleet.dash.bucketParts": "انتظار القطع",
      "fleet.dash.bucketFleet": "انتظار الأسطول",
      "fleet.dash.allBranches": "كل الفروع",
      "fleet.dash.quoteOnTime": "الالتزام بموعد عرض السعر",
      "fleet.dash.readyOnTime": "الالتزام بموعد الجاهزية",
      "fleet.dash.replacementMisses": "حالات تأخر تسجيل سيارة بديلة",
      "fleet.dash.replacementMisses_one": "إخفاق في توفير سيارة بديلة",
      "fleet.dash.replacementMisses_other": "إخفاق في توفير سيارة بديلة",
      "fleet.dash.replacementMisses_two": "إخفاقان في توفير سيارة بديلة",
      "fleet.dash.replacementMisses_few": "إخفاقات في توفير سيارة بديلة",
      "fleet.dash.replacementMisses_many": "إخفاقًا في توفير سيارة بديلة",
      "fleet.dash.priceChanges": "تغييرات أسعار الاتفاقية",
      "fleet.dash.perCar": "لكل سيارة",
      "fleet.dash.perGroup": "لكل مجموعة",
      "fleet.dash.downtime": "مدة التوقف",
      "fleet.dash.due": "الصيانة المستحقة",
      "fleet.dash.dueCount": "مستحق",
      "fleet.dash.dueCount_one": "بند مستحق",
      "fleet.dash.dueCount_other": "بند مستحق",
      "fleet.dash.dueCount_two": "بندان مستحقان",
      "fleet.dash.dueCount_few": "بنود مستحقة",
      "fleet.dash.dueCount_many": "بندًا مستحقًا",
      "fleet.dash.viewDue": "فتح قائمة الصيانة المستحقة",
      "fleet.due.title": "الصيانة المستحقة",
      "fleet.due.fleet": "الأسطول",
      "fleet.due.allFleets": "كل الأساطيل",
      "fleet.due.status": "الحالة",
      "fleet.due.statusAll": "الكل",
      "fleet.due.statusOk": "محدث",
      "fleet.due.statusOverdue": "متأخر",
      "fleet.due.statusDue": "مستحق",
      "fleet.due.startAt": "يبدأ في",
      "fleet.due.service": "الخدمة",
      "fleet.due.dueDate": "تاريخ الاستحقاق",
      "fleet.due.dueKm": "الاستحقاق بالكيلومتر",
      "fleet.due.projectedKm": "الكيلومترات المتوقعة",
      "fleet.due.book": "حجز موعد",
      "fleet.due.booked": "محجوز",
      "fleet.due.garage": "الفرع",
      "fleet.due.notes": "ملاحظات",
      "fleet.due.empty": "لا يوجد شيء مستحق",
      "fleet.due.created": "تم إنشاء الحجز",
      "fleet.templates.title": "قوالب اتفاقيات مستوى الخدمة",
      "fleet.templates.subtitle": "شروط قابلة لإعادة الاستخدام (ذهبي، فضي، أساسي). ينسخ الأسطول القالب — وتعديله لاحقاً لا يغيّر الأساطيل التي استخدمته.",
      "fleet.templates.stepName": "الأسماء",
      "fleet.templates.new": "قالب جديد",
      "fleet.templates.edit": "تعديل",
      "fleet.templates.editTitle": "تعديل القالب",
      "fleet.templates.name": "اسم القالب (بالإنجليزية)",
      "fleet.templates.nameAr": "اسم القالب (بالعربية)",
      "fleet.templates.nameCol": "القالب",
      "fleet.templates.nameRequired": "الاسمان مطلوبان",
      "fleet.templates.updated": "آخر تحديث",
      "fleet.templates.save": "حفظ القالب",
      "fleet.templates.saved": "حُفظ القالب",
      "fleet.templates.archive": "أرشفة",
      "fleet.templates.archived": "أُرشف القالب",
      "fleet.templates.archiveConfirm": "أرشفة هذا القالب؟ الأساطيل التي استخدمته لا تتأثر.",
      "fleet.templates.empty": "لا توجد قوالب بعد",
      "fleet.templates.emptyDesc": "احفظ شروط الاتفاقية الشائعة تحت اسم، ثم ابدأ الأساطيل منها.",
      "fleet.catalog.search": "ابحث في الخدمات أو القطع أو الفئات…",
      "fleet.catalog.empty": "لا نتائج",
      "fleet.catalog.branches": "في {{count}} فرع",
      "fleet.catalog.branches_one": "في فرع واحد",
      "fleet.catalog.branches_two": "في فرعين",
      "fleet.catalog.branches_few": "في {{count}} فروع",
      "fleet.catalog.branches_many": "في {{count}} فرعاً",
      "fleet.catalog.branches_other": "في {{count}} فرع",
      "fleet.import.title": "استيراد السيارات من Excel",
      "fleet.import.file": "ملف Excel (.xlsx)",
      "fleet.import.columns": "الأعمدة المطلوبة في الملف (رؤوس بالإنجليزية أو العربية)",
      "fleet.import.upload": "استيراد",
      "fleet.import.row": "الصف",
      "fleet.import.field": "الحقل",
      "fleet.import.problem": "السبب",
      "fleet.import.nothingSaved": "لم يُحفظ شيء — صحّح الصفوف المعلّمة ثم استورد من جديد.",
      "fleet.import.failed": "فشل الاستيراد",
      "fleet.import.created": "استُوردت {{count}} سيارات",
      "fleet.import.created_one": "استُوردت سيارة واحدة",
      "fleet.import.created_two": "استُوردت سيارتان",
      "fleet.import.created_few": "استُوردت {{count}} سيارات",
      "fleet.import.created_many": "استُوردت {{count}} سيارة",
      "fleet.import.created_other": "استُوردت {{count}} سيارة",
      "fleet.invoice.fleetLabel": "فاتورة الأسطول",
      "fleet.invoice.driverLabel": "فاتورة السائق",
      "fleet.checkin.title": "سيارة أسطول",
      "fleet.checkin.placeholder": "سيارة أسطول — اللوحة أو رقم الهيكل",
      "fleet.checkin.search": "بحث",
      "fleet.checkin.noMatch": "لا توجد سيارة أسطول بهذه اللوحة أو رقم الهيكل",
      "fleet.checkin.use": "استخدام هذه السيارة",
      "fleet.checkin.checkingIn": "جارٍ الاستقبال…",
      "fleet.checkin.linked": "رُبطت سيارة الأسطول — حقول السائق في الأسفل",
      "fleet.checkin.failed": "فشل استقبال السيارة",
      "fleet.checkin.driverName": "اسم السائق",
      "fleet.checkin.driverPhone": "جوال السائق",
      "fleet.checkin.customerLocked": "الفاتورة على الأسطول — العميل ثابت لأمر العمل هذا.",
      "fleet.checkin.required": "هذه السيارة تتبع أسطولاً ولا يمكن استقبالها كعميل فوري: يُسجَّل حساب الأسطول والسائق والاتفاقية في النموذج المعتمد.",
      "fleet.checkin.openStandardForm": "فتح نموذج الاستقبال المعتمد",
      "fleet.checkin.plateType": "نوع اللوحة",
      "fleet.credit.title": "حد ائتمان الأسطول",
      "fleet.credit.body": "فتح هذا الأمر سيتجاوز شروط الائتمان المتفق عليها مع الأسطول.",
      "fleet.credit.balance": "رصيد الأسطول المفتوح",
      "fleet.credit.overdue": "فواتير متأخرة",
      "fleet.credit.override": "فتح بتجاوز الحد",
      "fleet.credit.reason": "سبب التجاوز",
      "fleet.credit.reasonHint": "خمسة أحرف على الأقل — يُسجَّل السبب في سجل التجاوز.",
      "fleet.credit.reasonShort": "اكتب خمسة أحرف على الأقل",
      "fleet.credit.driverPaid": "فتح على حساب السائق",
      "fleet.approval.title": "اعتماد الأسطول",
      "fleet.approval.send": "إرسال للاعتماد",
      "fleet.approval.sent": "أُرسلت عروض الأسعار للاعتماد",
      "fleet.approval.pickApprover": "اختيار المعتمِد",
      "fleet.approval.poRequired": "رقم أمر الشراء مطلوب",
      "fleet.approval.autoApproved": "معتمد ضمن الاتفاقية",
      "fleet.approval.approvedBy": "اعتمد بواسطة {{name}}",
      "fleet.approval.approverName": "اسم المعتمِد",
      "fleet.approval.attachment": "مرفق (اختياري)",
      "fleet.approval.record": "تسجيل الاعتماد",
      "fleet.approval.recordTitle": "تسجيل اعتماد الأسطول",
      "fleet.approval.recorded": "تم تسجيل الاعتماد",
      "fleet.approval.recordedComment": "اعتماد مسجَّل من {{name}}، رقم أمر الشراء {{po}}",
      "fleet.approval.roleFleet": "عرض سعر الأسطول",
      "fleet.approval.roleDriver": "عرض سعر السائق",
      "fleet.approval.fleetTotal": "إجمالي الأسطول",
      "fleet.approval.driverTotal": "إجمالي السائق",
      "fleet.approval.empty": "لا توجد عروض أسعار للأسطول بعد",
      "fleet.approval.noApprover": "لا يوجد معتمِد يغطي هذا المبلغ — أضف معتمِداً في صفحة الأسطول.",
      "fleet.approval.note": "ملاحظة (اختيارية)",
      "fleet.approval.nameRequired": "اسم المعتمِد مطلوب",
      "fleetRead": "عرض الأساطيل وتقاريرها",
      "fleetManage": "إنشاء الأساطيل وتعديلها",
      "fleetCredit_override": "فتح أوامر عمل للأسطول تتجاوز حد الائتمان",
      "fleetStatements": "إرسال كشوف حساب الأسطول",
      "fleetPayments": "تسجيل دفعات الأسطول",
      // Multi-branch Release 2 (spec 2026-10-23-multi-branch-release-2.md §5 Step 0):
      // company catalogue, stock transfer, shared customers and suppliers.
      "set.companyCatalogue": "كتالوج الشركة",
      "compCat.title": "قائمة الشركة للقطع والخدمات",
      "compCat.navLabel": "كتالوج الشركة",
      "compCat.needsBranches": "تحتاج قائمة الشركة إلى فرعين على الأقل.",
      "compCat.prepare": "تجهيز قائمة الشركة",
      "compCat.referenceBranch": "الفرع المرجعي",
      "compCat.preview": "معاينة",
      "compCat.apply": "تفعيل قائمة الشركة",
      "compCat.confirm": "ستشترك كل الفروع في قائمة واحدة للقطع والخدمات بسعر بيع واحد، ويبقى المخزون والتكلفة وموقع الرف لكل فرع. هل تريد المتابعة؟",
      "compCat.active": "قائمة الشركة مفعّلة",
      "compCat.check": "فحص قائمة الشركة",
      "compCat.health.ok": "كل صفوف الفروع مدرجة في قائمة الشركة ومطابقة لها.",
      "compCat.health.unlinked": "{{count}} من صفوف الفروع لم تُضَف إلى قائمة الشركة بعد.",
      "compCat.health.drift": "{{count}} من صفوف الفروع تختلف عن قائمة الشركة.",
      "compCat.health.missing": "{{count}} من صفوف قائمة الشركة غير موجودة في الفروع بعد.",
      "compCat.reason.EXCLUDE_NOT_ALLOWED": "لا يُستبعد من قائمة الشركة إلا صف غير نشط بلا مخزون وبلا سجل حركات",
      "compCat.reason.DEACTIVATE_NOT_ALLOWED": "هذه الخدمة على أوامر عمل مفتوحة، فلا يمكن إيقافها",
      "compCat.reason.FROM_REFERENCE": "مختلف أو فارغ في بعض الفروع — تُستخدم قيمة الفرع المرجعي",
      "compCat.reason.CODE_DIFFERS": "رمز الخدمة مختلف بين الفروع — يُستخدم رمز الفرع المرجعي",
      "compCat.companyFieldNote": "قائمة الشركة — التغييرات تنطبق على كل الفروع",
      "compCat.chipCompany": "الشركة",
      "compCat.status.clean": "متطابق",
      "compCat.status.info": "اكتمل تلقائياً",
      "compCat.status.conflict": "يحتاج إلى قرار",
      "compCat.reason.NAME_DIFFERS": "الاسم مختلف بين الفروع",
      "compCat.reason.PRICE_DIFFERS": "سعر البيع مختلف بين الفروع",
      "compCat.reason.UNIT_DIFFERS": "الوحدة مختلفة بين الفروع",
      "compCat.reason.BARCODE_DIFFERS": "الباركود مختلف بين الفروع",
      "compCat.reason.SKU_DIFFERS": "رمز SKU مختلف بين الفروع",
      "compCat.reason.BARCODE_TAKEN": "هذا الباركود أو رمز SKU مستخدم لرقم قطعة آخر",
      "compCat.reason.DUPLICATE_IN_BRANCH": "لدى أحد الفروع أكثر من قطعة بهذا الرقم",
      "compCat.reason.INVALID_NUMBER": "رقم القطعة غير صالح",
      "compCat.reason.CODE_NAME_MISMATCH": "رمز الخدمة نفسه بأسماء مختلفة",
      "compCat.reason.AMBIGUOUS_NAME": "لدى أحد الفروع خدمتان بهذا الاسم",
      "compCat.reason.PACKAGE_DIFFERS": "إعداد الباقة مختلف بين الفروع",
      "compCat.reason.CODE_REASSIGNED": "الرمز مستخدم لخدمة أخرى — أُعطيت رمزاً جديداً",
      "compCat.reason.ACTIVE_DIFFERS": "نشط في بعض الفروع وغير نشط في أخرى — سيكون نشطاً",
      "compCat.reason.PACKAGE_HAS_BILLING_ONLY_LINES": "توجد بنود أوامر عمل مفتوحة تحتسب هذه الخدمة منفردة، لذا لا يمكن جعلها باقة بعد",
      "compCat.reason.INACTIVE_WITH_STOCK": "غير نشط في كل الفروع وعليه مخزون — سيُعاد تفعيله",
      "compCat.resolve.pickBranch": "استخدم قيمة هذا الفرع",
      "compCat.resolve.typeValue": "اكتب قيمة",
      "compCat.resolve.keepSeparate": "أبقِها أصنافاً منفصلة",
      "compCat.resolve.exclude": "استبعدها من قائمة الشركة",
      "compCat.resolve.useBranchForAll": "استخدم هذا الفرع لكل التعارضات",
      "compCat.companyFieldsSkipped": "لم تُغيَّر حقول الشركة. عدّلها من قائمة الشركة.",
      "compCat.skippedCompanyCatalogue": "تم التخطي: يُدار من قائمة الشركة",
      "compCat.catalogReused": "طابق صنفاً موجوداً في قائمة الشركة",
      "compCat.unitMismatch": "صنف الشركة يستخدم وحدة مختلفة",
      "compCat.itemInStock": "لا يزال عليه مخزون في أحد الفروع",
      "compCat.stockScopeBranch": "المخزون المعروض لهذا الفرع فقط",
      "compCat.reactivated": "أُعيد تفعيله",
      "pt.categoryUnitSkippedCompanyUnit": "احتفظت {{skipped}} قطعة بوحدتها لأن فرعاً آخر يخزّن رقم القطعة نفسه بتلك الوحدة.",
      "pt.bulkUnitSkippedCompanyUnit": "{{part_number}}: لم تُغيَّر الوحدة إلى {{to}} — فرع آخر يخزّن رقم القطعة هذا بوحدة {{from}}.",
      "bt.nav": "التحويل بين الفروع",
      "bt.new": "تحويل جديد",
      "bt.from": "من",
      "bt.to": "إلى",
      "bt.status.draft": "مسودة",
      "bt.status.sent": "قيد النقل",
      "bt.status.partially_received": "مستلم جزئياً",
      "bt.status.received": "مستلم",
      "bt.status.closed": "مغلق",
      "bt.status.cancelled": "ملغى",
      "bt.send": "إرسال",
      "bt.receive": "استلام",
      "bt.close": "إغلاق",
      "bt.cancel": "إلغاء التحويل",
      "bt.inTransit": "قيد النقل",
      "bt.received": "المستلم",
      "bt.returned": "المُرجَع",
      "bt.willCreatePart": "ستُنشأ قطعة جديدة في الفرع المستلم",
      "bt.cancelReturnsToSender": "كل ما هو قيد النقل يعود إلى مخزون الفرع المرسل. هل تريد المتابعة؟",
      "bt.needTwoBranches": "يحتاج تحويل المخزون إلى فرعين على الأقل.",
      "bt.qtyExceeds": "أكثر مما هو قيد النقل",
      "bt.ambiguousPart": "لدى الفرع المستلم أكثر من قطعة بهذا الرقم. اختر واحدة.",
      "bt.unitMismatch": "يحسب الفرع المستلم هذه القطعة بوحدة مختلفة ({{unit}}).",
      "bt.returnUnitMismatch": "تغيّرت وحدة هذه القطعة بعد إرسالها. أعِدها إلى {{unit}} أولاً.",
      "bt.unitSent": "أُرسلت بوحدة",
      "bt.draftNumber": "مسودة",
      "bt.linkedPart": "صنف من قائمة الشركة",
      "sc.createdAt": "أُنشئ في",
      "sc.createdAtFilter": "فرع الإنشاء",
      "sc.phoneExists": "يوجد عميل بهذا الرقم في {{branch}}.",
      "sc.useExisting": "استخدم هذا العميل",
      "sc.createAnyway": "أنشئ عميلاً جديداً رغم ذلك",
      "sc.vinExists": "هذه السيارة مسجلة في {{branch}}.",
      "sc.balanceByBranch": "الرصيد حسب الفرع",
      "sc.openingBalance": "الرصيد الافتتاحي (الشركة)",
      "sc.totalBalance": "الإجمالي",
      "sc.duplicatesTitle": "العملاء المكررون",
      "sc.mergeConfirm": "دمج في {{name}}؟ تنتقل المركبات إلى هذا العميل، وتبقى المستندات السابقة كما هي وتظهر في كشف الحساب.",
      "sc.mergedInto": "تم دمجه في {{name}}",
      "sc.unmerge": "إلغاء الدمج",
      "sc.statusFilter": "الحالة",
      "sc.statusActive": "نشط",
      "sc.statusInactive": "غير نشط",
      "sc.statusAll": "الكل",
      "sc.inactive": "غير نشط",
      "sc.reactivate": "إعادة التنشيط",
      "sc.reactivated": "تمت إعادة تنشيط العميل.",
      "sc.deactivate": "إيقاف",
      "sc.deactivateConfirm": "إيقاف {{name}}؟ تبقى المستندات والأرصدة السابقة، ويُخفى العميل من القوائم حتى يُعاد تنشيطه.",
      "sc.deactivated": "تم إيقاف العميل.",
      "sc.merge": "دمج",
      "sc.merged": "تم دمج العملاء.",
      "sc.unmerged": "تم إلغاء الدمج.",
      "ss.createdAt": "أُنشئ في",
      "ss.keep": "الإبقاء على هذا",
      "ss.noDuplicates": "لا يوجد موردون مكررون.",
      "ss.sameCodeTitle": "نفس الرمز لموردين مختلفين",
      "ss.sameCodeHint": "يشترك هؤلاء الموردون في رمز من الترقيم القديم لكل فرع، لكنهم لا يبدون نفس المورد. لا يوجد ما يُدمج؛ تأكد من استخدام المورد الصحيح في كل مستند.",
      "ss.createdAtFilter": "فرع الإنشاء",
      "ss.nameExists": "يوجد مورد بهذا الاسم في {{branch}}.",
      "ss.vatExists": "يوجد مورد بهذا الرقم الضريبي في {{branch}}.",
      "ss.useExisting": "استخدم هذا المورد",
      "ss.createAnyway": "أنشئ مورداً جديداً رغم ذلك",
      "ss.balanceByBranch": "الرصيد حسب الفرع",
      "ss.totalYourBranches": "الإجمالي (فروعك)",
      "ss.companyTotal": "إجمالي الشركة",
      "ss.duplicatesTitle": "الموردون المكررون",
      "ss.tierVat": "نفس الرقم الضريبي",
      "ss.tierLikely": "غالباً نفس المورد",
      "ss.mergeConfirm": "دمج في {{name}}؟ تبقى الفواتير والمصروفات والمدفوعات السابقة كما هي وتظهر في كشف حساب هذا المورد.",
      "ss.mergeAllSelected": "دمج كل المحدد",
      "ss.mergedInto": "تم دمجه في {{name}}",
      "ss.unmerge": "إلغاء الدمج",
      "stockTransfer_send": "إرسال مخزون إلى فرع آخر وإلغاء التحويلات التي لم تُستلم بعد",
      "stockTransfer_receive": "استلام مخزون مرسل من فرع آخر",
      "catalogManage": "تعديل أصناف وخدمات كتالوج الشركة لكل الفروع وتشغيل الدمج عند التفعيل",
      "suppliersMerge": "دمج الموردين المكررين وعرض تقرير التكرار",
      "customersMerge": "دمج العملاء المكررين وعرض تقرير التكرار",
      // Multi-branch Release 2 (Step 0): per-branch roles, booking brands and
      // document designs per branch.
      "ubr.title": "الدور في كل فرع",
      "ubr.sameAsDefault": "نفس الدور الافتراضي ({{role}})",
      "ubr.homeUsesDefault": "الفرع الرئيسي يستخدم الدور الافتراضي",
      "ubr.roleInBranch": "{{role}} في هذا الفرع",
      "ubr.badge": "+{{count}} أدوار فروع",
      "ubr.history": "سجل الأدوار",
      "ubr.allBranchesBlocked": "مع اختلاف الدور بين الفروع، لن يستطيع هذا المستخدم استخدام «كل الفروع».",
      "ubr.companyDiagnosticsHidden": "إجماليات الشركة مخفية: أنت لا تدير كل الفروع.",
      "booking.tabs.brands": "العلامات التجارية",
      "brand.mainPage": "الصفحة الرئيسية (كل الفروع)",
      "brand.add": "إضافة صفحة علامة تجارية",
      "brand.name": "اسم العلامة التجارية",
      "brand.slug": "اسم الرابط",
      "brand.branches": "الفروع في هذه الصفحة",
      "brand.inOther": "ضمن {{name}}",
      "brand.copyLink": "نسخ الرابط",
      "brand.deleteConfirm": "حذف صفحة العلامة؟ تعود فروعها إلى الصفحة الرئيسية.",
      "brand.pickPage": "صفحة الحجز",
      "brand.nameAr": "اسم العلامة التجارية بالعربية",
      "brand.edit": "تعديل",
      "brand.delete": "حذف",
      "brand.loadFailed": "تعذر تحميل صفحات العلامات التجارية",
      "docDesign.scopeBranch": "هذا الفرع",
      "docDesign.scopeCompany": "الافتراضي للشركة",
      "docDesign.inheritsCompany": "افتراضي الشركة: {{name}}",
      "docDesign.useCompany": "استخدم تصميم الشركة",
      "docDesign.useCompanyAll": "استخدم تصاميم الشركة للكل",
      "docDesign.companyOnlyNote": "قسائم الرواتب والكشوف والتقارير تستخدم تصميم الشركة.",
      // API error catalogue (backend/data/errorCodes.json)
      // Multi-branch Release 2 (Step 0)
      "err.CATALOG_SINGLE_BRANCH": "تحتاج قائمة الشركة إلى فرعين على الأقل.",
      "err.NEEDS_TWO_BRANCHES": "يتطلّب ذلك فرعين على الأقل في الشركة.",
      "err.BRANCH_TRANSFER_OPENING_CUTOFF": "الأرصدة الافتتاحية معتمدة بتاريخ {{opening_date}}. لا يمكن أن يبقى تحويل بين الفروع قيد النقل عبر هذا التاريخ: أرسله ابتداءً من ذلك التاريخ.",
      "err.CATALOG_ALREADY_ACTIVE": "قائمة الشركة مفعّلة بالفعل.",
      "err.CATALOG_MERGE_BUSY": "قائمة الشركة مشغولة الآن. حاول مرة أخرى بعد قليل.",
      "err.CATALOG_MERGE_CONFLICTS_OPEN": "احسم كل التعارضات في المعاينة قبل تفعيل قائمة الشركة.",
      "err.CATALOG_ITEM_STALE": "عدّل شخص آخر هذا الصنف. أعد التحميل ثم حاول مرة أخرى.",
      "err.CATALOG_NUMBER_TAKEN": "يستخدم صنف آخر في قائمة الشركة رقم القطعة هذا.",
      "err.CATALOG_BARCODE_TAKEN": "يستخدم صنف آخر في قائمة الشركة هذا الباركود.",
      "err.CATALOG_SKU_TAKEN": "يستخدم صنف آخر في قائمة الشركة رمز SKU هذا.",
      "err.CATALOG_CODE_TAKEN": "تستخدم خدمة أخرى في قائمة الشركة هذا الرمز.",
      "err.CATALOG_UNIT_MISMATCH": "تحسب قائمة الشركة هذه القطعة بوحدة مختلفة. استخدم وحدة صنف الشركة.",
      "err.CATALOG_ITEM_IN_STOCK": "لا يزال لهذا الصنف مخزون في أحد الفروع، لذا لا يمكن إيقافه.",
      "err.CATALOG_ITEM_FOREIGN": "صنف الشركة هذا تابع لشركة أخرى.",
      "err.PART_UNIT_COMPANY_CONFLICT": "الفرع {{garage_name}} يخزّن القطعة {{part_number}} بوحدة {{company_unit}}. استخدم الوحدة نفسها أو رقم قطعة مختلفًا.",
      "err.SUPPLIER_NAME_EXISTS": "يوجد مورد بهذا الاسم في الشركة.",
      "err.SUPPLIER_VAT_EXISTS": "يوجد مورد بهذا الرقم الضريبي في الشركة.",
      "err.SUPPLIER_MERGE_INVALID": "اختر مورداً واحداً للإبقاء عليه وما يصل إلى 20 مورداً آخر لدمجهم فيه.",
      "err.SUPPLIER_INACTIVE": "هذا المورد غير نشط. أعد تفعيله أولاً.",
      "err.GARAGE_MOVE_BLOCKED": "لهذا الفرع سجلات مدمجة أو تحويلات مفتوحة أو صفحات حجز في شركته. عالجها قبل نقله.",
      "err.SUPPLIER_ALREADY_MERGED": "هذا المورد مدمج بالفعل في مورد آخر. ألغِ ذلك الدمج أولاً.",
      "err.SUPPLIER_HAS_MERGES": "يوجد موردون مدمجون في هذا المورد. ألغِ تلك الدموج أولاً.",
      "err.SUPPLIER_MERGE_VAT_CONFLICT": "لهؤلاء الموردين أرقام ضريبية مختلفة، لذا لا يمكن دمجهم.",
      "err.SUPPLIER_MERGE_BUSY": "يجري تغيير آخر على هؤلاء الموردين. حاول مرة أخرى بعد قليل.",
      "err.SHARED_SUPPLIERS_MERGES_EXIST": "ألغِ دمج {{count}} من الموردين المدمجين قبل إيقاف هذه الميزة.",
      "err.BRANCH_TRANSFER_STATUS": "لا يمكن تنفيذ هذا الإجراء على التحويل في حالته الحالية.",
      "err.BRANCH_TRANSFER_SAME_BRANCH": "اختر فرعاً آخر للإرسال إليه.",
      "err.BRANCH_TRANSFER_DEST_INVALID": "هذا الفرع ليس من فروع هذه الشركة أو أنه غير نشط.",
      "err.BRANCH_TRANSFER_QTY_EXCEEDS": "لا يمكنك استلام كمية أكبر مما هو قيد النقل.",
      "err.BRANCH_TRANSFER_DEST_PART_INVALID": "هذه القطعة لا تتبع الفرع المستلم.",
      "err.BRANCH_TRANSFER_DEST_PART_AMBIGUOUS": "لدى الفرع المستلم أكثر من قطعة بهذا الرقم. اختر القطعة التي تستلم المخزون.",
      "err.BRANCH_TRANSFER_UNIT_MISMATCH": "يحسب الفرع المستلم هذه القطعة بوحدة غير الوحدة التي أُرسلت بها. صحّح الوحدة قبل الاستلام.",
      "err.BRANCH_TRANSFER_RETURN_UNIT_MISMATCH": "تغيّرت وحدة هذه القطعة بعد إرسالها. أعِدها إلى الوحدة التي أُرسلت بها ثم ألغِ مرة أخرى.",
      "err.BRANCH_TRANSFER_QTY_INVALID": "أدخل كمية أكبر من صفر (حتى 3 منازل عشرية).",
      "err.BRANCH_TRANSFER_GL_ACCOUNT_CONFLICT": "الحساب 1310 في دليل حساباتك مستخدم لغرض آخر. اربط «مخزون بالطريق» بحساب غير مستخدم في ربط الحسابات أولاً.",
      "err.BRANCH_TRANSFER_PART_NUMBER_REQUIRED": "هذه القطعة بلا رقم قطعة، فلا يمكن مطابقتها في الفرع المستلم. اختر القطعة المستلمة.",
      "bt.partNumberRequired": "بلا رقم قطعة — اختر القطعة المستلمة",
      "bt.addPart": "أضف قطعة — ابحث في هذا الفرع",
      "bt.allStatuses": "كل الحالات",
      "bt.available": "المتاح بعد الحجوزات: {{count}}",
      "bt.cancelledAt": "تاريخ الإلغاء",
      "bt.cancelledDone": "أُلغي التحويل",
      "bt.choosePart": "اختر قطعة مستلمة أخرى",
      "bt.col.number": "الرقم",
      "bt.createdAt": "تاريخ الإنشاء",
      "bt.draftSaved": "حُفظت المسودة",
      "bt.editDraft": "تعديل المسودة",
      "bt.empty": "لا توجد تحويلات هنا بعد.",
      "bt.fromLocation": "الإرسال من المستودع",
      "bt.lines": "القطع",
      "bt.loadFailed": "تعذّر تحميل التحويل. حاول مرة أخرى.",
      "bt.notFound": "هذا التحويل غير موجود أو لا يمكنك فتحه.",
      "bt.onHand": "المتوفر هنا: {{count}}",
      "bt.overOnHand": "أكثر من المتوفر هنا ({{count}})",
      "bt.reason": "السبب",
      "bt.receiveTitle": "استلام التحويل",
      "bt.receivedAt": "تاريخ الاستلام",
      "bt.receivedDone": "تم استلام المخزون",
      "bt.receivingPart": "القطعة المستلمة",
      "bt.saveDraft": "حفظ كمسودة",
      "bt.sendNow": "إرسال الآن",
      "bt.sent": "المُرسَل",
      "bt.sentAt": "تاريخ الإرسال",
      "bt.sentDone": "أُرسل التحويل",
      "bt.tab.all": "الكل",
      "bt.tab.incoming": "الوارد",
      "bt.tab.outgoing": "الصادر",
      "bt.toLocation": "الاستلام في المستودع",
      "bt.zeroCost": "التكلفة صفر في {{count}} من البنود؛ ستُنقل بقيمة صفر.",
      "bulk.unitSkippedInTransit": "لم تتغير الوحدة: القطعة في تحويل مفتوح ({{transfers}})",
      "err.OB_STOCK_IN_TRANSIT_AT_CUTOFF": "كان هناك مخزون قيد النقل بين الفروع في تاريخ الافتتاح. اختر تاريخ افتتاح لم يكن فيه أي تحويل قيد النقل.",
      "err.CUSTOMER_PHONE_EXISTS": "يوجد عميل بهذا الرقم في الشركة.",
      "err.VIN_IN_USE_COMPANY": "هذه السيارة مسجلة في فرع آخر من الشركة. افتح السيارة الموجودة بدلاً من ذلك.",
      "err.CUSTOMER_MERGE_INVALID": "اختر عميلاً واحداً للإبقاء عليه وما يصل إلى 20 عميلاً آخر لدمجهم فيه.",
      "err.CUSTOMER_ALREADY_MERGED": "هذا العميل مدمج بالفعل في عميل آخر. استخدم إلغاء الدمج بدلاً من ذلك.",
      "err.CUSTOMER_HAS_MERGES": "يوجد عملاء مدمجون في هذا العميل. ألغِ تلك الدموج أولاً.",
      "err.SHARED_CUSTOMERS_MERGES_EXIST": "ألغِ دمج {{count}} من العملاء المدمجين قبل إيقاف هذه الميزة.",
      "err.CUSTOMER_UNMERGE_CREDIT_IN_USE": "دفعة هذا العميل المقدمة محجوزة على مستند مفتوح. حرّر ذلك المستند أو سوِّه ثم ألغِ الدمج.",
      "err.PAYOUT_RECEIPT_BRANCH_DENIED": "جزء من هذا الرصيد استُلم في فرع لا يمكنك الصرف عنه.",
      "err.RECEIPT_BRANCH_DENIED": "هذا الإيصال استُلم في فرع لا يمكنك استخدامه فيه.",
      "err.CUSTOMER_MERGE_BUSY": "يجري تغيير هؤلاء العملاء الآن. حاول مرة أخرى بعد قليل.",
      "err.feature_disabled": "هذه الميزة غير مفعّلة لشركتك.",
      "err.RECEIPT_AUTH_REQUIRED": "تعذّر التحقق من الإيصال مقابل فروعك، لذا لم يُغيَّر شيء. تواصل مع الدعم.",
      "err.COMPANY_FEATURES_BUSY": "يجري تغيير آخر على هذه الشركة. حاول مرة أخرى بعد قليل.",
      "err.COMPANY_FEATURES_CHANGED": "غيّر شخص آخر ميزات الشركة. أعد التحميل ثم احفظ مرة أخرى.",
      "err.BOOKING_PAGE_NOT_FOUND": "صفحة الحجز هذه غير موجودة أو مؤرشفة.",
      "err.BRANCH_ROLE_HOME": "الفرع الرئيسي يستخدم الدور الافتراضي.",
      "err.FEATURE_DISABLED_BRANCH_ROLES": "الأدوار حسب الفرع غير مفعلة لشركتك.",
      "err.BRANCH_ROLE_ROW_KEPT": "لهذا المستخدم دور محدد في هذا الفرع. فعّل الأدوار حسب الفرع وأزله أولاً.",
      "err.BRANCH_SCOPE_ALL_MIXED_ROLES": "دورك يختلف بين الفروع، لذلك خيار «كل الفروع» غير متاح. اختر فرعاً واحداً.",
      "err.BOOKING_PAGE_SLUG_TAKEN": "اسم الرابط مستخدم",
      "err.BOOKING_PAGE_SLUG_INVALID": "استخدم أحرفاً لاتينية صغيرة وأرقاماً وشرطات",
      "err.BOOKING_PAGE_DEFAULT": "لا يمكن حذف الصفحة الرئيسية",
      "err.BOOKING_BRANCH_IN_OTHER_PAGE": "هذا الفرع مضاف إلى {{name}}",
      "err.BOOKING_BRANDS_NOT_READY": "صفحات العلامات التجارية غير جاهزة بعد. حاول لاحقاً.",
      "err.FLEET_JOB_LOCKED": "تُحدَّد سيارة أمر عمل الأسطول والجهة التي تدفع عنه عند الاستقبال، ولا يمكن تغييرهما. ألغِ أمر العمل هذا ثم استقبل السيارة الصحيحة.",
      "err.FLEET_IMPORT_INVALID": "في الملف صفوف لا يمكن استيرادها، ولم يُحفظ شيء منه.",
      "err.FLEET_DRIVER_REQUIRED": "أدخل اسم السائق ورقم جوال صحيحاً لسيارة الأسطول.",
      "err.FLEET_DUE_NOT_FOUND": "لم يُعثَر على بند الصيانة المستحقة.",
      "err.FLEET_DUE_ALREADY_BOOKED": "لبند الصيانة المستحقة هذا حجزٌ قائم بالفعل.",
      "err.FLEET_CHECKIN_REQUIRED": "هذه السيارة تتبع أسطولاً. استقبلها من خانة استقبال الأسطول في نموذج أمر العمل الجديد.",
      "err.FLEET_NOT_FOUND": "الأسطول غير موجود.",
      "err.FLEET_SETUP_INCOMPLETE": "أكمِل إعداد الأسطول أولاً: يحتاج إلى اتفاقية مستوى خدمة سارية وسيارة واحدة على الأقل.",
      "err.FLEET_ACCOUNT_INACTIVE": "هذا الأسطول غير نشط: لم يكتمل إعداده، أو أنه موقوف أو مغلق.",
      "err.FLEET_APPROVAL_REQUIRED": "يحتاج عرض سعر الأسطول هذا إلى تحديد المعتمِد من جهات اتصال الأسطول.",
      "err.FLEET_APPROVER_LIMIT": "حد اعتماد هذا المعتمِد أقل من إجمالي عرض السعر.",
      "err.FLEET_CUSTOMER_LINKED": "هذا العميل مرتبط بأسطول بالفعل.",
      "err.FLEET_CREDIT_OVERRIDE_REQUIRED": "تجاوز هذا الأسطول حد الائتمان أو لديه فواتير متأخرة. اكتب سبب التجاوز، أو افتح أمر العمل على حساب السائق.",
      "err.FLEET_CREDIT_OVERRIDE_REASON_REQUIRED": "اكتب سببًا من 5 أحرف على الأقل لتجاوز حد ائتمان الأسطول.",
      "err.FLEET_VEHICLE_IN_OTHER_FLEET": "هذه السيارة نشطة في أسطول بالفعل.",
      "err.FLEET_BUYER_FIELDS_LOCKED": "هذا العميل تابع لأسطول. عدّل بيانات الشركة من صفحة الأساطيل.",
      "err.FLEET_SLA_REQUIRED": "ابدأ اتفاقية مستوى الخدمة للأسطول أولاً: اختر قالباً، أو انسخ من أسطول آخر، أو ابدأ من دون قالب.",
      "err.FLEET_REPLACEMENT_OPEN": "سُلِّمت سيارة بديلة على أمر العمل هذا ولم تُعَد بعد.",
      "err.FLEET_REPLACEMENT_JOB_FINISHED": "لا تُسجَّل سيارة بديلة إلا وأمر العمل مفتوح.",
      "err.FLEET_LINE_PAYER_REQUIRED": "لم تُحدَّد جهة الدفع لبعض بنود أمر عمل الأسطول بعد. حدّد من يدفع عن كل بند في أمر العمل.",
      "err.FLEET_LINE_EXCLUDED": "هذا العمل مستثنى من اتفاقية الأسطول، فلا يدفعه إلا السائق.",
      "err.FLEET_LINE_ON_PAID_INVOICE": "هذا البند على فاتورة مدفوعة، لذا لا يمكن تغيير الجهة الدافعة له. رحّل تلك الفاتورة ثم صحّحها بإشعار دائن.",
      "err.FLEET_LINES_CHANGED": "تغيّر بند أو أكثر من بنود أمر العمل أثناء إنشاء الفاتورة. راجع البنود ثم أنشئ الفاتورة من جديد.",
      "err.FLEET_SLA_TERMS_REQUIRED": "لا تتضمن اتفاقية مستوى الخدمة لهذا الأسطول شروط سداد. حدّدها في الاتفاقية قبل إصدار الفاتورة.",
      "err.FLEET_STATEMENT_NO_FINANCE_CONTACT": "لا توجد لدى هذا الأسطول جهة اتصال مالية نشطة لاستلام كشف الحساب. أضِف واحدة في صفحة جهات اتصال الأسطول.",
      "err.FLEET_STATEMENT_PDF_FAILED": "تعذّر إنشاء ملف كشف الحساب، فلم يُرسَل شيء. أعد المحاولة، وإن تكرر الخطأ فتواصل مع الدعم.",
      "err.FLEET_FINANCE_CONTACT_REQUIRED": "أضف جهة اتصال مالية برقم جوال لهذا الأسطول لإرسال فواتيره.",
      "err.FLEET_QUOTATION_MIXED_PAYERS": "يحتاج أمر عمل الأسطول إلى عرض سعر لبنود الأسطول وآخر لبنود السائق، ولا يُجمع بينهما في عرض واحد.",
      "err.FLEET_QUOTATION_NO_DOWNPAYMENT": "لا يُطلب في عرض سعر الأسطول دفعة مقدمة، فالأسطول يتعامل بالآجل.",
      "err.FLEET_QUOTATION_LOCKED": "اعتُمد عرض سعر الأسطول هذا. أنشئ عرض سعر جديدًا للأعمال الإضافية.",
      "err.FLEET_QUOTATION_LINK_REFUSED": "لا يمكن ربط عرض سعر مستقل بأمر عمل لعميل أسطول. أنشئ عرض السعر من أمر العمل نفسه.",
      "err.FLEET_QUOTATION_ROLE_MISSING": "لم يُنشأ عرض السعر هذا لأمر العمل الخاص بالأسطول. أنشئ عرض السعر من أمر العمل نفسه.",
      "err.FLEET_PO_REQUIRED": "يتطلب تسجيل موافقة الأسطول رقم أمر الشراء الخاص به.",
      "err.FLEET_PAYMENT_SPLIT_MISMATCH": "يجب أن يساوي مجموعُ المبالغ الموزَّعة على الفواتير والمبلغِ المتبقي في الحساب مبلغَ الدفعة.",
      "taskMonitoring.completedAt": "اكتمل في",
      "jc.expectedCompletion": "الإنجاز المتوقع",
      "jc.expectedCompletionHint": "تاريخ ووقت الإنجاز المتوقع لهذه المهمة",
      "qcBr.branchToEdit": "الفرع الذي تعدّله",
      "qcBr.applyTo": "تطبيق الإعدادات على",
      "qcBr.thisBranch": "هذا الفرع فقط",
      "qcBr.allBranches": "كل الفروع",
      "qcBr.specificBranches": "فروع محددة…",
      "qcBr.notCopied": "لن تُنسخ: ",
      "qcBr.apply": "تطبيق",
      "qcBr.noSharedFields": "لا توجد حقول سياسة مشتركة في هذه الصفحة؛ احفظ كل فرع على حدة.",
      "qcBr.generalExcluded": "الاسم والعنوان والهاتف والبريد الإلكتروني والرقم الضريبي والموقع",
      "qcBr.notificationExcluded": "أرقام المستلمين وتفضيلات الإشعارات المرتبطة بكل رقم",
      "qcBr.feedbackExcluded": "رابط تقييم Google الخاص بالفرع",
      "qcBr.creditExcluded": "مستلمو التنبيهات وأرقام هواتفهم",
      "qcBr.featureExcluded": "حد الاستخدام الشهري ومستلِمو تنبيهات الائتمان",
      "qcBr.appointmentExcluded": "سعة المواعيد وساعات العمل وأيام الإغلاق الخاصة بكل فرع",
      "booking.aiDesign.notesLabel": "وصف إضافي لفكرتك (اختياري)",
      "booking.aiDesign.notesHint": "اشرح بكلماتك كيف تتخيّل الموقع: ما الذي تريد إبرازه، والأسلوب، وأي تفاصيل تساعد المصمم.",
      "booking.aiDesign.notesOf": "من",
      "err.BRANCH_NAME_TAKEN": "لكل فرع اسمه الخاص — يستخدم فرع آخر لهذه الشركة هذا الاسم",
      "err.MAPPING_ACCOUNT_INVALID": "لا يقبل هذا الحساب القيود: فهو غير موجود في دليل حساباتك، أو غير نشط، أو حساب رئيسي.",
      "err.MAPPING_TYPE_MISMATCH": "يتطلب دور الترحيل هذا حسابًا من نوع آخر. اختر حسابًا من الأنواع المبيّنة في السطر.",
      "err.MAPPING_KEY_FROZEN": "تستخدم فترة مقفلة هذا الربط. سجّل قيد يومية في شهر مفتوح لنقل الرصيد، أو أعد فتح الفترة أولًا.",
      "err.COST_CENTER_SCOPE": "يشمل هذا التقرير جميع الفروع، وحسابك لا يتيح لك الاطلاع عليها كلها. اطلب من المسؤول صلاحية جميع الفروع.",
      "err.COST_CENTER_REQUIRED": "يحتاج كل سطر إيرادات أو مصروفات إلى مركز تكلفة: فرع أو «غير موزع».",
      "gl.acctmap.nav": "ربط الحسابات",
      "gl.acctmap.title": "ربط الحسابات",
      "gl.acctmap.subtitle": "الحساب الذي يستخدمه كل قيد آلي. ينطبق التغيير على كل مستند يستخدم هذا الدور في الفترات المفتوحة.",
      "gl.acctmap.accessDenied": "لا تملك صلاحية عرض ربط الحسابات",
      "gl.acctmap.accessDeniedDesc": "يتطلب عرض هذه الشاشة صلاحية «عرض ربط الحسابات».",
      "gl.acctmap.featureDisabled": "ربط الحسابات غير مفعّل",
      "gl.acctmap.featureDisabledDesc": "تتطلب هذه الشاشة ميزة دفتر الأستاذ العام أو ميزة تصدير القيود المحاسبية في باقتك.",
      "gl.acctmap.readOnly": "يمكنك عرض الربط فقط. يتطلب تغييره صلاحية «إدارة ربط الحسابات».",
      "gl.acctmap.loadFailed": "تعذّر تحميل ربط الحسابات",
      "gl.acctmap.empty": "لا توجد أدوار ترحيل بعد",
      "gl.acctmap.emptyDesc": "تظهر أدوار الترحيل هنا بعد إعداد دفتر الأستاذ العام لشركتك.",
      "gl.acctmap.unmappedBanner": "أدوار ترحيل بلا حساب: {{count}}. تُرحَّل مبالغها إلى الحساب المعلّق حتى تربطها بحساب.",
      "gl.acctmap.group.sales": "المبيعات والعملاء",
      "gl.acctmap.group.purchases": "المشتريات والموردون",
      "gl.acctmap.group.vat": "ضريبة القيمة المضافة",
      "gl.acctmap.group.inventory": "المخزون والتكلفة",
      "gl.acctmap.group.payroll": "الرواتب",
      "gl.acctmap.group.cash": "النقدية والبنوك",
      "gl.acctmap.group.expenses": "فئات المصروفات",
      "gl.acctmap.group.equity": "حقوق الملكية والإقفال",
      "gl.acctmap.group.advanced": "متقدم",
      "gl.acctmap.advancedHint": "أسباب الحركات النقدية وأنواع القيود. لا تغيّرها إلا بتوجيه من محاسبك.",
      "gl.acctmap.col.role": "دور الترحيل",
      "gl.acctmap.col.current": "الحساب الحالي",
      "gl.acctmap.col.default": "الافتراضي",
      "gl.acctmap.accepts": "يقبل: {{types}}",
      "gl.acctmap.customized": "معدّل",
      "gl.acctmap.customizedCount": "المعدّل: {{count}}",
      "gl.acctmap.unmapped": "بلا حساب",
      "gl.acctmap.inactive": "غير نشط",
      "gl.acctmap.change": "تغيير",
      "gl.acctmap.reset": "استعادة الافتراضي",
      "gl.acctmap.resetNothing": "الحساب الافتراضي مستخدم أصلًا",
      "gl.acctmap.resetTitle": "استعادة الافتراضي لـ«{{label}}»؟",
      "gl.acctmap.resetMsg": "سيعود دور الترحيل هذا إلى الحساب الافتراضي {{code}}.",
      "gl.acctmap.resetMsgNoDefault": "لا يوجد حساب افتراضي لهذا الدور، فسيبقى بلا حساب حتى تربطه من جديد.",
      "gl.acctmap.resetConfirm": "استعادة",
      "gl.acctmap.resetDone": "أُعيد الربط إلى الحساب الافتراضي",
      "gl.acctmap.frozenShort": "مقفل: فترة مقفلة تستخدم هذا الحساب",
      "gl.acctmap.frozenReason": "رُحّلت قيود عبر هذا الدور في فترة مقفلة، وتغييره يعيد عرض تلك الفترة. لنقل الرصيد سجّل قيد يومية في شهر مفتوح، أو أعد فتح الفترة أولًا.",
      "gl.acctmap.role.accounts_receivable": "ذمم العملاء (فواتير المبيعات)",
      "gl.acctmap.role.sales_revenue": "إيرادات المبيعات",
      "gl.acctmap.role.customer_advances": "دفعات العملاء المقدمة",
      "gl.acctmap.role.gateway_clearing": "تسوية بوابات الدفع",
      "gl.acctmap.role.gateway_fees": "رسوم بوابات الدفع",
      "gl.acctmap.role.bad_debt_expense": "الديون المعدومة",
      "gl.acctmap.role.warranty_provision": "مخصص الضمان",
      "gl.acctmap.role.warranty_expense": "مصروف الضمان",
      "gl.acctmap.role.accounts_payable": "ذمم الموردين (فواتير المشتريات)",
      "gl.acctmap.role.purchases": "المشتريات (بنود غير مخزنية)",
      "gl.acctmap.role.accrued_expenses": "المصروفات المستحقة (مصروفات آجلة)",
      "gl.acctmap.role.prepaid_expenses": "المصروفات المدفوعة مقدمًا",
      "gl.acctmap.role.vat_output": "ضريبة القيمة المضافة المستحقة (ضريبة المخرجات)",
      "gl.acctmap.role.vat_input": "ضريبة القيمة المضافة القابلة للاسترداد (ضريبة المدخلات)",
      "gl.acctmap.role.inventory": "المخزون",
      "gl.acctmap.role.cost_of_goods_sold": "تكلفة القطع المبيعة",
      "gl.acctmap.role.inventory_adjustment": "فروقات المخزون وتسوياته",
      "gl.acctmap.role.inventory_writedown": "تخفيض قيمة المخزون",
      "gl.acctmap.role.inventory_in_transit": "مخزون بالطريق بين الفروع",
      "gl.acctmap.role.payroll_salaries_expense": "مصروف الرواتب",
      "gl.acctmap.role.payroll_gosi_expense": "مصروف التأمينات الاجتماعية",
      "gl.acctmap.role.payroll_eosb_expense": "مصروف مكافأة نهاية الخدمة",
      "gl.acctmap.role.payroll_salaries_payable": "رواتب مستحقة الدفع",
      "gl.acctmap.role.payroll_gosi_payable": "التأمينات الاجتماعية المستحقة",
      "gl.acctmap.role.payroll_eosb_provision": "مخصص مكافأة نهاية الخدمة",
      "gl.acctmap.role.payroll_fines_fund": "صندوق جزاءات الموظفين",
      "gl.acctmap.role.payroll_employee_loans": "سلف الموظفين",
      "gl.acctmap.role.leave_liability": "التزام الإجازات السنوية",
      "gl.acctmap.role.leave_expense": "مصروف الإجازات السنوية",
      "gl.acctmap.role.retained_earnings": "الأرباح المبقاة (إقفال السنة)",
      "gl.acctmap.role.document_discrepancy": "الحساب المعلّق (فروقات المستندات)",
      "gl.acctmap.role.rounding_difference": "فروقات التقريب",
      "gl.acctmap.role.asset_disposal_receivable": "مستحقات بيع الأصول الثابتة",
      "gl.acctmap.role.asset_disposal_gain": "أرباح استبعاد الأصول الثابتة",
      "gl.acctmap.role.asset_disposal_loss": "خسائر استبعاد الأصول الثابتة",
      "gl.cc.label": "مركز التكلفة",
      "gl.cc.unallocated": "غير موزع",
      "gl.cc.mixed": "متعدد",
      "gl.cc.choose": "اختر…",
      "gl.cc.headerHint": "تتبعه الأسطر ما لم تحدد لكل سطر مركزه.",
      "gl.cc.requiredOnLine": "اختر مركز تكلفة لكل سطر إيرادات أو مصروفات (فرعًا أو «غير موزع»).",
      "gl.cc.viewCompany": "الشركة",
      "gl.cc.viewCostCenter": "حسب مركز التكلفة",
      "gl.cc.tbTitle": "ميزان المراجعة حسب مركز التكلفة",
      "gl.cc.tbSubtitle": "ميزان مراجعة الشركة بعمود لكل فرع، إضافة إلى المبالغ غير الموزعة على أي فرع.",
      "gl.cc.isTitle": "قائمة الدخل حسب مركز التكلفة",
      "gl.cc.isSubtitle": "الإيرادات والمصروفات وصافي الربح لكل فرع جنبًا إلى جنب.",
      "gl.cc.tbNote": "لا يلزم أن يتوازن عمود الفرع بمفرده، فقد يكون المدين في فرع والدائن في فرع آخر، وقد يكون المستند على مستوى الشركة. وحده عمود الإجمالي يجب أن يتوازن، وهو يساوي ميزان مراجعة الشركة.",
      "gl.cc.isNote": "تظهر المبالغ غير المرتبطة بفرع تحت «غير موزع». عمود الإجمالي يساوي قائمة دخل الشركة.",
      "gl.cc.currencyNote": "المبالغ بالريال السعودي",
      "gl.cc.col.account": "الحساب",
      "gl.cc.total": "الإجمالي",
      "gl.cc.totalRow": "المجموع",
      "gl.cc.differenceRow": "الفرق (مدين − دائن)",
      "gl.cc.sectionTotal": "إجمالي {{section}}",
      "gl.cc.netProfit": "صافي الربح",
      "gl.cc.totalBalanced": "عمود الإجمالي متوازن",
      "gl.cc.totalUnbalanced": "عمود الإجمالي غير متوازن",
      "gl.cc.export": "تصدير CSV",
      "gl.cc.loadFailed": "تعذّر تحميل التقرير حسب مركز التكلفة",
      "gl.cc.empty": "لا توجد قيود في هذه الفترة",
      "gl.cc.emptyDesc": "اختر فترة أخرى، أو تحقق من إصدار مستندات خلالها.",
      "gl.acctmap.changeDesc": "الحساب الحالي {{code}}. يسري الحساب الجديد على القيود في الفترات المفتوحة، ولا تتغير الفترات المقفلة.",
      "gl.acctmap.changeDescUnmapped": "لا يوجد حساب لهذا الدور بعد، فمبالغه في الحساب المعلّق. اختر الحساب الذي تنتمي إليه.",
      "gl.close.warning.cost_center_unknown_branch": "الفرع المبيّن أدناه ليس من فروع هذه الشركة، فأُدرجت مبالغه ضمن «غير موزع».",
      "gl.map.codePlaceholder": "رمز الحساب",
      "gl.cc.tbSubtitleV2": "حركة المدين والدائن في الفترة المحددة لكل مركز تكلفة، دون أرصدة افتتاحية أو ختامية، وتُحسب مباشرةً عند كل عرض.",
      "gl.cc.tbNoteV2": "لا يلزم أن يتوازن عمود كل فرع بمفرده، فقد يُقيَّد المستند الواحد مدينًا على فرع ودائنًا على آخر، أو يكون على مستوى الشركة. وحده عمود الإجمالي يجب أن يتوازن، وهو يساوي عمودَي الحركة في ميزان مراجعة الشركة.",
      "gl.cc.closedSuffix": "(مغلق)",
      "gl.acctmap.changeDescV2": "الحساب الحالي {{code}}. سيُعاد احتساب كل شهر مفتوح يستخدم دور الترحيل هذا بالحساب الجديد، أما الأشهر المقفلة فمحمية.",
      "gl.acctmap.changeDescUnmappedV2": "لا حساب لهذا الدور بعد. سيُعاد احتساب كل شهر مفتوح يستخدم دور الترحيل هذا بالحساب الجديد، أما الأشهر المقفلة فمحمية.",
      "gl.acctmap.resetMsgV2": "سيعود دور الترحيل هذا إلى الحساب الافتراضي {{code}}، وسيُعاد احتساب كل شهر مفتوح يستخدمه بهذا الحساب، أما الأشهر المقفلة فمحمية.",
      "gl.acctmap.resetMsgNoDefaultV2": "لا حساب افتراضيًّا لدور الترحيل هذا، فسيبقى بلا حساب حتى تربطه من جديد، وستظهر مبالغه في الأشهر المفتوحة غير مربوطة، أما الأشهر المقفلة فمحمية.",
      "booking.aiDesign.builderOff": "التصميم بالذكاء الاصطناعي غير متاح، لأن منشئ الموقع غير مفعّل لهذه الشركة.",
      "booking.tabs.branches": "الفروع",
      "booking.branches.title": "فروع الحجز الإلكتروني",
      "booking.branches.summary": "{{enabled}} من {{total}} فروع تستقبل الحجوزات",
      "booking.branches.loadFailed": "تعذر تحميل فروع الحجز",
      "booking.branches.saveSuccess": "تم تحديث حالة الفرع بنجاح",
      "booking.branches.saveFailed": "تعذر حفظ حالة الفرع",
      "booking.branches.acceptsBookings": "يستقبل الحجوزات الإلكترونية",
      "booking.branches.hoursConfigured": "ساعات العمل محددة",
      "booking.branches.noHoursWarning": "لا ساعات عمل — لن تظهر مواعيد للعملاء",
      "booking.branches.editHoursLink": "ساعات العمل",
      "booking.branches.editSettings": "تعديل الإعدادات",
      "booking.branches.confirmDisableLastTitle": "إيقاف الحجز الإلكتروني",
      "booking.branches.confirmDisableLastMessage": "هذا هو الفرع الوحيد الذي يستقبل الحجوزات الإلكترونية. سيؤدي إيقافه إلى إيقاف استقبال الحجوزات الإلكترونية بالكامل.",
      "booking.branches.confirmDisable": "إيقاف",
      "booking.general.branchesHint": "يمكنك إدارة استقبال الحجوزات لجميع الفروع من تبويب ",
      "booking.editor.skip.branchMultipleActiveLead": "سيُتخطّى لأن فرعًا واحدًا فقط يستقبل الحجوزات — ",
      "booking.editor.skip.branchMultipleActiveLink": "فعّل بقية الفروع من تبويب «الفروع»",
      "err.INVALID_WAITLIST_REQUEST": "أدخل بيانات تواصل صحيحة واختر خدمة للانضمام إلى قائمة الانتظار.",
      "err.WAITLIST_DISABLED": "قائمة الانتظار غير متاحة لهذه الورشة.",
      "err.SLOTS_AVAILABLE": "توجد مواعيد متاحة الآن؛ اختر موعدًا متاحًا بدلًا من الانضمام إلى قائمة الانتظار.",
      "err.APPROVAL_LIMIT_EXCEEDED": "هذا المبلغ يتجاوز حدّ الاعتماد الخاص بك، ويجب أن يعتمده من يملك حدًّا أعلى.",
      "err.APPROVAL_LIMIT_SELF_EDIT": "لا يمكنك تعديل حدود الاعتماد الخاصة بحسابك؛ اطلب ذلك من مسؤول آخر.",
      "err.APPROVAL_LIMIT_GRANT_EXCEEDS_OWN": "لا يمكنك منح حدٍّ أعلى من حدّك الخاص.",
      "appt.col.time": "الوقت",
      "appt.override.action": "تجاوز القواعد",
      "appt.override.confirm": "تجاوز القواعد",
      "appt.override.minChars": "يجب إدخال ٥ أحرف على الأقل",
      "appt.override.reasonLabel": "سبب تجاوز القواعد (٥ أحرف على الأقل)",
      "appt.override.reasonPlaceholder": "أدخل سبب تجاوز القواعد…",
      "appt.override.title": "تجاوز قواعد الحجز",
      "appt.rescheduled": "تمت إعادة جدولة الموعد بنجاح",
      "booking.analyticsPlaceholder": "نظرة عامة على تحليلات الحجز",
      "booking.confirm.auto": "تلقائي",
      "booking.confirm.inherit": "افتراضي (موروث)",
      "booking.confirm.manual": "يدوي",
      "booking.deposit.credit": "إضافة إلى رصيد العميل",
      "booking.deposit.desc": "إلزام العملاء بدفع عربون لتأكيد الحجز، مع تحديد نوع العربون ومهلة السداد",
      "booking.deposit.holdMinutes": "مهلة دفع العربون (بالدقائق)",
      "booking.deposit.lateAction": "الإجراء عند تأخر السداد",
      "booking.deposit.loadFailed": "تعذّر تحميل إعدادات العربون",
      "booking.deposit.mode": "نظام العربون",
      "booking.deposit.modeAll": "مطلوب لجميع الحجوزات",
      "booking.deposit.modeOff": "معطل",
      "booking.deposit.modePerService": "حسب الخدمة",
      "booking.deposit.noShowAfter": "مهلة معالجة عربون عدم الحضور (بالدقائق)",
      "booking.deposit.noShowPlaceholder": "مثال: ٢",
      "booking.deposit.refund": "استرداد تلقائي",
      "booking.deposit.saveFailed": "تعذّر حفظ إعدادات العربون",
      "booking.deposit.saved": "تم حفظ إعدادات العربون بنجاح",
      "booking.deposit.title": "إعدادات العربون والدفع المسبق",
      "booking.deposit.type": "طريقة حساب العربون",
      "booking.deposit.typeFixed": "مبلغ ثابت (ر.س)",
      "booking.deposit.typePercent": "نسبة مئوية (%)",
      "booking.deposit.value": "قيمة العربون",
      "booking.domain.cancel": "إلغاء",
      "booking.domain.checkNow": "تحقّق الآن",
      "booking.editor.top.fullPreview": "معاينة كاملة",
      "booking.editor.top.fullPreviewHint": "تُخفي اللوحات وتعرض الصفحة كاملة كما يراها العميل.",
      "booking.editor.rail.pages": "الصفحات",
      "booking.editor.rail.header": "القائمة والترويسة",
      "booking.editor.empty.starterPage": "إضافة واجهة رئيسية",
      "booking.editor.publish.wholeSite": "الموقع كله",
      "booking.editor.preview.toolbar": "أدوات المعاينة",
      "booking.editor.preview.device": "الجهاز",
      "booking.editor.preview.exit": "خروج من المعاينة",
      "booking.editor.pages.title": "الصفحات",
      "booking.editor.pages.booking": "تفعيل الحجز",
      "booking.editor.pages.bookingOnHint": "يحجز العملاء من نموذج الحجز؛ يوجد نموذج واحد في الموقع كله.",
      "booking.editor.pages.bookingOffHint": "الموقع للتعريف فقط: لا نموذج حجز ولا روابط إليه.",
      "booking.editor.pages.bookingOffWizard": "ما زال في الموقع نموذج حجز؛ ستطلب منك قائمة النشر إزالته.",
      "booking.editor.pages.bookingOffTitle": "إيقاف الحجز",
      "booking.editor.pages.bookingOffMessage": "سيُزال نموذج الحجز من الموقع، وتُزال روابط القائمة إليه، وتُخفى الأزرار التي تؤدي إليه. يمكنك إعادة تفعيل الحجز لاحقًا.",
      "booking.editor.pages.bookingOffConfirm": "إيقاف الحجز",
      "booking.editor.pages.titleAr": "العنوان بالعربية",
      "booking.editor.pages.titleEn": "العنوان بالإنجليزية",
      "booking.editor.pages.slug": "عنوان الصفحة في الرابط",
      "booking.editor.pages.slugProblem.format": "استخدم حروفًا إنجليزية صغيرة وأرقامًا وشرطات فقط، حتى ٣٢ حرفًا.",
      "booking.editor.pages.slugProblem.reserved": "هذا العنوان محجوز للنظام؛ اختر غيره.",
      "booking.editor.pages.slugProblem.taken": "توجد صفحة بهذا العنوان؛ اختر غيره.",
      "booking.editor.pages.save": "حفظ",
      "booking.editor.pages.add": "إضافة",
      "booking.editor.pages.new": "إضافة صفحة",
      "booking.editor.pages.rename": "إعادة تسمية «{{name}}»",
      "booking.editor.pages.up": "تقديم «{{name}}»",
      "booking.editor.pages.down": "تأخير «{{name}}»",
      "booking.editor.pages.delete": "حذف «{{name}}»",
      "booking.editor.pages.max": "حتى {{n}} صفحات إلى جانب الرئيسية.",
      "booking.editor.pages.deleteTitle": "حذف الصفحة",
      "booking.editor.pages.deleteMessage": "ستُحذف صفحة «{{name}}» وكل أقسامها، وتُزال روابط القائمة إليها. هل تريد المتابعة؟",
      "booking.editor.pages.deleteConfirm": "حذف الصفحة",
      "booking.editor.nav.title": "القائمة والترويسة",
      "booking.editor.nav.intro": "أضف ترويسة تظهر في كل صفحات الموقع، فيها الشعار وقائمة الروابط وزر الحجز.",
      "booking.editor.nav.start": "إضافة ترويسة وقائمة",
      "booking.editor.nav.items": "عناصر القائمة",
      "booking.editor.nav.itemsHint": "كل عنصر يفتح صفحة أو ينتقل إلى قسم في صفحة.",
      "booking.editor.nav.untitled": "عنصر بلا اسم",
      "booking.editor.nav.up": "تقديم «{{name}}»",
      "booking.editor.nav.down": "تأخير «{{name}}»",
      "booking.editor.nav.remove": "حذف «{{name}}»",
      "booking.editor.nav.label": "النص الظاهر",
      "booking.editor.nav.add": "إضافة عنصر",
      "booking.editor.nav.max": "حتى {{n}} عناصر.",
      "booking.editor.nav.cta": "زر الدعوة",
      "booking.editor.nav.ctaHint": "زر بارز في طرف الترويسة، مثل «احجز الآن».",
      "booking.editor.nav.ctaShow": "إظهار الزر",
      "booking.editor.nav.look": "شكل الترويسة",
      "booking.editor.nav.height": "ارتفاع الترويسة",
      "booking.editor.nav.logoSize": "حجم الشعار",
      "booking.editor.nav.size.s": "صغير",
      "booking.editor.nav.size.m": "متوسط",
      "booking.editor.nav.size.l": "كبير",
      "booking.editor.nav.sticky": "تبقى ظاهرة عند التمرير",
      "booking.editor.nav.removeHeader": "إزالة الترويسة",
      "booking.editor.target.label": "يؤدي إلى",
      "booking.editor.target.choose": "اختر الوجهة",
      "booking.editor.target.type.page": "صفحة في الموقع",
      "booking.editor.target.type.section": "قسم في صفحة",
      "booking.editor.target.type.booking": "نموذج الحجز",
      "booking.editor.target.type.phone": "اتصال هاتفي",
      "booking.editor.target.type.whatsapp": "واتساب",
      "booking.editor.target.type.url": "رابط خارجي",
      "booking.editor.target.page": "الصفحة",
      "booking.editor.target.section": "القسم",
      "booking.editor.target.noSections": "لا أقسام في هذه الصفحة بعد",
      "booking.editor.target.url": "الرابط",
      "booking.editor.target.ownNumber": "يُستخدم رقم المنشأة المسجل في النظام.",
      "booking.editor.target.problem.page": "الصفحة غير موجودة.",
      "booking.editor.target.problem.section": "القسم غير موجود في هذه الصفحة.",
      "booking.editor.target.problem.url": "اكتب رابطًا كاملًا يبدأ بـ https://",
      "booking.editor.target.problem.booking": "الحجز غير مفعّل، فلا يعمل هذا الرابط.",
      "booking.editor.target.problem.type": "اختر وجهة الرابط.",
      "booking.editor.slides.untitled": "إعلان {{n}}",
      "booking.editor.slides.up": "تقديم «{{name}}»",
      "booking.editor.slides.down": "تأخير «{{name}}»",
      "booking.editor.slides.remove": "حذف «{{name}}»",
      "booking.editor.slides.needImage": "أضف صورة لهذا الإعلان قبل النشر.",
      "booking.editor.slides.image": "صورة الإعلان",
      "booking.editor.slides.alt": "وصف الصورة لقارئ الشاشة",
      "booking.editor.slides.title": "العنوان",
      "booking.editor.slides.text": "النص",
      "booking.editor.slides.align": "موضع النص",
      "booking.editor.slides.alignTo.start": "البداية",
      "booking.editor.slides.alignTo.center": "الوسط",
      "booking.editor.slides.alignTo.end": "النهاية",
      "booking.editor.slides.overlay": "التظليل",
      "booking.editor.slides.overlayHint": "طبقة داكنة فوق الصورة تجعل النص مقروءًا.",
      "booking.editor.slides.veil.none": "بلا",
      "booking.editor.slides.veil.s": "خفيف",
      "booking.editor.slides.veil.m": "متوسط",
      "booking.editor.slides.veil.l": "كثيف",
      "booking.editor.slides.button": "زر في الإعلان",
      "booking.editor.slides.buttonLabel": "نص الزر",
      "booking.editor.slides.add": "إضافة إعلان",
      "booking.editor.slides.max": "حتى {{n}} إعلانات.",
      "booking.editor.slider.slides": "الإعلانات",
      "booking.editor.slider.on": "نعم",
      "booking.editor.slider.off": "لا",
      "booking.editor.slider.autoplay": "تشغيل تلقائي",
      "booking.editor.slider.interval": "مدة كل إعلان (ثوانٍ)",
      "booking.editor.slider.loop": "تكرار من البداية",
      "booking.editor.slider.arrows": "أسهم التنقل",
      "booking.editor.slider.dots": "نقاط التنقل",
      "booking.editor.slider.transition": "الانتقال",
      "booking.editor.slider.transition.slide": "انزلاق",
      "booking.editor.slider.transition.fade": "تلاشٍ",
      "booking.editor.group.motion": "الحركة",
      "booking.editor.group.size": "الحجم والمسافات",
      "booking.editor.group.alignWidth": "المحاذاة والعرض",
      "booking.editor.size.device": "الجهاز",
      "booking.editor.size.desktop": "الحاسوب",
      "booking.editor.size.mobile": "الجوال",
      "booking.editor.size.phoneHint": "قيم الجوال تتبع الحاسوب حتى تغيّرها.",
      "booking.editor.size.inherited": "مأخوذة من الحاسوب",
      "booking.editor.size.followDesktop": "اتباع الحاسوب",
      "booking.editor.size.height": "الارتفاع",
      "booking.editor.size.h.default": "تلقائي",
      "booking.editor.size.h.s": "قصير",
      "booking.editor.size.h.m": "متوسط",
      "booking.editor.size.h.l": "طويل",
      "booking.editor.size.h.full": "ملء الشاشة",
      "booking.editor.size.gap": "المسافة بين العناصر",
      "booking.editor.size.gapStep.default": "تلقائية",
      "booking.editor.size.gapStep.s": "صغيرة",
      "booking.editor.size.gapStep.m": "متوسطة",
      "booking.editor.size.gapStep.l": "كبيرة",
      "booking.editor.size.columns": "الأعمدة",
      "booking.editor.size.auto": "تلقائي",
      "booking.editor.check.wizardOff": "الحجز غير مفعّل، وما زال في الموقع نموذج حجز.",
      "booking.editor.check.bookingLink": "الرابط «{{name}}» يؤدي إلى نموذج الحجز والحجز غير مفعّل.",
      "booking.editor.check.link.nav": "عنصر القائمة «{{name}}» يؤدي إلى صفحة أو قسم غير موجود أو رابط غير صالح.",
      "booking.editor.check.link.cta": "زر الترويسة «{{name}}» يؤدي إلى وجهة غير صالحة.",
      "booking.editor.check.link.slide": "زر في «{{name}}» يؤدي إلى وجهة غير صالحة.",
      "booking.editor.check.slideImage": "في «{{name}}» إعلان بلا صورة.",
      "set.websiteDesign": "تصميم الموقع",
      "booking.aiDesign.usage.usage": "استُخدم {{used}} من {{limit}} هذا الشهر، يتجدد في {{date}}",
      "booking.aiDesign.usage.reset": "إعادة تعيين المحاولات",
      "booking.aiDesign.usage.resetTitle": "إعادة تعيين محاولات التصميم",
      "booking.aiDesign.usage.resetMessage": "تبدأ محاولات تصميم الصفحة بالذكاء الاصطناعي من الصفر لهذا الشهر. التصاميم السابقة تبقى. هل تريد المتابعة؟",
      "booking.aiDesign.usage.resetDone": "أعيد تعيين المحاولات",
      "booking.aiDesign.usage.resetFailed": "تعذرت إعادة التعيين",
      "booking.aiDesign.usage.loadFailed": "تعذر تحميل الاستخدام",
      "set.websiteDesignHint": "صفحات موقعك وقائمته وتصميمه، مع صفحة الحجز إن فعّلتها.",
      "booking.editor.lang.ar": "بالعربية",
      "booking.editor.lang.en": "بالإنجليزية",
      "booking.editor.tabs.content": "المحتوى",
      "booking.editor.tabs.design": "التصميم",
      "booking.editor.tabs.advanced": "متقدم",
      "booking.editor.panel.page": "إعدادات الصفحة",
      "booking.editor.panel.pageHint": "اختر قسمًا في المعاينة لتعديله، أو عدّل إعدادات الصفحة هنا.",
      "booking.editor.panel.noFields": "لا توجد إعدادات في هذا التبويب.",
      "booking.editor.group.text": "النصوص",
      "booking.editor.group.media": "الصور",
      "booking.editor.group.items": "العناصر",
      "booking.editor.group.layout": "التخطيط",
      "booking.editor.group.colours": "الألوان",
      "booking.editor.group.background": "الخلفية",
      "booking.editor.group.spacing": "المسافات والعرض",
      "booking.editor.group.visibility": "الظهور",
      "booking.editor.group.brand": "الهوية",
      "booking.editor.group.type": "الخطوط",
      "booking.editor.group.shape": "الأشكال",
      "booking.editor.group.booking": "خطوات الحجز",
      "booking.editor.group.contact": "بيانات التواصل",
      "booking.editor.hint.layout": "اختر شكل القسم؛ يبقى المحتوى كما هو.",
      "booking.editor.hint.spacing": "المسافة أعلى القسم وأسفله.",
      "booking.editor.hint.textColor": "إن لم يكن اللون مقروءًا على الخلفية يُستبدل تلقائيًا.",
      "booking.editor.hint.hide": "يبقى القسم ظاهرًا هنا بشفافية حتى تجده.",
      "booking.editor.hint.focal": "انقر على الصورة لتحديد الجزء الأهم؛ يبقى ظاهرًا عند القص.",
      "booking.editor.style.group": "التنسيق",
      "booking.editor.style.background": "نوع الخلفية",
      "booking.editor.style.bg.none": "تلقائية",
      "booking.editor.style.bg.color": "لون",
      "booking.editor.style.bg.image": "صورة",
      "booking.editor.style.backgroundColor": "لون الخلفية",
      "booking.editor.style.backgroundImage": "صورة الخلفية",
      "booking.editor.style.textColor": "لون النص",
      "booking.editor.style.textAuto": "تلقائي",
      "booking.editor.style.alignment": "المحاذاة",
      "booking.editor.style.align.default": "تلقائية",
      "booking.editor.style.align.start": "البداية",
      "booking.editor.style.align.center": "الوسط",
      "booking.editor.style.spacing": "المسافة العمودية",
      "booking.editor.style.space.default": "تلقائية",
      "booking.editor.style.space.s": "صغيرة",
      "booking.editor.style.space.m": "متوسطة",
      "booking.editor.style.space.l": "كبيرة",
      "booking.editor.style.width": "العرض",
      "booking.editor.style.width.default": "تلقائي",
      "booking.editor.style.width.full": "كامل",
      "booking.editor.style.width.contained": "داخل إطار",
      "booking.editor.style.hideOnMobile": "إخفاء على الجوال",
      "booking.editor.style.hideOnDesktop": "إخفاء على الحاسوب",
      "booking.editor.style.focal": "نقطة التركيز",
      "booking.editor.style.reset": "إعادة التنسيق الافتراضي",
      "booking.editor.contrast.low": "النص غير مقروء على هذه الخلفية (التباين {{ratio}}). يظهر الآن بلون بديل.",
      "booking.editor.contrast.fix": "إصلاح",
      "booking.editor.color.theme": "ألوان الهوية",
      "booking.editor.color.custom": "لون مخصص",
      "booking.editor.color.clear": "بلا لون",
      "booking.editor.color.primary": "الأساسي",
      "booking.editor.color.secondary": "الثانوي",
      "booking.editor.color.accent": "التمييز",
      "booking.editor.color.surface": "الخلفية",
      "booking.editor.color.ink": "النص",
      "booking.editor.image.upload": "رفع صورة",
      "booking.editor.image.replace": "استبدال",
      "booking.editor.image.remove": "إزالة",
      "booking.editor.image.uploading": "جارٍ الرفع…",
      "booking.editor.image.empty": "لم تُختر صورة",
      "booking.editor.image.notImage": "اختر ملف صورة.",
      "booking.editor.image.tooLarge": "الصورة أكبر من ٥ ميغابايت.",
      "booking.editor.image.failed": "تعذّر رفع الصورة.",
      "booking.editor.image.types": "JPG أو PNG أو WebP حتى ٥ ميغابايت",
      "booking.editor.toolbar.up": "تحريك لأعلى",
      "booking.editor.toolbar.down": "تحريك لأسفل",
      "booking.editor.toolbar.duplicate": "تكرار",
      "booking.editor.toolbar.hideMobile": "إخفاء على الجوال",
      "booking.editor.toolbar.hideDesktop": "إخفاء على الحاسوب",
      "booking.editor.toolbar.style": "التصميم",
      "booking.editor.toolbar.delete": "حذف",
      "booking.editor.toolbar.parent": "تحديد القسم الأب",
      "booking.editor.toast.deleted": "حُذف القسم.",
      "booking.editor.toast.undo": "تراجع",
      "booking.editor.top.title": "تصميم الموقع",
      "booking.editor.top.undo": "تراجع",
      "booking.editor.top.redo": "إعادة",
      "booking.editor.top.preview": "معاينة",
      "booking.editor.top.previewHint": "تفتح المسودة في تبويب جديد كما ستظهر للعملاء.",
      "booking.editor.top.publish": "نشر",
      "booking.editor.top.more": "المزيد",
      "booking.editor.top.tour": "جولة سريعة",
      "booking.editor.save.saving": "جارٍ الحفظ…",
      "booking.editor.save.saved": "محفوظ",
      "booking.editor.save.error": "تعذّر الحفظ",
      "booking.editor.save.idle": "لا تغييرات",
      "booking.editor.save.publishing": "جارٍ النشر…",
      "booking.editor.publish.title": "قبل النشر",
      "booking.editor.publish.ready": "كل شيء جاهز. ستظهر الصفحة للعملاء فور النشر.",
      "booking.editor.publish.blocked": "أصلح المشكلات التالية لتتمكن من النشر:",
      "booking.editor.publish.warnings": "ملاحظات لا تمنع النشر:",
      "booking.editor.publish.confirm": "نشر الآن",
      "booking.editor.publish.cancel": "إلغاء",
      "booking.editor.publish.fix": "إصلاح",
      "booking.editor.publish.go": "انتقال",
      "booking.editor.check.wizardMissing": "لا يوجد نموذج حجز في الصفحة، فلن يستطيع العملاء الحجز.",
      "booking.editor.check.wizardDuplicate": "في الصفحة أكثر من نموذج حجز؛ يُسمح بنموذج واحد.",
      "booking.editor.check.primaryContrast": "اللون الأساسي فاتح جدًا للنص الأبيض على الأزرار.",
      "booking.editor.check.inkContrast": "لون نص الصفحة غير مقروء على خلفيتها.",
      "booking.editor.check.blockContrast": "نص «{{name}}» غير مقروء على خلفيته.",
      "booking.editor.check.image": "صورة في «{{name}}» ليست من صور هذه الصفحة، فلن تُقبل.",
      "booking.editor.check.rootImage": "صورة في إعدادات الصفحة ليست من صور هذه الصفحة.",
      "booking.editor.check.empty": "القسم «{{name}}» فارغ وسيظهر للعملاء بلا محتوى.",
      "booking.editor.library.title": "إضافة قسم",
      "booking.editor.library.hint": "انقر على قسم لإضافته بعد القسم المحدد، أو اسحبه من القائمة المختصرة.",
      "booking.editor.library.gallery": "معرض",
      "booking.editor.library.compact": "قائمة مختصرة",
      "booking.editor.library.added": "أُضيف «{{name}}».",
      "booking.editor.library.wizardExists": "في الصفحة نموذج حجز بالفعل.",
      "booking.editor.purpose.hero": "الواجهة",
      "booking.editor.purpose.services": "الخدمات",
      "booking.editor.purpose.trust": "الثقة",
      "booking.editor.purpose.gallery": "معرض الأعمال",
      "booking.editor.purpose.booking": "الحجز",
      "booking.editor.purpose.contact": "التواصل",
      "booking.editor.purpose.basics": "عناصر أساسية",
      "booking.editor.rail.sections": "الأقسام",
      "booking.editor.rail.layers": "الطبقات",
      "booking.editor.rail.identity": "الهوية",
      "booking.editor.identity.title": "هوية الصفحة",
      "booking.editor.identity.hint": "اختر طابعًا جاهزًا ثم عدّل التفاصيل من «إعدادات الصفحة».",
      "booking.editor.identity.applied": "طُبّق طابع «{{name}}».",
      "booking.editor.identity.current": "الحالي",
      "booking.editor.identity.fromLogo": "من الشعار",
      "booking.editor.identity.fromLogoHint": "يستخرج ألوان شعارك ويبني منها طابعًا مقروءًا.",
      "booking.editor.identity.noLogo": "ارفع الشعار أولًا من إعدادات الصفحة.",
      "booking.editor.identity.logoFailed": "تعذّر استخراج ألوان الشعار.",
      "booking.editor.identity.working": "جارٍ قراءة الألوان…",
      "booking.editor.tour.next": "التالي",
      "booking.editor.tour.done": "ابدأ",
      "booking.editor.tour.skip": "تخطٍّ",
      "booking.editor.tour.step": "الخطوة {{n}} من {{total}}",
      "booking.editor.tour.1.title": "أضف أقسامًا جاهزة",
      "booking.editor.tour.1.body": "من «الأقسام» اختر قسمًا مصممًا مسبقًا فيُضاف بعد القسم المحدد.",
      "booking.editor.tour.2.title": "عدّل بالنقر",
      "booking.editor.tour.2.body": "انقر قسمًا في المعاينة لتظهر أدواته، وانقر نصًا مرتين لتعديله في اللوحة.",
      "booking.editor.tour.3.title": "اختر هويتك ثم انشر",
      "booking.editor.tour.3.body": "من «الهوية» طبّق طابعًا جاهزًا، ثم «نشر» يتحقق من الصفحة قبل ظهورها للعملاء.",
      "booking.editor.empty.title": "صفحتك فارغة",
      "booking.editor.empty.body": "ابدأ بواجهة رئيسية ونموذج حجز، أو أضف أي قسم من «الأقسام».",
      "booking.editor.empty.starter": "إضافة واجهة ونموذج حجز",
      "booking.editor.kbd.title": "اختصارات لوحة المفاتيح",
      "booking.editor.previewRibbon": "معاينة المسودة — لم تُنشر بعد",
      "booking.editor.device.mobile": "جوال",
      "booking.editor.device.tablet": "جهاز لوحي",
      "booking.editor.device.desktop": "حاسوب",
      "booking.domain.checking": "جارٍ التحقق…",
      "booking.domain.confirm": "تأكيد الإزالة",
      "booking.domain.confirmRemove": "هل أنت متأكد من رغبتك في إزالة هذا النطاق المخصص؟ سيتوقف تقديم صفحة الحجز عبر هذا النطاق.",
      "booking.domain.connect": "ربط النطاق",
      "booking.domain.connecting": "جارٍ الربط…",
      "booking.domain.copied": "تم النسخ!",
      "booking.domain.copy": "نسخ",
      "booking.domain.description": "تقديم صفحة الحجز عبر نطاقك المخصص",
      "booking.domain.dnsInstructions": "أضف سجلات DNS التالية لدى مزود النطاق الخاص بك:",
      "booking.domain.errors.CNAME_MISMATCH": "سجل CNAME لا يشير إلى book.mmind.app",
      "booking.domain.errors.DOMAIN_TAKEN": "هذا النطاق مسجل لورشة أخرى بالفعل",
      "booking.domain.errors.INVALID_HOST": "صيغة النطاق غير صالحة. يُرجى إدخال نطاق صالح (مثال: book.yourdomain.sa)",
      "booking.domain.errors.RESERVED_HOST": "لا يمكن استخدام هذا النطاق",
      "booking.domain.errors.certFailed": "تعذّر إصدار شهادة الأمان لهذا النطاق. تأكّد من السجلين ثم تحقّق مرة أخرى، وتواصل مع الدعم إن استمرت المشكلة.",
      "booking.domain.errors.TXT_MISSING": "سجل التحقق TXT مفقود أو غير مطابق",
      "booking.domain.hostLabel": "اسم النطاق",
      "booking.domain.hostPlaceholder": "مثال: book.autofix.sa",
      "booking.domain.recordName": "الاسم",
      "booking.domain.recordType": "النوع",
      "booking.domain.recordValue": "القيمة",
      "booking.domain.remove": "إزالة النطاق",
      "booking.domain.status.cert_issued": "نشط وآمن",
      "booking.domain.status.failed": "فشل الإعداد",
      "booking.domain.status.none": "غير مهيأ",
      "booking.domain.status.pending_dns": "بانتظار DNS",
      "booking.domain.status.verified": "تم التحقق من DNS (جارٍ إصدار الشهادة)",
      "booking.domain.title": "النطاق المخصص",
      "booking.domainPlaceholder": "إعدادات النطاق المخصص",
      "booking.errors.FIELD_OUT_OF_RANGE": "القيمة خارج النطاق المسموح به",
      "booking.errors.durationRequired": "حدد مدة الخدمة قبل تفعيل الحجز الإلكتروني",
      "booking.errors.invalid": "بيانات غير صالحة",
      "booking.errors.outOfRange": "القيمة خارج النطاق المسموح به",
      "booking.general.autoConfirm": "التأكيد التلقائي للمواعيد",
      "booking.general.autoConfirmDesc": "تأكيد الحجز فورًا دون الحاجة لموافقة يدوية",
      "booking.general.cancelCutoffMinutes": "الحد الزمني للإلغاء الذاتي (بالدقائق)",
      "booking.general.enabled": "تفعيل الحجز عبر الإنترنت",
      "booking.general.enabledDesc": "السماح للعملاء بحجز المواعيد من خلال صفحة الحجز العامة",
      "booking.general.horizonDays": "أقصى مدة للحجز مسبقًا (بالأيام)",
      "booking.general.leadTimeMinutes": "أقل مدة قبل الموعد (بالدقائق)",
      "booking.general.loadFailed": "تعذّر تحميل الإعدادات",
      "booking.general.multiService": "السماح باختيار عدة خدمات",
      "booking.general.multiServiceDesc": "يمكن للعميل اختيار أكثر من خدمة في الحجز الواحد",
      "booking.general.noShowGraceMinutes": "فترة السماح قبل احتساب عدم الحضور (بالدقائق)",
      "booking.general.pendingExpiryMinutes": "مهلة انتهاء الحجز المعلق (بالدقائق)",
      "booking.general.rescheduleCutoffMinutes": "الحد الزمني لإعادة الجدولة (بالدقائق)",
      "booking.general.saveFailed": "تعذّر حفظ الإعدادات",
      "booking.general.saved": "تم حفظ الإعدادات بنجاح",
      "booking.general.slotMinutes": "مدة الفترة (بالدقائق)",
      "booking.general.timezone": "المنطقة الزمنية",
      "booking.general.title": "الإعدادات العامة للحجز",
      "booking.header.subtitle": "إدارة ساعات العمل، أماكن الخدمة، الخدمات، وإعدادات الحجز للعملاء",
      "booking.header.title": "إعدادات الحجز الإلكتروني",
      "booking.hours.addClosure": "إضافة إغلاق",
      "booking.hours.addShift": "إضافة فترة",
      "booking.hours.closed": "مغلق",
      "booking.hours.closureDate": "تاريخ الإغلاق",
      "booking.hours.closureExists": "هذا التاريخ مضاف مسبقًا",
      "booking.hours.closuresTitle": "أيام الإغلاق والعطل الرسمية",
      "booking.hours.invalidFormat": "بيانات ساعات العمل غير صحيحة",
      "booking.hours.loadFailed": "تعذّر تحميل ساعات العمل",
      "booking.hours.noClosures": "لا توجد أيام إغلاق مجدولة",
      "booking.hours.overlap": "تداخل في فترات العمل في هذا اليوم",
      "booking.hours.saveFailed": "تعذّر حفظ ساعات العمل",
      "booking.hours.saved": "تم حفظ ساعات العمل بنجاح",
      "booking.hours.title": "ساعات وفترات العمل الأسبوعية",
      "booking.messages.desc": "إرسال إشعارات وتذكيرات تلقائية عبر واتساب والرسائل النصية للعملاء قبل موعدهم",
      "booking.messages.previewsDesc": "يتم إرسال الرسائل باللغة العربية أو الإنجليزية وفق لغة العميل المفضلة",
      "booking.messages.previewsTitle": "معاينة نماذج الرسائل المرسلة للعميل",
      "booking.messages.reminder1": "موعد التذكير الأول (بالدقائق قبل الموعد)",
      "booking.messages.reminder1Hint": "مثال: ١٤٤٠ دقيقة تعادل ٢٤ ساعة قبل الموعد",
      "booking.messages.reminder2": "موعد التذكير الثاني (بالدقائق قبل الموعد)",
      "booking.messages.reminder2Hint": "مثال: ١٢٠ دقيقة تعادل ساعتين قبل الموعد",
      "booking.messages.reminder2Placeholder": "اختياري (اتركه فارغًا لإلغائه)",
      "booking.messages.saved": "تم حفظ إعدادات الرسائل والتذكيرات بنجاح",
      "booking.messages.title": "إعدادات رسائل التذكير التلقائية",
      "booking.noPermissions": "ليست لديك الصلاحية للاطلاع على إعدادات الحجز",
      "booking.pagePlaceholder": "محرر تصميم الصفحة",
      "booking.refusal.beyondHorizon": "الموعد يتجاوز أقصى مدة للحجز مسبقًا",
      "booking.refusal.branchNotBookable": "الفرع غير متاح للحجز عبر الإنترنت",
      "booking.refusal.dayClosed": "الورشة مغلقة في هذا اليوم",
      "booking.refusal.general": "تعذّر نقل الموعد بسبب تعارض مع القواعد",
      "booking.refusal.noBayFree": "لا يوجد مكان خدمة متاح في هذا الوقت",
      "booking.refusal.offGrid": "الموعد غير متوافق مع فترات الحجز",
      "booking.refusal.outsideHours": "الموعد خارج ساعات العمل",
      "booking.refusal.serviceNotBookable": "الخدمة المطلوبة غير متاحة للحجز عبر الإنترنت",
      "booking.refusal.tooSoon": "الموعد يقل عن أقل مدة قبل الموعد",
      "booking.selectTab": "أقسام الحجز الإلكتروني",
      "booking.services.colBayTypes": "فئات أماكن الخدمة",
      "booking.services.colBookable": "حجز عبر الإنترنت",
      "booking.services.colBuffer": "الفاصل (دقيقة)",
      "booking.services.colConfirm": "وضع التأكيد",
      "booking.services.colDeposit": "عربون خاص (ر.س)",
      "booking.services.colDuration": "المدة (دقيقة)",
      "booking.services.colName": "الخدمة",
      "booking.services.desc": "حدد الخدمات المتاحة للحجز عبر الإنترنت ومدة كل خدمة",
      "booking.services.saveFailed": "تعذّر حفظ الخدمة",
      "booking.services.saved": "تم تحديث إعدادات الخدمة",
      "booking.services.title": "إعدادات الخدمات للحجز عبر الإنترنت",
      "booking.tabs.analytics": "التحليلات",
      "booking.tabs.deposit": "العربون",
      "booking.tabs.domain": "النطاق المخصص",
      "booking.tabs.general": "عام",
      "booking.tabs.hours": "ساعات العمل",
      "booking.tabs.messages": "الرسائل والتذكيرات",
      "booking.tabs.page": "تصميم الصفحة",
      "booking.domain.emptyTitle": "ما هو النطاق المخصص؟",
      "booking.domain.emptyExplainer": "يمكنك ربط نطاقك الخاص (مثل «احجز.ورشتك.com» أو «book.yourworkshop.sa») بدلاً من رابط موتورمايند المباشر، ليظهر لعملائك بهوية ورشتك الخاصة وعلامتك التجارية.",
      "booking.domain.defaultLinkTitle": "الرابط الافتراضي الحالي لحجز ورشتك:",
      "booking.domain.stepGuideTitle": "دليل الإعداد (٣ خطوات)",
      "booking.domain.step1Title": "إضافة سجلي DNS لدى موفر النطاق",
      "booking.domain.step1Desc": "أضف هذين السجلين في لوحة تحكم مزود النطاق الخاص بك (مثل Cloudflare أو GoDaddy):",
      "booking.domain.step2Title": "التحقق من السجلات",
      "booking.domain.step2Desc": "بعد حفظ السجلات لدى موفر النطاق، اضغط على «تحقّق الآن». فحص سجلات DNS متاح مرة كل ٥ دقائق.",
      "booking.domain.step3Title": "إصدار شهادة الأمان تلقائياً",
      "booking.domain.step3Desc": "يتم إصدار شهادة الأمان (SSL) تلقائياً بمجرد اكتمال التحقق من السجلات، ويصبح الرابط مفعّلاً مباشرة.",
      "booking.domain.cooldownNotice": "فحص سجلات DNS متاح مرة كل ٥ دقائق لضمان استقرار الشبكة.",
      "booking.domain.cooldownRemaining": "الفحص التالي متاح خلال {{time}}",
      "booking.domain.liveUrlTitle": "رابط الحجز المباشر:",
      "booking.domain.openLink": "فتح الرابط",
      "booking.domain.confirmRemoveTitle": "إزالة النطاق المخصص",
      "booking.domain.errors.DOMAIN_NOT_SET": "لم يتم تعيين نطاق مخصص",
      "booking.domain.validation.noSpaces": "لا يمكن أن يحتوي اسم النطاق على مسافات",
      "booking.domain.validation.noProtocol": "أدخل اسم النطاق فقط دون http:// أو مسارات إضافية",
      "booking.domain.validation.noIp": "يرجى إدخال اسم نطاق صالح وليس عنوان IP أو localhost",
      "booking.domain.validation.reserved": "هذا النطاق محجوز لموتورمايند ولا يمكن استخدامه",
      "booking.domain.validation.needDot": "يجب أن يحتوي اسم النطاق على نقطة واحدة على الأقل (مثال: book.autofix.sa)",
      "booking.editor.customer.vehicle": "بعد التحقق يختار العميل العائد مركبة مسجلة أو يضيف مركبة جديدة. ويُدخل العميل الجديد مركبته.",
      "booking.editor.customerPreview": "نوع العميل في المعاينة",
      "booking.editor.customerPreview.new": "عميل جديد",
      "booking.editor.customerPreview.returning": "عميل سابق",
      "booking.wizard.sampleData": "بيانات توضيحية للمعاينة",
      "booking.wizard.addVehicle": "إضافة مركبة جديدة",
      "booking.aiDesign.disabled": "ميزة التصميم بالذكاء الاصطناعي غير مفعّلة لحسابك",
      "booking.aiDesign.briefInvalid": "بعض إجابات متطلبات التصميم غير صالحة.",
      "booking.aiDesign.rateLimited": "طلبات كثيرة جدًا. انتظر قبل إعادة المحاولة.",
      "booking.aiDesign.business": "النشاط",
      "booking.aiDesign.identity": "الهوية",
      "booking.aiDesign.sections": "الأقسام",
      "booking.aiDesign.materials": "المواد",
      "booking.aiDesign.analyse": "تحليل المتطلبات",
      "booking.aiDesign.design": "تصميم الصفحة",
      "booking.aiDesign.verify": "التحقق",
      "booking.aiDesign.save": "حفظ المسودة",
      "booking.aiDesign.unavailable": "خدمة التصميم غير متاحة حالياً. حاول لاحقاً.",
      "booking.aiDesign.limit": "بلغت الحد الشهري. يتجدد في",
      "booking.aiDesign.invalid": "تعذر التحقق من التصميم. حاول مرة أخرى.",
      "booking.aiDesign.uploadFailed": "تعذر رفع الصورة أو تجاوزت الحد المسموح.",
      "booking.aiDesign.logoNeeded": "أضف شعاراً أولاً.",
      "booking.aiDesign.title": "صمّم بالذكاء الاصطناعي",
      "booking.aiDesign.used": "استخدمت",
      "booking.aiDesign.of": "من",
      "booking.aiDesign.thisMonth": "هذا الشهر",
      "booking.aiDesign.steps": "خطوات التصميم",
      "booking.aiDesign.type": "نوع النشاط",
      "booking.aiDesign.audience": "العملاء",
      "booking.aiDesign.tone": "النبرة",
      "booking.aiDesign.logo": "الشعار",
      "booking.aiDesign.primary": "اللون الأساسي",
      "booking.aiDesign.secondary": "اللون الثانوي",
      "booking.aiDesign.extract": "استخرج من الشعار",
      "booking.aiDesign.style": "النمط",
      "booking.aiDesign.font": "الخط",
      "booking.aiDesign.wizardAlways": "نموذج الحجز مضمّن دائماً.",
      "booking.aiDesign.order": "ترتيب الأقسام",
      "booking.aiDesign.recommended": "الترتيب المقترح",
      "booking.aiDesign.selectedOrder": "ترتيب الاختيار",
      "booking.aiDesign.photos": "صور الورشة والسيارات (حتى ٨)",
      "booking.aiDesign.references": "لقطات مرجعية للأسلوب فقط (حتى ٣)",
      "booking.aiDesign.founded": "سنة التأسيس",
      "booking.aiDesign.branchesFromSystem": "عدد الفروع من النظام",
      "booking.aiDesign.specialities": "التخصصات",
      "booking.aiDesign.back": "السابق",
      "booking.aiDesign.cancel": "إلغاء",
      "booking.aiDesign.next": "التالي",
      "booking.aiDesign.generate": "صمّم الصفحة",
      "booking.aiDesign.draftBanner": "هذه مسودة صممها الذكاء الاصطناعي — راجعها ثم انشرها",
      "booking.aiDesign.restorePrevious": "استعادة النسخة السابقة",
      "booking.aiDesign.monthlyLimit": "تصاميم الصفحات بالذكاء الاصطناعي شهرياً (اتركه فارغاً لاعتماد ٥)",
      "err.AI_DESIGN_LIMIT": "استنفدت تصاميم الصفحة بالذكاء الاصطناعي لهذا الشهر.",
      "err.AI_DESIGN_INVALID": "تعذّر على الذكاء الاصطناعي إنشاء صفحة صالحة. عدّل المتطلبات ثم أعد المحاولة.",
      "err.AI_DESIGN_UNAVAILABLE": "مصمم الذكاء الاصطناعي غير متاح الآن. أعد المحاولة بعد قليل.",
      "err.AI_DESIGN_BRIEF_INVALID": "بعض الإجابات في متطلبات التصميم غير صالحة.",
      "err.AI_DESIGN_SECTION_INVALID": "أحد أقسام الصفحة المختارة غير صالح.",
      "err.AI_DESIGN_LIMIT_INVALID": "يجب أن يكون الحد الشهري عددًا صحيحًا يساوي صفرًا أو أكثر، أو اتركه فارغًا لاستخدام الحد الافتراضي.",
      "booking.editor.cards": "بطاقات",
      "booking.editor.grid": "شبكة",
      "booking.editor.list": "قائمة",
      "booking.editor.option.branchLayout": "طريقة عرض الفروع",
      "booking.editor.option.daysAhead": "الأيام المعروضة مقدمًا",
      "booking.editor.option.emailRequired": "البريد الإلكتروني إلزامي",
      "booking.editor.option.emailShown": "عرض البريد الإلكتروني",
      "booking.editor.option.multiService": "عدة خدمات",
      "booking.editor.option.notesRequired": "الملاحظات إلزامية",
      "booking.editor.option.notesShown": "عرض الملاحظات",
      "booking.editor.option.showAddress": "عرض العنوان",
      "booking.editor.option.showDuration": "عرض المدة",
      "booking.editor.option.showMapLink": "رابط الخريطة",
      "booking.editor.option.showPrice": "عرض السعر",
      "booking.editor.option.slotLayout": "طريقة عرض المواعيد",
      "booking.editor.option.termsRequired": "الموافقة على الشروط",
      "booking.editor.option.termsText": "نص الشروط",
      "booking.editor.options": "خيارات الخطوة",
      "booking.editor.stepType": "نوع الخطوة",
      "booking.editor.template.empty": "خطوة فارغة",
      "booking.editor.template.info": "معلومة للعميل",
      "booking.editor.template.photos": "رفع صور",
      "booking.editor.template.questions": "أسئلة",
      "booking.editor.template.terms": "موافقة على الشروط",
      "booking.editor.templateDesc.empty": "ابدأ من خطوة فارغة",
      "booking.editor.templateDesc.info": "اعرض معلومة دون إدخال",
      "booking.editor.templateDesc.photos": "استقبل صور المركبة",
      "booking.editor.templateDesc.questions": "اطرح أسئلة على العميل",
      "booking.editor.templateDesc.terms": "اطلب موافقة إلزامية",
      "booking.general.bookingOffNote": "الصفحة لا تستقبل حجوزات لأن الحجز الإلكتروني متوقف",
      "booking.general.copyUrl": "نسخ الرابط",
      "booking.general.downloadQr": "تنزيل رمز الاستجابة السريعة",
      "booking.general.openUrl": "فتح الصفحة",
      "booking.general.publicUrl": "رابط صفحة الحجز",
      "booking.page.alignment": "المحاذاة",
      "booking.page.background": "الخلفية",
      "booking.page.backgroundColor": "لون الخلفية",
      "booking.page.backgroundImage": "صورة الخلفية",
      "booking.page.buttonStyle": "شكل الزر",
      "booking.page.cornerRadius": "تدوير الزوايا",
      "booking.page.headerBookNow": "احجز الآن",
      "booking.page.headerStyle": "شكل الترويسة",
      "booking.page.hideOnDesktop": "إخفاء على الحاسوب",
      "booking.page.hideOnMobile": "إخفاء على الجوال",
      "booking.page.oneBranchNote": "خطوة الفرع لا تظهر للعميل لأن لديك فرعًا واحدًا",
      "booking.page.spacing": "التباعد العمودي",
      "booking.page.style": "التنسيق",
      "booking.page.textColor": "لون النص",
      "booking.page.width": "العرض",
      "booking.wizard.email": "البريد الإلكتروني",
      "booking.wizard.invalidEmail": "البريد الإلكتروني غير صالح",
      "booking.wizard.mapLink": "عرض على الخريطة",
      "booking.wizard.minutes": "دقيقة",
      "booking.editor.customFields": "حقول الخطوة",
      "booking.editor.details.name": "الاسم — إلزامي",
      "booking.editor.details.notes": "ملاحظات — اختيارية",
      "booking.editor.details.phone": "رقم الجوال — إلزامي، ويُتحقق منه برمز يُرسل إليه",
      "booking.editor.detailsFields": "ما يُطلب من العميل في هذه الخطوة",
      "booking.editor.expand": "إعدادات الخطوة",
      "booking.editor.fieldShown": "يظهر",
      "booking.editor.fixOrder": "نقل خطوة البيانات قبل المركبة",
      "booking.editor.hasErrors": "في هذه الخطوة أخطاء",
      "booking.editor.newField": "حقل جديد",
      "booking.editor.newFieldAr": "اسم الحقل بالعربية",
      "booking.editor.newFieldEn": "اسم الحقل بالإنجليزية",
      "booking.editor.newStep": "خطوة جديدة",
      "booking.editor.newStepAr": "عنوان الخطوة بالعربية",
      "booking.editor.newStepEn": "عنوان الخطوة بالإنجليزية",
      "booking.editor.noFields": "أضف حقلًا واحدًا على الأقل لتظهر هذه الخطوة للعميل.",
      "booking.editor.orderHint": "لعرض مركبات العميل المسجلة ضع خطوة البيانات قبل خطوة المركبة",
      "booking.editor.previewFrame": "صفحة الحجز كما يراها العميل",
      "booking.editor.previewSample": "معاينة فقط: لا تُرسل رسائل ولا يُنشأ حجز.",
      "booking.editor.previewSteps": "خطوات المعاينة",
      "booking.editor.savedVehicles": "عرض مركبات العميل المسجلة بعد التحقق من رقم الجوال",
      "booking.editor.savedVehiclesHint": "يختار العميل العائد مركبته من القائمة أو يُدخل مركبة أخرى، وتظهر أرقام اللوحات مخفية جزئيًا.",
      "booking.editor.skip.branch": "سيُتخطّى تلقائيًا لأن لديك فرعًا واحدًا",
      "booking.editor.skip.deposit": "يظهر فقط عندما يلزم الحجزَ عربون",
      "booking.editor.skip.empty": "سيُتخطّى تلقائيًا لأنه لا يحتوي على حقول",
      "booking.editor.stepTitle": "عنوان الخطوة",
      "booking.editor.untitled": "خطوة بلا عنوان",
      "booking.editor.vehicleField.make": "الشركة المصنعة",
      "booking.editor.vehicleField.model": "الطراز",
      "booking.editor.vehicleField.plate": "رقم اللوحة",
      "booking.editor.vehicleField.year": "سنة الصنع",
      "booking.editor.vehicleFields": "حقول المركبة",
      "booking.wizardTab.intro": "رتّب الخطوات بالسحب، وشغّل ما تحتاجه منها أو أوقفه، ثم افتح الخطوة لتعديل إعداداتها. تتغير المعاينة فور كل تعديل.",
      "booking.wizardTab.title": "خطوات نموذج الحجز",
      "err.STEP_OPTION_INVALID": "إعداد هذه الخطوة غير صالح.",
      "booking.general.capacityPerSlot": "عدد السيارات في الوقت نفسه",
      "booking.info.open": "معلومات عن الإعداد",
      "booking.info.auto_confirm": "يؤكد الحجز فوراً ما لم تضبط خدمة بعينها على التأكيد اليدوي.",
      "booking.info.enabled": "يسمح للعملاء بإنشاء حجوزات من صفحة الفرع العامة.",
      "booking.info.multi_service": "يسمح للعميل بجمع أكثر من خدمة في حجز واحد، وتجمع مددها لتحديد الوقت المطلوب.",
      "booking.info.timezone": "تحدد التوقيت المستخدم لساعات عمل الفرع وعرض المواعيد.",
      "booking.info.slot_minutes": "تحدد الفاصل بين أوقات بدء المواعيد. مثال: ٣٠ دقيقة تتيح البدء عند ٩:٠٠ و٩:٣٠.",
      "booking.info.capacity_per_slot": "الحد الأقصى للسيارات المحجوزة في الفرع في الوقت نفسه، حتى لو اختلف وقت بدء المواعيد.",
      "booking.info.horizon_days": "آخر يوم يمكن للعميل الحجز فيه مقدماً. مثال: ١٤ يوماً تتيح الحجز خلال الأسبوعين المقبلين.",
      "booking.info.lead_time_minutes": "أقل وقت بين إنشاء الحجز وبداية الموعد. مثال: ١٢٠ دقيقة تعني أن العميل لا يستطيع الحجز قبل ساعتين من الموعد.",
      "booking.info.pending_expiry_minutes": "ينتهي الحجز الذي ينتظر موافقة الموظف بعد هذه المهلة أو عند بدء الموعد، أيهما أسبق.",
      "booking.info.cancel_cutoff_minutes": "آخر وقت يسمح فيه للعميل بإلغاء الموعد بنفسه قبل بدايته.",
      "booking.info.reschedule_cutoff_minutes": "آخر وقت يسمح فيه للعميل بتغيير موعده بنفسه قبل بدايته.",
      "booking.info.no_show_grace_minutes": "الوقت الذي ينتظره النظام بعد بداية الموعد قبل تسجيل عدم الحضور.",
      "booking.services.colActions": "الإجراءات",
      "booking.info.service.colName": "اسم الخدمة المعروض للعميل في صفحة الحجز.",
      "booking.info.service.colBookable": "يحدد إمكان اختيار هذه الخدمة في الحجز الإلكتروني.",
      "booking.info.service.colDuration": "الوقت الذي تشغله الخدمة في جدول المواعيد.",
      "booking.info.service.colBuffer": "وقت إضافي بعد الخدمة قبل إتاحة السعة لحجز آخر.",
      "booking.info.service.colConfirm": "يمكن للخدمة اتباع الإعداد العام أو طلب موافقة يدوية أو تأكيد تلقائي.",
      "booking.info.service.colActions": "يحفظ التعديلات على هذه الخدمة فقط.",
      "booking.services.colDepositAmount": "عربون خاص",
      "booking.info.service.colDepositAmount": "مبلغ عربون خاص بهذه الخدمة؛ اتركه فارغاً لاستخدام الإعداد العام.",
      "booking.deposit.typeFixedSymbolFree": "مبلغ ثابت",
      "booking.deposit.holdMinutesPayment": "مهلة إتمام الدفع (بالدقائق)",
      "booking.header.subtitleCars": "إدارة مواعيد العمل، الخدمات، وتفاصيل الحجز للعملاء",
      "booking.tabs.wizard": "نموذج الحجز",
      "booking.page.reset": "استعادة الصفحة القياسية",
      "booking.page.resetTitle": "استعادة الصفحة القياسية",
      "booking.page.resetMessage": "ستحل الصفحة القياسية محل المسودة الحالية ولن تُنشر. هل تريد المتابعة؟",
      "booking.page.wizardNeedsConfig": "فعّل الحجز وحدد خدمة واحدة على الأقل لعرض النموذج الفعلي",
      "booking.editor.openTab": "تعديل خطوات الحجز",
      "booking.wizardTab.missing": "لا تحتوي صفحة الحجز على نموذج الحجز بعد",
      "booking.wizardTab.add": "إضافة نموذج الحجز",
      "booking.wizardTab.save": "حفظ المسودة",
      "booking.wizardTab.publish": "نشر",
      "booking.wizardTab.published": "تم النشر",
      "booking.wizardTab.invalid": "نموذج الحجز يحتوي على محتوى غير صالح. صحّح الأخطاء التالية ثم أعد المحاولة.",
      "err.SCHEMA_INVALID": "نموذج الحجز غير صالح. أعده إلى الوضع الافتراضي.",
      "err.TOO_MANY_STEPS": "لا يمكن أن يزيد عدد خطوات نموذج الحجز على ١٢ خطوة.",
      "err.TOO_MANY_FIELDS": "لا يمكن أن يزيد عدد الحقول المخصصة على ٤٠ حقلًا.",
      "err.STEP_LOCKED": "خطوات التاريخ والبيانات والمراجعة مطلوبة ولا يمكن إخفاؤها أو حذفها.",
      "err.REVIEW_NOT_LAST": "يجب أن تبقى خطوة المراجعة الأخيرة.",
      "err.STEP_INVALID": "هذه الخطوة غير صالحة.",
      "err.STEP_KEY_DUPLICATE": "خطوتان تحملان المعرّف نفسه.",
      "err.STEP_KEY_INVALID": "معرّف الخطوة غير صالح.",
      "err.LABEL_INVALID": "أدخل النص بالعربية والإنجليزية.",
      "err.LABEL_TOO_LONG": "النص أطول من المسموح.",
      "err.VEHICLE_FIELD_INVALID": "إعداد أحد حقول المركبة غير صالح.",
      "err.FIELD_KEY_INVALID": "معرّف الحقل غير صالح.",
      "err.FIELD_KEY_DUPLICATE": "حقلان يحملان المعرّف نفسه.",
      "err.FIELD_TYPE_INVALID": "اختر نوع الحقل.",
      "err.REQUIRED_INVALID": "إعداد الإلزام غير صالح.",
      "err.VISIBILITY_INVALID": "اختر مكان ظهور هذه الإجابة.",
      "err.OPTIONS_REQUIRED": "أضف خيارًا واحدًا على الأقل.",
      "err.OPTIONS_INVALID": "الخيارات غير صالحة (٣٠ كحد أقصى، ولكل خيار قيمة واسمان).",
      "err.CONDITIONS_INVALID": "استخدم ٣ شروط كحد أقصى.",
      "err.CONDITION_INVALID": "الشرط غير صالح.",
      "err.CONDITION_FORWARD_REF": "يمكن أن يعتمد الشرط على حقل يسبق هذا الحقل فقط.",
      "err.CONDITION_UNKNOWN_FIELD": "الشرط يستخدم حقلًا غير موجود.",
      "booking.editor.drag": "اسحب لإعادة الترتيب",
      "booking.editor.shown": "ظاهر",
      "booking.editor.required": "إلزامي",
      "booking.editor.showStep": "إظهار الخطوة",
      "booking.editor.lockedWhy": "هذه الخطوة مطلوبة في كل حجز ولا يمكن إخفاؤها.",
      "booking.editor.deleteStep": "حذف الخطوة",
      "booking.editor.addStep": "إضافة خطوة",
      "booking.editor.newStepTitle": "عنوان الخطوة الجديدة (بالإنجليزية)",
      "booking.editor.addField": "إضافة حقل",
      "booking.editor.newFieldLabel": "اسم الحقل الجديد (بالإنجليزية)",
      "booking.editor.newFieldType": "نوع الحقل الجديد",
      "booking.editor.deleteField": "حذف الحقل",
      "booking.editor.moveUp": "نقل للأعلى",
      "booking.editor.moveDown": "نقل للأسفل",
      "booking.editor.type": "نوع الحقل",
      "booking.editor.type.text": "نص قصير",
      "booking.editor.type.textarea": "نص طويل",
      "booking.editor.type.number": "رقم",
      "booking.editor.type.select": "اختيار واحد",
      "booking.editor.type.multiselect": "اختيار متعدد",
      "booking.editor.type.yesno": "نعم / لا",
      "booking.editor.type.date": "تاريخ",
      "booking.editor.type.photo": "صورة",
      "booking.editor.type.note": "ملاحظة (بدون إجابة)",
      "booking.editor.visibility": "الظهور",
      "booking.editor.internal": "داخلي فقط",
      "booking.editor.printable": "في المطبوعات",
      "booking.editor.min": "الأدنى",
      "booking.editor.max": "الأقصى",
      "booking.editor.optValue": "قيمة الخيار",
      "booking.editor.optAr": "الخيار (بالعربية)",
      "booking.editor.optEn": "الخيار (بالإنجليزية)",
      "booking.editor.addOption": "إضافة خيار",
      "booking.editor.remove": "إزالة",
      "booking.editor.conditions": "يظهر فقط عندما",
      "booking.editor.condWhen": "حقل الشرط",
      "booking.editor.condService": "الخدمات المختارة",
      "booking.editor.condOp": "عامل الشرط",
      "booking.editor.condValue": "قيمة الشرط",
      "booking.editor.op.in": "أحد القيم (مفصولة بفاصلة)",
      "booking.editor.op.eq": "يساوي",
      "booking.editor.op.neq": "لا يساوي",
      "booking.editor.op.truthy": "له إجابة",
      "booking.editor.addCondition": "إضافة شرط",
      "booking.editor.preview": "معاينة مباشرة",
      "booking.editor.previewBlocked": "صحّح الأخطاء أعلاه لعرض المعاينة.",
      "booking.wizard.choose": "اختر",
      "booking.wizard.yes": "نعم",
      "booking.wizard.no": "لا",
      "booking.wizard.branch": "الفرع",
      "booking.wizard.branchDesc": "اختر الفرع",
      "booking.wizard.servicesDescMulti": "اختر خدمة واحدة أو أكثر",
      "booking.wizard.vehicle": "المركبة",
      "booking.wizard.savedVehicles": "مركباتك المحفوظة",
      "booking.wizard.useAnotherVehicle": "إدخال مركبة أخرى",
      "booking.wizard.phoneVerified": "تم التحقق من رقم الجوال",
      "booking.wizard.uploading": "جارٍ الرفع…",
      "booking.wizard.photoN": "الصورة {{n}}",
      "booking.wizard.welcomeBack": "مرحبًا بعودتك، {{name}}",
      "booking.wizard.notYou": "لست أنت؟ غيّر رقم الجوال أعلاه.",
      "booking.wizard.newCustomer": "هذا الرقم غير مسجّل لدينا بعد. أدخل اسمك لنسجّلك عميلًا جديدًا.",
      "booking.wizard.removePhoto": "إزالة",
      "booking.wizard.depositDesc": "يلزم دفع عربون لتأكيد هذا الحجز.",
      "booking.wizard.depositAmount": "العربون المستحق",
      "booking.wizard.depositReason.setting": "تطلب الورشة عربونًا على الحجوزات الإلكترونية.",
      "booking.wizard.depositReason.no_show_rule": "يلزم عربون بسبب تكرار عدم الحضور",
      "booking.wizard.payToConfirm": "ادفع العربون لتأكيد حجزك.",
      "booking.wizard.payNow": "ادفع الآن",
      "booking.manage.title": "إدارة حجزك",
      "booking.manage.status": "الحالة",
      "booking.manage.statuses.pending": "قيد المراجعة",
      "booking.manage.statuses.confirmed": "مؤكد",
      "booking.manage.statuses.awaiting_payment": "بانتظار دفع العربون",
      "booking.manage.reschedule": "تغيير التاريخ أو الوقت",
      "booking.manage.confirmReschedule": "تأكيد الموعد الجديد",
      "booking.manage.rescheduled": "تم تغيير موعد حجزك.",
      "booking.manage.cancel": "إلغاء الحجز",
      "booking.manage.cancelConfirm": "هل تريد إلغاء هذا الحجز؟ لا يمكن التراجع عن ذلك.",
      "booking.manage.cancelled": "تم إلغاء حجزك.",
      "booking.manage.addToCalendar": "إضافة إلى التقويم",
      "err.NO_BAY_FREE": "تم حجز الوقت الذي اخترته للتو. يُرجى اختيار وقت آخر.",
      "booking.page.wholePage": "الصفحة",
      "booking.page.settingsRoot": "إعدادات الصفحة",
      "booking.page.loadFailed": "تعذر تحميل تصميم الصفحة",
      "booking.page.retry": "إعادة المحاولة",
      "booking.page.saving": "جارٍ الحفظ…",
      "booking.page.saved": "تم حفظ المسودة",
      "booking.page.saveFailed": "تعذر حفظ المسودة",
      "booking.page.conflict": "تم نشر نسخة أحدث من قبل مستخدم آخر",
      "booking.page.reload": "إعادة التحميل",
      "booking.page.invalid": "الصفحة تحتوي على محتوى غير صالح. صحّح الأخطاء التالية ثم أعد المحاولة. المسودة محفوظة على جهازك.",
      "booking.page.canvasLang": "لغة المعاينة",
      "booking.page.history": "السجل",
      "booking.page.publishing": "جارٍ النشر…",
      "booking.page.noVersions": "لا توجد نسخ منشورة بعد",
      "booking.page.version": "النسخة",
      "booking.page.restore": "استعادة",
      "booking.page.restoreTitle": "استعادة نسخة",
      "booking.page.restoreMessage": "ستحل هذه النسخة محل المسودة الحالية. هل تريد المتابعة؟",
      "booking.page.status.published": "منشورة",
      "booking.page.status.archived": "مؤرشفة",
      "err.PAGE_IMAGE_INVALID": "ارفع صورة بصيغة JPG أو PNG أو WebP أو GIF.",
      "err.PAGE_IMAGE_TOO_LARGE": "حجم الصورة أكبر من ٥ ميجابايت. اختر صورة أصغر.",
      "booking.page.localDraft": "يحتوي هذا المتصفح على تغييرات غير محفوظة من {{time}}",
      "booking.page.useLocal": "استخدامها",
      "booking.page.discardLocal": "تجاهلها",
      "err.CONTRAST_TOO_LOW": "اللون الأساسي فاتح جدًا للنص الأبيض. من «تصميم الصفحة» افتح إعدادات الصفحة واضغط «استخدم درجة أغمق» تحت اللون الأساسي.",
      "err.DEPTH_EXCEEDED": "هذا القسم متداخل بعمق زائد. انقله خارج الأقسام المحيطة به.",
      "err.DOC_TOO_LARGE": "الصفحة أكبر من أن تُحفظ. احذف بعض الأقسام أو الصور.",
      "err.IMAGE_NOT_ALLOWED": "لا يمكن استخدام هذه الصورة. ارفعها مجددًا من حقل الصورة.",
      "err.PROP_INVALID": "قيمة هذا الإعداد غير صحيحة. أعد إدخالها.",
      "err.PROP_UNKNOWN": "هذا الإعداد غير مدعوم. احذفه أو أعد ضبط القسم.",
      "err.TEXT_TOO_LONG": "هذا النص أطول من المسموح. اختصره في اللغتين.",
      "err.TOO_MANY_COMPONENTS": "في الصفحة أقسام كثيرة جدًا. احذف بعضها.",
      "err.TYPE_UNKNOWN": "نوع هذا القسم غير متاح. احذفه.",
      "err.WIZARD_DUPLICATE": "نموذج الحجز مكرر في الصفحة. أبقِ واحدًا فقط.",
      "err.WIZARD_MISSING": "تحتاج الصفحة إلى نموذج حجز. أضِف واحدًا قبل النشر.",
      "booking.tabs.services": "الخدمات",
      "booking.tabs.waitlist": "قائمة الانتظار",
      "booking.waitlist.desc": "عندما تكون فترات المواعيد ممتلئة بالكامل في يوم ما، يمكن للعميل الانضمام إلى قائمة الانتظار لتلقي عرض حجز تلقائي عند توفر فترة شاغرة",
      "booking.waitlist.enabled": "تفعيل قائمة الانتظار",
      "booking.waitlist.enabledDesc": "إتاحة زر الانضمام إلى قائمة الانتظار للعملاء في الأيام المكتملة",
      "booking.waitlist.loadFailed": "تعذّر تحميل إعدادات قائمة الانتظار",
      "booking.waitlist.offerMinutes": "مدة صلاحية عرض الفترة الشاغرة (بالدقائق)",
      "booking.waitlist.offerMinutesHint": "المهلة المتاحة للعميل لتأكيد الحجز قبل الانتقال للعميل التالي في قائمة الانتظار",
      "booking.waitlist.saved": "تم حفظ إعدادات قائمة الانتظار بنجاح",
      "booking.waitlist.title": "إعدادات قائمة الانتظار الذكية",
      "bookingAnalytics.bookings": "الحجوزات",
      "bookingAnalytics.cancelled": "الملغاة",
      "bookingAnalytics.completed": "المكتملة",
      "bookingAnalytics.confirmed": "المؤكدة",
      "bookingAnalytics.deposit_paid_rate": "نسبة سداد العربون",
      "bookingAnalytics.expired": "المنتهية",
      "bookingAnalytics.from": "من",
      "bookingAnalytics.funnel": "مسار الحجز",
      "bookingAnalytics.loadError": "تعذّر تحميل تحليلات الحجز",
      "bookingAnalytics.no_show_rate": "نسبة عدم الحضور",
      "bookingAnalytics.no_shows": "عدم الحضور",
      "bookingAnalytics.step.branch": "اختيار الفرع",
      "bookingAnalytics.step.datetime": "التاريخ والوقت",
      "bookingAnalytics.step.deposit": "سداد العربون",
      "bookingAnalytics.step.details": "بيانات العميل",
      "bookingAnalytics.step.review": "المراجعة والتأكيد",
      "bookingAnalytics.step.services": "اختيار الخدمات",
      "bookingAnalytics.step.vehicle": "بيانات المركبة",
      "bookingAnalytics.title": "تحليلات الحجز",
      "bookingAnalytics.to": "إلى",
      "bookingAnalytics.viewed": "المشاهدات",
      "bookingAnalytics.visits": "الزيارات",
      "common.accessDenied": "تم رفض الوصول",
      "common.noChanges": "لا توجد تغييرات للحفظ",
      "err.BODY_TOO_LARGE": "حجم الطلب كبير جدًا.",
      "err.BOOKING_NOT_FOUND": "الحجز غير موجود أو انتهت صلاحية الرابط.",
      "err.BRANCH_NOT_BOOKABLE": "هذا الفرع لا يستقبل الحجوزات الإلكترونية.",
      "err.BRANCH_NOT_FOUND": "هذا الفرع غير موجود أو لا يستقبل الحجوزات الإلكترونية.",
      "err.COMPANY_NOT_FOUND": "صفحة الحجز غير موجودة.",
      "err.CONFIG_FAILED": "تعذّر تحميل صفحة الحجز. حاول مرة أخرى.",
      "err.CUTOFF_PASSED": "اقترب موعدك كثيرًا ولا يمكن تغييره إلكترونيًا. تواصل مع الورشة.",
      "err.EVENT_INGEST_FAILED": "تعذّر تسجيل الحدث.",
      "err.FILE_INVALID": "ارفع صورة بصيغة JPG أو PNG أو WebP بحجم لا يتجاوز ٨ ميغابايت.",
      "err.INVALID_DATE_RANGE": "اختر نطاق تاريخ صحيحًا.",
      "err.INVALID_EVENT": "حدث حجز غير معروف.",
      "err.APPOINTMENT_ALREADY_CONVERTED": "حُوِّل هذا الموعد إلى بطاقة عمل من قبل.",
      "err.INVALID_TRANSITION": "لا يمكن نقل هذا الموعد إلى هذه الحالة من حالته الحالية.",
      "err.MANAGE_FAILED": "تعذّر تعديل الحجز. حاول مرة أخرى.",
      "err.OTP_NOT_VERIFIED": "تحقّق من رقم جوالك أولًا.",
      "err.OVERRIDE_NOT_ALLOWED": "ليست لديك صلاحية الحجز خارج قواعد الحجز.",
      "err.PAGE_INVALID": "تحتوي صفحة الحجز على محتوى غير صالح. صحّح الأقسام المحددة.",
      "err.PAGE_VERSION_NOT_FOUND": "إصدار الصفحة غير موجود.",
      "err.QUOTE_FAILED": "تعذّر حساب العربون. حاول مرة أخرى.",
      "err.TOO_MANY_FILES": "ارفع صورة واحدة في كل مرة.",
      "err.UNKNOWN": "حدث خطأ. حاول مرة أخرى.",
      "err.WAITLIST_JOIN_FAILED": "تعذّرت إضافتك إلى قائمة الانتظار. حاول مرة أخرى.",
      "err.INVALID_HOST": "اسم النطاق غير صالح. أدخل اسمًا مثل book.example.sa.",
      "err.RESERVED_HOST": "لا يمكن استخدام نطاقات MotorMind نطاقًا مخصصًا.",
      "err.DOMAIN_TAKEN": "هذا النطاق مرتبط بحساب آخر.",
      "err.DOMAIN_NOT_SET": "لم يُضَف نطاق مخصص بعد.",
      "err.CNAME_MISMATCH": "سجل CNAME غير موجود أو لا يشير إلى book.mmind.app.",
      "err.TXT_MISSING": "سجل التحقق TXT غير موجود أو قيمته غير صحيحة.",
      "err.APPROVAL_LIMIT_GRANT_EXCEEDS_OWN_PCT": "لا يمكنك منح حدٍّ أعلى من حدّك الخاص ({{own}}٪).",
      "err.APPROVAL_LIMIT_GRANT_EXCEEDS_OWN_SAR": "لا يمكنك منح حدٍّ أعلى من حدّك الخاص ({{own}} ريال).",
      "err.APPROVAL_PENDING": "فاتورة المشتريات هذه بانتظار الاعتماد، لذا لا يمكن سدادها بعد.",
      "err.DISCOUNT_APPROVAL_PENDING": "خصم على هذا المستند بانتظار الاعتماد. لا يُرسل شيء إلى العميل، ولا يمكن ترحيله أو سداده، حتى يُعتمد الخصم أو يُرفض.",
      "err.PURCHASE_APPROVAL_PENDING": "فاتورة المشتريات هذه بانتظار الاعتماد، ولا تُرحَّل حتى يعتمدها مُعتمِد.",
      "err.APPROVAL_REJECTED": "رُفضت فاتورة المشتريات هذه، لذا لا يمكن سدادها.",
      "err.APPROVAL_REQUIRED": "هذا يتجاوز حدّك، لذا أُرسل للاعتماد.",
      "err.SELF_APPROVAL_FORBIDDEN": "لا يمكنك اعتماد طلبك أو رفضه بنفسك.",
      "err.GARAGE_SCOPE_ALL_WRITE": "تُحفظ التغييرات في فرع واحد في كل مرة. اختر فرعًا ثم حاول مرة أخرى.",
      "err.LINKED_BRANCH_MISMATCH": "السجلات المرتبطة تتبع فروعًا مختلفة. اربط سجلات من فرع واحد فقط.",
      "err.GARAGE_ACCESS_DENIED": "ليس لديك صلاحية الوصول إلى هذا الفرع.",
      "err.GARAGE_SCOPE_ALL_UNSUPPORTED": "قراءة جميع الفروع معًا غير مدعومة هنا. اختر فرعًا واحدًا.",
      "err.IDEMPOTENCY_AUTH_CONTEXT_MISSING": "تعذر تحديد سياق المستخدم المسجل لهذه العملية.",
      "err.IDEMPOTENCY_KEY_INVALID": "يجب أن يكون معرّف العملية بصيغة UUID صحيحة.",
      "err.EXPECTED_VERSION_REQUIRED": "يتطلب هذا التعديل إصدار السجل الذي حمّلته. حدّث البيانات ثم أعد المحاولة.",
      "err.INVALID_EXPECTED_VERSION": "إصدار السجل المرسل مع هذا التعديل غير صالح.",
      "err.INVALID_FINDING_ITEMS": "قائمة القطع أو الخدمات في هذا الاكتشاف بصيغة غير صحيحة.",
      "err.INVALID_OCCURRED_AT": "وقت التنفيذ المُسجَّل غير صالح لبطاقة العمل هذه.",
      "err.INVALID_REQUEST": "صيغة الطلب غير صحيحة.",
      "err.INVALID_ROLE_ANALYSIS": "طلب تحليل الدور غير صالح.",
      "err.ROLE_ANALYSIS_UNAVAILABLE": "تحليل الدور غير متاح مؤقتاً. حاول بعد قليل.",
      "err.INVALID_ROLE_SUGGEST": "اكتب اسم الدور للحصول على الاقتراحات.",
      "err.ROLE_SUGGEST_UNAVAILABLE": "اقتراح الصلاحيات غير متاح مؤقتاً. حاول بعد قليل.",
      "err.CATALOG_SMART_REVIEW_UNAVAILABLE": "المراجعة الذكية غير متاحة — TzamunAI Decide غير مهيأ على هذا الخادم.",
      "err.MISS_NOT_OPEN": "حُسم هذا العنصر بالفعل. حدّث القائمة.",
      "err.INVALID_QUERY": "قيمة الصفحة أو الحد أو البحث أو حالة العمل غير صحيحة.",
      "err.MISSING_TENANT": "تعذر تحديد المنشأة لهذا الطلب.",
      "err.NOT_AUTHENTICATED": "يلزم تسجيل الدخول إلى تطبيق الجوال.",
      "err.OPERATION_IN_FLIGHT": "لا تزال العملية قيد المعالجة. أعد المحاولة باستخدام معرّف العملية نفسه.",
      "err.INSPECTION_MANDATORY_POINTS_MISSING": "لا تزال بعض نقاط الفحص الإلزامية فارغة. أجب عن كل نقطة إلزامية قبل الإنهاء.",
      "err.INSPECTION_NOT_FOUND": "قائمة الفحص هذه لم تعد موجودة أو أنها تتبع فرعاً آخر.",
      "err.INSPECTION_ALREADY_ISSUED": "اكتمل هذا الفحص مسبقاً.",
      "err.INSPECTION_RESULT_INVALID_VERSION": "تعذّر حفظ نقطة الفحص هذه. حدّث البيانات وأعد المحاولة.",
      "err.PHOTOS_NOT_ACCEPTED": "لا يمكن إرسال الصور مع هذا التعديل — أرفق الصور من زر الصور في نقطة الفحص بدلاً من ذلك.",
      "err.VEHICLE_NOT_FOUND": "هذه المركبة لم تعد موجودة أو أنها تتبع فرعاً آخر.",
      "err.VEHICLE_INCOMPLETE": "أضف الشركة الصانعة وطراز المركبة أولاً",
      "err.VIN_IN_USE": "رقم الهيكل هذا مسجل لمركبة أخرى في هذا الفرع",
      "err.VIN_REQUIRED": "أدخل رقم هيكل المركبة لإجراء هذا الفحص",
      "err.OPERATION_NOT_FOUND": "لا توجد عملية مسجّلة بهذا المعرّف.",
      "err.RATE_LIMITED": "طلبات كثيرة جدًا. انتظر قبل إعادة المحاولة.",
      "err.UNAVAILABLE": "الخدمة غير متاحة مؤقتًا. حاول مرة أخرى لاحقًا.",
      "err.VERSION_CONFLICT": "تم تعديل هذا السجل منذ تحميله. حدّث البيانات قبل إعادة المحاولة.",

      "err.CREDIT_NOTE_REJECTED_NO_PDF": "رفضت الهيئة هذا الإشعار الدائن، فلا توجد له نسخة للطباعة.",
      "err.CARD_REFUND_STILL_PROCESSING": "ما زال الاسترداد إلى البطاقة قيد المعالجة أو بانتظار الموافقة، ولم يُغيَّر شيء؛ تحقّق لاحقًا.",
      "err.CARD_RESOLUTION_INPUT_INVALID": "مرجع جهة الدفع (حتى 128 حرفًا) أو الملاحظة (حتى 255 حرفًا) غير صالح.",
      "err.CREDIT_NOTE_NOT_FOUND": "لا يوجد هذا الإشعار الدائن في هذه الفاتورة.",
      "err.CARD_NEEDS_CHECK_REQUIRED": "لم يعد هذا الإشعار الدائن بانتظار قرار الموظف، فلا يحتاج الاسترداد إلى البطاقة إلى تحقق.",
      "err.CREDIT_NOTE_CLEARED_RESEND_REFUSED": "اعتمدت الهيئة هذا الإشعار الدائن، وتُطبَّق آثاره من الإجابة المخزَّنة ولا يُعاد إرساله.",
      "err.INVOICE_WRITE_OFF_PENDING_CREDIT_NOTE": "ما زال إشعار دائن على هذه الفاتورة بانتظار اعتماد الهيئة. أعدِم الفاتورة بعد اعتماده أو رفضه.",
      "err.GL_CLOSE_PENDING_CREDIT_NOTE": "يوجد إشعار دائن مؤرخ في هذه الفترة بانتظار اعتماد الهيئة. أقفل الفترة بعد اعتماده أو رفضه.",
      "err.CREDIT_NOTE_REJECTED": "رفضت الهيئة هذا الإشعار الدائن، فلا يمكن إرساله مرة أخرى ولا أن يسري. أصدر إشعارًا دائنًا جديدًا.",
      "err.CREDIT_NOTE_STATE": "لا يصدر إشعار دائن إلا على فاتورة صادرة.",
      "err.CREDIT_NOTE_DOWNPAYMENT": "لا يصدر إشعار دائن على فاتورة دفعة مقدمة — استردَّ الدفعة بدلًا من ذلك.",
      "err.NOTHING_TO_CREDIT": "لم يبقَ في هذه الفاتورة ما يصدر به إشعار دائن.",
      "err.CREDIT_QTY_EXCEEDS": "الكمية أكبر مما بقي من هذا البند دون إشعار.",
      "err.CREDIT_EXCEEDS_REMAINING": "المبلغ أكبر مما بقي من هذه الفاتورة دون إشعار.",
      "err.CREDIT_LINE_INVALID": "راجع بنود الإشعار وكمياته.",
      "err.CREDIT_LINES_REQUIRED": "اختر بنود الإشعار، أو الفاتورة كاملة.",
      "err.CREDIT_NOTE_REASON_REQUIRED": "أدخل سببًا من 3 إلى 255 حرفًا.",
      "err.CREDIT_DISPOSITION_REQUIRED": "جزء من هذا الإشعار مسدَّد — اختر استرداده الآن أو إبقاءه رصيدًا دائنًا للعميل.",
      "err.CREDIT_DISPOSITION_INVALID": "اختر استرداده الآن أو إبقاءه رصيدًا دائنًا للعميل.",
      "err.CREDIT_REFUND_METHOD_INVALID": "اختر طريقة الاسترداد.",
      "err.CREDIT_REFUND_NO_CARD_PAYMENT": "لا توجد في هذه الفاتورة دفعة إلكترونية بالبطاقة لاستردادها — استردَّ نقدًا أو بتحويل بنكي.",
      "err.CREDIT_NO_CUSTOMER": "لا يوجد عميل لهذه الفاتورة يُحفَظ له الرصيد.",
      "err.CREDIT_NOTE_KEY_REUSED": "سبق استخدام هذا الطلب لفاتورة أخرى. أعد فتح النموذج وحاول مجددًا.",
      "err.IDEMPOTENCY_KEY_REQUIRED": "أعد فتح النموذج وحاول مجددًا.",
      "err.RETURNS_MOVED": "استُبدلت فواتير الإرجاع بالإشعارات الدائنة. استخدم «إصدار إشعار دائن» في الفاتورة.",
      "err.CREDIT_NOTE_LEGACY_RETURN": "لهذه الفاتورة فاتورة إرجاع ألغتها كاملةً، فلا يصدر عليها إشعار دائن.",
      "err.CREDIT_STOCK_NOT_RETURNABLE": "بعض هذه القطع لم يخرج من المخزون في هذه الفاتورة، فلا يمكن إرجاعه إليه. ألغِ «إرجاع إلى المخزون» لها.",
      "err.INVOICE_WRITTEN_OFF": "أُعدمت هذه الفاتورة دينًا معدومًا، فلا يصدر عليها إشعار دائن. تراجع عن الإعدام أولًا.",
      "err.JOB_CARD_ALREADY_ENDED": "بطاقة العمل هذه مُغلقة أو تم تسليم السيارة، فلا يمكن إرجاعها. أعد فتحها بدلاً من ذلك ليُسجَّل السبب والشخص.",
      "err.TEMPLATE_NOT_FOUND": "قالب قائمة الفحص غير موجود.",
      "err.TEMPLATE_NAME_REQUIRED": "يحتاج قالب قائمة الفحص إلى اسم بالعربية والإنجليزية معًا.",
      "err.TEMPLATE_NO_POINTS": "اختر نقطة فحص واحدة على الأقل لهذا القالب.",
      "err.TEMPLATE_POINT_UNKNOWN": "بعض نقاط الفحص المختارة ليست ضمن قائمة الفحص المصدر.",
      "err.TEMPLATE_READ_ONLY": "لا يمكن تغيير قوائم الفحص الجاهزة. أنشئ قالبك الخاص انطلاقًا من إحداها.",
      "err.TEMPLATE_NO_SOURCE": "لم يُبنَ هذا القالب من قائمة فحص، لذا لا يمكن إعادة اختيار نقاطه.",
      "err.TEMPLATE_SOURCE_INVALID": "اختر إحدى قوائم الفحص الجاهزة لبناء هذا القالب منها.",
      "err.TEMPLATE_MODE_MISMATCH": "لا يمكن استخدام قائمة الفحص هذه مع هذا النوع من الفحص.",
      "err.CHALLENGE_ALREADY_USED": "تم استخدام رمز الدخول هذا من قبل.",
      "err.CHALLENGE_EXPIRED": "انتهت صلاحية رمز الدخول هذا. اطلب رمزًا جديدًا للمتابعة.",
      "err.CHALLENGE_LOCKED": "تجاوزت عدد المحاولات المسموح به. انتظر 15 دقيقة ثم اطلب رمزًا جديدًا.",
      "err.INVALID_AMOUNT": "المبلغ غير صحيح.",
      "err.INVOICE_NO_PUBLIC_LINK": "لا يوجد رابط دفع لهذه الفاتورة. أعد إصدارها من بطاقة العمل ثم حاول مرة أخرى.",
      "err.INVOICE_POSTED_IMMUTABLE": "لا يمكن تعديل الفاتورة الصادرة ولا حذفها؛ ويكون تصحيحها بإصدار إشعار دائن.",
      // Per-service warranty (owner, 2026-09-07). Merged BY HAND from the six
      // *.i18n.json fragments. Arabic carries full CLDR plural forms for the day
      // and distance counts — Arabic has six, and an _one/_other pair renders
      // "متبقٍ ٣ يوم" where it should read "٣ أيام". Counts interpolate {{count}},
      // the variable i18next supplies itself, so a key can never render its own
      // placeholder because a caller forgot to pass a second copy of the number.
      //
      // wty.mon.* and wty.inv.* are namespaced because the monitor and the invoice
      // needed the same concept worded differently, and wty.kmOver took different
      // interpolation variables per surface — merging them would have broken one
      // at runtime rather than merely reading oddly.
      "err.WARRANTY_ALREADY_ACTIVE": "يوجد ضمان ساري على هذه الخدمة. ألغِه أولاً أو جدده من بطاقة العمل.",
      "err.WARRANTY_ALREADY_VOID": "تم إلغاء هذا الضمان بالفعل.",
      "err.WARRANTY_CORRECTION_REASON_REQUIRED": "سبب التعديل مطلوب لتعديل الضمان.",
      "err.WARRANTY_CORRECTION_NO_CHANGE": "لم يطرأ أي تغيير على حدود الضمان.",
      "err.WARRANTY_CORRECTION_STALE": "تم تعديل هذا الضمان من مستخدم آخر، حدّث الصفحة وحاول مجدداً.",
      "err.WARRANTY_CHANGED_CONCURRENTLY": "تغيّر ضمان هذه الخدمة أثناء إصدار إشعار الدائن. يُرجى المحاولة مرة أخرى.",
      "err.WARRANTY_CORRECTION_ALREADY_ENDED": "انتهت هذه المدة بالفعل من تاريخ البدء الأصلي. اختر مدة أطول أو ألغِ الضمان.",
      "err.WARRANTY_DURATION_INVALID": "راجع المدة: أدخل رقمًا صحيحًا واختر أيام أو شهور أو سنوات.",
      "err.WARRANTY_EXPIRED": "لم يعد هذا الضمان ساريًا — $t(err.WARRANTY_EXPIRED_{{limit}})",
      "err.WARRANTY_EXPIRED_distance": "تم تجاوز حده بمقدار {{km_over}} كم (الحد {{km_ceiling}} كم، حسب قراءة {{km_as_of}}).",
      "err.WARRANTY_EXPIRED_time": "انتهت مدته منذ {{days_over}} يوم (بتاريخ {{expires_on}}).",
      "err.WARRANTY_KM_BELOW_ANCHOR": "القراءة أقل من القراءة المسجلة عند بدء الضمان. تحقق من العداد وأعد إدخالها.",
      "err.WARRANTY_KM_REQUIRED": "يحمل هذا الضمان حد مسافة — يلزم إدخال قراءة عداد المسافة الحالية لإعادة الفتح بموجبه.",
      "err.WARRANTY_NOT_FOUND": "لم يتم العثور على ضمان ساري لهذه الخدمة.",
      "err.WARRANTY_NOT_ON_BILLING_LINE": "هذا بند فوترة وليس عملاً في المركز، فلا ضمان له.",
      "err.WARRANTY_NO_LIMIT": "حدد مدة أو حدًا للمسافة أو كليهما — لا بد للضمان من حد واحد على الأقل.",
      "err.WARRANTY_NO_ODOMETER": "لا توجد قراءة عداد مسجلة لهذه المركبة، فلا يوجد ما يبدأ منه حد المسافة. سجّل قراءة العداد أولاً.",
      "err.WARRANTY_OVERRIDE_FORBIDDEN": "ليس لديك صلاحية لتجاوز ضمان منتهي الصلاحية.",
      "err.WARRANTY_OVERRIDE_REASON_REQUIRED": "اكتب سبب تجاوز هذا الضمان المنتهي — يُسجَّل السبب مع إعادة فتح البطاقة.",
      "err.WARRANTY_RENEWAL_DECISION_INVALID": "اختر إما الاستمرار في الضمان الحالي أو إصدار ضمان جديد — لا يوجد قرار آخر متاح هنا.",
      "err.WARRANTY_SERVICE_NOT_COMPLETE": "أكمل هذه الخدمة أولاً — يبدأ الضمان عند انتهاء العمل.",
      "err.WARRANTY_SERVICE_REQUIRED": "اختر الخدمة المشمولة بالضمان لهذه المطالبة، ليتم التحقق من سريان الضمان.",
      "err.SERVICE_APPROVED_CANNOT_DELETE": "لا يمكن حذف خدمة معتمدة أو مكتملة — يمكن إلغاؤها فقط مع ذكر السبب.",
      "err.JOB_CARD_ENDED_NO_DELETE": "أمر العمل هذا منتهٍ — لا يمكن حذف أي شيء منه.",
      "err.JOB_CARD_ENDED": "أمر العمل هذا منتهٍ — لا يمكن تعديل خدماته بعد الآن.",
      "err.PAYMENT_FLOW_RETIRED": "طريقة الدفع هذه لم تعد متاحة. يرجى الدفع عبر رابط الدفع.",
      "err.SERVICE_CANCELLED_CANNOT_DELETE": "الخدمة الملغاة تبقى على أمر العمل كسجل لما تم الاتفاق عليه وسبب إيقافه — لا يمكن حذفها.",
      "err.SERVICE_CANCEL_HAS_WARRANTY": "على هذه الخدمة ضمان ساري. ألغِ الضمان أولاً، ثم ألغِ الخدمة.",
      "err.SERVICE_NOT_FOUND": "الخدمة غير موجودة",
      "err.SERVICE_ALREADY_CANCELLED": "هذه الخدمة ملغاة بالفعل.",
      "err.SERVICE_NOT_CANCELLABLE": "لا يمكن إلغاء إلا خدمة معتمدة أو مكتملة. الخدمة التي ما زالت بانتظار الموافقة يمكن حذفها بدلاً من ذلك.",
      "err.FINAL_INVOICE_DRAFT_EXISTS": "توجد مسودة فاتورة نهائية على أمر العمل هذا. احذف المسودة أولاً، ثم ألغِ الخدمة، ثم أنشئ الفاتورة من جديد.",
      "err.PART_DECISION_INVALID": "اختر «ترجع للمخزون» أو «تبقى على الفاتورة» لقطعة مركّبة تحت هذه الخدمة.",
      "err.PART_DECISIONS_REQUIRED": "اختر لكل قطعة مركّبة: ترجع للمخزون أو تبقى على الفاتورة.",
      "err.PARTS_UNDECIDED": "هذا العمل مكتمل: قرّر أولاً لكل قطعة مركّبة (ترجع للمخزون أو تبقى على الفاتورة)، ثم ألغِ الخدمة.",
      "err.PART_NOT_FITTED": "لا يمكن اتخاذ هذا القرار إلا لقطعة مركّبة.",
      "err.LEGACY_DEPOSIT_SETTLE_FIRST": "إلغاء هذه الخدمة لا يترك أي عمل للعميل على أمر العمل، والعربون أُخذ بفاتورة بالطريقة القديمة. سوِّ العربون بفاتورة مرتجع أولاً، ثم ألغِ الخدمة.",
      "err.SUB_TASK_CLOSE_FAILED": "تعذّر إغلاق خطوة تحت هذه الخدمة. حدّث الصفحة وحاول مرة أخرى.",
      "err.TRY_AGAIN": "قام شخص آخر بتعديل أمر العمل هذا في نفس اللحظة. لم يُحفظ شيء — حاول مرة أخرى.",
      "err.ITEM_INVOICED_LOCKED": "هذا البند على فاتورة مُصدرة ومقفل. اعكس الفاتورة (إشعار دائن) لتعديله.",
      "err.SERVICE_CANCELLED": "هذه الخدمة ملغاة — لا يمكن إضافة عمل أو قطع أو خطوات إليها.",
      "err.INVOICE_SOURCES_CHANGED": "تغيّر العمل على أمر العمل أثناء إنشاء الفاتورة (أُلغيت خدمة أو أُرجعت قطعة). حدّث الصفحة وأنشئ الفاتورة من جديد.",
      "err.SOURCE_CANCELLED": "لا يمكن إعادة تسعير خدمة ملغاة.",
      "err.PART_CANCEL_DECIDED": "لهذه القطعة قرار إلغاء مسبق (ترجع للمخزون أو تبقى على الفاتورة). غيّره من نافذة إلغاء الخدمة.",
      "svcCancel.action": "إلغاء الخدمة",
      "svcCancel.title": "إلغاء خدمة معتمدة",
      "svcCancel.description": "تبقى الخدمة في أمر العمل بحالة ملغاة مع السبب، ولا تُحتسب في الفاتورة.",
      "svcCancel.reason": "السبب",
      "svcCancel.reasonPlaceholder": "لماذا أُلغيت هذه الخدمة؟",
      "svcCancel.reasonRequired": "اكتب السبب (3 أحرف على الأقل).",
      "svcCancel.confirm": "إلغاء الخدمة",
      "svcCancel.done": "تم إلغاء الخدمة",
      "svcCancel.failed": "تعذّر إلغاء الخدمة",
      "svcCancel.previewFailed": "تعذّر تحميل تفاصيل الإلغاء",
      "svcCancel.decideFailed": "تعذّر حفظ القرار لهذه القطعة",
      "svcCancel.notCancellable": "لا يمكن إلغاء هذه الخدمة الآن.",
      "svcCancel.fittedParts": "قطع مركّبة",
      "svcCancel.fittedHint": "اختر لكل قطعة: ترجع للمخزون، أو تبقى على السيارة وفي الفاتورة.",
      "svcCancel.fittedHintCompleted": "العمل منتهٍ. قرّر لكل قطعة مركّبة أولًا، وبعدها يمكن إلغاء الخدمة.",
      "svcCancel.toStock": "ترجع للمخزون",
      "svcCancel.handBack": "تُعاد للعميل",
      "svcCancel.keep": "تبقى في الفاتورة",
      "svcCancel.decided.returned": "رجعت للمخزون",
      "svcCancel.decided.kept": "باقية في الفاتورة",
      "svcCancel.decideAll": "باقي {{count}} قطعة بدون قرار.",
      "svcCancel.unnamedPart": "قطعة",
      "svcCancel.changeDecisions": "إرجاع القطع المُبقاة",
      "svcCancel.decisionsTitle": "قطع مُبقاة على خدمة ملغاة",
      "svcCancel.decisionsDescription": "القطعة الباقية في الفاتورة يمكن إرجاعها للمخزون ما لم تُفوتر. القطعة المُرجعة قرارها نهائي.",
      "svcCancel.legacyMaybe": "يوجد عربون قديم على أمر العمل. إذا لم يبقَ للعميل شيء يُفوتر بعد هذا الإلغاء فسيُرفض حتى تتم تسوية العربون.",
      "svcCancel.badge": "ملغاة بعد الموافقة",
      "svcCancel.byLine": "ألغاها {{who}} في {{when}} — {{reason}}",
      "svcCancel.depositPreview": "بعد هذا الإلغاء لا يبقى عمل للعميل في أمر العمل. سيُعرض عربون العميل كاسترداد أو رصيد عند الفاتورة النهائية:",
      "svcCancel.depositNotice": "لا يبقى عمل للعميل في أمر العمل. سيُعرض العربون ({{amount}}) كاسترداد أو رصيد عند الفاتورة النهائية.",
      "svcCancel.allCancelled": "أُلغيت كل خدمات أمر العمل ولم يبقَ عمل. أضف خدمة جديدة، أو أنهِ أمر العمل.",
      "ntf.serviceCancelled.title": "أُلغيت خدمة",
      "ntf.serviceCancelled.message": "أُلغيت {{service_name}}: {{reason}}",
      "history.serviceCancelled": "ألغى الخدمة {{service}} — {{reason}}",
      "history.cancelPartDecided.returned": "{{part}} ({{service}}) رجعت للمخزون",
      "history.cancelPartDecided.kept": "{{part}} ({{service}}) باقية في الفاتورة",
      "err.SERVICE_HAS_LIVE_WARRANTY": "لا تزال هذه الخدمة مشمولة بضمان ساري. ألغِ الضمان أولاً — حذف الخدمة سيمحو الضمان وسجلّه معه.",
      "err.GL_OPENING_EMPTY": "لا يوجد ما يمكن اعتماده بعد — أضف رصيدًا افتتاحيًا واحدًا على الأقل أولاً.",
      "err.GL_OPENING_NOTHING_TO_SIGN": "لا توجد مسودة محفوظة بعد — احفظ الأرصدة الافتتاحية قبل الاعتماد.",
      "err.GL_OPENING_DATE_REQUIRED": "حدد تاريخ الافتتاح أولاً — هو التاريخ الذي تصحّ فيه هذه الأرصدة.",
      "wty.panelLoadFailed": "تعذّر تحميل حالة الضمان — هذا لا يعني أن الخدمة غير مشمولة.",
      "wty.retry": "إعادة المحاولة",
      "jc.reopenWarrantyExpiredKm": "تجاوز هذا الضمان حد المسافة بمقدار {{km}} كم (حتى قراءة {{date}}).",
      "jc.reopenWarrantyExpiredTime_few": "انتهى هذا الضمان منذ {{count}} أيام (بتاريخ {{date}}).",
      "jc.reopenWarrantyExpiredTime_many": "انتهى هذا الضمان منذ {{count}} يومًا (بتاريخ {{date}}).",
      "jc.reopenWarrantyExpiredTime_one": "انتهى هذا الضمان منذ يوم واحد (بتاريخ {{date}}).",
      "jc.reopenWarrantyExpiredTime_other": "انتهى هذا الضمان منذ {{count}} يوم (بتاريخ {{date}}).",
      "jc.reopenWarrantyExpiredTime_two": "انتهى هذا الضمان منذ يومين (بتاريخ {{date}}).",
      "jc.reopenWarrantyExpiredTime_zero": "انتهى هذا الضمان اليوم (بتاريخ {{date}}).",
      "jc.reopenWarrantyKmHint": "مطلوب — هذا الضمان له حد مسافة. أدخل ما يعرضه العداد الآن.",
      "jc.reopenWarrantyKmLabel": "قراءة عداد المسافة الحالية",
      "jc.reopenWarrantyKmRequiredMsg": "أدخل قراءة عداد المسافة الحالية للمتابعة",
      "jc.reopenWarrantyLoadingServices": "جارٍ تحميل الخدمات المشمولة…",
      "jc.reopenWarrantyOverrideHint": "يُسجَّل عند إعادة الفتح — من قام بالتجاوز والقراءة التي رُفضت.",
      "jc.reopenWarrantyOverrideLabel": "تجاوز الضمان وإعادة الفتح على أي حال",
      "jc.reopenWarrantyOverrideReasonLabel": "سبب التجاوز",
      "jc.reopenWarrantyOverrideReasonRequiredMsg": "أدخل سببًا للتجاوز",
      "jc.reopenWarrantyServiceLabel": "الخدمة المشمولة بالضمان",
      "jc.reopenWarrantyServicePlaceholder": "اختر الخدمة المشمولة بالضمان",
      "jc.reopenWarrantyServiceRequiredMsg": "يرجى اختيار الخدمة المشمولة التي تتعلق بها هذه المطالبة",
      "wty.accessDenied": "ليس لديك صلاحية الوصول إلى الضمانات",
      "wty.accessDeniedDesc": "اطلب من المسؤول منحك صلاحية \"عرض الضمانات\".",
      "wty.activate": "تفعيل الضمان",
      "wty.activateFailed": "تعذر تفعيل الضمان",
      "wty.activateSubmit": "تفعيل",
      "wty.activateSuccess": "تم تفعيل الضمان",
      "wty.asOf": "كما في {{date}}",
      "wty.cancel": "إلغاء",
      "wty.catalog.hint": "يملأ مدة الضمان مسبقًا عند تفعيل الفريق ضمانًا على هذه الخدمة. اترك المدة فارغة لضمان قائم على المسافة فقط، أو اترك كل الحقول فارغة إذا لم تقدّم هذه الخدمة ضمانًا افتراضيًا.",
      "wty.catalog.sectionTitle": "الضمان الافتراضي (اختياري)",
      "wty.col.activated": "تاريخ التفعيل",
      "wty.col.customer": "العميل",
      "wty.col.daysRemaining": "الأيام المتبقية",
      "wty.col.expires": "تاريخ الانتهاء",
      "wty.col.kmRemaining": "الكيلومترات المتبقية",
      "wty.col.service": "الخدمة",
      "wty.col.starts": "بداية الضمان",
      "wty.col.vehicle": "المركبة",
      "wty.covered": "مشمول بالضمان",
      "wty.customerPartsNote": "القطع التي يوفرها العميل غير مشمولة بهذا الضمان.",
      "wty.daysExpiredAgo_few": "انتهى الضمان منذ {{count}} أيام",
      "wty.daysExpiredAgo_many": "انتهى الضمان منذ {{count}} يومًا",
      "wty.daysExpiredAgo_one": "انتهى الضمان منذ يوم واحد",
      "wty.daysExpiredAgo_other": "انتهى الضمان منذ {{count}} يوم",
      "wty.daysExpiredAgo_two": "انتهى الضمان منذ يومين",
      "wty.daysExpiredAgo_zero": "انتهى الضمان اليوم",
      "wty.daysRemaining_few": "{{count}} أيام متبقية",
      "wty.daysRemaining_many": "{{count}} يومًا متبقيًا",
      "wty.daysRemaining_one": "يوم واحد متبقٍ",
      "wty.daysRemaining_other": "{{count}} يوم متبقٍ",
      "wty.daysRemaining_two": "يومان متبقيان",
      "wty.daysRemaining_zero": "لا أيام متبقية",
      "wty.dialogTitle": "تفعيل الضمان — {{service}}",
      "wty.duration.day_few": "{{count}} أيام",
      "wty.duration.day_many": "{{count}} يومًا",
      "wty.duration.day_one": "يوم واحد",
      "wty.duration.day_other": "{{count}} يوم",
      "wty.duration.day_two": "يومان",
      "wty.duration.day_zero": "0 يوم",
      "wty.duration.month_few": "{{count}} أشهر",
      "wty.duration.month_many": "{{count}} شهرًا",
      "wty.duration.month_one": "شهر واحد",
      "wty.duration.month_other": "{{count}} شهر",
      "wty.duration.month_two": "شهران",
      "wty.duration.month_zero": "0 شهر",
      "wty.duration.year_few": "{{count}} سنوات",
      "wty.duration.year_many": "{{count}} سنة",
      "wty.duration.year_one": "سنة واحدة",
      "wty.duration.year_other": "{{count}} سنة",
      "wty.duration.year_two": "سنتان",
      "wty.duration.year_zero": "0 سنة",
      "wty.empty": "لا توجد ضمانات سارية",
      "wty.emptyDesc": "لا توجد حاليًا أي خدمة مشمولة بضمان.",
      "wty.expiredDistance": "انتهى الضمان (المسافة)",
      "wty.expiredKmLimit": "تجاوز حد الكيلومترات",
      "wty.expiredTime": "انتهى الضمان",
      "wty.expiresOn": "ينتهي في",
      "wty.expiryPreview": "{{duration}} — تنتهي في {{date}}",
      "wty.featureDisabled": "ميزة ضمان الخدمة غير مفعّلة",
      "wty.featureDisabledDesc": "اطلب من المسؤول تفعيل ميزة ضمان الخدمة لهذه الشركة.",
      "wty.inv.title": "ضمان الخدمة",
      "wty.invalidKm": "أدخل رقمًا صحيحًا أكبر من صفر",
      "wty.invalidValue": "أدخل رقمًا صحيحًا أكبر من صفر",
      "wty.km": "كم",
      "wty.kmHint": "اتركه فارغًا لضمان زمني فقط",
      "wty.kmLabel": "حد المسافة (اختياري)",
      "wty.kmAnchorLabel": "قراءة العداد عند التفعيل",
      "wty.kmLimitLabel": "مسافة الضمان",
      "wty.kmOver": "تجاوز الحد بـ {{km}} كم (حتى قراءة {{date}})",
      "wty.kmPlaceholder": "مثال: 5000",
      "wty.kmRemaining": "{{km}} كم متبقية (حتى قراءة {{date}})",
      "wty.kmRemainingLabel": "المسافة المتبقية",
      "wty.kmUnknown": "لا توجد قراءة عداد مسجلة بعد",
      "wty.kmUndated": "توجد قراءة لكن بلا تاريخ",
      "wty.kmValue": "{{km}} كم",
      "wty.limitationNote": "لا يشمل القطع التي يوفرها العميل.",
      "wty.loadFailed": "تعذّر تحميل الضمانات.",
      "wty.mon.kmOver_few": "تجاوز الحد بـ {{n}} كيلومترات",
      "wty.mon.kmOver_many": "تجاوز الحد بـ {{n}} كيلومترًا",
      "wty.mon.kmOver_one": "تجاوز الحد بكيلومتر واحد",
      "wty.mon.kmOver_other": "تجاوز الحد بـ {{n}} كيلومتر",
      "wty.mon.kmOver_two": "تجاوز الحد بكيلومترين",
      "wty.mon.kmOver_zero": "تجاوز الحد للتو",
      "wty.mon.kmRemaining_few": "{{n}} كيلومترات متبقية",
      "wty.mon.kmRemaining_many": "{{n}} كيلومترًا متبقيًا",
      "wty.mon.kmRemaining_one": "كيلومتر واحد متبقٍ",
      "wty.mon.kmRemaining_other": "{{n}} كيلومتر متبقٍ",
      "wty.mon.kmRemaining_two": "كيلومتران متبقيان",
      "wty.mon.kmRemaining_zero": "لا كيلومترات متبقية",
      "wty.mon.kmUnknown": "لا توجد قراءة بعد",
      "wty.mon.kmUndated": "قراءة بلا تاريخ",
      "wty.mon.noKmLimit": "بلا حد كيلومترات",
      "wty.mon.noTimeLimit": "بلا حد زمني",
      "wty.navTitle": "مراقبة الضمانات",
      "wty.noKmLimit": "بدون حد للمسافة — حد زمني فقط",
      "wty.noLimitError": "أدخل مدة زمنية أو حد مسافة أو كليهما.",
      "wty.noTimeLimit": "بدون حد زمني — حد المسافة فقط",
      "wty.notCovered": "غير مشمول بالضمان",
      "wty.notEligibleBillingOnly": "بنود الفوترة فقط لا يشملها الضمان",
      "wty.notEligibleNotCompleted": "يتوفر بعد تحديد الخدمة كمكتملة",
      "wty.openJobCard": "فتح بطاقة العمل",
      "wty.panelTitle": "ضمان الخدمة",
      "wty.renewPrompt.continueHint": "لا تُعاد المدة من جديد — هذا العمل هو استكمال المركز لما وعد به بالفعل. تاريخ الانتهاء وحد المسافة الأصليان يبقيان كما هما.",
      "wty.renewPrompt.continueTitle": "متابعة نفس الضمان",
      "wty.renewPrompt.currentLabel": "التغطية الحالية",
      "wty.renewPrompt.intro": "تمت إعادة فتح بطاقة العمل هذه بموجب الضمان. اختر ما سيحدث لكل خدمة مشمولة قبل إغلاقها مرة أخرى.",
      "wty.renewPrompt.submit": "متابعة الإغلاق",
      "wty.renewPrompt.submitFailed": "تعذّر تحديث الضمان. يرجى المحاولة مرة أخرى.",
      "wty.renewPrompt.supersedeHint": "وعد جديد للعميل — تبدأ المدة من جديد اعتبارًا من اليوم ويُطبع هذا في الفاتورة القادمة.",
      "wty.renewPrompt.supersedeTitle": "إصدار ضمان جديد",
      "wty.renewPrompt.title": "متابعة الضمان الحالي أم إصدار ضمان جديد؟",
      "wty.showExpired": "إظهار الضمانات المنتهية",
      "wty.status.active": "ساري",
      "wty.status.expired": "منتهٍ",
      "wty.subtitle": "كل ضمان خدمة لا يزال ساريًا، الأقرب لانتهائه أولاً.",
      "wty.timeLimitLabel": "المدة الزمنية",
      "wty.timeRemainingLabel": "الوقت المتبقي",
      "wty.title": "مراقبة الضمانات",
      "wty.unit.day": "يوم",
      "wty.unit.month": "شهر",
      "wty.unit.year": "سنة",
      "wty.unitDay": "أيام",
      "wty.unitLabel": "الوحدة",
      "wty.unitMonth": "أشهر",
      "wty.unitYear": "سنوات",
      "wty.unitChoose": "اختر الوحدة",
      "wty.unitRequired": "اختر وحدة المدة: يوم أو شهر أو سنة",
      "wty.correct": "تعديل الضمان",
      "wty.correctTitle": "تعديل ضمان: {{service}}",
      "wty.correctReasonLabel": "سبب التعديل",
      "wty.correctReasonPlaceholder": "مثال: أُدخلت المدة بالأشهر بدلاً من السنوات",
      "wty.correctSubmit": "حفظ التعديل",
      "wty.correctSuccess": "تم تعديل الضمان",
      "wty.correctFailed": "تعذّر تعديل الضمان",
      "wty.correctNoChange": "لم تتغير حدود الضمان",
      "wty.correctStartHint": "تُحتسب المدة من تاريخ بدء الضمان الأصلي: {{date}}",
      "wty.void": "إلغاء الضمان",
      "wty.voidTitle": "إلغاء ضمان: {{service}}",
      "wty.voidBody": "سيتوقف هذا الضمان فوراً ويبقى في السجل. يمكنك تفعيل ضمان جديد للخدمة بعد ذلك.",
      "wty.voidReasonLabel": "سبب الإلغاء",
      "wty.voidSubmit": "إلغاء الضمان",
      "wty.voidSuccess": "تم إلغاء الضمان",
      "wty.voidFailed": "تعذّر إلغاء الضمان",
      "wty.unnamedService": "خدمة",
      "wty.valueLabel": "المدة",
      "wty.valuePlaceholder": "مثال: 3",
      "wty.whichever": "أيهما أسبق",
      "wty.mon.kmOver": "تجاوز بـ {{n}} كم",
      "err.SLOT_FULL": "هذا الموعد محجوز بالكامل.",
      "err.SUB_TASK_DEPENDENCY_CYCLE": "هذه الخطوات تنتظر بعضها في حلقة مغلقة.",
      // Short form shown UNDER the field, where the label already named it.
      // Optional per code: fieldErrorMap falls back to the full err.* sentence.
      "err.field.BRANCH_NAME_TAKEN": "يستخدم فرع آخر لهذه الشركة هذا الاسم",
      "err.field.REQUIRED_FIELD": "مطلوب",
      "err.field.REQUIRED_SELECTION": "اختر قيمة",
      "err.field.REQUIRED_FILE": "أرفق ملفًا",
      "err.field.LAST_UNLIMITED_APPROVER": "يجب أن يبقى مستخدم واحد على الأقل دون حد حتى يتمكن أحد من الاعتماد.",
      "err.field.REQUIRED_REASON": "اكتب السبب",
      "err.field.REASON_REQUIRED": "اكتب السبب",
      "err.field.WAIVER_REASON_REQUIRED": "اكتب السبب",
      "err.field.VALIDATION_FAILED": "قيمة غير صحيحة",
      "err.field.FIELD_INVALID": "قيمة غير صحيحة",
      "err.field.INVALID_INPUT": "قيمة غير صحيحة",
      "err.field.FIELD_MUST_BE_ARRAY": "قيمة غير صحيحة",
      "err.field.FIELD_OPTION_INVALID": "ليس من الخيارات المسموحة",
      "err.field.FIELD_TOO_LONG": "أطول من المسموح",
      "err.field.FIELD_OUT_OF_RANGE": "خارج النطاق المسموح",
      "err.field.FIELD_MUST_BE_NUMBER": "اكتب رقمًا",
      "err.field.DATE_INVALID": "تاريخ غير صحيح",
      "err.field.DATE_RANGE_INVALID": "فترة تاريخ غير صحيحة",
      "err.field.AMOUNT_POSITIVE_REQUIRED": "اكتب مبلغًا أكبر من صفر",
      "err.field.AMOUNT_NON_NEGATIVE_REQUIRED": "لا يمكن أن يكون أقل من صفر",
      "err.field.ALREADY_EXISTS": "مستخدم من قبل",
      "err.field.INVALID_REFERENCE": "غير موجود",
      "err.field.MUTUALLY_EXCLUSIVE_FIELDS": "لا يمكن استخدامه مع الحقل الآخر",
      "err.field.TOO_MANY_ITEMS": "أكثر من العدد المسموح",
      "err.ACCOUNT_CODE_INVALID": "رمز الحساب يجب أن يحتوي على الرموز المسموحة فقط ولا يتجاوز 50 حرفاً.",
      "err.DOCUMENT_NOT_FOUND": "المستند المطلوب غير موجود.",
      "err.DOWNPAYMENT_ARTIFACT_PENDING":
        "تم تسجيل موافقتك، لكن تعذر إنشاء مستند الدفع حتى الآن. يرجى المحاولة مرة أخرى بعد قليل — لن يتم خصم المبلغ مرتين.",
      "err.APPROVAL_NOT_RECORDED_RETRY":
        "تعذّر حفظ موافقتك ولم يتم تغيير أي شيء. يرجى المحاولة مرة أخرى — لم يتم خصم أي مبلغ منك.",
      "err.ACCOUNT_INACTIVE": "لا يمكن جعل الحساب المُعطّل حساباً افتراضياً.",
      "err.ACCOUNT_IS_DEFAULT": "الحساب الافتراضي يُستبدل بتعيين حساب آخر افتراضياً، ولا يُحذف.",
      "err.ACCOUNT_NOT_EMPTY": "هذا الحساب لا يزال به رصيد. حوّل الرصيد إلى حساب آخر أولاً، ثم عطّله.",
      "err.ACTION_NOT_ALLOWED": "هذا الإجراء غير مسموح به.",
      "err.AI_QUOTA_EXCEEDED": "استنفدت هذه الشركة حصتها الشهرية من الذكاء الاصطناعي. تواصل مع موتورمايند لزيادتها.",
      "err.AI_QUOTA_SUPER_ADMIN_ONLY": "يتم تحديد حدود الذكاء الاصطناعي من قبل موتورمايند.",
      "err.ALREADY_EXISTS": "يوجد سجل مطابق مسبقاً.",
      "err.ALREADY_TENANT": "رقم الجوال هذا مسجل بالفعل في حساب على موتورمايند.",
      "err.AMBIGUOUS_IDENTIFIER": "أرسل معرّفاً واحداً فقط، وليس أكثر.",
      "err.INVALID_CR_NUMBER": "رقم السجل التجاري غير صحيح.",
      "err.INVALID_VAT_NUMBER": "الرقم الضريبي غير صحيح.",
      "err.AMOUNT_INVALID": "المبلغ يجب أن يكون رقماً صحيحاً.",
      "err.AMOUNT_NON_NEGATIVE_REQUIRED": "المبلغ يجب ألا يكون سالباً.",
      "err.AMOUNT_POSITIVE_REQUIRED": "المبلغ يجب أن يكون أكبر من صفر.",
      "err.ANNOUNCEMENT_INVALID": "طلب الإعلان غير صالح.",
      "err.ANNOUNCEMENT_NOT_FOUND": "هذا الإعلان لم يعد موجودًا.",
      "err.ANNOUNCEMENT_ENDED": "انتهى هذا الإعلان. أعد نشره قبل إرساله عبر واتساب.",
      "err.CATEGORY_UNIT_IN_USE": "ما زال هناك رصيد بالوحدة الحالية. صرّفه قبل تغيير الوحدة.",
      "err.PART_UNIT_IN_USE": "ما زال لهذه القطعة رصيد في المخزون. صفّر الرصيد قبل تغيير وحدة القياس — الكمية لا تُحوَّل تلقائيًا.",
      "deployNotice.title": "إرسال إشعار إلى هذه القائمة",
      "deployNotice.hint": "رسالة واتساب واحدة من هاتف المنصة المرتبط إلى جميع من في الأعلى. تُرسل فورًا، ولا تُحفظ كمسودة.",
      "deployNotice.placeholder": "ما الذي تتضمنه هذه النسخة، وما الذي يجب اختباره…",
      "deployNotice.count": "{{n}} من {{max}} حرفًا",
      "deployNotice.send": "إرسال الآن",
      "deployNotice.sent": "أُرسلت إلى {{n}} — فشل {{failed}}",
      "deployNotice.failed": "لم تُرسل الرسالة. تحقّق من ربط واتساب وحاول مرة أخرى.",
      "err.DEPLOY_NO_RECIPIENTS": "لا يوجد أحد في هذه القائمة بعد. أضف شخصًا في الأعلى ثم أرسل.",
      "err.DEPLOY_EVENT_INVALID": "اختر الفريق الذي سيصله هذا الإشعار.",
      "err.DEPLOY_NOTICE_FAILED": "لم تصل الرسالة إلى أحد. تحقّق من ربط واتساب وحاول مرة أخرى.",
      "err.DEPLOY_RECIPIENTS_UNAVAILABLE": "تعذّرت قراءة قائمة المستلمين. حاول بعد قليل."
      ,"err.CATEGORY_IN_USE": "ما زالت هناك قطع مسجّلة تحت هذه الفئة."
      ,"err.CATEGORY_NAME_IN_USE": "توجد قطع مسجّلة تحت هذا الاسم. إعادة التسمية إليه ستدمج كتالوجين معًا."
      ,"err.QTY_MUST_BE_WHOLE": "هذه القطعة تُحسب بوحدات كاملة. أدخل رقمًا صحيحًا."
      ,"err.NOT_FOUND_CATEGORY": "هذه الفئة لم تعد موجودة."
      ,"err.PART_CATEGORY_INVALID": "راجع الحقول المحددة.",
      "err.DEPLOY_EVENT_INVALID": "حدث النشر هذا غير معروف لدى الخادم.",
      "err.ASSIGNEE_NOT_IN_BRANCH": "هذا المستخدم غير مرتبط بهذا الفرع. اربطه بالفرع لتتمكن من إسناد العمل إليه.",
      "err.ASSISTANT_DATA_INVALID": "بيانات المساعد يجب أن تكون كائن JSON صالح.",
      "err.ASSISTANT_DATA_TOO_LARGE": "حمل بيانات المساعد كبير جداً.",
      "err.ASSISTANT_GL_NOT_AVAILABLE": "الأستاذ العام غير متاح لك: الخاصية غير مفعّلة لهذه المنشأة، أو لا تملك صلاحية قيود اليومية.",
      "err.ASSISTANT_GL_WRITE_FORBIDDEN": "المساعد يُعدّ القيود كمسودّات فقط. ترحيل القيد، وإقفال الفترة أو إعادة فتحها، واعتماد الأرصدة الافتتاحية تبقى بيد المحاسب.",
      "err.ASSISTANT_MESSAGES_INVALID": "رسائل المساعد غير صالحة.",
      "err.ATTACHMENT_INVALID": "المرفق غير صالح.",
      "err.ATTACHMENTS_NOT_A_LIST": "يجب إرسال المرفقات كقائمة.",
      "err.ATTACHMENT_URL_INVALID": "هذا الملف لم يُرفع عبر MotorMind، لذلك لم يتم حفظه. ارفع المستند مرة أخرى.",
      "err.AUDIO_OR_TEXT_REQUIRED": "أرفق تسجيلاً صوتياً أو أدخل نصاً.",
      "err.AUTH_PASSWORD_INVALID": "كلمة المرور المُدخلة غير صالحة.",
      "err.BANK_SARIE_INVALID": "رمز البنك في نظام سريع (SARIE) يجب أن يكون 4 أحرف.",
      "err.BANK_STATEMENT_BALANCE_INVALID": "أرصدة كشف الحساب غير صالحة.",
      "err.BNPL_NOT_ELIGIBLE": "الدفع بالتقسيط غير متاح لهذا الطلب.",
      "err.BRANCH_INVALID": "الفرع غير صالح.",
      "err.BRANCH_REQUIRED": "اختر الفرع.",
      "err.BUILDING_NUMBER_INVALID": "رقم المبنى يجب أن يكون 4 أرقام بالضبط.",
      "err.CAR_HANDED_OVER_USE_RETURN_FLOW": "تم تسليم المركبة للمركز. أغلق أمر العمل باستخدام مسار الإرجاع.",
      "err.CASH_ACCOUNT_INVALID": "حساب النقدية غير صالح.",
      "err.CASH_TRANSFER_INVALID": "التحويل النقدي يحتاج إلى حسابين فرعيين صالحين.",
      "err.COMPANY_CONTEXT_REQUIRED": "سياق الشركة مطلوب.",
      "err.CONFIG_INVALID": "الإعدادات غير صالحة.",
      "err.CONFIRMATION_STATE_INVALID": "التأكيد ليس في الحالة الصحيحة لهذا الإجراء.",
      "err.CREDIT_ACTION_REQUIRED": "رصيد العميل يتجاوز قيمة الفاتورة — اختر الإبقاء عليه في الحساب أو استرداده.",
      "err.CREDIT_REASON_REQUIRED": "اكتب سبب إصدار الإذن بالآجل.",
      "err.CR_NUMBER_INVALID": "السجل التجاري يجب أن يكون 10 أرقام بالضبط.",
      "err.LAST_UNLIMITED_APPROVER": "هذا التغيير سيترك الشركة دون أي مستخدم يملك صلاحية الاعتماد. امنح مستخدمًا آخر صلاحية الاعتماد دون حد أولًا.",
      "err.BUYER_ID_REQUIRED": "يلزم للعميل التجاري رقم ضريبي، أو رقم معرّف مع تحديد نوعه.",
      "err.ID_NUMBER_INVALID": "رقم المعرّف لا يطابق نوعه.",
      "err.ID_TYPE_INVALID": "نوع المعرّف ليس من أنواع معرّفات المشتري المعتمدة لدى هيئة الزكاة والضريبة والجمارك.",
      "err.CUSTOMER_INVALID": "العميل غير صالح.",
      "err.CUSTOMER_PROVIDED_REQUIRES_CUSTOM_PART": "قطع العميل يجب إضافتها كقطع غير مخزنية.",
      "err.CUSTOM_REJECTION_REASONS_INVALID": "أسباب الرفض المخصّصة غير صالحة.",
      "err.DATE_INVALID": "التاريخ غير صالح.",
      "err.DATE_RANGE_INVALID": "نطاق التاريخ غير صالح.",
      "err.DAY_CLOSED": "الفرع مغلق في اليوم المختار.",
      "err.DEFAULT_PART_SOURCE_INVALID": "اختر قطعة من الكتالوج أو أدخل اسم قطعة مخصّصة، وليس كليهما.",
      "err.DELAY_FEE_ALREADY_BILLED": "بعض أيام التأخير بعد هذا التاريخ مُفوترة بالفعل. ألغِ الفاتورة أو أصدر إشعار دائن أولاً، ثم حدّد تاريخ الاستلام.",
      "err.DELAY_FEE_GATE_UNAVAILABLE": "تعذّر التحقق من رسوم التأخير المستحقة — لم يتم تسليم المركبة. يرجى المحاولة مرة أخرى.",
      "err.DELAY_FEE_OUTSTANDING": "أمر العمل لا يزال به رسوم تأخير غير مدفوعة.",
      "err.DELAY_TERMS_NOT_ACCEPTED": "اقبُل شروط التأخير قبل المتابعة.",
      "err.DELIVERY_CODE_INVALID": "رمز التسليم غير صالح.",
      "err.DEPENDENCY_NOT_ON_JOB_CARD": "المهمة المعتمدة غير موجودة في أمر العمل هذا.",
      "err.DIAGNOSIS_STATE_INVALID": "التشخيص ليس في الحالة الصحيحة لهذا الإجراء.",
      "err.EMAIL_ALREADY_REGISTERED": "عنوان البريد الإلكتروني مسجّل مسبقاً في هذه البوابة.",
      "err.EMAIL_INVALID": "صيغة البريد الإلكتروني غير صحيحة.",
      "err.EMPLOYEE_CONTRACT_INVALID": "بيانات عقد الموظف غير صالحة.",
      "err.EXPENSE_ACCOUNT_REQUIRED": "اختر الحساب الذي يخصّ هذا المصروف حتى يُسجَّل في مكانه الصحيح في الدفاتر.",
      "err.EXPENSE_INVALID": "المصروف غير صالح.",
      "err.EXPORT_IN_FLIGHT": "توجد عملية تصدير حالية للشركة.",
      "err.EXPORT_RATE_LIMIT": "يُسمح بعملية تصدير واحدة فقط للشركة في هذه الفترة.",
      "err.EXPORT_REVOKED": "تم إيقاف تصدير البيانات لهذه الشركة. تواصل مع دعم MotorMind.",
      "err.EXTRACTION_FAILED": "فشل الاستخراج الذكي لكل الصور.",
      "err.SCAN_META_INVALID": "بيانات المسح المرسلة مع هذا السجل غير صالحة.",
      "err.STREAM_FAILED": "انقطع مسح الفاتورة. أعد المحاولة.",
      "err.FEE_ALREADY_BILLED": "هذه الرسوم مُفوترة بالفعل. ألغِها بإشعار دائن بدلاً من الإعفاء.",
      "err.FIELD_INVALID": "راجع الحقول المُظلّلة.",
      "err.FIELD_MUST_BE_ARRAY": "الحقل يجب أن يكون قائمة.",
      "err.FIELD_MUST_BE_BOOLEAN": "الحقل يجب أن يكون صح أو خطأ.",
      "err.FIELD_MUST_BE_NUMBER": "الحقل يجب أن يكون رقماً.",
      "err.FIELD_MUST_BE_OBJECT": "الحقل يجب أن يكون كائناً.",
      "err.FIELD_OPTION_INVALID": "خيار الحقل غير صالح.",
      "err.FIELD_OUT_OF_RANGE": "قيمة الحقل خارج النطاق المسموح.",
      "err.FIELD_TOO_LONG": "قيمة الحقل أطول من المسموح.",
      "err.FIELD_TOO_SHORT": "قيمة الحقل أقصر من المطلوب.",
      "err.FILE_EMPTY": "الملف فارغ.",
      "err.FILE_UNREADABLE": "تعذّر قراءة الملف المُرفوع.",
      "err.GARAGE_CONTEXT_REQUIRED": "سياق المركز مطلوب.",
      "err.HOURS_OVERRIDE_NOT_VALID_WITH_SUB_TASKS": "لا يمكن تعديل الساعات ما دامت هناك مهام فرعية.",
      "err.IBAN_INVALID": "رقم الآيبان غير صالح.",
      "err.INVALID_DOCUMENT_DESIGNS": "إعدادات تصميم المستندات غير صالحة. اختر أحد التصميمات المعروضة لكل مستند.",
      "err.INVALID_INVOICE_BANK": "التفاصيل البنكية للفواتير غير صالحة. تحقَّق من رقم الآيبان واسم صاحب الحساب.",
      "err.INVALID_STAMP": "لا يمكن استخدام ملف الختم هذا. ارفع صورة PNG لا يتجاوز حجمها 1 ميجابايت، وأبعادها 2000 × 2000 بكسل على الأكثر.",
      "err.INSURER_INVALID": "شركة التأمين غير صالحة.",
      "err.INVALID_ATTACHMENT": "اختيار المرفق غير صالح.",
      "err.INVALID_CODE": "رمز الدخول غير صحيح. تحقّق من آخر رسالة وحاول مجددًا.",
      "err.INVALID_DELAY_TERMS": "شروط التأخير غير صالحة.",
      "err.INVALID_DEVICE_METADATA": "اسم الجهاز أو إصدار التطبيق طويل جدًا.",
      "err.INVALID_EXPECTED_AT": "تاريخ التوقع غير صالح.",
      "err.INVALID_INTERVAL": "الفاصل الزمني غير صالح.",
      "err.INVALID_MOBILE_NUMBER": "رقم الجوال غير صالح.",
      "err.INVALID_PHONE": "رقم واتساب غير صالح.",
      "err.INVALID_PLATFORM": "منصة الجهاز يجب أن تكون iOS أو Android.",
      "err.INVALID_QC_RESULT": "نتيجة فحص الجودة غير صالحة.",
      "err.INVALID_RECEIVED_AT": "تاريخ الاستلام غير صالح.",
      "err.INVALID_REFERENCE": "السجل المُشار إليه غير صالح.",
      "err.INVALID_SUB_TASK_STATUS": "حالة المهمة الفرعية غير صالحة.",
      "err.INVALID_TOKEN": "رمز تسجيل الإشعارات مفقود أو طويل جدًا.",
      "err.INVENTORY_LOCATION_INVALID": "موقع المخزون غير صالح.",
      "err.JOB_CARD_BRANCH_INVALID": "فرع أمر العمل غير صالح.",
      "err.JOB_CARD_CLOSED": "أمر العمل مُغلق.",
      "err.JOB_CARD_NOT_APPROVABLE": "أمر العمل مُغلق — لا يمكن للعميل اعتماد عرض السعر بعد الآن.",
      "err.JOB_CARD_NOT_LIVE": "أمر العمل لم يعد مفتوحاً.",
      "err.JOB_CARD_NOT_ON_HOLD": "أمر العمل ليس قيد الانتظار.",
      "err.JOB_CARD_STATE_INVALID": "أمر العمل ليس في الحالة الصحيحة لهذا الإجراء.",
      "err.LIMIT_BELOW_USAGE": "الحد الجديد أقل من الاستخدام الحالي للشركة.",
      "err.LIMIT_FILE_SIZE": "حجم الملف كبير جدًا. اختر ملفًا أصغر.",
      "err.LIMIT_FILE_COUNT": "عدد الملفات كبير جدًا. أرفق عددًا أقل.",
      "err.LIMIT_UNEXPECTED_FILE": "تعذّر قبول هذا الملف هنا.",
      "err.UPLOAD_TYPE_NOT_ALLOWED": "نوع هذا الملف غير مسموح به هنا.",
      "err.MEDIA_INVALID": "الوسائط غير صالحة.",
      "err.TOO_LARGE": "حجم الملف أكبر مما يسمح به هذا الرفع.",
      "err.INSPECTION_RESULT_NOT_FOUND": "نقطة الفحص غير موجودة.",
      "err.INVOICE_FILE_URL_INVALID": "يجب أن يكون ملف الفاتورة مرفوعاً في MotorMind، لذلك لم يتم حفظه. ارفع الملف مرة أخرى.",
      "err.MEDIA_DIRECT_UPLOAD_DISABLED": "غير موجود.",
      "err.MEDIA_REBIND_FAILED": "تعذّر نقل مستند مرفق بهذه المسودة، ولم يتم إنشاء أي شيء. المسودة كما هي — احذف المستند وأضفه من جديد ثم حاول مرة أخرى.",
      "err.MOBILE_AUTH_NOT_CONFIGURED": "الدخول عبر التطبيق غير متاح مؤقتًا. حاول مرة أخرى لاحقًا.",
      "err.MONEY_COLLECTED_USE_RETURN_FLOW": "أمر العمل به مبالغ محصّلة. أغلقه باستخدام مسار الإرجاع.",
      "err.MUTUALLY_EXCLUSIVE_FIELDS": "الحقول متناقضة — اختر واحداً منها فقط.",
      "err.NOT_A_QUALITY_CHECK_SUB_TASK": "المهمة الفرعية ليست فحص جودة.",
      "err.NOT_QIWA_CONTRACT": "الملف ليس عقد قوى.",
      "err.NO_ACCOUNT": "لا يوجد حساب نقدية لهذا التحويل.",
      "err.NO_BILLABLE_ITEMS": "لا توجد بنود قابلة للفوترة.",
      "err.NO_CHANGES_PROVIDED": "لم يتم إدخال أي تعديلات.",
      "err.NO_EDITABLE_FIELDS": "لم يتم تحديد حقول قابلة للتعديل.",
      "err.NO_FEES_OWED": "أمر العمل ليس به رسوم تأخير مستحقة للإفراج بالآجل.",
      "err.NO_HISTORY": "المركبة ليس لها تاريخ صيانة بعد.",
      "err.NO_INSURER_CONFIGURED": "لا توجد شركة تأمين نشطة.",
      "err.NO_PHOTOS": "لم تُرفَع أي صورة.",
      "err.NO_TERM": "الاشتراك لا يحتوي على فترة للتجديد.",
      "err.NO_VALID_ATTACHMENTS": "لم يتم إرفاق مرفقات صالحة.",
      "err.OPENING_BALANCE_NEGATIVE": "لا يمكن أن يكون الرصيد الافتتاحي سالباً؛ فالصندوق لا يحمل أقل من الصفر. إذا لم توجد على هذا الحساب أي حركات نقدية بعد، أدخل رقماً غير سالب لتصحيحه؛ أما إذا وُجدت حركات، فاترك رصيده الافتتاحي كما هو، واطلب من المالك أو مسؤول الشركة تصحيح الرصيد المعروض بتسجيل نقد وارد بسبب «إيداع رأس مال» لإضافة مبلغ، أو نقد صادر بسبب «سحب المالك» لخصم مبلغ.",
      "err.OPENING_LOCKED": "الرصيد الافتتاحي مُقفّل لوجود حركات سابقة. لتصحيحه الآن، اطلب من المالك أو مسؤول الشركة تسجيل نقد وارد بسبب «إيداع رأس مال» لإضافة مبلغ، أو نقد صادر بسبب «سحب المالك» لخصم مبلغ.",
      "err.OPENING_LOCKED_BANK": "الرصيد الافتتاحي مُقفّل لوجود حركات سابقة. لا يمكن تصحيح الرصيد الافتتاحي لحساب بنكي بحركة نقدية بعد القفل — فأي نقد وارد أو صادر بنفس التاريخ سيترك سطراً غير مطابق دائماً في تسوية البنك. اطلب من المالك أو مسؤول الشركة تسوية الحساب وتسجيل الفرق في ملاحظة الإقفال بدلاً من ذلك.",
      "err.OPENING_SIGNED_OFF": "الرصيد الافتتاحي مُقفّل لأن الأرصدة الافتتاحية مُوقّعة. لتصحيحه الآن، اطلب من المالك أو مسؤول الشركة تسجيل نقد وارد بسبب «إيداع رأس مال» لإضافة مبلغ، أو نقد صادر بسبب «سحب المالك» لخصم مبلغ.",
      "err.OPENING_SIGNED_OFF_BANK": "الرصيد الافتتاحي مُقفّل لأن الأرصدة الافتتاحية مُوقّعة. لا يمكن تصحيح الرصيد الافتتاحي لحساب بنكي بحركة نقدية بعد القفل — فأي نقد وارد أو صادر بنفس التاريخ سيترك سطراً غير مطابق دائماً في تسوية البنك. اطلب من المالك أو مسؤول الشركة تسوية الحساب وتسجيل الفرق في ملاحظة الإقفال بدلاً من ذلك.",
      "err.OPEN_JOB_CARD_EXISTS": "هذه المركبة لديها أمر عمل مفتوح بالفعل.",
      "err.OTP_EXPIRED": "رمز التحقق منتهي الصلاحية.",
      "err.OTP_INVALID": "رمز التحقق غير صحيح. المحاولات المتبقية: {{remaining}}.",
      "err.OTP_REQUIRED": "رمز التحقق مطلوب.",
      "err.OTP_SEND_FAILED": "تعذّر إرسال رمز التحقق. حاول مرة أخرى.",
      "err.OTP_TOO_MANY_ATTEMPTS": "محاولات التحقق كثيرة جداً. حاول لاحقاً.",
      // SEC-01 (review F2) — backend PHOTO_URL_INVALID, see utils/safeUrl.js.
      "err.PHOTO_URL_INVALID": "يجب أن تكون الصورة مرفوعة في MotorMind أو رابطاً يبدأ بـ https://.",
      "err.PARENT_TASK_NOT_ON_JOB_CARD": "المهمة الرئيسية غير موجودة في أمر العمل هذا.",
      "err.PART_HAS_NO_DEADLINE": "القطعة ليس لها موعد نهائي للتأخير.",
      "err.PART_INVALID": "القطعة غير صالحة.",
      "err.PART_UNIT_INVALID": "وحدة القطعة غير صالحة.",
      "err.PAYMENT_AMOUNT_INVALID": "مبلغ الدفع غير صالح.",
      "err.PAYMENT_EXCEEDS_BALANCE": "مبلغ الدفع أكبر من الرصيد المتبقي.",
      "err.PAYMENT_METHOD_INVALID": "طريقة الدفع غير صالحة.",
      "err.PAYMENT_METHOD_UNAVAILABLE": "طريقة الدفع غير متاحة لهذا السداد.",
      "err.PAYMENT_REFUND_INVALID": "طلب استرداد الدفع غير صالح.",
      "err.PAYMENT_TOKEN_REQUIRED": "رمز الدفع مطلوب.",
      "err.PAYMENT_VERIFICATION_FAILED": "فشل التحقق من الدفع.",
      "err.PAYOUT_INVALID": "طلب الصرف غير صالح.",
      "err.PERIOD_INVALID": "الفترة غير صالحة.",
      "err.PERMISSION_DENIED": "ليست لديك صلاحية تنفيذ هذا الإجراء. اطلب من المسؤول منحك الصلاحية.",
      "err.PERMISSION_DENIED_named": "ليست لديك صلاحية «{{permission}}». اطلب من المسؤول منحك إياها.",
      "err.PHONE_INVALID": "رقم الهاتف غير صالح.",
      "err.PACKAGE_PART_INVALID": "هذه الخدمة لا تشمل قطعها في سعرها.",
      "err.PACKAGE_PART_NOT_IN_TEMPLATE": "هذه القطعة ليست من قطع الباقة.",
      "err.PACKAGE_PART_QTY_EXCEEDED": "هذه الكمية أكثر مما تشمله الباقة.",
      "err.PACKAGE_NOT_APPROVED": "تُضاف قطع الباقة بعد اعتماد الباقة.",
      "err.PACKAGE_PARTS_MISSING": "قطع الباقة غير مضافة لأمر العمل بعد: {{list}}. أضفها من صندوق الباقة أولاً (القطعة غير المتوفرة تحتاج فاتورة مشتريات).",
      "err.PACKAGE_HAS_BILLING_ONLY_LINES": "لهذه الخدمة {{count}} سطر فوترة فقط في أوامر العمل، لذلك لا يمكن تحويلها إلى باقة.",
      "err.PART_NUMBER_TOO_LONG": "رقم القطعة أطول من المسموح؛ الحد الأقصى 100 حرف.",
      "err.PHONE_REQUIRED": "رقم الهاتف مطلوب.",
      "err.PHOTO_REQUIRED": "أرفق صورة واحدة على الأقل.",
      "err.PLAN_BRANCH_LIMIT": "تم الوصول إلى حد الفروع لهذه الخطة.",
      "err.PLAN_USER_LIMIT": "تم الوصول إلى حد المستخدمين لهذه الخطة.",
      "err.PLATE_INVALID": "حروف لوحة غير صالحة: {{letters}}. الحروف المسموحة: {{allowed}}.",
      "err.POSTAL_CODE_INVALID": "الرمز البريدي يجب أن يكون 5 أرقام بالضبط.",
      "err.PURCHASE_INVOICE_LINE_INVALID": "بند فاتورة الشراء غير صالح.",
      "err.PURCHASE_INVOICE_STATE_INVALID": "فاتورة الشراء ليست في الحالة الصحيحة لهذا الإجراء.",
      "err.QC_REOPEN_SERVICE_INVALID": "خدمة إعادة فتح فحص الجودة غير صالحة.",
      "err.QC_REOPEN_TARGET_INVALID": "هدف إعادة فتح فحص الجودة غير صالح.",
      "err.QR_CODE_INVALID": "رمز الاستجابة السريعة غير صالح.",
      "err.QUANTITY_POSITIVE_REQUIRED": "الكمية يجب أن تكون أكبر من صفر.",
      "err.QUOTATION_EXPIRED": "عرض السعر منتهي الصلاحية.",
      "err.ITEM_CUSTOMER_APPROVED_LOCKED": "وافق العميل على {{accepted_quantity}} × {{accepted_unit_price}} لهذا البند. يمكنك تخفيضه، أما رفع السعر أو الكمية أو تغيير الاسم فيحتاج موافقة جديدة على عرض سعر.",
      "err.QUOTATION_CUSTOMER_APPROVED_LOCKED": "وافق العميل على عرض السعر هذا، لذلك لا يمكن تعديله أو إعادة إرساله أو حذفه. أضف العمل الجديد في بطاقة العمل وأرسله للموافقة.",
      "err.INVOICE_LINE_EXCEEDS_CUSTOMER_APPROVAL": "{{count}} من بنود الفاتورة بسعر أعلى مما وافق عليه العميل. خفّضها إلى السعر المعتمد أو أرسل عرض سعر جديدًا أولًا.",
      "err.INVOICE_LINE_NOT_AGREED": "{{count}} من بنود الفاتورة رفضها العميل ولا يمكن فوترتها. احذفها أو أرسلها للعميل للموافقة مرة أخرى.",
      "err.QUOTATION_STATE_INVALID": "عرض السعر ليس في الحالة الصحيحة لهذا الإجراء.",
      "err.REASON_REQUIRED": "السبب مطلوب.",
      "err.RECEIVED_AT_IN_FUTURE": "تاريخ الاستلام لا يمكن أن يكون في المستقبل.",
      "err.RECEIPT_CREDIT_SHORTFALL": "رصيد سندات القبض المختارة لا يكفي لتغطية هذه الدفعة.",
      "err.REQUIRED_FIELD": "حقل مطلوب غير موجود.",
      "err.NO_RUNNING_JOB": "لا يوجد إنشاء بالذكاء الاصطناعي قيد التشغيل لإلغائه.",
      "err.REQUIRED_FILE": "ملف مطلوب غير موجود.",
      "err.REQUIRED_REASON": "سبب مطلوب غير موجود.",
      "err.REQUIRED_SELECTION": "اختيار مطلوب غير موجود.",
      "err.ROLE_INVALID": "الدور غير صالح.",
      "err.SERVICE_NOT_APPROVED": "الخدمة غير معتمدة.",
      "err.SERVICE_NOT_BOOKABLE": "لا يمكن حجز هذه الخدمة في هذا الفرع.",
      "err.SERVICE_TEMPLATE_INVALID": "قالب الخدمة غير صالح.",
      "err.SERVICE_TYPE_INVALID": "نوع الخدمة غير صالح.",
      "err.SESSION_NOT_CONNECTED": "الجلسة غير متصلة.",
      "err.SMS_BUDGET_EXHAUSTED": "تم بلوغ الحد اليومي لرسائل الدخول عبر التطبيق. حاول مرة أخرى لاحقًا.",
      "err.STATE_ALREADY_SET": "السجل في هذه الحالة بالفعل.",
      "err.STATE_INVALID": "السجل ليس في الحالة الصحيحة لهذا الإجراء.",
      "err.STOCK_CONSUMED": "لا يمكن العكس — بعض القطع من هذه الفاتورة استُخدمت بالفعل.",
      "err.STOCK_INSUFFICIENT": "المخزون غير كافٍ.",
      "err.PART_REQUEST_RELINKED": "نُقل طلب القطعة هذا إلى أمر عمل آخر أو قطعة أخرى. حدِّث الصفحة ثم أعد المحاولة.",
      "err.PART_REQUEST_INVOICE_OTHER_BRANCH": "مسودة فاتورة هذا العمل تابعة لفرع آخر، لذا لا يمكن اعتماد الطلب من هنا.",
      "err.BRANCH_COMPANY_MISSING": "هذا الفرع غير مرتبط بأي شركة، لذا لا يمكن الرد على طلبات القطع فيه. تواصل مع الدعم.",
      "err.STOCK_LINE_INVALID": "بند المخزون غير صالح.",
      "err.STOCK_REQUIRES_PURCHASE_INVOICE": "حركة المخزون تتطلّب فاتورة شراء.",
      "err.SUB_TASK_NAME_REQUIRED": "اسم المهمة الفرعية مطلوب.",
      "err.SUPERSEDED": "تم إرسال رابط تأكيد أحدث إلى جوالك. الرجاء استخدام الرابط الأخير.",
      "err.SUPPLIER_REQUIRED": "اختر المورّد.",
      "err.WARRANTY_MONTHS_INVALID": "يجب أن تكون مدة الضمان رقمًا صحيحًا بين ١ و ٦٠٠ شهرًا.",
      "err.WA_NOT_CONNECTED": "واتساب غير مرتبط. امسح رمز QR أولًا ثم أعد إرسال الرسالة التجريبية.",
      "err.WA_SEND_FAILED": "تم الربط لكن واتساب رفض إرسال الرسالة. جرّب إعادة ربط الهاتف.",
      "err.WA_CLOUD_INVALID_ID": "يجب أن يتكوّن معرّف حساب واتساب للأعمال ومعرّف رقم الهاتف من أرقام فقط.",
      "err.WA_CLOUD_INVALID_CREDENTIALS": "لم تقبل Meta هذه البيانات. تحقّق من رمز الوصول وسرّ التطبيق والمعرّفات ثم أعد المحاولة.",
      "err.WA_CLOUD_PHONE_IN_USE": "رقم الهاتف هذا مرتبط بورشة أخرى.",
      "err.WA_QR_PAIRING": "يوجد ربط عبر رمز QR قيد التنفيذ. أكمله أو ألغِه قبل ربط واجهة واتساب للأعمال.",
      "err.WA_QR_CLOUD_ACTIVE": "تستخدم هذه الورشة واجهة واتساب للأعمال. احذف ذلك الربط قبل ربط هاتف عبر رمز QR.",
      "err.WA_CLOUD_WINDOW_CLOSED": "مضى أكثر من 24 ساعة على آخر رسالة من العميل. يمكنك الرد بعد أن يراسلك مجددًا.",
      "err.WA_ACCOUNT_NOT_FOUND": "حساب واتساب هذا غير موجود.",
      "err.WA_UNSUBSCRIBE_PENDING": "لم تؤكد Meta الإزالة بعد. حاول إزالة الحساب مرة أخرى بعد دقيقة.",
      "err.LINE_NOT_ACCESSIBLE": "خط واتساب هذا غير مخصص لفرعك. اختر خطًا متاحًا لفرعك.",
      "err.LINE_NOT_FOUND": "خط واتساب المحدد غير موجود. يرجى اختيار خط نشط.",
      "err.LINE_REQUIRED": "يرجى اختيار خط واتساب لإرسال هذه الرسالة أو الحملة منه.",
      "err.NO_LINES_ACCESSIBLE": "لا توجد خطوط واتساب مخصصة لفرعك. اطلب من المشرف تعيين خط لفرعك.",
      "err.NO_LINE_AVAILABLE": "لا يتوفر خط واتساب حاليًا لهذا الفرع. اربط أو خصص خطًا من إعدادات واتساب.",
      "err.WA_ACCOUNT_INVALID": "حساب واتساب هذا غير صالح أو مقطوع الاتصال. أعد توثيق الحساب أو ربطه مجددًا.",
      "err.WA_ACCOUNT_IN_USE": "حساب واتساب هذا مستخدم حاليًا في فروع أو حملات نشطة. أزل تخصيص الفروع أو انتظر اكتمال الحملات.",
      "err.WA_CANNOT_ASSIGN_LEGACY_NUMBER": "لا يمكن تخصيص فرع لرقم واتساب قديم. قم بالترقية إلى نظام الأرقام المتعددة أولاً.",
      "err.WA_CLOUD_FAILED": "فشل تسليم رسالة واتساب السحابية. تحقق من حالة حساب Meta Business والقوالب.",
      "err.WA_CLOUD_SEND_FAILED": "فشل إرسال الرسالة عبر WhatsApp Cloud API. حاول مرة أخرى بعد لحظات.",
      "err.WA_CREDENTIALS_DECRYPT_FAILED": "تعذر فك تشفير بيانات اعتماد واتساب. أعد إدخال بيانات الاعتماد في إعدادات واتساب.",
      "err.WA_CREDENTIALS_INVALID_JSON": "صيغة بيانات اعتماد واتساب غير صحيحة. تحقق من بيانات JSON واحفظ مجددًا.",
      "err.WA_GARAGE_NOT_FOUND": "الفرع المحدد غير موجود. يرجى تحديث الصفحة واختيار فرع متاح.",
      "err.WA_MAIN_LINE_EXISTS": "لا يمكن تعيين خط افتراضي أثناء وجود خط رئيسي. قم بإلغاء ربط الخط الرئيسي أولاً.",
      "err.WA_MISSING_CREDENTIALS": "بيانات اعتماد واتساب مفقودة. أدخل رمز الوصول ومعرف رقم الهاتف للربط.",
      "err.WA_MULTI_ACTIVATING": "جاري تفعيل ميزة تعدد أرقام واتساب خلال مهلة التحويل (2 دقيقة). يرجى المحاولة بعد 2 دقيقة.",
      "err.WA_MULTI_DISABLED": "ميزة تعدد أرقام واتساب غير مفعلة لشركتك. تواصل مع الدعم الفني لتفعيلها.",
      "err.WA_MULTI_NO_NATIVE_NUMBER": "لا توجد أرقام واتساب مهيأة لشركتك. أضف رقمًا جديدًا من إعدادات واتساب.",
      "err.WA_NATIVE_DEFAULT_EXISTS": "يوجد رقم افتراضي نشط بالفعل. قم بإلغاء تعيين الرقم الافتراضي الحالي قبل تحديد رقم جديد.",
      "err.WA_NOT_NATIVE_ACCOUNT": "هذا الحساب ليس حساب واتساب متكامل. هذا الإجراء يتطلب حسابًا من النوع المدمج.",
      "err.WA_NUMBER_IN_USE": "رقم واتساب هذا مستخدم حاليًا في فروع أو حملات نشطة. قم بإلغاء تخصيصه أو إنهاء الحملات قبل إيقافه.",
      "err.WA_NUMBER_NOT_ACTIVE": "رقم واتساب هذا غير نشط. يرجى تفعيل الرقم أو إعادة ربطه قبل استخدامه.",
      "err.WA_NUMBER_NOT_NATIVE": "هذه العملية متاحة فقط لأرقام واتساب من النوع المدمج.",
      "err.WA_NUMBER_NOT_QR": "رقم واتساب هذا لا يستخدم الربط عبر مسح رمز الاستجابة السريعة (QR).",
      "err.WA_PHONE_IN_USE": "رقم الهاتف هذا مسجل بالفعل لخط أو حساب واتساب آخر.",
      "err.WA_PHONE_NOT_IN_WABA": "رقم الهاتف هذا غير مسجل تحت حساب واتساب للأعمال (WABA) المحدد.",
      "err.WA_WABA_IN_USE": "معرف حساب واتساب للأعمال (WABA) هذا مرتبط بشركة أخرى بالفعل.",
      "err.number_unavailable": "رقم واتساب هذا غير متاح حاليًا أو مقطوع الاتصال. أعد ربط الخط أو اختر خطًا آخر.",
      "err.CHAT_ID_REQUIRED": "افتح محادثة لعرض نشاط العميل.",
      "err.WA_CLOUD_API_REQUIRED": "إضافة حساب أو رقم من Meta تتطلب تفعيل ميزة WhatsApp Cloud API.",
      "err.WA_NOT_NATIVE_QR": "هذا الرقم غير مربوط برمز QR.",
      "err.WA_NUMBER_NOT_FOUND": "رقم واتساب هذا غير موجود.",
      "err.WA_CLOUD_TEMPLATE_UNKNOWN": "هذا ليس من قوالب واتساب القياسية.",
      "err.WA_CLOUD_TEMPLATE_UNAVAILABLE": "لم يعد هذا القالب موجودًا لدى Meta. تواصل مع الدعم.",
      "err.WA_CLOUD_CONNECTION_UNAVAILABLE": "اربط واجهة واتساب للأعمال أولًا، أو أعد ربطها من الإعدادات.",
      "err.WA_CLOUD_REMOVED": "حُذف ربط واجهة واتساب للأعمال، لذا أُوقفت هذه الحملة مؤقتًا.",
      "err.WA_CLOUD_MEDIA_NOT_SUPPORTED": "إرسال الملفات من صندوق الرسائل غير مدعوم بعد عبر واجهة واتساب للأعمال.",
      "err.WA_CLOUD_CONFIG_ERROR": "عنوان التطبيق العام غير مضبوط. تواصل مع الدعم.",
      "err.WA_CLOUD_INTERNAL": "حدث خطأ في ربط واجهة واتساب للأعمال. أعد المحاولة.",
      "err.WA_CLOUD_CAMPAIGN_TOO_LONG": "نص الحملة أطول مما يسمح به قالب واتساب (1024 حرفًا مع التحية والتذييل).",
      "err.WA_CLOUD_CAMPAIGN_MEDIA": "الحملات عبر واجهة واتساب للأعمال نصية فقط حاليًا.",
      "err.WA_CLOUD_CAMPAIGN_ADHOC": "الحملات عبر واجهة واتساب للأعمال تُرسل إلى العملاء المسجلين فقط.",
      "err.WA_CLOUD_CAMPAIGN_CONSENT_REQUIRED": "يجب أن يكون كل عميل في الحملة قد وافق على تلقي العروض عبر واتساب ولم يطلب الإيقاف.",
      "err.WA_CLOUD_TEMPLATE_NOT_APPROVED": "لم توافق Meta على رسالة هذه الحملة بعد.",
      "err.WA_CLOUD_RATE_LIMITED": "أوقفت Meta هذه الحملة مؤقتًا. يمكنك إرسالها مجددًا بعد بضع دقائق.",
      "err.LOSS_REASON_REQUIRED": "ستصدر هذه الفاتورة بخسارة. اكتب سبباً للمتابعة.",
      "err.INVOICE_ZERO_TOTAL": "هذه الفاتورة بلا مبلغ. أضف صنفًا واحدًا على الأقل بسعر قبل إصدارها.",
      "err.TASK_ASSIGNMENT_INVALID": "إسناد المهمة غير صالح.",
      "err.TASK_STATE_INVALID": "المهمة ليست في الحالة الصحيحة لهذا الإجراء.",
      "err.TENANT_CONTEXT_REQUIRED": "سياق المستأجر مطلوب.",
      "err.TERMS_NOT_PUBLISHED": "الشروط المنشورة مطلوبة.",
      "err.TOO_MANY_ITEMS": "العدد المُرسل كبير جداً.",
      "err.TOO_MANY_ATTACHMENTS": "يمكن إرفاق 10 صور كحد أقصى في المرة الواحدة.",
      "err.TOO_MANY_RECIPIENTS": "هذا الجمهور أكبر من أن يُرسل دفعة واحدة عبر واتساب. ضيّقه ثم أعد المحاولة.",
      "err.TOO_SOON": "هذا الوقت قريب جدًا ولا يمكن حجزه. يُرجى اختيار وقت لاحق.",
      "err.UNKNOWN_QUOTATION_ITEM": "بند عرض السعر غير معروف.",
      "err.URL_INVALID": "الرابط غير صالح.",
      "err.VALIDATION_FAILED": "فشل التحقق. راجع الحقول المُظلّلة.",
      "err.VAT_NUMBER_INVALID": "رقم ضريبة القيمة المضافة يجب أن يكون 15 رقماً ويبدأ وينتهي بالرقم 3.",
      "err.VAT_NOT_REGISTERED": "هذه المنشأة غير مسجلة في ضريبة القيمة المضافة، لذلك لا يمكن تسجيل ضريبة مدخلات قابلة للخصم على المصروف. أدخل المبلغ شاملاً الضريبة بدلاً من ذلك.",
      "err.VAT_RATE_INVALID": "المنشأة غير المعفاة من الضريبة يجب أن يكون معدلها أكبر من 0. ضع علامة \"معفاة من الضريبة\" بدلاً من جعل المعدل 0.",
      "err.VEHICLE_INVALID": "المركبة غير صالحة.",
      "err.WAIVER_REASON_REQUIRED": "سبب الإعفاء مطلوب.",
      "err.WPS_NO_PAYABLE_LINES": "لا توجد بنود مستحقة للدفع في حماية الأجور (WPS).",
      "err.WPS_SETTINGS_MISSING": "إعدادات منشأة حماية الأجور (WPS) غير مهيأة.",
      "err.YEAR_INVALID": "السنة يجب أن تكون 4 أرقام.",
      "err.INVALID_REJECTION_REASONS": "قائمة أسباب الرفض غير صالحة. راجع البنود ثم أعد المحاولة.",
      "err.CASH_MOVEMENT_DIRECTION_IN": "\"{{reason}}\" دائماً قبض (نقد داخل).",
      "err.CASH_MOVEMENT_DIRECTION_OUT": "\"{{reason}}\" دائماً صرف (نقد خارج).",
      "err.CASH_MOVEMENT_REASON_REQUIRED": "هذا الحساب يسجّل مالاً يخص شريكاً، فهو يحتاج إلى تحديد السبب والشريك الذي يخصه.",
      "err.CASH_REASON_MISSING": "لا يوجد سبب نقدي مُفعّل باسم «{{reason_name_ar}}» ({{reason_name}}) في هذه الشركة، لذلك لا يمكن تسجيل مبلغ بدون سبب. اختر سببًا في النموذج لتسجيله الآن، واطلب من المسؤول إعادة تفعيل هذا السبب في أسباب النقدية.",
      "err.CASH_MOVEMENT_REASON_ACCOUNT_MISMATCH": "هذا الحساب وهذا السبب لا يصفان النوع نفسه من المال. مال الشركاء يحتاج سبباً خاصاً بالشركاء.",
      "err.PLATE_FORMAT_INVALID": "صيغة رقم اللوحة غير صحيحة. أدخل حتى 4 أرقام وحتى 3 أحرف مسموحة.",
      "err.FEATURE_DISABLED": "خاصية \"{{feature}}\" غير مفعّلة في هذا المركز.",
      "err.INTAKE_CONFIRMATION_REQUIRED": "يجب أن يؤكد العميل الاستلام قبل متابعة أمر العمل. أرسل رابط التأكيد أو استخدم التجاوز.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS": "أمر العمل هذا منتهٍ، ولا يمكن إضافة قطع أو مهام جديدة.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS_closed": "أمر العمل هذا منتهٍ، ولا يمكن إضافة قطع أو مهام جديدة. أعد فتحه لإضافة أعمال أخرى.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS_delivered": "أمر العمل هذا منتهٍ، ولا يمكن إضافة قطع أو مهام جديدة. أعد فتحه لإضافة أعمال أخرى.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS_delivered_pending_payment": "أمر العمل هذا منتهٍ، ولا يمكن إضافة قطع أو مهام جديدة. تم تسليم السيارة على الحساب — سدّد المبلغ المتبقي أولاً.",
      "err.JOB_CARD_ENDED_NO_ADDITIONS_cancelled": "أمر العمل هذا ملغي، ولا يمكن إضافة قطع أو مهام جديدة. أنشئ أمر عمل جديداً بدلاً منه.",
      "err.INTAKE_NOT_APPLICABLE": "لا يتضمن هذا النوع من أوامر العمل خطوة استلام، فالفحص الشامل والخدمة السريعة ليس فيهما ما يُؤكَّد.",
      "err.INVALID_INPUT": "تم رفض الملف: {{reason}}",
      "err.JOB_CARD_NOT_APPROVABLE": "أمر العمل هذا مغلق. الرجاء التواصل مع مركز الخدمة.",
      "err.MECHANICS_NOT_ALLOWED": "لا يُسمح للفنيين باستخدام \"{{feature}}\" في هذا المركز.",
      "err.CHECKOUT_IN_PROGRESS": "جارٍ فتح رابط دفع لهذا السند. انتظر لحظة ثم اضغط على الدفع مرة أخرى.",
      "err.OPEN_CHECKOUT_SESSION": "لهذا السند رابط دفع إلكتروني مفتوح. تأكد أن العميل لم يدفع إلكترونياً قبل تسجيل دفعة يدوية.",
      "err.TENANT_PAYMENT_MANAGEMENT_DISABLED": "إعدادات الدفع لهذا الحساب تديرها موتورمايند.",
      "err.PAYEE_REQUIRED": "\"{{reason}}\" يجب أن تُنسب إلى شريك. اختر المستفيد.",
      "err.JOB_CARD_UNPAID_BALANCE": "ما زالت توجد فواتير غير مسددة على أمر العمل. المتبقي: {{amount}} ريال.",
      "err.JOB_CARD_UNQUOTED_WORK": "على أمر العمل أعمال جديدة لم يُرسل بها عرض سعر إلى العميل. أرسل عرض السعر أو احذف هذه الأعمال قبل تسليم السيارة.",
      "err.SKU_ALREADY_EXISTS": "رمز SKU \"{{sku}}\" مستخدم بالفعل للقطعة \"{{part}}\". أدخل رمزاً مختلفاً.",
      "err.TASK_PARTS_NOT_INSTALLED": "لا يمكن إنهاء المهمة — {{count}} قطعة لم تُركَّب بعد.",
      "err.TASK_SERVICES_INCOMPLETE": "لا يمكن إنهاء المهمة — {{count}} خدمة لم يتم إنجازها بعد.",
      "err.CUSTOMER_EMAIL_MISSING": "لا يوجد بريد إلكتروني لهذا العميل. أضفه في بيانات العميل أولاً.",
      "err.CUSTOMER_PHONE_MISSING": "لا يوجد رقم جوال لهذا العميل. أضفه في بيانات العميل أولاً.",
      "err.PARTNER_INVALID": "هذا الشخص ليس شريكاً نشطاً. أضفه في شاشة الشركاء أولاً، ثم سجّل المبلغ عليه.",
      "err.TEXT_OR_FILE_REQUIRED": "اكتب رسالة أو أرفق ملفاً قبل الإرسال.",
      "err.unexpected": "حدث خطأ غير متوقع. حاول مرة أخرى، وإن تكرر أبلغ مسؤول المركز.",
      // Fallback messages for toast.error(errorText(t, err, 'key')) replacements
      "intake.sendOtpFailed": "فشل إرسال رمز التحقق",
      "intake.invalidOtp": "رمز التحقق غير صحيح",
      "intake.describeIssue": "يرجى وصف المشكلة",
      "intake.submitFailed": "فشل الإرسال",
      "intake.confirmationRequired": "مطلوب تأكيد العميل قبل متابعة هذا العمل.",
      "public.paymentFailed": "فشلت عملية الدفع. يرجى المحاولة مرة أخرى.",
      "public.paymentCancelled": "تم إلغاء عملية الدفع.",
      "public.invoiceLoadFailed": "تعذّر تحميل الفاتورة",
      "public.selectPaymentMethod": "يرجى اختيار طريقة الدفع",
      "public.paymentCreateFailed": "تعذّر إنشاء عملية الدفع",
      "public.tamaraCheckoutFailed": "تعذّر إنشاء عملية الدفع عبر تمارا",
      "public.tabbyCheckoutFailed": "تعذّر إنشاء عملية الدفع عبر تابي",
      "public.payInGarageFailed": "تعذّر تأكيد الدفع في المركز",
      "public.paymentUnavailable": "الدفع غير متاح. يرجى التواصل مع الدعم.",
      "public.createPaymentFailed": "فشل إنشاء الدفع",
      "public.approveFailed": "فشل اعتماد عرض السعر",
      "payouts.nothingGenerated": "لم يتم إنشاء أي دفعة",
      "payouts.generateFailed": "فشل الإنشاء",
      "tnc.loadFailed": "فشل تحميل الشروط والأحكام",
      "tnc.generateFailed": "فشل الإنشاء",
      "tnc.enhanceFailed": "فشل التحسين",
      "tnc.cancelGeneration": "إلغاء",
      "tnc.generationCancelled": "تم إلغاء الإنشاء",
      "jc.inspectionPrice": "سعر الفحص (ريال)",
      "jc.inspectionPricePlaceholder": "مثال: 250",
      "jc.inspectionPriceHint": "يمكنك تعديله في أي وقت قبل سداد الفاتورة.",
      "tnc.generatingNote": "الذكاء الاصطناعي يكتب الشروط والأحكام — قد يستغرق ذلك بضع دقائق.",
      "reportPdf.failed": "تعذّر إنشاء ملف PDF. يرجى المحاولة مرة أخرى.",
      "vp.insightDisabled": "التشخيص الذكي غير مفعّل لهذا المركز",
      "vp.insightNoHistory": "لا يوجد سجل خدمة لإنشاء ملخص بعد",
      "vp.insightFailed": "فشل إنشاء الرؤية",
      "rpt.drawingsCutoverSaveError": "تعذّر حفظ تاريخ التحويل",
      "ins.buildingNumberInvalid": "رقم المبنى يجب أن يكون 4 أرقام بالضبط",
      "ins.postalCodeInvalid": "الرمز البريدي يجب أن يكون 5 أرقام بالضبط",
      "insuranceClaim.approvalRefRequired": "أدخل مرجع موافقة شركة التأمين",
      // General ledger — Arabic (merged from the financials/*.i18n.json fragments)
      "err.GL_ACCOUNT_CODE_DUPLICATE": "يوجد حساب بهذا الرمز بالفعل.",
      "gl.chart.errDuplicateInactive": "رمز الحساب {{code}} موجود بالفعل لكنه معطّل حاليًا. أعد تفعيله أو اختر رمزًا آخر.",
      "err.GL_ACCOUNT_CODE_INVALID": "رمز الحساب يجب أن يكون أربعة أرقام بالضبط.",
      "err.GL_RANGE_INVALID": "التواريخ يجب أن تكون بصيغة YYYY-MM-DD، و«من» يجب ألا يكون بعد «إلى».",
      "err.GL_RANGE_SPANS_FISCAL_YEAR": "لا يمكن لهذا التقرير أن يمتد عبر نهاية السنة المالية. اختر فترة داخل سنة واحدة — هذا الشهر أو هذه السنة مثلًا.",
      "err.RETAINED_EARNINGS_MUST_BE_EQUITY": "يجب أن تشير الأرباح المبقاة إلى حساب ضمن حقوق الملكية. أرباح السنوات السابقة تُرحَّل إليه، وتصنيفه كإيراد أو مصروف سيُظهر كل السنوات الماضية ضمن هذه السنة.",
      "err.GL_GARAGE_INVALID": "الفرع يجب أن يكون معرّف فرع أو «الكل».",
      "err.GL_GARAGE_UNKNOWN": "الفرع غير موجود.",
      "err.GL_PERIOD_INVALID": "هذه ليست فترة محاسبية. استخدم صيغة YYYYMM، مثال 202608.",
      "err.GL_VERIFY_NOT_CLOSED": "لا يمكن التحقق إلا من فترة مقفلة مقارنةً بلقطتها المحفوظة.",
      "err.OB_FRACTIONAL_HALALA": "المبالغ بالهللات الصحيحة — لا يمكن تخزين كسر.",
      "err.OB_AMOUNT_NEGATIVE": "لا يمكن أن يكون الرصيد الافتتاحي بالسالب.",
      "err.OB_AMOUNT_REQUIRED": "أدخل مبلغاً لهذا السطر.",
      "err.OB_LINE_TYPE_INVALID": "نوع هذا السطر ليس من الأنواع التي تتيحها هذه الشاشة.",
      "err.OB_COUNTERPART_REQUIRED": "اختر الحساب النقدي أو العميل أو المورّد الذي يخص هذا السطر.",
      "err.OB_SCHEMA_NOT_MIGRATED": "رموز حسابات الأرصدة الافتتاحية غير متاحة على قاعدة البيانات بعد. تواصل مع الدعم.",
      "err.OB_PREFILL_ALREADY_BOOKED": "هذا الإجمالي محتسب بالفعل من فواتيرك المُرحّلة — لا يوجد شيء لإدخاله هنا.",
      "err.OB_CASH_ACCOUNT_HAS_MOVEMENTS": "الرصيد الافتتاحي لهذا الحساب هو {{amount}} ريال ولا يمكن تغييره بعد وجود حركات نقدية. أدخل المبلغ نفسه، أو استبعد هذا الحساب من الأرصدة الافتتاحية.",
      "err.field.JV_ACCOUNT_INVALID": "لا يمكن الترحيل على هذا الحساب.",
      "err.field.CASH_MOVEMENT_REASON_REQUIRED": "يحتاج سبباً خاصاً بالشريك.",
      "err.field.CASH_MOVEMENT_REASON_ACCOUNT_MISMATCH": "لا يطابق هذا السبب.",
      "err.field.JV_ACCOUNT_LOCKED": "حساب مقفل — لا يقبل القيد أبداً.",
      "err.FA_ACQUIRED_BEFORE_BOOKS": "لا يمكن شراء الأصل قبل تاريخ بدء الدفاتر المحاسبية.",
      "err.FA_ACQUIRED_IN_CLOSED_PERIOD": "شهر الشراء مغلق محاسبيًا.",
      "err.FA_FOLD_TARGET_INVALID": "فترة الدمج المستهدفة ليست شهرًا صالحًا للترحيل.",
      "err.FA_FOLD_TARGET_TAKEN": "فترة الدمج المستهدفة تحتوي بالفعل على إهلاك مسجل لهذا الأصل.",
      "err.FA_OPENING_REQUIRES_LEGACY_IMPORT": "الأصول ذات مجمع الإهلاك الافتتاحي يجب استيرادها عبر استيراد الأصول السابقة.",
      "err.FA_RETRY": "حدث تغيير أثناء الحفظ — يرجى المحاولة مرة أخرى.",
      "err.FA_ASSET_CANCELLED": "أُلغي هذا الأصل الثابت عند عكس فاتورة شرائه ولا يمكن تغييره.",
      "err.FA_LEGACY_ALREADY_BOOKED": "تكلفة هذا الأصل السابق مسجلة بالفعل في قيده الافتتاحي ولا يمكن رسملتها مرة أخرى.",
      "err.FA_REVERSAL_ASSET_DISPOSED": "لا يمكن إلغاء أصل ثابت مستبعد بعكس فاتورة شرائه.",
      "err.FA_REVERSAL_BEFORE_LAST_CHARGE": "لهذا الأصل قيد إهلاك في هذا الشهر أو بعده. أرّخ العكس في شهر يلي آخر قيد إهلاك له.",
      "err.PERIOD_NOT_ENDED": "هذه الفترة لم تنتهِ بعد. يمكن تشغيل الإهلاك للفترات المنتهية فقط.",
      "err.FA_LIFE_INVALID": "العمر الافتراضي يجب أن يكون عددًا صحيحًا من الأشهر، شهرًا واحدًا على الأقل.",
      "err.FA_SALVAGE_EXCEEDS_COST": "قيمة الإنقاذ لا يمكن أن تتجاوز التكلفة.",
      "err.FA_OPENING_ACCUMULATED_INVALID": "يجب أن يكون مجمع الإهلاك الافتتاحي بين صفر والتكلفة ناقص قيمة الإنقاذ.",
      "err.FA_GARAGE_FORBIDDEN": "ليس لديك صلاحية الوصول إلى هذا الفرع.",
      "err.FA_DISPOSED_ON_INVALID": "أدخل تاريخ استبعاد صالحاً.",
      "err.FA_DISPOSED_ON_FUTURE": "لا يمكن أن يكون تاريخ الاستبعاد في المستقبل.",
      "err.FA_DISPOSED_BEFORE_RECOGNITION": "تاريخ الاستبعاد يسبق تاريخ الاعتراف بالأصل (تاريخ اقتنائه أو قيد الرصيد الافتتاحي).",
      "err.FA_DISPOSAL_PROCEEDS_INVALID": "يجب أن تكون متحصلات الاستبعاد مبلغاً صحيحاً غير سالب.",
      "err.FA_SALE_CREDIT_BLOCKED": "إشعارات الدائنة لفواتير بيع الأصول الثابتة غير مدعومة حالياً.",
      "err.FA_DISPOSAL_GIFT_UNSUPPORTED": "استبعاد الأصول الثابتة كهدية أو تبرع غير مدعوم.",
      "err.FA_DISPOSAL_REASON_INVALID": "يجب أن يكون سبب الاستبعاد بيعاً أو إتلافاً.",
      "err.FA_SALE_PROCEEDS_REQUIRED": "يتطلب بيع الأصل الثابت متحصلات موجبة قبل ضريبة القيمة المضافة.",
      "err.FA_SALE_BUYER_REQUIRED": "اختر مشتري الأصل.",
      "err.FA_SALE_DATE_TODAY_ONLY": "البيع المُفوتر يُؤرَّخ بتاريخ اليوم.",
      "err.FA_SALE_BUYER_INCOMPLETE": "أكمل اسم المشتري وعنوانه البريدي المهيكل قبل إصدار فاتورة بيع الأصل.",
      "err.FA_SALE_GARAGE_REQUIRED": "اختر فرعاً مسموحاً لك لبيع هذا الأصل المسجل على مستوى الشركة.",
      "err.FA_VAT_RATE_UNSUPPORTED": "بيع الأصول الثابتة يدعم فقط مساري ضريبة القيمة المضافة المهيأين بنسبة 0% أو 15%.",
      "err.FA_SALE_REISSUE_NOT_ELIGIBLE": "فاتورة بيع هذا الأصل غير مؤهلة لإعادة الإصدار.",
      "err.FA_SALE_NOT_PAYABLE": "يمكن تحصيل الدفعات فقط لفواتير بيع الأصول الثابتة المعتمدة.",
      "err.FA_REMAINING_MONTHS_REQUIRED": "يجب أن تكون فترة الإهلاك المتبقية شهرًا واحدًا على الأقل، أو 0 فقط عندما تتساوى القيمة الدفترية مع قيمة الإنقاذ.",
      "err.FA_BOOK_VALUE_INVALID": "يجب أن تكون القيمة الدفترية بين صفر والتكلفة.",
      "err.FA_SALVAGE_EXCEEDS_BOOK_VALUE": "قيمة الإنقاذ لا يمكن أن تتجاوز القيمة الدفترية.",
      "err.FA_ACQUIRED_AFTER_AS_OF": "لا يمكن أن يكون تاريخ اقتناء الأصل بعد تاريخ الوضع الدفتري.",
      "err.FA_AS_OF_BEFORE_BOOKS": "يجب أن يكون شهر الوضع الدفتري قد انتهى وأن يكون في تاريخ الأرصدة الافتتاحية أو بعده.",
      "err.FA_AS_OF_PERIOD_INVALID": "يجب أن يكون شهر الوضع الدفتري فترة صالحة بصيغة YYYYMM.",
      "err.PAYROLL_ACCRUAL_ACCOUNT_UNMAPPED": "لا يوجد حساب مربوط لـ {{account}}. اربطه في إعدادات الدليل ثم أعد الاعتماد.",
      "err.PAYROLL_ACCRUAL_ACCOUNT_MISSING": "لا يوجد في دليل حساباتك سطر لـ {{account}}. أضِفه ثم أعد الاعتماد.",
      "err.PAYROLL_ACCRUAL_OUT_OF_ORDER": "الفترة {{laterPeriod}} مستحقة بالفعل. اعتمد {{period}} قبلها، أو اعكس {{laterPeriod}} أولاً.",
      "err.PAYROLL_ACCRUAL_LATER_PERIOD_LIVE": "الفترة {{laterPeriod}} مستحقة فوق {{period}}. اعكسها أولاً.",
      "err.INVENTORY_WRITEDOWN_HAS_LATER_ACTIVITY": "تم استلام هذا الصنف أو إعادة تقييمه بعد التخفيض، لذا استرجاع التكلفة القديمة سيلغي ذلك. عدّل التكلفة مباشرة بدلاً من ذلك.",
      "err.FA_DISPOSED_BEFORE_BOOKS": "تاريخ الاستبعاد يسبق تاريخ أرصدتك الافتتاحية.",
      "err.FA_DISPOSED_ON_AFTER_DEPRECIATION": "هذا الشهر مُهلَك بالفعل — أرّخ الاستبعاد في الشهر التالي.",
      "err.PAYROLL_ACCRUAL_UNBALANCED": "قيد استحقاق الرواتب غير متوازن. تواصل مع الدعم ولا تعِد الاعتماد.",
      "err.PAYROLL_PERIOD_INVALID": "يجب أن تكون فترة الرواتب بصيغة YYYYMM.",
      "err.PAYROLL_ACCRUAL_SETTLEMENT_MISSING": "لا يوجد حساب تسوية لهذا المسير، لذا لا يمكن صرفه بعد.",
      "err.GOSI_ACCRUAL_SETTLEMENT_MISSING": "لا يوجد حساب تسوية للتأمينات الاجتماعية لهذا المسير، لذا لا يمكن تسجيل سداد التأمينات بعد.",
      "err.EOSB_ACCRUAL_SETTLEMENT_MISSING": "لهذا الموظف مخصص نهاية خدمة مستحق دون حساب مخصص مثبّت على مسيرات الرواتب، لذا لا يمكن تسجيل التسوية بعد.",
      "err.FA_COST_INVALID": "التكلفة يجب أن تكون أكثر من صفر، وبهلالات صحيحة.",
      "err.FA_CATEGORY_INVALID": "اختر فئة لهذا الأصل.",
      "err.FA_ALREADY_DEPRECIATED": "سبق أن رُحّل إهلاك لهذا الأصل، لذا لا يمكن تغيير تكلفته أو تاريخ اقتنائه أو عمره الافتراضي بعد الآن.",
      "err.FA_ALREADY_DISPOSED": "هذا الأصل مستبعد بالفعل.",
      "err.PURCHASE_INVOICE_REVERSED_IMMUTABLE": "فاتورة الشراء هذه معكوسة ولم يعد بالإمكان تعديلها. قيودها المحاسبية تُبنى من هذه السطور، فأي تغيير هنا ينقل الشراء الأصلي إلى فترة غير فترة عكسه.",
      "err.IDEMPOTENCY_KEY_REUSED": "مرجع إعادة المحاولة هذا استُخدم من قبل لطلب آخر. ابدأ من جديد.",
      "err.FA_IDEMPOTENCY_KEY_INVALID": "مرجع إعادة المحاولة يجب أن يكون نصًا لا يتجاوز 200 حرف.",
      "err.FA_LINKED_TO_PURCHASE": "هذا الأصل مرسمَل من فاتورة شراء مرحّلة، لذا لا يمكن تغيير حقوله المحاسبية. ألغِ ربط بند الشراء أو اعكس الفاتورة أولاً.",
      "err.FA_IMPORT_ROW_FAILED": "تعذّر استيراد هذا السطر.",
      "err.ASSET_LINE_REQUIRES_POSTING": "الفاتورة التي تشتري أصلاً ثابتاً يجب ترحيلها عند إنشائها، لا حفظها كمسودة.",
      "err.FA_ACQUIRED_ON_INVALID": "تاريخ الاقتناء يجب أن يكون تاريخاً صحيحاً.",
      "err.FA_ACQUIRED_ON_AFTER_INVOICE": "لا يمكن أن يكون تاريخ اقتناء الأصل بعد تاريخ الفاتورة التي اشترته.",
      "err.FA_ACQUIRED_ON_REQUIRED": "أدخل تاريخ الشراء الأصلي — فجدول الإهلاك يُحتسب منه.",
      "err.FA_OPENING_ACCUMULATED_REQUIRED": "حدّد مقدار الإهلاك المحمّل حتى الآن. أدخل صفراً إن لم يكن هناك إهلاك.",
      "err.FA_GARAGE_UNKNOWN": "الفرع غير موجود.",
      "err.FA_IMPORT_EMPTY": "لا يوجد ما يُستورد.",
      "err.FA_IMPORT_INVALID": "تعذّر استيراد بعض السطور، فلم يُحفظ أي شيء. صحّح السطور المذكورة أدناه ثم ارفع الملف مرة أخرى.",
      "err.FA_LINK_ALREADY_LINKED": "هذا البند مرتبط بالفعل بأصل ثابت آخر.",
      "err.FA_LINK_ASSET_LINKED": "هذا الأصل مرتبط بالفعل ببند شراء. ألغِ ذلك الارتباط أولاً.",
      "err.FA_LINK_STOCK_LINE": "لا يمكن رسملة بند مخزون — فحركته المخزنية سُجّلت بالفعل.",
      "err.FA_LINK_INVOICE_DRAFT": "فاتورة الشراء هذه ما زالت مسودة، فليست مستنداً في اليومية بعد. رحّلها ثم اربط.",
      "err.FA_LINK_INVOICE_REVERSED": "فاتورة الشراء هذه معكوسة، وإشعارها الدائن لن يعكس جانب الأصل.",
      "err.FA_LINK_PERIOD_CLOSED": "الشهر الذي تنتمي إليه هذه الفاتورة مقفل. اعكس الفاتورة وأعد إدخالها في فترة مفتوحة.",
      "err.FA_LINK_EXCEEDS_INVOICE": "رسملة هذا البند تجعل بنود الأصول تتجاوز تلك الفاتورة بمقدار {{excess}}، وصافي الفاتورة قبل ضريبة القيمة المضافة {{invoiceNet}}.",
      "err.FA_LINK_COST_MISMATCH": "قيمة هذا البند {{lineCost}} بينما الأصل مسجَّل بقيمة {{assetCost}}. صحّح أحدهما قبل الربط.",
      "err.FA_LINK_DEPRECIATED": "سبق أن رُحّل إهلاك لهذا الأصل، فلم يعد بالإمكان إلغاء ارتباطه بفاتورة الشراء.",
      "err.field.JV_ACCOUNT_RESTRICTED": "حساب رقابي — يحتاج إلى التجاوز.",
      "err.field.JV_FRACTIONAL_HALALA": "منزلتان عشريتان كحد أقصى.",
      "err.field.GL_ACCOUNT_CODE_UNKNOWN": "غير موجود في دليل الحسابات، أو غير نشط.",
      "err.field.GL_ACCOUNT_CODE_DUPLICATE": "مستخدم مسبقاً",
      "err.field.GL_ACCOUNT_CODE_INVALID": "أربعة أرقام",
      "err.field.GL_ACCOUNT_PARENT_UNKNOWN": "حساب غير معروف",
      "err.field.PERIOD_CLOSED": "الفترة مقفلة",
      "err.field.OB_ACCOUNT_CODE_REQUIRED": "اختر حساباً",
      "err.GL_ACCOUNT_CODE_UNKNOWN": "هذا الحساب غير موجود في دليل حساباتك، أو أنه غير نشط.",
      "err.GL_ACCOUNT_DEACTIVATE_MAPPED": "هذا الحساب ما زال مربوطاً بنوع مستند، وإيقافه سيحوّل تلك المستندات إلى حساب الوسيط. غيّر الربط أولاً.",
      "err.GL_ACCOUNT_IMMUTABLE_POSTED": "هذا الحساب عليه قيود مرحّلة، لذلك لا يمكن تغيير رمزه أو نوعه بعد الآن. غيّر اسمه، أو أوقفه وأضف حساباً جديداً.",
      "err.GL_ACCOUNT_PARENT_UNKNOWN": "الحساب الرئيسي الذي اخترته غير موجود.",
      "err.GL_ACCOUNT_TYPE_DIGIT_MISMATCH": "أول رقم في الرمز يجب أن يطابق نوع الحساب: ١ أصول، ٢ خصوم، ٣ حقوق ملكية، ٤ إيرادات، ٥ مصروفات.",
      "err.GL_CLOSE_BLOCKED_DOCUMENT": "يوجد مستند في هذه الفترة غير جاهز للإقفال. عالج المستندات المذكورة ثم أقفل مرة أخرى.",
      "err.GL_CLOSE_EARLIER_PERIOD_OPEN": "توجد فترة أقدم ما زالت مفتوحة. الفترات تُقفل بالترتيب، الأقدم أولاً.",
      "err.GL_CLOSE_WARNINGS_PRESENT": "دفتر اليومية لهذه الفترة عليه تنبيهات. راجعها ثم أقفل مع التجاوز، أو عالجها أولاً.",
      "err.GL_CLOSE_YEAR_END_SUSPENSE_NONZERO": "لا يمكن إقفال السنة بينما لا يزال في الحساب المعلّق مبلغ. أعد تصنيفه بقيد يومية بتاريخ داخل السنة ثم أقفل مرة أخرى.",
      "err.GRANT_EXPIRED": "انتهت مهلة اختيار الحساب لهذا الدخول. اطلب رمزًا جديدًا للمتابعة.",
      "err.DEPRECIATION_UNPOSTED": "رحّل الإهلاك حتى YYYY-MM قبل إقفال الشهر.",
      "err.ACCOUNT_CODE_UNKNOWN": "هذا الربط يشير إلى رمز حساب غير موجود في دليل الحسابات.",
      "err.ALREADY_FINISHED": "هذه المسودة مكتملة بالفعل ولا يمكن تعديلها.",
      "err.ALREADY_REVERSED": "تم عكس هذا بالفعل.",
      "err.EXPENSE_POSTED_FIELDS_LOCKED": "هذا المصروف مُرحَّل، لذلك لا يمكن تغيير المبلغ أو الضريبة أو المورّد أو التاريخ أو طريقة الدفع. ألغِه بإشعار دائن ثم سجّل مصروفاً جديداً.",
      "err.EXPENSE_POSTED_USE_REVERSE": "لا يمكن حذف مصروف مُرحَّل. ألغِه بإشعار دائن بدلاً من ذلك.",
      "err.REVERSAL_BASIS_INVALID": "اختر سبب إلغاء هذا المستند.",
      "err.REVERSAL_REASON_REQUIRED": "اكتب سبب إدخال هذا المستند بالخطأ.",
      "err.CREDIT_NOTE_PERIOD_CLOSED": "شهر الإشعار الدائن مغلق. رحّله بتاريخ اليوم بدلاً من ذلك، أو أعد فتح الشهر.",
      "err.VAT_IN_COST_INVALID": "اختر ما إذا كانت ضريبة القيمة المضافة قد استُردت أو أُضيفت إلى تكلفة القطع.",
      "err.VAT_IN_COST_ALREADY_RECORDED": "إجابة ضريبة هذه الفاتورة مسجّلة مسبقاً، أو أن الفاتورة غير مرحّلة.",
      "err.REVERSAL_SETTLEMENT_REQUIRED": "هذا المستند مدفوع. اختر ما إذا أُعيد المبلغ أو يحتفظ به المورّد رصيداً لصالحك.",
      "err.REVERSAL_SETTLEMENT_INVALID": "خيار الاسترداد أو حساب الاسترداد غير صالح لهذا المستند.",
      "err.REVERSAL_SUPPLIER_FIELDS_FORBIDDEN": "المستند المُدخل بالخطأ ليس له إشعار دائن من المورّد. احذف بيانات الإشعار.",
      "err.SUPPLIER_CN_NUMBER_INVALID": "أدخل رقم إشعار المورّد الدائن (حتى 64 حرفاً).",
      "err.SUPPLIER_CN_DATE_INVALID": "أدخل التاريخ المكتوب على إشعار المورّد الدائن.",
      "err.SUPPLIER_CN_DATE_FUTURE": "لا يمكن أن يكون تاريخ إشعار المورّد الدائن في المستقبل.",
      "err.SUPPLIER_CN_DATE_BEFORE_DOCUMENT": "لا يمكن أن يسبق تاريخ الإشعار الدائن تاريخ الفاتورة.",
      "err.SUPPLIER_CN_ATTACHMENT_REQUIRED": "أرفق إشعار المورّد الدائن.",
      "err.SUPPLIER_CN_NUMBER_DUPLICATE": "رقم الإشعار الدائن هذا مسجّل مسبقاً لهذا المورّد.",
      "err.ALREADY_SETTLED": "تمت تسوية مكافأة نهاية الخدمة لهذا الموظف بالفعل.",
      "err.BRANCH_NET_NOT_POSITIVE": "أحد الفروع في هذا المسير صافي رواتبه صفر أو أقل.",
      "err.BEYOND_HORIZON": "هذا التاريخ أبعد من المدة المسموح بالحجز خلالها.",
      "err.EMPLOYEE_NET_NOT_POSITIVE": "أحد الموظفين في هذا المسير صافي راتبه صفر أو أقل.",
      "err.EOSB_CATEGORY_MISSING": "لا يوجد بند مصروف لمكافأة نهاية الخدمة. أضِف بنداً في بنود المصروفات ثم أعد المحاولة.",
      "err.GOSI_CATEGORY_MISSING": "لا يوجد بند مصروف للتأمينات الاجتماعية. أضِف بنداً في بنود المصروفات ثم أعد المحاولة.",
      "err.GOSI_EXISTS": "توجد فاتورة تأمينات مسجّلة لهذه الفترة بالفعل.",
      "err.GOSI_RUN_NOT_FOUND": "لا يوجد مسير رواتب معتمد أو مدفوع لهذه الفترة.",
      "err.GOSI_RUN_NOT_RECONCILABLE": "لا يمكن مطابقة فاتورة التأمينات إلا مع مسير رواتب معتمد أو مدفوع.",
      "err.JOURNAL_BLOCKED": "لا يمكن تصدير اليومية وبها مشكلات. عالِج المشكلات ثم أعد المحاولة.",
      "err.LOAN_DISBURSED_AT_INVALID": "أدخل تاريخ صرف صحيحاً وغير مستقبلي.",
      "err.NOT_APPROVED": "يجب اعتماد مسير الرواتب أولاً.",
      "err.NOT_FOUND": "هذا السجل لم يعد موجوداً.",
      "err.INSPECTION_LINK_EXPIRED": "انتهت صلاحية رابط تقرير الفحص. اطلب من المركز إرسال رابط جديد.",
      "err.INSPECTION_NOT_ISSUED": "لم يُصدر هذا التقرير بعد، فلا يوجد ما يُطبع.",
      "err.INVALID_DTC_CODE": "هذا ليس رمز عطل. الرموز تكون بصيغة P0301 أو P0A80.",
      "err.INVALID_DTC_LIST": "يجب أن تكون رموز الأعطال قائمة.",
      "err.INVALID_WARNING_LIGHTS": "يجب أن تكون لمبات التحذير قائمة مفاتيح.",
      "err.INSPECTION_REPORT_MISSING": "تعذّرت قراءة ملف التقرير. أعد إصدار التقرير لإنشائه من جديد.",
      "err.INSPECTION_REPORT_UNPAID": "يتوفر تقرير الفحص بعد سداد المبلغ المستحق على هذه البطاقة.",
      "err.JOB_CARD_NOT_FOUND": "بطاقة العمل لم تعد موجودة، أو أنها تتبع فرعاً آخر.",
      "err.ASSIGNMENT_STALE": "تمت إعادة تعيين البطاقة منذ آخر مزامنة. حدّث قائمة الاختيار واختر من جديد.",
      "err.INVALID_EXPECTED_ASSIGNEE": "طلب التعيين غير صالح. حدِّث البيانات وحاول مرة أخرى.",
      "err.CUSTOMER_NOT_FOUND": "لم يتم العثور على العميل في ورشتك.",
      "err.NOT_PAID": "لا يمكن تنفيذ هذا إلا على مسير رواتب مدفوع.",
      "err.NOT_TERMINATED": "لا تتم تسوية نهاية الخدمة إلا بعد إنهاء خدمة الموظف.",
      "err.NO_CONTRACT": "لا يوجد عقد أو تاريخ تعيين لهذا الموظف، فلا يمكن حساب التسوية.",
      "err.PAID_TO_CREDIT": "لا يمكن تحويل مصروف مدفوع إلى آجل. اعكسه بدلاً من ذلك.",
      "err.RUN_EXISTS": "يوجد مسير رواتب لهذه الفترة بالفعل.",
      "err.STALE_RUN_REGENERATE": "لا يزال هذا المسير يخصم قروضاً لم تعد موجودة. أعد توليده قبل الاعتماد.",
      "err.ZAKAT_BASE_BLOCKED": "لا يزال في يومية هذه السنة مشكلات، لذا لا يمكن احتساب وعاء الزكاة. عالِج المشكلات ثم أعد المحاولة.",
      "err.ZAKAT_NO_COMPANY": "لا توجد شركة محددة لتقرير الزكاة هذا.",
      "err.ZAKAT_YEAR_INVALID": "أدخل سنة من أربعة أرقام بين 2000 و2100.",
      "err.EXPENSE_TIMING_INVALID": "راجع عدد أشهر التوزيع والتواريخ في هذا المصروف.",
      "err.EOSB_OPENING_NO_EMPLOYEES": "لا يوجد موظف على رأس العمل لديه عقد وتاريخ تعيين لتوزيع المخصص الافتتاحي عليه.",
      "err.GL_SETTING_INVALID": "هذا الإعداد المحاسبي خارج النطاق المسموح. أدخل عدداً صحيحاً ضمن النطاق.",
      "err.GARAGE_SCOPE_FORBIDDEN": "الفرع المطلوب لا يتبع منشأتك.",
      "err.TILL_NEGATIVE_BALANCE": "هذا الدفع سيجعل رصيد الصندوق أقل من صفر. سجّل المقبوضات أولاً أو ادفع من البنك.",
      "err.NOT_DRAFT": "لا يمكن التعديل هنا إلا على مسودة. هذه تم ترحيلها أو اعتمادها بالفعل.",
      "bank.featureDisabled": "تسوية الحساب البنكي غير مفعّلة لهذه الشركة",
      "bank.featureDisabledDesc": "اطلب من المسؤول تفعيل خاصية تسوية الحساب البنكي في باقتك.",
      "br.featureDisabled": "تسوية الحساب البنكي غير مفعّلة لهذه الشركة",
      "br.featureDisabledDesc": "اطلب من المسؤول تفعيل خاصية تسوية الحساب البنكي في باقتك.",
      "gl.ce.agrees": "متطابقة مع المركز المالي",
      "gl.ce.checkLine": "الرصيد الختامي هنا يجب أن يساوي الأصول ناقص الالتزامات.",
      "gl.ce.closingTotal": "الرصيد الختامي حسب هذه القائمة",
      "gl.ce.col.movement": "الحركة",
      "gl.ce.difference": "الفرق",
      "gl.ce.disagrees": "غير متطابقة مع المركز المالي",
      "gl.ce.disagreesDesc": "هذان الرقمان يُحتسبان بطريقتين مختلفتين ويجب أن يتطابقا. لم يتطابقا، إذًا أحدهما على الأقل خاطئ — لا تعتمد هذه القائمة حتى تُحل المشكلة.",
      "gl.ce.face": "قائمة التغيرات في حقوق الملكية",
      "gl.ce.nav": "التغيرات في حقوق الملكية",
      "gl.ce.perBalanceSheet": "حقوق الملكية حسب المركز المالي",
      "gl.ce.resultForPeriod": "نتيجة الفترة",
      "gl.ce.resultForPeriodNote": "نفس رقم نتيجة قائمة الدخل لهذه الفترة.",
      "gl.ce.subtitle": "حقوق الملكية أول المدة، وما حرّكها، وأين أُقفلت.",
      "gl.ce.title": "التغيرات في حقوق الملكية",
      "gl.cf.cashAccounts": "الحركة حسب الحساب النقدي",
      "gl.cf.cashAccountsNote": "حركة هذه الفترة لكل حساب نقدي وبنكي، وليست الرصيد الختامي.",
      "gl.cf.closing": "النقدية في نهاية الفترة",
      "gl.cf.methodNote": "الطريقة غير المباشرة — نتيجة الفترة معدّلة رجوعًا إلى النقدية.",
      "gl.cf.movement": "الحركة في النقدية خلال الفترة",
      "gl.cf.nav": "التدفقات النقدية",
      "gl.cf.netFor": "صافي هذا القسم",
      "gl.cf.opening": "النقدية في بداية الفترة",
      "gl.cf.reconciliation": "من نقدية أول المدة إلى نقدية آخر المدة",
      "gl.cf.subtitle": "من أين جاءت النقدية وإلى أين ذهبت خلال الفترة.",
      "gl.cf.title": "قائمة التدفقات النقدية",
      "gl.cf.unexplained": "حركة نقدية لم يطالب بها أي قسم",
      "gl.cf.unexplainedDesc": "نقدية حقيقية لا تفسّرها الأقسام أعلاه — غالبًا رمز حساب غير موجود في دليلك، أو بيع أصل تم تحصيله خارج هذه الفترة. تُعرض ولا تُدمج.",
      "gl.cf.unexplainedZero": "صفر — كل ريال من حركة نقدية الفترة فسّره قسم أعلاه.",
      "gl.close.yearEndSuspense.account": "الحساب المعلّق",
      "gl.close.yearEndSuspense.notOverridable": "لا يمكن تجاوز هذا. خيار «الإقفال رغم التنبيهات» الشهري لا ينطبق في نهاية السنة — أدخل قيد يومية بتاريخ داخل السنة يعيد تصنيف هذا الرصيد، ثم أقفل مرة أخرى.",
      "gl.export.action": "تصدير دفتر اليومية",
      "gl.export.blockedDesc": "لم يُنتَج أي ملف. صحّح المستندات أدناه ثم صدّر مرة أخرى — الملف الذي لا تتساوى فيه المدينة مع الدائنة سيرفضه برنامج محاسبك عند الاستيراد.",
      "gl.export.blockedDoc": "مستند",
      "gl.export.done": "تم تصدير دفتر اليومية",
      "gl.export.download": "تنزيل",
      "gl.export.format": "صيغة الملف",
      "gl.export.range": "الفترة: من {{from}} إلى {{to}}",
      "gl.export.subtitle": "كل مستندات الفترة المحددة، بصيغة يستطيع برنامج محاسبك استيرادها.",
      "gl.export.title": "تصدير دفتر اليومية العام",
      "gl.export.warnings": "تم التصدير، لكن {{count}} مستند في الملف مسجّل على الحساب المعلّق. اربطها بحسابات ثم صدّر مرة أخرى قبل تسليم الملف.",
      "gl.gj.docRefLabel": "بحث عن مستند",
      "gl.gj.docRefPlaceholder": "رقم فاتورة أو سند أو نص البيان…",
      "gl.is.face": "قائمة الدخل الشامل",
      "gl.is.nav": "قائمة الدخل",
      "gl.is.subtitle": "الإيرادات والتكاليف ونتيجة الفترة — قائمة الدخل الشامل.",
      "gl.is.title": "قائمة الدخل",
      "gl.is.totalComprehensive": "إجمالي الدخل الشامل",
      "gl.is.totalComprehensiveNote": "يساوي نتيجة الفترة: لا يوجد في هذا النشاط احتياطي إعادة تقييم ولا عمليات أجنبية ولا تحوّط، فلا يوجد دخل شامل آخر.",
      "gl.is.unexplainedDesc": "تم الترحيل إلى هذه الرموز وهي غير موجودة في دليل الحسابات، لذلك فهي ليست جزءًا من النتيجة أعلاه. أضِفها إلى الدليل ثم حدّث الصفحة.",
      "gl.is.unexplainedTitle": "حسابات غير موجودة في دليل حساباتك",
      "gl.link.viewInLedger": "عرض في دفتر اليومية",
      "gl.set.accessDenied": "لا تملك صلاحية الاطلاع على الإعدادات المحاسبية",
      "gl.set.accessDeniedDesc": "اطلب من المسؤول منحك صلاحية عرض القيود اليومية.",
      "gl.set.featureDisabled": "الأستاذ العام غير مفعّل لهذه الشركة",
      "gl.set.featureDisabledDesc": "اطلب من المسؤول تفعيل خاصية الأستاذ العام في باقتك.",
      "gl.set.fyDesc": "الشهر الذي تبدأ فيه سنتك المالية. كل الأرقام من بداية السنة تُحتسب منه.",
      "gl.set.fyMonth": "تبدأ السنة المالية في",
      "gl.set.fyTitle": "السنة المالية",
      "gl.set.fyWarning": "تغيير هذا يعيد احتساب كل رقم من بداية السنة في النظام، وينقل نهاية السنة التي يفحص عندها إقفال الفترة الحساب المعلّق.",
      "gl.set.invalid": "راجع القيم أعلاه قبل الحفظ.",
      "gl.set.loadFailed": "تعذّر تحميل الإعدادات المحاسبية",
      "gl.set.nav": "الإعدادات المحاسبية",
      "gl.set.provisionOff": "صفر — لا يتم ترحيل أي مخصص ضمان.",
      "gl.set.provisionRate": "نسبة المخصص",
      "gl.set.provisionRateHint": "نسبة مئوية من إيراد العمالة بمنزلتين عشريتين. 2.50 = 250 نقطة أساس.",
      "gl.set.readOnly": "يمكنك الاطلاع على هذه الإعدادات دون تعديلها. تعديلها يعيد إظهار الأرقام الدفترية، لذا يتطلب صلاحية ترحيل القيود اليومية.",
      "gl.set.saved": "تم حفظ الإعدادات المحاسبية",
      "gl.set.subtitle": "السياسات التي يقرأها الأستاذ العام: السنة المالية، وطريقة احتساب مخصص الضمان.",
      "gl.set.title": "الإعدادات المحاسبية",
      "gl.set.warrantyDesc": "نسبة من إيراد العمالة تُجنّب لإعادة العمل تحت الضمان، وتُحرَّر عند انتهاء الضمان دون استخدام.",
      "gl.set.warrantyMonths": "مدة الضمان",
      "gl.set.warrantyMonthsHint": "عدد الأشهر من تاريخ الفاتورة حتى تحرير المخصص غير المستخدم. من 1 إلى 60.",
      "gl.set.warrantyTitle": "مخصص الضمان",
      "gl.stmt.accessDenied": "لا تملك صلاحية الاطلاع على القوائم المالية",
      "gl.stmt.accessDeniedDesc": "اطلب من المسؤول منحك صلاحية عرض القيود اليومية.",
      "gl.stmt.blockedDesc": "هناك مستند واحد على الأقل غير متوازن، لذا فإن كل رقم في هذه القائمة سيكون خاطئًا. صحّح المستندات أدناه ثم حدّث الصفحة.",
      "gl.stmt.blockedDoc": "مستند",
      "gl.stmt.blockedTitle": "لا يمكن الاعتماد على هذه الفترة بعد",
      "gl.stmt.breakdownNote": "معروضة كمبالغ موجبة بإجمالي {{total}}؛ وجه القائمة يضيف هذا الإجمالي أو يخصمه.",
      "gl.stmt.col.account": "الحساب",
      "gl.stmt.col.amount": "المبلغ",
      "gl.stmt.col.code": "الرمز",
      "gl.stmt.empty": "لا يوجد ما يُعرض لهذه الفترة",
      "gl.stmt.emptyDesc": "لا توجد حركة في هذه الفترة وهذا النطاق. جرّب فترة أوسع.",
      "gl.stmt.export": "تصدير CSV",
      "gl.stmt.featureDisabled": "الأستاذ العام غير مفعّل لهذه الشركة",
      "gl.stmt.featureDisabledDesc": "اطلب من المسؤول تفعيل خاصية الأستاذ العام في باقتك.",
      "gl.stmt.loadFailed": "تعذّر تحميل هذه القائمة",
      "gl.stmt.noAccounts": "لا توجد حسابات خلف هذا البند",
      "gl.stmt.scopeBranch": "فرع واحد فقط. حسابات مستوى الشركة مُدرجة بالكامل.",
      "gl.stmt.scopeCompany": "الشركة كاملة — جميع الفروع.",
      "gl.stmt.showAccounts": "عرض {{count}} حساب خلف هذا البند",
      "gl.stmt.warningsCount": "{{count}} مستند في الحساب المعلّق — تعذّر ربطه بحساب",
      "hr.eosb.accessDenied": "لا تملك صلاحية الاطلاع على مخصص نهاية الخدمة الافتتاحي",
      "hr.eosb.accessDeniedDesc": "اطلب من المسؤول منحك صلاحية عرض الرواتب.",
      "hr.eosb.asOf": "كما في تاريخ",
      "hr.eosb.asOfHint": "عادةً تاريخ أرصدتك الافتتاحية. يُحتسب نصيب كل موظف من تاريخ توظيفه وأجر عقده في هذا اليوم.",
      "hr.eosb.col.amount": "نصيبه",
      "hr.eosb.col.employee": "الموظف",
      "hr.eosb.expected": "الافتتاحي المعتمد",
      "hr.eosb.gap": "الفرق",
      "hr.eosb.got": "هذا التوزيع",
      "hr.eosb.invalidRows": "{{count}} سطر لا يحمل مبلغاً صحيحاً.",
      "hr.eosb.mismatchDesc": "يجب أن يساوي مجموع السطور رقم نهاية الخدمة في أرصدتك الافتتاحية المعتمدة. عدّل الأنصبة حتى يتطابق الرقمان.",
      "hr.eosb.nav": "أرصدة نهاية الخدمة",
      "hr.eosb.noEmployees": "لا يوجد موظفون للتوزيع عليهم",
      "hr.eosb.noEmployeesDesc": "يُدرج فقط الموظفون النشطون الذين لديهم تاريخ توظيف وعقد ساري.",
      "hr.eosb.notPreviewed": "اختر تاريخاً ثم احتسب",
      "hr.eosb.notPreviewedDesc": "لا يُحتسب شيء قبل تحديد تاريخ فتح المخصص — رقم لتاريخ خاطئ أسوأ من لا رقم.",
      "hr.eosb.postCatchUp": "ترحيل قيد الاستدراك",
      "hr.eosb.postCatchUpDesc": "يرحّل الفرق بين هذا التوزيع والرقم الافتتاحي إلى الأستاذ العام. لا يمكن تنفيذه إلا مرة واحدة.",
      "hr.eosb.posted": "تم ترحيل قيد الاستدراك",
      "hr.eosb.postingDate": "تاريخ الترحيل",
      "hr.eosb.postingDateHint": "الافتراضي التاريخ أعلاه. ويجب أن يكون ذلك الشهر مفتوحاً.",
      "hr.eosb.preview": "احتساب",
      "hr.eosb.readyHint": "يجب أن يطابق الإجمالي أعلاه مخصصك الافتتاحي المعتمد.",
      "hr.eosb.save": "حفظ التوزيع",
      "hr.eosb.saved": "تم حفظ التوزيع",
      "hr.eosb.splitHeading": "لكل موظف",
      "hr.eosb.subtitle": "وزّع مخصص نهاية الخدمة الذي بدأت به على الموظفين الذين استحقوه.",
      "hr.eosb.title": "مخصص نهاية الخدمة الافتتاحي",
      "hr.eosb.unnamed": "موظف بلا اسم",
      "hr.file.loans.disbursedAt": "تاريخ الصرف",
      "hr.file.loans.disbursedAtHint": "اليوم الذي خرجت فيه النقدية. الافتراضي اليوم.",
      "hr.payroll.accrue.action": "استحقاق هذا المسير",
      "hr.payroll.accrue.desc": "قيد التزام الرواتب لمسير اعتُمد قبل وجود قيود الاستحقاق. ممكن فقط ما دام المسير معتمداً وغير مدفوع وغير مستحق مسبقاً.",
      "hr.payroll.accrue.done": "تم استحقاق مسير الرواتب",
      "hr.payroll.accrue.postingDate": "تاريخ الترحيل",
      "hr.payroll.accrue.postingDateHint": "الافتراضي آخر يوم في شهر المسير نفسه. ويجب أن يكون ذلك الشهر مفتوحاً.",
      "hr.payroll.unbanked.action": "صرف الجزء النقدي",
      "hr.payroll.unbanked.confirm": "تسجيل {{amount}} ريال كمصروف نقدي للموظفين الذين لم يصلهم التحويل. هذا يرحّل الحركة النقدية — نفّذه بعد تسليم المبلغ فعلياً.",
      "hr.payroll.unbanked.heading": "كيف صُرف هذا المسير",
      "hr.payroll.unbanked.headingDesc": "نظام حماية الأجور يحوّل فقط للموظفين الذين لديهم آيبان مسجّل. الباقي يُصرف نقداً ويجب تسجيله بشكل منفصل.",
      "hr.payroll.unbanked.inCash": "نقداً",
      "hr.payroll.unbanked.paid": "تم تسجيل الجزء النقدي",
      "hr.payroll.unbanked.settledOn": "سُجّل الجزء النقدي في {{date}}",
      "hr.payroll.unbanked.viaBank": "عبر حماية الأجور",
      "inv.wd.action": "خفض القيمة",
      "inv.wd.carryingCost": "التكلفة الدفترية للوحدة",
      "inv.wd.done": "تم خفض القيمة",
      "inv.wd.doneDesc": "شُطب {{amount}} ريال من القيمة الدفترية. أصبحت كل وحدة مسجّلة بـ {{unit}} ريال.",
      "inv.wd.doneTitle": "تم الخفض",
      "inv.wd.impact": "المبلغ المشطوب من المركز المالي",
      "inv.wd.intro": "خفّض قيمة هذا المخزون في الدفاتر. الكمية على الرف لا تتغير — استخدم «تعديل المخزون» لذلك.",
      "inv.wd.nrv": "القيمة الجديدة للوحدة",
      "inv.wd.nrvHint": "يجب أن تكون أقل من التكلفة الدفترية البالغة {{cost}}.",
      "inv.wd.onHand": "المتاح",
      "inv.wd.reason": "السبب",
      "inv.wd.reasonHint": "لماذا انخفضت قيمة المخزون — تلف، تقادم، أو انخفاض سعر السوق.",
      "inv.wd.reversed": "تم عكس خفض القيمة",
      "inv.wd.title": "خفض القيمة إلى صافي القيمة القابلة للتحقق",
      "inv.wd.undo": "التراجع عن هذا الخفض",
      "inv.wd.undoWindow": "يمكنك التراجع من هنا الآن. بعد إغلاق هذه النافذة لا توجد طريقة للعودة إليه — وبمجرد خروج أي من هذا المخزون من الرف لا يمكن التراجع إطلاقاً.",
      "inv.wd.writtenOn": "تاريخ الخفض",
      "inv.wd.writtenOnHint": "يجب أن يكون ذلك الشهر مفتوحاً.",
      "opex.timing.accruedOn": "قُيِّد كمستحق في {{date}}.",
      "opex.timing.accruedOnLabel": "قُيِّد كمستحق",
      "opex.timing.heading": "متى يُسجَّل في الدفاتر",
      "opex.timing.hint": "تاريخ المصروف أعلاه هو يوم تكبّد التكلفة ولا يتغير. هذه الحقول تحدد متى تخرج النقدية وعلى كم شهر تُوزَّع التكلفة.",
      "opex.timing.paidOn": "تاريخ السداد",
      "opex.timing.paidOnBadge": "سُدِّد في {{date}}",
      "opex.timing.paidOnHint": "اليوم الذي خرجت فيه النقدية فعلاً. الافتراضي اليوم؛ ويجب أن يكون ذلك الشهر مفتوحاً.",
      "opex.timing.paidOnLabel": "تاريخ السداد",
      "opex.timing.spreadBadge_one": "على شهر واحد",
      "opex.timing.spreadBadge_two": "على شهرين",

      "opex.timing.spreadBadge_few": "على {{count}} أشهر",
      "opex.timing.spreadBadge_many": "على {{count}} شهرًا",
      "opex.timing.spreadBadge_other": "على {{count}} شهر",
      "opex.timing.spreadDetail_one": "شهر واحد ابتداءً من {{date}}",
      "opex.timing.spreadDetail_two": "شهران ابتداءً من {{date}}",
      "opex.timing.spreadDetail_few": "{{count}} أشهر ابتداءً من {{date}}",
      "opex.timing.spreadDetail_many": "{{count}} شهرًا ابتداءً من {{date}}",
      "opex.timing.spreadDetail_other": "{{count}} شهر ابتداءً من {{date}}",
      "opex.timing.spreadLabel": "التوزيع",
      "opex.timing.spreadMonths": "يُوزَّع على",
      "opex.timing.spreadMonthsHint": "عدد الأشهر التي تغطيها هذه الدفعة، من 1 إلى 120. اتركه فارغاً ليُحمَّل بالكامل.",
      "opex.timing.spreadNone": "بدون توزيع",
      "opex.timing.spreadStart": "يبدأ التوزيع في",
      "opex.timing.spreadStartHint": "أول شهر يُحرَّر فيه جزء من التكلفة. الافتراضي تاريخ المصروف.",
      "opex.timing.unpaidBadge": "مستحق",
      "pt.featureDisabled": "خاصية «الوارد والمنصرف» غير مفعّلة لهذه الشركة",
      "pt.featureDisabledDesc": "الشركاء موجودون للسحب منهم والمساهمة لهم، وهذا يتطلب خاصية «الوارد والمنصرف». اطلب من المسؤول تفعيلها في باقتك.",
      "rpt.stock.inactiveValue": "قيمة القطع المعطّلة",
      "rpt.stock.inactiveValueDesc": "قطع أوقفتَ تفعيلها ولا يزال لديها مخزون. هذه القيمة داخل الإجمالي أعلاه ويحملها الأستاذ العام أيضاً — شاشة المتاح تُخفي هذه القطع، ولهذا يختلف الرقمان بهذا المقدار بالضبط.",
      "rpt.stock.writedowns": "مخفوضة إلى صافي القيمة القابلة للتحقق",
      "rpt.stock.writedownsDesc": "مقدار التكلفة الدفترية التي شُطبت من هذا المخزون. مخصومة أصلاً من القيمة أعلاه.",
      "rpt.stock.inTransit": "قيد النقل بين الفروع",
      "rpt.stock.inTransitDesc": "أُرسل من هنا ولم يُستلم بعد (عدد التحويلات: {{count}}). غير مشمول في القيمة أعلاه.",
      "vat.byTaxCategory": "حسب فئة الضريبة (هيئة الزكاة والضريبة)",
      "vat.glDifference": "الأستاذ العام وسطور الحركات يختلفان بمقدار {{sar}} ريال ({{halalas}} هللة)",
      "vat.glDifferenceDesc": "الخانات أعلاه هي الأرقام المعتمدة. أدناه الاحتساب سطراً بسطر لنفس الفترة — الفترة السليمة لا فرق فيها إطلاقاً.",
      "vat.glWarningDoc": "مستند",
      "vat.glWarnings": "{{count}} مستند تعذّرت قراءته بشكل سليم في الأستاذ العام",
      "vat.glWarningsMore": "…و{{count}} غيرها.",
      "vat.inputNet": "صافي المدخلات",
      "vat.legacyPrefix": "سطراً بسطر:",
      "vat.lines": "عدد السطور",
      "vat.outputNet": "صافي المخرجات",
      "vat.source.general_ledger": "من الأستاذ العام",
      "vat.source.legacy": "من سطور الحركات",
      "vat.source.legacy_gl_unavailable": "الأستاذ العام غير متاح",
      "vat.sourceDesc.general_ledger": "هذه الأرقام مشتقة من دفتر اليومية — نفس مصدر قوائمك المالية.",
      "vat.sourceDesc.legacy": "الأستاذ العام غير مفعّل لهذه الشركة، لذا تُحتسب الضريبة سطراً بسطر من الفواتير والمشتريات.",
      "vat.sourceDesc.legacy_gl_unavailable": "تمت مراجعة الأستاذ العام ولم يُجب، لذا هذه أرقام محتسبة سطراً بسطر. لا تعتمدها في الإقرار حتى يُقرأ الأستاذ العام بلا أخطاء.",
      "vat.taxCategory.exempt": "معفاة",
      "vat.taxCategory.out_of_scope": "خارج نطاق الضريبة",
      "vat.taxCategory.standard": "خاضعة بالنسبة الأساسية",
      "vat.taxCategory.unclassified": "غير مصنّفة",
      "vat.taxCategory.zero_rated": "خاضعة بنسبة صفر",
      "vat.unattributed": "{{output}} ريال من ضريبة المخرجات و{{input}} ريال من ضريبة المدخلات لا يمكن نسبتها إلى سطر واحد — ضريبة مدخلات سطور المخزون تُسجَّل على رأس المستند. لهذا لا تتطابق صفوف الفئات أدناه مع الخانات أعلاه.",
      "vat.unclassifiedLines": "{{count}} سطر بلا فئة ضريبية",
      "vat.unclassifiedLinesDesc": "هذه سطور بنسبة صفر لم يُسجَّل تصنيف التوريد لها، فلا يمكن وضعها في خانة من خانات الإقرار. تُحتسب تحت «غير مصنّفة» أدناه. حدّد الفئة الضريبية على سطور الفواتير والمشتريات قبل تقديم الإقرار.",
      "err.CASH_ADJUSTMENT_GATED": "هذه المنشأة تمسك دفاترها في دفتر الأستاذ العام برصيد افتتاحي معتمد. استخدم قيد يومية للتعديل.",
      "err.INVOICE_WRITE_OFF_STATE": "لا يمكن إعدام إلا فاتورة مُرحّلة. للفاتورة المسودة استخدم الخصم بدلاً من ذلك.",
      "err.INVOICE_ALREADY_WRITTEN_OFF": "تم إعدام هذه الفاتورة مسبقاً.",
      "err.INVOICE_WRITE_OFF_NOTHING_OWED": "لا يوجد ما يُعدم — الفاتورة ليس عليها رصيد مستحق.",
      "err.WRITE_OFF_PERIOD_CLOSED": "تلك الفترة المحاسبية مغلقة. أعد فتحها أو وثّق القيد في فترة مفتوحة.",
      "err.JOURNAL_EXPORT_RANGE_REQUIRED": "التصدير يحتاج تاريخ بداية وتاريخ نهاية.",
      "err.JOURNAL_EXPORT_RANGE_INVALID": "تحقق من تواريخ التصدير: استخدم صيغة YYYY-MM-DD واجعل تاريخ البداية قبل النهاية أو مساوياً لها.",
      "err.JOURNAL_EXPORT_ADAPTER_UNKNOWN": "صيغة التصدير هذه غير متاحة.",
      "err.JOURNAL_EXPORT_BLOCKED": "دفتر اليومية لهذه الفترة غير متوازن، لذلك لم يتم إنتاج ملف. عالج المستندات المذكورة ثم صدّر مرة أخرى.",
      "err.JOURNAL_RANGE_TOO_WIDE": "صدّر سنة واحدة كحد أقصى في كل مرة.",
      "err.JOURNAL_TOO_LARGE": "عدد القيود أكبر من أن يحتويه ملف واحد. صدّر فترة أقصر.",
      "err.INVENTORY_WRITEDOWN_INVALID": "صافي القيمة القابلة للتحقق والتاريخ والسبب مطلوبة",
      "err.INVENTORY_WRITEDOWN_UNAVAILABLE": "خفض قيمة المخزون غير متاح",
      "err.PART_NOT_FOUND": "قطعة الغيار غير موجودة",
      "err.INVENTORY_WRITEDOWN_NO_STOCK": "لا يوجد مخزون متاح من القطعة",
      "err.INVENTORY_WRITEDOWN_NOT_LOWER": "يجب أن يكون صافي القيمة القابلة للتحقق أقل من التكلفة الدفترية",
      "err.INVENTORY_WRITEDOWN_NOT_FOUND": "خفض قيمة المخزون غير موجود",
      "err.INVENTORY_WRITEDOWN_ALREADY_REVERSED": "تم عكس خفض قيمة المخزون مسبقاً",
      "err.INVENTORY_WRITEDOWN_HAS_OUTBOUND": "لا يمكن العكس بعد خروج المخزون",
      "err.PAYROLL_EMPLOYEE_LOANS_ACCOUNT_UNMAPPED": "حساب قروض الموظفين غير مربوط",
      "err.LEAVE_ACCRUAL_SETTLEMENT_MISSING": "حساب مخصص الإجازات السنوية مفقود",
      "err.PAYROLL_RUN_ALREADY_PAID": "لا يمكن استحقاق مسير رواتب مدفوع",
      "err.PAYROLL_RUN_ALREADY_ACCRUED": "تم استحقاق مسير الرواتب بالفعل",
      "err.PAYROLL_RUN_PREDATES_OPENING": "مسير الرواتب يسبق الأرصدة الافتتاحية المعتمدة",
      "payroll.zeroGosi": "لم يتم إنشاء سداد للتأمينات لأن المبلغ صفر",
      "err.EOSB_OPENING_SPLIT_MISMATCH": "توزيع مخصص نهاية الخدمة على الموظفين لا يطابق المخصص الافتتاحي المعتمد",
      "err.EOSB_CATCHUP_ALREADY_POSTED": "تم ترحيل قيد استدراك مخصص نهاية الخدمة الافتتاحي بالفعل",
      "err.OPENING_BALANCE_NOT_SIGNED_OFF": "يجب اعتماد الأرصدة الافتتاحية أولاً",
      "err.GL_OBLIGATION_EOSB": "مخصص نهاية الخدمة لهذه الفترة لم يُسجَّل.",
      "err.GL_OBLIGATION_GOSI": "اشتراك التأمينات الاجتماعية لهذه الفترة لم يُسجَّل.",
      "err.GL_OBLIGATION_PAYROLL_UNPAID": "توجد مسيرات رواتب معتمدة في هذه الفترة ولم تُدفع.",
      "err.GL_OBLIGATION_VAT_FILING": "إقرار ضريبة القيمة المضافة لهذه الفترة لم يُقدَّم بعد.",
      "err.GL_OPENING_LOCKED": "الأرصدة الافتتاحية مقفلة بالفعل. لتغييرها، استخدم قيد يومية.",
      "err.GL_OPENING_UNBALANCED": "حدث خلل في الأرقام أثناء الحفظ. يرجى المحاولة مرة أخرى أو التواصل مع الدعم.",
      "err.GL_REOPEN_VAT_FILED": "لا يمكن إعادة فتح هذه الفترة لأن إقرار ضريبة القيمة المضافة الخاص بها قُدِّم بالفعل. سجّل التصحيح في فترة مفتوحة بدلاً من ذلك.",
      "err.GL_CLOSE_PERIOD_NOT_ENDED": "هذا الشهر لم ينتهِ بعد. يمكنك إقفاله بعد انتهاء الشهر.",
      "err.GL_PERIOD_ALREADY_CLOSED": "هذه الفترة مُقفلة بالفعل. أعد فتحها أولاً إذا كنت تريد إقفالها مرة أخرى.",
      "err.JV_ACCOUNT_INVALID": "أحد حسابات هذا القيد غير قابل للاستخدام: فهو ليس في دليل حسابات هذه الشركة، أو غير نشط، أو حساب تجميعي.",
      "err.JV_ACCOUNT_LOCKED": "أحد السطور يستخدم حساباً مقفلاً. لا يجوز القيد على حسابات النقدية والحساب المعلّق أبداً.",
      "err.JV_ACCOUNT_RESTRICTED": "أحد السطور يستخدم حساباً رقابياً، ويتطلب تفعيل تجاوز الحسابات الرقابية لهذا القيد.",
      "err.JV_ALREADY_REVERSED": "سبق عكس هذا القيد.",
      "err.JV_SYSTEM_OWNED": "هذا القيد أنشأه النظام وليس من شاشة القيود. يُعكس استحقاق الرواتب بعكس مسير الرواتب الخاص به. أما المخصص الافتتاحي أو تسوية نهاية الخدمة فلا توجد لهما شاشة تراجع — فليُسجَّل قيد تصحيحي بدلاً من ذلك.",
      "err.JV_FRACTIONAL_HALALA": "أحد المبالغ ليس عدداً صحيحاً من الهللات. استخدم منزلتين عشريتين كحد أقصى.",
      "err.JV_NOT_DRAFT": "لم يعد هذا القيد مسودة، فلا يمكن تعديله أو حذفه.",
      "err.JV_NOT_POSTED": "القيد المُرحَّل وحده هو ما يمكن عكسه.",
      "err.JV_OVERRIDE_FORBIDDEN": "ليست لديك صلاحية تجاوز الحسابات الرقابية.",
      "err.JV_PERIOD_CLOSED": "الفترة المحاسبية لهذا التاريخ مقفلة، فلا يمكن الترحيل داخلها.",
      "err.JV_TOO_FEW_LINES": "يحتاج قيد اليومية إلى سطرين على الأقل، بسطر مدين واحد وسطر دائن واحد على الأقل.",
      "err.JV_UNBALANCED": "إجمالي المدين لا يساوي إجمالي الدائن.",
      "err.JV_AMOUNT_TOO_LARGE": "المبلغ أكبر مما يقبله هذا الدفتر.",
      "err.field.JV_AMOUNT_TOO_LARGE": "هذا المبلغ أكبر مما يمكن أن يحمله سطر قيد واحد.",
      "err.OB_ACCOUNT_CODE_REQUIRED": "اختر حساباً محدداً لهذا السطر. خيار «أخرى» يحتاج حساباً — أما النقدية والذمم المدينة والدائنة فلها حساباتها الجاهزة.",
      "err.OB_ACCOUNT_CODE_CONTROLLED": "هذا الحساب حساب رقابي — استخدم سطر النقدية أو العميل أو المورّد إن وُجد، وإلا فسجّله بقيد يومية يدوي بدلاً من «أخرى».",
      "err.OUTSIDE_HOURS": "الوقت المختار خارج ساعات عمل الفرع.",
      "err.OFF_GRID": "يُرجى اختيار أحد الأوقات المتاحة.",
      "err.PERIOD_CLOSED": "هذا التاريخ يقع في فترة محاسبية مقفلة. استخدم تاريخاً في فترة مفتوحة، أو اطلب من المسؤول إعادة فتحها.",
      "err.CREDIT_NOTE_AMOUNT_INVALID": "يجب أن يكون مبلغ الإشعار الدائن الجزئي أكبر من صفر.",
      "err.CREDIT_NOTE_EXCEEDS_BALANCE": "مبلغ الإشعار الدائن أكبر من الرصيد المستحق على الفاتورة.",
      "err.CREDIT_NOTE_TYPE_INVALID": "يجب أن يكون نوع الإشعار الدائن كاملاً أو جزئياً.",
      "err.PAYMENT_EXCEEDS_ADJUSTED_TOTAL": "هذه الدفعة أكبر من إجمالي الفاتورة بعد الخصم. أدخل مبلغاً لا يتجاوز الرصيد المتبقي.",
      "err.QUOTATION_LINE_ALREADY_QUOTED": "بعض البنود موجودة في عرض سعر آخر مفتوح. ألغِ ذلك العرض أو حدّثه أولاً.",
      "err.QUOTATION_CLOSED": "تم إلغاء عرض السعر هذا من قبل المركز.",
      "err.DOWNPAYMENT_ACTION_REQUIRED": "اختر استرداد العربون أو الاحتفاظ به رصيداً للعميل.",
      "err.CARD_AWAITING_CUSTOMER_APPROVAL": "لم يوافق العميل على أمر العمل بعد. اعتمد عرض السعر يدوياً أو ألغِه أولاً.",
      "err.DOWNPAYMENT_GATEWAY_REFUND_UNSUPPORTED": "تم دفع هذا العربون إلكترونياً ولا يمكن استرداده من هنا حالياً. احتفظ به رصيداً للعميل.",
      "err.QUOTATION_PAYMENT_IN_PROGRESS": "العميل يدفع هذا العرض الآن. حاول مرة أخرى بعد بضع دقائق.",
      "qclose.closeAction": "إلغاء عرض السعر",
      "qclose.manualApproveAction": "اعتماد يدوي",
      "qclose.modalTitle": "إلغاء عرض السعر {{number}}",
      "qclose.modalBody": "يبقى عرض السعر في السجل بحالة «ملغي»، ويتوقف رابط العميل عن العمل.",
      "qclose.reasonLabel": "السبب",
      "qclose.reason.duplicate": "عرض سعر مكرر",
      "qclose.reason.customer_changed_request": "العميل غيّر الطلب",
      "qclose.reason.price_changed": "تغيّر السعر",
      "qclose.reason.revised": "استُبدل بعرض سعر محدّث",
      "qclose.reason.other": "أخرى",
      "qclose.noteLabel": "ملاحظة",
      "qclose.noteRequired": "اكتب ملاحظة عند اختيار «أخرى».",
      "qclose.downpaymentTitle": "دفع العميل عربوناً بقيمة {{amount}}",
      "qclose.downpayment.refund": "استرداده للعميل",
      "qclose.downpayment.credit": "الاحتفاظ به رصيداً للعميل",
      "qclose.confirm": "إلغاء عرض السعر",
      "qclose.closed": "تم إلغاء عرض السعر",
      "qclose.badge": "ملغي",
      "qclose.backToDiagnosis": "عاد أمر العمل إلى مرحلة التشخيص.",
      "qclose.showClosed": "إظهار الملغاة",
      "qclose.publicClosedTitle": "تم إلغاء عرض السعر هذا",
      "qclose.publicClosedBody": "تم إلغاء عرض السعر هذا من قبل المركز. يرجى التواصل معهم.",
      "qclose.publicReplaced": "تم استبدال عرض السعر هذا بعرض أحدث. يرجى استخدام آخر رابط أرسله المركز.",
      "qclose.history": "تم إلغاء عرض السعر {{number}}: {{reason}}",
      "qclose.flag.noOpenQuotation": "بانتظار موافقة العميل بدون عرض سعر مفتوح",
      "qclose.flag.waitingDays": "بانتظار موافقة العميل منذ {{days}} يوم",
      "qclose.override.reasonLabel": "لماذا الإكمال قبل موافقة العميل؟",
      "gl.chart.accessDenied": "لا تملك صلاحية الوصول إلى دليل الحسابات",
      "gl.chart.accessDeniedDesc": "اطلب من المسؤول منحك صلاحية gl_accounts.view.",
      "gl.chart.activate": "تفعيل",
      "gl.chart.activated": "تم إعادة تفعيل الحساب.",
      "gl.chart.active": "نشط",
      "gl.chart.addAccount": "إضافة حساب",
      "gl.chart.addDesc": "اختر مكان هذا الحساب — يُشتق نوعه ورصيده الطبيعي من الحساب الرئيسي المختار.",
      "gl.chart.addTitle": "إضافة حساب",
      "gl.chart.advanced": "خيارات متقدمة",
      "gl.chart.advancedCollapse": "استخدام القيم الموروثة بدلاً من ذلك",
      "gl.chart.advancedConfirmButton": "أفهم ذلك، اسمح لي بالتجاوز",
      "gl.chart.advancedConfirmMessage": "هذه هي الطريقة التي يُنشأ بها الحساب المقابل (contra)، وهي نادرًا ما تكون الخيار الصحيح. غيّر هذه القيم فقط إذا كنت متأكدًا أن هذا الحساب يجب أن يتصرف بشكل مختلف عن حسابه الرئيسي.",
      "gl.chart.advancedConfirmTitle": "تجاوز النوع والرصيد الموروثين؟",
      "gl.chart.code": "الرمز",
      "gl.chart.codeHint": "مقترح من نطاق الحساب الرئيسي. يمكنك تغييره.",
      "gl.chart.col.account": "الحساب",
      "gl.chart.col.state": "الحالة",
      "gl.chart.col.type": "النوع",
      "gl.chart.createFailed": "تعذّر إنشاء الحساب.",
      "gl.chart.created": "تم إنشاء الحساب.",
      "gl.chart.credit": "دائن",
      "gl.chart.deactivate": "إلغاء التفعيل",
      "gl.chart.deactivateConfirm": "إلغاء التفعيل",
      "gl.chart.deactivateFailed": "تعذّر إلغاء تفعيل الحساب.",
      "gl.chart.deactivateMessage": "يبقى الحساب في الدليل وفي كل سجل سابق — فقط يختفي من قوائم الاختيار، بحيث لا يمكن اختياره لسطر قيد جديد.",
      "gl.chart.deactivateTitle": "إلغاء تفعيل هذا الحساب؟",
      "gl.chart.deactivated": "تم إلغاء تفعيل الحساب.",
      "gl.chart.debit": "مدين",
      "gl.chart.edit": "تعديل",
      "gl.chart.editTitle": "تعديل الحساب",
      "gl.chart.empty": "لا توجد حسابات بعد",
      "gl.chart.emptyDesc": "ستظهر هنا الحسابات التي تضيفها، مجمّعة حسب النوع.",
      "gl.chart.errName": "أدخل الاسم بالإنجليزية والعربية معًا.",
      "gl.chart.errParent": "اختر حسابًا رئيسيًا.",
      "gl.chart.featureDisabled": "ميزة دفتر الأستاذ العام غير مفعّلة لهذه الشركة",
      "gl.chart.featureDisabledDesc": "اطلب من المسؤول تفعيل ميزة دفتر الأستاذ العام.",
      "gl.chart.inactive": "غير نشط",
      "gl.chart.includeInactive": "إظهار الحسابات غير النشطة",
      "gl.chart.inherited": "النوع والرصيد الطبيعي",
      "gl.chart.inheritedHint": "موروثان من الحساب الرئيسي.",
      "gl.chart.loadFailed": "تعذّر تحميل دليل الحسابات.",
      "gl.chart.lockedTooltip": "مملوك للنظام — لا يمكن تسجيل أي قيد على هذا الحساب أبدًا.",
      "gl.chart.nameAr": "الاسم (عربي)",
      "gl.chart.nameEn": "الاسم (إنجليزي)",
      "gl.chart.normalBalance": "الرصيد الطبيعي",
      "gl.chart.parent": "الحساب الرئيسي",
      "gl.chart.restrictedTooltip": "يتطلب تسجيل قيد على هذا الحساب صلاحية تجاوز خاصة.",
      "gl.chart.saveFailed": "تعذّر حفظ الحساب.",
      "gl.chart.saved": "تم تحديث الحساب.",
      "gl.chart.selectParent": "اختر حسابًا رئيسيًا",
      "gl.chart.sortOrder": "ترتيب العرض",
      "gl.chart.subtitle": "كل حساب، مجمّع حسب النوع ومتفرّع تحت الحساب الرئيسي له.",
      "gl.chart.title": "دليل الحسابات",
      "gl.chart.type.asset": "أصول",
      "gl.chart.type.equity": "حقوق الملكية",
      "gl.chart.type.expense": "مصروفات",
      "gl.chart.type.liability": "التزامات",
      "gl.chart.type.revenue": "إيرادات",
      "gl.chart.typeLabel": "النوع",
      "gl.chart.warnDigitMismatch": "رمز هذا الحساب لا يطابق النمط المعتاد لنوعه. تم إنشاؤه رغم ذلك — تحقق من الرمز إذا لم يكن ذلك مقصودًا.",
      "gl.close.accessDenied": "ليست لديك صلاحية إقفال الفترة",
      "gl.close.accessDeniedDesc": "اطلب من المسؤول منحك صلاحية عرض قيود اليومية للوصول إلى هذه الشاشة.",
      "gl.close.acknowledgeCheckbox": "أعلم بهذا وأقوم بالإقفال رغم ذلك",
      "gl.close.closeAction": "إقفال الشهر",
      "gl.close.closeAnywayAction": "إقفال على أي حال",
      "gl.close.overrideWarningsHeading": "إقفال مع وجود تنبيهات",
      "gl.close.overrideWarningsBody": "يوجد {{count}} تنبيه في قيود هذه الفترة. الإقفال الآن يجمّد اللقطة كما هي، وأي تصحيح بعده يحتاج قيد يومية في فترة مفتوحة.",
      "gl.close.closeFailed": "تعذّر إقفال هذه الفترة.",
      "gl.close.closeSeq": "أُقفلت {{n}} مرات",
      "gl.close.closeSuccess": "تم إقفال الفترة.",
      "gl.close.closedBy": "أُقفلت بواسطة",
      "gl.close.closedOn": "أُقفلت في",
      "gl.close.col.accountKey": "الحساب",
      "gl.close.col.docRef": "المستند",
      "gl.close.col.recomputed": "المُعاد احتسابه اليوم",
      "gl.close.col.snapshot": "المبلغ المجمّد",
      "gl.close.confirmAckHeading": "أنت تُقرّ، دون تسجيل، بأن هذه المبالغ ما زالت مستحقة:",
      "gl.close.confirmAction": "إقفال هذا الشهر",
      "gl.close.confirmIntro": "أنت على وشك إقفال {{period}}. بعد الإقفال، تتجمّد كل الأرقام أدناه — أي تصحيح لاحق يعني ترحيل قيد يومية في الفترة المفتوحة، وليس تعديل هذه الفترة.",
      "gl.close.confirmTitle": "إقفال {{period}}؟",
      "gl.close.confirmWarning": "لا يمكن التراجع عن هذا من هذه الشاشة. بعد الإقفال، تحتاج أي تصحيحات إلى قيد يومية في الفترة المفتوحة.",
      "gl.close.featureDisabled": "دفتر الأستاذ العام غير مُفعّل",
      "gl.close.featureDisabledDesc": "اطلب من المسؤول تفعيل ميزة دفتر الأستاذ العام لهذه الشركة.",
      "gl.close.jumpToPeriod": "الانتقال إلى {{period}}",
      "gl.close.loadFailed": "تعذّر تحميل البيانات. حاول مرة أخرى.",
      "gl.close.nextYear": "السنة التالية",
      "gl.close.obligation.GL_OBLIGATION_EOSB": "مكافأة نهاية الخدمة المستحقة عن هذه الفترة",
      "gl.close.obligation.GL_OBLIGATION_GOSI": "حصة المنشأة في التأمينات الاجتماعية عن هذه الفترة",
      "gl.close.obligation.GL_OBLIGATION_PAYROLL_UNPAID": "دورة رواتب معتمدة ولم تُصرف بعد",
      "gl.close.obligation.GL_OBLIGATION_PENDING_GATEWAY": "مدفوعات بالبطاقة أو بالدفع الآجل لم تؤكّدها بوابة الدفع بعد",
      "gl.close.obligation.GL_OBLIGATION_VAT_FILING": "قيد إقرار ضريبة القيمة المضافة لهذه الفترة",
      "gl.close.obligationsHeading": "مبالغ لم تُسجَّل بعد",
      "gl.close.openDocument": "افتح المستند",
      "gl.close.openDocuments": "فتح المستندات",
      "gl.close.openHint": "لم تُقفل بعد",
      "gl.close.periodNotEnded": "هذا الشهر لم ينتهِ بعد. يمكن إقفاله بعد انتهاء الشهر.",
      "gl.close.pickMonth": "اختر شهرًا",
      "gl.close.pickMonthDesc": "اختر شهرًا أعلاه لمراجعة ما سيحدث عند إقفاله.",
      "gl.close.prevYear": "السنة السابقة",
      "gl.close.readyToClose": "لا يوجد ما يمنع الإقفال. راجع الإجماليات أعلاه ثم أقفل عندما تكون جاهزًا.",
      "gl.close.recordIt": "سجّلها",
      "gl.close.refusal.GL_CLOSE_BLOCKED_DOCUMENT": "أحد المستندات غير متوازن ويجب إصلاحه قبل إقفال هذا الشهر.",
      "gl.close.refusal.GL_CLOSE_EARLIER_PERIOD_OPEN": "فترة سابقة، {{period}}، ما زالت مفتوحة. تُقفل الأشهر بالترتيب.",
      "gl.close.refusal.GL_CLOSE_WARNINGS_PRESENT": "انتقل مبلغ إلى حساب معلّق لأن أحد المستندات تعذّر ربطه بحساب.",
      "gl.close.refusal.GL_CLOSE_YEAR_END_SUSPENSE_NONZERO": "نهاية السنة: لا يزال في الحساب المعلّق مبلغ غير مصنّف. أعد تصنيفه بقيد يومية قبل إقفال ديسمبر.",
      "gl.close.refusal.DEPRECIATION_UNPOSTED": "الإهلاك لم يُرحَّل حتى هذا الشهر بعد. رحّل الإهلاك ثم أقفل الشهر.",
      "gl.close.refusalsHeading": "لا يمكن إقفال هذا الشهر بعد",
      "gl.close.warning.total_mismatch": "مجموع بنود {{doc_ref}} هو {{recomputed}} ريال بينما صدرت الفاتورة بمبلغ {{stored}} ريال. الفرق مُقيَّد بالفعل في حساب فروق المستندات غير المسوّاة، فدفاتر هذا الشهر متوازنة. الفاتورة المرحَّلة لا يمكن تعديلها — افتحها للتحقق من المبالغ، ثم أقفل الشهر على أي حال واترك الفرق ليسوّيه المحاسب.",
      "gl.close.warning.no_line_items": "لا توجد بنود في {{doc_ref}}، لذلك تعذّر نسب مبلغها البالغ {{stored}} ريال إلى الإيرادات أو ضريبة القيمة المضافة، وقُيّد المبلغ كاملًا في حساب فروق المستندات غير المسوّاة. الفاتورة المرحَّلة لا يمكن تعديلها — افتحها للتحقق، ثم أقفل الشهر على أي حال واترك المبلغ ليسوّيه المحاسب.",
      "gl.close.warning.unmapped_account": "لا يوجد حساب مرتبط بـ \"{{label}}\" في {{doc_ref}}، لذلك وُضع المبلغ في حساب معلّق. استخدم زر «ربط» على هذا التنبيه في ميزان المراجعة لاختيار الحساب، ثم أعد التحقق من الشهر.",
      "gl.close.warning.unbalanced_document": "{{doc_ref}} غير متوازن — المدين لا يساوي الدائن. يجب تصحيحه قبل إقفال هذا الشهر.",
      "gl.close.warning.excluded_parent_document": "سُجّل {{doc_ref}} على {{parent_ref}}، وهو غير مدرج في دفاتر هذا الشهر، لذلك وُضع مبلغه في حساب معلّق. عالج {{parent_ref}} أولًا.",
      "gl.close.warning.unprojectable_document": "تعذّر تحويل {{doc_ref}} إلى قيد يومية، لذلك وُضع مبلغه في حساب معلّق. أرسل هذا الرقم المرجعي إلى الدعم.",
      "gl.close.warning.total_mismatch.purchase_invoice": "مجموع {{doc_ref}} لا يساوي المبلغ المسجَّل لها. الفرق مُقيَّد في حساب فروق المستندات غير المسوّاة، فدفاتر هذا الشهر متوازنة — افتحها للتحقق من المبالغ.",
      "gl.close.warning.total_mismatch.stock_movement": "تعذّر تقييم حركة مخزون ({{doc_ref}}) من التكلفة المسجَّلة لها، لذلك قُيّد الفرق في حساب فروق المستندات غير المسوّاة. حدِّد تكلفة القطعة لتُقيَّم الحركات اللاحقة بشكل صحيح.",
      "gl.close.warning.total_mismatch.stock_transfer": "تحويل مخزون ({{doc_ref}}) لا يتوازن بين الفرعين. الفرق مُقيَّد في حساب فروق المستندات غير المسوّاة.",
      "gl.map.action": "ربط حساب",
      "gl.close.openParts": "فتح قطع الغيار",
      "gl.close.noAction": "لا توجد شاشة لفتح هذا المستند",
      "gl.map.affected": "{{count}} مستند(ات) على هذا المفتاح — مرّ {{amount}} إجمالًا عبر الحساب المعلّق",
      "gl.map.andMore": "و {{count}} أخرى",
      "gl.map.title": "اربط «{{label}}» بحساب",
      "gl.map.description": "المبالغ على هذا المفتاح موضوعة في حساب معلّق حتى يُربط بحساب. اضبطه مرة واحدة وتُربط كل المستندات التي تستخدمه من الآن فصاعدًا.",
      "gl.map.account": "الحساب",
      "gl.map.pick": "اختر حسابًا",
      "gl.map.save": "حفظ الربط",
      "gl.map.saved": "تم الربط — جارٍ إعادة قراءة ميزان المراجعة.",
      "gl.map.saveFailed": "تعذّر حفظ الربط.",
      "gl.map.loadFailed": "تعذّر تحميل دليل الحسابات، لذلك لا يمكن اختيار حساب الآن.",
      "gl.close.reopen": "إعادة فتح",
      "gl.close.reopenConfirmAction": "إعادة فتح هذا الشهر",
      "gl.close.reopenConfirmMessage": "إعادة الفتح تسمح بترحيل قيود يومية جديدة إلى هذه الفترة مجددًا. الدفاتر جُمّدت لسبب — أعد الفتح فقط لتصحيح خطأ حقيقي، وأعد الإقفال بمجرد الانتهاء. هذا يزيد عدد مرات الإقفال ويظهر للمدقق.",
      "gl.close.reopenConfirmTitle": "إعادة فتح {{period}}؟",
      "gl.close.reopenFailed": "تعذّرت إعادة فتح هذه الفترة.",
      "gl.close.reopenSuccess": "تمت إعادة فتح الفترة.",
      "gl.close.status.closed": "مُقفلة",
      "gl.close.status.open": "مفتوحة",
      "gl.close.subtitle": "إقفال أو إعادة فتح أو التحقق من دفاتر شهر. الإقفال لا رجعة فيه عمليًا — أي تصحيح بعده يحتاج قيد يومية في الفترة المفتوحة.",
      "gl.close.title": "إقفال الفترة",
      "gl.close.totalCredit": "إجمالي الدائن",
      "gl.close.totalDebit": "إجمالي المدين",
      "gl.close.vatFiledFinal": "تم تقديم إقرار ضريبة القيمة المضافة لهذه الفترة. لا يمكن إعادة فتحها أبدًا.",
      "gl.close.verify": "تحقّق",
      "gl.close.verifyFailed": "تعذّر تشغيل التحقق.",
      "gl.close.verifyMatches": "الأرقام المجمّدة ما زالت مطابقة للمستندات. لا حاجة لأي إجراء.",
      "gl.close.verifyMismatch": "الأرقام المجمّدة لم تعد مطابقة لما تقوله المستندات اليوم.",
      "gl.close.verifyNote": "التحقق لا يعيد كتابة الفترة المجمّدة أبدًا. إن وُجد خطأ، صحّحه بقيد يومية في الفترة المفتوحة.",
      "gl.gj.blockedDesc": "أحد المستندات غير متوازن، لذلك لا يمكن عرض اليومية لهذه الفترة: {{refs}}. صحّح ذلك المستند وتعود اليومية للظهور.",
      "gl.gj.blockedTitle": "تعذّر عرض اليومية",
      "gl.gj.col.document": "المستند",
      "gl.gj.col.entry": "القيد",
      "gl.gj.cr": "دائن",
      "gl.gj.derived": "من وحدته",
      "gl.gj.derivedDialog.desc": "أُنشئ هذا القيد من المستند أدناه ولم يُدخَل يدويًا. صحّحه من الوحدة التي يتبعها.",
      "gl.gj.derivedDialog.noSource": "لا يوجد مستند يمكن فتحه لهذا القيد.",
      "gl.gj.derivedDialog.number": "المستند",
      "gl.gj.derivedDialog.openSource": "فتح المستند الأصلي",
      "gl.gj.derivedDialog.reference": "مرجع القيد",
      "gl.gj.derivedDialog.title": "قيد مُرحَّل من وحدة أخرى",
      "gl.gj.dr": "مدين",
      "gl.gj.empty": "لا حركة في هذه الفترة",
      "gl.gj.emptyDesc": "لم يُرحَّل أي فاتورة أو سند قبض أو مشتريات أو قيد بين هذين التاريخين.",
      "gl.gj.kind.advance_application": "تطبيق دفعة مقدمة",
      "gl.gj.kind.bad_debt_writeoff": "إعدام دين معدوم",
      "gl.gj.kind.zatca_credit_note_resolution": "إبقاء استرداد إشعار دائن رصيدًا دائنًا للعميل",
      "gl.gj.kind.expense_accrual": "استحقاق مصروف",
      "gl.gj.kind.fixed_asset_disposal": "استبعاد أصل ثابت",
      "gl.gj.kind.fixed_asset_cancellation": "إلغاء أصل ثابت",
      "gl.gj.kind.inventory_writedown": "تخفيض قيمة المخزون",
      "gl.gj.kind.prepaid_release": "إطفاء مصروف مقدم",
      "gl.gj.kind.stock_movement": "حركة مخزون",
      "gl.gj.kind.stock_transfer": "تحويل مخزون بين الفروع",
      "gl.gj.kind.warranty_credit_note": "إشعار دائن ضمان",
      "gl.gj.kind.warranty_provision": "مخصص ضمان",
      "gl.gj.kind.warranty_release": "رد مخصص ضمان",
      "gl.gj.kind.warranty_utilisation": "أعمال ضمان منفذة",
      "gl.gj.kind.cash_movement": "حركة نقدية",
      "gl.gj.kind.fixed_asset_depreciation": "إهلاك",
      "gl.gj.kind.gateway_settlement": "تسوية بوابة دفع",
      "gl.gj.kind.invoice": "فاتورة مبيعات",
      "gl.gj.kind.journal_entry": "قيد يدوي",
      "gl.gj.kind.journal_exception": "مبلغ غير مطابق",
      "gl.gj.kind.opening_balance": "رصيد افتتاحي",
      "gl.gj.kind.purchase_credit_note": "إشعار دائن مشتريات",
      "gl.gj.kind.expense_credit_note": "إشعار دائن مصروف",
      "gl.gj.kind.purchase_invoice": "فاتورة مشتريات",
      "gl.gj.kind.sales_credit_note": "إشعار دائن مبيعات",
      "gl.gj.kind.zatca_credit_note": "إشعار دائن مبيعات (هيئة الزكاة)",
      "gl.gj.readOnlyNote": "كل ما هنا معروض كما رحّلته الوحدة التي جاء منها. لتغيير أي منها انتقل إلى ذلك المستند — الفاتورة تُصحَّح بإشعار دائن، والأصل باستبعاده. القيود اليدوية وحدها يمكن تعديلها من هذه الشاشة.",
      "gl.gj.unmappedAccount": "حساب غير موجود في دليلك",
      "gl.gj.viewAll": "دفتر اليومية — كل الحركات",
      "gl.gj.viewLabel": "العرض",
      "gl.gj.viewManual": "القيود اليدوية فقط",
      "gl.jv.account.headerBadge": "تجميعي",
      "gl.jv.account.headerTip": "حساب تجميعي. يجمع أرصدة الحسابات التي تحته ولا يمكن الترحيل عليه مباشرة.",
      "gl.jv.account.lockedBadge": "مقفل",
      "gl.jv.account.lockedTip": "حسابات النقدية والحساب المعلّق لا يجوز القيد عليها أبداً، لأي مستخدم. النقدية تُثبت بجرد الصندوق، ولذلك تُصحَّح من كشف النقدية وليس من هنا.",
      "gl.jv.account.noMatch": "لا يوجد حساب مطابق لهذا البحث.",
      "gl.jv.account.noneDesc": "دليل الحسابات فارغ أو تعذّرت قراءته، فلا يوجد ما يمكن القيد عليه.",
      "gl.jv.account.noneTitle": "لا توجد حسابات متاحة",
      "gl.jv.account.pick": "اختر حساباً…",
      "gl.jv.account.restrictedBadge": "رقابي",
      "gl.jv.account.restrictedNoPermTip": "حساب رقابي يديره النظام عادةً. الترحيل عليه يتطلب صلاحية journal_entries.override_control، وهي غير متاحة لك.",
      "gl.jv.account.restrictedOnLine": "حساب رقابي — هذا القيد يحتاج إلى تجاوز الحسابات الرقابية.",
      "gl.jv.account.restrictedTip": "حساب رقابي يديره النظام عادةً (الذمم المدينة والدائنة، دفعات العملاء المقدمة، تسويات بوابات الدفع، ضريبة القيمة المضافة). اختياره يُفعّل تجاوز الحسابات الرقابية لهذا القيد.",
      "gl.jv.account.search": "ابحث بالرقم أو الاسم",
      "gl.jv.accountInvalidHint": "أحد الحسابات غير موجود في دليل حسابات هذه الشركة، أو غير نشط، أو حساب تجميعي. اختر حساباً آخر في السطر المحدد.",
      "gl.jv.accountsFailed": "تعذّر تحميل دليل الحسابات، لذا لا يمكن اختيار أي حساب.",
      "gl.jv.addLine": "إضافة سطر",
      "gl.jv.amountInvalid": "أحد المبالغ ليس رقماً صحيحاً. استخدم منزلتين عشريتين كحد أقصى.",
      "gl.jv.badge.overridden": "تجاوز الحسابات الرقابية",
      "gl.jv.badge.reversedBy": "عُكس بالقيد {{no}}",
      "gl.jv.badge.reverses": "يعكس القيد {{no}}",
      "gl.jv.balanced": "متوازن — المدين يساوي الدائن.",
      "gl.jv.branch": "الفرع",
      "gl.jv.branchShared": "على مستوى الشركة (بدون فرع)",
      "gl.jv.col.account": "الحساب",
      "gl.jv.col.amount": "إجمالي المدين",
      "gl.jv.col.createdBy": "بواسطة",
      "gl.jv.col.credit": "دائن",
      "gl.jv.col.date": "تاريخ القيد",
      "gl.jv.col.debit": "مدين",
      "gl.jv.col.description": "الوصف",
      "gl.jv.col.entryNo": "رقم القيد",
      "gl.jv.col.memo": "البيان",
      "gl.jv.col.status": "الحالة",
      "gl.jv.createdBy": "أنشأه",
      "gl.jv.dateRequired": "تاريخ القيد مطلوب.",
      "gl.jv.delete.message": "سيتم حذف المسودة وسطورها. لم يُرحَّل شيء إلى الأستاذ، فلا يتغير شيء آخر.",
      "gl.jv.delete.title": "هل تريد حذف هذه المسودة؟",
      "gl.jv.deleted": "تم حذف المسودة",
      "gl.jv.difference": "الفرق",
      "gl.jv.editor.desc": "كل سطر إما مدين أو دائن. يجب أن يتساوى إجمالي المدين مع إجمالي الدائن قبل ترحيل القيد.",
      "gl.jv.editor.newTitle": "قيد يومية جديد",
      "gl.jv.editor.readOnlyDesc": "تم ترحيل هذا القيد. يُعرض كما هو ولا يمكن تعديله.",
      "gl.jv.editor.title": "قيد يومية",
      "gl.jv.empty": "لا توجد قيود يومية في هذه الفترة",
      "gl.jv.emptyDesc": "لم يُسجَّل أي قيد للتواريخ والفرع المحددين. وسّع نطاق التاريخ، أو أنشئ أول قيد.",
      "gl.jv.enableOverride": "تفعيل تجاوز الحسابات الرقابية",
      "gl.jv.entryDate": "تاريخ القيد",
      "gl.jv.filterStatus": "الحالة",
      "gl.jv.fixBlockers": "عالِج النقاط المذكورة أدناه قبل الحفظ.",
      "gl.jv.impossible": "ما كان ينبغي أن يحدث هذا من هذه الشاشة — الرجاء الإبلاغ عنه.",
      "gl.jv.lines": "السطور",
      "gl.jv.lockedFinal": "هذا نهائي. لا يجوز القيد على حسابات النقدية والحساب المعلّق بأي صلاحية. صحِّح النقدية من كشف النقدية بدلاً من ذلك.",
      "gl.jv.memoAr": "البيان (بالعربية)",
      "gl.jv.memoEn": "البيان (بالإنجليزية)",
      "gl.jv.memoHint": "يلزم إدخال أحد البيانين على الأقل.",
      "gl.jv.memoRequired": "اكتب البيان بالعربية أو بالإنجليزية.",
      "gl.jv.nav": "قيود اليومية",
      "gl.jv.needBothSigns": "يحتاج القيد إلى سطر مدين واحد على الأقل وسطر دائن واحد على الأقل.",
      "gl.jv.needTwoLines": "يحتاج قيد اليومية إلى سطرين على الأقل.",
      "gl.jv.new": "قيد يومية جديد",
      "gl.jv.noAccess": "لا يمكنك عرض قيود اليومية",
      "gl.jv.noAccessDesc": "عرض قيود اليومية يتطلب صلاحية journal_entries.view. اطلب من المسؤول منحك الصلاحية.",
      "gl.jv.noAccountOnLine": "كل سطر يحتاج إلى حساب.",
      "gl.jv.noFeature": "الأستاذ العام غير مُفعَّل",
      "gl.jv.noFeatureDesc": "قيود اليومية جزء من الأستاذ العام، وهو غير مُفعَّل لهذه الشركة. اطلب من المسؤول تفعيله.",
      "gl.jv.notACreditNote": "لا يمكن تصحيح فاتورة عميل بقيد يومية — التصحيح يكون بإشعار دائن.",
      "gl.jv.notACreditNoteDetail": "إذا صدرت فاتورة خاطئة للعميل، أصدر إشعاراً دائناً على الفاتورة. قيد اليومية يحرّك الأستاذ دون المساس بالفاتورة ولا بالسجل الضريبي، فيتوقف الاثنان عن التطابق.",
      "gl.jv.notDraftHint": "المسودة وحدها هي ما يمكن تعديله أو حذفه. حدّث القائمة لمعرفة حالة القيد الحالية.",
      "gl.jv.openTwin": "فتح القيد المرتبط",
      "gl.jv.outOfBalance": "غير متوازن بمقدار {{amount}} ريال. لا يمكن ترحيل القيد حتى يصبح الفرق صفراً.",
      "gl.jv.outOfBalanceShort": "إجمالي المدين لا يساوي إجمالي الدائن.",
      "gl.jv.override.confirmAttribution": "يُسجَّل هذا التجاوز باسمك على هذا القيد.",
      "gl.jv.override.confirmBody": "سيحرّك هذا القيد رصيداً يديره النظام عادةً ويبقيه متوافقاً مع المستندات التي خلفه — الذمم المدينة أو الدائنة أو دفعات العملاء المقدمة أو تسويات بوابات الدفع أو ضريبة القيمة المضافة. بعد ذلك قد يتوقف الأستاذ وتلك المستندات عن التطابق.",
      "gl.jv.override.confirmTitle": "هل تريد تجاوز الحسابات الرقابية؟",
      "gl.jv.override.confirmYes": "نعم، تجاوز",
      "gl.jv.override.hint": "مطلوب لأن هذا القيد يمس {{codes}}. التجاوز يسري على القيد كاملاً ويُسجَّل باسمك.",
      "gl.jv.override.needPermission": "الترحيل على حساب رقابي يتطلب صلاحية journal_entries.override_control.",
      "gl.jv.override.title": "تجاوز الحسابات الرقابية",
      "gl.jv.overrideUsed": "تجاوز الحسابات الرقابية",
      "gl.jv.periodClosedHint": "هذا الشهر مقفل. أرّخ القيد في الفترة المفتوحة بدلاً من ذلك؛ فالشهر المقفل يُصحَّح بقيد جديد لا بتأريخ رجعي.",
      "gl.jv.post": "ترحيل القيد",
      "gl.jv.posted": "تم ترحيل القيد",
      "gl.jv.postedAt": "تاريخ الترحيل",
      "gl.jv.postedBy": "رحّله",
      "gl.jv.readOnlyPosted": "القيود المرحّلة للقراءة فقط. لا تعديل ولا حذف — التصحيح الوحيد هو العكس.",
      "gl.jv.readOnlyReversed": "سبق عكس هذا القيد. يبقى في الدفاتر بجانب قيد العكس الخاص به.",
      "gl.jv.refreshHint": "حدّث القائمة لمعرفة حالة القيد الحالية.",
      "gl.jv.removeLine": "حذف هذا السطر",
      "gl.jv.restrictedNeedsOverride": "القيد يستخدم حساباً رقابياً، لذا يجب تفعيل تجاوز الحسابات الرقابية.",
      "gl.jv.restrictedOfferOverride": "لديك صلاحية التجاوز. فعّلها لترحيل هذا القيد على الحساب الرقابي.",
      "gl.jv.reverse.action": "عكس القيد",
      "gl.jv.reverse.dateNote": "قيد العكس يحمل تاريخ اليوم، {{date}} — وليس تاريخ القيد الأصلي أبداً.",
      "gl.jv.reverse.done": "تم ترحيل قيد العكس",
      "gl.jv.reverse.message": "العكس يُنشئ قيداً مُرحَّلاً جديداً بعكس كل مدين ودائن. يبقى القيد الأصلي في الدفاتر موسوماً بأنه معكوس، ويرتبط الاثنان ببعضهما.",
      "gl.jv.reverse.notInPlace": "هو لا يلغي القيد في مكانه. ووقوع قيد عكس داخل شهر مقفل هو تحديداً ما يمنعه قفل الفترة.",
      "gl.jv.reverse.title": "هل تريد عكس هذا القيد؟",
      "gl.jv.saveDraft": "حفظ كمسودة",
      "gl.jv.saveFailed": "تعذّر حفظ قيد اليومية.",
      "gl.jv.saved": "تم حفظ المسودة",
      "gl.jv.status.all": "كل الحالات",
      "gl.jv.status.draft": "مسودة",
      "gl.jv.status.posted": "مُرحَّل",
      "gl.jv.status.reversed": "معكوس",
      "gl.jv.subtitle": "قيود يومية يدوية. يمكن تعديل المسودة؛ وبعد الترحيل يصبح القيد نهائياً ولا يمكن إلا عكسه.",
      "gl.jv.title": "قيود اليومية",
      "gl.jv.totalCredit": "إجمالي الدائن",
      "gl.jv.totalDebit": "إجمالي المدين",
      "gl.jv.zeroLine": "كل سطر يحتاج إلى مبلغ في خانة المدين أو خانة الدائن.",
      "gl.tb.accessDenied": "لا تملك صلاحية الوصول إلى ميزان المراجعة",
      "gl.tb.accessDeniedDesc": "اطلب من المسؤول منحك صلاحية عرض قيود اليومية.",
      "gl.tb.balanced": "متوازن — المدين يساوي الدائن",
      "gl.tb.blockedDesc": "هناك مستند واحد على الأقل غير متوازن، لذا فإن كل رقم في هذه الصفحة سيكون غير صحيح. صحّح المستندات أدناه ثم أعد التحميل.",
      "gl.tb.blockedDoc": "مستند",
      "gl.tb.blockedTitle": "لا يمكن الوثوق بهذه الفترة بعد",
      "gl.tb.col.code": "الرمز",
      "gl.tb.col.closing": "الرصيد الختامي",
      "gl.tb.col.credit": "دائن",
      "gl.tb.col.debit": "مدين",
      "gl.tb.col.opening": "الرصيد الافتتاحي",
      "gl.tb.col.name": "الحساب",
      "gl.tb.col.type": "النوع",
      "gl.tb.empty": "لا يوجد ما يُعرض لهذا النطاق",
      "gl.tb.emptyDesc": "لا يوجد نشاط حسابات في هذه الفترة والنطاق. جرّب نطاق تاريخ أوسع أو فعّل إظهار الحسابات ذات الرصيد الصفري.",
      "gl.tb.export": "تصدير CSV",
      "gl.tb.featureDisabled": "دفتر الأستاذ العام غير مفعّل لهذه الشركة",
      "gl.tb.featureDisabledDesc": "اطلب من المسؤول تفعيل خاصية دفتر الأستاذ العام لباقتكم.",
      "gl.tb.grandTotal": "الإجمالي الكلي",
      "gl.tb.ledgerClosing": "الرصيد الختامي",
      "gl.tb.ledgerCol.amount": "المبلغ",
      "gl.tb.ledgerCol.date": "التاريخ",
      "gl.tb.ledgerCol.reference": "المرجع",
      "gl.tb.ledgerCol.running": "الرصيد الجاري",
      "gl.tb.ledgerEmpty": "لا يوجد نشاط لهذا الحساب في هذا النطاق",
      "gl.tb.ledgerLoadFailed": "تعذّر تحميل دفتر الحساب",
      "gl.tb.ledgerOpening": "الرصيد الافتتاحي",
      "gl.tb.ledgerRange": "{{from}} – {{to}}",
      "gl.tb.ledgerViewEntry": "عرض القيد اليومي",
      "gl.tb.loadFailed": "تعذّر تحميل ميزان المراجعة",
      "gl.tb.manualOnly": "قيد يدوي فقط",
      "gl.tb.showZero": "إظهار الحسابات ذات الرصيد الصفري",
      "gl.tb.sourceClosed": "مُقفلة بتاريخ {{date}} — أرقام مجمّدة",
      "gl.tb.sourceLive": "مباشر — محسوب من نشاط فترة مفتوحة",
      "gl.tb.subtitle": "رصيد كل حساب: الرصيد الافتتاحي، والحركة، والرصيد الختامي.",
      "gl.tb.subtotal": "إجمالي فرعي — {{type}}",
      "gl.tb.title": "ميزان المراجعة",
      "gl.tb.type.asset": "الأصول",
      "gl.tb.type.equity": "حقوق الملكية",
      "gl.tb.type.expense": "المصروفات",
      "gl.tb.type.liability": "الخصوم",
      "gl.tb.type.other": "أخرى",
      "gl.tb.type.revenue": "الإيرادات",
      "gl.tb.unbalanced": "غير متوازن — المدين والدائن غير متطابقين",
      "gl.tb.warningsCount": "{{count}} مستند(ات) معلّقة في حساب التعليق — تعذّر ربطها بحساب",
      "ob.accessDenied": "ليس لديك صلاحية الوصول إلى الأرصدة الافتتاحية",
      "ob.accessDeniedDesc": "اطلب من المسؤول منحك صلاحية الوصول إلى الأرصدة الافتتاحية.",
      "ob.addRow.account": "حساب محدد (اختياري)",
      "ob.addRow.accountNone": "استخدم الافتراضي",
      "ob.addRow.amount": "المبلغ",
      "ob.addRow.button": "إضافة بند",
      "ob.addRow.errAmount": "أدخل مبلغًا أكبر من صفر.",
      "ob.addRow.heading": "أضف أي شيء آخر — قرض، معدات، أو أموالك الخاصة في المنشأة",
      "ob.addRow.lineType": "ما أقرب فئة لهذا البند؟",
      "ob.addRow.note": "ملاحظة (اختياري)",
      "ob.addRow.notePlaceholder": "مثال: قرض من بنك الراجحي",
      "ob.date.hint": "اليوم الذي تبدأ منه سجلاتك — عادةً يوم بدأت استخدام MotorMind.",
      "ob.date.label": "اعتبارًا من تاريخ",
      "ob.empty.desc": "لا يوجد لدينا شيء نعرفه تلقائيًا، ولم تُضِف شيئًا بعد. استخدم \"إضافة بند آخر\" أدناه عندما تكون جاهزًا.",
      "ob.empty.title": "لا يوجد شيء هنا بعد",
      "ob.errDate": "اختر التاريخ الذي تبدأ منه سجلاتك.",
      "ob.errRows": "بعض البنود غير مكتملة — راجع البنود المميّزة أدناه.",
      "ob.featureDisabled": "ميزة الأرصدة الافتتاحية غير مفعّلة لهذه الشركة",
      "ob.featureDisabledDesc": "اطلب من المسؤول تفعيل ميزة الأرصدة الافتتاحية.",
      "ob.lineType.ap": "أموال تدين بها للموردين",
      "ob.lineType.ar": "أموال يدين بها العملاء لك",
      "ob.lineType.cash": "نقدية أو بنك",
      "ob.loadFailed": "تعذّر تحميل أرصدتك الافتتاحية.",
      "ob.locked.banner": "الأرصدة الافتتاحية مقفلة",
      "ob.locked.bannerDate": "تم القفل بتاريخ {{date}}. لتغييرها الآن، استخدم قيد يومية.",
      "ob.prefill.alreadyBooked.note": "هذا المبلغ مُدرج بالفعل في دفاترك — لا يوجد شيء لإدخاله بشأنه.",
      "ob.prefill.button": "استخدم ما نعرفه بالفعل",
      "ob.prefill.chip.ap_open": "من فواتير الموردين غير المسددة",
      "ob.prefill.chip.ar_open": "من فواتير العملاء غير المسددة",
      "ob.prefill.chip.cash_account": "من حساباتك النقدية",
      "ob.prefill.chip.inventory": "من مخزونك",
      "ob.prefill.count.ap_open_one": "فاتورة مورد واحدة غير مسددة",
      "ob.prefill.count.ap_open_other": "{{count}} فاتورة مورد غير مسددة",
      "ob.prefill.count.ap_open_zero": "لا توجد فواتير موردين غير مسددة",
      "ob.prefill.count.ap_open_two": "فاتورتا مورد غير مسددتين",
      "ob.prefill.count.ap_open_few": "{{count}} فواتير موردين غير مسددة",
      "ob.prefill.count.ap_open_many": "{{count}} فاتورة مورد غير مسددة",
      "ob.prefill.count.ar_open_one": "فاتورة عميل واحدة غير مسددة",
      "ob.prefill.count.ar_open_other": "{{count}} فاتورة عميل غير مسددة",
      "ob.prefill.count.ar_open_zero": "لا توجد فواتير عملاء غير مسددة",
      "ob.prefill.count.ar_open_two": "فاتورتا عميل غير مسددتين",
      "ob.prefill.count.ar_open_few": "{{count}} فواتير عملاء غير مسددة",
      "ob.prefill.count.ar_open_many": "{{count}} فاتورة عميل غير مسددة",
      "ob.prefill.count.cash_account_one": "حساب نقدي واحد",
      "ob.prefill.count.cash_account_other": "{{count}} حسابات نقدية",
      "ob.prefill.count.cash_account_zero": "لا توجد حسابات نقدية",
      "ob.prefill.count.cash_account_two": "حسابان نقديان",
      "ob.prefill.count.cash_account_few": "{{count}} حسابات نقدية",
      "ob.prefill.count.cash_account_many": "{{count}} حسابًا نقديًا",
      "ob.prefill.count.inventory_one": "صنف مخزون واحد",
      "ob.prefill.count.inventory_other": "{{count}} صنف مخزون",
      "ob.prefill.count.inventory_zero": "لا توجد أصناف مخزون",
      "ob.prefill.count.inventory_two": "صنفا مخزون",
      "ob.prefill.count.inventory_few": "{{count}} أصناف مخزون",
      "ob.prefill.count.inventory_many": "{{count}} صنف مخزون",
      "ob.prefill.heading": "استخدم ما نعرفه بالفعل",
      "ob.prefill.inventory.estimatedNote": "يتم تقييم إجمالي المخزون هذا بتكلفة كل صنف الحالية، وليس بتكلفته كما كانت في تاريخ الافتتاح. كما يُحتسب أول مخزون لصنف ما، أو جرد أُدخل ورصيده المسجَّل صفر، كمخزون افتتاحي إذا تم إدخاله في تاريخ الافتتاح أو بعده، وهو ما قد يُضخِّم الإجمالي — راجعه جيدًا. أما التصحيح اللاحق لمخزون سبق تسجيله فلا يُدرَج ضمن ذلك. بمجرد قفل الأرصدة الافتتاحية، لا يمكن التراجع عن ذلك.",
      "ob.prefill.inventory.inactiveNote_one": "قطعة واحدة من هذه القطع لم تعد نشطة في كتالوجك. لا تزال محسوبة هنا لأنها كانت تحمل مخزونًا في تاريخ الافتتاح، لكن تقرير تقييم المخزون لن يشملها — والرقمان لن يتطابقا بهذا القدر.",
      "ob.prefill.inventory.inactiveNote_other": "{{count}} من هذه القطع لم تعد نشطة في كتالوجك. لا تزال محسوبة هنا لأنها كانت تحمل مخزونًا في تاريخ الافتتاح، لكن تقرير تقييم المخزون لن يشملها — والرقمان لن يتطابقا بهذا القدر.",
      "ob.prefill.inventory.inactiveNote_zero": "لا توجد قطع غير نشطة ضمن هذا الرقم.",
      "ob.prefill.inventory.inactiveNote_two": "قطعتان من هذه القطع لم تعودا نشطتين في كتالوجك. لا تزالان محسوبتين هنا لأنهما كانتا تحملان مخزونًا في تاريخ الافتتاح، لكن تقرير تقييم المخزون لن يشملهما — والرقمان لن يتطابقا بهذا القدر.",
      "ob.prefill.inventory.inactiveNote_few": "{{count}} قطع من هذه القطع لم تعد نشطة في كتالوجك. لا تزال محسوبة هنا لأنها كانت تحمل مخزونًا في تاريخ الافتتاح، لكن تقرير تقييم المخزون لن يشملها — والرقمان لن يتطابقا بهذا القدر.",
      "ob.prefill.inventory.inactiveNote_many": "{{count}} قطعة من هذه القطع لم تعد نشطة في كتالوجك. لا تزال محسوبة هنا لأنها كانت تحمل مخزونًا في تاريخ الافتتاح، لكن تقرير تقييم المخزون لن يشملها — والرقمان لن يتطابقا بهذا القدر.",
      "ob.prefill.inventory.unresolvedNote_one": "قطعة واحدة من مخزونك الحالي تعذّر تأكيدها كما كانت في تاريخ الافتتاح — قد تكون محتسبة بالفعل في مكان آخر (مثل الفرع الذي نُقلت منه)، فتحقق من ذلك قبل إضافتها يدويًا.",
      "ob.prefill.inventory.unresolvedNote_other": "{{count}} من مخزونك الحالي تعذّر تأكيدها كما كانت في تاريخ الافتتاح — قد تكون محتسبة بالفعل في مكان آخر (مثل الفرع الذي نُقلت منه)، فتحقق من ذلك قبل إضافتها يدويًا.",
      "ob.prefill.inventory.inTransitNote": "كان هناك مخزون قيد النقل بين الفروع في هذا التاريخ ({{transfers}}). لن يُقبل الاعتماد حتى تختار تاريخًا لم يكن فيه أي تحويل قيد النقل.",
      "ob.prefill.inventory.unresolvedNote_zero": "لا توجد قطع يتعذّر تأكيدها ضمن هذا الرقم.",
      "ob.prefill.inventory.unresolvedNote_two": "قطعتان من مخزونك الحالي تعذّر تأكيدهما كما كانتا في تاريخ الافتتاح — قد تكونان محتسبتين بالفعل في مكان آخر (مثل الفرع الذي نُقلتا منه)، فتحقق من ذلك قبل إضافتهما يدويًا.",
      "ob.prefill.inventory.unresolvedNote_few": "{{count}} قطع من مخزونك الحالي تعذّر تأكيدها كما كانت في تاريخ الافتتاح — قد تكون محتسبة بالفعل في مكان آخر (مثل الفرع الذي نُقلت منه)، فتحقق من ذلك قبل إضافتها يدويًا.",
      "ob.prefill.inventory.unresolvedNote_many": "{{count}} قطعة من مخزونك الحالي تعذّر تأكيدها كما كانت في تاريخ الافتتاح — قد تكون محتسبة بالفعل في مكان آخر (مثل الفرع الذي نُقلت منه)، فتحقق من ذلك قبل إضافتها يدويًا.",
      "ob.prefill.inventory.zeroCostNote_one": "قطعة واحدة من هذه القطع ليس لها تكلفة مسجلة وتُقيَّم بصفر — راجعها قبل قفل هذه الأرصدة.",
      "ob.prefill.inventory.zeroCostNote_other": "{{count}} من هذه القطع ليس لها تكلفة مسجلة وتُقيَّم بصفر — راجعها قبل قفل هذه الأرصدة.",
      "ob.prefill.inventory.zeroCostNote_zero": "لا توجد قطع بدون تكلفة مسجلة.",
      "ob.prefill.inventory.zeroCostNote_two": "قطعتان من هذه القطع ليس لهما تكلفة مسجلة وتُقيَّمان بصفر — راجعهما قبل قفل هذه الأرصدة.",
      "ob.prefill.inventory.zeroCostNote_few": "{{count}} قطع من هذه القطع ليس لها تكلفة مسجلة وتُقيَّم بصفر — راجعها قبل قفل هذه الأرصدة.",
      "ob.prefill.inventory.zeroCostNote_many": "{{count}} قطعة من هذه القطع ليس لها تكلفة مسجلة وتُقيَّم بصفر — راجعها قبل قفل هذه الأرصدة.",
      "ob.prefill.none": "لا يوجد لدينا شيء نُحضره تلقائيًا حتى الآن — أضف ما تعرفه أدناه.",
      "ob.prefill.summaryLead": "الضغط على هذا الزر سيُحضر:",
      "ob.row.remove": "إزالة",
      "ob.rows.empty": "لا توجد بنود بعد",
      "ob.rows.emptyDesc": "اضغط \"استخدم ما نعرفه بالفعل\" أعلاه، أو أضف بندًا بنفسك.",
      "ob.saveFailed": "تعذّر حفظ الأرصدة الافتتاحية.",
      "ob.saved": "تم حفظ الأرصدة الافتتاحية.",
      "ob.signOff.button": "اعتماد هذه الأرصدة نهائيًا",
      "ob.signOff.confirm": "نعم، اعتمدها نهائيًا",
      "ob.signOff.done": "تم اعتماد الأرصدة الافتتاحية وقفلها.",
      "ob.signOff.failed": "تعذّر اعتماد الأرصدة الافتتاحية.",
      "ob.signOff.message": "بمجرد القيام بذلك، سيتم قفل أرصدتك الافتتاحية. تغييرها لاحقًا سيتطلب قيد يومية، وليس هذه الشاشة.",
      "ob.signOff.title": "هل تريد اعتماد أرصدتك الافتتاحية نهائيًا؟",
      "ob.subtitle": "أخبرنا بما تملكه منشأتك وما عليها في اليوم الأول — ومعظمها نعرفه بالفعل.",
      "ob.summary.assets": "ما تملكه",
      "ob.summary.equity": "حقوق الملكية (محسوبة)",
      "ob.summary.equityDeficitLabel": "عجز",
      "ob.summary.equityTooltip": "هذا هو ببساطة المتبقي بعد احتساب كل شيء آخر — هذا أمر طبيعي، ولن تحتاج إلى إدخاله بنفسك أبدًا. يظهر هنا كرأس مال أضفته إلى العمل.",
      "ob.summary.equityTooltipDeficit": "هذا هو ببساطة المتبقي بعد احتساب كل شيء آخر — هذا أمر طبيعي، ولن تحتاج إلى إدخاله بنفسك أبدًا. وبما أن ما على منشأتك أكبر مما تملكه، فإنه يظهر هنا كعجز بدلاً من مبلغ أضفته.",
      "ob.summary.liabilities": "ما عليك",
      "ob.table.col.account": "حساب محدد",
      "ob.table.col.amount": "المبلغ",
      "ob.table.col.category": "الفئة",
      "ob.table.col.note": "ملاحظة",
      "ob.table.col.what": "ما هو",
      "ob.table.heading": "أرقامك الافتتاحية",
      "ob.title": "الأرصدة الافتتاحية",
      "ob.lineType.other": "شيء آخر (قرض، معدات، أموالك الخاصة)",
      "ob.table.col.counterpart": "لمن أو أين",
      "ob.counterpart.notNeeded": "غير مطلوب",
      "ob.counterpart.chosen": "تم الاختيار",
      "ob.counterpart.choose.cash": "اختر الحساب النقدي أو البنكي",
      "ob.counterpart.choose.ar": "اختر العميل",
      "ob.counterpart.choose.ap": "اختر المورد",
      "ob.counterpart.title.cash": "أي حساب نقدي أو بنكي؟",
      "ob.counterpart.title.ar": "أي عميل يدين بهذا المبلغ؟",
      "ob.counterpart.title.ap": "لأي مورد تدين بهذا المبلغ؟",
      "ob.counterpart.desc.cash": "اختر الصندوق أو الحساب البنكي الذي يوجد فيه هذا المبلغ في تاريخ البداية.",
      "ob.counterpart.desc.ar": "اختر العميل الذي ما زال مدينًا بهذا المبلغ في تاريخ البداية.",
      "ob.counterpart.desc.ap": "اختر المورد الذي ما زلت مدينًا له بهذا المبلغ في تاريخ البداية.",
      "ob.counterpart.field.cash": "الحساب النقدي أو البنكي",
      "ob.counterpart.field.ar": "العميل",
      "ob.counterpart.field.ap": "المورد",
      "ob.counterpart.placeholder.cash": "اختر حسابًا",
      "ob.counterpart.placeholder.ar": "اختر عميلًا",
      "ob.counterpart.placeholder.ap": "اختر موردًا",
      "ob.counterpart.empty.cash": "لا توجد حسابات نقدية أو بنكية",
      "ob.counterpart.empty.ar": "لا يوجد عملاء",
      "ob.counterpart.empty.ap": "لا يوجد موردون",
      "ob.counterpart.loadFailed": "تعذّر تحميل القائمة.",
      "ob.addRow.counterpart": "لمن أو أين",
      "ob.addRow.accountRequired": "أي حساب (مطلوب)",
      "ob.addRow.accountPick": "اختر حسابًا",
      "ob.incomplete.title": "بعض البنود ما زالت تحتاج إلى إجابة",
      "ob.incomplete.desc": "البنود المميّزة باللون الكهرماني تحتاج منك تحديد صاحب المبلغ أو الحساب الذي يوجد فيه. المتبقي: {{remaining}}.",
      "ob.err.counterpart.cash": "اختر الحساب النقدي أو البنكي الذي يوجد فيه هذا المبلغ.",
      "ob.err.counterpart.ar": "اختر العميل الذي يدين بهذا المبلغ.",
      "ob.err.counterpart.ap": "اختر المورد الذي تدين له بهذا المبلغ.",
      "ob.err.accountRequired": "اختر الحساب الذي يوجد فيه هذا المبلغ.",
      "ob.err.fractional": "يمكن أن يحتوي المبلغ على منزلتين عشريتين كحد أقصى.",
      "err.field.OB_COUNTERPART_REQUIRED": "اختر واحدًا.",
      "err.field.OB_AMOUNT_NEGATIVE": "أدخل مبلغًا موجبًا.",
      "err.field.OB_AMOUNT_REQUIRED": "أدخل مبلغًا.",
      "err.field.OB_FRACTIONAL_HALALA": "منزلتان عشريتان كحد أقصى.",
      "apv.title": "موافقات العميل",
      "apv.drawerTitle": "إثبات الموافقة",
      "apv.empty": "لا توجد موافقات مسجلة للعميل حتى الآن",
      "apv.loadFailed": "تعذر تحميل موافقات العميل",
      "apv.subjectTitle": "الموضوع",
      "apv.proofStrength": "قوة الإثبات",
      "apv.actorTitle": "من قام بالإجراء",
      "apv.staffBannerTitle": "اعتمدها موظف الورشة؛ لم يوقّع العميل إلكترونياً",
      "apv.legacyBannerTitle": "لم يُحفظ دليل لحظي لهذا القرار؛ البصمة محسوبة لاحقًا من البيانات المخزنة",
      "apv.whatCustomerSaw": "ما رآه العميل",
      "apv.howApproved": "كيف تمت الموافقة",
      "apv.mediaTitle": "بصمات الصور والوسائط",
      "apv.integrityTitle": "السلسلة وسلامة البيانات",
      "apv.downloadPdf": "تحميل PDF",
      "apv.shareWhatsApp": "مشاركة عبر واتساب",
      "apv.hashCopied": "تم نسخ البصمة",
      "apv.errViewPdf": "تعذّر فتح سجل الموافقة",
      "apv.errDownloadPdf": "تعذّر تحميل سجل الموافقة",
      "apv.errCopyLink": "تعذر نسخ الرابط",
      "apv.approved": "موافق عليه",
      "apv.rejected": "مرفوض",
      "apv.approvedTotal": "إجمالي المعتمد",
      "apv.delayTerms": "شروط التأخير",
      "apv.latePartsFee": "رسوم تأخير القطع/يوم",
      "apv.lateCollectionFee": "رسوم تأخير الاستلام/يوم",
      "apv.graceHours": "فترة السماح",
      "apv.termsVersion": "إصدار الشروط",
      "apv.showFullTerms": "عرض الشروط الكاملة",
      "apv.hideFullTerms": "إخفاء الشروط الكاملة",
      "apv.actorStaff": "الموظف الذي اعتمد",
      "apv.codeSentAt": "وقت إرسال الرمز",
      "apv.codeVerifiedAt": "وقت تأكيد الرمز",
      "apv.responseDuration": "مدة الاستجابة",
      "apv.otpAttempts": "محاولات التحقق",
      "apv.ipAddress": "عنوان IP",
      "apv.device": "الجهاز / المتصفح",
      "apv.mediaCounts": "{{fingerprinted}} محفوظة البصمة، {{missing}} بدون بصمة",
      "apv.fingerprinted": "محفوظة البصمة",
      "apv.notFingerprinted": "بدون بصمة",
      "apv.noMedia": "لا توجد وسائط مرفقة",
      "apv.snapshotOk": "بصمة المحتوى",
      "apv.eventHashOk": "بصمة السجل",
      "apv.prevLinkOk": "ترابط السلسلة",
      "apv.snapshotHash": "بصمة المحتوى",
      "apv.prevHash": "بصمة السجل السابق",
      "apv.eventHash": "بصمة السجل",
      "apv.hashedAt": "تاريخ أخذ البصمة",
      "apv.certificateHeading": "السجل",
      "apv.issueCertificate": "إصدار السجل",
      "apv.openCertificate": "فتح السجل",
      "apv.certificateNo": "رقم السجل",
      "apv.certificateDate": "تاريخ السجل",
      "apv.copyShareText": "نسخ نص المشاركة",
      "apv.shareCopied": "نُسخ نص المشاركة",
      "apv.certificateShare": "سجل رقم {{number}} بتاريخ {{date}}. للتحقق: {{url}}",
      "apv.certificateUnavailable": "تعذّر إصدار السجل الآن، تواصل مع الدعم",
      "apv.certificate": "سجل الموافقة",
      "apv.verifyLink": "رابط التحقق",
      "apv.fingerprint": "بصمة",
      "apv.viewProofOnJobCard": "عرض إثبات الموافقة في بطاقة العمل",
      "apv.actor.customer": "العميل",
      "apv.actor.staff": "موظف",
      "apv.actor.system": "النظام",
      "apv.media.inlinePhoto": "صورة مضمّنة {{n}}",
      "history.acceptance_certificate_opened": "تم فتح شهادة موافقة العميل",
      "apv.subject.intake_tnc": "شروط الاستلام",
      "apv.subject.quotation": "عرض السعر",
      "apv.subject.quotation_revision": "تعديل عرض السعر",
      "apv.subject.supplementary_quotation": "عرض سعر إضافي",
      "apv.subject.vehicle_handover": "استلام المركبة",
      "apv.subject.online_payment": "دفع إلكتروني",
      "apv.tier.otp_in_person": "رمز سلّمه العميل عند الاستلام",
      "apv.tier.payment_gateway": "مؤكد من بوابة الدفع",
      "apv.handover.title": "تسليم المركبة",
      "apv.handover.method": "طريقة التسليم",
      "apv.handover.methodCode": "سلّم العميل رمز التسليم المرسل إلى هاتفه",
      "apv.handover.methodOverride": "سلّمها الموظف دون الرمز",
      "apv.handover.overrideReason": "السبب",
      "apv.handover.handedOverBy": "سلّمها",
      "apv.handover.deliveredAt": "وقت التسليم",
      "apv.handover.onCredit": "سُلّمت بالآجل",
      "apv.handover.creditDue": "تاريخ الاستحقاق",
      "apv.handover.cancelledReturn": "أُعيدت دون عمل (أمر عمل ملغى)",
      "apv.handover.invoice": "الفاتورة",
      "apv.handover.invoiceTotal": "إجمالي الفاتورة",
      "apv.handover.paid": "المدفوع",
      "apv.handover.balanceDue": "المتبقي",
      "apv.payment.title": "دُفع إلكترونياً",
      "apv.payment.amount": "المبلغ",
      "apv.payment.gateway": "مزوّد الدفع",
      "apv.payment.reference": "مرجع المزوّد",
      "apv.payment.paidFor": "مقابل",
      "apv.payment.paidAt": "وقت الدفع",
      "apv.result.accepted": "موافق عليه",
      "apv.result.partially_accepted": "موافق عليه جزئياً",
      "apv.result.rejected": "مرفوض",
      "apv.result.disputed": "اعتراض",
      "apv.result.staff_attested": "معتمد من الموظف",
      "apv.result.sent": "تم الإرسال",
      "apv.result.withdrawn": "مسحوب",
      "apv.result.superseded": "مستبدل",
      "apv.result.link_expired": "انتهت صلاحية الرابط",
      "apv.tier.legacy": "من البيانات المخزنة، دون دليل لحظي",
      "apv.tier.staff_attested": "اعتمدها الموظف فقط",
      "apv.tier.link_only": "عبر الرابط فقط",
      "apv.tier.otp_link": "تم التحقق برمز SMS",
      "apv.tier.otp_link_anchored": "تم التحقق برمز SMS",
      "apv.tier.certified": "توقيع معتمد",
      "apv.retry": "إعادة المحاولة",
      "apv.note": "ملاحظة",
      "apv.vat": "ضريبة القيمة المضافة",
      "apv.seconds": "ثانية",
      "apv.copy": "نسخ",
      // Jev Vision documents (Task S2)
      "jevVision.docs.retake": "إعادة الالتقاط",
      "jevVision.docs.scanAnyway": "المتابعة بالمسح على أي حال",
      "jevVision.docs.rescanOriginal": "إعادة مسح الصورة الأصلية",
      "jevVision.docs.checking": "جارٍ فحص الصورة…",
      "jevVision.docs.cancel": "إلغاء",
      "jevVision.docs.unreadable": "الصورة غير واضحة أو باهتة ولا يمكن قراءتها.",
      "jevVision.docs.wrongDocument": "لا تبدو هذه الصورة كالمستند المطلوب.",
      "jevVision.docs.photoLabel": "صورة {{number}}",
      "jevVision.docs.bannerTitle": "تنبيه فحص المستند"
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    // Only en/ar exist; normalize region codes (e.g. a device set to 'ar-SA'
    // or 'en-GB') down to the base language so detection from navigator maps to
    // a real resource and i18n.language is a clean 'ar'/'en' for dir/lang logic.
    supportedLngs: ['en', 'ar'],
    load: 'languageOnly',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    // Return key if translation is missing instead of showing the key
    returnNull: false,
    returnEmptyString: false,
    // Use key as fallback if translation missing
    saveMissing: false,
    // Ensure we always return something readable
    parseMissingKeyHandler: (key, defaultValue) => {
      // i18next passes the caller's defaultValue (already interpolated) and
      // shows whatever this returns: honour it, or every t(key, { defaultValue })
      // on a missing key printed the humanized key instead of its fallback.
      if (defaultValue !== undefined) return defaultValue;
      // A dotted key that has no entry prints its LAST segment, readable:
      // "pay.method.manual" -> "Manual", "mp.status.PAID" -> "Paid". The whole
      // key humanized read as "Pay.method.manual" / "Mp.status. P A I D" on
      // screen. Anything else keeps the old camelCase -> Title Case behaviour.
      // Under tests the old loud form stays, so suites that look for a
      // humanized key still catch a missing translation.
      if (process.env.NODE_ENV !== 'test' && /^[\w-]+(\.[\w-]+)+$/.test(key)) {
        const words = key
          .split('.')
          .pop()
          .replace(/[_-]+/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .trim();
        const plain = /^[A-Z0-9 ]+$/.test(words) ? words.toLowerCase() : words;
        return plain.charAt(0).toUpperCase() + plain.slice(1);
      }
      return key
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/^./, (str) => str.toUpperCase());
    },
  });

// `load: 'languageOnly'` normalizes resolvedLanguage and which resources load, but
// it does NOT rewrite `i18n.language` itself: a device locale or a stale localStorage
// cache such as 'ar-SA' / 'en-GB' stays region-coded. ~38 components gate Arabic on a
// strict compare `i18n.language === 'ar'`, which is FALSE for 'ar-SA' (while t() still
// returns Arabic via resolvedLanguage), so their inline strings render English on an
// Arabic UI (e.g. the payment-method and remainder-decision pickers). Normalize the
// active language down to its base code so the strict-compare invariant the rest of
// the app assumes actually holds — this also rewrites the cached value, clearing any
// stale region code permanently.
const normalizeLanguage = () => {
  const lng = i18n.language || '';
  const base = lng.split('-')[0];
  // A device set to something we do not ship — 'fr-FR' — resolves to English
  // for t() but leaves i18n.language as 'fr-FR', which fails the same strict
  // compares and the same toggle. Land on the language actually being rendered.
  const target = ['en', 'ar'].includes(base) ? base : (i18n.resolvedLanguage || 'en');
  if (target && target !== lng) {
    i18n.changeLanguage(target); // fires languageChanged again with the bare code → no loop
  }
};
i18n.on('initialized', normalizeLanguage);
i18n.on('languageChanged', normalizeLanguage);
// And once, right now.
//
// init() resolves synchronously off the in-memory `resources` above, so
// 'initialized' and the first 'languageChanged' have BOTH already fired by the
// time those two listeners are attached. Every normalization missed, and
// i18n.language sat at the raw device locale for the whole session.
//
// That is what made the header's language button need two presses from
// English: the first press read 'en-US', decided it was not 'en', and
// "switched" to English — a no-op the user cannot see. Only then did the
// second press find a bare 'en' and go to Arabic.
normalizeLanguage();

export default i18n;
